/**
 * Lightweight in-webview localization.
 *
 * The UI ships in English by default; the user can switch language from the
 * settings panel. The choice is persisted in localStorage (available in both
 * the VS Code / Visual Studio webviews and the dev browser harness), so it
 * survives reloads without involving the host.
 */

/**
 * A language key inside {@link DICTS}. `en` and `hu` are hand-maintained; every
 * other key is filled in automatically by `KO_language_translator/main.py`
 * (Google Translate) — see the "Translate i18n" workflow. Any key present in
 * {@link LANGUAGES} is valid; unknown keys fall back to English via {@link t}.
 */
export type Lang = string;

/** Literal `"en"` so `DICTS[DEFAULT_LANG]` narrows to the complete dictionary. */
export const DEFAULT_LANG = "en";

/** Keys that need a right-to-left interface. */
const RTL_LANGS = new Set(["ar", "he", "fa", "ur", "ps", "sd", "ug", "ckb", "yi", "dv"]);

/** Whether `lang` is written right-to-left. */
export function isRTL(lang: Lang): boolean {
  return RTL_LANGS.has(lang);
}

/**
 * Languages offered in the settings dropdown, in display order: the two
 * hand-maintained languages first, then every language the translator supports,
 * sorted by native name. Labels are endonyms (the language's own name).
 */
export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "hu", label: "Magyar" },
  { code: "om", label: "Afaan Oromoo" },
  { code: "af", label: "Afrikaans" },
  { code: "gn", label: "Avañe'ẽ" },
  { code: "ay", label: "Aymar aru" },
  { code: "az", label: "Azərbaycanca" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "bm", label: "Bamanankan" },
  { code: "jv", label: "Basa Jawa" },
  { code: "su", label: "Basa Sunda" },
  { code: "bs", label: "Bosanski" },
  { code: "ca", label: "Català" },
  { code: "ceb", label: "Cebuano" },
  { code: "ny", label: "Chichewa" },
  { code: "sn", label: "ChiShona" },
  { code: "co", label: "Corsu" },
  { code: "cy", label: "Cymraeg" },
  { code: "da", label: "Dansk" },
  { code: "de", label: "Deutsch" },
  { code: "et", label: "Eesti" },
  { code: "es", label: "Español" },
  { code: "eo", label: "Esperanto" },
  { code: "eu", label: "Euskara" },
  { code: "ee", label: "Eʋegbe" },
  { code: "tl", label: "Filipino" },
  { code: "fr", label: "Français" },
  { code: "fy", label: "Frysk" },
  { code: "ga", label: "Gaeilge" },
  { code: "sm", label: "Gagana Samoa" },
  { code: "gl", label: "Galego" },
  { code: "gd", label: "Gàidhlig" },
  { code: "ha", label: "Hausa" },
  { code: "hmn", label: "Hmoob" },
  { code: "hr", label: "Hrvatski" },
  { code: "ig", label: "Igbo" },
  { code: "rw", label: "Ikinyarwanda" },
  { code: "ilo", label: "Ilokano" },
  { code: "xh", label: "isiXhosa" },
  { code: "zu", label: "isiZulu" },
  { code: "it", label: "Italiano" },
  { code: "sw", label: "Kiswahili" },
  { code: "ht", label: "Kreyòl ayisyen" },
  { code: "kri", label: "Krio" },
  { code: "ku", label: "Kurdî" },
  { code: "la", label: "Latina" },
  { code: "lv", label: "Latviešu" },
  { code: "lt", label: "Lietuvių" },
  { code: "ln", label: "Lingála" },
  { code: "lg", label: "Luganda" },
  { code: "lb", label: "Lëtzebuergesch" },
  { code: "mg", label: "Malagasy" },
  { code: "mt", label: "Malti" },
  { code: "lus", label: "Mizo ṭawng" },
  { code: "mi", label: "Māori" },
  { code: "nl", label: "Nederlands" },
  { code: "no", label: "Norsk" },
  { code: "uz", label: "Oʻzbekcha" },
  { code: "pl", label: "Polski" },
  { code: "pt", label: "Português" },
  { code: "ro", label: "Română" },
  { code: "qu", label: "Runa Simi" },
  { code: "nso", label: "Sepedi" },
  { code: "st", label: "Sesotho" },
  { code: "sq", label: "Shqip" },
  { code: "sk", label: "Slovenčina" },
  { code: "sl", label: "Slovenščina" },
  { code: "so", label: "Soomaali" },
  { code: "fi", label: "Suomi" },
  { code: "sv", label: "Svenska" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "ak", label: "Twi" },
  { code: "tk", label: "Türkmençe" },
  { code: "tr", label: "Türkçe" },
  { code: "ts", label: "Xitsonga" },
  { code: "yo", label: "Yorùbá" },
  { code: "is", label: "Íslenska" },
  { code: "cs", label: "Čeština" },
  { code: "haw", label: "ʻŌlelo Hawaiʻi" },
  { code: "el", label: "Ελληνικά" },
  { code: "be", label: "Беларуская" },
  { code: "bg", label: "Български" },
  { code: "ky", label: "Кыргызча" },
  { code: "mk", label: "Македонски" },
  { code: "mn", label: "Монгол" },
  { code: "ru", label: "Русский" },
  { code: "sr", label: "Српски" },
  { code: "tt", label: "Татарча" },
  { code: "tg", label: "Тоҷикӣ" },
  { code: "uk", label: "Українська" },
  { code: "kk", label: "Қазақ тілі" },
  { code: "hy", label: "Հայերեն" },
  { code: "yi", label: "ייִדיש" },
  { code: "he", label: "עברית" },
  { code: "ug", label: "ئۇيغۇرچە" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "sd", label: "سنڌي" },
  { code: "fa", label: "فارسی" },
  { code: "ps", label: "پښتو" },
  { code: "ckb", label: "کوردیی ناوەندی" },
  { code: "dv", label: "ދިވެހި" },
  { code: "gom", label: "कोंकणी" },
  { code: "doi", label: "डोगरी" },
  { code: "ne", label: "नेपाली" },
  { code: "bho", label: "भोजपुरी" },
  { code: "mr", label: "मराठी" },
  { code: "mai", label: "मैथिली" },
  { code: "sa", label: "संस्कृतम्" },
  { code: "hi", label: "हिन्दी" },
  { code: "as", label: "অসমীয়া" },
  { code: "bn", label: "বাংলা" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "or", label: "ଓଡ଼ିଆ" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "si", label: "සිංහල" },
  { code: "th", label: "ไทย" },
  { code: "lo", label: "ລາວ" },
  { code: "my", label: "မြန်မာ" },
  { code: "ka", label: "ქართული" },
  { code: "ti", label: "ትግርኛ" },
  { code: "am", label: "አማርኛ" },
  { code: "km", label: "ខ្មែរ" },
  { code: "zh", label: "中文（简体）" },
  { code: "zh-tw", label: "中文（繁體）" },
  { code: "ja", label: "日本語" },
  { code: "mni-mtei", label: "ꯃꯤꯇꯩ ꯂꯣꯟ" },
  { code: "ko", label: "한국어" },
];

/** Fast membership test for {@link isLang}. */
const LANG_CODES = new Set(LANGUAGES.map((l) => l.code));

/** All user-facing strings. `{n}` style placeholders are filled by t(). */
type Dict = {
  "toolbar.refresh": string;
  "toolbar.fetch": string;
  "toolbar.pull": string;
  "toolbar.push": string;
  "toolbar.commit": string;
  "toolbar.sync": string;
  "toolbar.jumpHead": string;
  "toolbar.reset": string;
  "toolbar.settings": string;
  "details.header": string;
  "details.close": string;
  "details.sha": string;
  "details.shortSha": string;
  "details.message": string;
  "details.author": string;
  "details.date": string;
  "details.labels": string;
  "details.location": string;
  "details.currentHead": string;
  "settings.title": string;
  "settings.language": string;
  "settings.theme": string;
  "settings.themeLight": string;
  "settings.themeDark": string;
  "settings.themeLightHint": string;
  "settings.themeDarkHint": string;
  "settings.mainBranch": string;
  "settings.mainBranchAuto": string;
  "settings.mainBranchSearch": string;
  "settings.mainBranchNoMatch": string;
  "settings.display": string;
  "settings.displayModern": string;
  "settings.displayClassic": string;
  "settings.displayModernHint": string;
  "settings.displayClassicHint": string;
  "settings.sectionGeneral": string;
  "settings.sectionGraph": string;
  "settings.sectionChanges": string;
  "settings.diffMinimap": string;
  "settings.diffMinimapOn": string;
  "settings.diffMinimapOff": string;
  "settings.diffMinimapOnHint": string;
  "settings.diffMinimapOffHint": string;
  "settings.commitReview": string;
  "settings.commitReviewOn": string;
  "settings.commitReviewOff": string;
  "settings.commitReviewOnHint": string;
  "settings.commitReviewOffHint": string;
  "settings.svnBranchDialog": string;
  "settings.svnBranchDialogHint": string;
  "settings.branchDialog": string;
  "settings.branchDialogSvnTitle": string;
  "settings.branchDialogNativeVscode": string;
  "settings.branchDialogNativeVs": string;
  "settings.branchDialogNativeHint": string;
  "settings.close": string;
  "settings.done": string;
  "settings.sectionAdvanced": string;
  "settings.gitSource": string;
  "settings.gitSourceBuiltin": string;
  "settings.gitSourceBuiltinHint": string;
  "settings.gitSourceCustom": string;
  "settings.gitSourceCustomHint": string;
  "settings.gitPath": string;
  "settings.gitPathPlaceholder": string;
  "settings.gitPathBrowse": string;
  "settings.jargon": string;
  "settings.jargonTranslate": string;
  "settings.jargonEnglish": string;
  "settings.jargonTranslateHint": string;
  "settings.jargonEnglishHint": string;
  "settings.sectionMerge": string;
  "settings.mergeMode": string;
  "settings.mergeModeMerge": string;
  "settings.mergeModeSquash": string;
  "settings.mergeModeMergeHint": string;
  "settings.mergeModeSquashHint": string;
  "settings.mergedView": string;
  "settings.mergedViewBranch": string;
  "settings.mergedViewTarget": string;
  "settings.mergedViewBranchHint": string;
  "settings.mergedViewTargetHint": string;
  "legend.title": string;
  "legend.head": string;
  "legend.local": string;
  "legend.remote": string;
  "legend.remoteOnly": string;
  "legend.tag": string;
  "legend.commit": string;
  "legend.stash": string;
  "legend.mergedIn": string;
  "legend.nodes": string;
  "legend.lines": string;
  "legend.edgeParent": string;
  "legend.edgeMerge": string;
  "legend.edgeBranch": string;
  "legend.edgeStash": string;
  "legend.edgeMergedChain": string;
  "legend.edgeMergedTie": string;
  "menu.jumpHead": string;
  "menu.resetView": string;
  "menu.createBranch": string;
  "menu.checkout": string;
  "menu.copySha": string;
  "menu.deleteBranch": string;
  "menu.pushBranch": string;
  "menu.renameBranch": string;
  "menu.renameCommit": string;
  "menu.undoCommit": string;
  "menu.viewChanges": string;
  "menu.mergeBranch": string;
  "menu.jumpToOriginal": string;
  "node.mergedInTooltip": string;
  "changes.title": string;
  "changes.tabChanged": string;
  "changes.tabAll": string;
  "changes.added": string;
  "changes.modified": string;
  "changes.deleted": string;
  "changes.renamed": string;
  "changes.noChanges": string;
  "changes.loading": string;
  "changes.selectFile": string;
  "changes.original": string;
  "changes.changed": string;
  "changes.binary": string;
  "changes.tooLarge": string;
  "changes.renamedFrom": string;
  "changes.close": string;
  "changes.prevChange": string;
  "changes.nextChange": string;
  "changes.collapseAll": string;
  "changes.expandAll": string;
  "changes.searchPlaceholder": string;
  "changes.contentSearchOn": string;
  "changes.contentSearchOff": string;
  "changes.noSearchResults": string;
  "changes.maximize": string;
  "changes.restore": string;
  "find.open": string;
  "find.placeholder": string;
  "find.prev": string;
  "find.next": string;
  "find.close": string;
  "find.noResults": string;
  "commit.title": string;
  "commit.close": string;
  "commit.loading": string;
  "commit.noChanges": string;
  "commit.selectFile": string;
  "commit.selectAll": string;
  "commit.selectNone": string;
  "commit.message": string;
  "commit.messagePlaceholder": string;
  "commit.selectedCount": string;
  "commit.review": string;
  "commit.reviewTitle": string;
  "commit.reviewSummary": string;
  "commit.back": string;
  "commit.commit": string;
  "menu.stashApply": string;
  "menu.stashPop": string;
  "menu.stashDrop": string;
  "merge.title": string;
  "merge.route": string;
  "merge.source": string;
  "merge.target": string;
  "merge.loading": string;
  "merge.upToDate": string;
  "merge.fastForward": string;
  "merge.summary": string;
  "merge.conflictsWarning": string;
  "merge.noChanges": string;
  "merge.files": string;
  "merge.added": string;
  "merge.modified": string;
  "merge.deleted": string;
  "merge.conflict": string;
  "merge.message": string;
  "merge.messagePlaceholder": string;
  "merge.messageFfHint": string;
  "merge.noFastForward": string;
  "merge.noFastForwardHint": string;
  "merge.merge": string;
  "merge.cancel": string;
  "merge.previewError": string;
  "merge.squashBadge": string;
  "merge.squashNote": string;
  "merge.messageSquash": string;
  "merge.squashMessagePlaceholder": string;
  "newBranch.title": string;
  "newBranch.startPoint": string;
  "newBranch.startPointOn": string;
  "newBranch.location": string;
  "newBranch.locationRoot": string;
  "newBranch.expandAll": string;
  "newBranch.collapseAll": string;
  "newBranch.expandFolder": string;
  "newBranch.collapseFolder": string;
  "newBranch.name": string;
  "newBranch.namePlaceholder": string;
  "newBranch.fullName": string;
  "newBranch.invalid": string;
  "newBranch.exists": string;
  "newBranch.checkout": string;
  "newBranch.create": string;
  "newBranch.cancel": string;
  "status.loading": string;
  "status.summary": string;
  "status.branchCreated": string;
  "status.error": string;
  "status.opFailed": string;
  "status.fetching": string;
  "status.pulling": string;
  "status.pushing": string;
  "status.committing": string;
  "status.commitCreated": string;
  "status.syncing": string;
  "status.noHead": string;
  "status.undoing": string;
  "status.commitUndone": string;
  "status.undoConflict": string;
  "status.stashApplying": string;
  "status.stashPopping": string;
  "status.stashDropping": string;
  "status.stashApplied": string;
  "status.stashPopped": string;
  "status.stashDropped": string;
  "status.stashConflict": string;
  "status.merging": string;
  "status.merged": string;
  "status.mergeConflict": string;
  "search.title": string;
  "search.tooltip": string;
  "search.placeholder": string;
  "search.previous": string;
  "search.next": string;
  "search.noResults": string;
  "search.resultCount": string;
  "search.filter": string;
  "search.highlight": string;
  "search.filterMode": string;
  "search.filterModeFilter": string;
  "search.filterModeHighlight": string;
  "search.filterModeHint": string;
  "search.visibility": string;
  "search.visibilityToolbar": string;
  "search.visibilityAlwaysVisible": string;
  "search.visibilityHint": string;
  "footer.github": string;
};

// `en` is the complete reference dictionary; `hu` is hand-maintained. Every
// other language is a (possibly empty) machine-filled partial — `t()` falls back
// to English for any key a translation is still missing.
const DICTS: Record<string, Partial<Dict>> & { en: Dict } = {
  en: {
    "toolbar.refresh": "⟳ Refresh",
    "toolbar.fetch": "⤓ Fetch",
    "toolbar.pull": "⇩ Pull",
    "toolbar.push": "⇧ Push",
    "toolbar.commit": "✓ Commit",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Go to checkout",
    "toolbar.reset": "⤢ Reset view",
    "toolbar.settings": "⚙ Settings",
    "details.header": "Commit Details",
    "details.close": "Close",
    "details.sha": "SHA",
    "details.shortSha": "Short SHA",
    "details.message": "Message",
    "details.author": "Author",
    "details.date": "Date",
    "details.labels": "Labels",
    "details.location": "Location",
    "details.currentHead": "HEAD — current checkout",
    "settings.title": "Settings",
    "settings.language": "Language",
    "settings.theme": "Theme",
    "settings.themeLight": "Light",
    "settings.themeDark": "Dark",
    "settings.themeLightHint": "Light background, dark text.",
    "settings.themeDarkHint": "Dark background, light text.",
    "settings.mainBranch": "Main branch",
    "settings.mainBranchAuto": "Automatic",
    "settings.mainBranchSearch": "Search branches…",
    "settings.mainBranchNoMatch": "No matching branches",
    "settings.display": "Display style",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Free canvas — drag to pan, scroll to zoom.",
    "settings.displayClassicHint": "Fixed canvas — trunk pinned left, no zoom, scroll only (like the SVN revision graph).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Graph",
    "settings.sectionChanges": "Changes view",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Shown",
    "settings.diffMinimapOff": "Hidden",
    "settings.diffMinimapOnHint": "Show a VS Code-style overview strip beside the diff — drag it to scroll; change markers included.",
    "settings.diffMinimapOffHint": "No overview strip — scroll the diff normally.",
    "settings.commitReview": "Commit review",
    "settings.commitReviewOn": "Ask before commit",
    "settings.commitReviewOff": "Commit directly",
    "settings.commitReviewOnHint": "Show a final file list and message check before creating the local commit.",
    "settings.commitReviewOffHint": "The Commit button creates the local commit immediately.",
    "settings.svnBranchDialog": "SVN-style branch dialog",
    "settings.svnBranchDialogHint": "Show a folder-tree picker when creating a branch (instead of a simple prompt).",
    "settings.branchDialog": "Branch dialog",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "VS Code style",
    "settings.branchDialogNativeVs": "Visual Studio style",
    "settings.branchDialogNativeHint": "Your IDE's built-in branch prompt.",
    "settings.close": "Close",
    "settings.done": "Done",
    "settings.sectionAdvanced": "Advanced",
    "settings.gitSource": "Git executable",
    "settings.gitSourceBuiltin": "Built-in",
    "settings.gitSourceBuiltinHint": "Use the git binary from the IDE's built-in Git extension.",
    "settings.gitSourceCustom": "Custom path",
    "settings.gitSourceCustomHint": "Specify the path to your own git executable (e.g. /usr/bin/git or C:\\…\\git.exe).",
    "settings.gitPath": "Path to git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Browse…",
    "settings.jargon": "Git terms",
    "settings.jargonTranslate": "Translate",
    "settings.jargonEnglish": "Keep in English",
    "settings.jargonTranslateHint": "Translate Git terms (pull, push, commit, branch…) into the interface language.",
    "settings.jargonEnglishHint": "Keep Git terms (pull, push, commit, branch…) in English; translate everything else.",
    "settings.sectionMerge": "Merge",
    "settings.mergeMode": "Merge style",
    "settings.mergeModeMerge": "Normal merge",
    "settings.mergeModeSquash": "Squash merge",
    "settings.mergeModeMergeHint": "Merges the branch as it is: its commits become part of the current branch's history, joined by a merge commit (or fast-forwarded when possible).",
    "settings.mergeModeSquashHint": "Collapses the whole branch into a single commit on the current branch (git merge --squash). The branch's own commits never enter the current branch's history — you only see one commit there. The branch itself is left untouched, so its commits stay visible in its own lane.",
    "settings.mergedView": "Merged commits in the graph",
    "settings.mergedViewBranch": "Only on their own branch",
    "settings.mergedViewTarget": "Also in the branch they were merged into",
    "settings.mergedViewBranchHint": "A merged branch's commits are drawn only in their own lane; the merge shows up as a connector. The classic view.",
    "settings.mergedViewTargetHint": "The commits a merge brought in are drawn in the receiving branch's lane as well, stacked under the merge commit and level with the originals — because that branch really does contain them now. The copies are pale and headed with the branch they were written on, so it stays clear they were not made there. Squash merges are unaffected: a squash writes one ordinary commit, so only that commit ever appears.",
    "legend.title": "Legend",
    "legend.head": "HEAD / current branch",
    "legend.local": "Local branch",
    "legend.remote": "Remote branch",
    "legend.remoteOnly": "Only in the cloud (not pulled)",
    "legend.tag": "Tag (version)",
    "legend.commit": "Commit",
    "legend.stash": "Stash (shelved work)",
    "legend.mergedIn": "Came in with a merge (written on another branch)",
    "legend.nodes": "Boxes",
    "legend.lines": "Lines",
    "legend.edgeParent": "Previous commit on the same line",
    "legend.edgeMerge": "Merged-in branch — the arrow points at the merge commit",
    "legend.edgeBranch": "A branch forked off here",
    "legend.edgeStash": "Stash and the commit it was shelved on",
    "legend.edgeMergedChain": "This branch's line running through the merged-in commits",
    "legend.edgeMergedTie": "The same commit on the branch it was written on",
    "menu.jumpHead": "⌖ Go to checkout",
    "menu.resetView": "⤢ Reset view",
    "menu.createBranch": "Create branch from here…",
    "menu.checkout": "Checkout this commit",
    "menu.copySha": "Copy commit SHA",
    "menu.pushBranch": "Push branch \"{name}\"",
    "menu.renameBranch": "Rename branch \"{name}\"…",
    "menu.deleteBranch": "Delete branch \"{name}\"…",
    "menu.renameCommit": "Rename commit message…",
    "menu.undoCommit": "Undo commit (keep changes)…",
    "menu.viewChanges": "View changes…",
    "menu.mergeBranch": "Merge \"{source}\" into \"{target}\"…",
    "menu.jumpToOriginal": "Go to the original commit",
    "node.mergedInTooltip": "Merge {merge} brought this commit into \"{target}\" — it was written on \"{origin}\".",
    "changes.title": "Changes in {sha}",
    "changes.tabChanged": "Changed",
    "changes.tabAll": "All Files",
    "changes.added": "Added",
    "changes.modified": "Modified",
    "changes.deleted": "Deleted",
    "changes.renamed": "Renamed",
    "changes.noChanges": "This commit changes no files.",
    "changes.loading": "Loading diff…",
    "changes.selectFile": "Select a file to see its changes.",
    "changes.original": "Original",
    "changes.changed": "This commit",
    "changes.binary": "Binary file — no text diff to show.",
    "changes.tooLarge": "File is too large to diff.",
    "changes.renamedFrom": "Renamed from {path}",
    "changes.close": "Close",
    "changes.prevChange": "Previous change",
    "changes.nextChange": "Next change",
    "changes.collapseAll": "Collapse all folders",
    "changes.expandAll": "Expand all folders",
    "changes.searchPlaceholder": "Search files…",
    "changes.contentSearchOn": "Search in file contents too (methods, identifiers): on",
    "changes.contentSearchOff": "Search in file contents too (methods, identifiers): off",
    "changes.noSearchResults": "No files match your search.",
    "changes.maximize": "Maximize",
    "changes.restore": "Restore size",
    "find.open": "Search in this diff (Ctrl+F)",
    "find.placeholder": "Find in diff…",
    "find.prev": "Previous match",
    "find.next": "Next match",
    "find.close": "Close search",
    "find.noResults": "No results",
    "commit.title": "Commit Changes",
    "commit.close": "Close",
    "commit.loading": "Reading working tree…",
    "commit.noChanges": "There are no local changes to commit.",
    "commit.selectFile": "Select a file to see its diff.",
    "commit.selectAll": "Select all",
    "commit.selectNone": "Clear all",
    "commit.message": "Commit message",
    "commit.messagePlaceholder": "Describe the change",
    "commit.selectedCount": "{count} file(s) selected",
    "commit.review": "Review",
    "commit.reviewTitle": "Final check before commit",
    "commit.reviewSummary": "Commit {count} file(s) with message: {message}",
    "commit.back": "Back",
    "commit.commit": "Create local commit",
    "menu.stashApply": "Apply stash",
    "menu.stashPop": "Pop stash (apply & remove)",
    "menu.stashDrop": "Drop stash…",
    "merge.title": "Merge Branch",
    "merge.route": "Merging into the current branch",
    "merge.source": "From (source)",
    "merge.target": "Into (current)",
    "merge.loading": "Analyzing merge…",
    "merge.upToDate": "Already up to date — nothing to merge.",
    "merge.fastForward": "Fast-forward — no merge commit needed (tick “Create a merge commit” to force one).",
    "merge.summary": "{files} file(s) changed, {conflicts} conflict(s).",
    "merge.conflictsWarning": "This merge has conflicts — you can still merge, then resolve them in the editor.",
    "merge.noChanges": "No file changes.",
    "merge.files": "Resulting changes",
    "merge.added": "Added",
    "merge.modified": "Modified",
    "merge.deleted": "Deleted",
    "merge.conflict": "Conflict",
    "merge.message": "Merge commit message",
    "merge.messagePlaceholder": "Merge branch 'source'",
    "merge.messageFfHint": "Ignored on a fast-forward merge (no commit is created).",
    "merge.noFastForward": "Create a merge commit (no fast-forward)",
    "merge.noFastForwardHint": "Always record a merge commit, even when the branch could fast-forward.",
    "merge.merge": "Merge",
    "merge.cancel": "Cancel",
    "merge.previewError": "Couldn't compute a preview: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge — every change lands as ONE commit on “{target}”; “{source}”'s own commits stay out of its history. Switch back in Settings › Merge.",
    "merge.messageSquash": "Commit message",
    "merge.squashMessagePlaceholder": "Squashed changes from 'source'",
    "newBranch.title": "Create Branch",
    "newBranch.startPoint": "New branch starting from {sha}",
    "newBranch.startPointOn": "on {refs}",
    "newBranch.location": "Location",
    "newBranch.locationRoot": "(root)",
    "newBranch.expandAll": "Expand all",
    "newBranch.collapseAll": "Collapse all",
    "newBranch.expandFolder": "Expand folder",
    "newBranch.collapseFolder": "Collapse folder",
    "newBranch.name": "Branch name",
    "newBranch.namePlaceholder": "my-branch",
    "newBranch.fullName": "Full name: {name}",
    "newBranch.invalid": "Invalid branch name",
    "newBranch.exists": "A branch with this name already exists",
    "newBranch.checkout": "Checkout branch after creation",
    "newBranch.create": "Create",
    "newBranch.cancel": "Cancel",
    "status.loading": "Loading graph…",
    "status.summary": "{repo}Showing {commits} commits, {refs} refs",
    "status.branchCreated": "Created branch \"{name}\" at {sha}",
    "status.error": "Error: {message}",
    "status.opFailed": "Operation failed",
    "status.fetching": "Fetching…",
    "status.pulling": "Pulling…",
    "status.pushing": "Pushing…",
    "status.committing": "Creating local commit…",
    "status.commitCreated": "Created local commit {sha}: {message}",
    "status.syncing": "Syncing…",
    "status.noHead": "No current checkout found in the graph.",
    "status.undoing": "Undoing commit…",
    "status.commitUndone": "Commit undone — changes are back in the working tree.",
    "status.undoConflict": "Undo hit conflicts — resolve them in the editor, then continue.",
    "status.stashApplying": "Applying stash…",
    "status.stashPopping": "Popping stash…",
    "status.stashDropping": "Dropping stash…",
    "status.stashApplied": "Stash applied.",
    "status.stashPopped": "Stash popped.",
    "status.stashDropped": "Stash dropped.",
    "status.stashConflict": "Stash conflicts — resolve them in the editor.",
    "status.merging": "Merging…",
    "status.merged": "Merge completed.",
    "status.mergeConflict": "Merge has conflicts — resolve them in the editor, then commit.",
    "search.title": "Search",
    "search.tooltip": "Search commits",
    "search.placeholder": "Search commits…",
    "search.previous": "Previous result",
    "search.next": "Next result",
    "search.noResults": "No results",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filter mode",
    "search.highlight": "Highlight mode",
    "search.filterMode": "Search display",
    "search.filterModeFilter": "Filter (hide non-matches)",
    "search.filterModeHighlight": "Highlight (show all, mark matches)",
    "search.filterModeHint": "Choose how search results are displayed in the graph.",
    "search.visibility": "Search location",
    "search.visibilityToolbar": "Toolbar button",
    "search.visibilityAlwaysVisible": "Always-visible bar",
    "search.visibilityHint": "Show search as a toolbar icon or persistent bar at the top.",
    "footer.github": "GitHub",
  },
  hu: {
    "toolbar.refresh": "⟳ Frissítés",
    "toolbar.fetch": "⤓ Fetch",
    "toolbar.pull": "⇩ Pull",
    "toolbar.push": "⇧ Push",
    "toolbar.commit": "✓ Commit",
    "toolbar.sync": "⇅ Szinkron",
    "toolbar.jumpHead": "⌖ Ugrás a checkout-ra",
    "toolbar.reset": "⤢ Nézet visszaállítása",
    "toolbar.settings": "⚙ Beállítások",
    "details.header": "Commit részletei",
    "details.close": "Bezárás",
    "details.sha": "SHA",
    "details.shortSha": "Rövid SHA",
    "details.message": "Üzenet",
    "details.author": "Szerző",
    "details.date": "Dátum",
    "details.labels": "Címkék",
    "details.location": "Elhelyezkedés",
    "details.currentHead": "HEAD — itt áll a kód",
    "settings.title": "Beállítások",
    "settings.language": "Nyelv",
    "settings.theme": "Téma",
    "settings.themeLight": "Világos",
    "settings.themeDark": "Sötét",
    "settings.themeLightHint": "Világos háttér, sötét szöveg.",
    "settings.themeDarkHint": "Sötét háttér, világos szöveg.",
    "settings.mainBranch": "Fő ág",
    "settings.mainBranchAuto": "Automatikus",
    "settings.mainBranchSearch": "Ágak keresése…",
    "settings.mainBranchNoMatch": "Nincs találat",
    "settings.display": "Megjelenítés stílusa",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Klasszikus",
    "settings.displayModernHint": "Szabad vászon — húzással mozgatható, görgővel nagyítható.",
    "settings.displayClassicHint": "Rögzített vászon — a fő ág balra rögzítve, nincs nagyítás, csak görgetés (mint az SVN revision graph).",
    "settings.sectionGeneral": "Általános",
    "settings.sectionGraph": "Gráf",
    "settings.sectionChanges": "Változások nézet",
    "settings.diffMinimap": "Diff minitérkép",
    "settings.diffMinimapOn": "Látható",
    "settings.diffMinimapOff": "Rejtett",
    "settings.diffMinimapOnHint": "VS Code-stílusú áttekintő sáv a diff mellett — húzva görgethető; a változásokat is jelöli.",
    "settings.diffMinimapOffHint": "Nincs áttekintő sáv — a diff hagyományosan görgethető.",
    "settings.commitReview": "Commit előtti ellenőrzés",
    "settings.commitReviewOn": "Rákérdezés",
    "settings.commitReviewOff": "Azonnali commit",
    "settings.commitReviewOnHint": "Commit előtt még egyszer megjelenik a fájllista és az üzenet ellenőrzésre.",
    "settings.commitReviewOffHint": "A Commit gomb rögtön létrehozza a lokális commitot.",
    "settings.svnBranchDialog": "SVN-stílusú branch ablak",
    "settings.svnBranchDialogHint": "Branch létrehozásakor mappafa-választó jelenjen meg (egyszerű beírás helyett).",
    "settings.branchDialog": "Branch ablak",
    "settings.branchDialogSvnTitle": "SVN-stílus",
    "settings.branchDialogNativeVscode": "VS Code stílus",
    "settings.branchDialogNativeVs": "Visual Studio stílus",
    "settings.branchDialogNativeHint": "Az IDE beépített branch ablaka.",
    "settings.close": "Bezárás",
    "settings.done": "Kész",
    "settings.sectionAdvanced": "Speciális",
    "settings.gitSource": "Git futtatható fájl",
    "settings.gitSourceBuiltin": "Beépített",
    "settings.gitSourceBuiltinHint": "Az IDE beépített Git bővítményének git binárisát használja.",
    "settings.gitSourceCustom": "Egyéni útvonal",
    "settings.gitSourceCustomHint": "Adja meg a saját git futtatható fájl elérési útját (pl. /usr/bin/git vagy C:\\…\\git.exe).",
    "settings.gitPath": "Git elérési útja",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Tallózás…",
    "settings.jargon": "Git szakkifejezések",
    "settings.jargonTranslate": "Fordítás",
    "settings.jargonEnglish": "Angolul hagyás",
    "settings.jargonTranslateHint": "A git szakszavak (pull, push, commit, branch…) lefordítása a felület nyelvére.",
    "settings.jargonEnglishHint": "A git szakszavak (pull, push, commit, branch…) angolul maradnak; minden más lefordítva.",
    "settings.sectionMerge": "Beolvasztás (merge)",
    "settings.mergeMode": "Merge módja",
    "settings.mergeModeMerge": "Normál merge",
    "settings.mergeModeSquash": "Squash merge",
    "settings.mergeModeMergeHint": "Az ágat úgy olvasztja be, ahogy van: a commitjai bekerülnek az aktuális ág történetébe, egy merge commit köti őket össze (vagy fast-forward, ha lehet).",
    "settings.mergeModeSquashHint": "Az egész ágat egyetlen commitba vonja össze az aktuális ágon (git merge --squash). Az ág saját commitjai nem kerülnek be az aktuális ág történetébe — ott csak egy commit látszik. Magát az ágat nem bántja, így annak commitjai a saját oszlopukban továbbra is látszanak.",
    "settings.mergedView": "Beolvasztott commitok a gráfon",
    "settings.mergedViewBranch": "Csak a saját águkon",
    "settings.mergedViewTarget": "Abban az ágban is, amelybe beolvadtak",
    "settings.mergedViewBranchHint": "A beolvasztott ág commitjai csak a saját sávjukban látszanak, a merge pedig összekötő vonalként. A megszokott nézet.",
    "settings.mergedViewTargetHint": "A merge-csel behozott commitok a fogadó ág sávjában is megjelennek, a merge commit alatt, az eredetivel egy sorban — mert az az ág valóban tartalmazza őket. A másolatok halvány hátterűek, és annak az ágnak a nevét viselik, amelyen készültek, így egyértelmű marad, hogy nem ott jöttek létre. A squash merge-re nincs hatással: a squash egyetlen közönséges commitot ír, tehát csak az az egy commit látszik.",
    "legend.title": "Jelmagyarázat",
    "legend.head": "HEAD / aktuális branch",
    "legend.local": "Lokális branch",
    "legend.remote": "Távoli branch (remote)",
    "legend.remoteOnly": "Csak a felhőben (nincs pull-olva)",
    "legend.tag": "Tag (verzió)",
    "legend.commit": "Commit",
    "legend.stash": "Stash (félretett munka)",
    "legend.mergedIn": "Merge-csel került ide (másik ágon készült)",
    "legend.nodes": "Dobozok",
    "legend.lines": "Vonalak",
    "legend.edgeParent": "Előző commit ugyanazon a vonalon",
    "legend.edgeMerge": "Beolvasztott branch — a nyíl a merge-commitra mutat",
    "legend.edgeBranch": "Innen ágazik le egy branch",
    "legend.edgeStash": "Stash és a commit, amire félre lett téve",
    "legend.edgeMergedChain": "Ennek a branchnek a vonala a beolvasztott commitokon át",
    "legend.edgeMergedTie": "Ugyanaz a commit azon a branchen, ahol készült",
    "menu.jumpHead": "⌖ Ugrás a checkout-ra",
    "menu.resetView": "⤢ Nézet visszaállítása",
    "menu.createBranch": "Branch létrehozása innen…",
    "menu.checkout": "Checkout erre a commitra",
    "menu.copySha": "Commit SHA másolása",
    "menu.pushBranch": "\"{name}\" branch pusholása",
    "menu.renameBranch": "\"{name}\" branch átnevezése…",
    "menu.deleteBranch": "\"{name}\" branch törlése…",
    "menu.renameCommit": "Commit üzenet átnevezése…",
    "menu.undoCommit": "Commit visszavonása (változások megtartása)…",
    "menu.viewChanges": "Változások megtekintése…",
    "menu.mergeBranch": "\"{source}\" beolvasztása ide: \"{target}\"…",
    "menu.jumpToOriginal": "Ugrás az eredeti commitra",
    "node.mergedInTooltip": "Ezt a commitot a(z) {merge} merge hozta be a(z) „{target}” ágba — eredetileg a(z) „{origin}” ágon készült.",
    "changes.title": "Változások — {sha}",
    "changes.tabChanged": "Változott",
    "changes.tabAll": "Összes fájl",
    "changes.added": "Hozzáadva",
    "changes.modified": "Módosítva",
    "changes.deleted": "Törölve",
    "changes.renamed": "Átnevezve",
    "changes.noChanges": "Ez a commit nem módosít fájlokat.",
    "changes.loading": "Diff betöltése…",
    "changes.selectFile": "Válassz egy fájlt a változások megtekintéséhez.",
    "changes.original": "Eredeti",
    "changes.changed": "Ebben a commitban",
    "changes.binary": "Bináris fájl — nincs szöveges diff.",
    "changes.tooLarge": "A fájl túl nagy a diff megjelenítéséhez.",
    "changes.renamedFrom": "Átnevezve innen: {path}",
    "changes.close": "Bezárás",
    "changes.prevChange": "Előző változás",
    "changes.nextChange": "Következő változás",
    "changes.collapseAll": "Minden mappa becsukása",
    "changes.expandAll": "Minden mappa kinyitása",
    "changes.searchPlaceholder": "Fájlok keresése…",
    "changes.contentSearchOn": "Keresés a fájlok tartalmában is (metódusok, azonosítók): be",
    "changes.contentSearchOff": "Keresés a fájlok tartalmában is (metódusok, azonosítók): ki",
    "changes.noSearchResults": "Nincs a keresésnek megfelelő fájl.",
    "changes.maximize": "Nagyítás",
    "changes.restore": "Eredeti méret",
    "find.open": "Keresés ebben a diffben (Ctrl+F)",
    "find.placeholder": "Keresés a diffben…",
    "find.prev": "Előző találat",
    "find.next": "Következő találat",
    "find.close": "Keresés bezárása",
    "find.noResults": "Nincs találat",
    "commit.title": "Változások commitolása",
    "commit.close": "Bezárás",
    "commit.loading": "Working tree beolvasása…",
    "commit.noChanges": "Nincs lokális változás, amit commitolni lehetne.",
    "commit.selectFile": "Válassz egy fájlt a diff megtekintéséhez.",
    "commit.selectAll": "Összes kijelölése",
    "commit.selectNone": "Kijelölés törlése",
    "commit.message": "Commit üzenet",
    "commit.messagePlaceholder": "Írd le a változást",
    "commit.selectedCount": "{count} fájl kijelölve",
    "commit.review": "Ellenőrzés",
    "commit.reviewTitle": "Utolsó ellenőrzés commit előtt",
    "commit.reviewSummary": "{count} fájl commitolása ezzel az üzenettel: {message}",
    "commit.back": "Vissza",
    "commit.commit": "Lokális commit létrehozása",
    "menu.stashApply": "Stash alkalmazása",
    "menu.stashPop": "Stash kivétele (alkalmaz és töröl)",
    "menu.stashDrop": "Stash eldobása…",
    "merge.title": "Branch beolvasztása (merge)",
    "merge.route": "Beolvasztás az aktuális branchbe",
    "merge.source": "Honnan (forrás)",
    "merge.target": "Ide (aktuális)",
    "merge.loading": "Merge elemzése…",
    "merge.upToDate": "Már naprakész — nincs mit beolvasztani.",
    "merge.fastForward": "Fast-forward — nem kell merge commit (pipáld be a „Merge commit létrehozása”-t, ha mégis kell).",
    "merge.summary": "{files} fájl változik, {conflicts} konfliktus.",
    "merge.conflictsWarning": "Ennek a merge-nek vannak konfliktusai — beolvaszthatod, majd a szerkesztőben feloldhatod őket.",
    "merge.noChanges": "Nincs fájlváltozás.",
    "merge.files": "Eredő változások",
    "merge.added": "Hozzáadva",
    "merge.modified": "Módosítva",
    "merge.deleted": "Törölve",
    "merge.conflict": "Konfliktus",
    "merge.message": "Merge commit üzenete",
    "merge.messagePlaceholder": "Merge branch 'forrás'",
    "merge.messageFfHint": "Fast-forward merge esetén nincs hatása (nem jön létre commit).",
    "merge.noFastForward": "Merge commit létrehozása (nincs fast-forward)",
    "merge.noFastForwardHint": "Mindig készüljön merge commit, akkor is, ha lehetne fast-forward.",
    "merge.merge": "Beolvasztás",
    "merge.cancel": "Mégse",
    "merge.previewError": "Az előnézet nem számítható ki: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge — minden változás EGYETLEN commitként kerül a(z) „{target}” ágra; a(z) „{source}” saját commitjai nem kerülnek be annak történetébe. A Beállítások › Beolvasztás (merge) alatt állítható vissza.",
    "merge.messageSquash": "Commit üzenet",
    "merge.squashMessagePlaceholder": "A 'source' ág változásai egy commitban",
    "newBranch.title": "Branch létrehozása",
    "newBranch.startPoint": "Új branch innen indul: {sha}",
    "newBranch.startPointOn": "({refs})",
    "newBranch.location": "Hely",
    "newBranch.locationRoot": "(gyökér)",
    "newBranch.expandAll": "Mind kinyit",
    "newBranch.collapseAll": "Mind becsuk",
    "newBranch.expandFolder": "Mappa kinyitása",
    "newBranch.collapseFolder": "Mappa becsukása",
    "newBranch.name": "Branch neve",
    "newBranch.namePlaceholder": "uj-branch",
    "newBranch.fullName": "Teljes név: {name}",
    "newBranch.invalid": "Érvénytelen branch név",
    "newBranch.exists": "Már létezik ilyen nevű branch",
    "newBranch.checkout": "Checkout a branch-re létrehozás után",
    "newBranch.create": "Létrehozás",
    "newBranch.cancel": "Mégse",
    "status.loading": "Gráf betöltése…",
    "status.summary": "{repo}{commits} commit, {refs} ref látható",
    "status.branchCreated": "\"{name}\" branch létrehozva itt: {sha}",
    "status.error": "Hiba: {message}",
    "status.opFailed": "A művelet sikertelen",
    "status.fetching": "Fetch folyamatban…",
    "status.pulling": "Pull folyamatban…",
    "status.pushing": "Push folyamatban…",
    "status.committing": "Lokális commit létrehozása…",
    "status.commitCreated": "Lokális commit létrejött {sha}: {message}",
    "status.syncing": "Szinkronizálás…",
    "status.noHead": "Nincs aktuális checkout a gráfban.",
    "status.undoing": "Commit visszavonása…",
    "status.commitUndone": "Commit visszavonva — a változások visszakerültek a working tree-be.",
    "status.undoConflict": "A visszavonás konfliktusba ütközött — oldd fel a szerkesztőben, majd folytasd.",
    "status.stashApplying": "Stash alkalmazása…",
    "status.stashPopping": "Stash kivétele…",
    "status.stashDropping": "Stash eldobása…",
    "status.stashApplied": "Stash alkalmazva.",
    "status.stashPopped": "Stash kivéve.",
    "status.stashDropped": "Stash eldobva.",
    "status.stashConflict": "Stash konfliktus — oldd fel a szerkesztőben.",
    "status.merging": "Beolvasztás…",
    "status.merged": "A merge elkészült.",
    "status.mergeConflict": "A merge konfliktusos — oldd fel a szerkesztőben, majd commitold.",
    "search.title": "Keresés",
    "search.tooltip": "Commitok keresése",
    "search.placeholder": "Commitok keresése…",
    "search.previous": "Előző találat",
    "search.next": "Következő találat",
    "search.noResults": "Nincs találat",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Szűrés mód",
    "search.highlight": "Kiemelés mód",
    "search.filterMode": "Keresési megjelenítés",
    "search.filterModeFilter": "Szűrés (nem egyezők elrejtése)",
    "search.filterModeHighlight": "Kiemelés (összes mutatása, egyezők jelölése)",
    "search.filterModeHint": "Válaszd ki, hogyan jelenjenek meg a keresési eredmények a gráfban.",
    "search.visibility": "Keresés helye",
    "search.visibilityToolbar": "Toolbar gomb",
    "search.visibilityAlwaysVisible": "Mindig látható sáv",
    "search.visibilityHint": "A keresést toolbar ikonként vagy állandó sávként mutasd.",
    "footer.github": "GitHub",
  },
  zh: {
    "toolbar.refresh": "⟳ 刷新",
    "toolbar.fetch": "⤓ 抓取",
    "toolbar.pull": "⇩ 拉取",
    "toolbar.push": "⇧ 推送",
    "toolbar.commit": "✓ 提交",
    "toolbar.sync": "⇅ 同步",
    "toolbar.jumpHead": "⌖ 跳转到检出位置",
    "toolbar.reset": "⤢ 重置视图",
    "toolbar.settings": "⚙ 设置",
    "details.header": "提交详情",
    "details.close": "关闭",
    "details.sha": "SHA",
    "details.shortSha": "短 SHA",
    "details.message": "信息",
    "details.author": "作者",
    "details.date": "日期",
    "details.labels": "标签",
    "details.location": "位置",
    "details.currentHead": "HEAD — 当前检出",
    "settings.title": "设置",
    "settings.language": "语言",
    "settings.theme": "主题",
    "settings.themeLight": "浅色",
    "settings.themeDark": "深色",
    "settings.themeLightHint": "浅色背景，深色文字。",
    "settings.themeDarkHint": "深色背景，浅色文字。",
    "settings.mainBranch": "主分支",
    "settings.mainBranchAuto": "自动",
    "settings.mainBranchSearch": "搜索分支…",
    "settings.mainBranchNoMatch": "没有匹配的分支",
    "settings.display": "显示样式",
    "settings.displayModern": "现代",
    "settings.displayClassic": "经典",
    "settings.displayModernHint": "自由画布 — 拖动平移，滚动缩放。",
    "settings.displayClassicHint": "固定画布 — 主干固定在左侧，无缩放，仅滚动（类似 SVN 修订图）。",
    "settings.sectionGeneral": "常规",
    "settings.sectionGraph": "图形",
    "settings.sectionChanges": "更改视图",
    "settings.diffMinimap": "差异缩略图",
    "settings.diffMinimapOn": "显示",
    "settings.diffMinimapOff": "隐藏",
    "settings.diffMinimapOnHint": "在差异旁显示 VS Code 风格的概览条 — 拖动它可滚动；包含更改标记。",
    "settings.diffMinimapOffHint": "无概览条 — 正常滚动差异。",
    "settings.commitReview": "提交前检查",
    "settings.commitReviewOn": "提交前询问",
    "settings.commitReviewOff": "直接提交",
    "settings.commitReviewOnHint": "创建本地提交前显示最终文件列表和提交信息。",
    "settings.commitReviewOffHint": "Commit 按钮会立即创建本地提交。",
    "settings.svnBranchDialog": "SVN 风格分支对话框",
    "settings.svnBranchDialogHint": "创建分支时显示文件夹树选择器（而非简单的输入框）。",
    "settings.branchDialog": "分支对话框",
    "settings.branchDialogSvnTitle": "SVN 风格",
    "settings.branchDialogNativeVscode": "VS Code 风格",
    "settings.branchDialogNativeVs": "Visual Studio 风格",
    "settings.branchDialogNativeHint": "您 IDE 内置的分支输入框。",
    "settings.close": "关闭",
    "settings.done": "完成",
    "settings.sectionAdvanced": "高级",
    "settings.gitSource": "Git 可执行文件",
    "settings.gitSourceBuiltin": "内置",
    "settings.gitSourceBuiltinHint": "使用 IDE 内置 Git 扩展的 git 二进制文件。",
    "settings.gitSourceCustom": "自定义路径",
    "settings.gitSourceCustomHint": "指定您自己的 git 可执行文件路径（例如 /usr/bin/git 或 C:\\…\\git.exe）。",
    "settings.gitPath": "Git 路径",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "浏览…",
    "settings.jargon": "Git 术语",
    "settings.jargonTranslate": "翻译",
    "settings.jargonEnglish": "保留英文",
    "settings.jargonTranslateHint": "将 Git 术语（pull、push、commit、branch…）翻译成界面语言。",
    "settings.jargonEnglishHint": "Git 术语（pull、push、commit、branch…）保留英文；其余全部翻译。",
    "settings.sectionMerge": "合并",
    "settings.mergeMode": "合并方式",
    "settings.mergeModeMerge": "普通合并",
    "settings.mergeModeSquash": "压缩合并（squash）",
    "settings.mergeModeMergeHint": "按原样合并分支：它的提交会成为当前分支历史的一部分，由一个合并提交连接（可以快进时则快进）。",
    "settings.mergeModeSquashHint": "把整个分支压缩成当前分支上的一个提交（git merge --squash）。分支自己的提交不会进入当前分支的历史——那里只看到一个提交。分支本身保持不变，它的提交仍显示在自己的列中。",
    "settings.mergedView": "图中的已合并提交",
    "settings.mergedViewBranch": "仅显示在各自的分支上",
    "settings.mergedViewTarget": "也显示在被合入的分支中",
    "settings.mergedViewBranchHint": "被合并分支的提交只画在自己的泳道里，合并本身显示为一条连接线。这是经典视图。",
    "settings.mergedViewTargetHint": "合并带入的提交同时画在接收分支的泳道中，堆叠在合并提交下方，与原始提交同一行——因为该分支确实已经包含它们。副本使用浅色底并标注它们真正被写入的分支，因此不会被误认为是在此处创建的。Squash 合并不受影响：squash 只写入一个普通提交，因此只会出现那一个提交。",
    "legend.title": "图例",
    "legend.head": "HEAD / 当前分支",
    "legend.local": "本地分支",
    "legend.remote": "远程分支",
    "legend.remoteOnly": "仅在云端（尚未拉取）",
    "legend.tag": "标签（版本）",
    "legend.commit": "提交",
    "legend.stash": "储藏（暂存的工作）",
    "legend.mergedIn": "随合并进入（在其他分支上创建）",
    "legend.nodes": "方框",
    "legend.lines": "连线",
    "legend.edgeParent": "同一条线上的上一个提交",
    "legend.edgeMerge": "被合并进来的分支——箭头指向合并提交",
    "legend.edgeBranch": "从这里分出了一个分支",
    "legend.edgeStash": "储藏及其所基于的提交",
    "legend.edgeMergedChain": "本分支的线穿过被合并进来的提交",
    "legend.edgeMergedTie": "同一个提交在它实际创建的分支上",
    "menu.jumpHead": "⌖ 跳转到检出位置",
    "menu.resetView": "⤢ 重置视图",
    "menu.createBranch": "从此处创建分支…",
    "menu.checkout": "检出此提交",
    "menu.copySha": "复制提交 SHA",
    "menu.pushBranch": "推送分支 “{name}”",
    "menu.renameBranch": "重命名分支 “{name}”…",
    "menu.deleteBranch": "删除分支 “{name}”…",
    "menu.renameCommit": "重命名提交信息…",
    "menu.undoCommit": "撤销提交（保留更改）…",
    "menu.viewChanges": "查看更改…",
    "menu.mergeBranch": "将 “{source}” 合并到 “{target}”…",
    "menu.jumpToOriginal": "跳转到原始提交",
    "node.mergedInTooltip": "合并 {merge} 将此提交带入 “{target}” — 它是在 “{origin}” 上创建的。",
    "changes.title": "{sha} 中的更改",
    "changes.tabChanged": "已更改",
    "changes.tabAll": "所有文件",
    "changes.added": "已添加",
    "changes.modified": "已修改",
    "changes.deleted": "已删除",
    "changes.renamed": "已重命名",
    "changes.noChanges": "此提交未更改任何文件。",
    "changes.loading": "正在加载差异…",
    "changes.selectFile": "选择一个文件以查看其更改。",
    "changes.original": "原始",
    "changes.changed": "此提交",
    "changes.binary": "二进制文件 — 无文本差异可显示。",
    "changes.tooLarge": "文件太大，无法显示差异。",
    "changes.renamedFrom": "重命名自 {path}",
    "changes.close": "关闭",
    "changes.prevChange": "上一处更改",
    "changes.nextChange": "下一处更改",
    "changes.collapseAll": "折叠所有文件夹",
    "changes.expandAll": "展开所有文件夹",
    "changes.searchPlaceholder": "搜索文件…",
    "changes.noSearchResults": "没有匹配的文件。",
    "changes.maximize": "最大化",
    "changes.restore": "还原大小",
    "find.open": "在此差异中搜索 (Ctrl+F)",
    "find.placeholder": "在差异中查找…",
    "find.prev": "上一个匹配",
    "find.next": "下一个匹配",
    "find.close": "关闭搜索",
    "find.noResults": "无结果",
    "commit.title": "提交更改",
    "commit.close": "关闭",
    "commit.loading": "正在读取工作区…",
    "commit.noChanges": "没有可提交的本地更改。",
    "commit.selectFile": "选择文件以查看差异。",
    "commit.selectAll": "全选",
    "commit.selectNone": "清除选择",
    "commit.message": "提交信息",
    "commit.messagePlaceholder": "描述此更改",
    "commit.selectedCount": "已选择 {count} 个文件",
    "commit.review": "检查",
    "commit.reviewTitle": "提交前最终检查",
    "commit.reviewSummary": "使用信息“{message}”提交 {count} 个文件",
    "commit.back": "返回",
    "commit.commit": "创建本地提交",
    "menu.stashApply": "应用储藏",
    "menu.stashPop": "弹出储藏（应用并移除）",
    "menu.stashDrop": "丢弃储藏…",
    "merge.title": "合并分支",
    "merge.route": "合并到当前分支",
    "merge.source": "从（来源）",
    "merge.target": "到（当前）",
    "merge.loading": "正在分析合并…",
    "merge.upToDate": "已是最新 — 无需合并。",
    "merge.fastForward": "快进 — 无需合并提交（勾选“创建合并提交”可强制创建）。",
    "merge.summary": "{files} 个文件更改，{conflicts} 处冲突。",
    "merge.conflictsWarning": "此合并存在冲突 — 您仍可合并，然后在编辑器中解决它们。",
    "merge.noChanges": "无文件更改。",
    "merge.files": "结果更改",
    "merge.added": "已添加",
    "merge.modified": "已修改",
    "merge.deleted": "已删除",
    "merge.conflict": "冲突",
    "merge.message": "合并提交信息",
    "merge.messagePlaceholder": "Merge branch 'source'",
    "merge.messageFfHint": "快进合并时忽略（不会创建提交）。",
    "merge.noFastForward": "创建合并提交（不快进）",
    "merge.noFastForwardHint": "始终记录合并提交，即使分支可以快进。",
    "merge.merge": "合并",
    "merge.cancel": "取消",
    "merge.previewError": "无法计算预览：{message}",
    "merge.squashBadge": "压缩",
    "merge.squashNote": "压缩合并 —— 所有改动作为“一个”提交落在“{target}”上；“{source}”自己的提交不会进入它的历史。可在“设置 › 合并”中改回。",
    "merge.messageSquash": "提交信息",
    "merge.squashMessagePlaceholder": "来自 'source' 的压缩改动",
    "newBranch.title": "创建分支",
    "newBranch.startPoint": "从 {sha} 开始的新分支",
    "newBranch.startPointOn": "位于 {refs}",
    "newBranch.location": "位置",
    "newBranch.locationRoot": "（根）",
    "newBranch.expandAll": "全部展开",
    "newBranch.collapseAll": "全部折叠",
    "newBranch.expandFolder": "展开文件夹",
    "newBranch.collapseFolder": "折叠文件夹",
    "newBranch.name": "分支名称",
    "newBranch.namePlaceholder": "my-branch",
    "newBranch.fullName": "完整名称：{name}",
    "newBranch.invalid": "无效的分支名称",
    "newBranch.exists": "已存在同名分支",
    "newBranch.checkout": "创建后检出分支",
    "newBranch.create": "创建",
    "newBranch.cancel": "取消",
    "status.loading": "正在加载图形…",
    "status.summary": "{repo}显示 {commits} 个提交，{refs} 个引用",
    "status.branchCreated": "已在 {sha} 创建分支 “{name}”",
    "status.error": "错误：{message}",
    "status.opFailed": "操作失败",
    "status.fetching": "正在抓取…",
    "status.pulling": "正在拉取…",
    "status.pushing": "正在推送…",
    "status.committing": "正在创建本地提交…",
    "status.commitCreated": "已创建本地提交 {sha}: {message}",
    "status.syncing": "正在同步…",
    "status.noHead": "在图形中未找到当前检出。",
    "status.undoing": "正在撤销提交…",
    "status.commitUndone": "提交已撤销 — 更改已回到工作树中。",
    "status.undoConflict": "撤销遇到冲突 — 请在编辑器中解决，然后继续。",
    "status.stashApplying": "正在应用储藏…",
    "status.stashPopping": "正在弹出储藏…",
    "status.stashDropping": "正在丢弃储藏…",
    "status.stashApplied": "储藏已应用。",
    "status.stashPopped": "储藏已弹出。",
    "status.stashDropped": "储藏已丢弃。",
    "status.stashConflict": "储藏冲突 — 请在编辑器中解决。",
    "status.merging": "正在合并…",
    "status.merged": "合并完成。",
    "status.mergeConflict": "合并存在冲突 — 请在编辑器中解决，然后提交。",
    "search.title": "搜索",
    "search.tooltip": "搜索提交",
    "search.placeholder": "搜索提交…",
    "search.previous": "上一个结果",
    "search.next": "下一个结果",
    "search.noResults": "无结果",
    "search.resultCount": "{index}/{count}",
    "search.filter": "筛选模式",
    "search.highlight": "高亮模式",
    "search.filterMode": "搜索显示",
    "search.filterModeFilter": "筛选（隐藏不匹配项）",
    "search.filterModeHighlight": "高亮（显示全部并标记匹配项）",
    "search.filterModeHint": "选择搜索结果在图中的显示方式。",
    "search.visibility": "搜索位置",
    "search.visibilityToolbar": "工具栏按钮",
    "search.visibilityAlwaysVisible": "始终可见的栏",
    "search.visibilityHint": "将搜索显示为工具栏图标或顶部固定栏。",
    "footer.github": "GitHub",
  },
  ru: {
    "toolbar.refresh": "⟳ Обновить",
    "toolbar.fetch": "⤓ Получить",
    "toolbar.pull": "⇩ Затянуть",
    "toolbar.push": "⇧ Отправить",
    "toolbar.commit": "✓ Commit",
    "toolbar.sync": "⇅ Синхр.",
    "toolbar.jumpHead": "⌖ Перейти к checkout",
    "toolbar.reset": "⤢ Сбросить вид",
    "toolbar.settings": "⚙ Настройки",
    "details.header": "Сведения о коммите",
    "details.close": "Закрыть",
    "details.sha": "SHA",
    "details.shortSha": "Короткий SHA",
    "details.message": "Сообщение",
    "details.author": "Автор",
    "details.date": "Дата",
    "details.labels": "Метки",
    "details.location": "Расположение",
    "details.currentHead": "HEAD — текущий checkout",
    "settings.title": "Настройки",
    "settings.language": "Язык",
    "settings.theme": "Тема",
    "settings.themeLight": "Светлая",
    "settings.themeDark": "Тёмная",
    "settings.themeLightHint": "Светлый фон, тёмный текст.",
    "settings.themeDarkHint": "Тёмный фон, светлый текст.",
    "settings.mainBranch": "Основная ветка",
    "settings.mainBranchAuto": "Автоматически",
    "settings.mainBranchSearch": "Поиск веток…",
    "settings.mainBranchNoMatch": "Нет подходящих веток",
    "settings.display": "Стиль отображения",
    "settings.displayModern": "Современный",
    "settings.displayClassic": "Классический",
    "settings.displayModernHint": "Свободный холст — перетаскивайте для панорамирования, прокручивайте для масштабирования.",
    "settings.displayClassicHint": "Фиксированный холст — ствол закреплён слева, без масштабирования, только прокрутка (как в графе ревизий SVN).",
    "settings.sectionGeneral": "Общие",
    "settings.sectionGraph": "Граф",
    "settings.sectionChanges": "Просмотр изменений",
    "settings.diffMinimap": "Мини-карта различий",
    "settings.diffMinimapOn": "Показана",
    "settings.diffMinimapOff": "Скрыта",
    "settings.diffMinimapOnHint": "Показать полосу обзора в стиле VS Code рядом с различиями — перетаскивайте её для прокрутки; включая маркеры изменений.",
    "settings.diffMinimapOffHint": "Без полосы обзора — прокручивайте различия обычным образом.",
    "settings.commitReview": "Проверка перед commit",
    "settings.commitReviewOn": "Спрашивать перед commit",
    "settings.commitReviewOff": "Commit сразу",
    "settings.commitReviewOnHint": "Перед локальным commit показать итоговый список файлов и сообщение.",
    "settings.commitReviewOffHint": "Кнопка Commit сразу создает локальный commit.",
    "settings.svnBranchDialog": "Диалог веток в стиле SVN",
    "settings.svnBranchDialogHint": "Показывать выбор дерева папок при создании ветки (вместо простого ввода).",
    "settings.branchDialog": "Диалог ветки",
    "settings.branchDialogSvnTitle": "Стиль SVN",
    "settings.branchDialogNativeVscode": "Стиль VS Code",
    "settings.branchDialogNativeVs": "Стиль Visual Studio",
    "settings.branchDialogNativeHint": "Встроенный диалог ветки вашей IDE.",
    "settings.close": "Закрыть",
    "settings.done": "Готово",
    "settings.sectionAdvanced": "Дополнительно",
    "settings.gitSource": "Исполняемый файл Git",
    "settings.gitSourceBuiltin": "Встроенный",
    "settings.gitSourceBuiltinHint": "Использовать бинарный файл git из встроенного расширения Git вашей IDE.",
    "settings.gitSourceCustom": "Свой путь",
    "settings.gitSourceCustomHint": "Укажите путь к собственному исполняемому файлу git (например, /usr/bin/git или C:\\…\\git.exe).",
    "settings.gitPath": "Путь к git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Обзор…",
    "settings.jargon": "Термины Git",
    "settings.jargonTranslate": "Переводить",
    "settings.jargonEnglish": "Оставить на английском",
    "settings.jargonTranslateHint": "Переводить термины Git (pull, push, commit, branch…) на язык интерфейса.",
    "settings.jargonEnglishHint": "Оставить термины Git (pull, push, commit, branch…) на английском; всё остальное переводить.",
    "settings.sectionMerge": "Слияние",
    "settings.mergeMode": "Способ слияния",
    "settings.mergeModeMerge": "Обычное слияние",
    "settings.mergeModeSquash": "Squash-слияние",
    "settings.mergeModeMergeHint": "Сливает ветку как есть: её коммиты входят в историю текущей ветки и связываются коммитом слияния (или выполняется fast-forward, если возможно).",
    "settings.mergeModeSquashHint": "Схлопывает всю ветку в один коммит в текущей ветке (git merge --squash). Собственные коммиты ветки не попадают в историю текущей ветки — там виден только один коммит. Сама ветка не изменяется, поэтому её коммиты остаются видны в своей колонке.",
    "settings.mergedView": "Влитые коммиты на графе",
    "settings.mergedViewBranch": "Только в своей ветке",
    "settings.mergedViewTarget": "Также в ветке, в которую их влили",
    "settings.mergedViewBranchHint": "Коммиты влитой ветки рисуются только в её собственной дорожке, а слияние — соединительной линией. Классический вид.",
    "settings.mergedViewTargetHint": "Коммиты, которые принесло слияние, рисуются и в дорожке принимающей ветки — под коммитом слияния, на одной строке с оригиналами, ведь эта ветка действительно их содержит. Копии показаны на светлом фоне и подписаны веткой, в которой были написаны, так что видно: созданы они не здесь. На squash-слияние это не влияет: squash пишет один обычный коммит, поэтому виден только он.",
    "legend.title": "Легенда",
    "legend.head": "HEAD / текущая ветка",
    "legend.local": "Локальная ветка",
    "legend.remote": "Удалённая ветка",
    "legend.remoteOnly": "Только в облаке (не затянуто)",
    "legend.tag": "Тег (версия)",
    "legend.commit": "Коммит",
    "legend.stash": "Stash (отложенная работа)",
    "legend.mergedIn": "Попал сюда со слиянием (создан в другой ветке)",
    "legend.nodes": "Блоки",
    "legend.lines": "Линии",
    "legend.edgeParent": "Предыдущий коммит на той же линии",
    "legend.edgeMerge": "Влитая ветка — стрелка указывает на коммит слияния",
    "legend.edgeBranch": "Отсюда ответвилась ветка",
    "legend.edgeStash": "Stash и коммит, на котором он отложен",
    "legend.edgeMergedChain": "Линия этой ветки, проходящая через влитые коммиты",
    "legend.edgeMergedTie": "Тот же коммит на ветке, где он был создан",
    "menu.jumpHead": "⌖ Перейти к checkout",
    "menu.resetView": "⤢ Сбросить вид",
    "menu.createBranch": "Создать ветку отсюда…",
    "menu.checkout": "Переключиться на этот коммит",
    "menu.copySha": "Копировать SHA коммита",
    "menu.pushBranch": "Отправить ветку «{name}»",
    "menu.renameBranch": "Переименовать ветку «{name}»…",
    "menu.deleteBranch": "Удалить ветку «{name}»…",
    "menu.renameCommit": "Изменить сообщение коммита…",
    "menu.undoCommit": "Отменить коммит (сохранить изменения)…",
    "menu.viewChanges": "Просмотреть изменения…",
    "menu.mergeBranch": "Слить «{source}» в «{target}»…",
    "menu.jumpToOriginal": "Перейти к исходному коммиту",
    "node.mergedInTooltip": "Слияние {merge} принесло этот коммит в «{target}» — он был написан в «{origin}».",
    "changes.title": "Изменения в {sha}",
    "changes.tabChanged": "Изменённые",
    "changes.tabAll": "Все файлы",
    "changes.added": "Добавлено",
    "changes.modified": "Изменено",
    "changes.deleted": "Удалено",
    "changes.renamed": "Переименовано",
    "changes.noChanges": "Этот коммит не меняет файлы.",
    "changes.loading": "Загрузка различий…",
    "changes.selectFile": "Выберите файл, чтобы увидеть его изменения.",
    "changes.original": "Оригинал",
    "changes.changed": "Этот коммит",
    "changes.binary": "Бинарный файл — нет текстовых различий.",
    "changes.tooLarge": "Файл слишком большой для показа различий.",
    "changes.renamedFrom": "Переименовано из {path}",
    "changes.close": "Закрыть",
    "changes.prevChange": "Предыдущее изменение",
    "changes.nextChange": "Следующее изменение",
    "changes.collapseAll": "Свернуть все папки",
    "changes.expandAll": "Развернуть все папки",
    "changes.searchPlaceholder": "Поиск файлов…",
    "changes.noSearchResults": "Нет файлов, соответствующих поиску.",
    "changes.maximize": "Развернуть",
    "changes.restore": "Восстановить размер",
    "find.open": "Поиск в этом diff (Ctrl+F)",
    "find.placeholder": "Поиск в diff…",
    "find.prev": "Предыдущее совпадение",
    "find.next": "Следующее совпадение",
    "find.close": "Закрыть поиск",
    "find.noResults": "Ничего не найдено",
    "commit.title": "Commit Changes",
    "commit.close": "Закрыть",
    "commit.loading": "Чтение working tree…",
    "commit.noChanges": "Нет локальных изменений для commit.",
    "commit.selectFile": "Выберите файл, чтобы увидеть diff.",
    "commit.selectAll": "Выбрать все",
    "commit.selectNone": "Снять выбор",
    "commit.message": "Commit message",
    "commit.messagePlaceholder": "Опишите изменение",
    "commit.selectedCount": "Выбрано файлов: {count}",
    "commit.review": "Проверить",
    "commit.reviewTitle": "Финальная проверка перед commit",
    "commit.reviewSummary": "Commit {count} file(s) with message: {message}",
    "commit.back": "Назад",
    "commit.commit": "Создать локальный commit",
    "menu.stashApply": "Применить stash",
    "menu.stashPop": "Извлечь stash (применить и удалить)",
    "menu.stashDrop": "Отбросить stash…",
    "merge.title": "Слияние ветки",
    "merge.route": "Слияние в текущую ветку",
    "merge.source": "Из (источник)",
    "merge.target": "В (текущая)",
    "merge.loading": "Анализ слияния…",
    "merge.upToDate": "Уже актуально — сливать нечего.",
    "merge.fastForward": "Перемотка — коммит слияния не нужен (отметьте «Создать коммит слияния», чтобы принудительно создать его).",
    "merge.summary": "Изменено файлов: {files}, конфликтов: {conflicts}.",
    "merge.conflictsWarning": "В этом слиянии есть конфликты — вы всё равно можете слить, а затем разрешить их в редакторе.",
    "merge.noChanges": "Нет изменений файлов.",
    "merge.files": "Итоговые изменения",
    "merge.added": "Добавлено",
    "merge.modified": "Изменено",
    "merge.deleted": "Удалено",
    "merge.conflict": "Конфликт",
    "merge.message": "Сообщение коммита слияния",
    "merge.messagePlaceholder": "Merge branch 'source'",
    "merge.messageFfHint": "Игнорируется при перемотке (коммит не создаётся).",
    "merge.noFastForward": "Создать коммит слияния (без перемотки)",
    "merge.noFastForwardHint": "Всегда создавать коммит слияния, даже если возможна перемотка.",
    "merge.merge": "Слить",
    "merge.cancel": "Отмена",
    "merge.previewError": "Не удалось вычислить предпросмотр: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash-слияние — все изменения попадут в ветку «{target}» ОДНИМ коммитом; собственные коммиты «{source}» не войдут в её историю. Вернуть обычное слияние можно в «Настройки › Слияние».",
    "merge.messageSquash": "Сообщение коммита",
    "merge.squashMessagePlaceholder": "Изменения ветки 'source' одним коммитом",
    "newBranch.title": "Создать ветку",
    "newBranch.startPoint": "Новая ветка, начиная с {sha}",
    "newBranch.startPointOn": "на {refs}",
    "newBranch.location": "Расположение",
    "newBranch.locationRoot": "(корень)",
    "newBranch.expandAll": "Развернуть все",
    "newBranch.collapseAll": "Свернуть все",
    "newBranch.expandFolder": "Развернуть папку",
    "newBranch.collapseFolder": "Свернуть папку",
    "newBranch.name": "Имя ветки",
    "newBranch.namePlaceholder": "my-branch",
    "newBranch.fullName": "Полное имя: {name}",
    "newBranch.invalid": "Недопустимое имя ветки",
    "newBranch.exists": "Ветка с таким именем уже существует",
    "newBranch.checkout": "Переключиться на ветку после создания",
    "newBranch.create": "Создать",
    "newBranch.cancel": "Отмена",
    "status.loading": "Загрузка графа…",
    "status.summary": "{repo}Показано коммитов: {commits}, ссылок: {refs}",
    "status.branchCreated": "Создана ветка «{name}» на {sha}",
    "status.error": "Ошибка: {message}",
    "status.opFailed": "Операция не удалась",
    "status.fetching": "Получение…",
    "status.pulling": "Затягивание…",
    "status.pushing": "Отправка…",
    "status.committing": "Создание локального commit…",
    "status.commitCreated": "Создан локальный commit {sha}: {message}",
    "status.syncing": "Синхронизация…",
    "status.noHead": "Текущий checkout не найден в графе.",
    "status.undoing": "Отмена коммита…",
    "status.commitUndone": "Коммит отменён — изменения вернулись в рабочее дерево.",
    "status.undoConflict": "При отмене возникли конфликты — разрешите их в редакторе, затем продолжите.",
    "status.stashApplying": "Применение stash…",
    "status.stashPopping": "Извлечение stash…",
    "status.stashDropping": "Отбрасывание stash…",
    "status.stashApplied": "Stash применён.",
    "status.stashPopped": "Stash извлечён.",
    "status.stashDropped": "Stash отброшен.",
    "status.stashConflict": "Конфликты stash — разрешите их в редакторе.",
    "status.merging": "Слияние…",
    "status.merged": "Слияние завершено.",
    "status.mergeConflict": "В слиянии есть конфликты — разрешите их в редакторе, затем сделайте коммит.",
    "search.title": "Поиск",
    "search.tooltip": "Искать коммиты",
    "search.placeholder": "Искать коммиты…",
    "search.previous": "Предыдущий результат",
    "search.next": "Следующий результат",
    "search.noResults": "Нет результатов",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Режим фильтра",
    "search.highlight": "Режим подсветки",
    "search.filterMode": "Отображение поиска",
    "search.filterModeFilter": "Фильтр (скрыть несовпадения)",
    "search.filterModeHighlight": "Подсветка (показать всё и отметить совпадения)",
    "search.filterModeHint": "Выберите, как показывать результаты поиска на графе.",
    "search.visibility": "Расположение поиска",
    "search.visibilityToolbar": "Кнопка панели",
    "search.visibilityAlwaysVisible": "Всегда видимая строка",
    "search.visibilityHint": "Показывать поиск кнопкой на панели или постоянной строкой сверху.",
    "footer.github": "GitHub",
  },
  om: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "⤢ Ilaalcha irra deebi'ii saagi",
    "toolbar.settings": "⚙ Sajoo",
    "details.header": "Bal'ina Raawwii",
    "details.close": "Cufuu",
    "details.sha": "SHA",
    "details.shortSha": "Gabaabaa SHA",
    "details.message": "Ergaa",
    "details.author": "Barreessaa",
    "details.date": "Guyyaa",
    "details.labels": "Asxaalee",
    "details.location": "Bakka",
    "details.currentHead": "HEAD — kaffaltii ammaa",
    "settings.title": "Sajoo",
    "settings.language": "Afaan",
    "settings.theme": "Dhimma haasaa",
    "settings.themeLight": "Ifa",
    "settings.themeDark": "Duukkana",
    "settings.themeLightHint": "Duubbee ifa, barreeffama dukkanaa'aa.",
    "settings.themeDarkHint": "Duubbee dukkanaa'aa, barreeffama ifaa.",
    "settings.mainBranch": "Damee guddaa",
    "settings.mainBranchAuto": "Ofumaan kan hojjetu",
    "settings.mainBranchSearch": "Dameewwan barbaadi...",
    "settings.mainBranchNoMatch": "Damee walsimsiisu hin jiru",
    "settings.display": "Akkaataa agarsiisaa",
    "settings.displayModern": "Ammayyaa",
    "settings.displayClassic": "Kilaasikii",
    "settings.displayModernHint": "Kanfaasi bilisaa — gara panitti harkisi, gara guddisuutti qajeeli.",
    "settings.displayClassicHint": "Kanfaasiin dhaabbataa — jirma bitaatti kan maxxane, guddinni hin qabu, garagalchi qofa (akkuma giraafii fooyya'iinsa SVN).",
    "settings.sectionGeneral": "Waliigala",
    "settings.sectionGraph": "Giraaphii",
    "settings.sectionChanges": "Ilaalcha jijjiira",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Kan agarsiifame",
    "settings.diffMinimapOff": "Dhokataa",
    "settings.diffMinimapOnHint": "Diff cinatti tarree ilaalcha waliigalaa akkaataa Koodii VS agarsiisi — garagalchuuf harkisi; mallattoolee jijjiiramaa dabalatee.",
    "settings.diffMinimapOffHint": "Dirree ilaalcha waliigalaa hin qabu — garaagarummaa akka idileetti garagalchi.",
    "settings.commitReview": "Gamaaggama raawwachuu",
    "settings.commitReviewOn": "Raawwachuu dura gaafadhu",
    "settings.commitReviewOff": "Kallattiin waadaa galuu",
    "settings.commitReviewOnHint": "Tarree faayilii dhumaa fi sakatta'iinsa ergaa osoo hin uumiin dura agarsiisi.",
    "settings.commitReviewOffHint": "Qabduu Raawwii battalumatti waadaa naannoo uuma.",
    "settings.svnBranchDialog": "Qaaqa damee akkaataa SVN",
    "settings.svnBranchDialogHint": "Yeroo damee uumtu (bakka gaaffii salphaa) fuuttuu muka galmee agarsiisi.",
    "settings.branchDialog": "Qaaqa damee",
    "settings.branchDialogSvnTitle": "Akkaataa SVN",
    "settings.branchDialogNativeVscode": "Akkaataa Koodii VS",
    "settings.branchDialogNativeVs": "Akkaataa Visual Studio",
    "settings.branchDialogNativeHint": "Gaaffii damee IDE kee keessaa ijaarame.",
    "settings.close": "Cufuu",
    "settings.done": "Raawwatame",
    "settings.sectionAdvanced": "Hammayyaa'aa",
    "settings.gitSource": "Git raawwatamuu danda'a",
    "settings.gitSourceBuiltin": "Kan ijaarame",
    "settings.gitSourceBuiltinHint": "git baayinarii dheerina Git IDE keessaa ijaarame irraa fayyadami.",
    "settings.gitSourceCustom": "Daandii amala",
    "settings.gitPath": "Daandii gara git",
    "settings.gitPathPlaceholder": "/usr/bin/git jedhamuun beekama",
    "settings.gitPathBrowse": "Daawwadhaa...",
    "settings.jargon": "Jechoota Git",
    "settings.jargonTranslate": "Hiikuu",
    "settings.jargonEnglish": "Afaan Oromootiin eegaa",
    "settings.jargonTranslateHint": "Jechoota Git (harkisuu, dhiibuu, raawwachuu, damee...) gara afaan walqunnamtiitti hiiku.",
    "settings.jargonEnglishHint": "Jechoota Git (harkisuu, dhiibuu, raawwachuu, damee...) Afaan Ingiliffaatiin qabadhu; waan biraa hunda hiikkaa.",
    "settings.sectionMerge": "Walitti makamuu",
    "settings.mergeMode": "Akkaataa walitti makuu",
    "settings.mergeModeMerge": "Walitti makamuu idilee",
    "settings.mergeModeSquash": "Squash walitti makamuu",
    "settings.mergeModeMergeHint": "Damee akka jirutti walitti maku: waadaa isaa qaama seenaa damee ammaa ta'a, waadaa walitti makamaatiin walitti makama (ykn yeroo danda'ametti saffisaan fuulduratti tarkaanfata).",
    "settings.mergeModeSquashHint": "Damee guutuu gara raawwachuu tokkootti damee ammaa irratti kuffisa (git merge --squash). Kufaatiin damee mataa isaa seenaa damee ammaa keessa gonkumaa hin seenu — achitti waadaa tokko qofa argita. Dameen mataan isaa osoo hin tuqamin hafe, kanaaf commits isaa daandii mataa isaa irratti mul'atu.",
    "settings.mergedView": "Giraaphii keessatti raawwattoota walitti makaman",
    "settings.mergedViewBranch": "Damee mataa isaanii qofa irratti",
    "settings.mergedViewTarget": "Akkasumas damee isaan itti walitti makaman keessatti",
    "settings.mergedViewBranchHint": "Waadaaleen damee walitti makame tokkoo daandii mataa isaanii qofa keessatti kan harkifaman yoo ta'u; walitti makamuun akka walqabsiisaa mul'ata. Ilaalcha kalaasikii.",
    "settings.mergedViewTargetHint": "Commits walitti makamuun fide akkasumas daandii damee fudhatu keessatti kaafamu, commit jalatti walitti tuulamee fi jalqabaa wajjin wal qixa — sababni isaas dameen sun dhuguma amma isaan of keessaa qaba. Koppiiwwan isaanii damee isaan irratti barreeffaman waliin kan walqabatan waan ta’aniif achitti akka hin hojjetamne ifa ta’ee hafa. Walitti makamuun squash hin tuqamu: squash tokko commit idilee tokko barreessa, kanaaf commit sana qofatu yoomiyyuu mul'ata.",
    "legend.title": "Cimaa",
    "legend.head": "HEAD / damee ammaa",
    "legend.local": "Damee naannoo sanaa",
    "legend.remote": "Damee fagoo",
    "legend.remoteOnly": "Duumessa keessa qofa (hin harkifamne) .",
    "legend.tag": "Tag (version) 1. .",
    "legend.commit": "Raawwachuu",
    "legend.stash": "Stash (hojii sheeldii) .",
    "legend.mergedIn": "Walitti makamuu (damee biraa irratti barreeffame) waliin dhufe .",
    "legend.nodes": "Sanduuqawwan",
    "legend.lines": "Sararoota",
    "legend.edgeParent": "Sarara walfakkaataa irratti raawwachuu duraanii",
    "legend.edgeMerge": "Damee walitti makame — xiyyaan gara waadaa walitti makamuutti akeeka",
    "legend.edgeBranch": "Dameen tokko asitti forked off ta'e",
    "legend.edgeStash": "Stash fi commit inni irratti shelved ture",
    "legend.edgeMergedChain": "Sararri damee kanaa kan walitti makaman keessa fiigu raawwatu",
    "legend.edgeMergedTie": "Damee irratti barreeffame irrattis commit wal fakkaata",
    "menu.jumpHead": "⌖ Gara kaffaltii deemi",
    "menu.resetView": "⤢ Ilaalcha irra deebi'ii saagi",
    "menu.createBranch": "Asirraa damee uumuu...",
    "menu.checkout": "Waadaa kana ilaalaa",
    "menu.copySha": "Koppii commit SHA",
    "menu.pushBranch": "Damee dhiibaa \"{name}\".",
    "menu.renameCommit": "Ergaa commit maqaa jijjiiri...",
    "menu.undoCommit": "Undo commit (jijjiirama eeguu)...",
    "menu.viewChanges": "Jijjiirama ilaali...",
    "menu.jumpToOriginal": "Gara commit jalqabaa deemi",
    "changes.title": "Jijjiirama {sha} keessatti",
    "changes.tabChanged": "Jijjiirame",
    "changes.tabAll": "Faayilota Hunda",
    "changes.added": "Itti dabalameera",
    "changes.modified": "Fooyya'ee jira",
    "changes.deleted": "Haqameera",
    "changes.renamed": "Maqaa jijjiirrame",
    "changes.noChanges": "Raawwiin kun faayilii tokkollee hin jijjiiru.",
    "changes.loading": "Garaagarummaa fe'aa jira...",
    "changes.selectFile": "Jijjiirama isaa ilaaluuf faayilii filadhu.",
    "changes.original": "Kan jalqabaa",
    "changes.changed": "Kun waadaa galuu",
    "changes.binary": "Faayilii baayinarii — garaagarummaa barruu agarsiisuuf hin jiru.",
    "changes.tooLarge": "Faayilli baay'ee guddaa waan ta'eef diff.",
    "changes.renamedFrom": "{path} irraa moggaafame",
    "changes.close": "Cufuu",
    "changes.prevChange": "Jijjiirama kanaan duraa",
    "changes.nextChange": "Jijjiirama itti aanu",
    "changes.collapseAll": "Galmeewwan hunda kuffisi",
    "changes.expandAll": "Galmeewwan hunda bal'isi",
    "changes.searchPlaceholder": "Faayilota barbaadi...",
    "changes.noSearchResults": "Faayilli barbaacha kee wajjin walsimu hin jiru.",
    "changes.maximize": "Maximize gochuu",
    "changes.restore": "Safara deebisi",
    "find.open": "Garaagarummaa kana keessatti barbaadi (Ctrl+F) .",
    "find.placeholder": "Diff keessatti barbaadi...",
    "find.prev": "Tapha kanaan duraa",
    "find.next": "Tapha itti aanu",
    "find.close": "Barbaadi cufi",
    "find.noResults": "Bu'aan hin jiru",
    "commit.title": "Jijjiirama Raawwachuu",
    "commit.close": "Cufuu",
    "commit.loading": "Muka hojii dubbisuu...",
    "commit.noChanges": "Jijjiiramni naannoo itti raawwatamu hin jiru.",
    "commit.selectFile": "Garaagarummaa isaa ilaaluuf faayilii filadhu.",
    "commit.selectAll": "Hundaa filadhu",
    "commit.selectNone": "Hundaa qulqulleessi",
    "commit.message": "Ergaa raawwadhu",
    "commit.messagePlaceholder": "Jijjiirama jiru ibsi",
    "commit.selectedCount": "Faayilii(wwan) {count} filataman",
    "commit.review": "Keessa deebi'uu",
    "commit.reviewTitle": "Raawwachuu dura sakatta'iinsa dhumaa",
    "commit.reviewSummary": "Faayilii(wwan) {count} ergaa: {message} waliin raawwadhu",
    "commit.back": "Dugda",
    "commit.commit": "Raawwii naannoo uumi",
    "menu.stashApply": "Stash dibadhu",
    "menu.stashPop": "Pop stash (dibachuu & balleessuu) .",
    "menu.stashDrop": "Drop stash...",
    "merge.title": "Damee Walitti Makamuu",
    "merge.route": "Gara damee amma jirutti walitti makamuu",
    "merge.source": "(Madda) irraa",
    "merge.target": "Gara (amma) .",
    "merge.loading": "Walitti makamuu xiinxaluu...",
    "merge.upToDate": "Duraanis kan yeroo ammaa — waan walitti makamu hin jiru.",
    "merge.fastForward": "Saffisaan gara fuulduraatti — raawwachuun walitti makamuu hin barbaachisu (“tokko dirqisiisuuf “Waadaa walitti makamuu uumi” irratti mallattoo kaa’i).",
    "merge.summary": "Faayilii(wwan) {files} jijjiirame, waldhabdee(wwan) {conflicts}.",
    "merge.conflictsWarning": "Walitti makamuun kun waldhabdee qaba — ammallee walitti makuu dandeessa, sana booda gulaalaa keessatti furuu dandeessa.",
    "merge.noChanges": "Jijjiiramni faayilii hin jiru.",
    "merge.files": "Jijjiirama bu’aa irraa argamu",
    "merge.added": "Itti dabalameera",
    "merge.modified": "Fooyya'ee jira",
    "merge.deleted": "Haqameera",
    "merge.conflict": "Waldhabdee",
    "merge.message": "Ergaa commit walitti makuu",
    "merge.messagePlaceholder": "Damee 'madda' walitti makuu.",
    "merge.messageFfHint": "Walitti makamuu saffisaa fuulduratti tuffatame (waaltiin hin uumamu).",
    "merge.noFastForward": "Waadaa walitti makamuu uumi (saffisaan fuulduratti tarkaanfachuu hin qabu) .",
    "merge.noFastForwardHint": "Yeroo hunda waadaa walitti makamuu galmeessi, yeroo dameen saffisaan fuulduratti tarkaanfachuu danda'us.",
    "merge.merge": "Walitti makamuu",
    "merge.cancel": "Haquu",
    "merge.previewError": "Durargii shallaguu hin dandeenye: {message}",
    "merge.squashBadge": "Iskuwaashii",
    "merge.squashNote": "Squash merge — jijjiiramni hundi akka ONE commit “{target}” irratti lafa bu’a; “{source}” ofii isaatii waadaa seene seenaa isaa keessaa ba’a. Sajoo › Walitti makamuu keessatti deebi'i.",
    "merge.messageSquash": "Ergaa raawwadhu",
    "merge.squashMessagePlaceholder": "Jijjiirama 'madda' irraa 'squashed'.",
    "newBranch.title": "Damee Uumuu",
    "newBranch.startPoint": "Damee haaraa {sha} irraa eegalee",
    "newBranch.startPointOn": "{refs} irratti",
    "newBranch.location": "Bakka",
    "newBranch.locationRoot": "(hundee)",
    "newBranch.expandAll": "Hundaa bal'isi",
    "newBranch.collapseAll": "Hundaa kuffisaa",
    "newBranch.expandFolder": "Galmee bal'isi",
    "newBranch.collapseFolder": "Galmee kuffisuu",
    "newBranch.name": "Maqaa damee",
    "newBranch.namePlaceholder": "damee-koo",
    "newBranch.fullName": "Maqaa guutuu: {name}",
    "newBranch.invalid": "Maqaa damee sirrii hin taane",
    "newBranch.exists": "Dameen maqaa kana qabu duruu jira",
    "newBranch.checkout": "Erga uumamee booda damee kaffaltii",
    "newBranch.create": "Uumuu",
    "newBranch.cancel": "Haquu",
    "status.loading": "Giraaphii fe'amaa jira...",
    "status.summary": "{repo}{commits} raawwachuu agarsiisuu, {refs} refs",
    "status.branchCreated": "Damee \"{name}\" {sha} irratti uume",
    "status.error": "Dogoggora: {message}",
    "status.opFailed": "Opireeshiniin hin milkoofne",
    "status.fetching": "Fetching...",
    "status.pulling": "Harkisuu...",
    "status.pushing": "Dhiibbaa...",
    "status.committing": "Commit naannoo uumuun...",
    "status.commitCreated": "Raawwii naannoo uumame {sha}: {message}",
    "status.syncing": "Walsimsiisuun...",
    "status.noHead": "Kaffaltiin ammaa giraafii keessatti hin argamne.",
    "status.undoing": "Commit irraa deebisuu...",
    "status.commitUndone": "Commit undone — jijjiiramoonni muka hojii keessatti deebi'aniiru.",
    "status.undoConflict": "Waldhabbii rukuttaa deebisi — gulaalaa keessatti furuu, sana booda itti fufi.",
    "status.stashApplying": "Stash hojiirra oolchuudhaan...",
    "status.stashPopping": "Popping stash...",
    "status.stashDropping": "Stash gadi dhiisuu...",
    "status.stashApplied": "Stash iyyate.",
    "status.stashPopped": "Stash jedhee ni mul'ate.",
    "status.stashDropped": "Stash gadi bu'e.",
    "status.stashConflict": "Waldhabbii dhoksuu — gulaalaa keessatti furuu.",
    "status.merging": "Walitti makamuu...",
    "status.merged": "Walitti makamuun xumurame.",
    "status.mergeConflict": "Walitti makamuun waldhabdee qaba — gulaalaa keessatti furuu, sana booda raawwadhu.",
    "search.title": "Barbaaduu",
    "search.tooltip": "Barbaacha raawwachuu",
    "search.placeholder": "Barbaacha waadaa...",
    "search.previous": "Bu'aa kanaan duraa",
    "search.next": "Bu'aa itti aanu",
    "search.noResults": "Bu'aan hin jiru",
    "search.resultCount": "{index}/{count} jedhamuun beekama",
    "search.filter": "Haalata calaqqee",
    "search.highlight": "Haalata calaqqisiisi",
    "search.filterMode": "Agarsiisa barbaacha",
    "search.filterModeFilter": "Filter (kan wal hin simne dhoksi) .",
    "search.filterModeHighlight": "Calaqqisiisi (hundaa agarsiisi, walsimsiisaa mallatteessi) .",
    "search.filterModeHint": "Akkaataa bu'aan barbaacha giraafii keessatti agarsiifamu filadhu.",
    "search.visibility": "Bakka barbaadi",
    "search.visibilityToolbar": "Qabduu barruu meeshaa",
    "search.visibilityAlwaysVisible": "Yeroo hunda-barruu mul'atu",
    "search.visibilityHint": "Barbaacha akka mallattoo barruu meeshaa ykn barruu itti fufiinsa qabuutti gubbaa agarsiisi.",
    "footer.github": "GitHub jedhamuun beekama",
  },
  af: {
    "toolbar.refresh": "⟳ Verfris",
    "toolbar.fetch": "⤓ Haal",
    "toolbar.pull": "⇩ Trek",
    "toolbar.push": "⇧ Druk",
    "toolbar.commit": "✓ Verbind",
    "toolbar.sync": "⇅ Sinkroniseer",
    "toolbar.jumpHead": "⌖ Gaan na betaalpunt",
    "toolbar.reset": "⤢ Stel aansig terug",
    "toolbar.settings": "⚙ Instellings",
    "details.header": "Verbind besonderhede",
    "details.close": "Maak toe",
    "details.sha": "SHA",
    "details.shortSha": "Kort SHA",
    "details.message": "Boodskap",
    "details.author": "Skrywer",
    "details.date": "Datum",
    "details.labels": "Etikette",
    "details.location": "Ligging",
    "details.currentHead": "HOOF — huidige betaalpunt",
    "settings.title": "Instellings",
    "settings.language": "Taal",
    "settings.theme": "Tema",
    "settings.themeLight": "Lig",
    "settings.themeDark": "Donker",
    "settings.themeLightHint": "Ligte agtergrond, donker teks.",
    "settings.themeDarkHint": "Donker agtergrond, ligte teks.",
    "settings.mainBranch": "Hooftak",
    "settings.mainBranchAuto": "Outomatiese",
    "settings.mainBranchSearch": "Soek takke …",
    "settings.mainBranchNoMatch": "Geen bypassende takke nie",
    "settings.display": "Vertoon styl",
    "settings.displayModern": "Moderne",
    "settings.displayClassic": "Klassiek",
    "settings.displayModernHint": "Gratis doek — sleep om te pan, blaai om te zoem.",
    "settings.displayClassicHint": "Vaste doek — romp links vasgespeld, geen zoom nie, blaai slegs (soos die SVN-hersieningsgrafiek).",
    "settings.sectionGeneral": "Algemeen",
    "settings.sectionGraph": "Grafiek",
    "settings.sectionChanges": "Verander aansig",
    "settings.diffMinimap": "Diff minikaart",
    "settings.diffMinimapOn": "Getoon",
    "settings.diffMinimapOff": "Versteek",
    "settings.diffMinimapOnHint": "Wys 'n VS-kode-styl oorsigstrook langs die verskil - sleep dit om te blaai; verander merkers ingesluit.",
    "settings.diffMinimapOffHint": "Geen oorsigstrook nie – blaai normaalweg deur die verskil.",
    "settings.commitReview": "Lewer hersiening",
    "settings.commitReviewOn": "Vra voor pleeg",
    "settings.commitReviewOff": "Verbind direk",
    "settings.commitReviewOnHint": "Wys 'n finale lêerlys en boodskapkontrole voordat die plaaslike commit geskep word.",
    "settings.commitReviewOffHint": "Die Commit-knoppie skep die plaaslike commit onmiddellik.",
    "settings.svnBranchDialog": "SVN-styl takdialoog",
    "settings.svnBranchDialogHint": "Wys 'n vouerboomkieser wanneer 'n tak geskep word (in plaas van 'n eenvoudige prompt).",
    "settings.branchDialog": "Takdialoog",
    "settings.branchDialogSvnTitle": "SVN-styl",
    "settings.branchDialogNativeVscode": "VS-kode styl",
    "settings.branchDialogNativeVs": "Visual Studio styl",
    "settings.branchDialogNativeHint": "Jou IDE se ingeboude takprompt.",
    "settings.close": "Maak toe",
    "settings.done": "Klaar",
    "settings.sectionAdvanced": "Gevorderd",
    "settings.gitSource": "Git uitvoerbaar",
    "settings.gitSourceBuiltin": "Ingeboude",
    "settings.gitSourceBuiltinHint": "Gebruik die git-binêre van die IDE se ingeboude Git-uitbreiding.",
    "settings.gitSourceCustom": "Pasgemaakte pad",
    "settings.gitPath": "Pad na git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Blaai deur …",
    "settings.jargon": "Git terme",
    "settings.jargonTranslate": "Vertaal",
    "settings.jargonEnglish": "Hou in Engels",
    "settings.jargonTranslateHint": "Vertaal Git terme (trek, druk, commit, vertak ...) in die koppelvlaktaal.",
    "settings.jargonEnglishHint": "Hou Git terme (trek, druk, pleeg, vertak ...) in Engels; vertaal alles anders.",
    "settings.sectionMerge": "Voeg saam",
    "settings.mergeMode": "Voeg styl saam",
    "settings.mergeModeMerge": "Normale samesmelting",
    "settings.mergeModeSquash": "Squash samesmelting",
    "settings.mergeModeMergeHint": "Voeg die tak saam soos dit is: sy verpligtinge word deel van die huidige tak se geskiedenis, saamgevoeg deur 'n samesmeltingsverbintenis (of versnel indien moontlik).",
    "settings.mergeModeSquashHint": "Vou die hele tak in 'n enkele commit op die huidige tak in (git merge --squash). Die tak se eie commits kom nooit in die huidige tak se geskiedenis in nie - jy sien net een commit daar. Die tak self word onaangeraak gelaat, so sy commits bly sigbaar in sy eie baan.",
    "settings.mergedView": "Saamgevoegde commits in die grafiek",
    "settings.mergedViewBranch": "Slegs op hul eie tak",
    "settings.mergedViewTarget": "Ook in die tak is hulle saamgesmelt",
    "settings.mergedViewBranchHint": "'n Saamgesmelte tak se commits word slegs in hul eie baan geteken; die samesmelting verskyn as 'n koppelaar. Die klassieke uitsig.",
    "settings.mergedViewTargetHint": "Die commits 'n samesmelting wat ingebring word, word ook in die ontvangende tak se baan geteken, gestapel onder die merge commit en gelyk met die oorspronklikes - want daardie tak bevat dit nou regtig. Die kopieë is bleek en met die kop met die tak waarop hulle geskryf is, is dit duidelik dat hulle nie daar gemaak is nie. Squash-samesmeltings word nie beïnvloed nie: 'n muurbal skryf een gewone commit, so net daardie commit verskyn ooit.",
    "legend.title": "Legende",
    "legend.head": "HOOF / huidige tak",
    "legend.local": "Plaaslike tak",
    "legend.remote": "Afgeleë tak",
    "legend.remoteOnly": "Slegs in die wolk (nie getrek nie)",
    "legend.tag": "Merker (weergawe)",
    "legend.commit": "Verbind",
    "legend.stash": "Stash (werk op die rak)",
    "legend.mergedIn": "Het ingekom met 'n samesmelting (op 'n ander tak geskryf)",
    "legend.nodes": "Bokse",
    "legend.lines": "Lyne",
    "legend.edgeParent": "Vorige pleeg op dieselfde lyn",
    "legend.edgeMerge": "Ingevoegde tak - die pyltjie wys na die samesmeltingstoewysing",
    "legend.edgeBranch": "'n Tak het hier afgevurk",
    "legend.edgeStash": "Stash en die commit dit is afgeskrik",
    "legend.edgeMergedChain": "Hierdie tak se lyn wat deur die saamgesmelte commits loop",
    "legend.edgeMergedTie": "Dieselfde commit op die tak waarop dit geskryf is",
    "menu.jumpHead": "⌖ Gaan na betaalpunt",
    "menu.resetView": "⤢ Stel aansig terug",
    "menu.createBranch": "Skep tak van hier af...",
    "menu.checkout": "Kyk na hierdie verbintenis",
    "menu.copySha": "Kopieer commit SHA",
    "menu.pushBranch": "Druk tak \"{name}\"",
    "menu.renameCommit": "Hernoem commit boodskap …",
    "menu.undoCommit": "Ontdoen commit (hou veranderinge)...",
    "menu.viewChanges": "Bekyk veranderinge …",
    "menu.jumpToOriginal": "Gaan na die oorspronklike commit",
    "node.mergedInTooltip": "Merge {merge} het hierdie commit in \"{target}\" gebring - dit is op \"{origin}\" geskryf.",
    "changes.title": "Veranderinge in {sha}",
    "changes.tabChanged": "Verander",
    "changes.tabAll": "Alle lêers",
    "changes.added": "Bygevoeg",
    "changes.modified": "Gewysig",
    "changes.deleted": "Geskrap",
    "changes.renamed": "Hernoem",
    "changes.noChanges": "Hierdie commit verander geen lêers nie.",
    "changes.loading": "Laai tans verskil …",
    "changes.selectFile": "Kies 'n lêer om sy veranderinge te sien.",
    "changes.original": "Oorspronklik",
    "changes.changed": "Hierdie commit",
    "changes.binary": "Binêre lêer - geen teksverskil om te wys nie.",
    "changes.tooLarge": "Lêer is te groot om te verskil.",
    "changes.renamedFrom": "Hernoem van {path}",
    "changes.close": "Maak toe",
    "changes.prevChange": "Vorige verandering",
    "changes.nextChange": "Volgende verandering",
    "changes.collapseAll": "Vou alle vouers in",
    "changes.expandAll": "Vou alle dopgehou uit",
    "changes.searchPlaceholder": "Soek lêers …",
    "changes.noSearchResults": "Geen lêers pas by jou soektog nie.",
    "changes.maximize": "Maksimeer",
    "changes.restore": "Herstel grootte",
    "find.open": "Soek in hierdie verskil (Ctrl+F)",
    "find.placeholder": "Vind in verskil...",
    "find.prev": "Vorige wedstryd",
    "find.next": "Volgende wedstryd",
    "find.close": "Maak soektog toe",
    "find.noResults": "Geen resultate nie",
    "commit.title": "Verander veranderinge",
    "commit.close": "Maak toe",
    "commit.loading": "Lees tans werkende boom …",
    "commit.noChanges": "Daar is geen plaaslike veranderinge om te pleeg nie.",
    "commit.selectFile": "Kies 'n lêer om sy verskil te sien.",
    "commit.selectAll": "Kies alles",
    "commit.selectNone": "Vee alles uit",
    "commit.message": "Verbind boodskap",
    "commit.messagePlaceholder": "Beskryf die verandering",
    "commit.selectedCount": "{count}-lêer(s) gekies",
    "commit.review": "Hersien",
    "commit.reviewTitle": "Finale kontrole voor pleeg",
    "commit.reviewSummary": "Bevestig {count}-lêer(s) met boodskap: {message}",
    "commit.back": "Terug",
    "commit.commit": "Skep plaaslike commit",
    "menu.stashApply": "Pas stash toe",
    "menu.stashPop": "Pop stash (pas toe en verwyder)",
    "menu.stashDrop": "Gooi voorraad weg...",
    "merge.title": "Voeg tak saam",
    "merge.route": "Voeg saam met die huidige tak",
    "merge.source": "Van (bron)",
    "merge.target": "In (huidige)",
    "merge.loading": "Ontleed tans samesmelting …",
    "merge.upToDate": "Reeds op datum - niks om saam te smelt nie.",
    "merge.fastForward": "Snel vorentoe - geen samesmeltingstoewysing nodig nie (merk \"Skep 'n samevoegingstoewysing\" om een ​​af ​​te dwing).",
    "merge.summary": "{files} lêer(s) verander, {conflicts} konflik(e).",
    "merge.conflictsWarning": "Hierdie samevoeging het konflikte – jy kan steeds saamsmelt en dit dan in die redigeerder oplos.",
    "merge.noChanges": "Geen lêerveranderings nie.",
    "merge.files": "Gevolglike veranderinge",
    "merge.added": "Bygevoeg",
    "merge.modified": "Gewysig",
    "merge.deleted": "Geskrap",
    "merge.conflict": "Konflik",
    "merge.message": "Voeg toewysingsboodskap saam",
    "merge.messagePlaceholder": "Voeg tak 'bron' saam",
    "merge.messageFfHint": "Geïgnoreer tydens 'n vinnige-vorentoe-samesmelting (geen commit word geskep nie).",
    "merge.noFastForward": "Skep 'n samesmeltingstoewysing (geen vinnig vorentoe nie)",
    "merge.noFastForwardHint": "Teken altyd 'n samesmeltingstoewysing aan, selfs wanneer die tak kan vinnig vorentoe beweeg.",
    "merge.merge": "Voeg saam",
    "merge.cancel": "Kanselleer",
    "merge.previewError": "Kon nie 'n voorskou bereken nie: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash-samesmelting — elke verandering land as EEN commit op \"{target}\"; “{source}” se eie verbintenisse bly uit sy geskiedenis. Skakel terug in Instellings › Voeg saam.",
    "merge.messageSquash": "Verbind boodskap",
    "merge.squashMessagePlaceholder": "Verpletterde veranderinge vanaf 'bron'",
    "newBranch.title": "Skep tak",
    "newBranch.startPoint": "Nuwe tak vanaf {sha}",
    "newBranch.startPointOn": "op {refs}",
    "newBranch.location": "Ligging",
    "newBranch.locationRoot": "(wortel)",
    "newBranch.expandAll": "Brei alles uit",
    "newBranch.collapseAll": "Vou alles in",
    "newBranch.expandFolder": "Vou vouer uit",
    "newBranch.collapseFolder": "Vou vouer in",
    "newBranch.name": "Tak naam",
    "newBranch.namePlaceholder": "my-tak",
    "newBranch.fullName": "Volle naam: {name}",
    "newBranch.invalid": "Ongeldige taknaam",
    "newBranch.exists": "'n Tak met hierdie naam bestaan ​​reeds",
    "newBranch.checkout": "Checkout tak na skepping",
    "newBranch.create": "Skep",
    "newBranch.cancel": "Kanselleer",
    "status.loading": "Laai tans grafiek …",
    "status.summary": "{repo}Wys {commits} commits, {refs} refs",
    "status.branchCreated": "Geskep tak \"{name}\" by {sha}",
    "status.error": "Fout: {message}",
    "status.opFailed": "Operasie het misluk",
    "status.fetching": "Haal tans …",
    "status.pulling": "Trek tans …",
    "status.pushing": "Druk tans …",
    "status.committing": "Skep tans plaaslike verbintenis …",
    "status.commitCreated": "Geskep plaaslike commit {sha}: {message}",
    "status.syncing": "Sinkroniseer tans …",
    "status.noHead": "Geen huidige betaalpunt in die grafiek gevind nie.",
    "status.undoing": "Ontdoen tans commit …",
    "status.commitUndone": "Bevestig ongedaan - veranderinge is terug in die werkende boom.",
    "status.undoConflict": "Ontdoen trefkonflik – los dit op in die redigeerder en gaan dan voort.",
    "status.stashApplying": "Pas tans stash toe …",
    "status.stashPopping": "Plof voorraad …",
    "status.stashDropping": "Los tans voorraad …",
    "status.stashApplied": "Stash toegepas.",
    "status.stashPopped": "Stash het gespring.",
    "status.stashDropped": "Stash het geval.",
    "status.stashConflict": "Stash konflikte - los dit op in die redigeerder.",
    "status.merging": "Voeg tans saam …",
    "status.merged": "Samevoeging voltooi.",
    "status.mergeConflict": "Samevoeging het konflikte - los dit op in die redigeerder, pleeg dan.",
    "search.title": "Soek",
    "search.tooltip": "Soek pleeg",
    "search.placeholder": "Soek pleeg …",
    "search.previous": "Vorige resultaat",
    "search.next": "Volgende resultaat",
    "search.noResults": "Geen resultate nie",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtermodus",
    "search.highlight": "Merkmodus",
    "search.filterMode": "Soek vertoon",
    "search.filterModeFilter": "Filter (versteek nie-passings)",
    "search.filterModeHighlight": "Merk (wys alles, merk passings)",
    "search.filterModeHint": "Kies hoe soekresultate in die grafiek vertoon word.",
    "search.visibility": "Soek ligging",
    "search.visibilityToolbar": "Nutsbalkknoppie",
    "search.visibilityAlwaysVisible": "Altyd sigbare balk",
    "search.visibilityHint": "Wys soektog as 'n nutsbalkikoon of aanhoudende balk aan die bokant.",
    "footer.github": "GitHub",
  },
  gn: {
    "toolbar.refresh": "⟳ Ñembopyahu",
    "toolbar.fetch": "⤓ Ogueru haguã",
    "toolbar.pull": "⇩ Jaipyso",
    "toolbar.push": "⇧ Oñembotapykue",
    "toolbar.commit": "✓ Oñekompromete",
    "toolbar.sync": "⇅ Ñembojoaju",
    "toolbar.jumpHead": "⌖ Eho caja-pe",
    "toolbar.reset": "⤢ Oñemohenda jey jehechauka",
    "toolbar.settings": "⚙ Ñemboheko",
    "details.header": "Umi Detalle Ñe’ẽme’ẽ rehegua",
    "details.close": "Mboty",
    "details.sha": "SHA",
    "details.shortSha": "Mbykymi SHA",
    "details.message": "Marandu",
    "details.author": "Apohára",
    "details.date": "Fecha",
    "details.labels": "Etiquetas rehegua",
    "details.location": "Ñeimeha",
    "details.currentHead": "HEAD — chequeo ko’áĝagua",
    "settings.title": "Ñemboheko",
    "settings.language": "Ñe'ẽ",
    "settings.theme": "Ñe'ẽrã",
    "settings.themeLight": "Tesakã",
    "settings.themeDark": "Pytũ",
    "settings.themeLightHint": "Fondo tesape, texto iñypytûva.",
    "settings.themeDarkHint": "Fondo iñypytũva, jehaipyre tesape.",
    "settings.mainBranch": "Sucursal principal",
    "settings.mainBranchAuto": "Automático rehegua",
    "settings.mainBranchSearch": "Ojeporeka umi rama...",
    "settings.mainBranchNoMatch": "Ndaipóri hakã ojoajúva",
    "settings.display": "Estilo jehechauka rehegua",
    "settings.displayModern": "Iyahúva",
    "settings.displayClassic": "Clásico",
    "settings.displayModernHint": "Lona isãsóva — egueraha pan-pe, emongu’e embotuichave hag̃ua.",
    "settings.displayClassicHint": "Lona oñemyatyrõva — tronco ojefija izquierda gotyo, ndaipóri zoom, desplazamiento añoite (gráfico revisión SVN-icha).",
    "settings.sectionGeneral": "Opavavépe",
    "settings.sectionGraph": "Gráfico rehegua",
    "settings.sectionChanges": "Omoambue jehecha",
    "settings.diffMinimap": "Diff mínimo mapa rehegua",
    "settings.diffMinimapOn": "Ojehechauka",
    "settings.diffMinimapOff": "Kañy",
    "settings.diffMinimapOnHint": "Ehechauka peteĩ tira jehechapyrã VS Código-ichagua diff ykére — emongu’e eñemongu’e hag̃ua; umi marcador cambio rehegua oike.",
    "settings.diffMinimapOffHint": "Ndaipóri tira jehechapyrã — emongu’e pe diff normalmente.",
    "settings.commitReview": "Oñekompromete jehesa’ỹijo",
    "settings.commitReviewOn": "Eporandu eñekompromete mboyve",
    "settings.commitReviewOff": "Eñekompromete directamente",
    "settings.commitReviewOnHint": "Ehechauka peteĩ vore lista paha ha marandu jesareko emoheñói mboyve commit local.",
    "settings.commitReviewOffHint": "Pe botón Commit omoheñói pya’e pe commit local.",
    "settings.svnBranchDialog": "Ñe’ẽñemi rama rehegua SVN-ichagua",
    "settings.svnBranchDialogHint": "Ehechauka peteĩ carpeta-yvyra poravoha emoheñóivo peteĩ hakã (peteĩ ñe’ẽmondo isãsóva rangue).",
    "settings.branchDialog": "Ñe’ẽñemi sucursal rehegua",
    "settings.branchDialogSvnTitle": "SVN-ichagua",
    "settings.branchDialogNativeVscode": "VS Código estilo rehegua",
    "settings.branchDialogNativeVs": "Visual Studio estilo rehegua",
    "settings.branchDialogNativeHint": "Nde IDE mba’e’oka ñe’ẽmondo oñemboguapýva.",
    "settings.close": "Mboty",
    "settings.done": "Apopyre",
    "settings.sectionAdvanced": "Tenonde",
    "settings.gitSource": "Git ejecutable rehegua",
    "settings.gitSourceBuiltin": "Oñemopu’ãva’ekue",
    "settings.gitSourceBuiltinHint": "Eipuru git binario IDE Git ñemombaꞌepyrégui.",
    "settings.gitSourceCustom": "Tape ojejapóva jeporupyre",
    "settings.gitPath": "Tape ohóva git-pe",
    "settings.gitPathPlaceholder": "/usr/bin/git rehegua",
    "settings.gitPathBrowse": "Ojehechávo...",
    "settings.jargon": "Umi término git rehegua",
    "settings.jargonTranslate": "Ñe'ẽmbohasa",
    "settings.jargonEnglish": "Eñongatu ingléspe",
    "settings.jargonTranslateHint": "Embohasa Git ñe’ẽñemi (tira, empuje, commit, rama...) interfaz ñe’ẽme.",
    "settings.jargonEnglishHint": "Eñongatu Git ñe’ẽ (pull, push, commit, branch...) inglés-pe; ombohasa opa mba’e ambuéva.",
    "settings.sectionMerge": "Oñembojoaju",
    "settings.mergeMode": "Estilo ñembojoaju rehegua",
    "settings.mergeModeMerge": "Fusión normal rehegua",
    "settings.mergeModeSquash": "Squash ñembojoaju",
    "settings.mergeModeMergeHint": "Ombojoaju pe rama oĩháicha: icommit-kuéragui oiko peteĩ pehẽngue pe rama ko’ag̃agua rembiasápe, oñembojoajúva peteĩ commit ñembojoaju rehegua (térã oñembohasa pya’e ikatu jave).",
    "settings.mergeModeSquashHint": "Ombogue pe rama tuichakue peteĩ commit-pe pe rama koꞌag̃aguápe (git merge --squash). Pe rama commit-kuéra voi araka’eve ndoikéi pe rama ko’áĝagua rembiasápe — peteĩ commit añoite rehecha upépe. Pe rama voi ojeheja ndojepokói hese, upévare umi ikomprometido opyta ojehecha icarril-pe voi.",
    "settings.mergedView": "Umi commit oñembojoajúva gráfico-pe",
    "settings.mergedViewBranch": "Hakã teépe añoite",
    "settings.mergedViewTarget": "Avei pe rama-pe oñembojoaju haguépe hikuái",
    "settings.mergedViewBranchHint": "Peteĩ rama oñembojoajúva commits ojedibuja icarril-pe añoite; pe ñembojoaju ohechauka peteĩ joajuha ramo. Pe jehecha clásico.",
    "settings.mergedViewTargetHint": "Umi commit peteĩ merge ojeguerúva ojedibuja pe rama ohupytýva carril-pe avei, oñemboheko commit fusión guýpe ha oñenivela umi original ndive — upe rama añetehápe oguerekógui koꞌág̃a. Umi kópia ipire morotĩ ha iñakãrague pe rama ojehai hague ndive, upévare opyta porã ndojejapói hague upépe. Umi squash ñembojoaju ndojeafectái: peteĩ squash ohai peteĩ commit ordinario, upévare upe commit añoite ojekuaa arakaꞌeve.",
    "legend.title": "Mombe'ugua'u",
    "legend.head": "HEAD / ko’áĝagua rama",
    "legend.local": "Sucursal local",
    "legend.remote": "Rama mombyrygua",
    "legend.remoteOnly": "Araipe añoite (ndaha'éi ojeipysóva) .",
    "legend.tag": "Etiqueta (versión) rehegua .",
    "legend.commit": "Japo",
    "legend.stash": "Stash (tembiapo estantería rehegua) .",
    "legend.mergedIn": "Oike peteĩ fusión reheve (ojehaíva ambue rama-pe) .",
    "legend.nodes": "Umi káha",
    "legend.lines": "Línea-kuéra",
    "legend.edgeParent": "Commit mboyvegua peteĩ línea-pe",
    "legend.edgeMerge": "Rakã oñembojoajúva — pe flecha ohechauka pe ñembojoaju commit-pe",
    "legend.edgeBranch": "Peteĩ hakã ojeforkáva ko’ápe",
    "legend.edgeStash": "Stash ha pe commit ha’e oñemoĩva’ekue estante-pe",
    "legend.edgeMergedChain": "Ko rama línea ohasáva umi commit oñembojoajúva rupive",
    "legend.edgeMergedTie": "Peteĩchagua commit pe rama-pe ojehai haguépe",
    "menu.jumpHead": "⌖ Eho caja-pe",
    "menu.resetView": "⤢ Oñemohenda jey jehechauka",
    "menu.createBranch": "Ojapo rama ko'águi...",
    "menu.checkout": "Ejesareko ko commit rehe",
    "menu.copySha": "Kopia commit SHA rehegua",
    "menu.pushBranch": "Empuje rama \"{name}\".",
    "menu.renameCommit": "Embohéra jey marandu commit...",
    "menu.undoCommit": "Deshacer commit (eñongatu umi ñemoambue)...",
    "menu.viewChanges": "Ojehecha umi ñemoambue...",
    "menu.jumpToOriginal": "Eike pe commit original-pe",
    "changes.title": "Oñemoambue {sha}-pe",
    "changes.tabChanged": "Oñemoambue",
    "changes.tabAll": "Opaite Archivo-kuéra",
    "changes.added": "Oñembojoapy",
    "changes.modified": "Oñemoambue",
    "changes.deleted": "Oñembogue",
    "changes.renamed": "Oñembohéra jey",
    "changes.noChanges": "Ko commit nomoambuéi mba’eveichagua archivo.",
    "changes.loading": "Ojekarga diff...",
    "changes.selectFile": "Eiporavo peteĩ vore rehecha hag̃ua umi ñemoambue.",
    "changes.original": "Ypykue",
    "changes.changed": "Kóva okompromete",
    "changes.binary": "Archivo binario — ndaipóri jehaipyre joavy ojehechauka hag̃ua.",
    "changes.tooLarge": "Archivo tuichaiterei ojoavy hag̃ua.",
    "changes.renamedFrom": "Oñembohéra jey {path}-gui",
    "changes.close": "Mboty",
    "changes.prevChange": "Ñemoambue yma guare",
    "changes.nextChange": "Pe ñemoambue oúva",
    "changes.collapseAll": "Embogue opaite kundaha",
    "changes.expandAll": "Embotuichave opaite kundaha",
    "changes.searchPlaceholder": "Ojeheka umi archivo...",
    "changes.noSearchResults": "Ndaipóri archivo ojoajúva ne jeheka rehe.",
    "changes.maximize": "Oñemomba’eguasuvévo",
    "changes.restore": "Emoĩjey tuichakue",
    "find.open": "Eheka ko diff-pe (Ctrl+F) .",
    "find.placeholder": "Ojejuhu diff-pe...",
    "find.prev": "Ñeha'ã ohasava'ekue",
    "find.next": "Ñeha'ã oúva",
    "find.close": "Oñemboty jeheka",
    "find.noResults": "Ndaipóri resultado",
    "commit.title": "Oñemoĩ umi ñemoambue",
    "commit.close": "Mboty",
    "commit.loading": "Omoñe'êvo yvyra omba'apóva...",
    "commit.noChanges": "Ndaipóri umi cambio local oñekompromete haĝua.",
    "commit.selectFile": "Eiporavo peteĩ vore rehecha hag̃ua iñambueha.",
    "commit.selectAll": "Eiporavo opaite",
    "commit.selectNone": "Emopotĩ opa mba’e",
    "commit.message": "Marandu ñembohasa rehegua",
    "commit.messagePlaceholder": "Emombeʼu mbaʼéichapa oñekambia",
    "commit.selectedCount": "{count} vore(kuéra) ojeporavóva",
    "commit.review": "Vichea",
    "commit.reviewTitle": "Cheque paha oñekompromete mboyve",
    "commit.reviewSummary": "Embohasa {count} vore(kuéra) marandu reheve: {message}",
    "commit.back": "Kupe",
    "commit.commit": "Ojapo commit local",
    "menu.stashApply": "Ojeporu stash rehe",
    "menu.stashPop": "Pop stash (oñemboja & ojeipe'a) .",
    "menu.stashDrop": "Gota stash rehegua...",
    "merge.title": "Ñembojoaju Rama",
    "merge.route": "Oñembojoajúvo rama ko'ágãguápe",
    "merge.source": "Oúva (fuente)-gui.",
    "merge.target": "Into (ko’áĝagua) .",
    "merge.loading": "Oñeanalisávo fusión...",
    "merge.upToDate": "Oĩma ára ha ára — mba’eve oñembojoaju hag̃ua.",
    "merge.fastForward": "Pya’e tenonde gotyo — noñeikotevẽi commit ñembojoaju rehegua (emoĩ “Emoheñói peteĩ ñe’ẽmondo ñembojoaju” emombarete hag̃ua peteĩva).",
    "merge.summary": "{files} vore(kuéra) oñemoambue, {conflicts} joavy(kuéra).",
    "merge.conflictsWarning": "Ko ñembojoaju oguereko joavy — ikatu gueteri embojoaju, upéi emyatyrõ editor-pe.",
    "merge.noChanges": "Ndaipóri archivo ñemoambue.",
    "merge.files": "Umi cambio oúva chugui",
    "merge.added": "Oñembojoapy",
    "merge.modified": "Oñemoambue",
    "merge.deleted": "Oñembogue",
    "merge.conflict": "Ñorairõ",
    "merge.message": "Marandu commit ñembojoaju",
    "merge.messagePlaceholder": "Ombojoaju rama 'fuente'.",
    "merge.messageFfHint": "Ojehejarei peteĩ ñembojoaju pya’e tenonde gotyo (ndojejapói commit).",
    "merge.noFastForward": "Ejapo peteĩ ñembojoaju ñemboguata (ndaipóri pya’e tenonde gotyo) .",
    "merge.noFastForwardHint": "Akóinte ehai peteĩ commit de fusión, jepe pe rama ikatu jave oñemotenonde pya’e.",
    "merge.merge": "Oñembojoaju",
    "merge.cancel": "Heja",
    "merge.previewError": "Ndaikatúi oñekomputa peteĩ jehechaukarã: {message}",
    "merge.squashBadge": "Calabaza rehegua",
    "merge.squashNote": "Squash merge — opaite ñemoambue oguejy ONE commit ramo “{target}”-pe; “{source}” ñe’ẽme’ẽ tee opyta okápe hembiaságui. Embohasa jey Ñemboheko › Ñembojoaju.",
    "merge.messageSquash": "Marandu ñembohasa rehegua",
    "merge.squashMessagePlaceholder": "Umi ñemoambue oñemboja'óva 'fuente'-gui.",
    "newBranch.title": "Ojapo Rama",
    "newBranch.startPoint": "Sucursal pyahu oñepyrûva {sha} guive",
    "newBranch.startPointOn": "{refs}-pe",
    "newBranch.location": "Ñeimeha",
    "newBranch.locationRoot": "(rapo)",
    "newBranch.expandAll": "Embotuichave opa mba’e",
    "newBranch.collapseAll": "Oitypaite",
    "newBranch.expandFolder": "Ombotuichave carpeta",
    "newBranch.collapseFolder": "Carpeta ñembyai",
    "newBranch.name": "Sucursal réra",
    "newBranch.namePlaceholder": "che-rakã",
    "newBranch.fullName": "Héra tee: {name}",
    "newBranch.invalid": "Rama réra ndojeporúiva",
    "newBranch.exists": "Oĩma peteĩ rama orekóva ko téra",
    "newBranch.checkout": "Checkout rama ojejapo rire",
    "newBranch.create": "Mboypy",
    "newBranch.cancel": "Heja",
    "status.loading": "Ojekargávo gráfico...",
    "status.summary": "{repo}Ohechaukávo {commits} ñemboguata, {refs} refs",
    "status.branchCreated": "Ojejapo rama \"{name}\" {sha}-pe",
    "status.error": "Ojejavy: {message}",
    "status.opFailed": "Operación ndoikói",
    "status.fetching": "Ojegueru haguã...",
    "status.pulling": "Otiravo...",
    "status.pushing": "Oempujávo...",
    "status.committing": "Omoheñóivo commit local...",
    "status.commitCreated": "Ojejapo commit local {sha}: {message}",
    "status.syncing": "Oñembojoajúvo...",
    "status.noHead": "Ndaipóri cheque ko'ágãgua ojejuhúva gráfico-pe.",
    "status.undoing": "Ojedeshacer commit...",
    "status.commitUndone": "Commit undone — umi ñemoambue oĩ jey yvyra omba’apóvape.",
    "status.undoConflict": "Embogue umi conflicto golpe rehegua — emohenda editor-pe, upéi eñepyrũ.",
    "status.stashApplying": "Ojeaplikavo stash...",
    "status.stashPopping": "Popping stash rehegua...",
    "status.stashDropping": "Oity stash...",
    "status.stashApplied": "Stash ojeaplika.",
    "status.stashPopped": "Stash opukavy.",
    "status.stashDropped": "Stash oguejy.",
    "status.stashConflict": "Stash conflictos — embohovái editor-pe.",
    "status.merging": "Oñembojoajúvo...",
    "status.merged": "Ñembojoaju oñemohu’ã.",
    "status.mergeConflict": "Merge oguereko joavy — embohovái editor-pe, upéi eñekompromete.",
    "search.title": "Jeheka",
    "search.tooltip": "Ojeporeka umi ñe’ẽme’ẽ",
    "search.placeholder": "Ojeporeka oñekompromete...",
    "search.previous": "Resultado yma guaréva",
    "search.next": "Resultado oúva",
    "search.noResults": "Ndaipóri resultado",
    "search.resultCount": "{index}/{count} rehegua",
    "search.filter": "Modo filtro rehegua",
    "search.highlight": "Modo de resaltado rehegua",
    "search.filterMode": "Jeporeka jehechauka",
    "search.filterModeFilter": "Filtro (eñomi umi ndojoajúiva) .",
    "search.filterModeHighlight": "Emomba’eguasu (ehechauka opaite, emarka umi joaju) .",
    "search.filterModeHint": "Eiporavo mba éichapa ojehechauka umi mba e ojehekáva gráfico-pe.",
    "search.visibility": "Eheka moõpa oĩ",
    "search.visibilityToolbar": "Tembipuru’i ryru botón",
    "search.visibilityAlwaysVisible": "Barra akóinte ojehecháva",
    "search.visibilityHint": "Ehechauka jeheka peteĩ tembipuru’i ryru ra’ãnga térã barra persistente ramo yvate gotyo.",
    "footer.github": "GitHub rehegua",
  },
  ay: {
    "toolbar.refresh": "⟳ Ukax mä juk’a pachanakanwa",
    "toolbar.fetch": "⤓ Uñstayaña",
    "toolbar.pull": "⇩ Jaqi",
    "toolbar.push": "⇧ Ukax mä juk’a pachanakanwa",
    "toolbar.commit": "✓ Compromiso luraña",
    "toolbar.sync": "⇅ Sincronización ukampi",
    "toolbar.jumpHead": "⌖ Chequeo ukar sarañamawa",
    "toolbar.reset": "⤢ Uñakipaña uñakipaña",
    "toolbar.settings": "⚙ Uñstayaña",
    "details.header": "Detalles ukanak luraña",
    "details.close": "Jist'antaña",
    "details.sha": "SHA",
    "details.shortSha": "Mä jisk’a SHA",
    "details.message": "Yatiya",
    "details.author": "Awtura",
    "details.date": "Uru",
    "details.labels": "Etiquetas ukanaka",
    "details.location": "Kawkhachiqa",
    "details.currentHead": "HEAD — jichha pacha qullqi",
    "settings.title": "Ukax mä juk’a pachanakanwa",
    "settings.language": "Aru",
    "settings.theme": "Timatika",
    "settings.themeLight": "Qhana",
    "settings.themeDark": "Ch'amaka",
    "settings.themeLightHint": "Qhana ch’iyara, ch’amaka qillqa.",
    "settings.themeDarkHint": "Ch’amaka qhipa, qhana qillqata.",
    "settings.mainBranch": "Jilïr sucursala",
    "settings.mainBranchAuto": "Automático ukhamawa",
    "settings.mainBranchSearch": "Sucursalanak thaqhaña...",
    "settings.mainBranchNoMatch": "Janiw mä khuskhaki ramanakax utjkiti",
    "settings.display": "Uñacht’ayaña estilo",
    "settings.displayModern": "Qhipa uñsta",
    "settings.displayClassic": "Clásico ukat juk’ampinaka",
    "settings.displayModernHint": "Libre lona — pankaru apsuña, jach’aptayaña.",
    "settings.displayClassicHint": "Lona askichata — tronco pinned ch’iqa, janiw zoom, desplazamiento ukaki (SVN revisión gráfico ukar uñtasita).",
    "settings.sectionGeneral": "Taqpacha",
    "settings.sectionGraph": "Gráfico ukax mä juk’a pachanakanwa",
    "settings.sectionChanges": "Mayjt’awinaka uñjawi",
    "settings.diffMinimap": "Diff minimap ukax mä jach’a uñacht’äwiwa",
    "settings.diffMinimapOn": "Uñacht’ayata",
    "settings.diffMinimapOff": "Imantata",
    "settings.diffMinimapOnHint": "Mä VS Código-estilo uñakipañ tira uñacht’ayaña diff lado — desplazamiento ukar apsuña; mayjt’awi markachirinakaxa uñt’ayatarakiwa.",
    "settings.diffMinimapOffHint": "Janiw mä uñakipaña tira — desplazamiento diff normalmente.",
    "settings.commitReview": "Commit uñakipaña",
    "settings.commitReviewOn": "Janïr commit lurañkamax jiskt’asiñamawa",
    "settings.commitReviewOff": "Chiqapa luraña",
    "settings.commitReviewOnHint": "Qhipa arsuwi lista uñacht’ayaña ukhamaraki yatiyawi uñakipaña janïra local commit lurañataki.",
    "settings.commitReviewOffHint": "Commit ukax jank’akiw local commit uñstayi.",
    "settings.svnBranchDialog": "SVN-estilo sucursal ukan aruskipäwipa",
    "settings.svnBranchDialogHint": "Mä carpeta-quqa apthapiri uñacht’ayaña kunapachatixa mä rama luraña (mä sanu jiskt’aña lanti).",
    "settings.branchDialog": "Sucursalan aruskipäwipa",
    "settings.branchDialogSvnTitle": "SVN-estilo ukan uñt’ayata",
    "settings.branchDialogNativeVscode": "VS Código ukan estilopa",
    "settings.branchDialogNativeVs": "Visual Studio ukax mä estilo ukhamawa",
    "settings.branchDialogNativeHint": "IDE ukax mä sucursal ukan luratawa.",
    "settings.close": "Jist'antaña",
    "settings.done": "Lurata",
    "settings.sectionAdvanced": "Nayrankaña",
    "settings.gitSource": "Git ukax ejecutable ukhamawa",
    "settings.gitSourceBuiltin": "Ukax mä juk’a pachanakanwa",
    "settings.gitSourceBuiltinHint": "IDE ukan Git ukan lurat extensión ukan git binario ukamp apnaqaña.",
    "settings.gitSourceCustom": "Sapa thakhi",
    "settings.gitPath": "Thakhix git ukaruw puri",
    "settings.gitPathPlaceholder": "/usr/bin/git ukax mä juk’a pachanakanwa",
    "settings.gitPathBrowse": "Uñakipt’añataki...",
    "settings.jargon": "Git ukax mä término ukhamawa",
    "settings.jargonTranslate": "Jaqukipaña",
    "settings.jargonEnglish": "Inglés arun imañamawa",
    "settings.jargonTranslateHint": "Git uka arunak (pull, push, commit, rama...) interfaz arut jaqukipaña.",
    "settings.jargonEnglishHint": "Git uka arunak (pull, push, commit, branch...) inglés arun imañamawa; taqi kunas jaqukipaña.",
    "settings.sectionMerge": "Mayacht’asiñawa",
    "settings.mergeMode": "Estilo de fusión ukampi",
    "settings.mergeModeMerge": "Normal uñt’ayawi",
    "settings.mergeModeSquash": "Squash ukaxa mayachatawa",
    "settings.mergeModeMergeHint": "Sucursalax kunjamäkitix ukhamarjamaw mayachthapi: commits ukax jichha sucursalan sarnaqäwipan chikanchatawa, mä commit de fusión ukamp chikt’ata (jan ukax kunapachatix wakiski ukhax jank’akiw nayrar sartayata).",
    "settings.mergeModeSquashHint": "Taqi ramax mä commit ukar jichha rama (git merge --squash) ukar t’unjaña. Sucursalan pachpa commits ukanakax janipuniw jichha sucursalan sarnaqäwiparux mantkiti — mä commit ukakiw ukanx uñjta. Pachpa sucursalax jan llamkt’ataw jaytatäxi, ukhamax commits ukanakax carril ukan uñjataw qhiparapxi.",
    "settings.mergedView": "Gráfico ukanx commits ukanakax mayacht’atawa",
    "settings.mergedViewBranch": "Jupanakan sucursalaparukiw ukham lurapxäna",
    "settings.mergedViewTarget": "Ukhamaraki sucursalaruw mayacht’asipxäna",
    "settings.mergedViewBranchHint": "Mä sucursal fusionada ukan commits ukanakax carril ukanakapankiwa; uka mayacht’asiwix mä conector ukham uñacht’ayatawa. Uka clásico uñtawi.",
    "settings.mergedViewTargetHint": "Mä mayacht’asiw apanit commits ukanakax katuqir sucursalan carril ukanx dibujat ukhamarakiwa, commit de fusión ukan pilat uñstayata ukat originales ukanakamp nivela — kunatix uka ramax chiqpachapuniw jichhax ukanakx utji. Copianakajj chʼiyar ñikʼutaniwa ukat kawkïr sucursalarutï qellqatäkäna uka pʼeqempiw pʼeqtʼatäpjje, ukhamajj janiw ukan luratäpkänti sasaw qhan amuyasi. Squash ukan mayacht’asiwinakapax janiw afectatakiti: mä squash ukax mä ordinario commit qillqt’i, ukhamax uka commit ukakiw uñsti.",
    "legend.title": "Sara",
    "legend.head": "P’iqi / jichha sucursala",
    "legend.local": "Uka markankir sucursala",
    "legend.remote": "Jayankir sucursala",
    "legend.remoteOnly": "Qhixu qhixu taypinkiwa (janiw jaqukipatakiti) .",
    "legend.tag": "Etiqueta (versión) ukat juk’ampinaka.",
    "legend.commit": "Phuqhawsaña",
    "legend.stash": "Stash (estantería ukan irnaqawi) .",
    "legend.mergedIn": "Mä fusión ukamp mantani (yaqha sucursalan qillqt’ata) .",
    "legend.nodes": "Ukax mä juk’a pachanakanwa",
    "legend.lines": "Líneas ukanaka",
    "legend.edgeParent": "Nayra commit ukax pachpa línea ukankiwa",
    "legend.edgeMerge": "Mayacht’at rama — flechax mayacht’asiñ commit ukar uñacht’ayi",
    "legend.edgeBranch": "Mä ramaw aka cheqan bifurcatäna",
    "legend.edgeStash": "Stash ukat commit ukax estante ukan uñt’ayatawa",
    "legend.edgeMergedChain": "Aka sucursalan línea ukax commits merged-in ukanakan t’ijt’aski",
    "legend.edgeMergedTie": "Uka pachpa commit ukax sucursalanx qillqt’atawa",
    "menu.jumpHead": "⌖ Chequeo ukar sarañamawa",
    "menu.resetView": "⤢ Uñakipaña uñakipaña",
    "menu.createBranch": "Aka chiqat sucursala luraña...",
    "menu.checkout": "Aka commit uñakipt’añäni",
    "menu.copySha": "Copia commit SHA ukax mä juk’a pachanakanwa",
    "menu.pushBranch": "Push rama \"{name}\" ukax mä juk'a pachanakanwa.",
    "menu.renameCommit": "Commit yatiyaw suti mayjt’ayaña...",
    "menu.undoCommit": "Undo commit (mayjt’awinak imañamawa)...",
    "menu.viewChanges": "Mayjt’awinak uñjaña...",
    "menu.jumpToOriginal": "Uka commit original ukar sarañamawa",
    "changes.title": "{sha} ukan mayjt’awinaka",
    "changes.tabChanged": "Mayjt’ayata",
    "changes.tabAll": "Taqi Archivonaka",
    "changes.added": "Ukhamaraki",
    "changes.modified": "Mayjt’ayata",
    "changes.deleted": "Chhaqtayata",
    "changes.renamed": "Suti mayjt’ayata",
    "changes.noChanges": "Aka commit ukax janiw kuna archivonakas mayjt’aykiti.",
    "changes.loading": "Ukax diff...",
    "changes.selectFile": "Mä fichero ajlliñamawa mayjt’awinaka uñjañataki.",
    "changes.original": "Urijinala",
    "changes.changed": "Aka commit",
    "changes.binary": "Binario archivo — janiw qillqat diff uñacht’ayañataki.",
    "changes.tooLarge": "Archivox wali jach’awa diff.",
    "changes.renamedFrom": "{path} ukan sutip mayjt’ayata",
    "changes.close": "Jist'antaña",
    "changes.prevChange": "Nayra mayjt’awinaka",
    "changes.nextChange": "Jutïr mayjt’äwi",
    "changes.collapseAll": "Taqi carpetas ukanaka t’unjaña",
    "changes.expandAll": "Taqi carpetas ukanaka jilxatayaña",
    "changes.searchPlaceholder": "Archivonaka thaqhaña...",
    "changes.noSearchResults": "Janiw kuna archivonakas thakhiparjamäkiti.",
    "changes.maximize": "Maximizar ukax mä juk’a pachanakanwa",
    "changes.restore": "Tamapa kutt’ayaña",
    "find.open": "Aka diff (Ctrl+F) ukan thaqhapxam.",
    "find.placeholder": "Ukax diff...",
    "find.prev": "Nayra uñt’ayawi",
    "find.next": "Jutïr uñstawi",
    "find.close": "Jist’antaña thaqhaña",
    "find.noResults": "Janiw kuna resultados utjkiti",
    "commit.title": "Mayjt’awinak luraña",
    "commit.close": "Jist'antaña",
    "commit.loading": "Uñakipt’aña irnaqir quqa...",
    "commit.noChanges": "Janiw kuna local mayjt’awinakax utjkiti compromiso lurañataki.",
    "commit.selectFile": "Mä fichero ajlliñamawa diff uñakipañataki.",
    "commit.selectAll": "Taqi ukanak ajlliñamawa",
    "commit.selectNone": "Taqi kunas q’umachaña",
    "commit.message": "Commit yatiyawi",
    "commit.messagePlaceholder": "Mayjt’awi tuqita qhananchaña",
    "commit.selectedCount": "{count} qillqata(naka) ajllitawa",
    "commit.review": "Uñakipaña",
    "commit.reviewTitle": "Qhipa uñakipaña janïra commit",
    "commit.reviewSummary": "{count} qillqata(naka) yatiyawimpi commit: {message}",
    "commit.back": "Qhipa",
    "commit.commit": "Local commit luraña",
    "menu.stashApply": "Ukaxa stash satawa",
    "menu.stashPop": "Pop stash (aplikaña & apsuña) .",
    "menu.stashDrop": "Ukax mä juk’a pachanakanwa...",
    "merge.title": "Sucursalax mayachatawa",
    "merge.route": "Jichha sucursalar mayachasiña",
    "merge.source": "Ukax (fuente) ukankiwa.",
    "merge.target": "Into (jichha pacha) .",
    "merge.loading": "Uñakipt’añatakix fusión...",
    "merge.upToDate": "Nayratpach jichha pacha — janiw kunas mayacht’asiñatakïkiti.",
    "merge.fastForward": "Jank’akiw nayrar sartaña — janiw mä commit de fusión ukax wakiskiti (“mä commit de fusión luraña” ukx ch’iqt’am mä ch’amanchañataki).",
    "merge.summary": "{files} arsuwi(naka) mayjt’ayata, {conflicts} ch’axwawi(naka).",
    "merge.conflictsWarning": "Aka mayacht’asiwix ch’axwawinakaniwa — wali mayacht’asiñamawa, ukatx editor ukan askichañamawa.",
    "merge.noChanges": "Janiw kuna fichero mayjt’ayañas utjkiti.",
    "merge.files": "Ukhamatwa mayjt’awinakax utji",
    "merge.added": "Ukhamaraki",
    "merge.modified": "Mayjt’ayata",
    "merge.deleted": "Chhaqtayata",
    "merge.conflict": "Jan walt'a",
    "merge.message": "Mä commit yatiyaw mayacht’aña",
    "merge.messagePlaceholder": "Sucursala ‘fuente’ ukar mayacht’aña.",
    "merge.messageFfHint": "Mä jank’ak nayrar sartañ mayacht’asiwinx janiw yäqatäkiti (janiw mä commit luratäkiti).",
    "merge.noFastForward": "Mä commit de fusión luraña (janiw jank’ak nayrar sartayañakiti) .",
    "merge.noFastForwardHint": "Sapa kutiw mä commit de fusión ukar qillqt’añama, kunapachatix sucursalax jank’ak nayrar sartaspa ukhas.",
    "merge.merge": "Mayacht’asiñawa",
    "merge.cancel": "Kansilaña",
    "merge.previewError": "Janiw mä nayrïr uñacht’äw jakthapiñjamäkiti: {message}",
    "merge.squashBadge": "Ukax mä juk’a pachanakanwa",
    "merge.squashNote": "Squash mayachawi — sapa mayjt’awix uraqiruw puri kunjamatix ONE commit “{target}”; “{source}” ukax pachpa commits ukanakax historiapat jan qhiparapxi. Configuración › Mayacht’aña ukar kutt’añamawa.",
    "merge.messageSquash": "Commit yatiyawi",
    "merge.squashMessagePlaceholder": "Squashed mayjt’awinakax ‘fuente’ ukanx utjiwa.",
    "newBranch.title": "Sucursala luraña",
    "newBranch.startPoint": "Machaq sucursalax {sha} ukan qalltawayi",
    "newBranch.startPointOn": "ukax {refs} ukan uñt’ayatawa",
    "newBranch.location": "Kawkhachiqa",
    "newBranch.locationRoot": "(saphi)",
    "newBranch.expandAll": "Taqi kuna jilxatayaña",
    "newBranch.collapseAll": "Taqi kunas t’unjaña",
    "newBranch.expandFolder": "Jach’a carpeta",
    "newBranch.collapseFolder": "Ukax mä carpeta ch’allt’atawa",
    "newBranch.name": "Sucursalan sutipa",
    "newBranch.namePlaceholder": "nayan-ramaja",
    "newBranch.fullName": "Taqi sutipax akhamawa: {name}",
    "newBranch.invalid": "Jan wali sucursala sutini",
    "newBranch.exists": "Uka sutimp uñt’at mä sucursalax nayratpach utjxiwa",
    "newBranch.checkout": "Checkout sucursala lurawi tukuyatatxa",
    "newBranch.create": "Uñstayaña",
    "newBranch.cancel": "Kansilaña",
    "status.loading": "Gráfico ukar apkatañataki...",
    "status.summary": "{repo}Uñacht’ayaña {commits} commits, {refs} refs",
    "status.branchCreated": "Uñstayata sucursal \"{name}\" ukaxa {sha} ukankiwa",
    "status.error": "Pantjasiwi: {message}",
    "status.opFailed": "Operación ukax janiw phuqhaskänti",
    "status.fetching": "Ukax mä...",
    "status.pulling": "Jaqukipaña...",
    "status.pushing": "Ukax mä juk’a...",
    "status.committing": "Local commit uñstayaña...",
    "status.commitCreated": "Uñstayata local commit {sha}: {message}",
    "status.syncing": "Sincronización ukax...",
    "status.noHead": "Janiw jichha pacha qullqix gráfico ukan jikxataskiti.",
    "status.undoing": "Commit ukar jan walt’ayaña...",
    "status.commitUndone": "Commit undone — mayjt’awinakax irnaqir quqaruw kutt’awayxi.",
    "status.undoConflict": "Undo hit conflicts — editor ukan askichaña, ukatx sarantaskakiwa.",
    "status.stashApplying": "Ukax stash...",
    "status.stashPopping": "Popping stash ukax...",
    "status.stashDropping": "Ukax stash ukar jaqukipatawa...",
    "status.stashApplied": "Stash ukax aplikasi.",
    "status.stashPopped": "Stash ukax pop sasaw säna.",
    "status.stashDropped": "Stash ukax jaqukipatawa.",
    "status.stashConflict": "Stash ch’axwawinaka — editor ukan askichaña.",
    "status.merging": "Uñt’ayasiñataki...",
    "status.merged": "Mayacht’asiñax tukuyatawa.",
    "status.mergeConflict": "Merge ukax ch’axwawinakaniwa — editor ukan askichaña, ukatx commit.",
    "search.title": "Thaqhata",
    "search.tooltip": "Uñakipt’añatakix commits ukanakaw utji",
    "search.placeholder": "Uñakipt’añatakix commits...",
    "search.previous": "Nayra pachanxa",
    "search.next": "Jutïr resultadojj utjarakiwa",
    "search.noResults": "Janiw kuna resultados utjkiti",
    "search.resultCount": "{index}/{count} ukax mä juk’a pachanakanwa",
    "search.filter": "Modo filtrado ukax mä juk’a pachanakanwa",
    "search.highlight": "Modo de resaltado ukax mä jach’a uñacht’äwiwa",
    "search.filterMode": "Thaqhaña uñacht’ayaña",
    "search.filterModeFilter": "Filtro (jan uñt’atanaka imantaña) .",
    "search.filterModeHighlight": "Uñacht’ayaña (taqi uñacht’ayaña, chimpu chimpuña) .",
    "search.filterModeHint": "Kunjamsa thakhinchawixa uñacht’ayatarakini ukxa ajlliñamawa.",
    "search.visibility": "Ukax kawkhantix utjki uk thaqhaña",
    "search.visibilityToolbar": "Barra de herramientas ukax mä botón ukankiwa",
    "search.visibilityAlwaysVisible": "Sapa kuti-uñjata barra",
    "search.visibilityHint": "Thakhi uñacht’ayaña mä icono de barra de herramientas jan ukaxa barra persistente ukhama pata tuqina.",
    "footer.github": "GitHub ukax mä juk’a pachanakanwa",
  },
  az: {
    "toolbar.refresh": "⟳ Yeniləyin",
    "toolbar.fetch": "⤓ gətirin",
    "toolbar.pull": "⇩ çəkin",
    "toolbar.push": "⇧ itələyin",
    "toolbar.commit": "✓ Əmin olun",
    "toolbar.sync": "⇅ Sinxronizasiya",
    "toolbar.jumpHead": "⌖ Kassaya keçin",
    "toolbar.reset": "⤢ Görünüşü sıfırlayın",
    "toolbar.settings": "⚙ Parametrlər",
    "details.header": "Təfərrüatları təhvil verin",
    "details.close": "Bağlayın",
    "details.sha": "SHA",
    "details.shortSha": "Qısa SHA",
    "details.message": "Mesaj",
    "details.author": "Müəllif",
    "details.date": "Tarix",
    "details.labels": "Etiketlər",
    "details.location": "Məkan",
    "details.currentHead": "HEAD — cari yoxlama",
    "settings.title": "Parametrlər",
    "settings.language": "Dil",
    "settings.theme": "Mövzu",
    "settings.themeLight": "İşıq",
    "settings.themeDark": "Qaranlıq",
    "settings.themeLightHint": "Açıq fon, qaranlıq mətn.",
    "settings.themeDarkHint": "Qara fon, açıq mətn.",
    "settings.mainBranch": "Əsas filial",
    "settings.mainBranchAuto": "Avtomatik",
    "settings.mainBranchSearch": "Filialları axtarın...",
    "settings.mainBranchNoMatch": "Uyğun filial yoxdur",
    "settings.display": "Ekran tərzi",
    "settings.displayModern": "Müasir",
    "settings.displayClassic": "Klassik",
    "settings.displayModernHint": "Pulsuz kətan — sürüşdürmək üçün sürüşdürün, böyütmək üçün sürüşdürün.",
    "settings.displayClassicHint": "Sabit kətan — magistral sola bərkidilib, böyütmə yoxdur, yalnız sürüşdürün (SVN reviziya qrafiki kimi).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Qrafik",
    "settings.sectionChanges": "Görünüşü dəyişdirir",
    "settings.diffMinimap": "Fərqli mini xəritə",
    "settings.diffMinimapOn": "Göstərildi",
    "settings.diffMinimapOff": "Gizli",
    "settings.diffMinimapOnHint": "Fərqin yanında VS Kodu üslublu icmal zolağı göstərin — sürüşdürmək üçün onu dartın; Dəyişiklik işarələri daxildir.",
    "settings.diffMinimapOffHint": "Ümumi baxış zolağı yoxdur — fərqi normal şəkildə sürüşdürün.",
    "settings.commitReview": "Nəzərdən keçirin",
    "settings.commitReviewOn": "Etməzdən əvvəl soruşun",
    "settings.commitReviewOff": "Birbaşa təhvil verin",
    "settings.commitReviewOnHint": "Yerli öhdəliyi yaratmazdan əvvəl son fayl siyahısını göstərin və mesaj yoxlayın.",
    "settings.commitReviewOffHint": "Commit düyməsi dərhal yerli öhdəliyi yaradır.",
    "settings.svnBranchDialog": "SVN tipli filial dialoqu",
    "settings.svnBranchDialogHint": "Filial yaratarkən qovluq ağacı seçicisini göstərin (sadə əmr əvəzinə).",
    "settings.branchDialog": "Filial dialoqu",
    "settings.branchDialogSvnTitle": "SVN tərzi",
    "settings.branchDialogNativeVscode": "VS Kod tərzi",
    "settings.branchDialogNativeVs": "Visual Studio üslubu",
    "settings.branchDialogNativeHint": "IDE-nin daxili filial sorğusu.",
    "settings.close": "Bağlayın",
    "settings.done": "Bitdi",
    "settings.sectionAdvanced": "Qabaqcıl",
    "settings.gitSource": "Git icra edilə bilən",
    "settings.gitSourceBuiltin": "Daxili",
    "settings.gitSourceBuiltinHint": "IDE-nin daxili Git uzantısından git binarını istifadə edin.",
    "settings.gitSourceCustom": "Fərdi yol",
    "settings.gitPath": "Git-ə gedən yol",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Baxın...",
    "settings.jargon": "Git şərtləri",
    "settings.jargonTranslate": "Tərcümə et",
    "settings.jargonEnglish": "İngilis dilində saxlayın",
    "settings.jargonTranslateHint": "Git terminlərini (çəkmək, itələmək, icra etmək, bölmək...) interfeys dilinə tərcümə edin.",
    "settings.jargonEnglishHint": "Git terminlərini ingilis dilində saxlayın (çəkin, itələyin, icra edin, budaqlayın...); qalan hər şeyi tərcümə edin.",
    "settings.sectionMerge": "Birləşdirin",
    "settings.mergeMode": "Stil birləşdirin",
    "settings.mergeModeMerge": "Normal birləşmə",
    "settings.mergeModeSquash": "Squash birləşməsi",
    "settings.mergeModeMergeHint": "Filialı olduğu kimi birləşdirir: onun öhdəlikləri birləşmə öhdəliyi ilə birləşdirilən (və ya mümkün olduqda sürətlə irəli sürülən) cari filialın tarixinin bir hissəsinə çevrilir.",
    "settings.mergeModeSquashHint": "Bütün filialı cari filialda vahid öhdəliyə yığır (git merge --squash). Filialın öz öhdəlikləri heç vaxt cari filialın tarixinə daxil olmur – orada yalnız bir öhdəliyi görürsünüz. Filialın özü toxunulmaz qalır, ona görə də onun öhdəlikləri öz zolağında görünür.",
    "settings.mergedView": "Qrafikdə birləşdirilmiş öhdəliklər",
    "settings.mergedViewBranch": "Yalnız öz filiallarında",
    "settings.mergedViewTarget": "Həm də filialda birləşdilər",
    "settings.mergedViewBranchHint": "Birləşdirilmiş filialın öhdəlikləri yalnız öz zolağında tərtib edilir; birləşmə birləşdirici kimi görünür. Klassik mənzərə.",
    "settings.mergedViewTargetHint": "Daxil edilən birləşmələr qəbuledici filialın zolağında da çəkilir, birləşmə öhdəliyi altında yığılır və orijinallarla bərabər səviyyədədir - çünki bu filial həqiqətən onları ehtiva edir. Nüsxələr solğundur və üzərində yazıldıqları budaqla başlanır, ona görə də onların orada hazırlanmadığı aydın qalır. Squash birləşmələri təsirlənmir: squash bir adi öhdəliyi yazır, beləliklə yalnız bu öhdəlik həmişə görünür.",
    "legend.title": "Əfsanə",
    "legend.head": "HEAD / cari filial",
    "legend.local": "Yerli filial",
    "legend.remote": "Uzaq filial",
    "legend.remoteOnly": "Yalnız buludda (çəkilməyib)",
    "legend.tag": "Etiket (versiya)",
    "legend.commit": "öhdəsindən gəlmək",
    "legend.stash": "Stash (rəfdə iş)",
    "legend.mergedIn": "Birləşmə ilə gəldi (başqa filialda yazılmışdır)",
    "legend.nodes": "Qutular",
    "legend.lines": "Xətlər",
    "legend.edgeParent": "Eyni xətt üzrə əvvəlki öhdəlik",
    "legend.edgeMerge": "Birləşdirilmiş filial — ox birləşmə öhdəliyini göstərir",
    "legend.edgeBranch": "Bir budaq buradan ayrıldı",
    "legend.edgeStash": "Stash və onun rəfdə olduğu öhdəlik",
    "legend.edgeMergedChain": "Bu filialın xətti birləşdirilmiş öhdəliklərdən keçir",
    "legend.edgeMergedTie": "Eyni öhdəliyi filialda da yazılmışdı",
    "menu.jumpHead": "⌖ Kassaya keçin",
    "menu.resetView": "⤢ Görünüşü sıfırlayın",
    "menu.createBranch": "Buradan filial yaradın...",
    "menu.checkout": "Bu öhdəliyi yoxlayın",
    "menu.copySha": "SHA-nı kopyalayın",
    "menu.pushBranch": "\"{name}\" filialını itələyin",
    "menu.renameCommit": "Göndərmə mesajının adını dəyişdirin...",
    "menu.undoCommit": "Öhdəliyi ləğv edin (dəyişiklikləri saxlayın)…",
    "menu.viewChanges": "Dəyişikliklərə baxın...",
    "menu.jumpToOriginal": "Orijinal öhdəliyə keçin",
    "changes.title": "{sha}-də dəyişikliklər",
    "changes.tabChanged": "Dəyişdi",
    "changes.tabAll": "Bütün Fayllar",
    "changes.added": "Əlavə edilib",
    "changes.modified": "Dəyişdirilib",
    "changes.deleted": "Silindi",
    "changes.renamed": "Adı dəyişdirildi",
    "changes.noChanges": "Bu öhdəlik heç bir faylı dəyişdirmir.",
    "changes.loading": "Fərq yüklənir...",
    "changes.selectFile": "Dəyişiklikləri görmək üçün faylı seçin.",
    "changes.original": "Orijinal",
    "changes.changed": "Bu öhdəlik",
    "changes.binary": "İkili fayl — göstərmək üçün mətn fərqi yoxdur.",
    "changes.tooLarge": "Fayl fərqlənmək üçün çox böyükdür.",
    "changes.renamedFrom": "{path}-dən adı dəyişdirilib",
    "changes.close": "Bağlayın",
    "changes.prevChange": "Əvvəlki dəyişiklik",
    "changes.nextChange": "Növbəti dəyişiklik",
    "changes.collapseAll": "Bütün qovluqları yığışdırın",
    "changes.expandAll": "Bütün qovluqları genişləndirin",
    "changes.searchPlaceholder": "Faylları axtarın...",
    "changes.noSearchResults": "Axtarışınıza uyğun fayl yoxdur.",
    "changes.maximize": "Maksimallaşdırın",
    "changes.restore": "Ölçüsü bərpa edin",
    "find.open": "Bu fərqdə axtarın (Ctrl+F)",
    "find.placeholder": "Fərqində tap...",
    "find.prev": "Əvvəlki matç",
    "find.next": "Növbəti matç",
    "find.close": "Axtarışı bağlayın",
    "find.noResults": "Nəticə yoxdur",
    "commit.title": "Dəyişiklikləri Təhvil verin",
    "commit.close": "Bağlayın",
    "commit.loading": "İşləyən ağac oxunur...",
    "commit.noChanges": "Ediləcək heç bir yerli dəyişiklik yoxdur.",
    "commit.selectFile": "Fərqini görmək üçün faylı seçin.",
    "commit.selectAll": "Hamısını seçin",
    "commit.selectNone": "Hamısını silin",
    "commit.message": "Mesajı qəbul edin",
    "commit.messagePlaceholder": "Dəyişikliyi təsvir edin",
    "commit.selectedCount": "{count} fayl(ları) seçildi",
    "commit.review": "Baxış",
    "commit.reviewTitle": "Qəbul etməzdən əvvəl son yoxlama",
    "commit.reviewSummary": "{count} fayl(lar)ını mesajla göndərin: {message}",
    "commit.back": "Geri",
    "commit.commit": "Yerli öhdəlik yaradın",
    "menu.stashApply": "Tətbiq edin",
    "menu.stashPop": "Pop saklama (tətbiq et və çıxar)",
    "menu.stashDrop": "Götürün...",
    "merge.title": "Filial birləşdirin",
    "merge.route": "Mövcud filiala birləşmə",
    "merge.source": "Mənbədən (mənbə)",
    "merge.target": "daxil (cari)",
    "merge.loading": "Birləşmə təhlil edilir...",
    "merge.upToDate": "Artıq yenilənir - birləşdiriləcək heç nə yoxdur.",
    "merge.fastForward": "Sürətli irəli - birləşmə öhdəliyinə ehtiyac yoxdur (birləşdirmə öhdəliyi yaratmaq üçün \"Birləşmə öhdəliyi yarat\"ı işarələyin).",
    "merge.summary": "{files} fayl(ları) dəyişdirildi, {conflicts} münaqişə(lər).",
    "merge.conflictsWarning": "Bu birləşmədə ziddiyyətlər var – siz hələ də birləşdirə, sonra onları redaktorda həll edə bilərsiniz.",
    "merge.noChanges": "Fayl dəyişikliyi yoxdur.",
    "merge.files": "Nəticə dəyişiklikləri",
    "merge.added": "Əlavə edilib",
    "merge.modified": "Dəyişdirilib",
    "merge.deleted": "Silindi",
    "merge.conflict": "Münaqişə",
    "merge.message": "Təhlükə mesajını birləşdirin",
    "merge.messagePlaceholder": "Filial 'mənbəsini' birləşdirin",
    "merge.messageFfHint": "Sürətli irəli birləşmədə nəzərə alınmır (heç bir öhdəlik yaradılmır).",
    "merge.noFastForward": "Birləşmə öhdəliyi yaradın (sürətli irəliləmə yoxdur)",
    "merge.noFastForwardHint": "Filial sürətlə irəliləyə bilsə belə, həmişə birləşmə öhdəliyini qeyd edin.",
    "merge.merge": "Birləşdirin",
    "merge.cancel": "Ləğv et",
    "merge.previewError": "Önizləməni hesablamaq mümkün olmadı: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash birləşməsi — hər dəyişiklik BİRİN “{target}” üzərində etdiyi kimi düşür; “{source}”in öz öhdəlikləri onun tarixindən kənarda qalır. Parametrlər › Birləşdirin.",
    "merge.messageSquash": "Mesajı qəbul edin",
    "merge.squashMessagePlaceholder": "'Mənbədən' əzilmiş dəyişikliklər",
    "newBranch.title": "Filial yaradın",
    "newBranch.startPoint": "{sha}-dən başlayan yeni filial",
    "newBranch.startPointOn": "{refs}-də",
    "newBranch.location": "Məkan",
    "newBranch.locationRoot": "(kök)",
    "newBranch.expandAll": "Hamısını genişləndirin",
    "newBranch.collapseAll": "Hamısını yığışdırın",
    "newBranch.expandFolder": "Qovluğu genişləndirin",
    "newBranch.collapseFolder": "Qovluğu yığcamlaşdırın",
    "newBranch.name": "Filial adı",
    "newBranch.namePlaceholder": "mənim filialım",
    "newBranch.fullName": "Tam adı: {name}",
    "newBranch.invalid": "Yanlış filial adı",
    "newBranch.exists": "Bu adda filial artıq mövcuddur",
    "newBranch.checkout": "Yaradıldıqdan sonra filialı yoxlayın",
    "newBranch.create": "Yaradın",
    "newBranch.cancel": "Ləğv et",
    "status.loading": "Qrafik yüklənir...",
    "status.summary": "{repo}{commits} öhdəliyini, {refs} referanslarını göstərir",
    "status.branchCreated": "{sha}-də \"{name}\" filialı yaradıldı",
    "status.error": "Xəta: {message}",
    "status.opFailed": "Əməliyyat uğursuz oldu",
    "status.fetching": "Alınır...",
    "status.pulling": "Çəkilir...",
    "status.pushing": "İtilir...",
    "status.committing": "Yerli öhdəlik yaradılır...",
    "status.commitCreated": "{sha}: {message} yerli öhdəliyi yaradıldı",
    "status.syncing": "Sinxronizasiya edilir...",
    "status.noHead": "Qrafikdə cari yoxlama tapılmadı.",
    "status.undoing": "Öhdəlik ləğv edilir...",
    "status.commitUndone": "Təhlükə geri qaytarıldı - dəyişikliklər işləyən ağaca qayıdır.",
    "status.undoConflict": "Zərərli münaqişələri ləğv edin - onları redaktorda həll edin, sonra davam edin.",
    "status.stashApplying": "Saxlama tətbiq edilir...",
    "status.stashPopping": "Yıxılır...",
    "status.stashDropping": "Zibil atılır...",
    "status.stashApplied": "Stash tətbiq edildi.",
    "status.stashPopped": "Stash çıxdı.",
    "status.stashDropped": "Stash düşdü.",
    "status.stashConflict": "Münaqişələri saklayın - onları redaktorda həll edin.",
    "status.merging": "Birləşir...",
    "status.merged": "Birləşmə tamamlandı.",
    "status.mergeConflict": "Birləşmədə konfliktlər var – onları redaktorda həll edin, sonra qəbul edin.",
    "search.title": "Axtar",
    "search.tooltip": "Axtarış öhdəliyi",
    "search.placeholder": "Axtarış öhdəliyi...",
    "search.previous": "Əvvəlki nəticə",
    "search.next": "Növbəti nəticə",
    "search.noResults": "Nəticə yoxdur",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtr rejimi",
    "search.highlight": "Vurğulama rejimi",
    "search.filterMode": "Axtarış ekranı",
    "search.filterModeFilter": "Filtr (uyğun olmayanları gizlət)",
    "search.filterModeHighlight": "Vurğulayın (hamısını göstərin, uyğunluqları qeyd edin)",
    "search.filterModeHint": "Axtarış nəticələrinin qrafikdə necə göstərildiyini seçin.",
    "search.visibility": "Məkanı axtarın",
    "search.visibilityToolbar": "Alətlər paneli düyməsi",
    "search.visibilityAlwaysVisible": "Həmişə görünən bar",
    "search.visibilityHint": "Axtarışı alətlər paneli ikonası və ya yuxarıda davamlı panel kimi göstərin.",
    "footer.github": "GitHub",
  },
  id: {
    "toolbar.refresh": "⟳ Segarkan",
    "toolbar.fetch": "⤓ Ambil",
    "toolbar.pull": "⇩ Tarik",
    "toolbar.push": "⇧ Dorong",
    "toolbar.commit": "✓ Berkomitmen",
    "toolbar.sync": "⇅ Sinkronisasi",
    "toolbar.jumpHead": "⌖ Pergi ke kasir",
    "toolbar.reset": "⤢ Atur ulang tampilan",
    "toolbar.settings": "⚙ Pengaturan",
    "details.header": "Detail Komit",
    "details.close": "Menutup",
    "details.sha": "SHA",
    "details.shortSha": "SHA pendek",
    "details.message": "Pesan",
    "details.author": "Pengarang",
    "details.date": "Tanggal",
    "details.labels": "Label",
    "details.location": "Lokasi",
    "details.currentHead": "HEAD — pembayaran saat ini",
    "settings.title": "Pengaturan",
    "settings.language": "Bahasa",
    "settings.theme": "Tema",
    "settings.themeLight": "Lampu",
    "settings.themeDark": "Gelap",
    "settings.themeLightHint": "Latar belakang terang, teks gelap.",
    "settings.themeDarkHint": "Latar belakang gelap, teks terang.",
    "settings.mainBranch": "Cabang utama",
    "settings.mainBranchAuto": "Otomatis",
    "settings.mainBranchSearch": "Cari cabang…",
    "settings.mainBranchNoMatch": "Tidak ada cabang yang cocok",
    "settings.display": "Gaya tampilan",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Klasik",
    "settings.displayModernHint": "Kanvas gratis — seret untuk menggeser, gulir untuk memperbesar.",
    "settings.displayClassicHint": "Memperbaiki kanvas — batang disematkan ke kiri, tanpa zoom, hanya gulir (seperti grafik revisi SVN).",
    "settings.sectionGeneral": "Umum",
    "settings.sectionGraph": "Grafik",
    "settings.sectionChanges": "Mengubah tampilan",
    "settings.diffMinimap": "Peta mini yang berbeda",
    "settings.diffMinimapOn": "Ditampilkan",
    "settings.diffMinimapOff": "Tersembunyi",
    "settings.diffMinimapOnHint": "Tampilkan strip ikhtisar bergaya VS Code di samping perbedaan — seret untuk menggulir; penanda perubahan disertakan.",
    "settings.diffMinimapOffHint": "Tidak ada strip ikhtisar — ​​gulirkan perbedaan secara normal.",
    "settings.commitReview": "Lakukan peninjauan",
    "settings.commitReviewOn": "Tanyakan sebelum melakukan",
    "settings.commitReviewOff": "Berkomitmen secara langsung",
    "settings.commitReviewOnHint": "Tampilkan daftar file akhir dan pemeriksaan pesan sebelum membuat komit lokal.",
    "settings.commitReviewOffHint": "Tombol Komit segera membuat komit lokal.",
    "settings.svnBranchDialog": "Dialog cabang bergaya SVN",
    "settings.svnBranchDialogHint": "Tampilkan pemilih pohon folder saat membuat cabang (bukan perintah sederhana).",
    "settings.branchDialog": "Dialog cabang",
    "settings.branchDialogSvnTitle": "gaya SVN",
    "settings.branchDialogNativeVscode": "Gaya VS Kode",
    "settings.branchDialogNativeVs": "Gaya Visual Studio",
    "settings.branchDialogNativeHint": "Prompt cabang bawaan IDE Anda.",
    "settings.close": "Menutup",
    "settings.done": "Selesai",
    "settings.sectionAdvanced": "Canggih",
    "settings.gitSource": "Git dapat dieksekusi",
    "settings.gitSourceBuiltin": "Bawaan",
    "settings.gitSourceBuiltinHint": "Gunakan biner git dari ekstensi Git bawaan IDE.",
    "settings.gitSourceCustom": "Jalur khusus",
    "settings.gitPath": "Jalan menuju git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Jelajahi…",
    "settings.jargon": "istilah Git",
    "settings.jargonTranslate": "Menerjemahkan",
    "settings.jargonEnglish": "Tetap dalam bahasa Inggris",
    "settings.jargonTranslateHint": "Terjemahkan istilah Git (tarik, dorong, komit, cabang…) ke dalam bahasa antarmuka.",
    "settings.jargonEnglishHint": "Pertahankan istilah Git (tarik, dorong, komit, cabang…) dalam bahasa Inggris; terjemahkan yang lainnya.",
    "settings.sectionMerge": "Menggabungkan",
    "settings.mergeMode": "Gabungkan gaya",
    "settings.mergeModeMerge": "Penggabungan biasa",
    "settings.mergeModeSquash": "Penggabungan labu",
    "settings.mergeModeMergeHint": "Menggabungkan cabang sebagaimana adanya: komitmennya menjadi bagian dari sejarah cabang saat ini, digabungkan dengan komitmen gabungan (atau dipercepat jika memungkinkan).",
    "settings.mergeModeSquashHint": "Meruntuhkan seluruh cabang menjadi satu komit pada cabang saat ini (git merge --squash). Komit yang dimiliki cabang tidak pernah memasuki riwayat cabang saat ini — Anda hanya melihat satu komit di sana. Cabang itu sendiri tidak disentuh, sehingga komitmennya tetap terlihat di jalurnya sendiri.",
    "settings.mergedView": "Gabungan komitmen dalam grafik",
    "settings.mergedViewBranch": "Hanya di cabang mereka sendiri",
    "settings.mergedViewTarget": "Juga di cabang tempat mereka digabungkan",
    "settings.mergedViewBranchHint": "Komit cabang yang digabungkan hanya diambil di jalurnya sendiri; penggabungan muncul sebagai konektor. Tampilan klasik.",
    "settings.mergedViewTargetHint": "Komit yang dibawa oleh gabungan juga diambil di jalur cabang penerima, ditumpuk di bawah komit gabungan dan sejajar dengan yang asli — karena cabang tersebut benar-benar memuatnya sekarang. Salinannya berwarna pucat dan diberi judul sesuai dengan cabang tempat tulisan itu ditulis, jadi jelas bahwa salinan tersebut tidak dibuat di sana. Penggabungan squash tidak terpengaruh: squash menulis satu komit biasa, jadi hanya komit itu yang muncul.",
    "legend.title": "Legenda",
    "legend.head": "HEAD / cabang saat ini",
    "legend.local": "Cabang lokal",
    "legend.remote": "Cabang terpencil",
    "legend.remoteOnly": "Hanya di cloud (tidak ditarik)",
    "legend.tag": "Tandai (versi)",
    "legend.commit": "Melakukan",
    "legend.stash": "Simpanan (pekerjaan yang disimpan)",
    "legend.mergedIn": "Masuk dengan gabungan (ditulis di cabang lain)",
    "legend.nodes": "Kotak",
    "legend.lines": "Garis",
    "legend.edgeParent": "Komit sebelumnya pada baris yang sama",
    "legend.edgeMerge": "Cabang yang digabungkan — panah menunjuk ke komit penggabungan",
    "legend.edgeBranch": "Sebuah cabang bercabang di sini",
    "legend.edgeStash": "Stash dan komitnya disimpan",
    "legend.edgeMergedChain": "Jalur cabang ini berjalan melalui komit yang digabungkan",
    "legend.edgeMergedTie": "Komit yang sama pada cabang tempat ditulisnya",
    "menu.jumpHead": "⌖ Pergi ke kasir",
    "menu.resetView": "⤢ Atur ulang tampilan",
    "menu.createBranch": "Buat cabang dari sini…",
    "menu.checkout": "Lihat komit ini",
    "menu.copySha": "Salin komit SHA",
    "menu.pushBranch": "Cabang dorong \"{name}\"",
    "menu.renameCommit": "Ganti nama pesan komit…",
    "menu.undoCommit": "Batalkan komit (simpan perubahan)…",
    "menu.viewChanges": "Lihat perubahan…",
    "menu.jumpToOriginal": "Pergi ke komit asli",
    "changes.title": "Perubahan {sha}",
    "changes.tabChanged": "Berubah",
    "changes.tabAll": "Semua File",
    "changes.added": "Ditambahkan",
    "changes.modified": "Dimodifikasi",
    "changes.deleted": "Dihapus",
    "changes.renamed": "Berganti nama",
    "changes.noChanges": "Komit ini tidak mengubah file apa pun.",
    "changes.loading": "Memuat perbedaan…",
    "changes.selectFile": "Pilih file untuk melihat perubahannya.",
    "changes.original": "Asli",
    "changes.changed": "Komitmen ini",
    "changes.binary": "File biner — tidak ada perbedaan teks untuk ditampilkan.",
    "changes.tooLarge": "File terlalu besar untuk dibedah.",
    "changes.renamedFrom": "Berganti nama dari {path}",
    "changes.close": "Menutup",
    "changes.prevChange": "Perubahan sebelumnya",
    "changes.nextChange": "Perubahan berikutnya",
    "changes.collapseAll": "Tutup semua folder",
    "changes.expandAll": "Perluas semua folder",
    "changes.searchPlaceholder": "Cari file…",
    "changes.noSearchResults": "Tidak ada file yang cocok dengan pencarian Anda.",
    "changes.maximize": "Maksimalkan",
    "changes.restore": "Kembalikan ukuran",
    "find.open": "Cari di perbedaan ini (Ctrl+F)",
    "find.placeholder": "Temukan di perbedaan…",
    "find.prev": "Pertandingan sebelumnya",
    "find.next": "Pertandingan berikutnya",
    "find.close": "Tutup pencarian",
    "find.noResults": "Tidak ada hasil",
    "commit.title": "Lakukan Perubahan",
    "commit.close": "Menutup",
    "commit.loading": "Membaca pohon yang berfungsi…",
    "commit.noChanges": "Tidak ada perubahan lokal yang perlu dilakukan.",
    "commit.selectFile": "Pilih file untuk melihat perbedaannya.",
    "commit.selectAll": "Pilih semua",
    "commit.selectNone": "Hapus semuanya",
    "commit.message": "Pesan komit",
    "commit.messagePlaceholder": "Jelaskan perubahannya",
    "commit.selectedCount": "File {count} dipilih",
    "commit.review": "Tinjauan",
    "commit.reviewTitle": "Pemeriksaan terakhir sebelum dilakukan",
    "commit.reviewSummary": "Komit file {count} dengan pesan: {message}",
    "commit.back": "Kembali",
    "commit.commit": "Buat komit lokal",
    "menu.stashApply": "Terapkan simpanan",
    "menu.stashPop": "Pop simpanan (terapkan & hapus)",
    "menu.stashDrop": "Jatuhkan simpanan…",
    "merge.title": "Gabungkan Cabang",
    "merge.route": "Menggabungkan ke cabang saat ini",
    "merge.source": "Dari (sumber)",
    "merge.target": "Ke (saat ini)",
    "merge.loading": "Menganalisis penggabungan…",
    "merge.upToDate": "Sudah terkini — tidak ada yang perlu digabungkan.",
    "merge.fastForward": "Percepat — tidak diperlukan komit penggabungan (centang “Buat komit gabungan” untuk memaksanya).",
    "merge.summary": "File {files} diubah, {conflicts} konflik.",
    "merge.conflictsWarning": "Penggabungan ini memiliki konflik — Anda masih dapat menggabungkan, lalu menyelesaikannya di editor.",
    "merge.noChanges": "Tidak ada perubahan file.",
    "merge.files": "Perubahan yang dihasilkan",
    "merge.added": "Ditambahkan",
    "merge.modified": "Dimodifikasi",
    "merge.deleted": "Dihapus",
    "merge.conflict": "Konflik",
    "merge.message": "Gabungkan pesan komit",
    "merge.messagePlaceholder": "Gabungkan 'sumber' cabang",
    "merge.messageFfHint": "Diabaikan pada penggabungan maju cepat (tidak ada komit yang dibuat).",
    "merge.noFastForward": "Buat komit gabungan (tidak ada fast-forward)",
    "merge.noFastForwardHint": "Selalu catat komit penggabungan, bahkan ketika cabang dapat melakukan fast-forward.",
    "merge.merge": "Menggabungkan",
    "merge.cancel": "Membatalkan",
    "merge.previewError": "Tidak dapat menghitung pratinjau: {message}",
    "merge.squashBadge": "Labu",
    "merge.squashNote": "Penggabungan squash — setiap perubahan terjadi sebagai SATU komitmen pada “{target}”; Komitmen “{source}” sendiri tidak dimasukkan dalam sejarahnya. Beralih kembali di Pengaturan ›Gabungkan.",
    "merge.messageSquash": "Pesan komit",
    "merge.squashMessagePlaceholder": "Perubahan tergencet dari 'sumber'",
    "newBranch.title": "Buat Cabang",
    "newBranch.startPoint": "Cabang baru dimulai dari {sha}",
    "newBranch.startPointOn": "pada {refs}",
    "newBranch.location": "Lokasi",
    "newBranch.locationRoot": "(akar)",
    "newBranch.expandAll": "Perluas semuanya",
    "newBranch.collapseAll": "Ciutkan semuanya",
    "newBranch.expandFolder": "Perluas folder",
    "newBranch.collapseFolder": "Ciutkan folder",
    "newBranch.name": "Nama cabang",
    "newBranch.namePlaceholder": "cabang saya",
    "newBranch.fullName": "Nama lengkap: {name}",
    "newBranch.invalid": "Nama cabang tidak valid",
    "newBranch.exists": "Cabang dengan nama ini sudah ada",
    "newBranch.checkout": "Cabang checkout setelah pembuatan",
    "newBranch.create": "Membuat",
    "newBranch.cancel": "Membatalkan",
    "status.loading": "Memuat grafik…",
    "status.summary": "{repo}Menampilkan komitmen {commits}, referensi {refs}",
    "status.branchCreated": "Membuat cabang \"{name}\" di {sha}",
    "status.error": "Kesalahan: {message}",
    "status.opFailed": "Operasi gagal",
    "status.fetching": "Mengambil…",
    "status.pulling": "Menarik…",
    "status.pushing": "Mendorong…",
    "status.committing": "Membuat komitmen lokal…",
    "status.commitCreated": "Membuat komit lokal {sha}: {message}",
    "status.syncing": "Menyinkronkan…",
    "status.noHead": "Tidak ada pembayaran saat ini yang ditemukan dalam grafik.",
    "status.undoing": "Membatalkan komitmen…",
    "status.commitUndone": "Komit dibatalkan — perubahan kembali ke pohon kerja.",
    "status.undoConflict": "Batalkan konflik yang terjadi — selesaikan konflik tersebut di editor, lalu lanjutkan.",
    "status.stashApplying": "Menerapkan simpanan…",
    "status.stashPopping": "Muncul simpanan…",
    "status.stashDropping": "Menjatuhkan simpanan…",
    "status.stashApplied": "Simpanan diterapkan.",
    "status.stashPopped": "Simpanan muncul.",
    "status.stashDropped": "Simpanan terjatuh.",
    "status.stashConflict": "Konflik simpanan — selesaikan di editor.",
    "status.merging": "Menggabungkan…",
    "status.merged": "Penggabungan selesai.",
    "status.mergeConflict": "Penggabungan memiliki konflik — selesaikan konflik tersebut di editor, lalu lakukan.",
    "search.title": "Mencari",
    "search.tooltip": "Pencarian dilakukan",
    "search.placeholder": "Komitmen pencarian…",
    "search.previous": "Hasil sebelumnya",
    "search.next": "Hasil selanjutnya",
    "search.noResults": "Tidak ada hasil",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modus penyaring",
    "search.highlight": "Modus sorotan",
    "search.filterMode": "Tampilan pencarian",
    "search.filterModeFilter": "Filter (sembunyikan yang tidak cocok)",
    "search.filterModeHighlight": "Sorot (tunjukkan semua, tandai kecocokan)",
    "search.filterModeHint": "Pilih bagaimana hasil pencarian ditampilkan dalam grafik.",
    "search.visibility": "Lokasi pencarian",
    "search.visibilityToolbar": "Tombol bilah alat",
    "search.visibilityAlwaysVisible": "Bilah yang selalu terlihat",
    "search.visibilityHint": "Tampilkan penelusuran sebagai ikon bilah alat atau bilah persisten di bagian atas.",
    "footer.github": "GitHub",
  },
  ms: {
    "toolbar.refresh": "⟳ Segarkan",
    "toolbar.fetch": "⤓ Ambil",
    "toolbar.pull": "⇩ Tarik",
    "toolbar.push": "⇧ Tolak",
    "toolbar.commit": "✓ Komited",
    "toolbar.sync": "⇅ Penyegerakan",
    "toolbar.jumpHead": "⌖ Pergi ke daftar keluar",
    "toolbar.reset": "⤢ Tetapkan semula paparan",
    "toolbar.settings": "⚙ Tetapan",
    "details.header": "Butiran Komit",
    "details.close": "tutup",
    "details.sha": "SHA",
    "details.shortSha": "SHA pendek",
    "details.message": "Mesej",
    "details.author": "Pengarang",
    "details.date": "tarikh",
    "details.labels": "Label",
    "details.location": "Lokasi",
    "details.currentHead": "HEAD — pembayaran semasa",
    "settings.title": "tetapan",
    "settings.language": "Bahasa",
    "settings.theme": "Tema",
    "settings.themeLight": "Cahaya",
    "settings.themeDark": "Gelap",
    "settings.themeLightHint": "Latar belakang terang, teks gelap.",
    "settings.themeDarkHint": "Latar belakang gelap, teks terang.",
    "settings.mainBranch": "Cawangan utama",
    "settings.mainBranchAuto": "Automatik",
    "settings.mainBranchSearch": "Cari cawangan…",
    "settings.mainBranchNoMatch": "Tiada cawangan yang sepadan",
    "settings.display": "Gaya paparan",
    "settings.displayModern": "moden",
    "settings.displayClassic": "Klasik",
    "settings.displayModernHint": "Kanvas percuma — seret untuk menyorot, tatal untuk zum.",
    "settings.displayClassicHint": "Kanvas tetap — batang disemat ke kiri, tiada zum, tatal sahaja (seperti graf semakan SVN).",
    "settings.sectionGeneral": "Umum",
    "settings.sectionGraph": "Graf",
    "settings.sectionChanges": "Perubahan pandangan",
    "settings.diffMinimap": "Peta minima perbezaan",
    "settings.diffMinimapOn": "Ditunjukkan",
    "settings.diffMinimapOff": "Tersembunyi",
    "settings.diffMinimapOnHint": "Tunjukkan jalur gambaran keseluruhan gaya Kod VS di sebelah perbezaan — seretnya untuk menatal; penanda perubahan disertakan.",
    "settings.diffMinimapOffHint": "Tiada jalur gambaran keseluruhan — tatal perbezaan seperti biasa.",
    "settings.commitReview": "Komit semakan",
    "settings.commitReviewOn": "Tanya sebelum commit",
    "settings.commitReviewOff": "Komit secara langsung",
    "settings.commitReviewOnHint": "Tunjukkan senarai fail akhir dan semakan mesej sebelum membuat komit setempat.",
    "settings.commitReviewOffHint": "Butang Komit mencipta komit setempat serta-merta.",
    "settings.svnBranchDialog": "Dialog cawangan gaya SVN",
    "settings.svnBranchDialogHint": "Tunjukkan pemilih pepohon folder semasa membuat cawangan (bukannya gesaan mudah).",
    "settings.branchDialog": "Dialog cawangan",
    "settings.branchDialogSvnTitle": "gaya SVN",
    "settings.branchDialogNativeVscode": "Gaya Kod VS",
    "settings.branchDialogNativeVs": "Gaya Visual Studio",
    "settings.branchDialogNativeHint": "Gesaan cawangan terbina dalam IDE anda.",
    "settings.close": "tutup",
    "settings.done": "Selesai",
    "settings.sectionAdvanced": "Maju",
    "settings.gitSource": "Git boleh laku",
    "settings.gitSourceBuiltin": "terbina dalam",
    "settings.gitSourceBuiltinHint": "Gunakan binari git daripada sambungan Git terbina dalam IDE.",
    "settings.gitSourceCustom": "Laluan tersuai",
    "settings.gitPath": "Laluan ke git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Semak imbas…",
    "settings.jargon": "Istilah Git",
    "settings.jargonTranslate": "Terjemah",
    "settings.jargonEnglish": "Simpan dalam bahasa Inggeris",
    "settings.jargonTranslateHint": "Terjemah istilah Git (tarik, tolak, komit, cabang…) ke dalam bahasa antara muka.",
    "settings.jargonEnglishHint": "Kekalkan istilah Git (tarik, tolak, komit, cabang…) dalam bahasa Inggeris; terjemahkan semua yang lain.",
    "settings.sectionMerge": "Bercantum",
    "settings.mergeMode": "Gaya gabungan",
    "settings.mergeModeMerge": "Gabungan biasa",
    "settings.mergeModeSquash": "Skuasy bergabung",
    "settings.mergeModeMergeHint": "Menggabungkan cawangan seperti sedia ada: komitnya menjadi sebahagian daripada sejarah cawangan semasa, disertai oleh komit gabungan (atau dimajukan pantas apabila boleh).",
    "settings.mergeModeSquashHint": "Runtuhkan keseluruhan cawangan menjadi satu komit pada cawangan semasa (git merge --squash). Komit cawangan sendiri tidak pernah memasuki sejarah cawangan semasa — anda hanya melihat satu komitmen di sana. Cawangan itu sendiri tidak disentuh, jadi komitnya kekal kelihatan di lorongnya sendiri.",
    "settings.mergedView": "Komit digabungkan dalam graf",
    "settings.mergedViewBranch": "Hanya di cawangan mereka sendiri",
    "settings.mergedViewTarget": "Juga di cawangan mereka digabungkan",
    "settings.mergedViewBranchHint": "Komit cawangan yang digabungkan dilukis hanya di lorong mereka sendiri; gabungan muncul sebagai penyambung. Pandangan klasik.",
    "settings.mergedViewTargetHint": "Komit gabungan yang dibawa masuk dilukis di lorong cawangan penerima juga, disusun di bawah komit gabungan dan setara dengan yang asal — kerana cawangan itu benar-benar mengandunginya sekarang. Naskhah-naskhah itu pucat dan berkepala dengan dahan yang ditulis di atasnya, jadi jelas ia tidak dibuat di sana. Gabungan skuasy tidak terjejas: skuasy menulis satu komit biasa, jadi hanya komit itu yang pernah muncul.",
    "legend.title": "Lagenda",
    "legend.head": "KETUA / cawangan semasa",
    "legend.local": "Cawangan tempatan",
    "legend.remote": "Cawangan terpencil",
    "legend.remoteOnly": "Hanya di awan (tidak ditarik)",
    "legend.tag": "Tag (versi)",
    "legend.commit": "komited",
    "legend.stash": "Simpanan (kerja yang disimpan)",
    "legend.mergedIn": "Datang dengan gabungan (ditulis pada cawangan lain)",
    "legend.nodes": "kotak",
    "legend.lines": "Garisan",
    "legend.edgeParent": "Komit sebelumnya pada baris yang sama",
    "legend.edgeMerge": "Cawangan gabungan - anak panah menunjuk pada komit gabungan",
    "legend.edgeBranch": "Cabang bercabang di sini",
    "legend.edgeStash": "Simpanan dan komitmen yang telah ditangguhkan",
    "legend.edgeMergedChain": "Barisan cawangan ini berjalan melalui penggabungan masuk",
    "legend.edgeMergedTie": "Komit yang sama pada cawangan yang ditulis padanya",
    "menu.jumpHead": "⌖ Pergi ke daftar keluar",
    "menu.resetView": "⤢ Tetapkan semula paparan",
    "menu.createBranch": "Buat cawangan dari sini…",
    "menu.checkout": "Semak komitmen ini",
    "menu.copySha": "Salin commit SHA",
    "menu.pushBranch": "Cawangan tolak \"{name}\"",
    "menu.renameCommit": "Namakan semula mesej komit…",
    "menu.undoCommit": "Buat asal komitmen (simpan perubahan)…",
    "menu.viewChanges": "Lihat perubahan…",
    "menu.jumpToOriginal": "Pergi ke komitmen asal",
    "changes.title": "Perubahan dalam {sha}",
    "changes.tabChanged": "Berubah",
    "changes.tabAll": "Semua Fail",
    "changes.added": "Ditambah",
    "changes.modified": "Diubah suai",
    "changes.deleted": "Dipadamkan",
    "changes.renamed": "Dinamakan semula",
    "changes.noChanges": "Komit ini tidak mengubah fail.",
    "changes.loading": "Memuatkan perbezaan…",
    "changes.selectFile": "Pilih fail untuk melihat perubahannya.",
    "changes.original": "Asal",
    "changes.changed": "Komitmen ini",
    "changes.binary": "Fail binari — tiada perbezaan teks untuk ditunjukkan.",
    "changes.tooLarge": "Fail terlalu besar untuk dibezakan.",
    "changes.renamedFrom": "Dinamakan semula daripada {path}",
    "changes.close": "tutup",
    "changes.prevChange": "Perubahan sebelumnya",
    "changes.nextChange": "Perubahan seterusnya",
    "changes.collapseAll": "Runtuhkan semua folder",
    "changes.expandAll": "Kembangkan semua folder",
    "changes.searchPlaceholder": "Cari fail…",
    "changes.noSearchResults": "Tiada fail yang sepadan dengan carian anda.",
    "changes.maximize": "Maksimumkan",
    "changes.restore": "Pulihkan saiz",
    "find.open": "Cari dalam perbezaan ini (Ctrl+F)",
    "find.placeholder": "Cari dalam perbezaan…",
    "find.prev": "Perlawanan sebelumnya",
    "find.next": "Perlawanan seterusnya",
    "find.close": "Tutup carian",
    "find.noResults": "Tiada hasil",
    "commit.title": "Lakukan Perubahan",
    "commit.close": "tutup",
    "commit.loading": "Membaca pokok kerja…",
    "commit.noChanges": "Tiada perubahan setempat untuk dilakukan.",
    "commit.selectFile": "Pilih fail untuk melihat perbezaannya.",
    "commit.selectAll": "Pilih semua",
    "commit.selectNone": "Kosongkan semua",
    "commit.message": "Komit mesej",
    "commit.messagePlaceholder": "Huraikan perubahan tersebut",
    "commit.selectedCount": "Fail {count} dipilih",
    "commit.review": "Semakan",
    "commit.reviewTitle": "Semakan akhir sebelum melakukan",
    "commit.reviewSummary": "Komit fail {count} dengan mesej: {message}",
    "commit.back": "belakang",
    "commit.commit": "Buat komitmen setempat",
    "menu.stashApply": "Sapukan simpanan",
    "menu.stashPop": "Simpanan pop (guna & keluarkan)",
    "menu.stashDrop": "Lepaskan simpanan…",
    "merge.title": "Gabungkan Cawangan",
    "merge.route": "Menggabungkan ke dalam cawangan semasa",
    "merge.source": "Dari (sumber)",
    "merge.target": "ke (semasa)",
    "merge.loading": "Menganalisis gabungan…",
    "merge.upToDate": "Sudah terkini — tiada apa yang perlu digabungkan.",
    "merge.fastForward": "Maju pantas — tiada komitmen gabungan diperlukan (tandakan “Buat komitmen gabungan” untuk memaksa satu).",
    "merge.summary": "Fail {files} ditukar, konflik {conflicts}.",
    "merge.conflictsWarning": "Gabungan ini mempunyai konflik — anda masih boleh bergabung, kemudian menyelesaikannya dalam editor.",
    "merge.noChanges": "Tiada perubahan fail.",
    "merge.files": "Perubahan yang terhasil",
    "merge.added": "Ditambah",
    "merge.modified": "Diubah suai",
    "merge.deleted": "Dipadamkan",
    "merge.conflict": "Konflik",
    "merge.message": "Gabungkan mesej komit",
    "merge.messagePlaceholder": "Gabungkan 'sumber' cawangan",
    "merge.messageFfHint": "Diabaikan pada gabungan ke hadapan pantas (tiada komit dibuat).",
    "merge.noFastForward": "Buat komitmen gabungan (tiada ke hadapan pantas)",
    "merge.noFastForwardHint": "Sentiasa rekodkan komit gabungan, walaupun apabila cawangan boleh maju pantas.",
    "merge.merge": "Bercantum",
    "merge.cancel": "Batal",
    "merge.previewError": "Tidak dapat mengira pratonton: {message}",
    "merge.squashBadge": "Skuasy",
    "merge.squashNote": "Gabungan skuasy — setiap perubahan berlaku apabila ONE berkomitmen pada “{target}”; Komitmen “{source}” sendiri menjauhkan diri daripada sejarahnya. Tukar semula dalam Tetapan › Gabung.",
    "merge.messageSquash": "Komit mesej",
    "merge.squashMessagePlaceholder": "Perubahan tergencet daripada 'sumber'",
    "newBranch.title": "Buat Cawangan",
    "newBranch.startPoint": "Cawangan baru bermula dari {sha}",
    "newBranch.startPointOn": "pada {refs}",
    "newBranch.location": "Lokasi",
    "newBranch.locationRoot": "(akar)",
    "newBranch.expandAll": "Kembangkan semua",
    "newBranch.collapseAll": "Runtuhkan semua",
    "newBranch.expandFolder": "Kembangkan folder",
    "newBranch.collapseFolder": "Runtuhkan folder",
    "newBranch.name": "Nama cawangan",
    "newBranch.namePlaceholder": "cawangan saya",
    "newBranch.fullName": "Nama penuh: {name}",
    "newBranch.invalid": "Nama cawangan tidak sah",
    "newBranch.exists": "Cawangan dengan nama ini sudah wujud",
    "newBranch.checkout": "Cawangan daftar keluar selepas dibuat",
    "newBranch.create": "Buat",
    "newBranch.cancel": "Batal",
    "status.loading": "Memuatkan graf…",
    "status.summary": "{repo}Menunjukkan komit {commits}, rujukan {refs}",
    "status.branchCreated": "Mencipta cawangan \"{name}\" di {sha}",
    "status.error": "Ralat: {message}",
    "status.opFailed": "Operasi gagal",
    "status.fetching": "Mengambil…",
    "status.pulling": "Menarik…",
    "status.pushing": "Menolak…",
    "status.committing": "Membuat komitmen setempat…",
    "status.commitCreated": "Komit tempatan {sha} dibuat: {message}",
    "status.syncing": "Menyegerakkan…",
    "status.noHead": "Tiada pembayaran semasa ditemui dalam graf.",
    "status.undoing": "Membatalkan komitmen…",
    "status.commitUndone": "Komit batal — perubahan kembali dalam pepohon berfungsi.",
    "status.undoConflict": "Buat asal konflik hit — selesaikannya dalam editor, kemudian teruskan.",
    "status.stashApplying": "Memohon simpanan…",
    "status.stashPopping": "Memunculkan simpanan…",
    "status.stashDropping": "Menggugurkan simpanan…",
    "status.stashApplied": "Simpanan digunakan.",
    "status.stashPopped": "Simpanan muncul.",
    "status.stashDropped": "Simpanan jatuh.",
    "status.stashConflict": "Simpan konflik — selesaikan dalam editor.",
    "status.merging": "Menggabungkan…",
    "status.merged": "Penggabungan selesai.",
    "status.mergeConflict": "Gabung mempunyai konflik — selesaikannya dalam editor, kemudian lakukan.",
    "search.title": "Cari",
    "search.tooltip": "Carian komited",
    "search.placeholder": "Carian komited…",
    "search.previous": "Keputusan sebelumnya",
    "search.next": "Keputusan seterusnya",
    "search.noResults": "Tiada hasil",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Mod penapis",
    "search.highlight": "Mod sorotan",
    "search.filterMode": "Paparan carian",
    "search.filterModeFilter": "Tapis (sembunyikan bukan padanan)",
    "search.filterModeHighlight": "Serlahkan (tunjukkan semua, tandai padanan)",
    "search.filterModeHint": "Pilih cara hasil carian dipaparkan dalam graf.",
    "search.visibility": "Cari lokasi",
    "search.visibilityToolbar": "Butang bar alat",
    "search.visibilityAlwaysVisible": "Bar sentiasa kelihatan",
    "search.visibilityHint": "Tunjukkan carian sebagai ikon bar alat atau bar berterusan di bahagian atas.",
    "footer.github": "GitHub",
  },
  bm: {
    "toolbar.refresh": "⟳ Lakurayali",
    "toolbar.fetch": "⤓ Lase",
    "toolbar.pull": "⇩ Ka sama",
    "toolbar.push": "⇧ Push (Dɔgɔtɔrɔso).",
    "toolbar.commit": "✓ Ka da a kan",
    "toolbar.sync": "⇅ Sinsinnan",
    "toolbar.jumpHead": "⌖ Taga jatebɔyɔrɔ la",
    "toolbar.reset": "⤢ Segin ka filɛli kɛ",
    "toolbar.settings": "⚙ Labɛnw",
    "details.header": "Komite Details (Dɛsɛw).",
    "details.close": "Ka datugu",
    "details.sha": "SHA",
    "details.shortSha": "Kurukuru SHA",
    "details.message": "Bataki",
    "details.author": "Wálebaga",
    "details.date": "Don",
    "details.labels": "Labeliw (Labeliw).",
    "details.location": "Sigiyɔrɔ",
    "details.currentHead": "HEAD — sisan jatebɔ",
    "settings.title": "Settings (Labɛnw)",
    "settings.language": "Kan",
    "settings.theme": "Dakun",
    "settings.themeLight": "Yeelen",
    "settings.themeDark": "Dibi",
    "settings.themeLightHint": "Background yeelen, sɛbɛnni dibiman.",
    "settings.themeDarkHint": "Dibi kɔkanna, sɛbɛnni yeelenma.",
    "settings.mainBranch": "Tɔnbolofara kunba",
    "settings.mainBranchAuto": "Otomatiki la",
    "settings.mainBranchSearch": "Boloci ɲini...",
    "settings.mainBranchNoMatch": "Bolo minnu bɛ bɛn ɲɔgɔn ma, olu tɛ yen",
    "settings.display": "A jiracogo cogoya",
    "settings.displayModern": "Kura",
    "settings.displayClassic": "Klasiki",
    "settings.displayModernHint": "Free canvas — sama ka taa pan, ka taa ka zoom kɛ.",
    "settings.displayClassicHint": "Kanpaɲi jɔlen — jirisun pinɛlen kinin fɛ, zoom tɛ, scroll dɔrɔn (i n’a fɔ SVN seginnkanni jatebɔsɛbɛn).",
    "settings.sectionGeneral": "Zenerali",
    "settings.sectionGraph": "Jaa",
    "settings.sectionChanges": "Yɛlɛma donna filɛli la",
    "settings.diffMinimap": "Diff minimap (kɛrɛnkɛrɛnnenya la).",
    "settings.diffMinimapOn": "A jiralen don",
    "settings.diffMinimapOff": "Dogolen",
    "settings.diffMinimapOnHint": "VS Code-style overview strip dɔ jira diff kɛrɛfɛ — a sama ka taa scroll; fɛn caman Changement markers (Yɛlɛma taamasiyɛnw) bɛ o kɔnɔ.",
    "settings.diffMinimapOffHint": "No overview strip — scroll diff normalement.",
    "settings.commitReview": "Seginkanni kɛ",
    "settings.commitReviewOn": "Aw ye ɲininkali kɛ sani aw ka aw yɛrɛ bila",
    "settings.commitReviewOff": "Aw ye aw yɛrɛ bila ka taa a fɛ",
    "settings.commitReviewOnHint": "Filenw lisɛli laban ni cikanw sɛgɛsɛgɛli jira sani i ka sigida commit da.",
    "settings.commitReviewOffHint": "Butɔn Commit bɛ sigida commit dabɔ o yɔrɔnin bɛɛ.",
    "settings.svnBranchDialog": "SVN-cogo bolofara ka kumaɲɔgɔnya",
    "settings.svnBranchDialogHint": "Foli-jiri-minɛn dɔ jira ni i bɛ bolofara dɔ da (sanni ka ɲininkali nɔgɔman kɛ).",
    "settings.branchDialog": "Tɔnbolofara ka baro",
    "settings.branchDialogSvnTitle": "SVN-cogo la",
    "settings.branchDialogNativeVscode": "VS Kode cogoya",
    "settings.branchDialogNativeVs": "Visual Studio cogoya",
    "settings.branchDialogNativeHint": "Aw ka IDE ka bolofara ɲinini min bɛ don a kɔnɔ.",
    "settings.close": "Ka datugu",
    "settings.done": "A kɛra",
    "settings.sectionAdvanced": "Min bɛ ɲɛfɛ",
    "settings.gitSource": "Git executable (Git) min bɛ Se ka Kɛ",
    "settings.gitSourceBuiltin": "A bɛ jɔ a kɔnɔ",
    "settings.gitSourceBuiltinHint": "Baara kɛ ni git binary ye ka bɔ IDE ka Git sɛgɛsɛgɛli kɔnɔ min bɛ a kɔnɔ.",
    "settings.gitSourceCustom": "Laada sira",
    "settings.gitPath": "Sira min bɛ taa git",
    "settings.gitPathPlaceholder": "/usr/bin/git ye",
    "settings.gitPathBrowse": "Browse ka...",
    "settings.jargon": "Git daɲɛw",
    "settings.jargonTranslate": "Ka bayɛlɛma",
    "settings.jargonEnglish": "Aw ka to tubabukan na",
    "settings.jargonTranslateHint": "Git daɲɛw bamanankan na (pull, push, commit, branch...) ka don interface kan na.",
    "settings.jargonEnglishHint": "Git daɲɛw (pull, push, commit, branch...) mara tubabukan na; fɛn tɔw bɛɛ bamanankan na.",
    "settings.sectionMerge": "Furuɲɔgɔnma",
    "settings.mergeMode": "Furakɛcogo min bɛ fara ɲɔgɔn kan",
    "settings.mergeModeMerge": "Furu normal (ɲɛnabɔli) normal",
    "settings.mergeModeSquash": "Squash ka fara ɲɔgɔn kan",
    "settings.mergeModeMergeHint": "Bolofara bɛ fara ɲɔgɔn kan i n’a fɔ a bɛ cogo min na: a ka kominiw bɛ kɛ sisan bolofara tariku dɔ ye, ka fara ɲɔgɔn kan ni jɛkulu ye min bɛ fara ɲɔgɔn kan (walima ka teliya ka taa ɲɛ ni a bɛ se ka kɛ).",
    "settings.mergeModeSquashHint": "A bɛ bolofara bɛɛ Blà ka Kɛ commit kelen ye sisan bolofara kan (git merge --squash). Tɔnbolofara yɛrɛ ka kominiw tɛ don abada bolofara min bɛ sen na sisan — i bɛ komiti kelen dɔrɔn de ye yen. Bolo yɛrɛ bɛ To a ma maga, o la a ka kominiw bɛ To ka Ye a yɛrɛ ka sira kan.",
    "settings.mergedView": "Commits faralen ɲɔgɔn kan graphique kɔnɔ",
    "settings.mergedViewBranch": "U yɛrɛ ka bolofara dɔrɔn de kan",
    "settings.mergedViewTarget": "U farala ɲɔgɔn kan bolofara min fana na",
    "settings.mergedViewBranchHint": "Bolofara min faralen don ɲɔgɔn kan, o ka kominiw bɛ jate u yɛrɛ ka sira dɔrɔn de la; farali bɛ jira i n’a fɔ ɲɔgɔndan. A ka klasiki filɛli.",
    "settings.mergedViewTargetHint": "Commits a merge bɛ na ni minnu ye, olu bɛ sama bolofara minɛbaga ka lane kɔnɔ fana, u bɛ dalajɛ merge commit jukɔrɔ ani ka kɛ kelen ye ni originals ye — bawo o bolofara bɛ u kɔnɔ tiɲɛ na sisan. Kopiw ɲɛ ye bulama ye ani u kunkolo bɛ ni bolofara ye u sɛbɛnna min kan, o la sa, a jɛlen don k’u ma dilan yen. Squash faralenw tɛ nɔ bila: squash bɛ commit gansan kelen sɛbɛn, o la o commit dɔrɔn de bɛ bɔ abada.",
    "legend.title": "Lezandi",
    "legend.head": "KUNGO / sisan bolofara",
    "legend.local": "Sigida tɔnbolofara",
    "legend.remote": "Bolo yɔrɔjan",
    "legend.remoteOnly": "Sankaba kɔnɔ dɔrɔn (a tɛ sama) .",
    "legend.tag": "Tag (version) .",
    "legend.commit": "Ka kalifa",
    "legend.stash": "Stash (baara min bɛ bila etajɛri kan) .",
    "legend.mergedIn": "A donna ni merge (a sɛbɛnnen bɛ bolofara wɛrɛ kan) .",
    "legend.nodes": "Box (kɛsu) minnu bɛ yen",
    "legend.lines": "Layiniw",
    "legend.edgeParent": "Komitɛri tɛmɛnenw bɛ layini kelen kan",
    "legend.edgeMerge": "Bolo min bɛ fara ɲɔgɔn kan — o bɛ taamasiyɛn jira jɛkulu ka komite la",
    "legend.edgeBranch": "Bolo dɔ foroko bɔra yan",
    "legend.edgeStash": "Stash ni commit a tun bɛ shelved kan",
    "legend.edgeMergedChain": "Nin bolofara in ka layini min bɛ boli ka tɛmɛn commits merged-in fɛ",
    "legend.edgeMergedTie": "O commit kelen in bɛ bolofara min kan, a sɛbɛnna min kan",
    "menu.jumpHead": "⌖ Taga jatebɔyɔrɔ la",
    "menu.resetView": "⤢ Segin ka filɛli kɛ",
    "menu.createBranch": "Bolo dabɔ ka bɔ yan...",
    "menu.checkout": "Aw ye nin commit in lajɛ",
    "menu.copySha": "Kopi commit SHA",
    "menu.pushBranch": "Push bolofara \"{name}\".",
    "menu.renameCommit": "Commit cikan tɔgɔ caman wɛrɛ...",
    "menu.undoCommit": "Undo commit (ka fɛn caman Changements)...",
    "menu.viewChanges": "Yɛlɛmaw lajɛ...",
    "menu.jumpToOriginal": "Taga commit fɔlɔ la",
    "changes.title": "Yɛlɛma donna {sha} la",
    "changes.tabChanged": "Yɛlɛma donna",
    "changes.tabAll": "Filenw bɛɛ",
    "changes.added": "A farala a kan",
    "changes.modified": "Yɛlɛma donna a la",
    "changes.deleted": "A bɔra",
    "changes.renamed": "A tɔgɔ jiginna",
    "changes.noChanges": "Nin commit in tɛ file si Changer.",
    "changes.loading": "Chargement diff...",
    "changes.selectFile": "Filen dɔ sugandi walasa k’a fɛn caman Changements (Yɛlɛma) ye.",
    "changes.original": "Yɛrɛyɛrɛ",
    "changes.changed": "Nin commit (dafalen) in",
    "changes.binary": "Binary file — sɛbɛnni danfara tɛ ka jira.",
    "changes.tooLarge": "File ka bon kojugu fo a tɛ se ka diff.",
    "changes.renamedFrom": "A tɔgɔ jiginna ka bɔ {path} la",
    "changes.close": "Ka datugu",
    "changes.prevChange": "Yɛlɛma min kɛra ka tɛmɛ",
    "changes.nextChange": "Yɛlɛma nata",
    "changes.collapseAll": "Foli bɛɛ lajɛlen bɔgɔbɔgɔ",
    "changes.expandAll": "Foli bɛɛ lajɛlen bonya",
    "changes.searchPlaceholder": "Filenw ɲini...",
    "changes.noSearchResults": "Dosiyɛri si tɛ bɛn i ka ɲinini ma.",
    "changes.maximize": "A ka ca a la",
    "changes.restore": "A bonya segin a cogo kɔrɔ la",
    "find.open": "Ɲini nin danfara in na (Ctrl+F) .",
    "find.placeholder": "Aw ye a sɔrɔ diff...",
    "find.prev": "Match tɛmɛnen",
    "find.next": "Ntɔn nata",
    "find.close": "Ɲini da tugu",
    "find.noResults": "O nɔ tɛ sɔrɔ",
    "commit.title": "Yɛlɛma donna",
    "commit.close": "Ka datugu",
    "commit.loading": "Kalan baara jiri...",
    "commit.noChanges": "Yɛlɛma si tɛ sigida la min ka kan ka kɛ.",
    "commit.selectFile": "Filen dɔ sugandi walasa k’a danfara ye.",
    "commit.selectAll": "A bɛɛ sugandi",
    "commit.selectNone": "Aw ye bɛɛ jɛya",
    "commit.message": "Cikan min bilala sen kan",
    "commit.messagePlaceholder": "Yɛlɛma min kɛra, o ɲɛfɔ",
    "commit.selectedCount": "{count} file(w) sugandilenw",
    "commit.review": "Ka sɛgɛsɛgɛ",
    "commit.reviewTitle": "Sɛgɛsɛgɛli laban ka sɔrɔ ka commit kɛ",
    "commit.reviewSummary": "{count} file(w) kɛ ni cikan ye: {message}",
    "commit.back": "Kɔ",
    "commit.commit": "Sigida commit dabɔ",
    "menu.stashApply": "Aw bɛ stash (stash) kɛ",
    "menu.stashPop": "Pop stash (a kɛ & ka bɔ) .",
    "menu.stashDrop": "Drop stash...",
    "merge.title": "Tɔnbolofara min bɛ fara ɲɔgɔn kan",
    "merge.route": "Ka fara ɲɔgɔn kan ka don bolofara min bɛ yen sisan",
    "merge.source": "Ka bɔ (bɔyɔrɔ) la .",
    "merge.target": "Ka don (sisan) kɔnɔ .",
    "merge.loading": "Analyse ka fara ɲɔgɔn kan...",
    "merge.upToDate": "Kaban, a bɛ waati la — foyi tɛ ka fara ɲɔgɔn kan.",
    "merge.fastForward": "Fast-forward — merge commit si mago tɛ (“Create a merge commit” taamasiyɛn walasa ka kelen wajibiya).",
    "merge.summary": "{files} filen(w) bεε bε bεn, {conflicts} kεlε(w) bεε bε bεn.",
    "merge.conflictsWarning": "Nin farali in bɛ ni bɛnbaliya ye — i bɛ se ka fara ɲɔgɔn kan hali bi, o kɔfɛ k’u ɲɛnabɔ sɛbɛnnikɛla kɔnɔ.",
    "merge.noChanges": "Filen si tɛ Changé.",
    "merge.files": "Yɛlɛma minnu bɛ sɔrɔ o la",
    "merge.added": "A farala a kan",
    "merge.modified": "Yɛlɛma donna a la",
    "merge.deleted": "A bɔra",
    "merge.conflict": "Kɛlɛ",
    "merge.message": "Merge commit cikan",
    "merge.messagePlaceholder": "Jɛkulu bolofara 'sɔrɔ'.",
    "merge.messageFfHint": "Ignored on a fast-forward merge (commit si ma dabɔ).",
    "merge.noFastForward": "Aw ye jɛ-ka-baara kɛcogo dɔ da (a tɛ teliya ka taa ɲɛ) .",
    "merge.noFastForwardHint": "Tuma bɛɛ ka merge commit sɛbɛn, hali ni bolofara tun bɛ se ka teliya ka taa ɲɛ.",
    "merge.merge": "Furuɲɔgɔnma",
    "merge.cancel": "ka dankari",
    "merge.previewError": "A ma se ka jatebɔ kɛ ɲɛjirali dɔ la: {message}",
    "merge.squashBadge": "Squash (sɔgɔsɔgɔninjɛ).",
    "merge.squashNote": "Squash merge — fɛn bɛɛ bɛ jigin iko ONE commit kan “{target}” kan; “{source}” yɛrɛ ka layidu talenw bɛ to a tariku kɔ. Aw bɛ segin ka segin Settings › Merge kɔnɔ.",
    "merge.messageSquash": "Cikan min bilala sen kan",
    "merge.squashMessagePlaceholder": "Yɛlɛma minnu bɛ sɔgɔsɔgɔ ka bɔ ‘source’ la.",
    "newBranch.title": "Branch (bolofara) dabɔ",
    "newBranch.startPoint": "Bolofara kura bɛ daminɛ {sha} la",
    "newBranch.startPointOn": "{refs} kan",
    "newBranch.location": "Sigiyɔrɔ",
    "newBranch.locationRoot": "(dili)",
    "newBranch.expandAll": "Aw ye bɛɛ bonya",
    "newBranch.collapseAll": "A bɛɛ ka bin",
    "newBranch.expandFolder": "Foli (foli) lajɛ",
    "newBranch.collapseFolder": "Foli (foli) min bɛ wele ko collapse",
    "newBranch.name": "Tɔn bolofara tɔgɔ",
    "newBranch.namePlaceholder": "n-bolofara",
    "newBranch.fullName": "Tɔgɔ dafalen: {name}",
    "newBranch.invalid": "Bolo tɔgɔ min tɛ tiɲɛ ye",
    "newBranch.exists": "O tɔgɔ in bolofara dɔ bɛ yen kaban",
    "newBranch.checkout": "Checkout bolofara dabɔlen kɔfɛ",
    "newBranch.create": "Ka dilan",
    "newBranch.cancel": "ka dankari",
    "status.loading": "Graphique chargement...",
    "status.summary": "{repo}{commits} jirali bɛ kɛ, {refs} refs",
    "status.branchCreated": "A dabɔra bolofara \"{name}\" la {sha} la",
    "status.error": "Misali: {message}",
    "status.opFailed": "Operasiyɔn ma ɲɛ",
    "status.fetching": "Fetching...",
    "status.pulling": "Pulling...",
    "status.pushing": "Pushing...",
    "status.committing": "Ka sigida commit dabɔ...",
    "status.commitCreated": "A dabɔra sigida commit {sha}: {message}",
    "status.syncing": "Sinsin kɛli...",
    "status.noHead": "Sisan jatebɔ si ma sɔrɔ ja in kɔnɔ.",
    "status.undoing": "Undoing commit...",
    "status.commitUndone": "Commit undone — fɛn caman b’a la ka fɛn caman Changements (Yɛlɛma) seginna baarakɛjiri kɔnɔ.",
    "status.undoConflict": "Undo hit conflicts — aw bɛ u ɲɛnabɔ editor kɔnɔ, o kɔ aw bɛ taa ɲɛ.",
    "status.stashApplying": "Aplikasi stash...",
    "status.stashPopping": "Popping stash ye...",
    "status.stashDropping": "Dropping stash... Bamako, Mali.",
    "status.stashApplied": "Stash ye baara kɛ.",
    "status.stashPopped": "Stash ye pop kɛ.",
    "status.stashDropped": "Stash ye a ka jigin.",
    "status.stashConflict": "Stash conflicts — u ɲɛnabɔ editor kɔnɔ.",
    "status.merging": "Ka fara ɲɔgɔn kan...",
    "status.merged": "Merge (Jɛɲɔgɔnya) dafara.",
    "status.mergeConflict": "Merge bɛ ni bɛnbaliyakow ye — u ɲɛnabɔ editor kɔnɔ, o kɔfɛ commit.",
    "search.title": "Ɲini",
    "search.tooltip": "Ɲini bɛ kɛ",
    "search.placeholder": "Ɲini commits...",
    "search.previous": "Jateminɛ tɛmɛnen",
    "search.next": "O kɔfɛ, a nɔfɛko",
    "search.noResults": "O nɔ tɛ sɔrɔ",
    "search.resultCount": "{index}/{count} ye fɛn ye min bɛ se ka kɛ",
    "search.filter": "Filɛri cogoya",
    "search.highlight": "Mode (hakilila) cogoya",
    "search.filterMode": "Ɲini jirali",
    "search.filterModeFilter": "Filɛri (minnu tɛ bɛn ɲɔgɔn ma, olu dogo) .",
    "search.filterModeHighlight": "I ka yɔrɔ jira (a bɛɛ jira, ka taamasiyɛn kɛ ni bɛnkanw ye) .",
    "search.filterModeHint": "Ɲini jaabiw bɛ jira cogo min na jatebɔsɛbɛn kɔnɔ, o sugandi.",
    "search.visibility": "Yɔrɔ ɲini",
    "search.visibilityToolbar": "Baarakɛminɛnw yɔrɔ butɔni",
    "search.visibilityAlwaysVisible": "Baro min bɛ ye tuma bɛɛ",
    "search.visibilityHint": "Ɲini jira i n’a fɔ baarakɛminɛnw taamasiyɛn walima taamasiyɛn basigilen min bɛ sanfɛ.",
    "footer.github": "GitHub ye",
  },
  jv: {
  },
  su: {
    "toolbar.refresh": "⟳ Refresh",
    "toolbar.fetch": "⤓ Candak",
    "toolbar.pull": "⇩ Tarik",
    "toolbar.push": "⇧ Nyorong",
    "toolbar.commit": "✓ Komitmen",
    "toolbar.sync": "⇅ singkronisasi",
    "toolbar.jumpHead": "⌖ Pindah ka kasir",
    "toolbar.reset": "⤢ Reset pintonan",
    "toolbar.settings": "⚙ Setélan",
    "details.header": "Rincian komitmen",
    "details.close": "Tutup",
    "details.sha": "SHA",
    "details.shortSha": "SHA pondok",
    "details.message": "talatah",
    "details.author": "Pangarang",
    "details.date": "titimangsa",
    "details.labels": "Labels",
    "details.location": "Lokasi",
    "details.currentHead": "HEAD - Checkout ayeuna",
    "settings.title": "Setélan",
    "settings.language": "Basa",
    "settings.theme": "Téma",
    "settings.themeLight": "Caang",
    "settings.themeDark": "Poek",
    "settings.themeLightHint": "Latar caang, téks poék.",
    "settings.themeDarkHint": "Latar poék, téks cahaya.",
    "settings.mainBranch": "Cabang utama",
    "settings.mainBranchAuto": "Otomatis",
    "settings.mainBranchSearch": "Pilarian cabang…",
    "settings.mainBranchNoMatch": "Henteu aya dahan anu cocog",
    "settings.display": "Gaya pintonan",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Klasik",
    "settings.displayModernHint": "Kanvas bébas - sered ka pan, gulung ka zum.",
    "settings.displayClassicHint": "Kanvas maneuh - batangna disematkeun ka kénca, teu aya zum, gulung wungkul (sapertos grafik révisi SVN).",
    "settings.sectionGeneral": "Umum",
    "settings.sectionGraph": "Grafik",
    "settings.sectionChanges": "Parobahan pintonan",
    "settings.diffMinimap": "Béda minimap",
    "settings.diffMinimapOn": "Ditémbongkeun",
    "settings.diffMinimapOff": "Disumputkeun",
    "settings.diffMinimapOnHint": "Témbongkeun strip gambaran gaya VS Code gigireun bédana - sered ka ngagulung; spidol robah kaasup.",
    "settings.diffMinimapOffHint": "Taya strip tinjauan - gulung bédana normal.",
    "settings.commitReview": "Komitmen review",
    "settings.commitReviewOn": "Tanya saméméh komitmen",
    "settings.commitReviewOff": "Komitmen langsung",
    "settings.commitReviewOnHint": "Tampilkeun daptar file ahir sareng cek pesen sateuacan ngadamel komitmen lokal.",
    "settings.commitReviewOffHint": "Tombol Komit nyiptakeun komitmen lokal langsung.",
    "settings.svnBranchDialog": "Dialog cabang gaya SVN",
    "settings.svnBranchDialogHint": "Témbongkeun picker folder-tangkal nalika nyieun cabang (gaganti ajakan basajan).",
    "settings.branchDialog": "Dialog cabang",
    "settings.branchDialogSvnTitle": "SVN-gaya",
    "settings.branchDialogNativeVscode": "VS Code gaya",
    "settings.branchDialogNativeVs": "Gaya Visual Studio",
    "settings.branchDialogNativeHint": "IDE anjeun diwangun-di cabang ajakan.",
    "settings.close": "Tutup",
    "settings.done": "Réngsé",
    "settings.sectionAdvanced": "Maju",
    "settings.gitSource": "Git tiasa dieksekusi",
    "settings.gitSourceBuiltin": "Diwangun-di",
    "settings.gitSourceBuiltinHint": "Anggo binér git tina ekstensi Git anu diwangun dina IDE.",
    "settings.gitSourceCustom": "Jalur custom",
    "settings.gitPath": "Jalur ka git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Kotektak…",
    "settings.jargon": "istilah Git",
    "settings.jargonTranslate": "Tarjamahkeun",
    "settings.jargonEnglish": "Tetep dina basa Inggris",
    "settings.jargonTranslateHint": "Tarjamahkeun istilah Git (tarik, push, commit, branch…) kana basa panganteur.",
    "settings.jargonEnglishHint": "Tetep istilah Git (tarik, push, commit, branch…) dina basa Inggris; narjamahkeun sagalana sejenna.",
    "settings.sectionMerge": "Ngahiji",
    "settings.mergeMode": "Ngagabungkeun gaya",
    "settings.mergeModeMerge": "Biasa ngahiji",
    "settings.mergeModeSquash": "Squash ngahiji",
    "settings.mergeModeMergeHint": "Ngagabung cabang sakumaha anu kasebut: commits na janten bagian tina sajarah cabang ayeuna, diiluan ku merge commit (atanapi diteruskeun gancang upami mungkin).",
    "settings.mergeModeSquashHint": "Collapses sakabeh cabang kana commit tunggal dina cabang ayeuna (git merge --squash). Komitmen cabang sorangan henteu pernah asup kana sajarah cabang ayeuna — anjeun ngan ukur ningali hiji komitmen di dinya. Cabang sorangan ditinggalkeun teu kacekel, jadi commits na tetep katingali dina jalur sorangan.",
    "settings.mergedView": "Dihijikeun commits dina grafik",
    "settings.mergedViewBranch": "Ngan dina dahan sorangan",
    "settings.mergedViewTarget": "Ogé dina cabang maranéhanana dihijikeun kana",
    "settings.mergedViewBranchHint": "Komitmen cabang anu dihijikeun digambar ngan dina jalur sorangan; ngahiji muncul salaku konektor. Pandangan klasik.",
    "settings.mergedViewTargetHint": "Komitmen gabungan anu dibawa digambar ogé dina jalur cabang anu nampi, ditumpuk dina komit gabungan sareng tingkat sareng anu asli - sabab cabang éta leres-leres ngandung aranjeunna ayeuna. Salinanna bulak-balik sareng dipingpin ku dahan anu ditulisna, janten tetep écés yén éta henteu dilakukeun di dinya. Gabungan squash teu kapangaruhan: squash nulis hiji komitmen biasa, jadi ngan komitmen anu pernah muncul.",
    "legend.title": "Katerangan",
    "legend.head": "HEAD / cabang ayeuna",
    "legend.local": "Cabang lokal",
    "legend.remote": "Cabang jauh",
    "legend.remoteOnly": "Ngan dina méga (teu ditarik)",
    "legend.tag": "Tag (versi)",
    "legend.commit": "Komitmen",
    "legend.stash": "Panyimpenan (pagawean anu disimpen)",
    "legend.mergedIn": "Datang sareng ngahiji (ditulis dina cabang anu sanés)",
    "legend.nodes": "kotak-kotak",
    "legend.lines": "Garis-garis",
    "legend.edgeParent": "Komitmen saméméhna dina garis anu sami",
    "legend.edgeMerge": "Cabang gabungan - titik panah dina commit ngahiji",
    "legend.edgeBranch": "Hiji dahan bercabang di dieu",
    "legend.edgeStash": "Stash sareng komitmen éta disimpen",
    "legend.edgeMergedChain": "Garis cabang ieu ngajalankeun ngaliwatan commits merged-in",
    "legend.edgeMergedTie": "Komitmen anu sami dina cabang anu diserat",
    "menu.jumpHead": "⌖ Pindah ka kasir",
    "menu.resetView": "⤢ Reset pintonan",
    "menu.createBranch": "Jieun cabang ti dieu…",
    "menu.checkout": "Pariksa komitmen ieu",
    "menu.copySha": "Nyalin commit SHA",
    "menu.pushBranch": "Push cabang \"{name}\"",
    "menu.renameCommit": "Ganti ngaran pesen commit…",
    "menu.undoCommit": "Batalkeun komitmen (tetep perobahan)…",
    "menu.viewChanges": "Tingali parobahan…",
    "menu.jumpToOriginal": "Pindah ka komitmen aslina",
    "changes.title": "Parobahan dina {sha}",
    "changes.tabChanged": "Dirobah",
    "changes.tabAll": "Sadaya File",
    "changes.added": "Ditambahkeun",
    "changes.modified": "Dirobih",
    "changes.deleted": "Dipupus",
    "changes.renamed": "Diganti ngaran",
    "changes.noChanges": "Komitmen ieu henteu ngarobih file.",
    "changes.loading": "Ngamuat diff…",
    "changes.selectFile": "Pilih file pikeun ningali parobahanana.",
    "changes.original": "Aslina",
    "changes.changed": "Komitmen ieu",
    "changes.binary": "File binér - henteu aya bédana téks pikeun ditingalikeun.",
    "changes.tooLarge": "File ageung teuing pikeun diff.",
    "changes.renamedFrom": "Diganti ngaran tina {path}",
    "changes.close": "Tutup",
    "changes.prevChange": "Parobahan saméméhna",
    "changes.nextChange": "robah salajengna",
    "changes.collapseAll": "Runtuhkeun sadaya polder",
    "changes.expandAll": "Kembangkeun sadaya polder",
    "changes.searchPlaceholder": "Pilarian file…",
    "changes.noSearchResults": "Taya file cocog pilarian Anjeun.",
    "changes.maximize": "Maksimalkeun",
    "changes.restore": "Malikkeun ukuran",
    "find.open": "Pilarian dina bédana ieu (Ctrl+F)",
    "find.placeholder": "Panggihan dina bédana…",
    "find.prev": "pertandingan saméméhna",
    "find.next": "Pertandingan salajengna",
    "find.close": "Tutup pilarian",
    "find.noResults": "Taya hasilna",
    "commit.title": "Ngalakukeun Parobahan",
    "commit.close": "Tutup",
    "commit.loading": "Maca tangkal kerja…",
    "commit.noChanges": "Henteu aya parobahan lokal anu kedah dilakukeun.",
    "commit.selectFile": "Pilih file pikeun ningali bédana.",
    "commit.selectAll": "Pilih sadayana",
    "commit.selectNone": "Pupus sadayana",
    "commit.message": "Koméntar pesen",
    "commit.messagePlaceholder": "Jelaskeun parobahanana",
    "commit.selectedCount": "{count} file (s) dipilih",
    "commit.review": "Tinjauan",
    "commit.reviewTitle": "Pariksa final saméméh komitmen",
    "commit.reviewSummary": "Komit {count} file (s) kalayan pesen: {message}",
    "commit.back": "Balik deui",
    "commit.commit": "Jieun komitmen lokal",
    "menu.stashApply": "Larapkeun stash",
    "menu.stashPop": "Pop stash (larapkeun & cabut)",
    "menu.stashDrop": "Leupaskeun neundeun…",
    "merge.title": "Ngagabung Cabang",
    "merge.route": "Ngagabung kana cabang ayeuna",
    "merge.source": "Ti (sumber)",
    "merge.target": "Kana (ayeuna)",
    "merge.loading": "Nganalisis gabungan…",
    "merge.upToDate": "Geus nepi ka tanggal - euweuh ngahiji.",
    "merge.fastForward": "Maju gancang - henteu kedah ngahijikeun komitmen (centang \"Jieun komitmen gabungan\" pikeun maksakeun hiji).",
    "merge.summary": "file {files} (s) robah, konflik {conflicts} (s).",
    "merge.conflictsWarning": "Gabungan ieu gaduh konflik - anjeun masih tiasa ngagabung, teras ngabéréskeunana dina éditor.",
    "merge.noChanges": "Taya parobahan file.",
    "merge.files": "Parobahan hasilna",
    "merge.added": "Ditambahkeun",
    "merge.modified": "Dirobih",
    "merge.deleted": "Dipupus",
    "merge.conflict": "Konflik",
    "merge.message": "Gabungkeun pesen komitmen",
    "merge.messagePlaceholder": "Gabungkeun cabang 'sumber'",
    "merge.messageFfHint": "Teu dipaliré dina gabungan gancang-gancang (henteu aya komitmen anu didamel).",
    "merge.noFastForward": "Jieun commit ngahiji (henteu maju-gancang)",
    "merge.noFastForwardHint": "Salawasna ngarekam komitmen gabungan, sanajan cabangna tiasa gancang-gancang.",
    "merge.merge": "Ngahiji",
    "merge.cancel": "Ngabolaykeun",
    "merge.previewError": "Teu bisa ngitung sawangan: {message}",
    "merge.squashBadge": "Waluh",
    "merge.squashNote": "Squash merge - unggal parobahan darat sakumaha ONE komitmen dina \"{target}\"; Komitmen \"{source}\" sorangan tetep kaluar tina sajarahna. Pindah deui dina Setélan › Gabung.",
    "merge.messageSquash": "Koméntar pesen",
    "merge.squashMessagePlaceholder": "Parobahan squashed tina 'sumber'",
    "newBranch.title": "Jieun Cabang",
    "newBranch.startPoint": "Cabang anyar mimitian ti {sha}",
    "newBranch.startPointOn": "dina {refs}",
    "newBranch.location": "Lokasi",
    "newBranch.locationRoot": "(akar)",
    "newBranch.expandAll": "Ngalegaan sadayana",
    "newBranch.collapseAll": "Rubuh sadayana",
    "newBranch.expandFolder": "Kembangkeun polder",
    "newBranch.collapseFolder": "Leupaskeun folder",
    "newBranch.name": "Ngaran cabang",
    "newBranch.namePlaceholder": "abdi-cabang",
    "newBranch.fullName": "Ngaran lengkep: {name}",
    "newBranch.invalid": "Ngaran cabang teu valid",
    "newBranch.exists": "Cabang nganggo nami ieu parantos aya",
    "newBranch.checkout": "Cabang Checkout sanggeus kreasi",
    "newBranch.create": "Nyiptakeun",
    "newBranch.cancel": "Ngabolaykeun",
    "status.loading": "Ngamuat grafik…",
    "status.summary": "{repo}Némbongkeun {commits} commits, {refs} refs",
    "status.branchCreated": "Dijieun cabang \"{name}\" di {sha}",
    "status.error": "Kasalahan: {message}",
    "status.opFailed": "Operasi gagal",
    "status.fetching": "Nyandak…",
    "status.pulling": "Narik…",
    "status.pushing": "Ngadorong…",
    "status.committing": "Nyiptakeun komitmen lokal…",
    "status.commitCreated": "Dijieun komit lokal {sha}: {message}",
    "status.syncing": "Nyingkronkeun…",
    "status.noHead": "Taya Checkout ayeuna kapanggih dina grafik.",
    "status.undoing": "Ngabatalkeun komitmen…",
    "status.commitUndone": "Komit undone - parobahan deui dina tangkal digawé.",
    "status.undoConflict": "Batalkeun konflik hit - ngabéréskeunana dina éditor, teras teraskeun.",
    "status.stashApplying": "Nerapkeun neundeun…",
    "status.stashPopping": "Nyusun tumpukan…",
    "status.stashDropping": "Ngaleungitkeun gudang…",
    "status.stashApplied": "Stash dilarapkeun.",
    "status.stashPopped": "Stash némbongan.",
    "status.stashDropped": "Stash turun.",
    "status.stashConflict": "Konflik Stash - ngabéréskeunana dina redaktur.",
    "status.merging": "Ngahijikeun…",
    "status.merged": "Ngahiji réngsé.",
    "status.mergeConflict": "Gabung gaduh konflik - ngabéréskeunana dina éditor, teras komitmen.",
    "search.title": "Pilarian",
    "search.tooltip": "Pilarian komitmen",
    "search.placeholder": "Pilarian komitmen…",
    "search.previous": "Hasil saméméhna",
    "search.next": "Hasil salajengna",
    "search.noResults": "Taya hasilna",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modeu saringan",
    "search.highlight": "Modeu sorot",
    "search.filterMode": "tampilan pilarian",
    "search.filterModeFilter": "Filter (sumputkeun teu cocog)",
    "search.filterModeHighlight": "Sorot (témbongkeun sadayana, tandai patandingan)",
    "search.filterModeHint": "Pilih kumaha hasil pamilarian dipintonkeun dina grafik.",
    "search.visibility": "Pilarian lokasi",
    "search.visibilityToolbar": "Tombol toolbar",
    "search.visibilityAlwaysVisible": "Salawasna-katingali bar",
    "search.visibilityHint": "Témbongkeun pilarian salaku ikon toolbar atawa bar pengkuh di luhur.",
    "footer.github": "GitHub",
  },
  bs: {
    "toolbar.refresh": "⟳ Osvježi",
    "toolbar.fetch": "⤓ Dohvati",
    "toolbar.pull": "⇩ Povucite",
    "toolbar.push": "⇧ Gurnite",
    "toolbar.commit": "✓ Posvetite se",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Idi na naplatu",
    "toolbar.reset": "⤢ Resetujte prikaz",
    "toolbar.settings": "⚙ Postavke",
    "details.header": "Commit Details",
    "details.close": "Zatvori",
    "details.sha": "SHA",
    "details.shortSha": "Short SHA",
    "details.message": "Poruka",
    "details.author": "Autor",
    "details.date": "Datum",
    "details.labels": "Etikete",
    "details.location": "Lokacija",
    "details.currentHead": "HEAD — trenutna naplata",
    "settings.title": "Postavke",
    "settings.language": "Jezik",
    "settings.theme": "Tema",
    "settings.themeLight": "Light",
    "settings.themeDark": "Dark",
    "settings.themeLightHint": "Svetla pozadina, tamni tekst.",
    "settings.themeDarkHint": "Tamna pozadina, svijetli tekst.",
    "settings.mainBranch": "Glavna podružnica",
    "settings.mainBranchAuto": "Automatski",
    "settings.mainBranchSearch": "Pretražite grane…",
    "settings.mainBranchNoMatch": "Nema odgovarajućih grana",
    "settings.display": "Stil prikaza",
    "settings.displayModern": "Moderna",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Besplatno platno — prevucite za pomicanje, skrolujte za zumiranje.",
    "settings.displayClassicHint": "Fiksno platno — deblo zakačeno levo, bez zumiranja, samo skrolovanje (kao SVN revizioni graf).",
    "settings.sectionGeneral": "Generale",
    "settings.sectionGraph": "Graf",
    "settings.sectionChanges": "Mijenja pogled",
    "settings.diffMinimap": "Diff minimapa",
    "settings.diffMinimapOn": "Prikazano",
    "settings.diffMinimapOff": "Skriveno",
    "settings.diffMinimapOnHint": "Prikažite preglednu traku u stilu VS koda pored diff-a — prevucite je da biste se pomerali; markeri za promjenu uključeni.",
    "settings.diffMinimapOffHint": "Nema trake za pregled — normalno skrolujte dif.",
    "settings.commitReview": "Popunite pregled",
    "settings.commitReviewOn": "Pitajte prije urezivanja",
    "settings.commitReviewOff": "Direktno se posvetite",
    "settings.commitReviewOnHint": "Prikaži konačnu listu datoteka i provjeru poruke prije kreiranja lokalnog urezivanja.",
    "settings.commitReviewOffHint": "Dugme Ureži odmah kreira lokalno urezivanje.",
    "settings.svnBranchDialog": "Dijalog grane u SVN stilu",
    "settings.svnBranchDialogHint": "Prikažite birač stabla foldera kada kreirate granu (umjesto jednostavnog upita).",
    "settings.branchDialog": "Dijalog grane",
    "settings.branchDialogSvnTitle": "SVN-stil",
    "settings.branchDialogNativeVscode": "VS stil koda",
    "settings.branchDialogNativeVs": "Visual Studio stil",
    "settings.branchDialogNativeHint": "Vaš IDE-ov ugrađeni prompt grane.",
    "settings.close": "Zatvori",
    "settings.done": "Gotovo",
    "settings.sectionAdvanced": "Napredno",
    "settings.gitSource": "Git izvršni",
    "settings.gitSourceBuiltin": "Ugrađeni",
    "settings.gitSourceBuiltinHint": "Koristite git binarnu datoteku iz ugrađene ekstenzije Git u IDE.",
    "settings.gitSourceCustom": "Prilagođena staza",
    "settings.gitPath": "Put do git-a",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Pregledaj…",
    "settings.jargon": "Git termini",
    "settings.jargonTranslate": "Prevedi",
    "settings.jargonEnglish": "Držite na engleskom",
    "settings.jargonTranslateHint": "Prevedite Git termine (pull, push, commit, branch…) u jezik interfejsa.",
    "settings.jargonEnglishHint": "Zadržite Git termine (povući, potisnite, urezivanje, grananje...) na engleskom; sve ostalo prevedite.",
    "settings.sectionMerge": "Spoji",
    "settings.mergeMode": "Spoji stil",
    "settings.mergeModeMerge": "Normalno spajanje",
    "settings.mergeModeSquash": "Squash spajanje",
    "settings.mergeModeMergeHint": "Spaja granu kakva jeste: njena urezivanja postaju deo istorije trenutne grane, pridružena spajanjem urezivanja (ili brzo premotavana kada je to moguće).",
    "settings.mergeModeSquashHint": "Sažima cijelu granu u jedno urezivanje na trenutnoj grani (git merge --squash). Vlastita urezivanja grane nikada ne ulaze u historiju trenutne grane – tamo vidite samo jedno urezivanje. Sama grana je ostavljena netaknuta, tako da njeni delovi ostaju vidljivi u sopstvenoj traci.",
    "settings.mergedView": "Spojena urezivanja u graf",
    "settings.mergedViewBranch": "Samo u vlastitoj poslovnici",
    "settings.mergedViewTarget": "Također u grani u koju su spojeni",
    "settings.mergedViewBranchHint": "Urezivanja spojene grane se crtaju samo u njihovoj vlastitoj traci; spajanje se pojavljuje kao konektor. Klasičan pogled.",
    "settings.mergedViewTargetHint": "Urezivanja koja su unesena spajanjem također su iscrtana u traci grane prijema, naslagana ispod urezivanja spajanja i u ravni sa originalima — jer ih ta grana sada zaista sadrži. Kopije su blijede i zaglavljene sa granom na kojoj su ispisane, tako da ostaje jasno da nisu tamo napravljene. Squash spajanja su nepromijenjena: squash piše jedno obično urezivanje, tako da se uvijek pojavljuje samo to urezivanje.",
    "legend.title": "Legenda",
    "legend.head": "GLAVA / trenutna grana",
    "legend.local": "Lokalna podružnica",
    "legend.remote": "Udaljena grana",
    "legend.remoteOnly": "Samo u oblaku (ne povučeno)",
    "legend.tag": "Oznaka (verzija)",
    "legend.commit": "Počinite",
    "legend.stash": "Skladište (odloženi rad)",
    "legend.mergedIn": "Ušao sa spajanjem (napisano na drugoj grani)",
    "legend.nodes": "Kutije",
    "legend.lines": "Linije",
    "legend.edgeParent": "Prethodno urezivanje na istoj liniji",
    "legend.edgeMerge": "Spojena grana — strelica pokazuje na urezivanje spajanja",
    "legend.edgeBranch": "Grana se ovdje račvala",
    "legend.edgeStash": "Stash i urezivanje na koje je bio odložen",
    "legend.edgeMergedChain": "Linija ove grane prolazi kroz spojena urezivanja",
    "legend.edgeMergedTie": "Isti urezivanje na grani na kojoj je napisano",
    "menu.jumpHead": "⌖ Idi na naplatu",
    "menu.resetView": "⤢ Resetujte prikaz",
    "menu.createBranch": "Kreirajte granu odavde…",
    "menu.checkout": "Provjerite ovu obvezu",
    "menu.copySha": "Kopiraj urezivanje SHA",
    "menu.pushBranch": "Push grana \"{name}\"",
    "menu.renameCommit": "Preimenujte poruku urezivanja…",
    "menu.undoCommit": "Poništi urezivanje (zadrži promjene)…",
    "menu.viewChanges": "Prikaži promjene…",
    "menu.jumpToOriginal": "Idite na originalno urezivanje",
    "changes.title": "Promjene u {sha}",
    "changes.tabChanged": "Promijenjeno",
    "changes.tabAll": "Svi fajlovi",
    "changes.added": "Dodano",
    "changes.modified": "Izmijenjeno",
    "changes.deleted": "Izbrisano",
    "changes.renamed": "Preimenovano",
    "changes.noChanges": "Ovo urezivanje ne mijenja datoteke.",
    "changes.loading": "Učitavanje razlike…",
    "changes.selectFile": "Odaberite datoteku da vidite njene promjene.",
    "changes.original": "Original",
    "changes.changed": "Ova obaveza",
    "changes.binary": "Binarna datoteka — nema razlike teksta za prikaz.",
    "changes.tooLarge": "Fajl je prevelik za razliku.",
    "changes.renamedFrom": "Preimenovano iz {path}",
    "changes.close": "Zatvori",
    "changes.prevChange": "Prethodna promjena",
    "changes.nextChange": "Sljedeća promjena",
    "changes.collapseAll": "Skupi sve foldere",
    "changes.expandAll": "Proširite sve fascikle",
    "changes.searchPlaceholder": "Pretraži fajlove…",
    "changes.noSearchResults": "Nijedan fajl ne odgovara vašem pretraživanju.",
    "changes.maximize": "Maksimiziraj",
    "changes.restore": "Vrati veličinu",
    "find.open": "Traži u ovom diffu (Ctrl+F)",
    "find.placeholder": "Pronađi u diff…",
    "find.prev": "Prethodna utakmica",
    "find.next": "Sljedeća utakmica",
    "find.close": "Zatvori pretragu",
    "find.noResults": "Nema rezultata",
    "commit.title": "Commit Changes",
    "commit.close": "Zatvori",
    "commit.loading": "Čitanje radnog stabla…",
    "commit.noChanges": "Nema lokalnih promjena za urezivanje.",
    "commit.selectFile": "Odaberite datoteku da vidite njenu razliku.",
    "commit.selectAll": "Odaberite sve",
    "commit.selectNone": "Obriši sve",
    "commit.message": "Porežite poruku",
    "commit.messagePlaceholder": "Opišite promjenu",
    "commit.selectedCount": "Odabran(e) fajl(e) {count}",
    "commit.review": "Pregled",
    "commit.reviewTitle": "Konačna provjera prije urezivanja",
    "commit.reviewSummary": "Urezivanje {count} fajl(ova) sa porukom: {message}",
    "commit.back": "Nazad",
    "commit.commit": "Kreirajte lokalno urezivanje",
    "menu.stashApply": "Primijenite zalihe",
    "menu.stashPop": "Pop zaliha (primijeni i ukloni)",
    "menu.stashDrop": "Baci zalihe…",
    "merge.title": "Merge Branch",
    "merge.route": "Spajanje u trenutnu granu",
    "merge.source": "Od (izvor)",
    "merge.target": "u (trenutno)",
    "merge.loading": "Analiza spajanja…",
    "merge.upToDate": "Već ažurirano - ništa za spajanje.",
    "merge.fastForward": "Premotavanje unaprijed — nije potrebno urezivanje spajanjem (označite “Kreiraj urezivanje spajanja” da ga prisilite).",
    "merge.summary": "{files} fajl(ovi) promenjen(i), {conflicts} konflikt(i).",
    "merge.conflictsWarning": "Ovo spajanje ima sukobe - još uvijek možete spojiti, a zatim ih riješiti u uređivaču.",
    "merge.noChanges": "Nema promjena u fajlu.",
    "merge.files": "Rezultirajuće promjene",
    "merge.added": "Dodano",
    "merge.modified": "Izmijenjeno",
    "merge.deleted": "Izbrisano",
    "merge.conflict": "Sukob",
    "merge.message": "Spajanje poruke urezivanja",
    "merge.messagePlaceholder": "Spoji 'izvor' grane",
    "merge.messageFfHint": "Ignorirano pri brzom spajanju unaprijed (nije kreirano urezivanje).",
    "merge.noFastForward": "Kreirajte spajanje urezivanja (bez premotavanja unaprijed)",
    "merge.noFastForwardHint": "Uvijek snimite urezivanje spajanja, čak i kada grana može brzo premotati naprijed.",
    "merge.merge": "Spoji",
    "merge.cancel": "Otkaži",
    "merge.previewError": "Nije moguće izračunati pregled: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash stapanje — svaka promjena dolazi kao JEDNO urezivanje na “{target}”; “{source}”-ove vlastite obaveze ostaju van njegove istorije. Vratite se u Postavkama › Spoji.",
    "merge.messageSquash": "Porežite poruku",
    "merge.squashMessagePlaceholder": "Zbijene promjene iz 'izvora'",
    "newBranch.title": "Create Branch",
    "newBranch.startPoint": "Nova grana počevši od {sha}",
    "newBranch.startPointOn": "na {refs}",
    "newBranch.location": "Lokacija",
    "newBranch.locationRoot": "(korijen)",
    "newBranch.expandAll": "Proširi sve",
    "newBranch.collapseAll": "Sažmi sve",
    "newBranch.expandFolder": "Proširi folder",
    "newBranch.collapseFolder": "Skupi folder",
    "newBranch.name": "Naziv ogranka",
    "newBranch.namePlaceholder": "moja filijala",
    "newBranch.fullName": "Puni naziv: {name}",
    "newBranch.invalid": "Nevažeći naziv grane",
    "newBranch.exists": "Grana sa ovim imenom već postoji",
    "newBranch.checkout": "Naplata filijala nakon kreiranja",
    "newBranch.create": "Kreiraj",
    "newBranch.cancel": "Otkaži",
    "status.loading": "Učitavanje grafikona…",
    "status.summary": "{repo}Prikazivanje {commits} urezivanja, {refs} ref",
    "status.branchCreated": "Stvorena podružnica \"{name}\" na {sha}",
    "status.error": "Greška: {message}",
    "status.opFailed": "Operacija nije uspjela",
    "status.fetching": "Preuzimanje…",
    "status.pulling": "Povlačenje…",
    "status.pushing": "Guranje…",
    "status.committing": "Kreiranje lokalnog urezivanja…",
    "status.commitCreated": "Kreirano lokalno urezivanje {sha}: {message}",
    "status.syncing": "Sinhroniziranje…",
    "status.noHead": "Na grafikonu nije pronađena trenutna naplata.",
    "status.undoing": "Poništavanje urezivanja…",
    "status.commitUndone": "Urezivanje je poništeno — promjene su ponovo u radnom stablu.",
    "status.undoConflict": "Poništite sukobe pogodaka — riješite ih u uređivaču, a zatim nastavite.",
    "status.stashApplying": "Primjena zaliha…",
    "status.stashPopping": "Iskakanje zaliha…",
    "status.stashDropping": "Izbacivanje zalihe…",
    "status.stashApplied": "Primijenjeno skrovište.",
    "status.stashPopped": "Skladište je iskočilo.",
    "status.stashDropped": "Skladište je ispalo.",
    "status.stashConflict": "Spremite konflikte — riješite ih u uređivaču.",
    "status.merging": "Spajanje…",
    "status.merged": "Spajanje je završeno.",
    "status.mergeConflict": "Spajanje ima sukobe - riješite ih u uređivaču, a zatim urezujte.",
    "search.title": "Traži",
    "search.tooltip": "Search commits",
    "search.placeholder": "Pretraga urezuje…",
    "search.previous": "Prethodni rezultat",
    "search.next": "Sljedeći rezultat",
    "search.noResults": "Nema rezultata",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Režim filtera",
    "search.highlight": "Highlight mode",
    "search.filterMode": "Prikaz pretrage",
    "search.filterModeFilter": "Filtriraj (sakrij nepodudarnosti)",
    "search.filterModeHighlight": "Istakni (prikaži sve, označi utakmice)",
    "search.filterModeHint": "Odaberite kako se rezultati pretraživanja prikazuju na grafikonu.",
    "search.visibility": "Traži lokaciju",
    "search.visibilityToolbar": "Dugme na alatnoj traci",
    "search.visibilityAlwaysVisible": "Uvijek vidljiva traka",
    "search.visibilityHint": "Prikaži pretragu kao ikonu trake sa alatkama ili trajnu traku na vrhu.",
    "footer.github": "GitHub",
  },
  ca: {
    "toolbar.refresh": "⟳ Actualitza",
    "toolbar.fetch": "⤓ Recuperar",
    "toolbar.pull": "⇩ Estirar",
    "toolbar.push": "⇧ Premeu",
    "toolbar.commit": "✓ Compromís",
    "toolbar.sync": "⇅ Sincronització",
    "toolbar.jumpHead": "⌖ Anar a la caixa",
    "toolbar.reset": "⤢ Restableix la vista",
    "toolbar.settings": "⚙ Configuració",
    "details.header": "Detalls del compromís",
    "details.close": "Tancar",
    "details.sha": "SHA",
    "details.shortSha": "SHA curt",
    "details.message": "Missatge",
    "details.author": "Autor",
    "details.date": "Data",
    "details.labels": "Etiquetes",
    "details.location": "Ubicació",
    "details.currentHead": "HEAD: pagament actual",
    "settings.title": "Configuració",
    "settings.language": "Llengua",
    "settings.theme": "Tema",
    "settings.themeLight": "Llum",
    "settings.themeDark": "Fosc",
    "settings.themeLightHint": "Fons clar, text fosc.",
    "settings.themeDarkHint": "Fons fosc, text clar.",
    "settings.mainBranch": "Branca principal",
    "settings.mainBranchAuto": "Automàtic",
    "settings.mainBranchSearch": "Cerca oficines...",
    "settings.mainBranchNoMatch": "No hi ha branques coincidents",
    "settings.display": "Estil de visualització",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Clàssic",
    "settings.displayModernHint": "Llenç gratuït: arrossegueu per panoràmica, desplaceu-vos per fer zoom.",
    "settings.displayClassicHint": "Llenç fixat: tronc fixat a l'esquerra, sense zoom, només desplaçament (com el gràfic de revisió SVN).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Gràfic",
    "settings.sectionChanges": "Canvia la vista",
    "settings.diffMinimap": "Minimapa de diferència",
    "settings.diffMinimapOn": "Es mostra",
    "settings.diffMinimapOff": "Ocult",
    "settings.diffMinimapOnHint": "Mostra una banda de visió general d'estil VS Code al costat de la diferència; arrossegueu-la per desplaçar-vos; marcadors de canvi inclosos.",
    "settings.diffMinimapOffHint": "Sense tira de visió general: desplaceu-vos per la diferència normalment.",
    "settings.commitReview": "Revisió de compromís",
    "settings.commitReviewOn": "Pregunta abans de comprometre't",
    "settings.commitReviewOff": "Compromís directament",
    "settings.commitReviewOnHint": "Mostra una llista final de fitxers i una comprovació de missatges abans de crear la confirmació local.",
    "settings.commitReviewOffHint": "El botó Commit crea el commit local immediatament.",
    "settings.svnBranchDialog": "Diàleg de branca d'estil SVN",
    "settings.svnBranchDialogHint": "Mostra un selector d'arbre de carpetes quan es crea una branca (en lloc d'una sol·licitud senzilla).",
    "settings.branchDialog": "Diàleg de branca",
    "settings.branchDialogSvnTitle": "Estil SVN",
    "settings.branchDialogNativeVscode": "Estil VS Code",
    "settings.branchDialogNativeVs": "Estil Visual Studio",
    "settings.branchDialogNativeHint": "L'indicador de branca integrat del vostre IDE.",
    "settings.close": "Tancar",
    "settings.done": "Fet",
    "settings.sectionAdvanced": "Avançat",
    "settings.gitSource": "Git executable",
    "settings.gitSourceBuiltin": "Integrat",
    "settings.gitSourceBuiltinHint": "Utilitzeu el binari git de l'extensió Git integrada de l'IDE.",
    "settings.gitSourceCustom": "Camí personalitzat",
    "settings.gitPath": "Camí a git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Navega…",
    "settings.jargon": "Termes Git",
    "settings.jargonTranslate": "Traduir",
    "settings.jargonEnglish": "Mantenir en anglès",
    "settings.jargonTranslateHint": "Tradueix els termes de Git (pull, push, commit, branch...) a l'idioma de la interfície.",
    "settings.jargonEnglishHint": "Mantenir els termes de Git (pull, push, commit, branch...) en anglès; traduir tota la resta.",
    "settings.sectionMerge": "Fusionar",
    "settings.mergeMode": "Combina l'estil",
    "settings.mergeModeMerge": "Fusió normal",
    "settings.mergeModeSquash": "Fusió d'esquaix",
    "settings.mergeModeMergeHint": "Fusiona la branca tal com és: les seves confirmacions passen a formar part de l'historial de la branca actual, s'uneixen per una confirmació de combinació (o s'envia ràpidament quan sigui possible).",
    "settings.mergeModeSquashHint": "Col·lapsa tota la branca en una única confirmació a la branca actual (git merge --squash). Les confirmacions pròpies de la branca mai entren a l'historial de la branca actual; només hi veus una confirmació. La branca en si es deixa intacta, de manera que els seus compromisos es mantenen visibles al seu propi carril.",
    "settings.mergedView": "Commits combinats al gràfic",
    "settings.mergedViewBranch": "Només a la seva pròpia branca",
    "settings.mergedViewTarget": "També a la branca es van fusionar",
    "settings.mergedViewBranchHint": "Els commits d'una branca fusionada només es dibuixen al seu propi carril; la fusió es mostra com un connector. La vista clàssica.",
    "settings.mergedViewTargetHint": "Les confirmacions d'una fusió introduïdes també es dibuixen al carril de la branca receptora, apilades sota la confirmació de fusió i a nivell dels originals, perquè aquesta branca realment els conté ara. Les còpies són pàl·lides i encapçalades amb la branca on estaven escrites, així que queda clar que no s'hi van fer. Les combinacions d'esquash no es veuen afectades: una esquaix escriu una confirmació ordinària, de manera que només apareix aquesta confirmació.",
    "legend.title": "Llegenda",
    "legend.head": "CAP / branca actual",
    "legend.local": "Filial local",
    "legend.remote": "Branca remota",
    "legend.remoteOnly": "Només al núvol (no estirat)",
    "legend.tag": "Etiqueta (versió)",
    "legend.commit": "Compromís",
    "legend.stash": "Stash (treball guardat)",
    "legend.mergedIn": "Va entrar amb una fusió (escrit en una altra branca)",
    "legend.nodes": "Caixes",
    "legend.lines": "Línies",
    "legend.edgeParent": "Compromís anterior a la mateixa línia",
    "legend.edgeMerge": "Branca fusionada: la fletxa apunta a la confirmació de fusió",
    "legend.edgeBranch": "Aquí s'ha bifurcat una branca",
    "legend.edgeStash": "Stash i el compromís en què es va guardar",
    "legend.edgeMergedChain": "La línia d'aquesta branca que passa per les confirmacions fusionades",
    "legend.edgeMergedTie": "El mateix commit a la branca on estava escrit",
    "menu.jumpHead": "⌖ Anar a la caixa",
    "menu.resetView": "⤢ Restableix la vista",
    "menu.createBranch": "Crea una branca des d'aquí...",
    "menu.checkout": "Fes una ullada a aquest compromís",
    "menu.copySha": "Copia SHA de confirmació",
    "menu.pushBranch": "Empènyer la branca \"{name}\"",
    "menu.renameBranch": "Canvieu el nom de la branca \"{name}\"...",
    "menu.renameCommit": "Canvia el nom del missatge de confirmació...",
    "menu.undoCommit": "Desfés la confirmació (conserva els canvis)...",
    "menu.viewChanges": "Mostra els canvis...",
    "menu.mergeBranch": "Combina \"{source}\" a \"{target}\"...",
    "menu.jumpToOriginal": "Aneu al commit original",
    "changes.title": "Canvis en {sha}",
    "changes.tabChanged": "Canviat",
    "changes.tabAll": "Tots els fitxers",
    "changes.added": "Afegit",
    "changes.modified": "Modificat",
    "changes.deleted": "S'ha suprimit",
    "changes.renamed": "Reanomenat",
    "changes.noChanges": "Aquesta confirmació no canvia cap fitxer.",
    "changes.loading": "S'està carregant la diferència...",
    "changes.selectFile": "Seleccioneu un fitxer per veure'n els canvis.",
    "changes.original": "Original",
    "changes.changed": "Aquest compromís",
    "changes.binary": "Fitxer binari: no hi ha cap diferència de text per mostrar.",
    "changes.tooLarge": "El fitxer és massa gran per diferenciar-lo.",
    "changes.renamedFrom": "S'ha canviat el nom de {path}",
    "changes.close": "Tancar",
    "changes.prevChange": "Canvi previ",
    "changes.nextChange": "Proper canvi",
    "changes.collapseAll": "Replega totes les carpetes",
    "changes.expandAll": "Amplieu totes les carpetes",
    "changes.searchPlaceholder": "Cerca fitxers...",
    "changes.noSearchResults": "No hi ha cap fitxer que coincideixi amb la vostra cerca.",
    "changes.maximize": "Maximitzar",
    "changes.restore": "Restaura la mida",
    "find.open": "Cerca en aquesta diferència (Ctrl+F)",
    "find.placeholder": "Troba a la diferència...",
    "find.prev": "Partit anterior",
    "find.next": "Proper partit",
    "find.close": "Tanca la cerca",
    "find.noResults": "Sense resultats",
    "commit.title": "Comença els canvis",
    "commit.close": "Tancar",
    "commit.loading": "Llegint l'arbre de treball...",
    "commit.noChanges": "No hi ha canvis locals per comprometre.",
    "commit.selectFile": "Seleccioneu un fitxer per veure'n la diferència.",
    "commit.selectAll": "Seleccioneu-ho tot",
    "commit.selectNone": "Esborra-ho tot",
    "commit.message": "Missatge de confirmació",
    "commit.messagePlaceholder": "Descriu el canvi",
    "commit.selectedCount": "Fitxers {count} seleccionats",
    "commit.review": "Revisió",
    "commit.reviewTitle": "Comprovació final abans de comprometre's",
    "commit.reviewSummary": "Envia fitxers {count} amb el missatge: {message}",
    "commit.back": "Enrere",
    "commit.commit": "Crear un compromís local",
    "menu.stashApply": "Apliqueu l'emmagatzematge",
    "menu.stashPop": "Pop stash (aplicar i eliminar)",
    "menu.stashDrop": "Llançar el dipòsit...",
    "merge.title": "Fusionar la branca",
    "merge.route": "Fusió a la branca actual",
    "merge.source": "De (font)",
    "merge.target": "A (actual)",
    "merge.loading": "S'està analitzant la fusió...",
    "merge.upToDate": "Ja està actualitzat, res a combinar.",
    "merge.fastForward": "Avança ràpid: no cal una confirmació de combinació (marqueu \"Crea una confirmació de combinació\" per forçar-ne una).",
    "merge.summary": "S'han canviat els fitxers {files}, conflictes {conflicts}.",
    "merge.conflictsWarning": "Aquesta fusió té conflictes; encara podeu fusionar-los i resoldre'ls a l'editor.",
    "merge.noChanges": "No hi ha canvis de fitxer.",
    "merge.files": "Canvis resultants",
    "merge.added": "Afegit",
    "merge.modified": "Modificat",
    "merge.deleted": "S'ha suprimit",
    "merge.conflict": "Conflicte",
    "merge.message": "Combina el missatge de confirmació",
    "merge.messagePlaceholder": "Combina la branca \"font\"",
    "merge.messageFfHint": "S'ignora en una fusió ràpida (no es crea cap compromís).",
    "merge.noFastForward": "Creeu una confirmació de combinació (sense avançament ràpid)",
    "merge.noFastForwardHint": "Enregistreu sempre una confirmació de combinació, fins i tot quan la branca pugui avançar ràpidament.",
    "merge.merge": "Fusionar",
    "merge.cancel": "Cancel·la",
    "merge.previewError": "No s'ha pogut calcular una previsualització: {message}",
    "merge.squashBadge": "Esquaix",
    "merge.squashNote": "Fusió d'esquaix: tots els canvis aterren com UNA missió a \"{target}\"; Els compromisos de “{source}” es mantenen fora de la seva història. Torna a Configuració › Combina.",
    "merge.messageSquash": "Missatge de confirmació",
    "merge.squashMessagePlaceholder": "Canvis esborrats de \"font\"",
    "newBranch.title": "Crear sucursal",
    "newBranch.startPoint": "Nova branca a partir de {sha}",
    "newBranch.startPointOn": "a {refs}",
    "newBranch.location": "Ubicació",
    "newBranch.locationRoot": "(arrel)",
    "newBranch.expandAll": "Ampliar-ho tot",
    "newBranch.collapseAll": "Col·lapsa-ho tot",
    "newBranch.expandFolder": "Amplieu la carpeta",
    "newBranch.collapseFolder": "Replega la carpeta",
    "newBranch.name": "Nom de la sucursal",
    "newBranch.namePlaceholder": "la meva-branca",
    "newBranch.fullName": "Nom complet: {name}",
    "newBranch.invalid": "Nom de sucursal no vàlid",
    "newBranch.exists": "Ja existeix una branca amb aquest nom",
    "newBranch.checkout": "Branca de pagament després de la creació",
    "newBranch.create": "Crear",
    "newBranch.cancel": "Cancel·la",
    "status.loading": "S'està carregant el gràfic...",
    "status.summary": "{repo}Mostrant confirmacions {commits}, refs {refs}",
    "status.branchCreated": "S'ha creat la branca \"{name}\" a {sha}",
    "status.error": "Error: {message}",
    "status.opFailed": "L'operació ha fallat",
    "status.fetching": "S'està obtenint…",
    "status.pulling": "Estirant…",
    "status.pushing": "Empenyant…",
    "status.committing": "S'està creant un compromís local...",
    "status.commitCreated": "S'ha creat la confirmació local {sha}: {message}",
    "status.syncing": "S'està sincronitzant...",
    "status.noHead": "No s'ha trobat cap pagament actual al gràfic.",
    "status.undoing": "S'està desfent el compromís...",
    "status.commitUndone": "Compromís desfet: els canvis tornen a estar a l'arbre de treball.",
    "status.undoConflict": "Desfés els conflictes de visita: resol-los a l'editor i, a continuació, continua.",
    "status.stashApplying": "S'està aplicant l'emmagatzematge…",
    "status.stashPopping": "Esclatant el dipòsit...",
    "status.stashDropping": "S'està deixant caure el dipòsit...",
    "status.stashApplied": "Stash aplicat.",
    "status.stashPopped": "Va aparèixer Stash.",
    "status.stashDropped": "Stash va caure.",
    "status.stashConflict": "Emmagatzema els conflictes: resol-los a l'editor.",
    "status.merging": "S'està fusionant…",
    "status.merged": "S'ha completat la fusió.",
    "status.mergeConflict": "La fusió té conflictes: resol-los a l'editor i, després, confirma.",
    "search.title": "Cerca",
    "search.tooltip": "Commits de cerca",
    "search.placeholder": "Compromis de cerca...",
    "search.previous": "Resultat anterior",
    "search.next": "Següent resultat",
    "search.noResults": "Sense resultats",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Mode de filtre",
    "search.highlight": "Mode de ressaltat",
    "search.filterMode": "Pantalla de cerca",
    "search.filterModeFilter": "Filtra (amaga les no coincidències)",
    "search.filterModeHighlight": "Ressaltar (mostrar-ho tot, marcar coincidències)",
    "search.filterModeHint": "Trieu com es mostren els resultats de la cerca al gràfic.",
    "search.visibility": "Cerca la ubicació",
    "search.visibilityToolbar": "Botó de la barra d'eines",
    "search.visibilityAlwaysVisible": "Barra sempre visible",
    "search.visibilityHint": "Mostra la cerca com a icona de barra d'eines o barra persistent a la part superior.",
    "footer.github": "GitHub",
  },
  ceb: {
    "toolbar.refresh": "⟳ I-refresh",
    "toolbar.fetch": "⤓ Kuhaa",
    "toolbar.pull": "⇩ Ibira",
    "toolbar.push": "⇧ Iduso",
    "toolbar.commit": "✓ Ipasalig",
    "toolbar.sync": "⇅ Pag-sync",
    "toolbar.jumpHead": "⌖ Adto sa checkout",
    "toolbar.reset": "⤢ I-reset ang pagtan-aw",
    "toolbar.settings": "⚙ Mga setting",
    "details.header": "Itugyan ang mga Detalye",
    "details.close": "Duol",
    "details.sha": "SHA",
    "details.shortSha": "Mubo nga SHA",
    "details.message": "Mensahe",
    "details.author": "Awtor",
    "details.date": "Petsa",
    "details.labels": "Mga label",
    "details.location": "Lokasyon",
    "details.currentHead": "HEAD - kasamtangan nga pag-checkout",
    "settings.title": "Mga setting",
    "settings.language": "Pinulongan",
    "settings.theme": "Tema",
    "settings.themeLight": "Kahayag",
    "settings.themeDark": "Ngitngit",
    "settings.themeLightHint": "Kahayag nga background, ngitngit nga teksto.",
    "settings.themeDarkHint": "Mangitngit nga background, gaan nga teksto.",
    "settings.mainBranch": "Panguna nga sanga",
    "settings.mainBranchAuto": "Awtomatiko",
    "settings.mainBranchSearch": "Pangitaa ang mga sanga…",
    "settings.mainBranchNoMatch": "Walay katugbang nga mga sanga",
    "settings.display": "Estilo sa pagpakita",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Klasiko",
    "settings.displayModernHint": "Libre nga canvas — drag to pan, scroll to zoom.",
    "settings.displayClassicHint": "Fixed canvas — trunk pinned left, no zoom, scroll only (sama sa SVN revision graph).",
    "settings.sectionGeneral": "Heneral",
    "settings.sectionGraph": "Graph",
    "settings.sectionChanges": "Mga pagbag-o sa pagtan-aw",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Gipakita",
    "settings.diffMinimapOff": "Gitagoan",
    "settings.diffMinimapOnHint": "Ipakita ang usa ka VS Code-style nga overview strip sa tupad sa diff - i-drag kini aron mag-scroll; gilakip ang mga marka sa pagbag-o.",
    "settings.diffMinimapOffHint": "Walay overview strip — scroll sa diff normally.",
    "settings.commitReview": "I-commit ang pagrepaso",
    "settings.commitReviewOn": "Pangutan-a before commit",
    "settings.commitReviewOff": "Direkta nga pasalig",
    "settings.commitReviewOnHint": "Ipakita ang katapusang lista sa file ug pagsusi sa mensahe sa dili pa maghimo sa lokal nga pasalig.",
    "settings.commitReviewOffHint": "Ang Commit nga buton nagmugna dayon sa lokal nga pasalig.",
    "settings.svnBranchDialog": "SVN-style nga branch dialog",
    "settings.svnBranchDialogHint": "Ipakita ang usa ka folder-tree picker kung maghimo usa ka sanga (imbes usa ka yano nga pag-aghat).",
    "settings.branchDialog": "Diyalogo sa sanga",
    "settings.branchDialogSvnTitle": "SVN-estilo",
    "settings.branchDialogNativeVscode": "Estilo sa VS Code",
    "settings.branchDialogNativeVs": "Estilo sa Visual Studio",
    "settings.branchDialogNativeHint": "Ang built-in nga branch prompt sa imong IDE.",
    "settings.close": "Duol",
    "settings.done": "Nahuman na",
    "settings.sectionAdvanced": "Abante",
    "settings.gitSource": "Git executable",
    "settings.gitSourceBuiltin": "Gitukod-sa",
    "settings.gitSourceBuiltinHint": "Gamita ang git binary gikan sa built-in nga Git extension sa IDE.",
    "settings.gitSourceCustom": "Pasadya nga agianan",
    "settings.gitPath": "Paagi sa git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Pag-browse…",
    "settings.jargon": "Git nga mga termino",
    "settings.jargonTranslate": "Hubad",
    "settings.jargonEnglish": "Padayon sa English",
    "settings.jargonTranslateHint": "Paghubad sa mga termino sa Git (pagbira, pagduso, pagpasalig, sanga…) ngadto sa pinulongan sa interface.",
    "settings.jargonEnglishHint": "Ipadayon ang mga termino sa Git (pagbira, pagduso, pagpasalig, sanga…) sa English; hubaron ang tanan nga uban pa.",
    "settings.sectionMerge": "Paghiusa",
    "settings.mergeMode": "Paghiusa sa estilo",
    "settings.mergeModeMerge": "Normal nga paghiusa",
    "settings.mergeModeSquash": "Paghiusa sa kalabasa",
    "settings.mergeModeMergeHint": "Gihiusa ang sanga sa kung unsa kini: ang mga pasalig niini nahimo nga bahin sa karon nga kasaysayan sa sanga, giubanan sa usa ka merge commit (o paspas nga gipasa kung mahimo).",
    "settings.mergeModeSquashHint": "Gi-collapse ang tibuok branch ngadto sa usa ka commit sa kasamtangan nga branch (git merge --squash). Ang kaugalingong mga commit sa branch dili gayud makasulod sa kasaysayan sa kasamtangan nga branch — usa ra ang imong makita didto. Ang sanga mismo gibiyaan nga wala matandog, mao nga ang mga commit niini magpabilin nga makita sa kaugalingon nga agianan.",
    "settings.mergedView": "Gihiusa nga mga pasalig sa graph",
    "settings.mergedViewBranch": "Sa ilang kaugalingong branch ra",
    "settings.mergedViewTarget": "Sa sanga usab sila gihiusa",
    "settings.mergedViewBranchHint": "Ang mga commit sa usa ka gihiusa nga sanga gikuha lamang sa ilang kaugalingon nga agianan; ang paghiusa nagpakita ingon usa ka konektor. Ang klasiko nga pagtan-aw.",
    "settings.mergedViewTargetHint": "Ang mga commit sa usa ka merge nga gidala gi-drawing usab sa nakadawat nga branch sa lane, gipatong-patong ubos sa merge commit ug ni-level sa mga orihinal — tungod kay kana nga branch naglangkob na niini karon. Ang mga kopya luspad ug ang ulohan sa sanga nga gisulatan niini, mao nga klaro nga wala kini gihimo didto. Ang mga pagsagol sa kalabasa dili maapektuhan: ang usa ka kalabasa nagsulat ug usa ka ordinaryo nga pasalig, mao nga kana ra nga pasalig ang makita.",
    "legend.title": "leyenda",
    "legend.head": "HEAD / kasamtangan nga sanga",
    "legend.local": "Lokal nga sanga",
    "legend.remote": "Layo nga sanga",
    "legend.remoteOnly": "Diha ra sa panganod (wala gibira)",
    "legend.tag": "Tag (bersyon)",
    "legend.commit": "Ipasalig",
    "legend.stash": "Stash (gibutang nga trabaho)",
    "legend.mergedIn": "Misulod uban ang usa ka merge (gisulat sa laing branch)",
    "legend.nodes": "Mga kahon",
    "legend.lines": "Mga linya",
    "legend.edgeParent": "Ang miaging commit sa parehas nga linya",
    "legend.edgeMerge": "Merged-in branch — ang mga udyong nagpunting sa merge commit",
    "legend.edgeBranch": "Usa ka sanga nga nagsanga dinhi",
    "legend.edgeStash": "Stash ug ang commit nga gi-estante niini",
    "legend.edgeMergedChain": "Ang linya sa kini nga sanga nga nagdagan pinaagi sa merge-in commit",
    "legend.edgeMergedTie": "Ang sama nga pasalig sa sanga nga gisulatan niini",
    "menu.jumpHead": "⌖ Adto sa checkout",
    "menu.resetView": "⤢ I-reset ang pagtan-aw",
    "menu.createBranch": "Paghimo sang sanga gikan dinhi...",
    "menu.checkout": "Susiha kini nga pasalig",
    "menu.copySha": "Kopyaha ang commit SHA",
    "menu.pushBranch": "Iduso ang sanga \"{name}\"",
    "menu.renameCommit": "Usba ang ngalan sa commit nga mensahe...",
    "menu.undoCommit": "I-undo ang commit (ipadayon ang mga pagbag-o)…",
    "menu.viewChanges": "Tan-awa ang mga pagbag-o…",
    "menu.jumpToOriginal": "Lakaw ngadto sa orihinal nga pasalig",
    "changes.title": "Mga pagbag-o sa {sha}",
    "changes.tabChanged": "Nausab",
    "changes.tabAll": "Tanan nga mga File",
    "changes.added": "Gidugang",
    "changes.modified": "Giusab",
    "changes.deleted": "Gipapas",
    "changes.renamed": "Giusab ang ngalan",
    "changes.noChanges": "Kini nga pasalig wala magbag-o sa mga file.",
    "changes.loading": "Nagkalainlain ang pagkarga…",
    "changes.selectFile": "Pagpili usa ka file aron makita ang mga pagbag-o niini.",
    "changes.original": "Orihinal",
    "changes.changed": "Kini nga pasalig",
    "changes.binary": "Binary file — walay text diff nga ipakita.",
    "changes.tooLarge": "Dagko kaayo ang file para magkalahi.",
    "changes.renamedFrom": "Giusab ang ngalan gikan sa {path}",
    "changes.close": "Duol",
    "changes.prevChange": "Kaniadto nga pagbag-o",
    "changes.nextChange": "Sunod nga pagbag-o",
    "changes.collapseAll": "I-collapse ang tanang folder",
    "changes.expandAll": "Pagpalapad sa tanan nga mga folder",
    "changes.searchPlaceholder": "Pangitaa ang mga file…",
    "changes.noSearchResults": "Walay mga file nga motakdo sa imong pagpangita.",
    "changes.maximize": "I-maximize",
    "changes.restore": "Iuli ang gidak-on",
    "find.open": "Pangitaa niining diff (Ctrl+F)",
    "find.placeholder": "Pangitaa sa diff…",
    "find.prev": "Miagi nga duwa",
    "find.next": "Sunod nga duwa",
    "find.close": "Isira ang pagpangita",
    "find.noResults": "Walay resulta",
    "commit.title": "Paghimo ug mga Pagbag-o",
    "commit.close": "Duol",
    "commit.loading": "Pagbasa sa working tree…",
    "commit.noChanges": "Wala’y lokal nga pagbag-o nga himuon.",
    "commit.selectFile": "Pagpili og file aron makita ang diff niini.",
    "commit.selectAll": "Pilia ang tanan",
    "commit.selectNone": "Klaro tanan",
    "commit.message": "Ihatag ang mensahe",
    "commit.messagePlaceholder": "Ihulagway ang kausaban",
    "commit.selectedCount": "{count} file(s) gipili",
    "commit.review": "Pagrepaso",
    "commit.reviewTitle": "Katapusan nga pagsusi sa dili pa mokomiti",
    "commit.reviewSummary": "I-commit ang (mga) file sa {count} nga adunay mensahe: {message}",
    "commit.back": "Balik",
    "commit.commit": "Paghimo lokal nga pasalig",
    "menu.stashApply": "Ibutang ang stash",
    "menu.stashPop": "Pop stash (i-apply ug kuhaa)",
    "menu.stashDrop": "Ihulog ang itago…",
    "merge.title": "Paghiusa sa Sanga",
    "merge.route": "Paghiusa sa kasamtangan nga sanga",
    "merge.source": "Gikan sa (tinubdan)",
    "merge.target": "Sa (kasamtangan)",
    "merge.loading": "Pag-analisar sa paghiusa…",
    "merge.upToDate": "Naa na sa petsa — wala’y isagol.",
    "merge.fastForward": "Fast-forward — walay merge commit nga gikinahanglan (i-tsek ang “Create a merge commit” para mapugos ang usa).",
    "merge.summary": "{files} file(s) nausab, {conflicts} conflict(s).",
    "merge.conflictsWarning": "Kini nga panagsama adunay mga panagbangi — mahimo ka gihapon nga maghiusa, unya masulbad kini sa editor.",
    "merge.noChanges": "Walay mga pagbag-o sa file.",
    "merge.files": "Resulta nga mga pagbag-o",
    "merge.added": "Gidugang",
    "merge.modified": "Giusab",
    "merge.deleted": "Gipapas",
    "merge.conflict": "Panagbangi",
    "merge.message": "Paghiusa sa commit nga mensahe",
    "merge.messagePlaceholder": "Isagol ang 'tinubdan' sa sanga",
    "merge.messageFfHint": "Gibalewala sa usa ka paspas nga pagpasa sa paghiusa (walay pasalig nga gihimo).",
    "merge.noFastForward": "Paghimo og merge commit (walay fast-forward)",
    "merge.noFastForwardHint": "Kanunay nga irekord ang usa ka merge commit, bisan kung ang branch maka-fast-forward.",
    "merge.merge": "Paghiusa",
    "merge.cancel": "Pagkanselar",
    "merge.previewError": "Dili makwenta ang preview: {message}",
    "merge.squashBadge": "Kalabasa",
    "merge.squashNote": "Paghiusa sa kalabasa — matag pagbag-o moabut samtang ang ONE mipasalig sa \"{target}\"; Ang kaugalingon nga mga pasalig sa \"{source}\" wala sa kasaysayan niini. Balik sa Mga Setting > Paghiusa.",
    "merge.messageSquash": "Ihatag ang mensahe",
    "merge.squashMessagePlaceholder": "Giputol nga mga pagbag-o gikan sa 'tinubdan'",
    "newBranch.title": "Paghimo Branch",
    "newBranch.startPoint": "Bag-ong sanga sugod sa {sha}",
    "newBranch.startPointOn": "sa {refs}",
    "newBranch.location": "Lokasyon",
    "newBranch.locationRoot": "(gamut)",
    "newBranch.expandAll": "Palapad ang tanan",
    "newBranch.collapseAll": "Collapse tanan",
    "newBranch.expandFolder": "Ipalapad ang folder",
    "newBranch.collapseFolder": "I-collapse ang folder",
    "newBranch.name": "Ngalan sa sanga",
    "newBranch.namePlaceholder": "akong-sanga",
    "newBranch.fullName": "Tibuok nga ngalan: {name}",
    "newBranch.invalid": "Dili balido nga ngalan sa sanga",
    "newBranch.exists": "Usa ka sanga nga adunay kini nga ngalan naglungtad na",
    "newBranch.checkout": "Pag-checkout nga sanga pagkahuman sa paghimo",
    "newBranch.create": "Paghimo",
    "newBranch.cancel": "Pagkanselar",
    "status.loading": "Nagkarga sa graph…",
    "status.summary": "{repo}Pagpakita sa {commits} commits, {refs} refs",
    "status.branchCreated": "Naghimo sang sanga nga \"{name}\" sa {sha}",
    "status.error": "Sayop: {message}",
    "status.opFailed": "Napakyas ang operasyon",
    "status.fetching": "Gikuha…",
    "status.pulling": "Gibira…",
    "status.pushing": "Nagduso…",
    "status.committing": "Paghimo sa lokal nga pasalig…",
    "status.commitCreated": "Gihimo ang lokal nga komit {sha}: {message}",
    "status.syncing": "Nag-sync…",
    "status.noHead": "Walay kasamtangan nga checkout nga makita sa graph.",
    "status.undoing": "Pagtangtang sa pasalig…",
    "status.commitUndone": "I-commit undone - ang mga pagbag-o nabalik sa nagtrabaho nga kahoy.",
    "status.undoConflict": "I-undo ang mga panagbangi sa hit — sulbaron kini sa editor, dayon ipadayon.",
    "status.stashApplying": "Nag-apply sa stash…",
    "status.stashPopping": "Nag-pop stash…",
    "status.stashDropping": "Naghulog sa stash…",
    "status.stashApplied": "Gi-apply ang stash.",
    "status.stashPopped": "Nigawas ang stash.",
    "status.stashDropped": "Nahulog ang stash.",
    "status.stashConflict": "Mga panagbangi sa pagtago - sulbad kini sa editor.",
    "status.merging": "Naghiusa…",
    "status.merged": "Nakompleto ang paghiusa.",
    "status.mergeConflict": "Ang paghiusa adunay mga panagbangi - sulbaron kini sa editor, dayon i-commit.",
    "search.title": "Pangitaa",
    "search.tooltip": "Ang pagpangita mipasalig",
    "search.placeholder": "Ang pagpangita mipasalig…",
    "search.previous": "Nauna nga resulta",
    "search.next": "Sunod nga resulta",
    "search.noResults": "Walay resulta",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filter mode",
    "search.highlight": "Highlight mode",
    "search.filterMode": "Display sa pagpangita",
    "search.filterModeFilter": "Filter (itago ang dili mga tugma)",
    "search.filterModeHighlight": "Highlight (ipakita ang tanan, markahan ang mga posporo)",
    "search.filterModeHint": "Pilia kung giunsa ang mga resulta sa pagpangita gipakita sa graph.",
    "search.visibility": "Pangitaa ang lokasyon",
    "search.visibilityToolbar": "Butang sa toolbar",
    "search.visibilityAlwaysVisible": "Kanunay nga makita nga bar",
    "search.visibilityHint": "Ipakita ang pagpangita isip icon sa toolbar o padayon nga bar sa ibabaw.",
    "footer.github": "GitHub",
  },
  ny: {
    "toolbar.refresh": "⟳ Onjezani",
    "toolbar.fetch": "⤓ Kutenga",
    "toolbar.pull": "⇩ Kokani",
    "toolbar.push": "⇧ Kankhani",
    "toolbar.commit": "✓ Kudzipereka",
    "toolbar.sync": "⇅ Kulunzanitsa",
    "toolbar.jumpHead": "⌖ Pitani kumalipiro",
    "toolbar.reset": "⤢ Bwezeretsani mawonekedwe",
    "toolbar.settings": "⚙ Zokonda",
    "details.header": "Perekani Tsatanetsatane",
    "details.close": "Tsekani",
    "details.sha": "SHA",
    "details.shortSha": "Short SHA",
    "details.message": "Uthenga",
    "details.author": "Wolemba",
    "details.date": "Tsiku",
    "details.labels": "Zolemba",
    "details.location": "Malo",
    "details.currentHead": "HEAD - potuluka pano",
    "settings.title": "Zokonda",
    "settings.language": "Chiyankhulo",
    "settings.theme": "Mutu",
    "settings.themeLight": "Kuwala",
    "settings.themeDark": "Chakuda",
    "settings.themeLightHint": "Kumbuyo kowala, mawu akuda.",
    "settings.themeDarkHint": "Kumbuyo kwakuda, mawu opepuka.",
    "settings.mainBranch": "Nthambi yayikulu",
    "settings.mainBranchAuto": "Zadzidzidzi",
    "settings.mainBranchSearch": "Sakani nthambi...",
    "settings.mainBranchNoMatch": "Palibe nthambi zofananira",
    "settings.display": "Mawonekedwe kalembedwe",
    "settings.displayModern": "Zamakono",
    "settings.displayClassic": "Zakale",
    "settings.displayModernHint": "Chinsalu chaulere - kokerani ku poto, pukutani kuti muwoneke.",
    "settings.displayClassicHint": "Chinsalu chokhazikika - thunthu lopanikizidwa kumanzere, palibe makulitsidwe, pukutani kokha (monga graph yokonzanso ya SVN).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Chithunzi",
    "settings.sectionChanges": "Kusintha mawonekedwe",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Zowonetsedwa",
    "settings.diffMinimapOff": "Zobisika",
    "settings.diffMinimapOnHint": "Onetsani chithunzithunzi cha mawonekedwe a VS Code pafupi ndi diff - kokerani kuti musunthe; zolembera zosintha zikuphatikizidwa.",
    "settings.diffMinimapOffHint": "Palibe chidule chachidule - sindikizani kusiyana moyenera.",
    "settings.commitReview": "Perekani ndemanga",
    "settings.commitReviewOn": "Funsani musanapereke",
    "settings.commitReviewOff": "Dziperekeni mwachindunji",
    "settings.commitReviewOnHint": "Onetsani mndandanda wamafayilo omaliza ndikuwona uthenga musanapange zomwe zaperekedwa kwanuko.",
    "settings.commitReviewOffHint": "Batani la Commit limapanga chopereka chapafupi nthawi yomweyo.",
    "settings.svnBranchDialog": "Nkhani ya nthambi yamtundu wa SVN",
    "settings.svnBranchDialogHint": "Onetsani chosankha chikwatu popanga nthambi (m'malo mwachidziwitso chosavuta).",
    "settings.branchDialog": "Nkhani ya nthambi",
    "settings.branchDialogSvnTitle": "Mtundu wa SVN",
    "settings.branchDialogNativeVscode": "VS Code style",
    "settings.branchDialogNativeVs": "Mtundu wa Visual Studio",
    "settings.branchDialogNativeHint": "IDE yanu yomangidwira munthambi mwamsanga.",
    "settings.close": "Tsekani",
    "settings.done": "Zatheka",
    "settings.sectionAdvanced": "Zapamwamba",
    "settings.gitSource": "Git akhoza kuchitidwa",
    "settings.gitSourceBuiltin": "Zomangidwa mkati",
    "settings.gitSourceBuiltinHint": "Gwiritsani ntchito git binary kuchokera ku IDE yomangidwa mu Git yowonjezera.",
    "settings.gitSourceCustom": "Njira yokhazikika",
    "settings.gitPath": "Njira yopita ku git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Sakatulani…",
    "settings.jargon": "Mawu a Git",
    "settings.jargonTranslate": "Tanthauzirani",
    "settings.jargonEnglish": "Sungani mu Chingerezi",
    "settings.jargonTranslateHint": "Tanthauzirani mawu a Git (kukoka, kukankha, perekani, nthambi…) muchilankhulo cholumikizira.",
    "settings.jargonEnglishHint": "Sungani mawu a Git (kukoka, kukankha, kudzipereka, nthambi…) mu Chingerezi; kumasulira china chirichonse.",
    "settings.sectionMerge": "Gwirizanitsani",
    "settings.mergeMode": "Kuphatikiza masitayilo",
    "settings.mergeModeMerge": "Kuphatikizika kwachibadwa",
    "settings.mergeModeSquash": "squash kuphatikiza",
    "settings.mergeModeMergeHint": "Imaphatikiza nthambi momwe ilili: zochita zake zimakhala gawo la mbiri yakale yanthambi, yophatikizidwa ndi mgwirizano wophatikizika (kapena kutumiza mwachangu ngati kuli kotheka).",
    "settings.mergeModeSquashHint": "Imagwetsa nthambi yonse kukhala chinthu chimodzi panthambi yomwe ilipo (git merge --squash). Zomwe nthambi zake zimachita sizimalowa m'mbiri ya nthambi yapano - mumangowona kudzipereka komweko. Nthambi yokhayo imasiyidwa yosakhudzidwa, kotero kuti zochita zake zimakhala zowonekera m'njira yakeyake.",
    "settings.mergedView": "Zochita zophatikizidwa mu graph",
    "settings.mergedViewBranch": "Panthambi yawo yokha",
    "settings.mergedViewTarget": "Komanso mu nthambi iwo anaphatikizidwa",
    "settings.mergedViewBranchHint": "Zochita za nthambi zophatikizidwa zimakokedwa m'njira zawo zokha; kuphatikiza kumawonekera ngati cholumikizira. The classic view.",
    "settings.mergedViewTargetHint": "Zophatikiza zomwe zabweretsedwa zimakokedwanso mumsewu wa nthambi yolandirira, zoyikidwa pansi pa mgwirizano wophatikizika ndikufanana ndi zoyambirira - chifukwa nthambiyo ili nazo tsopano. Makopewo ndi otumbululuka ndipo amatsogozedwa ndi nthambi yomwe adalembedwapo, kotero zikuwonekeratu kuti sanapangidwe pamenepo. Kuphatikizika kwa squash sikukhudzidwa: sikwashi imalemba chinthu chimodzi wamba, ndiye kudzipereka kokhako kumawonekera.",
    "legend.title": "Nthano",
    "legend.head": "HEAD / nthambi yapano",
    "legend.local": "Nthambi ya m’deralo",
    "legend.remote": "Nthambi yakutali",
    "legend.remoteOnly": "Mumtambo mokha (osakokedwa)",
    "legend.tag": "Tag (mtundu)",
    "legend.commit": "Kudzipereka",
    "legend.stash": "Stash (ntchito yosungidwa)",
    "legend.mergedIn": "Analowa ndi kuphatikiza (olembedwa pa nthambi ina)",
    "legend.nodes": "Mabokosi",
    "legend.lines": "Mizere",
    "legend.edgeParent": "Zopereka zam'mbuyomu pamzere womwewo",
    "legend.edgeMerge": "Nthambi yophatikizika - mivi yoloza pa mgwirizano",
    "legend.edgeBranch": "Nthambi ina yatsamira apa",
    "legend.edgeStash": "Stash ndi kudzipereka kwake kudayimitsidwa",
    "legend.edgeMergedChain": "Mzere wa nthambi iyi ukudutsa muzochita zophatikizidwa",
    "legend.edgeMergedTie": "Kudzipereka komweko panthambi komwe kunalembedwapo",
    "menu.jumpHead": "⌖ Pitani kumalipiro",
    "menu.resetView": "⤢ Bwezeretsani mawonekedwe",
    "menu.createBranch": "Pangani nthambi kuchokera apa...",
    "menu.checkout": "Tsatirani izi",
    "menu.copySha": "Koperani SHA",
    "menu.pushBranch": "Kankhani nthambi \"{name}\"",
    "menu.renameCommit": "Tchulani uthenga wa kutumiza...",
    "menu.undoCommit": "Bwezerani zomwe mukufuna (sungani zosintha)…",
    "menu.viewChanges": "Onani zosintha...",
    "menu.jumpToOriginal": "Pitani ku gawo loyamba",
    "changes.title": "Zithunzi za XPH0X",
    "changes.tabChanged": "Zasinthidwa",
    "changes.tabAll": "Mafayilo Onse",
    "changes.added": "Zowonjezedwa",
    "changes.modified": "Zosinthidwa",
    "changes.deleted": "Zachotsedwa",
    "changes.renamed": "Adasinthidwa dzina",
    "changes.noChanges": "Izi sizisintha mafayilo.",
    "changes.loading": "Kutsegula kusiyana...",
    "changes.selectFile": "Sankhani fayilo kuti muwone zosintha zake.",
    "changes.original": "Choyambirira",
    "changes.changed": "Kudzipereka uku",
    "changes.binary": "Fayilo ya binary - palibe mawu omwe angasonyezedwe.",
    "changes.tooLarge": "Fayilo ndi yayikulu kwambiri kuti isasiyanitsidwe.",
    "changes.renamedFrom": "Kusintha kwa mtengo wa {path}",
    "changes.close": "Tsekani",
    "changes.prevChange": "Kusintha kwam'mbuyo",
    "changes.nextChange": "Kusintha kotsatira",
    "changes.collapseAll": "Gonjetsani zikwatu zonse",
    "changes.expandAll": "Wonjezerani zikwatu zonse",
    "changes.searchPlaceholder": "Sakani mafayilo…",
    "changes.noSearchResults": "Palibe mafayilo ofanana ndi zomwe mwasaka.",
    "changes.maximize": "Onetsani",
    "changes.restore": "Bwezerani kukula",
    "find.open": "Sakani mu diff iyi (Ctrl+F)",
    "find.placeholder": "Pezani mosiyana…",
    "find.prev": "Masewera am'mbuyomu",
    "find.next": "Machesi otsatira",
    "find.close": "Tsekani kufufuza",
    "find.noResults": "Palibe zotsatira",
    "commit.title": "Chitani Zosintha",
    "commit.close": "Tsekani",
    "commit.loading": "Kuwerenga mtengo wogwira ntchito…",
    "commit.noChanges": "Palibe zosintha zakomweko zomwe mungachite.",
    "commit.selectFile": "Sankhani fayilo kuti muwone kusiyana kwake.",
    "commit.selectAll": "Sankhani zonse",
    "commit.selectNone": "Chotsani zonse",
    "commit.message": "Tumizani uthenga",
    "commit.messagePlaceholder": "Fotokozani kusintha",
    "commit.selectedCount": "{count} mafayilo osankhidwa",
    "commit.review": "Ndemanga",
    "commit.reviewTitle": "Cheke chomaliza musanapereke",
    "commit.reviewSummary": "Perekani mafayilo a {count} okhala ndi uthenga: {message}",
    "commit.back": "Kubwerera",
    "commit.commit": "Pangani zochita kwanuko",
    "menu.stashApply": "Ikani stash",
    "menu.stashPop": "Pop stash (ikani & chotsani)",
    "menu.stashDrop": "Ikani zobisika…",
    "merge.title": "Gwirizanitsani Nthambi",
    "merge.route": "Kulumikizana mu nthambi yakhayo",
    "merge.source": "Kuchokera (gwero)",
    "merge.target": "Ku (pano)",
    "merge.loading": "Kusanthula kuphatikiza...",
    "merge.upToDate": "Kale kwa tsiku - palibe kuphatikiza.",
    "merge.fastForward": "Kupita patsogolo - palibe kuphatikiza kofunikira (chongani \"Pangani mgwirizano\" kuti muumirize chimodzi).",
    "merge.summary": "Mafayilo a {files} asinthidwa, mikangano ya {conflicts}.",
    "merge.conflictsWarning": "Kuphatikiza uku kuli ndi mikangano - mutha kuphatikiza, kenako ndikuthetsa mu mkonzi.",
    "merge.noChanges": "Palibe kusintha kwamafayilo.",
    "merge.files": "Zotsatira zake zasintha",
    "merge.added": "Zowonjezedwa",
    "merge.modified": "Zosinthidwa",
    "merge.deleted": "Zachotsedwa",
    "merge.conflict": "Kusamvana",
    "merge.message": "Gwirizanitsani uthenga",
    "merge.messagePlaceholder": "Phatikizani nthambi 'source'",
    "merge.messageFfHint": "Kunyalanyazidwa pakuphatikizana kopita patsogolo (palibe kudzipereka komwe kumapangidwa).",
    "merge.noFastForward": "Pangani mgwirizano wophatikizira (popanda kupita patsogolo)",
    "merge.noFastForwardHint": "Nthawi zonse lembani mgwirizano wophatikizira, ngakhale nthambi ikatha kupita patsogolo.",
    "merge.merge": "Gwirizanitsani",
    "merge.cancel": "Letsani",
    "merge.previewError": "Sindinathe kuwerengera zowonera: {message}",
    "merge.squashBadge": "Sikwashi",
    "merge.squashNote": "Kuphatikizika kwa squash - kusintha kulikonse kumafika ngati MMODZI adzipereka pa \"{target}\"; \"{source}\"'s mwiniwake sakhala m'mbiri yake. Sinthaninso mu Zikhazikiko › Gwirizanitsani.",
    "merge.messageSquash": "Tumizani uthenga",
    "merge.squashMessagePlaceholder": "Kusintha kophwanyidwa kuchokera ku 'source'",
    "newBranch.title": "Pangani Nthambi",
    "newBranch.startPoint": "Nthambi yatsopano yoyambira {sha}",
    "newBranch.startPointOn": "ku XPH0X",
    "newBranch.location": "Malo",
    "newBranch.locationRoot": "(muzu)",
    "newBranch.expandAll": "Wonjezerani zonse",
    "newBranch.collapseAll": "Gwirani zonse",
    "newBranch.expandFolder": "Wonjezerani chikwatu",
    "newBranch.collapseFolder": "Gonjetsani chikwatu",
    "newBranch.name": "Dzina la nthambi",
    "newBranch.namePlaceholder": "wanga-nthambi",
    "newBranch.fullName": "Dzina lonse: {name}",
    "newBranch.invalid": "Dzina lanthambi lolakwika",
    "newBranch.exists": "Nthambi yokhala ndi dzinali ilipo kale",
    "newBranch.checkout": "Checkout nthambi pambuyo kulenga",
    "newBranch.create": "Pangani",
    "newBranch.cancel": "Letsani",
    "status.loading": "Tikutsegula chithunzi…",
    "status.summary": "{repo}Showing {commits} amachita, {refs} refs",
    "status.branchCreated": "Adapanga nthambi \"{name}\" ku {sha}",
    "status.error": "Cholakwika: {message}",
    "status.opFailed": "Ntchito yalephera",
    "status.fetching": "Kutenga…",
    "status.pulling": "Kukoka...",
    "status.pushing": "Kukankha…",
    "status.committing": "Kupanga kudzipereka kwanuko…",
    "status.commitCreated": "Adapanga kudzipereka kwanuko {sha}: {message}",
    "status.syncing": "Kuyanjanitsa...",
    "status.noHead": "Palibe chotuluka pano chomwe chapezeka pa graph.",
    "status.undoing": "Kuthetsa kudzipereka…",
    "status.commitUndone": "Kudzipereka kwasinthidwa - zosintha zabwerera mumtengo wogwira ntchito.",
    "status.undoConflict": "Bwezerani mikangano yomwe idagunda - zithetseni mu mkonzi, kenako pitilizani.",
    "status.stashApplying": "Kuyika stash…",
    "status.stashPopping": "Kutsegula stash…",
    "status.stashDropping": "Kuponya stash…",
    "status.stashApplied": "Stash ikugwiritsidwa ntchito.",
    "status.stashPopped": "Stash yatulukira.",
    "status.stashDropped": "Stash wagwa.",
    "status.stashConflict": "Mikangano ya Stash - ithetseni mu mkonzi.",
    "status.merging": "Kuphatikiza…",
    "status.merged": "Kuphatikiza kwatha.",
    "status.mergeConflict": "Kuphatikiza kuli ndi mikangano - thetsani mkonzi, kenako perekani.",
    "search.title": "Sakani",
    "search.tooltip": "Kufufuza kumakwaniritsa",
    "search.placeholder": "Kusaka kumachita…",
    "search.previous": "Zotsatira zam'mbuyo",
    "search.next": "Chotsatira chotsatira",
    "search.noResults": "Palibe zotsatira",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Zosefera",
    "search.highlight": "Onetsani mawonekedwe",
    "search.filterMode": "Sakani chiwonetsero",
    "search.filterModeFilter": "Sefa (bisani zosagwirizana)",
    "search.filterModeHighlight": "Onetsani (onetsani zonse, lembani machesi)",
    "search.filterModeHint": "Sankhani momwe zotsatira zosaka zimawonekera pazithunzi.",
    "search.visibility": "Sakani malo",
    "search.visibilityToolbar": "Toolbar batani",
    "search.visibilityAlwaysVisible": "Bar yowoneka nthawi zonse",
    "search.visibilityHint": "Onetsani kusaka ngati chizindikiro chazida kapena kapamwamba kopitilira pamwamba.",
    "footer.github": "GitHub",
  },
  sn: {
    "toolbar.refresh": "⟳ Zorora",
    "toolbar.fetch": "⤓ Tora",
    "toolbar.pull": "⇩ Dhonza",
    "toolbar.push": "⇧ Sunda",
    "toolbar.commit": "✓ Zvipire",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Enda kunobhadhara",
    "toolbar.reset": "⤢ Setazve maonero",
    "toolbar.settings": "⚙ Zvirongwa",
    "details.header": "Commit Details",
    "details.close": "Close",
    "details.sha": "SHA",
    "details.shortSha": "Pfupi SHA",
    "details.message": "Mharidzo",
    "details.author": "Munyori",
    "details.date": "Date",
    "details.labels": "Labels",
    "details.location": "Nzvimbo",
    "details.currentHead": "HEAD - kubuda ikozvino",
    "settings.title": "Settings",
    "settings.language": "Mutauro",
    "settings.theme": "Theme",
    "settings.themeLight": "Chiedza",
    "settings.themeDark": "Rima",
    "settings.themeLightHint": "Chiedza chemashure, mavara akasviba.",
    "settings.themeDarkHint": "Kumashure kwakasviba, mavara akareruka.",
    "settings.mainBranch": "Main bazi",
    "settings.mainBranchAuto": "Automatic",
    "settings.mainBranchSearch": "Tsvaga mapazi...",
    "settings.mainBranchNoMatch": "Hapana mapazi anoenderana",
    "settings.display": "Ratidza maitiro",
    "settings.displayModern": "Mazuvano",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Canvas yemahara - dhonza kune pani, skrota kuti uwedzere.",
    "settings.displayClassicHint": "Yakagadziriswa canvas - trunk yakanamirwa kuruboshwe, hapana zoom, skrota chete (senge SVN revision graph).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Grafu",
    "settings.sectionChanges": "Anochinja maonero",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Ratidzwa",
    "settings.diffMinimapOff": "Hidden",
    "settings.diffMinimapOnHint": "Ratidza VS Code-style overview strip padivi pe diff - dhonza kuti upururudze; chinja mamakisi akabatanidzwa.",
    "settings.diffMinimapOffHint": "Hapana chidimbu chekutarisisa - penya mutsauko kazhinji.",
    "settings.commitReview": "Zvipire kuongorora",
    "settings.commitReviewOn": "Bvunza usati wazvipira",
    "settings.commitReviewOff": "Zvipire zvakananga",
    "settings.commitReviewOnHint": "Ratidza yekupedzisira faira runyorwa uye meseji cheki usati wagadzira yemuno kuzvipira.",
    "settings.commitReviewOffHint": "Bhatani reCommit rinogadzira chibvumirano chemuno nekukurumidza.",
    "settings.svnBranchDialog": "SVN-maitiro ebazi dialog",
    "settings.svnBranchDialogHint": "Ratidza chinhongi chemuti paunenge uchigadzira bazi (panzvimbo yekukurumidza kukurumidza).",
    "settings.branchDialog": "Nhaurirano yebazi",
    "settings.branchDialogSvnTitle": "SVN-maitiro",
    "settings.branchDialogNativeVscode": "VS Code maitiro",
    "settings.branchDialogNativeVs": "Visual Studio style",
    "settings.branchDialogNativeHint": "Yako IDE yakavakirwa-mukati bazi nekukurumidza.",
    "settings.close": "Close",
    "settings.done": "Ndapedza",
    "settings.sectionAdvanced": "Advanced",
    "settings.gitSource": "Git inoshandiswa",
    "settings.gitSourceBuiltin": "Yakavakwa-mukati",
    "settings.gitSourceBuiltinHint": "Shandisa iyo git binary kubva kuIDE yakavakirwa-mukati Git yekuwedzera.",
    "settings.gitSourceCustom": "Custom nzira",
    "settings.gitPath": "Nzira yekuenda kugit",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Bhurawuza...",
    "settings.jargon": "Git mazwi",
    "settings.jargonTranslate": "Shandura",
    "settings.jargonEnglish": "Ramba uri muchirungu",
    "settings.jargonTranslateHint": "Shandura mazwi eGit (dhonza, kusundidzira, zvipira, bazi…) mumutauro wekushandisa.",
    "settings.jargonEnglishHint": "Chengetedza mazwi eGit (dhonza, kusunda, kuzvipira, bazi…) muChirungu; shandura zvimwe zvese.",
    "settings.sectionMerge": "Merge",
    "settings.mergeMode": "Merge style",
    "settings.mergeModeMerge": "Normal kubatanidza",
    "settings.mergeModeSquash": "Squash kusanganisa",
    "settings.mergeModeMergeHint": "Inobatanidza bazi sezvariri: zviito zvaro zvinova chikamu chenhoroondo yebazi razvino, inobatanidzwa nekubatanidza kuzvipira (kana kukurumidza-kuendesa mberi kana zvichibvira).",
    "settings.mergeModeSquashHint": "Inopeta bazi rese kuita chinhu chimwe chete pabazi razvino (git merge --squash). Iro bazi rayo harimbofa rakapinda munhoroondo yebazi razvino - unongoona imwe chete ichiita ipapo. Bazi pacharo rinosiiwa risina kubatwa, saka madhiri aro anogara achionekwa munzira yaro.",
    "settings.mergedView": "Akabatanidzwa anoita mugirafu",
    "settings.mergedViewBranch": "Pabazi ravo chete",
    "settings.mergedViewTarget": "Uyewo mubazi vakabatanidzwa",
    "settings.mergedViewBranchHint": "Mabazi akabatanidzwa anodhirowa chete munzira yavo; kusangana kunoratidza sechibatanidza. Maonero echinyakare.",
    "settings.mergedViewTargetHint": "Izvo zvakabatanidzwa zvakaunzwa zvinodhirowewa munzira yebazi rinogamuchira zvakare, zvakarongedzerwa pasi pekubatanidza chibvumirano uye nhanho neyekutanga - nekuti bazi iro rine izvo izvozvi. Makopi acho akacheneruka uye anotungamira nebazi raakanyorwa, saka zvinogara pachena kuti haana kugadzirwa ipapo. Squash inosanganiswa haina kukanganiswa: squash inonyora imwechete yakajairika, saka icho chete chibvumirano chinomboonekwa.",
    "legend.title": "Legend",
    "legend.head": "HEAD / bazi razvino",
    "legend.local": "Bazi romunzvimbomo",
    "legend.remote": "Remote bazi",
    "legend.remoteOnly": "Mugore chete (kwete kudhonzwa)",
    "legend.tag": "Tag (version)",
    "legend.commit": "Commit",
    "legend.stash": "Stash (basa rakavharwa)",
    "legend.mergedIn": "Yakapinda nekubatanidza (yakanyorwa pane rimwe bazi)",
    "legend.nodes": "Mabhokisi",
    "legend.lines": "Mitsetse",
    "legend.edgeParent": "Pamberi pekuita pamutsetse mumwe chete",
    "legend.edgeMerge": "Bazi rakabatanidzwa - museve unonongedza pakubatanidzwa",
    "legend.edgeBranch": "Bazi rakatemwa pano",
    "legend.edgeStash": "Stash uye kuzvipira kwayo kwakavharirwa",
    "legend.edgeMergedChain": "Mutsetse webazi uyu unofamba nepakati pezvakabatanidzwa-mukati",
    "legend.edgeMergedTie": "Kuzvipira kumwechete pabazi rakanyorwa",
    "menu.jumpHead": "⌖ Enda kunobhadhara",
    "menu.resetView": "⤢ Setazve maonero",
    "menu.createBranch": "Gadzira bazi kubva pano…",
    "menu.checkout": "Tarisa chibvumirano ichi",
    "menu.copySha": "Kopira kuita SHA",
    "menu.pushBranch": "Sunda bazi \"{name}\"",
    "menu.renameCommit": "Remesa zita rekutumira meseji…",
    "menu.undoCommit": "Dzora kuzvipira (chengetedza shanduko)…",
    "menu.viewChanges": "Ona shanduko...",
    "menu.jumpToOriginal": "Enda kune yekutanga kuzvipira",
    "node.mergedInTooltip": "Batanidza {merge} yakaunza chibvumirano ichi mu \"{target}\" — yakanyorwa pa \"{origin}\".",
    "changes.title": "Shanduko mu {sha}",
    "changes.tabChanged": "Changed",
    "changes.tabAll": "All Files",
    "changes.added": "Added",
    "changes.modified": "Modified",
    "changes.deleted": "Dzadzimwa",
    "changes.renamed": "Renamed",
    "changes.noChanges": "Ichi chibvumirano hachichinji mafaira.",
    "changes.loading": "Kurodha diff...",
    "changes.selectFile": "Sarudza faira kuti uone shanduko dzaro.",
    "changes.original": "Original",
    "changes.changed": "Kuzvipira uku",
    "changes.binary": "Binary faira - hapana mavara akasiyana ekuratidza.",
    "changes.tooLarge": "Faira yakakura kwazvo kuti isasiyana.",
    "changes.renamedFrom": "Yakatumidzwa kubva {path}",
    "changes.close": "Close",
    "changes.prevChange": "Shanduko yapfuura",
    "changes.nextChange": "Next shanduko",
    "changes.collapseAll": "Koroka maforodha ese",
    "changes.expandAll": "Wedzera maforodha ese",
    "changes.searchPlaceholder": "Tsvaga mafaera...",
    "changes.noSearchResults": "Hapana mafaira anoenderana netsvakiridzo yako.",
    "changes.maximize": "Maximize",
    "changes.restore": "Dzorera saizi",
    "find.open": "Tsvaga mune iyi diff (Ctrl+F)",
    "find.placeholder": "Tsvaga mune zvakasiyana…",
    "find.prev": "Previous match",
    "find.next": "Next match",
    "find.close": "Vhara kutsvaga",
    "find.noResults": "Hapana mhinduro",
    "commit.title": "Ita Zvinjo",
    "commit.close": "Close",
    "commit.loading": "Kuverenga muti unoshanda…",
    "commit.noChanges": "Iko hakuna shanduko yemunharaunda yekuita.",
    "commit.selectFile": "Sarudza faira kuti uone kusiyana kwayo.",
    "commit.selectAll": "Sarudza zvose",
    "commit.selectNone": "Bvisa zvese",
    "commit.message": "Isa meseji",
    "commit.messagePlaceholder": "Rondedzera shanduko",
    "commit.selectedCount": "{count} faira (s) yakasarudzwa",
    "commit.review": "Ongorora",
    "commit.reviewTitle": "Cheki yekupedzisira usati wazvipira",
    "commit.reviewSummary": "Isa {count} faira(s) ine meseji: {message}",
    "commit.back": "Back",
    "commit.commit": "Gadzira chibvumirano chemunharaunda",
    "menu.stashApply": "Isa stash",
    "menu.stashPop": "Pop stash (shandisa & bvisa)",
    "menu.stashDrop": "Donhedza stash…",
    "merge.title": "Merge Branch",
    "merge.route": "Kubatanidza mubazi razvino",
    "merge.source": "Kubva (kwakabva)",
    "merge.target": "Kupinda (ikozvino)",
    "merge.loading": "Kuongorora kusanganisa...",
    "merge.upToDate": "Yatove yazvino - hapana chekubatanidza.",
    "merge.fastForward": "Kurumidza-mberi - hapana kusanganisa kuita kunodiwa (tika \"Gadzira mubatanidzwa\" kumanikidza imwe).",
    "merge.summary": "{files} faira (s) yakashandurwa, {conflicts} kusawirirana (s).",
    "merge.conflictsWarning": "Uku kusanganisa kune makakatanwa - iwe unogona kusanganisa, wozoagadzirisa mupepeti.",
    "merge.noChanges": "Hapana shanduko yefaira.",
    "merge.files": "Zvichipa shanduko",
    "merge.added": "Added",
    "merge.modified": "Modified",
    "merge.deleted": "Dzadzimwa",
    "merge.conflict": "Kusawirirana",
    "merge.message": "Unganidza meseji yekutumira",
    "merge.messagePlaceholder": "Batanidza bazi 'source'",
    "merge.messageFfHint": "Kuregeredzwa pakubatanidza-mberi-mberi (hapana kuzvipira kunogadzirwa).",
    "merge.noFastForward": "Gadzira chibvumirano chekubatanidza (hapana kukurumidza-mberi)",
    "merge.noFastForwardHint": "Gara uchirekodha chibvumirano chekubatanidza, kunyangwe bazi richikwanisa kuenda mberi.",
    "merge.merge": "Merge",
    "merge.cancel": "Cancel",
    "merge.previewError": "Hatina kukwanisa kuverenga preview: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash kusanganisa - shanduko dzese dzinogara seIMWE kuzvipira pa \"{target}\"; \"{source}\"'s pachake anozvipira kugara kunze kwenhoroondo yayo. Chinja kumashure muZvirongwa › Batanidza.",
    "merge.messageSquash": "Isa meseji",
    "merge.squashMessagePlaceholder": "Squashed shanduko kubva 'source'",
    "newBranch.title": "Gadzira Bazi",
    "newBranch.startPoint": "Bazi idzva rinotanga kubva {sha}",
    "newBranch.startPointOn": "iri XPH0X",
    "newBranch.location": "Nzvimbo",
    "newBranch.locationRoot": "(mudzi)",
    "newBranch.expandAll": "Wedzera zvese",
    "newBranch.collapseAll": "Koroka zvese",
    "newBranch.expandFolder": "Wedzera folda",
    "newBranch.collapseFolder": "Korotsa folda",
    "newBranch.name": "Zita rebazi",
    "newBranch.namePlaceholder": "rangu-bazi",
    "newBranch.fullName": "Zita rakazara: {name}",
    "newBranch.invalid": "Zita rebazi hariite",
    "newBranch.exists": "Bazi rine zita iri ravepo",
    "newBranch.checkout": "Checkout bazi mushure mekugadzira",
    "newBranch.create": "Create",
    "newBranch.cancel": "Cancel",
    "status.loading": "Kurodha girafu...",
    "status.summary": "{repo}Showing {commits} inoita, {refs} refs",
    "status.branchCreated": "Yakagadzirwa bazi \"{name}\" pa {sha}",
    "status.error": "Kukanganisa: {message}",
    "status.opFailed": "Kushanda kwakundikana",
    "status.fetching": "Kutora…",
    "status.pulling": "Kudhonza...",
    "status.pushing": "Kusundidzira...",
    "status.committing": "Kugadzira chibvumirano chemunharaunda…",
    "status.commitCreated": "Yakagadzirwa yemuno kuzvipira {sha}: {message}",
    "status.syncing": "Kubatanidza...",
    "status.noHead": "Hapana chekubhadhara ikozvino chawanikwa mugirafu.",
    "status.undoing": "Kuregedza kuita…",
    "status.commitUndone": "Kuzvipira kuregedzwa - shanduko dzadzoka mumuti unoshanda.",
    "status.undoConflict": "Dzorera kunetsana - gadzirisa mupepeti, woenderera mberi.",
    "status.stashApplying": "Kuisa stash...",
    "status.stashPopping": "Kuvhura stash…",
    "status.stashDropping": "Kudonhedza stash…",
    "status.stashApplied": "Stash yakashandiswa.",
    "status.stashPopped": "Stash yakabuda.",
    "status.stashDropped": "Stash yadonha.",
    "status.stashConflict": "Stash kusawirirana - gadzirisa iwo mupepeti.",
    "status.merging": "Kubatanidza...",
    "status.merged": "Kubatanidza kwapera.",
    "status.mergeConflict": "Kubatanidza kune makakatanwa - agadzirise mupepeti, wobva waita.",
    "search.title": "Search",
    "search.tooltip": "Kutsvaga kunoita",
    "search.placeholder": "Kutsvaga kunoita…",
    "search.previous": "Zvapfuura",
    "search.next": "Next result",
    "search.noResults": "Hapana mhinduro",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Sefa mode",
    "search.highlight": "Highlight mode",
    "search.filterMode": "Tsvaga kuratidza",
    "search.filterModeFilter": "Sefa (viga zvisingaenderani)",
    "search.filterModeHighlight": "Simbisa (ratidza zvese, maka machisi)",
    "search.filterModeHint": "Sarudza kuti mhinduro dzekutsvaga dzinoratidzwa sei mugirafu.",
    "search.visibility": "Tsvaga nzvimbo",
    "search.visibilityToolbar": "Toolbar bhatani",
    "search.visibilityAlwaysVisible": "Nguva dzose-inooneka bar",
    "search.visibilityHint": "Ratidza tsvakiridzo sechiratidzo cheturusi kana bhaa rinoramba riri pamusoro.",
    "footer.github": "GitHub",
  },
  co: {
    "toolbar.refresh": "⟳ Refresh",
    "toolbar.fetch": "⤓ Pigliate",
    "toolbar.pull": "⇩ Tira",
    "toolbar.push": "⇧ Spingi",
    "toolbar.commit": "✓ Impegnate",
    "toolbar.sync": "⇅ Sincronizza",
    "toolbar.jumpHead": "⌖ Andà à u checkout",
    "toolbar.reset": "⤢ Resetta a vista",
    "toolbar.settings": "⚙ Settings",
    "details.header": "Detaglii di impegnu",
    "details.close": "Chiudi",
    "details.sha": "SHA",
    "details.shortSha": "Short SHA",
    "details.message": "Missaghju",
    "details.author": "Autore",
    "details.date": "Data",
    "details.labels": "Etichette",
    "details.location": "Locu",
    "details.currentHead": "HEAD - checkout attuale",
    "settings.title": "Settings",
    "settings.language": "Lingua",
    "settings.theme": "Tema",
    "settings.themeLight": "Luce",
    "settings.themeDark": "Scuru",
    "settings.themeLightHint": "Fondo chjaru, testu scuru.",
    "settings.themeDarkHint": "Fondo scuru, testu chjaru.",
    "settings.mainBranch": "Branca principale",
    "settings.mainBranchAuto": "autumàticu",
    "settings.mainBranchSearch": "Cerca filiali...",
    "settings.mainBranchNoMatch": "Nisun rami currispondenti",
    "settings.display": "Stile di visualizazione",
    "settings.displayModern": "Moderna",
    "settings.displayClassic": "Classicu",
    "settings.displayModernHint": "Canvas gratuitu - trascinate per panorà, scorri per zoom.",
    "settings.displayClassicHint": "Tela fissa - troncu appiccicatu à sinistra, senza zoom, scroll solu (cum'è u graficu di rivisione SVN).",
    "settings.sectionGeneral": "Generale",
    "settings.sectionGraph": "Graficu",
    "settings.sectionChanges": "Cambia a vista",
    "settings.diffMinimap": "Minimap diff",
    "settings.diffMinimapOn": "Dimustratu",
    "settings.diffMinimapOff": "Ammucciatu",
    "settings.diffMinimapOnHint": "Mostra una striscia di panoramica in stile VS Code accantu à u diff - trascinallu per scroll; Cambia marcatori inclusi.",
    "settings.diffMinimapOffHint": "Nisuna striscia di panoramica - scroll the diff nurmale.",
    "settings.commitReview": "Cunsigliu di rivisione",
    "settings.commitReviewOn": "Dumandate prima di impegnà",
    "settings.commitReviewOff": "Impegnu direttamente",
    "settings.commitReviewOnHint": "Mostra una lista finale di i fugliali è un cuntrollu di messagiu prima di creà l'impegnu lucale.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Mostra un selettore di l'arbulu di cartulare quandu crea un ramu (invece di un semplice prompt).",
    "settings.branchDialog": "dialogu Branch",
    "settings.branchDialogSvnTitle": "Stile SVN",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Chiudi",
    "settings.done": "Fattu",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "A strada per git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Unisce stile",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "succursale locale",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Cume precedente nantu à a stessa linea",
    "legend.edgeMerge": "Merged-in branch - a freccia punta à l'impegnu di fusione",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "⌖ Andà à u checkout",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Rinominate a branche \"{name}\"...",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "File binariu - nisuna differenza di testu per vede.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Chiudi",
    "changes.prevChange": "Cambiamentu precedente",
    "changes.nextChange": "U prossimu cambiamentu",
    "changes.collapseAll": "Collapse tutti i cartulare",
    "changes.expandAll": "Espandi tutti i cartulare",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Nisun schedariu currisponde à a vostra ricerca.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Cerca in questa diff (Ctrl+F)",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Prossima partita",
    "find.close": "Chiudi a ricerca",
    "find.noResults": "Nisun risultatu",
    "commit.title": "Cummettimu i cambiamenti",
    "commit.close": "Chiudi",
    "commit.loading": "Lettura di l'arbre di travagliu ...",
    "commit.noChanges": "Ùn ci hè micca cambiamenti lucali per impegnà.",
    "commit.selectFile": "Selezziunà un schedariu per vede u so diff.",
    "commit.selectAll": "Selezziunà tuttu",
    "commit.selectNone": "Pulisce tuttu",
    "commit.message": "Cummit missaghju",
    "commit.messagePlaceholder": "Descrive u cambiamentu",
    "commit.selectedCount": "U schedariu {count} hè sceltu",
    "commit.review": "Rivista",
    "commit.reviewTitle": "Verificazione finale prima di impegnà",
    "commit.reviewSummary": "Cummette u schedariu {count} cù u messagiu: {message}",
    "commit.back": "Torna",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "In (attuale)",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "I cambiamenti risultatu",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Unisce u missaghju di cummissione",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Ignoratu nantu à una fusione in avanti veloce (nessun commit hè creatu).",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Unisce",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Novu ramu partendu da {sha}",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  cy: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Wedi newid",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Wedi'i addasu",
    "changes.deleted": "Wedi'i ddileu",
    "changes.renamed": "Ailenwyd",
    "changes.noChanges": "Nid yw'r ymrwymiad hwn yn newid unrhyw ffeiliau.",
    "changes.loading": "Wrthi'n llwytho diff…",
    "changes.selectFile": "Dewiswch ffeil i weld ei newidiadau.",
    "changes.original": "Gwreiddiol",
    "changes.changed": "Yr ymrwymiad hwn",
    "changes.binary": "Ffeil ddeuaidd — dim testun yn wahanol i'w ddangos.",
    "changes.tooLarge": "Ffeil yn rhy fawr i diff.",
    "changes.renamedFrom": "Wedi'i ailenwi o {path}",
    "changes.close": "Cau",
    "changes.prevChange": "Newid blaenorol",
    "changes.nextChange": "Newid nesaf",
    "changes.collapseAll": "Crebachu pob ffolder",
    "changes.expandAll": "Ehangu pob ffolder",
    "changes.searchPlaceholder": "Chwilio ffeiliau…",
    "changes.noSearchResults": "Nid oes unrhyw ffeiliau yn cyfateb i'ch chwiliad.",
    "changes.maximize": "Mwyhau",
    "changes.restore": "Adfer maint",
    "find.open": "Chwiliwch yn y gwahaniaeth hwn (Ctrl+F)",
    "find.placeholder": "Darganfyddwch mewn diff…",
    "find.prev": "Gêm flaenorol",
    "find.next": "Gêm nesaf",
    "find.close": "Cau chwiliad",
    "find.noResults": "Dim canlyniadau",
    "commit.title": "Ymrwymo Newidiadau",
    "commit.close": "Cau",
    "commit.loading": "Darllen coeden waith…",
    "commit.noChanges": "Nid oes unrhyw newidiadau lleol i'w hymrwymo.",
    "commit.selectFile": "Dewiswch ffeil i weld ei diff.",
    "commit.selectAll": "Dewiswch bob un",
    "commit.selectNone": "Clirio'r cyfan",
    "commit.message": "Ymrwymo neges",
    "commit.messagePlaceholder": "Disgrifiwch y newid",
    "commit.selectedCount": "Ffeil(iau) {count} wedi'u dewis",
    "commit.review": "Adolygu",
    "commit.reviewTitle": "Gwiriad terfynol cyn ymrwymo",
    "commit.reviewSummary": "Ymrwymo ffeil(iau) {count} gyda neges: {message}",
    "commit.back": "Yn ol",
    "commit.commit": "Creu ymrwymiad lleol",
    "menu.stashApply": "Gwneud cais stash",
    "menu.stashPop": "Pop stash (gosod a dileu)",
    "menu.stashDrop": "Gollwng stash…",
    "merge.title": "Cangen Uno",
    "merge.route": "Cyfuno i'r gangen bresennol",
    "merge.source": "O (ffynhonnell)",
    "merge.target": "i mewn (cyfredol)",
    "merge.loading": "Wrthi'n dadansoddi uno…",
    "merge.upToDate": "Eisoes yn gyfredol - dim byd i'w uno.",
    "merge.fastForward": "Symud ymlaen — nid oes angen ymrwymiad uno (ticiwch “Creu ymrwymiad uno” i orfodi un).",
    "merge.summary": "Ffeil(iau) {files} wedi'u newid, gwrthdaro(au) {conflicts}.",
    "merge.conflictsWarning": "Mae gwrthdaro rhwng y cyfuniad hwn - gallwch chi uno o hyd, yna eu datrys yn y golygydd.",
    "merge.noChanges": "Dim newidiadau ffeil.",
    "merge.files": "Newidiadau o ganlyniad",
    "merge.added": "Ychwanegwyd",
    "merge.modified": "Wedi'i addasu",
    "merge.deleted": "Wedi'i ddileu",
    "merge.conflict": "Gwrthdaro",
    "merge.message": "Cyfuno neges ymrwymo",
    "merge.messagePlaceholder": "Cyfuno 'ffynhonnell' cangen",
    "merge.messageFfHint": "Wedi'i anwybyddu wrth uno'n gyflym (dim ymrwymiad yn cael ei greu).",
    "merge.noFastForward": "Creu ymrwymiad uno (dim cam ymlaen)",
    "merge.noFastForwardHint": "Cofnodwch ymrwymiad uno bob amser, hyd yn oed pan allai'r gangen symud ymlaen yn gyflym.",
    "merge.merge": "Uno",
    "merge.cancel": "Canslo",
    "merge.previewError": "Methu â chyfrifo rhagolwg: {message}",
    "merge.squashBadge": "Sboncen",
    "merge.squashNote": "Uno sboncen - mae pob newid yn glanio fel UN yn ymrwymo ar “{target}”; Mae ymrwymiadau “{source}” ei hun yn aros allan o'i hanes. Newidiwch yn ôl i Gosodiadau › Cyfuno.",
    "merge.messageSquash": "Ymrwymo neges",
    "merge.squashMessagePlaceholder": "Newidiadau wedi'u gwasgu o 'ffynhonnell'",
    "newBranch.title": "Creu Cangen",
    "newBranch.startPoint": "Cangen newydd yn dechrau o {sha}",
    "newBranch.startPointOn": "ar {refs}",
    "newBranch.location": "Lleoliad",
    "newBranch.locationRoot": "(gwraidd)",
    "newBranch.expandAll": "Ehangu i gyd",
    "newBranch.collapseAll": "Llewygwch y cwbl",
    "newBranch.expandFolder": "Ehangu ffolder",
    "newBranch.collapseFolder": "Crebachu ffolder",
    "newBranch.name": "Enw cangen",
    "newBranch.namePlaceholder": "fy-gangen",
    "newBranch.fullName": "Enw llawn: {name}",
    "newBranch.invalid": "Enw cangen annilys",
    "newBranch.exists": "Mae cangen gyda'r enw hwn eisoes yn bodoli",
    "newBranch.checkout": "Cangen desg dalu ar ôl creu",
    "newBranch.create": "Creu",
    "newBranch.cancel": "Canslo",
    "status.loading": "Wrthi'n llwytho graff…",
    "status.summary": "{repo}Showing {commits} yn ymrwymo, {refs} cyf",
    "status.branchCreated": "Wedi creu cangen \"{name}\" yn {sha}",
    "status.error": "Gwall: {message}",
    "status.opFailed": "Methodd y gweithrediad",
    "status.fetching": "Wrthi'n nôl…",
    "status.pulling": "Wrthi'n tynnu…",
    "status.pushing": "Wrth wthio…",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Creu ymrwymiad lleol {sha}: {message}",
    "status.syncing": "Wrthi'n cysoni…",
    "status.noHead": "Ni chanfuwyd til cyfredol yn y graff.",
    "status.undoing": "Wrthi'n dadwneud ymrwymiad…",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Dad-wneud gwrthdaro taro - datryswch nhw yn y golygydd, yna parhewch.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Yn gollwng stash…",
    "status.stashApplied": "Stash wedi'i gymhwyso.",
    "status.stashPopped": "Stash popped.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Gwrthdaro stash - datryswch nhw yn y golygydd.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Mae gwrthdaro rhwng Cyfuno - datryswch nhw yn y golygydd, yna ymrwymo.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Modd hidlo",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Amlygu (dangoswch y cyfan, marciwch gyfatebiaethau)",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  da: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "✓ Forpligte sig",
    "toolbar.sync": "⇅ Synkroniser",
    "toolbar.jumpHead": "⌖ Gå til kassen",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Tæt",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Kort SHA",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Lys baggrund, mørk tekst.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Automatisk",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Ingen matchende grene",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Klassisk",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Fast lærred — stammen er fastgjort til venstre, ingen zoom, kun rul (som SVN-revisionsgrafen).",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Kurve",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Vist",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Vis en VS-kode-stil oversigtsstrimmel ved siden af ​​forskellen — træk den for at rulle; skiftmarkører medfølger.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Vis en endelig filliste og meddelelseskontrol, før du opretter den lokale commit.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Vis en mappe-trævælger, når du opretter en gren (i stedet for en simpel prompt).",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Din IDE's indbyggede filialprompt.",
    "settings.close": "Tæt",
    "settings.done": "Færdig",
    "settings.sectionAdvanced": "Fremskreden",
    "settings.gitSource": "Git eksekverbar",
    "settings.gitSourceBuiltin": "Indbygget",
    "settings.gitSourceBuiltinHint": "Brug git binær fra IDE's indbyggede Git-udvidelse.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Vejen til git",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Git vilkår",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Hold Git-termer (pull, push, commit, branch…) på engelsk; oversætte alt andet.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Flet stil",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Merged commits i grafen",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Fjern gren",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Kom ind med en fletning (skrevet på en anden gren)",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Tidligere commit på samme linje",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Fortryd forpligtelse (behold ændringer)...",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Ændret",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Ændret",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Omdøbt",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Maksimer",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Ingen filændringer.",
    "merge.files": "Deraf følgende ændringer",
    "merge.added": "Tilføjet",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Konflikt",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Ignoreret ved en hurtig-forward fletning (ingen commit oprettes).",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Flet sammen",
    "merge.cancel": "Ophæve",
    "merge.previewError": "Kunne ikke beregne en forhåndsvisning: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash-fusion — hver ændring lander som EN commit på \"{target}\"; \"{source}\"'s egne forpligtelser forbliver uden for sin historie. Skift tilbage i Indstillinger › Flet.",
    "merge.messageSquash": "Bekræft besked",
    "merge.squashMessagePlaceholder": "Knuste ændringer fra 'kilde'",
    "newBranch.title": "Opret filial",
    "newBranch.startPoint": "Ny filial fra {sha}",
    "newBranch.startPointOn": "på {refs}",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "(rod)",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Skjul alle sammen",
    "newBranch.expandFolder": "Udvid mappe",
    "newBranch.collapseFolder": "Skjul mappe",
    "newBranch.name": "Filialnavn",
    "newBranch.namePlaceholder": "min-gren",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Ugyldigt filialnavn",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Skabe",
    "newBranch.cancel": "Ophæve",
    "status.loading": "Indlæser graf...",
    "status.summary": "{repo}Viser {commits} commits, {refs} refs",
    "status.branchCreated": "Oprettet filial \"{name}\" på {sha}",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Handlingen mislykkedes",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Oprettet lokal commit {sha}: {message}",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Fortryder forpligtelse...",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Fortryd hitkonflikter - løs dem i editoren, og fortsæt derefter.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Stash anvendt.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Sammenfletning afsluttet.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Tidligere resultat",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Søgedisplay",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Fremhæv (vis alle, markér match)",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Søg efter placering",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Vis søgning som et værktøjslinjeikon eller en vedvarende bjælke øverst.",
    "footer.github": "GitHub",
  },
  de: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "⤓ Holen",
    "toolbar.pull": "⇩ Ziehen",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "✓ Commit",
    "toolbar.sync": "⇅ Synchronisieren",
    "toolbar.jumpHead": "⌖ Zur Kasse gehen",
    "toolbar.reset": "⤢ Ansicht zurücksetzen",
    "toolbar.settings": "⚙ Einstellungen",
    "details.header": "Commit-Details",
    "details.close": "Schließen",
    "details.sha": "SHA",
    "details.shortSha": "Kurzes SHA",
    "details.message": "Nachricht",
    "details.author": "Autor",
    "details.date": "Datum",
    "details.labels": "Etiketten",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "HEAD – aktuelle Kasse",
    "settings.title": "Einstellungen",
    "settings.language": "Sprache",
    "settings.theme": "Thema",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Dunkel",
    "settings.themeLightHint": "Heller Hintergrund, dunkler Text.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Hauptzweig",
    "settings.mainBranchAuto": "Automatisch",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Keine passenden Zweige",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Klassiker",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Feste Leinwand – Rumpf links angeheftet, kein Zoom, nur Scrollen (wie das SVN-Revisionsdiagramm).",
    "settings.sectionGeneral": "Allgemein",
    "settings.sectionGraph": "Graph",
    "settings.sectionChanges": "Ansicht ändern",
    "settings.diffMinimap": "Diff-Minikarte",
    "settings.diffMinimapOn": "Gezeigt",
    "settings.diffMinimapOff": "Versteckt",
    "settings.diffMinimapOnHint": "Zeigen Sie einen Übersichtsstreifen im VS-Code-Stil neben dem Diff an – ziehen Sie ihn zum Scrollen; Wechselmarkierungen enthalten.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Direkt verpflichten",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Mit der Schaltfläche „Commit“ wird das lokale Commit sofort erstellt.",
    "settings.svnBranchDialog": "Verzweigungsdialog im SVN-Stil",
    "settings.svnBranchDialogHint": "Zeigen Sie beim Erstellen eines Zweigs eine Ordnerbaumauswahl an (anstelle einer einfachen Eingabeaufforderung).",
    "settings.branchDialog": "Verzweigungsdialog",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "VS-Code-Stil",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Erledigt",
    "settings.sectionAdvanced": "Fortschrittlich",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Verwenden Sie die Git-Binärdatei aus der integrierten Git-Erweiterung der IDE.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Übersetzen",
    "settings.jargonEnglish": "Bleiben Sie auf Englisch",
    "settings.jargonTranslateHint": "Übersetzen Sie Git-Begriffe (Pull, Push, Commit, Branch…) in die Schnittstellensprache.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Stil zusammenführen",
    "settings.mergeModeMerge": "Normale Zusammenführung",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Nur in der eigenen Filiale",
    "settings.mergedViewTarget": "Auch in der Filiale wurden sie zusammengelegt",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Nur in der Cloud (nicht gezogen)",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Benennen Sie den Zweig in „{name}“ um",
    "menu.deleteBranch": "Zweig „{name}“ löschen",
    "menu.renameCommit": "Commit-Nachricht umbenennen…",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Gehen Sie zum ursprünglichen Commit",
    "node.mergedInTooltip": "Durch die Zusammenführung von {merge} wurde dieser Commit in „{target}“ eingefügt – er wurde auf „{origin}“ geschrieben.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Alle Dateien",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Unterschiede werden geladen…",
    "changes.selectFile": "Wählen Sie eine Datei aus, um deren Änderungen anzuzeigen.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Binärdatei – kein Textunterschied zum Anzeigen.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Schließen",
    "changes.prevChange": "Vorherige Änderung",
    "changes.nextChange": "Nächste Änderung",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Erweitern Sie alle Ordner",
    "changes.searchPlaceholder": "Dateien durchsuchen…",
    "changes.noSearchResults": "Keine Dateien entsprechen Ihrer Suche.",
    "changes.maximize": "Maximieren",
    "changes.restore": "Größe wiederherstellen",
    "find.open": "In diesem Diff suchen (Strg+F)",
    "find.placeholder": "Finden Sie in verschiedenen…",
    "find.prev": "Vorheriges Spiel",
    "find.next": "Nächstes Spiel",
    "find.close": "Suche schließen",
    "find.noResults": "Keine Ergebnisse",
    "commit.title": "Änderungen übernehmen",
    "commit.close": "Schließen",
    "commit.loading": "Arbeitsbaum lesen…",
    "commit.noChanges": "Es müssen keine lokalen Änderungen festgeschrieben werden.",
    "commit.selectFile": "Wählen Sie eine Datei aus, um deren Unterschiede anzuzeigen.",
    "commit.selectAll": "Alles auswählen",
    "commit.selectNone": "Alles löschen",
    "commit.message": "Commit-Nachricht",
    "commit.messagePlaceholder": "Beschreiben Sie die Änderung",
    "commit.selectedCount": "{count}-Datei(en) ausgewählt",
    "commit.review": "Rezension",
    "commit.reviewTitle": "Letzte Prüfung vor dem Commit",
    "commit.reviewSummary": "Übertragen Sie die {count}-Datei(en) mit der Meldung: {message}",
    "commit.back": "Zurück",
    "commit.commit": "Erstellen Sie ein lokales Commit",
    "menu.stashApply": "Vorrat anwenden",
    "menu.stashPop": "Pop Stash (auftragen und entfernen)",
    "menu.stashDrop": "Vorrat ablegen…",
    "merge.title": "Zweig zusammenführen",
    "merge.route": "Zusammenführen in den aktuellen Zweig",
    "merge.source": "Von (Quelle)",
    "merge.target": "In (aktuell)",
    "merge.loading": "Zusammenführung wird analysiert…",
    "merge.upToDate": "Bereits auf dem neuesten Stand – nichts zum Zusammenführen.",
    "merge.fastForward": "Schneller Vorlauf – kein Merge-Commit erforderlich (aktivieren Sie „Merge-Commit erstellen“, um ein Commit zu erzwingen).",
    "merge.summary": "{files}-Datei(en) geändert, {conflicts}-Konflikt(e).",
    "merge.conflictsWarning": "Bei dieser Zusammenführung gibt es Konflikte – Sie können sie trotzdem zusammenführen und diese dann im Editor lösen.",
    "merge.noChanges": "Keine Dateiänderungen.",
    "merge.files": "Daraus resultierende Änderungen",
    "merge.added": "Hinzugefügt",
    "merge.modified": "Geändert",
    "merge.deleted": "Gelöscht",
    "merge.conflict": "Konflikt",
    "merge.message": "Commit-Nachricht zusammenführen",
    "merge.messagePlaceholder": "Zweig „Quelle“ zusammenführen",
    "merge.messageFfHint": "Wird bei einer Schnellvorlaufzusammenführung ignoriert (es wird kein Commit erstellt).",
    "merge.noFastForward": "Erstellen Sie einen Merge-Commit (kein Schnellvorlauf)",
    "merge.noFastForwardHint": "Zeichnen Sie immer einen Merge-Commit auf, auch wenn der Zweig einen schnellen Vorlauf ausführen könnte.",
    "merge.merge": "Verschmelzen",
    "merge.cancel": "Stornieren",
    "merge.previewError": "Es konnte keine Vorschau berechnet werden: {message}",
    "merge.squashBadge": "Quetschen",
    "merge.squashNote": "Squash-Merge – jede Änderung landet als EIN Commit auf „{target}“; Die eigenen Commits von „{source}“ bleiben aus seiner Geschichte heraus. Wechseln Sie zurück in „Einstellungen“ > „Zusammenführen“.",
    "merge.messageSquash": "Commit-Nachricht",
    "merge.squashMessagePlaceholder": "Unterdrückte Änderungen aus der „Quelle“",
    "newBranch.title": "Zweig erstellen",
    "newBranch.startPoint": "Neuer Zweig ab {sha}",
    "newBranch.startPointOn": "auf {refs}",
    "newBranch.location": "Standort",
    "newBranch.locationRoot": "(Wurzel)",
    "newBranch.expandAll": "Alles erweitern",
    "newBranch.collapseAll": "Alles einklappen",
    "newBranch.expandFolder": "Ordner erweitern",
    "newBranch.collapseFolder": "Ordner einklappen",
    "newBranch.name": "Filialname",
    "newBranch.namePlaceholder": "meine-Filiale",
    "newBranch.fullName": "Vollständiger Name: {name}",
    "newBranch.invalid": "Ungültiger Filialname",
    "newBranch.exists": "Ein Zweig mit diesem Namen existiert bereits",
    "newBranch.checkout": "Kassenzweig nach der Erstellung",
    "newBranch.create": "Erstellen",
    "newBranch.cancel": "Stornieren",
    "status.loading": "Grafik wird geladen…",
    "status.summary": "{repo}Zeigt {commits}-Commits, {refs}-Referenzen",
    "status.branchCreated": "Zweig „{name}“ bei {sha} erstellt",
    "status.error": "Fehler: {message}",
    "status.opFailed": "Der Vorgang ist fehlgeschlagen",
    "status.fetching": "Abrufen…",
    "status.pulling": "Ziehen…",
    "status.pushing": "Schieben…",
    "status.committing": "Lokales Commit erstellen…",
    "status.commitCreated": "Lokales Commit {sha} erstellt: {message}",
    "status.syncing": "Synchronisierung...",
    "status.noHead": "In der Grafik wurde kein aktueller Checkout gefunden.",
    "status.undoing": "Commit wird rückgängig gemacht…",
    "status.commitUndone": "Commit rückgängig gemacht – Änderungen sind wieder im Arbeitsbaum.",
    "status.undoConflict": "Machen Sie Trefferkonflikte rückgängig – lösen Sie sie im Editor und fahren Sie dann fort.",
    "status.stashApplying": "Stash wird angewendet…",
    "status.stashPopping": "Der Vorrat wird geplatzt …",
    "status.stashDropping": "Vorrat wird abgelegt …",
    "status.stashApplied": "Stash angewendet.",
    "status.stashPopped": "Der Vorrat ist aufgeplatzt.",
    "status.stashDropped": "Stash ist gefallen.",
    "status.stashConflict": "Stash-Konflikte – lösen Sie sie im Editor.",
    "status.merging": "Zusammenführen…",
    "status.merged": "Zusammenführung abgeschlossen.",
    "status.mergeConflict": "Beim Zusammenführen gibt es Konflikte – lösen Sie diese im Editor und führen Sie dann einen Commit durch.",
    "search.title": "Suchen",
    "search.tooltip": "Such-Commits",
    "search.placeholder": "Such-Commits…",
    "search.previous": "Vorheriges Ergebnis",
    "search.next": "Nächstes Ergebnis",
    "search.noResults": "Keine Ergebnisse",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtermodus",
    "search.highlight": "Highlight-Modus",
    "search.filterMode": "Suchanzeige",
    "search.filterModeFilter": "Filter (Nichtübereinstimmungen ausblenden)",
    "search.filterModeHighlight": "Hervorheben (alle anzeigen, Übereinstimmungen markieren)",
    "search.filterModeHint": "Wählen Sie aus, wie Suchergebnisse im Diagramm angezeigt werden.",
    "search.visibility": "Standort suchen",
    "search.visibilityToolbar": "Schaltfläche in der Symbolleiste",
    "search.visibilityAlwaysVisible": "Stets sichtbare Leiste",
    "search.visibilityHint": "Zeigen Sie die Suche oben als Symbolleistensymbol oder dauerhafte Leiste an.",
    "footer.github": "GitHub",
  },
  et: {
    "toolbar.refresh": "⟳ Värskenda",
    "toolbar.fetch": "⤓ Tõmba",
    "toolbar.pull": "⇩ Tõmmake",
    "toolbar.push": "⇧ Lükake",
    "toolbar.commit": "✓ Pühendu",
    "toolbar.sync": "⇅ Sünkroonimine",
    "toolbar.jumpHead": "⌖ Mine kassasse",
    "toolbar.reset": "⤢ Lähtesta vaade",
    "toolbar.settings": "⚙ Seaded",
    "details.header": "Kinnita üksikasjad",
    "details.close": "Sule",
    "details.sha": "SHA",
    "details.shortSha": "Lühike SHA",
    "details.message": "Sõnum",
    "details.author": "Autor",
    "details.date": "Kuupäev",
    "details.labels": "Sildid",
    "details.location": "Asukoht",
    "details.currentHead": "HEAD — praegune kassa",
    "settings.title": "Seaded",
    "settings.language": "Keel",
    "settings.theme": "Teema",
    "settings.themeLight": "Valgus",
    "settings.themeDark": "Tume",
    "settings.themeLightHint": "Hele taust, tume tekst.",
    "settings.themeDarkHint": "Tume taust, hele tekst.",
    "settings.mainBranch": "Peaharu",
    "settings.mainBranchAuto": "Automaatne",
    "settings.mainBranchSearch": "Otsi filiaalidest…",
    "settings.mainBranchNoMatch": "Sobivaid oksi pole",
    "settings.display": "Kuvamise stiil",
    "settings.displayModern": "Kaasaegne",
    "settings.displayClassic": "Klassikaline",
    "settings.displayModernHint": "Tasuta lõuend – lohistage panoraamimiseks, kerige suumimiseks.",
    "settings.displayClassicHint": "Fikseeritud lõuend – pagasiruum on vasakule kinnitatud, suumi puudub, ainult kerimine (nagu SVN-i versioonigraafik).",
    "settings.sectionGeneral": "Kindral",
    "settings.sectionGraph": "Graafik",
    "settings.sectionChanges": "Muudab vaadet",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Näidatud",
    "settings.diffMinimapOff": "Peidetud",
    "settings.diffMinimapOnHint": "Näidake erinevuse kõrval VS-koodi stiilis ülevaateriba – lohistage seda kerimiseks; markerid kaasas.",
    "settings.diffMinimapOffHint": "Ülevaateriba puudub – kerige erinevust tavapäraselt.",
    "settings.commitReview": "Tehke ülevaatus",
    "settings.commitReviewOn": "Küsi enne pühendumist",
    "settings.commitReviewOff": "Kohustage otse",
    "settings.commitReviewOnHint": "Enne kohaliku sissekande loomist kuvage lõplik failide loend ja sõnumite kontroll.",
    "settings.commitReviewOffHint": "Nupp Kinnita loob kohe kohaliku kohustuse.",
    "settings.svnBranchDialog": "SVN-stiilis harudialoog",
    "settings.svnBranchDialogHint": "Näidake haru loomisel kaustapuu valijat (lihtsa viipa asemel).",
    "settings.branchDialog": "Haru dialoog",
    "settings.branchDialogSvnTitle": "SVN-stiilis",
    "settings.branchDialogNativeVscode": "VS-koodi stiil",
    "settings.branchDialogNativeVs": "Visual Studio stiil",
    "settings.branchDialogNativeHint": "Teie IDE sisseehitatud haru viip.",
    "settings.close": "Sule",
    "settings.done": "Valmis",
    "settings.sectionAdvanced": "Täpsemalt",
    "settings.gitSource": "Giti käivitatav fail",
    "settings.gitSourceBuiltin": "Sisseehitatud",
    "settings.gitSourceBuiltinHint": "Kasutage IDE sisseehitatud Git-laienduse git-binaarfaili.",
    "settings.gitSourceCustom": "Kohandatud tee",
    "settings.gitPath": "Tee gitini",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Sirvi…",
    "settings.jargon": "Giti tingimused",
    "settings.jargonTranslate": "Tõlgi",
    "settings.jargonEnglish": "Hoidke inglise keeles",
    "settings.jargonTranslateHint": "Tõlkige Giti terminid (tõmmake, lükake, siduge, harutage ...) liidese keelde.",
    "settings.jargonEnglishHint": "Hoidke Giti terminid (pull, push, commit, branch…) inglise keeles; tõlkida kõik muu.",
    "settings.sectionMerge": "Ühendage",
    "settings.mergeMode": "Ühendage stiil",
    "settings.mergeModeMerge": "Tavaline liitmine",
    "settings.mergeModeSquash": "Suvikõrvits",
    "settings.mergeModeMergeHint": "Ühendab haru sellisel kujul, nagu see on: selle kohustustest saab osa praeguse haru ajaloost, millele lisandub liitmiskohustus (või võimalusel kiirelt edasi).",
    "settings.mergeModeSquashHint": "Ahendab kogu haru praeguse haru üheks sidumiseks (git merge --squash). Haru enda kohustused ei sisene kunagi praeguse haru ajalukku – näete seal ainult ühte täitmist. Filiaal ise jäetakse puutumata, nii et selle toimed jäävad oma sõidurajal nähtavaks.",
    "settings.mergedView": "Graafikus ühendatud kohustused",
    "settings.mergedViewBranch": "Ainult oma haru peal",
    "settings.mergedViewTarget": "Ka filiaalis need liideti",
    "settings.mergedViewBranchHint": "Ühendatud filiaali kohustused loositakse ainult nende oma rajale; liitmine kuvatakse konnektorina. Klassikaline vaade.",
    "settings.mergedViewTargetHint": "Sisse toodud liitmistoimingud joonistatakse samuti vastuvõtva haru rajale, virnastatud liitmiskohustuse alla ja originaalidega tasemele – sest see haru sisaldab neid nüüd tõesti. Koopiad on kahvatud ja peal on oksaga, millele need olid kirjutatud, nii et jääb selgeks, et neid ei tehtud seal. Suvikõrvitsate ühendamist see ei mõjuta: squash kirjutab ühe tavalise kohustuse, nii et kunagi ilmub ainult see kohustus.",
    "legend.title": "Legend",
    "legend.head": "PEA / praegune haru",
    "legend.local": "Kohalik filiaal",
    "legend.remote": "Kaugharu",
    "legend.remoteOnly": "Ainult pilves (mitte tõmmatud)",
    "legend.tag": "Silt (versioon)",
    "legend.commit": "Pühenduma",
    "legend.stash": "Varu (riiulitel töö)",
    "legend.mergedIn": "Tuli sisse liitmisega (kirjutatud teisele harule)",
    "legend.nodes": "Kastid",
    "legend.lines": "Jooned",
    "legend.edgeParent": "Eelmine kohustus samal real",
    "legend.edgeMerge": "Ühendatud haru — nool osutab ühendamise kohustusele",
    "legend.edgeBranch": "Siin hargnes maha oks",
    "legend.edgeStash": "Stash ja kohustus, millele see oli pandud",
    "legend.edgeMergedChain": "Selle haru rida, mis läbib ühendatud kohustusi",
    "legend.edgeMergedTie": "Sama kohustus harus, millele see oli kirjutatud",
    "menu.jumpHead": "⌖ Mine kassasse",
    "menu.resetView": "⤢ Lähtesta vaade",
    "menu.createBranch": "Loo haru siit…",
    "menu.checkout": "Kontrollige seda kohustust",
    "menu.copySha": "Kopeeri commit SHA",
    "menu.pushBranch": "Lükake haru \"{name}\"",
    "menu.renameCommit": "Nimeta kinnistamissõnum ümber…",
    "menu.undoCommit": "Võta kohustus tagasi (jäta muudatused alles)…",
    "menu.viewChanges": "Kuva muudatused…",
    "menu.jumpToOriginal": "Minge algse sidumise juurde",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "{sha} muudatused",
    "changes.tabChanged": "Muudetud",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Lisatud",
    "changes.modified": "Muudetud",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Ümbernimetatud",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Erinevuste laadimine…",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Originaal",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Binaarfail – pole teksti erinevust, mida näidata.",
    "changes.tooLarge": "Fail on diferentseerimiseks liiga suur.",
    "changes.renamedFrom": "Nimetatud ümber {path}-st",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Ahenda kõik kaustad",
    "changes.expandAll": "Laiendage kõiki kaustu",
    "changes.searchPlaceholder": "Otsi faile…",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Maksimeerida",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Otsi selles erinevuses (Ctrl+F)",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Järgmine matš",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Sule",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Valige fail, et näha selle erinevust.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Kirjeldage muutust",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Loo kohalik kohustus",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Ühendage filiaal",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Pärit (allikas)",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Ühenduse analüüsimine…",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Sellest tulenevad muudatused",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Squash-ühendamine – iga muudatus toimub ÜHE kohustusena {target}; \"{source}\" enda kohustused jäävad selle ajaloost välja. Lülitage tagasi menüüs Seaded › Ühenda.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Filiaali nimi",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Täisnimi: {name}",
    "newBranch.invalid": "Vale filiaali nimi",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Ühendamisel on konflikte – lahendage need redaktoris ja seejärel kinnitage.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Esiletõstmise režiim",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Alati nähtav riba",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  es: {
    "toolbar.refresh": "⟳ Actualizar",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Detalles de confirmación",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Diálogo de rama estilo SVN",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "estilo SVN",
    "settings.branchDialogNativeVscode": "Estilo de código VS",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "términos de git",
    "settings.jargonTranslate": "Traducir",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Mantenga los términos de Git (pull, push, commit, bifurcación…) en inglés; traducir todo lo demás.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "fusión de calabaza",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Contrae toda la rama en una única confirmación en la rama actual (git merge --squash). Las confirmaciones propias de la rama nunca ingresan al historial de la rama actual; solo verá una confirmación allí. La rama en sí no se modifica, por lo que sus confirmaciones permanecen visibles en su propio carril.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Sólo en su propia sucursal",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "CABEZA/rama actual",
    "legend.local": "Sucursal local",
    "legend.remote": "Sucursal remota",
    "legend.remoteOnly": "Sólo en la nube (no extraído)",
    "legend.tag": "Etiqueta (versión)",
    "legend.commit": "Comprometerse",
    "legend.stash": "Stash (trabajo archivado)",
    "legend.mergedIn": "Entró con una fusión (escrito en otra rama)",
    "legend.nodes": "Cajas",
    "legend.lines": "Pauta",
    "legend.edgeParent": "Confirmación anterior en la misma línea.",
    "legend.edgeMerge": "Rama fusionada: la flecha apunta a la confirmación de fusión",
    "legend.edgeBranch": "Una rama se bifurcó aquí",
    "legend.edgeStash": "Stash y el compromiso en el que fue archivado",
    "legend.edgeMergedChain": "La línea de esta rama que atraviesa las confirmaciones fusionadas",
    "legend.edgeMergedTie": "El mismo compromiso en la rama en la que fue escrito.",
    "menu.jumpHead": "⌖ Ir a caja",
    "menu.resetView": "⤢ Restablecer vista",
    "menu.createBranch": "Crear sucursal desde aquí...",
    "menu.checkout": "Mira este compromiso",
    "menu.copySha": "Copiar confirmación SHA",
    "menu.pushBranch": "Rama de empuje \"{name}\"",
    "menu.renameBranch": "Cambiar el nombre de la rama \"{name}\"...",
    "menu.deleteBranch": "Eliminar rama \"{name}\"...",
    "menu.renameCommit": "Cambiar el nombre del mensaje de confirmación…",
    "menu.undoCommit": "Deshacer confirmación (mantener cambios)…",
    "menu.viewChanges": "Ver cambios…",
    "menu.mergeBranch": "Fusionar \"{source}\" en \"{target}\"...",
    "menu.jumpToOriginal": "Ir al compromiso original",
    "node.mergedInTooltip": "Merge {merge} trajo este compromiso a \"{target}\"; estaba escrito en \"{origin}\".",
    "changes.title": "Cambios en {sha}",
    "changes.tabChanged": "Cambió",
    "changes.tabAll": "Todos los archivos",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Renombrado de {path}",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Contraer todas las carpetas",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Buscar archivos…",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Buscar en esta diferencia (Ctrl+F)",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Partido anterior",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Ya actualizado: nada que fusionar.",
    "merge.fastForward": "Avance rápido: no se necesita confirmación de fusión (marque \"Crear una confirmación de fusión\" para forzar una).",
    "merge.summary": "Archivo(s) {files} cambiado(s), conflicto(s) {conflicts}.",
    "merge.conflictsWarning": "Esta combinación tiene conflictos; aún puedes combinarlos y luego resolverlos en el editor.",
    "merge.noChanges": "Sin cambios de archivos.",
    "merge.files": "Cambios resultantes",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Modificado",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Fusionar mensaje de confirmación",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Crear una confirmación de fusión (sin avance rápido)",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Cambios aplastados de la 'fuente'",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Ubicación",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "mi-sucursal",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Crear",
    "newBranch.cancel": "Cancelar",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Conflictos ocultos: resuélvalos en el editor.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Confirmaciones de búsqueda...",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  eo: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "⇧ Premu",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "SHA",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "⌖ Iru al kaso",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Priskribu la ŝanĝon",
    "commit.selectedCount": "{count} dosiero(j) elektita(j).",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Fina kontrolo antaŭ ol fari",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Kreu lokan komvon",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Pop stash (apliki kaj forigi)",
    "menu.stashDrop": "Forigu kaŝejon...",
    "merge.title": "Kunfandi Branĉon",
    "merge.route": "Kunfandado en la nunan branĉon",
    "merge.source": "De (fonto)",
    "merge.target": "En (nuna)",
    "merge.loading": "Analizante kunfandiĝon...",
    "merge.upToDate": "Jam ĝisdatigita — nenio por kunfandi.",
    "merge.fastForward": "Rapide antaŭen — ne necesas kunfandi kommitton (marku \"Krei kunfandigon\" por devigi unu).",
    "merge.summary": "{files}-dosiero(j) ŝanĝiĝis, {conflicts}-konflikto(j).",
    "merge.conflictsWarning": "Ĉi tiu kunfandado havas konfliktojn — vi ankoraŭ povas kunfandi, poste solvi ilin en la redaktilo.",
    "merge.noChanges": "Neniuj dosieroŝanĝoj.",
    "merge.files": "Rezultaj ŝanĝoj",
    "merge.added": "Aldonita",
    "merge.modified": "Modifita",
    "merge.deleted": "Forigita",
    "merge.conflict": "Konflikto",
    "merge.message": "Kunfandi commit-mesaĝon",
    "merge.messagePlaceholder": "Kunfandi branĉon 'fonto'",
    "merge.messageFfHint": "Ignorita ĉe rapida antaŭenigo (neniu kommit estas kreita).",
    "merge.noFastForward": "Krei kunfandigon (neniu rapida antaŭen)",
    "merge.noFastForwardHint": "Ĉiam registri kunfandon, eĉ kiam la branĉo povus rapide antaŭen.",
    "merge.merge": "Kunfandi",
    "merge.cancel": "Nuligi",
    "merge.previewError": "Ne eblis komputi antaŭprezenton: {message}",
    "merge.squashBadge": "Skvaŝo",
    "merge.squashNote": "Squash merge — ĉiu ŝanĝo alteriĝas kiel UNU kompromiso sur \"{target}\"; La propraj devotoj de “{source}” restas ekster ĝia historio. Reenŝaltu en Agordoj › Kunfandi.",
    "merge.messageSquash": "Faru mesaĝon",
    "merge.squashMessagePlaceholder": "Premigitaj ŝanĝoj de \"fonto\"",
    "newBranch.title": "Kreu Branĉon",
    "newBranch.startPoint": "Nova branĉo ekde {sha}",
    "newBranch.startPointOn": "sur {refs}",
    "newBranch.location": "Loko",
    "newBranch.locationRoot": "(radiko)",
    "newBranch.expandAll": "Plivastigi ĉion",
    "newBranch.collapseAll": "Kolapu ĉion",
    "newBranch.expandFolder": "Vastigu dosierujon",
    "newBranch.collapseFolder": "Kolapsi dosierujon",
    "newBranch.name": "Nomo de branĉo",
    "newBranch.namePlaceholder": "mia-branĉo",
    "newBranch.fullName": "Plena nomo: {name}",
    "newBranch.invalid": "Nevalida filionomo",
    "newBranch.exists": "Branĉo kun ĉi tiu nomo jam ekzistas",
    "newBranch.checkout": "Kaŝa branĉo post kreado",
    "newBranch.create": "Krei",
    "newBranch.cancel": "Nuligi",
    "status.loading": "Ŝarĝante grafikaĵon...",
    "status.summary": "{repo}Montrante {commits}-komisiojn, {refs}-referojn",
    "status.branchCreated": "Kreita branĉo \"{name}\" ĉe {sha}",
    "status.error": "Eraro: {message}",
    "status.opFailed": "Operacio malsukcesis",
    "status.fetching": "Alportante…",
    "status.pulling": "Tirante…",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Kreita loka kommit {sha}: {message}",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtrila reĝimo",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  eu: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "SHA laburra",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "VS Code estiloa",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Git-erako bidea",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Arakatu…",
    "settings.jargon": "Git terminoak",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Jarrai ingelesez",
    "settings.jargonTranslateHint": "Itzuli Git terminoak (pull, push, commit, branch...) interfaze-hizkuntzara.",
    "settings.jargonEnglishHint": "Mantendu Git terminoak (pull, push, commit, branch...) ingelesez; beste guztia itzuli.",
    "settings.sectionMerge": "Batu",
    "settings.mergeMode": "Konbinatu estiloa",
    "settings.mergeModeMerge": "Bateratze normala",
    "settings.mergeModeSquash": "Squash bat egitea",
    "settings.mergeModeMergeHint": "Adarra den bezala bateratzen du: bere konpromisoak uneko adarraren historiaren parte bihurtzen dira, bateratze-konpromiso batekin (edo azkar aurreratu ahal denean).",
    "settings.mergeModeSquashHint": "Adar osoa tolesten du uneko adarreko konpromiso bakarrean (git merge --squash). Sukurtsalaren konpromezuak ez dira inoiz sartzen egungo adarraren historian; hor konprometitu bakarra ikusten duzu. Adarra bera ukitu gabe geratzen da, beraz, bere konpromisuak ikusgai geratzen dira bere erreian.",
    "settings.mergedView": "Konpromisoak bateratuak grafikoan",
    "settings.mergedViewBranch": "Euren adarrean bakarrik",
    "settings.mergedViewTarget": "Adarrean ere batu ziren",
    "settings.mergedViewBranchHint": "Batatutako adar baten konpromezuak beren erreian bakarrik ateratzen dira; batzea konektore gisa agertzen da. Ikuspegi klasikoa.",
    "settings.mergedViewTargetHint": "Ekartzen dituen bateratze-konpromisoak adar hartzailearen erreian ere marrazten dira, bateratze-konpromisoaren azpian pilatuta eta jatorrizkoekin berdinduta, adar horrek benetan dituelako orain. Kopiak zurbilak dira eta idatzita zeuden adarrarekin dute burua, beraz argi geratzen da ez zirela bertan egin. Squash-en bateratzeak ez du eraginik: squash batek konpromezu arrunt bat idazten du, beraz, konpromezu hori bakarrik agertzen da.",
    "legend.title": "Kondaira",
    "legend.head": "BURUA / egungo adarra",
    "legend.local": "Tokiko adarra",
    "legend.remote": "Urruneko adarra",
    "legend.remoteOnly": "Hodeian bakarrik (ez da tira)",
    "legend.tag": "Etiketa (bertsioa)",
    "legend.commit": "Konprometitu",
    "legend.stash": "Stash (apalategiko lana)",
    "legend.mergedIn": "Batuketa batekin etorri da (beste adar batean idatzia)",
    "legend.nodes": "Kutxak",
    "legend.lines": "Lerroak",
    "legend.edgeParent": "Aurreko konpromisoa lerro berean",
    "legend.edgeMerge": "Batatutako adarra — geziak bateratze-konpromisoan jartzen du",
    "legend.edgeBranch": "Hemen adar bat bidegurutu da",
    "legend.edgeStash": "Stash eta konpromisoa gordeta zegoen",
    "legend.edgeMergedChain": "Adar honen linea batutako konpromisoetatik igarotzen da",
    "legend.edgeMergedTie": "Konpromiso bera idatzi zen adarrean",
    "menu.jumpHead": "⌖ Joan ordaintzera",
    "menu.resetView": "⤢ Berrezarri ikuspegia",
    "menu.createBranch": "Sortu adarra hemendik...",
    "menu.checkout": "Begiratu konpromiso hau",
    "menu.copySha": "Kopiatu konpromisoa SHA",
    "menu.pushBranch": "Bultza ezazu \"{name}\" adarra",
    "menu.renameCommit": "Aldatu izena konprometitzeko mezua…",
    "menu.undoCommit": "Desegin konpromisoa (mantendu aldaketak)…",
    "menu.viewChanges": "Ikusi aldaketak…",
    "menu.jumpToOriginal": "Joan jatorrizko konpromisora",
    "changes.title": "{sha}-n aldaketak",
    "changes.tabChanged": "Aldatua",
    "changes.tabAll": "Fitxategi guztiak",
    "changes.added": "Gehituta",
    "changes.modified": "Aldatua",
    "changes.deleted": "Ezabatu da",
    "changes.renamed": "Izena aldatu",
    "changes.noChanges": "Konpromiso honek ez du fitxategirik aldatzen.",
    "changes.loading": "Desberdintasuna kargatzen…",
    "changes.selectFile": "Hautatu fitxategi bat bere aldaketak ikusteko.",
    "changes.original": "Jatorrizkoa",
    "changes.changed": "Konpromiso hau",
    "changes.binary": "Fitxategi bitarra — ez dago testu-diferentziarik erakusteko.",
    "changes.tooLarge": "Fitxategia handiegia da desberdintzeko.",
    "changes.renamedFrom": "{path} izena aldatu zaio",
    "changes.close": "Itxi",
    "changes.prevChange": "Aurreko aldaketa",
    "changes.nextChange": "Hurrengo aldaketa",
    "changes.collapseAll": "Tolestu karpeta guztiak",
    "changes.expandAll": "Zabaldu karpeta guztiak",
    "changes.searchPlaceholder": "Bilatu fitxategiak…",
    "changes.noSearchResults": "Ez dago zure bilaketarekin bat datorren fitxategirik.",
    "changes.maximize": "Maximizatu",
    "changes.restore": "Berrezarri tamaina",
    "find.open": "Bilatu ezberdin honetan (Ktrl+F)",
    "find.placeholder": "Aurkitu desberdinetan...",
    "find.prev": "Aurreko partida",
    "find.next": "Hurrengo partida",
    "find.close": "Itxi bilaketa",
    "find.noResults": "Ez dago emaitzarik",
    "commit.title": "Konprometitu Aldaketak",
    "commit.close": "Itxi",
    "commit.loading": "Laneko zuhaitza irakurtzen...",
    "commit.noChanges": "Ez dago tokiko aldaketarik konprometitzeko.",
    "commit.selectFile": "Hautatu fitxategi bat bere desberdintasuna ikusteko.",
    "commit.selectAll": "Hautatu guztiak",
    "commit.selectNone": "Garbitu dena",
    "commit.message": "Konprometitu mezua",
    "commit.messagePlaceholder": "Deskribatu aldaketa",
    "commit.selectedCount": "{count} fitxategiak hautatuta",
    "commit.review": "Berrikuspena",
    "commit.reviewTitle": "Azken egiaztapena konpromisoa hartu aurretik",
    "commit.reviewSummary": "Konprometitu {count} fitxategia(k) mezuarekin: {message}",
    "commit.back": "Itzuli",
    "commit.commit": "Sortu tokiko konpromisoa",
    "menu.stashApply": "Aplikatu stash",
    "menu.stashPop": "Pop stash (aplikatu eta kendu)",
    "menu.stashDrop": "Jarri gordelekua…",
    "merge.title": "Batu adarra",
    "merge.route": "Uneko adarrean bat egitea",
    "merge.source": "Nork (iturria)",
    "merge.target": "Into (unekoa)",
    "merge.loading": "Batzea aztertzen…",
    "merge.upToDate": "Dagoeneko eguneratuta, ez dago bat egiteko ezer.",
    "merge.fastForward": "Aurrera bizkorra — ez da bateratze-konpromisorik behar (markatu \"Sortu bateratze-konpromiso bat\" bat behartzeko).",
    "merge.summary": "{files} fitxategiak aldatu dira, {conflicts} gatazkak.",
    "merge.conflictsWarning": "Bateratze honek gatazkak ditu; hala ere, batu ditzakezu, gero ebatzi editorean.",
    "merge.noChanges": "Ez dago fitxategi-aldaketarik.",
    "merge.files": "Ondorioz sortutako aldaketak",
    "merge.added": "Gehituta",
    "merge.modified": "Aldatua",
    "merge.deleted": "Ezabatu da",
    "merge.conflict": "Gatazka",
    "merge.message": "Bateratu konpromiso mezua",
    "merge.messagePlaceholder": "Bateratu adarra 'iturria'",
    "merge.messageFfHint": "Aurrerapen bizkorreko bateratze batean ez ikusiarena egiten (ez da konpromisorik sortzen).",
    "merge.noFastForward": "Sortu bateratze-konpromiso bat (aurrera bizkorrik gabe)",
    "merge.noFastForwardHint": "Grabatu beti bateratze-konpromiso bat, nahiz eta adarrak azkar aurrera egin dezakeen.",
    "merge.merge": "Batu",
    "merge.cancel": "Utzi",
    "merge.previewError": "Ezin izan da aurrebista kalkulatu: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge — aldaketa bakoitzak ONE konpromiso gisa hartzen du \"{target}\"-n; “{source}”-ren konpromisoak bere historiatik kanpo geratzen dira. Itzuli Ezarpenak > Bateratu.",
    "merge.messageSquash": "Konprometitu mezua",
    "merge.squashMessagePlaceholder": "\"Iturburutik\" aldaketak zapuztuak",
    "newBranch.title": "Sortu Adarra",
    "newBranch.startPoint": "{sha}-tik adar berria",
    "newBranch.startPointOn": "{refs}-n",
    "newBranch.location": "Kokapena",
    "newBranch.locationRoot": "(erroa)",
    "newBranch.expandAll": "Zabaldu dena",
    "newBranch.collapseAll": "Tolestu dena",
    "newBranch.expandFolder": "Zabaldu karpeta",
    "newBranch.collapseFolder": "Tolestu karpeta",
    "newBranch.name": "Sukurtsalaren izena",
    "newBranch.namePlaceholder": "nire-adarra",
    "newBranch.fullName": "Izen osoa: {name}",
    "newBranch.invalid": "Adar-izen baliogabea",
    "newBranch.exists": "Izen hau duen adar bat badago jada",
    "newBranch.checkout": "Ordainketa adarra sortu ondoren",
    "newBranch.create": "Sortu",
    "newBranch.cancel": "Utzi",
    "status.loading": "Grafikoa kargatzen…",
    "status.summary": "{repo}{commits} konpromisoak erakusten, {refs} erref",
    "status.branchCreated": "\"{name}\" adarra sortu da {sha}-n",
    "status.error": "Errorea: {message}",
    "status.opFailed": "Eragiketak huts egin du",
    "status.fetching": "Eskuratzen…",
    "status.pulling": "Tiratzen…",
    "status.pushing": "Bultzatzen…",
    "status.committing": "Tokiko konpromisoa sortzen...",
    "status.commitCreated": "Tokiko konpromisoa sortu da {sha}: {message}",
    "status.syncing": "Sinkronizatzen…",
    "status.noHead": "Ez da uneko ordainketarik aurkitu grafikoan.",
    "status.undoing": "Konpromisoa desegiten…",
    "status.commitUndone": "Konpromisoa desegin — aldaketak lan-zuhaitzera itzuli dira.",
    "status.undoConflict": "Desegin hit-gatazkak — konpondu editorean, eta jarraitu.",
    "status.stashApplying": "Gorputza aplikatzen…",
    "status.stashPopping": "Gorputza ateratzen…",
    "status.stashDropping": "Gorputza erortzen…",
    "status.stashApplied": "Stash aplikatu da.",
    "status.stashPopped": "Stash agertu zen.",
    "status.stashDropped": "Stash erori egin da.",
    "status.stashConflict": "Ezkutatu gatazkak: konpondu itzazu editorean.",
    "status.merging": "Batzen…",
    "status.merged": "Bateratzea osatu da.",
    "status.mergeConflict": "Batek gatazkak ditu: konpondu editorean, eta gero konprometitu.",
    "search.title": "Bilatu",
    "search.tooltip": "Bilaketa-konpromisoak",
    "search.placeholder": "Bilaketa-konpromisoak...",
    "search.previous": "Aurreko emaitza",
    "search.next": "Hurrengo emaitza",
    "search.noResults": "Ez dago emaitzarik",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Iragazki modua",
    "search.highlight": "Nabarmendu modua",
    "search.filterMode": "Bilaketa pantaila",
    "search.filterModeFilter": "Iragazi (ezkutatu bat ez datozenak)",
    "search.filterModeHighlight": "Nabarmendu (erakutsi guztiak, markatu partidak)",
    "search.filterModeHint": "Aukeratu bilaketa-emaitzak grafikoan nola bistaratzen diren.",
    "search.visibility": "Bilatu kokapena",
    "search.visibilityToolbar": "Tresna-barrako botoia",
    "search.visibilityAlwaysVisible": "Beti ikusgai dagoen barra",
    "search.visibilityHint": "Erakutsi bilaketa tresna-barrako ikono edo barra iraunkor gisa goiko aldean.",
    "footer.github": "GitHub",
  },
  ee: {
    "toolbar.refresh": "⟳ Gbugbɔgadzɔ",
    "toolbar.fetch": "⤓ Xɔe",
    "toolbar.pull": "⇩ Hehe",
    "toolbar.push": "⇧ Tutu",
    "toolbar.commit": "✓ Ðokuitsɔtsɔna",
    "toolbar.sync": "⇅ Nuwɔwɔ ɖekae",
    "toolbar.jumpHead": "⌖ Yi nudzɔƒe",
    "toolbar.reset": "⤢ Gbugbɔ ɖo nukpɔkpɔa",
    "toolbar.settings": "⚙ Ðoɖowo",
    "details.header": "Tsɔ Nyatakakawo De Asi",
    "details.close": "Tu",
    "details.sha": "SHA",
    "details.shortSha": "Kpuie ko la, SHA",
    "details.message": "Gbedeasi",
    "details.author": "Nuŋlɔla",
    "details.date": "Ŋkeke",
    "details.labels": "Ŋkɔwo ƒe ŋkɔwo",
    "details.location": "Teƒe",
    "details.currentHead": "HEAD — fifia ƒe gaxɔgbalẽvi",
    "settings.title": "Ðoɖowo",
    "settings.language": "Gbegbᴐgblᴐ",
    "settings.theme": "Nyati",
    "settings.themeLight": "Kekeli",
    "settings.themeDark": "Nyrɔ",
    "settings.themeLightHint": "Megbe si me kɔ, nuŋɔŋlɔ doviviti.",
    "settings.themeDarkHint": "Megbe doviviti, nuŋɔŋlɔ si me kɔ.",
    "settings.mainBranch": "Alɔdzedɔwɔƒe gãtɔ",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Di alɔdzedɔwɔƒewo...",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Atsyã si woɖena fiana",
    "settings.displayModern": "Egbe nu",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Free canvas — he yi pan dzi, ʋu yi nu dzi.",
    "settings.displayClassicHint": "Woɖɔ canvas ɖo — trunk pinned left, zoom aɖeke meli o, scroll ɖeɖeko (abe SVN revision graph ene).",
    "settings.sectionGeneral": "Gbadza",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Diff ƒe nɔnɔmetata suetɔ",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Fia faɛl ƒe xexlẽdzesi mamlɛtɔ kple gbedasiwo ƒe dodokpɔ hafi nàwɔ teƒea ƒe ɖoɖo.",
    "settings.commitReviewOffHint": "Commit ƒe abɔta wɔa teƒea ƒe commit enumake.",
    "settings.svnBranchDialog": "SVN-style alɔdzedɔwɔƒe ƒe dzeɖonya",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "VS Code ƒe atsyã",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Wò IDE ƒe alɔdzedɔwɔƒe ƒe nyabiase si wotu ɖe eme.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Wɔe vɔ",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Git ƒe dɔwɔwɔ",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Stash kple commit si dzi wodae ɖo",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Gbãtɔ",
    "changes.changed": "Esia tsɔ eɖokui na",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Fail lolo akpa be wòato vovo.",
    "changes.renamedFrom": "Wotrɔ ŋkɔ nɛ tso {path}",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Tɔtrɔ si kplɔe ɖo",
    "changes.collapseAll": "Mitu agbalẽdzraɖoƒeawo katã",
    "changes.expandAll": "Keke agbalẽdzraɖoƒewo katã ɖe enu",
    "changes.searchPlaceholder": "Di faɛlwo...",
    "changes.noSearchResults": "Fail aɖeke mesɔ kple wò didi o.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Kpɔ didia ɖa",
    "find.noResults": "Nu aɖeke metso eme o",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Klɔ wo katã me",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "{count} faɛl(wo) tiatia",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "{files} faɛl(wo) trɔ, {conflicts} ƒe masɔmasɔ(wo).",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Tɔtrɔ siwo dona tso eme",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Tsɔ alɔdzedɔwɔƒe 'tsoƒe' ƒo ƒui.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Wɔ merge commit (yi ŋgɔ kabakaba aɖeke meli o) .",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Squashed tɔtrɔwo tso 'tsoƒe'.",
    "newBranch.title": "Wɔ Alɔdzedɔwɔƒea",
    "newBranch.startPoint": "Alɔdzedɔwɔƒe yeye si adze egɔme tso {sha}",
    "newBranch.startPointOn": "le {refs} dzi",
    "newBranch.location": "Teƒe",
    "newBranch.locationRoot": "(ke)",
    "newBranch.expandAll": "Keke wo katã ɖe enu",
    "newBranch.collapseAll": "Migbã wo katã",
    "newBranch.expandFolder": "Keke agbalẽdzraɖoƒea ɖe enu",
    "newBranch.collapseFolder": "Kplɔ agbalẽdzraɖoƒea",
    "newBranch.name": "Alɔdzedɔwɔƒea ƒe ŋkɔ",
    "newBranch.namePlaceholder": "nye-alɔdzedɔwɔƒe",
    "newBranch.fullName": "Ŋkɔ bliboa: {name}",
    "newBranch.invalid": "Alɔdzedɔwɔƒea ƒe ŋkɔ si mesɔ o",
    "newBranch.exists": "Alɔdze aɖe si ŋu ŋkɔ sia le li xoxo",
    "newBranch.checkout": "Checkout alɔdzedɔwɔƒea le nuwɔwɔ vɔ megbe",
    "newBranch.create": "Wᴐ",
    "newBranch.cancel": "Te fli ɖe eme",
    "status.loading": "Wole graph la tsɔm...",
    "status.summary": "{repo}Fifia {commits} ƒe ɖokuitsɔtsɔna, {refs} refs",
    "status.branchCreated": "Wowɔ alɔdze \"{name}\" le {sha}",
    "status.error": "Vodada: {message}",
    "status.opFailed": "Dɔwɔwɔa do kpo nu",
    "status.fetching": "Fetching...",
    "status.pulling": "Hehe...",
    "status.pushing": "Tututu...",
    "status.committing": "Nutoa me ƒe ɖokuitsɔtsɔna wɔwɔ...",
    "status.commitCreated": "Wowɔ teƒea ƒe commit {sha}: {message}",
    "status.syncing": "Syncing...",
    "status.noHead": "Womekpɔ gaxɔgbalẽvi si li fifia le nɔnɔmetata la me o.",
    "status.undoing": "Undoing commit...",
    "status.commitUndone": "Commit undone — tɔtrɔwo trɔ gbɔ va ati si le dɔ wɔm la me.",
    "status.undoConflict": "Trɔ hit masɔmasɔwo ɖa — kpɔ wo gbɔ le nuŋlɔla la me, emegbe nàyi edzi.",
    "status.stashApplying": "Wole stash zãm...",
    "status.stashPopping": "Popping stash...",
    "status.stashDropping": "Dropping stash...",
    "status.stashApplied": "Stash ye tsɔe wɔ dɔe.",
    "status.stashPopped": "Stash do ɖe ŋgɔ.",
    "status.stashDropped": "Stash dze anyi.",
    "status.stashConflict": "Stash masɔmasɔwo — kpɔ wo gbɔ le nuŋlɔla la me.",
    "status.merging": "Nusiwo wotsɔ ƒo ƒui...",
    "status.merged": "Wowu ƒoƒo ƒu ɖekae nu.",
    "status.mergeConflict": "Masɔmasɔwo le Merge si — kpɔ wo gbɔ le nuŋlɔla me, emegbe nàtsɔe ana.",
    "search.title": "Di",
    "search.tooltip": "Didi ƒe adzɔgbeɖeɖewo",
    "search.placeholder": "Didi ƒe adzɔgbeɖeɖewo...",
    "search.previous": "Nusi do tso eme va yi",
    "search.next": "Nusi kplɔe ɖo si do tso eme",
    "search.noResults": "Nu aɖeke metso eme o",
    "search.resultCount": "{index}/{count} ƒe ƒuƒoƒo",
    "search.filter": "Filter ƒe nɔnɔme",
    "search.highlight": "Te gbe ɖe nɔnɔme dzi",
    "search.filterMode": "Didi ƒe ɖeɖefia",
    "search.filterModeFilter": "Filter (ɣla esiwo mesɔ o) .",
    "search.filterModeHighlight": "Te gbe ɖe edzi (ɖe wo katã fia, de dzesi nusiwo sɔ) .",
    "search.filterModeHint": "Tia alesi woaɖe numekuku me tsonuawo afia le nɔnɔmetata la me.",
    "search.visibility": "Di teƒe si nèle",
    "search.visibilityToolbar": "Dɔwɔnu ƒe dzesi",
    "search.visibilityAlwaysVisible": "Bar si wokpɔna ɣesiaɣi",
    "search.visibilityHint": "Fia didi abe dɔwɔnu ƒe dzesi alo dzesi si nɔa anyi ɖaa le etame ene.",
    "footer.github": "GitHub ƒe ŋkɔ",
  },
  tl: {
    "toolbar.refresh": "⟳ I-refresh",
    "toolbar.fetch": "⤓ Kunin",
    "toolbar.pull": "⇩ Hilahin",
    "toolbar.push": "⇧ Itulak",
    "toolbar.commit": "✓ Mangako",
    "toolbar.sync": "⇅ I-sync",
    "toolbar.jumpHead": "⌖ Pumunta sa checkout",
    "toolbar.reset": "⤢ I-reset ang view",
    "toolbar.settings": "⚙ Mga Setting",
    "details.header": "Mag-commit ng mga Detalye",
    "details.close": "Isara",
    "details.sha": "SHA",
    "details.shortSha": "Maikling SHA",
    "details.message": "Mensahe",
    "details.author": "May-akda",
    "details.date": "Petsa",
    "details.labels": "Mga label",
    "details.location": "Lokasyon",
    "details.currentHead": "HEAD — kasalukuyang checkout",
    "settings.title": "Mga setting",
    "settings.language": "Wika",
    "settings.theme": "Tema",
    "settings.themeLight": "Liwanag",
    "settings.themeDark": "Madilim",
    "settings.themeLightHint": "Banayad na background, madilim na teksto.",
    "settings.themeDarkHint": "Madilim na background, maliwanag na teksto.",
    "settings.mainBranch": "Pangunahing sangay",
    "settings.mainBranchAuto": "Awtomatiko",
    "settings.mainBranchSearch": "Maghanap ng mga sangay...",
    "settings.mainBranchNoMatch": "Walang katugmang sangay",
    "settings.display": "Estilo ng pagpapakita",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Klasiko",
    "settings.displayModernHint": "Libreng canvas — i-drag para mag-pan, mag-scroll para mag-zoom.",
    "settings.displayClassicHint": "Fixed canvas — trunk pinned left, no zoom, scroll only (tulad ng SVN revision graph).",
    "settings.sectionGeneral": "Heneral",
    "settings.sectionGraph": "Graph",
    "settings.sectionChanges": "Mga pagbabago sa view",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Ipinakita",
    "settings.diffMinimapOff": "Nakatago",
    "settings.diffMinimapOnHint": "Magpakita ng VS Code-style overview strip sa tabi ng diff — i-drag ito upang mag-scroll; kasama ang mga marker ng pagbabago.",
    "settings.diffMinimapOffHint": "Walang pangkalahatang-ideya na strip — mag-scroll nang normal sa diff.",
    "settings.commitReview": "Gumawa ng pagsusuri",
    "settings.commitReviewOn": "Magtanong bago mag-commit",
    "settings.commitReviewOff": "Direktang mag-commit",
    "settings.commitReviewOnHint": "Magpakita ng panghuling listahan ng file at pagsuri ng mensahe bago gawin ang lokal na commit.",
    "settings.commitReviewOffHint": "Ang Commit button ay lumilikha ng lokal na commit kaagad.",
    "settings.svnBranchDialog": "SVN-style na branch dialog",
    "settings.svnBranchDialogHint": "Magpakita ng folder-tree picker kapag gumagawa ng branch (sa halip na isang simpleng prompt).",
    "settings.branchDialog": "Diyalogo ng sangay",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "Estilo ng VS Code",
    "settings.branchDialogNativeVs": "Estilo ng Visual Studio",
    "settings.branchDialogNativeHint": "Ang built-in na branch prompt ng iyong IDE.",
    "settings.close": "Isara",
    "settings.done": "Tapos na",
    "settings.sectionAdvanced": "Advanced",
    "settings.gitSource": "Napapatupad ang Git",
    "settings.gitSourceBuiltin": "Naka-built-in",
    "settings.gitSourceBuiltinHint": "Gamitin ang git binary mula sa built-in na Git extension ng IDE.",
    "settings.gitSourceCustom": "Custom na landas",
    "settings.gitPath": "Path sa git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Mag-browse…",
    "settings.jargon": "Mga tuntunin ng Git",
    "settings.jargonTranslate": "Isalin",
    "settings.jargonEnglish": "Panatilihin sa Ingles",
    "settings.jargonTranslateHint": "Isalin ang mga termino ng Git (pull, push, commit, branch...) sa interface na wika.",
    "settings.jargonEnglishHint": "Panatilihin ang mga termino ng Git (pull, push, commit, branch…) sa English; isalin ang lahat ng iba pa.",
    "settings.sectionMerge": "Pagsamahin",
    "settings.mergeMode": "Pagsamahin ang istilo",
    "settings.mergeModeMerge": "Normal na pagsasama",
    "settings.mergeModeSquash": "Squash merge",
    "settings.mergeModeMergeHint": "Pinagsasama ang branch kung ano ito: ang mga commit nito ay naging bahagi ng kasalukuyang kasaysayan ng branch, na sinamahan ng isang merge commit (o fast-forward kapag posible).",
    "settings.mergeModeSquashHint": "Kinu-collapse ang buong branch sa isang solong commit sa kasalukuyang branch (git merge --squash). Ang mga sariling commit ng sangay ay hindi kailanman pumapasok sa kasaysayan ng kasalukuyang sangay — isa lang ang makikita mo doon. Ang sangay mismo ay naiwang hindi nagalaw, kaya ang mga commit nito ay mananatiling nakikita sa sarili nitong lane.",
    "settings.mergedView": "Pinagsamang commit sa graph",
    "settings.mergedViewBranch": "Sa sarili nilang branch lang",
    "settings.mergedViewTarget": "Sa branch din sila pinagsanib",
    "settings.mergedViewBranchHint": "Ang mga commit ng pinagsanib na sangay ay iginuhit lamang sa sarili nilang lane; lalabas ang pagsasama bilang isang connector. Ang klasikong tanawin.",
    "settings.mergedViewTargetHint": "Ang mga commit na isang merge na dinala ay iginuhit din sa receiving branch's lane, na nakasalansan sa ilalim ng merge commit at nilevel sa mga orihinal — dahil ang branch na iyon ay talagang naglalaman ng mga ito ngayon. Ang mga kopya ay maputla at may ulo sa sangay kung saan nakasulat ang mga ito, kaya nananatiling malinaw na hindi sila ginawa doon. Ang mga merge ng squash ay hindi naaapektuhan: ang isang squash ay nagsusulat ng isang ordinaryong commit, kaya ang commit na iyon lang ang lilitaw.",
    "legend.title": "Alamat",
    "legend.head": "HEAD / kasalukuyang sangay",
    "legend.local": "Lokal na sangay",
    "legend.remote": "Malayong sangay",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Tag (bersyon)",
    "legend.commit": "Mangako",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Mga kahon",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Nakaraang commit sa parehong linya",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Isang sanga ang nagsanga dito",
    "legend.edgeStash": "Itago at ang commit kung saan ito naka-shelved",
    "legend.edgeMergedChain": "Ang linya ng sangay na ito na tumatakbo sa merge-in ay commit",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "⤢ I-reset ang view",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Push branch \"{name}\"",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Pumili ng file para makita ang mga pagbabago nito.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Napapanahon na — walang pagsasama-sama.",
    "merge.fastForward": "Fast-forward — hindi kailangan ng merge commit (lagyan ng tsek ang “Greate a merge commit” para pilitin ang isa).",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  fr: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Les commits d'une branche fusionnée sont dessinés uniquement dans leur propre file ; la fusion apparaît comme un connecteur. La vision classique.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "La fusion {merge} a introduit ce commit dans \"{target}\" - il a été écrit sur \"{origin}\".",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Rechercher dans ce différentiel (Ctrl+F)",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Fusionner dans la branche actuelle",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Fusionner le message de validation",
    "merge.messagePlaceholder": "Fusionner la branche 'source'",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "(racine)",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Création de la branche \"{name}\" chez {sha}",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Résultat précédent",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  fy: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "De line fan dizze tûke rint troch de gearfoege commits",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Alle triemmen",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Foarige feroaring",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "GitHub",
  },
  ga: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Taispeáin stiall forbhreathnú ar stíl an Chóid VS in aice leis an difr — tarraing é le scrollú; marcóirí athraithe san áireamh.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Taispeáin roghnóir crann fillteáin agus brainse á chruthú (in ionad leid simplí).",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Casta",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Cosán saincheaptha",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Cealaigh gealltanas (coinnigh na hathruithe)…",
    "menu.viewChanges": "Féach ar athruithe…",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Athruithe i {sha}",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Scriosta",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Ní athraíonn an gealltanas seo comhaid.",
    "changes.loading": "Difr á lódáil…",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Bunaidh",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Tá an comhad ró-mhór le difreáil.",
    "changes.renamedFrom": "Athainmnithe ó {path}",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Leathnaigh gach fillteán",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Uasmhéadaigh",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Faigh i difr…",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "An chéad chluiche eile",
    "find.close": "Dún an cuardach",
    "find.noResults": "Gan torthaí",
    "commit.title": "Tiomantas Athruithe",
    "commit.close": "Dún",
    "commit.loading": "Crann oibre á léamh…",
    "commit.noChanges": "Níl aon athruithe áitiúla le déanamh.",
    "commit.selectFile": "Roghnaigh comhad chun a dhifríocht a fheiceáil.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Glan go léir",
    "commit.message": "Tiomantas teachtaireacht",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Comhad(í) {count} roghnaithe",
    "commit.review": "Léirmheas",
    "commit.reviewTitle": "Seiceáil deiridh roimh gealltanas",
    "commit.reviewSummary": "Tiomantas do chomhad(í) {count} leis an teachtaireacht: {message}",
    "commit.back": "Ar ais",
    "commit.commit": "Cruthaigh tiomantas áitiúil",
    "menu.stashApply": "Cuir stash i bhfeidhm",
    "menu.stashPop": "Pop stash (cuir i bhfeidhm & bain)",
    "menu.stashDrop": "Scaoil stash…",
    "merge.title": "Cumaisc Brainse",
    "merge.route": "Cumasc isteach sa bhrainse reatha",
    "merge.source": "Ó (foinse)",
    "merge.target": "isteach (reatha)",
    "merge.loading": "Cumasc á anailísiú…",
    "merge.upToDate": "Suas chun dáta cheana féin - níl aon rud le cumasc.",
    "merge.fastForward": "Go tapa ar aghaidh — ní gá aon tiomantas cumaisc (cuir tic “Cruthaigh tiomnú cumaisc” chun ceann a chur i bhfeidhm).",
    "merge.summary": "Comhad(í) {files} athraithe, coinbhleacht(í) {conflicts}.",
    "merge.conflictsWarning": "Tá coinbhleachtaí ag baint leis an gcumasc seo - is féidir leat cumasc a dhéanamh fós, ansin iad a réiteach san eagarthóir.",
    "merge.noChanges": "Níl aon athruithe comhaid.",
    "merge.files": "Athruithe dá bharr",
    "merge.added": "Curtha leis",
    "merge.modified": "Athraithe",
    "merge.deleted": "Scriosta",
    "merge.conflict": "Coimhlint",
    "merge.message": "Cumasc teachtaireacht tiomantais",
    "merge.messagePlaceholder": "Cumaisc 'foinse' brainse",
    "merge.messageFfHint": "Neamhaird ar chumasc ar aghaidh go tapa (ní chruthaítear aon tiomantas).",
    "merge.noFastForward": "Cruthaigh tiomantas cumaisc (gan dul ar aghaidh go tapa)",
    "merge.noFastForwardHint": "Déan tiomnú cumaisc a thaifeadadh i gcónaí, fiú nuair a d'fhéadfadh an brainse dul ar aghaidh go tapa.",
    "merge.merge": "Cumaisc",
    "merge.cancel": "Cealaigh",
    "merge.previewError": "Níorbh fhéidir réamhamharc a ríomh: {message}",
    "merge.squashBadge": "Scuais",
    "merge.squashNote": "Cumasc scuaise — tagann gach athrú i dtír mar ghealltanas AMHÁIN ar “{target}”; Fanann gealltanais “{source}” féin amach óna stair. Téigh ar ais i Socruithe › Cumaisc.",
    "merge.messageSquash": "Tiomantas teachtaireacht",
    "merge.squashMessagePlaceholder": "Athruithe brúite ó 'foinse'",
    "newBranch.title": "Cruthaigh Brainse",
    "newBranch.startPoint": "Brainse nua ag tosú ó {sha}",
    "newBranch.startPointOn": "ar {refs}",
    "newBranch.location": "Suíomh",
    "newBranch.locationRoot": "(fréamh)",
    "newBranch.expandAll": "Leathnaigh go léir",
    "newBranch.collapseAll": "Laghdaigh go léir",
    "newBranch.expandFolder": "Leathnaigh fillteán",
    "newBranch.collapseFolder": "Laghdaigh an fillteán",
    "newBranch.name": "Ainm brainse",
    "newBranch.namePlaceholder": "mo-bhrainse",
    "newBranch.fullName": "Ainm iomlán: {name}",
    "newBranch.invalid": "Ainm brainse neamhbhailí",
    "newBranch.exists": "Tá brainse leis an ainm seo ann cheana",
    "newBranch.checkout": "Brainse Seiceáil Amach tar éis cruthú",
    "newBranch.create": "Cruthaigh",
    "newBranch.cancel": "Cealaigh",
    "status.loading": "Graf á lódáil…",
    "status.summary": "{repo}Showing Gealltanas {commits}, tags {refs}",
    "status.branchCreated": "Brainse cruthaithe \"{name}\" ag {sha}",
    "status.error": "Earráid: {message}",
    "status.opFailed": "Theip ar an oibríocht",
    "status.fetching": "Ag fáil…",
    "status.pulling": "Ag tarraingt…",
    "status.pushing": "Ag brú…",
    "status.committing": "Tiomantas áitiúil á chruthú…",
    "status.commitCreated": "Cruthaíodh tiomantas áitiúil {sha}: {message}",
    "status.syncing": "Ag sioncronú…",
    "status.noHead": "Níor aimsíodh aon seiceáil amach reatha sa ghraf.",
    "status.undoing": "Tiomantas á chealú…",
    "status.commitUndone": "Tiomantas gan déanamh - tá athruithe ar ais sa chrann oibre.",
    "status.undoConflict": "Cealaigh coinbhleachtaí buailte - réitigh san eagarthóir iad, ansin lean ar aghaidh.",
    "status.stashApplying": "Stash á chur i bhfeidhm…",
    "status.stashPopping": "Stáisiún popping…",
    "status.stashDropping": "Stash á scaoileadh…",
    "status.stashApplied": "Stash i bhfeidhm.",
    "status.stashPopped": "Stash popped.",
    "status.stashDropped": "Stash thit.",
    "status.stashConflict": "Coinbhleachtaí Stash - iad a réiteach san eagarthóir.",
    "status.merging": "Á chumasc…",
    "status.merged": "Cumasc críochnaithe.",
    "status.mergeConflict": "Tá coinbhleachtaí ag Merge - réitigh san eagarthóir iad, ansin déan gealltanas.",
    "search.title": "Cuardach",
    "search.tooltip": "Cuardach tiomantas",
    "search.placeholder": "Cuardach tiomanta…",
    "search.previous": "Toradh roimhe seo",
    "search.next": "An chéad toradh eile",
    "search.noResults": "Gan torthaí",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Mód scagaire",
    "search.highlight": "Mód Aibhsithe",
    "search.filterMode": "Taispeántas cuardaigh",
    "search.filterModeFilter": "Scagaire (folaigh neamhmheaitseáil)",
    "search.filterModeHighlight": "Aibhsigh (taispeáin gach rud, marcáil meaitseanna)",
    "search.filterModeHint": "Roghnaigh conas a thaispeánfar torthaí cuardaigh sa ghraf.",
    "search.visibility": "Suíomh cuardaigh",
    "search.visibilityToolbar": "Cnaipe Barra Uirlisí",
    "search.visibilityAlwaysVisible": "Barra le feiceáil i gcónaí",
    "search.visibilityHint": "Taispeáin cuardach mar dheilbhín barra uirlisí nó barra leanúnach ag an mbarr.",
    "footer.github": "GitHub",
  },
  sm: {
    "toolbar.refresh": "⟳ Toe fa'afou",
    "toolbar.fetch": "⤓ aumai",
    "toolbar.pull": "⇩ Toso",
    "toolbar.push": "⇧ Oomi",
    "toolbar.commit": "✓ Tautino",
    "toolbar.sync": "⇅ Fa'atasi",
    "toolbar.jumpHead": "⌖ Alu e siaki tupe",
    "toolbar.reset": "⤢ Toe seti le vaaiga",
    "toolbar.settings": "⚙ Fa'atonu",
    "details.header": "Tautino Auiliiliga",
    "details.close": "Tapuni",
    "details.sha": "SHA",
    "details.shortSha": "Puupu SHA",
    "details.message": "Savali",
    "details.author": "Tusitala",
    "details.date": "Aso",
    "details.labels": "Fa'ailoga",
    "details.location": "Nofoaga",
    "details.currentHead": "HEAD - siaki o lo'o iai nei",
    "settings.title": "Fa'atonu",
    "settings.language": "Gagana",
    "settings.theme": "Autu",
    "settings.themeLight": "Malamalama",
    "settings.themeDark": "Pogisa",
    "settings.themeLightHint": "Malamalama i tua, pogisa tusitusiga.",
    "settings.themeDarkHint": "Pogisa tua, malamalama tusitusiga.",
    "settings.mainBranch": "Lala autu",
    "settings.mainBranchAuto": "Otometi",
    "settings.mainBranchSearch": "Su'e lala...",
    "settings.mainBranchNoMatch": "Leai ni lala tutusa",
    "settings.display": "Sitaili fa'aali",
    "settings.displayModern": "Fa'aonaponei",
    "settings.displayClassic": "Tulaga masani",
    "settings.displayModernHint": "Tapoleni saoloto - toso i le apa, taʻavale e faʻasolo.",
    "settings.displayClassicHint": "Tapoleni fa'amau - fa'amau le ogalaau i le agavale, leai se so'o, na'o le tusi ta'avale (pei o le SVN revision graph).",
    "settings.sectionGeneral": "lautele",
    "settings.sectionGraph": "Kalafi",
    "settings.sectionChanges": "Suia le vaaiga",
    "settings.diffMinimap": "Eseese laititi",
    "settings.diffMinimapOn": "Faaalia",
    "settings.diffMinimapOff": "Natia",
    "settings.diffMinimapOnHint": "Faaali atu se VS Code-style overview strip i tafatafa o le diff - toso e ta'ai; e aofia ai fa'ailoga suiga.",
    "settings.diffMinimapOffHint": "Leai se va'aiga lautele - fa'asolo le 'ese'ese masani.",
    "settings.commitReview": "Tuuina atu le iloiloga",
    "settings.commitReviewOn": "Fesili atu a o lei tautino",
    "settings.commitReviewOff": "Tautino tuusao",
    "settings.commitReviewOnHint": "Fa'aali se lisi mulimuli o faila ma siaki fe'au a'o le'i faia le ta'utinoga fa'apitonu'u.",
    "settings.commitReviewOffHint": "O le ki o le Commit e fa'atupu vave ai le ta'utinoga fa'apitonu'u.",
    "settings.svnBranchDialog": "Fa'atalanoaga lala lala SVN",
    "settings.svnBranchDialogHint": "Fa'aali atu se la'au fa'apipi'i faila pe a fai se lala (nai lo se fa'anaunauga faigofie).",
    "settings.branchDialog": "Talanoaga lala",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "VS Code style",
    "settings.branchDialogNativeVs": "Sitaili Visual Studio",
    "settings.branchDialogNativeHint": "Fa'atonu lala o lo'o fausia e lau IDE.",
    "settings.close": "Tapuni",
    "settings.done": "Ua uma",
    "settings.sectionAdvanced": "Maualuluga",
    "settings.gitSource": "Git executable",
    "settings.gitSourceBuiltin": "Ua fausia i totonu",
    "settings.gitSourceBuiltinHint": "Fa'aoga le git binary mai le fa'aopoopoga Git fa'apipi'i a le IDE.",
    "settings.gitSourceCustom": "Ala masani",
    "settings.gitPath": "Auala i git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Su'e...",
    "settings.jargon": "Git faaupuga",
    "settings.jargonTranslate": "Fa'aliliu",
    "settings.jargonEnglish": "Taofi ile Igilisi",
    "settings.jargonTranslateHint": "Fa'aliliu upu Git (toso, tulei, ta'utino, lala...) i le gagana fa'aoga.",
    "settings.jargonEnglishHint": "Taofi upu Git (toso, tulei, ta'utino, lala...) ile Igilisi; faaliliu isi mea uma.",
    "settings.sectionMerge": "Tuufaatasi",
    "settings.mergeMode": "Sitaili fa'atasi",
    "settings.mergeModeMerge": "Fa'atasi masani",
    "settings.mergeModeSquash": "Squash fusia",
    "settings.mergeModeMergeHint": "Fa'atasia le paranesi e pei ona i ai: o ana tautinoga e avea ma vaega o le tala fa'asolopito o le paranesi o lo'o i ai nei, fa'atasi ma se tu'ufa'atasiga tu'ufa'atasia (po'o le fa'avavevave pe a mafai).",
    "settings.mergeModeSquashHint": "Fa'ato'ilaloina le lala atoa i totonu o se tautinoga e tasi i luga o le lala o iai nei (git merge --squash). O tautinoga a le paranesi lava ia e le mafai ona ulufale i le tala faasolopito o le lala o iai nei - e na'o le tasi e te va'ai i ai. O le paranesi lava ia e tu'u pea e le'i fa'aogaina, o lea o ana tautinoga e tumau pea ona iloa i lona lava ala.",
    "settings.mergedView": "Fa'atasi fa'atasi i le kalafi",
    "settings.mergedViewBranch": "Na'o latou lava lala",
    "settings.mergedViewTarget": "I totonu foi o le lala na tuufaatasia ai i latou",
    "settings.mergedViewBranchHint": "O tautinoga a se lala tu'ufa'atasia e tosoina na'o latou lava ala; o le tu'ufa'atasiga e fa'aalia o se so'oga. O le vaaiga masani.",
    "settings.mergedViewTargetHint": "O le tu'ufa'atasiga e aumai i totonu o lo'o tosoina fo'i i le laina o le lala o lo'o mauaina, fa'aputu i lalo o le tu'ufa'atasiga fa'atasi ma fa'atasi ma ulua'i - aua o lo'o i ai moni lava i lena lala i le taimi nei. O kopi e sesega ma ulu i le lala na tusia ai, o lea e manino ai e leʻi faia iina. E le a'afia le tu'ufa'atasiga o Squash: e tusia e le squash se tasi o tautinoga masani, e na'o lena tautinoga e aliali mai.",
    "legend.title": "Tala'aga",
    "legend.head": "HEAD / lala o iai nei",
    "legend.local": "Paranesi i le lotoifale",
    "legend.remote": "lala mamao",
    "legend.remoteOnly": "Na'o le ao (e le tosoina)",
    "legend.tag": "Tag (faiga)",
    "legend.commit": "Tautino",
    "legend.stash": "Taofi (galue fa'asao)",
    "legend.mergedIn": "Sau i totonu ma se fusuaga (tusia i se isi lala)",
    "legend.nodes": "Pusa",
    "legend.lines": "Laina",
    "legend.edgeParent": "Talosaga muamua i luga o le laina tutusa",
    "legend.edgeMerge": "Fa'atasi-i lala - o le aū e fa'asino i le tu'ufa'atasiga fa'atasi",
    "legend.edgeBranch": "O se lala na vavae ese iinei",
    "legend.edgeStash": "Stash ma le ta'utinoga na fa'amuta ai",
    "legend.edgeMergedChain": "Ole laina ole lala lea o lo'o fa'agasolo i totonu ole tu'ufa'atasiga",
    "legend.edgeMergedTie": "O le tautinoga lava lea e tasi i le paranesi sa tusia ai",
    "menu.jumpHead": "⌖ Alu e siaki tupe",
    "menu.resetView": "⤢ Toe seti le vaaiga",
    "menu.createBranch": "Fausia lala mai iinei...",
    "menu.checkout": "Siaki lenei tautinoga",
    "menu.copySha": "Kopi commit SHA",
    "menu.pushBranch": "Tu'i lala \"{name}\"",
    "menu.renameCommit": "Toe fa'aigoa fe'au commit...",
    "menu.undoCommit": "Tatala le tautinoga (tausia suiga)…",
    "menu.viewChanges": "Va'ai suiga...",
    "menu.jumpToOriginal": "Alu i le uluai tautinoga",
    "changes.title": "Suiga ile {sha}",
    "changes.tabChanged": "Suia",
    "changes.tabAll": "Faila uma",
    "changes.added": "Faaopoopo",
    "changes.modified": "Suia",
    "changes.deleted": "Ave'esea",
    "changes.renamed": "Toe fa'aigoa",
    "changes.noChanges": "O lenei tautinoga e leai ni faila e suia.",
    "changes.loading": "Tu'u ese ese...",
    "changes.selectFile": "Filifili se faila e iloa ai ona suiga.",
    "changes.original": "Uluai",
    "changes.changed": "O lenei tautinoga",
    "changes.binary": "Faila faila - leai ni tusitusiga ese e faʻaalia.",
    "changes.tooLarge": "E telē tele le faila e ese ai.",
    "changes.renamedFrom": "Toe fa'aigoa mai {path}",
    "changes.close": "Tapuni",
    "changes.prevChange": "Suiga muamua",
    "changes.nextChange": "O le isi suiga",
    "changes.collapseAll": "Fa'asolo uma faila",
    "changes.expandAll": "Fa'alautele faila uma",
    "changes.searchPlaceholder": "Su'e faila...",
    "changes.noSearchResults": "E leai ni faila e fetaui ma lau sailiga.",
    "changes.maximize": "Fa'atele",
    "changes.restore": "Toe fa'afo'i le tele",
    "find.open": "Su'e ile ese'esega lea (Ctrl+F)",
    "find.placeholder": "Su'e i le eseesega...",
    "find.prev": "Ta'aloga muamua",
    "find.next": "Le isi ta'aloga",
    "find.close": "Tapuni su'esu'ega",
    "find.noResults": "Leai se taunuuga",
    "commit.title": "Fai Suiga",
    "commit.close": "Tapuni",
    "commit.loading": "Faitau la'au galue...",
    "commit.noChanges": "E leai ni suiga fa'apitonu'u e fai.",
    "commit.selectFile": "Filifili se faila e iloa ai lona eseesega.",
    "commit.selectAll": "Filifili uma",
    "commit.selectNone": "Fa'amama uma",
    "commit.message": "Tuuina atu le savali",
    "commit.messagePlaceholder": "Faamatala le suiga",
    "commit.selectedCount": "{count} faila(s) filifilia",
    "commit.review": "Toe iloilo",
    "commit.reviewTitle": "Siaki mulimuli a'o le'i faia",
    "commit.reviewSummary": "Tuuina atu (s) faila {count} ma le feʻau: {message}",
    "commit.back": "Tua",
    "commit.commit": "Fausia tautinoga i le lotoifale",
    "menu.stashApply": "Fa'ataga teu",
    "menu.stashPop": "Fa'apipi'i (fa'aaoga ma aveese)",
    "menu.stashDrop": "Tu'u le teuina...",
    "merge.title": "Tuufaatasi Lala",
    "merge.route": "Fa'atasi i le lala o lo'o iai nei",
    "merge.source": "Mai (puna)",
    "merge.target": "I totonu (i le taimi nei)",
    "merge.loading": "Iloiloga tu'ufa'atasia...",
    "merge.upToDate": "Ua mae'a nei - leai se mea e tu'ufa'atasia.",
    "merge.fastForward": "Aga'i i luma - e leai se tu'ufa'atasiga e mana'omia (fa'ailoga \"Fausia se tu'ufa'atasiga tu'ufa'atasi\" e fa'amalosi ai se tasi).",
    "merge.summary": "{files} faila(s) sui, {conflicts} fete'ena'iga(s).",
    "merge.conflictsWarning": "O lenei tu'ufa'atasiga e iai fete'ena'iga - e mafai lava ona e tu'ufa'atasia, ona fo'ia lea i le fa'atonu.",
    "merge.noChanges": "Leai se faila e suia.",
    "merge.files": "Fa'ai'uga suiga",
    "merge.added": "Faaopoopo",
    "merge.modified": "Suia",
    "merge.deleted": "Ave'esea",
    "merge.conflict": "Fete'ena'iga",
    "merge.message": "Fe'au tu'u fa'atasi",
    "merge.messagePlaceholder": "Tuufaatasi lala 'puna'",
    "merge.messageFfHint": "Le amana'ia i luga o se fa'atasi vave (leai se ta'utinoga e faia).",
    "merge.noFastForward": "Fausia se tu'ufa'atasiga tu'u fa'atasi (leai se aga'i i luma)",
    "merge.noFastForwardHint": "Faamaumau i taimi uma se tautinoga tu'ufa'atasi, tusa lava pe mafai e le paranesi ona fa'anatinati i luma.",
    "merge.merge": "Tuufaatasi",
    "merge.cancel": "Fa'aleaogaina",
    "merge.previewError": "Le mafai ona fuafuaina se mua'i va'aiga: {message}",
    "merge.squashBadge": "Skuasi",
    "merge.squashNote": "Squash merge - so'o se suiga e tula'i mai e pei ona ta'utino le TASI i le \"{target}\"; “{source}” lava ia ta'utinoga e tu'u ese mai lona tala'aga. Su'e i tua i Fa'atonu > Fa'atasi.",
    "merge.messageSquash": "Tuuina atu le savali",
    "merge.squashMessagePlaceholder": "Suiga fa'aletonu mai 'puna'",
    "newBranch.title": "Fausia Lala",
    "newBranch.startPoint": "Paranesi fou amata mai le {sha}",
    "newBranch.startPointOn": "ile {refs}",
    "newBranch.location": "Nofoaga",
    "newBranch.locationRoot": "(a'a)",
    "newBranch.expandAll": "Faalautele mea uma",
    "newBranch.collapseAll": "Fa'ato'ilalo uma",
    "newBranch.expandFolder": "Fa'alautele le faila",
    "newBranch.collapseFolder": "Pa'u le faila",
    "newBranch.name": "Igoa o le lala",
    "newBranch.namePlaceholder": "lo'u lala",
    "newBranch.fullName": "Igoa atoa: {name}",
    "newBranch.invalid": "Le aoga igoa lala",
    "newBranch.exists": "O lo'o iai se lala e iai lea igoa",
    "newBranch.checkout": "Siaki lala pe a uma ona faia",
    "newBranch.create": "Fausia",
    "newBranch.cancel": "Fa'aleaogaina",
    "status.loading": "Tu'u le kalafi…",
    "status.summary": "{repo}Fa'aali {commits} commit, {refs} refs",
    "status.branchCreated": "Fausia lala \"{name}\" ile {sha}",
    "status.error": "Sese: {message}",
    "status.opFailed": "Ua le manuia galuega",
    "status.fetching": "Aveina...",
    "status.pulling": "Toso…",
    "status.pushing": "tulei…",
    "status.committing": "Fausia se tautinoga fa'apitonu'u...",
    "status.commitCreated": "Fausia le ta'utinoga fa'apitonu'u {sha}: {message}",
    "status.syncing": "Fa'atasi...",
    "status.noHead": "Leai se siaki o iai nei na maua i le kalafi.",
    "status.undoing": "Ta'atia le tautinoga...",
    "status.commitUndone": "Tautino le le faia - suiga ua toe foʻi i le laau galue.",
    "status.undoConflict": "Fa'ate'aina fete'ena'iga - fo'ia i le fa'atonu, ona fa'aauau lea.",
    "status.stashApplying": "Talosaga teuga…",
    "status.stashPopping": "Fa'asalaina le teuina...",
    "status.stashDropping": "Tu'u ese le teuina...",
    "status.stashApplied": "Fa'aaoga le Stash.",
    "status.stashPopped": "Stash oso mai.",
    "status.stashDropped": "Ua pa'u le Stash.",
    "status.stashConflict": "Taofi feteenaiga - foia i latou i le faatonu.",
    "status.merging": "Tuufaatasia...",
    "status.merged": "Fa'atasi ua mae'a.",
    "status.mergeConflict": "E iai fete'ena'iga o le Tu'ufa'atasi - fo'ia i le faatonu, ona fai lea.",
    "search.title": "Saili",
    "search.tooltip": "Fai sa'iliga",
    "search.placeholder": "Su'esu'e faia...",
    "search.previous": "I'uga muamua",
    "search.next": "Le isi iuga",
    "search.noResults": "Leai se taunuuga",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filifiliga faiga",
    "search.highlight": "Fa'ailoga faiga",
    "search.filterMode": "Fa'aaliga su'e",
    "search.filterModeFilter": "Filifili (natia le fetaui)",
    "search.filterModeHighlight": "Fa'ailoga (fa'aali uma, fa'ailoga fetaui)",
    "search.filterModeHint": "Filifili pe fa'apefea ona fa'aalia i'uga su'esu'e ile kalafi.",
    "search.visibility": "Su'e nofoaga",
    "search.visibilityToolbar": "faamau meafaigaluega",
    "search.visibilityAlwaysVisible": "Pa e vaaia i taimi uma",
    "search.visibilityHint": "Fa'aali le su'esu'ega o se fa'ailoga meafaigaluega po'o le pa fa'aauau i le pito i luga.",
    "footer.github": "GitHub",
  },
  gl: {
    "toolbar.refresh": "⟳ Actualizar",
    "toolbar.fetch": "⤓ Busca",
    "toolbar.pull": "⇩ Tirar",
    "toolbar.push": "⇧ Preme",
    "toolbar.commit": "✓ Compromiso",
    "toolbar.sync": "⇅ Sincronización",
    "toolbar.jumpHead": "⌖ Ir á caixa",
    "toolbar.reset": "⤢ Restablecer vista",
    "toolbar.settings": "⚙ Configuración",
    "details.header": "Detalles do compromiso",
    "details.close": "Pechar",
    "details.sha": "SHA",
    "details.shortSha": "SHA curto",
    "details.message": "Mensaxe",
    "details.author": "Autor",
    "details.date": "Data",
    "details.labels": "Etiquetas",
    "details.location": "Localización",
    "details.currentHead": "HEAD: pago actual",
    "settings.title": "Configuración",
    "settings.language": "Linguaxe",
    "settings.theme": "Tema",
    "settings.themeLight": "Luz",
    "settings.themeDark": "Escuro",
    "settings.themeLightHint": "Fondo claro, texto escuro.",
    "settings.themeDarkHint": "Fondo escuro, texto claro.",
    "settings.mainBranch": "Rama principal",
    "settings.mainBranchAuto": "Automático",
    "settings.mainBranchSearch": "Busca ramas...",
    "settings.mainBranchNoMatch": "Non hai ramas coincidentes",
    "settings.display": "Estilo de visualización",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Clásico",
    "settings.displayModernHint": "Lenzo gratuíto: arrastra para panorámica, desprázate para ampliar.",
    "settings.displayClassicHint": "Lenzo fixo: tronco fixado á esquerda, sen zoom, só desprazamento (como o gráfico de revisión SVN).",
    "settings.sectionGeneral": "Xeral",
    "settings.sectionGraph": "Gráfico",
    "settings.sectionChanges": "Cambia a vista",
    "settings.diffMinimap": "Minimapa diferencial",
    "settings.diffMinimapOn": "Amosado",
    "settings.diffMinimapOff": "Oculto",
    "settings.diffMinimapOnHint": "Amosar unha franxa xeral de estilo VS Code ao lado da diferenza: arrástraa para desprazarse; marcadores de cambio incluídos.",
    "settings.diffMinimapOffHint": "Sen tira de visión xeral: desprácese pola diferenza normalmente.",
    "settings.commitReview": "Revisión de compromiso",
    "settings.commitReviewOn": "Pregunta antes de comprometerte",
    "settings.commitReviewOff": "Comprométete directamente",
    "settings.commitReviewOnHint": "Mostra unha lista final de ficheiros e unha comprobación de mensaxes antes de crear a confirmación local.",
    "settings.commitReviewOffHint": "O botón Commit crea a confirmación local inmediatamente.",
    "settings.svnBranchDialog": "Diálogo de ramas de estilo SVN",
    "settings.svnBranchDialogHint": "Amosar un selector de árbore de cartafoles ao crear unha rama (en lugar dunha simple solicitude).",
    "settings.branchDialog": "Diálogo de ramas",
    "settings.branchDialogSvnTitle": "Estilo SVN",
    "settings.branchDialogNativeVscode": "Estilo VS Code",
    "settings.branchDialogNativeVs": "Estilo Visual Studio",
    "settings.branchDialogNativeHint": "Indicador de rama integrado no teu IDE.",
    "settings.close": "Pechar",
    "settings.done": "Feito",
    "settings.sectionAdvanced": "Avanzado",
    "settings.gitSource": "Git executable",
    "settings.gitSourceBuiltin": "Integrado",
    "settings.gitSourceBuiltinHint": "Use o binario git da extensión Git integrada no IDE.",
    "settings.gitSourceCustom": "Camiño personalizado",
    "settings.gitPath": "Camiño a git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Explorar…",
    "settings.jargon": "Condicións de Git",
    "settings.jargonTranslate": "Traducir",
    "settings.jargonEnglish": "Mantéñase en inglés",
    "settings.jargonTranslateHint": "Traduce os termos de Git (pull, push, commit, branch...) ao idioma da interface.",
    "settings.jargonEnglishHint": "Manteña os termos de Git (pull, push, commit, branch...) en inglés; traducir todo o demais.",
    "settings.sectionMerge": "Combinar",
    "settings.mergeMode": "Combinar estilo",
    "settings.mergeModeMerge": "Fusión normal",
    "settings.mergeModeSquash": "Combinación de squash",
    "settings.mergeModeMergeHint": "Combina a rama tal e como está: os seus commits pasan a formar parte do historial da rama actual, unidos por un commit de fusión (ou avanzados rapidamente cando sexa posible).",
    "settings.mergeModeSquashHint": "Contrae toda a rama nunha única confirmación na rama actual (git merge --squash). As propias confirmacións da rama nunca entran no historial da rama actual; só ves unha confirmación alí. A propia rama queda intacta, polo que os seus compromisos permanecen visibles no seu propio carril.",
    "settings.mergedView": "Commits combinados no gráfico",
    "settings.mergedViewBranch": "Só na súa propia rama",
    "settings.mergedViewTarget": "Tamén na rama se fusionaron",
    "settings.mergedViewBranchHint": "Os compromisos dunha rama fusionada só se debuxan no seu propio carril; a fusión aparece como un conector. A vista clásica.",
    "settings.mergedViewTargetHint": "Os commits que se introducen nunha fusión tamén se debuxan no carril da rama receptora, apilados baixo o compromiso de fusión e ao nivel dos orixinais, porque esa rama realmente os contén agora. As copias son pálidas e encabezadas coa póla na que estaban escritas, polo que queda claro que non se fixeron alí. As fusións de squash non se ven afectadas: unha squash escribe un commit normal, polo que só aparece ese commit.",
    "legend.title": "Lenda",
    "legend.head": "CABEZA / rama actual",
    "legend.local": "Delegación local",
    "legend.remote": "Rama remota",
    "legend.remoteOnly": "Só na nube (non tirado)",
    "legend.tag": "Etiqueta (versión)",
    "legend.commit": "Comprometerse",
    "legend.stash": "Stash (traballo en estantes)",
    "legend.mergedIn": "Entrou cunha combinación (escrita noutra rama)",
    "legend.nodes": "Caixas",
    "legend.lines": "Liñas",
    "legend.edgeParent": "Commit anterior na mesma liña",
    "legend.edgeMerge": "Rama fusionada: a frecha apunta ao compromiso de combinación",
    "legend.edgeBranch": "Aquí se bifurcou unha rama",
    "legend.edgeStash": "Stash e o compromiso no que se gardou",
    "legend.edgeMergedChain": "A liña desta rama que atravesa os commits incorporados",
    "legend.edgeMergedTie": "O mesmo commit na rama na que estaba escrito",
    "menu.jumpHead": "⌖ Ir á caixa",
    "menu.resetView": "⤢ Restablecer vista",
    "menu.createBranch": "Crea unha sucursal desde aquí...",
    "menu.checkout": "Consulta este compromiso",
    "menu.copySha": "Copiar a confirmación SHA",
    "menu.pushBranch": "Push rama \"{name}\"",
    "menu.renameCommit": "Cambiar o nome da mensaxe de confirmación...",
    "menu.undoCommit": "Desfacer a confirmación (mantener os cambios)...",
    "menu.viewChanges": "Ver os cambios...",
    "menu.jumpToOriginal": "Vaia ao commit orixinal",
    "changes.title": "Cambios en {sha}",
    "changes.tabChanged": "Cambiado",
    "changes.tabAll": "Todos os ficheiros",
    "changes.added": "Engadido",
    "changes.modified": "Modificado",
    "changes.deleted": "Eliminado",
    "changes.renamed": "Renomeado",
    "changes.noChanges": "Esta confirmación non cambia ningún ficheiro.",
    "changes.loading": "Cargando diferenza...",
    "changes.selectFile": "Seleccione un ficheiro para ver os seus cambios.",
    "changes.original": "Orixinal",
    "changes.changed": "Este compromiso",
    "changes.binary": "Ficheiro binario: non hai diferenza de texto para mostrar.",
    "changes.tooLarge": "O ficheiro é demasiado grande para diferenciar.",
    "changes.renamedFrom": "Cambiou o nome de {path}",
    "changes.close": "Pechar",
    "changes.prevChange": "Cambio anterior",
    "changes.nextChange": "Próximo cambio",
    "changes.collapseAll": "Contraer todos os cartafoles",
    "changes.expandAll": "Expande todos os cartafoles",
    "changes.searchPlaceholder": "Buscar ficheiros...",
    "changes.noSearchResults": "Ningún ficheiro coincide coa túa busca.",
    "changes.maximize": "Maximizar",
    "changes.restore": "Restaurar tamaño",
    "find.open": "Busca nesta diferenza (Ctrl+F)",
    "find.placeholder": "Atopa en diferencia...",
    "find.prev": "Partido anterior",
    "find.next": "Próximo partido",
    "find.close": "Pechar a busca",
    "find.noResults": "Sen resultados",
    "commit.title": "Confirmar cambios",
    "commit.close": "Pechar",
    "commit.loading": "Lendo árbore de traballo...",
    "commit.noChanges": "Non hai cambios locais para comprometer.",
    "commit.selectFile": "Seleccione un ficheiro para ver a súa diferenza.",
    "commit.selectAll": "Selecciona todo",
    "commit.selectNone": "Borrar todo",
    "commit.message": "Mensaxe de compromiso",
    "commit.messagePlaceholder": "Describe o cambio",
    "commit.selectedCount": "Ficheiro(s) {count} seleccionado(s).",
    "commit.review": "Revisión",
    "commit.reviewTitle": "Comprobación final antes de cometer",
    "commit.reviewSummary": "Confirmar ficheiros {count} coa mensaxe: {message}",
    "commit.back": "De volta",
    "commit.commit": "Crear compromiso local",
    "menu.stashApply": "Aplicar o alijo",
    "menu.stashPop": "Pop stash (aplicar e eliminar)",
    "menu.stashDrop": "Soltar o alixo…",
    "merge.title": "Unir rama",
    "merge.route": "Unirse á rama actual",
    "merge.source": "De (fonte)",
    "merge.target": "En (actual)",
    "merge.loading": "Analizando fusión...",
    "merge.upToDate": "Xa está actualizado, nada que fusionar.",
    "merge.fastForward": "Avance rápido: non se precisa unha confirmación de combinación (marca \"Crear unha confirmación de combinación\" para forzar unha).",
    "merge.summary": "Ficheiro(s) {files} modificado(s), conflito(s) {conflicts}.",
    "merge.conflictsWarning": "Esta combinación ten conflitos; aínda podes fusionar e despois resolvelos no editor.",
    "merge.noChanges": "Non hai cambios no ficheiro.",
    "merge.files": "Cambios resultantes",
    "merge.added": "Engadido",
    "merge.modified": "Modificado",
    "merge.deleted": "Eliminado",
    "merge.conflict": "Conflito",
    "merge.message": "Combina a mensaxe de confirmación",
    "merge.messagePlaceholder": "Combina rama \"fonte\"",
    "merge.messageFfHint": "Ignorouse nunha combinación de avance rápido (non se crea ningunha confirmación).",
    "merge.noFastForward": "Crear un compromiso de combinación (sen avance rápido)",
    "merge.noFastForwardHint": "Grava sempre unha confirmación de combinación, aínda que a rama poida avanzar rapidamente.",
    "merge.merge": "Combinar",
    "merge.cancel": "Cancelar",
    "merge.previewError": "Non se puido calcular unha vista previa: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Combinación de squash: todos os cambios chegan como UNHA confirmación en \"{target}\"; Os propios compromisos de “{source}” quedan fóra da súa historia. Volve en Configuración › Combinar.",
    "merge.messageSquash": "Mensaxe de compromiso",
    "merge.squashMessagePlaceholder": "Cambios eliminados desde \"fonte\"",
    "newBranch.title": "Crear sucursal",
    "newBranch.startPoint": "Nova sucursal a partir de {sha}",
    "newBranch.startPointOn": "en {refs}",
    "newBranch.location": "Localización",
    "newBranch.locationRoot": "(raíz)",
    "newBranch.expandAll": "Amplía todo",
    "newBranch.collapseAll": "Contraer todo",
    "newBranch.expandFolder": "Expande o cartafol",
    "newBranch.collapseFolder": "Contraer cartafol",
    "newBranch.name": "Nome da sucursal",
    "newBranch.namePlaceholder": "a miña rama",
    "newBranch.fullName": "Nome completo: {name}",
    "newBranch.invalid": "Nome da sucursal non válido",
    "newBranch.exists": "Xa existe unha rama con este nome",
    "newBranch.checkout": "Rama de compra despois da creación",
    "newBranch.create": "Crear",
    "newBranch.cancel": "Cancelar",
    "status.loading": "Cargando gráfico...",
    "status.summary": "{repo}Mostrando confirmacións {commits}, referencias {refs}",
    "status.branchCreated": "Creouse a rama \"{name}\" en {sha}",
    "status.error": "Erro: {message}",
    "status.opFailed": "Fallou a operación",
    "status.fetching": "Buscando…",
    "status.pulling": "Tirando…",
    "status.pushing": "Empuxando…",
    "status.committing": "Creando compromiso local...",
    "status.commitCreated": "Commit local creado {sha}: {message}",
    "status.syncing": "Sincronizando…",
    "status.noHead": "Non se atopou ningunha compra actual no gráfico.",
    "status.undoing": "Desfacendo o compromiso...",
    "status.commitUndone": "Commit desfeito: os cambios están de novo na árbore de traballo.",
    "status.undoConflict": "Desfacer os conflitos de hits. Resolvaos no editor e, a continuación, continúa.",
    "status.stashApplying": "Aplicando o alixo…",
    "status.stashPopping": "Despegando o alixo...",
    "status.stashDropping": "Soltando o alixo...",
    "status.stashApplied": "Aplicouse o alixo.",
    "status.stashPopped": "O alixo apareceu.",
    "status.stashDropped": "Caeu o alixo.",
    "status.stashConflict": "Guarda conflitos: resólveos no editor.",
    "status.merging": "Fusionando…",
    "status.merged": "Completouse a fusión.",
    "status.mergeConflict": "A combinación ten conflitos: resólvaos no editor e, a continuación, comprométeos.",
    "search.title": "Busca",
    "search.tooltip": "Commits de busca",
    "search.placeholder": "A busca comprométese...",
    "search.previous": "Resultado anterior",
    "search.next": "Próximo resultado",
    "search.noResults": "Sen resultados",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modo de filtro",
    "search.highlight": "Modo de resaltado",
    "search.filterMode": "Pantalla de busca",
    "search.filterModeFilter": "Filtrar (ocultar non coincidencias)",
    "search.filterModeHighlight": "Resaltar (mostrar todo, marcar coincidencias)",
    "search.filterModeHint": "Escolle como se mostran os resultados da busca no gráfico.",
    "search.visibility": "Busca localización",
    "search.visibilityToolbar": "Botón da barra de ferramentas",
    "search.visibilityAlwaysVisible": "Barra sempre visible",
    "search.visibilityHint": "Mostra a busca como icona da barra de ferramentas ou barra persistente na parte superior.",
    "footer.github": "GitHub",
  },
  gd: {
    "toolbar.refresh": "⟳ Ùraich",
    "toolbar.fetch": "⤓ Thoir",
    "toolbar.pull": "⇩ Tarraing",
    "toolbar.push": "⇧ Brùth",
    "toolbar.commit": "✓ Geall",
    "toolbar.sync": "⇅ Sioncronaich",
    "toolbar.jumpHead": "⌖ Rach gu àite-pàighidh",
    "toolbar.reset": "⤢ Ath-shuidheachadh sealladh",
    "toolbar.settings": "⚙ Roghainnean",
    "details.header": "Mion-fhiosrachadh gealltanas",
    "details.close": "Dùin",
    "details.sha": "SHA",
    "details.shortSha": "SHA goirid",
    "details.message": "Teachdaireachd",
    "details.author": "Ughdar",
    "details.date": "Ceann-latha",
    "details.labels": "Bileagan",
    "details.location": "Àite",
    "details.currentHead": "HEAD - àite-pàighidh gnàthach",
    "settings.title": "Suidhichidhean",
    "settings.language": "Cànain",
    "settings.theme": "Cuspair",
    "settings.themeLight": "Solas",
    "settings.themeDark": "Dorch",
    "settings.themeLightHint": "Cùl-fhiosrachadh aotrom, teacsa dorcha.",
    "settings.themeDarkHint": "Cùl-raon dorcha, teacsa aotrom.",
    "settings.mainBranch": "Prìomh mheur",
    "settings.mainBranchAuto": "fèin-ghluasadach",
    "settings.mainBranchSearch": "Lorg meuran…",
    "settings.mainBranchNoMatch": "Gun mheuran co-ionnan",
    "settings.display": "Stoidhle taisbeanaidh",
    "settings.displayModern": "Ùr-nodha",
    "settings.displayClassic": "Clasaigeach",
    "settings.displayModernHint": "Canabhas an-asgaidh - slaod gu pan, sgrolaich gus gluasad.",
    "settings.displayClassicHint": "Canabhas stèidhichte - stoc air a phinnadh air chlì, gun zoom, scrollaich a-mhàin (mar graf ath-sgrùdaidh SVN).",
    "settings.sectionGeneral": "Coitcheann",
    "settings.sectionGraph": "Graf",
    "settings.sectionChanges": "Atharraich sealladh",
    "settings.diffMinimap": "Minimap eadar-dhealaichte",
    "settings.diffMinimapOn": "Air a shealltainn",
    "settings.diffMinimapOff": "Falaichte",
    "settings.diffMinimapOnHint": "Seall stiall tar-shealladh ann an stoidhle Còd VS ri taobh an diff - slaod e gus scrollaich; comharran atharrachaidh air an toirt a-steach.",
    "settings.diffMinimapOffHint": "Gun stiall ath-shealladh - sgrolaich an diff gu h-àbhaisteach.",
    "settings.commitReview": "Dèan lèirmheas air",
    "settings.commitReviewOn": "Faighnich mus dèan thu geall",
    "settings.commitReviewOff": "Dèan gealladh gu dìreach",
    "settings.commitReviewOnHint": "Seall liosta faidhle deireannach agus sgrùdadh teachdaireachd mus cruthaich thu an gealltanas ionadail.",
    "settings.commitReviewOffHint": "Bidh am putan Geall a’ cruthachadh a’ gheallaidh ionadail sa bhad.",
    "settings.svnBranchDialog": "Còmhradh meur ann an stoidhle SVN",
    "settings.svnBranchDialogHint": "Seall neach-togail craobh pasgan nuair a chruthaicheas tu meur (an àite sgiobalta sìmplidh).",
    "settings.branchDialog": "Còmhradh meòir",
    "settings.branchDialogSvnTitle": "SVN-stoidhle",
    "settings.branchDialogNativeVscode": "Stoidhle Còd VS",
    "settings.branchDialogNativeVs": "Stoidhle Studio Studio",
    "settings.branchDialogNativeHint": "Spreagadh meur togte an IDE agad.",
    "settings.close": "Dùin",
    "settings.done": "Dèanta",
    "settings.sectionAdvanced": "Adhartach",
    "settings.gitSource": "Git so-ghnìomhaichte",
    "settings.gitSourceBuiltin": "Air a thogail a-steach",
    "settings.gitSourceBuiltinHint": "Cleachd an git binary bho leudachadh Git togte an IDE.",
    "settings.gitSourceCustom": "Slighe àbhaisteach",
    "settings.gitSourceCustomHint": "Sònraich an t-slighe chun an gnìomh git agad fhèin (m.e. /usr/bin/git no C:\\…\\git.exe).",
    "settings.gitPath": "An t-slighe gu git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Dèan brobhsadh…",
    "settings.jargon": "Teirmean Git",
    "settings.jargonTranslate": "Eadar-theangaich",
    "settings.jargonEnglish": "Cùm sa Bheurla",
    "settings.jargonTranslateHint": "Eadar-theangaich teirmean Git (tarraing, putadh, geall, meur…) gu cànan an eadar-aghaidh.",
    "settings.jargonEnglishHint": "Cùm teirmean Git (tarraing, putadh, tiomnadh, meur…) sa Bheurla; eadar-theangachadh a h-uile càil eile.",
    "settings.sectionMerge": "Thig còmhla",
    "settings.mergeMode": "Stoidhle còmhla",
    "settings.mergeModeMerge": "Co-aonadh àbhaisteach",
    "settings.mergeModeSquash": "Co-aonadh squash",
    "settings.mergeModeMergeHint": "A’ ceangal a’ mheur mar a tha e: bidh na geallaidhean aige gu bhith nam pàirt de dh’ eachdraidh a’ mheur làithreach, còmhla ri gealltanas aonaidh (no air a chuir air adhart gu sgiobalta nuair as urrainnear).",
    "settings.mergeModeSquashHint": "A 'tuiteam a' mheur gu lèir gu aon ghealladh air a 'mheur làithreach (git merge --squash). Cha tèid geallaidhean na meòir fhèin a-steach do dh'eachdraidh a' mheur a th' ann an-dràsta - chan fhaic thu ach aon ghealladh an sin. Tha am meur fhèin air fhàgail gun suathadh, agus mar sin bidh a geallaidhean ri fhaicinn na sreath fhèin.",
    "settings.mergedView": "Geallaidhean aonaichte sa ghraf",
    "settings.mergedViewBranch": "A-mhàin air a 'mheur aca fhèin",
    "settings.mergedViewTarget": "Cuideachd anns a 'mheur chaidh an cur còmhla",
    "settings.mergedViewBranchHint": "Tha geallaidhean meòir aonaichte air an tarraing a-mhàin nan sreath fhèin; tha an aonadh a’ nochdadh mar cheangal. An sealladh clasaigeach.",
    "settings.mergedViewTargetHint": "Tha na geallaidhean co-aonadh a chaidh a thoirt a-steach air an tarraing ann an sreath a’ mheur a tha a’ faighinn cuideachd, air an càrnadh fon ghealladh aonaidh agus an ìre leis na tùsan - oir tha iad dha-rìribh anns a’ mheur sin a-nis. Tha na leth-bhreacan bàn agus air an ceann leis a 'mheur air an robh iad sgrìobhte, agus mar sin tha e soilleir nach deach an dèanamh an sin. Chan eil buaidh sam bith air co-aonaidhean squash: bidh squash a’ sgrìobhadh aon ghealladh àbhaisteach, agus mar sin chan eil ach an gealladh sin a’ nochdadh a-riamh.",
    "legend.title": "Uirsgeul",
    "legend.head": "CEANN / meur làithreach",
    "legend.local": "Meur ionadail",
    "legend.remote": "Meur iomallach",
    "legend.remoteOnly": "A-mhàin san sgòth (gun a tharraing)",
    "legend.tag": "Tag (dreach)",
    "legend.commit": "Geall",
    "legend.stash": "Stash (obair sgeilp)",
    "legend.mergedIn": "Thàinig mi a-steach le aonadh (sgrìobhte air meur eile)",
    "legend.nodes": "Bogsaichean",
    "legend.lines": "loidhnichean",
    "legend.edgeParent": "Gealltanas roimhe seo air an aon loidhne",
    "legend.edgeMerge": "Meur aonaichte - na puingean saighead aig a’ ghealladh aonaidh",
    "legend.edgeBranch": "Chaidh meur dheth an seo",
    "legend.edgeStash": "Stash agus an gealladh a chaidh a chuir air ais",
    "legend.edgeMergedChain": "Tha loidhne a' mheur seo a tha a' ruith tro na geallaidhean aonaichte",
    "legend.edgeMergedTie": "An aon ghealladh air a’ mheur air an robh e sgrìobhte",
    "menu.jumpHead": "⌖ Rach gu àite-pàighidh",
    "menu.resetView": "⤢ Ath-shuidheachadh sealladh",
    "menu.createBranch": "Cruthaich meur às an seo…",
    "menu.checkout": "Thoir sùil air a’ ghealladh seo",
    "menu.copySha": "Dèan lethbhreac de ghealladh SHA",
    "menu.pushBranch": "Brùth meur \"{name}\"",
    "menu.renameBranch": "Ath-ainmich meur \"{name}\" a¦",
    "menu.renameCommit": "Ath-ainmich teachdaireachd gealltanas…",
    "menu.undoCommit": "Cuir às do ghealladh (cumaibh na h-atharrachaidhean)…",
    "menu.viewChanges": "Seall atharraichean…",
    "menu.jumpToOriginal": "Rach don gheama tùsail",
    "node.mergedInTooltip": "Thug Merge {merge} an gealltanas seo a-steach do \"{target}\" - chaidh a sgrìobhadh air \"{origin}\".",
    "changes.title": "Atharraichean anns an {sha}",
    "changes.tabChanged": "Dh'atharraich",
    "changes.tabAll": "A h-uile Files",
    "changes.added": "Air a chur ris",
    "changes.modified": "Atharraichte",
    "changes.deleted": "Air a sguabadh às",
    "changes.renamed": "Air ath-ainmeachadh",
    "changes.noChanges": "Chan atharraich an gealladh seo faidhlichean sam bith.",
    "changes.loading": "A’ luchdachadh diofar…",
    "changes.selectFile": "Tagh faidhle airson na h-atharrachaidhean aige fhaicinn.",
    "changes.original": "tùsail",
    "changes.changed": "An tiomnadh seo",
    "changes.binary": "Faidhle dà-chànanach - chan eil teacsa eadar-dhealaichte ri shealltainn.",
    "changes.tooLarge": "Tha am faidhle ro mhòr airson eadar-dhealachadh.",
    "changes.renamedFrom": "Air ath-ainmeachadh bho {path}",
    "changes.close": "Dùin",
    "changes.prevChange": "Atharrachadh roimhe",
    "changes.nextChange": "An ath atharrachadh",
    "changes.collapseAll": "Lùghdaich a h-uile pasgan",
    "changes.expandAll": "Leudaich a h-uile pasgan",
    "changes.searchPlaceholder": "Rannsaich faidhlichean…",
    "changes.noSearchResults": "Chan eil faidhlichean sam bith a' freagairt ris an rannsachadh agad.",
    "changes.maximize": "Meudaich",
    "changes.restore": "Thoir air ais meud",
    "find.open": "Rannsaich san diff seo (Ctrl+F)",
    "find.placeholder": "Lorg ann an diofar…",
    "find.prev": "Geama roimhe",
    "find.next": "An ath gheama",
    "find.close": "Dùin an lorg",
    "find.noResults": "Gun thoraidhean",
    "commit.title": "Geall Atharrachaidhean",
    "commit.close": "Dùin",
    "commit.loading": "A’ leughadh craobh obrach…",
    "commit.noChanges": "Chan eil atharrachaidhean ionadail ri ghealltainn.",
    "commit.selectFile": "Tagh faidhle airson a diff.",
    "commit.selectAll": "Tagh a h-uile",
    "commit.selectNone": "Glan a h-uile",
    "commit.message": "Geall teachdaireachd",
    "commit.messagePlaceholder": "Thoir cunntas air an atharrachadh",
    "commit.selectedCount": "Faidhle (ean) {count} air an taghadh",
    "commit.review": "Lèirmheas",
    "commit.reviewTitle": "Sgrùdadh deireannach mus cuir thu geall",
    "commit.reviewSummary": "Cuir a-steach faidhle (ean) {count} le teachdaireachd: {message}",
    "commit.back": "Air ais",
    "commit.commit": "Cruthaich geall ionadail",
    "menu.stashApply": "Cuir stash an sàs",
    "menu.stashPop": "Pop stash (cuir a-steach & thoir air falbh)",
    "menu.stashDrop": "Leig às stash…",
    "merge.title": "Meur còmhla",
    "merge.route": "A 'tighinn còmhla ris a' mheur làithreach",
    "merge.source": "Bho (stòr)",
    "merge.target": "A-steach (an-dràsta)",
    "merge.loading": "A’ dèanamh anailis air co-aonadh…",
    "merge.upToDate": "Mar-thà ùraichte - chan eil dad ri chur còmhla.",
    "merge.fastForward": "Fast air adhart - chan eil feum air gealltanas co-aonaidh (cuir strìochag “Cruthaich gealltanas co-aonaidh” gus fear a sparradh).",
    "merge.summary": "Faidhle (ean) {files} air atharrachadh, còmhstri (ean) {conflicts}.",
    "merge.conflictsWarning": "Tha còmhstri aig a’ cho-aonadh seo - faodaidh tu fhathast tighinn còmhla, agus an uairsin fhuasgladh san deasaiche.",
    "merge.noChanges": "Chan eil faidhle air atharrachadh.",
    "merge.files": "Atharrachaidhean mar thoradh air",
    "merge.added": "Air a chur ris",
    "merge.modified": "Atharraichte",
    "merge.deleted": "Air a sguabadh às",
    "merge.conflict": "Còmhstri",
    "merge.message": "Thig còmhla teachdaireachd gealltanas",
    "merge.messagePlaceholder": "Merge meur 'stòr'",
    "merge.messageFfHint": "Air a leigeil seachad air aonadh luath (chan eil gealltanas air a chruthachadh).",
    "merge.noFastForward": "Cruthaich gealltanas co-aonaidh (gun a bhith air adhart gu luath)",
    "merge.noFastForwardHint": "Clàraich gealltanas co-aonaidh an-còmhnaidh, eadhon nuair a dh’ fhaodadh am meur gluasad air adhart.",
    "merge.merge": "Thig còmhla",
    "merge.cancel": "Sguir dheth",
    "merge.previewError": "Cha b' urrainn dhuinn ro-shealladh a thomhas: {message}",
    "merge.squashBadge": "Sguab",
    "merge.squashNote": "Co-aonadh squash - bidh gach atharrachadh a’ tighinn gu tìr mar AON a’ gealltainn air “{target}”; Tha geallaidhean “{source}” fhèin a’ fuireach a-mach às an eachdraidh aige. Tionndaidh air ais ann an Roghainnean › Merge.",
    "merge.messageSquash": "Geall teachdaireachd",
    "merge.squashMessagePlaceholder": "Atharraichean briste bho 'source'",
    "newBranch.title": "Cruthaich Meur",
    "newBranch.startPoint": "Meur ùr a’ tòiseachadh bho {sha}",
    "newBranch.startPointOn": "air {refs}",
    "newBranch.location": "Àite",
    "newBranch.locationRoot": "(freumh)",
    "newBranch.expandAll": "Leudaich a h-uile",
    "newBranch.collapseAll": "Lùghdaich a h-uile",
    "newBranch.expandFolder": "Leudaich am pasgan",
    "newBranch.collapseFolder": "Lùghdaich am pasgan",
    "newBranch.name": "Ainm meur",
    "newBranch.namePlaceholder": "mo-mheur",
    "newBranch.fullName": "Ainm slàn: {name}",
    "newBranch.invalid": "Ainm meur mì-dhligheach",
    "newBranch.exists": "Tha meur leis an ainm seo ann mu thràth",
    "newBranch.checkout": "Meur checkout às deidh cruthachadh",
    "newBranch.create": "Cruthaich",
    "newBranch.cancel": "Sguir dheth",
    "status.loading": "A’ luchdachadh graf…",
    "status.summary": "{repo}Showing {commits} gealltanas, {refs} refs",
    "status.branchCreated": "Meur air a chruthachadh \"{name}\" aig {sha}",
    "status.error": "Mearachd: {message}",
    "status.opFailed": "Dh'fhàillig an obrachadh",
    "status.fetching": "A’ faighinn…",
    "status.pulling": "A’ tarraing…",
    "status.pushing": "A’ putadh…",
    "status.committing": "A’ cruthachadh gealltanas ionadail…",
    "status.commitCreated": "Cruthaich gealltanas ionadail {sha}: {message}",
    "status.syncing": "A’ sioncronachadh…",
    "status.noHead": "Chan eil àite-pàighidh làithreach air a lorg sa ghraf.",
    "status.undoing": "A’ cur às do ghealladh…",
    "status.commitUndone": "Gealltanas gun a dhèanamh - tha atharrachaidhean air ais anns a’ chraobh obrach.",
    "status.undoConflict": "Thoir air falbh còmhstrithean - fuasgail iad san deasaiche, agus lean ort.",
    "status.stashApplying": "A’ cur stash an sàs…",
    "status.stashPopping": "A’ dùsgadh stash…",
    "status.stashDropping": "A’ leigeil sìos stash…",
    "status.stashApplied": "Stash air a chuir an sàs.",
    "status.stashPopped": "Chaidh stash suas.",
    "status.stashDropped": "Chaidh Stash sìos.",
    "status.stashConflict": "Còmhstri stash - fuasgail iad san deasaiche.",
    "status.merging": "A’ tighinn còmhla…",
    "status.merged": "Co-aonadh crìochnaichte.",
    "status.mergeConflict": "Tha còmhstri aig Merge - fuasgail iad san deasaiche, an uairsin geall.",
    "search.title": "Rannsaich",
    "search.tooltip": "Rannsachadh a’ gealltainn",
    "search.placeholder": "Tha rannsachadh a’ gealltainn…",
    "search.previous": "Toradh roimhe",
    "search.next": "An ath thoradh",
    "search.noResults": "Gun thoraidhean",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modh sìoltachain",
    "search.highlight": "Modh comharrachaidh",
    "search.filterMode": "Taisbeanadh lorg",
    "search.filterModeFilter": "Filter (falaich neo-gheamannan)",
    "search.filterModeHighlight": "Cuir cuideam air (seall a h-uile càil, comharraich maidsean)",
    "search.filterModeHint": "Tagh mar a tha toraidhean rannsachaidh air an taisbeanadh sa ghraf.",
    "search.visibility": "Lorg àite",
    "search.visibilityToolbar": "Putan a' Chrann Inneal",
    "search.visibilityAlwaysVisible": "Bàr an-còmhnaidh ri fhaicinn",
    "search.visibilityHint": "Seall rannsachadh mar ìomhaigh bàr-inneal no bàr leantainneach aig a’ mhullach.",
    "footer.github": "GitHub",
  },
  ha: {
    "toolbar.refresh": "⟳ Sake sabuntawa",
    "toolbar.fetch": "⤓ Kawo",
    "toolbar.pull": "⇩ Ja",
    "toolbar.push": "⇧ Tura",
    "toolbar.commit": "✓ Yin alkawari",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Je zuwa wurin biya",
    "toolbar.reset": "⤢ Sake saita duba",
    "toolbar.settings": "⚙ Saituna",
    "details.header": "Cikakkun bayanai",
    "details.close": "Kusa",
    "details.sha": "SHA",
    "details.shortSha": "Short SHA",
    "details.message": "Sako",
    "details.author": "Marubuci",
    "details.date": "Kwanan wata",
    "details.labels": "Lakabi",
    "details.location": "Wuri",
    "details.currentHead": "HEAD - wurin biya na yanzu",
    "settings.title": "Saituna",
    "settings.language": "Harshe",
    "settings.theme": "Jigo",
    "settings.themeLight": "Haske",
    "settings.themeDark": "Duhu",
    "settings.themeLightHint": "Haske mai haske, rubutu mai duhu.",
    "settings.themeDarkHint": "Bayani mai duhu, rubutu mai haske.",
    "settings.mainBranch": "Babban reshe",
    "settings.mainBranchAuto": "Na atomatik",
    "settings.mainBranchSearch": "Bincika rassan…",
    "settings.mainBranchNoMatch": "Babu rassan da suka dace",
    "settings.display": "Salon nuni",
    "settings.displayModern": "Na zamani",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Canvas kyauta - ja zuwa kwanon rufi, gungura don zuƙowa.",
    "settings.displayClassicHint": "Kafaffen zane - gangar jikin hagu, babu zuƙowa, gungurawa kawai (kamar jadawali na SVN).",
    "settings.sectionGeneral": "Gabaɗaya",
    "settings.sectionGraph": "Graph",
    "settings.sectionChanges": "Canje-canje gani",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "An nuna",
    "settings.diffMinimapOff": "Boye",
    "settings.diffMinimapOnHint": "Nuna tsiri bayyani na nau'in lambar VS kusa da bambancin - ja shi don gungurawa; alamar canji sun haɗa.",
    "settings.diffMinimapOffHint": "Babu tsiri bayyani - gungurawa sabani kullum.",
    "settings.commitReview": "Ƙaddamar da nazari",
    "settings.commitReviewOn": "Tambayi kafin aikata",
    "settings.commitReviewOff": "Aikata kai tsaye",
    "settings.commitReviewOnHint": "Nuna jerin fayil na ƙarshe da duba saƙo kafin ƙirƙirar ƙaddamarwar gida.",
    "settings.commitReviewOffHint": "Maɓallin Ƙaddamarwa yana ƙirƙirar ƙaddamarwa na gida nan da nan.",
    "settings.svnBranchDialog": "Maganganun salon SVN",
    "settings.svnBranchDialogHint": "Nuna babban fayil-bishiyar bishiyar lokacin ƙirƙirar reshe (maimakon sauƙi mai sauƙi).",
    "settings.branchDialog": "Maganar reshe",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "VS Code style",
    "settings.branchDialogNativeVs": "Salon Studio na gani",
    "settings.branchDialogNativeHint": "Ƙaddamarwar reshe na IDE ɗinku.",
    "settings.close": "Kusa",
    "settings.done": "Anyi",
    "settings.sectionAdvanced": "Na ci gaba",
    "settings.gitSource": "Git mai aiwatarwa",
    "settings.gitSourceBuiltin": "Gina-ciki",
    "settings.gitSourceBuiltinHint": "Yi amfani da git binary daga ginanniyar Git na IDE.",
    "settings.gitSourceCustom": "Hanyar al'ada",
    "settings.gitPath": "Hanyar zuwa git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Bincika…",
    "settings.jargon": "Git sharuddan",
    "settings.jargonTranslate": "Fassara",
    "settings.jargonEnglish": "Ci gaba cikin Turanci",
    "settings.jargonTranslateHint": "Fassara Git sharuɗɗan (ja, tura, ƙaddamarwa, reshe…) zuwa yaren mu'amala.",
    "settings.jargonEnglishHint": "Rike sharuɗɗan Git (ja, tura, ƙaddamarwa, reshe…) cikin Ingilishi; fassara komai.",
    "settings.sectionMerge": "Haɗa",
    "settings.mergeMode": "Haɗe salon",
    "settings.mergeModeMerge": "Haɗuwa ta al'ada",
    "settings.mergeModeSquash": "Squash hade",
    "settings.mergeModeMergeHint": "Yana haɗa reshe kamar yadda yake: ayyukansa sun zama wani ɓangare na tarihin reshe na yanzu, tare da haɗin gwiwar haɗin gwiwa (ko gaba da sauri idan zai yiwu).",
    "settings.mergeModeSquashHint": "Yana rushe reshe gabaɗaya zuwa sadaukarwa ɗaya akan reshe na yanzu (git merge --squash). Ayyukan reshe ba su taɓa shiga tarihin reshe na yanzu ba - ɗaya kawai kuke gani a can. Shi kansa reshen ba a taba shi ba, don haka ayyukansa ya kasance a bayyane a cikin nasa layin.",
    "settings.mergedView": "Haɗaɗɗen aikatawa a cikin jadawali",
    "settings.mergedViewBranch": "Sai a kan nasu reshen",
    "settings.mergedViewTarget": "Hakanan a cikin reshe an haɗa su cikin",
    "settings.mergedViewBranchHint": "Ayyukan reshe da aka haɗa ana zana su ne kawai ta hanyar nasu; hade yana nunawa azaman mai haɗawa. A classic view.",
    "settings.mergedViewTargetHint": "An zana ayyukan haɗin gwiwar da aka shigo da su a cikin layin reshe na karɓa kuma, an jera su a ƙarƙashin aikin haɗin gwiwa da daidaitawa tare da na asali - saboda wannan reshen yana ɗauke da su a yanzu. Kofin ba su da haske kuma suna tafiya tare da reshen da aka rubuta su, don haka ya tabbata ba a sanya su a can ba. Haɗin squash ba shi da tasiri: squash yana rubuta alƙawarin yau da kullun, don haka aikatawa kawai ya taɓa bayyana.",
    "legend.title": "Labari",
    "legend.head": "HEAD / reshe na yanzu",
    "legend.local": "Reshe na gida",
    "legend.remote": "Reshe mai nisa",
    "legend.remoteOnly": "Kawai a cikin gajimare (ba a ja)",
    "legend.tag": "Tag (version)",
    "legend.commit": "Aikata",
    "legend.stash": "Stash (aikin da aka ajiye)",
    "legend.mergedIn": "Ya shigo tare da haɗaka (an rubuta a wani reshe)",
    "legend.nodes": "Kwalaye",
    "legend.lines": "Layuka",
    "legend.edgeParent": "Aikata baya akan layi ɗaya",
    "legend.edgeMerge": "Haɗe-haɗe-reshe - kibiya tana nuna alamar haɗin gwiwa",
    "legend.edgeBranch": "Wani reshe ya tsinke a nan",
    "legend.edgeStash": "Stash da alƙawarin an tsara shi",
    "legend.edgeMergedChain": "Layin wannan reshe yana gudana ta cikin ayyukan da aka haɗa",
    "legend.edgeMergedTie": "Haka aka yi akan reshen da aka rubuta a kai",
    "menu.jumpHead": "⌖ Je zuwa wurin biya",
    "menu.resetView": "⤢ Sake saita duba",
    "menu.createBranch": "Ƙirƙiri reshe daga nan…",
    "menu.checkout": "Duba wannan ƙaddamarwa",
    "menu.copySha": "Kwafi SHA",
    "menu.pushBranch": "Tura reshe \"{name}\"",
    "menu.renameCommit": "Sake suna yiwa saƙo suna…",
    "menu.undoCommit": "Gyara alkawari (ci gaba da canje-canje)…",
    "menu.viewChanges": "Duba canje-canje…",
    "menu.mergeBranch": "Haɗa \"{source}\" zuwa \"{target}\"â",
    "menu.jumpToOriginal": "Je zuwa alƙawarin asali",
    "changes.title": "Canje-canje a cikin {sha}",
    "changes.tabChanged": "Canza",
    "changes.tabAll": "Duk Fayiloli",
    "changes.added": "Kara",
    "changes.modified": "Gyara",
    "changes.deleted": "An share",
    "changes.renamed": "Sake suna",
    "changes.noChanges": "Wannan aikace-aikacen ba ya canza fayiloli.",
    "changes.loading": "Ana loda bambanci…",
    "changes.selectFile": "Zaɓi fayil don ganin canje-canjensa.",
    "changes.original": "Na asali",
    "changes.changed": "Wannan alkawari",
    "changes.binary": "Fayil na binary - babu bambancin rubutu da zai nuna.",
    "changes.tooLarge": "Fayil ya yi girma don ya bambanta.",
    "changes.renamedFrom": "An sake suna daga {path}",
    "changes.close": "Kusa",
    "changes.prevChange": "Canjin baya",
    "changes.nextChange": "Canji na gaba",
    "changes.collapseAll": "Rushe duk manyan fayiloli",
    "changes.expandAll": "Fadada duk manyan fayiloli",
    "changes.searchPlaceholder": "Bincika fayilolin…",
    "changes.noSearchResults": "Babu fayiloli da suka dace da bincikenku.",
    "changes.maximize": "Girma",
    "changes.restore": "Maida girman",
    "find.open": "Bincika a cikin wannan bambancin (Ctrl+F)",
    "find.placeholder": "Nemo cikin bambanci…",
    "find.prev": "Wasan baya",
    "find.next": "Wasa na gaba",
    "find.close": "Rufe bincike",
    "find.noResults": "Babu sakamako",
    "commit.title": "Aiwatar da Canje-canje",
    "commit.close": "Kusa",
    "commit.loading": "Bishiyar tana karatu…",
    "commit.noChanges": "Babu canje-canje na gida da za a yi.",
    "commit.selectFile": "Zaɓi fayil don ganin bambancinsa.",
    "commit.selectAll": "Zaɓi duka",
    "commit.selectNone": "Share duk",
    "commit.message": "Aiwatar da saƙo",
    "commit.messagePlaceholder": "Bayyana canjin",
    "commit.selectedCount": "{count} fayil(s) da aka zaɓa",
    "commit.review": "Bita",
    "commit.reviewTitle": "Duban ƙarshe kafin ƙaddamarwa",
    "commit.reviewSummary": "Aiwatar da fayil(s) {count} tare da saƙo: {message}",
    "commit.back": "Baya",
    "commit.commit": "Ƙirƙiri alkawari na gida",
    "menu.stashApply": "Aiwatar stash",
    "menu.stashPop": "Pop stash (a shafa & cire)",
    "menu.stashDrop": "Ajiye tsutsa…",
    "merge.title": "Haɗa Reshe",
    "merge.route": "Haɗuwa cikin reshe na yanzu",
    "merge.source": "Daga (source)",
    "merge.target": "Cikin (na yanzu)",
    "merge.loading": "Ana nazarin haɗuwa…",
    "merge.upToDate": "Tuni har zuwa yau - babu abin da zai haɗu.",
    "merge.fastForward": "Saurin gaba - babu buƙatar haɗin kai da ake buƙata (latsa \"Ƙirƙirar ƙaddamarwa\" don tilasta ɗaya).",
    "merge.summary": "Fayilolin {files} sun canza, rikici(s) {conflicts}.",
    "merge.conflictsWarning": "Wannan haɗin yana da rikice-rikice - har yanzu kuna iya haɗuwa, sannan ku warware su a cikin edita.",
    "merge.noChanges": "Babu fayil da ya canza.",
    "merge.files": "Sakamakon canje-canje",
    "merge.added": "Kara",
    "merge.modified": "Gyara",
    "merge.deleted": "An share",
    "merge.conflict": "Rikici",
    "merge.message": "Haɗa ƙaddamar da saƙo",
    "merge.messagePlaceholder": "Haɗa 'source' reshe",
    "merge.messageFfHint": "An yi watsi da haɗin kai da sauri (ba a ƙirƙira wani alƙawari ba).",
    "merge.noFastForward": "Ƙirƙirar haɗin kai (babu mai sauri)",
    "merge.noFastForwardHint": "Koyaushe yin rikodin ƙaddamar da haɗin gwiwa, koda lokacin da reshe zai iya ci gaba da sauri.",
    "merge.merge": "Haɗa",
    "merge.cancel": "Soke",
    "merge.previewError": "An kasa yin lissafin samfoti: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash haɗe - kowane sauyi ƙasa kamar yadda DAYA yayi akan \"{target}\"; “{source}” nasa alƙawarin ya daina cikin tarihin sa. Komawa cikin Saituna › Haɗa.",
    "merge.messageSquash": "Aiwatar da saƙo",
    "merge.squashMessagePlaceholder": "Canje-canje daga 'source'",
    "newBranch.title": "Ƙirƙiri Reshe",
    "newBranch.startPoint": "Sabon reshe yana farawa daga {sha}",
    "newBranch.startPointOn": "ku {refs}",
    "newBranch.location": "Wuri",
    "newBranch.locationRoot": "(tushen)",
    "newBranch.expandAll": "Fadada duka",
    "newBranch.collapseAll": "Rushe duka",
    "newBranch.expandFolder": "Fadada babban fayil",
    "newBranch.collapseFolder": "Rushe babban fayil",
    "newBranch.name": "Sunan reshe",
    "newBranch.namePlaceholder": "reshe na",
    "newBranch.fullName": "Cikakken suna: {name}",
    "newBranch.invalid": "Sunan reshe mara inganci",
    "newBranch.exists": "Akwai reshe mai wannan sunan",
    "newBranch.checkout": "Duba reshe bayan halitta",
    "newBranch.create": "Ƙirƙiri",
    "newBranch.cancel": "Soke",
    "status.loading": "Ana loda jadawali…",
    "status.summary": "{repo}Showing {commits} aikata, {refs} refs",
    "status.branchCreated": "Ƙirƙiri reshe \"{name}\" a {sha}",
    "status.error": "Kuskure: {message}",
    "status.opFailed": "An kasa aiki",
    "status.fetching": "Ana ɗauko…",
    "status.pulling": "Ana ja…",
    "status.pushing": "Turawa…",
    "status.committing": "Ƙirƙirar alkawari na gida…",
    "status.commitCreated": "Ƙirƙirar ƙaddamarwa na gida {sha}: {message}",
    "status.syncing": "Ana daidaitawa…",
    "status.noHead": "Ba a sami abin dubawa na yanzu a cikin jadawali ba.",
    "status.undoing": "Cire alƙawarin…",
    "status.commitUndone": "Ƙaddamar da baya - canje-canje sun dawo cikin bishiyar aiki.",
    "status.undoConflict": "Gyara rikice-rikicen da aka buga - warware su a cikin editan, sannan a ci gaba.",
    "status.stashApplying": "Ana amfani da stash…",
    "status.stashPopping": "Tashin hankali…",
    "status.stashDropping": "Ana zubar da tari…",
    "status.stashApplied": "An yi amfani da stash.",
    "status.stashPopped": "Stash ya buge.",
    "status.stashDropped": "Stash ya fadi.",
    "status.stashConflict": "Rikici-rikice-rikice - warware su a cikin edita.",
    "status.merging": "Haɗa…",
    "status.merged": "An gama haɗawa.",
    "status.mergeConflict": "Haɗuwa yana da rikice-rikice - warware su a cikin edita, sannan ku aikata.",
    "search.title": "Bincika",
    "search.tooltip": "Bincike ya aikata",
    "search.placeholder": "Bincike yayi…",
    "search.previous": "Sakamakon baya",
    "search.next": "Sakamako na gaba",
    "search.noResults": "Babu sakamako",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Yanayin tacewa",
    "search.highlight": "Yanayin haskakawa",
    "search.filterMode": "Nemo nuni",
    "search.filterModeFilter": "Tace (boye marasa wasa)",
    "search.filterModeHighlight": "Haskakawa (nuna duka, alamar matches)",
    "search.filterModeHint": "Zaɓi yadda ake nuna sakamakon bincike a cikin jadawali.",
    "search.visibility": "Nemo wurin",
    "search.visibilityToolbar": "Maɓallin Toolbar",
    "search.visibilityAlwaysVisible": "Koyaushe - mashaya mai gani",
    "search.visibilityHint": "Nuna bincike azaman gunkin kayan aiki ko mashaya mai tsayi a saman.",
    "footer.github": "GitHub",
  },
  hmn: {
    "toolbar.refresh": "☞ Refresh",
    "toolbar.fetch": "⤓ Fetch",
    "toolbar.pull": "⇩ Rub",
    "toolbar.push": "⇧ Push",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Mus rau qhov nyiaj them poob haujlwm",
    "toolbar.reset": "⤢ Reset saib",
    "toolbar.settings": "⚙ Chaw",
    "details.header": "Kom Paub meej",
    "details.close": "Kaw",
    "details.sha": "SHA",
    "details.shortSha": "Luv SHA",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Tus sau",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Daim ntawv lo",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "HEAD — tam sim no daim tshev tawm",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Teeb",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Lub teeb tom qab, cov ntawv tsaus.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Cov ceg loj",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Nrhiav ceg…",
    "settings.mainBranchNoMatch": "Tsis muaj ceg sib txuam",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Niaj hnub",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Dawb canvas — luag rau lauj kaub, scroll rau zoom.",
    "settings.displayClassicHint": "Tsau canvas - pob tw pinned sab laug, tsis muaj qhov zoom, scroll nkaus xwb (xws li SVN kho cov duab).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Daim duab",
    "settings.sectionChanges": "Hloov saib",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Qhia",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Qhia ib VS Code-style txheej txheem cej luam strip ntawm ib sab ntawm qhov sib txawv - luag nws mus scroll; hloov cov cim suav nrog.",
    "settings.diffMinimapOffHint": "Tsis muaj txheej txheem txheej txheem - scroll qhov txawv ib txwm.",
    "settings.commitReview": "Kev tshuaj xyuas",
    "settings.commitReviewOn": "Nug ua ntej cog lus",
    "settings.commitReviewOff": "Ua ncaj qha",
    "settings.commitReviewOnHint": "Qhia ib daim ntawv teev cov ntaub ntawv zaum kawg thiab cov lus kos ua ntej tsim kev cog lus hauv zos.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "SVN-style ceg dialog",
    "settings.svnBranchDialogHint": "Qhia ib tug folder-ntoo picker thaum tsim ib ceg (es tsis yog ib qho yooj yim prompt).",
    "settings.branchDialog": "Ceg dialog",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "VS Code style",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Kaw",
    "settings.done": "Ua tiav",
    "settings.sectionAdvanced": "Advanced",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Ua-hauv",
    "settings.gitSourceBuiltinHint": "Siv cov git binary los ntawm IDE's built-in Git extension.",
    "settings.gitSourceCustom": "Txoj kev cai",
    "settings.gitSourceCustomHint": "Qhia meej txoj hauv kev rau koj tus kheej git executable (piv txwv li /usr/bin/git lossis C:\\⢦\\git.exe).",
    "settings.gitPath": "Txoj kev mus git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Tshawb nrhiav…",
    "settings.jargon": "Git cov ntsiab lus",
    "settings.jargonTranslate": "Txhais",
    "settings.jargonEnglish": "Khaws ua lus Askiv",
    "settings.jargonTranslateHint": "Txhais Git cov ntsiab lus (rub, thawb, cog lus, ceg…) rau hauv cov lus interface.",
    "settings.jargonEnglishHint": "Khaws Git cov ntsiab lus (rub, thawb, cog lus, ceg…) hauv lus Askiv; txhais txhua yam ntxiv.",
    "settings.sectionMerge": "Ua ke",
    "settings.mergeMode": "Ua ke style",
    "settings.mergeModeMerge": "Kev sib koom ua ke",
    "settings.mergeModeSquash": "Squash sib tov",
    "settings.mergeModeMergeHint": "Merges ceg raws li nws yog: nws cov lus cog tseg los ua ib feem ntawm cov ceg ntoo tam sim no keeb kwm, koom nrog kev sib koom ua ke (lossis nrawm nrawm thaum ua tau).",
    "settings.mergeModeSquashHint": "Collapses tag nrho ceg rau hauv ib qho kev cog lus ntawm ceg tam sim no (git merge --squash). Cov ceg ntoo ntawm nws tus kheej tsis tau nkag mus rau ceg ntoo tam sim no keeb kwm - koj tsuas pom ib qho kev cog lus rau ntawd. Lub ceg nws tus kheej yog sab laug untouched, yog li nws cov commits nyob twj ywm pom nyob rau hauv nws tus kheej txoj kab.",
    "settings.mergedView": "Kev sib koom ua ke cog lus hauv daim duab",
    "settings.mergedViewBranch": "Tsuas yog ntawm lawv tus kheej ceg",
    "settings.mergedViewTarget": "Tsis tas li ntawd nyob rau hauv ceg lawv tau merged rau hauv",
    "settings.mergedViewBranchHint": "Ib ceg koom ua ke cov lus cog tseg tsuas yog kos rau hauv lawv txoj kab xwb; kev sib koom ua ke qhia tau tias yog ib qho txuas. Classic saib.",
    "settings.mergedViewTargetHint": "Cov kev cog lus ib qho kev sib koom ua ke tau kos rau hauv cov ceg tau txais txoj kab ib yam nkaus, stacked nyob rau hauv kev sib koom ua ke cog lus thiab theem nrog cov thawj - vim hais tias ceg ntawd yeej muaj lawv tam sim no. Cov ntawv luam yog daj ntseg thiab taub hau nrog ceg lawv tau sau rau, yog li nws nyob ruaj khov lawv tsis tau ua rau ntawd. Kev sib koom ua ke ntawm Squash tsis muaj kev cuam tshuam: squash sau ib qho kev cog lus zoo tib yam, yog li tsuas yog qhov kev cog lus tau tshwm sim.",
    "legend.title": "Lus dab neeg",
    "legend.head": "HEAD / tam sim no ceg",
    "legend.local": "ceg hauv zos",
    "legend.remote": "Tej thaj chaw deb ceg",
    "legend.remoteOnly": "Tsuas yog hauv huab (tsis rub)",
    "legend.tag": "Tag (version)",
    "legend.commit": "cog lus",
    "legend.stash": "Stash (shelved ua haujlwm)",
    "legend.mergedIn": "Tau los nrog kev sib koom ua ke (sau rau lwm ceg)",
    "legend.nodes": "Lub thawv",
    "legend.lines": "Kab",
    "legend.edgeParent": "Yav dhau los cog lus rau tib kab",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Ib ceg fork tawm ntawm no",
    "legend.edgeStash": "Stash thiab kev cog lus nws tau muab tso rau",
    "legend.edgeMergedChain": "Cov kab no khiav los ntawm kev sib koom ua ke hauv kev cog lus",
    "legend.edgeMergedTie": "Ib yam kev cog lus ntawm ceg uas nws tau sau rau",
    "menu.jumpHead": "⌖ Mus rau qhov nyiaj them poob haujlwm",
    "menu.resetView": "⤢ Reset saib",
    "menu.createBranch": "Tsim ceg ntawm no…",
    "menu.checkout": "Tshawb xyuas qhov kev cog lus no",
    "menu.copySha": "Copy commit SHA",
    "menu.pushBranch": "Push ceg \"{name}\"",
    "menu.renameBranch": "Rename ceg \"{name}\"â ¦",
    "menu.deleteBranch": "Rho tawm ceg \"{name}\"â ¦",
    "menu.renameCommit": "Rename commit message…",
    "menu.undoCommit": "Undo cog lus (kuj hloov)…",
    "menu.viewChanges": "Saib cov kev hloov pauv…",
    "menu.mergeBranch": "Merge \"{source}\" rau \"{target}\"â ¦",
    "menu.jumpToOriginal": "Mus rau qhov kev cog lus qub",
    "changes.title": "Kev hloov pauv hauv {sha}",
    "changes.tabChanged": "Hloov",
    "changes.tabAll": "Tag nrho cov ntaub ntawv",
    "changes.added": "Ntxiv",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Rho tawm",
    "changes.renamed": "Hloov npe",
    "changes.noChanges": "Qhov kev cog lus no hloov tsis muaj cov ntaub ntawv.",
    "changes.loading": "Loading diff…",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Keeb kwm",
    "changes.changed": "Qhov no cog lus",
    "changes.binary": "Cov ntaub ntawv Binary - tsis muaj cov ntawv sib txawv los qhia.",
    "changes.tooLarge": "Cov ntaub ntawv loj dhau qhov txawv.",
    "changes.renamedFrom": "Renamed los ntawm {path}",
    "changes.close": "Kaw",
    "changes.prevChange": "Kev hloov dhau los",
    "changes.nextChange": "Tom ntej no hloov",
    "changes.collapseAll": "Kaw tag nrho cov folders",
    "changes.expandAll": "Nthuav tag nrho cov folders",
    "changes.searchPlaceholder": "Nrhiav cov ntaub ntawv…",
    "changes.noSearchResults": "Tsis muaj ntaub ntawv phim koj qhov kev tshawb nrhiav.",
    "changes.maximize": "Maximize",
    "changes.restore": "Restore loj",
    "find.open": "Nrhiav hauv qhov sib txawv no (Ctrl + F)",
    "find.placeholder": "Nrhiav hauv diff…",
    "find.prev": "Yav dhau los match",
    "find.next": "Tom ntej no match",
    "find.close": "Kaw kev tshawb nrhiav",
    "find.noResults": "Tsis muaj qhov tshwm sim",
    "commit.title": "Hloov pauv",
    "commit.close": "Kaw",
    "commit.loading": "Nyeem cov ntoo ua haujlwm…",
    "commit.noChanges": "Tsis muaj kev hloov hauv zos los cog lus.",
    "commit.selectFile": "Xaiv ib cov ntaub ntawv kom pom nws qhov sib txawv.",
    "commit.selectAll": "Xaiv tag nrho",
    "commit.selectNone": "Clear tag nrho",
    "commit.message": "Lus cog tseg",
    "commit.messagePlaceholder": "Piav qhov kev hloov",
    "commit.selectedCount": "{count} cov ntaub ntawv xaiv",
    "commit.review": "Saib xyuas",
    "commit.reviewTitle": "Kev kuaj zaum kawg ua ntej cog lus",
    "commit.reviewSummary": "Muab {count} cov ntaub ntawv nrog cov lus: {message}",
    "commit.back": "Rov qab",
    "commit.commit": "Tsim kev cog lus hauv zos",
    "menu.stashApply": "Thov khaws cia",
    "menu.stashPop": "Pop stash (thov thiab tshem tawm)",
    "menu.stashDrop": "Tshem tawm…",
    "merge.title": "Merge ceg",
    "merge.route": "Kev koom ua ke rau hauv ceg tam sim no",
    "merge.source": "Los ntawm (source)",
    "merge.target": "Nyob rau hauv (tam sim no)",
    "merge.loading": "Kev txheeb xyuas kev sib koom ua ke…",
    "merge.upToDate": "Twb mus txog hnub tim - tsis muaj dab tsi los ua ke.",
    "merge.fastForward": "Fast-forward - tsis muaj kev sib koom ua ke xav tau (zuam \"Tsim kev sib koom ua ke\" kom yuam ib tus).",
    "merge.summary": "{files} file(s) hloov, {conflicts} teeb meem(s).",
    "merge.conflictsWarning": "Qhov kev sib koom ua ke no muaj kev tsis sib haum xeeb - koj tuaj yeem sib koom ua ke, tom qab ntawd daws lawv hauv tus editor.",
    "merge.noChanges": "Tsis muaj ntaub ntawv hloov.",
    "merge.files": "Cov kev hloov tshwm sim",
    "merge.added": "Ntxiv",
    "merge.modified": "Hloov kho",
    "merge.deleted": "Rho tawm",
    "merge.conflict": "Kev tsis sib haum xeeb",
    "merge.message": "Merge cog lus",
    "merge.messagePlaceholder": "Merge ceg 'qhov chaw'",
    "merge.messageFfHint": "Tsis quav ntsej txog kev sib koom ua ke sai sai (tsis muaj kev cog lus tsim).",
    "merge.noFastForward": "Tsim kom muaj kev sib koom ua ke (tsis muaj nrawm nrawm)",
    "merge.noFastForwardHint": "Nco ntsoov sau ib qho kev sib koom ua ke, txawm tias thaum ceg ntoo tuaj yeem nrawm nrawm.",
    "merge.merge": "Ua ke",
    "merge.cancel": "Tso tseg",
    "merge.previewError": "Tsis tuaj yeem suav qhov saib ua ntej: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash sib koom ua ke - txhua qhov kev hloov pauv av raws li ib qho kev cog lus ntawm \"{target}\"; “{source}” tus kheej cov lus cog tseg nyob tsis deb ntawm nws keeb kwm. Hloov rov qab rau hauv Chaw> Merge.",
    "merge.messageSquash": "Lus cog tseg",
    "merge.squashMessagePlaceholder": "Squashed hloov los ntawm 'qhov chaw'",
    "newBranch.title": "Tsim ceg",
    "newBranch.startPoint": "Cov ceg tshiab pib los ntawm {sha}",
    "newBranch.startPointOn": "ntawm {refs}",
    "newBranch.location": "Qhov chaw",
    "newBranch.locationRoot": "(hauv paus)",
    "newBranch.expandAll": "Nthuav tag nrho",
    "newBranch.collapseAll": "Kaw tag nrho",
    "newBranch.expandFolder": "Nthuav daim nplaub tshev",
    "newBranch.collapseFolder": "Kaw lub nplaub tshev",
    "newBranch.name": "Ceg npe",
    "newBranch.namePlaceholder": "kuv ceg",
    "newBranch.fullName": "Lub npe: {name}",
    "newBranch.invalid": "Lub npe ceg tsis raug",
    "newBranch.exists": "Ib ceg nrog lub npe no twb muaj lawm",
    "newBranch.checkout": "Checkout ceg tom qab tsim",
    "newBranch.create": "Tsim",
    "newBranch.cancel": "Tso tseg",
    "status.loading": "Loading graph…",
    "status.summary": "{repo}Showing {commits} commits, {refs} refs",
    "status.branchCreated": "Tsim ceg \"{name}\" ntawm {sha}",
    "status.error": "yuam kev: {message}",
    "status.opFailed": "Kev ua haujlwm ua tsis tiav",
    "status.fetching": "Nrhiav…",
    "status.pulling": "rub…",
    "status.pushing": "Pushing…",
    "status.committing": "Tsim kev cog lus hauv zos…",
    "status.commitCreated": "Tsim kev cog lus hauv zos {sha}: {message}",
    "status.syncing": "Syncing…",
    "status.noHead": "Tsis muaj kev kuaj xyuas tam sim no hauv daim duab.",
    "status.undoing": "Undoing cog lus…",
    "status.commitUndone": "Ua tsis tiav - cov kev hloov pauv rov qab rau hauv tsob ntoo ua haujlwm.",
    "status.undoConflict": "Undo ntaus kev tsis sib haum xeeb - daws lawv hauv tus editor, tom qab ntawd txuas ntxiv.",
    "status.stashApplying": "Siv cov khoom siv…",
    "status.stashPopping": "Popping khoom…",
    "status.stashDropping": "Muab pov tseg…",
    "status.stashApplied": "Stash siv.",
    "status.stashPopped": "Stash popped.",
    "status.stashDropped": "Stash poob.",
    "status.stashConflict": "Kev tsis sib haum xeeb - daws lawv hauv tus editor.",
    "status.merging": "Kev sib koom ua ke…",
    "status.merged": "Ua ke ua tiav.",
    "status.mergeConflict": "Merge muaj kev tsis sib haum xeeb - daws lawv hauv tus editor, tom qab ntawd cog lus.",
    "search.title": "Nrhiav",
    "search.tooltip": "Nrhiav kev cog lus",
    "search.placeholder": "Nrhiav kev cog lus…",
    "search.previous": "Cov txiaj ntsig dhau los",
    "search.next": "Cov txiaj ntsig tom ntej",
    "search.noResults": "Tsis muaj qhov tshwm sim",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Lim hom",
    "search.highlight": "Highlight hom",
    "search.filterMode": "Nrhiav zaub",
    "search.filterModeFilter": "Filter ( zais qhov tsis sib xws)",
    "search.filterModeHighlight": "Highlight (qhia tag nrho, cim qhov sib tw)",
    "search.filterModeHint": "Xaiv yuav ua li cas nrhiav tau tshwm sim nyob rau hauv daim duab.",
    "search.visibility": "Nrhiav qhov chaw",
    "search.visibilityToolbar": "Toolbar khawm",
    "search.visibilityAlwaysVisible": "Ib txwm pom bar",
    "search.visibilityHint": "Qhia kev tshawb fawb raws li ib lub cuab yeej icon lossis lub bar tsis tu ncua nyob rau saum toj.",
    "footer.github": "GitHub",
  },
  hr: {
    "toolbar.refresh": "⟳ Osvježi",
    "toolbar.fetch": "⤓ Dohvati",
    "toolbar.pull": "⇩ Povucite",
    "toolbar.push": "⇧ Gurnite",
    "toolbar.commit": "✓ Obvežite se",
    "toolbar.sync": "⇅ Sinkronizacija",
    "toolbar.jumpHead": "⌖ Idi na blagajnu",
    "toolbar.reset": "⤢ Poništi prikaz",
    "toolbar.settings": "⚙ Postavke",
    "details.header": "Pojedinosti o uvrštavanju",
    "details.close": "Zatvoriti",
    "details.sha": "SHA",
    "details.shortSha": "Kratki SHA",
    "details.message": "Poruka",
    "details.author": "Autor",
    "details.date": "Datum",
    "details.labels": "Etikete",
    "details.location": "Mjesto",
    "details.currentHead": "GLAVA — trenutna blagajna",
    "settings.title": "postavke",
    "settings.language": "Jezik",
    "settings.theme": "Tema",
    "settings.themeLight": "Svjetlo",
    "settings.themeDark": "tamno",
    "settings.themeLightHint": "Svijetla pozadina, taman tekst.",
    "settings.themeDarkHint": "Tamna pozadina, svijetli tekst.",
    "settings.mainBranch": "Glavna grana",
    "settings.mainBranchAuto": "Automatski",
    "settings.mainBranchSearch": "Traži grane…",
    "settings.mainBranchNoMatch": "Nema odgovarajućih grana",
    "settings.display": "Stil prikaza",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "klasična",
    "settings.displayModernHint": "Slobodno platno — povucite za pomicanje, pomaknite se za zumiranje.",
    "settings.displayClassicHint": "Fiksno platno — deblo zakačeno lijevo, bez zumiranja, samo pomicanje (poput SVN revizijskog grafikona).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Grafikon",
    "settings.sectionChanges": "Mijenja pogled",
    "settings.diffMinimap": "Diff minimapa",
    "settings.diffMinimapOn": "prikazano",
    "settings.diffMinimapOff": "Skriven",
    "settings.diffMinimapOnHint": "Prikaži traku s pregledom u stilu VS koda pored razlike — povucite je za pomicanje; uključeni markeri promjena.",
    "settings.diffMinimapOffHint": "Nema pregledne trake — normalno pomičite razliku.",
    "settings.commitReview": "Obavezni pregled",
    "settings.commitReviewOn": "Pitajte prije obveze",
    "settings.commitReviewOff": "Obvezujte se izravno",
    "settings.commitReviewOnHint": "Prikaži konačni popis datoteka i provjeru poruka prije stvaranja lokalnog uvrštavanja.",
    "settings.commitReviewOffHint": "Gumb Commit odmah stvara lokalno preuzimanje.",
    "settings.svnBranchDialog": "Dijalog grananja u SVN stilu",
    "settings.svnBranchDialogHint": "Prikaži alat za odabir stabla mapa prilikom stvaranja grane (umjesto jednostavnog odziva).",
    "settings.branchDialog": "Dijalog grane",
    "settings.branchDialogSvnTitle": "SVN stil",
    "settings.branchDialogNativeVscode": "VS Code stil",
    "settings.branchDialogNativeVs": "Visual Studio stil",
    "settings.branchDialogNativeHint": "Prompt za granu ugrađen u vaš IDE.",
    "settings.close": "Zatvoriti",
    "settings.done": "Gotovo",
    "settings.sectionAdvanced": "Napredno",
    "settings.gitSource": "Git izvršna datoteka",
    "settings.gitSourceBuiltin": "Ugrađeni",
    "settings.gitSourceBuiltinHint": "Upotrijebite binarnu datoteku git iz proširenja Git ugrađenog u IDE.",
    "settings.gitSourceCustom": "Prilagođeni put",
    "settings.gitPath": "Put do gita",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Pregledaj...",
    "settings.jargon": "Git pojmovi",
    "settings.jargonTranslate": "Prevesti",
    "settings.jargonEnglish": "Čuvati na engleskom",
    "settings.jargonTranslateHint": "Prevedite Git termine (povuci, guraj, predaj, granaj...) u jezik sučelja.",
    "settings.jargonEnglishHint": "Zadržite Git izraze (povuci, guraj, počini, granaj…) na engleskom; prevodi sve ostalo.",
    "settings.sectionMerge": "Spojiti",
    "settings.mergeMode": "Stil spajanja",
    "settings.mergeModeMerge": "Normalno spajanje",
    "settings.mergeModeSquash": "Spajanje tikvica",
    "settings.mergeModeMergeHint": "Spaja granu onakvu kakva jest: njezina predaja postaje dio povijesti trenutne grane, pridružuje se predajom spajanja (ili premotavanjem unaprijed kada je to moguće).",
    "settings.mergeModeSquashHint": "Sažima cijelu granu u jednu objavu na trenutnoj grani (git merge --squash). Vlastite obveze grane nikada ne ulaze u povijest trenutne grane — tamo vidite samo jednu predaju. Sama grana je ostavljena netaknuta, tako da njezine obveze ostaju vidljive u vlastitoj traci.",
    "settings.mergedView": "Spojena predanja u grafu",
    "settings.mergedViewBranch": "Samo na svojoj grani",
    "settings.mergedViewTarget": "Također u grani u koju su spojeni",
    "settings.mergedViewBranchHint": "Obaveze spojene grane iscrtavaju se samo u vlastitoj stazi; spajanje se prikazuje kao konektor. Klasičan pogled.",
    "settings.mergedViewTargetHint": "Obaveze koje je donijelo spajanje također se iscrtavaju u grani primatelja, naslagane ispod predaje spajanja i u razini s originalima — jer ih ta grana sada stvarno sadrži. Kopije su blijede i usmjerene na granu na kojoj su ispisane, tako da ostaje jasno da nisu tamo nastale. Squash spajanja su nepromijenjena: squash piše jedan obični commit, tako da se samo taj commit pojavljuje.",
    "legend.title": "Legenda",
    "legend.head": "GLAVA / trenutna grana",
    "legend.local": "Lokalni ogranak",
    "legend.remote": "Udaljena grana",
    "legend.remoteOnly": "Samo u oblaku (nije povučeno)",
    "legend.tag": "Oznaka (verzija)",
    "legend.commit": "Počiniti",
    "legend.stash": "Skladište (posao na polici)",
    "legend.mergedIn": "Ušao sa spajanjem (napisano na drugoj grani)",
    "legend.nodes": "Kutije",
    "legend.lines": "Linije",
    "legend.edgeParent": "Prethodno predanje u istom retku",
    "legend.edgeMerge": "Spojena grana — strelica pokazuje na predaju spajanja",
    "legend.edgeBranch": "Ovdje se račvala grana",
    "legend.edgeStash": "Skladište i obveza na kojoj je bio odložen",
    "legend.edgeMergedChain": "Linija ove grane prolazi kroz spojena predavanja",
    "legend.edgeMergedTie": "Isti commit na grani na kojoj je napisan",
    "menu.jumpHead": "⌖ Idi na blagajnu",
    "menu.resetView": "⤢ Poništi prikaz",
    "menu.createBranch": "Stvori granu odavde...",
    "menu.checkout": "Provjerite ovu predaju",
    "menu.copySha": "Kopiraj predaju SHA",
    "menu.pushBranch": "Push grana \"{name}\"",
    "menu.renameCommit": "Preimenuj poruku predaje…",
    "menu.undoCommit": "Poništi predaju (zadrži promjene)…",
    "menu.viewChanges": "Prikaži promjene...",
    "menu.jumpToOriginal": "Idite na izvornu predaju",
    "changes.title": "Promjene u {sha}",
    "changes.tabChanged": "Promijenjeno",
    "changes.tabAll": "Sve datoteke",
    "changes.added": "Dodano",
    "changes.modified": "Izmijenjeno",
    "changes.deleted": "Izbrisano",
    "changes.renamed": "Preimenovano",
    "changes.noChanges": "Ovo uvrštavanje ne mijenja datoteke.",
    "changes.loading": "Učitavanje razlike…",
    "changes.selectFile": "Odaberite datoteku da vidite njezine promjene.",
    "changes.original": "Izvornik",
    "changes.changed": "Ovo počiniti",
    "changes.binary": "Binarna datoteka — nema tekstualne razlike za prikaz.",
    "changes.tooLarge": "Datoteka je prevelika za razlikovanje.",
    "changes.renamedFrom": "Preimenovano iz {path}",
    "changes.close": "Zatvoriti",
    "changes.prevChange": "Prethodna promjena",
    "changes.nextChange": "Sljedeća promjena",
    "changes.collapseAll": "Sažmi sve mape",
    "changes.expandAll": "Proširi sve mape",
    "changes.searchPlaceholder": "Traži datoteke…",
    "changes.noSearchResults": "Nijedna datoteka ne odgovara vašem pretraživanju.",
    "changes.maximize": "Maksimiziraj",
    "changes.restore": "Vrati veličinu",
    "find.open": "Traži u ovoj razlici (Ctrl+F)",
    "find.placeholder": "Pronađi u razlici…",
    "find.prev": "Prethodna utakmica",
    "find.next": "Sljedeća utakmica",
    "find.close": "Zatvori pretragu",
    "find.noResults": "Nema rezultata",
    "commit.title": "Obavijesti promjene",
    "commit.close": "Zatvoriti",
    "commit.loading": "Čitanje radnog stabla…",
    "commit.noChanges": "Nema lokalnih promjena za uvrštavanje.",
    "commit.selectFile": "Odaberite datoteku da biste vidjeli njezinu razliku.",
    "commit.selectAll": "Odaberite sve",
    "commit.selectNone": "Obriši sve",
    "commit.message": "Potvrdi poruku",
    "commit.messagePlaceholder": "Opišite promjenu",
    "commit.selectedCount": "{count} datoteka(e) odabrana",
    "commit.review": "Pregled",
    "commit.reviewTitle": "Konačna provjera prije izvršenja",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Nazad",
    "commit.commit": "Stvorite lokalno preuzimanje",
    "menu.stashApply": "Primijeni stash",
    "menu.stashPop": "Pop stash (primijeni i ukloni)",
    "menu.stashDrop": "Baci zalihu…",
    "merge.title": "Spoji granu",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "U (trenutno)",
    "merge.loading": "Analiziranje spajanja...",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Izmijenjeno",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Sukob",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Spoji granu 'izvor'",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Stvorite obvezu spajanja (bez premotavanja unaprijed)",
    "merge.noFastForwardHint": "Uvijek zabilježite predaju spajanja, čak i kada se grana može premotati unaprijed.",
    "merge.merge": "Spojiti",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Nije moguće izračunati pregled: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Zgnječene promjene iz 'izvora'",
    "newBranch.title": "Stvori granu",
    "newBranch.startPoint": "Nova grana počevši od {sha}",
    "newBranch.startPointOn": "na {refs}",
    "newBranch.location": "Mjesto",
    "newBranch.locationRoot": "(korijen)",
    "newBranch.expandAll": "Proširi sve",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Proširi mapu",
    "newBranch.collapseFolder": "Sažmi mapu",
    "newBranch.name": "Naziv podružnice",
    "newBranch.namePlaceholder": "moja-grana",
    "newBranch.fullName": "Puno ime: {name}",
    "newBranch.invalid": "Nevažeći naziv grane",
    "newBranch.exists": "Ogranak s ovim imenom već postoji",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Stvoriti",
    "newBranch.cancel": "Otkazati",
    "status.loading": "Učitavanje grafikona…",
    "status.summary": "{repo} Prikazuje {commits} predaje, {refs} reference",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Pogreška: {message}",
    "status.opFailed": "Operacija nije uspjela",
    "status.fetching": "Dohvaćanje...",
    "status.pulling": "Povlačenje...",
    "status.pushing": "Guranje...",
    "status.committing": "Stvaranje lokalne obveze…",
    "status.commitCreated": "Kreirano lokalno predanje {sha}: {message}",
    "status.syncing": "Sinkronizacija…",
    "status.noHead": "Na grafikonu nije pronađena trenutna naplata.",
    "status.undoing": "Poništavanje predaje...",
    "status.commitUndone": "Potvrda je poništena — promjene su ponovno u radnom stablu.",
    "status.undoConflict": "Poništavanje sukoba pogodaka — riješite ih u uređivaču, a zatim nastavite.",
    "status.stashApplying": "Primjena zalihe…",
    "status.stashPopping": "Iskakanje zalihe…",
    "status.stashDropping": "Bacanje zalihe...",
    "status.stashApplied": "Stash je primijenjen.",
    "status.stashPopped": "Stash je iskočio.",
    "status.stashDropped": "Stash je pao.",
    "status.stashConflict": "Stash sukobi — riješite ih u uređivaču.",
    "status.merging": "Spajanje...",
    "status.merged": "Spajanje dovršeno.",
    "status.mergeConflict": "Spajanje ima sukobe — riješite ih u uređivaču, a zatim izvršite.",
    "search.title": "Pretraživanje",
    "search.tooltip": "Traženje obvezuje",
    "search.placeholder": "Pretraživanje obvezuje...",
    "search.previous": "Prethodni rezultat",
    "search.next": "Sljedeći rezultat",
    "search.noResults": "Nema rezultata",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Način filtriranja",
    "search.highlight": "Način isticanja",
    "search.filterMode": "Prikaz pretraživanja",
    "search.filterModeFilter": "Filter (sakrij nepoklapanja)",
    "search.filterModeHighlight": "Istakni (prikaži sve, označi podudaranja)",
    "search.filterModeHint": "Odaberite kako se rezultati pretraživanja prikazuju na grafikonu.",
    "search.visibility": "Lokacija traženja",
    "search.visibilityToolbar": "Gumb alatne trake",
    "search.visibilityAlwaysVisible": "Uvijek vidljiva traka",
    "search.visibilityHint": "Prikaži pretraživanje kao ikonu alatne trake ili trajnu traku na vrhu.",
    "footer.github": "GitHub",
  },
  ig: {
    "toolbar.refresh": "⟳ nweta ume",
    "toolbar.fetch": "⤓ Weta",
    "toolbar.pull": "⇩ sere",
    "toolbar.push": "⇧ Pịa",
    "toolbar.commit": "✓ Nkwenye",
    "toolbar.sync": "⇅ Mmekọrịta",
    "toolbar.jumpHead": "⌖ Gaa na ndenye ego",
    "toolbar.reset": "⤢ Tọgharịa nlele",
    "toolbar.settings": "⚙ Ntọala",
    "details.header": "Nye nkọwa",
    "details.close": "Mechie",
    "details.sha": "SHA",
    "details.shortSha": "Short SHA",
    "details.message": "Ozi",
    "details.author": "Odee",
    "details.date": "Ụbọchị",
    "details.labels": "Akara aha",
    "details.location": "Ebe",
    "details.currentHead": "Isi - ndenye ọpụpụ ugbu a",
    "settings.title": "Ntọala",
    "settings.language": "Asụsụ",
    "settings.theme": "Isiokwu",
    "settings.themeLight": "Ìhè",
    "settings.themeDark": "Ọchịchịrị",
    "settings.themeLightHint": "Okirikiri ọkụ, ederede gbara ọchịchịrị.",
    "settings.themeDarkHint": "Okirikiri gbara ọchịchịrị, ederede ọkụ.",
    "settings.mainBranch": "Isi alaka",
    "settings.mainBranchAuto": "Akpaaka",
    "settings.mainBranchSearch": "Chọọ alaka…",
    "settings.mainBranchNoMatch": "Enweghị alaka dakọtara",
    "settings.display": "Ngosipụta ụdị",
    "settings.displayModern": "Oge a",
    "settings.displayClassic": "Omuma",
    "settings.displayModernHint": "Canvas efu - dọrọ gaa na pan, pịgharịa gaa na mbugharị.",
    "settings.displayClassicHint": "Kwaaji edobere — akpati etinyere aka ekpe, enweghị mbugharị, pịgharịa naanị (dị ka eserese SVN ntụgharị).",
    "settings.sectionGeneral": "Izugbe",
    "settings.sectionGraph": "Eserese",
    "settings.sectionChanges": "Na-agbanwe echiche",
    "settings.diffMinimap": "Minimap dị iche",
    "settings.diffMinimapOn": "Egosiri",
    "settings.diffMinimapOff": "Zoro ezo",
    "settings.diffMinimapOnHint": "Gosi warara nlele ụdị koodu VS n'akụkụ ihe dị iche - dọrọ ya ka ọ pịgharịa; akara mgbanwe gụnyere.",
    "settings.diffMinimapOffHint": "Enweghị mpempe nleba anya - pịgharịa n'ụdị dị iche.",
    "settings.commitReview": "Wepụta nyocha",
    "settings.commitReviewOn": "Jụọ tupu itinye aka",
    "settings.commitReviewOff": "Mee ozugbo",
    "settings.commitReviewOnHint": "Gosi ndepụta faịlụ ikpeazụ yana nlele ozi tupu ịmepụta nkwekọrịta mpaghara.",
    "settings.commitReviewOffHint": "Bọtịnụ Nkwekọrịta na-emepụta ntinye mpaghara ozugbo.",
    "settings.svnBranchDialog": "Mkparịta ụka alaka ụdị SVN",
    "settings.svnBranchDialogHint": "Gosi onye na-atụtụ osisi folda mgbe ị na-eke alaka (kama ngwa ngwa ngwa ngwa).",
    "settings.branchDialog": "Mkparịta ụka alaka",
    "settings.branchDialogSvnTitle": "Ụdị SVN",
    "settings.branchDialogNativeVscode": "Ụdị koodu VS",
    "settings.branchDialogNativeVs": "Ụdị Studio Visual",
    "settings.branchDialogNativeHint": "Mkpesa alaka ụlọ ọrụ arụnyere n'ime IDE gị.",
    "settings.close": "Mechie",
    "settings.done": "Emechaala",
    "settings.sectionAdvanced": "Nke dị elu",
    "settings.gitSource": "Git nwere ike ime ya",
    "settings.gitSourceBuiltin": "Wuru n'ime",
    "settings.gitSourceBuiltinHint": "Jiri git binary sitere na ndọtị Git arụnyere na IDE.",
    "settings.gitSourceCustom": "Ụzọ omenala",
    "settings.gitPath": "Ụzọ na git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Chọgharịa…",
    "settings.jargon": "Okwu git",
    "settings.jargonTranslate": "Tụgharịa asụsụ",
    "settings.jargonEnglish": "Debe na Bekee",
    "settings.jargonTranslateHint": "Tụgharịa asụsụ Git (dọrọ, ịkwanye, tinye, alaka…) n'ime asụsụ interface.",
    "settings.jargonEnglishHint": "Debe okwu Git (dọrọ, ịkwanye, tinye, alaka…) n'asụsụ Bekee; tụgharịa ihe niile ọzọ.",
    "settings.sectionMerge": "Jikota",
    "settings.mergeMode": "Jikota ụdị",
    "settings.mergeModeMerge": "Ngwakọta nkịtị",
    "settings.mergeModeSquash": "Ngwakọta skwọsh",
    "settings.mergeModeMergeHint": "Na-ejikọta alaka ahụ ka ọ dị: ọrụ ya na-aghọ akụkụ nke akụkọ ihe mere eme nke ngalaba ugbu a, jikọtara ya na ntinye aka (ma ọ bụ na-ebuga ngwa ngwa mgbe enwere ike).",
    "settings.mergeModeSquashHint": "Na-akụda alaka ụlọ ọrụ ahụ n'otu ntinye aka na ngalaba dị ugbu a (git merge --squash). Ọrụ nke alaka ụlọ ọrụ anaghị abanye n'akụkọ ihe mere eme nke ugbu a - naanị ị na-ahụ otu na-eme ebe ahụ. A na-ahapụ alaka ahụ n'onwe ya, n'ihi ya, a na-ahụ ihe omume ya n'ụzọ nke ya.",
    "settings.mergedView": "Ejikọtara eme na eserese ahụ",
    "settings.mergedViewBranch": "Naanị na alaka nke ha",
    "settings.mergedViewTarget": "Ọzọkwa n'alaka ụlọ ọrụ ha jikọtara n'ime",
    "settings.mergedViewBranchHint": "A na-adọta ọrụ alaka ụlọ ọrụ jikọtara ọnụ naanị n'ụzọ nke ha; Njikọ ahụ na-egosi dị ka njikọ. The kpochapụwo echiche.",
    "settings.mergedViewTargetHint": "A na-adọta njikọ ndị a na-ebute n'okporo ụzọ alaka ụlọ ọrụ na-anata, nke etinyere n'okpuru njikọ njikọ yana ọkwa nke mbụ - n'ihi na alaka ahụ nwere ha ugbu a. Mpempe akwụkwọ ndị ahụ chachapụrụ agbachapụ ma buru alaka ụlọ ọrụ e dere na ya, n'ihi ya, o doro anya na e meghị ha ebe ahụ. Ngwakọta skwọsh adịghị emetụta: skwọsh na-ede otu mmemme nkịtị, yabụ naanị nke ahụ ga-apụta.",
    "legend.title": "Akụkọ mgbe ochie",
    "legend.head": "Isi / ngalaba ugbu a",
    "legend.local": "Alaka mpaghara",
    "legend.remote": "Alaka dịpụrụ adịpụ",
    "legend.remoteOnly": "Naanị n'igwe ojii (anaghị adọta)",
    "legend.tag": "Mkpado (ụdị)",
    "legend.commit": "Nkwenye",
    "legend.stash": "Stash (ọrụ echekwara)",
    "legend.mergedIn": "Eji ngwakọta wee bata (nke e dere na alaka ọzọ)",
    "legend.nodes": "Igbe",
    "legend.lines": "Ahịrị",
    "legend.edgeParent": "Emere mbụ n'otu ahịrị",
    "legend.edgeMerge": "Ejikọtara-na alaka - na akụ na-atụ na jikota ime",
    "legend.edgeBranch": "Otu alaka gbapụrụ ebe a",
    "legend.edgeStash": "A na-echekwa stash na nkwa ya",
    "legend.edgeMergedChain": "Ahịrị alaka ụlọ ọrụ a na-agafe n'ime mmekọ agbakwunyere",
    "legend.edgeMergedTie": "Otu itinye aka na alaka ụlọ ọrụ e dere na ya",
    "menu.jumpHead": "⌖ Gaa na ndenye ego",
    "menu.resetView": "⤢ Tọgharịa nlele",
    "menu.createBranch": "Mepụta alaka si ebe a…",
    "menu.checkout": "Lelee nkwekọrịta a",
    "menu.copySha": "Detuo SHA",
    "menu.pushBranch": "Pịa alaka \"{name}\"",
    "menu.renameCommit": "Nyegharịa aha nyefe aha…",
    "menu.undoCommit": "Weghachite nkwa (dobe mgbanwe)…",
    "menu.viewChanges": "Lelee mgbanwe…",
    "menu.jumpToOriginal": "Gaa na nkwa mbụ",
    "changes.title": "Mgbanwe na {sha}",
    "changes.tabChanged": "Gbanwee",
    "changes.tabAll": "Faịlụ niile",
    "changes.added": "agbakwunyere",
    "changes.modified": "Emegharịrị",
    "changes.deleted": "ehichapụ",
    "changes.renamed": "Akpọgharịrị aha",
    "changes.noChanges": "Nkwekọrịta a anaghị agbanwe faịlụ ọ bụla.",
    "changes.loading": "Na-ebu ihe dị iche…",
    "changes.selectFile": "Họrọ faịlụ ka ịhụ mgbanwe ya.",
    "changes.original": "Nke mbụ",
    "changes.changed": "Nkwenye a",
    "changes.binary": "Faịlụ ọnụọgụ abụọ - enweghị ọdịiche ederede iji gosi.",
    "changes.tooLarge": "Faịlụ buru oke ibu ka ọ dị iche.",
    "changes.renamedFrom": "Akpọgharịrị aha ya site na {path}",
    "changes.close": "Mechie",
    "changes.prevChange": "Mgbanwe gara aga",
    "changes.nextChange": "Mgbanwe ọzọ",
    "changes.collapseAll": "Kpochapụ folda niile",
    "changes.expandAll": "Gbasaa folda niile",
    "changes.searchPlaceholder": "Chọọ faịlụ…",
    "changes.noSearchResults": "Ọ nweghị faịlụ dabara na nchọ gị.",
    "changes.maximize": "Bulie elu",
    "changes.restore": "Weghachi nha",
    "find.open": "Chọọ na diff a (Ctrl+F)",
    "find.placeholder": "Chọta na diff…",
    "find.prev": "Egwuregwu gara aga",
    "find.next": "Egwuregwu na-esote",
    "find.close": "Mechie ọchụchọ",
    "find.noResults": "Enweghị nsonaazụ ọ bụla",
    "commit.title": "Mee mgbanwe",
    "commit.close": "Mechie",
    "commit.loading": "Na-agụ osisi na-arụ ọrụ…",
    "commit.noChanges": "Enweghị mgbanwe mpaghara ime.",
    "commit.selectFile": "Họrọ faịlụ ka ịhụ ọdịiche ya.",
    "commit.selectAll": "Họrọ ha niile",
    "commit.selectNone": "Kpochapụ ihe niile",
    "commit.message": "Nyefee ozi",
    "commit.messagePlaceholder": "Kọwaa mgbanwe ahụ",
    "commit.selectedCount": "ahọpụtara faịlụ {count}",
    "commit.review": "Nyochaa",
    "commit.reviewTitle": "Nyocha ikpeazụ tupu ime ya",
    "commit.reviewSummary": "Jiri ozi nyefee faịlụ {count}: {message}",
    "commit.back": "Azu",
    "commit.commit": "Mepụta nkwa ime obodo",
    "menu.stashApply": "Tinye stash",
    "menu.stashPop": "Pop stash (tinye ma wepụ)",
    "menu.stashDrop": "Tụfuo mkpofu…",
    "merge.title": "Njikọ Alaka",
    "merge.route": "Na-ejikọta na ngalaba dị ugbu a",
    "merge.source": "Site na (isi iyi)",
    "merge.target": "N'ime (ugbu a)",
    "merge.loading": "Na-enyocha njikọ…",
    "merge.upToDate": "Ugbua emelitere - ọ nweghị ihe jikọrọ.",
    "merge.fastForward": "Na-aga n'ihu ngwa ngwa - enweghị nkwekọrịta ọ bụla achọrọ (pịa \"Mepụta nkwa nkwekọrịta\" iji manye otu).",
    "merge.summary": "gbanwere faịlụ {files}, esemokwu {conflicts}.",
    "merge.conflictsWarning": "Ngwakọta a nwere esemokwu - ị ka nwere ike ijikọ, wee dozie ha na nchịkọta akụkọ.",
    "merge.noChanges": "Enweghị mgbanwe faịlụ.",
    "merge.files": "Mgbanwe na-ebute",
    "merge.added": "agbakwunyere",
    "merge.modified": "Emegharịrị",
    "merge.deleted": "ehichapụ",
    "merge.conflict": "Esemokwu",
    "merge.message": "Jikota nyefe ozi",
    "merge.messagePlaceholder": "Jikota alaka 'isi iyi'",
    "merge.messageFfHint": "Elegharala anya na njikọta na-aga n'ihu (ọ nweghị nkwa emepụtara).",
    "merge.noFastForward": "Mepụta nkwekọrịta mmekọ ọnụ (ọ dịghị aga n'ihu ngwa ngwa)",
    "merge.noFastForwardHint": "Na-edekọ nkwakọ ọnụ mgbe niile, ọbụlagodi mgbe alaka ụlọ ọrụ nwere ike ịga n'ihu ngwa ngwa.",
    "merge.merge": "Jikota",
    "merge.cancel": "Kagbuo",
    "merge.previewError": "Enweghị ike ịgbakọ nlele: {message}",
    "merge.squashBadge": "Skwọsh",
    "merge.squashNote": "Njikọ Squash - ala mgbanwe ọ bụla dị ka ONE na-eme na \"{target}\"; Ihe omume nke “{source}” ga-apụ na akụkọ ntolite ya. Banye azụ na Ntọala › Jikọọ.",
    "merge.messageSquash": "Nyefee ozi",
    "merge.squashMessagePlaceholder": "Mgbanwe squashed site na 'isi iyi'",
    "newBranch.title": "Mepụta Alaka",
    "newBranch.startPoint": "Alaka ọhụrụ malite na {sha}",
    "newBranch.startPointOn": "na {refs}",
    "newBranch.location": "Ebe",
    "newBranch.locationRoot": "(mgbọrọgwụ)",
    "newBranch.expandAll": "Gbasaa ihe niile",
    "newBranch.collapseAll": "Kpochapụ ha niile",
    "newBranch.expandFolder": "Gbasaa folda",
    "newBranch.collapseFolder": "Kpochapụ folda",
    "newBranch.name": "Aha alaka",
    "newBranch.namePlaceholder": "ngalaba m",
    "newBranch.fullName": "Aha zuru oke: {name}",
    "newBranch.invalid": "Aha alaka na ezighi ezi",
    "newBranch.exists": "Alaka nwere aha a adịlarị",
    "newBranch.checkout": "Lelee alaka ụlọ ọrụ ka emechara",
    "newBranch.create": "Mepụta",
    "newBranch.cancel": "Kagbuo",
    "status.loading": "Na-ebu eserese…",
    "status.summary": "{repo}Showing {commits} eme, {refs} refs",
    "status.branchCreated": "Alaka emepụtara \"{name}\" na {sha}",
    "status.error": "Njehie: {message}",
    "status.opFailed": "Ọrụ dara",
    "status.fetching": "Na-ebute…",
    "status.pulling": "Na-adọta…",
    "status.pushing": "Na-agbanye…",
    "status.committing": "Na-emepe nkwado mpaghara…",
    "status.commitCreated": "Emebere mpaghara ime {sha}: {message}",
    "status.syncing": "Na-emekọrịta…",
    "status.noHead": "Enweghị ndenye ọpụpụ ugbu a na eserese ahụ.",
    "status.undoing": "Na-emezi nkwa…",
    "status.commitUndone": "Ekwela megharịa - mgbanwe alaghachila n'osisi na-arụ ọrụ.",
    "status.undoConflict": "Wepu esemokwu kụrụ - dozie ha na nchịkọta akụkọ, wee gaa n'ihu.",
    "status.stashApplying": "Na-etinye stash…",
    "status.stashPopping": "Mpempe akwụkwọ na-agbapụta…",
    "status.stashDropping": "Na-atụba stash…",
    "status.stashApplied": "etinyere stash.",
    "status.stashPopped": "Stash pụtara.",
    "status.stashDropped": "Stash kwụsịrị.",
    "status.stashConflict": "Esemokwu stash - dozie ha na nchịkọta akụkọ.",
    "status.merging": "Na-ejikọta…",
    "status.merged": "Ngwakọta agwụla.",
    "status.mergeConflict": "Njikọ nwere esemokwu - dozie ha na nchịkọta akụkọ, wee mee ya.",
    "search.title": "Chọọ",
    "search.tooltip": "Chọọ na-eme",
    "search.placeholder": "Ọchụchọ na-eme…",
    "search.previous": "Nsonaazụ gara aga",
    "search.next": "Nsonaazụ na-esote",
    "search.noResults": "Enweghị nsonaazụ ọ bụla",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Ụdị nzacha",
    "search.highlight": "Hụ ọnọdụ",
    "search.filterMode": "Chọọ ngosi",
    "search.filterModeFilter": "Filter (zoo ihe na-abụghị egwuregwu)",
    "search.filterModeHighlight": "Kwupụta ìhè (gosi ihe niile, akara dakọtara)",
    "search.filterModeHint": "Họrọ ka esi egosipụta nsonaazụ ọchụchọ na eserese ahụ.",
    "search.visibility": "Chọọ ebe",
    "search.visibilityToolbar": "bọtịnụ Toolbar",
    "search.visibilityAlwaysVisible": "Ogwe a na-ahụ anya mgbe niile",
    "search.visibilityHint": "Gosi ọchụchọ dị ka akara ngosi ngwaọrụ ma ọ bụ mmanya na-adịgide adịgide n'elu.",
    "footer.github": "GitHub",
  },
  rw: {
    "toolbar.refresh": "Fasha",
    "toolbar.fetch": "Fetch",
    "toolbar.pull": "Kurura",
    "toolbar.push": "⇧ Gusunika",
    "toolbar.commit": "Iyemeze",
    "toolbar.sync": "C Guhuza",
    "toolbar.jumpHead": "⌖ Jya kuri cheque",
    "toolbar.reset": "Ongera usubire kureba",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Iyemeze Ibisobanuro",
    "details.close": "Funga",
    "details.sha": "SHA",
    "details.shortSha": "SHA ngufi",
    "details.message": "Ubutumwa",
    "details.author": "Umwanditsi",
    "details.date": "Itariki",
    "details.labels": "Ibirango",
    "details.location": "Aho biherereye",
    "details.currentHead": "UMUTWE - kugenzura ubu",
    "settings.title": "Igenamiterere",
    "settings.language": "Ururimi",
    "settings.theme": "Insanganyamatsiko",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Umwijima",
    "settings.themeLightHint": "Amatara yoroheje, umwijima.",
    "settings.themeDarkHint": "Inyuma yijimye, inyandiko yoroheje.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Shakisha amashami…",
    "settings.mainBranchNoMatch": "Nta mashami ahuye",
    "settings.display": "Kwerekana uburyo",
    "settings.displayModern": "Ibigezweho",
    "settings.displayClassic": "Ibisanzwe",
    "settings.displayModernHint": "Canvas yubusa - gukurura isafuriya, kuzenguruka kuri zoom.",
    "settings.displayClassicHint": "Canvas ihamye - umutiba wometse ibumoso, nta zoom, kuzunguruka gusa (nkigishushanyo mbonera cya SVN).",
    "settings.sectionGeneral": "Jenerali",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Yerekanwa",
    "settings.diffMinimapOff": "Hihishe",
    "settings.diffMinimapOnHint": "Erekana VS Kode-yuburyo rusange yerekana umurongo kuruhande rwa diff - kurura kumuzingo; Guhindura ibimenyetso birimo.",
    "settings.diffMinimapOffHint": "Nta bisobanuro rusange - hindura itandukaniro mubisanzwe.",
    "settings.commitReview": "Ongera usubiremo",
    "settings.commitReviewOn": "Baza mbere yo kwiyemeza",
    "settings.commitReviewOff": "Iyemeze mu buryo butaziguye",
    "settings.commitReviewOnHint": "Erekana urutonde rwanyuma rwa dosiye nubutumwa bugenzura mbere yo gukora ibyo wiyemeje.",
    "settings.commitReviewOffHint": "Akabuto ko kwiyemeza gushiraho ako kanya ako kanya.",
    "settings.svnBranchDialog": "SVN-Imiterere yishami ryibiganiro",
    "settings.svnBranchDialogHint": "Erekana ububiko-bwibiti bitoragura mugihe uremye ishami (aho kugirango byoroshye).",
    "settings.branchDialog": "Ikiganiro",
    "settings.branchDialogSvnTitle": "SVN",
    "settings.branchDialogNativeVscode": "Imiterere ya VS",
    "settings.branchDialogNativeVs": "Imiterere ya Studio igaragara",
    "settings.branchDialogNativeHint": "IDE yawe yubatswe mumashami.",
    "settings.close": "Funga",
    "settings.done": "Byakozwe",
    "settings.sectionAdvanced": "Yateye imbere",
    "settings.gitSource": "Git birashoboka",
    "settings.gitSourceBuiltin": "Yubatswe",
    "settings.gitSourceBuiltinHint": "Koresha git binary uhereye kuri IDE yubatswe muri Git.",
    "settings.gitSourceCustom": "Inzira yihariye",
    "settings.gitSourceCustomHint": "Kugaragaza inzira igana git yawe wenyine (urugero: usr / bin / git cyangwa C:\\…\\git.exe).",
    "settings.gitPath": "Inzira yo kujya",
    "settings.gitPathPlaceholder": "/ usr / bin / git",
    "settings.gitPathBrowse": "Gushakisha…",
    "settings.jargon": "Amagambo ya Git",
    "settings.jargonTranslate": "Sobanura",
    "settings.jargonEnglish": "Komeza mucyongereza",
    "settings.jargonTranslateHint": "Sobanura amagambo ya Git (gukurura, gusunika, kwiyemeza, ishami…) mururimi rwimbere.",
    "settings.jargonEnglishHint": "Komeza amagambo ya Git (gukurura, gusunika, kwiyemeza, ishami…) mucyongereza; Sobanura ibindi byose.",
    "settings.sectionMerge": "Gukomatanya",
    "settings.mergeMode": "Guhuza uburyo",
    "settings.mergeModeMerge": "Guhuza bisanzwe",
    "settings.mergeModeSquash": "Kwishyira hamwe",
    "settings.mergeModeMergeHint": "Guhuza ishami uko rimeze: ibyo ryiyemeje bihinduka mumateka yishami ryubu, bifatanije no guhuriza hamwe (cyangwa byihuta-byihuse iyo bishoboka).",
    "settings.mergeModeSquashHint": "Gusenya ishami ryose mukwiyemeza kumashami yubu (git merge --squash). Ishami ryiyemeje ubwaryo ntirigera ryinjira mumateka yishami ryubu - urabona gusa umwe yiyemeje. Ishami ubwaryo risigaye ridakorwaho, bityo ibyo ryiyemeje biguma bigaragara mumurongo waryo.",
    "settings.mergedView": "Byahujwe byiyemeje mubishushanyo",
    "settings.mergedViewBranch": "Gusa ku ishami ryabo",
    "settings.mergedViewTarget": "No mu ishami bahujwe",
    "settings.mergedViewBranchHint": "Amashami yahujwe yiyemeje gushushanya gusa mumurongo wabo; ihuriro ryerekana nkumuhuza. Ibitekerezo bya kera.",
    "settings.mergedViewTargetHint": "Imihigo yahujwe yazanywe no kumurongo wakira amashami nayo, ashyizwe munsi yiyemeje guhuza hamwe nurwego rwumwimerere - kuko iryo shami rwose ririmo ubu. Amakopi afite ibara ryera kandi yerekejweho nishami ryanditseho, bityo bikaguma bigaragara ko bitakozwe aho. Kwishyira hamwe kwa squash ntaho bihuriye: squash yandika icyemezo kimwe gisanzwe, kubwibyo rero byiyemeje kugaragara.",
    "legend.title": "Umugani",
    "legend.head": "UMUTWE / ishami ryubu",
    "legend.local": "Ishami ryaho",
    "legend.remote": "Ishami rya kure",
    "legend.remoteOnly": "Gusa mu gicu (ntabwo gikururwa)",
    "legend.tag": "Tag (verisiyo)",
    "legend.commit": "Iyemeze",
    "legend.stash": "Stash (akazi kabitswe)",
    "legend.mergedIn": "Yaje hamwe no guhuza (byanditswe ku rindi shami)",
    "legend.nodes": "Agasanduku",
    "legend.lines": "Imirongo",
    "legend.edgeParent": "Kwiyemeza mbere kumurongo umwe",
    "legend.edgeMerge": "Ishami ryahujwe - ishami ryimyambi yo guhuza kwiyemeza",
    "legend.edgeBranch": "Ishami ryashize hano",
    "legend.edgeStash": "Komera no kwiyemeza byari byateganijwe",
    "legend.edgeMergedChain": "Umurongo w'iri shami unyuze mubikorwa byahujwe",
    "legend.edgeMergedTie": "Icyemezo kimwe ku ishami cyanditsweho",
    "menu.jumpHead": "⌖ Jya kuri cheque",
    "menu.resetView": "Ongera usubire kureba",
    "menu.createBranch": "Kora ishami kuva hano…",
    "menu.checkout": "Kugenzura iyi mihigo",
    "menu.copySha": "Gukoporora SHA",
    "menu.pushBranch": "Shyira ishami \"{name}\"",
    "menu.renameBranch": "Hindura ishami \"{name}\" â ¦",
    "menu.deleteBranch": "Siba ishami \"{name}\" â ¦",
    "menu.renameCommit": "Hindura izina ubutumwa bwiyemeje…",
    "menu.undoCommit": "Kuraho kwiyemeza (komeza impinduka)…",
    "menu.viewChanges": "Reba impinduka…",
    "menu.mergeBranch": "Huza \"{source}\" muri \"{target}\" â ¦",
    "menu.jumpToOriginal": "Jya ku cyemezo cya mbere",
    "node.mergedInTooltip": "Guhuza {merge} yazanye iki cyemezo muri \"{target}\" — cyanditswe kuri \"{origin}\".",
    "changes.title": "Impinduka muri {sha}",
    "changes.tabChanged": "Byahinduwe",
    "changes.tabAll": "Amadosiye yose",
    "changes.added": "Yongeyeho",
    "changes.modified": "Byahinduwe",
    "changes.deleted": "Gusiba",
    "changes.renamed": "Yahinduwe",
    "changes.noChanges": "Kwiyemeza guhindura nta dosiye.",
    "changes.loading": "Kuremera diff…",
    "changes.selectFile": "Hitamo dosiye kugirango urebe impinduka zayo.",
    "changes.original": "Umwimerere",
    "changes.changed": "Iyi mihigo",
    "changes.binary": "Idosiye ya binary - nta nyandiko itandukanye yo kwerekana.",
    "changes.tooLarge": "Idosiye nini cyane kuburyo itandukanye.",
    "changes.renamedFrom": "Yahinduwe kuva {path}",
    "changes.close": "Funga",
    "changes.prevChange": "Impinduka zabanje",
    "changes.nextChange": "Impinduka ikurikira",
    "changes.collapseAll": "Gusenya ububiko bwose",
    "changes.expandAll": "Kwagura ububiko bwose",
    "changes.searchPlaceholder": "Shakisha dosiye…",
    "changes.noSearchResults": "Nta dosiye ihuye nubushakashatsi bwawe.",
    "changes.maximize": "Mugure",
    "changes.restore": "Kugarura ingano",
    "find.open": "Shakisha muri iyi diff (Ctrl + F)",
    "find.placeholder": "Shakisha muri diff…",
    "find.prev": "Umukino ubanza",
    "find.next": "Umukino ukurikira",
    "find.close": "Shakisha hafi",
    "find.noResults": "Nta bisubizo",
    "commit.title": "Iyemeze Guhinduka",
    "commit.close": "Funga",
    "commit.loading": "Gusoma igiti gikora…",
    "commit.noChanges": "Nta mpinduka zaho zo kwiyemeza.",
    "commit.selectFile": "Hitamo dosiye kugirango urebe itandukaniro ryayo.",
    "commit.selectAll": "Hitamo byose",
    "commit.selectNone": "Kuraho byose",
    "commit.message": "Iyemeze ubutumwa",
    "commit.messagePlaceholder": "Sobanura impinduka",
    "commit.selectedCount": "Idosiye ya {count} yatoranijwe",
    "commit.review": "Isubiramo",
    "commit.reviewTitle": "Igenzura rya nyuma mbere yo kwiyemeza",
    "commit.reviewSummary": "Iyemeze dosiye (s) ya {count} hamwe n'ubutumwa: {message}",
    "commit.back": "Inyuma",
    "commit.commit": "Shiraho ibyo wiyemeje",
    "menu.stashApply": "Koresha stash",
    "menu.stashPop": "Igikoresho cya pop (gusaba & gukuraho)",
    "menu.stashDrop": "Kureka stash…",
    "merge.title": "Guhuza Ishami",
    "merge.route": "Kwishyira hamwe mumashami yubu",
    "merge.source": "Kuva (isoko)",
    "merge.target": "Muri (ubu)",
    "merge.loading": "Gusesengura guhuza…",
    "merge.upToDate": "Byarangiye bigezweho - ntakintu cyo guhuza.",
    "merge.fastForward": "Kwihuta-imbere - nta kwiyemeza gukenewe gukenewe (tike “Kurema guhuriza hamwe” guhatira umwe).",
    "merge.summary": "Idosiye ya {files} yarahindutse, amakimbirane ya {conflicts}.",
    "merge.conflictsWarning": "Uku guhuza bifite amakimbirane - urashobora gukomeza guhuza, hanyuma ukabikemura mubanditsi.",
    "merge.noChanges": "Nta dosiye ihinduka.",
    "merge.files": "Ibisubizo",
    "merge.added": "Yongeyeho",
    "merge.modified": "Byahinduwe",
    "merge.deleted": "Gusiba",
    "merge.conflict": "Amakimbirane",
    "merge.message": "Guhuza ubutumwa bwiyemeje",
    "merge.messagePlaceholder": "Guhuza ishami 'isoko'",
    "merge.messageFfHint": "Kwirengagiza kwihuta-imbere guhuza (nta kwiyemeza kuremwa).",
    "merge.noFastForward": "Kora icyemezo cyo guhuza (nta kwihuta-imbere)",
    "merge.noFastForwardHint": "Buri gihe wandike guhuza, nubwo ishami rishobora kwihuta-imbere.",
    "merge.merge": "Gukomatanya",
    "merge.cancel": "Kureka",
    "merge.previewError": "Ntushobora kubara ibyerekanwe: {message}",
    "merge.squashBadge": "Amashanyarazi",
    "merge.squashNote": "Guhuza squash - impinduka zose zigwa nkuko UMWE yiyemeje kuri “{target}”; “{source}” ibyo yiyemeje kutaguma mu mateka yayo. Subira inyuma muri Igenamiterere ›Guhuza.",
    "merge.messageSquash": "Iyemeze ubutumwa",
    "merge.squashMessagePlaceholder": "Impinduka zahinduwe kuva 'isoko'",
    "newBranch.title": "Kurema Ishami",
    "newBranch.startPoint": "Ishami rishya ritangirira kuri {sha}",
    "newBranch.startPointOn": "kuri {refs}",
    "newBranch.location": "Aho biherereye",
    "newBranch.locationRoot": "(umuzi)",
    "newBranch.expandAll": "Kwagura byose",
    "newBranch.collapseAll": "Gusenya byose",
    "newBranch.expandFolder": "Kwagura ububiko",
    "newBranch.collapseFolder": "Gusenya ububiko",
    "newBranch.name": "Izina ryishami",
    "newBranch.namePlaceholder": "ishami ryanjye",
    "newBranch.fullName": "Izina ryuzuye: {name}",
    "newBranch.invalid": "Izina ryishami ritemewe",
    "newBranch.exists": "Ishami rifite iri zina rimaze kubaho",
    "newBranch.checkout": "Ishami rya cheque nyuma yo kurema",
    "newBranch.create": "Kurema",
    "newBranch.cancel": "Kureka",
    "status.loading": "Igishushanyo mbonera…",
    "status.summary": "{repo}Kwerekana {commits} yiyemeje, {refs} ref",
    "status.branchCreated": "Hashyizweho ishami \"{name}\" kuri {sha}",
    "status.error": "Ikosa: {message}",
    "status.opFailed": "Igikorwa cyatsinzwe",
    "status.fetching": "Kubona…",
    "status.pulling": "Gukurura…",
    "status.pushing": "Gusunika…",
    "status.committing": "Gushiraho ibyemezo byaho…",
    "status.commitCreated": "Hashyizweho ibyemezo byibanze {sha}: {message}",
    "status.syncing": "Guhuza…",
    "status.noHead": "Nta cheque iriho iboneka mubishushanyo.",
    "status.undoing": "Kurangiza amasezerano…",
    "status.commitUndone": "Iyemeze gukuraho - impinduka zagarutse mugiti gikora.",
    "status.undoConflict": "Kuraho amakimbirane - ubikemure mubanditsi, hanyuma ukomeze.",
    "status.stashApplying": "Koresha stash…",
    "status.stashPopping": "Amashanyarazi yamashanyarazi…",
    "status.stashDropping": "Kureka stash…",
    "status.stashApplied": "Stash ikoreshwa.",
    "status.stashPopped": "Stash yagaragaye.",
    "status.stashDropped": "Stash yagabanutse.",
    "status.stashConflict": "Kuraho amakimbirane - ubikemure mubanditsi.",
    "status.merging": "Guhuriza hamwe…",
    "status.merged": "Gukomatanya birangiye.",
    "status.mergeConflict": "Gukomatanya bifite amakimbirane - ubikemure mubanditsi, hanyuma wiyemeze.",
    "search.title": "Shakisha",
    "search.tooltip": "Shakisha",
    "search.placeholder": "Gushakisha…",
    "search.previous": "Ibisubizo byabanje",
    "search.next": "Igisubizo gikurikira",
    "search.noResults": "Nta bisubizo",
    "search.resultCount": "{index} / {count}",
    "search.filter": "Akayunguruzo",
    "search.highlight": "Shyira ahagaragara uburyo",
    "search.filterMode": "Shakisha kwerekana",
    "search.filterModeFilter": "Akayunguruzo (guhisha ibitari bihuye)",
    "search.filterModeHighlight": "Shyira ahagaragara (erekana byose, shyira akamenyetso)",
    "search.filterModeHint": "Hitamo uburyo ibisubizo by'ishakisha bigaragara mu gishushanyo.",
    "search.visibility": "Shakisha ahantu",
    "search.visibilityToolbar": "Akabuto k'ibikoresho",
    "search.visibilityAlwaysVisible": "Buri gihe-igaragara",
    "search.visibilityHint": "Erekana gushakisha nkigikoresho cyibikoresho cyangwa umurongo uhoraho hejuru.",
    "footer.github": "GitHub",
  },
  ilo: {
    "toolbar.refresh": "⟳ Agpabaro",
    "toolbar.fetch": "⤓ Mangala",
    "toolbar.pull": "⇩ Guyoden ti",
    "toolbar.push": "⇧ Iduron",
    "toolbar.commit": "✓ Agkumit",
    "toolbar.sync": "⇅ Agsinkronisar",
    "toolbar.jumpHead": "⌖ Mapanka iti checkout",
    "toolbar.reset": "⤢ I-reset ti panagkita",
    "toolbar.settings": "⚙ Dagiti Pagsaad",
    "details.header": "Dagiti Detalye ti Panagkumit",
    "details.close": "Iserra",
    "details.sha": "SHA",
    "details.shortSha": "Ababa a SHA",
    "details.message": "Mensahe",
    "details.author": "Mannurat",
    "details.date": "Petsa",
    "details.labels": "Dagiti etiketa",
    "details.location": "Lokasion",
    "details.currentHead": "HEAD — agdama a panag-checkout",
    "settings.title": "Dagiti Setting",
    "settings.language": "Lengguahe",
    "settings.theme": "Amad",
    "settings.themeLight": "Silaw",
    "settings.themeDark": "Nasipnget",
    "settings.themeLightHint": "Nalawag a background, nasipnget a teksto.",
    "settings.themeDarkHint": "Nasipnget a background, nalawag a teksto.",
    "settings.mainBranch": "Kangrunaan a sanga",
    "settings.mainBranchAuto": "Automatiko nga",
    "settings.mainBranchSearch": "Agbirok kadagiti sanga...",
    "settings.mainBranchNoMatch": "Awan dagiti agtunos a sanga",
    "settings.display": "Estilo ti display",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Klasiko",
    "settings.displayModernHint": "Libre a canvas — i-drag iti pan, i-scroll tapno ag-zoom.",
    "settings.displayClassicHint": "Natarimaan ti canvas — ti baul ket naipit iti kannigid, awan ti zoom, ag-scroll laeng (kas ti grapo ti rebision ti SVN).",
    "settings.sectionGeneral": "Sapasap",
    "settings.sectionGraph": "Grapo",
    "settings.sectionChanges": "Baliwan ti panagkita",
    "settings.diffMinimap": "Diff nga minimap",
    "settings.diffMinimapOn": "Naipakita",
    "settings.diffMinimapOff": "Naidulin",
    "settings.diffMinimapOnHint": "Ipakita ti VS Code-style overview strip iti abay ti diff — i-drag daytoy tapno ag-scroll; nairaman dagiti marka ti panagbalbaliw.",
    "settings.diffMinimapOffHint": "Awan ti overview strip — i-scroll ti diff a gagangay.",
    "settings.commitReview": "Ikumit ti panangrepaso",
    "settings.commitReviewOn": "Agsaludsod sakbay nga agkumit",
    "settings.commitReviewOff": "Direkta nga agkumit",
    "settings.commitReviewOnHint": "Ipakita ti maudi a listaan ​​ti papeles ken panagsukimat ti mensahe sakbay a mangpartuat ti lokal a panagkumit.",
    "settings.commitReviewOffHint": "Ti buton ti Panagkumit ket mangpartuat ti lokal a panagkumit a dagus.",
    "settings.svnBranchDialog": "Dialogo ti sanga nga estilo ti SVN",
    "settings.svnBranchDialogHint": "Ipakita ti folder-tree picker no mangpartuat ti sanga (imbes a simple a prompt).",
    "settings.branchDialog": "Dialogo ti sanga",
    "settings.branchDialogSvnTitle": "Estilo ti SVN",
    "settings.branchDialogNativeVscode": "VS Estilo ti Kodigo",
    "settings.branchDialogNativeVs": "Estilo ti Visual Studio",
    "settings.branchDialogNativeHint": "Ti naibangon a sanga a prompt ti IDE-mo.",
    "settings.close": "Iserra",
    "settings.done": "Nalpasen",
    "settings.sectionAdvanced": "Naiyuna",
    "settings.gitSource": "Git nga maipatungpal",
    "settings.gitSourceBuiltin": "Naibangon ti uneg",
    "settings.gitSourceBuiltinHint": "Usaren ti git binary manipud iti naibangon a Git nga ekstension ti IDE.",
    "settings.gitSourceCustom": "Kostumbre a dalan",
    "settings.gitPath": "Dalan nga agturong iti git",
    "settings.gitPathPlaceholder": "/ usr / bin / git",
    "settings.gitPathBrowse": "Ag-browse iti...",
    "settings.jargon": "Dagiti termino ti git",
    "settings.jargonTranslate": "Itarus",
    "settings.jargonEnglish": "Idulin iti Ingles",
    "settings.jargonTranslateHint": "Ipatarus dagiti termino ti Git (pull, push, commit, branch...) iti pagsasao ti interface.",
    "settings.jargonEnglishHint": "Idulin dagiti termino ti Git (pull, push, commit, branch...) iti Ingles; ipatarus ti amin a dadduma pay.",
    "settings.sectionMerge": "Agtipon",
    "settings.mergeMode": "Estilo ti panagtipon",
    "settings.mergeModeMerge": "Normal a panagtipon",
    "settings.mergeModeSquash": "Pagtipon ti kalabasa",
    "settings.mergeModeMergeHint": "Pagtiponenna ti sanga a kas iti kasasaadna: dagiti panagkumitna ket agbalin a paset ti pakasaritaan ti agdama a sanga, a maitipon babaen ti panagtipon a panagkumit (wenno napardas a maipasango no mabalin).",
    "settings.mergeModeSquashHint": "Irakrak ti sibubukel a sanga iti maymaysa a panagkumit iti agdama a sanga (git merge --squash). Dagiti bukod a commit ti sanga ket pulos a di sumrek iti pakasaritaan ti agdama a sanga — maysa laeng a commit ti makitam sadiay. Ti sanga a mismo ket nabaybay-an a di masagid, isu a dagiti commit-na ket agtalinaed a makita iti bukodna a lane.",
    "settings.mergedView": "Nagtipon a panagkumit iti grapo",
    "settings.mergedViewBranch": "Iti laeng bukodda a sanga",
    "settings.mergedViewTarget": "Kasta met iti sanga a nakatiponanda",
    "settings.mergedViewBranchHint": "Dagiti panagkumit ti naikaykaysa a sanga ket maidrowing laeng iti bukodda a lane; ti panagtipon ket agparang a kas konektor. Ti klasiko a buya.",
    "settings.mergedViewTargetHint": "Dagiti panagkumit a naiyeg ti panagtipon ket naidrowing iti lane ti umaw-awat a sanga pay, naurnong iti baba ti panagkumit ti panagtipon ken maipada kadagiti orihinal — gapu ta dayta a sanga ket talaga a naglaon kadagitoy ita. Nalabaga dagiti kopia ken naturong iti sanga a nakaisuratanda, isu nga agtalinaed a nalawag a saan a naaramid sadiay. Dagiti panagtipon ti squash ket saan a maapektaran: ti maysa a squash ket agsurat ti maysa nga ordinario a panagkumit, isu a dayta laeng a panagkumit ket agparang.",
    "legend.title": "Tanda",
    "legend.head": "HEAD / agdama a sanga",
    "legend.local": "Lokal a sanga",
    "legend.remote": "Adayo a sanga",
    "legend.remoteOnly": "Iti laeng ulep (saan a naguyod) .",
    "legend.tag": "Tag (bersion) .",
    "legend.commit": "Italek",
    "legend.stash": "Stash (trabaho nga estante) .",
    "legend.mergedIn": "Simrek nga addaan iti merge (naisurat iti sabali a sanga) .",
    "legend.nodes": "Dagiti kahon",
    "legend.lines": "Dagiti linia",
    "legend.edgeParent": "Dati a panagkumit iti isu met laeng a linia",
    "legend.edgeMerge": "Nagtipon a sanga — ti pana ket mangitudo iti merge commit",
    "legend.edgeBranch": "Maysa a sanga ti nag-fork off ditoy",
    "legend.edgeStash": "Stash ken ti commit a naka-shelf dayta",
    "legend.edgeMergedChain": "Ti linia daytoy a sanga nga agtartaray kadagiti naikaykaysa a panagkumit",
    "legend.edgeMergedTie": "Isu met laeng a commit iti sanga a nakaisuratanna",
    "menu.jumpHead": "⌖ Mapanka iti checkout",
    "menu.resetView": "⤢ I-reset ti panagkita",
    "menu.createBranch": "Mangaramid ti sanga manipud ditoy...",
    "menu.checkout": "I-checkout daytoy a panagkumit",
    "menu.copySha": "Kopiaen ti commit SHA",
    "menu.pushBranch": "Iduron ti sanga \"{name}\".",
    "menu.renameCommit": "Baliwan ti nagan ti mensahe ti panagkumit...",
    "menu.undoCommit": "Undo commit (ipagtalinaeden dagiti panagbalbaliw)...",
    "menu.viewChanges": "Kitaen dagiti panagbalbaliw...",
    "menu.jumpToOriginal": "Mapanka iti orihinal a commit",
    "changes.title": "Dagiti panagbalbaliw ti {sha}",
    "changes.tabChanged": "Nagbaliw",
    "changes.tabAll": "Amin a File",
    "changes.added": "Nainayon",
    "changes.modified": "Nabaliwan",
    "changes.deleted": "Nabura",
    "changes.renamed": "Nabaliwan ti naganna",
    "changes.noChanges": "Daytoy a panagkumit ket mangbalbaliw kadagiti awan a file.",
    "changes.loading": "Ti panagkarga ti diff...",
    "changes.selectFile": "Pilien ti file tapno makita dagiti panagbalbaliwna.",
    "changes.original": "Orihinal",
    "changes.changed": "Daytoy a panagkumit",
    "changes.binary": "Binary file — awan ti teksto a diff nga ipakita.",
    "changes.tooLarge": "Dakkel unay ti file tapno diff.",
    "changes.renamedFrom": "Nabaliwan ti nagan manipud iti {path}",
    "changes.close": "Iserra",
    "changes.prevChange": "Dati a panagbalbaliw",
    "changes.nextChange": "Sumaruno a panagbalbaliw",
    "changes.collapseAll": "I-collapse amin a folder",
    "changes.expandAll": "Palawaen amin a folder",
    "changes.searchPlaceholder": "Agbirok kadagiti file...",
    "changes.noSearchResults": "Awan dagiti file a maitunos iti panagbirokmo.",
    "changes.maximize": "Maximize ti",
    "changes.restore": "Isubli ti kadakkel",
    "find.open": "Agbirok iti daytoy a diff (Ctrl+F) .",
    "find.placeholder": "Sapulen iti diff...",
    "find.prev": "Dati a panaglalaban",
    "find.next": "Sumaruno a laban",
    "find.close": "Serraan ti panagbirok",
    "find.noResults": "Awan ti resultana",
    "commit.title": "Mangikumit kadagiti Panagbalbaliw",
    "commit.close": "Iserra",
    "commit.loading": "Panagbasa nga agtartrabaho a kayo...",
    "commit.noChanges": "Awan dagiti lokal a panagbalbaliw a maikumit.",
    "commit.selectFile": "Pilien ti file tapno makita ti diff na.",
    "commit.selectAll": "Pilien amin",
    "commit.selectNone": "Clear amin",
    "commit.message": "Commit ti mensahe",
    "commit.messagePlaceholder": "Iladawan ti panagbalbaliw",
    "commit.selectedCount": "Napili ti (dagiti) file ti {count}",
    "commit.review": "Repasoen",
    "commit.reviewTitle": "Maudi a tseke sakbay ti panagkumit",
    "commit.reviewSummary": "Ikumit ti (dagiti) file ti {count} nga addaan iti mensahe: {message}",
    "commit.back": "Likud",
    "commit.commit": "Mangaramid ti lokal a panagkumit",
    "menu.stashApply": "I-apply ti stash",
    "menu.stashPop": "Pop stash (i-apply & ikkaten) .",
    "menu.stashDrop": "Drop ti stash...",
    "merge.title": "Pagtiponen ti Sanga",
    "merge.route": "Panagtipon iti agdama a sanga",
    "merge.source": "Manipud (gubuayan) .",
    "merge.target": "Iti (agdama) .",
    "merge.loading": "Panag-analisar ti panagtipon...",
    "merge.upToDate": "Sigud nga up to date — awan ti maitipon.",
    "merge.fastForward": "Napartak nga agpasango — awan ti kasapulan a panagkumit ti panagtipon (i-tick ti “Mangpartuat ti panagkumit ti panagtipon” tapno mapilit ti maysa).",
    "merge.summary": "Nabaliwan ti (dagiti) file ti {files}, (dagiti) panagsusupiat ti {conflicts}.",
    "merge.conflictsWarning": "Daytoy a panagtipon ket addaan kadagiti panagsusuppiat — mabalinmo pay laeng ti agtipon, kalpasanna risuten dagitoy iti editor.",
    "merge.noChanges": "Awan ti agbaliw ti file.",
    "merge.files": "Nagbanagan dagiti panagbalbaliw",
    "merge.added": "Nainayon",
    "merge.modified": "Nabaliwan",
    "merge.deleted": "Nabura",
    "merge.conflict": "Susik",
    "merge.message": "Pagtiponen ti mensahe ti panagkumit",
    "merge.messagePlaceholder": "Pagtiponen ti sanga nga 'gubuayan'.",
    "merge.messageFfHint": "Nailaksid iti napardas nga agpasango a panagtipon (awan ti naparsua a panagkumit).",
    "merge.noFastForward": "Mangaramid ti merge commit (awan ti fast-forward) .",
    "merge.noFastForwardHint": "Kanayon nga irekord ti merge commit, uray no ti sanga ket mabalin a napardas nga agpasango.",
    "merge.merge": "Agtipon",
    "merge.cancel": "Ikansela",
    "merge.previewError": "Saan a makakuenta ti maysa a preview: {message}",
    "merge.squashBadge": "Kalabasa",
    "merge.squashNote": "Squash merge — tunggal panagbalbaliw ket agdisso kas MAYSA a panagkumit iti “{target}”; Dagiti bukod a commits ti “{source}” ket agtalinaed iti ruar ti pakasaritaanna. Agsubli iti Settings › Pagtipon.",
    "merge.messageSquash": "Commit ti mensahe",
    "merge.squashMessagePlaceholder": "Na-squash a panagbalbaliw manipud iti 'gubuayan'.",
    "newBranch.title": "Mangbukel iti Sanga",
    "newBranch.startPoint": "Baro a sanga a mangrugi iti {sha}",
    "newBranch.startPointOn": "iti {refs}",
    "newBranch.location": "Lokasion",
    "newBranch.locationRoot": "(ramot)",
    "newBranch.expandAll": "Palawaen amin",
    "newBranch.collapseAll": "Marpuog amin",
    "newBranch.expandFolder": "Palawaen ti folder",
    "newBranch.collapseFolder": "I-collapse ti folder",
    "newBranch.name": "Nagan ti sanga",
    "newBranch.namePlaceholder": "my-sanga",
    "newBranch.fullName": "Buo a nagan: {name}",
    "newBranch.invalid": "Di umiso a nagan ti sanga",
    "newBranch.exists": "Addan ti sanga nga addaan iti daytoy a nagan",
    "newBranch.checkout": "Checkout branch kalpasan ti pannakaparsua",
    "newBranch.create": "Agaramid",
    "newBranch.cancel": "Ikansela",
    "status.loading": "Karga ti grapo...",
    "status.summary": "{repo}Ipakita ti {commits} a panagkumit, {refs} refs",
    "status.branchCreated": "Naparsua ti sanga a \"{name}\" iti {sha}",
    "status.error": "Biddut: {message}",
    "status.opFailed": "Napaay ti operasion",
    "status.fetching": "Fetching ti...",
    "status.pulling": "Pulling ti...",
    "status.pushing": "Iduron ti...",
    "status.committing": "Panagparnuay ti lokal a panagkumit...",
    "status.commitCreated": "Naparsua ti lokal a panagkumit {sha}: {message}",
    "status.syncing": "Panagsinkronisa...",
    "status.noHead": "Awan ti agdama a checkout a masarakan iti graph.",
    "status.undoing": "Undoing ti panagkumit...",
    "status.commitUndone": "Commit undone — dagiti panagbalbaliw ket nagsubli iti agtartrabaho a kayo.",
    "status.undoConflict": "Undo dagiti panagsusuppiat ti nadungpar — solbaren dagitoy iti editor, kalpasanna ituloy.",
    "status.stashApplying": "Panag-apply ti stash...",
    "status.stashPopping": "Popping nga stash...",
    "status.stashDropping": "Agtinnag ti stash...",
    "status.stashApplied": "Nag-apply ni Stash.",
    "status.stashPopped": "Nagtubo ni Stash.",
    "status.stashDropped": "Nagtinnag ni Stash.",
    "status.stashConflict": "Stash conflicts — risuten dagitoy iti editor.",
    "status.merging": "Panagtipon ti...",
    "status.merged": "Nalpasen ti panagtipon.",
    "status.mergeConflict": "Ti panagtipon ket addaan kadagiti panagsusuppiat — solbaren dagitoy iti editor, kalpasanna agkumit.",
    "search.title": "Agbirok",
    "search.tooltip": "Dagiti panagkumit ti panagsapul",
    "search.placeholder": "Dagiti panagkumit ti panagsapul...",
    "search.previous": "Dati a resulta",
    "search.next": "Sumaruno a resulta",
    "search.noResults": "Awan ti resultana",
    "search.resultCount": "{index}/{count} ti maysa",
    "search.filter": "Modo ti pagsala",
    "search.highlight": "Modo ti panangi-highlight",
    "search.filterMode": "Display ti panagbirok",
    "search.filterModeFilter": "Salaan (ilemmeng dagiti saan a maitunos) .",
    "search.filterModeHighlight": "I-highlight (ipakita amin, markaan dagiti panagtunos) .",
    "search.filterModeHint": "Pilien no kasano a maiparang dagiti resulta ti panagbirok iti graph.",
    "search.visibility": "Agbirok iti lokasion",
    "search.visibilityToolbar": "Buton ti toolbar",
    "search.visibilityAlwaysVisible": "Kanayon-makita a bar",
    "search.visibilityHint": "Ipakita ti panagsapul kas toolbar icon wenno persistent bar iti ngato.",
    "footer.github": "GitHub ti",
  },
  xh: {
    "toolbar.refresh": "⟳ Hlaziya",
    "toolbar.fetch": "⤓ Landa",
    "toolbar.pull": "⇩ Tsala",
    "toolbar.push": "⇧ Tyhala",
    "toolbar.commit": "✓ Zibophelele",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Yiya ekuphumeni",
    "toolbar.reset": "⤢ Seta kwakhona umbono",
    "toolbar.settings": "⚙ Useto",
    "details.header": "Zibophelele ngeNkcukacha",
    "details.close": "Vala",
    "details.sha": "SHA",
    "details.shortSha": "I-SHA emfutshane",
    "details.message": "Umyalezo",
    "details.author": "Umbhali",
    "details.date": "Umhla",
    "details.labels": "Iileyibhile",
    "details.location": "Indawo",
    "details.currentHead": "HEAD — uphume ngoku",
    "settings.title": "Iisetingi",
    "settings.language": "Ulwimi",
    "settings.theme": "Umxholo",
    "settings.themeLight": "Ukukhanya",
    "settings.themeDark": "Mnyama",
    "settings.themeLightHint": "Imvelaphi ekhanyayo, isicatshulwa esimnyama.",
    "settings.themeDarkHint": "Imvelaphi emnyama, isicatshulwa esikhanyayo.",
    "settings.mainBranch": "Isebe eliphambili",
    "settings.mainBranchAuto": "Iyazenzekela",
    "settings.mainBranchSearch": "Phendla amasebe...",
    "settings.mainBranchNoMatch": "Akukho masebe ahambelanayo",
    "settings.display": "Isimbo sokubonisa",
    "settings.displayModern": "Yangoku",
    "settings.displayClassic": "Yakudala",
    "settings.displayModernHint": "Iseyile yasimahla — tsala uye kwipani, skrola ukusondeza.",
    "settings.displayClassicHint": "Iseyile esisigxina — umboko uphiniwe ngasekhohlo, akukho sondeza, skrola kuphela (njengegrafu yohlaziyo ye-SVN).",
    "settings.sectionGeneral": "Ngokubanzi",
    "settings.sectionGraph": "Igrafu",
    "settings.sectionChanges": "Itshintsha imbonakalo",
    "settings.diffMinimap": "Imephu encinci eyahlukileyo",
    "settings.diffMinimapOn": "Ibonisiwe",
    "settings.diffMinimapOff": "Ifihliwe",
    "settings.diffMinimapOnHint": "Bonisa i-VS Code-style overview strip ecaleni kwe diff — yitsale ukuze uskrole; izimakishi zokutshintsha zibandakanyiwe.",
    "settings.diffMinimapOffHint": "Akukho luhlu lwesishwankathelo — skrola umahluko ngokwesiqhelo.",
    "settings.commitReview": "Zibophelele ngophononongo",
    "settings.commitReviewOn": "Buza ngaphambi kokuba wenze",
    "settings.commitReviewOff": "Zibophelele ngokuthe ngqo",
    "settings.commitReviewOnHint": "Bonisa uluhlu lokugqibela lwefayile kunye nokukhangela umyalezo phambi kokudala ukuzinikela kwendawo.",
    "settings.commitReviewOffHint": "Iqhosha lokuZibophelela lenza ukuzibophelela kwendawo ngoko nangoko.",
    "settings.svnBranchDialog": "Incoko yababini yesebe yesimbo se-SVN",
    "settings.svnBranchDialogHint": "Bonisa isikhethi somthi xa usenza isebe (endaweni yomyalelo olula).",
    "settings.branchDialog": "Ingxoxo yesebe",
    "settings.branchDialogSvnTitle": "Isimbo se-SVN",
    "settings.branchDialogNativeVscode": "Isimbo seKhowudi yeVS",
    "settings.branchDialogNativeVs": "Isimbo seStudio esibonakalayo",
    "settings.branchDialogNativeHint": "I-ID yakho eyakhelwe-ngaphakathi yesebe yokwazisa.",
    "settings.close": "Vala",
    "settings.done": "Ugqibile",
    "settings.sectionAdvanced": "NONE",
    "settings.gitSource": "IGit iyaphunyezwa",
    "settings.gitSourceBuiltin": "Yakhelwe ngaphakathi",
    "settings.gitSourceBuiltinHint": "Sebenzisa i-git yokubini ukusuka kwi-IDE eyakhelwe-ngaphakathi yolwandiso lweGit.",
    "settings.gitSourceCustom": "Indlela eqhelekileyo",
    "settings.gitPath": "Indlela eya kwi-git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Khangela...",
    "settings.jargon": "Imiqathango yeGit",
    "settings.jargonTranslate": "Guqulela",
    "settings.jargonEnglish": "Gcina ngesiNgesi",
    "settings.jargonTranslateHint": "Guqulela amagama eGit (tsala, tyhala, zibophelele, isebe...) kulwimi olujongana nayo.",
    "settings.jargonEnglishHint": "Gcina amagama e-Git (tsala, ukutyhala, ukuzibophelela, isebe…) ngesiNgesi; guqulela yonke enye into.",
    "settings.sectionMerge": "Hlanganisa",
    "settings.mergeMode": "Isimbo sokudibanisa",
    "settings.mergeModeMerge": "Ukudibanisa okuqhelekileyo",
    "settings.mergeModeSquash": "Ukudibanisa iSquash",
    "settings.mergeModeMergeHint": "Idibanisa isebe njengoko linjalo: izibophelelo zalo ziba yinxalenye yembali yesebe langoku, kudityaniswe nesibophelelo sokudityaniswa (okanye ukuthunyelwa ngokukhawuleza xa kunokwenzeka).",
    "settings.mergeModeSquashHint": "Ithoba isebe lonke kwisibophelelo esinye kwisebe langoku (git merge --squash). Izibophelelo zesebe azinakungena kwimbali yesebe langoku - ubona kuphela ukuzibophelela omnye apho. Isebe ngokwalo lishiywe lingachukunyiswanga, ngoko ukuzibophelela kwalo kuhlala kubonakala kwindlela yalo.",
    "settings.mergedView": "Ukudityaniswa kwezibophelelo kwigrafu",
    "settings.mergedViewBranch": "Kuphela kwisebe labo",
    "settings.mergedViewTarget": "Kwakhona kwisebe baye badityaniswa",
    "settings.mergedViewBranchHint": "Izibophelelo zesebe ezidityanisiweyo zizotywa kuphela kwindlela yazo; ukudibanisa kubonisa njengekhonkco. Umbono weklasikhi.",
    "settings.mergedViewTargetHint": "Udibaniso oluzisiweyo luzotywe kwindlela yesebe efumanayo ngokunjalo, lubekwe phantsi kwesibophelelo sodibaniso kunye nenqanaba kunye nemvelaphi - kuba elo sebe liziqulathile ngoku. Iikopi zimbatshile kwaye zinentloko yesebe ebezibhalwe kulo, kucace mhlophe ukuba ayenziwanga apho. Ukudityaniswa kwesquash akuchaphazeli: i-squash ibhala isibophelelo esinye esiqhelekileyo, ngoko kuphela kweso sibophelelo esivelayo.",
    "legend.title": "Intsomi",
    "legend.head": "INTLOKO / isebe langoku",
    "legend.local": "Isebe lasekuhlaleni",
    "legend.remote": "Isebe elikude",
    "legend.remoteOnly": "Efini kuphela (engatsalwanga)",
    "legend.tag": "Ithegi (uguqulelo)",
    "legend.commit": "Zibophelele",
    "legend.stash": "Stash (umsebenzi ogciniweyo)",
    "legend.mergedIn": "Kungene nokudibanisa (ibhalwe kwelinye isebe)",
    "legend.nodes": "Iibhokisi",
    "legend.lines": "Imigca",
    "legend.edgeParent": "Ukuzibophelela kwangaphambili kumgca omnye",
    "legend.edgeMerge": "Isebe elidityanisiweyo - utolo lukhomba kwisibophelelo sokudibanisa",
    "legend.edgeBranch": "Isebe lihlulwe apha",
    "legend.edgeStash": "I-Stash kunye nokuzibophelela kwayo kwabekelwe bucala",
    "legend.edgeMergedChain": "Lo mgca wesebe osebenza ngokudityaniswa-ngaphakathi kwezibophelelo",
    "legend.edgeMergedTie": "Isibophelelo esifanayo kwisebe ekubhalwe kulo",
    "menu.jumpHead": "⌖ Yiya ekuphumeni",
    "menu.resetView": "⤢ Seta kwakhona umbono",
    "menu.createBranch": "Yila isebe ukusuka apha...",
    "menu.checkout": "Khangela esi sibophelelo",
    "menu.copySha": "Khuphela ukuzinikela kwi-SHA",
    "menu.pushBranch": "Tyhila isebe \"{name}\"",
    "menu.renameCommit": "Phinda unike igama lomyalezo...",
    "menu.undoCommit": "Hlehlisa ukuzibophelela (gcina utshintsho)...",
    "menu.viewChanges": "Jonga utshintsho...",
    "menu.jumpToOriginal": "Yiya kwisibophelelo sokuqala",
    "changes.title": "Utshintsho lwe-{sha}",
    "changes.tabChanged": "Itshintshiwe",
    "changes.tabAll": "Zonke Iifayile",
    "changes.added": "Yongeziwe",
    "changes.modified": "Ilungisiwe",
    "changes.deleted": "Icinyiwe",
    "changes.renamed": "Inikwe elinye igama",
    "changes.noChanges": "Oku kuzibophelela akutshintshi zifayile.",
    "changes.loading": "Ilayisha umahluko...",
    "changes.selectFile": "Khetha ifayile ukubona utshintsho lwayo.",
    "changes.original": "Eyoqobo",
    "changes.changed": "Oku kuzibophelela",
    "changes.binary": "Ifayile yokubini — akukho mahluko abhaliweyo aza kuboniswa.",
    "changes.tooLarge": "Ifayile inkulu kakhulu ukuba idiff.",
    "changes.renamedFrom": "Inikwe elinye igama ukusuka kwi-{path}",
    "changes.close": "Vala",
    "changes.prevChange": "Utshintsho lwangaphambili",
    "changes.nextChange": "Utshintsho olulandelayo",
    "changes.collapseAll": "Sila zonke iifolda",
    "changes.expandAll": "Yandisa zonke iifolda",
    "changes.searchPlaceholder": "Phendla iifayile...",
    "changes.noSearchResults": "Akukho zifayile ezihambelana nophendlo lwakho.",
    "changes.maximize": "Yandisa",
    "changes.restore": "Buyisela ubungakanani",
    "find.open": "Phendla kule diff (Ctrl+F)",
    "find.placeholder": "Fumana kwidiff...",
    "find.prev": "Umdlalo odlulileyo",
    "find.next": "Umdlalo olandelayo",
    "find.close": "Vala uphendlo",
    "find.noResults": "Akukho ziphumo",
    "commit.title": "Yenza iinguqulelo",
    "commit.close": "Vala",
    "commit.loading": "Ifunda umthi osebenzayo...",
    "commit.noChanges": "Akukho zinguqu zasekuhlaleni zokuzibophelela.",
    "commit.selectFile": "Khetha ifayile ukubona umahluko.",
    "commit.selectAll": "Khetha Zonke",
    "commit.selectNone": "Yicime yonke",
    "commit.message": "Zibophelele ngomyalezo",
    "commit.messagePlaceholder": "Chaza utshintsho",
    "commit.selectedCount": "{count} iifayile ezikhethiweyo",
    "commit.review": "Uphononongo",
    "commit.reviewTitle": "Ukuhlolwa kokugqibela ngaphambi kokuzibophelela",
    "commit.reviewSummary": "Zibophelele {count} iifayile ezinomyalezo: {message}",
    "commit.back": "Emva",
    "commit.commit": "Yenza ukuzibophelela kwendawo",
    "menu.stashApply": "Faka i-stash",
    "menu.stashPop": "Pop stash (sebenzisa & susa)",
    "menu.stashDrop": "Lahla i-stash...",
    "merge.title": "Dibanisa iSebe",
    "merge.route": "Ukudibanisa kwisebe langoku",
    "merge.source": "Ukusuka (umthombo)",
    "merge.target": "Iya (ngoku)",
    "merge.loading": "Ihlalutya ukudibanisa...",
    "merge.upToDate": "Sele ihlaziywe - akukho nto yokudibanisa.",
    "merge.fastForward": "Ukuya phambili ngokukhawuleza - akukho sivumelwano sokudibanisa esifunekayo (phawula \"Yenza isibophelelo sokudibanisa\" ukunyanzela enye).",
    "merge.summary": "Iifayile ze-{files} zitshintshiwe, iingxabano ze-{conflicts}.",
    "merge.conflictsWarning": "Oku kudityaniswa kunongquzulwano - usenakho ukudibanisa, emva koko uzisombulule kumhleli.",
    "merge.noChanges": "Akukho lutshintsho lwefayile.",
    "merge.files": "Iziphumo zotshintsho",
    "merge.added": "Yongeziwe",
    "merge.modified": "Ilungisiwe",
    "merge.deleted": "Icinyiwe",
    "merge.conflict": "Ungquzulwano",
    "merge.message": "Hlanganisa umyalezo",
    "merge.messagePlaceholder": "Dibanisa isebe 'umthombo'",
    "merge.messageFfHint": "Ukungahoywa kudibaniso oluya phambili ngokukhawuleza (akukho sibophelelo senziweyo).",
    "merge.noFastForward": "Yenza isibophelelo sokudibanisa (akukho phambili ngokukhawuleza)",
    "merge.noFastForwardHint": "Soloko urekhoda isibophelelo sokudibanisa, nokuba isebe linokuqhubela phambili ngokukhawuleza.",
    "merge.merge": "Hlanganisa",
    "merge.cancel": "Rhoxisa",
    "merge.previewError": "Ayinakubala imboniso yangaphambili: {message}",
    "merge.squashBadge": "I-squash",
    "merge.squashNote": "Ukudibanisa kwesquash - lonke utshintsho luhlala njengoko OMNYE uzibophelele kwi \"{target}\"; \"{source}\"'s eyakhe izimisele ukuhlala ngaphandle kwembali yayo. Tshintsha kwakhona kwiSetingi › Dibanisa.",
    "merge.messageSquash": "Zibophelele ngomyalezo",
    "merge.squashMessagePlaceholder": "Utshintsho olucuthiweyo ukusuka 'kumthombo'",
    "newBranch.title": "Yenza iSebe",
    "newBranch.startPoint": "Isebe elitsha eliqala kwi-{sha}",
    "newBranch.startPointOn": "kwi {refs}",
    "newBranch.location": "Indawo",
    "newBranch.locationRoot": "(ingcambu)",
    "newBranch.expandAll": "Yandisa konke",
    "newBranch.collapseAll": "Zigobe zonke",
    "newBranch.expandFolder": "Yandisa ifolda",
    "newBranch.collapseFolder": "Sila isiqulathi seefayili",
    "newBranch.name": "Igama lesebe",
    "newBranch.namePlaceholder": "isebe lam",
    "newBranch.fullName": "Igama elipheleleyo: {name}",
    "newBranch.invalid": "Igama lesebe elingasebenziyo",
    "newBranch.exists": "Isebe elinali gama sele likhona",
    "newBranch.checkout": "Isebe le-Checkout emva kokudalwa",
    "newBranch.create": "Yenza",
    "newBranch.cancel": "Rhoxisa",
    "status.loading": "Ilayisha igrafu...",
    "status.summary": "{repo}Showing {commits} izibophelelo, {refs} refs",
    "status.branchCreated": "Yenziwe isebe \"{name}\" e-{sha}",
    "status.error": "Imposiso: {message}",
    "status.opFailed": "Umsebenzi awuphumelelanga",
    "status.fetching": "Iyalanda...",
    "status.pulling": "Iyatsala...",
    "status.pushing": "Iyatyhala...",
    "status.committing": "Kusenziwa isibophelelo sasekhaya...",
    "status.commitCreated": "Yenziwe yalapha ekhaya {sha}: {message}",
    "status.syncing": "Ingqamanisa...",
    "status.noHead": "Akukho ntlawulo yangoku ifunyenwe kwigrafu.",
    "status.undoing": "Ihlehlisa isibophelelo...",
    "status.commitUndone": "Ukuzibophelela akukwenziwanga - utshintsho lubuyele kumthi osebenzayo.",
    "status.undoConflict": "Hlehlisa ukungqubana okubethileyo — zisombulule kumhleli, emva koko uqhubeke.",
    "status.stashApplying": "Ifaka i-stash...",
    "status.stashPopping": "Kuvela i-stash...",
    "status.stashDropping": "Ilahla i-stash...",
    "status.stashApplied": "I-Stash ifakiwe.",
    "status.stashPopped": "Kwavela uStash.",
    "status.stashDropped": "I-Stash iwile.",
    "status.stashConflict": "Iingxabano ze-Stash - zisombulule kumhleli.",
    "status.merging": "Iyadibanisa...",
    "status.merged": "Ukudibanisa kugqityiwe.",
    "status.mergeConflict": "Ukudibanisa kuneengxabano-zisombulule kumhleli, emva koko uzibophelele.",
    "search.title": "Khangela",
    "search.tooltip": "Uphendlo luyazibophelela",
    "search.placeholder": "Uphendlo lubophelela...",
    "search.previous": "Iziphumo zangaphambili",
    "search.next": "Isiphumo esilandelayo",
    "search.noResults": "Akukho ziphumo",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Imo yokuhluza",
    "search.highlight": "Imowudi yokuqaqambisa",
    "search.filterMode": "Khangela umboniso",
    "search.filterModeFilter": "Hluza (fihla okungahambelaniyo)",
    "search.filterModeHighlight": "Gqamisa (bonisa zonke, phawula iimatshisi)",
    "search.filterModeHint": "Khetha ukuba iziphumo zophendlo ziboniswa njani kwigrafu.",
    "search.visibility": "Khangela indawo",
    "search.visibilityToolbar": "Iqhosha lebar yesixhobo",
    "search.visibilityAlwaysVisible": "Ibha ehlala ibonwa",
    "search.visibilityHint": "Bonisa uphendlo njenge icon yebar yesixhobo okanye ibha eqhubekayo phezulu.",
    "footer.github": "GitHub",
  },
  zu: {
    "toolbar.refresh": "⟳ Vuselela",
    "toolbar.fetch": "⤓ Landa",
    "toolbar.pull": "⇩ Donsa",
    "toolbar.push": "⇧ Phusha",
    "toolbar.commit": "✓ Zibophezele",
    "toolbar.sync": "⇅ Vumelanisa",
    "toolbar.jumpHead": "⌖ Iya ekuphumeni",
    "toolbar.reset": "⤢ Setha kabusha ukubuka",
    "toolbar.settings": "⚙ Izilungiselelo",
    "details.header": "Zibophezele Imininingwane",
    "details.close": "Vala",
    "details.sha": "SHA",
    "details.shortSha": "I-SHA emfushane",
    "details.message": "Umlayezo",
    "details.author": "Umbhali",
    "details.date": "Usuku",
    "details.labels": "Amalebula",
    "details.location": "Indawo",
    "details.currentHead": "HEAD - ukuphuma kwamanje",
    "settings.title": "Izilungiselelo",
    "settings.language": "Ulimi",
    "settings.theme": "Itimu",
    "settings.themeLight": "Ukukhanya",
    "settings.themeDark": "Kumnyama",
    "settings.themeLightHint": "Ingemuva elikhanyayo, umbhalo omnyama.",
    "settings.themeDarkHint": "Ingemuva elimnyama, umbhalo okhanyayo.",
    "settings.mainBranch": "Igatsha eliyinhloko",
    "settings.mainBranchAuto": "Okuzenzakalelayo",
    "settings.mainBranchSearch": "Sesha amagatsha...",
    "settings.mainBranchNoMatch": "Awekho amagatsha afanayo",
    "settings.display": "Isitayela sokubonisa",
    "settings.displayModern": "Okwesimanje",
    "settings.displayClassic": "Okwakudala",
    "settings.displayModernHint": "Ikhanvasi yamahhala — donsela epanini, skrola ukuze usondeze.",
    "settings.displayClassicHint": "Ikhanvasi egxilile — isiqu siphiniwe kwesokunxele, akukho ukusondeza, skrola kuphela (njengegrafu yokubuyekeza ye-SVN).",
    "settings.sectionGeneral": "Okujwayelekile",
    "settings.sectionGraph": "Igrafu",
    "settings.sectionChanges": "Ishintsha ukubuka",
    "settings.diffMinimap": "Imephu encane ehlukile",
    "settings.diffMinimapOn": "Kubonisiwe",
    "settings.diffMinimapOff": "Kufihliwe",
    "settings.diffMinimapOnHint": "Bonisa umugqa wokubuka konke wesitayela se-VS Code eduze kwe-diff — ihudule ukuze uskrole; shintsha omaka abafakiwe.",
    "settings.diffMinimapOffHint": "Awukho umugqa wokubuka konke - skrola i-diff ngokujwayelekile.",
    "settings.commitReview": "Zibophezele ekubuyekezeni",
    "settings.commitReviewOn": "Buza ngaphambi kokuzibophezela",
    "settings.commitReviewOff": "Zibophezele ngqo",
    "settings.commitReviewOnHint": "Bonisa uhlu lokugcina lwamafayela kanye nokuhlolwa komlayezo ngaphambi kokudala isivumelwano sendawo.",
    "settings.commitReviewOffHint": "Inkinobho yokuzibophezela idala ukuzibophezela kwasendaweni ngokushesha.",
    "settings.svnBranchDialog": "Ibhokisi legatsha lesitayela se-SVN",
    "settings.svnBranchDialogHint": "Bonisa isikhethi sesihlahla sefolda lapho udala igatsha (esikhundleni sokwaziswa okulula).",
    "settings.branchDialog": "Ibhokisi legatsha",
    "settings.branchDialogSvnTitle": "Isitayela se-SVN",
    "settings.branchDialogNativeVscode": "Isitayela sekhodi ye-VS",
    "settings.branchDialogNativeVs": "Isitayela se-Visual Studio",
    "settings.branchDialogNativeHint": "Ukwaziswa kwegatsha lakho kwe-IDE eyakhelwe ngaphakathi.",
    "settings.close": "Vala",
    "settings.done": "Kwenziwe",
    "settings.sectionAdvanced": "Okuthuthukile",
    "settings.gitSource": "I-Git iyasebenziseka",
    "settings.gitSourceBuiltin": "Kwakhelwe ngaphakathi",
    "settings.gitSourceBuiltinHint": "Sebenzisa i-git kanambambili kusukela kusandiso se-IDE esakhelwe ngaphakathi se-Git.",
    "settings.gitSourceCustom": "Indlela yangokwezifiso",
    "settings.gitPath": "Indlela eya ku-git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Phequlula...",
    "settings.jargon": "Imigomo ye-Git",
    "settings.jargonTranslate": "Humusha",
    "settings.jargonEnglish": "Gcina ngesiNgisi",
    "settings.jargonTranslateHint": "Humusha amagama e-Git (donsa, phusha, bophezela, igatsha...) uwayise olimini lwesixhumi esibonakalayo.",
    "settings.jargonEnglishHint": "Gcina imigomo ye-Git (donsa, phusha, yenza, igatsha…) ngesiNgisi; humusha konke okunye.",
    "settings.sectionMerge": "Hlanganisa",
    "settings.mergeMode": "Hlanganisa isitayela",
    "settings.mergeModeMerge": "Ukuhlanganisa okuvamile",
    "settings.mergeModeSquash": "Ukuhlanganisa i-squash",
    "settings.mergeModeMergeHint": "Ihlanganisa igatsha njengoba linjalo: ukuzibophezela kwalo kuba yingxenye yomlando wegatsha lamanje, elihlanganiswe nesibopho sokuhlanganisa (noma sidluliselwe phambili ngokushesha uma kungenzeka).",
    "settings.mergeModeSquashHint": "Igoqa lonke igatsha ekwenzeni okukodwa egatsheni lamanje (git merge --squash). Imisebenzi yegatsha ngokwalo ayifaki umlando wegatsha lamanje - ubona kuphela ukuzibophezela okukodwa lapho. Igatsha ngokwalo lishiywe lingathinteki, ngakho-ke imisebenzi yalo ihlala ibonakala emzileni wayo.",
    "settings.mergedView": "Izibopho ezihlanganisiwe kugrafu",
    "settings.mergedViewBranch": "Kuphela egatsheni labo",
    "settings.mergedViewTarget": "Futhi egatsheni bahlanganiswa",
    "settings.mergedViewBranchHint": "Izibopho zegatsha ezihlanganisiwe zidwetshwa kuphela emzileni wazo; ukuhlanganisa kuvela njengesixhumi. Ukubuka kwakudala.",
    "settings.mergedViewTargetHint": "Izivumelwano ezihlanganisiwe zidwetshwa emzileni wegatsha elamukelayo futhi, zipakishwe ngaphansi kwesibopho sokuhlanganisa futhi zileveli nokwangempela - ngoba lelo gatsha selikuqukethe ngempela manje. Amakhophi aphaphatheka futhi aqondiswe negatsha ayebhalwe kulo, ngakho kuhlale obala ukuthi awenziwanga lapho. Ukuhlanganiswa kwe-squash akuthintwa: i-squash ibhala isivumelwano esisodwa esijwayelekile, ngakho-ke leso sibopho kuphela esike sivele.",
    "legend.title": "Inganekwane",
    "legend.head": "HEAD / igatsha lamanje",
    "legend.local": "Igatsha lendawo",
    "legend.remote": "Igatsha elikude",
    "legend.remoteOnly": "Emafini kuphela (akudonswanga)",
    "legend.tag": "Ithegi (inguqulo)",
    "legend.commit": "Zibophezele",
    "legend.stash": "I-Stash (umsebenzi wesheluli)",
    "legend.mergedIn": "Ingene nenhlanganisela (ibhalwe kwelinye igatsha)",
    "legend.nodes": "Amabhokisi",
    "legend.lines": "Imigqa",
    "legend.edgeParent": "Ukuzibophezela kwangaphambilini kulayini ofanayo",
    "legend.edgeMerge": "Igatsha elihlanganisiwe — umcibisholo ukhomba ekuhlanganiseni isivumelwano",
    "legend.edgeBranch": "Igatsha linqanyuliwe lapha",
    "legend.edgeStash": "I-Stash kanye nokuzibophezela kwayo kushejuliwe",
    "legend.edgeMergedChain": "Ulayini waleli gatsha ogijima phakathi kwezibopho ezihlanganisiwe",
    "legend.edgeMergedTie": "Isibophezelo esifanayo egatsheni okubhalwe kulo",
    "menu.jumpHead": "⌖ Iya ekuphumeni",
    "menu.resetView": "⤢ Setha kabusha ukubuka",
    "menu.createBranch": "Dala igatsha kusuka lapha...",
    "menu.checkout": "Bheka lokhu kuzinikela",
    "menu.copySha": "Kopisha ukuzibophezela kwe-SHA",
    "menu.pushBranch": "Phusha igatsha \"{name}\"",
    "menu.renameCommit": "Qamba kabusha umlayezo wokuzibophezela...",
    "menu.undoCommit": "Hlehlisa ukuzibophezela (gcina izinguquko)...",
    "menu.viewChanges": "Buka izinguquko...",
    "menu.jumpToOriginal": "Yiya ekuzibopheleleni kwasekuqaleni",
    "changes.title": "Ukushintsha kwe-{sha}",
    "changes.tabChanged": "Kushintshiwe",
    "changes.tabAll": "Wonke Amafayela",
    "changes.added": "Kwengeziwe",
    "changes.modified": "Kulungisiwe",
    "changes.deleted": "Kususiwe",
    "changes.renamed": "Iqanjwe kabusha",
    "changes.noChanges": "Lokhu kuzibophezela akushintshi amafayela.",
    "changes.loading": "Ilayisha ukuhluka...",
    "changes.selectFile": "Khetha ifayela ukuze ubone izinguquko zalo.",
    "changes.original": "Okwangempela",
    "changes.changed": "Lokhu kuzibophezela",
    "changes.binary": "Ifayela kanambambili — akukho mehluko wombhalo ongaboniswa.",
    "changes.tooLarge": "Ifayela likhulu kakhulu ukuthi lingahluka.",
    "changes.renamedFrom": "Iqanjwe kabusha kusuka ku-{path}",
    "changes.close": "Vala",
    "changes.prevChange": "Ushintsho lwangaphambilini",
    "changes.nextChange": "Ushintsho olulandelayo",
    "changes.collapseAll": "Goqa wonke amafolda",
    "changes.expandAll": "Nweba wonke amafolda",
    "changes.searchPlaceholder": "Sesha amafayela...",
    "changes.noSearchResults": "Awekho amafayela afana nosesho lwakho.",
    "changes.maximize": "Khulisa",
    "changes.restore": "Buyisela usayizi",
    "find.open": "Sesha kulo diff (Ctrl+F)",
    "find.placeholder": "Thola ngokuhlukile...",
    "find.prev": "Umeshi odlule",
    "find.next": "Umeshi olandelayo",
    "find.close": "Vala ukusesha",
    "find.noResults": "Ayikho imiphumela",
    "commit.title": "Yenza Izinguquko",
    "commit.close": "Vala",
    "commit.loading": "Ifunda isihlahla esisebenzayo...",
    "commit.noChanges": "Azikho izinguquko zasendaweni okufanele zenziwe.",
    "commit.selectFile": "Khetha ifayela ukuze ubone ukuhluka kwalo.",
    "commit.selectAll": "Khetha konke",
    "commit.selectNone": "Sula konke",
    "commit.message": "Zibophezele umlayezo",
    "commit.messagePlaceholder": "Chaza ushintsho",
    "commit.selectedCount": "Amafayela we-{count} akhethiwe",
    "commit.review": "Buyekeza",
    "commit.reviewTitle": "Ukuhlolwa kokugcina ngaphambi kokuzibophezela",
    "commit.reviewSummary": "Zibophezele ifayela(ama) {count} ngomlayezo: {message}",
    "commit.back": "Emuva",
    "commit.commit": "Dala ukuzibophezela kwasendaweni",
    "menu.stashApply": "Faka isicelo se-stash",
    "menu.stashPop": "I-Pop stash (sebenzisa futhi ususe)",
    "menu.stashDrop": "Beka okufihliwe...",
    "merge.title": "Hlanganisa Igatsha",
    "merge.route": "Ukuhlanganisa egatsheni lamanje",
    "merge.source": "Kusuka (umthombo)",
    "merge.target": "Ku (okwamanje)",
    "merge.loading": "Ihlaziya ukuhlanganisa...",
    "merge.upToDate": "Kakade kusesimweni - akukho okuzohlanganiswa.",
    "merge.fastForward": "Iya phambili ngokushesha — asikho isivumelwano sokuhlanganisa esidingekayo (thika “Dala isibophezelo sokuhlanganisa” ukuze uphoqe eyodwa).",
    "merge.summary": "Amafayela we-{files} ashintshiwe, ukungqubuzana kwe-{conflicts}.",
    "merge.conflictsWarning": "Lokhu kuhlanganisa kunokungqubuzana — usengakwazi ukuhlanganisa, bese ukuxazulula kusihleli.",
    "merge.noChanges": "Azikho izinguquko zefayela.",
    "merge.files": "Umphumela wezinguquko",
    "merge.added": "Kwengeziwe",
    "merge.modified": "Kulungisiwe",
    "merge.deleted": "Kususiwe",
    "merge.conflict": "Ukungqubuzana",
    "merge.message": "Hlanganisa umlayezo wokuzibophezela",
    "merge.messagePlaceholder": "Hlanganisa 'umthombo' wegatsha",
    "merge.messageFfHint": "Kushaywe indiva ekuhlanganisweni okuya phambili ngokushesha (akukho ukuzibophezela okudaliwe).",
    "merge.noFastForward": "Dala isivumelwano sokuhlanganisa (akukho okuya phambili ngokushesha)",
    "merge.noFastForwardHint": "Hlala urekhoda isivumelwano sokuhlanganisa, noma ngabe igatsha lingadlulela phambili ngokushesha.",
    "merge.merge": "Hlanganisa",
    "merge.cancel": "Khansela",
    "merge.previewError": "Ayikwazanga ukubala ukubuka kuqala: {message}",
    "merge.squashBadge": "Ugalonci",
    "merge.squashNote": "Ukuhlangana kwe-squash - lonke ushintsho luba njengesibopho EYODWA ku-“{target}”; Ukuzibophezela kwe-“{source}” kuphuma emlandweni wayo. Shintshela emuva kokuthi Izilungiselelo › Hlanganisa.",
    "merge.messageSquash": "Zibophezele umlayezo",
    "merge.squashMessagePlaceholder": "Izinguquko ezishintshile ukusuka 'kumthombo'",
    "newBranch.title": "Dala Igatsha",
    "newBranch.startPoint": "Igatsha elisha eliqala ku-{sha}",
    "newBranch.startPointOn": "ku-{refs}",
    "newBranch.location": "Indawo",
    "newBranch.locationRoot": "(impande)",
    "newBranch.expandAll": "Nweba konke",
    "newBranch.collapseAll": "Goqa konke",
    "newBranch.expandFolder": "Nweba ifolda",
    "newBranch.collapseFolder": "Goqa ifolda",
    "newBranch.name": "Igama legatsha",
    "newBranch.namePlaceholder": "igatsha lami",
    "newBranch.fullName": "Igama eligcwele: {name}",
    "newBranch.invalid": "Igama legatsha elingavumelekile",
    "newBranch.exists": "Igatsha elinaleli gama selivele likhona",
    "newBranch.checkout": "Igatsha le-Checkout ngemva kokudala",
    "newBranch.create": "Dala",
    "newBranch.cancel": "Khansela",
    "status.loading": "Ilayisha igrafu...",
    "status.summary": "{repo}Showing {commits} izibophezelo, {refs} refs",
    "status.branchCreated": "Kudalwe igatsha \"{name}\" ngo-{sha}",
    "status.error": "Iphutha: {message}",
    "status.opFailed": "Umsebenzi wehlulekile",
    "status.fetching": "Ilanda...",
    "status.pulling": "Iyadonsa...",
    "status.pushing": "Iyaphusha...",
    "status.committing": "Idala ukuzibophezela kwasendaweni...",
    "status.commitCreated": "Kudalwe ukuzibophezela kwasendaweni {sha}: {message}",
    "status.syncing": "Iyavumelanisa...",
    "status.noHead": "Akukho ukuphuma kwamanje okutholakele kugrafu.",
    "status.undoing": "Ihlehlisa ukuzibophezela...",
    "status.commitUndone": "Ukuzibophezela kuhlehlisiwe - izinguquko zibuyele esihlahleni esisebenzayo.",
    "status.undoConflict": "Hlehlisa izingxabano ezishaywayo — zixazulule kusihleli, bese uqhubeka.",
    "status.stashApplying": "Ifaka i-stash...",
    "status.stashPopping": "Kuvela ukufihlwa...",
    "status.stashDropping": "Ilahla okufihliwe…",
    "status.stashApplied": "Okufihliwe kusetshenzisiwe.",
    "status.stashPopped": "I-Stash ivele.",
    "status.stashDropped": "I-Stash iwile.",
    "status.stashConflict": "Izingxabano ze-Stash — zixazulule kumhleli.",
    "status.merging": "Iyahlanganisa...",
    "status.merged": "Ukuhlanganisa kuqediwe.",
    "status.mergeConflict": "Ukuhlanganisa kunokungqubuzana — kuxazulule kumhleli, bese wenza.",
    "search.title": "Sesha",
    "search.tooltip": "Ukusesha kuzibophezela",
    "search.placeholder": "Ukusesha kuzibophezela...",
    "search.previous": "Umphumela wangaphambilini",
    "search.next": "Umphumela olandelayo",
    "search.noResults": "Ayikho imiphumela",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Imodi yokuhlunga",
    "search.highlight": "Gqamisa imodi",
    "search.filterMode": "Sesha isibonisi",
    "search.filterModeFilter": "Hlunga (fihla okungafani)",
    "search.filterModeHighlight": "Gqamisa (bonisa konke, maka okufanayo)",
    "search.filterModeHint": "Khetha ukuthi imiphumela yosesho iboniswa kanjani kugrafu.",
    "search.visibility": "Sesha indawo",
    "search.visibilityToolbar": "Inkinobho yebha yamathuluzi",
    "search.visibilityAlwaysVisible": "Ibha ehlala ibonakala",
    "search.visibilityHint": "Bonisa ukusesha njengesithonjana sebha yamathuluzi noma ibha eqhubekayo phezulu.",
    "footer.github": "GitHub",
  },
  it: {
    "toolbar.refresh": "⟳ Aggiorna",
    "toolbar.fetch": "⤓ Recupera",
    "toolbar.pull": "⇩ Tirare",
    "toolbar.push": "⇧ Spingere",
    "toolbar.commit": "✓ Impegnarsi",
    "toolbar.sync": "⇅ Sincronizzazione",
    "toolbar.jumpHead": "⌖ Vai alla cassa",
    "toolbar.reset": "⤢ Ripristina la visualizzazione",
    "toolbar.settings": "⚙ Impostazioni",
    "details.header": "Dettagli impegno",
    "details.close": "Vicino",
    "details.sha": "SHA",
    "details.shortSha": "SHA breve",
    "details.message": "Messaggio",
    "details.author": "Autore",
    "details.date": "Data",
    "details.labels": "Etichette",
    "details.location": "Posizione",
    "details.currentHead": "HEAD: cassa corrente",
    "settings.title": "Impostazioni",
    "settings.language": "Lingua",
    "settings.theme": "Tema",
    "settings.themeLight": "Leggero",
    "settings.themeDark": "Buio",
    "settings.themeLightHint": "Sfondo chiaro, testo scuro.",
    "settings.themeDarkHint": "Sfondo scuro, testo chiaro.",
    "settings.mainBranch": "Ramo principale",
    "settings.mainBranchAuto": "Automatico",
    "settings.mainBranchSearch": "Cerca filiali…",
    "settings.mainBranchNoMatch": "Nessun ramo corrispondente",
    "settings.display": "Stile di visualizzazione",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Classico",
    "settings.displayModernHint": "Area di disegno libera: trascina per eseguire la panoramica, scorri per ingrandire.",
    "settings.displayClassicHint": "Tela fissa: tronco bloccato a sinistra, nessuno zoom, solo scorrimento (come il grafico di revisione SVN).",
    "settings.sectionGeneral": "Generale",
    "settings.sectionGraph": "Grafico",
    "settings.sectionChanges": "Cambia vista",
    "settings.diffMinimap": "Minimappa differenziale",
    "settings.diffMinimapOn": "Mostrato",
    "settings.diffMinimapOff": "Nascosto",
    "settings.diffMinimapOnHint": "Mostra una striscia panoramica in stile VS Code accanto alla differenza: trascinala per scorrere; indicatori di modifica inclusi.",
    "settings.diffMinimapOffHint": "Nessuna striscia panoramica: scorri normalmente le differenze.",
    "settings.commitReview": "Revisione dell'impegno",
    "settings.commitReviewOn": "Chiedi prima di impegnarti",
    "settings.commitReviewOff": "Impegnarsi direttamente",
    "settings.commitReviewOnHint": "Mostra un elenco finale di file e un controllo dei messaggi prima di creare il commit locale.",
    "settings.commitReviewOffHint": "Il pulsante Commit crea immediatamente il commit locale.",
    "settings.svnBranchDialog": "Finestra di dialogo del ramo in stile SVN",
    "settings.svnBranchDialogHint": "Mostra un selettore dell'albero delle cartelle durante la creazione di un ramo (invece di un semplice prompt).",
    "settings.branchDialog": "Finestra di dialogo del ramo",
    "settings.branchDialogSvnTitle": "Stile SVN",
    "settings.branchDialogNativeVscode": "Stile VS Code",
    "settings.branchDialogNativeVs": "Stile di Visual Studio",
    "settings.branchDialogNativeHint": "Il prompt di ramo integrato del tuo IDE.",
    "settings.close": "Vicino",
    "settings.done": "Fatto",
    "settings.sectionAdvanced": "Avanzato",
    "settings.gitSource": "Eseguibile Git",
    "settings.gitSourceBuiltin": "Integrato",
    "settings.gitSourceBuiltinHint": "Utilizza il binario git dall'estensione Git integrata nell'IDE.",
    "settings.gitSourceCustom": "Percorso personalizzato",
    "settings.gitPath": "Percorso verso Git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Sfoglia...",
    "settings.jargon": "Termini Git",
    "settings.jargonTranslate": "Tradurre",
    "settings.jargonEnglish": "Mantieni l'inglese",
    "settings.jargonTranslateHint": "Traduci i termini Git (pull, push, commit, branch…) nel linguaggio dell'interfaccia.",
    "settings.jargonEnglishHint": "Mantieni i termini Git (pull, push, commit, branch…) in inglese; tradurre tutto il resto.",
    "settings.sectionMerge": "Unisci",
    "settings.mergeMode": "Unisci stile",
    "settings.mergeModeMerge": "Unione normale",
    "settings.mergeModeSquash": "Unisci la zucca",
    "settings.mergeModeMergeHint": "Unisce il ramo così com'è: i suoi commit diventano parte della cronologia del ramo corrente, uniti da un commit di unione (o avanzano velocemente quando possibile).",
    "settings.mergeModeSquashHint": "Comprime l'intero ramo in un singolo commit sul ramo corrente (git merge --squash). I commit del ramo non entrano mai nella cronologia del ramo corrente: lì vedi solo un commit. Il ramo stesso viene lasciato intatto, quindi i suoi commit rimangono visibili nella propria corsia.",
    "settings.mergedView": "Commit uniti nel grafico",
    "settings.mergedViewBranch": "Solo sulla propria filiale",
    "settings.mergedViewTarget": "Anche nel ramo in cui sono stati accorpati",
    "settings.mergedViewBranchHint": "I commit di un ramo unito vengono tracciati solo nella propria corsia; l'unione viene visualizzata come un connettore. La visione classica.",
    "settings.mergedViewTargetHint": "I commit portati da una fusione vengono disegnati anche nella corsia del ramo ricevente, impilati sotto il commit della fusione e allo stesso livello degli originali, perché quel ramo ora li contiene davvero. Le copie sono chiare e hanno l'intestazione del ramo su cui sono state scritte, quindi è chiaro che non sono state realizzate lì. Le fusioni di squash non sono influenzate: uno squash scrive un commit ordinario, quindi appare solo quel commit.",
    "legend.title": "Leggenda",
    "legend.head": "TESTA / ramo attuale",
    "legend.local": "Filiale locale",
    "legend.remote": "Filiale remota",
    "legend.remoteOnly": "Solo nel cloud (non tirato)",
    "legend.tag": "Etichetta (versione)",
    "legend.commit": "Impegnarsi",
    "legend.stash": "Stash (lavoro accantonato)",
    "legend.mergedIn": "È arrivato con una fusione (scritto su un altro ramo)",
    "legend.nodes": "Scatole",
    "legend.lines": "Linee",
    "legend.edgeParent": "Commit precedente sulla stessa riga",
    "legend.edgeMerge": "Ramo unito: la freccia punta al commit dell'unione",
    "legend.edgeBranch": "Un ramo si biforcava qui",
    "legend.edgeStash": "Stash e il commit su cui è stato accantonato",
    "legend.edgeMergedChain": "La linea di questo ramo che attraversa i commit uniti",
    "legend.edgeMergedTie": "Lo stesso commit sul ramo su cui è stato scritto",
    "menu.jumpHead": "⌖ Vai alla cassa",
    "menu.resetView": "⤢ Ripristina la visualizzazione",
    "menu.createBranch": "Crea ramo da qui...",
    "menu.checkout": "Dai un'occhiata a questo impegno",
    "menu.copySha": "Copia il commit SHA",
    "menu.pushBranch": "Derivazione a spingere \"{name}\"",
    "menu.renameCommit": "Rinomina il messaggio di commit...",
    "menu.undoCommit": "Annulla il commit (mantieni le modifiche)...",
    "menu.viewChanges": "Visualizza modifiche...",
    "menu.jumpToOriginal": "Vai al commit originale",
    "changes.title": "Modifiche in {sha}",
    "changes.tabChanged": "Cambiato",
    "changes.tabAll": "Tutti i file",
    "changes.added": "Aggiunto",
    "changes.modified": "Modificato",
    "changes.deleted": "Eliminato",
    "changes.renamed": "Rinominato",
    "changes.noChanges": "Questo commit non modifica alcun file.",
    "changes.loading": "Caricamento differenziale...",
    "changes.selectFile": "Seleziona un file per visualizzarne le modifiche.",
    "changes.original": "Originale",
    "changes.changed": "Questo impegno",
    "changes.binary": "File binario: nessuna differenza di testo da mostrare.",
    "changes.tooLarge": "Il file è troppo grande per il diff.",
    "changes.renamedFrom": "Rinominato da {path}",
    "changes.close": "Vicino",
    "changes.prevChange": "Modifica precedente",
    "changes.nextChange": "Prossimo cambio",
    "changes.collapseAll": "Comprimi tutte le cartelle",
    "changes.expandAll": "Espandi tutte le cartelle",
    "changes.searchPlaceholder": "Cerca file...",
    "changes.noSearchResults": "Nessun file corrisponde alla tua ricerca.",
    "changes.maximize": "Massimizzare",
    "changes.restore": "Ripristina dimensione",
    "find.open": "Cerca in questa differenza (Ctrl+F)",
    "find.placeholder": "Trova nelle differenze...",
    "find.prev": "Partita precedente",
    "find.next": "Prossima partita",
    "find.close": "Chiudi la ricerca",
    "find.noResults": "Nessun risultato",
    "commit.title": "Applica modifiche",
    "commit.close": "Vicino",
    "commit.loading": "Lettura dell'albero di lavoro...",
    "commit.noChanges": "Non ci sono modifiche locali da confermare.",
    "commit.selectFile": "Seleziona un file per vederne le differenze.",
    "commit.selectAll": "Seleziona tutto",
    "commit.selectNone": "Cancella tutto",
    "commit.message": "Invia messaggio",
    "commit.messagePlaceholder": "Descrivi il cambiamento",
    "commit.selectedCount": "File {count} selezionati",
    "commit.review": "Revisione",
    "commit.reviewTitle": "Controllo finale prima del commit",
    "commit.reviewSummary": "Effettua il commit dei file {count} con il messaggio: {message}",
    "commit.back": "Indietro",
    "commit.commit": "Crea commit locale",
    "menu.stashApply": "Applica la scorta",
    "menu.stashPop": "Pop stash (applica e rimuovi)",
    "menu.stashDrop": "Lascia la scorta...",
    "merge.title": "Unisci ramo",
    "merge.route": "Fusione nel ramo attuale",
    "merge.source": "Da (fonte)",
    "merge.target": "In (corrente)",
    "merge.loading": "Analisi dell'unione in corso...",
    "merge.upToDate": "Già aggiornato: niente da unire.",
    "merge.fastForward": "Avanzamento veloce: non è necessario alcun commit di unione (seleziona \"Crea un commit di unione\" per forzarne uno).",
    "merge.summary": "File {files} modificati, conflitti {conflicts}.",
    "merge.conflictsWarning": "Questa unione presenta dei conflitti: puoi comunque unirli e poi risolverli nell'editor.",
    "merge.noChanges": "Nessuna modifica al file.",
    "merge.files": "Cambiamenti conseguenti",
    "merge.added": "Aggiunto",
    "merge.modified": "Modificato",
    "merge.deleted": "Eliminato",
    "merge.conflict": "Conflitto",
    "merge.message": "Unisci messaggio di commit",
    "merge.messagePlaceholder": "Unisci ramo 'sorgente'",
    "merge.messageFfHint": "Ignorato in un'unione rapida (non viene creato alcun commit).",
    "merge.noFastForward": "Crea un commit di unione (senza avanzamento rapido)",
    "merge.noFastForwardHint": "Registra sempre un commit di unione, anche quando il ramo potrebbe avanzare rapidamente.",
    "merge.merge": "Unisci",
    "merge.cancel": "Cancellare",
    "merge.previewError": "Impossibile calcolare un'anteprima: {message}",
    "merge.squashBadge": "Schiacciare",
    "merge.squashNote": "Squash merge: ogni modifica viene eseguita come UNO commit su \"{target}\"; I commit di \"{source}\" rimangono fuori dalla sua cronologia. Torna indietro in Impostazioni > Unisci.",
    "merge.messageSquash": "Invia messaggio",
    "merge.squashMessagePlaceholder": "Modifiche schiacciate dalla \"fonte\"",
    "newBranch.title": "Crea ramo",
    "newBranch.startPoint": "Nuovo ramo a partire da {sha}",
    "newBranch.startPointOn": "su {refs}",
    "newBranch.location": "Posizione",
    "newBranch.locationRoot": "(radice)",
    "newBranch.expandAll": "Espandi tutto",
    "newBranch.collapseAll": "Comprimi tutto",
    "newBranch.expandFolder": "Espandi cartella",
    "newBranch.collapseFolder": "Comprimi cartella",
    "newBranch.name": "Nome della filiale",
    "newBranch.namePlaceholder": "il mio ramo",
    "newBranch.fullName": "Nome completo: {name}",
    "newBranch.invalid": "Nome della filiale non valido",
    "newBranch.exists": "Esiste già un ramo con questo nome",
    "newBranch.checkout": "Ramo di pagamento dopo la creazione",
    "newBranch.create": "Creare",
    "newBranch.cancel": "Cancellare",
    "status.loading": "Caricamento grafico…",
    "status.summary": "{repo}Visualizzazione dei commit {commits}, dei riferimenti {refs}",
    "status.branchCreated": "Creato il ramo \"{name}\" in {sha}",
    "status.error": "Errore: {message}",
    "status.opFailed": "Operazione fallita",
    "status.fetching": "Recupero…",
    "status.pulling": "Tirando…",
    "status.pushing": "Spingendo…",
    "status.committing": "Creazione del commit locale…",
    "status.commitCreated": "Commit locale creato {sha}: {message}",
    "status.syncing": "Sincronizzazione…",
    "status.noHead": "Nessun pagamento corrente trovato nel grafico.",
    "status.undoing": "Annullamento impegno…",
    "status.commitUndone": "Commit annullato: le modifiche sono tornate nell'albero di lavoro.",
    "status.undoConflict": "Annulla i conflitti di successo: risolvili nell'editor, quindi continua.",
    "status.stashApplying": "Applicazione della scorta…",
    "status.stashPopping": "Fare scorta…",
    "status.stashDropping": "Lasciando la scorta...",
    "status.stashApplied": "Stash applicato.",
    "status.stashPopped": "La scorta è saltata fuori.",
    "status.stashDropped": "La scorta è caduta.",
    "status.stashConflict": "Conflitti di archiviazione: risolvili nell'editor.",
    "status.merging": "Fusione…",
    "status.merged": "Unione completata.",
    "status.mergeConflict": "L'unione presenta dei conflitti: risolvili nell'editor, quindi esegui il commit.",
    "search.title": "Ricerca",
    "search.tooltip": "La ricerca si impegna",
    "search.placeholder": "La ricerca si impegna...",
    "search.previous": "Risultato precedente",
    "search.next": "Prossimo risultato",
    "search.noResults": "Nessun risultato",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modalità filtro",
    "search.highlight": "Modalità evidenziazione",
    "search.filterMode": "Visualizzazione della ricerca",
    "search.filterModeFilter": "Filtra (nascondi le non corrispondenze)",
    "search.filterModeHighlight": "Evidenzia (mostra tutto, contrassegna le corrispondenze)",
    "search.filterModeHint": "Scegli la modalità di visualizzazione dei risultati della ricerca nel grafico.",
    "search.visibility": "Cerca posizione",
    "search.visibilityToolbar": "Pulsante della barra degli strumenti",
    "search.visibilityAlwaysVisible": "Barra sempre visibile",
    "search.visibilityHint": "Mostra la ricerca come icona della barra degli strumenti o barra persistente in alto.",
    "footer.github": "GitHub",
  },
  sw: {
    "toolbar.refresh": "⟳ Onyesha upya",
    "toolbar.fetch": "⤓ Leta",
    "toolbar.pull": "⇩ Vuta",
    "toolbar.push": "⇧ Kusukuma",
    "toolbar.commit": "✓ Kujitolea",
    "toolbar.sync": "⇅ Sawazisha",
    "toolbar.jumpHead": "⌖ Nenda kwenye malipo",
    "toolbar.reset": "⤢ Weka upya mwonekano",
    "toolbar.settings": "⚙ Mipangilio",
    "details.header": "Maelezo ya Kujitolea",
    "details.close": "Funga",
    "details.sha": "SHA",
    "details.shortSha": "SHA fupi",
    "details.message": "Ujumbe",
    "details.author": "Mwandishi",
    "details.date": "Tarehe",
    "details.labels": "Lebo",
    "details.location": "Mahali",
    "details.currentHead": "HEAD - malipo ya sasa",
    "settings.title": "Mipangilio",
    "settings.language": "Lugha",
    "settings.theme": "Mandhari",
    "settings.themeLight": "Mwanga",
    "settings.themeDark": "Giza",
    "settings.themeLightHint": "Mandhari meusi, maandishi meusi.",
    "settings.themeDarkHint": "Mandhari meusi, maandishi mepesi.",
    "settings.mainBranch": "Tawi kuu",
    "settings.mainBranchAuto": "Otomatiki",
    "settings.mainBranchSearch": "Tafuta matawi...",
    "settings.mainBranchNoMatch": "Hakuna matawi yanayolingana",
    "settings.display": "Mtindo wa kuonyesha",
    "settings.displayModern": "Kisasa",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Turubai isiyolipishwa - buruta hadi kwenye sufuria, sogeza ili kukuza.",
    "settings.displayClassicHint": "Turubai isiyobadilika - shina limebandikwa kushoto, hakuna kukuza, tembeza pekee (kama grafu ya marekebisho ya SVN).",
    "settings.sectionGeneral": "Mkuu",
    "settings.sectionGraph": "Grafu",
    "settings.sectionChanges": "Mabadiliko ya mtazamo",
    "settings.diffMinimap": "Tofauti ya ramani ndogo",
    "settings.diffMinimapOn": "Imeonyeshwa",
    "settings.diffMinimapOff": "Imefichwa",
    "settings.diffMinimapOnHint": "Onyesha ukanda wa muhtasari wa mtindo wa VS Code kando ya diff - iburute ili kusogeza; mabadiliko ya alama pamoja.",
    "settings.diffMinimapOffHint": "Hakuna ukanda wa muhtasari - tembeza tofauti kwa kawaida.",
    "settings.commitReview": "Toa ukaguzi",
    "settings.commitReviewOn": "Uliza kabla ya kujitolea",
    "settings.commitReviewOff": "Jitoe moja kwa moja",
    "settings.commitReviewOnHint": "Onyesha orodha ya mwisho ya faili na ukaguzi wa ujumbe kabla ya kuunda ahadi ya ndani.",
    "settings.commitReviewOffHint": "Kitufe cha Commit huunda ahadi ya ndani mara moja.",
    "settings.svnBranchDialog": "Kidirisha cha tawi cha mtindo wa SVN",
    "settings.svnBranchDialogHint": "Onyesha kiteua folda wakati wa kuunda tawi (badala ya kidokezo rahisi).",
    "settings.branchDialog": "Kidirisha cha tawi",
    "settings.branchDialogSvnTitle": "Mtindo wa SVN",
    "settings.branchDialogNativeVscode": "Mtindo wa Msimbo wa VS",
    "settings.branchDialogNativeVs": "Mtindo wa Visual Studio",
    "settings.branchDialogNativeHint": "Kidokezo cha tawi kilichojengwa ndani ya IDE yako.",
    "settings.close": "Funga",
    "settings.done": "Imekamilika",
    "settings.sectionAdvanced": "Advanced",
    "settings.gitSource": "Git inayoweza kutekelezwa",
    "settings.gitSourceBuiltin": "Imejengwa ndani",
    "settings.gitSourceBuiltinHint": "Tumia git binary kutoka kwa kiendelezi cha Git kilichojengwa ndani ya IDE.",
    "settings.gitSourceCustom": "Njia maalum",
    "settings.gitPath": "Njia ya git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Vinjari...",
    "settings.jargon": "Masharti ya Git",
    "settings.jargonTranslate": "Tafsiri",
    "settings.jargonEnglish": "Weka kwa Kiingereza",
    "settings.jargonTranslateHint": "Tafsiri maneno ya Git (vuta, sukuma, tuma, tawi…) katika lugha ya kiolesura.",
    "settings.jargonEnglishHint": "Weka masharti ya Git (vuta, sukuma, ahadi, tawi…) kwa Kiingereza; kutafsiri kila kitu kingine.",
    "settings.sectionMerge": "Unganisha",
    "settings.mergeMode": "Mtindo wa kuunganisha",
    "settings.mergeModeMerge": "Mchanganyiko wa kawaida",
    "settings.mergeModeSquash": "Boga kuunganisha",
    "settings.mergeModeMergeHint": "Huunganisha tawi jinsi lilivyo: ahadi zake huwa sehemu ya historia ya tawi la sasa, iliyounganishwa na ahadi ya kuunganisha (au kusambazwa kwa haraka inapowezekana).",
    "settings.mergeModeSquashHint": "Hukunja tawi zima kuwa ahadi moja kwenye tawi la sasa (git merge --squash). Ahadi za tawi haziingii kwenye historia ya tawi la sasa - unaona ahadi moja tu hapo. Tawi lenyewe limeachwa bila kuguswa, kwa hivyo ahadi zake hubaki zikionekana kwenye njia yake yenyewe.",
    "settings.mergedView": "Ahadi zilizounganishwa kwenye grafu",
    "settings.mergedViewBranch": "Tu kwenye tawi lao wenyewe",
    "settings.mergedViewTarget": "Pia katika tawi waliunganishwa",
    "settings.mergedViewBranchHint": "Ahadi za tawi zilizounganishwa huchorwa tu kwa njia yao wenyewe; muunganisho huonekana kama kiunganishi. Mtazamo wa classic.",
    "settings.mergedViewTargetHint": "Ahadi za muunganisho zinazoletwa huchorwa kwenye njia ya tawi la kupokea pia, zikiwa zimepangwa chini ya ahadi ya kuunganisha na kusawazishwa na zile asili - kwa sababu tawi hilo kweli linazo sasa. Nakala zimepauka na zinaongozwa na tawi walizoandikiwa, kwa hivyo inabaki wazi kuwa hazikutengenezwa hapo. Muunganisho wa boga hauathiriwi: boga huandika ahadi moja ya kawaida, kwa hivyo ni ahadi hiyo pekee inayowahi kutokea.",
    "legend.title": "Hadithi",
    "legend.head": "HEAD / tawi la sasa",
    "legend.local": "Tawi la mtaa",
    "legend.remote": "Tawi la mbali",
    "legend.remoteOnly": "Katika wingu tu (haijavutwa)",
    "legend.tag": "Tag (toleo)",
    "legend.commit": "Jitolea",
    "legend.stash": "Stash (kazi iliyohifadhiwa)",
    "legend.mergedIn": "Iliingia na unganisho (iliyoandikwa kwenye tawi lingine)",
    "legend.nodes": "Masanduku",
    "legend.lines": "Mistari",
    "legend.edgeParent": "Ahadi ya awali kwenye mstari huo huo",
    "legend.edgeMerge": "Tawi lililounganishwa - mshale unaelekeza kwenye ahadi ya kuunganisha",
    "legend.edgeBranch": "Tawi limegawanyika hapa",
    "legend.edgeStash": "Stash na ahadi yake iliwekwa rafu",
    "legend.edgeMergedChain": "Mstari wa tawi hili unapitia ahadi zilizounganishwa",
    "legend.edgeMergedTie": "Ahadi hiyo hiyo kwenye tawi iliandikwa",
    "menu.jumpHead": "⌖ Nenda kwenye malipo",
    "menu.resetView": "⤢ Weka upya mwonekano",
    "menu.createBranch": "Unda tawi kutoka hapa...",
    "menu.checkout": "Angalia ahadi hii",
    "menu.copySha": "Nakili ahadi ya SHA",
    "menu.pushBranch": "Shinikiza tawi \"{name}\"",
    "menu.renameCommit": "Badilisha jina la ujumbe wa ahadi...",
    "menu.undoCommit": "Tendua ahadi (hifadhi mabadiliko)...",
    "menu.viewChanges": "Tazama mabadiliko...",
    "menu.jumpToOriginal": "Nenda kwa ahadi ya asili",
    "changes.title": "Mabadiliko ya {sha}",
    "changes.tabChanged": "Imebadilishwa",
    "changes.tabAll": "Faili Zote",
    "changes.added": "Imeongezwa",
    "changes.modified": "Imebadilishwa",
    "changes.deleted": "Imefutwa",
    "changes.renamed": "Imepewa jina jipya",
    "changes.noChanges": "Ahadi hii haibadilishi faili.",
    "changes.loading": "Inapakia tofauti...",
    "changes.selectFile": "Chagua faili ili kuona mabadiliko yake.",
    "changes.original": "Asili",
    "changes.changed": "Ahadi hii",
    "changes.binary": "Faili ya binary - hakuna tofauti ya maandishi ya kuonyesha.",
    "changes.tooLarge": "Faili ni kubwa sana kuweza kutofautisha.",
    "changes.renamedFrom": "Imebadilishwa jina kutoka {path}",
    "changes.close": "Funga",
    "changes.prevChange": "Mabadiliko ya awali",
    "changes.nextChange": "Mabadiliko yanayofuata",
    "changes.collapseAll": "Kunja folda zote",
    "changes.expandAll": "Panua folda zote",
    "changes.searchPlaceholder": "Tafuta faili...",
    "changes.noSearchResults": "Hakuna faili zinazolingana na utafutaji wako.",
    "changes.maximize": "Ongeza",
    "changes.restore": "Rejesha ukubwa",
    "find.open": "Tafuta katika tofauti hii (Ctrl+F)",
    "find.placeholder": "Pata tofauti...",
    "find.prev": "Mechi iliyotangulia",
    "find.next": "Mechi inayofuata",
    "find.close": "Funga utafutaji",
    "find.noResults": "Hakuna matokeo",
    "commit.title": "Fanya Mabadiliko",
    "commit.close": "Funga",
    "commit.loading": "Inasoma mti unaofanya kazi...",
    "commit.noChanges": "Hakuna mabadiliko ya ndani ya kufanya.",
    "commit.selectFile": "Chagua faili ili kuona tofauti zake.",
    "commit.selectAll": "Chagua zote",
    "commit.selectNone": "Futa zote",
    "commit.message": "Ujumbe wa ahadi",
    "commit.messagePlaceholder": "Eleza mabadiliko",
    "commit.selectedCount": "Faili za {count} zimechaguliwa",
    "commit.review": "Kagua",
    "commit.reviewTitle": "Ukaguzi wa mwisho kabla ya ahadi",
    "commit.reviewSummary": "Weka faili za {count} zenye ujumbe: {message}",
    "commit.back": "Nyuma",
    "commit.commit": "Unda ahadi ya ndani",
    "menu.stashApply": "Omba stash",
    "menu.stashPop": "Pop stash (tuma na uondoe)",
    "menu.stashDrop": "Dondosha stash...",
    "merge.title": "Unganisha Tawi",
    "merge.route": "Kuunganisha kwenye tawi la sasa",
    "merge.source": "Kutoka (chanzo)",
    "merge.target": "Ndani (ya sasa)",
    "merge.loading": "Inachanganua muunganisho...",
    "merge.upToDate": "Tayari imesasishwa - hakuna cha kuunganisha.",
    "merge.fastForward": "Kusonga mbele kwa kasi - hakuna ahadi ya kuunganisha inayohitajika (tiki \"Unda ahadi ya kuunganisha\" ili kulazimisha moja).",
    "merge.summary": "Faili za {files} zimebadilishwa, migogoro ya {conflicts}.",
    "merge.conflictsWarning": "Muunganisho huu una migogoro - bado unaweza kuunganisha, kisha uitatue kwenye kihariri.",
    "merge.noChanges": "Hakuna mabadiliko ya faili.",
    "merge.files": "Matokeo ya mabadiliko",
    "merge.added": "Imeongezwa",
    "merge.modified": "Imebadilishwa",
    "merge.deleted": "Imefutwa",
    "merge.conflict": "Migogoro",
    "merge.message": "Unganisha ujumbe wa ahadi",
    "merge.messagePlaceholder": "Unganisha 'chanzo' cha tawi",
    "merge.messageFfHint": "Imepuuzwa kwenye muunganisho wa kusonga mbele kwa kasi (hakuna ahadi iliyoundwa).",
    "merge.noFastForward": "Unda ahadi ya kuunganisha (hakuna kusonga mbele haraka)",
    "merge.noFastForwardHint": "Rekodi ahadi ya kuunganisha kila wakati, hata wakati tawi linaweza kusonga mbele kwa haraka.",
    "merge.merge": "Unganisha",
    "merge.cancel": "Ghairi",
    "merge.previewError": "Haikuweza kukokotoa onyesho la kukagua: {message}",
    "merge.squashBadge": "Boga",
    "merge.squashNote": "Kuunganishwa kwa boga - kila mabadiliko hutukia kama ahadi ya ONE kwenye \"{target}\"; Ahadi za \"{source}\" zinabaki nje ya historia yake. Rudi kwenye Mipangilio › Unganisha.",
    "merge.messageSquash": "Ujumbe wa ahadi",
    "merge.squashMessagePlaceholder": "Mabadiliko yaliyopunguzwa kutoka kwa 'chanzo'",
    "newBranch.title": "Unda Tawi",
    "newBranch.startPoint": "Tawi jipya kuanzia {sha}",
    "newBranch.startPointOn": "kwa {refs}",
    "newBranch.location": "Mahali",
    "newBranch.locationRoot": "(mzizi)",
    "newBranch.expandAll": "Panua zote",
    "newBranch.collapseAll": "Kunja zote",
    "newBranch.expandFolder": "Panua folda",
    "newBranch.collapseFolder": "Kunja folda",
    "newBranch.name": "Jina la tawi",
    "newBranch.namePlaceholder": "tawi langu",
    "newBranch.fullName": "Jina kamili: {name}",
    "newBranch.invalid": "Jina batili la tawi",
    "newBranch.exists": "Tawi lenye jina hili tayari lipo",
    "newBranch.checkout": "Lipa tawi baada ya kuunda",
    "newBranch.create": "Unda",
    "newBranch.cancel": "Ghairi",
    "status.loading": "Inapakia grafu...",
    "status.summary": "{repo}Showing {commits} ahadi, {refs} ref",
    "status.branchCreated": "Imeunda tawi la \"{name}\" huko {sha}",
    "status.error": "Hitilafu: {message}",
    "status.opFailed": "Uendeshaji haukufaulu",
    "status.fetching": "Inaleta...",
    "status.pulling": "Inavuta...",
    "status.pushing": "Inasukuma...",
    "status.committing": "Inaunda ahadi ya ndani...",
    "status.commitCreated": "Imeunda ahadi ya ndani {sha}: {message}",
    "status.syncing": "Inasawazisha...",
    "status.noHead": "Hakuna malipo ya sasa yaliyopatikana kwenye grafu.",
    "status.undoing": "Inatengua ahadi...",
    "status.commitUndone": "Ahadi imetenduliwa - mabadiliko yanarudi kwenye mti unaofanya kazi.",
    "status.undoConflict": "Tendua migongano iliyogonga - isuluhishe katika kihariri, kisha uendelee.",
    "status.stashApplying": "Inaweka stash...",
    "status.stashPopping": "Inatokea stash...",
    "status.stashDropping": "Inadondosha stash...",
    "status.stashApplied": "Stash imetumika.",
    "status.stashPopped": "Stash ilitokea.",
    "status.stashDropped": "Stash imeshuka.",
    "status.stashConflict": "Migogoro ya Stash - isuluhishe kwenye kihariri.",
    "status.merging": "Inaunganisha...",
    "status.merged": "Kuunganisha kumekamilika.",
    "status.mergeConflict": "Kuunganisha kuna mizozo - isuluhishe kwenye kihariri, kisha ujitolee.",
    "search.title": "Tafuta",
    "search.tooltip": "Shughuli za utafutaji",
    "search.placeholder": "Shughuli za utafutaji...",
    "search.previous": "Matokeo ya awali",
    "search.next": "Matokeo yanayofuata",
    "search.noResults": "Hakuna matokeo",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Hali ya kichujio",
    "search.highlight": "Hali ya kuangazia",
    "search.filterMode": "Tafuta onyesho",
    "search.filterModeFilter": "Chuja (ficha zisizolingana)",
    "search.filterModeHighlight": "Angazia (onyesha yote, weka alama zinazolingana)",
    "search.filterModeHint": "Chagua jinsi matokeo ya utafutaji yanavyoonyeshwa kwenye grafu.",
    "search.visibility": "Tafuta eneo",
    "search.visibilityToolbar": "Kitufe cha upau wa vidhibiti",
    "search.visibilityAlwaysVisible": "Baa inayoonekana kila wakati",
    "search.visibilityHint": "Onyesha utafutaji kama ikoni ya upau wa vidhibiti au upau unaoendelea juu.",
    "footer.github": "GitHub",
  },
  ht: {
    "toolbar.refresh": "⟳ Rafrechi",
    "toolbar.fetch": "⤓ Al chache",
    "toolbar.pull": "⇩ Rale",
    "toolbar.push": "⇧ Pouse",
    "toolbar.commit": "✓ Angaje",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Ale nan kesye",
    "toolbar.reset": "⤢ Reyajiste View",
    "toolbar.settings": "⚙ Anviwònman",
    "details.header": "Detay komèt",
    "details.close": "Fèmen",
    "details.sha": "SHA",
    "details.shortSha": "Kout SHA",
    "details.message": "Mesaj",
    "details.author": "Otè",
    "details.date": "Dat",
    "details.labels": "Etikèt",
    "details.location": "Kote",
    "details.currentHead": "HEAD - aktyèl kesye",
    "settings.title": "Anviwònman",
    "settings.language": "Lang",
    "settings.theme": "Tèm",
    "settings.themeLight": "Limyè",
    "settings.themeDark": "Fè nwa",
    "settings.themeLightHint": "Limyè background, tèks nwa.",
    "settings.themeDarkHint": "Fond nwa, tèks limyè.",
    "settings.mainBranch": "Branch prensipal la",
    "settings.mainBranchAuto": "Otomatik",
    "settings.mainBranchSearch": "Chèche branch yo...",
    "settings.mainBranchNoMatch": "Pa gen branch matche",
    "settings.display": "Ekspozisyon style",
    "settings.displayModern": "Modèn",
    "settings.displayClassic": "Klasik",
    "settings.displayModernHint": "Twal gratis - trennen pou pano, woulo liv pou rale.",
    "settings.displayClassicHint": "Twal fiks — kòf kole sou bò gòch, pa gen rale, woulo liv sèlman (tankou graf revizyon SVN).",
    "settings.sectionGeneral": "Jeneral",
    "settings.sectionGraph": "Grafik",
    "settings.sectionChanges": "Chanje vi",
    "settings.diffMinimap": "Dif minimap",
    "settings.diffMinimapOn": "Montre",
    "settings.diffMinimapOff": "Kache",
    "settings.diffMinimapOnHint": "Montre yon teren BECA VS Code-style bò kote diferans lan - trennen li pou woulo liv; makè chanjman enkli.",
    "settings.diffMinimapOffHint": "Pa gen yon seri apèsi - woule diferans nòmalman.",
    "settings.commitReview": "Komite revizyon",
    "settings.commitReviewOn": "Mande anvan komèt",
    "settings.commitReviewOff": "Komèt dirèkteman",
    "settings.commitReviewOnHint": "Montre yon lis fichye final ak chèk mesaj anvan ou kreye komèt lokal la.",
    "settings.commitReviewOffHint": "Bouton komèt la kreye komèt lokal la imedyatman.",
    "settings.svnBranchDialog": "SVN-style branch dyalòg",
    "settings.svnBranchDialogHint": "Montre yon katab-pyebwa chwazi lè w ap kreye yon branch (olye pou yon senp èd).",
    "settings.branchDialog": "Dyalòg Branch",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "Style VS Kòd",
    "settings.branchDialogNativeVs": "Estidyo vizyèl style",
    "settings.branchDialogNativeHint": "Èd memwa branch entegre IDE ou a.",
    "settings.close": "Fèmen",
    "settings.done": "Fè",
    "settings.sectionAdvanced": "Avanse",
    "settings.gitSource": "Git ègzekutabl",
    "settings.gitSourceBuiltin": "Bati-an",
    "settings.gitSourceBuiltinHint": "Sèvi ak binè git ki soti nan ekstansyon Git IDE a.",
    "settings.gitSourceCustom": "Chemen koutim",
    "settings.gitPath": "Chemen pou git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Navige…",
    "settings.jargon": "Git tèm",
    "settings.jargonTranslate": "Tradui",
    "settings.jargonEnglish": "Kenbe nan lang angle",
    "settings.jargonTranslateHint": "Tradui tèm Git (rale, pouse, komèt, branch...) nan lang koòdone a.",
    "settings.jargonEnglishHint": "Kenbe tèm Git (rale, pouse, komèt, branch...) nan lang angle; tradui tout lòt bagay.",
    "settings.sectionMerge": "Rantre",
    "settings.mergeMode": "Rantre style",
    "settings.mergeModeMerge": "Nòmal fizyone",
    "settings.mergeModeSquash": "Squash fizyone",
    "settings.mergeModeMergeHint": "Fizyone branch lan jan li ye a: komèt li yo vin yon pati nan istwa branch aktyèl la, ansanm ak yon komèt fizyone (oswa rapid-voye lè sa posib).",
    "settings.mergeModeSquashHint": "Efondre tout branch lan nan yon sèl komèt sou branch aktyèl la (git merge --squash). Pwòp commits branch lan pa janm antre nan istwa aktyèl branch lan — ou sèlman wè yon sèl commit la. Branch nan tèt li rete intact, kidonk komèt li yo rete vizib nan pwòp liy li yo.",
    "settings.mergedView": "Fizyone komèt nan graf la",
    "settings.mergedViewBranch": "Se sèlman sou pwòp branch yo",
    "settings.mergedViewTarget": "Epitou nan branch lan yo te fizyone nan",
    "settings.mergedViewBranchHint": "Commits yon branch fusionné yo trase sèlman nan pwòp liy pa yo; fizyon an montre moute kòm yon konektè. Vi klasik la.",
    "settings.mergedViewTargetHint": "Commits yon fusion pote yo trase nan liy branch k ap resevwa a tou, anpile anba komite fusion a ak nivo orijinal yo - paske branch sa a vrèman genyen yo kounye a. Kopi yo pal epi yo gen tèt ak branch yo te ekri a, kidonk li rete klè yo pa te fè la. Fizyon kalbas yo pa afekte: yon kalbas ekri yon sèl komèt òdinè, kidonk sèlman komèt sa a parèt.",
    "legend.title": "Lejand",
    "legend.head": "TÈT / branch aktyèl la",
    "legend.local": "Branch lokal",
    "legend.remote": "Branch aleka",
    "legend.remoteOnly": "Se sèlman nan nwaj la (pa rale)",
    "legend.tag": "Tag (vèsyon)",
    "legend.commit": "Komèt",
    "legend.stash": "Stash (travay etajè)",
    "legend.mergedIn": "Te vini ak yon fizyon (ekri sou yon lòt branch)",
    "legend.nodes": "Bwat yo",
    "legend.lines": "Liy",
    "legend.edgeParent": "Previous commit sou menm liy lan",
    "legend.edgeMerge": "Branch fizyone - flèch la pwente nan komèt fizyon an",
    "legend.edgeBranch": "Yon branch koupe isit la",
    "legend.edgeStash": "Stash ak komèt la li te etajè sou",
    "legend.edgeMergedChain": "Liy branch sa a ap pase nan komite ki fusionné yo",
    "legend.edgeMergedTie": "Menm komèt la sou branch li te ekri a",
    "menu.jumpHead": "⌖ Ale nan kesye",
    "menu.resetView": "⤢ Reyajiste View",
    "menu.createBranch": "Kreye branch soti isit la...",
    "menu.checkout": "Kite komèt sa a",
    "menu.copySha": "Kopi komite SHA",
    "menu.pushBranch": "Pouse branch \"{name}\"",
    "menu.renameCommit": "Chanje non mesaj komèt...",
    "menu.undoCommit": "Defèt komèt (kenbe chanjman)...",
    "menu.viewChanges": "Gade chanjman...",
    "menu.jumpToOriginal": "Ale nan komite orijinal la",
    "changes.title": "Chanjman nan {sha}",
    "changes.tabChanged": "Chanje",
    "changes.tabAll": "Tout Dosye",
    "changes.added": "Te ajoute",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Efase",
    "changes.renamed": "Chanje non",
    "changes.noChanges": "Komite sa a pa chanje okenn dosye.",
    "changes.loading": "Chaje diferans...",
    "changes.selectFile": "Chwazi yon fichye pou wè chanjman li yo.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Angajman sa a",
    "changes.binary": "Fichye binè - pa gen okenn diferans tèks yo montre.",
    "changes.tooLarge": "Fichye a twò gwo pou diferans.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Chanjman anvan",
    "changes.nextChange": "Pwochen chanjman",
    "changes.collapseAll": "Ranpli tout dosye yo",
    "changes.expandAll": "Elaji tout dosye yo",
    "changes.searchPlaceholder": "Chèche fichye...",
    "changes.noSearchResults": "Pa gen okenn dosye ki matche ak rechèch ou a.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Retabli gwosè",
    "find.open": "Chèche nan diferans sa a (Ctrl+F)",
    "find.placeholder": "Jwenn nan diferan...",
    "find.prev": "Match anvan",
    "find.next": "Pwochen match",
    "find.close": "Fèmen rechèch",
    "find.noResults": "Pa gen rezilta",
    "commit.title": "Komite Chanjman",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Lekti pye bwa k ap travay...",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Chwazi tout",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Revizyon",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Aplike kachèt",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Kache kachèt...",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  kri: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Visual Studio stayl we dɛn kin yuz",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Go bifo",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Path fɔ go na git",
    "settings.gitPathPlaceholder": "/usr/bin/git, we de tɔk bɔt",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Kip insay Inglish",
    "settings.jargonTranslateHint": "Translet Git tɛm dɛn (pul, push, kɔmit, branch...) insay di intafɛs langwej.",
    "settings.jargonEnglishHint": "Kip Git wɔd dɛn (pul, push, kɔmit, branch...) insay Inglish; translet ɔl di ɔda tin dɛn.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Merj di branch as i de: in kɔmit dɛn kin bi pat pan di branch we de naw in istri, we dɛn jɔyn wit wan mɛj kɔmit (ɔ fast-fɔwad we i pɔsibul).",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Merged kɔmit dɛn na di grafik",
    "settings.mergedViewBranch": "Na dɛn yon branch nɔmɔ",
    "settings.mergedViewTarget": "Dɔn bak na di branch we dɛn bin jɔyn dɛn",
    "settings.mergedViewBranchHint": "Wan branch we dɛn jɔyn, dɛn kin drɔ di kɔmit dɛn nɔmɔ na dɛn yon len; di merge sho op as kɔnɛkta. Di klashik we fɔ si tin.",
    "settings.mergedViewTargetHint": "Di kɔmit dɛn we wan mɛj we dɛn briŋ kam, dɛn kin drɔ dɛn na di branch we de risiv in len bak, dɛn kin stak dɛn ɔnda di mɛj kɔmit ɛn lɛvɛl wit di ɔrijinal dɛn — bikɔs da branch de rili gɛt dɛn naw. Di kɔpi dɛn nɔ gɛt wan kɔlɔ ɛn dɛn gɛt ed wit di branch we dɛn rayt dɛn pan, so i stil klia se dɛn nɔ mek dɛn de. Skwash mɛj nɔ afɛkt: skwash de rayt wan ɔdinari kɔmit, so na da kɔmit de nɔmɔ de ɛva apia.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Branch we de na di eria",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Na di klawd nɔmɔ (nɔ pul am) .",
    "legend.tag": "Tag (vɛshɔn) .",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Kam in wit wan merge (we dɛn rayt pan ɔda branch) .",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Layn dɛn",
    "legend.edgeParent": "Bifo kɔmit pan di sem layn",
    "legend.edgeMerge": "Merged-in branch — di aro de pɔynt na di merge kɔmit",
    "legend.edgeBranch": "Wan branch bin fɔk ɔf ya",
    "legend.edgeStash": "Stash ɛn di kɔmit i bin shel pan",
    "legend.edgeMergedChain": "Dis branch in layn we de rɔn tru di kɔmit dɛn we dɛn jɔyn",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "⤢ Riset di we aw yu de si",
    "menu.createBranch": "Krio branch frɔm ya...",
    "menu.checkout": "Checkout dis kɔmit",
    "menu.copySha": "Kopi komit SHA",
    "menu.pushBranch": "Push branch \"{name}\".",
    "menu.renameCommit": "Rinem kɔmit mɛsej...",
    "menu.undoCommit": "Undo commit (kip chenj dɛn)...",
    "menu.viewChanges": "Luk di chenj dɛn...",
    "menu.jumpToOriginal": "Go to di ɔrijinal kɔmit",
    "changes.title": "Chenj dɛn na {sha}",
    "changes.tabChanged": "Dɛn chenj am",
    "changes.tabAll": "Ɔl di Fayl dɛn",
    "changes.added": "Dɛn dɔn ad am",
    "changes.modified": "Dɛn dɔn chenj am",
    "changes.deleted": "Dɛn dɔn dilit am",
    "changes.renamed": "Dɛn chenj in nem",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Pik wan fayl fɔ si di chenj dɛn we i dɔn mek.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Dis kɔmit",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Maksimayz am",
    "changes.restore": "Ristɔr saiz",
    "find.open": "Sɔch insay dis difrɛns (Ctrl+F) .",
    "find.placeholder": "Find in diff...",
    "find.prev": "Mats we bin dɔn de bifo",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "No rizulyt nɔ de",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "No lokal chenj nɔ de fɔ kɔmit.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Pik ɔl",
    "commit.selectNone": "Klin ɔltin",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Tɔk bɔt di chenj",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Faynal chɛk bifo yu kɔmit",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  ku: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Şaxa \"{name}\" jêbirin",
    "menu.renameCommit": "Navê peyama kombûnê biguherîne…",
    "menu.undoCommit": "Betalkirina peywirê (guhertinan bidomîne)…",
    "menu.viewChanges": "Guhertinan bibînin…",
    "menu.mergeBranch": "\"{source}\" di \"{target}\" de bike yek",
    "menu.jumpToOriginal": "Biçe ser komîteya bingehîn",
    "node.mergedInTooltip": "Merge {merge} ev peywir anîn nav \"{target}\" - ew li ser \"{origin}\" hate nivîsandin.",
    "changes.title": "Guhertinên di {sha} de",
    "changes.tabChanged": "Guhertin",
    "changes.tabAll": "Hemû Pelên",
    "changes.added": "Zêde kirin",
    "changes.modified": "Modified",
    "changes.deleted": "Jêbirin",
    "changes.renamed": "Binavkirin",
    "changes.noChanges": "Ev commit tu pelan naguherîne.",
    "changes.loading": "Cûdahî tê barkirin…",
    "changes.selectFile": "Pelek hilbijêrin da ku guheztinên wê bibînin.",
    "changes.original": "Eslî",
    "changes.changed": "Ev pêk anîn",
    "changes.binary": "Pelê binary - ferqa nivîsê tune ku were xuyang kirin.",
    "changes.tooLarge": "Pelê pir mezin e ku cûdahiyê bike.",
    "changes.renamedFrom": "Navê xwe ji {path} veguherand",
    "changes.close": "Nêzîkî",
    "changes.prevChange": "Guhertina berê",
    "changes.nextChange": "Guhertina paşîn",
    "changes.collapseAll": "Hemî peldankan hilweşîne",
    "changes.expandAll": "Hemî peldankan berfireh bikin",
    "changes.searchPlaceholder": "Li pelan bigerin…",
    "changes.noSearchResults": "Tu pelên lêgerîna te li hev nayên.",
    "changes.maximize": "Mezin bikin",
    "changes.restore": "Mezinahiyê vegerîne",
    "find.open": "Li vê cudahiyê bigerin (Ctrl+F)",
    "find.placeholder": "Di cudahiyê de bibînin…",
    "find.prev": "Maça berê",
    "find.next": "Maça Next",
    "find.close": "Lêgerînê bigire",
    "find.noResults": "Encam tune",
    "commit.title": "Guhertinên Komit",
    "commit.close": "Nêzîkî",
    "commit.loading": "Xwendina dara xebatê…",
    "commit.noChanges": "Guhertinên herêmî yên ku bêne kirin tune.",
    "commit.selectFile": "Pelek hilbijêrin ku cûdahiya wê bibînin.",
    "commit.selectAll": "Hemî hilbijêrin",
    "commit.selectNone": "Hemî paqij bikin",
    "commit.message": "Peyama bipejirîne",
    "commit.messagePlaceholder": "Guhertinê diyar bike",
    "commit.selectedCount": "Pelên {count} hatine hilbijartin",
    "commit.review": "Axaftin",
    "commit.reviewTitle": "Kontrola dawîn berî kombûnê",
    "commit.reviewSummary": "Pelê (yên) {count} bi peyamê: {message}",
    "commit.back": "Paş",
    "commit.commit": "Peymana herêmî ava bikin",
    "menu.stashApply": "Serlêdana stash",
    "menu.stashPop": "Pop stash (sepandin û jêbirin)",
    "menu.stashDrop": "Veşêre…",
    "merge.title": "Merge Şaxa",
    "merge.route": "Di nav şaxê heyî de tevdigerin",
    "merge.source": "Ji (çavkaniyê)",
    "merge.target": "nav (niha)",
    "merge.loading": "Analîzkirina yekbûnê…",
    "merge.upToDate": "Jixwe nûvekirî ye - tiştek ji bo yekbûnê tune.",
    "merge.fastForward": "Pêşveçûna bilez - ne hewce ye ku pevgirêdana hevgirtinê (\"Pêvekek yekbûnê biafirîne\" bikirtînin da ku yek bi zorê bikin).",
    "merge.summary": "Pelên {files} guherî, {conflicts} nakokî(yên).",
    "merge.conflictsWarning": "Di vê hevgirtinê de nakokî hene - hûn hîn jî dikarin hev bikin, paşê wan di edîtorê de çareser bikin.",
    "merge.noChanges": "Guhertinên pelê tune.",
    "merge.files": "Guhertinên encam",
    "merge.added": "Zêde kirin",
    "merge.modified": "Modified",
    "merge.deleted": "Jêbirin",
    "merge.conflict": "Şer",
    "merge.message": "Peyama commit Merge",
    "merge.messagePlaceholder": "Şaxa 'çavkaniyê' bike yek",
    "merge.messageFfHint": "Li ser yekbûnek bilez-pêşverû hate paşguh kirin (tiştek nayê afirandin).",
    "merge.noFastForward": "Komîta yekbûnê biafirînin (bê lez-pêşveçûn)",
    "merge.noFastForwardHint": "Her gav peywirek hevgirtinê tomar bikin, tewra gava ku şax dikare bilez pêşde bibe.",
    "merge.merge": "Bihevkelyan",
    "merge.cancel": "Bişûndekirin",
    "merge.previewError": "Nekarî pêşdîtinek hesab bike: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge - her guheztin dema ku ONE li ser \"{target}\" tê kirin; Peywirên xwe yên \"{source}\" li derveyî dîroka xwe dimînin. Vegere li Settings › Merge.",
    "merge.messageSquash": "Peyama bipejirîne",
    "merge.squashMessagePlaceholder": "Guhertinên ji 'çavkaniyê' hatin hilanîn",
    "newBranch.title": "Şaxek çêbikin",
    "newBranch.startPoint": "Şaxa nû ji {sha} dest pê dike",
    "newBranch.startPointOn": "li ser {refs}",
    "newBranch.location": "Cîh",
    "newBranch.locationRoot": "(reh)",
    "newBranch.expandAll": "Hemî berfireh bikin",
    "newBranch.collapseAll": "Hemî hilweşînin",
    "newBranch.expandFolder": "Peldanka berfireh bike",
    "newBranch.collapseFolder": "Peldankê hilweşîne",
    "newBranch.name": "Navê şaxê",
    "newBranch.namePlaceholder": "şaxa min",
    "newBranch.fullName": "Navê tevahî: {name}",
    "newBranch.invalid": "Navê şaxê nederbasdar e",
    "newBranch.exists": "Jixwe şaxek bi vî navî heye",
    "newBranch.checkout": "Şaxa Checkout piştî afirandinê",
    "newBranch.create": "Xûliqandin",
    "newBranch.cancel": "Bişûndekirin",
    "status.loading": "Grafik tê barkirin…",
    "status.summary": "{repo}Nişandana {commits} commits, {refs} refs",
    "status.branchCreated": "Li {sha} şaxê \"{name}\" hate afirandin",
    "status.error": "Çewtî: {message}",
    "status.opFailed": "Operasyon bi ser neket",
    "status.fetching": "Têkişandin…",
    "status.pulling": "Dikişîne…",
    "status.pushing": "Pevçûn…",
    "status.committing": "Afirandina komîteya herêmî…",
    "status.commitCreated": "Peymana herêmî {sha} hate afirandin: {message}",
    "status.syncing": "Hevdemkirin…",
    "status.noHead": "Di grafîkê de qaseya heyî nehat dîtin.",
    "status.undoing": "Betalkirina peywirê…",
    "status.commitUndone": "Pêkhatin betal kirin - guhertin di dara xebatê de vedigerin.",
    "status.undoConflict": "Pevçûnên lêdanê hilweşînin - wan di edîtorê de çareser bikin, dûv re bidomînin.",
    "status.stashApplying": "Serlêdana veşartinê…",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Daxistina sitar…",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Stash derket.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Hevgirtin…",
    "status.merged": "Merge qediya.",
    "status.mergeConflict": "Pevçûnên hevgirtinê hene - wan di edîtorê de çareser bikin, dûv re bikin.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Lêgerînê dike",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Encama paşîn",
    "search.noResults": "Encam tune",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Cihê lêgerînê",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "GitHub",
  },
  la: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "ventilabis",
    "toolbar.commit": "Committe",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Reset visum",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Nuntius",
    "details.author": "Author",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "PATER - current Checkout",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "lux",
    "settings.themeDark": "Dark",
    "settings.themeLightHint": "Luce color, textus tenebrosus.",
    "settings.themeDarkHint": "Obscurum, lumen, textum.",
    "settings.mainBranch": "Principalis ramus",
    "settings.mainBranchAuto": "Automatic",
    "settings.mainBranchSearch": "Quaerere ramos...",
    "settings.mainBranchNoMatch": "Non matching rami",
    "settings.display": "Propono style",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Free carbasa - trahere ad sartagine, volumen to zoom.",
    "settings.displayClassicHint": "Canvas fixum — sinister truncus adfixus, nullum zoom, solum volumen (sicut graphi recognitionis SVN).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Aliquam lacinia purus",
    "settings.sectionChanges": "Mutationes view",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Ostensum",
    "settings.diffMinimapOff": "Occultus",
    "settings.diffMinimapOnHint": "Ostende a VS Codicis-style overview habena iuxta diff — eam ad librum trah; mutationem figentium comprehendit.",
    "settings.diffMinimapOffHint": "Nulla overview habena — scroll the diss Northmanni.",
    "settings.commitReview": "Committere recensionem",
    "settings.commitReviewOn": "Ask ante committere",
    "settings.commitReviewOff": "Committere directe",
    "settings.commitReviewOnHint": "Ostendere finalem fasciculi indicem ac nuntium reprimendam ante creationem loci committere.",
    "settings.commitReviewOffHint": "Button Commit locali statim creat.",
    "settings.svnBranchDialog": "SVN-style ramus alternis",
    "settings.svnBranchDialogHint": "Ostendere folder-arborem picker cum ramum creando (pro prompto simplici).",
    "settings.branchDialog": "Germen alternis",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "VS Codex style",
    "settings.branchDialogNativeVs": "Visual Studio style",
    "settings.branchDialogNativeHint": "IDE tuum in ramo promptum aedificatum est.",
    "settings.close": "Close",
    "settings.done": "Done",
    "settings.sectionAdvanced": "Provecta",
    "settings.gitSource": "git exsecutabile",
    "settings.gitSourceBuiltin": "inaedificata",
    "settings.gitSourceBuiltinHint": "Utere binario git ab IDE constructo in Git extensione.",
    "settings.gitSourceCustom": "Custom iter",
    "settings.gitSourceCustomHint": "Denota iter ad proprium git exsecutabile (exe.g. /usr/bin/git vel C:\\…\\git.exe).",
    "settings.gitPath": "Iter ad git",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Browse…",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Serva Anglice",
    "settings.jargonTranslateHint": "Translate verba Git (traho, dis, committo, ramum…) in linguam instrumenti.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Mergit ramus ut est: eius committit partem historiae rami currentis, cum commissura coniungitur (vel cum fieri potest celeriter transmissa).",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Committit graph est in merged",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Ramus immersus nonnisi in suo angiporto ducuntur; merge ostendit sicut iungo. Classica est sententia.",
    "settings.mergedViewTargetHint": "Mergae inductae trahuntur in recipiendo rami lane, reclinati sub merge et aequa cum originalibus committunt — quia ille ramus eas nunc vere continet. Codices pallidi et capitati ramo inscripti sunt, ut patet non ibi facta. Cucurbitae migrationes non carent: cucurbitae scribens unum ordinarium committat, ut solum, quod committat, semper appareat.",
    "legend.title": "Legend",
    "legend.head": "PATER / ramus current",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Lineae",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Altera mutatio",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Nullae imagini tuae quaestioni congruit.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Commutationes committere",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Nullae loci mutationes committere.",
    "commit.selectFile": "Dim lima videre suum.",
    "commit.selectAll": "Select all",
    "commit.selectNone": "Patet omnia",
    "commit.message": "committere nuntium",
    "commit.messagePlaceholder": "Describere mutationem",
    "commit.selectedCount": "{count} fasciculus (s) electus",
    "commit.review": "Recognoscere",
    "commit.reviewTitle": "Finalis reprehendo ante committere",
    "commit.reviewSummary": "Committe {count} fasciculum(s) cum epistula: {message}",
    "commit.back": "Retro",
    "commit.commit": "Create loci committere",
    "menu.stashApply": "Applicare LATESCO",
    "menu.stashPop": "Pop LATESCO (applicare & remove)",
    "menu.stashDrop": "Iacta stash…",
    "merge.title": "Merge germen",
    "merge.route": "In current ramo bus",
    "merge.source": "Ex (source)",
    "merge.target": "In (vena)",
    "merge.loading": "Mere examinans…",
    "merge.upToDate": "Iam ad date — nihil merge.",
    "merge.fastForward": "Ieiunium - ante - nullum opus committere merge (tick \"Conde merge committere\" ad unum cogendum).",
    "merge.summary": "{files} fasciculus mutatus, {conflicts} conflictus(s).",
    "merge.conflictsWarning": "Haec merge certamina habet — adhuc merge potes, deinde eas in editore compone.",
    "merge.noChanges": "Nulla lima mutatur.",
    "merge.files": "Inde mutationes",
    "merge.added": "adiecit",
    "merge.modified": "Modified",
    "merge.deleted": "Deletum",
    "merge.conflict": "Conflictus",
    "merge.message": "Merge committere nuntium",
    "merge.messagePlaceholder": "Merge ramus 'fons'",
    "merge.messageFfHint": "Neglexit in merge ante jejunium (nullum committere creatum est).",
    "merge.noFastForward": "Merge committere creare (nemo ante ieiunium)",
    "merge.noFastForwardHint": "Mergi semper notare committere, etiam cum ramus celeriter progredi potuit.",
    "merge.merge": "Merge",
    "merge.cancel": "Cancel",
    "merge.previewError": "Non potuit computare preview: {message}",
    "merge.squashBadge": "Cucurbitae",
    "merge.squashNote": "Cucurbitae merge - singulas terras commutationes sicut in {target} committunt; Proprium \"{source}\" manendum ex historia sua committit. Vertas tergum in Occasus › Merge.",
    "merge.messageSquash": "committere nuntium",
    "merge.squashMessagePlaceholder": "Mutationes pressae a fonte",
    "newBranch.title": "Partum germen",
    "newBranch.startPoint": "Novus ramus incipiens ab {sha}",
    "newBranch.startPointOn": "in {refs}",
    "newBranch.location": "Locus",
    "newBranch.locationRoot": "(radix)",
    "newBranch.expandAll": "Expand all",
    "newBranch.collapseAll": "Collapse omnes",
    "newBranch.expandFolder": "Expand folder",
    "newBranch.collapseFolder": "Collapse folder",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "my-ramus",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Aliquam genere nomen",
    "newBranch.exists": "Iam hoc nomine ramum",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Create",
    "newBranch.cancel": "Cancel",
    "status.loading": "Loading graph…",
    "status.summary": "{repo}Showing {commits} committit {refs} refs",
    "status.branchCreated": "Ramus creatus \"{name}\" ad {sha}",
    "status.error": "Error: {message}",
    "status.opFailed": "Operatio defecit",
    "status.fetching": "Arcessere…",
    "status.pulling": "trahens…",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Partum loci committere…",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Perii, committere…",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Stash applicata.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Stash fluens.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Bus…",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Investigatio",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Toolbar button",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Investigationem monstrare ut icon toolbaricis vel in summo vecte persistentes.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  lv: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "⤢ Atiestatīt skatu",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Apņemšanās detaļas",
    "details.close": "Aizvērt",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Etiķetes",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "HEAD — pašreizējā kase",
    "settings.title": "Iestatījumi",
    "settings.language": "Valoda",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Gaisma",
    "settings.themeDark": "Tumšs",
    "settings.themeLightHint": "Gaišs fons, tumšs teksts.",
    "settings.themeDarkHint": "Tumšs fons, gaišs teksts.",
    "settings.mainBranch": "Galvenā filiāle",
    "settings.mainBranchAuto": "Automātiski",
    "settings.mainBranchSearch": "Meklēt filiāles…",
    "settings.mainBranchNoMatch": "Nav atbilstošu zaru",
    "settings.display": "Displeja stils",
    "settings.displayModern": "Mūsdienīgs",
    "settings.displayClassic": "Klasika",
    "settings.displayModernHint": "Bezmaksas audekls — velciet, lai panoramētu, ritiniet, lai tuvinātu.",
    "settings.displayClassicHint": "Fiksēts audekls — bagāžnieks piesprausts pa kreisi, bez tālummaiņas, tikai ritināšana (piemēram, SVN pārskatīšanas diagramma).",
    "settings.sectionGeneral": "Ģenerālis",
    "settings.sectionGraph": "Grafiks",
    "settings.sectionChanges": "Maina skatu",
    "settings.diffMinimap": "Diff minikarte",
    "settings.diffMinimapOn": "Parādīts",
    "settings.diffMinimapOff": "Slēpts",
    "settings.diffMinimapOnHint": "Rādīt VS koda stila pārskata joslu blakus diff — velciet to, lai ritinātu; iekļauti maiņas marķieri.",
    "settings.diffMinimapOffHint": "Nav pārskata joslas — ritiniet atšķirību kā parasti.",
    "settings.commitReview": "Veiciet pārskatīšanu",
    "settings.commitReviewOn": "Jautājiet pirms apņemšanās",
    "settings.commitReviewOff": "Apņemties tieši",
    "settings.commitReviewOnHint": "Rādīt galīgo failu sarakstu un ziņojumu pārbaudi pirms vietējās apstiprināšanas izveides.",
    "settings.commitReviewOffHint": "Poga Commit nekavējoties izveido vietējo saistību izpildi.",
    "settings.svnBranchDialog": "SVN stila filiāles dialoglodziņš",
    "settings.svnBranchDialogHint": "Veidojot filiāli, parādiet mapju koka atlasītāju (vienkāršas uzvednes vietā).",
    "settings.branchDialog": "Nozares dialoglodziņš",
    "settings.branchDialogSvnTitle": "SVN stilā",
    "settings.branchDialogNativeVscode": "VS koda stils",
    "settings.branchDialogNativeVs": "Visual Studio stils",
    "settings.branchDialogNativeHint": "Jūsu IDE iebūvētā filiāles uzvedne.",
    "settings.close": "Aizvērt",
    "settings.done": "Gatavs",
    "settings.sectionAdvanced": "Papildu",
    "settings.gitSource": "Git izpildāmā programma",
    "settings.gitSourceBuiltin": "Iebūvēts",
    "settings.gitSourceBuiltinHint": "Izmantojiet Git bināro failu no IDE iebūvētā Git paplašinājuma.",
    "settings.gitSourceCustom": "Pielāgots ceļš",
    "settings.gitPath": "Ceļš uz gitu",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Pārlūkot…",
    "settings.jargon": "Git noteikumi",
    "settings.jargonTranslate": "Tulkot",
    "settings.jargonEnglish": "Saglabājiet angļu valodā",
    "settings.jargonTranslateHint": "Tulkojiet Git terminus (pull, push, commit, branch…) interfeisa valodā.",
    "settings.jargonEnglishHint": "Saglabājiet Git terminus (pull, push, commit, branch…) angļu valodā; tulkot visu pārējo.",
    "settings.sectionMerge": "Apvienot",
    "settings.mergeMode": "Apvienot stilu",
    "settings.mergeModeMerge": "Normāla saplūšana",
    "settings.mergeModeSquash": "Skvoša sapludināšana",
    "settings.mergeModeMergeHint": "Savieno filiāli tādu, kāda tā ir: tās saistības kļūst par pašreizējās filiāles vēstures daļu, kurām pievieno sapludināšanas apņemšanos (vai, ja iespējams, pārtīt uz priekšu).",
    "settings.mergeModeSquashHint": "Sakļauj visu atzaru vienā saistībā ar pašreizējo filiāli (git merge --squash). Pašas filiāles saistības nekad neietilpst pašreizējās filiāles vēsturē — tajā ir redzama tikai viena izpilde. Pats zars ir atstāts neskarts, tāpēc tā apņemšanās paliek redzama savā joslā.",
    "settings.mergedView": "Apvienotās saistības diagrammā",
    "settings.mergedViewBranch": "Tikai savā zarā",
    "settings.mergedViewTarget": "Arī filiālē tie tika apvienoti",
    "settings.mergedViewBranchHint": "Apvienotās filiāles saistības tiek ievilktas tikai savā joslā; sapludināšana tiek parādīta kā savienotājs. Klasiskais skats.",
    "settings.mergedViewTargetHint": "Ienestās sapludināšanas saistības tiek zīmētas arī saņēmēja filiāles joslā, saliktas zem sapludināšanas saistības un vienā līmenī ar oriģināliem — jo šajā filiālē tās patiešām ir. Kopijas ir bālas un ar zaru, uz kuras tās bija rakstītas, ir skaidrs, ka tās tur nav izgatavotas. Skvoša sapludināšana netiek ietekmēta: skvošs ieraksta vienu parastu apņemšanos, tāpēc vienmēr parādās tikai šī apņemšanās.",
    "legend.title": "Leģenda",
    "legend.head": "GALVA / pašreizējā filiāle",
    "legend.local": "Vietējā filiāle",
    "legend.remote": "Attālā filiāle",
    "legend.remoteOnly": "Tikai mākonī (nav vilkts)",
    "legend.tag": "Tags (versija)",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Atlicināt (darbs plauktos)",
    "legend.mergedIn": "Ienāca ar sapludināšanu (rakstīts citā filiālē)",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Līnijas",
    "legend.edgeParent": "Iepriekšēja apņemšanās tajā pašā rindā",
    "legend.edgeMerge": "Apvienotā filiāle — bultiņa norāda uz sapludināšanas apņemšanos",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Atlicinātājs un apņemšanās, uz kuras tā tika novietota",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "⌖ Dodieties uz kasi",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Spiedzars \"{name}\"",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Pārdēvēt apņemšanās ziņojumu…",
    "menu.undoCommit": "Atsaukt apņemšanos (saglabāt izmaiņas)…",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Iepriekšējās izmaiņas",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Maksimizēt",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Aizvērt meklēšanu",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Notīrīt visu",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Atlasīts(-i) {count} fails(-i).",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Atpakaļ",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Izmest krātuvi…",
    "merge.title": "Apvienot filiāli",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "No (avota)",
    "merge.target": "Uz (pašreizējo)",
    "merge.loading": "Notiek sapludināšanas analīze…",
    "merge.upToDate": "Jau atjaunināts — nav ko apvienot.",
    "merge.fastForward": "Pārtīt uz priekšu — nav nepieciešama sapludināšanas apņemšanās (lai piespiestu to izdarīt, atzīmējiet “Izveidot sapludināšanas apņemšanos”).",
    "merge.summary": "{files} fails(-i) mainīts(-i), {conflicts} konflikts(-i).",
    "merge.conflictsWarning": "Šai sapludināšanai ir konflikti — jūs joprojām varat sapludināt un pēc tam tos atrisināt redaktorā.",
    "merge.noChanges": "Nav faila izmaiņu.",
    "merge.files": "Rezultātā radušās izmaiņas",
    "merge.added": "Pievienots",
    "merge.modified": "Pārveidots",
    "merge.deleted": "Izdzēsts",
    "merge.conflict": "Konflikts",
    "merge.message": "Apvienot apņemšanās ziņojumu",
    "merge.messagePlaceholder": "Apvienot filiāli \"avots\"",
    "merge.messageFfHint": "Ignorēts ātrās sapludināšanas laikā (savienošana netiek izveidota).",
    "merge.noFastForward": "Izveidot sapludināšanas apņemšanos (nav pārtīšanas uz priekšu)",
    "merge.noFastForwardHint": "Vienmēr ierakstiet sapludināšanas apņemšanos, pat ja filiāle var ātri pārtīt uz priekšu.",
    "merge.merge": "Apvienot",
    "merge.cancel": "Atcelt",
    "merge.previewError": "Nevarēja aprēķināt priekšskatījumu: {message}",
    "merge.squashBadge": "Skvošs",
    "merge.squashNote": "Skvoša sapludināšana — katras izmaiņas tiek veiktas kā VIENA apņemšanās uz “{target}”; “{source}” pašas saistības paliek ārpus tās vēstures. Pārslēdzieties atpakaļ sadaļā Iestatījumi › Apvienot.",
    "merge.messageSquash": "Apstiprināt ziņojumu",
    "merge.squashMessagePlaceholder": "Saspiestas izmaiņas no avota",
    "newBranch.title": "Izveidot filiāli",
    "newBranch.startPoint": "Jauna filiāle sākot no {sha}",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Atrašanās vieta",
    "newBranch.locationRoot": "(sakne)",
    "newBranch.expandAll": "Izvērst visu",
    "newBranch.collapseAll": "Sakļaut visu",
    "newBranch.expandFolder": "Izvērst mapi",
    "newBranch.collapseFolder": "Sakļaut mapi",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "mans-zars",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Nederīgs filiāles nosaukums",
    "newBranch.exists": "Filiāle ar šādu nosaukumu jau pastāv",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "{repo}Rāda {commits} apņemšanos, {refs} ats",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Vilkšana…",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Izveidots lokāls apstiprinājums {sha}: {message}",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Notiek atlicināšanas nomešana…",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Meklēt atrašanās vietu",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  lt: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Pranešimas",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Nėra apžvalgos juostos – įprastai slinkite skirtumą.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Įsipareigokite tiesiogiai",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "SVN stiliaus",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Uždaryti",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Išplėstinė",
    "settings.gitSource": "Git vykdomasis failas",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Kelias į gitą",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Sujungti",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Normalus susiliejimas",
    "settings.mergeModeSquash": "Skvošo susiliejimas",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Sutraukia visą šaką į vieną įsipareigojimą dabartinėje šakoje (git merge --squash). Pačios filialo įsipareigojimai niekada neįeina į dabartinės šakos istoriją – ten matote tik vieną įsipareigojimą. Pati atšaka lieka nepaliesta, todėl jos įsipareigojimai lieka matomi savo juostoje.",
    "settings.mergedView": "Sujungti įsipareigojimai diagramoje",
    "settings.mergedViewBranch": "Tik savo šakoje",
    "settings.mergedViewTarget": "Taip pat filiale jie buvo sujungti",
    "settings.mergedViewBranchHint": "Sujungto filialo įsipareigojimai traukiami tik savo juostoje; sujungimas rodomas kaip jungtis. Klasikinis vaizdas.",
    "settings.mergedViewTargetHint": "Įvesti sujungimo įpareigojimai taip pat brėžiami priimančios šakos juostoje, sukrauti po sujungimo įsipareigojimu ir lygyje su originalais, nes toje šakoje jų dabar tikrai yra. Kopijos yra blyškios ir su šakele, ant kurios buvo parašytos, todėl lieka aišku, kad jos nebuvo ten padarytos. Skvošo suliejimas neturi įtakos: skvošas parašo vieną įprastą įsipareigojimą, taigi visada pasirodo tik tas įsipareigojimas.",
    "legend.title": "Legenda",
    "legend.head": "GALVA / dabartinė šaka",
    "legend.local": "Vietinis filialas",
    "legend.remote": "Nuotolinis filialas",
    "legend.remoteOnly": "Tik debesyje (netraukiama)",
    "legend.tag": "Žyma (versija)",
    "legend.commit": "Įsipareigokite",
    "legend.stash": "Stash (darbas lentynose)",
    "legend.mergedIn": "Atėjo su sujungimu (parašyta ant kito filialo)",
    "legend.nodes": "Dėžutės",
    "legend.lines": "Linijos",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Sujungta šaka – rodyklė rodo sujungimo įsipareigojimą",
    "legend.edgeBranch": "Čia išsišakojusi šaka",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "⤢ Iš naujo nustatyti rodinį",
    "menu.createBranch": "Sukurti filialą iš čia…",
    "menu.checkout": "Patikrinkite šį įsipareigojimą",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Pervardyti įsipareigojimo pranešimą…",
    "menu.undoCommit": "Anuliuoti įsipareigojimą (išlaikyti pakeitimus)…",
    "menu.viewChanges": "Peržiūrėti pakeitimus…",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "{sha} pakeitimai",
    "changes.tabChanged": "Pasikeitė",
    "changes.tabAll": "Visi failai",
    "changes.added": "Pridėta",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Ištrinta",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Pasirinkite failą, kad pamatytumėte jo pakeitimus.",
    "changes.original": "Originalus",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Ankstesnis pakeitimas",
    "changes.nextChange": "Kitas pakeitimas",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Atkurti dydį",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Uždaryti paiešką",
    "find.noResults": "Jokių rezultatų",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Uždaryti",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Įsipareigoti pranešimą",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Apžvalga",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Taikyti atlicināt",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Analizuojamas sujungimas…",
    "merge.upToDate": "Jau atnaujinta – nėra ką sujungti.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Šis sujungimas turi prieštaravimų – vis tiek galite sujungti, tada išspręsti juos redaktoriuje.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Dėl to atsirandantys pokyčiai",
    "merge.added": "Pridėta",
    "merge.modified": "Modifikuota",
    "merge.deleted": "Ištrinta",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Sukurti sujungimo įsipareigojimą (be greito sukimo į priekį)",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Sujungti",
    "merge.cancel": "Atšaukti",
    "merge.previewError": "Nepavyko apskaičiuoti peržiūros: {message}",
    "merge.squashBadge": "Skvošas",
    "merge.squashNote": "Skvošo sujungimas – kiekvienas pakeitimas vyksta kaip VIENAS įsipareigojimas „{target}“; Patys „{source}“ įsipareigojimai lieka už istorijos ribų. Perjunkite atgal skiltyje Nustatymai › Sujungti.",
    "merge.messageSquash": "Įsipareigoti pranešimą",
    "merge.squashMessagePlaceholder": "Sutrinti pakeitimai iš „šaltinio“",
    "newBranch.title": "Sukurti filialą",
    "newBranch.startPoint": "Naujas filialas nuo {sha}",
    "newBranch.startPointOn": "{refs}",
    "newBranch.location": "Vieta",
    "newBranch.locationRoot": "(šaknis)",
    "newBranch.expandAll": "Išskleisti viską",
    "newBranch.collapseAll": "Sutraukti viską",
    "newBranch.expandFolder": "Išskleisti aplanką",
    "newBranch.collapseFolder": "Sutraukti aplanką",
    "newBranch.name": "Filialo pavadinimas",
    "newBranch.namePlaceholder": "mano filialas",
    "newBranch.fullName": "Visas pavadinimas: {name}",
    "newBranch.invalid": "Neteisingas filialo pavadinimas",
    "newBranch.exists": "Filialas tokiu pavadinimu jau yra",
    "newBranch.checkout": "Kasos filialas po sukūrimo",
    "newBranch.create": "Sukurti",
    "newBranch.cancel": "Atšaukti",
    "status.loading": "Įkeliama diagrama…",
    "status.summary": "{repo} Rodomas {commits} įsipareigojimas, {refs} refs",
    "status.branchCreated": "Sukurtas filialas \"{name}\" {sha}",
    "status.error": "Klaida: {message}",
    "status.opFailed": "Operacija nepavyko",
    "status.fetching": "Gaunama…",
    "status.pulling": "Traukia…",
    "status.pushing": "Stumiama…",
    "status.committing": "Kuriamas vietinis įsipareigojimas…",
    "status.commitCreated": "Sukurtas vietinis patvirtinimas {sha}: {message}",
    "status.syncing": "Sinchronizuojama…",
    "status.noHead": "Grafike nerasta dabartinės kasos.",
    "status.undoing": "Anuliuojamas įsipareigojimas…",
    "status.commitUndone": "Įsipareigojimas anuliuotas – pakeitimai vėl rodomi darbiniame medyje.",
    "status.undoConflict": "Anuliuoti smūgių konfliktus – išspręskite juos redaktoriuje, tada tęskite.",
    "status.stashApplying": "Pritaikomas atidėjimas…",
    "status.stashPopping": "Atlieka atsargas…",
    "status.stashDropping": "Atidėti atsargas…",
    "status.stashApplied": "Stash kreipėsi.",
    "status.stashPopped": "Stash iššoko.",
    "status.stashDropped": "Stash nukrito.",
    "status.stashConflict": "Išsaugokite konfliktus – išspręskite juos redaktoriuje.",
    "status.merging": "Sujungiama…",
    "status.merged": "Sujungimas baigtas.",
    "status.mergeConflict": "Sujungimas turi prieštaravimų – išspręskite juos redaktoriuje, tada patvirtinkite.",
    "search.title": "Ieškoti",
    "search.tooltip": "Paieškos įsipareigojimai",
    "search.placeholder": "Ieškoti įsipareigojimų…",
    "search.previous": "Ankstesnis rezultatas",
    "search.next": "Kitas rezultatas",
    "search.noResults": "Jokių rezultatų",
    "search.resultCount": "{index} / {count}",
    "search.filter": "Filtro režimas",
    "search.highlight": "Paryškinimo režimas",
    "search.filterMode": "Paieškos ekranas",
    "search.filterModeFilter": "Filtruoti (slėpti neatitikimus)",
    "search.filterModeHighlight": "Paryškinti (rodyti viską, pažymėti atitikmenis)",
    "search.filterModeHint": "Pasirinkite, kaip diagramoje bus rodomi paieškos rezultatai.",
    "search.visibility": "Ieškokite vietos",
    "search.visibilityToolbar": "Įrankių juostos mygtukas",
    "search.visibilityAlwaysVisible": "Visada matoma juosta",
    "search.visibilityHint": "Rodyti paiešką kaip įrankių juostos piktogramą arba nuolatinę juostą viršuje.",
    "footer.github": "GitHub",
  },
  ln: {
    "toolbar.refresh": "⟳ Kozongisa sika",
    "toolbar.fetch": "⤓ Kozwa",
    "toolbar.pull": "⇩ Kobenda",
    "toolbar.push": "⇧ Kotindika",
    "toolbar.commit": "✓ Komipesa",
    "toolbar.sync": "⇅ Kosala synchronisation",
    "toolbar.jumpHead": "⌖ Kende na esika ya kofuta mbongo",
    "toolbar.reset": "⤢ Kozongisa botali",
    "toolbar.settings": "⚙ Ba paramètres",
    "details.header": "Kopesa Ba Détails",
    "details.close": "Kokanga",
    "details.sha": "SHA",
    "details.shortSha": "Mokuse SHA",
    "details.message": "Nsango",
    "details.author": "Mokomi",
    "details.date": "Dati",
    "details.labels": "Ba étiquettes",
    "details.location": "Esika",
    "details.currentHead": "HEAD — mbongo ya kofuta ya lelo",
    "settings.title": "Ba paramètres",
    "settings.language": "Lokota",
    "settings.theme": "Moto ya likambo",
    "settings.themeLight": "Pole",
    "settings.themeDark": "Molili",
    "settings.themeLightHint": "Fond ya pole, makomi ya molili.",
    "settings.themeDarkHint": "Fond ya molili, makomi ya pole.",
    "settings.mainBranch": "Filiale monene",
    "settings.mainBranchAuto": "Automatique ya kosala",
    "settings.mainBranchSearch": "Boluka ba filiales...",
    "settings.mainBranchNoMatch": "Bitape oyo ekokani te",
    "settings.display": "Style ya kolakisa",
    "settings.displayModern": "Ya mikolo oyo",
    "settings.displayClassic": "Classique",
    "settings.displayModernHint": "Toile ya ofele — benda na pan, défiler na zoom.",
    "settings.displayClassicHint": "Toile fixe — tronc pinné gauche, zoom te, défilement kaka (lokola graphique ya revision ya SVN).",
    "settings.sectionGeneral": "Mbala mingi",
    "settings.sectionGraph": "Graphique oyo ezali na graphique",
    "settings.sectionChanges": "Ebongoli ndenge ya kotala",
    "settings.diffMinimap": "Minimap ya bokeseni",
    "settings.diffMinimapOn": "Elakisami",
    "settings.diffMinimapOff": "Ya kobombana",
    "settings.diffMinimapOnHint": "Lakisa bande ya botali ya lolenge ya VS Code pembeni ya diff — benda yango mpo na kosala défilement; bilembo ya mbongwana ekɔtisamaki.",
    "settings.diffMinimapOffHint": "Bande ya botali ya monene te — défiler diff normalement.",
    "settings.commitReview": "Komipesa na botali lisusu",
    "settings.commitReviewOn": "Tuna liboso ya komipesa",
    "settings.commitReviewOff": "Komipesa mbala moko",
    "settings.commitReviewOnHint": "Lakisa liste ya ba fichiers ya suka mpe vérification ya message avant ya kosala commit local.",
    "settings.commitReviewOffHint": "Bouton Commit esala commit ya esika mbala moko.",
    "settings.svnBranchDialog": "Dialogue ya filiale ya ndenge ya SVN",
    "settings.svnBranchDialogHint": "Lakisa moponi ya nzete ya dossier tango ozali kosala etape (na esika ya kotuna ya pete).",
    "settings.branchDialog": "Dialogue ya filiale",
    "settings.branchDialogSvnTitle": "Na lolenge ya SVN",
    "settings.branchDialogNativeVscode": "VS Code ya style",
    "settings.branchDialogNativeVs": "Style ya Studio ya Visual",
    "settings.branchDialogNativeHint": "Prompt ya branche intégré ya IDE na yo.",
    "settings.close": "Kokanga",
    "settings.done": "Esalemi",
    "settings.sectionAdvanced": "Kokende liboso",
    "settings.gitSource": "Git ekoki kosalama",
    "settings.gitSourceBuiltin": "Etongami na kati",
    "settings.gitSourceBuiltinHint": "Salelá binaire git oyo euti na extension Git oyo etongami na IDE.",
    "settings.gitSourceCustom": "Nzela ya personnalisé",
    "settings.gitPath": "Nzela ya kokende na git",
    "settings.gitPathPlaceholder": "/usr/bin/git, oyo ezali na ntina mingi",
    "settings.gitPathBrowse": "Naviguer na...",
    "settings.jargon": "Maloba ya git",
    "settings.jargonTranslate": "Kobongola",
    "settings.jargonEnglish": "Bomba na Lingelesi",
    "settings.jargonTranslateHint": "Bobongola maloba ya Git (benda, kotindika, komipesa, etape...) na monoko ya interface.",
    "settings.jargonEnglishHint": "Bomba maloba ya Git (benda, kotindika, komipesa, etape...) na Lingelesi; kobongola makambo mosusu nyonso.",
    "settings.sectionMerge": "Kosangisa",
    "settings.mergeMode": "Style ya kosangisa",
    "settings.mergeModeMerge": "Kosangisa ya momesano",
    "settings.mergeModeSquash": "Kosangisa squash",
    "settings.mergeModeMergeHint": "Esangisaka filiale ndenge ezali : ba commits na yango ekomi eteni ya histoire ya filiale ya lelo, esangani na commit ya kosangisa (to ekende liboso mbangu soki likoki ezali).",
    "settings.mergeModeSquashHint": "Ezali ko collapser branche mobimba na commit moko na branche ya lelo (git merge --squash). Ba commits ya filiale yango moko ekotaka jamais na histoire ya filiale ya lelo — omoni kaka commit moko kuna. Etape yango moko etikali kozanga kosimbama, yango wana ba commits na yango etikalaka komonana na voie na yango moko.",
    "settings.mergedView": "Ba commits oyo esangisami na graphique",
    "settings.mergedViewBranch": "Kaka na filiale na bango moko",
    "settings.mergedViewTarget": "Lisusu na filiale oyo basangisaki bango",
    "settings.mergedViewBranchHint": "Ba commits ya filiale oyo esangisami esalemi kaka na voie na yango moko; kosangisa yango emonisaka lokola connecteur. Vue ya classique.",
    "settings.mergedViewTargetHint": "Ba commits oyo merge ememi ebendami na voie ya branche oyo eyambaka lokola, e pile na se ya commit ya merge mpe e niveau na ba originals — po branche wana ezali vraiment na yango sikoyo. Bakopi yango ezali mpɛmbɛ mpe ezali na motó na filiale oyo bakomaki yango, yango wana etikalaka polele ete basalaki yango kuna te. Ba fusions ya squash ezali affecté te : squash ekomaka commit moko ya ordinaire, donc kaka commit wana nde ebimaka jamais.",
    "legend.title": "Legende",
    "legend.head": "MOTO / filiale ya lelo",
    "legend.local": "Filiale ya mboka",
    "legend.remote": "Etape ya mosika",
    "legend.remoteOnly": "Kaka na lipata (ebendami te) .",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Kosala",
    "legend.stash": "Stash (mosala ya étagère) .",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Bakɛsi",
    "legend.lines": "Milɔngɔ́ ya milɔngɔ",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Etape oyo esangisami — flèche elakisaka na commit ya kosangisa",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Kopi commit SHA",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Mbongwana na {sha}",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Ba Fichiers Nionso",
    "changes.added": "Ebakisami",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Babongoli nkombo mosusu",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Pona fisyé moko mpo na komona mbongwana na yango.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Analyse ya fusion...",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Luká esika oyo ozali",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  lg: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "⤓ Okuleeta",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "⇧ Okunyiga",
    "toolbar.commit": "✓ Okwewaayo",
    "toolbar.sync": "⇅ Okukwataganya",
    "toolbar.jumpHead": "⌖ Genda ku kugula ssente",
    "toolbar.reset": "⤢ Okuzzaawo okulaba",
    "toolbar.settings": "⚙ Enteekateeka",
    "details.header": "Commit Ebisingawo",
    "details.close": "Okuggalawo",
    "details.sha": "SHA",
    "details.shortSha": "Mu bumpimpi SHA",
    "details.message": "Obubaka",
    "details.author": "Omuwandiisi",
    "details.date": "Olunaku olw'omweezi",
    "details.labels": "Ebiwandiiko ebiwandiikibwako",
    "details.location": "Ekifo",
    "details.currentHead": "HEAD — okukebera mu kiseera kino",
    "settings.title": "Enteekateeka",
    "settings.language": "Olulimi",
    "settings.theme": "Omulamwa",
    "settings.themeLight": "Koleeza",
    "settings.themeDark": "Ekizikiza",
    "settings.themeLightHint": "Ennyuma enzirugavu, ekiwandiiko ekiddugavu.",
    "settings.themeDarkHint": "Ennyuma enzirugavu, ebiwandiiko ebitangaavu.",
    "settings.mainBranch": "Ettabi eddene",
    "settings.mainBranchAuto": "Otomatiki",
    "settings.mainBranchSearch": "Noonya amatabi...",
    "settings.mainBranchNoMatch": "Tewali matabi gakwatagana",
    "settings.display": "Omusono gw’okulaga",
    "settings.displayModern": "Munnakuzino",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Free canvas — ssika okutuuka ku pan, genda ku zoom.",
    "settings.displayClassicHint": "Fixed canvas — trunk pinned left, tewali zoom, scroll yokka (nga SVN revision graph).",
    "settings.sectionGeneral": "Okutwaaliza awamu",
    "settings.sectionGraph": "Graph",
    "settings.sectionChanges": "Akyusa endowooza",
    "settings.diffMinimap": "Enjawulo entono",
    "settings.diffMinimapOn": "Kiragiddwa",
    "settings.diffMinimapOff": "Okukweekebwa",
    "settings.diffMinimapOnHint": "Laga VS Code-style overview strip ku mabbali ga diff — kikutte okutambula; obubonero bw’enkyukakyuka buzingirwamu.",
    "settings.diffMinimapOffHint": "Tewali strip ya overview — genda mu diff normally.",
    "settings.commitReview": "Okwewaayo okwekenneenya",
    "settings.commitReviewOn": "Buuza nga tonnaba kwewaayo",
    "settings.commitReviewOff": "Weewaayo butereevu",
    "settings.commitReviewOnHint": "Laga olukalala lwa fayiro olusembayo n'okukebera obubaka nga tonnaba kukola kussa mu kitundu.",
    "settings.commitReviewOffHint": "Bataamu ya Commit ekola commit ey'omu kitundu amangu ddala.",
    "settings.svnBranchDialog": "Ensengeka y'ettabi ey'omulembe gwa SVN",
    "settings.svnBranchDialogHint": "Laga omulonda w'omuti gwa folda nga okola ettabi (mu kifo ky'okubuuza okwangu).",
    "settings.branchDialog": "Okukubaganya ebirowoozo ku matabi",
    "settings.branchDialogSvnTitle": "Mu ngeri ya SVN",
    "settings.branchDialogNativeVscode": "Omusono gwa VS Code",
    "settings.branchDialogNativeVs": "Omusono gwa Visual Studio",
    "settings.branchDialogNativeHint": "IDE yo ekizimbibwamu ettabi prompt.",
    "settings.close": "Okuggalawo",
    "settings.done": "Okumala",
    "settings.sectionAdvanced": "Okweyongerako",
    "settings.gitSource": "Git ekolebwa",
    "settings.gitSourceBuiltin": "Ebizimbibwamu",
    "settings.gitSourceBuiltinHint": "Kozesa git binary okuva mu IDE's built-in Git extension.",
    "settings.gitSourceCustom": "Ekkubo ery’ennono",
    "settings.gitPath": "Ekkubo erigenda ku git",
    "settings.gitPathPlaceholder": "/ usr / bin / git",
    "settings.gitPathBrowse": "Browse...",
    "settings.jargon": "Ebigambo bya git",
    "settings.jargonTranslate": "Okuvvunula",
    "settings.jargonEnglish": "Kuuma mu Lungereza",
    "settings.jargonTranslateHint": "Okuvvuunula ebigambo bya Git (sika, okusika, okwewaayo, amatabi...) mu lulimi lw'enkolagana.",
    "settings.jargonEnglishHint": "Kuuma ebigambo bya Git (okusika, okusika, okwewaayo, ettabi...) mu Lungereza; okuvvuunula ebirala byonna.",
    "settings.sectionMerge": "Okugatta",
    "settings.mergeMode": "Omusono gw’okugatta",
    "settings.mergeModeMerge": "Okugatta okwa bulijjo",
    "settings.mergeModeSquash": "Squash okugatta",
    "settings.mergeModeMergeHint": "Egatta ettabi nga bwe liri: commits zaayo zifuuka ekitundu ku byafaayo by'ettabi eririwo kati, nga zigattibwako commit y'okugatta (oba egenda mu maaso amangu bwe kiba kisoboka).",
    "settings.mergeModeSquashHint": "Egwa ettabi lyonna mu commit emu ku ttabi eririwo (git merge --squash). Commits z’ettabi lyenyini teziyingira mu byafaayo by’ettabi eririwo kati — olaba commit emu yokka eyo. Ettabi lyenyini lirekebwa nga terikwatibwako, kale commits zaayo zisigala nga zirabika mu lane yaayo.",
    "settings.mergedView": "Commits ezigatta mu graph",
    "settings.mergedViewBranch": "Ku ttabi lyabwe lyokka",
    "settings.mergedViewTarget": "Era mu ttabi mwe baagatta",
    "settings.mergedViewBranchHint": "Commits z’ettabi erigatta zikubiddwa mu lane yazo yokka; okugatta kulaga nga ekiyungo. Endowooza ya classic.",
    "settings.mergedViewTargetHint": "Commits a merge ereeteddwa zikubiddwa mu lane y'ettabi erifuna nga bwe kiri, ne zitumbibwa wansi w'okugatta commit ne zikwatagana n'ezo ezasooka — kubanga ettabi eryo ddala lizirimu kati. Kopi zino zibeera za bbululu era nga zitunuuliddwa n’ettabi lye zaawandiikibwako, kale kisigala nga kyeyoleka bulungi nti tezaakolebwayo. Okugatta kwa squash tekukwatibwako: squash ewandiika commit emu eya bulijjo, kale commit eyo yokka y’etera okulabika.",
    "legend.title": "Omuzira",
    "legend.head": "OMUTWE / ettabi eririko kati",
    "legend.local": "Ettabi ly’omu kitundu",
    "legend.remote": "Ettabi eriri ewala",
    "legend.remoteOnly": "Mu kire kyokka (ekitali kikubiddwa) .",
    "legend.tag": "Tag (enkyusa) .",
    "legend.commit": "Okwewaayo",
    "legend.stash": "Stash (omulimu ogw’oku ssowaani) .",
    "legend.mergedIn": "Yayingira n'okugatta (ewandiikiddwa ku ttabi eddala)",
    "legend.nodes": "Ebibokisi",
    "legend.lines": "Ennyiriri",
    "legend.edgeParent": "Commit eyasooka ku layini y'emu",
    "legend.edgeMerge": "Ettabi erigatta — akasaale kasonga ku commit y'okugatta",
    "legend.edgeBranch": "Ettabi lyafuka wano",
    "legend.edgeStash": "Stash ne commit gye yali eteekeddwa ku shelved",
    "legend.edgeMergedChain": "Layini y'ettabi lino eddukira mu bikolwa ebigattibwa",
    "legend.edgeMergedTie": "Commit y’emu ku ttabi lye yawandiikibwako",
    "menu.jumpHead": "⌖ Genda ku kugula ssente",
    "menu.resetView": "⤢ Okuzzaawo okulaba",
    "menu.createBranch": "Tonda ettabi okuva wano...",
    "menu.checkout": "Kebera ku commit eno",
    "menu.copySha": "Koppa commit SHA",
    "menu.pushBranch": "Ettabi ly'okusika \"{name}\".",
    "menu.renameCommit": "Kyuusa erinnya ly'obubaka bwa commit...",
    "menu.undoCommit": "Undo commit (kuuma enkyukakyuka)...",
    "menu.viewChanges": "Laba enkyukakyuka...",
    "menu.jumpToOriginal": "Genda ku commit eyasooka",
    "changes.title": "Enkyukakyuka mu {sha}",
    "changes.tabChanged": "Yakyusiddwa",
    "changes.tabAll": "Fayiro zonna",
    "changes.added": "Yayongeddeko",
    "changes.modified": "Ekyusiddwa",
    "changes.deleted": "Esaziddwamu",
    "changes.renamed": "Yakyusiddwa erinnya",
    "changes.noChanges": "Commit eno ekyusa tewali fayiro.",
    "changes.loading": "Okutikka enjawulo...",
    "changes.selectFile": "Londa fayiro olabe enkyukakyuka zaayo.",
    "changes.original": "Yiiye",
    "changes.changed": "Kino commit",
    "changes.binary": "Fayiro ya binary — tewali diff ya biwandiiko kulaga.",
    "changes.tooLarge": "Fayiro nnene nnyo okusobola okwawukana.",
    "changes.renamedFrom": "Erinnya lyakyusibwa okuva ku {path}",
    "changes.close": "Okuggalawo",
    "changes.prevChange": "Enkyukakyuka eyasooka",
    "changes.nextChange": "Enkyukakyuka eddako",
    "changes.collapseAll": "Zingulula ebitabo byonna",
    "changes.expandAll": "Gaziya ebitabo byonna",
    "changes.searchPlaceholder": "Noonya fayiro...",
    "changes.noSearchResults": "Tewali fayiro zikwatagana na kunoonya kwo.",
    "changes.maximize": "Maximize nnyo",
    "changes.restore": "Okuzzaawo sayizi",
    "find.open": "Noonya mu diff eno (Ctrl+F)",
    "find.placeholder": "Funa mu diff...",
    "find.prev": "Omupiira oguwedde",
    "find.next": "Omupiira oguddako",
    "find.close": "Ggalawo okunoonya",
    "find.noResults": "Tewali bivuddemu",
    "commit.title": "Teeka Enkyukakyuka",
    "commit.close": "Okuggalawo",
    "commit.loading": "Okusoma omuti ogukola...",
    "commit.noChanges": "Tewali nkyukakyuka za kitundu ze tulina kwewaayo.",
    "commit.selectFile": "Londa fayiro olabe enjawulo yaayo.",
    "commit.selectAll": "Londa byonna",
    "commit.selectNone": "Byonna birongoose",
    "commit.message": "Obubaka bwa Commit",
    "commit.messagePlaceholder": "Nnyonnyola enkyukakyuka",
    "commit.selectedCount": "{count} fayiro (s) erongooseddwa",
    "commit.review": "Okwekkaanya",
    "commit.reviewTitle": "Okukebera okusembayo nga tonnaba kwewaayo",
    "commit.reviewSummary": "Teeka fayiro(s) za {count} n'obubaka: {message}",
    "commit.back": "Mabega",
    "commit.commit": "Tonda okwewaayo mu kitundu",
    "menu.stashApply": "Siiga stash",
    "menu.stashPop": "Pop stash (siiga & okuggyawo)",
    "menu.stashDrop": "Drop stash...",
    "merge.title": "Ettabi ly'okugatta",
    "merge.route": "Okugatta mu ttabi eririwo kati",
    "merge.source": "Okuva mu (ensibuko)",
    "merge.target": "Mu (ekiriwo kati) .",
    "merge.loading": "Okwekenenya okugatta...",
    "merge.upToDate": "Edda ku mulembe — tewali kintu kyonna kigatta.",
    "merge.fastForward": "Fast-forward — tewali kugatta kwewaayo kwetaagisa (tick “Create a merge commit” okukaka ekimu).",
    "merge.summary": "{files} fayiro (s) ekyusiddwa, {conflicts} okukontana (s).",
    "merge.conflictsWarning": "Okugatta kuno kulina obutakkaanya — okyayinza okugatta, olwo okubigonjoola mu mulongoosa.",
    "merge.noChanges": "Tewali nkyukakyuka mu fayiro.",
    "merge.files": "Enkyukakyuka ezivaamu",
    "merge.added": "Yayongeddeko",
    "merge.modified": "Ekyusiddwa",
    "merge.deleted": "Esaziddwamu",
    "merge.conflict": "Katabanguko",
    "merge.message": "Obubaka bwa commit okugatta",
    "merge.messagePlaceholder": "Gatta ettabi 'ensibuko'.",
    "merge.messageFfHint": "Esiwuuka empisa ku kugatta okw'amangu mu maaso (tewali kussaawo kutondebwa).",
    "merge.noFastForward": "Tonda okussaawo okugatta (tewali kugenda mu maaso mangu) .",
    "merge.noFastForwardHint": "Bulijjo wandiika merge commit, ne bwe kiba nti ettabi liyinza okugenda mu maaso amangu.",
    "merge.merge": "Okugatta",
    "merge.cancel": "okusazaamu",
    "merge.previewError": "Teyasobodde kubalirira kusooka kulaba: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge — buli nkyukakyuka ekka nga ONE commit ku “{target}”; “{source}”’s own commits zisigala nga teziyingira mu byafaayo byayo. Kyusa oddeyo mu Nteekateeka › Gatta.",
    "merge.messageSquash": "Obubaka bwa Commit",
    "merge.squashMessagePlaceholder": "Enkyukakyuka ezikubiddwa okuva ku 'source'.",
    "newBranch.title": "Tonda Ettabi",
    "newBranch.startPoint": "Ettabi eppya nga litandikira ku {sha}",
    "newBranch.startPointOn": "ku {refs}",
    "newBranch.location": "Ekifo",
    "newBranch.locationRoot": "(omuzi)",
    "newBranch.expandAll": "Gaziya byonna",
    "newBranch.collapseAll": "Mugwamu byonna",
    "newBranch.expandFolder": "Gaziya folda",
    "newBranch.collapseFolder": "Okugwa mu folda",
    "newBranch.name": "Erinnya ly’ettabi",
    "newBranch.namePlaceholder": "ettabi lyange",
    "newBranch.fullName": "Amannya mu bujjuvu: {name}",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Ettabi eririna erinnya lino lyaliwo dda",
    "newBranch.checkout": "Checkout branch oluvannyuma lw'okutonda",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "okusazaamu",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "{repo}Okulaga {commits} ekola, {refs} refs",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Ensobi: {message}",
    "status.opFailed": "Okulongoosa kwalemereddwa",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Okusika...",
    "status.pushing": "Okusika...",
    "status.committing": "Okukola local commit...",
    "status.commitCreated": "Yatondebwawo okwewaayo mu kitundu {sha}: {message}",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Popping stash...",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Stash yabuuka.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Stash enkaayana — zigonjoole mu editor.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Okunoonya kwewaayo",
    "search.placeholder": "Okunoonya kwewaayo...",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Noonya ekifo",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Laga okunoonya ng'akabonero k'ebbaala y'ebikozesebwa oba ebbaala etaggwaawo waggulu.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  lb: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Zoumaachen",
    "details.sha": "SHA",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Frot virum Engagement",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Vun (Quelle)",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Dës Fusioun huet Konflikter - Dir kënnt ëmmer nach fusionéieren, dann léisen se am Editor.",
    "merge.noChanges": "Keng Datei Ännerungen.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Merge engagéieren Message",
    "merge.messagePlaceholder": "Filial 'Quell' fusionéieren",
    "merge.messageFfHint": "Ignoréiert op engem Fast-Forward Fusioun (kee Verpflichtung gëtt erstallt).",
    "merge.noFastForward": "Erstellt e Fusiounsverpflichtung (kee Fast-Forward)",
    "merge.noFastForwardHint": "Notéiert ëmmer e Fusiounsverpflichtung, och wann d'Branche séier viru kéint.",
    "merge.merge": "Fusioun",
    "merge.cancel": "Ofbriechen",
    "merge.previewError": "Konnt eng Virschau net berechnen: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash fusionéieren - all Ännerung landen als EEN Engagement op \"{target}\"; Dem \"{source}\" seng eege Verpflichtungen bleiwen aus senger Geschicht. Wiesselt zréck an Astellungen › Merge.",
    "merge.messageSquash": "Engagéiert Message",
    "merge.squashMessagePlaceholder": "Gepresst Ännerungen aus 'Quell'",
    "newBranch.title": "Branch erstellen",
    "newBranch.startPoint": "Nei Filial ab {sha}",
    "newBranch.startPointOn": "op {refs}",
    "newBranch.location": "Location",
    "newBranch.locationRoot": "(root)",
    "newBranch.expandAll": "All erweideren",
    "newBranch.collapseAll": "Zesummebroch alles",
    "newBranch.expandFolder": "Dossier erweideren",
    "newBranch.collapseFolder": "Dossier zesummeklappen",
    "newBranch.name": "Branch Numm",
    "newBranch.namePlaceholder": "meng Branche",
    "newBranch.fullName": "Voll Numm: {name}",
    "newBranch.invalid": "Invalid Branche Numm",
    "newBranch.exists": "Eng Filial mat dësem Numm gëtt et schonn",
    "newBranch.checkout": "Checkout Branche no Kreatioun",
    "newBranch.create": "Schafen",
    "newBranch.cancel": "Ofbriechen",
    "status.loading": "Lueden Grafik…",
    "status.summary": "{repo}Showing {commits} commits, {refs} refs",
    "status.branchCreated": "Erstellt Filial \"{name}\" bei {sha}",
    "status.error": "Feeler: {message}",
    "status.opFailed": "Operatioun gescheitert",
    "status.fetching": "Erhalen…",
    "status.pulling": "Zitt…",
    "status.pushing": "Dréckt…",
    "status.committing": "Lokal Engagement erstellen ...",
    "status.commitCreated": "Lokal Verpflichtung {sha} erstallt: {message}",
    "status.syncing": "Synchroniséiert…",
    "status.noHead": "Keng aktuell Kees fonnt an der Grafik.",
    "status.undoing": "Engagement annuléieren ...",
    "status.commitUndone": "Engagéiert undoen - Ännerungen sinn zréck am schaffen Bam.",
    "status.undoConflict": "Undoen Hit Konflikter - léisen se am Redakter, da weider.",
    "status.stashApplying": "Stash uwenden…",
    "status.stashPopping": "Klappt Stash…",
    "status.stashDropping": "Stash erofsetzen ...",
    "status.stashApplied": "Stash applizéiert.",
    "status.stashPopped": "Stash ass gefall.",
    "status.stashDropped": "Stash gefall.",
    "status.stashConflict": "Stash Konflikter - léisen se am Editor.",
    "status.merging": "Fusioun…",
    "status.merged": "Fusioun fäerdeg.",
    "status.mergeConflict": "Fusioun huet Konflikter - léist se am Editor, dann engagéiert.",
    "search.title": "Sichen",
    "search.tooltip": "Sich engagéiert",
    "search.placeholder": "Sich engagéiert ...",
    "search.previous": "Virdrun Resultat",
    "search.next": "Nächst Resultat",
    "search.noResults": "Keng Resultater",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtermodus",
    "search.highlight": "Highlight Modus",
    "search.filterMode": "Sich Display",
    "search.filterModeFilter": "Filter (verstoppen Net-Matcher)",
    "search.filterModeHighlight": "Highlight (Alles weisen, Mätscher markéieren)",
    "search.filterModeHint": "Wielt wéi d'Sichresultater an der Grafik ugewise ginn.",
    "search.visibility": "Sich Plaz",
    "search.visibilityToolbar": "Toolbar Knäppchen",
    "search.visibilityAlwaysVisible": "Ëmmer sichtbar Bar",
    "search.visibilityHint": "Show Sich als Toolbar Ikon oder persistent Bar uewen.",
    "footer.github": "GitHub",
  },
  mg: {
    "toolbar.refresh": "⟳ Havaozina",
    "toolbar.fetch": "⤓ Alao",
    "toolbar.pull": "⇩ Sintonina",
    "toolbar.push": "⇧ Tosika",
    "toolbar.commit": "✓ Manolo-tena",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Mandehana any amin'ny fivarotana",
    "toolbar.reset": "⤢ Avereno indray ny fijery",
    "toolbar.settings": "⚙ Fikirana",
    "details.header": "Manome antsipiriany",
    "details.close": "AKAIKY",
    "details.sha": "hoe sha",
    "details.shortSha": "SHA fohy",
    "details.message": "hAFATRA",
    "details.author": "Mpanoratra",
    "details.date": "Daty",
    "details.labels": "etikety",
    "details.location": "Toerana",
    "details.currentHead": "HEAD - fisavana ankehitriny",
    "settings.title": "Fikirana",
    "settings.language": "Fiteny",
    "settings.theme": "FOTO-KEVITRA",
    "settings.themeLight": "fahazavana",
    "settings.themeDark": "Maizina",
    "settings.themeLightHint": "Lafo mazava, soratra maizina.",
    "settings.themeDarkHint": "Maizina ambadika, lahatsoratra maivana.",
    "settings.mainBranch": "Sampana lehibe",
    "settings.mainBranchAuto": "mandeha ho azy",
    "settings.mainBranchSearch": "Mitadiava sampana…",
    "settings.mainBranchNoMatch": "Tsy misy sampana mifanandrify",
    "settings.display": "Fomba fampisehoana",
    "settings.displayModern": "ANKEHITRINY",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Canvas maimaim-poana - tarihina hankany amin'ny pan, horonan-taratasy mba zoom.",
    "settings.displayClassicHint": "Canvas raikitra - vatan-kazo voapetaka havia, tsy misy zoom, horonana fotsiny (toy ny kisary fanavaozana SVN).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Tabilao",
    "settings.sectionChanges": "Manova fijery",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Aseho",
    "settings.diffMinimapOff": "Zavatra tsy",
    "settings.diffMinimapOnHint": "Asehoy ny VS Code-style overview strip eo anilan'ny diff — tariho izany mba hikoriana; misy marika fanovana.",
    "settings.diffMinimapOffHint": "Tsy misy tsipika famintinana - scrolloly ny diff mahazatra.",
    "settings.commitReview": "Manaova famerenana",
    "settings.commitReviewOn": "Anontanio alohan'ny hanolo-tena",
    "settings.commitReviewOff": "Manolotena mivantana",
    "settings.commitReviewOnHint": "Asehoy ny lisitry ny rakitra farany sy ny fanamarinana hafatra alohan'ny hamoronana ny commit eo an-toerana.",
    "settings.commitReviewOffHint": "Ny bokotra Commit dia mamorona ny commit eo an-toerana avy hatrany.",
    "settings.svnBranchDialog": "Dinika sampana fomba SVN",
    "settings.svnBranchDialogHint": "Asehoy ny mpanangona hazo fampirimana rehefa mamorona sampana (fa tsy bitsika tsotra).",
    "settings.branchDialog": "Dinika sampana",
    "settings.branchDialogSvnTitle": "SVN-style",
    "settings.branchDialogNativeVscode": "VS Code style",
    "settings.branchDialogNativeVs": "Visual Studio fomba",
    "settings.branchDialogNativeHint": "Fampandrenesan'ny sampana naorina ao amin'ny IDE anao.",
    "settings.close": "AKAIKY",
    "settings.done": "hatao",
    "settings.sectionAdvanced": "Advanced",
    "settings.gitSource": "Git executable",
    "settings.gitSourceBuiltin": "Naorina-in",
    "settings.gitSourceBuiltinHint": "Ampiasao ny binary git avy amin'ny fanitarana Git naorina ao amin'ny IDE.",
    "settings.gitSourceCustom": "Lalana manokana",
    "settings.gitPath": "Lalana mankany git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Hijery…",
    "settings.jargon": "Terms Git",
    "settings.jargonTranslate": "translate",
    "settings.jargonEnglish": "Tazony amin'ny teny anglisy",
    "settings.jargonTranslateHint": "Adikao ny teny Git (misintona, manosika, manolo-tena, sampana…) amin'ny fiteny interface.",
    "settings.jargonEnglishHint": "Tazony ny teny Git (misintona, manosika, manolo-tena, sampana…) amin'ny teny anglisy; handika ny hafa rehetra.",
    "settings.sectionMerge": "mampiray",
    "settings.mergeMode": "Fomba fanakambanana",
    "settings.mergeModeMerge": "Normal merge",
    "settings.mergeModeSquash": "Squash mitambatra",
    "settings.mergeModeMergeHint": "Mampitambatra ny sampana amin'ny maha izy azy: lasa ampahany amin'ny tantaran'ny sampana amin'izao fotoana izao ny commits, miaraka amin'ny merge commit (na mandroso haingana raha azo atao).",
    "settings.mergeModeSquashHint": "Mamerina ny sampana manontolo ho commit tokana amin'ny sampana ankehitriny (git merge --squash). Tsy miditra amin'ny tantaran'ny sampana amin'izao fotoana izao ny fanoloran-tenan'ny sampana — iray ihany no hitanao ao. Ny sampana mihitsy no avela tsy voakitika, noho izany dia mijanona ho hita eo amin'ny lalany ny commit-ny.",
    "settings.mergedView": "Mitambatra commit ao amin'ny grafika",
    "settings.mergedViewBranch": "Ao amin'ny sampany ihany",
    "settings.mergedViewTarget": "Ao amin'ny sampana ihany koa izy ireo no nitambatra",
    "settings.mergedViewBranchHint": "Ny faneken'ny sampana mitambatra dia atao amin'ny lalany ihany; ny fampiraisana dia miseho ho toy ny connector. Ny fomba fijery mahazatra.",
    "settings.mergedViewTargetHint": "Ny fampifangaroana nampidirina dia voasokajy ao amin'ny lalan'ny sampana mandray ihany koa, atambatra eo ambanin'ny commit fampifangaroana sy mifanandrify amin'ny tany am-boalohany — satria io sampana io dia tena misy azy ireo ankehitriny. Hatsatra ireo dika mitovy ary miaraka amin'ny sampana nanoratana azy ireo, ka mazava ho azy fa tsy natao tao. Tsy misy fiantraikany ny fikambanan'ny squash: manoratra commit mahazatra ny squash, ka io commit io ihany no miseho.",
    "legend.title": "Maribolana",
    "legend.head": "HEAD / sampana ankehitriny",
    "legend.local": "Sampana eo an-toerana",
    "legend.remote": "Sampana lavitra",
    "legend.remoteOnly": "Ao amin'ny rahona ihany (tsy voasintona)",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Stash sy ny commit najanona teo",
    "legend.edgeMergedChain": "Ny andalana an'ity sampana ity dia mandeha amin'ny commit mitambatra",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Ity fanoloran-tena ity",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Ampodio ny lahatahiry rehetra",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Avereno ny habeny",
    "find.open": "Karoka amin'ity diff ity (Ctrl+F)",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Atambatra ny hafatra commit",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Mamorona commit merge (tsy mandroso haingana)",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  mt: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Kollass folder",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "L-operazzjoni falliet",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Is-sinkronizzazzjoni...",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  lus: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  mi: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Waihangahia he mahi hanumi (kaore he tere whakamua)",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Waihanga Peka",
    "newBranch.startPoint": "Peka hou mai i te {sha}",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Tauwāhi",
    "newBranch.locationRoot": "(pakiaka)",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Tiango katoa",
    "newBranch.expandFolder": "Roha kōpaki",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "ingoa peka",
    "newBranch.namePlaceholder": "taku-manga",
    "newBranch.fullName": "Ingoa katoa: {name}",
    "newBranch.invalid": "He muhu te ingoa peka",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Peka tirotiro i muri i te hanganga",
    "newBranch.create": "Waihanga",
    "newBranch.cancel": "Whakakore",
    "status.loading": "Uta ana te kauwhata…",
    "status.summary": "{repo}Whakaatu {commits} mahi, {refs} refs",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Hapa: {message}",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Tikina…",
    "status.pulling": "E kumea ana…",
    "status.pushing": "E pana ana…",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "I waihangahia te mahi a rohe {sha}: {message}",
    "status.syncing": "Tukutahi…",
    "status.noHead": "Karekau he utu i kitea i te kauwhata.",
    "status.undoing": "Te whakakore i te tukunga…",
    "status.commitUndone": "Whakakorehia - kua hoki mai nga huringa ki te rakau mahi.",
    "status.undoConflict": "Wetekina nga papā kua pa - whakatauhia i roto i te ētita, ka haere tonu.",
    "status.stashApplying": "Kei te tono whakapuranga…",
    "status.stashPopping": "Pupuri putunga…",
    "status.stashDropping": "Whakataka ana i te putunga…",
    "status.stashApplied": "Ka tonohia a Stash.",
    "status.stashPopped": "Puta ana a Stash.",
    "status.stashDropped": "I heke a Stash.",
    "status.stashConflict": "Pumau nga papā — whakatauhia i roto i te ētita.",
    "status.merging": "Hanumi…",
    "status.merged": "Kua oti te hanumi.",
    "status.mergeConflict": "He raruraru te Hanumi — whakatauhia i roto i te ētita, katahi ka mahia.",
    "search.title": "Rapu",
    "search.tooltip": "Ka mahi te rapu",
    "search.placeholder": "Ka mahi te rapu…",
    "search.previous": "Hua o mua",
    "search.next": "Hua whai muri",
    "search.noResults": "Karekau he hua",
    "search.resultCount": "{index}/{count}",
    "search.filter": "aratau tātari",
    "search.highlight": "Aratau miramira",
    "search.filterMode": "Whakaaturanga rapu",
    "search.filterModeFilter": "Tātari (huna he ōrite kore)",
    "search.filterModeHighlight": "Miramira (whakaatuhia te katoa, tohu i nga whakataetae)",
    "search.filterModeHint": "Kōwhiria me pehea e whakaatuhia ai nga hua rapu ki te kauwhata.",
    "search.visibility": "Rapu tauwāhi",
    "search.visibilityToolbar": "Pātene paeutauta",
    "search.visibilityAlwaysVisible": "Pae kitea i nga wa katoa",
    "search.visibilityHint": "Whakaatuhia te rapu hei tohu paeutauta, he pae tohe ranei kei runga.",
    "footer.github": "GitHub",
  },
  nl: {
    "toolbar.refresh": "⟳ Vernieuwen",
    "toolbar.fetch": "⤓ Ophalen",
    "toolbar.pull": "⇩ Trek",
    "toolbar.push": "⇧ Duwen",
    "toolbar.commit": "✓ Betrokkenheid",
    "toolbar.sync": "⇅ Synchroniseren",
    "toolbar.jumpHead": "⌖ Ga naar afrekenen",
    "toolbar.reset": "⤢ Weergave resetten",
    "toolbar.settings": "⚙ Instellingen",
    "details.header": "Toezeggingsdetails",
    "details.close": "Dichtbij",
    "details.sha": "SHA",
    "details.shortSha": "Korte SHA",
    "details.message": "Bericht",
    "details.author": "Auteur",
    "details.date": "Datum",
    "details.labels": "Etiketten",
    "details.location": "Locatie",
    "details.currentHead": "HOOFD — huidige kassa",
    "settings.title": "Instellingen",
    "settings.language": "Taal",
    "settings.theme": "Thema",
    "settings.themeLight": "Licht",
    "settings.themeDark": "Donker",
    "settings.themeLightHint": "Lichte achtergrond, donkere tekst.",
    "settings.themeDarkHint": "Donkere achtergrond, lichte tekst.",
    "settings.mainBranch": "Hoofdtak",
    "settings.mainBranchAuto": "Automatisch",
    "settings.mainBranchSearch": "Zoek filialen…",
    "settings.mainBranchNoMatch": "Geen overeenkomende takken",
    "settings.display": "Weergavestijl",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Klassiek",
    "settings.displayModernHint": "Vrij canvas: sleep om te pannen, scroll om te zoomen.",
    "settings.displayClassicHint": "Vast canvas - kofferbak links vastgezet, geen zoom, alleen scrollen (zoals de SVN-revisiegrafiek).",
    "settings.sectionGeneral": "Algemeen",
    "settings.sectionGraph": "Grafiek",
    "settings.sectionChanges": "Verandert de weergave",
    "settings.diffMinimap": "Verschil minimap",
    "settings.diffMinimapOn": "Getoond",
    "settings.diffMinimapOff": "Verborgen",
    "settings.diffMinimapOnHint": "Toon een overzichtsstrook in VS Code-stijl naast de diff - sleep deze om te scrollen; wisselmarkeringen inbegrepen.",
    "settings.diffMinimapOffHint": "Geen overzichtsstrook - blader normaal door de diff.",
    "settings.commitReview": "Beoordeling plegen",
    "settings.commitReviewOn": "Vraag voordat u zich vastlegt",
    "settings.commitReviewOff": "Direct toezeggen",
    "settings.commitReviewOnHint": "Toon een definitieve bestandslijst en berichtcontrole voordat u de lokale commit maakt.",
    "settings.commitReviewOffHint": "Met de knop Commit wordt de lokale commit onmiddellijk gemaakt.",
    "settings.svnBranchDialog": "SVN-stijl vertakkingsdialoog",
    "settings.svnBranchDialogHint": "Toon een mappenboomkiezer bij het maken van een vertakking (in plaats van een eenvoudige prompt).",
    "settings.branchDialog": "Vertakkingsdialoog",
    "settings.branchDialogSvnTitle": "SVN-stijl",
    "settings.branchDialogNativeVscode": "VS Code-stijl",
    "settings.branchDialogNativeVs": "Visual Studio-stijl",
    "settings.branchDialogNativeHint": "De ingebouwde vertakkingsprompt van uw IDE.",
    "settings.close": "Dichtbij",
    "settings.done": "Klaar",
    "settings.sectionAdvanced": "Geavanceerd",
    "settings.gitSource": "Git-uitvoerbaar bestand",
    "settings.gitSourceBuiltin": "Ingebouwd",
    "settings.gitSourceBuiltinHint": "Gebruik het binaire git-bestand van de ingebouwde Git-extensie van de IDE.",
    "settings.gitSourceCustom": "Aangepast pad",
    "settings.gitPath": "Pad naar git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Blader…",
    "settings.jargon": "Git-voorwaarden",
    "settings.jargonTranslate": "Vertalen",
    "settings.jargonEnglish": "Houd het in het Engels",
    "settings.jargonTranslateHint": "Vertaal Git-termen (pull, push, commit, branch…) naar de interfacetaal.",
    "settings.jargonEnglishHint": "Houd Git-termen (pull, push, commit, branch…) in het Engels; vertaal al het andere.",
    "settings.sectionMerge": "Samenvoegen",
    "settings.mergeMode": "Stijl samenvoegen",
    "settings.mergeModeMerge": "Normale samenvoeging",
    "settings.mergeModeSquash": "Squash samenvoegen",
    "settings.mergeModeMergeHint": "Voegt de branch samen zoals deze is: zijn commits worden onderdeel van de geschiedenis van de huidige branch, samen met een merge commit (of versneld indien mogelijk).",
    "settings.mergeModeSquashHint": "Vouwt de hele branch samen in een enkele commit op de huidige branch (git merge --squash). De eigen commits van de branch komen nooit in de geschiedenis van de huidige branch terecht; je ziet daar slechts één commit. De tak zelf blijft onaangeroerd, zodat de commits zichtbaar blijven in zijn eigen baan.",
    "settings.mergedView": "Samengevoegde commits in de grafiek",
    "settings.mergedViewBranch": "Alleen op hun eigen filiaal",
    "settings.mergedViewTarget": "Ook in de branche waarin ze zijn opgegaan",
    "settings.mergedViewBranchHint": "De commits van een samengevoegde vertakking worden alleen in hun eigen baan getrokken; de samenvoeging wordt weergegeven als een connector. De klassieke visie.",
    "settings.mergedViewTargetHint": "De commits die door een merge zijn binnengebracht, worden ook in de baan van de ontvangende branch getekend, gestapeld onder de merge commit en op gelijke hoogte met de originelen — omdat die branch ze nu echt bevat. De kopieën zijn bleek en hebben de kop van de tak waarop ze zijn geschreven, dus het blijft duidelijk dat ze daar niet zijn gemaakt. Squash-samenvoegingen worden niet beïnvloed: een squash schrijft één gewone commit, dus alleen die commit verschijnt ooit.",
    "legend.title": "Legende",
    "legend.head": "HOOFD / huidige tak",
    "legend.local": "Lokale vestiging",
    "legend.remote": "Afgelegen filiaal",
    "legend.remoteOnly": "Alleen in de cloud (niet getrokken)",
    "legend.tag": "Label (versie)",
    "legend.commit": "Verbinden",
    "legend.stash": "Stash (werk op de plank)",
    "legend.mergedIn": "Kwam binnen met een samenvoeging (geschreven op een andere tak)",
    "legend.nodes": "Dozen",
    "legend.lines": "Lijnen",
    "legend.edgeParent": "Vorige commit op dezelfde regel",
    "legend.edgeMerge": "Samengevoegde branch — de pijl wijst naar de merge commit",
    "legend.edgeBranch": "Hier is een tak afgesplitst",
    "legend.edgeStash": "Stash en de commit waarvoor het op de plank lag",
    "legend.edgeMergedChain": "De lijn van deze vertakking loopt door de samengevoegde commits",
    "legend.edgeMergedTie": "Dezelfde commit voor de branch waarop het is geschreven",
    "menu.jumpHead": "⌖ Ga naar afrekenen",
    "menu.resetView": "⤢ Weergave resetten",
    "menu.createBranch": "Maak hier een filiaal aan...",
    "menu.checkout": "Bekijk deze commit",
    "menu.copySha": "Kopieer commit SHA",
    "menu.pushBranch": "Duwaftakking \"{name}\"",
    "menu.renameCommit": "Hernoem het commit-bericht…",
    "menu.undoCommit": "Vastlegging ongedaan maken (wijzigingen behouden)…",
    "menu.viewChanges": "Wijzigingen bekijken…",
    "menu.mergeBranch": "Voeg \"{source}\" samen met \"{target}\"…",
    "menu.jumpToOriginal": "Ga naar de oorspronkelijke commit",
    "changes.title": "Veranderingen in {sha}",
    "changes.tabChanged": "Gewijzigd",
    "changes.tabAll": "Alle bestanden",
    "changes.added": "Toegevoegd",
    "changes.modified": "Gewijzigd",
    "changes.deleted": "Verwijderd",
    "changes.renamed": "Hernoemd",
    "changes.noChanges": "Deze commit verandert geen bestanden.",
    "changes.loading": "Verschil laden…",
    "changes.selectFile": "Selecteer een bestand om de wijzigingen te bekijken.",
    "changes.original": "Origineel",
    "changes.changed": "Deze toezegging",
    "changes.binary": "Binair bestand — geen tekstverschil om weer te geven.",
    "changes.tooLarge": "Bestand is te groot om te diff.",
    "changes.renamedFrom": "Hernoemd van {path}",
    "changes.close": "Dichtbij",
    "changes.prevChange": "Vorige wijziging",
    "changes.nextChange": "Volgende wijziging",
    "changes.collapseAll": "Vouw alle mappen samen",
    "changes.expandAll": "Vouw alle mappen uit",
    "changes.searchPlaceholder": "Bestanden zoeken…",
    "changes.noSearchResults": "Er zijn geen bestanden die overeenkomen met uw zoekopdracht.",
    "changes.maximize": "Maximaliseren",
    "changes.restore": "Grootte herstellen",
    "find.open": "Zoeken in dit diff (Ctrl+F)",
    "find.placeholder": "Zoek in verschil…",
    "find.prev": "Vorige wedstrijd",
    "find.next": "Volgende wedstrijd",
    "find.close": "Zoekopdracht sluiten",
    "find.noResults": "Geen resultaten",
    "commit.title": "Wijzigingen doorvoeren",
    "commit.close": "Dichtbij",
    "commit.loading": "Werkende boom lezen…",
    "commit.noChanges": "Er zijn geen lokale wijzigingen die moeten worden vastgelegd.",
    "commit.selectFile": "Selecteer een bestand om het verschil te zien.",
    "commit.selectAll": "Selecteer alles",
    "commit.selectNone": "Alles wissen",
    "commit.message": "Bericht vastleggen",
    "commit.messagePlaceholder": "Beschrijf de verandering",
    "commit.selectedCount": "{count}-bestand(en) geselecteerd",
    "commit.review": "Beoordeling",
    "commit.reviewTitle": "Laatste controle vóór commit",
    "commit.reviewSummary": "Commit {count}-bestand(en) met bericht: {message}",
    "commit.back": "Rug",
    "commit.commit": "Maak een lokale commit",
    "menu.stashApply": "Breng stash aan",
    "menu.stashPop": "Pop stash (aanbrengen en verwijderen)",
    "menu.stashDrop": "Laat voorraad vallen...",
    "merge.title": "Filiaal samenvoegen",
    "merge.route": "Samenvoegen met de huidige tak",
    "merge.source": "Van (bron)",
    "merge.target": "In (huidig)",
    "merge.loading": "Samenvoeging analyseren…",
    "merge.upToDate": "Al up-to-date - niets om samen te voegen.",
    "merge.fastForward": "Snel vooruit: geen merge commit nodig (vink “Create a merge commit” aan om er een te forceren).",
    "merge.summary": "{files}-bestand(en) gewijzigd, {conflicts}-conflict(en).",
    "merge.conflictsWarning": "Deze samenvoeging brengt conflicten met zich mee. Je kunt nog steeds samenvoegen en deze vervolgens oplossen in de editor.",
    "merge.noChanges": "Geen bestandswijzigingen.",
    "merge.files": "Resulterende veranderingen",
    "merge.added": "Toegevoegd",
    "merge.modified": "Gewijzigd",
    "merge.deleted": "Verwijderd",
    "merge.conflict": "Conflict",
    "merge.message": "Commitbericht samenvoegen",
    "merge.messagePlaceholder": "Tak 'bron' samenvoegen",
    "merge.messageFfHint": "Genegeerd bij een snelle samenvoeging (er wordt geen commit gemaakt).",
    "merge.noFastForward": "Maak een merge commit (geen fast forward)",
    "merge.noFastForwardHint": "Leg altijd een merge commit vast, zelfs als de branch snel vooruit kan spoelen.",
    "merge.merge": "Samenvoegen",
    "merge.cancel": "Annuleren",
    "merge.previewError": "Kan geen voorbeeld berekenen: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge — elke verandering komt terecht als EEN commit op “{target}”; De eigen commits van “{source}” blijven buiten de geschiedenis. Schakel terug via Instellingen › Samenvoegen.",
    "merge.messageSquash": "Bericht vastleggen",
    "merge.squashMessagePlaceholder": "Wijzigingen uit 'bron' vernietigd",
    "newBranch.title": "Filiaal aanmaken",
    "newBranch.startPoint": "Nieuwe vestiging vanaf {sha}",
    "newBranch.startPointOn": "op {refs}",
    "newBranch.location": "Locatie",
    "newBranch.locationRoot": "(wortel)",
    "newBranch.expandAll": "Alles uitvouwen",
    "newBranch.collapseAll": "Alles samenvouwen",
    "newBranch.expandFolder": "Vouw map uit",
    "newBranch.collapseFolder": "Map samenvouwen",
    "newBranch.name": "Naam van filiaal",
    "newBranch.namePlaceholder": "mijn-filiaal",
    "newBranch.fullName": "Volledige naam: {name}",
    "newBranch.invalid": "Ongeldige filiaalnaam",
    "newBranch.exists": "Er bestaat al een tak met deze naam",
    "newBranch.checkout": "Afrekenfiliaal na creatie",
    "newBranch.create": "Creëren",
    "newBranch.cancel": "Annuleren",
    "status.loading": "Grafiek laden…",
    "status.summary": "{repo}Toon {commits} commits, {refs} refs",
    "status.branchCreated": "Tak \"{name}\" gemaakt op {sha}",
    "status.error": "Fout: {message}",
    "status.opFailed": "Operatie mislukt",
    "status.fetching": "Ophalen…",
    "status.pulling": "Trekken…",
    "status.pushing": "Duwen…",
    "status.committing": "Lokale commitment creëren…",
    "status.commitCreated": "Lokale commit {sha} gemaakt: {message}",
    "status.syncing": "Synchroniseren...",
    "status.noHead": "Er is geen huidige kassa gevonden in de grafiek.",
    "status.undoing": "Vastlegging ongedaan maken…",
    "status.commitUndone": "Vastlegging ongedaan gemaakt - de wijzigingen zijn terug in de werkboom.",
    "status.undoConflict": "Maak hitconflicten ongedaan: los ze op in de editor en ga vervolgens verder.",
    "status.stashApplying": "Stash aanbrengen…",
    "status.stashPopping": "Opruimen voorraad…",
    "status.stashDropping": "Voorraad laten vallen…",
    "status.stashApplied": "Stash toegepast.",
    "status.stashPopped": "Stash knalde.",
    "status.stashDropped": "Stash is gevallen.",
    "status.stashConflict": "Conflicten opslaan: los ze op in de editor.",
    "status.merging": "Samenvoegen…",
    "status.merged": "Samenvoegen voltooid.",
    "status.mergeConflict": "Samenvoegen heeft conflicten: los ze op in de editor en voer vervolgens een commit uit.",
    "search.title": "Zoekopdracht",
    "search.tooltip": "Zoek commits",
    "search.placeholder": "Zoek commits...",
    "search.previous": "Vorig resultaat",
    "search.next": "Volgende resultaat",
    "search.noResults": "Geen resultaten",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtermodus",
    "search.highlight": "Markeer modus",
    "search.filterMode": "Zoekweergave",
    "search.filterModeFilter": "Filter (verberg niet-overeenkomsten)",
    "search.filterModeHighlight": "Markeer (alles weergeven, overeenkomsten markeren)",
    "search.filterModeHint": "Kies hoe zoekresultaten in de grafiek worden weergegeven.",
    "search.visibility": "Zoek locatie",
    "search.visibilityToolbar": "Werkbalkknop",
    "search.visibilityAlwaysVisible": "Altijd zichtbare balk",
    "search.visibilityHint": "Toon zoeken als een werkbalkpictogram of een permanente balk bovenaan.",
    "footer.github": "GitHub",
  },
  no: {
    "toolbar.refresh": "⟳ Oppdater",
    "toolbar.fetch": "⤓ Hent",
    "toolbar.pull": "⇩ Trekk",
    "toolbar.push": "⇧ Trykk",
    "toolbar.commit": "✓ Forplikte seg",
    "toolbar.sync": "⇅ Synkroniser",
    "toolbar.jumpHead": "⌖ Gå til kassen",
    "toolbar.reset": "⤢ Tilbakestill visning",
    "toolbar.settings": "⚙ Innstillinger",
    "details.header": "Forpliktelsesdetaljer",
    "details.close": "Lukke",
    "details.sha": "SHA",
    "details.shortSha": "Kort SHA",
    "details.message": "Beskjed",
    "details.author": "Forfatter",
    "details.date": "Dato",
    "details.labels": "Etiketter",
    "details.location": "Sted",
    "details.currentHead": "HEAD — gjeldende utsjekking",
    "settings.title": "Innstillinger",
    "settings.language": "Språk",
    "settings.theme": "Tema",
    "settings.themeLight": "Lys",
    "settings.themeDark": "Mørk",
    "settings.themeLightHint": "Lys bakgrunn, mørk tekst.",
    "settings.themeDarkHint": "Mørk bakgrunn, lys tekst.",
    "settings.mainBranch": "Hovedgren",
    "settings.mainBranchAuto": "Automatisk",
    "settings.mainBranchSearch": "Søk i grener...",
    "settings.mainBranchNoMatch": "Ingen samsvarende grener",
    "settings.display": "Vis stil",
    "settings.displayModern": "Moderne",
    "settings.displayClassic": "Klassisk",
    "settings.displayModernHint": "Gratis lerret — dra for å panorere, rull for å zoome.",
    "settings.displayClassicHint": "Fast lerret - stammen festet til venstre, ingen zoom, bare rull (som SVN-revisjonsgrafen).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Kurve",
    "settings.sectionChanges": "Endrer visning",
    "settings.diffMinimap": "Diff minikart",
    "settings.diffMinimapOn": "Vist",
    "settings.diffMinimapOff": "Skjult",
    "settings.diffMinimapOnHint": "Vis en oversiktsstripe i VS-kode ved siden av diffen — dra den for å rulle; endre markører inkludert.",
    "settings.diffMinimapOffHint": "Ingen oversiktsstripe — rull forskjellen på vanlig måte.",
    "settings.commitReview": "Forplikte anmeldelse",
    "settings.commitReviewOn": "Spør før du forplikter deg",
    "settings.commitReviewOff": "Forplikte seg direkte",
    "settings.commitReviewOnHint": "Vis en siste filliste og meldingssjekk før du oppretter den lokale forpliktelsen.",
    "settings.commitReviewOffHint": "Commit-knappen oppretter den lokale commit umiddelbart.",
    "settings.svnBranchDialog": "SVN-lignende grendialog",
    "settings.svnBranchDialogHint": "Vis en mappe-trevelger når du oppretter en gren (i stedet for en enkel ledetekst).",
    "settings.branchDialog": "Grendialog",
    "settings.branchDialogSvnTitle": "SVN-stil",
    "settings.branchDialogNativeVscode": "VS-kode stil",
    "settings.branchDialogNativeVs": "Visual Studio-stil",
    "settings.branchDialogNativeHint": "Din IDEs innebygde grenmelding.",
    "settings.close": "Lukke",
    "settings.done": "Ferdig",
    "settings.sectionAdvanced": "Avansert",
    "settings.gitSource": "Git kjørbar",
    "settings.gitSourceBuiltin": "Innebygd",
    "settings.gitSourceBuiltinHint": "Bruk git-binæren fra IDEs innebygde Git-utvidelse.",
    "settings.gitSourceCustom": "Egendefinert bane",
    "settings.gitPath": "Veien til git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Bla gjennom...",
    "settings.jargon": "Git-vilkår",
    "settings.jargonTranslate": "Oversett",
    "settings.jargonEnglish": "Hold på engelsk",
    "settings.jargonTranslateHint": "Oversett Git-termer (pull, push, commit, branch ...) til grensesnittspråket.",
    "settings.jargonEnglishHint": "Hold Git-termer (pull, push, commit, branch...) på engelsk; oversett alt annet.",
    "settings.sectionMerge": "Fusjonere",
    "settings.mergeMode": "Slå sammen stil",
    "settings.mergeModeMerge": "Normal sammenslåing",
    "settings.mergeModeSquash": "Squash-sammenslåing",
    "settings.mergeModeMergeHint": "Slår sammen grenen slik den er: dens forpliktelser blir en del av den nåværende grenens historie, sammen med en sammenslåingsforpliktelse (eller spole fremover når det er mulig).",
    "settings.mergeModeSquashHint": "Skjuler hele grenen til en enkelt commit på gjeldende gren (git merge --squash). Filialens egne forpliktelser kommer aldri inn i gjeldende filials historie - du ser bare én forpliktelse der. Selve grenen blir stående urørt, så forpliktelsene forblir synlige i sin egen bane.",
    "settings.mergedView": "Sammenslåtte forpliktelser i grafen",
    "settings.mergedViewBranch": "Kun på egen gren",
    "settings.mergedViewTarget": "Også i grenen ble de slått sammen til",
    "settings.mergedViewBranchHint": "En sammenslått grens commits tegnes kun i deres egen bane; sammenslåingen vises som en kobling. Den klassiske utsikten.",
    "settings.mergedViewTargetHint": "Commitene en sammenfletting hentes inn, tegnes også i mottakergrenens bane, stablet under sammenslåingskommisjonen og på nivå med originalene - fordi den grenen virkelig inneholder dem nå. Kopiene er bleke og merket med grenen de er skrevet på, så det er tydelig at de ikke ble laget der. Squash-sammenslåinger er upåvirket: en squash skriver en ordinær commit, så bare den commit dukker opp.",
    "legend.title": "Legende",
    "legend.head": "HODE / nåværende gren",
    "legend.local": "Lokal avdeling",
    "legend.remote": "Ekstern gren",
    "legend.remoteOnly": "Bare i skyen (ikke trukket)",
    "legend.tag": "Tag (versjon)",
    "legend.commit": "Begå",
    "legend.stash": "Stash (hyllet arbeid)",
    "legend.mergedIn": "Kom inn med en sammenslåing (skrevet på en annen gren)",
    "legend.nodes": "Bokser",
    "legend.lines": "Linjer",
    "legend.edgeParent": "Forrige forpliktelse på samme linje",
    "legend.edgeMerge": "Merged-in-gren — pilen peker på sammenslåingen",
    "legend.edgeBranch": "En gren gaffelet av her",
    "legend.edgeStash": "Stash og forpliktelsen det ble skrinlagt på",
    "legend.edgeMergedChain": "Denne grenens linje som går gjennom de sammenslåtte forpliktelsene",
    "legend.edgeMergedTie": "Samme commit på grenen den ble skrevet på",
    "menu.jumpHead": "⌖ Gå til kassen",
    "menu.resetView": "⤢ Tilbakestill visning",
    "menu.createBranch": "Opprett filial herfra...",
    "menu.checkout": "Sjekk denne forpliktelsen",
    "menu.copySha": "Kopier commit SHA",
    "menu.pushBranch": "Skyv gren \"{name}\"",
    "menu.renameCommit": "Gi nytt navn til commit-melding...",
    "menu.undoCommit": "Angre forpliktelse (behold endringer)...",
    "menu.viewChanges": "Se endringer...",
    "menu.jumpToOriginal": "Gå til den opprinnelige forpliktelsen",
    "node.mergedInTooltip": "Merge {merge} brakte denne forpliktelsen inn i \"{target}\" - den ble skrevet på \"{origin}\".",
    "changes.title": "Endringer i {sha}",
    "changes.tabChanged": "Endret",
    "changes.tabAll": "Alle filer",
    "changes.added": "Lagt til",
    "changes.modified": "Modifisert",
    "changes.deleted": "Slettet",
    "changes.renamed": "Omdøpt",
    "changes.noChanges": "Denne commit endrer ingen filer.",
    "changes.loading": "Laster inn diff...",
    "changes.selectFile": "Velg en fil for å se endringene.",
    "changes.original": "Opprinnelig",
    "changes.changed": "Denne forpliktelsen",
    "changes.binary": "Binær fil — ingen tekstdiff å vise.",
    "changes.tooLarge": "Filen er for stor til å avvike.",
    "changes.renamedFrom": "Omdøpt fra {path}",
    "changes.close": "Lukke",
    "changes.prevChange": "Tidligere endring",
    "changes.nextChange": "Neste endring",
    "changes.collapseAll": "Skjul alle mapper",
    "changes.expandAll": "Utvid alle mapper",
    "changes.searchPlaceholder": "Søk etter filer …",
    "changes.noSearchResults": "Ingen filer samsvarer med søket ditt.",
    "changes.maximize": "Maksimer",
    "changes.restore": "Gjenopprett størrelse",
    "find.open": "Søk i denne forskjellen (Ctrl+F)",
    "find.placeholder": "Finn i diff...",
    "find.prev": "Forrige kamp",
    "find.next": "Neste kamp",
    "find.close": "Lukk søk",
    "find.noResults": "Ingen resultater",
    "commit.title": "Forplikte endringer",
    "commit.close": "Lukke",
    "commit.loading": "Leser arbeidstre …",
    "commit.noChanges": "Det er ingen lokale endringer å forplikte seg til.",
    "commit.selectFile": "Velg en fil for å se forskjellen.",
    "commit.selectAll": "Velg alle",
    "commit.selectNone": "Fjern alle",
    "commit.message": "Bekreft melding",
    "commit.messagePlaceholder": "Beskriv endringen",
    "commit.selectedCount": "{count}-fil(er) er valgt",
    "commit.review": "Gjennomgå",
    "commit.reviewTitle": "Siste sjekk før forpliktelse",
    "commit.reviewSummary": "Commit {count}-fil(er) med melding: {message}",
    "commit.back": "Tilbake",
    "commit.commit": "Opprett lokal forpliktelse",
    "menu.stashApply": "Påfør stash",
    "menu.stashPop": "Pop stash (påfør og fjern)",
    "menu.stashDrop": "Slipp oppbevaring...",
    "merge.title": "Slå sammen gren",
    "merge.route": "Slår sammen til gjeldende gren",
    "merge.source": "Fra (kilde)",
    "merge.target": "Inn i (nåværende)",
    "merge.loading": "Analyserer sammenslåing …",
    "merge.upToDate": "Allerede oppdatert — ingenting å slå sammen.",
    "merge.fastForward": "Spol fremover — ingen sammenslåingsforpliktelse nødvendig (kryss av for \"Opprett en sammenslåingsforpliktelse\" for å tvinge en sammenslåing).",
    "merge.summary": "{files}-fil(er) endret, {conflicts}-konflikt(er).",
    "merge.conflictsWarning": "Denne sammenslåingen har konflikter - du kan fortsatt slå sammen og deretter løse dem i redigeringsprogrammet.",
    "merge.noChanges": "Ingen filendringer.",
    "merge.files": "Resulterende endringer",
    "merge.added": "Lagt til",
    "merge.modified": "Modifisert",
    "merge.deleted": "Slettet",
    "merge.conflict": "Konflikt",
    "merge.message": "Slå sammen forpliktelsesmelding",
    "merge.messagePlaceholder": "Slå sammen gren \"kilde\"",
    "merge.messageFfHint": "Ignorert ved en fremspoling sammenslåing (ingen commit opprettes).",
    "merge.noFastForward": "Opprett en sammenslåingsforpliktelse (ingen spole fremover)",
    "merge.noFastForwardHint": "Registrer alltid en sammenslåing, selv når grenen kan spole fremover.",
    "merge.merge": "Fusjonere",
    "merge.cancel": "Kansellere",
    "merge.previewError": "Kunne ikke beregne en forhåndsvisning: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash-sammenslåing — hver endring lander som EN commit på \"{target}\"; \"{source}\" sine egne forpliktelser holder seg utenfor historien. Bytt tilbake i Innstillinger › Slå sammen.",
    "merge.messageSquash": "Bekreft melding",
    "merge.squashMessagePlaceholder": "Klemte endringer fra \"kilde\"",
    "newBranch.title": "Opprett gren",
    "newBranch.startPoint": "Ny gren fra {sha}",
    "newBranch.startPointOn": "på {refs}",
    "newBranch.location": "Sted",
    "newBranch.locationRoot": "(rot)",
    "newBranch.expandAll": "Utvid alle",
    "newBranch.collapseAll": "Skjul alle",
    "newBranch.expandFolder": "Utvid mappen",
    "newBranch.collapseFolder": "Skjul mappen",
    "newBranch.name": "Filialnavn",
    "newBranch.namePlaceholder": "min-gren",
    "newBranch.fullName": "Fullt navn: {name}",
    "newBranch.invalid": "Ugyldig filialnavn",
    "newBranch.exists": "En gren med dette navnet finnes allerede",
    "newBranch.checkout": "Kassefilial etter opprettelse",
    "newBranch.create": "Skape",
    "newBranch.cancel": "Kansellere",
    "status.loading": "Laster inn graf …",
    "status.summary": "{repo} Viser {commits} forplikter, {refs} refs",
    "status.branchCreated": "Opprettet filial \"{name}\" på {sha}",
    "status.error": "Feil: {message}",
    "status.opFailed": "Operasjonen mislyktes",
    "status.fetching": "Henter …",
    "status.pulling": "Trekker …",
    "status.pushing": "Skyver …",
    "status.committing": "Oppretter lokal forpliktelse …",
    "status.commitCreated": "Opprettet lokal forpliktelse {sha}: {message}",
    "status.syncing": "Synkroniserer …",
    "status.noHead": "Ingen gjeldende kasse funnet i grafen.",
    "status.undoing": "Angrer forpliktelse …",
    "status.commitUndone": "Commit undone — endringer er tilbake i arbeidstreet.",
    "status.undoConflict": "Angre treffkonflikter – løs dem i redigeringsprogrammet, og fortsett.",
    "status.stashApplying": "Bruker stash …",
    "status.stashPopping": "Pokker stash …",
    "status.stashDropping": "Slipper oppbevaring …",
    "status.stashApplied": "Stash brukt.",
    "status.stashPopped": "Stash spratt.",
    "status.stashDropped": "Stash falt.",
    "status.stashConflict": "Stash-konflikter – løs dem i redigeringsprogrammet.",
    "status.merging": "Slår sammen …",
    "status.merged": "Sammenslåing fullført.",
    "status.mergeConflict": "Slå sammen har konflikter - løs dem i redigeringsprogrammet, og forplikt dem.",
    "search.title": "Søk",
    "search.tooltip": "Søk forplikter",
    "search.placeholder": "Søk forplikter …",
    "search.previous": "Forrige resultat",
    "search.next": "Neste resultat",
    "search.noResults": "Ingen resultater",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtermodus",
    "search.highlight": "Uthevingsmodus",
    "search.filterMode": "Søkeskjerm",
    "search.filterModeFilter": "Filter (skjul ikke-treff)",
    "search.filterModeHighlight": "Fremhev (vis alle, merk treff)",
    "search.filterModeHint": "Velg hvordan søkeresultater skal vises i grafen.",
    "search.visibility": "Søk plassering",
    "search.visibilityToolbar": "Verktøylinjeknapp",
    "search.visibilityAlwaysVisible": "Alltid synlig bar",
    "search.visibilityHint": "Vis søk som et verktøylinjeikon eller vedvarende linje øverst.",
    "footer.github": "GitHub",
  },
  uz: {
    "toolbar.refresh": "⟳ Yangilash",
    "toolbar.fetch": "⤓ Olib olish",
    "toolbar.pull": "⇩ torting",
    "toolbar.push": "⇧ Bosish",
    "toolbar.commit": "✓ Majburiyat",
    "toolbar.sync": "⇅ Sinxronlash",
    "toolbar.jumpHead": "⌖ Kassaga oʻting",
    "toolbar.reset": "⤢ Ko'rinishni tiklash",
    "toolbar.settings": "⚙ Sozlamalar",
    "details.header": "Tafsilotlarni topshirish",
    "details.close": "Yopish",
    "details.sha": "SHA",
    "details.shortSha": "Qisqa SHA",
    "details.message": "Xabar",
    "details.author": "Muallif",
    "details.date": "Sana",
    "details.labels": "Yorliqlar",
    "details.location": "Joylashuv",
    "details.currentHead": "HEAD - joriy to'lov",
    "settings.title": "Sozlamalar",
    "settings.language": "Til",
    "settings.theme": "Mavzu",
    "settings.themeLight": "Nur",
    "settings.themeDark": "Qorong'i",
    "settings.themeLightHint": "Yengil fon, qora matn.",
    "settings.themeDarkHint": "Qorong'i fon, ochiq matn.",
    "settings.mainBranch": "Asosiy filial",
    "settings.mainBranchAuto": "Avtomatik",
    "settings.mainBranchSearch": "Filiallarni qidirish…",
    "settings.mainBranchNoMatch": "Mos keladigan filiallar yo'q",
    "settings.display": "Ko'rsatish uslubi",
    "settings.displayModern": "Zamonaviy",
    "settings.displayClassic": "Klassik",
    "settings.displayModernHint": "Bepul tuval — panorama uchun sudrang, kattalashtirish uchun aylantiring.",
    "settings.displayClassicHint": "Ruxsat etilgan kanvas - magistral chapga mahkamlangan, masshtabsiz, faqat aylantiring (SVN tahrir grafigi kabi).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Grafik",
    "settings.sectionChanges": "Ko'rinishni o'zgartiradi",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Ko'rsatilgan",
    "settings.diffMinimapOff": "Yashirin",
    "settings.diffMinimapOnHint": "Farq yonidagi VS Code uslubidagi umumiy koʻrinish chizigʻini koʻrsating — aylantirish uchun uni sudrab oʻtkazing; o'zgartirish belgilari kiritilgan.",
    "settings.diffMinimapOffHint": "Umumiy koʻrinish chizigʻi yoʻq — farqni odatdagidek aylantiring.",
    "settings.commitReview": "Ko'rib chiqishga ruxsat bering",
    "settings.commitReviewOn": "Qabul qilishdan oldin so'rang",
    "settings.commitReviewOff": "To'g'ridan-to'g'ri topshiring",
    "settings.commitReviewOnHint": "Mahalliy majburiyatni yaratishdan oldin yakuniy fayllar ro'yxatini va xabarni tekshirishni ko'rsating.",
    "settings.commitReviewOffHint": "\"Commit\" tugmasi darhol mahalliy majburiyatni yaratadi.",
    "settings.svnBranchDialog": "SVN uslubidagi filial dialogi",
    "settings.svnBranchDialogHint": "Filial yaratishda papka daraxtini tanlash vositasini ko'rsating (oddiy taklif o'rniga).",
    "settings.branchDialog": "Filial dialogi",
    "settings.branchDialogSvnTitle": "SVN uslubi",
    "settings.branchDialogNativeVscode": "VS kod uslubi",
    "settings.branchDialogNativeVs": "Visual Studio uslubi",
    "settings.branchDialogNativeHint": "Sizning IDE o'rnatilgan filial so'rovi.",
    "settings.close": "Yopish",
    "settings.done": "Bajarildi",
    "settings.sectionAdvanced": "Murakkab",
    "settings.gitSource": "Git bajariladigan",
    "settings.gitSourceBuiltin": "O'rnatilgan",
    "settings.gitSourceBuiltinHint": "IDE-ning o'rnatilgan Git kengaytmasidan git ikkilik faylidan foydalaning.",
    "settings.gitSourceCustom": "Maxsus yo'l",
    "settings.gitPath": "Gitga yo'l",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Ko‘rib chiqish…",
    "settings.jargon": "Git shartlari",
    "settings.jargonTranslate": "Tarjima",
    "settings.jargonEnglish": "Ingliz tilida saqlang",
    "settings.jargonTranslateHint": "Git atamalarini (tortish, surish, topshirish, filiallar...) interfeys tiliga tarjima qiling.",
    "settings.jargonEnglishHint": "Git shartlarini ingliz tilida saqlang (tortish, surish, bajarish, filial...); qolgan hamma narsani tarjima qiling.",
    "settings.sectionMerge": "Birlashtirish",
    "settings.mergeMode": "Birlashtirish uslubi",
    "settings.mergeModeMerge": "Oddiy birlashma",
    "settings.mergeModeSquash": "Squash birlashishi",
    "settings.mergeModeMergeHint": "Filialni qanday bo'lsa, shunday birlashtiradi: uning topshiriqlari joriy filial tarixining bir qismiga aylanadi, birlashtirish majburiyati bilan qo'shiladi (yoki iloji bo'lsa, tez yo'naltiriladi).",
    "settings.mergeModeSquashHint": "Butun filialni joriy filialda bitta majburiyatga aylantiradi (git merge --squash). Filialning o'z majburiyatlari hech qachon joriy filial tarixiga kirmaydi - u erda faqat bitta majburiyatni ko'rasiz. Filialning o'zi tegilmagan, shuning uchun uning majburiyatlari o'z chizig'ida ko'rinadigan bo'lib qoladi.",
    "settings.mergedView": "Grafikda birlashtirilgan majburiyatlar",
    "settings.mergedViewBranch": "Faqat o'z filiallarida",
    "settings.mergedViewTarget": "Shuningdek, filialda ular birlashtirildi",
    "settings.mergedViewBranchHint": "Birlashtirilgan filialning majburiyatlari faqat o'z chizig'ida tuziladi; birlashma ulagich sifatida namoyon bo'ladi. Klassik ko'rinish.",
    "settings.mergedViewTargetHint": "Olingan birlashma majburiyatlari qabul qiluvchi filialning qatorida ham chiziladi, birlashma majburiyati ostida yig'iladi va asl nusxalar bilan tenglashtiriladi - chunki bu filial haqiqatan ham ularni o'z ichiga oladi. Nusxalar oqarib ketgan va ular yozilgan novda bilan boshlangan, shuning uchun ular u erda qilinmaganligi aniq bo'lib qoladi. Squash birlashmalariga ta'sir qilmaydi: qovoq bitta oddiy topshiriqni yozadi, shuning uchun faqat o'sha majburiyat paydo bo'ladi.",
    "legend.title": "Afsona",
    "legend.head": "HEAD / joriy filial",
    "legend.local": "Mahalliy filial",
    "legend.remote": "Masofaviy filial",
    "legend.remoteOnly": "Faqat bulutda (tortib olinmagan)",
    "legend.tag": "Teg (versiya)",
    "legend.commit": "Qabul qiling",
    "legend.stash": "Stash (javon ish)",
    "legend.mergedIn": "Birlashma bilan keldi (boshqa filialda yozilgan)",
    "legend.nodes": "Qutilar",
    "legend.lines": "Chiziqlar",
    "legend.edgeParent": "Xuddi shu qatorda oldingi majburiyat",
    "legend.edgeMerge": "Birlashtirilgan filial - strelka birlashish majburiyatini ko'rsatadi",
    "legend.edgeBranch": "Shu yerdan bir shox uzilib qoldi",
    "legend.edgeStash": "Stash va u to'xtatilgan edi",
    "legend.edgeMergedChain": "Ushbu filialning liniyasi birlashtirilgan majburiyatlar orqali o'tadi",
    "legend.edgeMergedTie": "Filialda xuddi shunday majburiyat yozilgan edi",
    "menu.jumpHead": "⌖ Kassaga oʻting",
    "menu.resetView": "⤢ Ko'rinishni tiklash",
    "menu.createBranch": "Bu yerdan filial yarating…",
    "menu.checkout": "Ushbu majburiyatni tekshiring",
    "menu.copySha": "SHA-ni nusxalash",
    "menu.pushBranch": "\"{name}\" filialini bosing",
    "menu.renameCommit": "Qabul qilish xabarini qayta nomlash…",
    "menu.undoCommit": "Majburiyatni bekor qilish (o‘zgarishlarni saqlash)…",
    "menu.viewChanges": "Oʻzgarishlarni koʻrish…",
    "menu.jumpToOriginal": "Asl majburiyatga o'ting",
    "changes.title": "{sha}-dagi o'zgarishlar",
    "changes.tabChanged": "O'zgartirildi",
    "changes.tabAll": "Barcha fayllar",
    "changes.added": "Qo'shilgan",
    "changes.modified": "Oʻzgartirilgan",
    "changes.deleted": "Oʻchirildi",
    "changes.renamed": "Qayta nomlandi",
    "changes.noChanges": "Ushbu majburiyat hech qanday faylni o'zgartirmaydi.",
    "changes.loading": "Farq yuklanmoqda…",
    "changes.selectFile": "O'zgarishlarni ko'rish uchun faylni tanlang.",
    "changes.original": "Asl",
    "changes.changed": "Bu majburiyat",
    "changes.binary": "Ikkilik fayl - ko'rsatish uchun matn farqi yo'q.",
    "changes.tooLarge": "Fayl farq qilish uchun juda katta.",
    "changes.renamedFrom": "{path} nomidan qayta nomlandi",
    "changes.close": "Yopish",
    "changes.prevChange": "Oldingi o'zgarish",
    "changes.nextChange": "Keyingi o'zgarish",
    "changes.collapseAll": "Barcha jildlarni yig'ish",
    "changes.expandAll": "Barcha papkalarni kengaytiring",
    "changes.searchPlaceholder": "Fayllarni qidirish…",
    "changes.noSearchResults": "Qidiruvingizga hech qanday fayl mos kelmaydi.",
    "changes.maximize": "Maksimallashtirish",
    "changes.restore": "Hajmi tiklash",
    "find.open": "Bu farqda qidirish (Ctrl+F)",
    "find.placeholder": "Farqda toping…",
    "find.prev": "Oldingi o'yin",
    "find.next": "Keyingi o'yin",
    "find.close": "Qidiruvni yopish",
    "find.noResults": "Natija yoʻq",
    "commit.title": "O'zgarishlarni qabul qilish",
    "commit.close": "Yopish",
    "commit.loading": "Ishlaydigan daraxt oʻqilmoqda…",
    "commit.noChanges": "Amal qilish uchun mahalliy o'zgarishlar yo'q.",
    "commit.selectFile": "Uning farqini ko'rish uchun faylni tanlang.",
    "commit.selectAll": "Hammasini tanlang",
    "commit.selectNone": "Hammasini tozalang",
    "commit.message": "Xabarni qabul qilish",
    "commit.messagePlaceholder": "O'zgarishlarni tavsiflang",
    "commit.selectedCount": "{count} fayl(lar) tanlandi",
    "commit.review": "Ko‘rib chiqish",
    "commit.reviewTitle": "Qabul qilishdan oldin yakuniy tekshirish",
    "commit.reviewSummary": "{count} fayl(lar)ini quyidagi xabar bilan topshiring: {message}",
    "commit.back": "Orqaga",
    "commit.commit": "Mahalliy majburiyatni yarating",
    "menu.stashApply": "Stashni qo'llang",
    "menu.stashPop": "Pop stash (qo'llash va olib tashlash)",
    "menu.stashDrop": "Saqlash…",
    "merge.title": "Filialni birlashtirish",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Manbadan (manba)",
    "merge.target": "Kiritilgan (joriy)",
    "merge.loading": "Birlashtirish tahlil qilinmoqda…",
    "merge.upToDate": "Allaqachon yangilangan - birlashadigan hech narsa yo'q.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "{files} fayl(lar)i o'zgartirildi, {conflicts} ziddiyat(lar).",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Hech qanday fayl o'zgarmadi.",
    "merge.files": "Natijada yuzaga keladigan o'zgarishlar",
    "merge.added": "Qo'shilgan",
    "merge.modified": "Oʻzgartirilgan",
    "merge.deleted": "Oʻchirildi",
    "merge.conflict": "Mojaro",
    "merge.message": "Xabarni birlashtirish",
    "merge.messagePlaceholder": "\"manba\" filialini birlashtirish",
    "merge.messageFfHint": "Oldinga tez birlashtirishda e'tiborga olinmaydi (hech qanday majburiyat yaratilmaydi).",
    "merge.noFastForward": "Birlashtirish majburiyatini yarating (tezkor oldinga siljitish yo'q)",
    "merge.noFastForwardHint": "Filial oldinga siljishi mumkin bo'lsa ham, har doim birlashish majburiyatini yozib oling.",
    "merge.merge": "Birlashtirish",
    "merge.cancel": "Bekor qilish",
    "merge.previewError": "Ko‘rib chiqishni hisoblab bo‘lmadi: {message}",
    "merge.squashBadge": "Skoush",
    "merge.squashNote": "Squash birlashmasi - har bir o'zgarish ONE \"{target}\" da bajarilganidek tushadi; \"{source}\" ning o'z majburiyatlari uning tarixidan chetda qoladi. Sozlamalar › Birlashtirish orqali orqaga o‘ting.",
    "merge.messageSquash": "Xabarni qabul qilish",
    "merge.squashMessagePlaceholder": "\"Manba\" dan qisqartirilgan o'zgarishlar",
    "newBranch.title": "Filial yaratish",
    "newBranch.startPoint": "{sha} dan boshlab yangi filial",
    "newBranch.startPointOn": "{refs} da",
    "newBranch.location": "Joylashuv",
    "newBranch.locationRoot": "(ildiz)",
    "newBranch.expandAll": "Hammasini kengaytiring",
    "newBranch.collapseAll": "Hammasini yig'ish",
    "newBranch.expandFolder": "Jildni kengaytirish",
    "newBranch.collapseFolder": "Jildni yig'ish",
    "newBranch.name": "Filial nomi",
    "newBranch.namePlaceholder": "mening filialim",
    "newBranch.fullName": "To'liq nomi: {name}",
    "newBranch.invalid": "Filial nomi noto‘g‘ri",
    "newBranch.exists": "Bunday nomli filial allaqachon mavjud",
    "newBranch.checkout": "Yaratgandan so'ng filialni to'lash",
    "newBranch.create": "Yaratish",
    "newBranch.cancel": "Bekor qilish",
    "status.loading": "Grafik yuklanmoqda…",
    "status.summary": "{repo}{commits} majburiyatlari, {refs} referanslari ko'rsatilmoqda",
    "status.branchCreated": "{sha} da \"{name}\" filiali yaratilgan",
    "status.error": "Xato: {message}",
    "status.opFailed": "Operatsiya muvaffaqiyatsiz tugadi",
    "status.fetching": "Yuklanmoqda…",
    "status.pulling": "Tortish...",
    "status.pushing": "Bosish…",
    "status.committing": "Mahalliy majburiyat yaratilmoqda…",
    "status.commitCreated": "{sha} mahalliy majburiyati yaratildi: {message}",
    "status.syncing": "Sinxronlanmoqda…",
    "status.noHead": "Grafikda joriy hisob-kitob topilmadi.",
    "status.undoing": "Majburiyat bekor qilinmoqda…",
    "status.commitUndone": "Amalga oshirish bekor qilindi - o'zgarishlar ishchi daraxtga qaytadi.",
    "status.undoConflict": "Xit nizolarni bekor qiling - ularni muharrirda hal qiling, keyin davom eting.",
    "status.stashApplying": "Saqlash qoʻllanmoqda…",
    "status.stashPopping": "Saqlash ochilmoqda…",
    "status.stashDropping": "Saqlash tashlanmoqda…",
    "status.stashApplied": "Stash qo'llanildi.",
    "status.stashPopped": "Stash paydo bo'ldi.",
    "status.stashDropped": "Stash tushib ketdi.",
    "status.stashConflict": "Stash mojarolari - ularni muharrirda hal qiling.",
    "status.merging": "Birlashtirilmoqda…",
    "status.merged": "Birlashtirish tugallandi.",
    "status.mergeConflict": "Birlashtirishda nizolar bor - ularni muharrirda hal qiling, so'ngra bajaring.",
    "search.title": "Qidiruv",
    "search.tooltip": "Qidiruv majburiyatlari",
    "search.placeholder": "Qidiruv majburiyatlari…",
    "search.previous": "Oldingi natija",
    "search.next": "Keyingi natija",
    "search.noResults": "Natija yoʻq",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Filtr rejimi",
    "search.highlight": "Ajratish rejimi",
    "search.filterMode": "Displeyni qidirish",
    "search.filterModeFilter": "Filtr (mos kelmaydiganlarni yashirish)",
    "search.filterModeHighlight": "Ajratish (hammasini ko'rsatish, moslikni belgilash)",
    "search.filterModeHint": "Qidiruv natijalari grafikda qanday ko'rsatilishini tanlang.",
    "search.visibility": "Joyni qidirish",
    "search.visibilityToolbar": "Asboblar paneli tugmasi",
    "search.visibilityAlwaysVisible": "Har doim ko'rinadigan panel",
    "search.visibilityHint": "Qidiruvni asboblar paneli belgisi yoki tepada doimiy panel sifatida ko'rsatish.",
    "footer.github": "GitHub",
  },
  pl: {
    "toolbar.refresh": "⟳ Odśwież",
    "toolbar.fetch": "⤓ Przynieś",
    "toolbar.pull": "⇩ Pociągnij",
    "toolbar.push": "⇧ Naciśnij",
    "toolbar.commit": "✓ Zaangażuj się",
    "toolbar.sync": "⇅ Synchronizuj",
    "toolbar.jumpHead": "⌖ Przejdź do kasy",
    "toolbar.reset": "⤢ Zresetuj widok",
    "toolbar.settings": "⚙ Ustawienia",
    "details.header": "Szczegóły zatwierdzenia",
    "details.close": "Zamknąć",
    "details.sha": "SH",
    "details.shortSha": "Krótkie SHA",
    "details.message": "Wiadomość",
    "details.author": "Autor",
    "details.date": "Data",
    "details.labels": "Etykiety",
    "details.location": "Lokalizacja",
    "details.currentHead": "HEAD — bieżąca kasa",
    "settings.title": "Ustawienia",
    "settings.language": "Język",
    "settings.theme": "Temat",
    "settings.themeLight": "Światło",
    "settings.themeDark": "Ciemny",
    "settings.themeLightHint": "Jasne tło, ciemny tekst.",
    "settings.themeDarkHint": "Ciemne tło, jasny tekst.",
    "settings.mainBranch": "Główny oddział",
    "settings.mainBranchAuto": "Automatyczny",
    "settings.mainBranchSearch": "Wyszukaj oddziały…",
    "settings.mainBranchNoMatch": "Brak pasujących gałęzi",
    "settings.display": "Styl wyświetlania",
    "settings.displayModern": "Nowoczesny",
    "settings.displayClassic": "Klasyczny",
    "settings.displayModernHint": "Darmowe płótno — przeciągnij, aby przesuwać, przewiń, aby powiększyć.",
    "settings.displayClassicHint": "Naprawiono płótno — pień przypięty w lewo, bez powiększania, tylko przewijanie (jak wykres wersji SVN).",
    "settings.sectionGeneral": "Ogólny",
    "settings.sectionGraph": "Wykres",
    "settings.sectionChanges": "Zmienia widok",
    "settings.diffMinimap": "Różnicowa minimapa",
    "settings.diffMinimapOn": "Pokazano",
    "settings.diffMinimapOff": "Ukryty",
    "settings.diffMinimapOnHint": "Pokaż pasek przeglądu w stylu VS Code obok różnicy — przeciągnij go, aby przewinąć; znaczniki zmian w zestawie.",
    "settings.diffMinimapOffHint": "Brak paska przeglądu — normalnie przewijaj różnicę.",
    "settings.commitReview": "Zatwierdź recenzję",
    "settings.commitReviewOn": "Zapytaj przed zatwierdzeniem",
    "settings.commitReviewOff": "Zaangażuj się bezpośrednio",
    "settings.commitReviewOnHint": "Pokaż ostateczną listę plików i sprawdź komunikaty przed utworzeniem lokalnego zatwierdzenia.",
    "settings.commitReviewOffHint": "Przycisk Zatwierdź natychmiast tworzy lokalne zatwierdzenie.",
    "settings.svnBranchDialog": "Okno gałęzi w stylu SVN",
    "settings.svnBranchDialogHint": "Pokaż selektor drzewa folderów podczas tworzenia gałęzi (zamiast prostego monitu).",
    "settings.branchDialog": "Okno oddziału",
    "settings.branchDialogSvnTitle": "W stylu SVN",
    "settings.branchDialogNativeVscode": "Styl VS Code",
    "settings.branchDialogNativeVs": "Styl Visual Studio",
    "settings.branchDialogNativeHint": "Wbudowany monit o rozgałęzienie Twojego IDE.",
    "settings.close": "Zamknąć",
    "settings.done": "Zrobione",
    "settings.sectionAdvanced": "Zaawansowany",
    "settings.gitSource": "Plik wykonywalny Git",
    "settings.gitSourceBuiltin": "Wbudowany",
    "settings.gitSourceBuiltinHint": "Użyj pliku binarnego git z wbudowanego rozszerzenia Git w środowisku IDE.",
    "settings.gitSourceCustom": "Ścieżka niestandardowa",
    "settings.gitPath": "Ścieżka do gita",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Przeglądać…",
    "settings.jargon": "Warunki Gita",
    "settings.jargonTranslate": "Tłumaczyć",
    "settings.jargonEnglish": "Zachowaj po angielsku",
    "settings.jargonTranslateHint": "Przetłumacz terminy Git (ciągnięcie, wypychanie, zatwierdzanie, rozgałęzianie…) na język interfejsu.",
    "settings.jargonEnglishHint": "Zachowaj warunki Git (pull, push, commit, rozgałęzienie…) w języku angielskim; przetłumacz wszystko inne.",
    "settings.sectionMerge": "Łączyć",
    "settings.mergeMode": "Połącz styl",
    "settings.mergeModeMerge": "Normalne połączenie",
    "settings.mergeModeSquash": "Połączenie squasha",
    "settings.mergeModeMergeHint": "Łączy gałąź w takiej postaci, w jakiej jest: jej zatwierdzenia stają się częścią historii bieżącej gałęzi, do której dołącza się zatwierdzenie scalające (lub przewijane, jeśli to możliwe).",
    "settings.mergeModeSquashHint": "Zwija całą gałąź w jedno zatwierdzenie w bieżącej gałęzi (git merge --squash). Własne zatwierdzenia gałęzi nigdy nie trafiają do historii bieżącej gałęzi — widzisz tam tylko jedno zatwierdzenie. Sama gałąź pozostaje nietknięta, więc jej zatwierdzenia pozostają widoczne na jej własnym pasie.",
    "settings.mergedView": "Połączone zatwierdzenia na wykresie",
    "settings.mergedViewBranch": "Tylko na własnym oddziale",
    "settings.mergedViewTarget": "Również w gałęzi, do której zostały połączone",
    "settings.mergedViewBranchHint": "Zatwierdzenia połączonej gałęzi są rysowane tylko na ich własnym pasie; połączenie pojawi się jako łącznik. Klasyczny widok.",
    "settings.mergedViewTargetHint": "Zatwierdzenia wprowadzone przez scalanie są również rysowane na linii gałęzi odbierającej, ułożone pod zatwierdzeniem scalającym i na poziomie oryginałów — ponieważ ta gałąź naprawdę je teraz zawiera. Kopie są blade i mają zatytułowaną gałąź, na której zostały napisane, więc jest jasne, że nie tam zostały wykonane. Nie ma to wpływu na scalanie w squasha: squash zapisuje jedno zwykłe zatwierdzenie, więc pojawia się tylko to zatwierdzenie.",
    "legend.title": "Legenda",
    "legend.head": "HEAD / bieżąca gałąź",
    "legend.local": "Lokalny oddział",
    "legend.remote": "Oddział zdalny",
    "legend.remoteOnly": "Tylko w chmurze (nie pobierana)",
    "legend.tag": "Etykieta (wersja)",
    "legend.commit": "Popełniać",
    "legend.stash": "Skrytka (praca odłożona na półkę)",
    "legend.mergedIn": "Przyszedł z połączeniem (napisany na innej gałęzi)",
    "legend.nodes": "Pudełka",
    "legend.lines": "Kwestia",
    "legend.edgeParent": "Poprzednie zatwierdzenie w tej samej linii",
    "legend.edgeMerge": "Połączona gałąź — strzałka wskazuje zatwierdzenie scalania",
    "legend.edgeBranch": "Tutaj rozwidlała się gałąź",
    "legend.edgeStash": "Stash i zatwierdzenie, na którym został odłożony",
    "legend.edgeMergedChain": "Linia tej gałęzi przebiegająca przez scalone zatwierdzenia",
    "legend.edgeMergedTie": "To samo zatwierdzenie na gałęzi, na której zostało zapisane",
    "menu.jumpHead": "⌖ Przejdź do kasy",
    "menu.resetView": "⤢ Zresetuj widok",
    "menu.createBranch": "Utwórz oddział stąd…",
    "menu.checkout": "Sprawdź to zatwierdzenie",
    "menu.copySha": "Skopiuj zatwierdzenie SHA",
    "menu.pushBranch": "Odgałęzienie pchające „{name}”",
    "menu.renameBranch": "Zmień nazwę gałęzi „{name}”…",
    "menu.deleteBranch": "Usuń gałąź „{name}”…",
    "menu.renameCommit": "Zmień nazwę wiadomości zatwierdzenia…",
    "menu.undoCommit": "Cofnij zatwierdzenie (zachowaj zmiany)…",
    "menu.viewChanges": "Zobacz zmiany…",
    "menu.mergeBranch": "Połącz „{source}” z „{target}”…",
    "menu.jumpToOriginal": "Przejdź do oryginalnego zatwierdzenia",
    "node.mergedInTooltip": "Scal {merge} przeniósł to zatwierdzenie do „{target}” – zostało napisane na „{origin}”.",
    "changes.title": "Zmiany w {sha}",
    "changes.tabChanged": "Zmieniono",
    "changes.tabAll": "Wszystkie pliki",
    "changes.added": "W dodatku",
    "changes.modified": "Zmodyfikowany",
    "changes.deleted": "Usunięto",
    "changes.renamed": "Zmieniono nazwę",
    "changes.noChanges": "To zatwierdzenie nie zmienia żadnych plików.",
    "changes.loading": "Ładowanie różnicy…",
    "changes.selectFile": "Wybierz plik, aby zobaczyć zmiany.",
    "changes.original": "Oryginalny",
    "changes.changed": "To zatwierdzenie",
    "changes.binary": "Plik binarny — brak różnic tekstowych do pokazania.",
    "changes.tooLarge": "Plik jest zbyt duży, aby go porównać.",
    "changes.renamedFrom": "Zmieniono nazwę z {path}",
    "changes.close": "Zamknąć",
    "changes.prevChange": "Poprzednia zmiana",
    "changes.nextChange": "Następna zmiana",
    "changes.collapseAll": "Zwiń wszystkie foldery",
    "changes.expandAll": "Rozwiń wszystkie foldery",
    "changes.searchPlaceholder": "Wyszukaj pliki…",
    "changes.noSearchResults": "Żadne pliki nie pasują do Twojego wyszukiwania.",
    "changes.maximize": "Wyolbrzymiać",
    "changes.restore": "Przywróć rozmiar",
    "find.open": "Wyszukaj w tej różnicy (Ctrl+F)",
    "find.placeholder": "Znajdź w różnic…",
    "find.prev": "Poprzedni mecz",
    "find.next": "Następny mecz",
    "find.close": "Zamknij wyszukiwanie",
    "find.noResults": "Brak wyników",
    "commit.title": "Zatwierdź zmiany",
    "commit.close": "Zamknąć",
    "commit.loading": "Czytanie drzewa roboczego…",
    "commit.noChanges": "Nie ma żadnych lokalnych zmian do zatwierdzenia.",
    "commit.selectFile": "Wybierz plik, aby zobaczyć jego różnicę.",
    "commit.selectAll": "Zaznacz wszystko",
    "commit.selectNone": "Wyczyść wszystko",
    "commit.message": "Zatwierdź wiadomość",
    "commit.messagePlaceholder": "Opisz zmianę",
    "commit.selectedCount": "Wybrano plik(i) {count}",
    "commit.review": "Recenzja",
    "commit.reviewTitle": "Ostateczna kontrola przed zatwierdzeniem",
    "commit.reviewSummary": "Zatwierdź plik(i) {count} z komunikatem: {message}",
    "commit.back": "Z powrotem",
    "commit.commit": "Utwórz lokalne zatwierdzenie",
    "menu.stashApply": "Zastosuj skrytkę",
    "menu.stashPop": "Pop skrytka (zastosuj i usuń)",
    "menu.stashDrop": "Upuść skrytkę…",
    "merge.title": "Połącz oddział",
    "merge.route": "Połączenie z obecnym oddziałem",
    "merge.source": "Od (źródło)",
    "merge.target": "Do (prądu)",
    "merge.loading": "Analizuję scalanie…",
    "merge.upToDate": "Już aktualne — nie ma co łączyć.",
    "merge.fastForward": "Przewijanie do przodu — nie jest wymagane zatwierdzenie scalania (zaznacz opcję „Utwórz zatwierdzenie scalania”, aby je wymusić).",
    "merge.summary": "Zmieniono plik(i) {files}, konflikt(y) {conflicts}.",
    "merge.conflictsWarning": "W tym łączeniu występują konflikty — nadal możesz je scalić, a następnie rozwiązać je w edytorze.",
    "merge.noChanges": "Żadnych zmian w plikach.",
    "merge.files": "Wynikłe zmiany",
    "merge.added": "W dodatku",
    "merge.modified": "Zmodyfikowany",
    "merge.deleted": "Usunięto",
    "merge.conflict": "Konflikt",
    "merge.message": "Scal wiadomość zatwierdzenia",
    "merge.messagePlaceholder": "Połącz gałąź „źródło”",
    "merge.messageFfHint": "Ignorowane przy szybkim łączeniu do przodu (nie jest tworzone żadne zatwierdzenie).",
    "merge.noFastForward": "Utwórz zatwierdzenie scalania (bez przewijania do przodu)",
    "merge.noFastForwardHint": "Zawsze zapisuj zatwierdzenie scalania, nawet jeśli gałąź może przewijać do przodu.",
    "merge.merge": "Łączyć",
    "merge.cancel": "Anulować",
    "merge.previewError": "Nie można obliczyć podglądu: {message}",
    "merge.squashBadge": "Zdusić",
    "merge.squashNote": "Squash scalanie — każda zmiana kończy się jako JEDNOŚĆ zatwierdzenia w „{target}”; Własne zatwierdzenia „{source}” pozostają poza jego historią. Przełącz się ponownie w Ustawieniach › Scal.",
    "merge.messageSquash": "Zatwierdź wiadomość",
    "merge.squashMessagePlaceholder": "Zmiażdżone zmiany ze „źródła”",
    "newBranch.title": "Utwórz oddział",
    "newBranch.startPoint": "Nowa gałąź począwszy od {sha}",
    "newBranch.startPointOn": "na {refs}",
    "newBranch.location": "Lokalizacja",
    "newBranch.locationRoot": "(źródło)",
    "newBranch.expandAll": "Rozwiń wszystko",
    "newBranch.collapseAll": "Zwiń wszystko",
    "newBranch.expandFolder": "Rozwiń folder",
    "newBranch.collapseFolder": "Zwiń folder",
    "newBranch.name": "Nazwa oddziału",
    "newBranch.namePlaceholder": "mój oddział",
    "newBranch.fullName": "Pełna nazwa: {name}",
    "newBranch.invalid": "Nieprawidłowa nazwa oddziału",
    "newBranch.exists": "Oddział o tej nazwie już istnieje",
    "newBranch.checkout": "Oddział kasy po utworzeniu",
    "newBranch.create": "Tworzyć",
    "newBranch.cancel": "Anulować",
    "status.loading": "Ładowanie wykresu…",
    "status.summary": "{repo}Pokazuje zatwierdzenia {commits}, referencje {refs}",
    "status.branchCreated": "Utworzono gałąź „{name}” w {sha}",
    "status.error": "Błąd: {message}",
    "status.opFailed": "Operacja nie powiodła się",
    "status.fetching": "Ujmujący…",
    "status.pulling": "Ciągnięcie…",
    "status.pushing": "Popychanie…",
    "status.committing": "Tworzenie lokalnego zatwierdzenia…",
    "status.commitCreated": "Utworzono lokalne zatwierdzenie {sha}: {message}",
    "status.syncing": "Synchronizowanie…",
    "status.noHead": "Na wykresie nie znaleziono aktualnej transakcji.",
    "status.undoing": "Cofam zatwierdzenie…",
    "status.commitUndone": "Zatwierdzenie cofnięte — zmiany wracają do drzewa roboczego.",
    "status.undoConflict": "Cofnij konflikty trafień — rozwiąż je w edytorze, a następnie kontynuuj.",
    "status.stashApplying": "Stosowanie skrytki…",
    "status.stashPopping": "Wyskakująca skrytka…",
    "status.stashDropping": "Upuszczanie skrytki…",
    "status.stashApplied": "Zastosowano skrytkę.",
    "status.stashPopped": "Stach wyskoczył.",
    "status.stashDropped": "Stach spadł.",
    "status.stashConflict": "Konflikty w skrytce — rozwiązuj je w edytorze.",
    "status.merging": "Łączenie…",
    "status.merged": "Łączenie zakończone.",
    "status.mergeConflict": "W przypadku scalania występują konflikty — rozwiąż je w edytorze, a następnie zatwierdź.",
    "search.title": "Szukaj",
    "search.tooltip": "Wyszukaj zatwierdzenia",
    "search.placeholder": "Wyszukaj zatwierdzenia…",
    "search.previous": "Poprzedni wynik",
    "search.next": "Następny wynik",
    "search.noResults": "Brak wyników",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Tryb filtra",
    "search.highlight": "Tryb podświetlenia",
    "search.filterMode": "Wyświetlanie wyszukiwania",
    "search.filterModeFilter": "Filtruj (ukryj niedopasowania)",
    "search.filterModeHighlight": "Podświetl (pokaż wszystko, zaznacz dopasowania)",
    "search.filterModeHint": "Wybierz sposób wyświetlania wyników wyszukiwania na wykresie.",
    "search.visibility": "Wyszukaj lokalizację",
    "search.visibilityToolbar": "Przycisk paska narzędzi",
    "search.visibilityAlwaysVisible": "Zawsze widoczny pasek",
    "search.visibilityHint": "Pokaż wyszukiwanie jako ikonę na pasku narzędzi lub stały pasek u góry.",
    "footer.github": "GitHub",
  },
  pt: {
    "toolbar.refresh": "⟳ Atualizar",
    "toolbar.fetch": "⤓ Buscar",
    "toolbar.pull": "⇩ Puxar",
    "toolbar.push": "⇧ Empurrar",
    "toolbar.commit": "✓ Comprometer-se",
    "toolbar.sync": "⇅ Sincronizar",
    "toolbar.jumpHead": "⌖ Vá para finalizar a compra",
    "toolbar.reset": "⤢ Redefinir visualização",
    "toolbar.settings": "⚙ Configurações",
    "details.header": "Detalhes do compromisso",
    "details.close": "Fechar",
    "details.sha": "SHA",
    "details.shortSha": "SHA curto",
    "details.message": "Mensagem",
    "details.author": "Autor",
    "details.date": "Data",
    "details.labels": "Etiquetas",
    "details.location": "Localização",
    "details.currentHead": "HEAD – checkout atual",
    "settings.title": "Configurações",
    "settings.language": "Linguagem",
    "settings.theme": "Tema",
    "settings.themeLight": "Luz",
    "settings.themeDark": "Escuro",
    "settings.themeLightHint": "Fundo claro, texto escuro.",
    "settings.themeDarkHint": "Fundo escuro, texto claro.",
    "settings.mainBranch": "Filial principal",
    "settings.mainBranchAuto": "Automático",
    "settings.mainBranchSearch": "Pesquisar filiais…",
    "settings.mainBranchNoMatch": "Nenhuma ramificação correspondente",
    "settings.display": "Estilo de exibição",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Clássico",
    "settings.displayModernHint": "Tela livre — arraste para deslocar, role para ampliar.",
    "settings.displayClassicHint": "Tela fixa — tronco fixado à esquerda, sem zoom, apenas rolagem (como o gráfico de revisão do SVN).",
    "settings.sectionGeneral": "Em geral",
    "settings.sectionGraph": "Gráfico",
    "settings.sectionChanges": "Visualização de alterações",
    "settings.diffMinimap": "Minimapa diferencial",
    "settings.diffMinimapOn": "Mostrado",
    "settings.diffMinimapOff": "Escondido",
    "settings.diffMinimapOnHint": "Mostre uma faixa de visão geral no estilo VS Code ao lado da comparação - arraste-a para rolar; marcadores de mudança incluídos.",
    "settings.diffMinimapOffHint": "Sem faixa de visão geral – role a comparação normalmente.",
    "settings.commitReview": "Confirmar revisão",
    "settings.commitReviewOn": "Pergunte antes de confirmar",
    "settings.commitReviewOff": "Comprometa-se diretamente",
    "settings.commitReviewOnHint": "Mostre uma lista final de arquivos e verificação de mensagens antes de criar o commit local.",
    "settings.commitReviewOffHint": "O botão Commit cria o commit local imediatamente.",
    "settings.svnBranchDialog": "Diálogo de ramificação estilo SVN",
    "settings.svnBranchDialogHint": "Mostrar um seletor de árvore de pastas ao criar uma ramificação (em vez de um simples prompt).",
    "settings.branchDialog": "Diálogo de ramificação",
    "settings.branchDialogSvnTitle": "Estilo SVN",
    "settings.branchDialogNativeVscode": "Estilo de código VS",
    "settings.branchDialogNativeVs": "Estilo do Visual Studio",
    "settings.branchDialogNativeHint": "O prompt de ramificação integrado do seu IDE.",
    "settings.close": "Fechar",
    "settings.done": "Feito",
    "settings.sectionAdvanced": "Avançado",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Integrado",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Caminho personalizado",
    "settings.gitPath": "Caminho para git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Navegar…",
    "settings.jargon": "Termos do Git",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Traduza os termos do Git (pull, push, commit, branch…) para a linguagem da interface.",
    "settings.jargonEnglishHint": "Mantenha os termos do Git (pull, push, commit, branch…) em inglês; traduzir todo o resto.",
    "settings.sectionMerge": "Mesclar",
    "settings.mergeMode": "Mesclar estilo",
    "settings.mergeModeMerge": "Mesclagem normal",
    "settings.mergeModeSquash": "Mesclagem de abóbora",
    "settings.mergeModeMergeHint": "Mescla o branch como ele está: seus commits tornam-se parte do histórico do branch atual, unidos por um commit de mesclagem (ou avanço rápido quando possível).",
    "settings.mergeModeSquashHint": "Recolhe todo o branch em um único commit no branch atual (git merge --squash). Os commits do próprio branch nunca entram no histórico do branch atual — você vê apenas um commit lá. O branch em si permanece intacto, então seus commits permanecem visíveis em sua própria lane.",
    "settings.mergedView": "Confirmações mescladas no gráfico",
    "settings.mergedViewBranch": "Somente em sua própria filial",
    "settings.mergedViewTarget": "Também na filial eles foram incorporados",
    "settings.mergedViewBranchHint": "Os commits de um branch mesclado são desenhados apenas em sua própria pista; a mesclagem aparece como um conector. A visão clássica.",
    "settings.mergedViewTargetHint": "Os commits trazidos por uma mesclagem também são desenhados na linha da ramificação receptora, empilhados sob o commit da mesclagem e nivelados com os originais - porque essa ramificação realmente os contém agora. As cópias são pálidas e têm o título do galho em que foram escritas, então fica claro que não foram feitas lá. As mesclagens de squash não são afetadas: um squash grava um commit comum, então apenas esse commit aparece.",
    "legend.title": "Lenda",
    "legend.head": "HEAD/ramo atual",
    "legend.local": "Filial local",
    "legend.remote": "Filial remota",
    "legend.remoteOnly": "Somente na nuvem (não puxado)",
    "legend.tag": "Etiqueta (versão)",
    "legend.commit": "Comprometer-se",
    "legend.stash": "Stash (trabalho arquivado)",
    "legend.mergedIn": "Entrou com uma mesclagem (escrita em outro branch)",
    "legend.nodes": "Caixas",
    "legend.lines": "Linhas",
    "legend.edgeParent": "Confirmação anterior na mesma linha",
    "legend.edgeMerge": "Branch mesclado — a seta aponta para o commit de mesclagem",
    "legend.edgeBranch": "Um galho bifurcado aqui",
    "legend.edgeStash": "Stash e o commit em que foi arquivado",
    "legend.edgeMergedChain": "A linha desta ramificação passando pelos commits mesclados",
    "legend.edgeMergedTie": "O mesmo commit no branch em que foi escrito",
    "menu.jumpHead": "⌖ Vá para finalizar a compra",
    "menu.resetView": "⤢ Redefinir visualização",
    "menu.createBranch": "Crie uma ramificação a partir daqui…",
    "menu.checkout": "Confira este commit",
    "menu.copySha": "Copiar confirmação SHA",
    "menu.pushBranch": "Empurre o ramo \"{name}\"",
    "menu.renameBranch": "Renomear ramificação \"{name}\"...",
    "menu.deleteBranch": "Excluir ramificação \"{name}\"…",
    "menu.renameCommit": "Renomear mensagem de confirmação…",
    "menu.undoCommit": "Desfazer commit (manter alterações)…",
    "menu.viewChanges": "Ver alterações…",
    "menu.mergeBranch": "Mesclar \"{source}\" em \"{target}\"…",
    "menu.jumpToOriginal": "Vá para o commit original",
    "node.mergedInTooltip": "Merge {merge} trouxe este commit para \"{target}\" - foi escrito em \"{origin}\".",
    "changes.title": "Mudanças no {sha}",
    "changes.tabChanged": "Mudado",
    "changes.tabAll": "Todos os arquivos",
    "changes.added": "Adicionado",
    "changes.modified": "Modificado",
    "changes.deleted": "Excluído",
    "changes.renamed": "Renomeado",
    "changes.noChanges": "Este commit não altera nenhum arquivo.",
    "changes.loading": "Carregando diferença…",
    "changes.selectFile": "Selecione um arquivo para ver suas alterações.",
    "changes.original": "Original",
    "changes.changed": "Este commit",
    "changes.binary": "Arquivo binário — nenhuma diferença de texto para mostrar.",
    "changes.tooLarge": "O arquivo é muito grande para ser comparado.",
    "changes.renamedFrom": "Renomeado de {path}",
    "changes.close": "Fechar",
    "changes.prevChange": "Mudança anterior",
    "changes.nextChange": "Próxima mudança",
    "changes.collapseAll": "Recolher todas as pastas",
    "changes.expandAll": "Expanda todas as pastas",
    "changes.searchPlaceholder": "Pesquisar arquivos…",
    "changes.noSearchResults": "Nenhum arquivo corresponde à sua pesquisa.",
    "changes.maximize": "Maximizar",
    "changes.restore": "Restaurar tamanho",
    "find.open": "Pesquise nesta comparação (Ctrl+F)",
    "find.placeholder": "Encontre em dif…",
    "find.prev": "Partida anterior",
    "find.next": "Próxima partida",
    "find.close": "Fechar pesquisa",
    "find.noResults": "Nenhum resultado",
    "commit.title": "Confirmar alterações",
    "commit.close": "Fechar",
    "commit.loading": "Lendo a árvore de trabalho…",
    "commit.noChanges": "Não há alterações locais para confirmar.",
    "commit.selectFile": "Selecione um arquivo para ver sua diferença.",
    "commit.selectAll": "Selecionar tudo",
    "commit.selectNone": "Limpar tudo",
    "commit.message": "Confirmar mensagem",
    "commit.messagePlaceholder": "Descreva a mudança",
    "commit.selectedCount": "Arquivo(s) {count} selecionado(s)",
    "commit.review": "Análise",
    "commit.reviewTitle": "Verificação final antes do commit",
    "commit.reviewSummary": "Confirmar arquivo(s) {count} com mensagem: {message}",
    "commit.back": "Voltar",
    "commit.commit": "Criar commit local",
    "menu.stashApply": "Aplicar estoque",
    "menu.stashPop": "Stash pop (aplicar e remover)",
    "menu.stashDrop": "Solte o estoque…",
    "merge.title": "Mesclar Filial",
    "merge.route": "Mesclando no branch atual",
    "merge.source": "De (fonte)",
    "merge.target": "Em (atual)",
    "merge.loading": "Analisando mesclagem…",
    "merge.upToDate": "Já atualizado – nada para mesclar.",
    "merge.fastForward": "Avanço rápido - não é necessário commit de mesclagem (marque “Criar um commit de mesclagem” para forçar um).",
    "merge.summary": "Arquivo(s) {files} alterado(s), conflito(s) {conflicts}.",
    "merge.conflictsWarning": "Esta mesclagem tem conflitos — você ainda pode mesclar e resolvê-los no editor.",
    "merge.noChanges": "Nenhuma alteração no arquivo.",
    "merge.files": "Mudanças resultantes",
    "merge.added": "Adicionado",
    "merge.modified": "Modificado",
    "merge.deleted": "Excluído",
    "merge.conflict": "Conflito",
    "merge.message": "Mesclar mensagem de commit",
    "merge.messagePlaceholder": "Mesclar branch 'fonte'",
    "merge.messageFfHint": "Ignorado em uma mesclagem de avanço rápido (nenhuma confirmação é criada).",
    "merge.noFastForward": "Crie um commit de mesclagem (sem avanço rápido)",
    "merge.noFastForwardHint": "Sempre registre um commit de mesclagem, mesmo quando o branch puder avançar rapidamente.",
    "merge.merge": "Mesclar",
    "merge.cancel": "Cancelar",
    "merge.previewError": "Não foi possível calcular uma visualização: {message}",
    "merge.squashBadge": "Abóbora",
    "merge.squashNote": "Squash merge – cada mudança resulta em UM commit em “{target}”; Os commits do próprio “{source}” ficam fora de sua história. Volte em Configurações › Mesclar.",
    "merge.messageSquash": "Confirmar mensagem",
    "merge.squashMessagePlaceholder": "Alterações suprimidas da 'fonte'",
    "newBranch.title": "Criar filial",
    "newBranch.startPoint": "Nova filial a partir de {sha}",
    "newBranch.startPointOn": "em {refs}",
    "newBranch.location": "Localização",
    "newBranch.locationRoot": "(raiz)",
    "newBranch.expandAll": "Expandir tudo",
    "newBranch.collapseAll": "Recolher tudo",
    "newBranch.expandFolder": "Expandir pasta",
    "newBranch.collapseFolder": "Recolher pasta",
    "newBranch.name": "Nome da filial",
    "newBranch.namePlaceholder": "minha filial",
    "newBranch.fullName": "Nome completo: {name}",
    "newBranch.invalid": "Nome de filial inválido",
    "newBranch.exists": "Já existe uma filial com este nome",
    "newBranch.checkout": "Ramo de checkout após a criação",
    "newBranch.create": "Criar",
    "newBranch.cancel": "Cancelar",
    "status.loading": "Carregando gráfico…",
    "status.summary": "{repo}Mostrando commits {commits}, referências {refs}",
    "status.branchCreated": "Filial \"{name}\" criada em {sha}",
    "status.error": "Erro: {message}",
    "status.opFailed": "Falha na operação",
    "status.fetching": "Buscando…",
    "status.pulling": "Puxando…",
    "status.pushing": "Empurrando…",
    "status.committing": "Criando commit local…",
    "status.commitCreated": "Confirmação local {sha} criada: {message}",
    "status.syncing": "Sincronizando…",
    "status.noHead": "Nenhum check-out atual encontrado no gráfico.",
    "status.undoing": "Desfazendo confirmação…",
    "status.commitUndone": "Confirmação desfeita — as alterações estão de volta à árvore de trabalho.",
    "status.undoConflict": "Desfaça conflitos de acertos — resolva-os no editor e continue.",
    "status.stashApplying": "Aplicando estoque…",
    "status.stashPopping": "Estoque estourando…",
    "status.stashDropping": "Deixando cair o estoque…",
    "status.stashApplied": "Estoque aplicado.",
    "status.stashPopped": "Stash estourou.",
    "status.stashDropped": "Stash caiu.",
    "status.stashConflict": "Conflitos de stash – resolva-os no editor.",
    "status.merging": "Mesclando…",
    "status.merged": "Mesclagem concluída.",
    "status.mergeConflict": "A mesclagem tem conflitos – resolva-os no editor e depois confirme.",
    "search.title": "Procurar",
    "search.tooltip": "Confirmações de pesquisa",
    "search.placeholder": "Confirmações de pesquisa…",
    "search.previous": "Resultado anterior",
    "search.next": "Próximo resultado",
    "search.noResults": "Nenhum resultado",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modo de filtro",
    "search.highlight": "Modo de destaque",
    "search.filterMode": "Exibição de pesquisa",
    "search.filterModeFilter": "Filtrar (ocultar não correspondências)",
    "search.filterModeHighlight": "Destacar (mostrar tudo, marcar correspondências)",
    "search.filterModeHint": "Escolha como os resultados da pesquisa serão exibidos no gráfico.",
    "search.visibility": "Local de pesquisa",
    "search.visibilityToolbar": "Botão da barra de ferramentas",
    "search.visibilityAlwaysVisible": "Barra sempre visível",
    "search.visibilityHint": "Mostre a pesquisa como um ícone da barra de ferramentas ou uma barra persistente na parte superior.",
    "footer.github": "GitHub",
  },
  ro: {
    "toolbar.refresh": "⟳ Actualizează",
    "toolbar.fetch": "⤓ Preluați",
    "toolbar.pull": "⇩ Trageți",
    "toolbar.push": "⇧ Apăsați",
    "toolbar.commit": "✓ Angajați-vă",
    "toolbar.sync": "⇅ Sincronizare",
    "toolbar.jumpHead": "⌖ Mergeți la casă",
    "toolbar.reset": "⤢ Resetați vizualizarea",
    "toolbar.settings": "⚙ Setări",
    "details.header": "Detalii de angajare",
    "details.close": "Aproape",
    "details.sha": "SHA",
    "details.shortSha": "SHA scurt",
    "details.message": "Mesaj",
    "details.author": "Autor",
    "details.date": "Data",
    "details.labels": "Etichete",
    "details.location": "Locaţie",
    "details.currentHead": "HEAD — finalizarea curentului",
    "settings.title": "Setări",
    "settings.language": "Limbă",
    "settings.theme": "Temă",
    "settings.themeLight": "Aprinde",
    "settings.themeDark": "Întuneric",
    "settings.themeLightHint": "Fundal deschis, text întunecat.",
    "settings.themeDarkHint": "Fundal întunecat, text deschis.",
    "settings.mainBranch": "Ramura principală",
    "settings.mainBranchAuto": "Automat",
    "settings.mainBranchSearch": "Caută ramuri...",
    "settings.mainBranchNoMatch": "Nu există ramuri care se potrivesc",
    "settings.display": "Stilul de afișare",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Clasic",
    "settings.displayModernHint": "Pânză gratuită — trageți pentru a deplasa, derulați pentru a mări.",
    "settings.displayClassicHint": "Pânză fixă ​​— trunchi fixat la stânga, fără zoom, doar derulare (cum ar fi graficul de revizuire SVN).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Grafic",
    "settings.sectionChanges": "Schimbă vizualizarea",
    "settings.diffMinimap": "Minimapa diferențelor",
    "settings.diffMinimapOn": "Afisat",
    "settings.diffMinimapOff": "Ascuns",
    "settings.diffMinimapOnHint": "Afișați o bandă de prezentare generală în stil VS Code lângă diferență - trageți-o pentru a derula; markere de schimbare incluse.",
    "settings.diffMinimapOffHint": "Fără bandă de prezentare generală - derulați diferența în mod normal.",
    "settings.commitReview": "Examinare comite",
    "settings.commitReviewOn": "Întreabă înainte de a te angaja",
    "settings.commitReviewOff": "Angajează-te direct",
    "settings.commitReviewOnHint": "Afișați o listă finală de fișiere și verificarea mesajelor înainte de a crea comiterea locală.",
    "settings.commitReviewOffHint": "Butonul Commit creează imediat commit-ul local.",
    "settings.svnBranchDialog": "Dialog de ramură în stil SVN",
    "settings.svnBranchDialogHint": "Afișați un selector de arbore de foldere atunci când creați o ramură (în loc de un simplu prompt).",
    "settings.branchDialog": "Dialog de ramură",
    "settings.branchDialogSvnTitle": "Stil SVN",
    "settings.branchDialogNativeVscode": "Stilul VS Code",
    "settings.branchDialogNativeVs": "Stilul Visual Studio",
    "settings.branchDialogNativeHint": "Promptul de ramură încorporat al IDE-ului dvs.",
    "settings.close": "Aproape",
    "settings.done": "Făcut",
    "settings.sectionAdvanced": "Avansat",
    "settings.gitSource": "Git executabil",
    "settings.gitSourceBuiltin": "Încorporat",
    "settings.gitSourceBuiltinHint": "Utilizați binarul git din extensia Git încorporată a IDE-ului.",
    "settings.gitSourceCustom": "Cale personalizată",
    "settings.gitPath": "Calea către git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Răsfoiți...",
    "settings.jargon": "Termenii Git",
    "settings.jargonTranslate": "Traduce",
    "settings.jargonEnglish": "Păstrați în engleză",
    "settings.jargonTranslateHint": "Traduceți termenii Git (pull, push, commit, branch...) în limba interfeței.",
    "settings.jargonEnglishHint": "Păstrați termenii Git (pull, push, commit, branch...) în engleză; traduce orice altceva.",
    "settings.sectionMerge": "Merge",
    "settings.mergeMode": "Îmbinați stilul",
    "settings.mergeModeMerge": "Fuziune normală",
    "settings.mergeModeSquash": "Fuziune de dovlecei",
    "settings.mergeModeMergeHint": "Îmbină ramura așa cum este: comite-urile sale devin parte din istoria ramurii curente, alăturate de un commit de îmbinare (sau redirecționate rapid atunci când este posibil).",
    "settings.mergeModeSquashHint": "Restrânge întreaga ramură într-un singur commit pe ramura curentă (git merge --squash). Comiterile proprii ale sucursalei nu intră niciodată în istoricul sucursalei curente - acolo vezi doar un comit. Ramura în sine este lăsată neatinsă, astfel încât comite-urile sale rămân vizibile pe propria bandă.",
    "settings.mergedView": "Comite îmbinate în grafic",
    "settings.mergedViewBranch": "Doar pe propria ramură",
    "settings.mergedViewTarget": "Tot în ramură au fost comasate",
    "settings.mergedViewBranchHint": "Comiterile unei filiale fuzionate sunt trase numai pe propria bandă; îmbinarea apare ca un conector. Vederea clasică.",
    "settings.mergedViewTargetHint": "Comiterile aduse de o îmbinare sunt desenate și pe banda ramurilor primitoare, stivuite sub comiterea de îmbinare și la nivel cu originalele - pentru că acea ramificație chiar le conține acum. Copiile sunt palide și au intitulat ramura pe care au fost scrise, așa că rămâne clar că nu au fost făcute acolo. Fusiunile squash nu sunt afectate: un squash scrie un comit obișnuit, astfel încât doar acel comit apare vreodată.",
    "legend.title": "Legendă",
    "legend.head": "CAP / ramura curentă",
    "legend.local": "Filiala locala",
    "legend.remote": "Ramura la distanta",
    "legend.remoteOnly": "Numai în nor (nu tras)",
    "legend.tag": "Etichetă (versiune)",
    "legend.commit": "Angajează-te",
    "legend.stash": "Stash (lucrare pe rafturi)",
    "legend.mergedIn": "A venit cu o îmbinare (scrisă pe altă ramură)",
    "legend.nodes": "Cutii",
    "legend.lines": "Linii",
    "legend.edgeParent": "Comitarea anterioară pe aceeași linie",
    "legend.edgeMerge": "Ramura îmbinată — săgeata indică spre comiterea de îmbinare",
    "legend.edgeBranch": "O creangă s-a bifurcat aici",
    "legend.edgeStash": "Stash și commit-ul pentru care a fost păstrat",
    "legend.edgeMergedChain": "Linia acestei ramuri care trece prin comiterile îmbinate",
    "legend.edgeMergedTie": "Același comit pe ramura pe care a fost scris",
    "menu.jumpHead": "⌖ Mergeți la casă",
    "menu.resetView": "⤢ Resetați vizualizarea",
    "menu.createBranch": "Creați o ramură de aici...",
    "menu.checkout": "Verificați acest commit",
    "menu.copySha": "Copiați SHA",
    "menu.pushBranch": "Împingeți ramura „{name}”",
    "menu.renameCommit": "Redenumiți mesajul de comitere...",
    "menu.undoCommit": "Anulați comiterea (păstrați modificările)...",
    "menu.viewChanges": "Vedeți modificările...",
    "menu.mergeBranch": "Îmbinați „{source}” în „{target}”…¦",
    "menu.jumpToOriginal": "Mergeți la comiterea inițială",
    "changes.title": "Modificări în {sha}",
    "changes.tabChanged": "Schimbat",
    "changes.tabAll": "Toate Fișierele",
    "changes.added": "Adăugat",
    "changes.modified": "Modificat",
    "changes.deleted": "Șters",
    "changes.renamed": "Redenumită",
    "changes.noChanges": "Această confirmare nu modifică niciun fișier.",
    "changes.loading": "Se încarcă diferența...",
    "changes.selectFile": "Selectați un fișier pentru a vedea modificările acestuia.",
    "changes.original": "Original",
    "changes.changed": "Acest angajament",
    "changes.binary": "Fișier binar - nu există diferență de text de afișat.",
    "changes.tooLarge": "Fișierul este prea mare pentru a fi diferit.",
    "changes.renamedFrom": "Redenumită din {path}",
    "changes.close": "Aproape",
    "changes.prevChange": "Modificare anterioară",
    "changes.nextChange": "Următoarea schimbare",
    "changes.collapseAll": "Restrângeți toate folderele",
    "changes.expandAll": "Extindeți toate folderele",
    "changes.searchPlaceholder": "Căutați fișiere...",
    "changes.noSearchResults": "Niciun fișier nu corespunde căutării dvs.",
    "changes.maximize": "Maximizați",
    "changes.restore": "Restabiliți dimensiunea",
    "find.open": "Căutați în această diferență (Ctrl+F)",
    "find.placeholder": "Găsiți în diferență...",
    "find.prev": "Meciul anterior",
    "find.next": "Următorul meci",
    "find.close": "Închide căutarea",
    "find.noResults": "Niciun rezultat",
    "commit.title": "Commiteți modificări",
    "commit.close": "Aproape",
    "commit.loading": "Se citesc arborele de lucru...",
    "commit.noChanges": "Nu există modificări locale de efectuat.",
    "commit.selectFile": "Selectați un fișier pentru a vedea diferența acestuia.",
    "commit.selectAll": "Selectați toate",
    "commit.selectNone": "Ștergeți totul",
    "commit.message": "Mesaj de angajare",
    "commit.messagePlaceholder": "Descrieți schimbarea",
    "commit.selectedCount": "Fișier(e) {count} selectat(e).",
    "commit.review": "Recenzie",
    "commit.reviewTitle": "Verificare finală înainte de comitere",
    "commit.reviewSummary": "Commiteți fișierele {count} cu mesajul: {message}",
    "commit.back": "Spate",
    "commit.commit": "Creați un commit local",
    "menu.stashApply": "Aplicați stash",
    "menu.stashPop": "Pop stash (aplicați și îndepărtați)",
    "menu.stashDrop": "Aruncă depozitul...",
    "merge.title": "Merge Branch",
    "merge.route": "Fuzionarea în ramura curentă",
    "merge.source": "De la (sursa)",
    "merge.target": "În (actual)",
    "merge.loading": "Se analizează îmbinarea...",
    "merge.upToDate": "Deja la zi — nimic de îmbinat.",
    "merge.fastForward": "Derulare rapidă înainte — nu este nevoie de comitere de îmbinare (bifați „Creați un comit de îmbinare” pentru a forța unul).",
    "merge.summary": "Fișier(ele) {files} schimbat(e), conflict(e) {conflicts}.",
    "merge.conflictsWarning": "Această îmbinare are conflicte - puteți încă îmbina, apoi le puteți rezolva în editor.",
    "merge.noChanges": "Nicio modificare a fișierului.",
    "merge.files": "Schimbările rezultate",
    "merge.added": "Adăugat",
    "merge.modified": "Modificat",
    "merge.deleted": "Șters",
    "merge.conflict": "Conflict",
    "merge.message": "Merge mesaj de comitere",
    "merge.messagePlaceholder": "Îmbinați ramura „sursă”",
    "merge.messageFfHint": "Ignorat la o îmbinare rapidă înainte (nu este creată nicio comitere).",
    "merge.noFastForward": "Creați un commit de îmbinare (fără derulare rapidă)",
    "merge.noFastForwardHint": "Înregistrați întotdeauna un commit de îmbinare, chiar și atunci când ramura ar putea avansa rapid.",
    "merge.merge": "Merge",
    "merge.cancel": "Anula",
    "merge.previewError": "Nu s-a putut calcula o previzualizare: {message}",
    "merge.squashBadge": "Suc de fructe",
    "merge.squashNote": "Fuziune squash — fiecare schimbare ajunge ca ONE comite pe „{target}”; Propriile angajamente ale lui „{source}” rămân în afara istoriei sale. Comutați înapoi în Setări › Îmbinați.",
    "merge.messageSquash": "Mesaj de angajare",
    "merge.squashMessagePlaceholder": "Modificări eliminate de la „sursă”",
    "newBranch.title": "Creați sucursală",
    "newBranch.startPoint": "Sucursală nouă începând de la {sha}",
    "newBranch.startPointOn": "pe {refs}",
    "newBranch.location": "Locaţie",
    "newBranch.locationRoot": "(rădăcină)",
    "newBranch.expandAll": "Extindeți-le pe toate",
    "newBranch.collapseAll": "Restrângeți totul",
    "newBranch.expandFolder": "Extinde folderul",
    "newBranch.collapseFolder": "Restrângeți dosarul",
    "newBranch.name": "Numele sucursalei",
    "newBranch.namePlaceholder": "ramura mea",
    "newBranch.fullName": "Nume complet: {name}",
    "newBranch.invalid": "Nume de ramură nevalid",
    "newBranch.exists": "O sucursală cu acest nume există deja",
    "newBranch.checkout": "Sucursala de checkout după creare",
    "newBranch.create": "Crea",
    "newBranch.cancel": "Anula",
    "status.loading": "Se încarcă graficul...",
    "status.summary": "{repo}Se afișează comiterile {commits}, ref. {refs}",
    "status.branchCreated": "A creat ramura „{name}” la {sha}",
    "status.error": "Eroare: {message}",
    "status.opFailed": "Operațiunea a eșuat",
    "status.fetching": "Se preia…",
    "status.pulling": "Trag...",
    "status.pushing": "Împingând…",
    "status.committing": "Se creează un commit local...",
    "status.commitCreated": "S-a creat comiterea locală {sha}: {message}",
    "status.syncing": "Se sincronizează…",
    "status.noHead": "Nu s-a găsit nicio plată curentă în grafic.",
    "status.undoing": "Se anulează comiterea...",
    "status.commitUndone": "Commit undone — modificările sunt din nou în arborele de lucru.",
    "status.undoConflict": "Anulați conflictele de accesare - rezolvați-le în editor, apoi continuați.",
    "status.stashApplying": "Se aplică depozitul...",
    "status.stashPopping": "Scoaterea ascunzii...",
    "status.stashDropping": "Aruncarea depozitului...",
    "status.stashApplied": "Stash aplicat.",
    "status.stashPopped": "A apărut Stash.",
    "status.stashDropped": "Stash a căzut.",
    "status.stashConflict": "Ascundeți conflictele - rezolvați-le în editor.",
    "status.merging": "Fuzionarea…",
    "status.merged": "Fuziunea finalizată.",
    "status.mergeConflict": "Merge are conflicte - rezolvă-le în editor, apoi comite.",
    "search.title": "Căutare",
    "search.tooltip": "Căutarea se comite",
    "search.placeholder": "Căutarea se comite...",
    "search.previous": "Rezultatul anterior",
    "search.next": "Următorul rezultat",
    "search.noResults": "Niciun rezultat",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modul de filtrare",
    "search.highlight": "Modul de evidențiere",
    "search.filterMode": "Căutare afișaj",
    "search.filterModeFilter": "Filtrați (ascundeți nepotrivirile)",
    "search.filterModeHighlight": "Evidențiați (afișați toate, marcați potrivirile)",
    "search.filterModeHint": "Alegeți cum sunt afișate rezultatele căutării în grafic.",
    "search.visibility": "Căutați locația",
    "search.visibilityToolbar": "Butonul din bara de instrumente",
    "search.visibilityAlwaysVisible": "Bara mereu vizibilă",
    "search.visibilityHint": "Afișați căutarea ca pictogramă din bara de instrumente sau bară persistentă în partea de sus.",
    "footer.github": "GitHub",
  },
  qu: {
    "toolbar.refresh": "⟳ Musuqyachiy",
    "toolbar.fetch": "⤓ Apamuy",
    "toolbar.pull": "⇩ Aysay",
    "toolbar.push": "⇧ Tanqay",
    "toolbar.commit": "✓ Compromiso ruway",
    "toolbar.sync": "⇅ Sincronización",
    "toolbar.jumpHead": "⌖ Chequeo nisqaman riy",
    "toolbar.reset": "⤢ Qhawayta wakmanta churay",
    "toolbar.settings": "⚙ Ajustes nisqa",
    "details.header": "Detalles de Compromiso nisqa",
    "details.close": "Wichqay",
    "details.sha": "SHA",
    "details.shortSha": "Pisi rimayllapi SHA",
    "details.message": "Willakuy",
    "details.author": "Ruwaq",
    "details.date": "Imay pacha",
    "details.labels": "Etiquetas nisqakuna",
    "details.location": "Tarikusqan",
    "details.currentHead": "UMA — kunan pacha qullqi quy",
    "settings.title": "Ajustes nisqa",
    "settings.language": "Simi",
    "settings.theme": "Rimay",
    "settings.themeLight": "Kanchi",
    "settings.themeDark": "Tutayasqa",
    "settings.themeLightHint": "K’anchaq qhipa, yana qillqa.",
    "settings.themeDarkHint": "Tutayaq qhipa, k’anchay qillqa.",
    "settings.mainBranch": "Sucursal principal",
    "settings.mainBranchAuto": "Kikinmanta",
    "settings.mainBranchSearch": "Sucursalkunata maskay...",
    "settings.mainBranchNoMatch": "Mana tupaq k’allmakuna",
    "settings.display": "Qhawachiy estilo",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Clásico nisqa",
    "settings.displayModernHint": "Libre lona — aysay panman, kuyuy hatunyachinapaq.",
    "settings.displayClassicHint": "Allinchasqa lona — tronco lluq'iman k'askasqa, mana hatunyachiy, kuyuylla (SVN musuqchay siq'i hina).",
    "settings.sectionGeneral": "Llapan",
    "settings.sectionGraph": "Gráfico nisqa",
    "settings.sectionChanges": "Qhawayta tikran",
    "settings.diffMinimap": "Diff minimap nisqa",
    "settings.diffMinimapOn": "Qawachisqa",
    "settings.diffMinimapOff": "Pakasqa",
    "settings.diffMinimapOnHint": "Huk VS Código-hina hatun qhaway tira diff kaqpa ladunpi rikuchiy — aysay kuyurinaykipaq; cambio marcadores nisqakuna churasqa.",
    "settings.diffMinimapOffHint": "Mana hatun qhaway tira — diff normalta kuyuchiy.",
    "settings.commitReview": "Repaso ruway",
    "settings.commitReviewOn": "Manaraq commit kachkaptin tapukuy",
    "settings.commitReviewOff": "Chiqamanta compromiso ruway",
    "settings.commitReviewOnHint": "Huk qhipa willañiqi listata chaymanta willayta qhawayta rikuchiy manaraq kitipi kamachiyta ruwachkaspa.",
    "settings.commitReviewOffHint": "Commit ñit'inaqa chaylla llaqtapi commit nisqatam ruwan.",
    "settings.svnBranchDialog": "SVN-hina rama rimanakuy",
    "settings.svnBranchDialogHint": "Huk k'allmata paqarichispa huk qillqana mayt'u-sach'a akllanata rikuchiy (huk sasan tapuypa rantinpi).",
    "settings.branchDialog": "Sucursalmanta rimanakuy",
    "settings.branchDialogSvnTitle": "SVN-hina",
    "settings.branchDialogNativeVscode": "VS Código nisqa estilo",
    "settings.branchDialogNativeVs": "Visual Studio nisqa estilo",
    "settings.branchDialogNativeHint": "IDE kaqpa ruwasqa rama tapuynin.",
    "settings.close": "Wichqay",
    "settings.done": "Ñam",
    "settings.sectionAdvanced": "Ñawparisqa",
    "settings.gitSource": "Git ruwanapaq",
    "settings.gitSourceBuiltin": "Instruido-in",
    "settings.gitSourceBuiltinHint": "IDE kaqpa Git mast'ariyninmanta git iskayniyuqta llamk'achiy.",
    "settings.gitSourceCustom": "Sapanchasqa ñan",
    "settings.gitPath": "Ñan git nisqaman",
    "settings.gitPathPlaceholder": "/usr/bin/git nisqa",
    "settings.gitPathBrowse": "Navegación...",
    "settings.jargon": "Git nisqa rimaykuna",
    "settings.jargonTranslate": "Tikray",
    "settings.jargonEnglish": "Inglés simipi waqaychay",
    "settings.jargonTranslateHint": "Git simikunata (aysay, tanqay, quy, rama...) interfaz simiman tikray.",
    "settings.jargonEnglishHint": "Git simikunata (aysay, tanqay, quy, rama...) inglés simipi waqaychay; tukuy imata t’ikray.",
    "settings.sectionMerge": "Huñunakuy",
    "settings.mergeMode": "Huñuy estilo",
    "settings.mergeModeMerge": "Normal nisqa huñunakuy",
    "settings.mergeModeSquash": "Squash huñunakuy",
    "settings.mergeModeMergeHint": "Sucursalta imayna kasqanman hina hukllachan: commitninkuna kunan sucursalpa historianpa hukninmi tukun, hukllana commitwan hukllachasqa (utaq atikuptin utqaylla ñawpaqman).",
    "settings.mergeModeSquashHint": "Lliw ramata kunan ramapi huklla commit nisqaman thuñichin (git merge --squash). Kikin sucursalpa commitkuna mana hayk'aqpas kunan sucursalpa historianman yaykunkuchu — huk commitllata chaypi rikunki. Kikin sucursalqa mana llamisqa saqisqa, chaymi commits nisqakuna kikin carrilninpi rikukuqlla qhipakunku.",
    "settings.mergedView": "Huñusqa commitkuna grafico nisqapi",
    "settings.mergedViewBranch": "Kikin sucursalninkullapi",
    "settings.mergedViewTarget": "Hinallataqmi sucursalpipas hukllawasqa kasqankupi",
    "settings.mergedViewBranchHint": "Huk huñusqa sucursalpa commits nisqakunaqa kikin carrilninkullapim siq’isqa kanku; chay huñunakuyqa tinkuchiq hinam rikurin. Chay clásico nisqa qhawariy.",
    "settings.mergedViewTargetHint": "Huk huñunakuy apamusqa commitkuna chaskiq sucursalpa carrilninpipas siq'isqa kanku, huñusqa commit urapi pilasqa chaymanta originalkunawan kuskachasqa — imaraykuchus chay rama chiqamanta kunan chaykunata hap'in. Copiakunaqa llimp’i llimp’i, umayuqtaq sucursalpi qillqasqa kasqanwan, chayrayku sut’i kachkan mana chaypi ruwasqa kasqankuta. Squash huñunakuykunaqa manam afectasqachu: huk squash huk ordinario commit nisqatam qillqan, chaymi chay commit nisqalla haykapipas rikurin.",
    "legend.title": "Yuyapachiq",
    "legend.head": "UMA / kunan kaq rama",
    "legend.local": "Sucursal local",
    "legend.remote": "Karu rama",
    "legend.remoteOnly": "Puyullapi (mana aysasqa) .",
    "legend.tag": "Etiqueta (versión) .",
    "legend.commit": "Ruway",
    "legend.stash": "Stash (estantepi llamk'ay) .",
    "legend.mergedIn": "Huk huñunakuywan yaykumurqan (huk ramapi qillqasqa) .",
    "legend.nodes": "Cajas nisqakuna",
    "legend.lines": "Chirukuna",
    "legend.edgeParent": "Ñawpaq commit kikin chirupi",
    "legend.edgeMerge": "Huñusqa rama — flechaqa huñunakuy commit nisqapi rikuchin",
    "legend.edgeBranch": "Kaypiqa huk k’allma bifurcakurqan",
    "legend.edgeStash": "Stash hinaspa commit chaypi estantepi churasqa",
    "legend.edgeMergedChain": "Kay sucursalpa chirun hukllachasqa commits kaqninta purichkaq",
    "legend.edgeMergedTie": "Chay kikin commit nisqa sucursalpi qelqasqa kasqanpi",
    "menu.jumpHead": "⌖ Chequeo nisqaman riy",
    "menu.resetView": "⤢ Qhawayta wakmanta churay",
    "menu.createBranch": "Kaymanta sucursalta ruway...",
    "menu.checkout": "Kay committa qhaway",
    "menu.copySha": "Copia commit SHA",
    "menu.pushBranch": "Push rama \"{name}\".",
    "menu.renameCommit": "Commit willayta sutichay...",
    "menu.undoCommit": "Deshacer commit (t'ikraykunata waqaychay)...",
    "menu.viewChanges": "Tikraykunata qhaway...",
    "menu.jumpToOriginal": "Riy original commit nisqaman",
    "changes.title": "{sha} nisqapi tikrakuykuna",
    "changes.tabChanged": "Cambiado",
    "changes.tabAll": "Tukuy Archivokuna",
    "changes.added": "Yapachisqa",
    "changes.modified": "Modificasqa",
    "changes.deleted": "Qullusqa",
    "changes.renamed": "Huk sutiwan sutichasqa",
    "changes.noChanges": "Kay commit mana willañiqikunata tikranchu.",
    "changes.loading": "Cargando diff...",
    "changes.selectFile": "Huk willañiqita akllay tikrayninkunata qhawanaykipaq.",
    "changes.original": "Kikin",
    "changes.changed": "Kay commit",
    "changes.binary": "Iskayniyuq willañiqi — mana qillqa diff rikuchinapaq.",
    "changes.tooLarge": "Archivoqa ancha hatunmi diff.",
    "changes.renamedFrom": "{path} nisqamanta musuq sutiwan sutichasqa",
    "changes.close": "Wichqay",
    "changes.prevChange": "Ñawpaq tikray",
    "changes.nextChange": "Qatiqnin tikray",
    "changes.collapseAll": "Llapan qillqana mayt’ukunata thuñichiy",
    "changes.expandAll": "Llapan qillqana mayt’ukunata mast’ariy",
    "changes.searchPlaceholder": "Archivokunata maskay...",
    "changes.noSearchResults": "Mana ima willañiqikuna maskasqaykiwan tupanchu.",
    "changes.maximize": "Maximizar",
    "changes.restore": "Tamañota kutichiy",
    "find.open": "Kay diff nisqapi maskay (Ctrl+F) .",
    "find.placeholder": "Tariy in diff...",
    "find.prev": "Ñawpaq tupanachiy",
    "find.next": "Qatiqnin atipanakuy",
    "find.close": "Wichqay maskay",
    "find.noResults": "Mana imapas ruwasqachu",
    "commit.title": "Cambiokunata ruway",
    "commit.close": "Wichqay",
    "commit.loading": "Ñawinchay llamk'aq sacha...",
    "commit.noChanges": "Mana kanchu llaqtapi tikrakuykuna compromiso ruwanapaq.",
    "commit.selectFile": "Huk willañiqita akllay diff nisqa qhawanaykipaq.",
    "commit.selectAll": "Llapallanta akllay",
    "commit.selectNone": "Tukuyta chuyanchay",
    "commit.message": "Commit nisqa willakuy",
    "commit.messagePlaceholder": "Chay cambio kasqanmanta willakuy",
    "commit.selectedCount": "{count} willañiqi(kuna) akllasqa",
    "commit.review": "Qawapay",
    "commit.reviewTitle": "Tukupay qhaway manaraq commit kachkaptin",
    "commit.reviewSummary": "{count} willañiqi(kuna) willakuywan kamachiy: {message}",
    "commit.back": "Qipaman",
    "commit.commit": "Local commit nisqa ruway",
    "menu.stashApply": "Stash nisqawan churay",
    "menu.stashPop": "Pop stash (aplicar & hurquy) .",
    "menu.stashDrop": "Gota stash...",
    "merge.title": "Sucursal Huñuy",
    "merge.route": "Kunan sucursalman hukllawakuy",
    "merge.source": "(pukyuta) nisqamanta.",
    "merge.target": "Into (kunan) .",
    "merge.loading": "Huñunakuyta t'aqwispa...",
    "merge.upToDate": "Ña kunankama — mana imapas hukllanapaq.",
    "merge.fastForward": "Utqaylla ñawpaqman — mana hukllana commit necesitakun (“Huñunakuy commit ruway” nisqapi ñit’iy hukta kallpachanaykipaq).",
    "merge.summary": "{files} willañiqi(kuna) tikrasqa, {conflicts} ch'aqway(kuna).",
    "merge.conflictsWarning": "Kay huñunakuyqa ch'aqwayniyuqmi — hukllachayta atinkiraqmi, chaymantataq allichaqpi allichay.",
    "merge.noChanges": "Mana willañiqi tikrakunchu.",
    "merge.files": "Chaywan lluqsiq tikrakuykuna",
    "merge.added": "Yapachisqa",
    "merge.modified": "Modificasqa",
    "merge.deleted": "Qullusqa",
    "merge.conflict": "Awqanakuy",
    "merge.message": "Huñuy commit willayta",
    "merge.messagePlaceholder": "Huñuy rama 'pukyuta'.",
    "merge.messageFfHint": "Utqaylla ñawpaqman huñunakuypi mana qhawasqa (mana ima commit ruwasqachu).",
    "merge.noFastForward": "Huñunakuy kamachiyta paqarichiy (mana utqaylla ñawpaqman puriq) .",
    "merge.noFastForwardHint": "Sapa kuti huk hukllanakuy committa qillqay, sucursal utqaylla ñawpaqman puriyta atichkaptinpas.",
    "merge.merge": "Huñunakuy",
    "merge.cancel": "Sayachiy",
    "merge.previewError": "Mana ñawpaq qhawayta yupayta atirqanchu: {message}",
    "merge.squashBadge": "Calabaza",
    "merge.squashNote": "Squash huñunakuy — sapa tikrayqa “{target}” kaqpi HUQ commit hina allpaman; “{source}” kikin compromisokuna historianmanta hawapi qhipakunku. Ajustes › Huñuy nisqapi kutiy.",
    "merge.messageSquash": "Commit nisqa willakuy",
    "merge.squashMessagePlaceholder": "'pukyuta' llallisqa tikrakuykuna.",
    "newBranch.title": "Sucursalta paqarichiy",
    "newBranch.startPoint": "Musuq sucursal {sha} nisqamanta qallarispa",
    "newBranch.startPointOn": "{refs} nisqapi",
    "newBranch.location": "Tarikusqan",
    "newBranch.locationRoot": "(sapi)",
    "newBranch.expandAll": "Tukuyta mastariy",
    "newBranch.collapseAll": "Tukuy tuñichiy",
    "newBranch.expandFolder": "Carpeta mast’ariy",
    "newBranch.collapseFolder": "T'uqyay qillqana mayt'u",
    "newBranch.name": "Sucursalpa sutin",
    "newBranch.namePlaceholder": "mi-rama",
    "newBranch.fullName": "Tukuy suti: {name}",
    "newBranch.invalid": "Mana allin rama suti",
    "newBranch.exists": "Kay sutiyuq sucursalqa kachkanñam",
    "newBranch.checkout": "Kamasqa kaptin sucursal de caja",
    "newBranch.create": "Paqarichiy",
    "newBranch.cancel": "Sayachiy",
    "status.loading": "Grafico nisqa cargakuchkan...",
    "status.summary": "{repo}{commits} commitkunata rikuchispa, {refs} refs",
    "status.branchCreated": "{sha} nisqapi \"{name}\" nisqa rama kamasqa",
    "status.error": "Pantasqa: {message}",
    "status.opFailed": "Operación mana allintachu ruwakurqa",
    "status.fetching": "Apamuspa...",
    "status.pulling": "Aysana...",
    "status.pushing": "Tanqaspa...",
    "status.committing": "Local commit nisqa paqarichispa...",
    "status.commitCreated": "Ruwasqa local commit {sha}: {message}",
    "status.syncing": "Sincronización...",
    "status.noHead": "Mana kunan qullqi quy tarisqachu grafikupi.",
    "status.undoing": "Deshacer commit...",
    "status.commitUndone": "Commit undone — tikraykunaqa llamk'aq sach'api kutimun.",
    "status.undoConflict": "Hit ch'aqwaykunata kutichiy — allichaqpi allichay, chaymanta qatiy.",
    "status.stashApplying": "Aplicando stash...",
    "status.stashPopping": "Popping stash nisqa...",
    "status.stashDropping": "Escota de caída...",
    "status.stashApplied": "Stash nisqa aplikasqa.",
    "status.stashPopped": "Stashqa pop nisqa.",
    "status.stashDropped": "Stash urmaykusqa.",
    "status.stashConflict": "Pakasqa ch'aqwaykunata — allichaqpi allichay.",
    "status.merging": "Huñusqa...",
    "status.merged": "Huñunakuy tukusqa.",
    "status.mergeConflict": "Huñunakuyqa ch'aqwayniyuqmi — allichaqpi allichay, chaymanta kamachiy.",
    "search.title": "Maskay",
    "search.tooltip": "Maskay commit",
    "search.placeholder": "Maskay commits...",
    "search.previous": "Ñawpaq ruwasqa",
    "search.next": "Qatiqnin ruway",
    "search.noResults": "Mana imapas ruwasqachu",
    "search.resultCount": "{index}/{count} nisqa",
    "search.filter": "Modo filtrado nisqa",
    "search.highlight": "Modo de resaltar nisqa",
    "search.filterMode": "Maskana qhaway",
    "search.filterModeFilter": "Filtro (mana tupaqkunata pakay) .",
    "search.filterModeHighlight": "Resaltay (llapallanta qawachiy, tupachiykunata marcay) .",
    "search.filterModeHint": "Imayna maskana ruwasqakuna siq’ipi rikuchikun chayta akllay.",
    "search.visibility": "Maypi kasqanmanta maskay",
    "search.visibilityToolbar": "Barra de herramientas nisqa ñit’ina",
    "search.visibilityAlwaysVisible": "Siempre-rikukuq barra",
    "search.visibilityHint": "Maskana llamkanakuna barra icono utaq mana tukukuq barra hina pata kaqpi rikuchiy.",
    "footer.github": "GitHub nisqapi",
  },
  nso: {
    "toolbar.refresh": "⟳ Hlabolla",
    "toolbar.fetch": "⤓ Tšea",
    "toolbar.pull": "⇩ Goga",
    "toolbar.push": "⇧ Kgorometša",
    "toolbar.commit": "✓ Itlama",
    "toolbar.sync": "⇅ Go nyalantšha",
    "toolbar.jumpHead": "⌖ Eya go reka",
    "toolbar.reset": "⤢ Seta pono gape",
    "toolbar.settings": "⚙ Dipeakanyo",
    "details.header": "Dintlha tša go Itlama",
    "details.close": "Tswalela",
    "details.sha": "SHA",
    "details.shortSha": "Khutshwane SHA",
    "details.message": "Molaetša",
    "details.author": "Mongwadi",
    "details.date": "Letšatšikgwedi",
    "details.labels": "Dileibole",
    "details.location": "Lefelo",
    "details.currentHead": "HLOGO - checkout jwale",
    "settings.title": "Dipeakanyo",
    "settings.language": "Polelo",
    "settings.theme": "Tabataba",
    "settings.themeLight": "Seetša",
    "settings.themeDark": "Leswiswi",
    "settings.themeLightHint": "Lemorago la seetša, sengwalwa se se lefsifsi.",
    "settings.themeDarkHint": "Bokamorago bjo bo lefsifsi, sengwalwa se se bofefo.",
    "settings.mainBranch": "Lekala le legolo",
    "settings.mainBranchAuto": "E itiriša",
    "settings.mainBranchSearch": "Batla makala...",
    "settings.mainBranchNoMatch": "Ga go na makala ao a swanago",
    "settings.display": "Pontšo setaele",
    "settings.displayModern": "Sebjalebjale",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Free seile - hula ho pan, phelela ho zoom.",
    "settings.displayClassicHint": "Fixed seile - kutu pinned letšehali, ha ho na zoom, phelela feela (joaloka SVN phetolo kerafo).",
    "settings.sectionGeneral": "Kakaretšo",
    "settings.sectionGraph": "Kerafo",
    "settings.sectionChanges": "E fetola pono",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "E bontšhitšwe",
    "settings.diffMinimapOff": "Fihlilwe",
    "settings.diffMinimapOnHint": "Bontša VS Khoutu-mokhoa kakaretso hlobolisa ka thoko ga diff - e goge go sepelasepela; maswao a phetogo a akareditšwe.",
    "settings.diffMinimapOffHint": "Ha ho kakaretso hlobolisa - phelela diff ka tloaelo.",
    "settings.commitReview": "Itlama ka tshekatsheko",
    "settings.commitReviewOn": "Botšiša pele o itlama",
    "settings.commitReviewOff": "Itlama ka go lebanya",
    "settings.commitReviewOnHint": "Bontšha lenaneo la mafelelo la faele le tshekatsheko ya molaetša pele o hlama boikgafo bja selegae.",
    "settings.commitReviewOffHint": "Konopo ya Itlama e hlola boikgafo bja selegae ka pela.",
    "settings.svnBranchDialog": "Poledišano ya lekala ya setaele sa SVN",
    "settings.svnBranchDialogHint": "Bontšha mokgethi wa sehlare sa foltara ge o hlama lekala (go e na le kgopelo ye bonolo).",
    "settings.branchDialog": "Poledišano ya lekala",
    "settings.branchDialogSvnTitle": "SVN-mokhoa",
    "settings.branchDialogNativeVscode": "VS Khoutu setaele",
    "settings.branchDialogNativeVs": "Setaele sa Visual Studio",
    "settings.branchDialogNativeHint": "Kgopelo ya lekala yeo e agetšwego ka gare ya IDE ya gago.",
    "settings.close": "Tswalela",
    "settings.done": "Phethilwe",
    "settings.sectionAdvanced": "Gatetšego pele",
    "settings.gitSource": "Git phethagatšwa",
    "settings.gitSourceBuiltin": "E ageleditšwe ka gare",
    "settings.gitSourceBuiltinHint": "Šomiša binary ya git go tšwa go katoloso ya Git ye ​​e agetšwego ka gare ya IDE.",
    "settings.gitSourceCustom": "Tsela ya tlwaelo",
    "settings.gitPath": "Tsela ya go ya go git",
    "settings.gitPathPlaceholder": "/ usr / bin / git",
    "settings.gitPathBrowse": "Browse...",
    "settings.jargon": "Dipehelo tša git",
    "settings.jargonTranslate": "Fetolela",
    "settings.jargonEnglish": "Boloka ka Seisemane",
    "settings.jargonTranslateHint": "Fetolela mareo a Git (goga, kgorometša, itlama, lekala...) ka polelong ya segokanyimmediamentsi sa sebolokigolo.",
    "settings.jargonEnglishHint": "Boloka mareo a Git (goga, kgorometša, itlama, lekala...) ka Seisemane; fetolela se sengwe le se sengwe.",
    "settings.sectionMerge": "Kopanya",
    "settings.mergeMode": "Kopanya setaele",
    "settings.mergeModeMerge": "Go kopanya mo go tlwaelegilego",
    "settings.mergeModeSquash": "Squash kopanya",
    "settings.mergeModeMergeHint": "E kopanya lekala ka moo le lego ka gona: di-commit tša lona e ba karolo ya histori ya lekala la bjale, di kopanywa ke commit ya go kopanya (goba e fetišetšwa pele ka lebelo ge go kgonega).",
    "settings.mergeModeSquashHint": "E phuhlama lekala ka moka go ba go itlama go tee lekaleng la bjale (git merge --squash). Di-commit tša lekala ka bolona ga di ke di tsena historing ya lekala la bjale — o bona commit e tee fela moo. Lekala ka bolona le tlogetšwe le sa kgongwa, ka fao di-commit tša lona di dula di bonagala mo tseleng ya lona.",
    "settings.mergedView": "Di itlama tše di kopantšwego ka gare ga kerafo",
    "settings.mergedViewBranch": "Feela ka lekaleng la bona",
    "settings.mergedViewTarget": "Gape lekaleng leo ba ilego ba kopanywa go lona",
    "settings.mergedViewBranchHint": "Di-commit tša lekala leo le kopantšwego di thalwa feela mo tseleng ya tšona; kopanyo e bontša e le sehokedi. Pono ya classic.",
    "settings.mergedViewTargetHint": "The commits a kopanyo tlisoa ka ba thalwa ka amohela lekala lane hammoho, stacked tlas'a kopanya itlama le levels le originals - hobane lekala leo hlile le na le bona hona joale. Dikopi di na le mmala o mohwibidu gomme di lebile lekaleng leo di ngwadilwego go lona, ​​ka gona go dula go le molaleng gore ga se tša dirwa moo. Dikopano tša squash ga di amege: squash e ngwala commit e tee ye e tlwaelegilego, ka fao ke fela commit yeo e kilego ya tšwelela.",
    "legend.title": "Nonwane",
    "legend.head": "HLOGO / lekala la bjale",
    "legend.local": "Lekala la lefelong leo",
    "legend.remote": "Lekala la kgole",
    "legend.remoteOnly": "Feela ka lerung (e sego gogwa) .",
    "legend.tag": "Tag (phetolelo) .",
    "legend.commit": "Itlama",
    "legend.stash": "Stash (mošomo wa šelefo) .",
    "legend.mergedIn": "E tsene ka kopanyo (e ngwadilwe lekaleng le lengwe) .",
    "legend.nodes": "Mapokisi a",
    "legend.lines": "Methaladi",
    "legend.edgeParent": "Boitlamo bja peleng mo moleng o swanago",
    "legend.edgeMerge": "Kopantswe-ka lekala - motsu o supa ka kopanya itlama",
    "legend.edgeBranch": "Lekala le ile la foroka mo",
    "legend.edgeStash": "Stash le commit yeo e bego e beilwe godimo ga yona",
    "legend.edgeMergedChain": "Mothaladi wa lekala le o kitimago ka gare ga di-commit tše di kopantšwego",
    "legend.edgeMergedTie": "Commit e swanago lekaleng leo e ngwadilwego go lona",
    "menu.jumpHead": "⌖ Eya go reka",
    "menu.resetView": "⤢ Seta pono gape",
    "menu.createBranch": "Hlama lekala go tšwa mo...",
    "menu.checkout": "Checkout ena itlama",
    "menu.copySha": "Khopi itlama SHA",
    "menu.pushBranch": "Kgorometša lekala \"{name}\".",
    "menu.renameCommit": "Rename molaetša wa go itlama...",
    "menu.undoCommit": "Undo commit (boloka diphetogo)...",
    "menu.viewChanges": "Lebelela diphetogo...",
    "menu.jumpToOriginal": "Eya go itlama ya mathomo",
    "changes.title": "Diphetogo ka go {sha}",
    "changes.tabChanged": "E fetotšwe",
    "changes.tabAll": "Difaele ka moka",
    "changes.added": "E okeditšwe",
    "changes.modified": "E fetotšwe",
    "changes.deleted": "E phumotšwe",
    "changes.renamed": "E reeletšwe lefsa",
    "changes.noChanges": "Ena itlama fetola ha ho difaele.",
    "changes.loading": "Loading diff...",
    "changes.selectFile": "Kgetha faele go bona diphetogo tša yona.",
    "changes.original": "Mathomo",
    "changes.changed": "Se se itlama",
    "changes.binary": "Binary faele - ha ho mongolo diff ho bontša.",
    "changes.tooLarge": "Faele e kgolo kudu go diff.",
    "changes.renamedFrom": "E reeletšwe lefsa go tšwa go {path}",
    "changes.close": "Tswalela",
    "changes.prevChange": "Phetogo ya peleng",
    "changes.nextChange": "Phetogo ye e latelago",
    "changes.collapseAll": "Phuhla difoldara ka moka",
    "changes.expandAll": "Atološa difoldara ka moka",
    "changes.searchPlaceholder": "Batla difaele...",
    "changes.noSearchResults": "Ga go na difaele tšeo di swanago le nyakišišo ya gago.",
    "changes.maximize": "Godiša",
    "changes.restore": "Tsošološa bogolo",
    "find.open": "Batla ka diff ye (Ctrl+F) .",
    "find.placeholder": "Hwetša ka diff...",
    "find.prev": "Papadi ya peleng",
    "find.next": "Papadi ye e latelago",
    "find.close": "Tswala nyakišišo",
    "find.noResults": "Ga go na dipoelo",
    "commit.title": "Itlama Diphetogo",
    "commit.close": "Tswalela",
    "commit.loading": "Go bala sehlare sa go šoma...",
    "commit.noChanges": "Ga go na diphetogo tša selegae tšeo di swanetšego go itlama.",
    "commit.selectFile": "Kgetha faele go bona diff ya yona.",
    "commit.selectAll": "Kgetha ka moka",
    "commit.selectNone": "Hlakola ka moka",
    "commit.message": "Molaetša wa go itlama",
    "commit.messagePlaceholder": "Hlaloša phetogo",
    "commit.selectedCount": "{count} faele (s) kgethilwe",
    "commit.review": "Lekodišiša",
    "commit.reviewTitle": "Tlhahlobo ya mafelelo pele ga go itlama",
    "commit.reviewSummary": "Itlama faele (s) ya {count} ka molaetša: {message}",
    "commit.back": "Morago",
    "commit.commit": "Hlama boikgafo bja selegae",
    "menu.stashApply": "Dira kopo stash",
    "menu.stashPop": "Pop stash (dira kopo & tlosa) .",
    "menu.stashDrop": "Lerotholi la stash...",
    "merge.title": "Kopanya Lekala",
    "merge.route": "Go kopanya lekaleng la bjale",
    "merge.source": "Go tšwa go (mothopo) .",
    "merge.target": "Ka gare ga (ya bjale) .",
    "merge.loading": "Go sekaseka kopanyo...",
    "merge.upToDate": "Se a ntse a ho fihlela joale - ha ho letho ho kopanya.",
    "merge.fastForward": "Ka lebelo-pele - ga go na go kopanya go itlama mo go nyakegago (swaya “Thea go kopanya go itlama” go gapeletša e tee).",
    "merge.summary": "{files} faele (s) fetotšwe, {conflicts} thulano (s).",
    "merge.conflictsWarning": "Kopano ye e na le dithulano - o sa kgona go kopanya, ke moka wa di rarolla ka go morulaganyi.",
    "merge.noChanges": "Ga go na diphetogo tša faele.",
    "merge.files": "Diphetogo tšeo di feleletšago",
    "merge.added": "E okeditšwe",
    "merge.modified": "E fetotšwe",
    "merge.deleted": "E phumotšwe",
    "merge.conflict": "Thulano",
    "merge.message": "Kopanya molaetša wa go itlama",
    "merge.messagePlaceholder": "Kopanya lekala 'mohlodi'.",
    "merge.messageFfHint": "E hlokomologwa ka kopanyo ya go ya pele ka lebelo (ga go na go itlama mo go hlotšwego).",
    "merge.noFastForward": "Hlama boikgafo bja go kopanya (ga go na go ya pele ka lebelo) .",
    "merge.noFastForwardHint": "Ka mehla rekota boikgafo bja go kopanya, le ge lekala le be le ka kgona go tšwela pele ka lebelo.",
    "merge.merge": "Kopanya",
    "merge.cancel": "Khansela",
    "merge.previewError": "Ga se ya kgona go bala ponelopele: {message}",
    "merge.squashBadge": "Sekwaše",
    "merge.squashNote": "Squash kopanya - mong le e mong phetoho lands e le ONE itlama ka “{target}”; “{source}”’s own commits di dula ka ntle ga histori ya yona. Fetogela morago go Dipeakanyo › Kopanya.",
    "merge.messageSquash": "Molaetša wa go itlama",
    "merge.squashMessagePlaceholder": "Diphetogo tše di pšhatlagantšwego go tšwa go 'mothopo'.",
    "newBranch.title": "Hlama Lekala",
    "newBranch.startPoint": "Lekala le lefsa go thoma go tšwa go {sha}",
    "newBranch.startPointOn": "ka {refs}",
    "newBranch.location": "Lefelo",
    "newBranch.locationRoot": "(modu)",
    "newBranch.expandAll": "Katološa ka moka",
    "newBranch.collapseAll": "Phuhla ka moka",
    "newBranch.expandFolder": "Atološa foltara",
    "newBranch.collapseFolder": "Foltara ya go phuhlama",
    "newBranch.name": "Leina la lekala",
    "newBranch.namePlaceholder": "lekala la-ka",
    "newBranch.fullName": "Lebitso ka botlalo: {name}",
    "newBranch.invalid": "Leina la lekala leo le sa šomego",
    "newBranch.exists": "Lekala leo le nago le leina le le šetše le le gona",
    "newBranch.checkout": "Checkout lekala ka morago ga tlholo",
    "newBranch.create": "Hlama",
    "newBranch.cancel": "Khansela",
    "status.loading": "Go laetša kerafo...",
    "status.summary": "{repo}Go bontšha {commits} e itlama, {refs} refs",
    "status.branchCreated": "Bōpiloe lekala \"{name}\" ka {sha}",
    "status.error": "Phošo: {message}",
    "status.opFailed": "Tshepedišo e paletšwe",
    "status.fetching": "Go lata...",
    "status.pulling": "Go goga...",
    "status.pushing": "Go kgorometša...",
    "status.committing": "Go hlola boikgafo bja selegae...",
    "status.commitCreated": "E hlotšwe go itlama ga selegae {sha}: {message}",
    "status.syncing": "Go nyalantšha...",
    "status.noHead": "Ga go na go tšhela ga bjale mo go hweditšwego ka gare ga kerafo.",
    "status.undoing": "Go bušetša morago go itlama...",
    "status.commitUndone": "Itlama undone - diphetogo di khutlile ka sefateng sebetsa.",
    "status.undoConflict": "Undo otla dithulano - rarolla bona ka morulaganyi, ebe tsoela pele.",
    "status.stashApplying": "Go diriša stash...",
    "status.stashPopping": "Popping stash...",
    "status.stashDropping": "Go lahlela stash...",
    "status.stashApplied": "Stash e ile ya dirišwa.",
    "status.stashPopped": "Stash o ile a tšwelela.",
    "status.stashDropped": "Stash o ile a wela fase.",
    "status.stashConflict": "Stash dithulano - rarolla bona ka mohlophisi.",
    "status.merging": "Go kopanya...",
    "status.merged": "Kopanya e phethilwe.",
    "status.mergeConflict": "Kopanya na le dithulano - rarolla bona ka mohlophisi, ebe itlama.",
    "search.title": "Nyaka",
    "search.tooltip": "Go nyaka go itlama",
    "search.placeholder": "Go nyaka go itlama...",
    "search.previous": "Sephetho sa peleng",
    "search.next": "Sephetho se se latelago",
    "search.noResults": "Ga go na dipoelo",
    "search.resultCount": "{index}/{count} ya go swana le yona",
    "search.filter": "Mokgwa wa sefa",
    "search.highlight": "Totobatsa mokgwa wa",
    "search.filterMode": "Pontšo ya go nyaka",
    "search.filterModeFilter": "Sefa (pata tšeo e sego tša go swana) .",
    "search.filterModeHighlight": "Hlaola (bontšha ka moka, swaya ditshwantšho) .",
    "search.filterModeHint": "Kgetha ka moo dipoelo tša nyakišišo di bontšhwago ka gona ka gare ga kerafo.",
    "search.visibility": "Batla lefelo",
    "search.visibilityToolbar": "Konopo ya bara ya didirišwa",
    "search.visibilityAlwaysVisible": "Ka mehla-bonaha bareng",
    "search.visibilityHint": "Bontšha nyakišišo bjalo ka leswao la bara ya didirišwa goba bara ye e phegelelago ka godimo.",
    "footer.github": "GitHub ya go swana le yona",
  },
  st: {
    "toolbar.refresh": "⟳ Hlasimolla",
    "toolbar.fetch": "⤓ Lata",
    "toolbar.pull": "⇩ Hula",
    "toolbar.push": "⇧ Sututsa",
    "toolbar.commit": "✓ Boitlamo",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Eya ho tefo",
    "toolbar.reset": "⤢ Khutlisa pono",
    "toolbar.settings": "⚙ Litlhophiso",
    "details.header": "Nehelana ka lintlha",
    "details.close": "Koala",
    "details.sha": "SHA",
    "details.shortSha": "SHA e khutšoanyane",
    "details.message": "Molaetsa",
    "details.author": "Mongoli",
    "details.date": "Letsatsi",
    "details.labels": "Labels",
    "details.location": "Sebaka",
    "details.currentHead": "HEAD - ho lefa hona joale",
    "settings.title": "Litlhophiso",
    "settings.language": "Puo",
    "settings.theme": "Sehlooho",
    "settings.themeLight": "Leseli",
    "settings.themeDark": "Lefifi",
    "settings.themeLightHint": "Bokaholimo bo khanyang, mongolo o lefifi.",
    "settings.themeDarkHint": "Bokaholimo bo lefifi, mongolo o bobebe.",
    "settings.mainBranch": "Lekala le ka sehloohong",
    "settings.mainBranchAuto": "Ka ho iketsa",
    "settings.mainBranchSearch": "Batla makala…",
    "settings.mainBranchNoMatch": "Ha ho makala a tšoanang",
    "settings.display": "Setaele sa pontsho",
    "settings.displayModern": "Ea kajeno",
    "settings.displayClassic": "Ea khale",
    "settings.displayModernHint": "Canvas ea mahala - hula ho pan, tsamaisa ho atometsa.",
    "settings.displayClassicHint": "Canvas e tsitsitseng - kutu e tlanngoe ka ho le letšehali, ha ho na zoom, tsamaisa feela (joaloka graph ea ntlafatso ea SVN).",
    "settings.sectionGeneral": "Kakaretso",
    "settings.sectionGraph": "Kerafo",
    "settings.sectionChanges": "Fetola pono",
    "settings.diffMinimap": "Phapang minimap",
    "settings.diffMinimapOn": "Bontshitswe",
    "settings.diffMinimapOff": "E patiloe",
    "settings.diffMinimapOnHint": "Bontša setšoantšo sa kakaretso sa mofuta oa VS Code haufi le diff - e hule ho tsamaisa; matshwao a ho fetola a kenyeleditswe.",
    "settings.diffMinimapOffHint": "Ha ho setšoantšo se akaretsang - tsamaisa diff ka tloaelo.",
    "settings.commitReview": "Ikemisetse tlhahlobo",
    "settings.commitReviewOn": "Botsa pele u itlama",
    "settings.commitReviewOff": "Ikemisetse ka ho toba",
    "settings.commitReviewOnHint": "Hlahisa lenane la ho qetela la lifaele le tlhahlobo ea molaetsa pele u theha boitlamo ba lehae.",
    "settings.commitReviewOffHint": "Konopo ea Commit e theha boitlamo ba lehae hanghang.",
    "settings.svnBranchDialog": "Puisano ea lekala ea mofuta oa SVN",
    "settings.svnBranchDialogHint": "Bontša motho ea khethang lifate ha u theha lekala (ho e-na le molaetsa o bonolo).",
    "settings.branchDialog": "Moqoqo oa lekala",
    "settings.branchDialogSvnTitle": "Mokhoa oa SVN",
    "settings.branchDialogNativeVscode": "Mokhoa oa VS Code",
    "settings.branchDialogNativeVs": "Mokhoa oa Visual Studio",
    "settings.branchDialogNativeHint": "Molaetsa oa lekala o hahelletsoeng ka har'a IDE ea hau.",
    "settings.close": "Koala",
    "settings.done": "E felile",
    "settings.sectionAdvanced": "E tsoetseng pele",
    "settings.gitSource": "Git e ka phethahala",
    "settings.gitSourceBuiltin": "E ahiloeng",
    "settings.gitSourceBuiltinHint": "Sebelisa binary ea git ho tsoa ho katoloso ea Git e hahiloeng ka har'a IDE.",
    "settings.gitSourceCustom": "Tsela e tloaelehileng",
    "settings.gitPath": "Tsela ea ho git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Batla…",
    "settings.jargon": "Mantsoe a Git",
    "settings.jargonTranslate": "Fetolela",
    "settings.jargonEnglish": "Boloka ka Senyesemane",
    "settings.jargonTranslateHint": "Fetolela mantsoe a Git (hula, sututsa, itlama, lekala…) puong ea sehokelo.",
    "settings.jargonEnglishHint": "Boloka mantsoe a Git (hula, sututsa, itlama, lekala…) ka Senyesemane; fetolela tse ding tsohle.",
    "settings.sectionMerge": "Kopanya",
    "settings.mergeMode": "Kopanya mokhoa",
    "settings.mergeModeMerge": "Ho kopanya ho tloaelehileng",
    "settings.mergeModeSquash": "Squash e kopanya",
    "settings.mergeModeMergeHint": "E kopanya lekala kamoo le leng ka teng: boitlamo ba lona bo fetoha karolo ea nalane ea lekala la hajoale, e kopantsoeng ke boitlamo ba ho kopanya (kapa ho fetisoa kapele ha ho khoneha).",
    "settings.mergeModeSquashHint": "E putla lekala kaofela hore e be boitlamo bo le bong lekaleng la hajoale (git merge --squash). Mesebetsi ea lekala ha e kene nalaneng ea lekala la hajoale - o bona feela boitlamo bo le bong moo. Lekala ka bolona le sala le sa ts'oaroe, kahoo li-commit tsa lona li lula li bonahala ka tsela ea lona.",
    "settings.mergedView": "Likopo tse kopantsoeng ka har'a kerafo",
    "settings.mergedViewBranch": "Feela lekaleng la bona",
    "settings.mergedViewTarget": "Hape lekaleng ba ile ba kopanngoa ho",
    "settings.mergedViewBranchHint": "Likopo tsa lekala tse kopaneng li huloa feela ka tsela ea tsona; kopanyo e hlahella joalo ka sehokedi. Pono ea khale.",
    "settings.mergedViewTargetHint": "Lipalo-palo tse kentsoeng li huloa le tseleng ea lekala le amohelang, li behiloe tlas'a tumellano ea ho kopanya 'me li lekana le tsa mantlha - hobane lekala leo ehlile le na le tsona hona joale. Likopi li soeufetse 'me li tsamaile le lekala leo li neng li ngotsoe ho lona, ​​kahoo ho hlakile hore ha lia etsoa moo. Likopano tsa squash ha li amehe: squash e ngola boitlamo bo le bong feela bo tloaelehileng, kahoo ke boitlamo boo feela bo kileng ba hlaha.",
    "legend.title": "Tšōmo",
    "legend.head": "HLOOHO / lekala la jwale",
    "legend.local": "Lekala la lehae",
    "legend.remote": "Lekala le hole",
    "legend.remoteOnly": "Lerung feela (ha le huloe)",
    "legend.tag": "Theke (mofuta)",
    "legend.commit": "Boitlamo",
    "legend.stash": "Stash (mosebetsi o sirelelitsoeng)",
    "legend.mergedIn": "E kene ka kopanyo (e ngotsoeng lekaleng le leng)",
    "legend.nodes": "Mabokose",
    "legend.lines": "Mela",
    "legend.edgeParent": "Boitlamo bo fetileng ka tsela e tšoanang",
    "legend.edgeMerge": "Lekala le kopaneng - motsu o supa boitlamo ba ho kopanya",
    "legend.edgeBranch": "Lekala le fapohile mona",
    "legend.edgeStash": "Stash le boitlamo ba eona li ile tsa emisoa",
    "legend.edgeMergedChain": "Mohala oa lekala lena o tsamaeang ka har'a li-commite tse kopantsoeng",
    "legend.edgeMergedTie": "Eona boitlamo bo tšoanang lekaleng boo e neng e ngotsoe ho eona",
    "menu.jumpHead": "⌖ Eya ho tefo",
    "menu.resetView": "⤢ Khutlisa pono",
    "menu.createBranch": "Theha lekala ho tloha mona…",
    "menu.checkout": "Ela hloko tlhahiso ena",
    "menu.copySha": "Kopitsa boitlamo ba SHA",
    "menu.pushBranch": "Sututsa lekala \"{name}\"",
    "menu.renameCommit": "Fetola molaetsa oa boitlamo…",
    "menu.undoCommit": "Hlakola boitlamo (boloka liphetoho)…",
    "menu.viewChanges": "Sheba liphetoho...",
    "menu.jumpToOriginal": "Eya ho boitlamo ba pele",
    "node.mergedInTooltip": "Kopanya {merge} e tlisitse boitlamo bona ho \"{target}\" — e ngotsoe ho \"{origin}\".",
    "changes.title": "Liphetoho ho {sha}",
    "changes.tabChanged": "Fetohile",
    "changes.tabAll": "Lifaele Tsohle",
    "changes.added": "E kentsoe",
    "changes.modified": "Fetisitsoe",
    "changes.deleted": "E hlakotsoe",
    "changes.renamed": "E reheletsoe bocha",
    "changes.noChanges": "Ts'ebetso ena ha e fetole lifaele.",
    "changes.loading": "E kenya phapang…",
    "changes.selectFile": "Khetha faele ho bona liphetoho tsa eona.",
    "changes.original": "Ea mantlha",
    "changes.changed": "Boitlamo bona",
    "changes.binary": "Binary file - ha ho na mongolo o ka hlahisoang.",
    "changes.tooLarge": "Faele e kholo haholo hore e ka fapana.",
    "changes.renamedFrom": "E rehiloe bocha ho tsoa {path}",
    "changes.close": "Koala",
    "changes.prevChange": "Phetoho e fetileng",
    "changes.nextChange": "Phetoho e latelang",
    "changes.collapseAll": "Hlakola lifoldara tsohle",
    "changes.expandAll": "Eketsa lifoldara tsohle",
    "changes.searchPlaceholder": "Batla lifaele...",
    "changes.noSearchResults": "Ha ho lifaele tse lumellanang le seo u se batlang.",
    "changes.maximize": "Eketsa",
    "changes.restore": "Khutlisa boholo",
    "find.open": "Batla ho diff ena (Ctrl+F)",
    "find.placeholder": "Fumana ho fapana...",
    "find.prev": "Papali e fetileng",
    "find.next": "Papali e latelang",
    "find.close": "Kwala patlo",
    "find.noResults": "Ha ho liphetho",
    "commit.title": "Etsa Liphetoho",
    "commit.close": "Koala",
    "commit.loading": "E bala sefate se sebetsang…",
    "commit.noChanges": "Ha ho na liphetoho tsa lehae tse lokelang ho etsoa.",
    "commit.selectFile": "Khetha faele ho bona phapang ea eona.",
    "commit.selectAll": "Khetha tsohle",
    "commit.selectNone": "Hlakola tsohle",
    "commit.message": "Molaetsa oa boitlamo",
    "commit.messagePlaceholder": "Hlalosa phetoho",
    "commit.selectedCount": "Lifaele tsa {count} li khethiloe",
    "commit.review": "Hlahloba",
    "commit.reviewTitle": "Chelete ea ho qetela pele o etsa kopo",
    "commit.reviewSummary": "Nehelana ka lifaele tsa {count} tse nang le molaetsa: {message}",
    "commit.back": "Morao",
    "commit.commit": "Etsa boitlamo ba lehae",
    "menu.stashApply": "Etsa kopo ea stash",
    "menu.stashPop": "Pop stash (sebelisa le ho tlosa)",
    "menu.stashDrop": "Tlosa stash…",
    "merge.title": "Kopanya Lekala",
    "merge.route": "Ho kopanya lekaleng la hajoale",
    "merge.source": "Ho tsoa ho (mohloli)",
    "merge.target": "Ho kena (hona joale)",
    "merge.loading": "E sekaseka kopanyo...",
    "merge.upToDate": "E se e ntse e le teng - ha ho letho le ka kopanyang.",
    "merge.fastForward": "Tsoela pele ka potlako - ha ho boitlamo ba ho kopanya bo hlokahalang (tšoaea \"Etsa boitlamo ba ho kopanya\" ho qobella e le 'ngoe).",
    "merge.summary": "Lifaele tsa {files} li fetohile, likhohlano tsa {conflicts}.",
    "merge.conflictsWarning": "Khokahano ena e na le likhohlano - u ntse u ka khona ho li kopanya, ebe u li rarolla ho mohlophisi.",
    "merge.noChanges": "Ha ho lifaele tse fetotsoeng.",
    "merge.files": "Liphetho tsa liphetoho",
    "merge.added": "E kentsoe",
    "merge.modified": "Fetisitsoe",
    "merge.deleted": "E hlakotsoe",
    "merge.conflict": "Khohlano",
    "merge.message": "Kopanya molaetsa oa boitlamo",
    "merge.messagePlaceholder": "Kopanya 'mohloli' oa lekala",
    "merge.messageFfHint": "E hlokomolohuoa ho kopantseng pele ka potlako (ha ho boitlamo bo entsoeng).",
    "merge.noFastForward": "Etsa boitlamo ba ho kopanya (ha bo fetele pele)",
    "merge.noFastForwardHint": "Kamehla rekota boitlamo ba ho kopanya, leha lekala le ka tsoela pele kapele.",
    "merge.merge": "Kopanya",
    "merge.cancel": "Hlakola",
    "merge.previewError": "Ha e khone ho etsa ponelo-pele: {message}",
    "merge.squashBadge": "Sekoashe",
    "merge.squashNote": "Ho kopanya squash - phetoho e 'ngoe le e' ngoe e oela ha ONE itlama ho \"{target}\"; \"{source}\"'s boitlamo bo ke ke ba tsoa nalaneng ea eona. Khutlela morao ho Litlhophiso › Kopanya.",
    "merge.messageSquash": "Molaetsa oa boitlamo",
    "merge.squashMessagePlaceholder": "Liphetoho tse fokotsehileng ho tsoa ho 'mohloli'",
    "newBranch.title": "Theha Lekala",
    "newBranch.startPoint": "Lekala le lecha le qalang ho tloha {sha}",
    "newBranch.startPointOn": "ho {refs}",
    "newBranch.location": "Sebaka",
    "newBranch.locationRoot": "(motso)",
    "newBranch.expandAll": "Atolosa tsohle",
    "newBranch.collapseAll": "Hlakola kaofela",
    "newBranch.expandFolder": "Atolosa sephutheli",
    "newBranch.collapseFolder": "Hlakola sephutheli",
    "newBranch.name": "Lebitso la lekala",
    "newBranch.namePlaceholder": "lekala laka",
    "newBranch.fullName": "Lebitso ka botlalo: {name}",
    "newBranch.invalid": "Lebitso le fosahetseng",
    "newBranch.exists": "Lekala le nang le lebitso lena le se le le teng",
    "newBranch.checkout": "Checkout lekala ka mor'a pōpo",
    "newBranch.create": "Theha",
    "newBranch.cancel": "Hlakola",
    "status.loading": "E kenya graph...",
    "status.summary": "{repo}Showing {commits} boitlamo, {refs} refs",
    "status.branchCreated": "E thehile lekala \"{name}\" ho {sha}",
    "status.error": "Phoso: {message}",
    "status.opFailed": "Ts'ebetso e hlolehile",
    "status.fetching": "E lata...",
    "status.pulling": "Ho hula...",
    "status.pushing": "E sutumelletsa...",
    "status.committing": "E theha boitlamo ba lehae…",
    "status.commitCreated": "E thehiloe boitlamo ba lehae {sha}: {message}",
    "status.syncing": "E hokahanya...",
    "status.noHead": "Ha ho na chelete ea ho lefa hona joale ho kerafo.",
    "status.undoing": "E phethola boitlamo…",
    "status.commitUndone": "Boitlamo bo etsollotsoe - liphetoho li khutletse sefateng se sebetsang.",
    "status.undoConflict": "Etsolla likhohlano tse otlang - li rarolle ho sehlophisi, ebe u tsoela pele.",
    "status.stashApplying": "E sebelisa stash…",
    "status.stashPopping": "Ho hlaha stash…",
    "status.stashDropping": "E lahla stash…",
    "status.stashApplied": "Stash e kentsoe.",
    "status.stashPopped": "Stash e hlahile.",
    "status.stashDropped": "Stash e ile ea theoha.",
    "status.stashConflict": "Likhohlano tsa Stash - li rarolle ho mohlophisi.",
    "status.merging": "E kopanya…",
    "status.merged": "Ho kopanya ho phethiloe.",
    "status.mergeConflict": "Kopanya ho na le likhohlano - li rarolle ho mohlophisi, ebe u itlama.",
    "search.title": "Batla",
    "search.tooltip": "Ho batla boitlamo",
    "search.placeholder": "Patlisiso e ikemiselitse…",
    "search.previous": "Sephetho se fetileng",
    "search.next": "Sephetho se latelang",
    "search.noResults": "Ha ho liphetho",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Mokhoa oa ho sefa",
    "search.highlight": "Hlahisa mokhoa",
    "search.filterMode": "Batla ponts'o",
    "search.filterModeFilter": "Sefe (pata tse sa bapisang)",
    "search.filterModeHighlight": "Totobatsa (bontša tsohle, tšoaea litšoantšiso)",
    "search.filterModeHint": "Khetha mokhoa oo liphetho tsa lipatlisiso li hlahang ho graph.",
    "search.visibility": "Batla sebaka",
    "search.visibilityToolbar": "Konopo ea Toolbar",
    "search.visibilityAlwaysVisible": "Sebaka se bonoang kamehla",
    "search.visibilityHint": "Hlahisa patlo joalo ka lets'oao la lithulusi kapa bara e phehellang holimo.",
    "footer.github": "GitHub",
  },
  sq: {
    "toolbar.refresh": "⟳ Rifresko",
    "toolbar.fetch": "⤓ Merr",
    "toolbar.pull": "⇩ Tërhiq",
    "toolbar.push": "⇧ Shtyni",
    "toolbar.commit": "✓ Angazhohuni",
    "toolbar.sync": "⇅ Sinkronizimi",
    "toolbar.jumpHead": "⌖ Shko te arka",
    "toolbar.reset": "⤢ Rivendos pamjen",
    "toolbar.settings": "⚙ Cilësimet",
    "details.header": "Detajet e zotimit",
    "details.close": "Mbylle",
    "details.sha": "SHA",
    "details.shortSha": "SHA e shkurtër",
    "details.message": "Mesazh",
    "details.author": "Autori",
    "details.date": "Data",
    "details.labels": "Etiketat",
    "details.location": "Vendndodhja",
    "details.currentHead": "KOKË - arka aktuale",
    "settings.title": "Cilësimet",
    "settings.language": "Gjuha",
    "settings.theme": "Tema",
    "settings.themeLight": "Drita",
    "settings.themeDark": "E errët",
    "settings.themeLightHint": "Sfondi i lehtë, tekst i errët.",
    "settings.themeDarkHint": "Sfondi i errët, tekst i lehtë.",
    "settings.mainBranch": "Dega kryesore",
    "settings.mainBranchAuto": "Automatik",
    "settings.mainBranchSearch": "Kërko degët…",
    "settings.mainBranchNoMatch": "Nuk ka degë që përputhen",
    "settings.display": "Stili i shfaqjes",
    "settings.displayModern": "Moderne",
    "settings.displayClassic": "Klasike",
    "settings.displayModernHint": "Kanavacë falas — tërhiqeni për të shtrirë, lëvizni për të zmadhuar.",
    "settings.displayClassicHint": "Kanavacë e fiksuar - trungu i mbërthyer majtas, pa zmadhim, vetëm lëviz (si grafiku i rishikimit SVN).",
    "settings.sectionGeneral": "Gjeneral",
    "settings.sectionGraph": "Grafiku",
    "settings.sectionChanges": "Ndryshon pamjen",
    "settings.diffMinimap": "Diff Minimap",
    "settings.diffMinimapOn": "Treguar",
    "settings.diffMinimapOff": "I fshehur",
    "settings.diffMinimapOnHint": "Shfaq një shirit të përgjithshëm të stilit të kodit VS pranë ndryshimit — tërhiqeni për të lëvizur; shënuesit e ndryshimit të përfshirë.",
    "settings.diffMinimapOffHint": "Nuk ka shirit përmbledhje - lëvizni ndryshimin normalisht.",
    "settings.commitReview": "Kryeni rishikimin",
    "settings.commitReviewOn": "Pyetni përpara kryerjes",
    "settings.commitReviewOff": "Angazhohen drejtpërdrejt",
    "settings.commitReviewOnHint": "Shfaq listën përfundimtare të skedarëve dhe kontrollin e mesazheve përpara se të krijoni komitetin lokal.",
    "settings.commitReviewOffHint": "Butoni Commit krijon menjëherë commit lokal.",
    "settings.svnBranchDialog": "Dialogu i degës në stilin SVN",
    "settings.svnBranchDialogHint": "Tregoni një zgjedhës të pemës së dosjeve kur krijoni një degë (në vend të një urdhri të thjeshtë).",
    "settings.branchDialog": "Dialogu i degës",
    "settings.branchDialogSvnTitle": "SVN-stili",
    "settings.branchDialogNativeVscode": "Stili i kodit VS",
    "settings.branchDialogNativeVs": "Stili Visual Studio",
    "settings.branchDialogNativeHint": "Prompti i degës së integruar të IDE-së tuaj.",
    "settings.close": "Mbylle",
    "settings.done": "U krye",
    "settings.sectionAdvanced": "E avancuar",
    "settings.gitSource": "Git i ekzekutueshëm",
    "settings.gitSourceBuiltin": "I integruar",
    "settings.gitSourceBuiltinHint": "Përdorni binarin git nga zgjerimi Git i integruar i IDE.",
    "settings.gitSourceCustom": "Rrugë e personalizuar",
    "settings.gitPath": "Rruga drejt git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Shfleto…",
    "settings.jargon": "Kushtet e Git",
    "settings.jargonTranslate": "Përkthejeni",
    "settings.jargonEnglish": "Mbani në anglisht",
    "settings.jargonTranslateHint": "Përkthe termat e Git (tërheq, shty, angazho, degë…) në gjuhën e ndërfaqes.",
    "settings.jargonEnglishHint": "Mbani termat Git (tërheq, shtyj, angazhohem, degë…) në anglisht; përktheni gjithçka tjetër.",
    "settings.sectionMerge": "Bashkohen",
    "settings.mergeMode": "Stili i bashkimit",
    "settings.mergeModeMerge": "Bashkim normal",
    "settings.mergeModeSquash": "Kungull bashkohen",
    "settings.mergeModeMergeHint": "Bashkon degën ashtu siç është: angazhimet e saj bëhen pjesë e historisë së degës aktuale, të bashkuara nga një bashkim (ose përcillet me shpejtësi kur është e mundur).",
    "settings.mergeModeSquashHint": "E mbulon të gjithë degën në një kryerje të vetme në degën aktuale (git merge --squash). Detyrimet e vetë degës nuk hyjnë kurrë në historinë e degës aktuale - ju shihni vetëm një kryerje atje. Vetë dega është lënë e paprekur, kështu që angazhimet e saj mbeten të dukshme në korsinë e saj.",
    "settings.mergedView": "Komitetet e bashkuara në grafik",
    "settings.mergedViewBranch": "Vetëm në degën e tyre",
    "settings.mergedViewTarget": "Gjithashtu në degë u bashkuan",
    "settings.mergedViewBranchHint": "Komitetet e një dege të bashkuar tërhiqen vetëm në korsinë e tyre; bashkimi shfaqet si një lidhës. Pamja klasike.",
    "settings.mergedViewTargetHint": "Kryerjet e bashkimit të sjella vizatohen gjithashtu në korsinë e degës marrëse, të grumbulluara nën komitetin e bashkimit dhe në nivel me origjinalet - sepse ajo degë me të vërtetë i përmban ato tani. Kopjet janë të zbehta dhe të drejtuara me degën ku janë shkruar, kështu që mbetet e qartë se ato nuk janë bërë aty. Shkrirjet e kungujve nuk ndikohen: një kungull shkruan një commit të zakonshëm, kështu që vetëm ai commit shfaqet ndonjëherë.",
    "legend.title": "Legjenda",
    "legend.head": "KREU / dega aktuale",
    "legend.local": "Dega lokale",
    "legend.remote": "Dega në distancë",
    "legend.remoteOnly": "Vetëm në re (jo të tërhequr)",
    "legend.tag": "Etiketa (versioni)",
    "legend.commit": "Angazhohen",
    "legend.stash": "Stash (punë në raft)",
    "legend.mergedIn": "Erdhi me një bashkim (të shkruar në një degë tjetër)",
    "legend.nodes": "Kutitë",
    "legend.lines": "Linjat",
    "legend.edgeParent": "Kryerja e mëparshme në të njëjtën linjë",
    "legend.edgeMerge": "Dega e bashkuar - shigjeta tregon në kryerjen e bashkimit",
    "legend.edgeBranch": "Një degë u shkëput këtu",
    "legend.edgeStash": "Stash dhe angazhimi në të cilin ishte vendosur",
    "legend.edgeMergedChain": "Linja e kësaj dege kalon nëpër komitetet e bashkuara",
    "legend.edgeMergedTie": "I njëjti zotim në degën ku ishte shkruar",
    "menu.jumpHead": "⌖ Shko te arka",
    "menu.resetView": "⤢ Rivendos pamjen",
    "menu.createBranch": "Krijo degë nga këtu…",
    "menu.checkout": "Kontrolloni këtë angazhim",
    "menu.copySha": "Kopjo commit SHA",
    "menu.pushBranch": "Push dega \"{name}\"",
    "menu.renameCommit": "Riemërto mesazhin e kryerjes…",
    "menu.undoCommit": "Zhbëj kryerjen (mbaj ndryshimet)…",
    "menu.viewChanges": "Shiko ndryshimet…",
    "menu.jumpToOriginal": "Shkoni te detyra origjinale",
    "changes.title": "Ndryshimet në {sha}",
    "changes.tabChanged": "Ndryshuar",
    "changes.tabAll": "Të gjithë skedarët",
    "changes.added": "Shtuar",
    "changes.modified": "I modifikuar",
    "changes.deleted": "Fshirë",
    "changes.renamed": "Riemërtuar",
    "changes.noChanges": "Ky angazhim nuk ndryshon asnjë skedar.",
    "changes.loading": "Po ngarkohet ndryshimi…",
    "changes.selectFile": "Zgjidhni një skedar për të parë ndryshimet e tij.",
    "changes.original": "Origjinale",
    "changes.changed": "Ky angazhohem",
    "changes.binary": "Skedar binar - nuk ka ndryshim teksti për t'u shfaqur.",
    "changes.tooLarge": "Skedari është shumë i madh për të ndryshuar.",
    "changes.renamedFrom": "Riemërtuar nga {path}",
    "changes.close": "Mbylle",
    "changes.prevChange": "Ndryshimi i mëparshëm",
    "changes.nextChange": "Ndryshimi i radhës",
    "changes.collapseAll": "Palos të gjitha dosjet",
    "changes.expandAll": "Zgjero të gjitha dosjet",
    "changes.searchPlaceholder": "Kërko skedarët…",
    "changes.noSearchResults": "Asnjë skedar nuk përputhet me kërkimin tuaj.",
    "changes.maximize": "Maksimizoni",
    "changes.restore": "Rivendos madhësinë",
    "find.open": "Kërko në këtë ndryshim (Ctrl+F)",
    "find.placeholder": "Gjeni në ndryshim…",
    "find.prev": "Ndeshja e mëparshme",
    "find.next": "Ndeshja e radhës",
    "find.close": "Mbyll kërkimin",
    "find.noResults": "Nuk ka rezultate",
    "commit.title": "Kryeni Ndryshime",
    "commit.close": "Mbylle",
    "commit.loading": "Leximi i pemës së punës…",
    "commit.noChanges": "Nuk ka ndryshime lokale për të kryer.",
    "commit.selectFile": "Zgjidhni një skedar për të parë ndryshimin e tij.",
    "commit.selectAll": "Zgjidh të gjitha",
    "commit.selectNone": "Pastro të gjitha",
    "commit.message": "Detyroni mesazh",
    "commit.messagePlaceholder": "Përshkruani ndryshimin",
    "commit.selectedCount": "Skedari(et) {count} i zgjedhur",
    "commit.review": "Rishikimi",
    "commit.reviewTitle": "Kontrolli përfundimtar përpara kryerjes",
    "commit.reviewSummary": "Kryeni skedar(s) {count} me mesazhin: {message}",
    "commit.back": "Mbrapa",
    "commit.commit": "Krijo angazhim lokal",
    "menu.stashApply": "Aplikoni fshehje",
    "menu.stashPop": "Nxjerr fshehje (apliko dhe hiq)",
    "menu.stashDrop": "Hidhni ruajtje…",
    "merge.title": "Bashkoni Degën",
    "merge.route": "Bashkimi në degën aktuale",
    "merge.source": "Nga (burimi)",
    "merge.target": "Në (aktual)",
    "merge.loading": "Po analizon bashkimin…",
    "merge.upToDate": "Tashmë i përditësuar - asgjë për t'u bashkuar.",
    "merge.fastForward": "Shpejt përpara - nuk nevojitet kryerja e bashkimit (shënoni \"Krijo një bashkim të bashkimit\" për ta detyruar atë).",
    "merge.summary": "Skedari(et) {files} u ndryshuan, konflikt(et) {conflicts}.",
    "merge.conflictsWarning": "Ky bashkim ka konflikte - prapë mund t'i bashkoni, pastaj t'i zgjidhni ato në redaktues.",
    "merge.noChanges": "Asnjë ndryshim i skedarit.",
    "merge.files": "Ndryshimet që rezultojnë",
    "merge.added": "Shtuar",
    "merge.modified": "I modifikuar",
    "merge.deleted": "Fshirë",
    "merge.conflict": "Konflikti",
    "merge.message": "Merge commit mesazh",
    "merge.messagePlaceholder": "Bashkoni 'burimin' e degës",
    "merge.messageFfHint": "Injoruar në një bashkim të shpejtë përpara (nuk krijohet asnjë detyrim).",
    "merge.noFastForward": "Krijoni një angazhim për bashkim (pa përcjellje të shpejtë)",
    "merge.noFastForwardHint": "Gjithmonë regjistroni një kryerje bashkimi, edhe kur dega mund të përparonte shpejt.",
    "merge.merge": "Bashkohen",
    "merge.cancel": "Anulo",
    "merge.previewError": "Nuk mund të llogaritet një pamje paraprake: {message}",
    "merge.squashBadge": "Kungull",
    "merge.squashNote": "Bashkimi i kungujve - çdo ndryshim zbret kur ONE kryen në \"{target}\"; Vetë angazhimet e \"{source}\" qëndrojnë jashtë historisë së tij. Kthehu përsëri te Cilësimet › Bashko.",
    "merge.messageSquash": "Detyroni mesazh",
    "merge.squashMessagePlaceholder": "Ndryshimet e shtypura nga 'burimi'",
    "newBranch.title": "Krijo një degë",
    "newBranch.startPoint": "Dega e re duke filluar nga {sha}",
    "newBranch.startPointOn": "në {refs}",
    "newBranch.location": "Vendndodhja",
    "newBranch.locationRoot": "(rrënjë)",
    "newBranch.expandAll": "Zgjero të gjitha",
    "newBranch.collapseAll": "Palos të gjitha",
    "newBranch.expandFolder": "Zgjero dosjen",
    "newBranch.collapseFolder": "Palos dosjen",
    "newBranch.name": "Emri i degës",
    "newBranch.namePlaceholder": "dega ime",
    "newBranch.fullName": "Emri i plotë: {name}",
    "newBranch.invalid": "Emër i pavlefshëm dege",
    "newBranch.exists": "Një degë me këtë emër ekziston tashmë",
    "newBranch.checkout": "Përfundoni degën pas krijimit",
    "newBranch.create": "Krijo",
    "newBranch.cancel": "Anulo",
    "status.loading": "Grafiku po ngarkohet…",
    "status.summary": "{repo}Duke treguar angazhimet e {commits}, refs {refs}",
    "status.branchCreated": "Krijoi degën \"{name}\" në {sha}",
    "status.error": "Gabim: {message}",
    "status.opFailed": "Operacioni dështoi",
    "status.fetching": "Po merr…",
    "status.pulling": "Duke tërhequr…",
    "status.pushing": "Duke shtyrë…",
    "status.committing": "Po krijohet angazhimi lokal…",
    "status.commitCreated": "Komiteti lokal i krijuar {sha}: {message}",
    "status.syncing": "Po sinkronizohet…",
    "status.noHead": "Nuk u gjet asnjë arkë aktuale në grafik.",
    "status.undoing": "Po zhbëj angazhimin…",
    "status.commitUndone": "Angazhimi i zhbërë - ndryshimet janë kthyer në pemën e punës.",
    "status.undoConflict": "Zhbëni konfliktet e goditjeve - zgjidhini ato në redaktues, pastaj vazhdoni.",
    "status.stashApplying": "Po aplikohet ruajtja…",
    "status.stashPopping": "Po shfaqet akumulimi…",
    "status.stashDropping": "Po hidhet fshehtësia…",
    "status.stashApplied": "U aplikua Stash.",
    "status.stashPopped": "Shpallja e fshehtë.",
    "status.stashDropped": "Stash ra.",
    "status.stashConflict": "Konfliktet e fshehjes - zgjidhini ato në redaktues.",
    "status.merging": "Duke u bashkuar…",
    "status.merged": "Bashkimi përfundoi.",
    "status.mergeConflict": "Merge ka konflikte - zgjidhini ato në redaktues, pastaj kryeni.",
    "search.title": "Kërko",
    "search.tooltip": "Komponimet e kërkimit",
    "search.placeholder": "Kërkimi kryen…",
    "search.previous": "Rezultati i mëparshëm",
    "search.next": "Rezultati i radhës",
    "search.noResults": "Nuk ka rezultate",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Modaliteti i filtrit",
    "search.highlight": "Modaliteti i nënvizimit",
    "search.filterMode": "Shfaqja e kërkimit",
    "search.filterModeFilter": "Filtro (fsheh që nuk përputhen)",
    "search.filterModeHighlight": "Thekso (shfaq të gjitha, shëno ndeshjet)",
    "search.filterModeHint": "Zgjidhni se si shfaqen rezultatet e kërkimit në grafik.",
    "search.visibility": "Kërko vendndodhjen",
    "search.visibilityToolbar": "Butoni i shiritit të veglave",
    "search.visibilityAlwaysVisible": "Bar gjithmonë i dukshëm",
    "search.visibilityHint": "Shfaq kërkimin si një ikonë të shiritit të veglave ose shiritin e vazhdueshëm në krye.",
    "footer.github": "GitHub",
  },
  sk: {
    "toolbar.refresh": "⟳ Obnoviť",
    "toolbar.fetch": "⤓ Načítať",
    "toolbar.pull": "⇩ Potiahnite",
    "toolbar.push": "⇧ Stlačiť",
    "toolbar.commit": "✓ Zaviazať sa",
    "toolbar.sync": "⇅ Synchronizovať",
    "toolbar.jumpHead": "⌖ Prejdite k pokladni",
    "toolbar.reset": "⤢ Obnoviť zobrazenie",
    "toolbar.settings": "⚙ Nastavenia",
    "details.header": "Podrobnosti o záväzku",
    "details.close": "Zavrieť",
    "details.sha": "SHA",
    "details.shortSha": "Krátke SHA",
    "details.message": "Správa",
    "details.author": "Autor",
    "details.date": "Dátum",
    "details.labels": "Štítky",
    "details.location": "Poloha",
    "details.currentHead": "HEAD — aktuálna pokladňa",
    "settings.title": "Nastavenia",
    "settings.language": "Jazyk",
    "settings.theme": "Téma",
    "settings.themeLight": "Svetlo",
    "settings.themeDark": "Tmavý",
    "settings.themeLightHint": "Svetlé pozadie, tmavý text.",
    "settings.themeDarkHint": "Tmavé pozadie, svetlý text.",
    "settings.mainBranch": "Hlavná pobočka",
    "settings.mainBranchAuto": "Automaticky",
    "settings.mainBranchSearch": "Hľadať pobočky…",
    "settings.mainBranchNoMatch": "Žiadne zodpovedajúce pobočky",
    "settings.display": "Štýl zobrazenia",
    "settings.displayModern": "Moderné",
    "settings.displayClassic": "klasické",
    "settings.displayModernHint": "Voľné plátno – posúvaním posúvajte, posúvaním zväčšujte.",
    "settings.displayClassicHint": "Opravené plátno – kmeň pripnutý vľavo, bez priblíženia, iba posúvanie (ako graf revízie SVN).",
    "settings.sectionGeneral": "generál",
    "settings.sectionGraph": "Graf",
    "settings.sectionChanges": "Zmení pohľad",
    "settings.diffMinimap": "Minimapa rozdielov",
    "settings.diffMinimapOn": "Zobrazené",
    "settings.diffMinimapOff": "Skryté",
    "settings.diffMinimapOnHint": "Zobrazte pruh s prehľadom v štýle VS Code vedľa rozdielu – posúvajte ho potiahnutím; vrátane značiek zmien.",
    "settings.diffMinimapOffHint": "Žiadny prehľadový pás – posúvajte rozdiel normálne.",
    "settings.commitReview": "Vykonať kontrolu",
    "settings.commitReviewOn": "Pred potvrdením sa opýtajte",
    "settings.commitReviewOff": "Zaviazať sa priamo",
    "settings.commitReviewOnHint": "Pred vytvorením lokálneho odovzdania ukážte konečný zoznam súborov a kontrolu správ.",
    "settings.commitReviewOffHint": "Tlačidlo Commit okamžite vytvorí lokálne potvrdenie.",
    "settings.svnBranchDialog": "Dialóg vetvy v štýle SVN",
    "settings.svnBranchDialogHint": "Pri vytváraní vetvy zobraziť výber stromu priečinkov (namiesto jednoduchej výzvy).",
    "settings.branchDialog": "Dialóg vetvy",
    "settings.branchDialogSvnTitle": "v štýle SVN",
    "settings.branchDialogNativeVscode": "Štýl VS kódu",
    "settings.branchDialogNativeVs": "Štýl Visual Studio",
    "settings.branchDialogNativeHint": "Vstavaná výzva vetvy vášho IDE.",
    "settings.close": "Zavrieť",
    "settings.done": "Hotovo",
    "settings.sectionAdvanced": "Pokročilé",
    "settings.gitSource": "Spustiteľný súbor Git",
    "settings.gitSourceBuiltin": "Vstavaný",
    "settings.gitSourceBuiltinHint": "Použite binárny súbor git zo vstavaného rozšírenia Git v IDE.",
    "settings.gitSourceCustom": "Vlastná cesta",
    "settings.gitPath": "Cesta ku git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Prehliadať…",
    "settings.jargon": "Podmienky Git",
    "settings.jargonTranslate": "Preložiť",
    "settings.jargonEnglish": "Ponechať v angličtine",
    "settings.jargonTranslateHint": "Preložte výrazy Git (pull, push, commit, branch…) do jazyka rozhrania.",
    "settings.jargonEnglishHint": "Ponechajte výrazy Git (pull, push, commit, branch…) v angličtine; preložiť všetko ostatné.",
    "settings.sectionMerge": "Zlúčiť",
    "settings.mergeMode": "Zlúčiť štýl",
    "settings.mergeModeMerge": "Normálne zlúčenie",
    "settings.mergeModeSquash": "Squash zlúčiť",
    "settings.mergeModeMergeHint": "Zlúči vetvu tak, ako je: jej odovzdania sa stanú súčasťou histórie aktuálnej vetvy, ku ktorej sa pripojí zlučovacie odovzdanie (alebo ak je to možné, rýchle posunutie dopredu).",
    "settings.mergeModeSquashHint": "Zbalí celú vetvu do jedného odovzdania na aktuálnej vetve (git merge --squash). Vlastné potvrdenia vetvy nikdy nevstupujú do histórie aktuálnej vetvy – vidíte tam len jedno potvrdenie. Samotná vetva je ponechaná nedotknutá, takže jej commity zostávajú viditeľné v jej vlastnom pruhu.",
    "settings.mergedView": "Zlúčené odovzdania v grafe",
    "settings.mergedViewBranch": "Iba na vlastnej vetve",
    "settings.mergedViewTarget": "Aj v pobočke boli zlúčené",
    "settings.mergedViewBranchHint": "Záväzky zlúčenej pobočky sa čerpajú iba v ich vlastnom pruhu; zlúčenie sa zobrazí ako konektor. Klasický pohľad.",
    "settings.mergedViewTargetHint": "Potvrdenia, ktoré prinesie zlúčenie, sú tiež nakreslené v pruhu prijímajúcej vetvy, naskladané pod odovzdaním zlúčenia a na úrovni s originálmi – pretože táto vetva ich teraz skutočne obsahuje. Kópie sú bledé a majú vetvu, na ktorej boli napísané, takže je jasné, že tam neboli. Zlúčenia squashu nie sú ovplyvnené: squash zapíše jedno obyčajné odovzdanie, takže sa vždy objaví iba toto odovzdanie.",
    "legend.title": "Legenda",
    "legend.head": "HEAD / súčasná pobočka",
    "legend.local": "Miestna pobočka",
    "legend.remote": "Vzdialená pobočka",
    "legend.remoteOnly": "Iba v cloude (nevytiahnuté)",
    "legend.tag": "Značka (verzia)",
    "legend.commit": "Zaviazať sa",
    "legend.stash": "Skrýša (odložená práca)",
    "legend.mergedIn": "Prišlo so zlúčením (napísané na inej vetve)",
    "legend.nodes": "Krabice",
    "legend.lines": "Čiary",
    "legend.edgeParent": "Predchádzajúce odovzdanie na rovnakom riadku",
    "legend.edgeMerge": "Zlúčená vetva — šípka ukazuje na odovzdanie zlúčenia",
    "legend.edgeBranch": "Tu sa rozvetvil konár",
    "legend.edgeStash": "Skrýša a záväzok, ktorý bol odložený",
    "legend.edgeMergedChain": "Linka tejto vetvy prechádzajúca zlúčenými potvrdeniami",
    "legend.edgeMergedTie": "Rovnaký commit na vetve, na ktorej bol napísaný",
    "menu.jumpHead": "⌖ Prejdite k pokladni",
    "menu.resetView": "⤢ Obnoviť zobrazenie",
    "menu.createBranch": "Vytvorte vetvu odtiaľto…",
    "menu.checkout": "Skontrolujte tento záväzok",
    "menu.copySha": "Kopírovať odovzdanie SHA",
    "menu.pushBranch": "Tlačná vetva \"{name}\"",
    "menu.renameCommit": "Premenovať správu potvrdenia…",
    "menu.undoCommit": "Zrušiť potvrdenie (ponechať zmeny)…",
    "menu.viewChanges": "Zobraziť zmeny…",
    "menu.jumpToOriginal": "Prejdite na pôvodný príkaz",
    "changes.title": "Zmeny v {sha}",
    "changes.tabChanged": "Zmenené",
    "changes.tabAll": "Všetky súbory",
    "changes.added": "Pridané",
    "changes.modified": "Upravené",
    "changes.deleted": "Odstránené",
    "changes.renamed": "Premenovaný",
    "changes.noChanges": "Toto potvrdenie nezmení žiadne súbory.",
    "changes.loading": "Načítava sa rozdiel…",
    "changes.selectFile": "Ak chcete zobraziť zmeny, vyberte súbor.",
    "changes.original": "Originál",
    "changes.changed": "Tento záväzok",
    "changes.binary": "Binárny súbor – žiadny textový rozdiel na zobrazenie.",
    "changes.tooLarge": "Súbor je príliš veľký na porovnanie.",
    "changes.renamedFrom": "Premenovaný z {path}",
    "changes.close": "Zavrieť",
    "changes.prevChange": "Predchádzajúca zmena",
    "changes.nextChange": "Ďalšia zmena",
    "changes.collapseAll": "Zbaliť všetky priečinky",
    "changes.expandAll": "Rozbaľte všetky priečinky",
    "changes.searchPlaceholder": "Hľadať súbory…",
    "changes.noSearchResults": "Vášmu vyhľadávaniu nezodpovedajú žiadne súbory.",
    "changes.maximize": "Maximalizovať",
    "changes.restore": "Obnoviť veľkosť",
    "find.open": "Hľadať v tomto rozdiele (Ctrl+F)",
    "find.placeholder": "Nájsť v rozdiele…",
    "find.prev": "Predchádzajúci zápas",
    "find.next": "Ďalší zápas",
    "find.close": "Zavrieť vyhľadávanie",
    "find.noResults": "Žiadne výsledky",
    "commit.title": "Potvrdiť zmeny",
    "commit.close": "Zavrieť",
    "commit.loading": "Číta sa pracovný strom…",
    "commit.noChanges": "Neexistujú žiadne miestne zmeny, ktoré je potrebné vykonať.",
    "commit.selectFile": "Vyberte súbor, aby ste videli jeho rozdiel.",
    "commit.selectAll": "Vybrať všetko",
    "commit.selectNone": "Vymazať všetko",
    "commit.message": "Potvrdiť správu",
    "commit.messagePlaceholder": "Popíšte zmenu",
    "commit.selectedCount": "Vybraté súbory {count}",
    "commit.review": "recenzia",
    "commit.reviewTitle": "Záverečná kontrola pred odovzdaním",
    "commit.reviewSummary": "Potvrdiť súbor(y) {count} so správou: {message}",
    "commit.back": "Späť",
    "commit.commit": "Vytvorte lokálny commit",
    "menu.stashApply": "Použiť skrýšu",
    "menu.stashPop": "Pop skrýš (použiť a odstrániť)",
    "menu.stashDrop": "Zahodiť skrýšu…",
    "merge.title": "Zlúčiť pobočku",
    "merge.route": "Zlúčenie do aktuálnej pobočky",
    "merge.source": "Od (zdroj)",
    "merge.target": "Do (aktuálne)",
    "merge.loading": "Prebieha analýza zlúčenia…",
    "merge.upToDate": "Už aktuálne — nie je čo zlúčiť.",
    "merge.fastForward": "Rýchly posun vpred — nie je potrebné žiadne odovzdanie zlúčenia (zaškrtnutím možnosti „Vytvoriť odovzdanie zlúčenia“ vynútite potvrdenie).",
    "merge.summary": "Súbor(y) {files} zmenené, konflikt(y) {conflicts}.",
    "merge.conflictsWarning": "Toto zlúčenie má konflikty – stále ich môžete zlúčiť a potom ich vyriešiť v editore.",
    "merge.noChanges": "Žiadne zmeny súboru.",
    "merge.files": "Výsledné zmeny",
    "merge.added": "Pridané",
    "merge.modified": "Upravené",
    "merge.deleted": "Odstránené",
    "merge.conflict": "Konflikt",
    "merge.message": "Zlúčiť správu potvrdenia",
    "merge.messagePlaceholder": "Zlúčiť vetvu „zdroj“",
    "merge.messageFfHint": "Ignorované pri rýchlom zlúčení (nevytvorí sa žiadne potvrdenie).",
    "merge.noFastForward": "Vytvorte odovzdanie zlúčenia (žiadne rýchle pretáčanie dopredu)",
    "merge.noFastForwardHint": "Vždy zaznamenajte odovzdanie zlúčenia, aj keď sa vetva môže rýchlo posúvať dopredu.",
    "merge.merge": "Zlúčiť",
    "merge.cancel": "Zrušiť",
    "merge.previewError": "Nepodarilo sa vypočítať ukážku: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge — každá zmena pristane ako JEDNO odovzdanie na „{target}“; Vlastné odovzdania „{source}“ zostávajú mimo jeho histórie. Prepnite späť v časti Nastavenia › Zlúčiť.",
    "merge.messageSquash": "Potvrdiť správu",
    "merge.squashMessagePlaceholder": "Stlačené zmeny zo zdroja",
    "newBranch.title": "Vytvoriť pobočku",
    "newBranch.startPoint": "Nová vetva začínajúca od {sha}",
    "newBranch.startPointOn": "na {refs}",
    "newBranch.location": "Poloha",
    "newBranch.locationRoot": "(koreň)",
    "newBranch.expandAll": "Rozbaliť všetko",
    "newBranch.collapseAll": "Zbaliť všetko",
    "newBranch.expandFolder": "Rozbaliť priečinok",
    "newBranch.collapseFolder": "Zbaliť priečinok",
    "newBranch.name": "Názov pobočky",
    "newBranch.namePlaceholder": "moja pobočka",
    "newBranch.fullName": "Celé meno: {name}",
    "newBranch.invalid": "Neplatný názov pobočky",
    "newBranch.exists": "Pobočka s týmto názvom už existuje",
    "newBranch.checkout": "Po vytvorení pobočky pokladne",
    "newBranch.create": "Vytvorte",
    "newBranch.cancel": "Zrušiť",
    "status.loading": "Načítava sa graf…",
    "status.summary": "{repo}Zobrazuje {commits} potvrdenia, {refs} ref",
    "status.branchCreated": "Vytvorená pobočka \"{name}\" na {sha}",
    "status.error": "Chyba: {message}",
    "status.opFailed": "Operácia zlyhala",
    "status.fetching": "Načítava sa…",
    "status.pulling": "Ťahanie…",
    "status.pushing": "Tlačí sa…",
    "status.committing": "Vytvára sa lokálny príkaz…",
    "status.commitCreated": "Vytvorené lokálne potvrdenie {sha}: {message}",
    "status.syncing": "Synchronizuje sa…",
    "status.noHead": "V grafe sa nenašla žiadna aktuálna platba.",
    "status.undoing": "Ruší sa záväzok…",
    "status.commitUndone": "Potvrdenie zrušené – zmeny sú späť v pracovnom strome.",
    "status.undoConflict": "Vráťte späť konflikty prístupov – vyriešte ich v editore a potom pokračujte.",
    "status.stashApplying": "Uplatňuje sa skrýša…",
    "status.stashPopping": "Praskajúca skrýša…",
    "status.stashDropping": "Zahadzovanie skrýš…",
    "status.stashApplied": "Použitá skrýša.",
    "status.stashPopped": "Stash vyskočil.",
    "status.stashDropped": "Stash klesol.",
    "status.stashConflict": "Konflikty ukladania — vyriešte ich v editore.",
    "status.merging": "Zlučovanie…",
    "status.merged": "Zlúčenie dokončené.",
    "status.mergeConflict": "Zlúčenie má konflikty – vyriešte ich v editore a potom potvrďte.",
    "search.title": "Hľadať",
    "search.tooltip": "Vyhľadávanie sa zaväzuje",
    "search.placeholder": "Vyhľadávanie sa zaväzuje…",
    "search.previous": "Predchádzajúci výsledok",
    "search.next": "Ďalší výsledok",
    "search.noResults": "Žiadne výsledky",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Režim filtra",
    "search.highlight": "Režim zvýraznenia",
    "search.filterMode": "Vyhľadať zobrazenie",
    "search.filterModeFilter": "Filtrovať (skryť nezhody)",
    "search.filterModeHighlight": "Zvýrazniť (zobraziť všetko, označiť zhody)",
    "search.filterModeHint": "Vyberte spôsob zobrazenia výsledkov vyhľadávania v grafe.",
    "search.visibility": "Vyhľadajte miesto",
    "search.visibilityToolbar": "Tlačidlo na paneli s nástrojmi",
    "search.visibilityAlwaysVisible": "Vždy viditeľná lišta",
    "search.visibilityHint": "Zobraziť vyhľadávanie ako ikonu panela s nástrojmi alebo trvalý panel v hornej časti.",
    "footer.github": "GitHub",
  },
  sl: {
    "toolbar.refresh": "⟳ Osveži",
    "toolbar.fetch": "⤓ Prinesi",
    "toolbar.pull": "⇩ Potegnite",
    "toolbar.push": "⇧ Potisnite",
    "toolbar.commit": "✓ Zavežite se",
    "toolbar.sync": "⇅ Sinhronizacija",
    "toolbar.jumpHead": "⌖ Pojdite na blagajno",
    "toolbar.reset": "⤢ Ponastavi pogled",
    "toolbar.settings": "⚙ Nastavitve",
    "details.header": "Podrobnosti potrditve",
    "details.close": "Zapri",
    "details.sha": "SHA",
    "details.shortSha": "Kratek SHA",
    "details.message": "Sporočilo",
    "details.author": "Avtor",
    "details.date": "Datum",
    "details.labels": "Oznake",
    "details.location": "Lokacija",
    "details.currentHead": "HEAD — trenutna blagajna",
    "settings.title": "nastavitve",
    "settings.language": "Jezik",
    "settings.theme": "Tema",
    "settings.themeLight": "Svetloba",
    "settings.themeDark": "Temno",
    "settings.themeLightHint": "Svetlo ozadje, temno besedilo.",
    "settings.themeDarkHint": "Temno ozadje, svetlo besedilo.",
    "settings.mainBranch": "Glavna veja",
    "settings.mainBranchAuto": "Samodejno",
    "settings.mainBranchSearch": "Iskanje poslovalnic…",
    "settings.mainBranchNoMatch": "Ni ustreznih vej",
    "settings.display": "Prikazni slog",
    "settings.displayModern": "Moderno",
    "settings.displayClassic": "Klasična",
    "settings.displayModernHint": "Brezplačno platno — povlecite za premikanje, pomaknite se za povečavo.",
    "settings.displayClassicHint": "Popravljeno platno — deblo pripeto levo, brez povečave, samo pomikanje (kot graf revizij SVN).",
    "settings.sectionGeneral": "Splošno",
    "settings.sectionGraph": "Graf",
    "settings.sectionChanges": "Spremeni pogled",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "Prikazano",
    "settings.diffMinimapOff": "Skrito",
    "settings.diffMinimapOnHint": "Pokaži pregledni trak v slogu kode VS poleg razlike — povlecite ga za pomikanje; vključeni označevalci sprememb.",
    "settings.diffMinimapOffHint": "Brez traku s pregledom — običajno se pomikajte po razlikah.",
    "settings.commitReview": "Zavrni pregled",
    "settings.commitReviewOn": "Vprašaj, preden se zavežeš",
    "settings.commitReviewOff": "Zavežite se neposredno",
    "settings.commitReviewOnHint": "Prikažite končni seznam datotek in preverite sporočilo, preden ustvarite lokalno objavo.",
    "settings.commitReviewOffHint": "Gumb Potrdi takoj ustvari lokalno potrditev.",
    "settings.svnBranchDialog": "Pogovorno okno veje v slogu SVN",
    "settings.svnBranchDialogHint": "Pokaži izbirnik drevesa map, ko ustvarjaš vejo (namesto preprostega poziva).",
    "settings.branchDialog": "Pogovorno okno veje",
    "settings.branchDialogSvnTitle": "SVN slog",
    "settings.branchDialogNativeVscode": "Slog kode VS",
    "settings.branchDialogNativeVs": "Slog Visual Studio",
    "settings.branchDialogNativeHint": "Poziv za vejo, vgrajen v vaš IDE.",
    "settings.close": "Zapri",
    "settings.done": "Končano",
    "settings.sectionAdvanced": "Napredno",
    "settings.gitSource": "Izvršljiva datoteka Git",
    "settings.gitSourceBuiltin": "Vgradna",
    "settings.gitSourceBuiltinHint": "Uporabite dvojiško datoteko git iz razširitve Git, vgrajene v IDE.",
    "settings.gitSourceCustom": "Pot po meri",
    "settings.gitPath": "Pot do git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Prebrskaj ...",
    "settings.jargon": "Pogoji Git",
    "settings.jargonTranslate": "Prevajaj",
    "settings.jargonEnglish": "Ohrani v angleščini",
    "settings.jargonTranslateHint": "Prevedite izraze Git (vlečenje, potiskanje, potrditev, razvejanje ...) v jezik vmesnika.",
    "settings.jargonEnglishHint": "Ohranite izraze Git (vlečenje, potiskanje, potrditev, veja ...) v angleščini; prevesti vse ostalo.",
    "settings.sectionMerge": "Spoji",
    "settings.mergeMode": "Slog spajanja",
    "settings.mergeModeMerge": "Običajno spajanje",
    "settings.mergeModeSquash": "Squash spajanje",
    "settings.mergeModeMergeHint": "Spoji vejo, kakršna je: njene objave postanejo del trenutne zgodovine veje, združene z združitveno potrditvijo (ali hitro previjanje naprej, kadar je to mogoče).",
    "settings.mergeModeSquashHint": "Strne celotno vejo v eno objavo na trenutni veji (git merge --squash). Lastne objave veje nikoli ne vstopijo v zgodovino trenutne veje — tam vidite samo eno objavo. Sama veja ostane nedotaknjena, tako da njene objave ostanejo vidne na svojem pasu.",
    "settings.mergedView": "Združene objave v grafu",
    "settings.mergedViewBranch": "Samo na lastni veji",
    "settings.mergedViewTarget": "Tudi v panogi, v katero so bili združeni",
    "settings.mergedViewBranchHint": "Povezave združene veje so narisane samo na njihovem lastnem pasu; spajanje se prikaže kot konektor. Klasičen pogled.",
    "settings.mergedViewTargetHint": "Objave, ki jih prinese spajanje, so prav tako narisane v prejemnem pasu, zložene pod odobritvijo spajanja in poravnane z izvirniki — ker jih ta veja zdaj res vsebuje. Kopije so blede in usmerjene proti veji, na kateri so bile napisane, tako da ostane jasno, da niso nastale tam. Na združevanja squasha to ne vpliva: squash napiše eno navadno potrditev, tako da se le ta potrditev vedno pojavi.",
    "legend.title": "Legenda",
    "legend.head": "GLAVA / trenutna veja",
    "legend.local": "Lokalna podružnica",
    "legend.remote": "Oddaljena podružnica",
    "legend.remoteOnly": "Samo v oblaku (ni potegnjeno)",
    "legend.tag": "Oznaka (različica)",
    "legend.commit": "Zaveži se",
    "legend.stash": "Zaloga (odloženo delo)",
    "legend.mergedIn": "Prišlo je z združitvijo (napisano na drugi veji)",
    "legend.nodes": "Škatle",
    "legend.lines": "Črte",
    "legend.edgeParent": "Prejšnja potrditev v isti vrstici",
    "legend.edgeMerge": "Spojena veja — puščica kaže na potrditev spajanja",
    "legend.edgeBranch": "Tu se je odcepila veja",
    "legend.edgeStash": "Stash in potrditev, na kateri je bil odložen",
    "legend.edgeMergedChain": "Vrstica te veje poteka skozi združene objave",
    "legend.edgeMergedTie": "Ista potrditev na veji, na kateri je bila zapisana",
    "menu.jumpHead": "⌖ Pojdite na blagajno",
    "menu.resetView": "⤢ Ponastavi pogled",
    "menu.createBranch": "Ustvari vejo od tukaj ...",
    "menu.checkout": "Preverite to obvezo",
    "menu.copySha": "Kopiraj potrditev SHA",
    "menu.pushBranch": "Potisna veja \"{name}\"",
    "menu.renameCommit": "Preimenuj sporočilo o potrditvi ...",
    "menu.undoCommit": "Razveljavi potrditev (ohrani spremembe) …",
    "menu.viewChanges": "Ogled sprememb ...",
    "menu.mergeBranch": "Spoji \"{source}\" v \"{target}\"...",
    "menu.jumpToOriginal": "Pojdite na prvotno objavo",
    "changes.title": "Spremembe v {sha}",
    "changes.tabChanged": "Spremenjeno",
    "changes.tabAll": "Vse datoteke",
    "changes.added": "Dodano",
    "changes.modified": "Spremenjeno",
    "changes.deleted": "Izbrisano",
    "changes.renamed": "Preimenovano",
    "changes.noChanges": "Ta potrditev ne spremeni nobene datoteke.",
    "changes.loading": "Nalaganje razlike ...",
    "changes.selectFile": "Izberite datoteko, da vidite njene spremembe.",
    "changes.original": "Original",
    "changes.changed": "Ta zaveza",
    "changes.binary": "Binarna datoteka — ni besedilne razlike za prikaz.",
    "changes.tooLarge": "Datoteka je prevelika za razlikovanje.",
    "changes.renamedFrom": "Preimenovano iz {path}",
    "changes.close": "Zapri",
    "changes.prevChange": "Prejšnja sprememba",
    "changes.nextChange": "Naslednja sprememba",
    "changes.collapseAll": "Strni vse mape",
    "changes.expandAll": "Razširi vse mape",
    "changes.searchPlaceholder": "Iskanje datotek ...",
    "changes.noSearchResults": "Nobena datoteka ne ustreza vašemu iskanju.",
    "changes.maximize": "Povečaj",
    "changes.restore": "Obnovi velikost",
    "find.open": "Išči v tej razliki (Ctrl+F)",
    "find.placeholder": "Najdi v razl.",
    "find.prev": "Prejšnja tekma",
    "find.next": "Naslednja tekma",
    "find.close": "Zapri iskanje",
    "find.noResults": "Brez rezultatov",
    "commit.title": "Potrdi spremembe",
    "commit.close": "Zapri",
    "commit.loading": "Branje delovnega drevesa …",
    "commit.noChanges": "Ni lokalnih sprememb za objavo.",
    "commit.selectFile": "Izberite datoteko, da si ogledate njene razlike.",
    "commit.selectAll": "Izberi vse",
    "commit.selectNone": "Počisti vse",
    "commit.message": "Objavi sporočilo",
    "commit.messagePlaceholder": "Opišite spremembo",
    "commit.selectedCount": "Izbrane datoteke {count}",
    "commit.review": "Pregled",
    "commit.reviewTitle": "Končno preverjanje pred izvršitvijo",
    "commit.reviewSummary": "Potrdite datoteko(e) {count} s sporočilom: {message}",
    "commit.back": "Nazaj",
    "commit.commit": "Ustvari lokalno objavo",
    "menu.stashApply": "Uporabi zalogo",
    "menu.stashPop": "Pop stash (uporabi in odstrani)",
    "menu.stashDrop": "Spustite zalogo …",
    "merge.title": "Spoji vejo",
    "merge.route": "Združitev v trenutno vejo",
    "merge.source": "Od (vir)",
    "merge.target": "V (trenutno)",
    "merge.loading": "Analiziranje spajanja ...",
    "merge.upToDate": "Že posodobljeno — nič za združitev.",
    "merge.fastForward": "Hitro previjanje naprej — potrditev združevanja ni potrebna (označite »Ustvari potrditev združitve«, da jo vsilite).",
    "merge.summary": "Datoteke {files} spremenjene, konflikt(i) {conflicts}.",
    "merge.conflictsWarning": "Ta spojina ima spore — še vedno lahko spajate in jih nato razrešite v urejevalniku.",
    "merge.noChanges": "Brez sprememb datoteke.",
    "merge.files": "Posledične spremembe",
    "merge.added": "Dodano",
    "merge.modified": "Spremenjeno",
    "merge.deleted": "Izbrisano",
    "merge.conflict": "Konflikt",
    "merge.message": "Sporočilo o objavi združitve",
    "merge.messagePlaceholder": "Spoji vejo 'vir'",
    "merge.messageFfHint": "Prezrto pri hitrem spajanju naprej (potrditev ni ustvarjena).",
    "merge.noFastForward": "Ustvari potrditev združevanja (brez previjanja naprej)",
    "merge.noFastForwardHint": "Vedno zabeležite potrditev združevanja, tudi če bi lahko veja hitro previjala naprej.",
    "merge.merge": "Spoji",
    "merge.cancel": "Prekliči",
    "merge.previewError": "Ni bilo mogoče izračunati predogleda: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Squash merge — vsaka sprememba pristane kot ENA potrditev na »{target}«; Lastne zaveze »{source}« ostanejo zunaj njegove zgodovine. Preklopite nazaj v Nastavitve › Združi.",
    "merge.messageSquash": "Objavi sporočilo",
    "merge.squashMessagePlaceholder": "Strnjene spremembe iz 'vira'",
    "newBranch.title": "Ustvari podružnico",
    "newBranch.startPoint": "Nova veja, ki se začne od {sha}",
    "newBranch.startPointOn": "na {refs}",
    "newBranch.location": "Lokacija",
    "newBranch.locationRoot": "(koren)",
    "newBranch.expandAll": "Razširi vse",
    "newBranch.collapseAll": "Strni vse",
    "newBranch.expandFolder": "Razširi mapo",
    "newBranch.collapseFolder": "Strni mapo",
    "newBranch.name": "Ime podružnice",
    "newBranch.namePlaceholder": "moja veja",
    "newBranch.fullName": "Polno ime: {name}",
    "newBranch.invalid": "Neveljavno ime podružnice",
    "newBranch.exists": "Podružnica s tem imenom že obstaja",
    "newBranch.checkout": "Odjavi podružnico po ustvarjanju",
    "newBranch.create": "Ustvari",
    "newBranch.cancel": "Prekliči",
    "status.loading": "Nalaganje grafa ...",
    "status.summary": "{repo} Prikazuje {commits} commits, {refs} ref",
    "status.branchCreated": "Ustvarjena veja \"{name}\" na {sha}",
    "status.error": "Napaka: {message}",
    "status.opFailed": "Operacija ni uspela",
    "status.fetching": "Pridobivanje ...",
    "status.pulling": "Vlečenje ...",
    "status.pushing": "Potiskanje ...",
    "status.committing": "Ustvarjanje lokalne objave …",
    "status.commitCreated": "Ustvarjena lokalna potrditev {sha}: {message}",
    "status.syncing": "Sinhronizacija ...",
    "status.noHead": "V grafu ni bilo mogoče najti trenutnega plačila.",
    "status.undoing": "Razveljavitev potrditve ...",
    "status.commitUndone": "Potrditev razveljavljena — spremembe so spet v delovnem drevesu.",
    "status.undoConflict": "Razveljavi konflikte zadetkov — razreši jih v urejevalniku in nato nadaljuj.",
    "status.stashApplying": "Uporaba zaloge …",
    "status.stashPopping": "Izpiranje zaloge …",
    "status.stashDropping": "Spuščanje blaga ...",
    "status.stashApplied": "Stash je uporabljen.",
    "status.stashPopped": "Stash je počil.",
    "status.stashDropped": "Stash je padel.",
    "status.stashConflict": "Konflikti med shranjevanjem — rešite jih v urejevalniku.",
    "status.merging": "Združevanje ...",
    "status.merged": "Spajanje končano.",
    "status.mergeConflict": "Spajanje ima konflikte — razrešite jih v urejevalniku in nato potrdite.",
    "search.title": "Iskanje",
    "search.tooltip": "Iskalne obveznosti",
    "search.placeholder": "Iskanje zavezuje ...",
    "search.previous": "Prejšnji rezultat",
    "search.next": "Naslednji rezultat",
    "search.noResults": "Brez rezultatov",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Način filtra",
    "search.highlight": "Način osvetlitve",
    "search.filterMode": "Prikaz iskanja",
    "search.filterModeFilter": "Filter (skrij neujemajoče se)",
    "search.filterModeHighlight": "Označi (prikaži vse, označi ujemanja)",
    "search.filterModeHint": "Izberite, kako so rezultati iskanja prikazani v grafu.",
    "search.visibility": "Lokacija iskanja",
    "search.visibilityToolbar": "Gumb orodne vrstice",
    "search.visibilityAlwaysVisible": "Vedno vidna vrstica",
    "search.visibilityHint": "Pokaži iskanje kot ikono orodne vrstice ali trajne vrstice na vrhu.",
    "footer.github": "GitHub",
  },
  so: {
    "toolbar.refresh": "⟳ Cusbooneysii",
    "toolbar.fetch": "Soo qaado",
    "toolbar.pull": "⇩ Jiid",
    "toolbar.push": "⇧ Riix",
    "toolbar.commit": "Go'aan qaadasho",
    "toolbar.sync": "⇅ Isku xidh",
    "toolbar.jumpHead": "⌖ U bax si aad u hubiso",
    "toolbar.reset": "⤢ Dib u habeynta muuqaalka",
    "toolbar.settings": "⚙ Settings",
    "details.header": "Bixi Faahfaahin",
    "details.close": "Xir",
    "details.sha": "SHA",
    "details.shortSha": "Gaaban SHA",
    "details.message": "Fariinta",
    "details.author": "Qore",
    "details.date": "Taariikhda",
    "details.labels": "Calaamadaha",
    "details.location": "Goobta",
    "details.currentHead": "MADAX — hubinta hadda",
    "settings.title": "Dejinta",
    "settings.language": "Luuqad",
    "settings.theme": "Dulucda",
    "settings.themeLight": "Iftiin",
    "settings.themeDark": "Madow",
    "settings.themeLightHint": "Sooyaal iftiin leh, qoraal madow.",
    "settings.themeDarkHint": "Sooyaal madow, qoraal khafiif ah.",
    "settings.mainBranch": "Laanta ugu weyn",
    "settings.mainBranchAuto": "Toos ah",
    "settings.mainBranchSearch": "Raadi laamaha...",
    "settings.mainBranchNoMatch": "Ma jiro laamo u dhigma",
    "settings.display": "Qaabka muuqaalka",
    "settings.displayModern": "Casri ah",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Sharaca bilaashka ah - u jiid digsiga, u rog si aad u dhaweyso.",
    "settings.displayClassicHint": "Sharaca go'an - jirridda bidix ku dheggan, wax-soo-dhoweyn ma leh, duuduub kaliya (sida garaafka dib-u-eegista SVN).",
    "settings.sectionGeneral": "Guud ahaan",
    "settings.sectionGraph": "garaafka",
    "settings.sectionChanges": "Beddela aragtida",
    "settings.diffMinimap": "Khariidad yar oo kala duwan",
    "settings.diffMinimapOn": "Muujiyay",
    "settings.diffMinimapOff": "Qarsoon",
    "settings.diffMinimapOnHint": "Tus duleelka dulmarka qaabka VS Code-ka agtiisa kala duwanaanshiyaha - u jiid si aad u rogto; calaamadaha isbeddelka ayaa lagu daray.",
    "settings.diffMinimapOffHint": "Ma jiro xariijin dulmar ah - si caadi ah ugu rog farqiga.",
    "settings.commitReview": "Ballanqaadka dib u eegista",
    "settings.commitReviewOn": "Weydii intaadan ballan qaadin",
    "settings.commitReviewOff": "Si toos ah u go'aan",
    "settings.commitReviewOnHint": "Tus liiska faylka ugu dambeeya iyo hubinta fariinta ka hor intaadan abuurin ballan-qaadka deegaanka.",
    "settings.commitReviewOffHint": "Badhanka Ballanqaadku wuxuu isla markiiba abuuraa ballan qaadka deegaanka.",
    "settings.svnBranchDialog": "wada hadalka laanta qaabka SVN",
    "settings.svnBranchDialogHint": "Tus galka-geedka-qaade marka la abuurayo laan (halkii degdeg fudud).",
    "settings.branchDialog": "wada hadalka laanta",
    "settings.branchDialogSvnTitle": "Qaabka SVN",
    "settings.branchDialogNativeVscode": "Habka VS Code",
    "settings.branchDialogNativeVs": "Qaabka Visual Studio",
    "settings.branchDialogNativeHint": "Laanta IDE-ga lagu dhex dhisay.",
    "settings.close": "Xir",
    "settings.done": "La sameeyay",
    "settings.sectionAdvanced": "Sareeye",
    "settings.gitSource": "Git la fulin karo",
    "settings.gitSourceBuiltin": "Lagu dhex dhisay",
    "settings.gitSourceBuiltinHint": "Ka isticmaal git binary-ga IDE-ga ku dhex-dhisan ee Git-ga.",
    "settings.gitSourceCustom": "Jidka gaarka ah",
    "settings.gitPath": "Waddada loo maro",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "baadh…",
    "settings.jargon": "Shuruudaha Git",
    "settings.jargonTranslate": "Turjun",
    "settings.jargonEnglish": "Ku hay Ingiriis",
    "settings.jargonTranslateHint": "U tarjun ereyada Git (jiid, riix, go'aan, laan…) una beddel luqadda is dhexgalka.",
    "settings.jargonEnglishHint": "Hayso shuruudaha Git (jiid, riix, ballan, laan…) Ingiriisi; wax walba oo kale turjun.",
    "settings.sectionMerge": "Isku dar",
    "settings.mergeMode": "Qaabka isku dar",
    "settings.mergeModeMerge": "Isku darka caadiga ah",
    "settings.mergeModeSquash": "Isku darka Squash",
    "settings.mergeModeMergeHint": "Isku-dubbarid laanta sida ay tahay: hawl-qabadkeedu waxa ay noqonayaan qayb ka mid ah taariikhda laanta hadda, oo ay ku biirto ballan-qaadid (ama si degdeg ah loo gudbiyo marka ay suurtogal tahay).",
    "settings.mergeModeSquashHint": "Waxay u burburisaa laanta oo dhan hal ballan oo laanta hadda jirta (git merge --squash). Waxqabadyada laanta waligeed ma gelin taariikhda laanta hadda - waxaad arkaysaa mid keliya oo halkaas ku hawlan. Laanta lafteedu lama taabtaan, sidaa awgeed waxa ay ku kacday waa mid ka muuqda haadkeeda.",
    "settings.mergedView": "Isku dhafka ah ee garaafyada",
    "settings.mergedViewBranch": "Kaliya laantooda",
    "settings.mergedViewTarget": "Sidoo kale laanta iyaga ayaa la isku daray",
    "settings.mergedViewBranchHint": "Ballanqaadyada laanta la isku daray waxaa lagu sawiray oo keliya haadkooda; isku darka waxay u muuqataa isku xire ahaan. Aragtida caadiga ah.",
    "settings.mergedViewTargetHint": "Ballanqaadyada isku-dhafka ah ee la keenay waxaa lagu sawiraa haadka laanta helaya sidoo kale, lagu hoos dhejiyay ballan-qaadka isku-dhafka iyo heerka asalka - sababtoo ah laantaas runtii hadda way ka kooban tahay. Nuqullada ayaa cirro leh oo la socda laantii ay ku qornaayeen, markaa way caddahay inaan halkaas lagu samayn. Isku-darka Squash ma saameynayaan: squash wuxuu qoraa hal ballan oo caadi ah, sidaas darteed kaligaa ayaa waligeed soo muuqda.",
    "legend.title": "Halyeyga",
    "legend.head": "MADAX/ laan hadda jirta",
    "legend.local": "Laanta deegaanka",
    "legend.remote": "Laanta fog",
    "legend.remoteOnly": "Kaliya daruurta dhexdeeda (aan la jiidin)",
    "legend.tag": "Tag (nooca)",
    "legend.commit": "Ballanqaad",
    "legend.stash": "Istaash (shaqo aan go' lahayn)",
    "legend.mergedIn": "Waxa uu la yimid isku-dar (laan kale ku qoran)",
    "legend.nodes": "Sanduuqyada",
    "legend.lines": "Khadadka",
    "legend.edgeParent": "Horey u galeen isla xariiq isku mid ah",
    "legend.edgeMerge": "Laan-isku-dhafan - falaarta ayaa tilmaamaysa ballan-qaadka isku-dhafka",
    "legend.edgeBranch": "Laan ayaa fargeeto ka baxday halkan",
    "legend.edgeStash": "Is-daba-marin iyo ballan-qaadkii waa la dajiyay",
    "legend.edgeMergedChain": "Xariiqda laantan ee dhex marta ballan-qaadyada la isku daray",
    "legend.edgeMergedTie": "Isla sidaas oo kale ayaa laanta lagu qoray",
    "menu.jumpHead": "⌖ U bax si aad u hubiso",
    "menu.resetView": "⤢ Dib u habeynta muuqaalka",
    "menu.createBranch": "Laan ka samee halkan...",
    "menu.checkout": "Hubi ballan-qaadkan",
    "menu.copySha": "Nuqul ka samee SHA",
    "menu.pushBranch": "Riix laanta \"{name}\"",
    "menu.renameCommit": "Dib u magacaw fariinta ballanta…",
    "menu.undoCommit": "Ka noqo ballanta (isbedelka wad)...",
    "menu.viewChanges": "Daawo isbedelada…",
    "menu.mergeBranch": "Ku biir \"{source}\" una gal \"{target}\"â",
    "menu.jumpToOriginal": "Tag ballan-qaadkii asalka ahaa",
    "changes.title": "Isbeddellada {sha}",
    "changes.tabChanged": "Bedelay",
    "changes.tabAll": "Dhammaan Faylasha",
    "changes.added": "Lagu daray",
    "changes.modified": "wax laga beddelay",
    "changes.deleted": "La tirtiray",
    "changes.renamed": "La magacaabay",
    "changes.noChanges": "Ballanqaadkani waxba ma beddelo.",
    "changes.loading": "Soodejinaya kala duwanaanshiyaha…",
    "changes.selectFile": "Dooro fayl si aad u aragto isbedelkiisa.",
    "changes.original": "Asalka",
    "changes.changed": "Ballanqaadkan",
    "changes.binary": "Faylka binary-ma jiro farqi u dhexeeya si loo muujiyo.",
    "changes.tooLarge": "Faylku aad buu u weyn yahay oo lagu kala duwanaan karo",
    "changes.renamedFrom": "Waxaa laga bedelay {path}",
    "changes.close": "Xir",
    "changes.prevChange": "Isbeddel hore",
    "changes.nextChange": "Isbeddelka xiga",
    "changes.collapseAll": "Burbur dhammaan faylalka",
    "changes.expandAll": "Balaadhi dhammaan faylalka",
    "changes.searchPlaceholder": "Raadi faylasha…",
    "changes.noSearchResults": "Ma jiro fayl ku habboon raadintaada.",
    "changes.maximize": "Kordhi",
    "changes.restore": "Soo celi cabbirka",
    "find.open": "Ka raadi kala duwanaanshahan (Ctrl+F)",
    "find.placeholder": "Ka hel farqiga…",
    "find.prev": "Ciyaartii hore",
    "find.next": "Ciyaarta xigta",
    "find.close": "Xir raadinta",
    "find.noResults": "Natiijooyin ma jiraan",
    "commit.title": "Samee isbedel",
    "commit.close": "Xir",
    "commit.loading": "Akhriska geedka shaqeeya…",
    "commit.noChanges": "Ma jiraan isbeddello maxalli ah oo la sameeyo.",
    "commit.selectFile": "Dooro fayl si aad u aragto kala duwanaanshihiisa.",
    "commit.selectAll": "Dhammaan dooro",
    "commit.selectNone": "Nadiifi dhammaan",
    "commit.message": "Gudbi fariin",
    "commit.messagePlaceholder": "Qeex isbedelka",
    "commit.selectedCount": "{count} fayl(yada) la doortay",
    "commit.review": "Dib u eegis",
    "commit.reviewTitle": "Hubinta kama dambaysta ah ka hor intaanad ballan qaadin",
    "commit.reviewSummary": "Samee faylka {count} oo wata fariin: {message}",
    "commit.back": "Dib u noqo",
    "commit.commit": "Samee ballanqaad maxalli ah",
    "menu.stashApply": "Codso qashinka",
    "menu.stashPop": "Booska xajinta (codso oo saar)",
    "menu.stashDrop": "Tuur qashinka...",
    "merge.title": "Isku-darka Laanta",
    "merge.route": "Ku biiritaanka laanta hadda",
    "merge.source": "Laga soo bilaabo (ilo)",
    "merge.target": "galay (hadda)",
    "merge.loading": "Falanqaynta isku darka…",
    "merge.upToDate": "Durba ilaa hadda - ma jiraan wax la isku daraa.",
    "merge.fastForward": "Dhaqso u socod - looma baahna ballan isku dar ah (sax \"Create a merge commitment\" si aad mid u qasbid).",
    "merge.summary": "{files} faylka(yada) waa isbedelay, isku dhacyada {conflicts}",
    "merge.conflictsWarning": "Isku darkani wuxuu leeyahay khilaafyo - wali waad ku milmi kartaa, ka bacdina waxaad ku xalin kartaa tafatiraha.",
    "merge.noChanges": "Wax feyl ah ma jiro.",
    "merge.files": "Isbadalada ka dhashay",
    "merge.added": "Lagu daray",
    "merge.modified": "wax laga beddelay",
    "merge.deleted": "La tirtiray",
    "merge.conflict": "Colaad",
    "merge.message": "Isku dar fariinta ballan",
    "merge.messagePlaceholder": "Isku dar laanta 'source'",
    "merge.messageFfHint": "La iska indho tiray isku darka hore u socda (wax ballan ah lama abuurin).",
    "merge.noFastForward": "Samee ballan-qaad isku-dhafan (dhakhso u-socod)",
    "merge.noFastForwardHint": "Had iyo jeer diiwaan geli ballan-qaadka isku-dhafka, xitaa marka laantu ay si degdeg ah u socon karto.",
    "merge.merge": "Isku dar",
    "merge.cancel": "Jooji",
    "merge.previewError": "Waa la xisaabin kari waayay dulmar: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "Isku-darka Squash - isbeddel kasta oo dhulka ah sida MID u sameeyo \"{target}\"; \"{source}\" iyadu waxay ka go'an tahay inay ka baxdo taariikhdeeda. Dib ugu laabo Settings › Isku dar.",
    "merge.messageSquash": "Gudbi fariin",
    "merge.squashMessagePlaceholder": "Isbeddel la jabsaday oo ka yimid 'Isha'",
    "newBranch.title": "Samee Laanta",
    "newBranch.startPoint": "Laan cusub oo ka bilaabma {sha}",
    "newBranch.startPointOn": "ee {refs}",
    "newBranch.location": "Goobta",
    "newBranch.locationRoot": "(xidid)",
    "newBranch.expandAll": "Balaadhi dhammaan",
    "newBranch.collapseAll": "Burbur dhammaan",
    "newBranch.expandFolder": "Kordhi gal",
    "newBranch.collapseFolder": "Burbur galka",
    "newBranch.name": "Magaca laanta",
    "newBranch.namePlaceholder": "laanteyda",
    "newBranch.fullName": "Magaca buuxa: {name}",
    "newBranch.invalid": "Magaca laanta aan ansax ahayn",
    "newBranch.exists": "Laan magacan leh ayaa horay u jirtay",
    "newBranch.checkout": "Hubi laanta ka dib abuurista",
    "newBranch.create": "Abuur",
    "newBranch.cancel": "Jooji",
    "status.loading": "Soodejinaya garaaf…",
    "status.summary": "{repo}Showing {commits} ballan, {refs} refs",
    "status.branchCreated": "Laanta laga sameeyay \"{name}\" ee {sha}",
    "status.error": "Khaladka: {message}",
    "status.opFailed": "Hawlgalku wuu fashilmay",
    "status.fetching": "Soo qaadaya…",
    "status.pulling": "jiidaya",
    "status.pushing": "riixaya...",
    "status.committing": "Samaynta ballan-qaad maxalli ah…",
    "status.commitCreated": "Sameeyay ballanqaad maxalli ah {sha}: {message}",
    "status.syncing": "Isku xidhka…",
    "status.noHead": "Wax hubin hadda ah lagama helin garaafka",
    "status.undoing": "Dib u dhigista ballan-qaadkii…",
    "status.commitUndone": "Ballanqaadka dib-u-noqoshada - isbeddellada ayaa ku soo noqday geedkii shaqada.",
    "status.undoConflict": "Ka noqo isku dhacyada ku dhuftay - ku xalliya tafatiraha, ka dibna sii wad.",
    "status.stashApplying": "Codsanaya kaydinta…",
    "status.stashPopping": "Burburka soo boodaya",
    "status.stashDropping": "Tuurya meel",
    "status.stashApplied": "Istaash ayaa la adeegsaday",
    "status.stashPopped": "Gariir ayaa soo booday",
    "status.stashDropped": "Istaashka ayaa dhacay",
    "status.stashConflict": "Isku dhacyada fadhiidka ah - ku xalliya tafatiraha.",
    "status.merging": "Isku darka…",
    "status.merged": "Isku darka waa la dhameeyay",
    "status.mergeConflict": "Isku-dhafka ayaa leh khilaafyo - ku xalliya tafatiraha, ka dibna go'aanso.",
    "search.title": "Raadi",
    "search.tooltip": "Baadhitaanku wuu sameeyaa",
    "search.placeholder": "Baadhitaanku wuxuu sameeyaa…",
    "search.previous": "Natiijadii hore",
    "search.next": "Natiijada xigta",
    "search.noResults": "Natiijooyin ma jiraan",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Qaabka shaandhaynta",
    "search.highlight": "Habka iftiiminta",
    "search.filterMode": "Muujinta raadinta",
    "search.filterModeFilter": "Shaandhee (qari kuwa aan ciyaar ahayn)",
    "search.filterModeHighlight": "Iftiimin (muuji dhammaan, calaamadee kulammada)",
    "search.filterModeHint": "Dooro sida natiijooyinka raadinta loogu muujiyo garaafka.",
    "search.visibility": "Ka raadi goobta",
    "search.visibilityToolbar": "badhanka Toolbar",
    "search.visibilityAlwaysVisible": "Had iyo jeer-baarka muuqda",
    "search.visibilityHint": "U muuji raadinta sida summada aaladda ama bar joogta ah xagga sare.",
    "footer.github": "GitHub",
  },
  fi: {
    "toolbar.refresh": "⟳ Päivitä",
    "toolbar.fetch": "⤓ Hae",
    "toolbar.pull": "⇩ Vedä",
    "toolbar.push": "⇧ Työnnä",
    "toolbar.commit": "✓ Sitoudu",
    "toolbar.sync": "⇅ Synkronointi",
    "toolbar.jumpHead": "⌖ Mene kassalle",
    "toolbar.reset": "⤢ Palauta näkymä",
    "toolbar.settings": "⚙ Asetukset",
    "details.header": "Sitoumustiedot",
    "details.close": "Lähellä",
    "details.sha": "SHA",
    "details.shortSha": "Lyhyt SHA",
    "details.message": "Viesti",
    "details.author": "Tekijä",
    "details.date": "Päivämäärä",
    "details.labels": "Tarrat",
    "details.location": "Sijainti",
    "details.currentHead": "HEAD — nykyinen kassa",
    "settings.title": "Asetukset",
    "settings.language": "Kieli",
    "settings.theme": "Teema",
    "settings.themeLight": "Kevyt",
    "settings.themeDark": "Tumma",
    "settings.themeLightHint": "Vaalea tausta, tumma teksti.",
    "settings.themeDarkHint": "Tumma tausta, vaalea teksti.",
    "settings.mainBranch": "Päähaara",
    "settings.mainBranchAuto": "Automaattinen",
    "settings.mainBranchSearch": "Hae toimipisteitä…",
    "settings.mainBranchNoMatch": "Ei vastaavia oksia",
    "settings.display": "Näytön tyyli",
    "settings.displayModern": "Moderni",
    "settings.displayClassic": "Klassinen",
    "settings.displayModernHint": "Ilmainen kangas — panoroi vetämällä ja zoomaa vierittämällä.",
    "settings.displayClassicHint": "Kiinteä kangas – runko kiinnitetty vasemmalle, ei zoomia, vain vierittäminen (kuten SVN-versiokaavio).",
    "settings.sectionGeneral": "Kenraali",
    "settings.sectionGraph": "Kaavio",
    "settings.sectionChanges": "Muuttaa näkymää",
    "settings.diffMinimap": "Diff minikartta",
    "settings.diffMinimapOn": "Näytetään",
    "settings.diffMinimapOff": "Piilotettu",
    "settings.diffMinimapOnHint": "Näytä VS Code -tyylinen yleiskatsauskaistale eron vieressä — vedä se vierittääksesi; vaihtomerkit mukana.",
    "settings.diffMinimapOffHint": "Ei yleiskuvaliuskaa – vieritä eroa normaalisti.",
    "settings.commitReview": "Tee tarkistus",
    "settings.commitReviewOn": "Kysy ennen sitoutumista",
    "settings.commitReviewOff": "Sitoudu suoraan",
    "settings.commitReviewOnHint": "Näytä lopullinen tiedostoluettelo ja viestien tarkistus ennen paikallisen toimituksen luomista.",
    "settings.commitReviewOffHint": "Toimita-painike luo paikallisen sitoumuksen välittömästi.",
    "settings.svnBranchDialog": "SVN-tyylinen haaraikkuna",
    "settings.svnBranchDialogHint": "Näytä kansiopuun valitsin haaraa luotaessa (yksinkertaisen kehotteen sijaan).",
    "settings.branchDialog": "Haara-valintaikkuna",
    "settings.branchDialogSvnTitle": "SVN-tyyliin",
    "settings.branchDialogNativeVscode": "VS-koodin tyyli",
    "settings.branchDialogNativeVs": "Visual Studio -tyyli",
    "settings.branchDialogNativeHint": "IDE:n sisäänrakennettu haarakehote.",
    "settings.close": "Lähellä",
    "settings.done": "Tehty",
    "settings.sectionAdvanced": "Edistynyt",
    "settings.gitSource": "Git suoritettava tiedosto",
    "settings.gitSourceBuiltin": "Sisäänrakennettu",
    "settings.gitSourceBuiltinHint": "Käytä IDE:n sisäänrakennetun Git-laajennuksen git-binaaria.",
    "settings.gitSourceCustom": "Mukautettu polku",
    "settings.gitPath": "Polku gitiin",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Selaa…",
    "settings.jargon": "Git ehdot",
    "settings.jargonTranslate": "Kääntää",
    "settings.jargonEnglish": "Säilytä englanniksi",
    "settings.jargonTranslateHint": "Käännä Git-termit (pull, push, commit, haara…) käyttöliittymäkielelle.",
    "settings.jargonEnglishHint": "Säilytä Git-termit (pull, push, commit, branch…) englanniksi; kääntää kaikki muu.",
    "settings.sectionMerge": "Yhdistää",
    "settings.mergeMode": "Yhdistä tyyli",
    "settings.mergeModeMerge": "Normaali yhdistäminen",
    "settings.mergeModeSquash": "Squash yhdistäminen",
    "settings.mergeModeMergeHint": "Yhdistää haaran sellaisenaan: sen sitoumuksista tulee osa nykyisen haaran historiaa, ja ne yhdistetään yhdistämistoimituksella (tai kelataan eteenpäin, kun mahdollista).",
    "settings.mergeModeSquashHint": "Tiivistää koko haaran yhdeksi sitomaksi nykyisessä haarassa (git merge --squash). Haaraan omat sitoumukset eivät koskaan mene nykyisen haaran historiaan – näet siellä vain yhden sitoumuksen. Itse haara jätetään koskemattomaksi, joten sen sitoumukset pysyvät näkyvissä omalla kaistallaan.",
    "settings.mergedView": "Yhdistetyt sitoumukset kaaviossa",
    "settings.mergedViewBranch": "Vain omalla haarallaan",
    "settings.mergedViewTarget": "Myös osastolla ne yhdistettiin",
    "settings.mergedViewBranchHint": "Yhdistetyn haaran sitoumukset arvotaan vain heidän omalle kaistalleen; yhdistäminen näkyy liittimenä. Klassinen näkymä.",
    "settings.mergedViewTargetHint": "Tuodut yhdistämiset piirretään myös vastaanottavan haaran kaistalle, pinottuna yhdistämissitoumuksen alle ja tasolle alkuperäisten kanssa - koska tuo haara todella sisältää ne nyt. Kopiot ovat vaaleita ja niiden oksalla, jolle ne kirjoitettiin, joten jää selväksi, että niitä ei ole tehty siellä. Squash-fuusiot eivät vaikuta: squash kirjoittaa yhden tavallisen sitoumuksen, joten vain tämä sitoumus ilmestyy koskaan.",
    "legend.title": "Legenda",
    "legend.head": "PÄÄ / nykyinen haara",
    "legend.local": "Paikallinen haara",
    "legend.remote": "Etähaara",
    "legend.remoteOnly": "Vain pilvessä (ei vedetty)",
    "legend.tag": "Tag (versio)",
    "legend.commit": "Sitoudu",
    "legend.stash": "Stash (hyllytyö)",
    "legend.mergedIn": "Tuli sisään yhdistämällä (kirjoitettu toiseen haaraan)",
    "legend.nodes": "Laatikot",
    "legend.lines": "Linjat",
    "legend.edgeParent": "Edellinen sitoumus samalla rivillä",
    "legend.edgeMerge": "Yhdistetty haara — nuoli osoittaa yhdistämistoimitusta",
    "legend.edgeBranch": "Täältä haarautui oksa",
    "legend.edgeStash": "Stash ja sitoumus hyllytettiin",
    "legend.edgeMergedChain": "Tämän haaran rivi kulkee yhdistettyjen sitoumusten läpi",
    "legend.edgeMergedTie": "Sama sitoumus haarassa, johon se kirjoitettiin",
    "menu.jumpHead": "⌖ Mene kassalle",
    "menu.resetView": "⤢ Palauta näkymä",
    "menu.createBranch": "Luo haara täältä…",
    "menu.checkout": "Tarkista tämä sitoumus",
    "menu.copySha": "Kopioi commit SHA",
    "menu.pushBranch": "Työnnä haara \"{name}\"",
    "menu.renameCommit": "Nimeä vahvistusviesti uudelleen…",
    "menu.undoCommit": "Kumoa sitoumus (säilytä muutokset)…",
    "menu.viewChanges": "Näytä muutokset…",
    "menu.jumpToOriginal": "Siirry alkuperäiseen sitoumukseen",
    "changes.title": "Muutoksia {sha}:ssä",
    "changes.tabChanged": "Muutettu",
    "changes.tabAll": "Kaikki tiedostot",
    "changes.added": "Lisätty",
    "changes.modified": "Muokattu",
    "changes.deleted": "Poistettu",
    "changes.renamed": "Nimetty uudelleen",
    "changes.noChanges": "Tämä vahvistus ei muuta tiedostoja.",
    "changes.loading": "Ladataan eroa…",
    "changes.selectFile": "Valitse tiedosto nähdäksesi sen muutokset.",
    "changes.original": "Alkuperäinen",
    "changes.changed": "Tämä sitoumus",
    "changes.binary": "Binääritiedosto – ei näytettävää tekstieroa.",
    "changes.tooLarge": "Tiedosto on liian suuri erotettavaksi.",
    "changes.renamedFrom": "Nimetty uudelleen {path}:stä",
    "changes.close": "Lähellä",
    "changes.prevChange": "Edellinen muutos",
    "changes.nextChange": "Seuraava muutos",
    "changes.collapseAll": "Kutista kaikki kansiot",
    "changes.expandAll": "Laajenna kaikki kansiot",
    "changes.searchPlaceholder": "Hae tiedostoja…",
    "changes.noSearchResults": "Yksikään tiedosto ei vastaa hakuasi.",
    "changes.maximize": "Maksimoida",
    "changes.restore": "Palauta koko",
    "find.open": "Hae tästä erosta (Ctrl+F)",
    "find.placeholder": "Etsi eri…",
    "find.prev": "Edellinen ottelu",
    "find.next": "Seuraava ottelu",
    "find.close": "Sulje haku",
    "find.noResults": "Ei tuloksia",
    "commit.title": "Tee muutokset",
    "commit.close": "Lähellä",
    "commit.loading": "Luetaan työpuuta…",
    "commit.noChanges": "Paikallisia muutoksia ei ole sitova.",
    "commit.selectFile": "Valitse tiedosto nähdäksesi sen eron.",
    "commit.selectAll": "Valitse kaikki",
    "commit.selectNone": "Tyhjennä kaikki",
    "commit.message": "Sitoudu viestiin",
    "commit.messagePlaceholder": "Kuvaile muutosta",
    "commit.selectedCount": "{count}-tiedosto(t) valittu",
    "commit.review": "Arvostelu",
    "commit.reviewTitle": "Viimeinen tarkistus ennen sitoutumista",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Käytä stash",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Pudota kätköä…",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Into (nykyinen)",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Pikakelaus eteenpäin – yhdistämissitoumusta ei tarvita (rasti \"Luo yhdistämissitoumus\" pakottaaksesi sen).",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Yhdistä haara 'lähde'",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Uusi haara alkaen {sha}",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "(juuri)",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Laajenna kansio",
    "newBranch.collapseFolder": "Kutista kansio",
    "newBranch.name": "Sivukonttorin nimi",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Koko nimi: {name}",
    "newBranch.invalid": "Virheellinen haaran nimi",
    "newBranch.exists": "Tämän niminen haara on jo olemassa",
    "newBranch.checkout": "Kassahaara luomisen jälkeen",
    "newBranch.create": "Luoda",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Ladataan kaaviota…",
    "status.summary": "{repo}Näytetään {commits}-sitoumukset, {refs}-viitteet",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Virhe: {message}",
    "status.opFailed": "Toiminto epäonnistui",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Vedetään…",
    "status.pushing": "Työnnetään…",
    "status.committing": "Luodaan paikallista sitoumusta…",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Synkronoidaan…",
    "status.noHead": "Kaaviosta ei löydy nykyistä kassaa.",
    "status.undoing": "Kumotaan sitoumusta…",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Kumoa osumaristiriidat – ratkaise ne editorissa ja jatka sitten.",
    "status.stashApplying": "Lisätään kätköä…",
    "status.stashPopping": "Varastoa pomppaamassa…",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Stash poksahti.",
    "status.stashDropped": "Stash putosi.",
    "status.stashConflict": "Tallennusristiriidat – ratkaise ne editorissa.",
    "status.merging": "Yhdistetään…",
    "status.merged": "Yhdistäminen valmis.",
    "status.mergeConflict": "Yhdistämisessä on ristiriitoja – ratkaise ne editorissa ja vahvista.",
    "search.title": "Haku",
    "search.tooltip": "Haku sitoo",
    "search.placeholder": "Haku sitoo…",
    "search.previous": "Edellinen tulos",
    "search.next": "Seuraava tulos",
    "search.noResults": "Ei tuloksia",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Suodatintila",
    "search.highlight": "Korosta tila",
    "search.filterMode": "Hakunäyttö",
    "search.filterModeFilter": "Suodata (piilota ei-vastaavat)",
    "search.filterModeHighlight": "Korosta (näytä kaikki, merkitse ottelut)",
    "search.filterModeHint": "Valitse, miten hakutulokset näytetään kaaviossa.",
    "search.visibility": "Hae sijaintia",
    "search.visibilityToolbar": "Työkalupalkin painike",
    "search.visibilityAlwaysVisible": "Aina näkyvä palkki",
    "search.visibilityHint": "Näytä haku työkalupalkin kuvakkeena tai pysyvänä palkkina yläreunassa.",
    "footer.github": "GitHub",
  },
  sv: {
    "toolbar.refresh": "⟳ Uppdatera",
    "toolbar.fetch": "⤓ Hämta",
    "toolbar.pull": "⇩ Dra",
    "toolbar.push": "⇧ Tryck",
    "toolbar.commit": "✓ Engagera sig",
    "toolbar.sync": "⇅ Synk",
    "toolbar.jumpHead": "⌖ Gå till kassan",
    "toolbar.reset": "⤢ Återställ vyn",
    "toolbar.settings": "⚙ Inställningar",
    "details.header": "Begär detaljer",
    "details.close": "Nära",
    "details.sha": "SHA",
    "details.shortSha": "Kort SHA",
    "details.message": "Meddelande",
    "details.author": "Författare",
    "details.date": "Datum",
    "details.labels": "Etiketter",
    "details.location": "Plats",
    "details.currentHead": "HEAD — aktuell kassa",
    "settings.title": "Inställningar",
    "settings.language": "Språk",
    "settings.theme": "Tema",
    "settings.themeLight": "Ljus",
    "settings.themeDark": "Mörk",
    "settings.themeLightHint": "Ljus bakgrund, mörk text.",
    "settings.themeDarkHint": "Mörk bakgrund, ljus text.",
    "settings.mainBranch": "Huvudgren",
    "settings.mainBranchAuto": "Automatisk",
    "settings.mainBranchSearch": "Sök grenar...",
    "settings.mainBranchNoMatch": "Inga matchande grenar",
    "settings.display": "Visa stil",
    "settings.displayModern": "Modern",
    "settings.displayClassic": "Klassisk",
    "settings.displayModernHint": "Gratis duk — dra för att panorera, rulla för att zooma.",
    "settings.displayClassicHint": "Fast duk — stammen fäst till vänster, ingen zoom, endast rullning (som SVN-revisionsgrafen).",
    "settings.sectionGeneral": "Allmän",
    "settings.sectionGraph": "Graf",
    "settings.sectionChanges": "Ändrar vy",
    "settings.diffMinimap": "Diff minikarta",
    "settings.diffMinimapOn": "Visas",
    "settings.diffMinimapOff": "Dold",
    "settings.diffMinimapOnHint": "Visa en översiktsremsa i VS-kod bredvid skillnaden — dra den för att rulla; bytesmarkörer ingår.",
    "settings.diffMinimapOffHint": "Ingen översiktsremsa — rulla skillnaden normalt.",
    "settings.commitReview": "Begär recension",
    "settings.commitReviewOn": "Fråga innan du bestämmer dig",
    "settings.commitReviewOff": "Begå direkt",
    "settings.commitReviewOnHint": "Visa en sista fillista och meddelandekontroll innan du skapar den lokala commit.",
    "settings.commitReviewOffHint": "Knappen Commit skapar den lokala commit direkt.",
    "settings.svnBranchDialog": "SVN-liknande grendialog",
    "settings.svnBranchDialogHint": "Visa en mapp-trädväljare när du skapar en gren (istället för en enkel uppmaning).",
    "settings.branchDialog": "Filial dialog",
    "settings.branchDialogSvnTitle": "SVN-stil",
    "settings.branchDialogNativeVscode": "VS Code stil",
    "settings.branchDialogNativeVs": "Visual Studio stil",
    "settings.branchDialogNativeHint": "Din IDE:s inbyggda grenprompt.",
    "settings.close": "Nära",
    "settings.done": "Gjort",
    "settings.sectionAdvanced": "Avancerad",
    "settings.gitSource": "Git körbar",
    "settings.gitSourceBuiltin": "Inbyggt",
    "settings.gitSourceBuiltinHint": "Använd git-binären från IDE:s inbyggda Git-tillägg.",
    "settings.gitSourceCustom": "Anpassad sökväg",
    "settings.gitPath": "Vägen till git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Bläddra…",
    "settings.jargon": "Git termer",
    "settings.jargonTranslate": "Översätta",
    "settings.jargonEnglish": "Håll på engelska",
    "settings.jargonTranslateHint": "Översätt Git-termer (pull, push, commit, branch...) till gränssnittsspråket.",
    "settings.jargonEnglishHint": "Behåll Git-termer (pull, push, commit, branch...) på engelska; översätt allt annat.",
    "settings.sectionMerge": "Slå ihop",
    "settings.mergeMode": "Sammanfoga stil",
    "settings.mergeModeMerge": "Normal sammanslagning",
    "settings.mergeModeSquash": "Squash sammanfogar",
    "settings.mergeModeMergeHint": "Slår samman grenen som den är: dess commits blir en del av den nuvarande grenens historia, sammanfogade av en merge commit (eller snabbspolas framåt när det är möjligt).",
    "settings.mergeModeSquashHint": "Kollapserar hela grenen till en enda commit på den aktuella grenen (git merge --squash). Filialens egna commits kommer aldrig in i den nuvarande grenens historia – du ser bara en commit där. Själva grenen lämnas orörd, så dess commits förblir synliga i sitt eget körfält.",
    "settings.mergedView": "Merged commits i grafen",
    "settings.mergedViewBranch": "Bara på den egna grenen",
    "settings.mergedViewTarget": "Även i grenen slogs de ihop till",
    "settings.mergedViewBranchHint": "En sammanslagen grens commits ritas endast i deras eget körfält; sammanslagningen visas som en koppling. Den klassiska utsikten.",
    "settings.mergedViewTargetHint": "Commits en merge som tas in ritas också i den mottagande grenens körfält, staplade under merge commit och i nivå med originalen - eftersom den grenen verkligen innehåller dem nu. Kopiorna är bleka och har rubriken med grenen de skrevs på, så det är tydligt att de inte gjordes där. Squash-fusioner påverkas inte: en squash skriver en vanlig commit, så bara den commit visas någonsin.",
    "legend.title": "Legend",
    "legend.head": "HEAD / nuvarande gren",
    "legend.local": "Lokal filial",
    "legend.remote": "Fjärrgren",
    "legend.remoteOnly": "Endast i molnet (ej dragen)",
    "legend.tag": "Tag (version)",
    "legend.commit": "Begå",
    "legend.stash": "Stash (hylla arbete)",
    "legend.mergedIn": "Kom in med en sammanslagning (skriven på en annan gren)",
    "legend.nodes": "Rutor",
    "legend.lines": "Rader",
    "legend.edgeParent": "Tidigare commit på samma linje",
    "legend.edgeMerge": "Merged-in gren — pilen pekar på sammanslagningsbekräftelsen",
    "legend.edgeBranch": "En gren klaffade av här",
    "legend.edgeStash": "Stash och commit det lades på hyllan",
    "legend.edgeMergedChain": "Den här grenens linje som löper genom de sammanslagna commits",
    "legend.edgeMergedTie": "Samma commit på grenen som den skrevs på",
    "menu.jumpHead": "⌖ Gå till kassan",
    "menu.resetView": "⤢ Återställ vyn",
    "menu.createBranch": "Skapa filial härifrån...",
    "menu.checkout": "Kolla in detta åtagande",
    "menu.copySha": "Kopiera commit SHA",
    "menu.pushBranch": "Tryck gren \"{name}\"",
    "menu.renameCommit": "Byt namn på commit-meddelande...",
    "menu.undoCommit": "Ångra commit (behåll ändringar)...",
    "menu.viewChanges": "Visa ändringar...",
    "menu.jumpToOriginal": "Gå till den ursprungliga commit",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Ändringar i {sha}",
    "changes.tabChanged": "Ändrad",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Tillagd",
    "changes.modified": "Ändrad",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Omdöpt",
    "changes.noChanges": "Denna commit ändrar inga filer.",
    "changes.loading": "Laddar diff...",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Expandera alla mappar",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Inga filer matchar din sökning.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Återställ storlek",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Hitta i diff...",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Beskriv förändringen",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Analyserar sammanslagning...",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Ändrad",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Skapa en sammanslagning (ingen snabbspolning framåt)",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Det gick inte att beräkna en förhandsvisning: {message}",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Komprimera alla",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Stash applicerad.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Stash tappade.",
    "status.stashConflict": "Stash-konflikter – lös dem i redigeraren.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Nästa resultat",
    "search.noResults": "Inga resultat",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Filterläge",
    "search.highlight": "Markeringsläge",
    "search.filterMode": "Sökskärm",
    "search.filterModeFilter": "Filtrera (dölj icke-matchningar)",
    "search.filterModeHighlight": "Markera (visa alla, markera matchningar)",
    "search.filterModeHint": "Välj hur sökresultaten ska visas i diagrammet.",
    "search.visibility": "Sök plats",
    "search.visibilityToolbar": "Verktygsfältsknapp",
    "search.visibilityAlwaysVisible": "Alltid synlig bar",
    "search.visibilityHint": "Visa sökning som en verktygsfältsikon eller beständig bar högst upp.",
    "footer.github": "GitHub",
  },
  vi: {
    "toolbar.refresh": "⟳ Làm mới",
    "toolbar.fetch": "⤓ Tìm nạp",
    "toolbar.pull": "⇩ Kéo",
    "toolbar.push": "⇧ Đẩy",
    "toolbar.commit": "✓ Cam kết",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "⌖ Đến quầy thanh toán",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "⚙ Cài đặt",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "SHA",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Tin nhắn",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Nhãn",
    "details.location": "Vị trí",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Tối tăm",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Tự động",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Ẩn giấu",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Phong cách Visual Studio",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Sử dụng tệp nhị phân git từ tiện ích mở rộng Git tích hợp của IDE.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Dịch các thuật ngữ Git (kéo, đẩy, cam kết, phân nhánh…) sang ngôn ngữ giao diện.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Hợp nhất bí",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Các cam kết đã hợp nhất trong biểu đồ",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Các cam kết của chi nhánh đã hợp nhất chỉ được rút ra trong làn đường riêng của họ; sự hợp nhất hiển thị dưới dạng một trình kết nối. Quan điểm cổ điển.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Chi nhánh địa phương",
    "legend.remote": "Chi nhánh từ xa",
    "legend.remoteOnly": "Chỉ trên đám mây (không được kéo)",
    "legend.tag": "Thẻ (phiên bản)",
    "legend.commit": "Làm",
    "legend.stash": "Stash (công việc bị gác lại)",
    "legend.mergedIn": "Đến với sự hợp nhất (được viết trên một nhánh khác)",
    "legend.nodes": "Hộp",
    "legend.lines": "dòng",
    "legend.edgeParent": "Cam kết trước đó trên cùng một dòng",
    "legend.edgeMerge": "Nhánh đã hợp nhất - mũi tên chỉ vào cam kết hợp nhất",
    "legend.edgeBranch": "Một nhánh rẽ ra ở đây",
    "legend.edgeStash": "Stash và cam kết nó đã được gác lại",
    "legend.edgeMergedChain": "Dòng của nhánh này chạy qua các cam kết được hợp nhất",
    "legend.edgeMergedTie": "Cam kết tương tự trên nhánh mà nó được viết trên đó",
    "menu.jumpHead": "⌖ Đến quầy thanh toán",
    "menu.resetView": "⤢ Đặt lại chế độ xem",
    "menu.createBranch": "Tạo chi nhánh từ đây…",
    "menu.checkout": "Kiểm tra cam kết này",
    "menu.copySha": "Sao chép cam kết SHA",
    "menu.pushBranch": "Đẩy nhánh \"{name}\"",
    "menu.renameCommit": "Đổi tên tin nhắn cam kết…",
    "menu.undoCommit": "Hoàn tác cam kết (giữ các thay đổi)…",
    "menu.viewChanges": "Xem các thay đổi…",
    "menu.jumpToOriginal": "Đi đến cam kết ban đầu",
    "node.mergedInTooltip": "Hợp nhất {merge} đã đưa cam kết này vào \"{target}\" - nó được viết trên \"{origin}\".",
    "changes.title": "Những thay đổi trong {sha}",
    "changes.tabChanged": "Đã thay đổi",
    "changes.tabAll": "Tất cả các tệp",
    "changes.added": "Đã thêm",
    "changes.modified": "Đã sửa đổi",
    "changes.deleted": "Đã xóa",
    "changes.renamed": "Đã đổi tên",
    "changes.noChanges": "Cam kết này không thay đổi tập tin.",
    "changes.loading": "Đang tải khác biệt…",
    "changes.selectFile": "Chọn một tập tin để xem những thay đổi của nó.",
    "changes.original": "Nguyên bản",
    "changes.changed": "Cam kết này",
    "changes.binary": "Tệp nhị phân - không có văn bản khác biệt để hiển thị.",
    "changes.tooLarge": "Tệp quá lớn để phân biệt.",
    "changes.renamedFrom": "Đổi tên từ {path}",
    "changes.close": "Đóng",
    "changes.prevChange": "Thay đổi trước đó",
    "changes.nextChange": "Thay đổi tiếp theo",
    "changes.collapseAll": "Thu gọn tất cả các thư mục",
    "changes.expandAll": "Mở rộng tất cả các thư mục",
    "changes.searchPlaceholder": "Tìm kiếm tập tin…",
    "changes.noSearchResults": "Không có tập tin phù hợp với tìm kiếm của bạn.",
    "changes.maximize": "Tối đa hóa",
    "changes.restore": "Khôi phục kích thước",
    "find.open": "Tìm kiếm trong khác biệt này (Ctrl+F)",
    "find.placeholder": "Tìm sự khác biệt…",
    "find.prev": "Trận đấu trước",
    "find.next": "Trận đấu tiếp theo",
    "find.close": "Đóng tìm kiếm",
    "find.noResults": "Không có kết quả",
    "commit.title": "Cam kết thay đổi",
    "commit.close": "Đóng",
    "commit.loading": "Đọc cây làm việc…",
    "commit.noChanges": "Không có thay đổi cục bộ để cam kết.",
    "commit.selectFile": "Chọn một tập tin để xem sự khác biệt của nó.",
    "commit.selectAll": "Chọn tất cả",
    "commit.selectNone": "Xóa tất cả",
    "commit.message": "Thông báo cam kết",
    "commit.messagePlaceholder": "Mô tả sự thay đổi",
    "commit.selectedCount": "Đã chọn (các) tệp {count}",
    "commit.review": "Ôn tập",
    "commit.reviewTitle": "Kiểm tra lần cuối trước khi cam kết",
    "commit.reviewSummary": "Cam kết (các) tệp {count} với thông báo: {message}",
    "commit.back": "Mặt sau",
    "commit.commit": "Tạo cam kết cục bộ",
    "menu.stashApply": "Áp dụng kho lưu trữ",
    "menu.stashPop": "Pop stash (áp dụng và loại bỏ)",
    "menu.stashDrop": "Thả kho…",
    "merge.title": "Hợp nhất chi nhánh",
    "merge.route": "Sáp nhập vào chi nhánh hiện tại",
    "merge.source": "Từ (nguồn)",
    "merge.target": "Vào (hiện tại)",
    "merge.loading": "Đang phân tích việc hợp nhất…",
    "merge.upToDate": "Đã cập nhật — không có gì để hợp nhất.",
    "merge.fastForward": "Chuyển tiếp nhanh - không cần cam kết hợp nhất (đánh dấu vào “Tạo cam kết hợp nhất” để buộc thực hiện).",
    "merge.summary": "(Các) tệp {files} đã thay đổi, (các) xung đột {conflicts}.",
    "merge.conflictsWarning": "Việc hợp nhất này có xung đột — bạn vẫn có thể hợp nhất rồi giải quyết chúng trong trình chỉnh sửa.",
    "merge.noChanges": "Không có thay đổi tập tin.",
    "merge.files": "Kết quả thay đổi",
    "merge.added": "Đã thêm",
    "merge.modified": "Đã sửa đổi",
    "merge.deleted": "Đã xóa",
    "merge.conflict": "Xung đột",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Tạo một cam kết hợp nhất (không chuyển tiếp nhanh)",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Hợp nhất",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Không thể tính toán bản xem trước: {message}",
    "merge.squashBadge": "bí",
    "merge.squashNote": "Hợp nhất bí mật - mọi thay đổi đều diễn ra dưới dạng MỘT cam kết trên “{target}”; Cam kết riêng của “{source}” nằm ngoài lịch sử của nó. Chuyển về Cài đặt > Hợp nhất.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Tạo chi nhánh",
    "newBranch.startPoint": "Chi nhánh mới bắt đầu từ {sha}",
    "newBranch.startPointOn": "trên {refs}",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Tên chi nhánh không hợp lệ",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Chi nhánh thanh toán sau khi tạo",
    "newBranch.create": "Tạo nên",
    "newBranch.cancel": "Hủy bỏ",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "{repo}Hiển thị các cam kết {commits}, giới thiệu {refs}",
    "status.branchCreated": "Đã tạo nhánh \"{name}\" tại {sha}",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Đẩy…",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Đang đồng bộ hóa…",
    "status.noHead": "Không tìm thấy thanh toán hiện tại trong biểu đồ.",
    "status.undoing": "Đang hoàn tác cam kết…",
    "status.commitUndone": "Hoàn tác cam kết - các thay đổi sẽ quay trở lại cây đang hoạt động.",
    "status.undoConflict": "Hoàn tác các xung đột xảy ra — giải quyết chúng trong trình chỉnh sửa, sau đó tiếp tục.",
    "status.stashApplying": "Đang áp dụng kho lưu trữ…",
    "status.stashPopping": "Đang xuất hiện kho lưu trữ…",
    "status.stashDropping": "Đánh rơi kho báu…",
    "status.stashApplied": "Đã áp dụng Stash.",
    "status.stashPopped": "Stash xuất hiện.",
    "status.stashDropped": "Stash bị rơi.",
    "status.stashConflict": "Ngăn chặn xung đột — giải quyết chúng trong trình chỉnh sửa.",
    "status.merging": "Hợp nhất…",
    "status.merged": "Hợp nhất hoàn tất.",
    "status.mergeConflict": "Hợp nhất có xung đột — giải quyết chúng trong trình chỉnh sửa, sau đó cam kết.",
    "search.title": "Tìm kiếm",
    "search.tooltip": "Tìm kiếm cam kết",
    "search.placeholder": "Tìm kiếm cam kết…",
    "search.previous": "Kết quả trước đó",
    "search.next": "Kết quả tiếp theo",
    "search.noResults": "Không có kết quả",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Chế độ lọc",
    "search.highlight": "Chế độ đánh dấu",
    "search.filterMode": "Hiển thị tìm kiếm",
    "search.filterModeFilter": "Lọc (ẩn những kết quả không phù hợp)",
    "search.filterModeHighlight": "Đánh dấu (hiển thị tất cả, đánh dấu kết quả phù hợp)",
    "search.filterModeHint": "Chọn cách hiển thị kết quả tìm kiếm trong biểu đồ.",
    "search.visibility": "Tìm kiếm vị trí",
    "search.visibilityToolbar": "Nút thanh công cụ",
    "search.visibilityAlwaysVisible": "Thanh luôn hiển thị",
    "search.visibilityHint": "Hiển thị tìm kiếm dưới dạng biểu tượng thanh công cụ hoặc thanh cố định ở trên cùng.",
    "footer.github": "GitHub",
  },
  ak: {
    "toolbar.refresh": "⟳ Yɛ foforo",
    "toolbar.fetch": "⤓ Fa no kɔ",
    "toolbar.pull": "⇩ Twe",
    "toolbar.push": "⇧ Piapia",
    "toolbar.commit": "✓ Fa wo ho to wo ho so",
    "toolbar.sync": "⇅ Sync",
    "toolbar.jumpHead": "⌖ Kɔ baabi a wɔde sika totɔ nneɛma",
    "toolbar.reset": "⤢ San fa hwɛbea no si hɔ",
    "toolbar.settings": "⚙ Nsiesiei ahorow",
    "details.header": "Commit Nsɛm a Ɛkɔ Akyiri",
    "details.close": "To mu",
    "details.sha": "SHA",
    "details.shortSha": "Tiatiaa SHA",
    "details.message": "Nkratoɔ",
    "details.author": "Ɔtwerɛfoɔ",
    "details.date": "Da",
    "details.labels": "Nkyerɛwde ahorow",
    "details.location": "Beaeɛ",
    "details.currentHead": "HEAD — mprempren sika a wɔde totɔ nneɛma",
    "settings.title": "Nsiesiei a Wɔde Yɛ Adwuma",
    "settings.language": "Kasa",
    "settings.theme": "Nsɛmpɔ",
    "settings.themeLight": "Kanea",
    "settings.themeDark": "Sum",
    "settings.themeLightHint": "Hann akyi, nkyerɛwee a ɛyɛ sum.",
    "settings.themeDarkHint": "Akyi a ɛyɛ sum, nkyerɛwee a ɛyɛ hann.",
    "settings.mainBranch": "Baa dwumadibea titiriw",
    "settings.mainBranchAuto": "Ɔkwan a wɔfa so yɛ adwuma",
    "settings.mainBranchSearch": "Hwehwɛ baa dwumadibea ahorow...",
    "settings.mainBranchNoMatch": "Nkorabata a ɛne ne ho hyia biara nni hɔ",
    "settings.display": "Ɔkwan a wɔfa so kyerɛ ade",
    "settings.displayModern": "Abɛɛfosɛm",
    "settings.displayClassic": "Classic",
    "settings.displayModernHint": "Free canvas — twetwe kɔ pan, twetwe kɔ zoom.",
    "settings.displayClassicHint": "Fixed canvas — trunk pinned benkum, no zoom, scroll nkutoo (te sɛ SVN nsakrae graph).",
    "settings.sectionGeneral": "Daa daa",
    "settings.sectionGraph": "Mfonini a wɔde kyerɛw",
    "settings.sectionChanges": "Sesa adwene a wɔde hwɛ ade",
    "settings.diffMinimap": "Nsonsonoe a ɛwɔ minimap",
    "settings.diffMinimapOn": "Wɔakyerɛ",
    "settings.diffMinimapOff": "Asuma",
    "settings.diffMinimapOnHint": "Kyerɛ VS Code-style overview strip wɔ diff no nkyɛn — twetwe no kɔ scroll; nsakrae agyiraehyɛde ahorow ka ho.",
    "settings.diffMinimapOffHint": "No overview strip — twe diff no sɛnea ɛte daa.",
    "settings.commitReview": "Fa wo ho hyɛ mu sɛ wobɛsan ahwɛ mu",
    "settings.commitReviewOn": "Bisa ansa na woahyɛ wo ho bɔ",
    "settings.commitReviewOff": "Fa wo ho to wo ho so tẽẽ",
    "settings.commitReviewOnHint": "Kyerɛ fael list a etwa to ne nkra nhwehwɛmu ansa na woayɛ local commit.",
    "settings.commitReviewOffHint": "Commit button no yɛ local commit no ntɛm ara.",
    "settings.svnBranchDialog": "SVN-style baa dwumadibea nkɔmmɔbɔ",
    "settings.svnBranchDialogHint": "Kyerɛ folda-dua pawfo bere a woreyɛ baa dwumadibea (sen sɛ wobɛka asɛm a ɛnyɛ den).",
    "settings.branchDialog": "Baa dwumadibea nkɔmmɔbɔ",
    "settings.branchDialogSvnTitle": "SVN-kwan so",
    "settings.branchDialogNativeVscode": "VS Code kwan so",
    "settings.branchDialogNativeVs": "Visual Studio kwan a wɔfa so yɛ no",
    "settings.branchDialogNativeHint": "Wo IDE no baa dwumadibea a wɔde ahyɛ mu no.",
    "settings.close": "To mu",
    "settings.done": "Mawie",
    "settings.sectionAdvanced": "Kɔ anim",
    "settings.gitSource": "Git a wotumi di dwuma",
    "settings.gitSourceBuiltin": "Wɔde ahyɛ mu",
    "settings.gitSourceBuiltinHint": "Fa git binary a efi IDE no Git ntrɛwmu a wɔahyɛ no mu no di dwuma.",
    "settings.gitSourceCustom": "Amanne kwan so",
    "settings.gitPath": "Ɔkwan a ɛkɔ git",
    "settings.gitPathPlaceholder": "/usr / bin / git",
    "settings.gitPathBrowse": "Browse...",
    "settings.jargon": "Git nsɛmfua",
    "settings.jargonTranslate": "Kyerɛ aseɛ",
    "settings.jargonEnglish": "Fa sie wɔ Borɔfo kasa mu",
    "settings.jargonTranslateHint": "Kyerɛ Git nsɛmfua (twe, pia, hyɛ, nkorabata...) ase kɔ nkitahodi kasa no mu.",
    "settings.jargonEnglishHint": "Fa Git nsɛmfua (twe, pia, hyɛ, nkorabata...) sie wɔ Borɔfo kasa mu; kyerɛ biribiara a aka ase.",
    "settings.sectionMerge": "Ka bom",
    "settings.mergeMode": "Merge ɔkwan a wɔfa so yɛ ade",
    "settings.mergeModeMerge": "Nkabom a ɛyɛ daa",
    "settings.mergeModeSquash": "Squash a wɔde ka bom",
    "settings.mergeModeMergeHint": "Ka baa dwumadibea no bom sɛnea ɛte: ne bɔhyɛ ahorow no bɛyɛ mprempren baa dwumadibea no abakɔsɛm fã, a wɔde nkabom ahyɛde (anaasɛ wɔde kɔ anim ntɛmntɛm bere a ɛbɛyɛ yiye) ka ho.",
    "settings.mergeModeSquashHint": "Ɛbubu baa dwumadibea no nyinaa kɔ commit biako mu wɔ mprempren baa dwumadibea no so (git merge --squash). Baa dwumadibea no ankasa commits nhyɛn mprempren baa dwumadibea no abakɔsɛm mu da — wohu commit biako pɛ wɔ hɔ. Wɔagyaw baa dwumadibea no ankasa a wɔmfa wɔn nsa nka, enti ne commits no kɔ so da adi wɔ n’ankasa kwan so.",
    "settings.mergedView": "Commits a wɔaka abom wɔ graph no mu",
    "settings.mergedViewBranch": "Wɔn ankasa baa dwumadibea nkutoo so",
    "settings.mergedViewTarget": "Afei nso wɔ baa dwumadibea a wɔkaa wɔn boom no mu",
    "settings.mergedViewBranchHint": "Wɔtwe baa dwumadibea a wɔaka abom no commits wɔ wɔn ankasa lane nkutoo mu; nkabom no da adi sɛ nkitahodi. Adwene a wɔde di dwuma wɔ tete mmere mu no.",
    "settings.mergedViewTargetHint": "Wɔtwe commits a merge de ba no wɔ receiving branch no lane nso, stacked wɔ merge commit no ase na ɛne originals no yɛ pɛ — ɛfiri sɛ saa branch no ankasa kura wɔn seesei. Nkyerɛwee no yɛ bruu na wɔde baa dwumadibea a wɔkyerɛwee wɔ so no ti, enti ɛda so ara da adi pefee sɛ wɔanyɛ wɔ hɔ. Squash nkabom no nnya nkɛntɛnso biara: squash kyerɛw commit a ɛyɛ mpapahwekwa biako, enti saa commit no nkutoo na ɛda adi da.",
    "legend.title": "Okunini",
    "legend.head": "HEAD / mprempren baa dwumadibea",
    "legend.local": "Baa dwumadibea a ɛwɔ mpɔtam hɔ",
    "legend.remote": "Baa dwumadibea a ɛwɔ akyirikyiri",
    "legend.remoteOnly": "Mununkum no mu nkutoo (ɛnyɛ nea wɔtwe) .",
    "legend.tag": "Tag (nkyerɛase) .",
    "legend.commit": "Yɛ",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Came in with a merge (wɔakyerɛw wɔ baa dwumadibea foforo so) .",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Ntrɛwmu",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Commit koro no ara wɔ baa dwumadibea a wɔkyerɛwee wɔ so no so",
    "menu.jumpHead": "⌖ Kɔ baabi a wɔde sika totɔ nneɛma",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Wɔasesa no",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "San fa kɛseyɛ ba",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Paw fael bi na hwɛ ne diff.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Saa nkabom yi wɔ ntawntawdi — woda so ara betumi abɔ mu, afei woasiesie wɔ editor no mu.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Wɔde aka ho",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Merge commit nkrasɛm",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Yɛ merge commit (ɛnyɛ fast-forward) .",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Commit nkrasɛm",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Bɔ Baa Dwumadibea",
    "newBranch.startPoint": "Baa dwumadibea foforo a efi ase fi {sha}",
    "newBranch.startPointOn": "wɔ {refs} so",
    "newBranch.location": "Beaeɛ",
    "newBranch.locationRoot": "(nhini)",
    "newBranch.expandAll": "Trɛw ne nyinaa mu",
    "newBranch.collapseAll": "Bu ne nyinaa gui",
    "newBranch.expandFolder": "Trɛw folda no mu",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Edin mũ no nyinaa: {name}",
    "newBranch.invalid": "Baa dwumadibea din a ɛnteɛ",
    "newBranch.exists": "Baa bi a ɛwɔ saa din yi wɔ hɔ dedaw",
    "newBranch.checkout": "Checkout baa dwumadibea bere a wɔabɔ no akyi",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Wɔabɔ baa dwumadibea \"{name}\" wɔ {sha}",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  tk: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Lightagty fon, gara tekst.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Iňlis dilinde saklaň",
    "settings.jargonTranslateHint": "Git adalgalaryny (çekmek, iteklemek, tabşyrmak, şaha…) interfeýs diline terjime ediň.",
    "settings.jargonEnglishHint": "Git terminlerini iňlis dilinde saklaň (çekmek, iteklemek, tabşyrmak, şaha…) hemme zady terjime et.",
    "settings.sectionMerge": "Birleşmek",
    "settings.mergeMode": "Stili birleşdirmek",
    "settings.mergeModeMerge": "Adaty birleşme",
    "settings.mergeModeSquash": "Squash birleşmek",
    "settings.mergeModeMergeHint": "Şahamçany bolşy ýaly birleşdirýär: borçnamalary häzirki şahanyň taryhynyň bir bölegine öwrülýär, birleşmek (ýa-da mümkin boldugyça çalt ugradylýar).",
    "settings.mergeModeSquashHint": "Bütin şahany häzirki şahada ýeke-täk borçnama (git merge --squash) ýykýar. Şahamçanyň öz borçlary hiç wagt häzirki şahamçanyň taryhyna girmeýär - diňe şol ýerde diňe biriniň edenini görýärsiňiz. Şahanyň özi el galdyrylmaýar, şonuň üçinem borçnamalary öz ugrunda görünýär.",
    "settings.mergedView": "Grafada birleşdirilen işler",
    "settings.mergedViewBranch": "Diňe öz şahalarynda",
    "settings.mergedViewTarget": "Şeýle hem şahada birleşdirildi",
    "settings.mergedViewBranchHint": "Birleşdirilen şahanyň borçnamalary diňe öz ugrunda çekilýär; birleşdiriji birleşdiriji hökmünde görkezilýär. Nusgawy görnüş.",
    "settings.mergedViewTargetHint": "Getirilen birleşmeler kabul ediş şahasynyň zolagynda-da çekilýär, birleşdirme borçnamasy we asyl nusgalary bilen deňeşdirilýär - sebäbi bu şahada hakykatdanam häzirki wagtda bar. Göçürmeler reňkli we ýazylan şahasy bilen ugrukdyrylýar, şonuň üçin ol ýerde ýasalmandygy aýdyň görünýär. Gawunyň birleşmegi täsir etmeýär: bir gök önüm adaty bir wada ýazýar, şonuň üçin diňe bu borçnama peýda bolýar.",
    "legend.title": "Rowaýat",
    "legend.head": "Başlyk / häzirki şahasy",
    "legend.local": "Brancherli şahamça",
    "legend.remote": "Uzakdaky şaha",
    "legend.remoteOnly": "Diňe bulutda (çekilmeýär)",
    "legend.tag": "Bellik (wersiýa)",
    "legend.commit": "Borç ber",
    "legend.stash": "Stash (tekjeli iş)",
    "legend.mergedIn": "Birleşmek bilen geldi (başga bir şahada ýazylan)",
    "legend.nodes": "Gutular",
    "legend.lines": "Setirler",
    "legend.edgeParent": "Öňki setirde",
    "legend.edgeMerge": "Birleşdirilen şaha - birleşmekdäki ok nokatlary",
    "legend.edgeBranch": "Bu ýerde bir şaha çykdy",
    "legend.edgeStash": "Stash we ýerine ýetirilen borçnama",
    "legend.edgeMergedChain": "Bu şahanyň ugry birleşdirilen borçnamalardan geçýär",
    "legend.edgeMergedTie": "Şahamça-da ýazylan şol bir wada",
    "menu.jumpHead": "Check Çykyşa git",
    "menu.resetView": "View Görnüşi täzeden düzmek",
    "menu.createBranch": "Bu ýerden şahamça dörediň…",
    "menu.checkout": "Bu borçnamany ýerine ýetiriň",
    "menu.copySha": "SHA-ny göçüriň",
    "menu.pushBranch": "\"{name}\" şahasyny basyň",
    "menu.renameBranch": "\"{name}\" şahamçasynyň adyny üýtgediň â ¦",
    "menu.deleteBranch": "\"{name}\" şahamçasyny pozuň â",
    "menu.renameCommit": "Wada habarynyň adyny üýtgetmek…",
    "menu.undoCommit": "Yza gaýtarmak (üýtgetmeleri saklamak)…",
    "menu.viewChanges": "Üýtgeşmeleri görüň ...",
    "menu.mergeBranch": "\"{source}\" -ni \"{target}\" â birleşdiriň",
    "menu.jumpToOriginal": "Asyl borçnama geç",
    "node.mergedInTooltip": "Merge {merge} bu borçnamany \"{target}\" -e getirdi — \"{origin}\" -da ýazyldy.",
    "changes.title": "{sha} üýtgeşmeler",
    "changes.tabChanged": "Üýtgedildi",
    "changes.tabAll": "Allhli faýllar",
    "changes.added": "Goşuldy",
    "changes.modified": "Üýtgedildi",
    "changes.deleted": "Öçürildi",
    "changes.renamed": "Ady üýtgedildi",
    "changes.noChanges": "Bu borçnama hiç hili faýl üýtgetmeýär.",
    "changes.loading": "Diff ýüklemek…",
    "changes.selectFile": "Üýtgeşmelerini görmek üçin bir faýl saýlaň.",
    "changes.original": "Asyl",
    "changes.changed": "Bu borç",
    "changes.binary": "Ikilik faýl - görkezmek üçin tekst ýok.",
    "changes.tooLarge": "Faýl tapawutlandyrmak üçin gaty uly.",
    "changes.renamedFrom": "{path} adyndan üýtgedildi",
    "changes.close": ".Akyn",
    "changes.prevChange": "Öňki üýtgeşme",
    "changes.nextChange": "Indiki üýtgeşme",
    "changes.collapseAll": "Papshli bukjalary ýykyň",
    "changes.expandAll": "Papkalaryň hemmesini giňeldiň",
    "changes.searchPlaceholder": "Faýllary gözläň…",
    "changes.noSearchResults": "Gözlegiňize hiç hili faýl gabat gelenok.",
    "changes.maximize": "Ulaltmak",
    "changes.restore": "Ululygyny dikeltmek",
    "find.open": "Bu tapawutda gözläň (Ctrl + F)",
    "find.placeholder": "Tapawutda tapyň…",
    "find.prev": "Öňki oýun",
    "find.next": "Indiki oýun",
    "find.close": "Gözlegi ýapyň",
    "find.noResults": "Netije ýok",
    "commit.title": "Üýtgeşmeler",
    "commit.close": ".Akyn",
    "commit.loading": "Iş agajyny okamak…",
    "commit.noChanges": "Etjek ýerli üýtgeşmeler ýok.",
    "commit.selectFile": "Tapawudyny görmek üçin bir faýl saýlaň.",
    "commit.selectAll": "Hemmesini saýlaň",
    "commit.selectNone": "Hemmesini arassala",
    "commit.message": "Borç habary",
    "commit.messagePlaceholder": "Üýtgeşmäni suratlandyryň",
    "commit.selectedCount": "{count} faýllary saýlandy",
    "commit.review": "Syn",
    "commit.reviewTitle": "Etmezden ozal soňky barlag",
    "commit.reviewSummary": "{count} faýl (lar) habary bilen tabşyryň: {message}",
    "commit.back": "Yzyna",
    "commit.commit": "Localerli borçnamany dörediň",
    "menu.stashApply": "Stash ulanyň",
    "menu.stashPop": "Pop stash (ulanyň we aýyryň)",
    "menu.stashDrop": "Taşlamak ...",
    "merge.title": "Şahamçany birleşdir",
    "merge.route": "Häzirki şaha birleşmek",
    "merge.source": "(Çeşme)",
    "merge.target": "Içine (häzirki)",
    "merge.loading": "Birleşmegi derňemek…",
    "merge.upToDate": "Eýýäm täzelenen - birleşjek zat ýok.",
    "merge.fastForward": "Çalt öňe sürmek - birleşdirmek borçnamasy gerek däl (birini mejbur etmek üçin “Birleşmek borçnamasyny dörediň”).",
    "merge.summary": "{files} faýl (lar) üýtgedi, {conflicts} gapma-garşylyklar.",
    "merge.conflictsWarning": "Bu birleşmegiň gapma-garşylyklary bar - siz henizem birleşip bilersiňiz, soňra redaktorda çözüp bilersiňiz.",
    "merge.noChanges": "Faýl üýtgemeýär.",
    "merge.files": "Netijeler",
    "merge.added": "Goşuldy",
    "merge.modified": "Üýtgedildi",
    "merge.deleted": "Öçürildi",
    "merge.conflict": "Konflikt",
    "merge.message": "Berlen habary birleşdir",
    "merge.messagePlaceholder": "Şahamçanyň 'çeşmesini' birleşdiriň",
    "merge.messageFfHint": "Çalt öňe sürülýän birleşmä ähmiýet berilmeýär (hiç hili borçnama döredilmeýär).",
    "merge.noFastForward": "Birleşmek borçnamasyny dörediň (çalt öňe sürülmeýär)",
    "merge.noFastForwardHint": "Elmydama birleşme borçnamasyny ýazyň, hatda şahamça çalt öňe gidip bilse-de.",
    "merge.merge": "Birleşmek",
    "merge.cancel": "Elatyr",
    "merge.previewError": "Öňünden görüşi hasaplap bilmedim: {message}",
    "merge.squashBadge": "Gawun",
    "merge.squashNote": "Squash birleşmek - BIR “{target}” -e tabşyran ýaly her üýtgeşik ýer; “{source}” -iň eden işleri, taryhdan daşda. Sazlamalar ›Birleşmek.",
    "merge.messageSquash": "Borç habary",
    "merge.squashMessagePlaceholder": "'Çeşmeden' üýtgeýän üýtgeşmeler",
    "newBranch.title": "Şahamça dörediň",
    "newBranch.startPoint": "{sha}-den başlap täze şaha",
    "newBranch.startPointOn": "{refs}-da",
    "newBranch.location": "Locationerleşýän ýeri",
    "newBranch.locationRoot": "(kök)",
    "newBranch.expandAll": "Hemmesini giňelt",
    "newBranch.collapseAll": "Hemmesini ýyk",
    "newBranch.expandFolder": "Papkany giňeltmek",
    "newBranch.collapseFolder": "Papkany ýykmak",
    "newBranch.name": "Şahamçanyň ady",
    "newBranch.namePlaceholder": "şahasy",
    "newBranch.fullName": "Doly ady: {name}",
    "newBranch.invalid": "Nädogry şahanyň ady",
    "newBranch.exists": "Bu at bilen bir şaha eýýäm bar",
    "newBranch.checkout": "Döredilenden soň töleg bölümi",
    "newBranch.create": "Dörediň",
    "newBranch.cancel": "Elatyr",
    "status.loading": "Grafik ýüklenýär…",
    "status.summary": "{repo}S {commits} görkezmek, {refs} ref",
    "status.branchCreated": "{sha}-de \"{name}\" şahamçasy döredildi",
    "status.error": "Roralňyşlyk: {message}",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Getirmek…",
    "status.pulling": "Çekmek…",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Localerli borçnama döredildi {sha}: {message}",
    "status.syncing": "Sinhronlamak…",
    "status.noHead": "Grafada häzirki töleg ýok.",
    "status.undoing": "Ygtyýarnamany yzyna gaýtarmak…",
    "status.commitUndone": "Yzyna gaýtaryň - üýtgeşmeler iş agajyna gaýdyp geldi.",
    "status.undoConflict": "Çaknyşyklary yzyna al - redaktorda çöz, soň dowam et.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Çekiş ...",
    "status.stashDropping": "Taşlamak ...",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Stash açyldy.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Birleşmegiň gapma-garşylyklary bar - olary redaktorda çözüň, soňra ýerine ýetiriň.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Öňki netije",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Süzgüç tertibi",
    "search.highlight": "Re modeimi belläň",
    "search.filterMode": "Gözleg ekrany",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Aýratynlaşdyryň (hemmesini görkeziň, gabat gelýänleri belläň)",
    "search.filterModeHint": "Gözleg netijeleriniň grafada nähili görkezilýändigini saýlaň.",
    "search.visibility": "Locationer gözleýän ýer",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Gözlegiň ýokarsynda gurallar paneli nyşany ýa-da dowamly çyzyk hökmünde görkeziň.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  tr: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "⚙ Ayarlar",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Karanlık",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Taahhüt etmeden önce sor",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Kabak birleştirme",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Birleştirmeyle geldi (başka bir dalda yazılmış)",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Stash ve rafa kaldırılan taahhüt",
    "legend.edgeMergedChain": "Bu şubenin birleştirilmiş taahhütlerden geçen hattı",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "İkili dosya — gösterilecek metin farkı yok.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Aramayı kapat",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Değişiklikleri Kaydet",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Taahhüt edilecek yerel değişiklik yok.",
    "commit.selectFile": "Farkını görmek için bir dosya seçin.",
    "commit.selectAll": "Tümünü seç",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Mesajı tamamla",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "{count} dosyası/dosyaları seçildi",
    "commit.review": "Gözden geçirmek",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "{count} dosyasını/dosyalarını şu mesajla kaydedin: {message}",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Yerel taahhüt oluştur",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Zulayı bırak…",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Mevcut şubeyle birleşme",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "(geçerli) içine",
    "merge.loading": "Birleştirme analiz ediliyor…",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Bu birleştirmede çakışmalar var; yine de birleştirip ardından bunları düzenleyicide çözebilirsiniz.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Eklendi",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Anlaşmazlık",
    "merge.message": "Taahhüt mesajını birleştir",
    "merge.messagePlaceholder": "'Kaynak' şubesini birleştir",
    "merge.messageFfHint": "Hızlı ileri sarma birleştirmede yoksayılır (taahhüt oluşturulmaz).",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Şube hızlı ileri sarabildiğinde bile her zaman bir birleştirme taahhüdünü kaydedin.",
    "merge.merge": "Birleştir",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Squash birleştirme — her değişiklik \"{target}\" üzerinde BİR taahhüt olarak gerçekleşir; “{source}”in kendi taahhütleri geçmişinin dışında kalıyor. Ayarlar › Birleştir'e giderek geri dönün.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "{refs}'te",
    "newBranch.location": "Konum",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Klasörü genişlet",
    "newBranch.collapseFolder": "Klasörü daralt",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Tam adı: {name}",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Bu isimde bir şube zaten mevcut",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "İptal etmek",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "{sha}'te \"{name}\" şubesi oluşturuldu",
    "status.error": "Hata: {message}",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Yerel taahhüt {sha} oluşturuldu: {message}",
    "status.syncing": "Senkronize ediliyor…",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Patlayan zula…",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Stash düştü.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Arama taahhütleri…",
    "search.previous": "Önceki sonuç",
    "search.next": "Sonraki sonuç",
    "search.noResults": "Sonuç yok",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  ts: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "⤓ Ku teka",
    "toolbar.pull": "⇩ Ku koka",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Hungu",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Xinyama",
    "settings.themeLightHint": "Ndzandzelelano wo vonikela, tsalwa ra munyama.",
    "settings.themeDarkHint": "Ndzandzelelano wa ntima, tsalwa ro vevuka.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Xikhale xa xikhale",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Fixed canvas — trunk pinned left, ku hava zoom, scroll ntsena (ku fana na girafu ya SVN revision).",
    "settings.sectionGeneral": "Angarhela",
    "settings.sectionGraph": "Girafamu",
    "settings.sectionChanges": "Ku cinca langutelo",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Ku kombisiwile",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Ku hava xiphemu xa nkatsakanyo — scroll diff hi ntolovelo.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Vutisa u nga si tinyiketela",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Kombisa nxaxamelo wo hetelela wa fayili na ku kambela marungula u nga si tumbuluxa ku tinyiketela ka laha kaya.",
    "settings.commitReviewOffHint": "Batani ya Ku Tinyiketela yi tumbuluxa ku tinyiketela ka laha kaya hi ku hatlisa.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Kombisa muhlawuri wa murhi wa folda loko u tumbuluxa rhavi (ematshan’wini ya xitsundzuxo xo olova).",
    "settings.branchDialog": "Nkulumo ya rhavi",
    "settings.branchDialogSvnTitle": "Xitayili xa SVN",
    "settings.branchDialogNativeVscode": "VS Xitayili xa Khodi",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Xitsundzuxo xa rhavi lexi akiweke xa IDE ya wena.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Endlile",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Browse...",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Ku hlanganisiwa ka ntolovelo",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Rhavi leri hlanganisiweke — museve wu kombetela eka ku tinyiketela ka ku hlanganisiwa",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Commit leyi fanaka eka rhavi leri yi tsariweke eka rona",
    "menu.jumpHead": "⌖ Yana eka ku xava",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Endla rhavi ku suka laha...",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Kopa ku tinyiketela SHA",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Languta ku cinca...",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Ku engeteriwile",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Ku susiwile",
    "changes.renamed": "Yi thyiwile vito lerintshwa",
    "changes.noChanges": "Ku tinyiketela loku ku cinca ku hava tifayela.",
    "changes.loading": "Ku layicha diff...",
    "changes.selectFile": "Hlawula fayili leswaku u vona ku cinca ka yona.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Yi thyiwile vito lerintshwa ku suka eka {path}",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Ku cinca loku landzelaka",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Ku ndlandlamuxa tifolda hinkwato",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Pfala",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Hlawula hinkwaswo",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Hlela hi vuntshwa",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Tirhisa stash",
    "menu.stashPop": "Pop stash (tirhisa & susa) .",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Ku suka eka (xihlovo) .",
    "merge.target": "Eka (xa sweswi) .",
    "merge.loading": "Ku xopaxopa ku hlanganisiwa...",
    "merge.upToDate": "Se swi le ka nkarhi wa sweswi — a ku na nchumu wo hlanganisa.",
    "merge.fastForward": "Hi ku hatlisa — ku hava ku tinyiketela ka ku hlanganisa loku lavekaka (tick “Create a merge commit” ku sindzisa yin’we).",
    "merge.summary": "{files} fayili (ti) yi cinciwile, {conflicts} ku lwisana (ti).",
    "merge.conflictsWarning": "Ku hlanganisa loku ku na ku kanetana — wa ha swi kota ku hlanganisa, kutani u swi lulamisa eka muhleri.",
    "merge.noChanges": "Ku hava ku cinca ka fayili.",
    "merge.files": "Ku cinca loku vangiwaka hi sweswo",
    "merge.added": "Ku engeteriwile",
    "merge.modified": "Ku cinciwile",
    "merge.deleted": "Ku susiwile",
    "merge.conflict": "Nkwetlembetano",
    "merge.message": "Hlanganisa rungula ra ku tinyiketela",
    "merge.messagePlaceholder": "Hlanganisa rhavi 'xihlovo'.",
    "merge.messageFfHint": "Ku honisiwa eka ku hlanganisiwa loku hatlisaka emahlweni (ku hava ku tinyiketela loku tumbuluxiweke).",
    "merge.noFastForward": "Endla ku tinyiketela ka ku hlanganisa (ku hava ku ya emahlweni hi ku hatlisa) .",
    "merge.noFastForwardHint": "Minkarhi hinkwayo rhekhoda ku tinyiketela ka ku hlanganisa, hambiloko rhavi ri nga hatlisa ri ya emahlweni.",
    "merge.merge": "Hlanganisa",
    "merge.cancel": "Khansela",
    "merge.previewError": "A swi koteki ku hlayela xikombiso xa le mahlweni: {message}",
    "merge.squashBadge": "Xikwavava",
    "merge.squashNote": "Squash merge — ku cinca kun’wana na kun’wana ku fika ehansi tani hi ONE commit eka “{target}”; Ti commits ta “{source}” hi toxe ti tshama ehandle ka matimu ya yona. Swi tlhelela endzhaku eka Switirhisiwa › Hlanganisa.",
    "merge.messageSquash": "Ku tinyiketela rungula",
    "merge.squashMessagePlaceholder": "Ku cinca loku squashed ku suka eka 'xihlovo'.",
    "newBranch.title": "Endla Rhavi",
    "newBranch.startPoint": "Rhavi lerintshwa ku sukela eka {sha}",
    "newBranch.startPointOn": "eka {refs}",
    "newBranch.location": "Ndhawu",
    "newBranch.locationRoot": "(rimitsu)",
    "newBranch.expandAll": "Ku ndlandlamuxa hinkwaswo",
    "newBranch.collapseAll": "Pfuxeta hinkwaswo",
    "newBranch.expandFolder": "Ku ndlandlamuxa folda",
    "newBranch.collapseFolder": "Ku wisa folda",
    "newBranch.name": "Vito ra rhavi",
    "newBranch.namePlaceholder": "rhavi ra mina",
    "newBranch.fullName": "Vito leri heleleke: {name}",
    "newBranch.invalid": "Vito ra rhavi leri nga tirhiki",
    "newBranch.exists": "Rhavi leri nga ni vito leri se ri kona",
    "newBranch.checkout": "Checkout branch endzhaku ka ku tumbuluxiwa",
    "newBranch.create": "Vumba",
    "newBranch.cancel": "Khansela",
    "status.loading": "Ku layicha girafu...",
    "status.summary": "{repo}Ku kombisa ku tinyiketela ka {commits}, ti-ref ta {refs}",
    "status.branchCreated": "Ku endliwe rhavi ra \"{name}\" eka {sha}",
    "status.error": "Xihoxo: {message}",
    "status.opFailed": "Ntirho wu tsandzekile",
    "status.fetching": "Ku lata...",
    "status.pulling": "Ku koka...",
    "status.pushing": "Ku susumeta...",
    "status.committing": "Ku tumbuluxa ku tinyiketela ka laha kaya...",
    "status.commitCreated": "Ku endliwe ku tinyiketela ka laha kaya {sha}: {message}",
    "status.syncing": "Ku hlanganisa...",
    "status.noHead": "Ku hava ku xava ka sweswi loku kumiweke eka girafu.",
    "status.undoing": "Ku herisa ku tinyiketela...",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Undo hit conflicts — yi lulamisa eka muhleri, kutani u ya emahlweni.",
    "status.stashApplying": "Ku tirhisa stash...",
    "status.stashPopping": "Ku popping stash...",
    "status.stashDropping": "Ku hoxa stash...",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Stash u rhurhumerile.",
    "status.stashDropped": "Stash u wile ehansi.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Ku hlanganisa ku hetisiwile.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Vuyelo lebyi landzelaka",
    "search.noResults": "Ku hava vuyelo",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Mode ya ku kandziyisa",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Hlamusela (kombisa hinkwaswo, fungha ku yelana) .",
    "search.filterModeHint": "Hlawula ndlela leyi mimbuyelo ya ku lavisisa yi kombisiwaka ha yona eka girafu.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Kombisa ku lavisisa tanihi xifaniso xa barhi ya switirhisiwa kumbe barhi leyi phikelelaka ehenhla.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  yo: {
    "toolbar.refresh": "⟳ Tuntun",
    "toolbar.fetch": "⤓ Gbe",
    "toolbar.pull": "⇩ Fa",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "✓ Ṣe adehun",
    "toolbar.sync": "⇅ Amuṣiṣẹpọ",
    "toolbar.jumpHead": "⌖ Lọ si ibi isanwo",
    "toolbar.reset": "⤢ Tunto wiwo",
    "toolbar.settings": "⚙ Eto",
    "details.header": "Awọn alaye adehun",
    "details.close": "Sunmọ",
    "details.sha": "SHA",
    "details.shortSha": "Kukuru SHA",
    "details.message": "Ifiranṣẹ",
    "details.author": "Onkọwe",
    "details.date": "Ọjọ",
    "details.labels": "Awọn akole",
    "details.location": "Ipo",
    "details.currentHead": "HEAD - isanwo lọwọlọwọ",
    "settings.title": "Eto",
    "settings.language": "Ede",
    "settings.theme": "Akori",
    "settings.themeLight": "Imọlẹ",
    "settings.themeDark": "Dudu",
    "settings.themeLightHint": "Imọlẹ ina, ọrọ dudu.",
    "settings.themeDarkHint": "Ilẹ dudu, ọrọ ina.",
    "settings.mainBranch": "Ẹka akọkọ",
    "settings.mainBranchAuto": "Laifọwọyi",
    "settings.mainBranchSearch": "Wa awọn ẹka…",
    "settings.mainBranchNoMatch": "Ko si awọn ẹka ti o baamu",
    "settings.display": "Àfihàn ara",
    "settings.displayModern": "Igbalode",
    "settings.displayClassic": "Alailẹgbẹ",
    "settings.displayModernHint": "Kanfasi ọfẹ - fa si pan, yi lọ si sun-un.",
    "settings.displayClassicHint": "Kanfasi ti o wa titi - ẹhin mọto pin si apa osi, ko si sun-un, yi lọ nikan (bii aworan atunwo SVN).",
    "settings.sectionGeneral": "Gbogboogbo",
    "settings.sectionGraph": "Aworan",
    "settings.sectionChanges": "Awọn ayipada wiwo",
    "settings.diffMinimap": "Iyatọ minimap",
    "settings.diffMinimapOn": "Afihan",
    "settings.diffMinimapOff": "Farasin",
    "settings.diffMinimapOnHint": "Ṣe afihan rinhoho Akopọ ara koodu VS lẹgbẹẹ iyatọ — fa lati yi lọ; ayipada asami pẹlu.",
    "settings.diffMinimapOffHint": "Ko si rinhoho Akopọ - yi lọ si iyatọ deede.",
    "settings.commitReview": "Ṣe awotẹlẹ",
    "settings.commitReviewOn": "Beere ṣaaju ṣiṣe",
    "settings.commitReviewOff": "Ṣe adehun taara",
    "settings.commitReviewOnHint": "Ṣe afihan atokọ faili ikẹhin ati ṣayẹwo ifiranṣẹ ṣaaju ṣiṣẹda ifaramo agbegbe.",
    "settings.commitReviewOffHint": "Bọtini Ifaramọ ṣẹda ifaramo agbegbe lẹsẹkẹsẹ.",
    "settings.svnBranchDialog": "SVN-ara ajọṣọ",
    "settings.svnBranchDialogHint": "Ṣe afihan oluyan igi folda kan nigbati o ṣẹda ẹka kan (dipo itọsẹ ti o rọrun).",
    "settings.branchDialog": "Ifọrọwerọ Ẹka",
    "settings.branchDialogSvnTitle": "SVN-ara",
    "settings.branchDialogNativeVscode": "VS Code ara",
    "settings.branchDialogNativeVs": "Visual Studio ara",
    "settings.branchDialogNativeHint": "Itọnisọna ẹka ti a ṣe sinu IDE rẹ.",
    "settings.close": "Sunmọ",
    "settings.done": "Ti ṣe",
    "settings.sectionAdvanced": "To ti ni ilọsiwaju",
    "settings.gitSource": "Git executable",
    "settings.gitSourceBuiltin": "Ti a ṣe sinu",
    "settings.gitSourceBuiltinHint": "Lo alakomeji git lati itẹsiwaju Git ti a ṣe sinu IDE.",
    "settings.gitSourceCustom": "Ona aṣa",
    "settings.gitPath": "Ọna si git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Ṣawakiri…",
    "settings.jargon": "Git awọn ofin",
    "settings.jargonTranslate": "Tumọ",
    "settings.jargonEnglish": "Jeki ni English",
    "settings.jargonTranslateHint": "Tumọ awọn ofin Git (fa, titari, ṣe, ẹka…) sinu ede wiwo.",
    "settings.jargonEnglishHint": "Jeki awọn ofin Git (fa, titari, ṣe, ẹka…) ni Gẹẹsi; tumọ ohun gbogbo miiran.",
    "settings.sectionMerge": "Darapọ",
    "settings.mergeMode": "Apapọ ara",
    "settings.mergeModeMerge": "Ijọpọ deede",
    "settings.mergeModeSquash": "Squash dapọ",
    "settings.mergeModeMergeHint": "Ṣepọ ẹka naa bi o ti jẹ: awọn iṣẹ rẹ di apakan ti itan-akọọlẹ ẹka lọwọlọwọ, ti o darapọ mọ nipasẹ ifarapọ (tabi fira siwaju nigbati o ṣee ṣe).",
    "settings.mergeModeSquashHint": "Pa gbogbo ẹka naa ṣubu sinu adehun ẹyọkan lori ẹka lọwọlọwọ (git merge --squash). Awọn iṣẹ ti eka ti ara rẹ ko tẹ itan-akọọlẹ ẹka lọwọlọwọ - o rii pe o ṣe adehun kan nikan ni o wa nibẹ. Ẹka ara rẹ ni a ko fi ọwọ kan, nitorinaa awọn iṣẹ rẹ duro han ni ọna tirẹ.",
    "settings.mergedView": "Dapọ ṣẹ ni awonya",
    "settings.mergedViewBranch": "Nikan lori ara wọn ẹka",
    "settings.mergedViewTarget": "Paapaa ni ẹka wọn ti dapọ si",
    "settings.mergedViewBranchHint": "A ti dapọ ẹka ká dá ti wa ni kale nikan ni ara wọn ona; awọn àkópọ fihan soke bi a asopo. The Ayebaye wiwo.",
    "settings.mergedViewTargetHint": "Awọn ifarapọ ti a mu wọle ni a fa ni ọna ọna ti ẹka gbigba bi daradara, ti o tolera labẹ ifarapọ ati ipele pẹlu awọn ipilẹṣẹ - nitori pe ẹka yẹn ni ninu wọn gaan ni bayi. Àwọn ẹ̀dà náà jẹ́ bàìbàì, wọ́n sì kó ẹ̀ka ọ́fíìsì tí wọ́n kọ sára wọn lọ, torí náà ó dájú pé wọn ò ṣe níbẹ̀. Awọn iṣọpọ Squash ko ni ipa: elegede kan kọwe adehun lasan kan, nitorinaa iyẹn nikan yoo han lailai.",
    "legend.title": "Àlàyé",
    "legend.head": "HEAD / lọwọlọwọ ẹka",
    "legend.local": "Ẹka agbegbe",
    "legend.remote": "Latọna eka",
    "legend.remoteOnly": "Nikan ninu awọsanma (ko fa)",
    "legend.tag": "Tag (ẹya)",
    "legend.commit": "Ṣe adehun",
    "legend.stash": "Stash (iṣẹ ti a fi pamọ)",
    "legend.mergedIn": "Wọle pẹlu iṣọpọ kan (ti a kọ si ẹka miiran)",
    "legend.nodes": "Awọn apoti",
    "legend.lines": "Awọn ila",
    "legend.edgeParent": "Ti tẹlẹ ṣẹ lori kanna ila",
    "legend.edgeMerge": "Ẹka ti a dapọ - awọn itọka itọka ni ifarapọ idapọ",
    "legend.edgeBranch": "Ẹka kan forked nibi",
    "legend.edgeStash": "Stash ati ifaramo ti o ti wa ni ipamọ lori",
    "legend.edgeMergedChain": "Laini ti ẹka yii nṣiṣẹ nipasẹ awọn ifarapọ ti a dapọ",
    "legend.edgeMergedTie": "Iṣe kanna lori ẹka ti a kọ si",
    "menu.jumpHead": "⌖ Lọ si ibi isanwo",
    "menu.resetView": "⤢ Tunto wiwo",
    "menu.createBranch": "Ṣẹda ẹka lati ibi…",
    "menu.checkout": "Ṣayẹwo adehun yii",
    "menu.copySha": "Daakọ ṣẹ SHA",
    "menu.pushBranch": "Titari ẹka \"{name}\"",
    "menu.deleteBranch": "Pa ẹka \"{name}\" rẹ kuro",
    "menu.renameCommit": "Fun lorukọ mii ifiranṣẹ…",
    "menu.undoCommit": "Pada adehun (pa awọn ayipada duro)…",
    "menu.viewChanges": "Wo awọn ayipada…",
    "menu.jumpToOriginal": "Lọ si ipilẹṣẹ atilẹba",
    "changes.title": "Awọn ayipada ninu {sha}",
    "changes.tabChanged": "Yipada",
    "changes.tabAll": "Gbogbo Awọn faili",
    "changes.added": "Fi kun",
    "changes.modified": "Títúnṣe",
    "changes.deleted": "Parẹ",
    "changes.renamed": "Fun lorukọ mii",
    "changes.noChanges": "Iṣe yii ko yipada awọn faili.",
    "changes.loading": "Nkojọpọ iyatọ…",
    "changes.selectFile": "Yan faili kan lati wo awọn ayipada rẹ.",
    "changes.original": "Atilẹba",
    "changes.changed": "Ifarabalẹ yii",
    "changes.binary": "Faili alakomeji - ko si iyatọ ọrọ lati ṣafihan.",
    "changes.tooLarge": "Faili ti tobi ju lati yato.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Iyipada iṣaaju",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Pa gbogbo awọn folda",
    "changes.expandAll": "Faagun gbogbo awọn folda",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "O pọju",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Pade wiwa",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Fi kun",
    "merge.modified": "Títúnṣe",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Darapọ",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Fi ifiranṣẹ ranṣẹ",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Faagun gbogbo rẹ",
    "newBranch.collapseAll": "Kọ gbogbo rẹ silẹ",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Orukọ ẹka",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Orukọ kikun: {name}",
    "newBranch.invalid": "Orukọ ẹka ti ko tọ",
    "newBranch.exists": "Ẹka kan ti o ni orukọ yii ti wa tẹlẹ",
    "newBranch.checkout": "Ṣayẹwo ẹka lẹhin ẹda",
    "newBranch.create": "Ṣẹda",
    "newBranch.cancel": "Fagilee",
    "status.loading": "Nkojọpọ awonya…",
    "status.summary": "{repo}Showing {commits} ṣe, {refs} refs",
    "status.branchCreated": "Ṣẹda ẹka \"{name}\" ni {sha}",
    "status.error": "Aṣiṣe: {message}",
    "status.opFailed": "Iṣiṣẹ kuna",
    "status.fetching": "Ngba…",
    "status.pulling": "Nfa…",
    "status.pushing": "Titari…",
    "status.committing": "Ṣiṣẹda adehun agbegbe…",
    "status.commitCreated": "Ṣẹda agbegbe ṣe {sha}: {message}",
    "status.syncing": "Mimuuṣiṣẹpọ…",
    "status.noHead": "Ko si isanwo lọwọlọwọ ti a rii ninu iyaya naa.",
    "status.undoing": "Imupadasẹyin…",
    "status.commitUndone": "Ṣe ifasilẹ silẹ - awọn ayipada ti pada si igi iṣẹ.",
    "status.undoConflict": "Mu awọn ija kọlu kuro - yanju wọn ni olootu, lẹhinna tẹsiwaju.",
    "status.stashApplying": "Nfi idọti…",
    "status.stashPopping": "Apo ti njade…",
    "status.stashDropping": "Nfi idalẹnu silẹ…",
    "status.stashApplied": "Stash loo.",
    "status.stashPopped": "Stash popped.",
    "status.stashDropped": "Stash silẹ.",
    "status.stashConflict": "Awọn ija ikọlu - yanju wọn ni olootu.",
    "status.merging": "Iṣakojọpọ…",
    "status.merged": "Ijọpọ ti pari.",
    "status.mergeConflict": "Ijọpọ ni awọn ija - yanju wọn ni olootu, lẹhinna ṣe.",
    "search.title": "Wa",
    "search.tooltip": "Àwárí ṣẹ",
    "search.placeholder": "Ṣiṣawari ṣe…",
    "search.previous": "Abajade iṣaaju",
    "search.next": "Abajade to nbọ",
    "search.noResults": "Ko si esi",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Ipo àlẹmọ",
    "search.highlight": "Ipo afihan",
    "search.filterMode": "Afihan wiwa",
    "search.filterModeFilter": "Àlẹmọ (fi awọn ere ti kii ṣe ere pamọ)",
    "search.filterModeHighlight": "Ṣe afihan (fi gbogbo rẹ han, samisi awọn ibaamu)",
    "search.filterModeHint": "Yan bawo ni awọn abajade wiwa ṣe han ninu awọn aworan.",
    "search.visibility": "Wa ipo",
    "search.visibilityToolbar": "Bọtini irinṣẹ",
    "search.visibilityAlwaysVisible": "Pẹpẹ ti o han nigbagbogbo",
    "search.visibilityHint": "Ṣe afihan wiwa bi aami ọpa irinṣẹ tabi ọpa itẹramọṣẹ ni oke.",
    "footer.github": "GitHub",
  },
  is: {
    "toolbar.refresh": "⟳ Endurnýjaðu",
    "toolbar.fetch": "⤓ Sækja",
    "toolbar.pull": "⇩ Dragðu",
    "toolbar.push": "⇧ Ýttu",
    "toolbar.commit": "✓ Skuldbinda sig",
    "toolbar.sync": "⇅ Samstilling",
    "toolbar.jumpHead": "⌖ Farðu í kassa",
    "toolbar.reset": "⤢ Endurstilla útsýni",
    "toolbar.settings": "⚙ Stillingar",
    "details.header": "Skuldbindingarupplýsingar",
    "details.close": "Loka",
    "details.sha": "SHA",
    "details.shortSha": "Stutt SHA",
    "details.message": "Skilaboð",
    "details.author": "Höfundur",
    "details.date": "Dagsetning",
    "details.labels": "Merki",
    "details.location": "Staðsetning",
    "details.currentHead": "HEAD — núverandi útskráning",
    "settings.title": "Stillingar",
    "settings.language": "Tungumál",
    "settings.theme": "Þema",
    "settings.themeLight": "Ljós",
    "settings.themeDark": "Myrkur",
    "settings.themeLightHint": "Ljós bakgrunnur, dökkur texti.",
    "settings.themeDarkHint": "Dökkur bakgrunnur, ljós texti.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Sjálfvirk",
    "settings.mainBranchSearch": "Leita í útibúum…",
    "settings.mainBranchNoMatch": "Engar samsvarandi útibú",
    "settings.display": "Sýnastíll",
    "settings.displayModern": "Nútímalegt",
    "settings.displayClassic": "Klassískt",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Engin yfirlitsræma — flettu mismuninn venjulega.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Spyrðu áður en þú skuldbindur þig",
    "settings.commitReviewOff": "Skuldbinda beint",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Útibúgluggi í SVN-stíl",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Útibúsgluggi",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Innbyggður",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Skoða…",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Þýða",
    "settings.jargonEnglish": "Halda á ensku",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Venjuleg sameining",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Sameinar útibúið eins og það er: Skuldbindingar þess verða hluti af sögu núverandi útibús, sameinast með sameiningu (eða flýtt áfram þegar hægt er).",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Goðsögn",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Útibú á staðnum",
    "legend.remote": "Fjargrein",
    "legend.remoteOnly": "Aðeins í skýinu (ekki dregið)",
    "legend.tag": "Merki (útgáfa)",
    "legend.commit": "Skuldbinda sig",
    "legend.stash": "Stash (vinnu í hillum)",
    "legend.mergedIn": "Kom inn með sameiningu (skrifað á aðra grein)",
    "legend.nodes": "Kassar",
    "legend.lines": "Línur",
    "legend.edgeParent": "Fyrri skuldbinding á sömu línu",
    "legend.edgeMerge": "Sameinuð grein — örin bendir á samrunaframkvæmdina",
    "legend.edgeBranch": "Grein gafst af hér",
    "legend.edgeStash": "Stash og skuldbindingin sem það var sett á hilluna",
    "legend.edgeMergedChain": "Lína þessarar útibús liggur í gegnum sameinuðu skuldbindingarnar",
    "legend.edgeMergedTie": "Sama skuldbinding á greininni og var skrifað á",
    "menu.jumpHead": "⌖ Farðu í kassa",
    "menu.resetView": "⤢ Endurstilla útsýni",
    "menu.createBranch": "Búðu til útibú héðan…",
    "menu.checkout": "Skoðaðu þessa skuldbindingu",
    "menu.copySha": "Afritaðu skuldbinda SHA",
    "menu.pushBranch": "Ýttu greininni \"{name}\"",
    "menu.renameCommit": "Endurnefna skuldbindingarskilaboð...",
    "menu.undoCommit": "Afturkalla skuldbindingu (halda breytingum)...",
    "menu.viewChanges": "Skoða breytingar…",
    "menu.jumpToOriginal": "Farðu í upprunalegu skuldbindinguna",
    "node.mergedInTooltip": "Merge {merge} færði þessa skuldbindingu inn í \"{target}\" — það var skrifað á \"{origin}\".",
    "changes.title": "Breytingar á {sha}",
    "changes.tabChanged": "Breytt",
    "changes.tabAll": "Allar skrár",
    "changes.added": "Bætt við",
    "changes.modified": "Breytt",
    "changes.deleted": "Eytt",
    "changes.renamed": "Endurnefnt",
    "changes.noChanges": "Þessi skuldbinding breytir engum skrám.",
    "changes.loading": "Hleður mismun…",
    "changes.selectFile": "Veldu skrá til að sjá breytingar hennar.",
    "changes.original": "Upprunalegt",
    "changes.changed": "Þessi skuldbinding",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Endurnefnt frá {path}",
    "changes.close": "Loka",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Stækkaðu allar möppur",
    "changes.searchPlaceholder": "Leita að skrám...",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Fyrri leikur",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Lokaðu leit",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Loka",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Veldu allt",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Lokaskoðun fyrir skuldbindingu",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Búðu til staðbundna skuldbindingu",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Inn í (núverandi)",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Spóla áfram — engin þörf á samrunaframkvæmd (merktu við \"Búa til samrunaframkvæmd\" til að þvinga fram einn).",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Skuldbinding afturkölluð - breytingar eru aftur í vinnutrénu.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Síustilling",
    "search.highlight": "Hápunktastilling",
    "search.filterMode": "Leitarskjár",
    "search.filterModeFilter": "Sía (fela ekki samsvörun)",
    "search.filterModeHighlight": "Auðkenna (sýna allt, merkja samsvörun)",
    "search.filterModeHint": "Veldu hvernig leitarniðurstöður birtast á línuritinu.",
    "search.visibility": "Leitaðu að staðsetningu",
    "search.visibilityToolbar": "Tækjastikuhnappur",
    "search.visibilityAlwaysVisible": "Alltaf sýnilegur bar",
    "search.visibilityHint": "Sýna leit sem tækjastikutákn eða viðvarandi stiku efst.",
    "footer.github": "GitHub",
  },
  cs: {
    "toolbar.refresh": "⟳ Obnovit",
    "toolbar.fetch": "⤓ Přines",
    "toolbar.pull": "⇩ Vytáhněte",
    "toolbar.push": "⇧ Zatlačte",
    "toolbar.commit": "✓ Zavázat se",
    "toolbar.sync": "⇅ Synchronizace",
    "toolbar.jumpHead": "⌖ Přejděte k pokladně",
    "toolbar.reset": "⤢ Obnovit zobrazení",
    "toolbar.settings": "⚙ Nastavení",
    "details.header": "Podrobnosti závazku",
    "details.close": "Blízko",
    "details.sha": "SHA",
    "details.shortSha": "Krátké SHA",
    "details.message": "Zpráva",
    "details.author": "Autor",
    "details.date": "Datum",
    "details.labels": "Štítky",
    "details.location": "Umístění",
    "details.currentHead": "HEAD — aktuální pokladna",
    "settings.title": "Nastavení",
    "settings.language": "Jazyk",
    "settings.theme": "Téma",
    "settings.themeLight": "Světlo",
    "settings.themeDark": "Tmavý",
    "settings.themeLightHint": "Světlé pozadí, tmavý text.",
    "settings.themeDarkHint": "Tmavé pozadí, světlý text.",
    "settings.mainBranch": "Hlavní větev",
    "settings.mainBranchAuto": "Automatický",
    "settings.mainBranchSearch": "Hledat pobočky…",
    "settings.mainBranchNoMatch": "Žádné odpovídající větve",
    "settings.display": "Styl zobrazení",
    "settings.displayModern": "Moderní",
    "settings.displayClassic": "Klasický",
    "settings.displayModernHint": "Volné plátno – přetažením se posunete, rolováním přiblížíte.",
    "settings.displayClassicHint": "Opravené plátno – kmen připnutý vlevo, žádné přiblížení, pouze rolování (jako graf revizí SVN).",
    "settings.sectionGeneral": "Generál",
    "settings.sectionGraph": "Graf",
    "settings.sectionChanges": "Změní pohled",
    "settings.diffMinimap": "Rozdílová minimapa",
    "settings.diffMinimapOn": "Zobrazeno",
    "settings.diffMinimapOff": "Skrytý",
    "settings.diffMinimapOnHint": "Zobrazte pruh s přehledem ve stylu VS Code vedle rozdílu – přetažením jej můžete posouvat; včetně značek změn.",
    "settings.diffMinimapOffHint": "Žádný přehledový pruh – posouvejte rozdíl normálně.",
    "settings.commitReview": "Potvrdit kontrolu",
    "settings.commitReviewOn": "Před potvrzením se zeptejte",
    "settings.commitReviewOff": "Zavázat se přímo",
    "settings.commitReviewOnHint": "Před vytvořením lokálního odevzdání ukažte konečný seznam souborů a kontrolu zpráv.",
    "settings.commitReviewOffHint": "Tlačítko Potvrdit okamžitě vytvoří místní potvrzení.",
    "settings.svnBranchDialog": "Dialog větve ve stylu SVN",
    "settings.svnBranchDialogHint": "Při vytváření větve zobrazit výběr stromu složek (místo jednoduché výzvy).",
    "settings.branchDialog": "Dialog větve",
    "settings.branchDialogSvnTitle": "ve stylu SVN",
    "settings.branchDialogNativeVscode": "Styl VS kódu",
    "settings.branchDialogNativeVs": "Styl Visual Studio",
    "settings.branchDialogNativeHint": "Vestavěná větev vašeho IDE.",
    "settings.close": "Blízko",
    "settings.done": "Hotovo",
    "settings.sectionAdvanced": "Moderní",
    "settings.gitSource": "Spustitelný soubor Git",
    "settings.gitSourceBuiltin": "Vestavěný",
    "settings.gitSourceBuiltinHint": "Použijte binární soubor git z vestavěného rozšíření Git v IDE.",
    "settings.gitSourceCustom": "Vlastní cesta",
    "settings.gitPath": "Cesta ke git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Prohlížet…",
    "settings.jargon": "Podmínky Git",
    "settings.jargonTranslate": "Přeložit",
    "settings.jargonEnglish": "Udržujte v angličtině",
    "settings.jargonTranslateHint": "Přeložte termíny Git (pull, push, commit, branch…) do jazyka rozhraní.",
    "settings.jargonEnglishHint": "Udržujte výrazy Git (pull, push, commit, branch…) v angličtině; přeložit vše ostatní.",
    "settings.sectionMerge": "Spojit",
    "settings.mergeMode": "Sloučit styl",
    "settings.mergeModeMerge": "Normální sloučení",
    "settings.mergeModeSquash": "Squash sloučení",
    "settings.mergeModeMergeHint": "Sloučí větev tak, jak je: její odevzdání se stanou součástí historie aktuální větve a připojí se ke sloučení (nebo pokud je to možné, rychle vpřed).",
    "settings.mergeModeSquashHint": "Sbalí celou větev do jediného potvrzení na aktuální větvi (git merge --squash). Vlastní odevzdání větve nikdy nevstoupí do historie aktuální větve – vidíte tam pouze jeden odevzdání. Samotná větev je ponechána nedotčená, takže její commity zůstávají viditelné v jejím vlastním pruhu.",
    "settings.mergedView": "Sloučené commity v grafu",
    "settings.mergedViewBranch": "Pouze na vlastní větvi",
    "settings.mergedViewTarget": "Také v pobočce byly sloučeny",
    "settings.mergedViewBranchHint": "Závazky sloučené pobočky jsou čerpány pouze v jejich vlastním pruhu; sloučení se zobrazí jako konektor. Klasický pohled.",
    "settings.mergedViewTargetHint": "Potvrzení, které přineslo sloučení, jsou také nakresleny v pruhu přijímající větve, naskládané pod odevzdáním sloučení a na úrovni s originály – protože tato větev je nyní skutečně obsahuje. Kopie jsou bledé a mají větev, na které byly napsány, takže je jasné, že tam nebyly vytvořeny. Sloučení squashe nejsou ovlivněna: squash zapíše jedno obyčejné potvrzení, takže se vždy objeví pouze toto potvrzení.",
    "legend.title": "Legenda",
    "legend.head": "HEAD / aktuální větev",
    "legend.local": "Místní pobočka",
    "legend.remote": "Vzdálená pobočka",
    "legend.remoteOnly": "Pouze v cloudu (nevytaženo)",
    "legend.tag": "Značka (verze)",
    "legend.commit": "Spáchat",
    "legend.stash": "Stash (odložená práce)",
    "legend.mergedIn": "Přišel se sloučením (napsaným na jiné větvi)",
    "legend.nodes": "Krabice",
    "legend.lines": "Čáry",
    "legend.edgeParent": "Předchozí potvrzení na stejném řádku",
    "legend.edgeMerge": "Sloučená větev — šipka ukazuje na slučovací commit",
    "legend.edgeBranch": "Tady se rozvětvovala větev",
    "legend.edgeStash": "Stash a závazek, který byl odložen",
    "legend.edgeMergedChain": "Řádek této větve procházející sloučenými potvrzeními",
    "legend.edgeMergedTie": "Stejný commit na větvi, na které byl napsán",
    "menu.jumpHead": "⌖ Přejděte k pokladně",
    "menu.resetView": "⤢ Obnovit zobrazení",
    "menu.createBranch": "Vytvořit větev odtud…",
    "menu.checkout": "Zkontrolujte tento závazek",
    "menu.copySha": "Kopírovat potvrzení SHA",
    "menu.pushBranch": "Push větev \"{name}\"",
    "menu.renameCommit": "Přejmenovat zprávu potvrzení…",
    "menu.undoCommit": "Zrušit potvrzení (zachovat změny)…",
    "menu.viewChanges": "Zobrazit změny…",
    "menu.jumpToOriginal": "Přejděte na původní potvrzení",
    "changes.title": "Změny v {sha}",
    "changes.tabChanged": "Změněno",
    "changes.tabAll": "Všechny soubory",
    "changes.added": "Přidáno",
    "changes.modified": "Upraveno",
    "changes.deleted": "Smazáno",
    "changes.renamed": "Přejmenováno",
    "changes.noChanges": "Toto potvrzení nemění žádné soubory.",
    "changes.loading": "Načítání rozdílu…",
    "changes.selectFile": "Výběrem souboru zobrazíte jeho změny.",
    "changes.original": "Originál",
    "changes.changed": "Tento závazek",
    "changes.binary": "Binární soubor – žádný textový rozdíl k zobrazení.",
    "changes.tooLarge": "Soubor je příliš velký na porovnání.",
    "changes.renamedFrom": "Přejmenováno z {path}",
    "changes.close": "Blízko",
    "changes.prevChange": "Předchozí změna",
    "changes.nextChange": "Další změna",
    "changes.collapseAll": "Sbalit všechny složky",
    "changes.expandAll": "Rozbalte všechny složky",
    "changes.searchPlaceholder": "Hledat soubory…",
    "changes.noSearchResults": "Vašemu hledání neodpovídají žádné soubory.",
    "changes.maximize": "Maximalizovat",
    "changes.restore": "Obnovit velikost",
    "find.open": "Hledat v tomto rozdílu (Ctrl+F)",
    "find.placeholder": "Najít v rozdílu…",
    "find.prev": "Předchozí zápas",
    "find.next": "Další zápas",
    "find.close": "Zavřít vyhledávání",
    "find.noResults": "Žádné výsledky",
    "commit.title": "Potvrdit změny",
    "commit.close": "Blízko",
    "commit.loading": "Čtení pracovního stromu…",
    "commit.noChanges": "Neexistují žádné místní změny k potvrzení.",
    "commit.selectFile": "Vyberte soubor pro zobrazení jeho rozdílu.",
    "commit.selectAll": "Vyberte vše",
    "commit.selectNone": "Vymazat vše",
    "commit.message": "Potvrdit zprávu",
    "commit.messagePlaceholder": "Popište změnu",
    "commit.selectedCount": "Byly vybrány soubory {count}",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Závěrečná kontrola před odevzdáním",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Zadní",
    "commit.commit": "Vytvořte místní potvrzení",
    "menu.stashApply": "Použít skrýš",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Zahodit skrýš…",
    "merge.title": "Sloučit pobočku",
    "merge.route": "Sloučení do aktuální větve",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Do (aktuální)",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Soubor(y) {files} změněny, konflikt(y) {conflicts}.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Žádné změny souboru.",
    "merge.files": "Výsledné změny",
    "merge.added": "Přidáno",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Smazáno",
    "merge.conflict": "Konflikt",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Sloučit větev 'zdroj'",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Vždy zaznamenejte slučovací odevzdání, i když se větev může rychle posunout vpřed.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Nová větev začínající od {sha}",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Neplatný název pobočky",
    "newBranch.exists": "Pobočka s tímto názvem již existuje",
    "newBranch.checkout": "Pokladní větev po vytvoření",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "{repo}Zobrazuje potvrzení {commits}, reference {refs}",
    "status.branchCreated": "Vytvořena pobočka \"{name}\" na {sha}",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Tlačí…",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Stash vyskočil.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Tlačítko na panelu nástrojů",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Zobrazit vyhledávání jako ikonu na panelu nástrojů nebo trvalý panel v horní části.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  haw: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Hōʻike i kahi ʻohi lāʻau waihona i ka wā e hana ai i kahi lālā (ma kahi o ka wikiwiki maʻalahi).",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "HEAD / lālā o kēia manawa",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Hoʻololi i ka {sha}",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Huli i nā faila…",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Pāʻani aʻe",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Holoi ʻia",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "E hoʻopaʻa mau i kahi hana hoʻohui, ʻoiai hiki i ka lālā ke holo wikiwiki.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "E hoʻonui i nā mea a pau",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "E hāʻule i ka waihona",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Aia kekahi lālā me kēia inoa",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Hana ʻia ka lālā \"{name}\" ma {sha}",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Ua hāʻule ka hana",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Huki ana…",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "ʻAʻole i loaʻa i ka pakuhi i kēia manawa.",
    "status.undoing": "Ke wehe nei i ka hoʻopaʻa ʻana…",
    "status.commitUndone": "Hoʻopau i ka hana - ua hoʻi hou nā loli i ka lāʻau hana.",
    "status.undoConflict": "Hoʻopau i nā paio paʻa - hoʻoholo iā lākou i ka mea hoʻoponopono, a laila hoʻomau.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Ke waiho nei i ka waihona…",
    "status.stashApplied": "Hoʻopili ʻia ʻo Stash.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Ua hāʻule ʻo Stash.",
    "status.stashConflict": "Hoʻopaʻa i nā hakakā - hoʻoholo iā lākou ma ka mea hoʻoponopono.",
    "status.merging": "Hoʻohui ʻia…",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Pihi mea hana",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  el: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "⇩ Τραβήξτε",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Σφάλμα: {message}",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Δημιουργήθηκε τοπική δέσμευση {sha}: {message}",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Εφαρμογή απόκρυψης…",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Ο Stash έπεσε.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Προηγούμενο αποτέλεσμα",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Κουμπί γραμμής εργαλείων",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "GitHub",
  },
  be: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "⤓ Атрымаць",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "⇧ Націсніце",
    "toolbar.commit": "✓ Здзейсніць",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "⌖ Перайсці да касы",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "SHA",
    "details.shortSha": "Кароткі SHA",
    "details.message": "паведамленне",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Дата",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "HEAD — бягучая каса",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Святло",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Цёмны фон, светлы тэкст.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Сучасны",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Стыль Visual Studio",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Убудаваны",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Аб'яднаныя фіксацыі ў графе",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "⌖ Перайсці да касы",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Выдалены",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Пошук файлаў…",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Наступны матч",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Зафіксаваць паведамленне",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Назад",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Схаваць канфлікты — вырашаць іх у рэдактары.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Зліццё мае канфлікты — развяжыце іх у рэдактары, а потым зафіксуйце.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Пошук абавязвае...",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Рэжым фільтра",
    "search.highlight": "Рэжым падсвятлення",
    "search.filterMode": "Дысплей пошуку",
    "search.filterModeFilter": "Фільтр (схаваць несупадзенні)",
    "search.filterModeHighlight": "Вылучыць (паказаць усе, адзначыць супадзенні)",
    "search.filterModeHint": "Выберыце спосаб адлюстравання вынікаў пошуку на графіцы.",
    "search.visibility": "Месца пошуку",
    "search.visibilityToolbar": "Кнопка панэлі інструментаў",
    "search.visibilityAlwaysVisible": "Заўсёды бачная панэль",
    "search.visibilityHint": "Паказаць пошук у выглядзе значка на панэлі інструментаў або пастаяннай панэлі ўверсе.",
    "footer.github": "GitHub",
  },
  bg: {
    "toolbar.refresh": "⟳ Опресняване",
    "toolbar.fetch": "⤓ Извличане",
    "toolbar.pull": "⇩ Издърпайте",
    "toolbar.push": "⇧ Натиснете",
    "toolbar.commit": "✓ Ангажирайте се",
    "toolbar.sync": "⇅ Синхронизиране",
    "toolbar.jumpHead": "⌖ Отидете на касата",
    "toolbar.reset": "⤢ Нулирайте изгледа",
    "toolbar.settings": "⚙ Настройки",
    "details.header": "Подробности за ангажимент",
    "details.close": "затвори",
    "details.sha": "SHA",
    "details.shortSha": "Кратко SHA",
    "details.message": "Съобщение",
    "details.author": "Автор",
    "details.date": "Дата",
    "details.labels": "Етикети",
    "details.location": "Местоположение",
    "details.currentHead": "HEAD — текущо плащане",
    "settings.title": "Настройки",
    "settings.language": "език",
    "settings.theme": "Тема",
    "settings.themeLight": "светлина",
    "settings.themeDark": "Тъмно",
    "settings.themeLightHint": "Светъл фон, тъмен текст.",
    "settings.themeDarkHint": "Тъмен фон, светъл текст.",
    "settings.mainBranch": "Основен клон",
    "settings.mainBranchAuto": "Автоматично",
    "settings.mainBranchSearch": "Търсене на клонове...",
    "settings.mainBranchNoMatch": "Няма съответстващи клонове",
    "settings.display": "Стил на показване",
    "settings.displayModern": "Модерен",
    "settings.displayClassic": "Класически",
    "settings.displayModernHint": "Безплатно платно — плъзнете за панорамиране, превъртете за мащабиране.",
    "settings.displayClassicHint": "Фиксирано платно — стволът е закрепен отляво, без увеличение, само превъртане (като графиката на ревизията на SVN).",
    "settings.sectionGeneral": "генерал",
    "settings.sectionGraph": "Графика",
    "settings.sectionChanges": "Променя изгледа",
    "settings.diffMinimap": "Разл. миникарта",
    "settings.diffMinimapOn": "Показано",
    "settings.diffMinimapOff": "Скрити",
    "settings.diffMinimapOnHint": "Показване на лента за преглед в стил VS Code до разликата — плъзнете я, за да превъртите; включени маркери за промяна.",
    "settings.diffMinimapOffHint": "Няма лента за преглед — превъртете разликата нормално.",
    "settings.commitReview": "Извършете преглед",
    "settings.commitReviewOn": "Попитайте преди да се ангажирате",
    "settings.commitReviewOff": "Ангажирайте се директно",
    "settings.commitReviewOnHint": "Показване на окончателен списък с файлове и проверка на съобщенията преди създаване на локалния комит.",
    "settings.commitReviewOffHint": "Бутонът Commit създава незабавно локалния комит.",
    "settings.svnBranchDialog": "Диалогов клон в стил SVN",
    "settings.svnBranchDialogHint": "Показване на инструмент за избор на дърво на папки при създаване на клон (вместо обикновена подкана).",
    "settings.branchDialog": "Диалогов прозорец за разклонения",
    "settings.branchDialogSvnTitle": "SVN стил",
    "settings.branchDialogNativeVscode": "VS Code стил",
    "settings.branchDialogNativeVs": "Стил Visual Studio",
    "settings.branchDialogNativeHint": "Подкана за разклонение, вградена във вашата IDE.",
    "settings.close": "затвори",
    "settings.done": "Готово",
    "settings.sectionAdvanced": "Разширено",
    "settings.gitSource": "Git изпълним файл",
    "settings.gitSourceBuiltin": "Вградена",
    "settings.gitSourceBuiltinHint": "Използвайте двоичния файл git от вграденото разширение Git на IDE.",
    "settings.gitSourceCustom": "Персонализиран път",
    "settings.gitPath": "Път до git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Преглед...",
    "settings.jargon": "Условия на Git",
    "settings.jargonTranslate": "Превеждай",
    "settings.jargonEnglish": "Съхранявайте на английски",
    "settings.jargonTranslateHint": "Преведете термините на Git (дърпане, натискане, ангажиране, разклоняване...) на езика на интерфейса.",
    "settings.jargonEnglishHint": "Поддържайте термините на Git (дърпане, натискане, ангажиране, разклоняване...) на английски; преведете всичко останало.",
    "settings.sectionMerge": "Обединяване",
    "settings.mergeMode": "Стил на сливане",
    "settings.mergeModeMerge": "Нормално сливане",
    "settings.mergeModeSquash": "Обединяване на скуош",
    "settings.mergeModeMergeHint": "Обединява клона такъв, какъвто е: ангажиментите му стават част от историята на текущия клон, присъединени чрез ангажимент за сливане (или бързо превъртане напред, когато е възможно).",
    "settings.mergeModeSquashHint": "Свива целия клон в един комит на текущия клон (git merge --squash). Собствените ангажименти на клона никога не влизат в историята на текущия клон — виждате само един ангажимент там. Самият клон е оставен недокоснат, така че ангажиментите му остават видими в собствената му лента.",
    "settings.mergedView": "Обединени ангажименти в графиката",
    "settings.mergedViewBranch": "Само на собствен клон",
    "settings.mergedViewTarget": "Също така в клона, в който бяха обединени",
    "settings.mergedViewBranchHint": "Ангажиментите на обединения клон се изтеглят само в тяхната собствена лента; сливането се показва като конектор. Класическият изглед.",
    "settings.mergedViewTargetHint": "Комитите, въведени от сливане, също се изчертават в лентата на получаващия клон, подредени под комита за сливане и на едно ниво с оригиналите — защото този клон наистина ги съдържа сега. Копията са бледи и са насочени към клона, на който са написани, така че остава ясно, че не са направени там. Сливанията на скуош не са засегнати: скуош записва един обикновен ангажимент, така че само този комит се появява.",
    "legend.title": "Легенда",
    "legend.head": "HEAD / текущ клон",
    "legend.local": "Местен клон",
    "legend.remote": "Отдалечен клон",
    "legend.remoteOnly": "Само в облака (не е изтеглен)",
    "legend.tag": "Етикет (версия)",
    "legend.commit": "Ангажирайте се",
    "legend.stash": "Скривалище (отложена работа)",
    "legend.mergedIn": "Влезе с обединяване (написано на друг клон)",
    "legend.nodes": "Кутии",
    "legend.lines": "линии",
    "legend.edgeParent": "Предишен ангажимент на същия ред",
    "legend.edgeMerge": "Обединен клон — стрелката сочи към ангажимента за сливане",
    "legend.edgeBranch": "Тук се разклони клон",
    "legend.edgeStash": "Скривалището и ангажиментът, върху който беше отложено",
    "legend.edgeMergedChain": "Редът на този клон минава през обединените комити",
    "legend.edgeMergedTie": "Същият ангажимент на клона, на който е писано",
    "menu.jumpHead": "⌖ Отидете на касата",
    "menu.resetView": "⤢ Нулирайте изгледа",
    "menu.createBranch": "Създайте клон от тук...",
    "menu.checkout": "Разгледайте този ангажимент",
    "menu.copySha": "Копиране на SHA",
    "menu.pushBranch": "Пуш клон \"{name}\"",
    "menu.renameCommit": "Преименуване на съобщението за ангажиране...",
    "menu.undoCommit": "Отмяна на ангажимент (запазване на промените)...",
    "menu.viewChanges": "Преглед на промените...",
    "menu.mergeBranch": "Обединете „{source}“ в „{target}“…",
    "menu.jumpToOriginal": "Отидете до оригиналния ангажимент",
    "changes.title": "Промени в {sha}",
    "changes.tabChanged": "Променен",
    "changes.tabAll": "Всички файлове",
    "changes.added": "Добавено",
    "changes.modified": "Променен",
    "changes.deleted": "Изтрито",
    "changes.renamed": "Преименуван",
    "changes.noChanges": "Този ангажимент не променя никакви файлове.",
    "changes.loading": "Разл. се зарежда...",
    "changes.selectFile": "Изберете файл, за да видите промените в него.",
    "changes.original": "Оригинален",
    "changes.changed": "Този ангажимент",
    "changes.binary": "Двоичен файл — няма текстова разлика за показване.",
    "changes.tooLarge": "Файлът е твърде голям за разлика.",
    "changes.renamedFrom": "Преименувано от {path}",
    "changes.close": "затвори",
    "changes.prevChange": "Предишна промяна",
    "changes.nextChange": "Следваща промяна",
    "changes.collapseAll": "Свиване на всички папки",
    "changes.expandAll": "Разгънете всички папки",
    "changes.searchPlaceholder": "Търсене на файлове...",
    "changes.noSearchResults": "Няма файлове, отговарящи на вашето търсене.",
    "changes.maximize": "Увеличете максимално",
    "changes.restore": "Възстановяване на размера",
    "find.open": "Търсене в тази разлика (Ctrl+F)",
    "find.placeholder": "Намери в разл.",
    "find.prev": "Предишен мач",
    "find.next": "Следващ мач",
    "find.close": "Затворете търсенето",
    "find.noResults": "Няма резултати",
    "commit.title": "Извършване на промени",
    "commit.close": "затвори",
    "commit.loading": "Работното дърво се чете...",
    "commit.noChanges": "Няма локални промени за извършване.",
    "commit.selectFile": "Изберете файл, за да видите неговите разл.",
    "commit.selectAll": "Изберете всички",
    "commit.selectNone": "Изчисти всички",
    "commit.message": "Съобщение за ангажиране",
    "commit.messagePlaceholder": "Опишете промяната",
    "commit.selectedCount": "Избран(и) файл(ове) {count}",
    "commit.review": "Преглед",
    "commit.reviewTitle": "Последна проверка преди ангажиране",
    "commit.reviewSummary": "Комитирайте {count} файл(ове) със съобщение: {message}",
    "commit.back": "Назад",
    "commit.commit": "Създайте локален ангажимент",
    "menu.stashApply": "Приложете скривалище",
    "menu.stashPop": "Поп скривалище (прилагане и премахване)",
    "menu.stashDrop": "Пуснете скривалище…",
    "merge.title": "Сливане на клон",
    "merge.route": "Сливане в текущия клон",
    "merge.source": "От (източник)",
    "merge.target": "В (текущо)",
    "merge.loading": "Обединяването се анализира...",
    "merge.upToDate": "Вече е актуален — нищо за обединяване.",
    "merge.fastForward": "Бързо превъртане напред — не е необходим ангажимент за сливане (маркирайте „Създаване на ангажимент за сливане“, за да го принудите).",
    "merge.summary": "{files} файл(ове) е променен, {conflicts} конфликт(и).",
    "merge.conflictsWarning": "Това сливане има конфликти — все още можете да слеете, след което да ги разрешите в редактора.",
    "merge.noChanges": "Няма промени във файла.",
    "merge.files": "Промените в резултат",
    "merge.added": "Добавено",
    "merge.modified": "Променен",
    "merge.deleted": "Изтрито",
    "merge.conflict": "Конфликт",
    "merge.message": "Съобщение за ангажиране на сливане",
    "merge.messagePlaceholder": "Обединяване на клон „източник“",
    "merge.messageFfHint": "Игнорира се при сливане с бързо превъртане (не се създава ангажимент).",
    "merge.noFastForward": "Създайте ангажимент за сливане (без превъртане напред)",
    "merge.noFastForwardHint": "Винаги записвайте ангажимент за сливане, дори когато клонът може да превърта бързо напред.",
    "merge.merge": "Обединяване",
    "merge.cancel": "Отказ",
    "merge.previewError": "Не можа да се изчисли предварителен преглед: {message}",
    "merge.squashBadge": "скуош",
    "merge.squashNote": "Обединяване на скуош — всяка промяна се приземява като ЕДИН ангажимент на „{target}“; Собствените ангажименти на “{source}” остават извън неговата история. Превключете обратно в Настройки › Обединяване.",
    "merge.messageSquash": "Съобщение за ангажиране",
    "merge.squashMessagePlaceholder": "Смачкани промени от „източник“",
    "newBranch.title": "Създаване на клон",
    "newBranch.startPoint": "Нов клон, започващ от {sha}",
    "newBranch.startPointOn": "на {refs}",
    "newBranch.location": "Местоположение",
    "newBranch.locationRoot": "(корен)",
    "newBranch.expandAll": "Разгънете всички",
    "newBranch.collapseAll": "Свиване на всички",
    "newBranch.expandFolder": "Разширете папката",
    "newBranch.collapseFolder": "Свиване на папка",
    "newBranch.name": "Име на клон",
    "newBranch.namePlaceholder": "моят клон",
    "newBranch.fullName": "Пълно име: {name}",
    "newBranch.invalid": "Невалидно име на клон",
    "newBranch.exists": "Клон с това име вече съществува",
    "newBranch.checkout": "Checkout клон след създаване",
    "newBranch.create": "Създавайте",
    "newBranch.cancel": "Отказ",
    "status.loading": "Графиката се зарежда...",
    "status.summary": "{repo} Показване на {commits} ангажименти, {refs} реф",
    "status.branchCreated": "Създаден клон \"{name}\" на {sha}",
    "status.error": "Грешка: {message}",
    "status.opFailed": "Операцията е неуспешна",
    "status.fetching": "Извлича се...",
    "status.pulling": "Издърпване...",
    "status.pushing": "Натискане...",
    "status.committing": "Създава се локален ангажимент...",
    "status.commitCreated": "Създаден локален ангажимент {sha}: {message}",
    "status.syncing": "Синхронизира се...",
    "status.noHead": "В графиката не е намерено текущо плащане.",
    "status.undoing": "Ангажирането се отменя...",
    "status.commitUndone": "Ангажирането е отменено — промените са обратно в работното дърво.",
    "status.undoConflict": "Отмяна на конфликти при попадения — разрешете ги в редактора, след което продължете.",
    "status.stashApplying": "Скривалището се прилага...",
    "status.stashPopping": "Изскача скривалище...",
    "status.stashDropping": "Скривалището се пуска...",
    "status.stashApplied": "Stash е приложен.",
    "status.stashPopped": "Скривалището изскочи.",
    "status.stashDropped": "Скривалището падна.",
    "status.stashConflict": "Конфликти на скривалище — разрешавайте ги в редактора.",
    "status.merging": "Сливане...",
    "status.merged": "Обединяването е завършено.",
    "status.mergeConflict": "Сливането има конфликти — разрешете ги в редактора, след което ангажирайте.",
    "search.title": "Търсене",
    "search.tooltip": "Ангажименти за търсене",
    "search.placeholder": "Ангажименти за търсене...",
    "search.previous": "Предишен резултат",
    "search.next": "Следващ резултат",
    "search.noResults": "Няма резултати",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Режим на филтриране",
    "search.highlight": "Режим на подчертаване",
    "search.filterMode": "Дисплей за търсене",
    "search.filterModeFilter": "Филтриране (скриване на несъответствия)",
    "search.filterModeHighlight": "Маркиране (показване на всички, маркиране на съвпадения)",
    "search.filterModeHint": "Изберете как резултатите от търсенето да се показват в графиката.",
    "search.visibility": "Търсете местоположение",
    "search.visibilityToolbar": "Бутон на лентата с инструменти",
    "search.visibilityAlwaysVisible": "Винаги видима лента",
    "search.visibilityHint": "Показване на търсене като икона на лента с инструменти или постоянна лента в горната част.",
    "footer.github": "GitHub",
  },
  ky: {
    "toolbar.refresh": "⟳ Жаңылоо",
    "toolbar.fetch": "⤓ Алып келүү",
    "toolbar.pull": "⇩ Тартуу",
    "toolbar.push": "⇧ Түртүү",
    "toolbar.commit": "✓ Милдеттениңиз",
    "toolbar.sync": "⇅ Синхрондоштуруу",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Жабуу",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Кыска SHA",
    "details.message": "Кабар",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Дата",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Жайгашкан жер",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Орнотуулар",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Жарык",
    "settings.themeDark": "Караңгы",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Кара фон, ачык текст.",
    "settings.mainBranch": "Негизги филиал",
    "settings.mainBranchAuto": "Автоматтык",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Дал келген бутактар ​​жок",
    "settings.display": "Дисплей стили",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Акысыз кенеп — панорама үчүн сүйрөңүз, чоңойтуу үчүн сыдырыңыз.",
    "settings.displayClassicHint": "Туруктуу холст — магистралдык солго кадалган, масштабы жок, сыдыруу гана (SVN кайра кароо графиги сыяктуу).",
    "settings.sectionGeneral": "Генерал",
    "settings.sectionGraph": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Диф миникарта",
    "settings.diffMinimapOn": "Көрсөтүлдү",
    "settings.diffMinimapOff": "Жашырылган",
    "settings.diffMinimapOnHint": "Диффтин жанында VS Code стилиндеги сереп салуу тилкесин көрсөтүңүз — сыдыруу үчүн аны сүйрөңүз; өзгөртүү маркерлер камтылган.",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Карап чыгуу",
    "settings.commitReviewOn": "Жасаганга чейин сураңыз",
    "settings.commitReviewOff": "Түздөн-түз тапшырыңыз",
    "settings.commitReviewOnHint": "Жергиликтүү милдеттенмелерди түзүүдөн мурун акыркы файлдардын тизмесин жана билдирүүнү текшерүүнү көрсөтүңүз.",
    "settings.commitReviewOffHint": "Commit баскычы дароо жергиликтүү милдеттенмени түзөт.",
    "settings.svnBranchDialog": "SVN стилиндеги бутак диалогу",
    "settings.svnBranchDialogHint": "Бутакты түзүүдө папка-даракты тандагычты көрсөтүңүз (жөнөкөй сунуштун ордуна).",
    "settings.branchDialog": "Филиал диалогу",
    "settings.branchDialogSvnTitle": "SVN стили",
    "settings.branchDialogNativeVscode": "VS Code стили",
    "settings.branchDialogNativeVs": "Visual Studio стили",
    "settings.branchDialogNativeHint": "Сиздин IDE'иңиздин орнотулган филиалы.",
    "settings.close": "Жабуу",
    "settings.done": "Бүттү",
    "settings.sectionAdvanced": "Өркүндөтүлгөн",
    "settings.gitSource": "Git аткарылуучу",
    "settings.gitSourceBuiltin": "Камтылган",
    "settings.gitSourceBuiltinHint": "IDE орнотулган Git кеңейтүүсүнөн git бинарын колдонуңуз.",
    "settings.gitSourceCustom": "Ыңгайлаштырылган жол",
    "settings.gitPath": "Гитке жол",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Серептөө…",
    "settings.jargon": "Git шарттары",
    "settings.jargonTranslate": "Translate",
    "settings.jargonEnglish": "Англис тилинде сакта",
    "settings.jargonTranslateHint": "Git терминдерин (тартуу, түртүү, ишке ашыруу, бутактоо...) интерфейс тилине которуңуз.",
    "settings.jargonEnglishHint": "Git терминдерин англис тилинде сактаңыз (тартуу, түртүү, ишке ашыруу, филиал…); башка баарын которуп.",
    "settings.sectionMerge": "Бириктирүү",
    "settings.mergeMode": "Бириктирүү стили",
    "settings.mergeModeMerge": "Кадимки биригүү",
    "settings.mergeModeSquash": "Сквош бириктирүү",
    "settings.mergeModeMergeHint": "Филиалды кандай болсо, ошол бойдон бириктирет: анын милдеттенмелери бириктирүү милдети менен кошулуп (же мүмкүн болсо, алдыга жылдыруу) учурдагы филиалдын тарыхынын бир бөлүгү болуп калат.",
    "settings.mergeModeSquashHint": "Учурдагы бутактагы бүт бутакты бир бүтүмгө жыйыштырат (git merge --squash). Филиалдын өз милдеттенмелери эч качан учурдагы филиалдын тарыхына кирбейт — ал жерде бир гана милдеттенмени көрөсүз. Тармактын өзү тийбегендиктен, анын милдеттенмелери өз тилкесинде көрүнүп турат.",
    "settings.mergedView": "Графикте бириктирилген милдеттенмелер",
    "settings.mergedViewBranch": "Өзүнүн филиалында гана",
    "settings.mergedViewTarget": "Ошондой эле филиалда алар бириктирилген",
    "settings.mergedViewBranchHint": "Бириктирилген филиалдын милдеттенмелери өз тилкесинде гана түзүлөт; бириктирүү туташтыргыч катары көрсөтөт. Классикалык көрүнүш.",
    "settings.mergedViewTargetHint": "Киргизилген бириктирүү милдеттенмелери кабыл алуучу филиалдын тилкесинде да тартылып, бириктирүү милдеттенмесинин астына тизилет жана түпнускалар менен түзүлөт, анткени ал бутагы азыр аларды камтыйт. Көчүрмөлөр кубарып, алар жазылган бутак менен коштолгон, ошондуктан алар ал жерде жасалбаганы анык бойдон калууда. Сквош биригүүсүнө таасир этпейт: сквош бир катардагы милдеттенмени жазат, андыктан ошол милдеттенме гана пайда болот.",
    "legend.title": "Легенда",
    "legend.head": "HEAD / учурдагы филиал",
    "legend.local": "Жергиликтүү филиал",
    "legend.remote": "Алыскы филиал",
    "legend.remoteOnly": "Булуттун ичинде гана (тартылган эмес)",
    "legend.tag": "Тег (версия)",
    "legend.commit": "Commit",
    "legend.stash": "Stash (текчелүү иш)",
    "legend.mergedIn": "Биригүү менен кирди (башка бутакта жазылган)",
    "legend.nodes": "Коробкалар",
    "legend.lines": "Саптар",
    "legend.edgeParent": "Ошол эле сапта мурунку милдеттенме",
    "legend.edgeMerge": "Бириктирилген бутак - жебе бириктирүү тапшырмасын көрсөтөт",
    "legend.edgeBranch": "Бул жерде бир бутак чыгып кетти",
    "legend.edgeStash": "Stash жана ал токтоп калган милдеттенме",
    "legend.edgeMergedChain": "Бул филиалдын линиясы бириктирилген милдеттенмелер аркылуу өтөт",
    "legend.edgeMergedTie": "Ошол эле филиал боюнча жазылган",
    "menu.jumpHead": "⌖ Кассага өтүңүз",
    "menu.resetView": "⤢ Көрүнүштү калыбына келтирүү",
    "menu.createBranch": "Бул жерден филиал түзүү…",
    "menu.checkout": "Бул милдеттенмени текшериңиз",
    "menu.copySha": "Көчүрмө SHA",
    "menu.pushBranch": "\"{name}\" бутагын басыңыз",
    "menu.renameBranch": "Филиалдын атын \"{name}\"…",
    "menu.renameCommit": "Милдеттүү билдирүүнүн атын өзгөртүү…",
    "menu.undoCommit": "Милдеттенүүнү жокко чыгаруу (өзгөртүүлөрдү сактоо)…",
    "menu.viewChanges": "Өзгөртүүлөрдү көрүү…",
    "menu.jumpToOriginal": "Баштапкы милдеттенмеге өтүңүз",
    "changes.title": "{sha} өзгөртүүлөр",
    "changes.tabChanged": "Өзгөрүлдү",
    "changes.tabAll": "Бардык файлдар",
    "changes.added": "Кошулган",
    "changes.modified": "Өзгөртүлгөн",
    "changes.deleted": "Жок кылынды",
    "changes.renamed": "Аты өзгөртүлдү",
    "changes.noChanges": "Бул милдеттенме эч кандай файлды өзгөртпөйт.",
    "changes.loading": "Айырма жүктөлүүдө…",
    "changes.selectFile": "Анын өзгөрүүлөрүн көрүү үчүн файлды тандаңыз.",
    "changes.original": "Original",
    "changes.changed": "Бул милдеттенме",
    "changes.binary": "Бинардык файл — көрсөтүү үчүн текст айырмасы жок.",
    "changes.tooLarge": "Файл айырмалоо үчүн өтө чоң.",
    "changes.renamedFrom": "{path} аты өзгөртүлдү",
    "changes.close": "Жабуу",
    "changes.prevChange": "Мурунку өзгөртүү",
    "changes.nextChange": "Кийинки өзгөртүү",
    "changes.collapseAll": "Бардык папкаларды жыйыштыруу",
    "changes.expandAll": "Бардык папкаларды жайыңыз",
    "changes.searchPlaceholder": "Файлдарды издөө…",
    "changes.noSearchResults": "Издөөгө эч кандай файл дал келбейт.",
    "changes.maximize": "Максималдуу кылуу",
    "changes.restore": "Өлчөмдү калыбына келтирүү",
    "find.open": "Бул айырма менен издөө (Ctrl+F)",
    "find.placeholder": "Айырмачылыктан табуу…",
    "find.prev": "Мурунку матч",
    "find.next": "Кийинки матч",
    "find.close": "Издөөнү жабуу",
    "find.noResults": "Натыйжа жок",
    "commit.title": "Өзгөртүүлөрдү киргизүү",
    "commit.close": "Жабуу",
    "commit.loading": "Иштеп жаткан дарак окулууда…",
    "commit.noChanges": "Эч кандай жергиликтүү өзгөртүүлөр жок.",
    "commit.selectFile": "Анын айырмасын көрүү үчүн файлды тандаңыз.",
    "commit.selectAll": "Баарын танда",
    "commit.selectNone": "Баарын тазалоо",
    "commit.message": "Билдирүүнү тапшырыңыз",
    "commit.messagePlaceholder": "Өзгөрүүнү сүрөттөп бериңиз",
    "commit.selectedCount": "{count} файл(дары) тандалды",
    "commit.review": "карап чыгуу",
    "commit.reviewTitle": "Жасоого чейин акыркы текшерүү",
    "commit.reviewSummary": "{count} файл(дарын) төмөнкү билдирүү менен тапшырыңыз: {message}",
    "commit.back": "Артка",
    "commit.commit": "Жергиликтүү милдеттенме түзүү",
    "menu.stashApply": "Сташ колдонуу",
    "menu.stashPop": "Поп stash (колдонуу жана алып салуу)",
    "menu.stashDrop": "Сактоо таштап…",
    "merge.title": "Филиалды бириктирүү",
    "merge.route": "Учурдагы филиалга биригүү",
    "merge.source": "(булактан)",
    "merge.target": "Into (учурдагы)",
    "merge.loading": "Бириктирүү талдоодо…",
    "merge.upToDate": "Жаңыртылган — бириктире турган эч нерсе жок.",
    "merge.fastForward": "Тез алдыга жылдыруу — бириктирүү милдеттенмеси талап кылынбайт (бирөөнү мажбурлоо үчүн “Бириктирүү милдеттенмесин түзүү” белгисин коюңуз).",
    "merge.summary": "{files} файл(дары) өзгөрдү, {conflicts} конфликти(лер).",
    "merge.conflictsWarning": "Бул бириктирүүдө карама-каршылыктар бар — сиз дагы эле бириктирип, анан аларды редактордо чечсеңиз болот.",
    "merge.noChanges": "Файл өзгөрбөйт.",
    "merge.files": "Натыйжада өзгөрүүлөр",
    "merge.added": "Кошулган",
    "merge.modified": "Өзгөртүлгөн",
    "merge.deleted": "Жок кылынды",
    "merge.conflict": "Конфликт",
    "merge.message": "Бириктирүү билдирүүсү",
    "merge.messagePlaceholder": "Филиалды бириктирүү \"булагы\"",
    "merge.messageFfHint": "Тез алдыга бириктирүүдө көңүл бурулбайт (милдеттенме түзүлбөйт).",
    "merge.noFastForward": "Бириктирүү милдеттенмесин түзүү (тез алдыга жылдыруу жок)",
    "merge.noFastForwardHint": "Филиал алдыга жылдыра алса да, ар дайым биригүү милдеттенмесин жазыңыз.",
    "merge.merge": "Бириктирүү",
    "merge.cancel": "Жокко чыгаруу",
    "merge.previewError": "Алдын ала көрүү эсептелбей койду: {message}",
    "merge.squashBadge": "Сквош",
    "merge.squashNote": "Сквош бириктирүү — ар бир өзгөртүү ONE \"{target}\" боюнча ишке ашат; \"{source}\" өз милдеттенмелери анын тарыхынан четте калат. Орнотуулар › Бириктирүү бөлүмүнөн кайра которулуңуз.",
    "merge.messageSquash": "Билдирүүнү тапшырыңыз",
    "merge.squashMessagePlaceholder": "\"Булактан\" кыскартылган өзгөртүүлөр",
    "newBranch.title": "Филиалды түзүү",
    "newBranch.startPoint": "{sha} баштап жаңы филиал",
    "newBranch.startPointOn": "{refs} боюнча",
    "newBranch.location": "Жайгашкан жер",
    "newBranch.locationRoot": "(тамыр)",
    "newBranch.expandAll": "Баарын кеңейтүү",
    "newBranch.collapseAll": "Баарын жыйноо",
    "newBranch.expandFolder": "Папканы кеңейтүү",
    "newBranch.collapseFolder": "Папканы жыйыштыруу",
    "newBranch.name": "Филиал аты",
    "newBranch.namePlaceholder": "менин филиалым",
    "newBranch.fullName": "Толук аты-жөнү: {name}",
    "newBranch.invalid": "Филиалдын аты жараксыз",
    "newBranch.exists": "Мындай аталыштагы филиал мурунтан эле бар",
    "newBranch.checkout": "Түзүлгөндөн кийин текшерүү филиалы",
    "newBranch.create": "Түзүү",
    "newBranch.cancel": "Жокко чыгаруу",
    "status.loading": "График жүктөлүүдө…",
    "status.summary": "{repo}{commits} милдеттенмелерин көрсөтүү, {refs} реф",
    "status.branchCreated": "{sha} боюнча \"{name}\" филиалы түзүлгөн",
    "status.error": "Ката: {message}",
    "status.opFailed": "Операция ишке ашкан жок",
    "status.fetching": "Алынууда…",
    "status.pulling": "Тартуу…",
    "status.pushing": "Түртүүдө…",
    "status.committing": "Жергиликтүү комиссия түзүлүүдө…",
    "status.commitCreated": "{sha}: {message} жергиликтүү милдеттенмеси түзүлдү",
    "status.syncing": "Шайкештирилүүдө…",
    "status.noHead": "Графикте учурдагы текшерүү табылган жок.",
    "status.undoing": "Милдеттенме жокко чыгарылууда…",
    "status.commitUndone": "Артка кылынды - өзгөртүүлөр жумушчу даракка кайра келет.",
    "status.undoConflict": "Катуу конфликттерди жокко чыгаруу — аларды редактордо чечип, андан соң улантыңыз.",
    "status.stashApplying": "Сактоо колдонулууда…",
    "status.stashPopping": "Таштанды чыгаруу…",
    "status.stashDropping": "Сактоо ташталууда…",
    "status.stashApplied": "Stash колдонулду.",
    "status.stashPopped": "Сташ чыкты.",
    "status.stashDropped": "Сташ түшүп калды.",
    "status.stashConflict": "Сташ конфликттери — аларды редактордо чечиңиз.",
    "status.merging": "Биригүү…",
    "status.merged": "Бириктирүү аяктады.",
    "status.mergeConflict": "Бириктирүүдө карама-каршылыктар бар — аларды редактордо чечип, андан кийин аткарыңыз.",
    "search.title": "Издөө",
    "search.tooltip": "Издөө иштери",
    "search.placeholder": "Издөө иштери…",
    "search.previous": "Мурунку натыйжа",
    "search.next": "Кийинки жыйынтык",
    "search.noResults": "Натыйжа жок",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Чыпка режими",
    "search.highlight": "Басып чыгаруу режими",
    "search.filterMode": "Издөө дисплей",
    "search.filterModeFilter": "Чыпка (дал келбеген нерселерди жашыруу)",
    "search.filterModeHighlight": "Бөлүп көрсөтүү (баарын көрсөтүү, дал келгендерди белгилөө)",
    "search.filterModeHint": "Издөө натыйжалары графикте кантип көрсөтүлөрүн тандаңыз.",
    "search.visibility": "Жайгашкан жерди издөө",
    "search.visibilityToolbar": "Куралдар панелинин баскычы",
    "search.visibilityAlwaysVisible": "Ар дайым көрүнүүчү тилке",
    "search.visibilityHint": "Издөөнү куралдар панелинин сөлөкөтү же өйдө жагындагы туруктуу тилке катары көрсөтүү.",
    "footer.github": "GitHub",
  },
  mk: {
    "toolbar.refresh": "⟳ Освежи",
    "toolbar.fetch": "⤓ Преземи",
    "toolbar.pull": "⇩ Повлечете",
    "toolbar.push": "⇧ Притисни",
    "toolbar.commit": "✓ Посветете се",
    "toolbar.sync": "⇅ Синхронизирај",
    "toolbar.jumpHead": "⌖ Одете на наплата",
    "toolbar.reset": "⤢ Ресетирајте го приказот",
    "toolbar.settings": "⚙ Поставки",
    "details.header": "Обврски Детали",
    "details.close": "Затвори",
    "details.sha": "SHA",
    "details.shortSha": "Краток SHA",
    "details.message": "Порака",
    "details.author": "Автор",
    "details.date": "Датум",
    "details.labels": "Етикети",
    "details.location": "Локација",
    "details.currentHead": "ГЛАВА - тековно плаќање",
    "settings.title": "Поставки",
    "settings.language": "Јазик",
    "settings.theme": "Тема",
    "settings.themeLight": "Светлина",
    "settings.themeDark": "Темно",
    "settings.themeLightHint": "Светла позадина, темен текст.",
    "settings.themeDarkHint": "Темна позадина, светол текст.",
    "settings.mainBranch": "Главна гранка",
    "settings.mainBranchAuto": "Автоматски",
    "settings.mainBranchSearch": "Пребарајте филијали…",
    "settings.mainBranchNoMatch": "Нема соодветни гранки",
    "settings.display": "Стил на прикажување",
    "settings.displayModern": "Модерен",
    "settings.displayClassic": "Класичен",
    "settings.displayModernHint": "Бесплатно платно — повлечете за да влечете, скролувајте за да зумирате.",
    "settings.displayClassicHint": "Фиксирано платно - багажникот е закачен лево, без зумирање, само лизгање (како графикот за ревизија на SVN).",
    "settings.sectionGeneral": "Општо",
    "settings.sectionGraph": "Графикон",
    "settings.sectionChanges": "Го менува погледот",
    "settings.diffMinimap": "Минимапа на разлика",
    "settings.diffMinimapOn": "Прикажани",
    "settings.diffMinimapOff": "Скриени",
    "settings.diffMinimapOnHint": "Прикажи прегледна лента во стилот на VS код покрај разликата - повлечете ја за да скролувате; вклучени маркери за промена.",
    "settings.diffMinimapOffHint": "Нема прегледна лента - скролувајте ја разликата нормално.",
    "settings.commitReview": "Извршете преглед",
    "settings.commitReviewOn": "Прашајте пред да се изврши",
    "settings.commitReviewOff": "Посветете се директно",
    "settings.commitReviewOnHint": "Прикажи конечна листа на датотеки и проверка на пораки пред да се создаде локалниот commit.",
    "settings.commitReviewOffHint": "Копчето Commit го креира локалниот commit веднаш.",
    "settings.svnBranchDialog": "Дијалог за разгранување во стил на SVN",
    "settings.svnBranchDialogHint": "Прикажувај избирач на папка-дрво кога креираш гранка (наместо едноставен промпт).",
    "settings.branchDialog": "Дијалог за гранка",
    "settings.branchDialogSvnTitle": "SVN-стил",
    "settings.branchDialogNativeVscode": "Стил VS код",
    "settings.branchDialogNativeVs": "Стил на Visual Studio",
    "settings.branchDialogNativeHint": "Промптот за вградена гранка на вашиот IDE.",
    "settings.close": "Затвори",
    "settings.done": "Готово",
    "settings.sectionAdvanced": "Напредно",
    "settings.gitSource": "Git извршна",
    "settings.gitSourceBuiltin": "Вграден",
    "settings.gitSourceBuiltinHint": "Користете го бинарното git од вградената наставка Git на IDE.",
    "settings.gitSourceCustom": "Прилагодена патека",
    "settings.gitPath": "Пат до git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Прелистајте…",
    "settings.jargon": "Услови за Git",
    "settings.jargonTranslate": "Преведи",
    "settings.jargonEnglish": "Задржи на англиски",
    "settings.jargonTranslateHint": "Преведете ги термините на Git (повлечете, туркате, обврзете, разгранете…) на јазикот на интерфејсот.",
    "settings.jargonEnglishHint": "Чувајте ги Git термините (влечете, туркате, посветете, разгранете…) на англиски јазик; преведете се друго.",
    "settings.sectionMerge": "Спојување",
    "settings.mergeMode": "Спојување на стил",
    "settings.mergeModeMerge": "Нормално спојување",
    "settings.mergeModeSquash": "Сквош се спојуваат",
    "settings.mergeModeMergeHint": "Ја спојува гранката таква каква што е: нејзините обврзници стануваат дел од историјата на тековната гранка, на кои им се придружува обврзување за спојување (или се препраќаат кога е можно).",
    "settings.mergeModeSquashHint": "Ја собира целата гранка во едно обврзување на тековната гранка (git merge --squash). Обврските на самата филијала никогаш не влегуваат во историјата на тековната филијала - таму гледате само едно извршување. Самата гранка е оставена недопрена, па нејзините заложби остануваат видливи во сопствената лента.",
    "settings.mergedView": "Споени обврски во графикот",
    "settings.mergedViewBranch": "Само на своја филијала",
    "settings.mergedViewTarget": "Исто така во гранката тие беа споени во",
    "settings.mergedViewBranchHint": "Обврските на споената гранка се извлекуваат само во нивната сопствена лента; спојувањето се прикажува како конектор. Класичниот поглед.",
    "settings.mergedViewTargetHint": "Обврските за спојување се нацртани во лентата на гранката што прима, исто така, наредени под обврзувањето за спојување и се израмнуваат со оригиналите - бидејќи таа гранка навистина ги содржи сега. Копиите се бледи и се наведени со гранката на која се напишани, така што останува јасно дека не се направени таму. Сквош спојувањата не се засегнати: сквош пишува еден обичен commit, па само тој commit некогаш се појавува.",
    "legend.title": "Легенда",
    "legend.head": "ГЛАВА / тековна гранка",
    "legend.local": "Локална филијала",
    "legend.remote": "Далечинска гранка",
    "legend.remoteOnly": "Само во облакот (не влечен)",
    "legend.tag": "Ознака (верзија)",
    "legend.commit": "Посветете се",
    "legend.stash": "Скривање (работа на полици)",
    "legend.mergedIn": "Дојде со спојување (напишано на друга гранка)",
    "legend.nodes": "Кутии",
    "legend.lines": "Линии",
    "legend.edgeParent": "Претходна заложба на истата линија",
    "legend.edgeMerge": "Спојувана гранка - стрелката покажува на заложбата за спојување",
    "legend.edgeBranch": "Овде се откина гранка",
    "legend.edgeStash": "Скриј и заложбата на која беше ставена на полица",
    "legend.edgeMergedChain": "Линијата на оваа гранка се протега низ спојуваните обврзници",
    "legend.edgeMergedTie": "Истата заложба на гранката на која пишуваше",
    "menu.jumpHead": "⌖ Одете на наплата",
    "menu.resetView": "⤢ Ресетирајте го приказот",
    "menu.createBranch": "Создајте филијала од тука…",
    "menu.checkout": "Проверете ја оваа обврска",
    "menu.copySha": "Copy commit SHA",
    "menu.pushBranch": "Притисни гранка „{name}“",
    "menu.renameCommit": "Преименувајте ја пораката за обврзување…",
    "menu.undoCommit": "Врати обврзување (задржи ги промените)…",
    "menu.viewChanges": "Погледнете ги промените…",
    "menu.jumpToOriginal": "Одете на оригиналниот commit",
    "node.mergedInTooltip": "Спојувањето на {merge} ја донесе оваа заложба во „{target}“ - тоа беше напишано на „{origin}“.",
    "changes.title": "Промени во {sha}",
    "changes.tabChanged": "Променет",
    "changes.tabAll": "Сите датотеки",
    "changes.added": "Додадено",
    "changes.modified": "Изменето",
    "changes.deleted": "Избришано",
    "changes.renamed": "Преименувано",
    "changes.noChanges": "Оваа заложба не менува датотеки.",
    "changes.loading": "Се вчитува разлика…",
    "changes.selectFile": "Изберете датотека за да ги видите нејзините промени.",
    "changes.original": "Оригинален",
    "changes.changed": "Оваа заложба",
    "changes.binary": "Бинарна датотека - нема разлика во текстот за прикажување.",
    "changes.tooLarge": "Датотеката е преголема за да се разликува.",
    "changes.renamedFrom": "Преименувано од {path}",
    "changes.close": "Затвори",
    "changes.prevChange": "Претходна промена",
    "changes.nextChange": "Следна промена",
    "changes.collapseAll": "Собери ги сите папки",
    "changes.expandAll": "Проширете ги сите папки",
    "changes.searchPlaceholder": "Пребарувајте датотеки…",
    "changes.noSearchResults": "Ниедна датотека не одговара на вашето пребарување.",
    "changes.maximize": "Максимизирај",
    "changes.restore": "Вратете ја големината",
    "find.open": "Пребарајте во оваа разлика (Ctrl+F)",
    "find.placeholder": "Најдете во различноста…",
    "find.prev": "Претходен натпревар",
    "find.next": "Следен натпревар",
    "find.close": "Затворете го пребарувањето",
    "find.noResults": "Нема резултати",
    "commit.title": "Обврски промени",
    "commit.close": "Затвори",
    "commit.loading": "Читање работно дрво…",
    "commit.noChanges": "Нема локални промени за извршување.",
    "commit.selectFile": "Изберете датотека за да ја видите нејзината разлика.",
    "commit.selectAll": "Изберете ги сите",
    "commit.selectNone": "Исчистете ги сите",
    "commit.message": "Обврска порака",
    "commit.messagePlaceholder": "Опишете ја промената",
    "commit.selectedCount": "Избрана(и) датотека(и) {count}",
    "commit.review": "Преглед",
    "commit.reviewTitle": "Конечна проверка пред да се изврши",
    "commit.reviewSummary": "Испратете датотека(и) {count} со порака: {message}",
    "commit.back": "Назад",
    "commit.commit": "Создадете локален commit",
    "menu.stashApply": "Примени скривам",
    "menu.stashPop": "Поп зачувај (примени и отстрани)",
    "menu.stashDrop": "Спуштете го прикривањето…",
    "merge.title": "Спојување на гранка",
    "merge.route": "Спојување во тековната гранка",
    "merge.source": "Од (извор)",
    "merge.target": "Во (тековно)",
    "merge.loading": "Се анализира спојувањето…",
    "merge.upToDate": "Веќе ажурирано - нема што да се спои.",
    "merge.fastForward": "Брзо напред — не е потребно извршување на спојување (штиклирајте „Креирај обврзување за спојување“ за да го присилите).",
    "merge.summary": "{files} датотека(и) се сменети, {conflicts} конфликт(и).",
    "merge.conflictsWarning": "Ова спојување има конфликти - сè уште можете да се споите, а потоа да ги решите во уредникот.",
    "merge.noChanges": "Нема промени во датотеката.",
    "merge.files": "Како резултат на промените",
    "merge.added": "Додадено",
    "merge.modified": "Изменето",
    "merge.deleted": "Избришано",
    "merge.conflict": "Конфликт",
    "merge.message": "Порака за обврзување на спојување",
    "merge.messagePlaceholder": "Спој гранка „извор“",
    "merge.messageFfHint": "Игнорирано при спојување брзо напред (не се создава обврзување).",
    "merge.noFastForward": "Создадете обврзување за спојување (без брзо напредување)",
    "merge.noFastForwardHint": "Секогаш снимајте обврзување за спојување, дури и кога гранката може брзо да напредува.",
    "merge.merge": "Спојување",
    "merge.cancel": "Откажи",
    "merge.previewError": "Не може да се пресмета преглед: {message}",
    "merge.squashBadge": "Сквош",
    "merge.squashNote": "Сквош спојување - секоја промена се приземјува додека ONE се обврзува на „{target}“; Сопствените обврски на „{source}“ остануваат надвор од неговата историја. Вратете се назад во Поставки › Спој.",
    "merge.messageSquash": "Обврска порака",
    "merge.squashMessagePlaceholder": "Скршени промени од „извор“",
    "newBranch.title": "Креирај филијала",
    "newBranch.startPoint": "Нова филијала почнувајќи од {sha}",
    "newBranch.startPointOn": "на {refs}",
    "newBranch.location": "Локација",
    "newBranch.locationRoot": "(корен)",
    "newBranch.expandAll": "Прошири ги сите",
    "newBranch.collapseAll": "Собори ги сите",
    "newBranch.expandFolder": "Проширете ја папката",
    "newBranch.collapseFolder": "Собери ја папката",
    "newBranch.name": "Име на филијала",
    "newBranch.namePlaceholder": "мојата-гранка",
    "newBranch.fullName": "Целосно име: {name}",
    "newBranch.invalid": "Неважечко име на гранка",
    "newBranch.exists": "Веќе постои гранка со ова име",
    "newBranch.checkout": "Исплата гранка по создавањето",
    "newBranch.create": "Креирај",
    "newBranch.cancel": "Откажи",
    "status.loading": "Се вчитува графиконот…",
    "status.summary": "{repo} Прикажување на {commits} обврзници, {refs} реф",
    "status.branchCreated": "Создадена гранка „{name}“ на {sha}",
    "status.error": "Грешка: {message}",
    "status.opFailed": "Операцијата не успеа",
    "status.fetching": "Се презема…",
    "status.pulling": "Повлекување…",
    "status.pushing": "Туркање…",
    "status.committing": "Се создава локален ангажман…",
    "status.commitCreated": "Создаден локален залог {sha}: {message}",
    "status.syncing": "Се синхронизира…",
    "status.noHead": "Не е пронајдено тековно плаќање во графиконот.",
    "status.undoing": "Поништување на обврската…",
    "status.commitUndone": "Обврската е вратена - промените се враќаат во работното стебло.",
    "status.undoConflict": "Вратете ги конфликтите на ударите - решете ги во уредникот, а потоа продолжете.",
    "status.stashApplying": "Се применува зачувување…",
    "status.stashPopping": "Се појавува скришно место…",
    "status.stashDropping": "Испуштање на скривалиште…",
    "status.stashApplied": "Применет е Скриј.",
    "status.stashPopped": "Скринието се појави.",
    "status.stashDropped": "Скриј падна.",
    "status.stashConflict": "Скријте конфликти - решете ги во уредникот.",
    "status.merging": "Спојување…",
    "status.merged": "Спојувањето е завршено.",
    "status.mergeConflict": "Спојувањето има конфликти - решете ги во уредникот, а потоа извршете.",
    "search.title": "Пребарување",
    "search.tooltip": "Обврски за пребарување",
    "search.placeholder": "Пребарувањето обврзува…",
    "search.previous": "Претходен резултат",
    "search.next": "Следен резултат",
    "search.noResults": "Нема резултати",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Режим на филтрирање",
    "search.highlight": "Режим за означување",
    "search.filterMode": "Приказ за пребарување",
    "search.filterModeFilter": "Филтер (сокриј што не се совпаѓа)",
    "search.filterModeHighlight": "Нагласи (прикажи ги сите, означи совпаѓања)",
    "search.filterModeHint": "Изберете како резултатите од пребарувањето се прикажуваат на графиконот.",
    "search.visibility": "Пребарајте локација",
    "search.visibilityToolbar": "Копче на лентата со алатки",
    "search.visibilityAlwaysVisible": "Секогаш видлива лента",
    "search.visibilityHint": "Прикажи го пребарувањето како икона на лентата со алатки или постојана лента на врвот.",
    "footer.github": "GitHub",
  },
  mn: {
    "toolbar.refresh": "⟳ Сэргээх",
    "toolbar.fetch": "⤓ Авах",
    "toolbar.pull": "⇩ Татах",
    "toolbar.push": "⇧ түлхэх",
    "toolbar.commit": "✓ Амлалт өгөх",
    "toolbar.sync": "⇅ Синк хийх",
    "toolbar.jumpHead": "⌖ Тооцоо хийх хэсэгт очно уу",
    "toolbar.reset": "⤢ Харагдах байдлыг дахин тохируулах",
    "toolbar.settings": "⚙ Тохиргоо",
    "details.header": "Дэлгэрэнгүй мэдээлэл өгөх",
    "details.close": "Хаах",
    "details.sha": "ША",
    "details.shortSha": "Богино SHA",
    "details.message": "Мессеж",
    "details.author": "Зохиогч",
    "details.date": "Огноо",
    "details.labels": "Шошго",
    "details.location": "Байршил",
    "details.currentHead": "HEAD - одоогийн тооцоо",
    "settings.title": "Тохиргоо",
    "settings.language": "Хэл",
    "settings.theme": "Сэдэв",
    "settings.themeLight": "Гэрэл",
    "settings.themeDark": "Харанхуй",
    "settings.themeLightHint": "Цайвар дэвсгэр, бараан текст.",
    "settings.themeDarkHint": "Бараан дэвсгэр, цайвар текст.",
    "settings.mainBranch": "Үндсэн салбар",
    "settings.mainBranchAuto": "Автомат",
    "settings.mainBranchSearch": "Салбар хайх...",
    "settings.mainBranchNoMatch": "Тохирох салбар байхгүй байна",
    "settings.display": "Дэлгэцийн хэв маяг",
    "settings.displayModern": "Орчин үеийн",
    "settings.displayClassic": "Сонгодог",
    "settings.displayModernHint": "Чөлөөт зотон зураг — хайруулын тавган дээр чирж, томруулахын тулд гүйлгэ.",
    "settings.displayClassicHint": "Тогтмол зураг — их бие зүүн тийш хавчуулсан, томруулахгүй, зөвхөн гүйлгэх боломжтой (SVN засварын график гэх мэт).",
    "settings.sectionGeneral": "Генерал",
    "settings.sectionGraph": "График",
    "settings.sectionChanges": "Харагдах байдлыг өөрчилдөг",
    "settings.diffMinimap": "Диф мини зураг",
    "settings.diffMinimapOn": "Үзүүлсэн",
    "settings.diffMinimapOff": "Нуугдсан",
    "settings.diffMinimapOnHint": "Ялгааны хажууд VS код маягийн тойм зурвас харуулах — гүйлгэхийн тулд чирнэ үү; өөрчлөлтийн тэмдэглэгээг оруулсан болно.",
    "settings.diffMinimapOffHint": "Тойм зурвас байхгүй — ялгааг хэвийн гүйлгэ.",
    "settings.commitReview": "Шалгалт хийх",
    "settings.commitReviewOn": "Үйлдэл хийхээсээ өмнө асуу",
    "settings.commitReviewOff": "Шууд үүрэг өг",
    "settings.commitReviewOnHint": "Орон нутгийн амлалт үүсгэхээс өмнө эцсийн файлын жагсаалт болон мессежийг шалгана уу.",
    "settings.commitReviewOffHint": "Commit товчлуур нь локал коммитыг нэн даруй үүсгэдэг.",
    "settings.svnBranchDialog": "SVN загварын салбар харилцах цонх",
    "settings.svnBranchDialogHint": "Салбар үүсгэх үед хавтас мод сонгогчийг харуулах (энгийн сануулгын оронд).",
    "settings.branchDialog": "Салбарын харилцах цонх",
    "settings.branchDialogSvnTitle": "SVN хэв маяг",
    "settings.branchDialogNativeVscode": "VS кодын хэв маяг",
    "settings.branchDialogNativeVs": "Visual Studio загвар",
    "settings.branchDialogNativeHint": "Таны IDE-ийн суулгасан салбар сануулга.",
    "settings.close": "Хаах",
    "settings.done": "Дууслаа",
    "settings.sectionAdvanced": "Дэвшилтэт",
    "settings.gitSource": "Git гүйцэтгэх боломжтой",
    "settings.gitSourceBuiltin": "Баригдсан",
    "settings.gitSourceBuiltinHint": "IDE-ийн Git өргөтгөлөөс git хоёртын файлыг ашиглана уу.",
    "settings.gitSourceCustom": "Захиалгат зам",
    "settings.gitPath": "Git хүрэх зам",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Үзэх...",
    "settings.jargon": "Git нөхцөл",
    "settings.jargonTranslate": "Орчуулах",
    "settings.jargonEnglish": "Англи хэл дээр хадгал",
    "settings.jargonTranslateHint": "Git-ийн нэр томъёог (татах, түлхэх, гүйцэтгэх, салбарлах...) интерфэйсийн хэл рүү орчуулна уу.",
    "settings.jargonEnglishHint": "Git-н нэр томъёог (татах, түлхэх, гүйцэтгэх, салбарлах...) англи хэл дээр хадгална; бусад бүх зүйлийг орчуулах.",
    "settings.sectionMerge": "Нэгтгэх",
    "settings.mergeMode": "Загварыг нэгтгэх",
    "settings.mergeModeMerge": "Ердийн нэгдэл",
    "settings.mergeModeSquash": "Сквош нэгтгэх",
    "settings.mergeModeMergeHint": "Салбарыг байгаагаар нь нэгтгэдэг: түүний үүрэг нь одоогийн салбарын түүхийн нэг хэсэг болж, нэгтгэх комментоор нэгддэг (эсвэл боломжтой бол хурдан дамжуулдаг).",
    "settings.mergeModeSquashHint": "Бүхэл бүтэн салбарыг одоогийн салбар дээр нэг үүрэгт болгон буулгана (git merge --squash). Салбарын өөрийн амлалтууд нь одоогийн салбарын түүхэнд хэзээ ч ордоггүй - та тэнд зөвхөн нэг үйлдлийг хардаг. Салбар нь өөрөө хөндөгдөөгүй тул үйлдлүүд нь өөрийн эгнээнд харагдах болно.",
    "settings.mergedView": "График дахь нэгтгэсэн үүрэг хариуцлага",
    "settings.mergedViewBranch": "Зөвхөн өөрсдийн салбар дээр",
    "settings.mergedViewTarget": "Мөн салбартаа тэднийг нэгтгэсэн",
    "settings.mergedViewBranchHint": "Нэгдсэн салбаруудын үүргүүдийг зөвхөн өөрийн эгнээнд зурдаг; нэгтгэх нь холбогч хэлбэрээр харагдана. Сонгодог үзэл.",
    "settings.mergedViewTargetHint": "Оруулсан нэгдэл нь хүлээн авагчийн эгнээнд мөн адил зурж, нэгтгэх үүргийн доор давхарлаж, эх хувьтай нь тэгшилдэг - учир нь энэ салбар үнэхээр одоо тэдгээрийг агуулж байна. Хуулбарууд нь цайвар өнгөтэй, дээр нь бичсэн мөчиртэй хамт байгаа тул тэнд хийгээгүй нь тодорхой хэвээр байна. Сквош нэгдлүүд нь нөлөөлөлд өртөхгүй: сквош нь нэг энгийн коммит бичдэг тул зөвхөн тэр амлалт гарч ирдэг.",
    "legend.title": "Домог",
    "legend.head": "HEAD / одоогийн салбар",
    "legend.local": "Орон нутгийн салбар",
    "legend.remote": "Алсын салбар",
    "legend.remoteOnly": "Зөвхөн үүлэн дотор (татаагүй)",
    "legend.tag": "Шошго (хувилбар)",
    "legend.commit": "Амлах",
    "legend.stash": "Сташ (тавиуртай ажил)",
    "legend.mergedIn": "Нэгтгэж ирсэн (өөр салбар дээр бичсэн)",
    "legend.nodes": "Хайрцаг",
    "legend.lines": "Шугамууд",
    "legend.edgeParent": "Өмнө нь ижил мөрөнд үйлдсэн",
    "legend.edgeMerge": "Нэгтгэсэн салбар - сум нь нэгтгэх үйлдлийг зааж байна",
    "legend.edgeBranch": "Эндээс нэг салбар салаав",
    "legend.edgeStash": "Stash болон түүнийг тавиур дээр тавьсан байсан",
    "legend.edgeMergedChain": "Энэ салбарын шугам нь нэгтгэсэн үүргүүдээр дамждаг",
    "legend.edgeMergedTie": "Салбар дээр нь мөн адил амлалт бичдэг",
    "menu.jumpHead": "⌖ Тооцоо хийх хэсэгт очно уу",
    "menu.resetView": "⤢ Харагдах байдлыг дахин тохируулах",
    "menu.createBranch": "Эндээс салбар үүсгэх...",
    "menu.checkout": "Энэ амлалтыг шалгана уу",
    "menu.copySha": "SHA-г хуулах",
    "menu.pushBranch": "\"{name}\" салбарыг дарах",
    "menu.renameBranch": "Салбарыг \"{name}\"-ийн нэрийг өөрчлөх",
    "menu.renameCommit": "Захиалах мессежийн нэрийг өөрчлөх...",
    "menu.undoCommit": "Үйлдлийг буцаах (өөрчлөлтийг хадгалах)…",
    "menu.viewChanges": "Өөрчлөлтүүдийг харах...",
    "menu.jumpToOriginal": "Анхны амлалт руу оч",
    "changes.title": "{sha}-д гарсан өөрчлөлтүүд",
    "changes.tabChanged": "Өөрчлөгдсөн",
    "changes.tabAll": "Бүх файлууд",
    "changes.added": "Нэмсэн",
    "changes.modified": "Өөрчлөгдсөн",
    "changes.deleted": "Устгасан",
    "changes.renamed": "Нэр нь өөрчлөгдсөн",
    "changes.noChanges": "Энэ үүрэг нь ямар ч файлыг өөрчлөхгүй.",
    "changes.loading": "Ялгааг ачаалж байна…",
    "changes.selectFile": "Өөрчлөлтийг нь харахын тулд файлаа сонгоно уу.",
    "changes.original": "Жинхэнэ",
    "changes.changed": "Энэ амлалт",
    "changes.binary": "Хоёртын файл — харуулах текстийн ялгаа байхгүй.",
    "changes.tooLarge": "Файл нь ялгахад хэтэрхий том байна.",
    "changes.renamedFrom": "{path}-ээс нэрийг нь өөрчилсөн",
    "changes.close": "Хаах",
    "changes.prevChange": "Өмнөх өөрчлөлт",
    "changes.nextChange": "Дараагийн өөрчлөлт",
    "changes.collapseAll": "Бүх фолдерыг хумих",
    "changes.expandAll": "Бүх фолдерыг дэлгэнэ үү",
    "changes.searchPlaceholder": "Файл хайх...",
    "changes.noSearchResults": "Таны хайлтад тохирох файл алга.",
    "changes.maximize": "Томруулах",
    "changes.restore": "Хэмжээг сэргээх",
    "find.open": "Энэ ялгаагаар хайх (Ctrl+F)",
    "find.placeholder": "Ялгаагаар олох…",
    "find.prev": "Өмнөх тоглолт",
    "find.next": "Дараагийн тоглолт",
    "find.close": "Хайлтыг хаах",
    "find.noResults": "Үр дүн алга",
    "commit.title": "Өөрчлөлт оруулах",
    "commit.close": "Хаах",
    "commit.loading": "Ажлын модыг уншиж байна...",
    "commit.noChanges": "Орон нутагт хийх өөрчлөлт байхгүй.",
    "commit.selectFile": "Ялгааг нь харахын тулд файлыг сонгоно уу.",
    "commit.selectAll": "Бүгдийг сонгоно уу",
    "commit.selectNone": "Бүгдийг арилгах",
    "commit.message": "Зурвас илгээх",
    "commit.messagePlaceholder": "Өөрчлөлтийг тайлбарлана уу",
    "commit.selectedCount": "{count} файлыг сонгосон",
    "commit.review": "Шүүмж",
    "commit.reviewTitle": "Үйлдэл хийхээс өмнө эцсийн шалгалт",
    "commit.reviewSummary": "{count} файлыг илгээх: {message}",
    "commit.back": "Буцах",
    "commit.commit": "Орон нутгийн амлалт үүсгэх",
    "menu.stashApply": "Хадгалах",
    "menu.stashPop": "Поп stash (хэрэглэх, устгах)",
    "menu.stashDrop": "Хаяг хаях...",
    "merge.title": "Салбарыг нэгтгэх",
    "merge.route": "Одоогийн салбар руу нэгдэж байна",
    "merge.source": "Эх сурвалжаас (эх сурвалж)",
    "merge.target": "Дотор (одоогийн)",
    "merge.loading": "Нийлэхэд дүн шинжилгээ хийж байна...",
    "merge.upToDate": "Аль хэдийн шинэчлэгдсэн - нэгтгэх зүйл алга.",
    "merge.fastForward": "Хурдан урагшлуулах - нэгтгэх үүрэг хүлээх шаардлагагүй (\"Нэгдүүлэх үүрэг\"-ийг идэвхжүүлэхийн тулд \"Нэгдүүлэх үүрэг\"-ийг сонгоно уу).",
    "merge.summary": "{files} файл(ууд) өөрчлөгдсөн, {conflicts} зөрчил(үүд).",
    "merge.conflictsWarning": "Энэ нэгдэл нь зөрчилтэй байна - та нэгтгэж, дараа нь засварлагчаар шийдвэрлэх боломжтой.",
    "merge.noChanges": "Файлын өөрчлөлт байхгүй.",
    "merge.files": "Үүний үр дүнд гарсан өөрчлөлтүүд",
    "merge.added": "Нэмсэн",
    "merge.modified": "Өөрчлөгдсөн",
    "merge.deleted": "Устгасан",
    "merge.conflict": "Зөрчилдөөн",
    "merge.message": "Үйлдлийн мессежийг нэгтгэх",
    "merge.messagePlaceholder": "Салбарын \"эх сурвалж\"-ыг нэгтгэх",
    "merge.messageFfHint": "Хурдан урагшлах нэгтгэхэд үл тоомсорлодог (ямар ч үүрэг хүлээгдээгүй).",
    "merge.noFastForward": "Нэгтгэх үүрэг үүсгэх (хурдан урагшлуулахгүй)",
    "merge.noFastForwardHint": "Салбар хурдан урагшлах боломжтой байсан ч нэгтгэх амлалтаа үргэлж бүртгээрэй.",
    "merge.merge": "Нэгтгэх",
    "merge.cancel": "Цуцлах",
    "merge.previewError": "Урьдчилан үзэхийг тооцоолж чадсангүй: {message}",
    "merge.squashBadge": "Сквош",
    "merge.squashNote": "Сквош нэгтгэх - өөрчлөлт бүр нь \"{target}\" дээр НЭГДСЭН үүрэг хүлээснээр буух; \"{source}\"-ийн өөрийн амлалтууд нь түүний түүхэнд үлддэг. Тохиргоо › Нэгтгэх хэсэгт буцаж сэлгэнэ үү.",
    "merge.messageSquash": "Зурвас илгээх",
    "merge.squashMessagePlaceholder": "\"Эх сурвалж\"-аас жижиглэсэн өөрчлөлтүүд",
    "newBranch.title": "Салбар үүсгэх",
    "newBranch.startPoint": "{sha}-с эхлэн шинэ салбар",
    "newBranch.startPointOn": "{refs} дээр",
    "newBranch.location": "Байршил",
    "newBranch.locationRoot": "(үндэс)",
    "newBranch.expandAll": "Бүгдийг өргөжүүлэх",
    "newBranch.collapseAll": "Бүгдийг нураа",
    "newBranch.expandFolder": "Фолдерыг өргөжүүлэх",
    "newBranch.collapseFolder": "Фолдерыг буулгах",
    "newBranch.name": "Салбарын нэр",
    "newBranch.namePlaceholder": "миний салбар",
    "newBranch.fullName": "Бүтэн нэр: {name}",
    "newBranch.invalid": "Салбарын нэр буруу байна",
    "newBranch.exists": "Ийм нэртэй салбар аль хэдийн байна",
    "newBranch.checkout": "Үүсгэсэний дараа тооцоо хийх салбар",
    "newBranch.create": "Үүсгэх",
    "newBranch.cancel": "Цуцлах",
    "status.loading": "Графикийг ачаалж байна...",
    "status.summary": "{repo}{commits}-ийн үйлдлийг харуулж байна, {refs}-ийн лавлагаа",
    "status.branchCreated": "{sha} дээр \"{name}\" салбарыг үүсгэсэн",
    "status.error": "Алдаа: {message}",
    "status.opFailed": "Ажиллагаа амжилтгүй боллоо",
    "status.fetching": "Дуудаж байна...",
    "status.pulling": "Татаж байна...",
    "status.pushing": "Түлхэж байна...",
    "status.committing": "Орон нутгийн амлалт үүсгэж байна...",
    "status.commitCreated": "{sha}: {message} орон нутгийн амлалт үүсгэсэн",
    "status.syncing": "Синк хийж байна...",
    "status.noHead": "Графикаас одоогийн тооцоо олдсонгүй.",
    "status.undoing": "Амлалтаа цуцалж байна...",
    "status.commitUndone": "Үйлдлийг болиулсан - өөрчлөлтүүд ажлын мод руу буцаж байна.",
    "status.undoConflict": "Зөрчилдөөнийг буцаах - тэдгээрийг засварлагчаар шийдэж, дараа нь үргэлжлүүлнэ үү.",
    "status.stashApplying": "Нөөцийг ашиглаж байна...",
    "status.stashPopping": "Хадгалах газар...",
    "status.stashDropping": "Хаяг хаяж байна...",
    "status.stashApplied": "Stash ашигласан.",
    "status.stashPopped": "Сташ гарч ирэв.",
    "status.stashDropped": "Сташ унав.",
    "status.stashConflict": "Зөрчилдөөнийг хадгалах - тэдгээрийг засварлагч дээр шийдвэрлэх.",
    "status.merging": "Нэгтгэж байна...",
    "status.merged": "Нэгтгэж дууслаа.",
    "status.mergeConflict": "Нэгтгэх нь зөрчилтэй байна - тэдгээрийг засварлагчаар шийдэж, дараа нь гүйцэтгээрэй.",
    "search.title": "Хайх",
    "search.tooltip": "Хайлтын даалгавар",
    "search.placeholder": "Хайлтын даалгавар...",
    "search.previous": "Өмнөх үр дүн",
    "search.next": "Дараагийн үр дүн",
    "search.noResults": "Үр дүн алга",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Шүүлтүүрийн горим",
    "search.highlight": "Тодруулга горим",
    "search.filterMode": "Дэлгэц хайх",
    "search.filterModeFilter": "Шүүлтүүр (тохирохгүй зүйлийг нуух)",
    "search.filterModeHighlight": "Тодруулга (бүгдийг харуулах, тохирохыг тэмдэглэх)",
    "search.filterModeHint": "Хайлтын үр дүнг график дээр хэрхэн харуулахыг сонгоно уу.",
    "search.visibility": "Байршлыг хайх",
    "search.visibilityToolbar": "Хэрэгслийн самбарын товчлуур",
    "search.visibilityAlwaysVisible": "Үргэлж харагдах баар",
    "search.visibilityHint": "Хайлтыг дээд талд нь хэрэгслийн самбарын дүрс эсвэл байнгын мөр болгон харуулах.",
    "footer.github": "GitHub",
  },
  sr: {
    "toolbar.refresh": "⟳ Освежи",
    "toolbar.fetch": "⤓ Дохвати",
    "toolbar.pull": "⇩ Повуците",
    "toolbar.push": "⇧ Гурните",
    "toolbar.commit": "✓ Посветите се",
    "toolbar.sync": "⇅ Синц",
    "toolbar.jumpHead": "⌖ Идите на касу",
    "toolbar.reset": "⤢ Ресетујте приказ",
    "toolbar.settings": "⚙ Подешавања",
    "details.header": "Цоммит Детаилс",
    "details.close": "Затвори",
    "details.sha": "СХА",
    "details.shortSha": "Кратки СХА",
    "details.message": "Порука",
    "details.author": "Аутор",
    "details.date": "Датум",
    "details.labels": "Етикете",
    "details.location": "Локација",
    "details.currentHead": "ГЛАВА — тренутна наплата",
    "settings.title": "Подешавања",
    "settings.language": "Језик",
    "settings.theme": "Тема",
    "settings.themeLight": "Светлост",
    "settings.themeDark": "Дарк",
    "settings.themeLightHint": "Светла позадина, тамни текст.",
    "settings.themeDarkHint": "Тамна позадина, светли текст.",
    "settings.mainBranch": "Главна грана",
    "settings.mainBranchAuto": "Аутоматски",
    "settings.mainBranchSearch": "Претражи гране…",
    "settings.mainBranchNoMatch": "Нема одговарајућих грана",
    "settings.display": "Стил приказа",
    "settings.displayModern": "Модерна",
    "settings.displayClassic": "Цлассиц",
    "settings.displayModernHint": "Бесплатно платно — превуците за померање, скролујте да бисте зумирали.",
    "settings.displayClassicHint": "Фиксно платно — дебло закачено лево, без зумирања, само скроловање (као СВН ревизиони графикон).",
    "settings.sectionGeneral": "генерал",
    "settings.sectionGraph": "Граф",
    "settings.sectionChanges": "Мења поглед",
    "settings.diffMinimap": "Дифф минимапа",
    "settings.diffMinimapOn": "Показано",
    "settings.diffMinimapOff": "Скривено",
    "settings.diffMinimapOnHint": "Прикажите траку за преглед у стилу ВС кода поред разлике — превуците је да бисте се померали; маркери промене укључени.",
    "settings.diffMinimapOffHint": "Нема траке за преглед — нормално скролујте диф.",
    "settings.commitReview": "Учините преглед",
    "settings.commitReviewOn": "Питајте пре обавезивања",
    "settings.commitReviewOff": "Обавезно директно",
    "settings.commitReviewOnHint": "Прикажи коначну листу датотека и проверу порука пре креирања локалног урезивања.",
    "settings.commitReviewOffHint": "Дугме Урежи одмах креира локално урезивање.",
    "settings.svnBranchDialog": "Дијалог гране у СВН стилу",
    "settings.svnBranchDialogHint": "Прикажите бирач стабла фасцикли када креирате грану (уместо једноставног упита).",
    "settings.branchDialog": "Дијалог гране",
    "settings.branchDialogSvnTitle": "СВН-стил",
    "settings.branchDialogNativeVscode": "ВС стил кода",
    "settings.branchDialogNativeVs": "Висуал Студио стил",
    "settings.branchDialogNativeHint": "Уграђени промпт гране вашег ИДЕ-а.",
    "settings.close": "Затвори",
    "settings.done": "Готово",
    "settings.sectionAdvanced": "Напредно",
    "settings.gitSource": "Гит извршни",
    "settings.gitSourceBuiltin": "Уграђени",
    "settings.gitSourceBuiltinHint": "Користите гит бинарни фајл из уграђеног Гит екстензије у ИДЕ.",
    "settings.gitSourceCustom": "Прилагођена путања",
    "settings.gitSourceCustomHint": "Одредите путању до сопственог гит извршног фајла (нпр. /уср/бин/гит или Ц:\\А¢АА¦\\гит.еке).",
    "settings.gitPath": "Пут до гит-а",
    "settings.gitPathPlaceholder": "/уср/бин/гит",
    "settings.gitPathBrowse": "Прегледај…",
    "settings.jargon": "Гит термини",
    "settings.jargonTranslate": "Преведи",
    "settings.jargonEnglish": "Држите на енглеском",
    "settings.jargonTranslateHint": "Преведите Гит термине (повући, пусх, урезивање, гранање...) у језик интерфејса.",
    "settings.jargonEnglishHint": "Задржите Гит термине (повући, потисните, урезивање, гранање...) на енглеском; преводи све остало.",
    "settings.sectionMerge": "Споји",
    "settings.mergeMode": "Споји стил",
    "settings.mergeModeMerge": "Нормално спајање",
    "settings.mergeModeSquash": "Скуасх спајање",
    "settings.mergeModeMergeHint": "Спаја грану каква јесте: њена урезивања постају део историје тренутне гране, придружена спајањем урезивања (или брзо премотавање унапред када је то могуће).",
    "settings.mergeModeSquashHint": "Сажима целу грану у једно урезивање на тренутној грани (гит мерге --скуасх). Сопствена урезивања гране никада не улазе у историју тренутне гране – тамо видите само једно урезивање. Сама грана је остављена нетакнута, тако да њени делови остају видљиви у сопственој траци.",
    "settings.mergedView": "Обједињена урезивања на графикону",
    "settings.mergedViewBranch": "Само на сопственој грани",
    "settings.mergedViewTarget": "Такође у грани у коју су спојени",
    "settings.mergedViewBranchHint": "Урезивања спојене гране се цртају само у сопственој траци; спајање се појављује као конектор. Класичан поглед.",
    "settings.mergedViewTargetHint": "Урезивања које је унето спајањем такође су исцртане у траци гране пријема, наслагане испод урезивања спајања и у равни са оригиналима — јер их та грана сада заиста садржи. Копије су бледе и заглављене са граном на којој су исписане, тако да остаје јасно да нису тамо направљене. Скуасх спајања су непромењена: скуасх пише једно обично урезивање, тако да се увек појављује само то урезивање.",
    "legend.title": "Легенда",
    "legend.head": "ГЛАВА / тренутна грана",
    "legend.local": "Локална филијала",
    "legend.remote": "Удаљена грана",
    "legend.remoteOnly": "Само у облаку (не повучено)",
    "legend.tag": "Ознака (верзија)",
    "legend.commit": "Цоммит",
    "legend.stash": "Складиште (одложени рад)",
    "legend.mergedIn": "Ушао са спајањем (написано на другој грани)",
    "legend.nodes": "Кутије",
    "legend.lines": "Линије",
    "legend.edgeParent": "Претходно урезивање на истој линији",
    "legend.edgeMerge": "Обједињена грана — стрелица показује на урезивање спајања",
    "legend.edgeBranch": "Овде се рачвала грана",
    "legend.edgeStash": "Стасх и урезивање на које је било одложено",
    "legend.edgeMergedChain": "Линија ове гране пролази кроз спојена урезивања",
    "legend.edgeMergedTie": "Исти урез на грани на којој је написано",
    "menu.jumpHead": "⌖ Идите на касу",
    "menu.resetView": "⤢ Ресетујте приказ",
    "menu.createBranch": "Креирајте грану одавде…",
    "menu.checkout": "Погледајте ову обвезу",
    "menu.copySha": "Копирај урезивање СХА",
    "menu.pushBranch": "Пусх грана \"КСПЛХ0Кс\"",
    "menu.renameBranch": "Преименујте грану „КСПЛХ0Кс“а¦",
    "menu.deleteBranch": "Избришите грану „КСПЛХ0Кс“а¦",
    "menu.renameCommit": "Преименуј поруку урезивања…",
    "menu.undoCommit": "Опозови урезивање (задржи промене)…",
    "menu.viewChanges": "Прикажи промене…",
    "menu.mergeBranch": "Спојите „КСПЛХ0Кс“ у „КСПЛХ1Кс“а¦",
    "menu.jumpToOriginal": "Идите на оригинално урезивање",
    "node.mergedInTooltip": "Обједињавање КСПЛХ0Кс је донело ово урезивање у \"КСПЛХ1Кс\" А¢АА написано је на \"КСПЛХ2Кс\".",
    "changes.title": "Промене у КСПЛХ0Кс",
    "changes.tabChanged": "Промењено",
    "changes.tabAll": "Све датотеке",
    "changes.added": "Додато",
    "changes.modified": "Модификовано",
    "changes.deleted": "Избрисано",
    "changes.renamed": "Преименовано",
    "changes.noChanges": "Ово урезивање не мења датотеке.",
    "changes.loading": "Учитавање разлике…",
    "changes.selectFile": "Изаберите датотеку да бисте видели њене промене.",
    "changes.original": "Оригинал",
    "changes.changed": "Ова обавеза",
    "changes.binary": "Бинарна датотека — нема разлике текста за приказ.",
    "changes.tooLarge": "Датотека је превелика за разлику.",
    "changes.renamedFrom": "Преименовано из КСПЛХ0Кс",
    "changes.close": "Затвори",
    "changes.prevChange": "Претходна промена",
    "changes.nextChange": "Следећа промена",
    "changes.collapseAll": "Скупи све фасцикле",
    "changes.expandAll": "Проширите све фасцикле",
    "changes.searchPlaceholder": "Претражи датотеке…",
    "changes.noSearchResults": "Ниједна датотека не одговара вашој претрази.",
    "changes.maximize": "Максимизирајте",
    "changes.restore": "Врати величину",
    "find.open": "Тражи у овој диф (Цтрл+Ф)",
    "find.placeholder": "Пронађи у дифф…",
    "find.prev": "Претходни меч",
    "find.next": "Следећи меч",
    "find.close": "Затвори претрагу",
    "find.noResults": "Нема резултата",
    "commit.title": "Урезивање промена",
    "commit.close": "Затвори",
    "commit.loading": "Читање радног стабла…",
    "commit.noChanges": "Нема локалних промена за обавезивање.",
    "commit.selectFile": "Изаберите датотеку да бисте видели њену разлику.",
    "commit.selectAll": "Изаберите све",
    "commit.selectNone": "Обриши све",
    "commit.message": "Порежите поруку",
    "commit.messagePlaceholder": "Опишите промену",
    "commit.selectedCount": "Одабран(е) фајл(е) КСПЛХ0Кс",
    "commit.review": "Преглед",
    "commit.reviewTitle": "Коначна провера пре урезивања",
    "commit.reviewSummary": "Урежите КСПЛХ0Кс фајл(е) са поруком: КСПЛХ1Кс",
    "commit.back": "Назад",
    "commit.commit": "Креирајте локално урезивање",
    "menu.stashApply": "Примени залихе",
    "menu.stashPop": "Поп залиха (примени и уклони)",
    "menu.stashDrop": "Баци залихе…",
    "merge.title": "Мерге Бранцх",
    "merge.route": "Спајање у тренутну грану",
    "merge.source": "Од (извор)",
    "merge.target": "у (тренутно)",
    "merge.loading": "Анализира се спајање…",
    "merge.upToDate": "Већ ажуриран — ништа за спајање.",
    "merge.fastForward": "Премотавање унапред — није потребно урезивање спајањем (означите „Креирај урезивање спајањем“ да бисте га присилили).",
    "merge.summary": "КСПЛХ0Кс фајл(е) промењен(е), КСПЛХ1Кс конфликт(и).",
    "merge.conflictsWarning": "Ово спајање има сукобе — још увек можете да их спојите, а затим их решите у уређивачу.",
    "merge.noChanges": "Нема промена датотеке.",
    "merge.files": "Настале промене",
    "merge.added": "Додато",
    "merge.modified": "Модификовано",
    "merge.deleted": "Избрисано",
    "merge.conflict": "Сукоб",
    "merge.message": "Обједињавање поруке урезивања",
    "merge.messagePlaceholder": "Споји 'извор' гране",
    "merge.messageFfHint": "Занемарен при брзом премотавању унапред (урезивање није направљено).",
    "merge.noFastForward": "Направите урезивање спајања (без брзог премотавања унапред)",
    "merge.noFastForwardHint": "Увек снимајте урезивање спајања, чак и када грана може да се премотава унапред.",
    "merge.merge": "Споји",
    "merge.cancel": "Откажи",
    "merge.previewError": "Није могуће израчунати преглед: КСПЛХ0Кс",
    "merge.squashBadge": "Скуасх",
    "merge.squashNote": "Скуасх спајање — свака промена долази као ЈЕДНО урезивање на „КСПЛХ0Кс“; „КСПЛХ1Кс” сопствене обавезе остају ван његове историје. Вратите се у Подешавања › Споји.",
    "merge.messageSquash": "Порежите поруку",
    "merge.squashMessagePlaceholder": "Згуснуте промене из 'извора'",
    "newBranch.title": "Цреате Бранцх",
    "newBranch.startPoint": "Нова грана почевши од КСПЛХ0Кс",
    "newBranch.startPointOn": "на КСПЛХ0Кс",
    "newBranch.location": "Локација",
    "newBranch.locationRoot": "(корен)",
    "newBranch.expandAll": "Прошири све",
    "newBranch.collapseAll": "Скупи све",
    "newBranch.expandFolder": "Прошири фасциклу",
    "newBranch.collapseFolder": "Скупи фасциклу",
    "newBranch.name": "Назив огранка",
    "newBranch.namePlaceholder": "моја-грана",
    "newBranch.fullName": "Пуно име: КСПЛХ0Кс",
    "newBranch.invalid": "Неважећи назив филијале",
    "newBranch.exists": "Огранак са овим именом већ постоји",
    "newBranch.checkout": "Филијала за касу након креирања",
    "newBranch.create": "Креирај",
    "newBranch.cancel": "Откажи",
    "status.loading": "Учитавање графикона…",
    "status.summary": "КСПЛХ0Кс Приказ КСПЛХ1Кс урезивања, КСПЛХ2Кс реф",
    "status.branchCreated": "Направљена грана \"КСПЛХ0Кс\" на КСПЛХ1Кс",
    "status.error": "Грешка: КСПЛХ0Кс",
    "status.opFailed": "Операција није успела",
    "status.fetching": "Преузимање…",
    "status.pulling": "Повлачење…",
    "status.pushing": "Гурање…",
    "status.committing": "Прављење локалне урезивања…",
    "status.commitCreated": "Направљено локално урезивање КСПЛХ0Кс: КСПЛХ1Кс",
    "status.syncing": "Синхронизација…",
    "status.noHead": "На графикону није пронађено тренутно плаћање.",
    "status.undoing": "Поништавање урезивања…",
    "status.commitUndone": "Урезивање је опозвано — промене су поново у радном стаблу.",
    "status.undoConflict": "Поништите сукобе погодака — решите их у уређивачу, а затим наставите.",
    "status.stashApplying": "Примена скривеног…",
    "status.stashPopping": "Искакање залихе…",
    "status.stashDropping": "Избацивање залихе…",
    "status.stashApplied": "Складиште примењено.",
    "status.stashPopped": "Складиште је искочило.",
    "status.stashDropped": "Складиште је испало.",
    "status.stashConflict": "Спремите конфликте — решите их у уређивачу.",
    "status.merging": "Спајање…",
    "status.merged": "Спајање је завршено.",
    "status.mergeConflict": "Спајање има сукобе — решите их у уређивачу, а затим урезујте.",
    "search.title": "Тражи",
    "search.tooltip": "Сеарцх цоммитс",
    "search.placeholder": "Претрага урезује…",
    "search.previous": "Претходни резултат",
    "search.next": "Следећи резултат",
    "search.noResults": "Нема резултата",
    "search.resultCount": "КСПЛХ0Кс/КСПЛХ1Кс",
    "search.filter": "Режим филтера",
    "search.highlight": "Хигхлигхт моде",
    "search.filterMode": "Приказ претраге",
    "search.filterModeFilter": "Филтер (сакриј неподударности)",
    "search.filterModeHighlight": "Истакни (прикажи све, означи подударања)",
    "search.filterModeHint": "Одаберите како се резултати претраге приказују на графикону.",
    "search.visibility": "Претражите локацију",
    "search.visibilityToolbar": "Дугме на траци са алаткама",
    "search.visibilityAlwaysVisible": "Увек видљива трака",
    "search.visibilityHint": "Прикажи претрагу као икону траке са алаткама или сталну траку на врху.",
    "footer.github": "ГитХуб",
  },
  tt: {
    "toolbar.refresh": "Resh Яңарту",
    "toolbar.fetch": "Et Алыгыз",
    "toolbar.pull": "Ull тарту",
    "toolbar.push": "⇧ этәрү",
    "toolbar.commit": "✓ Тапшыру",
    "toolbar.sync": "⇅ Синхронизация",
    "toolbar.jumpHead": "Check Түләүгә бар",
    "toolbar.reset": "View Карарга",
    "toolbar.settings": "Ings көйләүләр",
    "details.header": "Детальләр",
    "details.close": "Ябу",
    "details.sha": "SHA",
    "details.shortSha": "Кыска SHA",
    "details.message": "Хәбәр",
    "details.author": "Автор",
    "details.date": "Дата",
    "details.labels": "Ярлыклар",
    "details.location": "Урын",
    "details.currentHead": "Баш - агымдагы теркәлү",
    "settings.title": "Көйләүләр",
    "settings.language": "Тел",
    "settings.theme": "Тема",
    "settings.themeLight": "Яктылык",
    "settings.themeDark": "Караңгы",
    "settings.themeLightHint": "Якты фон, кара текст.",
    "settings.themeDarkHint": "Караңгы фон, җиңел текст.",
    "settings.mainBranch": "Төп филиал",
    "settings.mainBranchAuto": "Автомат",
    "settings.mainBranchSearch": "Филиалларны эзләү…",
    "settings.mainBranchNoMatch": "Бер-берсенә туры килгән ботаклар юк",
    "settings.display": "Күрсәтү стиле",
    "settings.displayModern": "Заманча",
    "settings.displayClassic": "Классик",
    "settings.displayModernHint": "Бушлай киндер - табага тарту, зурайту өчен әйләндерү.",
    "settings.displayClassicHint": "Тикшерелгән киндер - магистраль сулга кадакланган, зурайту юк, әйләндерү генә (SVN ревизия графигы кебек).",
    "settings.sectionGeneral": "Генерал",
    "settings.sectionGraph": "Граф",
    "settings.sectionChanges": "Viewзгәрешләр",
    "settings.diffMinimap": "Минимап",
    "settings.diffMinimapOn": "Күрсәтелә",
    "settings.diffMinimapOff": "Яшерен",
    "settings.diffMinimapOnHint": "Дифф янында VS Код стилендәге күзәтү полосасын күрсәтегез - аны әйләндерү өчен сөйрәгез; маркерларны үзгәртү.",
    "settings.diffMinimapOffHint": "Гомуми күзәтү полосасы юк - диффны гадәттә әйләндерегез.",
    "settings.commitReview": "Карап чыгу",
    "settings.commitReviewOn": "Тапшырганчы сора",
    "settings.commitReviewOff": "Турыдан-туры эшләгез",
    "settings.commitReviewOnHint": "Соңгы файл исемлеген һәм җирле тикшерүне ясаганчы хәбәрне тикшерегез.",
    "settings.commitReviewOffHint": "Тапшыру төймәсе шунда ук җирле бурычны тудыра.",
    "settings.svnBranchDialog": "SVN стилендәге филиал диалогы",
    "settings.svnBranchDialogHint": "Филиал ясаганда папка-агач сайлаучыны күрсәтегез (гади сорау урынына).",
    "settings.branchDialog": "Филиал диалогы",
    "settings.branchDialogSvnTitle": "SVN стиле",
    "settings.branchDialogNativeVscode": "VS коды стиле",
    "settings.branchDialogNativeVs": "Визуаль студия стиле",
    "settings.branchDialogNativeHint": "Сезнең IDE-ның урнаштырылган филиал соравы.",
    "settings.close": "Ябу",
    "settings.done": "Булды",
    "settings.sectionAdvanced": "Алга киткән",
    "settings.gitSource": "Гит башкарыла",
    "settings.gitSourceBuiltin": "Эчке",
    "settings.gitSourceBuiltinHint": "IDE-ның урнаштырылган Git киңәйтүеннән гит бинарны кулланыгыз.",
    "settings.gitSourceCustom": "Махсус юл",
    "settings.gitSourceCustomHint": "Gзегезнең git башкарыла торган юлны күрсәтегез (мәс. / Usr / bin / git яки C:\\…\\git.exe).",
    "settings.gitPath": "Гит",
    "settings.gitPathPlaceholder": "/ usr / bin / git",
    "settings.gitPathBrowse": "Карагыз…",
    "settings.jargon": "Гит терминнары",
    "settings.jargonTranslate": "Тәрҗемә итү",
    "settings.jargonEnglish": "Инглиз телендә саклагыз",
    "settings.jargonTranslateHint": "Git терминнарын интерфейс теленә тәрҗемә итегез (тарту, этәрү, тапшыру, филиал…).",
    "settings.jargonEnglishHint": "Инглиз телендә Git терминнарын саклагыз (тарту, этәрү, ясау, филиал…); бүтәннәрне тәрҗемә итү.",
    "settings.sectionMerge": "Кушылу",
    "settings.mergeMode": "Стильне берләштерү",
    "settings.mergeModeMerge": "Нормаль кушылу",
    "settings.mergeModeSquash": "Скваш кушылу",
    "settings.mergeModeMergeHint": "Филиалны булганча берләштерә: аның йөкләмәләре хәзерге филиал тарихының бер өлешенә әверелә, кушылу кушылуы белән кушыла (яки мөмкин булганда тиз алга җибәрелә).",
    "settings.mergeModeSquashHint": "Бөтен филиалны хәзерге филиалдагы бер йөкләмәгә тарта (git merge --squash). Филиалның үз бурычлары хәзерге филиал тарихына керми - сез анда бер генә эшне күрәсез. Филиал үзе кагылмаган, шуңа күрә аның бурычлары үз полосасында күренеп тора.",
    "settings.mergedView": "Графикта кушылган эшләр",
    "settings.mergedViewBranch": "Onlyз филиалында гына",
    "settings.mergedViewTarget": "Шулай ук ​​филиалда алар кушылды",
    "settings.mergedViewBranchHint": "Берләшкән филиалның бурычлары үз полосасында гына ясала; кушылу тоташтыручы булып күрсәтелә. Классик күренеш.",
    "settings.mergedViewTargetHint": "Керү комбинаты кабул итү филиалының полосасында да ясала, берләштерү йөкләмәсе һәм оригиналлары белән дәрәҗәгә тезелгән - чөнки бу филиал хәзер аларны үз эченә ала. Күчермәләре алсу һәм алар язылган филиал белән бара, шуңа күрә алар анда ясалмаган. Сквашның кушылуы тәэсир итми: скваш бер гади бурыч яза, шуңа күрә бары тик беркайчан да күренми.",
    "legend.title": "Легенда",
    "legend.head": "Баш / хәзерге филиал",
    "legend.local": "Localирле филиал",
    "legend.remote": "Ерак филиал",
    "legend.remoteOnly": "Болытта гына (тартылмый)",
    "legend.tag": "Тег (версия)",
    "legend.commit": "Тапшыру",
    "legend.stash": "Саклау (киштә эшләнгән)",
    "legend.mergedIn": "Кушылу белән килде (бүтән филиалда язылган)",
    "legend.nodes": "Бокслар",
    "legend.lines": "Сызыклар",
    "legend.edgeParent": "Элеккеге юл бер үк юлда",
    "legend.edgeMerge": "Берләшкән филиал - кушылудагы ук нокталары",
    "legend.edgeBranch": "Монда филиал китте",
    "legend.edgeStash": "Саклау һәм бирелгән бурыч сакланган",
    "legend.edgeMergedChain": "Бу филиалның берләшкән бурычлары аша уза",
    "legend.edgeMergedTie": "Шул ук филиалда язылган",
    "menu.jumpHead": "Check Түләүгә бар",
    "menu.resetView": "View Карарга",
    "menu.createBranch": "Моннан филиал булдырыгыз…",
    "menu.checkout": "Бу йөкләмәне тикшерегез",
    "menu.copySha": "SHA тапшыру",
    "menu.pushBranch": "\"{name}\" филиалы.",
    "menu.renameBranch": "\"{name}\" филиалының исемен үзгәртү â ¦",
    "menu.deleteBranch": "\"{name}\" филиалын бетерегез â branch",
    "menu.renameCommit": "Ышаныч хәбәренең исемен үзгәртү…",
    "menu.undoCommit": "Бурычны кире кайтару (үзгәртүләрне саклагыз)…",
    "menu.viewChanges": "Changesзгәрешләрне карау…",
    "menu.mergeBranch": "\"{source}\" ны \"{target}\" â intoга кушыгыз",
    "menu.jumpToOriginal": "Оригиналь бурычка бар",
    "node.mergedInTooltip": "Берләштерү {merge} бу бурычны \"{target}\" га китерде — ул \"{origin}\" да язылган.",
    "changes.title": "{sha} үзгәреше",
    "changes.tabChanged": ".Згәртелде",
    "changes.tabAll": "Барлык файллар",
    "changes.added": "Өстәлде",
    "changes.modified": "Modзгәртелде",
    "changes.deleted": "Бетерелгән",
    "changes.renamed": "Аның исеме үзгәртелде",
    "changes.noChanges": "Бу файл юк.",
    "changes.loading": "Дифф йөкләү…",
    "changes.selectFile": "Аның үзгәрүен күрү өчен файлны сайлагыз.",
    "changes.original": "Оригиналь",
    "changes.changed": "Бу бурыч",
    "changes.binary": "Бинар файл - күрсәтергә текст юк.",
    "changes.tooLarge": "Файл бик зур.",
    "changes.renamedFrom": "{path} исеменнән үзгәртелде",
    "changes.close": "Ябу",
    "changes.prevChange": "Алдагы үзгәрешләр",
    "changes.nextChange": "Киләсе үзгәреш",
    "changes.collapseAll": "Барлык папкаларны җимерегез",
    "changes.expandAll": "Барлык папкаларны киңәйтегез",
    "changes.searchPlaceholder": "Файлларны эзләү…",
    "changes.noSearchResults": "Сезнең эзләүгә бернинди файл да туры килми.",
    "changes.maximize": "Максимальләштерегез",
    "changes.restore": "Зурлыкны торгызу",
    "find.open": "Бу диффта эзләгез (Ctrl + F)",
    "find.placeholder": "Төрлелектә табыгыз ...",
    "find.prev": "Алдагы матч",
    "find.next": "Киләсе матч",
    "find.close": "Ябу",
    "find.noResults": "Нәтиҗә юк",
    "commit.title": "Changзгәрешләр кертү",
    "commit.close": "Ябу",
    "commit.loading": "Эш агачын уку…",
    "commit.noChanges": "Localирле үзгәрешләр юк.",
    "commit.selectFile": "Аның аермасын күрү өчен файлны сайлагыз.",
    "commit.selectAll": "Барысын да сайлагыз",
    "commit.selectNone": "Барысын да чистарт",
    "commit.message": "Тапшыру хәбәре",
    "commit.messagePlaceholder": "Theзгәрешне сурәтләгез",
    "commit.selectedCount": "{count} файллары сайланган",
    "commit.review": "Кабатлау",
    "commit.reviewTitle": "Соңгы тикшерү",
    "commit.reviewSummary": "{count} файл (ларын) хәбәр белән бирегез: {message}",
    "commit.back": "Кире",
    "commit.commit": "Localирле бурыч булдыру",
    "menu.stashApply": "Чүпрәкне кулланыгыз",
    "menu.stashPop": "Поп ташы (кулланыгыз һәм бетерегез)",
    "menu.stashDrop": "Тамчы ташлау…",
    "merge.title": "Филиалны берләштерү",
    "merge.route": "Хәзерге филиалга кушылу",
    "merge.source": "(Чыганактан)",
    "merge.target": "Керү (агым)",
    "merge.loading": "Берләшүне анализлау…",
    "merge.upToDate": "Инде заманча - кушылырга бернәрсә дә юк.",
    "merge.fastForward": "Тиз алга - берләштерү бурычы кирәк түгел (берсен мәҗбүр итү өчен \"Кушылу бурычы яса\").",
    "merge.summary": "{files} файл (лар) үзгәрде, {conflicts} конфликтлары.",
    "merge.conflictsWarning": "Бу кушылуның конфликтлары бар - сез барыбер берләшә аласыз, аннары аларны редакторда чишә аласыз.",
    "merge.noChanges": "Файл үзгәрми.",
    "merge.files": "Нәтиҗә",
    "merge.added": "Өстәлде",
    "merge.modified": "Modзгәртелде",
    "merge.deleted": "Бетерелгән",
    "merge.conflict": "Конфликт",
    "merge.message": "Берләшү хәбәрен берләштерү",
    "merge.messagePlaceholder": "Филиалны \"чыганак\" ны берләштерү",
    "merge.messageFfHint": "Тиз алга кушылуга игътибар ителми (бернинди бурыч та булдырылмый).",
    "merge.noFastForward": "Берләштерү бурычы төзегез (тиз алга түгел)",
    "merge.noFastForwardHint": "Филиал тиз алга китә алса да, һәрвакыт кушылу турында языгыз.",
    "merge.merge": "Кушылу",
    "merge.cancel": "Баш тарту",
    "merge.previewError": "Алдан карау хисаплый алмый: {message}",
    "merge.squashBadge": "Скваш",
    "merge.squashNote": "Сквашны берләштерү - һәрбер үзгәрү җирләре БЕР “{target}” буенча; \"{source}\" үз бурычлары аның тарихыннан читтә кала. Көйләүләргә кире кайтыгыз ›Кушылу.",
    "merge.messageSquash": "Тапшыру хәбәре",
    "merge.squashMessagePlaceholder": "\"Чыганактан\" үзгәртелгән үзгәрешләр.",
    "newBranch.title": "Филиал булдыру",
    "newBranch.startPoint": "{sha} дан яңа филиал",
    "newBranch.startPointOn": "{refs}",
    "newBranch.location": "Урын",
    "newBranch.locationRoot": "(тамыр)",
    "newBranch.expandAll": "Барысын да киңәйтегез",
    "newBranch.collapseAll": "Барысын да җимерегез",
    "newBranch.expandFolder": "Папканы киңәйтү",
    "newBranch.collapseFolder": "Папканы җимерү",
    "newBranch.name": "Филиал исеме",
    "newBranch.namePlaceholder": "минем филиал",
    "newBranch.fullName": "Тулы исеме: {name}",
    "newBranch.invalid": "Филиал исеме дөрес түгел",
    "newBranch.exists": "Бу исемле филиал инде бар",
    "newBranch.checkout": "Иҗаттан соң теркәлү бүлеге",
    "newBranch.create": "Ярат",
    "newBranch.cancel": "Баш тарту",
    "status.loading": "График йөкләү…",
    "status.summary": "{repo} {commits} күрсәтү, {refs} эшкәртү",
    "status.branchCreated": "{sha} \"{name}\" филиалы булдырылды",
    "status.error": "Хата: {message}",
    "status.opFailed": "Операция уңышсыз булды",
    "status.fetching": "Алу…",
    "status.pulling": "Тарту ...",
    "status.pushing": "Көтү…",
    "status.committing": "Localирле бурыч булдыру…",
    "status.commitCreated": "{sha}: {message} җирле йөкләмә булдырылды",
    "status.syncing": "Синхронлаштыру…",
    "status.noHead": "Графикта хәзерге вакытта теркәлү табылмады.",
    "status.undoing": "Бурычны кире кагу…",
    "status.commitUndone": "Кире кайтару - үзгәрешләр эш агачында.",
    "status.undoConflict": "Конфликтларны кире кайтару - аларны редакторда чишегез, аннары дәвам итегез.",
    "status.stashApplying": "Табак куллану…",
    "status.stashPopping": "Чүп ташлау…",
    "status.stashDropping": "Чүп ташлау…",
    "status.stashApplied": "Таш кулланыла.",
    "status.stashPopped": "Сакла.",
    "status.stashDropped": "Акча төште.",
    "status.stashConflict": "Конфликтларны ташлагыз - аларны редакторда чишегез.",
    "status.merging": "Кушылу…",
    "status.merged": "Берләштерү тәмамланды.",
    "status.mergeConflict": "Берләшүдә конфликтлар бар - аларны редакторда чишегез, аннары эшләгез.",
    "search.title": "Эзләү",
    "search.tooltip": "Эзләү",
    "search.placeholder": "Эзләү эшләре…",
    "search.previous": "Алдагы нәтиҗәләр",
    "search.next": "Киләсе нәтиҗә",
    "search.noResults": "Нәтиҗә юк",
    "search.resultCount": "{index} / {count}",
    "search.filter": "Фильтр режимы",
    "search.highlight": "Тәртипне аерып алыгыз",
    "search.filterMode": "Эзләү дисплей",
    "search.filterModeFilter": "Фильтр (туры килмәгәннәрне яшерегез)",
    "search.filterModeHighlight": "Аңлатма (барысын да күрсәт, матчларны билгеләргә)",
    "search.filterModeHint": "Эзләү нәтиҗәләре графикта ничек күрсәтелгәнен сайлагыз.",
    "search.visibility": "Эзләү урыны",
    "search.visibilityToolbar": "Кораллар аскысы",
    "search.visibilityAlwaysVisible": "Waysәрвакыт күренеп торган бар",
    "search.visibilityHint": "Эзләнүне кораллар плитәсе яки өстендә тотрыклы сызык итеп күрсәтегез.",
    "footer.github": "GitHub",
  },
  tg: {
    "toolbar.refresh": "⟳ Навсозӣ",
    "toolbar.fetch": "⤓ Гирифтан",
    "toolbar.pull": "⇩ кашед",
    "toolbar.push": "⇧ тела диҳед",
    "toolbar.commit": "✓ Уҳдадор шавед",
    "toolbar.sync": "⇅ Синхронизатсия",
    "toolbar.jumpHead": "⌖ Ба касса гузаред",
    "toolbar.reset": "⤢ Намоишро барқарор кунед",
    "toolbar.settings": "⚙ Танзимот",
    "details.header": "Тафсилоти ӯҳдадорӣ",
    "details.close": "Пӯшед",
    "details.sha": "ША",
    "details.shortSha": "SHA кӯтоҳ",
    "details.message": "Паём",
    "details.author": "Муаллиф",
    "details.date": "Сана",
    "details.labels": "Тамғакоғазҳо",
    "details.location": "Ҷойгиршавӣ",
    "details.currentHead": "HEAD — кассири ҷорӣ",
    "settings.title": "Танзимотҳо",
    "settings.language": "Забон",
    "settings.theme": "Мавзӯъ",
    "settings.themeLight": "Нур",
    "settings.themeDark": "Торик",
    "settings.themeLightHint": "Заминаи равшан, матни торик.",
    "settings.themeDarkHint": "Заминаи торик, матни равшан.",
    "settings.mainBranch": "Филиали асосӣ",
    "settings.mainBranchAuto": "Автоматӣ",
    "settings.mainBranchSearch": "Ҷустуҷӯи филиалҳо…",
    "settings.mainBranchNoMatch": "Не шохаҳои мувофиқ",
    "settings.display": "Услуби намоиш",
    "settings.displayModern": "муосир",
    "settings.displayClassic": "Классик",
    "settings.displayModernHint": "Канваси ройгон — кашола кунед, то пажмурда кунед, барои калон кардан ҳаракат кунед.",
    "settings.displayClassicHint": "Канваси собит — танаи ба тарафи чап часпидашуда, масштаб нест, танҳо ҳаракат кунед (ба монанди графики таҷдиди SVN).",
    "settings.sectionGeneral": "Генерал",
    "settings.sectionGraph": "График",
    "settings.sectionChanges": "Намоишро тағир медиҳад",
    "settings.diffMinimap": "Минихаритаи фарқият",
    "settings.diffMinimapOn": "Нишон дода шудааст",
    "settings.diffMinimapOff": "Пинҳон",
    "settings.diffMinimapOnHint": "Дар паҳлӯи фарқият рахи шарҳи услуби VS Code-ро нишон диҳед — барои ҳаракат кардан онро кашед; аломатҳои тағир дохил карда шудаанд.",
    "settings.diffMinimapOffHint": "Ҳеҷ як рахи шарҳ нест — фарқиятро одатан ҳаракат кунед.",
    "settings.commitReview": "Баррасиро иҷро кунед",
    "settings.commitReviewOn": "Пеш аз анҷом додан пурсед",
    "settings.commitReviewOff": "Бевосита супоред",
    "settings.commitReviewOnHint": "Пеш аз сохтани ӯҳдадории маҳаллӣ рӯйхати ниҳоии файл ва санҷиши паёмро нишон диҳед.",
    "settings.commitReviewOffHint": "Тугмаи Уҳдадорӣ фавран ӯҳдадории маҳаллиро эҷод мекунад.",
    "settings.svnBranchDialog": "Муколамаи филиали SVN",
    "settings.svnBranchDialogHint": "Ҳангоми сохтани филиал (ба ҷои дархости оддӣ) интихобкунандаи дарахти ҷузвдонҳоро нишон диҳед.",
    "settings.branchDialog": "Муколамаи филиал",
    "settings.branchDialogSvnTitle": "SVN-сабки",
    "settings.branchDialogNativeVscode": "Услуби рамзи VS",
    "settings.branchDialogNativeVs": "Услуби Visual Studio",
    "settings.branchDialogNativeHint": "Саволи филиали IDE-и шумо.",
    "settings.close": "Пӯшед",
    "settings.done": "Иҷро шуд",
    "settings.sectionAdvanced": "Мукаммал",
    "settings.gitSource": "Git иҷрошаванда",
    "settings.gitSourceBuiltin": "Дарунсохт",
    "settings.gitSourceBuiltinHint": "Бинарии git-ро аз васеъшавии дарунсохтаи Git IDE истифода баред.",
    "settings.gitSourceCustom": "Роҳи фармоишӣ",
    "settings.gitPath": "Роҳ ба git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Мурури…",
    "settings.jargon": "Шартҳои Git",
    "settings.jargonTranslate": "Тарҷума кунед",
    "settings.jargonEnglish": "Бо забони англисӣ нигоҳ доред",
    "settings.jargonTranslateHint": "Шартҳои Git-ро ба забони интерфейс тарҷума кунед (кашед, тела диҳед, иҷро кунед, филиал…)",
    "settings.jargonEnglishHint": "Шартҳои Git-ро бо забони англисӣ нигоҳ доред (кашед, тела диҳед, иҷро кунед, филиал…) ҳама чизи дигарро тарҷума кунед.",
    "settings.sectionMerge": "Якҷоя кардан",
    "settings.mergeMode": "Услуби якҷоякунӣ",
    "settings.mergeModeMerge": "Якҷоякунии муқаррарӣ",
    "settings.mergeModeSquash": "Якҷоя кардани сквош",
    "settings.mergeModeMergeHint": "Филиалро тавре, ки ҳаст, муттаҳид мекунад: ӯҳдадориҳои он ба таърихи филиали ҷорӣ табдил меёбанд, ки бо ӯҳдадории якҷояшавӣ ҳамроҳ мешаванд (ё дар ҳолати имконпазир).",
    "settings.mergeModeSquashHint": "Тамоми шохаро ба як ӯҳдадории ягона дар шохаи ҷорӣ мепӯшонад (git merge --squash). Уҳдадориҳои худи филиал ҳеҷ гоҳ ба таърихи филиали ҷорӣ дохил намешаванд - шумо дар он ҷо танҳо як ӯҳдадориро мебинед. Худи филиал бетаъсир мемонад, бинобар ин ӯҳдадориҳои он дар хатти худ намоён мемонанд.",
    "settings.mergedView": "Уҳдадориҳои муттаҳидшуда дар график",
    "settings.mergedViewBranch": "Танҳо дар филиали худ",
    "settings.mergedViewTarget": "Инчунин дар филиал онҳо якҷоя карда шуданд",
    "settings.mergedViewBranchHint": "Уҳдадориҳои филиали якҷояшуда танҳо дар хатти худ кашида мешаванд; якҷоякунӣ ҳамчун пайвасткунанда нишон дода мешавад. Манзараи классикӣ.",
    "settings.mergedViewTargetHint": "Уҳдадориҳои якҷояшуда дар хатти филиали қабулкунанда низ кашида мешаванд, дар зери ӯҳдадории якҷоякунӣ ҷамъ карда мешаванд ва бо нусхаҳои аслӣ баробар мешаванд - зеро он филиал воқеан ҳоло онҳоро дар бар мегирад. Нусхаҳо рангоранганд ва бо шохае, ки дар он навишта шудаанд, сарлавҳа доранд, аз ин рӯ маълум аст, ки онҳо дар он ҷо сохта нашудаанд. Якҷоякунии сквош бетаъсир нест: каду як супориши муқаррариро менависад, бинобар ин танҳо он ӯҳдадорӣ ҳамеша пайдо мешавад.",
    "legend.title": "Афсона",
    "legend.head": "HEAD / филиали ҷорӣ",
    "legend.local": "Филиали маҳаллӣ",
    "legend.remote": "Филиали дурдаст",
    "legend.remoteOnly": "Танҳо дар абр (кабул карда нашудааст)",
    "legend.tag": "Тег (версия)",
    "legend.commit": "Уҳдадор шавед",
    "legend.stash": "Сташ (кори рафъшуда)",
    "legend.mergedIn": "Бо якҷоякунӣ ворид шуд (дар филиали дигар навишта шудааст)",
    "legend.nodes": "Қуттиҳо",
    "legend.lines": "Хатҳо",
    "legend.edgeParent": "Ухдадории қаблӣ дар ҳамон хат",
    "legend.edgeMerge": "Филиали муттаҳидшуда - тирча дар ӯҳдадории якҷоякунӣ ишора мекунад",
    "legend.edgeBranch": "Дар ин ҷо як шоха канда шуд",
    "legend.edgeStash": "Stash ва ӯҳдадории он рафъ карда шуд",
    "legend.edgeMergedChain": "Хатти ин филиал тавассути ӯҳдадориҳои муттаҳидшуда мегузарад",
    "legend.edgeMergedTie": "Худи хамон ухдадорй дар филиал навишта шуда буд",
    "menu.jumpHead": "⌖ Ба касса гузаред",
    "menu.resetView": "⤢ Намоишро барқарор кунед",
    "menu.createBranch": "Аз ин ҷо филиал эҷод кунед…",
    "menu.checkout": "Ин ӯҳдадориро тафтиш кунед",
    "menu.copySha": "Нусхабардории SHA",
    "menu.pushBranch": "Филиали \"{name}\"-ро пахш кунед",
    "menu.renameCommit": "Номи паёми ӯҳдадориро тағир диҳед…",
    "menu.undoCommit": "Бекор кардани ӯҳдадорӣ (нигоҳ доштани тағирот)…",
    "menu.viewChanges": "Дидани тағирот…",
    "menu.mergeBranch": "\"{source}\" -ро ба \"{target}\" якҷоя кунед…",
    "menu.jumpToOriginal": "Ба ӯҳдадории аслӣ равед",
    "changes.title": "Тағирот дар {sha}",
    "changes.tabChanged": "Тағйир дода шуд",
    "changes.tabAll": "Ҳама файлҳо",
    "changes.added": "Илова карда шуд",
    "changes.modified": "Тағир дода шудааст",
    "changes.deleted": "Нобуд",
    "changes.renamed": "Номи дигар дода шуд",
    "changes.noChanges": "Ин ӯҳдадорӣ ягон файлро тағир намедиҳад.",
    "changes.loading": "Фарқият бор карда мешавад…",
    "changes.selectFile": "Барои дидани тағйироти он файлро интихоб кунед.",
    "changes.original": "Асли",
    "changes.changed": "Ин ухдадорй",
    "changes.binary": "Файли дуӣ - барои нишон додани фарқияти матн вуҷуд надорад.",
    "changes.tooLarge": "Файл барои фарқ кардан хеле калон аст.",
    "changes.renamedFrom": "Аз {path} номгузорӣ шуд",
    "changes.close": "Пӯшед",
    "changes.prevChange": "Тағйироти қаблӣ",
    "changes.nextChange": "Тағйироти навбатӣ",
    "changes.collapseAll": "Ҳама ҷузвдонҳоро пӯшонед",
    "changes.expandAll": "Ҳама ҷузвдонҳоро васеъ кунед",
    "changes.searchPlaceholder": "Ҷустуҷӯи файлҳо…",
    "changes.noSearchResults": "Ягон файл ба ҷустуҷӯи шумо мувофиқат намекунад.",
    "changes.maximize": "Максимум кардан",
    "changes.restore": "Андозаро барқарор кунед",
    "find.open": "Ҷустуҷӯ дар ин фарқият (Ctrl+F)",
    "find.placeholder": "Дар фарқият пайдо кунед…",
    "find.prev": "Бозии қаблӣ",
    "find.next": "Бозии навбатй",
    "find.close": "Ҷустуҷӯро пӯшед",
    "find.noResults": "Ҳеҷ натиҷае нест",
    "commit.title": "Тағйиротро ворид кунед",
    "commit.close": "Пӯшед",
    "commit.loading": "Хониши дарахти корӣ…",
    "commit.noChanges": "Ҳеҷ гуна тағироти маҳаллӣ вуҷуд надорад.",
    "commit.selectFile": "Барои дидани фарқияти он файлро интихоб кунед.",
    "commit.selectAll": "Ҳамаро интихоб кунед",
    "commit.selectNone": "Ҳамаро тоза кунед",
    "commit.message": "Паёмро қабул кунед",
    "commit.messagePlaceholder": "Тағйиротро тавсиф кунед",
    "commit.selectedCount": "{count} файл(ҳо) интихоб карда шуд",
    "commit.review": "Барраси",
    "commit.reviewTitle": "Санҷиши ниҳоӣ пеш аз иҷроиш",
    "commit.reviewSummary": "Файл(ҳо)-и {count}-ро бо паём: {message} иҷро кунед",
    "commit.back": "Бозгашт",
    "commit.commit": "Эҷоди ӯҳдадории маҳаллӣ",
    "menu.stashApply": "Анборро татбиқ кунед",
    "menu.stashPop": "Поп stash (кор кардан ва хориҷ кардан)",
    "menu.stashDrop": "Захира партоед…",
    "merge.title": "Филиали муттаҳид",
    "merge.route": "Муттаҳидшавӣ ба филиали ҷорӣ",
    "merge.source": "Аз (манбаъ)",
    "merge.target": "Ба (ҷори)",
    "merge.loading": "Таҳлили якҷояшавӣ…",
    "merge.upToDate": "Аллакай то имрӯз - ҳеҷ чиз барои якҷоя кардан нест.",
    "merge.fastForward": "Ба пеш ҳаракат кардан - ӯҳдадории якҷоякунӣ лозим нест (барои маҷбур кардани як \"Сохтани ӯҳдадории якҷоя\" -ро қайд кунед).",
    "merge.summary": "Файл(ҳо)-и {files} тағир ёфт, ихтилоф(ҳо) {conflicts}.",
    "merge.conflictsWarning": "Ин якҷоякунӣ ихтилофҳо дорад - шумо ҳоло ҳам метавонед якҷоя кунед ва онҳоро дар муҳаррир ҳал кунед.",
    "merge.noChanges": "Тағироти файл нест.",
    "merge.files": "Тағйироти натиҷа",
    "merge.added": "Илова карда шуд",
    "merge.modified": "Тағир дода шудааст",
    "merge.deleted": "Нобуд",
    "merge.conflict": "Низоъ",
    "merge.message": "Якҷоя кардани паёми ӯҳдадорӣ",
    "merge.messagePlaceholder": "Якҷоя кардани филиали \"манбаъ\"",
    "merge.messageFfHint": "Ҳангоми муттаҳидшавии зуд ба пеш нодида гирифта мешавад (ҳеҷ ӯҳдадорӣ эҷод намешавад).",
    "merge.noFastForward": "Эҷоди ӯҳдадории якҷоякунӣ (бе пешбурди зуд)",
    "merge.noFastForwardHint": "Ҳамеша ӯҳдадории якҷоякуниро сабт кунед, ҳатто вақте ки филиал метавонад ба пеш ҳаракат кунад.",
    "merge.merge": "Якҷоя кардан",
    "merge.cancel": "Бекор кардан",
    "merge.previewError": "Пешнамоишро ҳисоб карда натавонист: {message}",
    "merge.squashBadge": "Каду",
    "merge.squashNote": "Якҷоякунии сквош - ҳар як тағирот ҳамчун ЯК ба \"{target}\" ворид мешавад; Ухдадорихои худи «{source}» аз таърихи худ дур мемонанд. Бозгаштан дар Танзимот › Якҷоя.",
    "merge.messageSquash": "Паёмро қабул кунед",
    "merge.squashMessagePlaceholder": "Тағиротҳои нопадидшуда аз \"манбаъ\"",
    "newBranch.title": "Филиал эҷод кунед",
    "newBranch.startPoint": "Филиали нав аз {sha} сар мешавад",
    "newBranch.startPointOn": "дар {refs}",
    "newBranch.location": "Ҷойгиршавӣ",
    "newBranch.locationRoot": "(реша)",
    "newBranch.expandAll": "Ҳамаро васеъ кунед",
    "newBranch.collapseAll": "Ҳамаро вайрон кунед",
    "newBranch.expandFolder": "Папкаро васеъ кунед",
    "newBranch.collapseFolder": "Папкаро вайрон кунед",
    "newBranch.name": "Номи филиал",
    "newBranch.namePlaceholder": "филиали ман",
    "newBranch.fullName": "Номи пурра: {name}",
    "newBranch.invalid": "Номи филиал нодуруст",
    "newBranch.exists": "Филиал бо ин ном аллакай вуҷуд дорад",
    "newBranch.checkout": "Филиали пас аз таъсис",
    "newBranch.create": "Эҷод кунед",
    "newBranch.cancel": "Бекор кардан",
    "status.loading": "График бор карда мешавад…",
    "status.summary": "{repo}Намоиши {commits} ӯҳдадор мешавад, {refs} муроҷиат мекунад",
    "status.branchCreated": "Филиали \"{name}\" дар {sha} таъсис дода шудааст",
    "status.error": "Хатогӣ: {message}",
    "status.opFailed": "Амалиёт ноком шуд",
    "status.fetching": "Гирифтан…",
    "status.pulling": "Кашидан…",
    "status.pushing": "Тела додан…",
    "status.committing": "Эҷоди ӯҳдадории маҳаллӣ…",
    "status.commitCreated": "Ташкили ӯҳдадории маҳаллӣ {sha}: {message}",
    "status.syncing": "Синхронизатсия…",
    "status.noHead": "Дар график ягон кассири ҷорӣ ёфт нашуд.",
    "status.undoing": "Бекор кардани ӯҳдадорӣ…",
    "status.commitUndone": "Тағйирот бекор карда шуд - тағирот дар дарахти корӣ бармегардад.",
    "status.undoConflict": "Бартараф кардани низоъҳои зарба - онҳоро дар муҳаррир ҳал кунед ва сипас идома диҳед.",
    "status.stashApplying": "Татбиқи нигоҳдорӣ…",
    "status.stashPopping": "Ҷойгиркунии…",
    "status.stashDropping": "Партофтани анбор…",
    "status.stashApplied": "Stash татбиқ карда шуд.",
    "status.stashPopped": "Сташ баромад.",
    "status.stashDropped": "Stash афтод.",
    "status.stashConflict": "Низоъҳои нигоҳдорӣ - онҳоро дар муҳаррир ҳал кунед.",
    "status.merging": "Якҷоя кардан…",
    "status.merged": "Якҷоякунӣ анҷом ёфт.",
    "status.mergeConflict": "Якҷоя ихтилофҳо дорад - онҳоро дар муҳаррир ҳал кунед ва сипас иҷро кунед.",
    "search.title": "Ҷустуҷӯ",
    "search.tooltip": "Ҷустуҷӯро иҷро мекунад",
    "search.placeholder": "Ҷустуҷӯи ӯҳдадориҳо…",
    "search.previous": "Натиҷаи қаблӣ",
    "search.next": "Натиҷаи навбатӣ",
    "search.noResults": "Ҳеҷ натиҷае нест",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Ҳолати филтр",
    "search.highlight": "Ҳолати таъкид",
    "search.filterMode": "Намоиши ҷустуҷӯ",
    "search.filterModeFilter": "Филтр (пинҳон кардани мувофиқатҳо)",
    "search.filterModeHighlight": "Равшансозӣ (ҳама нишон диҳед, мувофиқатҳоро қайд кунед)",
    "search.filterModeHint": "Интихоб кунед, ки чӣ гуна натиҷаҳои ҷустуҷӯ дар график нишон дода мешаванд.",
    "search.visibility": "Ҷустуҷӯи макон",
    "search.visibilityToolbar": "Тугмаи панели асбобҳо",
    "search.visibilityAlwaysVisible": "Бари ҳамеша намоён",
    "search.visibilityHint": "Ҷустуҷӯро ҳамчун тасвири панели асбобҳо ё сатри доимӣ дар боло нишон диҳед.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  uk: {
    "toolbar.refresh": "⟳ Оновити",
    "toolbar.fetch": "⤓ Отримати",
    "toolbar.pull": "⇩ Потягніть",
    "toolbar.push": "⇧ Натисніть",
    "toolbar.commit": "✓ Взяти зобов'язання",
    "toolbar.sync": "⇅ Синхронізація",
    "toolbar.jumpHead": "⌖ Перейдіть до каси",
    "toolbar.reset": "⤢ Скинути перегляд",
    "toolbar.settings": "⚙ Налаштування",
    "details.header": "Деталі фіксації",
    "details.close": "Закрити",
    "details.sha": "SHA",
    "details.shortSha": "Короткий SHA",
    "details.message": "повідомлення",
    "details.author": "автор",
    "details.date": "Дата",
    "details.labels": "етикетки",
    "details.location": "Розташування",
    "details.currentHead": "HEAD — поточна перевірка",
    "settings.title": "Налаштування",
    "settings.language": "Мова",
    "settings.theme": "Тема",
    "settings.themeLight": "світло",
    "settings.themeDark": "Темний",
    "settings.themeLightHint": "Світлий фон, темний текст.",
    "settings.themeDarkHint": "Темний фон, світлий текст.",
    "settings.mainBranch": "Основна гілка",
    "settings.mainBranchAuto": "Автоматичний",
    "settings.mainBranchSearch": "Пошук відділень…",
    "settings.mainBranchNoMatch": "Немає відповідних гілок",
    "settings.display": "Стиль відображення",
    "settings.displayModern": "Сучасний",
    "settings.displayClassic": "класичний",
    "settings.displayModernHint": "Вільне полотно — перетягніть для панорамування, прокрутіть для збільшення.",
    "settings.displayClassicHint": "Виправлено полотно — стовбур закріплений ліворуч, без масштабування, лише прокручування (як графік версій SVN).",
    "settings.sectionGeneral": "Загальний",
    "settings.sectionGraph": "Графік",
    "settings.sectionChanges": "Змінює вигляд",
    "settings.diffMinimap": "Різниця міні-карта",
    "settings.diffMinimapOn": "Показано",
    "settings.diffMinimapOff": "Прихований",
    "settings.diffMinimapOnHint": "Показати смужку огляду у стилі коду VS поруч із різницею — перетягніть її, щоб прокрутити; включені маркери змін.",
    "settings.diffMinimapOffHint": "Немає оглядової смуги — прокручуйте різницю нормально.",
    "settings.commitReview": "Здійснити перевірку",
    "settings.commitReviewOn": "Запитайте перед тим, як фіксувати",
    "settings.commitReviewOff": "Здійснюйте безпосередньо",
    "settings.commitReviewOnHint": "Показати остаточний список файлів і перевірити повідомлення перед створенням локального коміту.",
    "settings.commitReviewOffHint": "Кнопка Commit миттєво створює локальну фіксацію.",
    "settings.svnBranchDialog": "Діалог розгалуження у стилі SVN",
    "settings.svnBranchDialogHint": "Показувати засіб вибору дерева папок під час створення гілки (замість простого підказки).",
    "settings.branchDialog": "Діалог гілки",
    "settings.branchDialogSvnTitle": "SVN-стиль",
    "settings.branchDialogNativeVscode": "Стиль VS Code",
    "settings.branchDialogNativeVs": "Стиль Visual Studio",
    "settings.branchDialogNativeHint": "Підказка про розгалуження, вбудована у вашу IDE.",
    "settings.close": "Закрити",
    "settings.done": "Готово",
    "settings.sectionAdvanced": "Просунутий",
    "settings.gitSource": "Виконуваний файл Git",
    "settings.gitSourceBuiltin": "Вбудований",
    "settings.gitSourceBuiltinHint": "Використовуйте двійковий файл git із вбудованого розширення Git IDE.",
    "settings.gitSourceCustom": "Спеціальний шлях",
    "settings.gitPath": "Шлях до git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Перегляд…",
    "settings.jargon": "Терміни Git",
    "settings.jargonTranslate": "Перекласти",
    "settings.jargonEnglish": "Тримайте англійською",
    "settings.jargonTranslateHint": "Перекладіть терміни Git (pull, push, commit, branch...) на мову інтерфейсу.",
    "settings.jargonEnglishHint": "Зберігайте терміни Git (витягувати, надсилати, фіксувати, розгалужуватися…) англійською мовою; переклади все інше.",
    "settings.sectionMerge": "Об’єднати",
    "settings.mergeMode": "Стиль злиття",
    "settings.mergeModeMerge": "Звичайне злиття",
    "settings.mergeModeSquash": "Злиття кабачків",
    "settings.mergeModeMergeHint": "Об’єднує гілку як є: її коміти стають частиною історії поточної гілки, об’єднуються комітом злиття (або перемотуванням вперед, якщо це можливо).",
    "settings.mergeModeSquashHint": "Згортає всю гілку в один коміт на поточній гілці (git merge --squash). Власні коміти гілки ніколи не потрапляють в історію поточної гілки — ви бачите там лише один коміт. Сама гілка залишається недоторканою, тому її коміти залишаються видимими на власній смузі.",
    "settings.mergedView": "Об’єднані коміти в графі",
    "settings.mergedViewBranch": "Тільки на своїй гілці",
    "settings.mergedViewTarget": "Також у відділенні вони були об’єднані",
    "settings.mergedViewBranchHint": "Коміти об’єднаної гілки відображаються лише на власній смузі; злиття відображається як з’єднувач. Класичний вид.",
    "settings.mergedViewTargetHint": "Коміти, внесені злиттям, також малюються на смузі приймаючої гілки, складені під комітом злиття та на одному рівні з оригіналами — тому що ця гілка дійсно містить їх зараз. Копії бліді та мають головку з гілкою, на якій вони написані, тому залишається зрозумілим, що вони зроблені не там. На злиття сквошу це не впливає: сквош записує один звичайний комміт, тому з’являється тільки цей комміт.",
    "legend.title": "Легенда",
    "legend.head": "HEAD / поточна гілка",
    "legend.local": "Місцеве відділення",
    "legend.remote": "Віддалене відділення",
    "legend.remoteOnly": "Тільки в хмарі (не тягне)",
    "legend.tag": "Тег (версія)",
    "legend.commit": "Здійснити",
    "legend.stash": "Схованка (робота на полиці)",
    "legend.mergedIn": "Прийшов із злиттям (написано на іншій гілці)",
    "legend.nodes": "Коробки",
    "legend.lines": "Лінії",
    "legend.edgeParent": "Попередній коміт у тому самому рядку",
    "legend.edgeMerge": "Об’єднана гілка — стрілка вказує на фіксацію злиття",
    "legend.edgeBranch": "Тут відгалужується гілка",
    "legend.edgeStash": "Схованка та фіксація, на якій вона була відкладена",
    "legend.edgeMergedChain": "Рядок цієї гілки проходить через об’єднані коміти",
    "legend.edgeMergedTie": "Той самий коміт на гілці, на якій було написано",
    "menu.jumpHead": "⌖ Перейдіть до каси",
    "menu.resetView": "⤢ Скинути перегляд",
    "menu.createBranch": "Створити гілку звідси…",
    "menu.checkout": "Ознайомтеся з цим комітом",
    "menu.copySha": "Копіювати фіксацію SHA",
    "menu.pushBranch": "Push гілка \"{name}\"",
    "menu.renameBranch": "Перейменуйте гілку \"{name}\"…",
    "menu.renameCommit": "Перейменувати повідомлення коміту…",
    "menu.undoCommit": "Скасувати фіксацію (зберегти зміни)…",
    "menu.viewChanges": "Переглянути зміни…",
    "menu.mergeBranch": "Об’єднайте «{source}» у «{target}»…",
    "menu.jumpToOriginal": "Перейти до вихідного коміту",
    "changes.title": "Зміни в {sha}",
    "changes.tabChanged": "Змінено",
    "changes.tabAll": "Усі файли",
    "changes.added": "Додано",
    "changes.modified": "Змінено",
    "changes.deleted": "Видалено",
    "changes.renamed": "Перейменовано",
    "changes.noChanges": "Цей комміт не змінює файли.",
    "changes.loading": "Завантаження різниці…",
    "changes.selectFile": "Виберіть файл, щоб побачити його зміни.",
    "changes.original": "Оригінал",
    "changes.changed": "Цей комміт",
    "changes.binary": "Двійковий файл — немає текстових відмінностей для показу.",
    "changes.tooLarge": "Файл завеликий для розрізнення.",
    "changes.renamedFrom": "Перейменовано з {path}",
    "changes.close": "Закрити",
    "changes.prevChange": "Попередня зміна",
    "changes.nextChange": "Наступна зміна",
    "changes.collapseAll": "Згорнути всі папки",
    "changes.expandAll": "Розгорнути всі папки",
    "changes.searchPlaceholder": "Пошук файлів…",
    "changes.noSearchResults": "Немає файлів, що відповідають вашому пошуку.",
    "changes.maximize": "Максимізувати",
    "changes.restore": "Відновити розмір",
    "find.open": "Пошук у цій різниці (Ctrl+F)",
    "find.placeholder": "Знайти в різниці…",
    "find.prev": "Попередній матч",
    "find.next": "Наступний матч",
    "find.close": "Закрити пошук",
    "find.noResults": "Результатів немає",
    "commit.title": "Зафіксувати зміни",
    "commit.close": "Закрити",
    "commit.loading": "Читання робочого дерева…",
    "commit.noChanges": "Немає локальних змін для внесення.",
    "commit.selectFile": "Виберіть файл, щоб побачити його різницю.",
    "commit.selectAll": "Вибрати все",
    "commit.selectNone": "Очистити все",
    "commit.message": "Зафіксувати повідомлення",
    "commit.messagePlaceholder": "Опишіть зміну",
    "commit.selectedCount": "Вибрані файли {count}",
    "commit.review": "огляд",
    "commit.reviewTitle": "Остаточна перевірка перед фіксацією",
    "commit.reviewSummary": "Зафіксуйте файл(и) {count} із повідомленням: {message}",
    "commit.back": "Назад",
    "commit.commit": "Створіть локальну фіксацію",
    "menu.stashApply": "Застосувати заначку",
    "menu.stashPop": "Схованка (застосувати та видалити)",
    "menu.stashDrop": "Скиньте заначку…",
    "merge.title": "Злиття гілок",
    "merge.route": "Об'єднання в поточну гілку",
    "merge.source": "Від (джерело)",
    "merge.target": "В (поточний)",
    "merge.loading": "Аналіз об’єднання…",
    "merge.upToDate": "Вже актуально — нічого злити.",
    "merge.fastForward": "Швидке перемотування вперед — не потрібно фіксувати злиття (позначте «Створити фіксацію злиття», щоб примусово його зробити).",
    "merge.summary": "Файл(и) {files} змінено, конфлікт(и) {conflicts}.",
    "merge.conflictsWarning": "Це об’єднання має конфлікти — ви все ще можете об’єднати, а потім вирішити їх у редакторі.",
    "merge.noChanges": "Без змін файлів.",
    "merge.files": "Результуючі зміни",
    "merge.added": "Додано",
    "merge.modified": "Змінено",
    "merge.deleted": "Видалено",
    "merge.conflict": "Конфлікт",
    "merge.message": "Повідомлення про злиття",
    "merge.messagePlaceholder": "Об’єднати гілку «джерело»",
    "merge.messageFfHint": "Ігнорується під час швидкого злиття (комітів не створюється).",
    "merge.noFastForward": "Створення коміту злиття (без перемотування вперед)",
    "merge.noFastForwardHint": "Завжди записуйте комміт злиття, навіть якщо гілка може перемотувати вперед.",
    "merge.merge": "Об’єднати",
    "merge.cancel": "Скасувати",
    "merge.previewError": "Не вдалося обчислити попередній перегляд: {message}",
    "merge.squashBadge": "Сквош",
    "merge.squashNote": "Сквош-злиття — кожна зміна виконується як ОДНА фіксація на «{target}»; Власні коміти “{source}” залишаються осторонь його історії. Перейдіть назад у Налаштування › Об’єднати.",
    "merge.messageSquash": "Зафіксувати повідомлення",
    "merge.squashMessagePlaceholder": "Здавлені зміни з «джерела»",
    "newBranch.title": "Створити гілку",
    "newBranch.startPoint": "Нова гілка, починаючи з {sha}",
    "newBranch.startPointOn": "на {refs}",
    "newBranch.location": "Розташування",
    "newBranch.locationRoot": "(корінь)",
    "newBranch.expandAll": "Розгорнути все",
    "newBranch.collapseAll": "Згорнути все",
    "newBranch.expandFolder": "Розгорнути папку",
    "newBranch.collapseFolder": "Згорнути папку",
    "newBranch.name": "Назва відділення",
    "newBranch.namePlaceholder": "моя гілка",
    "newBranch.fullName": "Повне ім'я: {name}",
    "newBranch.invalid": "Недійсна назва гілки",
    "newBranch.exists": "Гілка з такою назвою вже існує",
    "newBranch.checkout": "Оформлення гілки після створення",
    "newBranch.create": "Створити",
    "newBranch.cancel": "Скасувати",
    "status.loading": "Завантаження графіка…",
    "status.summary": "{repo} Показ {commits} фіксації, {refs} посилання",
    "status.branchCreated": "Створено гілку \"{name}\" за адресою {sha}",
    "status.error": "Помилка: {message}",
    "status.opFailed": "Операція не вдалася",
    "status.fetching": "Отримання…",
    "status.pulling": "Витягування…",
    "status.pushing": "Натискання…",
    "status.committing": "Створення локального коміту…",
    "status.commitCreated": "Створено локальний коміт {sha}: {message}",
    "status.syncing": "Синхронізація…",
    "status.noHead": "На графіку не знайдено поточного замовлення.",
    "status.undoing": "Скасування фіксації…",
    "status.commitUndone": "Закріплення скасовано — зміни повернулися до робочого дерева.",
    "status.undoConflict": "Скасуйте конфлікти звернень — вирішуйте їх у редакторі, а потім продовжуйте.",
    "status.stashApplying": "Застосування тайника…",
    "status.stashPopping": "Викопувати заначку…",
    "status.stashDropping": "Скидання тайника…",
    "status.stashApplied": "Закладка застосована.",
    "status.stashPopped": "Заначка вискочила.",
    "status.stashDropped": "Заначка випала.",
    "status.stashConflict": "Конфлікти збереження — вирішуйте їх у редакторі.",
    "status.merging": "Об’єднання…",
    "status.merged": "Об’єднання завершено.",
    "status.mergeConflict": "Злиття має конфлікти — вирішіть їх у редакторі, а потім зафіксуйте.",
    "search.title": "Пошук",
    "search.tooltip": "Пошук комітів",
    "search.placeholder": "Пошук зобов’язаний…",
    "search.previous": "Попередній результат",
    "search.next": "Наступний результат",
    "search.noResults": "Результатів немає",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Режим фільтра",
    "search.highlight": "Режим виділення",
    "search.filterMode": "Відображення пошуку",
    "search.filterModeFilter": "Фільтр (приховати невідповідності)",
    "search.filterModeHighlight": "Виділити (показати все, позначити збіги)",
    "search.filterModeHint": "Виберіть спосіб відображення результатів пошуку на графіку.",
    "search.visibility": "Місце пошуку",
    "search.visibilityToolbar": "Кнопка панелі інструментів",
    "search.visibilityAlwaysVisible": "Завжди видима панель",
    "search.visibilityHint": "Показати пошук у вигляді значка панелі інструментів або постійної панелі вгорі.",
    "footer.github": "GitHub",
  },
  kk: {
    "toolbar.refresh": "⟳ Жаңарту",
    "toolbar.fetch": "⤓ Алу",
    "toolbar.pull": "⇩ Тартыңыз",
    "toolbar.push": "⇧ басыңыз",
    "toolbar.commit": "✓ Міндеттеме беру",
    "toolbar.sync": "⇅ Синхрондау",
    "toolbar.jumpHead": "⌖ Кассаға өтіңіз",
    "toolbar.reset": "⤢ Көріністі қалпына келтіру",
    "toolbar.settings": "⚙ Параметрлер",
    "details.header": "Мәліметтер",
    "details.close": "Жабу",
    "details.sha": "SHA",
    "details.shortSha": "Қысқа SHA",
    "details.message": "Хабарлама",
    "details.author": "Автор",
    "details.date": "Күн",
    "details.labels": "Белгілер",
    "details.location": "Орналасқан жері",
    "details.currentHead": "HEAD — ағымдағы тексеру",
    "settings.title": "Параметрлер",
    "settings.language": "Тіл",
    "settings.theme": "Тақырып",
    "settings.themeLight": "Жарық",
    "settings.themeDark": "Қараңғы",
    "settings.themeLightHint": "Ашық фон, қараңғы мәтін.",
    "settings.themeDarkHint": "Қараңғы фон, ашық мәтін.",
    "settings.mainBranch": "Негізгі филиал",
    "settings.mainBranchAuto": "Автоматты",
    "settings.mainBranchSearch": "Филиалдар іздеу…",
    "settings.mainBranchNoMatch": "Сәйкес филиалдар жоқ",
    "settings.display": "Көрсету стилі",
    "settings.displayModern": "Қазіргі заманғы",
    "settings.displayClassic": "Классикалық",
    "settings.displayModernHint": "Тегін кенеп — панорамалау үшін сүйреңіз, масштабтау үшін айналдырыңыз.",
    "settings.displayClassicHint": "Бекітілген кенеп — магистраль солға бекітілген, масштабтау жоқ, тек айналдыру (SVN қайта қарау графигі сияқты).",
    "settings.sectionGeneral": "Жалпы",
    "settings.sectionGraph": "График",
    "settings.sectionChanges": "Көрінісін өзгертеді",
    "settings.diffMinimap": "Айырмашылық миникарта",
    "settings.diffMinimapOn": "Көрсетілген",
    "settings.diffMinimapOff": "Жасырын",
    "settings.diffMinimapOnHint": "Айырмашылықтың жанындағы VS Code стиліндегі шолу жолағын көрсетіңіз — айналдыру үшін оны сүйреңіз; өзгерту маркерлері енгізілген.",
    "settings.diffMinimapOffHint": "Шолу жолағы жоқ — дифференцияны әдеттегідей айналдырыңыз.",
    "settings.commitReview": "Қарауды тапсыру",
    "settings.commitReviewOn": "Жасалмас бұрын сұраңыз",
    "settings.commitReviewOff": "Тікелей тапсырыңыз",
    "settings.commitReviewOnHint": "Жергілікті тапсырманы жасамас бұрын соңғы файлдар тізімін және хабарды тексеруді көрсетіңіз.",
    "settings.commitReviewOffHint": "Орындау түймесі жергілікті міндеттемені дереу жасайды.",
    "settings.svnBranchDialog": "SVN стиліндегі тармақ диалогы",
    "settings.svnBranchDialogHint": "Тармақ құру кезінде қалта ағашын таңдау құралын көрсетіңіз (қарапайым шақырудың орнына).",
    "settings.branchDialog": "Филиал диалогы",
    "settings.branchDialogSvnTitle": "SVN стилі",
    "settings.branchDialogNativeVscode": "VS код стилі",
    "settings.branchDialogNativeVs": "Visual Studio стилі",
    "settings.branchDialogNativeHint": "IDE кірістірілген бөлімше сұрауы.",
    "settings.close": "Жабу",
    "settings.done": "Дайын",
    "settings.sectionAdvanced": "Жетілдірілген",
    "settings.gitSource": "Git орындалатын",
    "settings.gitSourceBuiltin": "Кірістірілген",
    "settings.gitSourceBuiltinHint": "IDE кірістірілген Git кеңейтімінен git екілік нұсқасын пайдаланыңыз.",
    "settings.gitSourceCustom": "Арнаулы жол",
    "settings.gitPath": "Гитке апаратын жол",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Шолу…",
    "settings.jargon": "Git шарттары",
    "settings.jargonTranslate": "Аудару",
    "settings.jargonEnglish": "Ағылшын тілінде сақтаңыз",
    "settings.jargonTranslateHint": "Git терминдерін (тарту, итеру, орындау, тармақтау...) интерфейс тіліне аударыңыз.",
    "settings.jargonEnglishHint": "Git терминдерін ағылшын тілінде сақтаңыз (тарту, итеру, орындау, тармақтау…); қалғанының бәрін аударыңыз.",
    "settings.sectionMerge": "Біріктіру",
    "settings.mergeMode": "Біріктіру стилі",
    "settings.mergeModeMerge": "Қалыпты біріктіру",
    "settings.mergeModeSquash": "Сквош біріктіру",
    "settings.mergeModeMergeHint": "Филиалды сол күйінде біріктіреді: оның міндеттемелері біріктіру тапсырмасымен қосылатын (немесе мүмкін болса, жылдам алға жіберілетін) ағымдағы филиал тарихының бір бөлігіне айналады.",
    "settings.mergeModeSquashHint": "Бүкіл тармақты ағымдағы тармақтағы жалғыз міндеттемеге қысқартады (git merge --squash). Филиалдың жеке міндеттемелері ешқашан ағымдағы филиалдың тарихына кірмейді — онда сіз тек бір міндеттемені көресіз. Филиалдың өзі қозғалмайды, сондықтан оның міндеттемелері өз жолағында көрінеді.",
    "settings.mergedView": "Графиктегі біріктірілген міндеттемелер",
    "settings.mergedViewBranch": "Тек өз филиалында",
    "settings.mergedViewTarget": "Сондай-ақ филиалда олар біріктірілді",
    "settings.mergedViewBranchHint": "Біріктірілген филиалдың міндеттемелері тек өз жолағында шығарылады; біріктіру қосқыш ретінде көрсетіледі. Классикалық көрініс.",
    "settings.mergedViewTargetHint": "Енгізілген біріктіру міндеттемелері қабылдаушы тармақтың жолағында сызылады, біріктіру тапсырмасының астына жиналады және түпнұсқалармен деңгейлеседі — өйткені бұл тармақта қазір олар шынымен де бар. Көшірмелер ақшыл және олар жазылған тармақпен басылған, сондықтан олар онда жасалмағаны анық. Сквош біріктірулеріне әсер етпейді: сквош бір кәдімгі міндеттемені жазады, сондықтан тек сол міндеттеме пайда болады.",
    "legend.title": "Аңыз",
    "legend.head": "HEAD / ағымдағы филиал",
    "legend.local": "Жергілікті филиал",
    "legend.remote": "Қашықтағы филиал",
    "legend.remoteOnly": "Тек бұлтта (тартылмаған)",
    "legend.tag": "Тег (нұсқа)",
    "legend.commit": "Міндеттеме беру",
    "legend.stash": "Сташ (сөредегі жұмыс)",
    "legend.mergedIn": "Біріктірумен келді (басқа филиалда жазылған)",
    "legend.nodes": "Қораптар",
    "legend.lines": "Сызықтар",
    "legend.edgeParent": "Сол жолда алдыңғы міндеттеме",
    "legend.edgeMerge": "Біріктірілген тармақ — көрсеткі біріктіру тапсырмасын көрсетеді",
    "legend.edgeBranch": "Бұтақ осы жерден шығып кетті",
    "legend.edgeStash": "Stash және ол орындалмаған міндеттеме",
    "legend.edgeMergedChain": "Бұл тармақтың сызығы біріктірілген міндеттемелер арқылы өтеді",
    "legend.edgeMergedTie": "Филиалда дәл осындай міндеттеме жазылған",
    "menu.jumpHead": "⌖ Кассаға өтіңіз",
    "menu.resetView": "⤢ Көріністі қалпына келтіру",
    "menu.createBranch": "Осы жерден филиал жасаңыз…",
    "menu.checkout": "Бұл міндеттемені тексеріңіз",
    "menu.copySha": "SHA-ны көшіру",
    "menu.pushBranch": "\"{name}\" тармағын басыңыз",
    "menu.renameBranch": "«{name}» тармағының атын өзгерту…",
    "menu.renameCommit": "Тапсырыс беру хабарының атын өзгерту…",
    "menu.undoCommit": "Міндеттемені болдырмау (өзгерістерді сақтау)…",
    "menu.viewChanges": "Өзгерістерді көру…",
    "menu.jumpToOriginal": "Бастапқы міндеттемеге өтіңіз",
    "changes.title": "{sha} өзгерістері",
    "changes.tabChanged": "Өзгертілді",
    "changes.tabAll": "Барлық файлдар",
    "changes.added": "Қосылды",
    "changes.modified": "Өзгертілген",
    "changes.deleted": "Жойылды",
    "changes.renamed": "Аты өзгертілді",
    "changes.noChanges": "Бұл міндеттеме файлдарды өзгертпейді.",
    "changes.loading": "Айырма жүктелуде…",
    "changes.selectFile": "Өзгерістерді көру үшін файлды таңдаңыз.",
    "changes.original": "Түпнұсқа",
    "changes.changed": "Бұл міндеттеме",
    "changes.binary": "Екілік файл — көрсетілетін мәтін айырмашылығы жоқ.",
    "changes.tooLarge": "Файл ажырату үшін тым үлкен.",
    "changes.renamedFrom": "{path} атауынан өзгертілді",
    "changes.close": "Жабу",
    "changes.prevChange": "Алдыңғы өзгеріс",
    "changes.nextChange": "Келесі өзгеріс",
    "changes.collapseAll": "Барлық қалталарды жию",
    "changes.expandAll": "Барлық қалталарды кеңейтіңіз",
    "changes.searchPlaceholder": "Файлдарды іздеу…",
    "changes.noSearchResults": "Іздеуіңізге ешбір файл сәйкес келмейді.",
    "changes.maximize": "Үлкейту",
    "changes.restore": "Өлшемді қалпына келтіру",
    "find.open": "Осы айырмашылықта іздеу (Ctrl+F)",
    "find.placeholder": "Айырмашылықта табыңыз…",
    "find.prev": "Алдыңғы матч",
    "find.next": "Келесі матч",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Жабу",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Барлығын таңдаңыз",
    "commit.selectNone": "Барлығын өшіру",
    "commit.message": "Хабарламаны тапсыру",
    "commit.messagePlaceholder": "Өзгерісті сипаттаңыз",
    "commit.selectedCount": "{count} файлдары таңдалды",
    "commit.review": "Қарау",
    "commit.reviewTitle": "Орындау алдында соңғы тексеру",
    "commit.reviewSummary": "{count} файл(дар)ын келесі хабарламамен жіберіңіз: {message}",
    "commit.back": "Артқа",
    "commit.commit": "Жергілікті міндеттеме жасаңыз",
    "menu.stashApply": "Сақтау қолданыңыз",
    "menu.stashPop": "Қалқымалы қойма (қолдану және жою)",
    "menu.stashDrop": "Сақтауды тастаңыз…",
    "merge.title": "Филиалды біріктіру",
    "merge.route": "Ағымдағы филиалға біріктіру",
    "merge.source": "(көзі)",
    "merge.target": "Кіру (ағымдағы)",
    "merge.loading": "Біріктіру талдануда…",
    "merge.upToDate": "Қазірдің өзінде жаңартылған — біріктіретін ештеңе жоқ.",
    "merge.fastForward": "Жылдам алға — біріктіру міндеттемесі қажет емес (біріктіру үшін «Біріктіру тапсырмасын жасау» құсбелгісін қойыңыз).",
    "merge.summary": "{files} файлдары өзгертілді, {conflicts} қайшылығы(лар).",
    "merge.conflictsWarning": "Бұл біріктіруде қайшылықтар бар — сіз әлі де біріктіріп, содан кейін оларды өңдегіште шеше аласыз.",
    "merge.noChanges": "Файл өзгерістері жоқ.",
    "merge.files": "Нәтижедегі өзгерістер",
    "merge.added": "Қосылды",
    "merge.modified": "Өзгертілген",
    "merge.deleted": "Жойылды",
    "merge.conflict": "Қақтығыс",
    "merge.message": "Біріктіру туралы хабарлама",
    "merge.messagePlaceholder": "Филиалды біріктіру \"көзі\"",
    "merge.messageFfHint": "Жылдам алға біріктіру кезінде еленбейді (ешқандай міндеттеме жасалмайды).",
    "merge.noFastForward": "Біріктіру тапсырмасын жасаңыз (жылдам алға жылжытусыз)",
    "merge.noFastForwardHint": "Филиал алға жылжу мүмкін болса да, әрқашан біріктіру тапсырмасын жазып алыңыз.",
    "merge.merge": "Біріктіру",
    "merge.cancel": "Болдырмау",
    "merge.previewError": "Алдын ала қарауды есептеу мүмкін болмады: {message}",
    "merge.squashBadge": "Сквош",
    "merge.squashNote": "Сквош біріктіру — әрбір өзгеріс «{target}» бойынша БІР орындалғанда орындалады; «{source}» өз міндеттемелері оның тарихынан тыс қалады. Параметрлер › Біріктіру тармағында кері ауысыңыз.",
    "merge.messageSquash": "Хабарламаны тапсыру",
    "merge.squashMessagePlaceholder": "\"Дереккөзден\" қысқартылған өзгерістер",
    "newBranch.title": "Филиал құру",
    "newBranch.startPoint": "{sha} бастап жаңа филиал",
    "newBranch.startPointOn": "{refs} бойынша",
    "newBranch.location": "Орналасқан жері",
    "newBranch.locationRoot": "(түбір)",
    "newBranch.expandAll": "Барлығын кеңейту",
    "newBranch.collapseAll": "Барлығын жию",
    "newBranch.expandFolder": "Қалтаны кеңейту",
    "newBranch.collapseFolder": "Қалтаны жию",
    "newBranch.name": "Филиал атауы",
    "newBranch.namePlaceholder": "менің филиалым",
    "newBranch.fullName": "Толық аты-жөні: {name}",
    "newBranch.invalid": "Жарамсыз филиал атауы",
    "newBranch.exists": "Мұндай атаумен филиал бұрыннан бар",
    "newBranch.checkout": "Жасалғаннан кейін бөлімшесі",
    "newBranch.create": "Жасау",
    "newBranch.cancel": "Болдырмау",
    "status.loading": "График жүктелуде…",
    "status.summary": "{repo}{commits} тапсырмаларын, {refs} сілтемелерін көрсету",
    "status.branchCreated": "{sha} орнында «{name}» филиалы құрылды",
    "status.error": "Қате: {message}",
    "status.opFailed": "Операция сәтсіз аяқталды",
    "status.fetching": "Алылуда…",
    "status.pulling": "Тарту…",
    "status.pushing": "Басылуда…",
    "status.committing": "Жергілікті міндеттеме жасалуда…",
    "status.commitCreated": "{sha}: {message} жергілікті міндеттемесі жасалды",
    "status.syncing": "Синхрондалуда…",
    "status.noHead": "Графикте ағымдағы тексеру табылмады.",
    "status.undoing": "Міндеттемені қайтару…",
    "status.commitUndone": "Орындалмады - өзгертулер жұмыс тармағына қайта оралады.",
    "status.undoConflict": "Тиісті қайшылықтарды болдырмаңыз — оларды редакторда шешіңіз, содан кейін жалғастырыңыз.",
    "status.stashApplying": "Сақтау қолданылуда…",
    "status.stashPopping": "Қойылған қойма…",
    "status.stashDropping": "Қойма тасталуда…",
    "status.stashApplied": "Stash қолданылды.",
    "status.stashPopped": "Сташ шықты.",
    "status.stashDropped": "Сташ құлады.",
    "status.stashConflict": "Сақтау қайшылықтары — оларды редакторда шешіңіз.",
    "status.merging": "Біріктіруде…",
    "status.merged": "Біріктіру аяқталды.",
    "status.mergeConflict": "Біріктіруде қайшылықтар бар — оларды редакторда шешіңіз, содан кейін орындаңыз.",
    "search.title": "Іздеу",
    "search.tooltip": "Іздеу тапсырмалары",
    "search.placeholder": "Іздеу тапсырмалары…",
    "search.previous": "Алдыңғы нәтиже",
    "search.next": "Келесі нәтиже",
    "search.noResults": "Нәтиже жоқ",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Сүзгі режимі",
    "search.highlight": "Бөлектеу режимі",
    "search.filterMode": "Іздеу дисплейі",
    "search.filterModeFilter": "Сүзгі (сәйкес келмейтіндерді жасыру)",
    "search.filterModeHighlight": "Бөлектеу (барлығын көрсету, сәйкестіктерді белгілеу)",
    "search.filterModeHint": "Графикте іздеу нәтижелерінің қалай көрсетілетінін таңдаңыз.",
    "search.visibility": "Орынды іздеу",
    "search.visibilityToolbar": "Құралдар тақтасының түймесі",
    "search.visibilityAlwaysVisible": "Әрқашан көрінетін жолақ",
    "search.visibilityHint": "Іздеуді құралдар тақтасының белгішесі немесе жоғарғы жағындағы тұрақты жолақ ретінде көрсету.",
    "footer.github": "GitHub",
  },
  hy: {
    "toolbar.refresh": "⟳ Թարմացնել",
    "toolbar.fetch": "⤓ Բերել",
    "toolbar.pull": "⇩ Քաշեք",
    "toolbar.push": "⇧ Հրել",
    "toolbar.commit": "✓ Պարտավորվեք",
    "toolbar.sync": "⇅ Համաժամեցում",
    "toolbar.jumpHead": "⌖ Գնացեք վճարում",
    "toolbar.reset": "⤢ Վերականգնել տեսքը",
    "toolbar.settings": "⚙ Կարգավորումներ",
    "details.header": "Պարտավորել մանրամասները",
    "details.close": "Փակել",
    "details.sha": "SHA",
    "details.shortSha": "Կարճ SHA",
    "details.message": "Հաղորդագրություն",
    "details.author": "Հեղինակ",
    "details.date": "Ամսաթիվ",
    "details.labels": "Պիտակներ",
    "details.location": "Գտնվելու վայրը",
    "details.currentHead": "ԳԼԽԱՎՈՐ — ընթացիկ վճարում",
    "settings.title": "Կարգավորումներ",
    "settings.language": "Լեզու",
    "settings.theme": "Թեմա",
    "settings.themeLight": "Լույս",
    "settings.themeDark": "Մութ",
    "settings.themeLightHint": "Բաց ֆոն, մուգ տեքստ։",
    "settings.themeDarkHint": "Մուգ ֆոն, բաց տեքստ:",
    "settings.mainBranch": "Հիմնական մասնաճյուղ",
    "settings.mainBranchAuto": "Ավտոմատ",
    "settings.mainBranchSearch": "Որոնել մասնաճյուղեր…",
    "settings.mainBranchNoMatch": "Համապատասխան ճյուղեր չկան",
    "settings.display": "Ցուցադրման ոճը",
    "settings.displayModern": "Ժամանակակից",
    "settings.displayClassic": "Դասական",
    "settings.displayModernHint": "Անվճար կտավ — քաշել՝ ոլորելու համար, ոլորել՝ մեծացնելու համար:",
    "settings.displayClassicHint": "Ֆիքսված կտավ — բեռնախցիկը ամրացված է ձախ, առանց խոշորացման, միայն ոլորել (ինչպես SVN-ի վերանայման գրաֆիկը):",
    "settings.sectionGeneral": "Գեներալ",
    "settings.sectionGraph": "Գրաֆիկ",
    "settings.sectionChanges": "Փոխում է տեսքը",
    "settings.diffMinimap": "Տարբերության մինի քարտեզ",
    "settings.diffMinimapOn": "Ցուցադրված է",
    "settings.diffMinimapOff": "Թաքնված",
    "settings.diffMinimapOnHint": "Ցուցադրել VS Code-ի ոճի համառոտ ժապավենը տարբերության կողքին — քաշել այն ոլորելու համար; ներառված են փոփոխության մարկերներ:",
    "settings.diffMinimapOffHint": "Համառոտ գծապատկեր չկա. նորմալ ոլորեք տարբերությունը:",
    "settings.commitReview": "Ստանձնել վերանայում",
    "settings.commitReviewOn": "Հարցրեք նախքան կատարելը",
    "settings.commitReviewOff": "Անմիջապես պարտավորվել",
    "settings.commitReviewOnHint": "Ցույց տալ վերջնական ֆայլերի ցանկը և հաղորդագրությունների ստուգումը, նախքան տեղական commit-ը ստեղծելը:",
    "settings.commitReviewOffHint": "Commit կոճակը անմիջապես ստեղծում է տեղական commit:",
    "settings.svnBranchDialog": "SVN ոճով ճյուղային երկխոսություն",
    "settings.svnBranchDialogHint": "Ցույց տալ թղթապանակ-ծառ ընտրող՝ ճյուղ ստեղծելիս (պարզ հուշի փոխարեն):",
    "settings.branchDialog": "Մասնաճյուղի երկխոսություն",
    "settings.branchDialogSvnTitle": "SVN ոճով",
    "settings.branchDialogNativeVscode": "VS Code ոճը",
    "settings.branchDialogNativeVs": "Visual Studio ոճը",
    "settings.branchDialogNativeHint": "Ձեր IDE-ի ներկառուցված մասնաճյուղի հուշումը:",
    "settings.close": "Փակել",
    "settings.done": "Կատարված է",
    "settings.sectionAdvanced": "Ընդլայնված",
    "settings.gitSource": "Git գործարկվող",
    "settings.gitSourceBuiltin": "Ներկառուցված",
    "settings.gitSourceBuiltinHint": "Օգտագործեք git երկուականը IDE-ի ներկառուցված Git ընդլայնումից:",
    "settings.gitSourceCustom": "Պատվերով ճանապարհ",
    "settings.gitPath": "Ճանապարհ դեպի git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "Թերթիր…",
    "settings.jargon": "Git պայմաններ",
    "settings.jargonTranslate": "Թարգմանել",
    "settings.jargonEnglish": "Պահել անգլերեն",
    "settings.jargonTranslateHint": "Թարգմանեք Git տերմինները (քաշեք, մղել, պարտավորեցնել, ճյուղավորել…) ինտերֆեյսի լեզվով:",
    "settings.jargonEnglishHint": "Պահպանեք Git տերմինները (քաշեք, մղել, պարտավորվել, ճյուղավորել…) անգլերենով; թարգմանել մնացած ամեն ինչ:",
    "settings.sectionMerge": "Միաձուլել",
    "settings.mergeMode": "Միաձուլման ոճը",
    "settings.mergeModeMerge": "Նորմալ միաձուլում",
    "settings.mergeModeSquash": "Squash միաձուլվել",
    "settings.mergeModeMergeHint": "Միաձուլում է մասնաճյուղը այնպես, ինչպես որ կա. նրա պարտավորությունները դառնում են ընթացիկ մասնաճյուղի պատմության մի մասը, որին միանում է միաձուլման հանձնարարականը (կամ հնարավորության դեպքում արագ փոխանցում):",
    "settings.mergeModeSquashHint": "Ամբողջ ճյուղը փլուզում է ընթացիկ ճյուղի մեկ կատարման մեջ (git merge --squash): Մասնաճյուղի սեփական պարտավորությունները երբեք չեն մտնում ընթացիկ մասնաճյուղի պատմության մեջ. այնտեղ դուք տեսնում եք միայն մեկ պարտավորություն: Մասնաճյուղն ինքնին մնացել է անձեռնմխելի, ուստի նրա պարտավորությունները տեսանելի են մնում սեփական գծում:",
    "settings.mergedView": "Միաձուլված պարտավորությունները գրաֆիկում",
    "settings.mergedViewBranch": "Միայն իրենց սեփական մասնաճյուղում",
    "settings.mergedViewTarget": "Մասնաճյուղում նույնպես դրանք միացվել են",
    "settings.mergedViewBranchHint": "Միաձուլված մասնաճյուղի պարտավորությունները կազմվում են միայն իրենց սեփական գոտում. միաձուլումը ցուցադրվում է որպես միակցիչ: Դասական տեսակետ.",
    "settings.mergedViewTargetHint": "Բերված միաձուլման պարտավորությունները գծվում են նաև ընդունող ճյուղի գծում, դրվում են միաձուլման հանձնման տակ և հավասարվում բնօրինակներին, քանի որ այդ ճյուղն իսկապես պարունակում է դրանք հիմա: Պատճենները գունատ են և վերնագրված են այն ճյուղով, որի վրա գրված է եղել, ուստի պարզ է մնում, որ դրանք այնտեղ չեն արվել: Squash-ի միաձուլումները չեն ազդում. squash-ը գրում է մեկ սովորական commit, այնպես որ միայն այդ commit-ը երբևէ հայտնվում է:",
    "legend.title": "Լեգենդ",
    "legend.head": "HEAD / ընթացիկ մասնաճյուղ",
    "legend.local": "Տեղական մասնաճյուղ",
    "legend.remote": "Հեռավոր մասնաճյուղ",
    "legend.remoteOnly": "Միայն ամպի մեջ (չքաշված)",
    "legend.tag": "Պիտակ (տարբերակ)",
    "legend.commit": "Պարտավորվել",
    "legend.stash": "Stash (դարակաշարային աշխատանք)",
    "legend.mergedIn": "Եկավ միաձուլմամբ (գրված է մեկ այլ ճյուղի վրա)",
    "legend.nodes": "Արկղեր",
    "legend.lines": "Գծեր",
    "legend.edgeParent": "Նախորդ պարտավորությունը նույն գծի վրա",
    "legend.edgeMerge": "Միաձուլված ճյուղ — սլաքը ցույց է տալիս միաձուլման հանձնումը",
    "legend.edgeBranch": "Այստեղ ճյուղ է ճեղքվել",
    "legend.edgeStash": "Ստաշը և այն պարտավորությունը, որի վրա դրված էր",
    "legend.edgeMergedChain": "Այս ճյուղի գիծն անցնում է միաձուլված ստորաբաժանումների միջով",
    "legend.edgeMergedTie": "Նույն պարտավորությունը ճյուղի վրա, որի վրա գրված էր",
    "menu.jumpHead": "⌖ Գնացեք վճարում",
    "menu.resetView": "⤢ Վերականգնել տեսքը",
    "menu.createBranch": "Ստեղծեք մասնաճյուղ այստեղից…",
    "menu.checkout": "Ստուգեք այս պարտավորությունը",
    "menu.copySha": "Պատճենել commit SHA",
    "menu.pushBranch": "«{name}» ճյուղ",
    "menu.deleteBranch": "Ջնջել «{name}» մասնաճյուղը",
    "menu.renameCommit": "Վերանվանել հանձնառության հաղորդագրությունը…",
    "menu.undoCommit": "Հետարկել պարտավորությունը (պահել փոփոխությունները)…",
    "menu.viewChanges": "Դիտել փոփոխությունները…",
    "menu.mergeBranch": "Միավորել «{source}»-ը «{target}»-ի մեջ",
    "menu.jumpToOriginal": "Անցեք սկզբնական հանձնառությանը",
    "node.mergedInTooltip": "Merge {merge}-ը բերեց այս պարտավորությունը «{target}»-ի մեջ, այն գրված էր «{origin}»-ի վրա:",
    "changes.title": "Փոփոխություններ {sha}-ում",
    "changes.tabChanged": "Փոխվել է",
    "changes.tabAll": "Բոլոր Ֆայլերը",
    "changes.added": "Ավելացված է",
    "changes.modified": "Փոփոխված",
    "changes.deleted": "Ջնջված է",
    "changes.renamed": "Վերանվանվել է",
    "changes.noChanges": "Այս հանձնարարականը ոչ մի ֆայլ չի փոխում:",
    "changes.loading": "Բեռնվում է տարբերությունը…",
    "changes.selectFile": "Ընտրեք ֆայլ՝ դրա փոփոխությունները տեսնելու համար:",
    "changes.original": "Օրիգինալ",
    "changes.changed": "Այս պարտավորությունը",
    "changes.binary": "Երկուական ֆայլ — ցուցադրման համար տեքստի տարբերություն չկա:",
    "changes.tooLarge": "Ֆայլը չափազանց մեծ է տարբերվելու համար:",
    "changes.renamedFrom": "Վերանվանվել է {path}-ից",
    "changes.close": "Փակել",
    "changes.prevChange": "Նախորդ փոփոխություն",
    "changes.nextChange": "Հաջորդ փոփոխությունը",
    "changes.collapseAll": "Ծալել բոլոր թղթապանակները",
    "changes.expandAll": "Ընդլայնել բոլոր թղթապանակները",
    "changes.searchPlaceholder": "Որոնել ֆայլեր…",
    "changes.noSearchResults": "Ոչ մի ֆայլ չի համապատասխանում ձեր որոնմանը:",
    "changes.maximize": "Առավելագույնի հասցնել",
    "changes.restore": "Վերականգնել չափը",
    "find.open": "Որոնել այս տարբերությամբ (Ctrl+F)",
    "find.placeholder": "Գտեք տարբերության մեջ…",
    "find.prev": "Նախորդ հանդիպում",
    "find.next": "Հաջորդ հանդիպումը",
    "find.close": "Փակել որոնումը",
    "find.noResults": "Ոչ մի արդյունք",
    "commit.title": "Կատարել փոփոխություններ",
    "commit.close": "Փակել",
    "commit.loading": "Աշխատանքային ծառի ընթերցում…",
    "commit.noChanges": "Տեղական փոփոխություններ չկան կատարելու:",
    "commit.selectFile": "Ընտրեք ֆայլ՝ դրա տարբերությունը տեսնելու համար:",
    "commit.selectAll": "Ընտրեք բոլորը",
    "commit.selectNone": "Մաքրել բոլորը",
    "commit.message": "Հանձնարարել հաղորդագրությունը",
    "commit.messagePlaceholder": "Նկարագրեք փոփոխությունը",
    "commit.selectedCount": "Ընտրված է {count} ֆայլ(ներ):",
    "commit.review": "Վերանայում",
    "commit.reviewTitle": "Վերջնական ստուգում կատարելուց առաջ",
    "commit.reviewSummary": "Կատարեք {count} ֆայլ(ներ)՝ {message} հաղորդագրությամբ",
    "commit.back": "Ետ",
    "commit.commit": "Ստեղծեք տեղական հանձնառություն",
    "menu.stashApply": "Կիրառել stash",
    "menu.stashPop": "Pop stash (կիրառել և հեռացնել)",
    "menu.stashDrop": "Բաց թողեք պահոցը…",
    "merge.title": "Միավորել մասնաճյուղը",
    "merge.route": "Միաձուլվելով ընթացիկ ճյուղին",
    "merge.source": "From (աղբյուր)",
    "merge.target": "Մեջ (ներկայիս)",
    "merge.loading": "Միաձուլման վերլուծություն…",
    "merge.upToDate": "Արդեն արդիական է՝ միավորելու ոչինչ:",
    "merge.fastForward": "Արագ առաջ – միաձուլման պարտավորություն չկա (կտտացրեք «Ստեղծել միաձուլման պարտավորություն»՝ պարտադրելու համար):",
    "merge.summary": "{files} ֆայլ(ներ) փոխվել է, {conflicts} կոնֆլիկտ(ներ):",
    "merge.conflictsWarning": "Այս միաձուլումը հակասություններ ունի. դուք դեռ կարող եք միաձուլվել, ապա դրանք լուծել խմբագրում:",
    "merge.noChanges": "Ֆայլի փոփոխություններ չկան:",
    "merge.files": "Արդյունքում փոփոխություններ",
    "merge.added": "Ավելացված է",
    "merge.modified": "Փոփոխված",
    "merge.deleted": "Ջնջված է",
    "merge.conflict": "Կոնֆլիկտ",
    "merge.message": "Միաձուլման հանձնման հաղորդագրություն",
    "merge.messagePlaceholder": "Միավորել մասնաճյուղի «աղբյուրը»",
    "merge.messageFfHint": "Անտեսվել է արագ առաջ միաձուլման ժամանակ (ոչ մի պարտավորություն չի ստեղծվում):",
    "merge.noFastForward": "Ստեղծեք միաձուլման հանձնարարություն (առանց արագ առաջընթացի)",
    "merge.noFastForwardHint": "Միշտ գրանցեք միաձուլման պարտավորություն, նույնիսկ երբ մասնաճյուղը կարող էր արագ առաջ շարժվել:",
    "merge.merge": "Միաձուլել",
    "merge.cancel": "Չեղարկել",
    "merge.previewError": "Չհաջողվեց հաշվարկել նախադիտում՝ {message}",
    "merge.squashBadge": "Դդմիկ",
    "merge.squashNote": "Squash-ի միաձուլում. յուրաքանչյուր փոփոխություն տեղի է ունենում, երբ ONE-ը կատարում է «{target}»-ի վրա; «{source}»-ի սեփական պարտավորությունները դուրս են մնում իր պատմությունից: Վերադարձեք Կարգավորումներ › Միաձուլում:",
    "merge.messageSquash": "Հանձնարարել հաղորդագրությունը",
    "merge.squashMessagePlaceholder": "Կծկված փոփոխություններ «աղբյուրից»",
    "newBranch.title": "Ստեղծել մասնաճյուղ",
    "newBranch.startPoint": "Նոր մասնաճյուղ՝ սկսած {sha}-ից",
    "newBranch.startPointOn": "{refs}-ի վրա",
    "newBranch.location": "Գտնվելու վայրը",
    "newBranch.locationRoot": "(արմատ)",
    "newBranch.expandAll": "Ընդլայնել բոլորը",
    "newBranch.collapseAll": "Փլուզել բոլորը",
    "newBranch.expandFolder": "Ընդլայնել թղթապանակը",
    "newBranch.collapseFolder": "Ծալել թղթապանակը",
    "newBranch.name": "Մասնաճյուղի անվանումը",
    "newBranch.namePlaceholder": "իմ-մասնաճյուղ",
    "newBranch.fullName": "Ամբողջական անունը՝ {name}",
    "newBranch.invalid": "Անվավեր մասնաճյուղի անուն",
    "newBranch.exists": "Այս անունով մասնաճյուղ արդեն գոյություն ունի",
    "newBranch.checkout": "Ստուգեք մասնաճյուղը ստեղծումից հետո",
    "newBranch.create": "Ստեղծել",
    "newBranch.cancel": "Չեղարկել",
    "status.loading": "Գրաֆիկի բեռնում…",
    "status.summary": "{repo} Ցույց տալով {commits} պարտավորությունները, {refs} refs",
    "status.branchCreated": "Ստեղծվել է «{name}» մասնաճյուղը {sha}-ում",
    "status.error": "Սխալ՝ {message}",
    "status.opFailed": "Գործողությունը ձախողվեց",
    "status.fetching": "Բերվում է…",
    "status.pulling": "Քաշում…",
    "status.pushing": "Հրում…",
    "status.committing": "Տեղական հանձնառության ստեղծում…",
    "status.commitCreated": "Ստեղծվել է տեղական commit {sha}՝ {message}",
    "status.syncing": "Համաժամացում…",
    "status.noHead": "Գրաֆիկում ընթացիկ վճարում չի գտնվել:",
    "status.undoing": "Պարտավորության չեղարկում…",
    "status.commitUndone": "Պարտավորությունը չեղարկված է. փոփոխությունները վերադարձել են աշխատանքային ծառին:",
    "status.undoConflict": "Հետարկել հարվածների կոնֆլիկտները. լուծել դրանք խմբագրում, ապա շարունակել:",
    "status.stashApplying": "Կիրառվում է պահոց…",
    "status.stashPopping": "Թափվող պահոց…",
    "status.stashDropping": "Բաց թողնում…",
    "status.stashApplied": "Ստաշը դիմել է:",
    "status.stashPopped": "Ստաշը հայտնվեց:",
    "status.stashDropped": "Ստաշը ընկավ:",
    "status.stashConflict": "Stash կոնֆլիկտներ — լուծեք դրանք խմբագրում:",
    "status.merging": "Միաձուլում…",
    "status.merged": "Միաձուլումն ավարտված է:",
    "status.mergeConflict": "Միաձուլումը հակասություններ ունի. լուծեք դրանք խմբագրում, ապա կատարեք:",
    "search.title": "Որոնում",
    "search.tooltip": "Որոնումը պարտավորվում է",
    "search.placeholder": "Որոնումը պարտավորեցնում է…",
    "search.previous": "Նախորդ արդյունքը",
    "search.next": "Հաջորդ արդյունքը",
    "search.noResults": "Ոչ մի արդյունք",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Զտման ռեժիմ",
    "search.highlight": "Ընդգծման ռեժիմ",
    "search.filterMode": "Որոնման ցուցադրում",
    "search.filterModeFilter": "Զտել (թաքցնել չհամընկնող)",
    "search.filterModeHighlight": "Նշել (ցույց տալ բոլորը, նշել համընկնումները)",
    "search.filterModeHint": "Ընտրեք, թե ինչպես են որոնման արդյունքները ցուցադրվում գրաֆիկում:",
    "search.visibility": "Որոնել գտնվելու վայրը",
    "search.visibilityToolbar": "Գործիքադարակի կոճակ",
    "search.visibilityAlwaysVisible": "Միշտ տեսանելի բար",
    "search.visibilityHint": "Ցուցադրել որոնումը որպես գործիքագոտու պատկերակ կամ մշտական ​​տող վերևում:",
    "footer.github": "GitHub",
  },
  yi: {
    "toolbar.refresh": "⟳ דערפרישן",
    "toolbar.fetch": "⤓ ברענגען",
    "toolbar.pull": "⇩ ציען",
    "toolbar.push": "⇧ שטופּן",
    "toolbar.commit": "✓ יבערגעבן",
    "toolbar.sync": "⇅ סינק",
    "toolbar.jumpHead": "⌖ גיין צו ויסשרייַבן",
    "toolbar.reset": "⤢ באַשטעטיק מיינונג",
    "toolbar.settings": "⚙ סעטטינגס",
    "details.header": "קאָממיט דעטאַילס",
    "details.close": "נאָענט",
    "details.sha": "SHA",
    "details.shortSha": "קורץ SHA",
    "details.message": "אָנזאָג",
    "details.author": "מחבר",
    "details.date": "טאָג",
    "details.labels": "לאַבעלס",
    "details.location": "אָרט",
    "details.currentHead": "HEAD - קראַנט ויסשרייַבן",
    "settings.title": "סעטטינגס",
    "settings.language": "שפּראַך",
    "settings.theme": "טעמע",
    "settings.themeLight": "ליכט",
    "settings.themeDark": "טונקל",
    "settings.themeLightHint": "ליכט הינטערגרונט, טונקל טעקסט.",
    "settings.themeDarkHint": "טונקל הינטערגרונט, ליכט טעקסט.",
    "settings.mainBranch": "הויפּט צווייַג",
    "settings.mainBranchAuto": "אָטאַמאַטיק",
    "settings.mainBranchSearch": "זוך צווייגן...",
    "settings.mainBranchNoMatch": "ניט וואָס ריכטן צווייגן",
    "settings.display": "ווייַז סטיל",
    "settings.displayModern": "מאָדערן",
    "settings.displayClassic": "קלאַסיש",
    "settings.displayModernHint": "פריי לייַוונט - שלעפּן צו פּאַן, מעגילע צו פארגרעסער.",
    "settings.displayClassicHint": "פאַרפעסטיקט לייַוונט - שטאַם פּינד לינקס, קיין פארגרעסער, מעגילע בלויז (ווי די SVN רעוויזיע גראַפיק).",
    "settings.sectionGeneral": "אַלגעמיינע",
    "settings.sectionGraph": "גראַפיק",
    "settings.sectionChanges": "ענדערונגען מיינונג",
    "settings.diffMinimap": "אַנדערש מינימאַפּ",
    "settings.diffMinimapOn": "געוויזן",
    "settings.diffMinimapOff": "פאַרבאָרגן",
    "settings.diffMinimapOnHint": "ווייַזן אַ ווס קאָוד-נוסח איבערבליק פּאַס בייַ די חילוק - שלעפּן עס צו מעגילע; טוישן מאַרקערס אַרייַנגערעכנט.",
    "settings.diffMinimapOffHint": "קיין איבערבליק פּאַס - מעגילע די חילוק נאָרמאַלי.",
    "settings.commitReview": "יבערגעבן רעצענזיע",
    "settings.commitReviewOn": "פרעגן איידער טוען",
    "settings.commitReviewOff": "יבערגעבן גלייַך",
    "settings.commitReviewOnHint": "ווייַזן אַ לעצט טעקע רשימה און אָנזאָג טשעק איידער קריייטינג די היגע יבערגעבן.",
    "settings.commitReviewOffHint": "די קאָממיט קנעפּל קריייץ די היגע יבערגעבן מיד.",
    "settings.svnBranchDialog": "SVN-נוסח צווייַג דיאַלאָג",
    "settings.svnBranchDialogHint": "ווייַזן אַ טעקע-בוים פּיקער ווען קריייטינג אַ צווייַג (אַנשטאָט פון אַ פּשוט פּינטלעך).",
    "settings.branchDialog": "צווייַג דיאַלאָג",
    "settings.branchDialogSvnTitle": "SVN-נוסח",
    "settings.branchDialogNativeVscode": "VS קאָד נוסח",
    "settings.branchDialogNativeVs": "וויסואַל סטודיאָ סטיל",
    "settings.branchDialogNativeHint": "דיין IDE ס געבויט-אין צווייַג פּינטלעך.",
    "settings.close": "נאָענט",
    "settings.done": "געטאן",
    "settings.sectionAdvanced": "אַוואַנסירטע",
    "settings.gitSource": "גיט עקסעקוטאַבלע",
    "settings.gitSourceBuiltin": "געבויט-אין",
    "settings.gitSourceBuiltinHint": "ניצן די גיט ביינערי פֿון די IDE ס געבויט-אין גיט פאַרלענגערונג.",
    "settings.gitSourceCustom": "מנהג דרך",
    "settings.gitPath": "דרך צו גיין",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "בלעטער…",
    "settings.jargon": "גיט טערמינען",
    "settings.jargonTranslate": "איבערזעצן",
    "settings.jargonEnglish": "האַלטן אין ענגליש",
    "settings.jargonTranslateHint": "איבערזעצן Git טערמינען (ציען, שטופּן, יבערגעבן, צווייַג ...) אין די צובינד שפּראַך.",
    "settings.jargonEnglishHint": "האַלטן Git טערמינען (ציען, שטופּן, יבערגעבן, צווייַג ...) אין ענגליש; איבערזעצן אַלץ אַנדערש.",
    "settings.sectionMerge": "צונויפגיסן",
    "settings.mergeMode": "צונויפגיסן נוסח",
    "settings.mergeModeMerge": "נאָרמאַל צונויפגיסן",
    "settings.mergeModeSquash": "קאַבאַק צונויפגיסן",
    "settings.mergeModeMergeHint": "צונויפגיסן די צווייַג ווי עס איז: זייַן קאַמישאַנז ווערן אַ טייל פון די קראַנט צווייַג ס געשיכטע, דזשוינד דורך אַ צונויפגיסן קאַמיט (אָדער שנעל פאָרווערדיד ווען מעגלעך).",
    "settings.mergeModeSquashHint": "קאַלאַפּס די גאנצע צווייַג אין אַ איין יבערגעבן אויף די קראַנט צווייַג (גיט מערגע - קאַבאַק). די אייגענע קאָממיטס פון די צווייַג קומען קיינמאָל נישט אריין אין די געשיכטע פון ​​​​דער איצטיקער צווייַג - איר זען בלויז איין קאָמיטעט דאָרט. די צווייַג זיך איז לינקס אַנטאַטשט, אַזוי זיין קאַמיץ בלייבן קענטיק אין זיין אייגענע שטעג.",
    "settings.mergedView": "מערדזשד קאַמיץ אין די גראַפיק",
    "settings.mergedViewBranch": "בלויז אויף זייער אייגן צווייַג",
    "settings.mergedViewTarget": "אוי ך אי ן דע ר צװײגע ר הא ט מע ן ז ײ אײנגעשלאםן",
    "settings.mergedViewBranchHint": "אַ צונויפגיסן צווייַג ס קאַמיץ זענען געצויגן בלויז אין זייער אייגן שטעג; די צונויפגיסן איז געוויזן ווי אַ קאַנעקטער. דער קלאַסיש מיינונג.",
    "settings.mergedViewTargetHint": "די קאַמיץ אַ צונויפגיסן געבראכט אין זענען ציען אין די ריסיווינג צווייַג ס ליין אויך, סטאַקט אונטער די צונויפגיסן קאַמיט און גלייַך מיט די אָריגינאַלס - ווייַל די צווייַג טאַקע כּולל זיי איצט. די קאפיעס זענען בלאַס און כעדאַד מיט די צווייַג זיי זענען געשריבן אויף, אַזוי עס בלייבט קלאָר זיי זענען נישט געמאכט דאָרט. קאַבאַק צונויפגיסן זענען אַנאַפעקטיד: אַ קאַבאַק שרייבט איין פּראָסט קאַמיט, אַזוי בלויז אַז קאַמיט איז שטענדיק ארויס.",
    "legend.title": "לעגענדע",
    "legend.head": "קאָפּ / קראַנט צווייַג",
    "legend.local": "לאקאלע צווייַג",
    "legend.remote": "ווייַט צווייַג",
    "legend.remoteOnly": "בלויז אין די וואָלקן (ניט פּולד)",
    "legend.tag": "טאַג (ווערסיע)",
    "legend.commit": "קאַמיט",
    "legend.stash": "סטאַש (שעווד אַרבעט)",
    "legend.mergedIn": "געקומען אין מיט אַ צונויפגיסן (געשריבן אויף אן אנדער צווייַג)",
    "legend.nodes": "באָקסעס",
    "legend.lines": "שורות",
    "legend.edgeParent": "פֿריִערדיקע יבערגעבן אויף דער זעלביקער שורה",
    "legend.edgeMerge": "מערדזשד-אין צווייַג - די פייַל ווייזט אויף די צונויפגיסן יבערגעבן",
    "legend.edgeBranch": "א צווייג האט זיך דא אפגעשטעלט",
    "legend.edgeStash": "סטאַש און די יבערגעבן עס איז געווען שעלווד אויף",
    "legend.edgeMergedChain": "דעם צווייַג ס שורה פליסנדיק דורך די מערדזשד-אין קאַמיץ",
    "legend.edgeMergedTie": "דער זעלביקער יבערגעבן אויף די צווייַג עס איז געווען געשריבן אויף",
    "menu.jumpHead": "⌖ גיין צו ויסשרייַבן",
    "menu.resetView": "⤢ באַשטעטיק מיינונג",
    "menu.createBranch": "שאַפֿן צווייַג פֿון דאָ ...",
    "menu.checkout": "טשעק אויס דעם אָנזאָג",
    "menu.copySha": "קאָפּי יבערגעבן SHA",
    "menu.pushBranch": "שטופּן צווייַג \"{name}\"",
    "menu.renameCommit": "רענאַמע קאָממיט אָנזאָג ...",
    "menu.undoCommit": "ופמאַכן יבערגעבן (האַלטן ענדערונגען) ...",
    "menu.viewChanges": "זען ענדערונגען ...",
    "menu.mergeBranch": "צונויפגיסן \"{source}\" אין \"{target}\" ...",
    "menu.jumpToOriginal": "גיין צו דער אָריגינעל אָנזאָג",
    "node.mergedInTooltip": "צונויפגיסן {merge} געבראכט דעם יבערגעבן אין \"{target}\" - עס איז געווען געשריבן אויף \"{origin}\".",
    "changes.title": "ענדערונגען אין {sha}",
    "changes.tabChanged": "געביטן",
    "changes.tabAll": "אַלע טעקעס",
    "changes.added": "צוגעגעבן",
    "changes.modified": "מאַדאַפייד",
    "changes.deleted": "אויסגעמעקט",
    "changes.renamed": "ריניימד",
    "changes.noChanges": "דעם יבערגעבן ענדערונגען קיין טעקעס.",
    "changes.loading": "לאָודינג חילוק…",
    "changes.selectFile": "אויסקלייַבן אַ טעקע צו זען די ענדערונגען.",
    "changes.original": "אָריגינעל",
    "changes.changed": "דעם יבערגעבן",
    "changes.binary": "ביינערי טעקע - קיין טעקסט חילוק צו ווייַזן.",
    "changes.tooLarge": "טעקע איז צו גרויס צו אַנדערש.",
    "changes.renamedFrom": "ריניימד פֿון {path}",
    "changes.close": "נאָענט",
    "changes.prevChange": "פֿריִערדיקע טוישן",
    "changes.nextChange": "ווייַטער טוישן",
    "changes.collapseAll": "ייַנבראָך אַלע פאָלדערס",
    "changes.expandAll": "יקספּאַנד אַלע פאָלדערס",
    "changes.searchPlaceholder": "זוך טעקעס...",
    "changes.noSearchResults": "קיין טעקעס גלייַכן דיין זוכן.",
    "changes.maximize": "מאַקסאַמייז",
    "changes.restore": "ומקערן גרייס",
    "find.open": "זוכן אין דעם חילוק (Ctrl+F)",
    "find.placeholder": "געפֿינען אין פאַרשידענע…",
    "find.prev": "פֿריִערדיקע גלייַכן",
    "find.next": "ווייַטער גלייַכן",
    "find.close": "נאָענט זוכן",
    "find.noResults": "קיין רעזולטאַטן",
    "commit.title": "יבערגעבן ענדערונגען",
    "commit.close": "נאָענט",
    "commit.loading": "לייענען ארבעטן בוים ...",
    "commit.noChanges": "עס זענען קיין היגע ענדערונגען צו יבערגעבן.",
    "commit.selectFile": "סעלעקטירן אַ טעקע צו זען די חילוק.",
    "commit.selectAll": "אויסקלייַבן אַלע",
    "commit.selectNone": "קלאָר אַלע",
    "commit.message": "יבערגעבן אָנזאָג",
    "commit.messagePlaceholder": "באַשרייַבן די ענדערונג",
    "commit.selectedCount": "{count} טעקע (s) אויסגעקליבן",
    "commit.review": "איבערבליק",
    "commit.reviewTitle": "לעצט טשעק איידער טוען",
    "commit.reviewSummary": "יבערגעבן {count} טעקע (s) מיט אָנזאָג: {message}",
    "commit.back": "צוריק",
    "commit.commit": "שאַפֿן היגע יבערגעבן",
    "menu.stashApply": "צולייגן סטאַש",
    "menu.stashPop": "פּאָפּ סטאַש (צולייגן און אַראָפּנעמען)",
    "menu.stashDrop": "נעם אַוועק סטאַש ...",
    "merge.title": "צונויפגיסן בראַנטש",
    "merge.route": "צונויפגיסן אין דעם קראַנט צווייַג",
    "merge.source": "פון (מקור)",
    "merge.target": "אין (קראַנט)",
    "merge.loading": "אַנאַליזינג צונויפגיסן…",
    "merge.upToDate": "שוין אַרויף צו טאָג - גאָרנישט צו צונויפגיסן.",
    "merge.fastForward": "שנעל-פאָרויס - קיין צונויפגיסן קאַמיט דארף (טיקען \"שאַפֿן אַ צונויפגיסן קאַמיט\" צו צווינגען איינער).",
    "merge.summary": "{files} טעקע (s) געביטן, {conflicts} קאָנפליקט (s).",
    "merge.conflictsWarning": "דער צונויפגיסן האט קאָנפליקט - איר קענען נאָך צונויפגיסן און סאָלווע זיי אין דער רעדאַקטאָר.",
    "merge.noChanges": "קיין טעקע ענדערונגען.",
    "merge.files": "ריזאַלטינג ענדערונגען",
    "merge.added": "צוגעגעבן",
    "merge.modified": "מאַדאַפייד",
    "merge.deleted": "אויסגעמעקט",
    "merge.conflict": "קאָנפליקט",
    "merge.message": "צונויפגיסן אָנזאָג אָנזאָג",
    "merge.messagePlaceholder": "צונויפגיסן צווייַג 'מקור'",
    "merge.messageFfHint": "איגנאָרירט אויף אַ שנעל-פאָרויס צונויפגיסן (קיין יבערגעבן איז באשאפן).",
    "merge.noFastForward": "שאַפֿן אַ צונויפגיסן קאַמיט (קיין שנעל פאָרויס)",
    "merge.noFastForwardHint": "שטענדיק רעקאָרדירן אַ צונויפגיסן יבערגעבן, אפילו ווען די צווייַג קען שנעל-פאָרויס.",
    "merge.merge": "צונויפגיסן",
    "merge.cancel": "באָטל מאַכן",
    "merge.previewError": "קען נישט רעכענען אַ פאָרויסיקע ווייַזונג: {message}",
    "merge.squashBadge": "קאַבאַק",
    "merge.squashNote": "קאַבאַק צונויפגיסן - יעדער ענדערונג לאַנדיד ווי איין יבערגעבן אויף \"{target}\"; \"{source}\" ס אייגענע קאַמיץ בלייבן אויס פון זיין געשיכטע. באַשטימען צוריק אין סעטטינגס › צונויפגיסן.",
    "merge.messageSquash": "יבערגעבן אָנזאָג",
    "merge.squashMessagePlaceholder": "קוואשט ענדערונגען פון 'מקור'",
    "newBranch.title": "שאַפֿן בראַנטש",
    "newBranch.startPoint": "ניו צווייַג סטאַרטינג פון {sha}",
    "newBranch.startPointOn": "אויף {refs}",
    "newBranch.location": "אָרט",
    "newBranch.locationRoot": "(שורש)",
    "newBranch.expandAll": "יקספּאַנד אַלע",
    "newBranch.collapseAll": "ייַנבראָך אַלע",
    "newBranch.expandFolder": "עקספּאַנד טעקע",
    "newBranch.collapseFolder": "ייַנבראָך טעקע",
    "newBranch.name": "צווייַג נאָמען",
    "newBranch.namePlaceholder": "מיין-צווייג",
    "newBranch.fullName": "גאַנץ נאָמען: {name}",
    "newBranch.invalid": "ניט גילטיק צווייַג נאָמען",
    "newBranch.exists": "א צווייג מיט דעם נאמען עקזיסטירט שוין",
    "newBranch.checkout": "ויסשרייַבן צווייַג נאָך שאַפונג",
    "newBranch.create": "שאַפֿן",
    "newBranch.cancel": "באָטל מאַכן",
    "status.loading": "לאָודינג גראַפיק…",
    "status.summary": "{repo}Showing {commits} קאַמיץ, {refs} רעפס",
    "status.branchCreated": "באשאפן צווייַג \"{name}\" ביי {sha}",
    "status.error": "טעות: {message}",
    "status.opFailed": "אָפּעראַציע ניט אַנדערש",
    "status.fetching": "באקומט...",
    "status.pulling": "ציען…",
    "status.pushing": "שטופּן...",
    "status.committing": "שאפן לאקאלע איבערזעצן...",
    "status.commitCreated": "באשאפן היגע יבערגעבן {sha}: {message}",
    "status.syncing": "סינגקינג…",
    "status.noHead": "קיין קראַנט ויסשרייַבן געפֿונען אין די גראַפיק.",
    "status.undoing": "אַנטלאָפן אָפּמאַך…",
    "status.commitUndone": "יבערגעבן ונדאָנע - ענדערונגען זענען צוריק אין די אַרבעט בוים.",
    "status.undoConflict": "ופמאַכן שלאָגן קאָנפליקט - סאָלווע זיי אין דער רעדאַקטאָר, און פאָרזעצן.",
    "status.stashApplying": "אַפּלייינג סטאַש…",
    "status.stashPopping": "שפּאַנונג סטאַש ...",
    "status.stashDropping": "אָפּלייגן סטאַטש ...",
    "status.stashApplied": "סטאַש געווענדט.",
    "status.stashPopped": "סטאַש האָט זיך אָנגעהויבן.",
    "status.stashDropped": "סטאַש דראַפּט.",
    "status.stashConflict": "סטאַש קאָנפליקט - סאָלווע זיי אין דער רעדאַקטאָר.",
    "status.merging": "צונויפגיסן...",
    "status.merged": "צונויפגיסן געענדיקט.",
    "status.mergeConflict": "צונויפגיסן האט קאָנפליקט - סאָלווע זיי אין דער רעדאַקטאָר, דעמאָלט יבערגעבן.",
    "search.title": "זוכן",
    "search.tooltip": "זוכן קאַמיץ",
    "search.placeholder": "זוכן קאַמיץ ...",
    "search.previous": "פֿריִערדיקע רעזולטאַט",
    "search.next": "ווייַטער רעזולטאַט",
    "search.noResults": "קיין רעזולטאַטן",
    "search.resultCount": "XPH0X/{count}",
    "search.filter": "פילטער מאָדע",
    "search.highlight": "הויכפּונקט מאָדע",
    "search.filterMode": "זוכן אַרויסווייַזן",
    "search.filterModeFilter": "פילטער (באַהאַלטן ניט-שוועבעלעך)",
    "search.filterModeHighlight": "הויכפּונקט (ווייַזן אַלע, צייכן שוועבעלעך)",
    "search.filterModeHint": "קלייַבן ווי זוכן רעזולטאַטן זענען געוויזן אין די גראַפיק.",
    "search.visibility": "זוכן אָרט",
    "search.visibilityToolbar": "Toolbar קנעפּל",
    "search.visibilityAlwaysVisible": "שטענדיק קענטיק באַר",
    "search.visibilityHint": "ווייַזן זוכן ווי אַ מכשיר ייקאַן אָדער פּערסיסטענט באַר אין דער שפּיץ.",
    "footer.github": "גיטהוב",
  },
  he: {
    "toolbar.refresh": "⟳ רענן",
    "toolbar.fetch": "⤓ אחזר",
    "toolbar.pull": "⇩ משוך",
    "toolbar.push": "⇧ דחיפה",
    "toolbar.commit": "✓ התחייבות",
    "toolbar.sync": "⇅ סנכרון",
    "toolbar.jumpHead": "⌖ עבור לקופה",
    "toolbar.reset": "⤢ איפוס תצוגה",
    "toolbar.settings": "⚙ הגדרות",
    "details.header": "פרטי התחייבות",
    "details.close": "לִסְגוֹר",
    "details.sha": "SHA",
    "details.shortSha": "SHA קצר",
    "details.message": "הוֹדָעָה",
    "details.author": "מְחַבֵּר",
    "details.date": "תַאֲרִיך",
    "details.labels": "תוויות",
    "details.location": "מִקוּם",
    "details.currentHead": "HEAD - התשלום הנוכחי",
    "settings.title": "הגדרות",
    "settings.language": "שָׂפָה",
    "settings.theme": "נוֹשֵׂא",
    "settings.themeLight": "אוֹר",
    "settings.themeDark": "כֵּהֶה",
    "settings.themeLightHint": "רקע בהיר, טקסט כהה.",
    "settings.themeDarkHint": "רקע כהה, טקסט בהיר.",
    "settings.mainBranch": "סניף ראשי",
    "settings.mainBranchAuto": "אוֹטוֹמָטִי",
    "settings.mainBranchSearch": "חפש בסניפים...",
    "settings.mainBranchNoMatch": "אין סניפים תואמים",
    "settings.display": "סגנון תצוגה",
    "settings.displayModern": "מוֹדֶרנִי",
    "settings.displayClassic": "קלַאסִי",
    "settings.displayModernHint": "קנבס חופשי - גרור כדי להזיז, גלול כדי להתקרב.",
    "settings.displayClassicHint": "קנבס קבוע - תא המטען מוצמד שמאלה, ללא זום, גלילה בלבד (כמו גרף הגרסה של SVN).",
    "settings.sectionGeneral": "כְּלָלִי",
    "settings.sectionGraph": "גרָף",
    "settings.sectionChanges": "משנה תצוגה",
    "settings.diffMinimap": "מינימפה שונה",
    "settings.diffMinimapOn": "מוצג",
    "settings.diffMinimapOff": "מוּסתָר",
    "settings.diffMinimapOnHint": "הצג רצועת סקירה בסגנון VS Code לצד ההבדל - גרור אותו כדי לגלול; סמני שינוי כלולים.",
    "settings.diffMinimapOffHint": "אין רצועת סקירה כללית - גלול את ההבדל כרגיל.",
    "settings.commitReview": "בצע ביקורת",
    "settings.commitReviewOn": "שאל לפני התחייבות",
    "settings.commitReviewOff": "התחייב ישירות",
    "settings.commitReviewOnHint": "הצג רשימת קבצים סופית ובדיקת הודעות לפני יצירת ה-commit המקומי.",
    "settings.commitReviewOffHint": "הלחצן Commit יוצר את המחויבות המקומית באופן מיידי.",
    "settings.svnBranchDialog": "דו-שיח סניף בסגנון SVN",
    "settings.svnBranchDialogHint": "הצג בורר עץ תיקיות בעת יצירת ענף (במקום הנחיה פשוטה).",
    "settings.branchDialog": "דו-שיח סניף",
    "settings.branchDialogSvnTitle": "בסגנון SVN",
    "settings.branchDialogNativeVscode": "סגנון VS Code",
    "settings.branchDialogNativeVs": "סגנון Visual Studio",
    "settings.branchDialogNativeHint": "בקשת הסניף המובנית של ה-IDE שלך.",
    "settings.close": "לִסְגוֹר",
    "settings.done": "נַעֲשָׂה",
    "settings.sectionAdvanced": "מִתקַדֵם",
    "settings.gitSource": "קובץ הפעלה של Git",
    "settings.gitSourceBuiltin": "מובנה",
    "settings.gitSourceBuiltinHint": "השתמש ב-git בינארי מהסיומת Git המובנית של ה-IDE.",
    "settings.gitSourceCustom": "נתיב מותאם אישית",
    "settings.gitPath": "נתיב לגיט",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "לְדַפדֵף…",
    "settings.jargon": "תנאי Git",
    "settings.jargonTranslate": "לְתַרְגֵם",
    "settings.jargonEnglish": "שמור באנגלית",
    "settings.jargonTranslateHint": "תרגם את מונחי Git (משיכה, דחיפה, התחייבות, הסתעפות...) לשפת הממשק.",
    "settings.jargonEnglishHint": "שמור את מונחי Git (משיכה, דחיפה, התחייבות, ענף...) באנגלית; לתרגם את כל השאר.",
    "settings.sectionMerge": "לְמַזֵג",
    "settings.mergeMode": "מיזוג סגנון",
    "settings.mergeModeMerge": "מיזוג רגיל",
    "settings.mergeModeSquash": "מיזוג סקווש",
    "settings.mergeModeMergeHint": "ממזג את הסניף כפי שהוא: ההתחייבויות שלו הופכות לחלק מההיסטוריה של הסניף הנוכחי, ומצטרפות אליהן התחייבות למיזוג (או העברה קדימה כשאפשר).",
    "settings.mergeModeSquashHint": "מכווץ את כל הענף ל-commit יחיד בענף הנוכחי (git merge --squash). ההתחייבויות של הסניף עצמו לעולם לא נכנסות להיסטוריה של הסניף הנוכחי - אתה רואה רק התחייבות אחת שם. הסניף עצמו נותר ללא נגיעה, כך שההתחייבויות שלו נשארות גלויות בנתיב שלו.",
    "settings.mergedView": "התחייבויות ממוזגות בגרף",
    "settings.mergedViewBranch": "רק בסניף משלהם",
    "settings.mergedViewTarget": "גם בסניף הם אוחדו",
    "settings.mergedViewBranchHint": "ההתחייבויות של סניף ממוזג מצוירות רק בנתיב שלהן; המיזוג מופיע כמחבר. הנוף הקלאסי.",
    "settings.mergedViewTargetHint": "המחויבים שמיזוג שהובאו מצוירים גם בנתיב הסניף המקבל, מוערמים מתחת למחויבות המיזוג וברמה עם המקורות - כי הסניף הזה באמת מכיל אותם עכשיו. העותקים חיוורים ובראשם הענף שעליו נכתבו, כך שנשאר ברור שהם לא נעשו שם. מיזוג סקווש אינו מושפע: סקווש כותב commit רגיל אחד, כך שרק commit זה מופיע אי פעם.",
    "legend.title": "אַגָדָה",
    "legend.head": "HEAD / סניף נוכחי",
    "legend.local": "סניף מקומי",
    "legend.remote": "סניף מרוחק",
    "legend.remoteOnly": "רק בענן (לא נמשך)",
    "legend.tag": "תג (גרסה)",
    "legend.commit": "לְבַצֵעַ",
    "legend.stash": "סטאש (עבודה על מדפים)",
    "legend.mergedIn": "הגיע עם מיזוג (נכתב על סניף אחר)",
    "legend.nodes": "קופסאות",
    "legend.lines": "קווים",
    "legend.edgeParent": "התחייבות קודמת באותו קו",
    "legend.edgeMerge": "סניף ממוזג - החץ מצביע על ביצוע המיזוג",
    "legend.edgeBranch": "סניף התפצל כאן",
    "legend.edgeStash": "סטאש וההתחייבות שעליה נגנז",
    "legend.edgeMergedChain": "הקו של סניף זה עובר דרך המחויבויות הממוזגות",
    "legend.edgeMergedTie": "אותה התחייבות על הסניף עליו נכתב",
    "menu.jumpHead": "⌖ עבור לקופה",
    "menu.resetView": "⤢ איפוס תצוגה",
    "menu.createBranch": "צור סניף מכאן...",
    "menu.checkout": "בדוק את ההתחייבות הזו",
    "menu.copySha": "העתק commit SHA",
    "menu.pushBranch": "דחוף ענף \"{name}\"",
    "menu.renameCommit": "שנה את שם הודעת התחייבות...",
    "menu.undoCommit": "בטל התחייבות (שמור שינויים)...",
    "menu.viewChanges": "הצג שינויים...",
    "menu.jumpToOriginal": "עבור ל-commit המקורי",
    "node.mergedInTooltip": "מיזוג {merge} הביא את ההתחייבות הזו ל-\"{target}\" - היא נכתבה ב-\"{origin}\".",
    "changes.title": "שינויים ב-{sha}",
    "changes.tabChanged": "השתנה",
    "changes.tabAll": "כל הקבצים",
    "changes.added": "נוסף",
    "changes.modified": "שונה",
    "changes.deleted": "נמחק",
    "changes.renamed": "שונה שם",
    "changes.noChanges": "ההתחייבות הזו לא משנה קבצים.",
    "changes.loading": "טוען הבדל...",
    "changes.selectFile": "בחר קובץ כדי לראות את השינויים שלו.",
    "changes.original": "מְקוֹרִי",
    "changes.changed": "ההתחייבות הזו",
    "changes.binary": "קובץ בינארי - אין הבדל טקסט להצגה.",
    "changes.tooLarge": "הקובץ גדול מכדי להבדיל.",
    "changes.renamedFrom": "השם שונה מ-{path}",
    "changes.close": "לִסְגוֹר",
    "changes.prevChange": "שינוי קודם",
    "changes.nextChange": "השינוי הבא",
    "changes.collapseAll": "כווץ את כל התיקיות",
    "changes.expandAll": "הרחב את כל התיקיות",
    "changes.searchPlaceholder": "חפש קבצים...",
    "changes.noSearchResults": "אין קבצים שתואמים לחיפוש שלך.",
    "changes.maximize": "לְהַגדִיל",
    "changes.restore": "שחזר גודל",
    "find.open": "חפש בהבדל הזה (Ctrl+F)",
    "find.placeholder": "מצא בהבדל…",
    "find.prev": "משחק קודם",
    "find.next": "המשחק הבא",
    "find.close": "סגור חיפוש",
    "find.noResults": "אין תוצאות",
    "commit.title": "בצע שינויים",
    "commit.close": "לִסְגוֹר",
    "commit.loading": "קורא עץ עובד...",
    "commit.noChanges": "אין שינויים מקומיים להתחייב.",
    "commit.selectFile": "בחר קובץ כדי לראות את ההבדל שלו.",
    "commit.selectAll": "בחר הכל",
    "commit.selectNone": "נקה הכל",
    "commit.message": "הודעת התחייבות",
    "commit.messagePlaceholder": "תאר את השינוי",
    "commit.selectedCount": "נבחרו קבצי {count}",
    "commit.review": "סְקִירָה",
    "commit.reviewTitle": "בדיקה סופית לפני התחייבות",
    "commit.reviewSummary": "בצע קבצי {count} עם ההודעה: {message}",
    "commit.back": "בְּחֲזָרָה",
    "commit.commit": "צור מחויבות מקומית",
    "menu.stashApply": "החל מחסן",
    "menu.stashPop": "מחסנית פופ (להחיל והסר)",
    "menu.stashDrop": "זרוק מחסן...",
    "merge.title": "מיזוג סניף",
    "merge.route": "מיזוג לתוך הסניף הנוכחי",
    "merge.source": "מאת (מקור)",
    "merge.target": "לתוך (נוכחי)",
    "merge.loading": "מנתח מיזוג...",
    "merge.upToDate": "כבר מעודכן - אין מה למזג.",
    "merge.fastForward": "הרצה קדימה - אין צורך בהתחייבות למיזוג (סמן \"צור התחייבות למיזוג\" כדי לאלץ אחת).",
    "merge.summary": "קבצי {files} השתנו, התנגשות {conflicts}.",
    "merge.conflictsWarning": "למיזוג הזה יש התנגשויות - אתה עדיין יכול למזג, ואז לפתור אותם בעורך.",
    "merge.noChanges": "אין שינויים בקובץ.",
    "merge.files": "שינויים כתוצאה מכך",
    "merge.added": "נוסף",
    "merge.modified": "שונה",
    "merge.deleted": "נמחק",
    "merge.conflict": "סְתִירָה",
    "merge.message": "מיזוג הודעת התחייבות",
    "merge.messagePlaceholder": "מיזוג סניף 'מקור'",
    "merge.messageFfHint": "התעלמו בעת מיזוג מהיר קדימה (לא נוצרה מחויבות).",
    "merge.noFastForward": "צור התחייבות למיזוג (ללא הרצה קדימה)",
    "merge.noFastForwardHint": "רשום תמיד התחייבות למיזוג, גם כשהסניף יכול להריץ קדימה.",
    "merge.merge": "לְמַזֵג",
    "merge.cancel": "לְבַטֵל",
    "merge.previewError": "לא ניתן היה לחשב תצוגה מקדימה: {message}",
    "merge.squashBadge": "סקווֹשׁ",
    "merge.squashNote": "מיזוג סקווש - כל שינוי נוחת כ-ONE commit ב-\"{target}\"; ההתחייבויות של \"{source}\" עצמו נשארות מחוץ להיסטוריה שלה. עבור חזרה בהגדרות › מיזוג.",
    "merge.messageSquash": "הודעת התחייבות",
    "merge.squashMessagePlaceholder": "שינויים מעוכים מ'מקור'",
    "newBranch.title": "צור סניף",
    "newBranch.startPoint": "סניף חדש החל מ-{sha}",
    "newBranch.startPointOn": "ב-{refs}",
    "newBranch.location": "מִקוּם",
    "newBranch.locationRoot": "(שׁוֹרֶשׁ)",
    "newBranch.expandAll": "הרחב הכל",
    "newBranch.collapseAll": "כווץ הכל",
    "newBranch.expandFolder": "הרחב את התיקיה",
    "newBranch.collapseFolder": "כיווץ התיקיה",
    "newBranch.name": "שם הסניף",
    "newBranch.namePlaceholder": "שלי-ענף",
    "newBranch.fullName": "שם מלא: {name}",
    "newBranch.invalid": "שם סניף לא חוקי",
    "newBranch.exists": "סניף בשם זה כבר קיים",
    "newBranch.checkout": "סניף קופה לאחר היצירה",
    "newBranch.create": "לִיצוֹר",
    "newBranch.cancel": "לְבַטֵל",
    "status.loading": "טוען גרף...",
    "status.summary": "{repo} מציג {commits} commits, {refs} refs",
    "status.branchCreated": "נוצר סניף \"{name}\" ב-{sha}",
    "status.error": "שגיאה: {message}",
    "status.opFailed": "הפעולה נכשלה",
    "status.fetching": "מַקסִים…",
    "status.pulling": "מושך…",
    "status.pushing": "דוחף...",
    "status.committing": "יוצר מחויבות מקומית...",
    "status.commitCreated": "נוצר commit מקומי {sha}: {message}",
    "status.syncing": "מסנכרן...",
    "status.noHead": "לא נמצאה תשלום נוכחי בגרף.",
    "status.undoing": "מבטל את ההתחייבות...",
    "status.commitUndone": "Commit בוטלה - השינויים חזרו לעץ העבודה.",
    "status.undoConflict": "בטל התנגשויות היט - פתור אותן בעורך, ולאחר מכן המשך.",
    "status.stashApplying": "מחיל מחסנים...",
    "status.stashPopping": "מחסן קופץ...",
    "status.stashDropping": "מסיר מחסן...",
    "status.stashApplied": "סטאש הוחל.",
    "status.stashPopped": "סטאש קפץ.",
    "status.stashDropped": "סטאש נפל.",
    "status.stashConflict": "התנגשויות מחסניות - פתור אותן בעורך.",
    "status.merging": "מתמזג...",
    "status.merged": "המיזוג הושלם.",
    "status.mergeConflict": "למיזוג יש התנגשויות - פתור אותם בעורך, ואז בצע ביצוע.",
    "search.title": "לְחַפֵּשׂ",
    "search.tooltip": "חיפוש מתחייב",
    "search.placeholder": "חיפוש מתחייב...",
    "search.previous": "תוצאה קודמת",
    "search.next": "התוצאה הבאה",
    "search.noResults": "אין תוצאות",
    "search.resultCount": "{index}/{count}",
    "search.filter": "מצב סינון",
    "search.highlight": "מצב הדגשה",
    "search.filterMode": "תצוגת חיפוש",
    "search.filterModeFilter": "סינון (הסתר אי-התאמות)",
    "search.filterModeHighlight": "הדגש (הצג הכל, סמן התאמות)",
    "search.filterModeHint": "בחר כיצד תוצאות החיפוש יוצגו בתרשים.",
    "search.visibility": "חיפוש מיקום",
    "search.visibilityToolbar": "כפתור סרגל הכלים",
    "search.visibilityAlwaysVisible": "בר גלוי תמיד",
    "search.visibilityHint": "הצג את החיפוש כסמל סרגל כלים או כסרגל קבוע בחלק העליון.",
    "footer.github": "GitHub",
  },
  ug: {
    "toolbar.refresh": "Resh يېڭىلاش",
    "toolbar.fetch": "Et Fetch",
    "toolbar.pull": "Ull Pull",
    "toolbar.push": "⇧ Push",
    "toolbar.commit": "✓ Commit",
    "toolbar.sync": "C Sync",
    "toolbar.jumpHead": "Check چىقىمدانغا بېرىڭ",
    "toolbar.reset": "View Reset view",
    "toolbar.settings": "⚙ تەڭشەك",
    "details.header": "تەپسىلاتى",
    "details.close": "تاقاش",
    "details.sha": "SHA",
    "details.shortSha": "قىسقا SHA",
    "details.message": "ئۇچۇر",
    "details.author": "ئاپتور",
    "details.date": "چېسلا",
    "details.labels": "Labels",
    "details.location": "ئورنى",
    "details.currentHead": "HEAD - نۆۋەتتىكى تەكشۈرۈش",
    "settings.title": "تەڭشەك",
    "settings.language": "تىل",
    "settings.theme": "باشتېما",
    "settings.themeLight": "نۇر",
    "settings.themeDark": "قاراڭغۇ",
    "settings.themeLightHint": "سۇس تەگلىك ، قېنىق تېكىست.",
    "settings.themeDarkHint": "قاراڭغۇ تەگلىك ، يېنىك تېكىست.",
    "settings.mainBranch": "ئاساسلىق تارماق",
    "settings.mainBranchAuto": "ئاپتوماتىك",
    "settings.mainBranchSearch": "ئىزدەش تارماقلىرى…",
    "settings.mainBranchNoMatch": "ماس كېلىدىغان شاخ يوق",
    "settings.display": "كۆرسىتىش ئۇسلۇبى",
    "settings.displayModern": "زامانىۋى",
    "settings.displayClassic": "كلاسسىك",
    "settings.displayModernHint": "ھەقسىز كانۋا - قازانغا سۆرەپ ، چوڭايتىش ئۈچۈن سىيرىڭ.",
    "settings.displayClassicHint": "مۇقىم كاناي - غول ساندۇق سولغا باغلانغان ، چوڭايتىش يوق ، پەقەت سىيرىلىدۇ (SVN تۈزىتىش گرافىكىسىغا ئوخشاش).",
    "settings.sectionGeneral": "General",
    "settings.sectionGraph": "Graph",
    "settings.sectionChanges": "كۆرۈنۈشنى ئۆزگەرتىدۇ",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "كۆرسىتىلدى",
    "settings.diffMinimapOff": "يۇشۇرۇن",
    "settings.diffMinimapOnHint": "پەرقنىڭ يېنىدا VS كود ئۇسلۇبىدىكى ئومۇمىي كۆرۈنۈش بەلگىسىنى كۆرسەت - ئۇنى سۆرەپ يۆتكەڭ. ئۆزگەرتىش بەلگىسىنى ئۆز ئىچىگە ئالىدۇ.",
    "settings.diffMinimapOffHint": "ئومۇمىي كۆرۈنۈش يوق - پەرقنى نورمال ئۆرۈڭ.",
    "settings.commitReview": "قايتا تەكشۈرۈش",
    "settings.commitReviewOn": "ۋەدە بېرىشتىن بۇرۇن سوراڭ",
    "settings.commitReviewOff": "بىۋاسىتە ۋەدە قىلىڭ",
    "settings.commitReviewOnHint": "يەرلىك ۋەدىلەرنى قۇرۇشتىن بۇرۇن ئاخىرقى ھۆججەت تىزىملىكى ۋە ئۇچۇر تەكشۈرۈشنى كۆرسىتىڭ.",
    "settings.commitReviewOffHint": "ۋەدىلىشىش كۇنۇپكىسى دەرھال يەرلىك ۋەدىلەرنى ھاسىل قىلىدۇ.",
    "settings.svnBranchDialog": "SVN ئۇسلۇبىدىكى تارماق سۆزلىشىش",
    "settings.svnBranchDialogHint": "شاخ قۇرغاندا ھۆججەت قىسقۇچ تاللىغۇچنى كۆرسىتىڭ (ئاددىي ئەسكەرتىشنىڭ ئورنىغا).",
    "settings.branchDialog": "تارماق دىئالوگ",
    "settings.branchDialogSvnTitle": "SVN ئۇسلۇبى",
    "settings.branchDialogNativeVscode": "VS كود ئۇسلۇبى",
    "settings.branchDialogNativeVs": "Visual Studio ئۇسلۇبى",
    "settings.branchDialogNativeHint": "IDE نىڭ ئىچىگە تارماق شاخچە ئەسكەرتىش.",
    "settings.close": "تاقاش",
    "settings.done": "تامام",
    "settings.sectionAdvanced": "Advanced",
    "settings.gitSource": "ئىجرا قىلىشقا بولىدۇ",
    "settings.gitSourceBuiltin": "ئىچىگە قاچىلانغان",
    "settings.gitSourceBuiltinHint": "IDE نىڭ ئىچىگە Git كېڭەيتىلمىسىدىكى git ئىككىلىك سىستېمىنى ئىشلىتىڭ.",
    "settings.gitSourceCustom": "ئىختىيارى يول",
    "settings.gitSourceCustomHint": "ئۆزىڭىزنىڭ git ئىجرا بولىدىغان يولىنى بەلگىلەڭ (مەسىلەن: usr / bin / git ياكى C:\\…\\git.exe).",
    "settings.gitPath": "گىتتار يولى",
    "settings.gitPathPlaceholder": "/ usr / bin / git",
    "settings.gitPathBrowse": "Browse…",
    "settings.jargon": "Git terms",
    "settings.jargonTranslate": "تەرجىمە قىلىڭ",
    "settings.jargonEnglish": "ئىنگىلىزچە ساقلاڭ",
    "settings.jargonTranslateHint": "گىت ئاتالغۇلىرىنى (تارتىش ، ئىتتىرىش ، ۋەدە بېرىش ، شاخ…) كۆرۈنمە يۈزى تىلىغا تەرجىمە قىلىڭ.",
    "settings.jargonEnglishHint": "Git ئاتالغۇلىرىنى (تارتىش ، ئىتتىرىش ، ۋەدە بېرىش ، شاخ…) ئىنگلىزچىدا ساقلاڭ. باشقا ھەممە نەرسىنى تەرجىمە قىلىڭ.",
    "settings.sectionMerge": "بىرلەشتۈرۈش",
    "settings.mergeMode": "ئۇسلۇبنى بىرلەشتۈرۈش",
    "settings.mergeModeMerge": "نورمال بىرلەشتۈرۈش",
    "settings.mergeModeSquash": "كاۋا بىرلەشتۈرۈش",
    "settings.mergeModeMergeHint": "شاخنى ھازىرقىدەك بىرلەشتۈرىدۇ: ئۇنىڭ ۋەدىسى ھازىرقى شاخ تارىخىنىڭ بىر قىسمىغا ئايلىنىدۇ ، قوشۇۋېلىش ۋەدىسىگە قوشۇلدى (ياكى مۇمكىن بولغاندا تېز ئىلگىرىلەيدۇ).",
    "settings.mergeModeSquashHint": "پۈتكۈل شاخنى ھازىرقى شاخ (git merge --squash) دا بىرلا ۋەدىگە ئايلاندۇرىدۇ. بۇ تارماقنىڭ ئۆزىنىڭ ۋەدىسى ھەرگىزمۇ ھازىرقى تارماقنىڭ تارىخىغا كىرمەيدۇ - سىز ئۇ يەردە پەقەت بىرلا ۋەدىسىنى كۆرىسىز. شاخنىڭ ئۆزى تۇتۇلمىغان ، شۇڭا ۋەدىسى ئۆز يولىدا كۆرۈنۈپ تۇرىدۇ.",
    "settings.mergedView": "بىرلەشتۈرۈلگەن گرافىك",
    "settings.mergedViewBranch": "پەقەت ئۇلارنىڭ تارمىقىدا",
    "settings.mergedViewTarget": "شاختا ئۇلار بىرلەشتۈرۈلدى",
    "settings.mergedViewBranchHint": "بىرلەشتۈرۈلگەن شاخنىڭ ۋەدىلىرى پەقەت ئۆز يولىدىلا سىزىلىدۇ. بىرلەشتۈرۈش ئۇلىغۇچ سۈپىتىدە كۆرسىتىلىدۇ. كلاسسىك قاراش.",
    "settings.mergedViewTargetHint": "بىرلەشتۈرۈلگەن ۋەدىلەر قوبۇل قىلىش شۆبىسىنىڭ يولىغىمۇ سىزىلغان بولۇپ ، بىرلەشتۈرۈش ۋەدىسى بىلەن ئەسلىدىكى سەۋىيىگە تىزىپ قويۇلغان - چۈنكى بۇ شاخ ھازىر ئۇلارنى ئۆز ئىچىگە ئالىدۇ. كۆپەيتىلگەن نۇسخىسى سۇس ھەم ئۇلار يېزىلغان شاخ بىلەن باشلىنىدۇ ، شۇڭا ئۇلارنىڭ ئۇ يەردە ياسالمىغانلىقى ئېنىق. كاۋاۋىچىننىڭ قوشۇلۇشى تەسىرگە ئۇچرىمايدۇ: كاۋا بىر ئادەتتىكى ۋەدىسىنى يازىدۇ ، شۇڭا پەقەت بۇ ۋەدىلەرلا كۆرۈلىدۇ.",
    "legend.title": "رىۋايەت",
    "legend.head": "HEAD / current branch",
    "legend.local": "يەرلىك تارماق",
    "legend.remote": "يىراق شاخ",
    "legend.remoteOnly": "پەقەت بۇلۇتتا (تارتىلمايدۇ)",
    "legend.tag": "خەتكۈچ (نەشرى)",
    "legend.commit": "ۋەدىگە ئەمەل قىلىڭ",
    "legend.stash": "Stash (ساقلانغان خىزمەت)",
    "legend.mergedIn": "قوشۇۋېلىش بىلەن كەلدى (باشقا شاخقا يېزىلغان)",
    "legend.nodes": "Boxes",
    "legend.lines": "قۇرلار",
    "legend.edgeParent": "ئالدىنقى ئوخشاش لىنىيىدە ۋەدە بېرىش",
    "legend.edgeMerge": "بىرلەشتۈرۈلگەن شاخ - بىرلەشتۈرۈش ۋەدىسىدىكى ئوق نۇقتىلىرى",
    "legend.edgeBranch": "بىر شاخ بۇ يەردىن ئايرىلدى",
    "legend.edgeStash": "Stash ۋە ئۇنىڭ ۋەدىسىگە ئەمەل قىلىندى",
    "legend.edgeMergedChain": "بۇ شاخنىڭ لىنىيىسى بىرلەشتۈرۈلگەن ۋەدىلەردىن ئۆتىدۇ",
    "legend.edgeMergedTie": "ئۇ يېزىلغان شاخقا ئوخشاش ۋەدىلەر",
    "menu.jumpHead": "Check چىقىمدانغا بېرىڭ",
    "menu.resetView": "View Reset view",
    "menu.createBranch": "بۇ يەردىن شاخ قۇرۇڭ…",
    "menu.checkout": "بۇ ۋەدىڭىزنى تەكشۈرۈڭ",
    "menu.copySha": "SHA نى كۆپەيتىش",
    "menu.pushBranch": "«{name}» تارمىقى",
    "menu.renameBranch": "تارماقنىڭ نامىنى «{name}» â غا ئۆزگەرتىڭ",
    "menu.deleteBranch": "«{name}» تارمىقىنى ئۆچۈرۈڭ",
    "menu.renameCommit": "ۋەدە ئۇچۇرىنىڭ نامىنى ئۆزگەرتىش…",
    "menu.undoCommit": "ئەمەلدىن قالدۇرۇش (ئۆزگەرتىشنى ساقلاش)…",
    "menu.viewChanges": "ئۆزگىرىشلەرنى كۆرۈش…",
    "menu.mergeBranch": "\"{source}\" نى \"{target}\" غا بىرلەشتۈرۈڭ",
    "menu.jumpToOriginal": "ئەسلى ۋەدىگە بېرىڭ",
    "node.mergedInTooltip": "بىرلەشتۈرۈش {merge} بۇ ۋەدىسىنى «{target}» غا ئېلىپ كەلدى — ئۇ «{origin}» غا يېزىلغان.",
    "changes.title": "{sha} دىكى ئۆزگىرىش",
    "changes.tabChanged": "ئۆزگەرتىلدى",
    "changes.tabAll": "بارلىق ھۆججەتلەر",
    "changes.added": "قوشۇلدى",
    "changes.modified": "ئۆزگەرتىلدى",
    "changes.deleted": "ئۆچۈرۈلدى",
    "changes.renamed": "ئىسمى ئۆزگەرتىلدى",
    "changes.noChanges": "بۇ ھۆججەت ھېچقانداق ھۆججەتنى ئۆزگەرتمەيدۇ.",
    "changes.loading": "Loading diff…",
    "changes.selectFile": "ئۇنىڭ ئۆزگىرىشىنى كۆرۈش ئۈچۈن ھۆججەت تاللاڭ.",
    "changes.original": "ئەسلى",
    "changes.changed": "بۇ ۋەدە",
    "changes.binary": "ئىككىلىك ھۆججەت - كۆرسىتىدىغان تېكىست يوق.",
    "changes.tooLarge": "ھۆججەت بەك چوڭ.",
    "changes.renamedFrom": "{path} دىن ئۆزگەرتىلگەن",
    "changes.close": "تاقاش",
    "changes.prevChange": "ئالدىنقى ئۆزگىرىش",
    "changes.nextChange": "كېيىنكى ئۆزگىرىش",
    "changes.collapseAll": "بارلىق ھۆججەت قىسقۇچلارنى يىمىرىڭ",
    "changes.expandAll": "بارلىق قىسقۇچلارنى كېڭەيتىڭ",
    "changes.searchPlaceholder": "ھۆججەتلەرنى ئىزدەش…",
    "changes.noSearchResults": "ئىزدەشكە ھېچقانداق ھۆججەت ماس ​​كەلمەيدۇ.",
    "changes.maximize": "Maximize",
    "changes.restore": "چوڭلۇقىنى ئەسلىگە كەلتۈرۈڭ",
    "find.open": "بۇ پەرقنى ئىزدەڭ (Ctrl + F)",
    "find.placeholder": "Diff in Find…",
    "find.prev": "ئالدىنقى مۇسابىقە",
    "find.next": "كېيىنكى مۇسابىقە",
    "find.close": "ئىزدەشنى تاقاش",
    "find.noResults": "ھېچقانداق نەتىجە يوق",
    "commit.title": "ئۆزگەرتىش",
    "commit.close": "تاقاش",
    "commit.loading": "خىزمەت دەرىخىنى ئوقۇش…",
    "commit.noChanges": "قىلىدىغان يەرلىك ئۆزگەرتىش يوق.",
    "commit.selectFile": "ئۇنىڭ پەرقىنى كۆرۈش ئۈچۈن ھۆججەت تاللاڭ.",
    "commit.selectAll": "ھەممىنى تاللاڭ",
    "commit.selectNone": "ھەممىنى تازىلاڭ",
    "commit.message": "ۋەدە ئۇچۇرى",
    "commit.messagePlaceholder": "ئۆزگەرتىشنى تەسۋىرلەڭ",
    "commit.selectedCount": "{count} ھۆججىتى تاللانغان",
    "commit.review": "ئوبزور",
    "commit.reviewTitle": "ۋەدە بېرىشتىن بۇرۇنقى ئاخىرقى تەكشۈرۈش",
    "commit.reviewSummary": "ئۇچۇر بىلەن {count} ھۆججىتىنى تاپشۇرۇڭ: {message}",
    "commit.back": "قايتىش",
    "commit.commit": "يەرلىك ۋەدىلەرنى بەرپا قىلىڭ",
    "menu.stashApply": "چاپلاش",
    "menu.stashPop": "Pop stash (apply & remove)",
    "menu.stashDrop": "Stash stash…",
    "merge.title": "شاخلىتىپ سېتىش",
    "merge.route": "ھازىرقى شاخقا قوشۇلۇش",
    "merge.source": "مەنبە (مەنبە)",
    "merge.target": "Into (current)",
    "merge.loading": "بىرلەشتۈرۈشنى تەھلىل قىلىش…",
    "merge.upToDate": "ئاللىبۇرۇن يېڭىلاندى - قوشۇلىدىغان نەرسە يوق.",
    "merge.fastForward": "تېز ئىلگىرىلەش - قوشۇۋېلىش مەجبۇرىيىتى يوق (بىرنى زورلاش ئۈچۈن «قوشۇۋېلىش ۋەدىسى قۇرۇش» نى چېكىڭ).",
    "merge.summary": "{files} ھۆججىتى ئۆزگەرتىلدى ، {conflicts} توقۇنۇشى.",
    "merge.conflictsWarning": "بۇ بىرلەشتۈرۈشتە زىددىيەت بار - سىز يەنىلا بىرلەشتۈرەلەيسىز ، ئاندىن ئۇلارنى تەھرىرلىگۈچتە ھەل قىلالايسىز.",
    "merge.noChanges": "ھۆججەت ئۆزگەرمەيدۇ.",
    "merge.files": "ئۆزگەرتىش نەتىجىسى",
    "merge.added": "قوشۇلدى",
    "merge.modified": "ئۆزگەرتىلدى",
    "merge.deleted": "ئۆچۈرۈلدى",
    "merge.conflict": "توقۇنۇش",
    "merge.message": "ۋەدىلەرنى بىرلەشتۈرۈش",
    "merge.messagePlaceholder": "شۆبە «مەنبە» نى بىرلەشتۈرۈش",
    "merge.messageFfHint": "تېز ئىلگىرىلەش قوشۇلۇشىغا پەرۋا قىلىنمايدۇ (ھېچقانداق ۋەدە يارىتىلمايدۇ).",
    "merge.noFastForward": "بىرلەشتۈرۈش ۋەدىسىنى ھاسىل قىلىڭ (تېز ئىلگىرىلەش يوق)",
    "merge.noFastForwardHint": "شاخ تېز ئىلگىرىلىيەلەيدىغان ھالەتتىمۇ ھەمىشە بىرلەشتۈرۈش ۋەدىسىنى خاتىرىلەڭ.",
    "merge.merge": "بىرلەشتۈرۈش",
    "merge.cancel": "بىكار قىلىش",
    "merge.previewError": "ئالدىن كۆرۈشنى ھېسابلىيالمىدى: {message}",
    "merge.squashBadge": "Squash",
    "merge.squashNote": "كاۋا بىرلەشتۈرۈش - ONE «{target}» غا ۋەدە بەرگەن ھەر بىر ئۆزگىرىش يەرلىرى «{source}» نىڭ ئۆزىنىڭ ۋەدىسى تارىختىن يىراق. تەڭشەكلەرگە قايتىڭ ›بىرلەشتۈرۈش.",
    "merge.messageSquash": "ۋەدە ئۇچۇرى",
    "merge.squashMessagePlaceholder": "«مەنبە» دىن ئۆزگەرتىلگەن ئۆزگىرىشلەر",
    "newBranch.title": "شۆبە قۇرۇش",
    "newBranch.startPoint": "{sha} دىن باشلانغان يېڭى تارماق",
    "newBranch.startPointOn": "on {refs}",
    "newBranch.location": "ئورنى",
    "newBranch.locationRoot": "(root)",
    "newBranch.expandAll": "ھەممىنى كېڭەيتىڭ",
    "newBranch.collapseAll": "ھەممىنى يىمىرىڭ",
    "newBranch.expandFolder": "قىسقۇچنى كېڭەيتىڭ",
    "newBranch.collapseFolder": "ھۆججەت قىسقۇچنى يىمىرىڭ",
    "newBranch.name": "تارماق ئىسمى",
    "newBranch.namePlaceholder": "مېنىڭ شاخ",
    "newBranch.fullName": "تولۇق ئىسمى: {name}",
    "newBranch.invalid": "ئىناۋەتسىز تارماق ئىسمى",
    "newBranch.exists": "بۇ ئىسىم بىلەن بىر تارماق مەۋجۇت",
    "newBranch.checkout": "قۇرۇلغاندىن كېيىن چىقىم قىلىش تارمىقى",
    "newBranch.create": "قۇر",
    "newBranch.cancel": "بىكار قىلىش",
    "status.loading": "گرافىك يۈكلەۋاتىدۇ…",
    "status.summary": "{repo} {commits} نى كۆرسىتىدۇ ، {refs} رەت قىلىدۇ",
    "status.branchCreated": "{sha} دا «{name}» تارمىقى قۇرۇلدى",
    "status.error": "خاتالىق: {message}",
    "status.opFailed": "مەشغۇلات مەغلۇپ بولدى",
    "status.fetching": "Fetching…",
    "status.pulling": "Pulling…",
    "status.pushing": "Pushing…",
    "status.committing": "يەرلىك ۋەدىلەرنى يارىتىش…",
    "status.commitCreated": "يەرلىك ۋەدە {sha}: {message}",
    "status.syncing": "ماسقەدەملەش…",
    "status.noHead": "گرافىكتا نۆۋەتتىكى تەكشۈرۈش يوق.",
    "status.undoing": "ئەمەلدىن قالدۇرۇش…",
    "status.commitUndone": "ئەمەلدىن قالدۇرۇڭ - ئۆزگەرتىش خىزمەت دەرىخىگە قايتىپ كەلدى.",
    "status.undoConflict": "زىددىيەتنى بىكار قىلىش - ئۇلارنى تەھرىرلىگۈچتە ھەل قىلىڭ ، ئاندىن داۋاملاشتۇرۇڭ.",
    "status.stashApplying": "چاپلاش…",
    "status.stashPopping": "Popping stash…",
    "status.stashDropping": "تاشلاندۇق تاش…",
    "status.stashApplied": "Stash applied.",
    "status.stashPopped": "Stash pop.",
    "status.stashDropped": "Stash dropped.",
    "status.stashConflict": "زىددىيەتنى تۈگىتىش - ئۇلارنى تەھرىرلىگۈچتە ھەل قىلىڭ.",
    "status.merging": "بىرلەشتۈرۈش…",
    "status.merged": "بىرلەشتۈرۈش تاماملاندى.",
    "status.mergeConflict": "بىرلەشتۈرۈشتە زىددىيەت بار - ئۇلارنى تەھرىرلىگۈچتە ھەل قىلىڭ ، ئاندىن ۋەدە قىلىڭ.",
    "search.title": "ئىزدەش",
    "search.tooltip": "ئىزدەش ۋەدىلىرى",
    "search.placeholder": "ئىزدەش ۋەدىلىرى…",
    "search.previous": "ئالدىنقى نەتىجە",
    "search.next": "كېيىنكى نەتىجە",
    "search.noResults": "ھېچقانداق نەتىجە يوق",
    "search.resultCount": "{index} / {count}",
    "search.filter": "سۈزگۈچ ھالىتى",
    "search.highlight": "يورۇتۇش ھالىتى",
    "search.filterMode": "ئىزدەش ئېكرانى",
    "search.filterModeFilter": "سۈزگۈچ (ماس كەلمەيدىغانلارنى يوشۇرۇش)",
    "search.filterModeHighlight": "يارقىن نۇقتا (ھەممىنى كۆرسىتىش ، ماسلاشتۇرۇش)",
    "search.filterModeHint": "ئىزدەش نەتىجىسىنىڭ گرافىكتا قانداق كۆرسىتىلىدىغانلىقىنى تاللاڭ.",
    "search.visibility": "ئىزدەش ئورنى",
    "search.visibilityToolbar": "قورالبالدىقى كۇنۇپكىسى",
    "search.visibilityAlwaysVisible": "ھەمىشە كۆرۈنىدىغان بالداق",
    "search.visibilityHint": "ئۈستىدىكى قورالبالدىقى سىنبەلگىسى ياكى ئۈزلۈكسىز تاياقچە قىلىپ ئىزدەڭ.",
    "footer.github": "GitHub",
  },
  ur: {
    "toolbar.refresh": "⟳ ریفریش کریں۔",
    "toolbar.fetch": "⤓ بازیافت کریں۔",
    "toolbar.pull": "⇩ کھینچیں۔",
    "toolbar.push": "⇧ دھکا",
    "toolbar.commit": "✓ عہد کریں۔",
    "toolbar.sync": "⇅ مطابقت پذیری",
    "toolbar.jumpHead": "⌖ چیک آؤٹ پر جائیں۔",
    "toolbar.reset": "⤢ منظر کو دوبارہ ترتیب دیں۔",
    "toolbar.settings": "⚙ ترتیبات",
    "details.header": "کمٹ کی تفصیلات",
    "details.close": "بند",
    "details.sha": "SHA",
    "details.shortSha": "مختصر SHA",
    "details.message": "پیغام",
    "details.author": "مصنف",
    "details.date": "تاریخ",
    "details.labels": "لیبلز",
    "details.location": "مقام",
    "details.currentHead": "ہیڈ - موجودہ چیک آؤٹ",
    "settings.title": "ترتیبات",
    "settings.language": "زبان",
    "settings.theme": "تھیم",
    "settings.themeLight": "روشنی",
    "settings.themeDark": "اندھیرا",
    "settings.themeLightHint": "ہلکا پس منظر، گہرا متن۔",
    "settings.themeDarkHint": "گہرا پس منظر، ہلکا متن۔",
    "settings.mainBranch": "مین برانچ",
    "settings.mainBranchAuto": "خودکار",
    "settings.mainBranchSearch": "شاخیں تلاش کریں…",
    "settings.mainBranchNoMatch": "کوئی مماثل شاخیں نہیں۔",
    "settings.display": "ڈسپلے اسٹائل",
    "settings.displayModern": "جدید",
    "settings.displayClassic": "کلاسک",
    "settings.displayModernHint": "مفت کینوس — پین پر گھسیٹیں، زوم کرنے کے لیے اسکرول کریں۔",
    "settings.displayClassicHint": "فکسڈ کینوس — ٹرنک کو بائیں پن کیا گیا، کوئی زوم نہیں، صرف اسکرول کریں (جیسے SVN ریویژن گراف)۔",
    "settings.sectionGeneral": "جنرل",
    "settings.sectionGraph": "گراف",
    "settings.sectionChanges": "منظر بدلتا ہے۔",
    "settings.diffMinimap": "ڈیف منی میپ",
    "settings.diffMinimapOn": "دکھایا گیا",
    "settings.diffMinimapOff": "پوشیدہ",
    "settings.diffMinimapOnHint": "فرق کے ساتھ ایک VS کوڈ طرز کا جائزہ پٹی دکھائیں — اسے سکرول کرنے کے لیے گھسیٹیں؛ تبدیلی مارکر شامل ہیں.",
    "settings.diffMinimapOffHint": "کوئی جائزہ پٹی نہیں - عام طور پر فرق کو اسکرول کریں۔",
    "settings.commitReview": "جائزہ لینے کا عہد کریں۔",
    "settings.commitReviewOn": "عہد کرنے سے پہلے پوچھیں۔",
    "settings.commitReviewOff": "براہ راست عہد کریں۔",
    "settings.commitReviewOnHint": "مقامی کمٹ بنانے سے پہلے حتمی فائل کی فہرست اور میسج چیک دکھائیں۔",
    "settings.commitReviewOffHint": "کمٹ بٹن فوری طور پر مقامی کمٹ بناتا ہے۔",
    "settings.svnBranchDialog": "SVN طرز کی برانچ ڈائیلاگ",
    "settings.svnBranchDialogHint": "برانچ بناتے وقت فولڈر ٹری چننے والا دکھائیں (ایک سادہ پرامپٹ کے بجائے)۔",
    "settings.branchDialog": "برانچ ڈائیلاگ",
    "settings.branchDialogSvnTitle": "SVN طرز",
    "settings.branchDialogNativeVscode": "وی ایس کوڈ اسٹائل",
    "settings.branchDialogNativeVs": "بصری اسٹوڈیو کا انداز",
    "settings.branchDialogNativeHint": "آپ کا IDE کا بلٹ ان برانچ پرامپٹ۔",
    "settings.close": "بند",
    "settings.done": "ہو گیا",
    "settings.sectionAdvanced": "اعلی درجے کی",
    "settings.gitSource": "Git قابل عمل ہے۔",
    "settings.gitSourceBuiltin": "بلٹ ان",
    "settings.gitSourceBuiltinHint": "IDE کی بلٹ ان Git ایکسٹینشن سے گٹ بائنری استعمال کریں۔",
    "settings.gitSourceCustom": "اپنی مرضی کا راستہ",
    "settings.gitPath": "گٹ کا راستہ",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "براؤز کریں…",
    "settings.jargon": "گٹ کی شرائط",
    "settings.jargonTranslate": "ترجمہ کریں۔",
    "settings.jargonEnglish": "انگریزی میں رکھیں",
    "settings.jargonTranslateHint": "گٹ کی اصطلاحات (پل، پش، کمٹ، برانچ…) کو انٹرفیس کی زبان میں ترجمہ کریں۔",
    "settings.jargonEnglishHint": "انگریزی میں گٹ کی اصطلاحات (پل، پش، کمٹ، برانچ…) رکھیں؛ باقی سب کا ترجمہ کریں.",
    "settings.sectionMerge": "ضم کرنا",
    "settings.mergeMode": "انداز کو ضم کریں۔",
    "settings.mergeModeMerge": "عام انضمام",
    "settings.mergeModeSquash": "اسکواش انضمام",
    "settings.mergeModeMergeHint": "برانچ کو اس طرح ضم کرتا ہے جیسے یہ ہے: اس کے کمٹ موجودہ برانچ کی تاریخ کا حصہ بن جاتے ہیں، انضمام کمٹ کے ساتھ شامل ہوتے ہیں (یا جب ممکن ہو فاسٹ فارورڈ کیا جاتا ہے)۔",
    "settings.mergeModeSquashHint": "موجودہ برانچ (git merge --squash) پر پوری برانچ کو ایک واحد کمٹ میں سمیٹتا ہے۔ برانچ کے اپنے وعدے کبھی بھی موجودہ برانچ کی تاریخ میں داخل نہیں ہوتے ہیں - آپ کو وہاں صرف ایک کمٹ نظر آتا ہے۔ شاخ خود اچھوت رہ ​​گئی ہے، اس لیے اس کی کمٹ اپنی ہی گلی میں نظر آتی ہے۔",
    "settings.mergedView": "گراف میں ضم شدہ کمٹ",
    "settings.mergedViewBranch": "صرف ان کی اپنی برانچ پر",
    "settings.mergedViewTarget": "برانچ میں بھی ان کو ضم کر دیا گیا۔",
    "settings.mergedViewBranchHint": "ضم شدہ برانچ کی کمٹ صرف ان کی اپنی لین میں تیار کی جاتی ہے۔ انضمام ایک کنیکٹر کے طور پر ظاہر ہوتا ہے۔ کلاسک نظارہ۔",
    "settings.mergedViewTargetHint": "انضمام میں لائے جانے والے کمٹ کو وصول کرنے والی برانچ کی لین میں بھی تیار کیا جاتا ہے، انضمام کے کمٹ کے نیچے اسٹیک کیا جاتا ہے اور اصل کے ساتھ لیول ہوتا ہے - کیونکہ وہ برانچ واقعی میں اب ان پر مشتمل ہے۔ کاپیاں پیلی ہیں اور اس شاخ کے ساتھ ہیں جس پر وہ لکھے گئے تھے، لہذا یہ واضح رہتا ہے کہ وہ وہاں نہیں بنی تھیں۔ اسکواش کے انضمام غیر متاثر ہوتے ہیں: ایک اسکواش ایک عام کمٹ لکھتا ہے، لہذا صرف وہی کمٹ ظاہر ہوتا ہے۔",
    "legend.title": "لیجنڈ",
    "legend.head": "ہیڈ / موجودہ برانچ",
    "legend.local": "مقامی شاخ",
    "legend.remote": "دور دراز کی شاخ",
    "legend.remoteOnly": "صرف بادل میں (نکالا نہیں)",
    "legend.tag": "ٹیگ (ورژن)",
    "legend.commit": "عزم",
    "legend.stash": "سٹیش (شیلف کام)",
    "legend.mergedIn": "انضمام کے ساتھ آیا (دوسری شاخ پر لکھا ہوا)",
    "legend.nodes": "بکس",
    "legend.lines": "لکیریں",
    "legend.edgeParent": "اسی لائن پر پچھلا عہد",
    "legend.edgeMerge": "ضم شدہ برانچ - انضمام کمٹ پر تیر کے نشانات",
    "legend.edgeBranch": "ایک شاخ یہاں سے نکل گئی۔",
    "legend.edgeStash": "سٹیش اور کمٹ جس پر اسے محفوظ کیا گیا تھا۔",
    "legend.edgeMergedChain": "اس برانچ کی لائن ضم شدہ کمٹ کے ذریعے چلتی ہے۔",
    "legend.edgeMergedTie": "وہی عہد جس شاخ پر لکھا تھا۔",
    "menu.jumpHead": "⌖ چیک آؤٹ پر جائیں۔",
    "menu.resetView": "⤢ منظر کو دوبارہ ترتیب دیں۔",
    "menu.createBranch": "یہاں سے برانچ بنائیں…",
    "menu.checkout": "اس عہد کو چیک آؤٹ کریں۔",
    "menu.copySha": "کمٹ SHA کاپی کریں۔",
    "menu.pushBranch": "پش برانچ \"{name}\"",
    "menu.renameCommit": "کمٹ میسج کا نام تبدیل کریں…",
    "menu.undoCommit": "کمٹ کو کالعدم کریں (تبدیلیاں رکھیں)…",
    "menu.viewChanges": "تبدیلیاں دیکھیں…",
    "menu.mergeBranch": "\"{source}\" کو \"{target}\" میں ضم کریں۔",
    "menu.jumpToOriginal": "اصل کمٹ پر جائیں۔",
    "node.mergedInTooltip": "{merge} کو ضم کریں اس کمٹ کو \"{target}\" میں لے آئے اور یہ \"{origin}\" پر لکھا ہوا تھا۔",
    "changes.title": "{sha} میں تبدیلیاں",
    "changes.tabChanged": "بدل گیا۔",
    "changes.tabAll": "تمام فائلیں۔",
    "changes.added": "شامل کیا گیا۔",
    "changes.modified": "ترمیم شدہ",
    "changes.deleted": "حذف کر دیا گیا۔",
    "changes.renamed": "نام تبدیل کر دیا گیا۔",
    "changes.noChanges": "یہ کمٹ کسی فائل کو تبدیل نہیں کرتا ہے۔",
    "changes.loading": "فرق لوڈ ہو رہا ہے…",
    "changes.selectFile": "ایک فائل کو اس کی تبدیلیاں دیکھنے کے لیے منتخب کریں۔",
    "changes.original": "اصل",
    "changes.changed": "یہ عہد",
    "changes.binary": "بائنری فائل — دکھانے کے لیے متن میں کوئی فرق نہیں۔",
    "changes.tooLarge": "فائل اختلاف کے لیے بہت بڑی ہے۔",
    "changes.renamedFrom": "{path} سے نام تبدیل کر دیا گیا۔",
    "changes.close": "بند",
    "changes.prevChange": "پچھلی تبدیلی",
    "changes.nextChange": "اگلی تبدیلی",
    "changes.collapseAll": "تمام فولڈرز کو سکیڑیں۔",
    "changes.expandAll": "تمام فولڈرز کو پھیلائیں۔",
    "changes.searchPlaceholder": "فائلیں تلاش کریں…",
    "changes.noSearchResults": "کوئی فائل آپ کی تلاش سے مماثل نہیں ہے۔",
    "changes.maximize": "زیادہ سے زیادہ کرنا",
    "changes.restore": "سائز کو بحال کریں۔",
    "find.open": "اس فرق میں تلاش کریں (Ctrl+F)",
    "find.placeholder": "فرق میں تلاش کریں…",
    "find.prev": "پچھلا میچ",
    "find.next": "اگلا میچ",
    "find.close": "تلاش بند کریں۔",
    "find.noResults": "کوئی نتیجہ نہیں۔",
    "commit.title": "تبدیلیاں کریں۔",
    "commit.close": "بند",
    "commit.loading": "کام کرنے والے درخت کو پڑھنا…",
    "commit.noChanges": "ارتکاب کے لیے کوئی مقامی تبدیلیاں نہیں ہیں۔",
    "commit.selectFile": "ایک فائل کا فرق دیکھنے کے لیے اسے منتخب کریں۔",
    "commit.selectAll": "سبھی کو منتخب کریں۔",
    "commit.selectNone": "سب صاف کریں۔",
    "commit.message": "عہد کا پیغام",
    "commit.messagePlaceholder": "تبدیلی کی وضاحت کریں۔",
    "commit.selectedCount": "{count} فائلیں منتخب کی گئیں۔",
    "commit.review": "جائزہ لیں",
    "commit.reviewTitle": "کمٹمنٹ سے پہلے فائنل چیک",
    "commit.reviewSummary": "پیغام کے ساتھ {count} فائلیں بھیجیں: {message}",
    "commit.back": "پیچھے",
    "commit.commit": "مقامی کمٹ بنائیں",
    "menu.stashApply": "اسٹیش لگائیں۔",
    "menu.stashPop": "پاپ اسٹیش (لاگو اور ہٹا دیں)",
    "menu.stashDrop": "ذخیرہ ڈالیں…",
    "merge.title": "برانچ کو ضم کریں۔",
    "merge.route": "موجودہ برانچ میں ضم ہونا",
    "merge.source": "منجانب (ذریعہ)",
    "merge.target": "میں (موجودہ)",
    "merge.loading": "انضمام کا تجزیہ کیا جا رہا ہے…",
    "merge.upToDate": "پہلے سے ہی اپ ٹو ڈیٹ — ضم کرنے کے لیے کچھ نہیں۔",
    "merge.fastForward": "فاسٹ فارورڈ - انضمام کی کمٹ کی ضرورت نہیں ہے (کسی کو مجبور کرنے کے لیے \"مرج کمٹ بنائیں\" پر نشان لگائیں)۔",
    "merge.summary": "{files} فائل(ز) کو تبدیل کر دیا گیا، {conflicts} تنازعات۔",
    "merge.conflictsWarning": "اس انضمام میں تنازعات ہیں — آپ اب بھی ضم کر سکتے ہیں، پھر انہیں ایڈیٹر میں حل کر سکتے ہیں۔",
    "merge.noChanges": "فائل میں کوئی تبدیلی نہیں۔",
    "merge.files": "نتیجہ خیز تبدیلیاں",
    "merge.added": "شامل کیا گیا۔",
    "merge.modified": "ترمیم شدہ",
    "merge.deleted": "حذف کر دیا گیا۔",
    "merge.conflict": "تنازعہ",
    "merge.message": "کمٹ میسیج کو ضم کریں۔",
    "merge.messagePlaceholder": "شاخ 'ذریعہ' کو ضم کریں",
    "merge.messageFfHint": "فاسٹ فارورڈ انضمام پر نظر انداز کیا گیا (کوئی کمٹ نہیں بنایا گیا)۔",
    "merge.noFastForward": "انضمام کا عہد بنائیں (کوئی فاسٹ فارورڈ نہیں)",
    "merge.noFastForwardHint": "انضمام کی کمٹ کو ہمیشہ ریکارڈ کریں، یہاں تک کہ جب برانچ تیزی سے آگے بڑھ سکے۔",
    "merge.merge": "ضم کرنا",
    "merge.cancel": "منسوخ کریں۔",
    "merge.previewError": "پیش منظر کی گنتی نہیں کی جا سکی: {message}",
    "merge.squashBadge": "امریکی کدّو",
    "merge.squashNote": "اسکواش انضمام - ہر تبدیلی \"{target}\" پر ایک عہد کے طور پر آتی ہے۔ \"{source}\" کے اپنے وعدے اس کی تاریخ سے باہر رہتے ہیں۔ ترتیبات میں واپس سوئچ کریں › ضم کریں۔",
    "merge.messageSquash": "عہد کا پیغام",
    "merge.squashMessagePlaceholder": "'ذریعہ' سے اسکواش شدہ تبدیلیاں",
    "newBranch.title": "برانچ بنائیں",
    "newBranch.startPoint": "{sha} سے شروع ہونے والی نئی برانچ",
    "newBranch.startPointOn": "{refs} پر",
    "newBranch.location": "مقام",
    "newBranch.locationRoot": "(جڑ)",
    "newBranch.expandAll": "سبھی کو پھیلائیں۔",
    "newBranch.collapseAll": "سب کو سمیٹیں۔",
    "newBranch.expandFolder": "فولڈر کو پھیلائیں۔",
    "newBranch.collapseFolder": "فولڈر کو سکیڑیں۔",
    "newBranch.name": "برانچ کا نام",
    "newBranch.namePlaceholder": "میری برانچ",
    "newBranch.fullName": "پورا نام: {name}",
    "newBranch.invalid": "برانچ کا نام غلط ہے۔",
    "newBranch.exists": "اس نام کی ایک شاخ پہلے سے موجود ہے۔",
    "newBranch.checkout": "تخلیق کے بعد چیک آؤٹ برانچ",
    "newBranch.create": "بنائیں",
    "newBranch.cancel": "منسوخ کریں۔",
    "status.loading": "گراف لوڈ ہو رہا ہے…",
    "status.summary": "{repo} دکھا رہا ہے {commits} کمٹ، {refs} refs",
    "status.branchCreated": "{sha} پر برانچ \"{name}\" بنائی",
    "status.error": "خرابی: {message}",
    "status.opFailed": "آپریشن ناکام ہو گیا۔",
    "status.fetching": "بازیافت ہو رہا ہے…",
    "status.pulling": "کھینچ رہا ہے…",
    "status.pushing": "دھکیل رہا ہے…",
    "status.committing": "مقامی کمٹ کی تخلیق ہو رہی ہے…",
    "status.commitCreated": "مقامی کمٹ {sha} بنایا گیا: {message}",
    "status.syncing": "مطابقت پذیری ہو رہی ہے…",
    "status.noHead": "گراف میں کوئی موجودہ چیک آؤٹ نہیں ملا۔",
    "status.undoing": "عہد کو کالعدم کیا جا رہا ہے…",
    "status.commitUndone": "کمٹ کو کالعدم کریں - کام کرنے والے درخت میں تبدیلیاں واپس آ گئی ہیں۔",
    "status.undoConflict": "ہٹ تنازعات کو کالعدم کریں — انہیں ایڈیٹر میں حل کریں، پھر جاری رکھیں۔",
    "status.stashApplying": "اسٹیش لاگو کیا جا رہا ہے…",
    "status.stashPopping": "پوپنگ سٹیش…",
    "status.stashDropping": "ذخیرہ چھوڑ رہا ہے…",
    "status.stashApplied": "سٹیش لاگو کر دیا گیا۔",
    "status.stashPopped": "سٹیش پاپ ہو گیا.",
    "status.stashDropped": "سٹیش گرا دیا گیا۔",
    "status.stashConflict": "اسٹیش تنازعات — انہیں ایڈیٹر میں حل کریں۔",
    "status.merging": "ضم ہو رہا ہے…",
    "status.merged": "انضمام مکمل ہو گیا۔",
    "status.mergeConflict": "ضم کرنے میں تنازعات ہیں - انہیں ایڈیٹر میں حل کریں، پھر کمٹ کریں۔",
    "search.title": "تلاش کریں۔",
    "search.tooltip": "تلاش کا عہد کرتا ہے۔",
    "search.placeholder": "تلاش کا عہد کرتا ہے…",
    "search.previous": "پچھلا نتیجہ",
    "search.next": "اگلا نتیجہ",
    "search.noResults": "کوئی نتیجہ نہیں۔",
    "search.resultCount": "{index}/{count}",
    "search.filter": "فلٹر موڈ",
    "search.highlight": "ہائی لائٹ موڈ",
    "search.filterMode": "تلاش ڈسپلے",
    "search.filterModeFilter": "فلٹر (غیر مماثلتیں چھپائیں)",
    "search.filterModeHighlight": "نمایاں کریں (تمام دکھائیں، نشان زد میچ)",
    "search.filterModeHint": "منتخب کریں کہ گراف میں تلاش کے نتائج کیسے دکھائے جاتے ہیں۔",
    "search.visibility": "مقام تلاش کریں۔",
    "search.visibilityToolbar": "ٹول بار بٹن",
    "search.visibilityAlwaysVisible": "ہمیشہ نظر آنے والا بار",
    "search.visibilityHint": "تلاش کو بطور ٹول بار آئیکن یا سب سے اوپر پرسسٹنٹ بار دکھائیں۔",
    "footer.github": "گٹ ہب",
  },
  ar: {
    "toolbar.refresh": "⟳ تحديث",
    "toolbar.fetch": "⤓ جلب",
    "toolbar.pull": "⇩ اسحب",
    "toolbar.push": "⇧ ادفع",
    "toolbar.commit": "✓ الالتزام",
    "toolbar.sync": "⇅ مزامنة",
    "toolbar.jumpHead": "⌖ الذهاب إلى الخروج",
    "toolbar.reset": "⤢ إعادة ضبط العرض",
    "toolbar.settings": "⚙ الإعدادات",
    "details.header": "تفاصيل الالتزام",
    "details.close": "يغلق",
    "details.sha": "شا",
    "details.shortSha": "شا قصير",
    "details.message": "رسالة",
    "details.author": "مؤلف",
    "details.date": "تاريخ",
    "details.labels": "التسميات",
    "details.location": "موقع",
    "details.currentHead": "HEAD - الخروج الحالي",
    "settings.title": "إعدادات",
    "settings.language": "لغة",
    "settings.theme": "سمة",
    "settings.themeLight": "ضوء",
    "settings.themeDark": "مظلم",
    "settings.themeLightHint": "خلفية فاتحة، نص داكن.",
    "settings.themeDarkHint": "خلفية داكنة، نص فاتح.",
    "settings.mainBranch": "الفرع الرئيسي",
    "settings.mainBranchAuto": "تلقائي",
    "settings.mainBranchSearch": "بحث في الفروع...",
    "settings.mainBranchNoMatch": "لا توجد فروع مطابقة",
    "settings.display": "أسلوب العرض",
    "settings.displayModern": "حديث",
    "settings.displayClassic": "كلاسيكي",
    "settings.displayModernHint": "قماش مجاني - اسحب للتحريك، وقم بالتمرير للتكبير/التصغير.",
    "settings.displayClassicHint": "قماش ثابت - تم تثبيت الجذع على اليسار، بدون تكبير، قم بالتمرير فقط (مثل الرسم البياني لمراجعة SVN).",
    "settings.sectionGeneral": "عام",
    "settings.sectionGraph": "رسم بياني",
    "settings.sectionChanges": "عرض التغييرات",
    "settings.diffMinimap": "خريطة مصغرة مختلفة",
    "settings.diffMinimapOn": "معروض",
    "settings.diffMinimapOff": "مختفي",
    "settings.diffMinimapOnHint": "اعرض شريط نظرة عامة على نمط VS Code بجوار الفرق — اسحبه للتمرير؛ علامات التغيير متضمنة.",
    "settings.diffMinimapOffHint": "لا يوجد شريط نظرة عامة — قم بتمرير الفرق بشكل طبيعي.",
    "settings.commitReview": "الالتزام بالمراجعة",
    "settings.commitReviewOn": "اسأل قبل الالتزام",
    "settings.commitReviewOff": "الالتزام مباشرة",
    "settings.commitReviewOnHint": "قم بعرض قائمة الملفات النهائية والتحقق من الرسائل قبل إنشاء الالتزام المحلي.",
    "settings.commitReviewOffHint": "يقوم زر الالتزام بإنشاء الالتزام المحلي على الفور.",
    "settings.svnBranchDialog": "مربع حوار فرعي على نمط SVN",
    "settings.svnBranchDialogHint": "إظهار منتقي شجرة المجلدات عند إنشاء فرع (بدلاً من المطالبة البسيطة).",
    "settings.branchDialog": "حوار الفرع",
    "settings.branchDialogSvnTitle": "على غرار SVN",
    "settings.branchDialogNativeVscode": "نمط رمز VS",
    "settings.branchDialogNativeVs": "أسلوب الاستوديو المرئي",
    "settings.branchDialogNativeHint": "موجه الفرع المدمج في IDE الخاص بك.",
    "settings.close": "يغلق",
    "settings.done": "منتهي",
    "settings.sectionAdvanced": "متقدم",
    "settings.gitSource": "بوابة قابلة للتنفيذ",
    "settings.gitSourceBuiltin": "مدمج",
    "settings.gitSourceBuiltinHint": "استخدم git الثنائي من ملحق Git المدمج في IDE.",
    "settings.gitSourceCustom": "مسار مخصص",
    "settings.gitPath": "الطريق إلى البوابة",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "تصفح…",
    "settings.jargon": "شروط جيت",
    "settings.jargonTranslate": "يترجم",
    "settings.jargonEnglish": "احتفظ باللغة الإنجليزية",
    "settings.jargonTranslateHint": "ترجمة مصطلحات Git (السحب، الدفع، الالتزام، التفرع...) إلى لغة الواجهة.",
    "settings.jargonEnglishHint": "احتفظ بمصطلحات Git (سحب، دفع، التزام، فرع...) باللغة الإنجليزية؛ ترجمة كل شيء آخر.",
    "settings.sectionMerge": "دمج",
    "settings.mergeMode": "دمج النمط",
    "settings.mergeModeMerge": "دمج عادي",
    "settings.mergeModeSquash": "دمج الاسكواش",
    "settings.mergeModeMergeHint": "يدمج الفرع كما هو: تصبح التزاماته جزءًا من تاريخ الفرع الحالي، وينضم إليها التزام دمج (أو يتم تقديمه سريعًا عندما يكون ذلك ممكنًا).",
    "settings.mergeModeSquashHint": "ينهار الفرع بأكمله في التزام واحد على الفرع الحالي (git merge --squash). لا تدخل التزامات الفرع أبدًا في سجل الفرع الحالي - ترى التزامًا واحدًا فقط هناك. يتم ترك الفرع نفسه دون تغيير، لذا تظل التزاماته مرئية في المسار الخاص به.",
    "settings.mergedView": "الالتزامات المدمجة في الرسم البياني",
    "settings.mergedViewBranch": "فقط على فرعهم الخاص",
    "settings.mergedViewTarget": "أيضا في الفرع تم دمجهم فيه",
    "settings.mergedViewBranchHint": "يتم رسم التزامات الفرع المدمج فقط في المسار الخاص به؛ يظهر الدمج كموصل. المنظر الكلاسيكي.",
    "settings.mergedViewTargetHint": "يتم رسم عمليات الدمج التي تم جلبها في مسار الفرع المتلقي أيضًا، ومكدسة تحت التزام الدمج ومستوية مع النسخ الأصلية - لأن هذا الفرع يحتوي عليها بالفعل الآن. النسخ شاحبة ومتجهة نحو الفرع الذي كتبت عليه، لذا يبقى من الواضح أنها لم تصنع هناك. لا تتأثر عمليات دمج الاسكواش: يكتب الاسكواش التزامًا عاديًا واحدًا، لذلك يظهر هذا الالتزام فقط على الإطلاق.",
    "legend.title": "أسطورة",
    "legend.head": "رئيس / الفرع الحالي",
    "legend.local": "فرع محلي",
    "legend.remote": "فرع بعيد",
    "legend.remoteOnly": "فقط في السحابة (غير مسحوبة)",
    "legend.tag": "العلامة (الإصدار)",
    "legend.commit": "يقترف",
    "legend.stash": "خبأ (العمل على الرفوف)",
    "legend.mergedIn": "جاء بدمج (مكتوب على فرع آخر)",
    "legend.nodes": "صناديق",
    "legend.lines": "خطوط",
    "legend.edgeParent": "الالتزام السابق على نفس السطر",
    "legend.edgeMerge": "الفرع المدمج - يشير السهم إلى التزام الدمج",
    "legend.edgeBranch": "فرع متشعب هنا",
    "legend.edgeStash": "خبأ والالتزام الذي تم وضعه على الرف",
    "legend.edgeMergedChain": "يعمل خط هذا الفرع من خلال الالتزامات المدمجة",
    "legend.edgeMergedTie": "نفس الالتزام على الفرع الذي كتب عليه",
    "menu.jumpHead": "⌖ الذهاب إلى الخروج",
    "menu.resetView": "⤢ إعادة ضبط العرض",
    "menu.createBranch": "قم بإنشاء فرع من هنا...",
    "menu.checkout": "الخروج من هذا الالتزام",
    "menu.copySha": "نسخ الالتزام SHA",
    "menu.pushBranch": "فرع الدفع \"{name}\"",
    "menu.renameBranch": "إعادة تسمية الفرع \"{name}\" ¦",
    "menu.deleteBranch": "حذف الفرع \"{name}\" ¦",
    "menu.renameCommit": "إعادة تسمية رسالة الالتزام...",
    "menu.undoCommit": "التراجع عن الالتزام (الاحتفاظ بالتغييرات)...",
    "menu.viewChanges": "عرض التغييرات...",
    "menu.mergeBranch": "دمج \"{source}\" في \"{target}\" ¦",
    "menu.jumpToOriginal": "انتقل إلى الالتزام الأصلي",
    "node.mergedInTooltip": "أدى دمج {merge} إلى جلب هذا الالتزام إلى \"{target}\" - لقد تمت كتابته على \"{origin}\".",
    "changes.title": "التغييرات في {sha}",
    "changes.tabChanged": "تغيرت",
    "changes.tabAll": "جميع الملفات",
    "changes.added": "تمت الإضافة",
    "changes.modified": "معدل",
    "changes.deleted": "تم الحذف",
    "changes.renamed": "تمت إعادة تسميته",
    "changes.noChanges": "هذا الالتزام لا يغير أي ملفات.",
    "changes.loading": "جارٍ تحميل الفرق…",
    "changes.selectFile": "حدد ملفًا لرؤية تغييراته.",
    "changes.original": "إبداعي",
    "changes.changed": "هذا الالتزام",
    "changes.binary": "ملف ثنائي - لا يوجد فرق نصي للعرض.",
    "changes.tooLarge": "الملف كبير جدًا بحيث لا يمكن التمييز.",
    "changes.renamedFrom": "تمت إعادة تسميته من {path}",
    "changes.close": "يغلق",
    "changes.prevChange": "التغيير السابق",
    "changes.nextChange": "التغيير التالي",
    "changes.collapseAll": "طي كافة المجلدات",
    "changes.expandAll": "قم بتوسيع كافة المجلدات",
    "changes.searchPlaceholder": "بحث في الملفات...",
    "changes.noSearchResults": "لا توجد ملفات تطابق بحثك.",
    "changes.maximize": "تعظيم",
    "changes.restore": "استعادة الحجم",
    "find.open": "البحث في هذا الاختلاف (Ctrl+F)",
    "find.placeholder": "البحث في فرق…",
    "find.prev": "المباراة السابقة",
    "find.next": "المباراة القادمة",
    "find.close": "إغلاق البحث",
    "find.noResults": "لا توجد نتائج",
    "commit.title": "ارتكاب التغييرات",
    "commit.close": "يغلق",
    "commit.loading": "قراءة شجرة العمل...",
    "commit.noChanges": "لا توجد تغييرات محلية للالتزام بها.",
    "commit.selectFile": "حدد ملفًا لمعرفة اختلافاته.",
    "commit.selectAll": "حدد الكل",
    "commit.selectNone": "مسح الكل",
    "commit.message": "رسالة الالتزام",
    "commit.messagePlaceholder": "وصف التغيير",
    "commit.selectedCount": "تم تحديد ملف (ملفات) {count}",
    "commit.review": "مراجعة",
    "commit.reviewTitle": "التحقق النهائي قبل الالتزام",
    "commit.reviewSummary": "قم بتنفيذ ملف (ملفات) {count} مع الرسالة: {message}",
    "commit.back": "خلف",
    "commit.commit": "إنشاء الالتزام المحلي",
    "menu.stashApply": "تطبيق مخبأ",
    "menu.stashPop": "خبأ البوب ​​(تطبيق وإزالة)",
    "menu.stashDrop": "إسقاط مخبأ…",
    "merge.title": "فرع الدمج",
    "merge.route": "الاندماج في الفرع الحالي",
    "merge.source": "من (المصدر)",
    "merge.target": "في (الحالي)",
    "merge.loading": "جارٍ تحليل الدمج…",
    "merge.upToDate": "محدث بالفعل - لا يوجد شيء لدمجه.",
    "merge.fastForward": "التقديم السريع - ليست هناك حاجة إلى التزام دمج (ضع علامة في \"إنشاء التزام دمج\" لفرض واحد).",
    "merge.summary": "تم تغيير ملف (ملفات) {files}، وتعارض (تعارضات) {conflicts}.",
    "merge.conflictsWarning": "يحتوي هذا الدمج على تعارضات — لا يزال بإمكانك الدمج، ثم حلها في المحرر.",
    "merge.noChanges": "لا توجد تغييرات الملف.",
    "merge.files": "التغييرات الناتجة",
    "merge.added": "تمت الإضافة",
    "merge.modified": "معدل",
    "merge.deleted": "تم الحذف",
    "merge.conflict": "صراع",
    "merge.message": "دمج رسالة الالتزام",
    "merge.messagePlaceholder": "دمج فرع \"المصدر\"",
    "merge.messageFfHint": "تم التجاهل عند الدمج السريع (لم يتم إنشاء أي التزام).",
    "merge.noFastForward": "إنشاء التزام دمج (بدون تقديم سريع)",
    "merge.noFastForwardHint": "قم دائمًا بتسجيل التزام الدمج، حتى عندما يتمكن الفرع من التقديم السريع.",
    "merge.merge": "دمج",
    "merge.cancel": "يلغي",
    "merge.previewError": "تعذر حساب المعاينة: {message}",
    "merge.squashBadge": "قرع",
    "merge.squashNote": "دمج الاسكواش - كل تغيير يحدث عند التزام واحد على \"{target}\"؛ تظل التزامات \"{source}\" خارج تاريخها. قم بالتبديل مرة أخرى إلى الإعدادات › دمج.",
    "merge.messageSquash": "رسالة الالتزام",
    "merge.squashMessagePlaceholder": "تغييرات سحقت من \"المصدر\"",
    "newBranch.title": "إنشاء فرع",
    "newBranch.startPoint": "فرع جديد يبدأ من {sha}",
    "newBranch.startPointOn": "على {refs}",
    "newBranch.location": "موقع",
    "newBranch.locationRoot": "(جذر)",
    "newBranch.expandAll": "قم بتوسيع الكل",
    "newBranch.collapseAll": "طي الكل",
    "newBranch.expandFolder": "قم بتوسيع المجلد",
    "newBranch.collapseFolder": "طي المجلد",
    "newBranch.name": "اسم الفرع",
    "newBranch.namePlaceholder": "فرعي",
    "newBranch.fullName": "الاسم الكامل: {name}",
    "newBranch.invalid": "اسم الفرع غير صالح",
    "newBranch.exists": "يوجد فرع بهذا الاسم بالفعل",
    "newBranch.checkout": "فرع الخروج بعد الإنشاء",
    "newBranch.create": "يخلق",
    "newBranch.cancel": "يلغي",
    "status.loading": "جارٍ تحميل الرسم البياني…",
    "status.summary": "{repo}إظهار التزامات {commits}، ومراجع {refs}",
    "status.branchCreated": "تم إنشاء الفرع \"{name}\" في {sha}",
    "status.error": "خطأ: {message}",
    "status.opFailed": "فشلت العملية",
    "status.fetching": "جارٍ الجلب…",
    "status.pulling": "سحب…",
    "status.pushing": "دفع…",
    "status.committing": "خلق الالتزام المحلي ...",
    "status.commitCreated": "تم إنشاء الالتزام المحلي {sha}: {message}",
    "status.syncing": "جارٍ المزامنة…",
    "status.noHead": "لم يتم العثور على الخروج الحالي في الرسم البياني.",
    "status.undoing": "التراجع عن الالتزام...",
    "status.commitUndone": "تم التراجع عن الالتزام — عادت التغييرات إلى شجرة العمل.",
    "status.undoConflict": "التراجع عن تعارضات النتائج - قم بحلها في المحرر، ثم تابع.",
    "status.stashApplying": "جارٍ تطبيق المخزن…",
    "status.stashPopping": "ظهرت مخبأ…",
    "status.stashDropping": "إسقاط المخبأ…",
    "status.stashApplied": "تم تطبيق خبأ.",
    "status.stashPopped": "برزت مخبأ.",
    "status.stashDropped": "انخفض المخبأ.",
    "status.stashConflict": "خبأ الصراعات - قم بحلها في المحرر.",
    "status.merging": "جار الدمج…",
    "status.merged": "اكتمل الدمج.",
    "status.mergeConflict": "يحتوي الدمج على تعارضات - قم بحلها في المحرر، ثم الالتزام.",
    "search.title": "يبحث",
    "search.tooltip": "التزامات البحث",
    "search.placeholder": "يلتزم البحث…",
    "search.previous": "النتيجة السابقة",
    "search.next": "النتيجة التالية",
    "search.noResults": "لا توجد نتائج",
    "search.resultCount": "{index}/{count}",
    "search.filter": "وضع التصفية",
    "search.highlight": "وضع تسليط الضوء",
    "search.filterMode": "عرض البحث",
    "search.filterModeFilter": "التصفية (إخفاء العناصر غير المتطابقة)",
    "search.filterModeHighlight": "تمييز (إظهار الكل، وضع علامة على التطابقات)",
    "search.filterModeHint": "اختر كيفية عرض نتائج البحث في الرسم البياني.",
    "search.visibility": "موقع البحث",
    "search.visibilityToolbar": "زر شريط الأدوات",
    "search.visibilityAlwaysVisible": "شريط مرئي دائمًا",
    "search.visibilityHint": "إظهار البحث كأيقونة شريط أدوات أو شريط ثابت في الأعلى.",
    "footer.github": "جيثب",
  },
  sd: {
    "toolbar.refresh": "⟳ ريفريش ڪريو",
    "toolbar.fetch": "⤓ آڻڻ",
    "toolbar.pull": "⇩ ڇڪيو",
    "toolbar.push": "⇧ ڌڪ",
    "toolbar.commit": "✓ عزم",
    "toolbar.sync": "⇅ هم وقت",
    "toolbar.jumpHead": "⌖ چيڪ آئوٽ ڏانھن وڃو",
    "toolbar.reset": "⤢ ڏيک ري سيٽ ڪريو",
    "toolbar.settings": "⚙ سيٽنگون",
    "details.header": "واعدو تفصيل",
    "details.close": "بند",
    "details.sha": "SHA",
    "details.shortSha": "مختصر SHA",
    "details.message": "نياپو",
    "details.author": "ليکڪ",
    "details.date": "تاريخ",
    "details.labels": "ليبلز",
    "details.location": "مقام",
    "details.currentHead": "هيڊ - موجوده چيڪ آئوٽ",
    "settings.title": "سيٽنگون",
    "settings.language": "ٻولي",
    "settings.theme": "موضوع",
    "settings.themeLight": "روشني",
    "settings.themeDark": "اونداهو",
    "settings.themeLightHint": "هلڪو پس منظر، ڳاڙهو متن.",
    "settings.themeDarkHint": "ڪارو پس منظر، هلڪو متن.",
    "settings.mainBranch": "مکيه شاخ",
    "settings.mainBranchAuto": "خودڪار",
    "settings.mainBranchSearch": "شاخون ڳولھيو…",
    "settings.mainBranchNoMatch": "نه ملندڙ شاخون",
    "settings.display": "ڊسپلي انداز",
    "settings.displayModern": "جديد",
    "settings.displayClassic": "ڪلاسيڪل",
    "settings.displayModernHint": "مفت ڪينوس - ڇڪيو پين ڏانھن، اسڪرول ڪرڻ لاءِ زوم.",
    "settings.displayClassicHint": "فڪسڊ ڪئنوس — ٽرن پن ٿيل کاٻي، ڪو به زوم نه، صرف اسڪرول (جهڙوڪ SVN نظرثاني گراف).",
    "settings.sectionGeneral": "جنرل",
    "settings.sectionGraph": "گراف",
    "settings.sectionChanges": "منظر تبديل ڪري ٿو",
    "settings.diffMinimap": "فرق minimap",
    "settings.diffMinimapOn": "ڏيکاريل",
    "settings.diffMinimapOff": "لڪيل",
    "settings.diffMinimapOnHint": "ڏيکاريو هڪ VS ڪوڊ طرز جو جائزو واري پٽي مختلف جي ڀرسان - ڇڪيو ان کي سکرول ڪرڻ لاءِ؛ تبديلي جا نشان شامل آهن.",
    "settings.diffMinimapOffHint": "ڪابه نظرثاني واري پٽي - عام طور تي فرق کي اسڪرول ڪريو.",
    "settings.commitReview": "جائزو وٺو",
    "settings.commitReviewOn": "انجام ڏيڻ کان اڳ پڇو",
    "settings.commitReviewOff": "سڌو سنئون",
    "settings.commitReviewOnHint": "مقامي ڪمٽ ٺاهڻ کان پهريان حتمي فائل لسٽ ۽ پيغام چيڪ ڏيکاريو.",
    "settings.commitReviewOffHint": "ڪمٽ بٽڻ فوري طور تي مقامي ڪمٽ ٺاهي ٿو.",
    "settings.svnBranchDialog": "SVN طرز برانچ ڊائلاگ",
    "settings.svnBranchDialogHint": "هڪ فولڊر-وڻ چونڊيندڙ ڏيکاريو جڏهن هڪ شاخ ٺاهي (هڪ سادي پرامٽ جي بدران).",
    "settings.branchDialog": "برانچ ڊائلاگ",
    "settings.branchDialogSvnTitle": "SVN طرز",
    "settings.branchDialogNativeVscode": "VS ڪوڊ جو انداز",
    "settings.branchDialogNativeVs": "بصري اسٽوڊيو انداز",
    "settings.branchDialogNativeHint": "توهان جي IDE جي ٺاهيل برانچ پرامپٹ.",
    "settings.close": "بند",
    "settings.done": "ٿي ويو",
    "settings.sectionAdvanced": "ترقي يافته",
    "settings.gitSource": "Git قابل عمل",
    "settings.gitSourceBuiltin": "تعمير ٿيل",
    "settings.gitSourceBuiltinHint": "IDE جي بلٽ ان Git ايڪسٽينشن مان گٽ بائنري استعمال ڪريو.",
    "settings.gitSourceCustom": "مرضي مطابق رستو",
    "settings.gitPath": "گيٽ ڏانهن رستو",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "برائوز ڪريو…",
    "settings.jargon": "گيٽ جا شرط",
    "settings.jargonTranslate": "ترجمو",
    "settings.jargonEnglish": "انگريزي ۾ رکو",
    "settings.jargonTranslateHint": "Git جي اصطلاحن جو ترجمو ڪريو (Pull, Push, Commit, branch...) انٽرفيس جي ٻولي ۾.",
    "settings.jargonEnglishHint": "انگريزيءَ ۾ Git جا اصطلاح (pull, push, commit, branch…) رکو؛ ٻيو سڀ ڪجھ ترجمو ڪريو.",
    "settings.sectionMerge": "ملائڻ",
    "settings.mergeMode": "ملائڻ جو انداز",
    "settings.mergeModeMerge": "عام ميلاپ",
    "settings.mergeModeSquash": "اسڪواش جو ميلاپ",
    "settings.mergeModeMergeHint": "برانچ کي ضم ڪري ٿو جيئن ته: ان جا ڪمٽ موجوده برانچ جي تاريخ جو حصو بڻجي وڃن ٿا، ضم ڪرڻ واري ڪمٽ سان شامل ٿيو (يا جڏهن ممڪن هجي فاسٽ فارورڊ).",
    "settings.mergeModeSquashHint": "موجوده برانچ تي سڄي شاخ کي ھڪڙي ڪمٽ ۾ ختم ڪري ٿو (گٽ ضم - اسڪواش). برانچ جا پنهنجا ڪمٽ ڪڏهن به موجوده برانچ جي تاريخ ۾ داخل نٿا ٿين - توهان اتي صرف هڪ ڪمٽ ڏسو. شاخ پاڻ اڻڄاتل رهجي وئي آهي، تنهنڪري ان جي ڪمن کي پنهنجي لين ۾ نظر اچي ٿو.",
    "settings.mergedView": "گراف ۾ ضم ٿيل ڪمنٽس",
    "settings.mergedViewBranch": "صرف پنهنجي برانچ تي",
    "settings.mergedViewTarget": "برانچ ۾ پڻ ضم ٿي ويا",
    "settings.mergedViewBranchHint": "ضم ٿيل برانچ جا ڪمٽ صرف انهن جي پنهنجي لين ۾ ٺهيل آهن؛ ضم هڪ ڪنيڪٽر جي طور تي ظاهر ٿئي ٿو. کلاسي نظارو.",
    "settings.mergedViewTargetHint": "انضمام ۾ شامل ڪيل ڪمٽس وصول ڪندڙ برانچ جي لين ۾ پڻ ٺاهيا ويا آهن، ضم ڪرڻ جي ڪمٽ هيٺ اسٽيڪ ٿيل آهن ۽ اصل سان سطح - ڇاڪاڻ ته اها شاخ واقعي انهن تي مشتمل آهي. ڪاپيون پيلا آهن ۽ انهن شاخن سان مٿاهون آهن جن تي اهي لکيل هئا، تنهنڪري اهو واضح رهي ٿو ته اهي اتي نه ٺاهيا ويا آهن. اسڪواش جا ضمير متاثر نه ٿيندا آهن: هڪ اسڪواش هڪ عام ڪمٽ لکي ٿو، تنهنڪري صرف اهو ڪم ڪڏهن ظاهر ٿئي ٿو.",
    "legend.title": "ڏند ڪٿا",
    "legend.head": "هيڊ / موجوده شاخ",
    "legend.local": "مقامي شاخ",
    "legend.remote": "دور دراز شاخ",
    "legend.remoteOnly": "صرف بادل ۾ (نه ڇڪيو)",
    "legend.tag": "ٽيگ (نسخ)",
    "legend.commit": "انجام",
    "legend.stash": "ڍڪيل ڪم",
    "legend.mergedIn": "ضم سان اندر آيو (ٻي شاخ تي لکيل)",
    "legend.nodes": "باڪس",
    "legend.lines": "لڪيرون",
    "legend.edgeParent": "ساڳئي لڪير تي اڳوڻو واعدو",
    "legend.edgeMerge": "ضم ٿيل برانچ - ضم ڪرڻ واري ڪم تي تير پوائنٽ",
    "legend.edgeBranch": "هتي هڪ شاخ ٽٽي وئي",
    "legend.edgeStash": "اسٽش ۽ واعدو ان کي محفوظ ڪيو ويو",
    "legend.edgeMergedChain": "هن شاخ جي لڪير ۾ ضم ٿيل ڪمن ذريعي هلندو آهي",
    "legend.edgeMergedTie": "اهو ساڳيو عهدو جنهن شاخ تي لکيل هو",
    "menu.jumpHead": "⌖ چيڪ آئوٽ ڏانھن وڃو",
    "menu.resetView": "⤢ ڏيک ري سيٽ ڪريو",
    "menu.createBranch": "ھتان برانچ ٺاھيو...",
    "menu.checkout": "هن واعدي کي چيڪ ڪريو",
    "menu.copySha": "SHA کي نقل ڪريو",
    "menu.pushBranch": "پش برانچ \"{name}\"",
    "menu.renameCommit": "ڪمٽٽ پيغام جو نالو تبديل ڪريو...",
    "menu.undoCommit": "انجام کي واپس ڪريو (تبديليون رکو)...",
    "menu.viewChanges": "تبديليون ڏسو...",
    "menu.mergeBranch": "\"{source}\" کي \"{target}\" ۾ ضم ڪريو",
    "menu.jumpToOriginal": "اصل ڪميٽ ڏانھن وڃو",
    "node.mergedInTooltip": "ضم ڪريو {merge} هن واعدي کي \"{target}\" ۾ آندو ۽ اهو \"{origin}\" تي لکيل هو.",
    "changes.title": "{sha} ۾ تبديليون",
    "changes.tabChanged": "تبديل ٿيل",
    "changes.tabAll": "سڀ فائلون",
    "changes.added": "شامل ڪيو ويو",
    "changes.modified": "تبديل ٿيل",
    "changes.deleted": "حذف ٿيل",
    "changes.renamed": "نالو مٽايو",
    "changes.noChanges": "هي عهدو ڪا به فائل تبديل نٿو ڪري.",
    "changes.loading": "فرق لوڊ ٿي رهيو آهي…",
    "changes.selectFile": "ان جي تبديلين کي ڏسڻ لاء فائل چونڊيو.",
    "changes.original": "اصل",
    "changes.changed": "هي عزم",
    "changes.binary": "بائنري فائل - ڏيکارڻ لاءِ متن ۾ ڪوبه فرق ناهي.",
    "changes.tooLarge": "فائل تمام وڏي آھي فرق ڪرڻ لاءِ.",
    "changes.renamedFrom": "{path} کان نالو تبديل ڪيو ويو",
    "changes.close": "بند",
    "changes.prevChange": "اڳوڻي تبديلي",
    "changes.nextChange": "ايندڙ تبديلي",
    "changes.collapseAll": "سڀ فولڊر کوليو",
    "changes.expandAll": "سڀني فولڊر کي وڌايو",
    "changes.searchPlaceholder": "فائلون ڳولھيو…",
    "changes.noSearchResults": "ڪابه فائل توهان جي ڳولا سان نه ملندي آهي.",
    "changes.maximize": "وڌ ۾ وڌ ڪرڻ",
    "changes.restore": "سائز بحال ڪريو",
    "find.open": "ھن فرق ۾ ڳولھيو (Ctrl+F)",
    "find.placeholder": "فرق ۾ ڳولهيو…",
    "find.prev": "پويون ميچ",
    "find.next": "ايندڙ ميچ",
    "find.close": "ڳولها بند ڪريو",
    "find.noResults": "ڪي به نتيجا نه",
    "commit.title": "تبديلين جو عزم",
    "commit.close": "بند",
    "commit.loading": "ڪم ڪندڙ وڻ پڙهڻ…",
    "commit.noChanges": "انجام ڏيڻ لاءِ ڪي به مقامي تبديليون نه آهن.",
    "commit.selectFile": "ان جي اختلاف کي ڏسڻ لاء هڪ فائل چونڊيو.",
    "commit.selectAll": "سڀ چونڊيو",
    "commit.selectNone": "سڀ صاف ڪريو",
    "commit.message": "پيغام موڪليو",
    "commit.messagePlaceholder": "تبديلي جي وضاحت ڪريو",
    "commit.selectedCount": "{count} فائل چونڊيو ويو",
    "commit.review": "جائزو",
    "commit.reviewTitle": "انجام ڏيڻ کان اڳ حتمي چيڪ",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "مقامي عزم ٺاهيو",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "پاپ اسٽش (لاڳو ۽ هٽايو)",
    "menu.stashDrop": "لڪايو...",
    "merge.title": "ضم شاخ",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "انضمام جو تجزيو ڪيو پيو وڃي...",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "{files} فائلن کي تبديل ڪيو ويو، {conflicts} تڪرار",
    "merge.conflictsWarning": "ھن ضم ۾ تضاد آھن - توھان اڃا تائين ضم ڪري سگھو ٿا، پوءِ انھن کي ايڊيٽر ۾ حل ڪريو.",
    "merge.noChanges": "ڪابه فائل تبديلي نه آهي.",
    "merge.files": "نتيجي ۾ تبديليون",
    "merge.added": "شامل ڪيو ويو",
    "merge.modified": "تبديل ٿيل",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "(جڙ)",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  fa: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "نمودار",
    "settings.sectionChanges": "نما را تغییر می دهد",
    "settings.diffMinimap": "مینیمپ تفاوت",
    "settings.diffMinimapOn": "نشان داده شده است",
    "settings.diffMinimapOff": "پنهان شده است",
    "settings.diffMinimapOnHint": "نمایش یک نوار نمای کلی به سبک کد VS در کنار تفاوت - آن را بکشید تا اسکرول شود. نشانگرهای تغییر گنجانده شده است.",
    "settings.diffMinimapOffHint": "بدون نوار نمای کلی - تفاوت را به طور معمول پیمایش کنید.",
    "settings.commitReview": "انجام بررسی",
    "settings.commitReviewOn": "قبل از تعهد بپرسید",
    "settings.commitReviewOff": "مستقیماً متعهد شوید",
    "settings.commitReviewOnHint": "قبل از ایجاد commit محلی، لیست فایل نهایی و بررسی پیام را نشان دهید.",
    "settings.commitReviewOffHint": "دکمه Commit بلافاصله commit محلی را ایجاد می کند.",
    "settings.svnBranchDialog": "گفتگوی شاخه ای به سبک SVN",
    "settings.svnBranchDialogHint": "هنگام ایجاد یک شاخه (به جای یک اعلان ساده) انتخابگر درخت پوشه را نشان دهید.",
    "settings.branchDialog": "گفتگوی شعبه",
    "settings.branchDialogSvnTitle": "به سبک SVN",
    "settings.branchDialogNativeVscode": "سبک کد VS",
    "settings.branchDialogNativeVs": "سبک ویژوال استودیو",
    "settings.branchDialogNativeHint": "درخواست شعبه داخلی IDE شما.",
    "settings.close": "بستن",
    "settings.done": "انجام شد",
    "settings.sectionAdvanced": "پیشرفته",
    "settings.gitSource": "Git قابل اجرا",
    "settings.gitSourceBuiltin": "ساخته شده در",
    "settings.gitSourceBuiltinHint": "از باینری git از پسوند داخلی Git IDE استفاده کنید.",
    "settings.gitSourceCustom": "مسیر سفارشی",
    "settings.gitPath": "مسیر git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "مرور…",
    "settings.jargon": "شرایط Git",
    "settings.jargonTranslate": "ترجمه کنید",
    "settings.jargonEnglish": "به زبان انگلیسی نگه دارید",
    "settings.jargonTranslateHint": "اصطلاحات Git (کشش، فشار، متعهد، شاخه…) را به زبان رابط ترجمه کنید.",
    "settings.jargonEnglishHint": "اصطلاحات Git (کشش، فشار، متعهد، شاخه ...) را به زبان انگلیسی حفظ کنید. همه چیزهای دیگر را ترجمه کنید",
    "settings.sectionMerge": "ادغام",
    "settings.mergeMode": "ادغام سبک",
    "settings.mergeModeMerge": "ادغام عادی",
    "settings.mergeModeSquash": "ادغام کدو حلوایی",
    "settings.mergeModeMergeHint": "شعبه را همانطور که هست ادغام می کند: تعهدات آن بخشی از تاریخچه شاخه فعلی می شود که با یک تعهد ادغام (یا در صورت امکان فوروارد سریع) به آن می پیوندد.",
    "settings.mergeModeSquashHint": "کل شاخه را به یک commit در شاخه فعلی جمع می کند (git merge --squash). commit های خود شعبه هرگز وارد تاریخچه شعبه فعلی نمی شوند - فقط یک commit را در آنجا می بینید. خود این شاخه دست نخورده باقی مانده است، بنابراین تعهدات آن در خط خودش قابل مشاهده است.",
    "settings.mergedView": "ادغام تعهدات در نمودار",
    "settings.mergedViewBranch": "فقط در شعبه خودشان",
    "settings.mergedViewTarget": "همچنین در شعبه آنها ادغام شدند",
    "settings.mergedViewBranchHint": "تعهدات یک شعبه ادغام شده فقط در خط خودشان ترسیم می شوند. ادغام به عنوان یک رابط نشان داده می شود. نمای کلاسیک",
    "settings.mergedViewTargetHint": "تعهدات ادغام وارد شده نیز در خط شعبه دریافت کننده ترسیم می شوند، در زیر commit ادغام قرار می گیرند و در سطح اصلی قرار می گیرند - زیرا آن شاخه واقعاً اکنون آنها را شامل می شود. کپی ها رنگ پریده هستند و با شاخه ای که روی آن نوشته شده است، قرار دارند، بنابراین واضح است که در آنجا ساخته نشده اند. ادغام اسکواش بی تاثیر است: یک اسکواش یک کامیت معمولی می نویسد، بنابراین فقط آن کامیت ظاهر می شود.",
    "legend.title": "افسانه",
    "legend.head": "HEAD / شاخه فعلی",
    "legend.local": "شعبه محلی",
    "legend.remote": "شعبه از راه دور",
    "legend.remoteOnly": "فقط در ابر (کشیده نشده)",
    "legend.tag": "برچسب (نسخه)",
    "legend.commit": "متعهد شوید",
    "legend.stash": "انبار (کار قفسه بندی شده)",
    "legend.mergedIn": "با ادغام وارد شد (نوشته شده در شاخه دیگری)",
    "legend.nodes": "جعبه ها",
    "legend.lines": "خطوط",
    "legend.edgeParent": "ارتکاب قبلی در همان خط",
    "legend.edgeMerge": "شاخه ادغام شده - فلش به سمت تعهد ادغام اشاره می کند",
    "legend.edgeBranch": "یک شاخه از اینجا جدا شد",
    "legend.edgeStash": "Stash و تعهدی که در قفسه گذاشته شده بود",
    "legend.edgeMergedChain": "خط این شاخه که از طریق commit های ادغام شده می گذرد",
    "legend.edgeMergedTie": "همان commit روی شاخه ای که روی آن نوشته شده بود",
    "menu.jumpHead": "⌖ به تسویه حساب بروید",
    "menu.resetView": "⤢ بازنشانی نمای",
    "menu.createBranch": "ایجاد شعبه از اینجا…",
    "menu.checkout": "این تعهد را بررسی کنید",
    "menu.copySha": "commit SHA را کپی کنید",
    "menu.pushBranch": "شاخه فشاری \"{name}\"",
    "menu.deleteBranch": "شاخه \"{name}\" را حذف کنید",
    "menu.renameCommit": "تغییر نام پیام commit…",
    "menu.undoCommit": "لغو تعهد (حفظ تغییرات)…",
    "menu.viewChanges": "مشاهده تغییرات…",
    "menu.mergeBranch": "\"{source}\" را در \"{target}\" ادغام کنید",
    "menu.jumpToOriginal": "به کامیت اصلی بروید",
    "node.mergedInTooltip": "ادغام {merge} این commit را به \"{target}\" آورد و روی \"{origin}\" نوشته شده بود.",
    "changes.title": "تغییرات در {sha}",
    "changes.tabChanged": "تغییر کرد",
    "changes.tabAll": "همه فایل ها",
    "changes.added": "اضافه شد",
    "changes.modified": "اصلاح شد",
    "changes.deleted": "حذف شد",
    "changes.renamed": "تغییر نام داد",
    "changes.noChanges": "این commit هیچ فایلی را تغییر نمی دهد.",
    "changes.loading": "در حال بارگیری تفاوت…",
    "changes.selectFile": "یک فایل را انتخاب کنید تا تغییرات آن را ببینید.",
    "changes.original": "اصلی",
    "changes.changed": "این متعهد شدن",
    "changes.binary": "فایل باینری - هیچ تفاوت متنی برای نمایش وجود ندارد.",
    "changes.tooLarge": "فایل بیش از حد بزرگ است که نمی تواند متفاوت باشد.",
    "changes.renamedFrom": "تغییر نام از {path}",
    "changes.close": "بستن",
    "changes.prevChange": "تغییر قبلی",
    "changes.nextChange": "تغییر بعدی",
    "changes.collapseAll": "همه پوشه ها را جمع کنید",
    "changes.expandAll": "همه پوشه ها را باز کنید",
    "changes.searchPlaceholder": "جستجوی فایل ها…",
    "changes.noSearchResults": "هیچ فایلی با جستجوی شما مطابقت ندارد.",
    "changes.maximize": "به حداکثر رساندن",
    "changes.restore": "بازیابی اندازه",
    "find.open": "جستجو در این تفاوت (Ctrl+F)",
    "find.placeholder": "یافتن در تفاوت…",
    "find.prev": "مسابقه قبلی",
    "find.next": "مسابقه بعدی",
    "find.close": "جستجو را ببندید",
    "find.noResults": "هیچ نتیجه ای وجود ندارد",
    "commit.title": "انجام تغییرات",
    "commit.close": "بستن",
    "commit.loading": "خواندن درخت کار…",
    "commit.noChanges": "هیچ تغییر محلی برای انجام وجود ندارد.",
    "commit.selectFile": "یک فایل را انتخاب کنید تا تفاوت آن را ببینید.",
    "commit.selectAll": "همه را انتخاب کنید",
    "commit.selectNone": "پاک کردن همه",
    "commit.message": "پیام متعهد",
    "commit.messagePlaceholder": "تغییر را شرح دهید",
    "commit.selectedCount": "فایل(های) {count} انتخاب شد",
    "commit.review": "بررسی کنید",
    "commit.reviewTitle": "بررسی نهایی قبل از انجام",
    "commit.reviewSummary": "فایل(های) {count} را با پیام: {message} انجام دهید",
    "commit.back": "برگشت",
    "commit.commit": "ایجاد commit محلی",
    "menu.stashApply": "پنهان کاری را اعمال کنید",
    "menu.stashPop": "ذخیره سازی پاپ (اعمال و حذف)",
    "menu.stashDrop": "ذخیره سازی…",
    "merge.title": "ادغام شعبه",
    "merge.route": "ادغام به شاخه فعلی",
    "merge.source": "از (منبع)",
    "merge.target": "به (جاری)",
    "merge.loading": "در حال تجزیه و تحلیل ادغام…",
    "merge.upToDate": "در حال حاضر به روز - چیزی برای ادغام نیست.",
    "merge.fastForward": "سریع به جلو - بدون نیاز به ادغام commit (برای اجبار، \"Create a merge commit\" را علامت بزنید).",
    "merge.summary": "فایل(های) {files} تغییر کرد، تداخل(های) {conflicts}.",
    "merge.conflictsWarning": "این ادغام دارای تضادهایی است - همچنان می توانید ادغام کنید، سپس آنها را در ویرایشگر حل کنید.",
    "merge.noChanges": "بدون تغییر فایل",
    "merge.files": "تغییرات حاصله",
    "merge.added": "اضافه شد",
    "merge.modified": "اصلاح شد",
    "merge.deleted": "حذف شد",
    "merge.conflict": "درگیری",
    "merge.message": "پیام ادغام commit",
    "merge.messagePlaceholder": "ادغام شاخه \"منبع\"",
    "merge.messageFfHint": "در یک ادغام سریع به جلو نادیده گرفته می شود (هیچ تعهدی ایجاد نمی شود).",
    "merge.noFastForward": "ایجاد یک تعهد ادغام (بدون فوروارد سریع)",
    "merge.noFastForwardHint": "همیشه یک تعهد ادغام را ضبط کنید، حتی زمانی که شعبه می تواند سریع به جلو حرکت کند.",
    "merge.merge": "ادغام",
    "merge.cancel": "لغو کنید",
    "merge.previewError": "پیش‌نمایش محاسبه نشد: {message}",
    "merge.squashBadge": "کدو حلوایی",
    "merge.squashNote": "ادغام اسکواش - هر تغییری با ارتکاب ONE در \"{target}\" انجام می شود. تعهدات خود \"{source}\" از تاریخچه خود خارج می شود. در تنظیمات › ادغام به عقب برگردید.",
    "merge.messageSquash": "پیام متعهد",
    "merge.squashMessagePlaceholder": "تغییرات له شده از \"منبع\"",
    "newBranch.title": "ایجاد شعبه",
    "newBranch.startPoint": "شعبه جدید از {sha} شروع می شود",
    "newBranch.startPointOn": "در {refs}",
    "newBranch.location": "مکان",
    "newBranch.locationRoot": "(ریشه)",
    "newBranch.expandAll": "همه را گسترش دهید",
    "newBranch.collapseAll": "همه را جمع کن",
    "newBranch.expandFolder": "پوشه را باز کنید",
    "newBranch.collapseFolder": "جمع کردن پوشه",
    "newBranch.name": "نام شعبه",
    "newBranch.namePlaceholder": "شعبه من",
    "newBranch.fullName": "نام کامل: {name}",
    "newBranch.invalid": "نام شعبه نامعتبر است",
    "newBranch.exists": "شعبه ای با این نام از قبل وجود دارد",
    "newBranch.checkout": "پس از ایجاد، شعبه را پرداخت کنید",
    "newBranch.create": "ایجاد کنید",
    "newBranch.cancel": "لغو کنید",
    "status.loading": "در حال بارگیری نمودار…",
    "status.summary": "{repo} نمایش {commits} commits، {refs} refs",
    "status.branchCreated": "شعبه \"{name}\" را در {sha} ایجاد کرد",
    "status.error": "خطا: {message}",
    "status.opFailed": "عملیات شکست خورد",
    "status.fetching": "در حال واکشی…",
    "status.pulling": "کشیدن…",
    "status.pushing": "هل دادن…",
    "status.committing": "ایجاد تعهد محلی…",
    "status.commitCreated": "ایجاد commit محلی {sha}: {message}",
    "status.syncing": "در حال همگام سازی…",
    "status.noHead": "هیچ تسویه حساب فعلی در نمودار یافت نشد.",
    "status.undoing": "در حال لغو تعهد…",
    "status.commitUndone": "تعهد لغو شد - تغییرات در درخت کار برگشته است.",
    "status.undoConflict": "لغو تضادهای ضربه - آنها را در ویرایشگر حل کنید، سپس ادامه دهید.",
    "status.stashApplying": "در حال اعمال مخفی کاری…",
    "status.stashPopping": "مخفی کردن…",
    "status.stashDropping": "انداختن انبار…",
    "status.stashApplied": "Stash اعمال شد.",
    "status.stashPopped": "مخفیگاه ظاهر شد.",
    "status.stashDropped": "استاش سقوط کرد.",
    "status.stashConflict": "تضادهای ذخیره - آنها را در ویرایشگر حل کنید.",
    "status.merging": "ادغام…",
    "status.merged": "ادغام کامل شد.",
    "status.mergeConflict": "ادغام تداخل دارد - آنها را در ویرایشگر حل کنید، سپس commit کنید.",
    "search.title": "جستجو کنید",
    "search.tooltip": "جستجو متعهد می شود",
    "search.placeholder": "جستجو متعهد می شود…",
    "search.previous": "نتیجه قبلی",
    "search.next": "نتیجه بعدی",
    "search.noResults": "هیچ نتیجه ای وجود ندارد",
    "search.resultCount": "{index}/{count}",
    "search.filter": "حالت فیلتر",
    "search.highlight": "حالت برجسته",
    "search.filterMode": "صفحه نمایش جستجو",
    "search.filterModeFilter": "فیلتر (پنهان کردن موارد غیر منطبق)",
    "search.filterModeHighlight": "برجسته (نمایش همه، علامت‌گذاری موارد منطبق)",
    "search.filterModeHint": "نحوه نمایش نتایج جستجو در نمودار را انتخاب کنید.",
    "search.visibility": "جستجوی مکان",
    "search.visibilityToolbar": "دکمه نوار ابزار",
    "search.visibilityAlwaysVisible": "نوار همیشه قابل مشاهده",
    "search.visibilityHint": "جستجو را به عنوان نماد نوار ابزار یا نوار دائمی در بالا نشان دهید.",
    "footer.github": "GitHub",
  },
  ps: {
    "toolbar.refresh": "⟳ تازه کول",
    "toolbar.fetch": "⤓ راوړل",
    "toolbar.pull": "⇩ کش کړئ",
    "toolbar.push": "⇧ فشار",
    "toolbar.commit": "✓ ژمنه",
    "toolbar.sync": "⇅ همغږي",
    "toolbar.jumpHead": "⌖ چیک آوټ ته لاړشئ",
    "toolbar.reset": "⤢ لید بیا تنظیم کړئ",
    "toolbar.settings": "⚙ ترتیبات",
    "details.header": "د ژمنې توضیحات",
    "details.close": "تړل",
    "details.sha": "SHA",
    "details.shortSha": "لنډ SHA",
    "details.message": "پیغام",
    "details.author": "لیکوال",
    "details.date": "نیټه",
    "details.labels": "لیبلونه",
    "details.location": "ځای",
    "details.currentHead": "HEAD - اوسنی چیک آوټ",
    "settings.title": "ترتیبات",
    "settings.language": "ژبه",
    "settings.theme": "موضوع",
    "settings.themeLight": "رڼا",
    "settings.themeDark": "تیاره",
    "settings.themeLightHint": "روښانه پس منظر، تیاره متن.",
    "settings.themeDarkHint": "تیاره پس منظر، روښانه متن.",
    "settings.mainBranch": "اصلي څانګه",
    "settings.mainBranchAuto": "اتوماتیک",
    "settings.mainBranchSearch": "د څانګو لټون…",
    "settings.mainBranchNoMatch": "نه ورته والی ښاخونه",
    "settings.display": "د نندارې سټایل",
    "settings.displayModern": "عصري",
    "settings.displayClassic": "کلاسیک",
    "settings.displayModernHint": "وړیا کینوس - پین ته کش کړئ، زوم ته سکرول کړئ.",
    "settings.displayClassicHint": "ثابت کینوس - ډنډ چپه شوی، هیڅ زوم نشته، یوازې سکرول (لکه د SVN بیاکتنې ګراف).",
    "settings.sectionGeneral": "جنرال",
    "settings.sectionGraph": "ګراف",
    "settings.sectionChanges": "لید بدلوي",
    "settings.diffMinimap": "توپیر minimap",
    "settings.diffMinimapOn": "ښودل شوي",
    "settings.diffMinimapOff": "پټ",
    "settings.diffMinimapOnHint": "د توپیر تر څنګ د VS کوډ سټایل عمومي لید پټه وښایاست - د سکرول کولو لپاره یې کش کړئ؛ د بدلون مارکر شامل دي.",
    "settings.diffMinimapOffHint": "د کتنې پټه نشته - توپیر په نورمال ډول سکرول کړئ.",
    "settings.commitReview": "د بیاکتنې ژمنه وکړئ",
    "settings.commitReviewOn": "د ژمنې دمخه پوښتنه وکړئ",
    "settings.commitReviewOff": "په مستقیم ډول ژمنه وکړئ",
    "settings.commitReviewOnHint": "د ځایی ژمنې رامینځته کولو دمخه د وروستي فایل لیست او پیغام چیک وښایاست.",
    "settings.commitReviewOffHint": "د ژمنې تڼۍ سمدلاسه محلي ژمنې رامینځته کوي.",
    "settings.svnBranchDialog": "د SVN سټایل څانګې ډیالوګ",
    "settings.svnBranchDialogHint": "کله چې څانګه جوړه کړئ د فولډر ونې غوره کونکي وښایاست (د ساده پرامپ پرځای).",
    "settings.branchDialog": "د څانګې ډیالوګ",
    "settings.branchDialogSvnTitle": "SVN سټایل",
    "settings.branchDialogNativeVscode": "د VS کوډ سټایل",
    "settings.branchDialogNativeVs": "د بصری سټوډیو سټایل",
    "settings.branchDialogNativeHint": "ستاسو د IDE جوړ شوی برانچ پرامپټ.",
    "settings.close": "تړل",
    "settings.done": "ترسره شو",
    "settings.sectionAdvanced": "پرمختللی",
    "settings.gitSource": "Git د اجرا وړ",
    "settings.gitSourceBuiltin": "جوړ شوی",
    "settings.gitSourceBuiltinHint": "د IDE جوړ شوي Git توسیع څخه git بائنری وکاروئ.",
    "settings.gitSourceCustom": "دودیزه لاره",
    "settings.gitPath": "ګیټ ته لاره",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "لټون…",
    "settings.jargon": "د ګیټ شرایط",
    "settings.jargonTranslate": "ژباړه",
    "settings.jargonEnglish": "په انګلیسي کې وساتئ",
    "settings.jargonTranslateHint": "د ګیټ اصطلاحات د انٹرفیس ژبې ته وژباړئ (پول، پش، ژمن، څانګه…)",
    "settings.jargonEnglishHint": "د Git اصطلاحات په انګلیسي کې وساتئ (pll, push, commit, branch…) نور هر څه وژباړي.",
    "settings.sectionMerge": "یوځای کول",
    "settings.mergeMode": "سټایل یوځای کړئ",
    "settings.mergeModeMerge": "عادي یوځای کول",
    "settings.mergeModeSquash": "د اسکواش یوځای کول",
    "settings.mergeModeMergeHint": "څانګه ضم کوي لکه څنګه چې دا ده: د دې ژمنې د اوسني څانګې تاریخ برخه کیږي ، د ادغام ژمنې سره یوځای کیږي (یا د امکان په صورت کې ګړندي وړاندې کیږي).",
    "settings.mergeModeSquashHint": "ټوله څانګه په اوسني څانګه کې په یوه واحد کمیټ کې سقوط کوي (git merge --squash). د څانګې خپلې ژمنې هیڅکله د اوسني څانګې تاریخ ته نه ننوځي - تاسو هلته یوازې یوه ژمنه ګورئ. څانګه پخپله پاتې ده، نو د هغې ژمنې په خپل لین کې لیدل کیږي.",
    "settings.mergedView": "په ګراف کې یوځای شوي ژمنې",
    "settings.mergedViewBranch": "یوازې په خپله څانګه کې",
    "settings.mergedViewTarget": "همدارنګه په څانګه کې دوی یوځای شوي",
    "settings.mergedViewBranchHint": "د یوځای شوي څانګې ژمنې یوازې د دوی په خپله لین کې ایستل کیږي؛ ضمیمه د نښلونکي په توګه ښکاره کیږي. کلاسیک لید.",
    "settings.mergedViewTargetHint": "هغه ژمنې چې راوړل شوي انضمام د ترلاسه کونکي څانګې په لین کې هم رسم شوي ، د ادغام ژمنې لاندې پټ شوي او د اصلي سره کچه - ځکه چې دا څانګه واقعیا اوس دوی لري. نقلونه سور دي او د هغه څانګې سره سر لري چې دوی پرې لیکل شوي ، نو دا روښانه پاتې کیږي چې دوی هلته ندي جوړ شوي. د سکواش ادغامونه بې اغیزې دي: سکواش یو عادي ژمنې لیکي، نو یوازې هغه ژمنې څرګندیږي.",
    "legend.title": "افسانه",
    "legend.head": "سر / اوسنی څانګه",
    "legend.local": "سیمه ایز څانګه",
    "legend.remote": "لیرې څانګه",
    "legend.remoteOnly": "یوازې په بادل کې (نه ایستل شوی)",
    "legend.tag": "ټاګ (نسخه)",
    "legend.commit": "ژمن",
    "legend.stash": "پټه (د سربند کار)",
    "legend.mergedIn": "د یوځای کیدو سره راغلی (په بله څانګه لیکل شوی)",
    "legend.nodes": "بکسونه",
    "legend.lines": "کرښې",
    "legend.edgeParent": "پخوانۍ ژمنه په ورته کرښه کې",
    "legend.edgeMerge": "یوځای شوی څانګه - د ادغام ژمنې کې تیر ټکي",
    "legend.edgeBranch": "دلته یوه څانګه بنده شوه",
    "legend.edgeStash": "سټیش او هغه ژمنې چې په کې ساتل شوې وې",
    "legend.edgeMergedChain": "د دې څانګې لاین د یوځای شوي کمیټ له لارې تیریږي",
    "legend.edgeMergedTie": "ورته ژمنه په هغه څانګه کې چې لیکل شوې وه",
    "menu.jumpHead": "⌖ چیک آوټ ته لاړشئ",
    "menu.resetView": "⤢ لید بیا تنظیم کړئ",
    "menu.createBranch": "له دې ځایه څانګه جوړه کړئ ...",
    "menu.checkout": "دا ژمنه وګورئ",
    "menu.copySha": "د ژمنې SHA کاپي کړئ",
    "menu.pushBranch": "پش څانګه \"{name}\"",
    "menu.deleteBranch": "څانګه \"{name}\" ړنګ کړئ",
    "menu.renameCommit": "د ژمنې پیغام نوم بدل کړئ…",
    "menu.undoCommit": "ژمنې بیرته راګرځول (بدلونونه ساتل)…",
    "menu.viewChanges": "بدلونونه وګورئ…",
    "menu.mergeBranch": "\"{source}\" په \"{target}\" سره ضم کړئ",
    "menu.jumpToOriginal": "اصلي ژمنې ته لاړ شئ",
    "node.mergedInTooltip": "ضم کړئ {merge} دا ژمنه په \"{target}\" کې راوړه - دا په \"{origin}\" لیکل شوې وه.",
    "changes.title": "په {sha} کې بدلونونه",
    "changes.tabChanged": "بدل شو",
    "changes.tabAll": "ټول فایلونه",
    "changes.added": "زیاته کړه",
    "changes.modified": "تعدیل شوی",
    "changes.deleted": "ړنګ شوی",
    "changes.renamed": "نوم بدل کړ",
    "changes.noChanges": "دا ژمنه هیڅ فایل نه بدلوي.",
    "changes.loading": "توپیر پورته کول…",
    "changes.selectFile": "د بدلونونو لیدلو لپاره یو فایل غوره کړئ.",
    "changes.original": "اصلي",
    "changes.changed": "دا ژمنه",
    "changes.binary": "بائنری فایل - د ښودلو لپاره هیڅ متن توپیر نلري.",
    "changes.tooLarge": "دوتنه د توپیر لپاره ډیره لویه ده.",
    "changes.renamedFrom": "له {path} څخه نوم بدل شوی",
    "changes.close": "تړل",
    "changes.prevChange": "پخوانی بدلون",
    "changes.nextChange": "راتلونکی بدلون",
    "changes.collapseAll": "ټول فولډرونه مات کړئ",
    "changes.expandAll": "ټول فولډر پراخ کړئ",
    "changes.searchPlaceholder": "د فایلونو لټون…",
    "changes.noSearchResults": "هیڅ فایل ستاسو د لټون سره سمون نلري.",
    "changes.maximize": "اعظمي کړئ",
    "changes.restore": "اندازه بیرته راګرځول",
    "find.open": "په دې توپیر کې لټون وکړئ (Ctrl+F)",
    "find.placeholder": "په توپیر کې ومومئ…",
    "find.prev": "پخوانۍ لوبه",
    "find.next": "بله لوبه",
    "find.close": "لټون بند کړئ",
    "find.noResults": "هیڅ پایلې نلري",
    "commit.title": "بدلونونه ژمن کړئ",
    "commit.close": "تړل",
    "commit.loading": "د کاري ونې لوستل…",
    "commit.noChanges": "د ژمنې لپاره هیڅ سیمه ایز بدلون شتون نلري.",
    "commit.selectFile": "د توپیر لیدلو لپاره یو فایل غوره کړئ.",
    "commit.selectAll": "ټول وټاکئ",
    "commit.selectNone": "ټول پاک کړئ",
    "commit.message": "د ژمنې پیغام",
    "commit.messagePlaceholder": "بدلون تشریح کړئ",
    "commit.selectedCount": "د {count} دوتنې غوره شوې",
    "commit.review": "بیاکتنه",
    "commit.reviewTitle": "د ژمنې دمخه وروستی چیک",
    "commit.reviewSummary": "د پیغام سره د {count} فایلونه ژمن کړئ: {message}",
    "commit.back": "شاته",
    "commit.commit": "محلي ژمنې رامینځته کړئ",
    "menu.stashApply": "سټیش پلي کړئ",
    "menu.stashPop": "پاپ سټیش (تطبیق او لرې کول)",
    "menu.stashDrop": "پټې پرې کړئ…",
    "merge.title": "د څانګې یوځای کول",
    "merge.route": "په اوسني څانګه کې یوځای کول",
    "merge.source": "له (سرچینې)",
    "merge.target": "دننه (اوسني)",
    "merge.loading": "ادغام تحلیل…",
    "merge.upToDate": "لا تر اوسه پورې - د یوځای کولو لپاره هیڅ شی نشته.",
    "merge.fastForward": "ګړندی - د ادغام ژمنې ته اړتیا نشته (د یو مجبورولو لپاره \"د انضمام ژمنې رامینځته کړئ\" ټیک کړئ).",
    "merge.summary": "د {files} فایلونه بدل شوي، د {conflicts} شخړه.",
    "merge.conflictsWarning": "دا انضمام شخړه لري - تاسو لاهم کولی شئ یوځای کړئ، بیا یې په مدیر کې حل کړئ.",
    "merge.noChanges": "هیڅ فایل بدلون نه کوي.",
    "merge.files": "په پایله کې بدلونونه",
    "merge.added": "زیاته کړه",
    "merge.modified": "تعدیل شوی",
    "merge.deleted": "ړنګ شوی",
    "merge.conflict": "شخړه",
    "merge.message": "د ژمنې پیغام سره یوځای کړئ",
    "merge.messagePlaceholder": "څانګه 'سرچینه' سره یوځای کړئ",
    "merge.messageFfHint": "په ګړندۍ ضم کولو کې سترګې پټې شوې (هیڅ ژمنه نه ده رامینځته شوې).",
    "merge.noFastForward": "د ادغام ژمنې رامینځته کړئ (چټک نه مخکې)",
    "merge.noFastForwardHint": "تل د ادغام ژمنې ثبت کړئ ، حتی کله چې څانګه ګړندی پرمخ وړي.",
    "merge.merge": "یوځای کول",
    "merge.cancel": "لغوه کړئ",
    "merge.previewError": "د مخکتنې محاسبه نشي کولی: {message}",
    "merge.squashBadge": "اسکواش",
    "merge.squashNote": "د سکواش ادغام - هر بدلون په \"{target}\" کې د یوې ژمنې په توګه راځي؛ د \"{source}\" خپل ژمنې د دې تاریخ څخه پاتې دي. په ترتیباتو کې بیرته وګرځئ> یوځای کړئ.",
    "merge.messageSquash": "د ژمنې پیغام",
    "merge.squashMessagePlaceholder": "د 'سرچینې' څخه غورځول شوي بدلونونه",
    "newBranch.title": "څانګه جوړه کړئ",
    "newBranch.startPoint": "نوې څانګه د {sha} څخه پیل کیږي",
    "newBranch.startPointOn": "په {refs} کې",
    "newBranch.location": "ځای",
    "newBranch.locationRoot": "(ريښه)",
    "newBranch.expandAll": "ټول پراخ کړئ",
    "newBranch.collapseAll": "ټول مات کړئ",
    "newBranch.expandFolder": "فولډر پراخ کړئ",
    "newBranch.collapseFolder": "فولډر مات کړئ",
    "newBranch.name": "د څانګې نوم",
    "newBranch.namePlaceholder": "زما څانګه",
    "newBranch.fullName": "بشپړ نوم: {name}",
    "newBranch.invalid": "د برانچ نوم",
    "newBranch.exists": "د دې نوم سره یوه څانګه لا دمخه شتون لري",
    "newBranch.checkout": "د جوړیدو وروسته څانګه چیک کړئ",
    "newBranch.create": "جوړ کړئ",
    "newBranch.cancel": "لغوه کړئ",
    "status.loading": "ګراف پورته کول…",
    "status.summary": "{repo} ښودل {commits} ژمنې کوي، {refs} refs",
    "status.branchCreated": "په {sha} کې \"{name}\" څانګه جوړه کړه",
    "status.error": "تېروتنه: {message}",
    "status.opFailed": "عملیات ناکام شول",
    "status.fetching": "راوړل…",
    "status.pulling": "ایستل…",
    "status.pushing": "فشار ورکول…",
    "status.committing": "د محلي ژمنې رامینځته کول…",
    "status.commitCreated": "ځایی کمیټ {sha} جوړ شوی: {message}",
    "status.syncing": "همغږي کول…",
    "status.noHead": "په ګراف کې اوسنی چیک آوټ ندی موندل شوی.",
    "status.undoing": "د ژمنې له منځه وړل…",
    "status.commitUndone": "ژمنې ناشونی - بدلونونه بیرته په کاري ونې کې راځي.",
    "status.undoConflict": "هټ شوي شخړې بیرته راګرځوئ - په مدیر کې یې حل کړئ، بیا دوام ورکړئ.",
    "status.stashApplying": "د سټیش پلي کول…",
    "status.stashPopping": "پټه خوله کول…",
    "status.stashDropping": "د پټیدو غورځول…",
    "status.stashApplied": "سټیش پلي شوی.",
    "status.stashPopped": "پټه خپره شوه.",
    "status.stashDropped": "پټه راښکته شوه.",
    "status.stashConflict": "د سټش شخړې - په مدیر کې یې حل کړئ.",
    "status.merging": "یوځای کول…",
    "status.merged": "یوځای کول بشپړ شول.",
    "status.mergeConflict": "یوځای کول شخړې لري - په مدیر کې یې حل کړئ، بیا ژمنه وکړئ.",
    "search.title": "لټون",
    "search.tooltip": "لټون ژمنې کوي",
    "search.placeholder": "لټون ژمنې…",
    "search.previous": "پخوانۍ پایله",
    "search.next": "بله پایله",
    "search.noResults": "هیڅ پایلې نلري",
    "search.resultCount": "{index}/{count}",
    "search.filter": "د فلټر حالت",
    "search.highlight": "د روښانه کولو حالت",
    "search.filterMode": "د لټون ښودنه",
    "search.filterModeFilter": "فلټر (غیر میچونه پټول)",
    "search.filterModeHighlight": "روښانه کړئ (ټول وښایاست، میچونه په نښه کړئ)",
    "search.filterModeHint": "غوره کړئ چې څنګه د لټون پایلې په ګراف کې ښودل کیږي.",
    "search.visibility": "د ځای لټون",
    "search.visibilityToolbar": "د وسیلې تڼۍ",
    "search.visibilityAlwaysVisible": "تل ښکاره بار",
    "search.visibilityHint": "لټون د وسیلې بار آیکون یا په سر کې دوامداره بار په توګه وښایاست.",
    "footer.github": "GitHub",
  },
  ckb: {
    "toolbar.refresh": "⟳ نوێکردنەوە",
    "toolbar.fetch": "⤓ فیتچ",
    "toolbar.pull": "⇩ ڕاکێشان",
    "toolbar.push": "⇧ پاڵدان",
    "toolbar.commit": "✓ پابەندبوون",
    "toolbar.sync": "⇅ هاوکاتکردن",
    "toolbar.jumpHead": "⌖ بڕۆ بۆ وەرگرتنی پارە",
    "toolbar.reset": "⤢ دیمەنی ڕێستکردنەوە",
    "toolbar.settings": "⚙ ڕێکخستنەکان",
    "details.header": "وردەکارییەکانی پابەندبوون",
    "details.close": "داخستن",
    "details.sha": "شا",
    "details.shortSha": "کورتە SHA",
    "details.message": "پەیام",
    "details.author": "نووسەر",
    "details.date": "ڕێکەوت",
    "details.labels": "لیبێڵەکان",
    "details.location": "شوێن",
    "details.currentHead": "HEAD — کۆتایی هاتنی ئێستا",
    "settings.title": "ڕێکخستنەکان",
    "settings.language": "زمان",
    "settings.theme": "مەبەستی سەرەکی",
    "settings.themeLight": "ڕووناکی",
    "settings.themeDark": "تاریک",
    "settings.themeLightHint": "پاشبنەمای ڕووناکی، دەقی تاریک.",
    "settings.themeDarkHint": "پاشبنەمای تاریک، دەقی سووک.",
    "settings.mainBranch": "لقی سەرەکی",
    "settings.mainBranchAuto": "ئۆتۆماتیکی",
    "settings.mainBranchSearch": "گەڕان بەدوای لقەکاندا...",
    "settings.mainBranchNoMatch": "هیچ لقێکی هاوتا نییە",
    "settings.display": "شێوازی پیشاندانی",
    "settings.displayModern": "مۆدێرن",
    "settings.displayClassic": "کلاسیک",
    "settings.displayModernHint": "کانڤاسی ئازاد — ڕاکێشان بۆ پان، سکڕۆڵ بۆ گەورەکردن.",
    "settings.displayClassicHint": "کانڤاسی جێگیر — چەقۆکە بە چەپەوە پێن کراوە، هیچ زوومێک نییە، تەنها سکڕۆڵ بکە (وەک گرافیکی پێداچوونەوەی SVN).",
    "settings.sectionGeneral": "گشتی",
    "settings.sectionGraph": "گرافیک",
    "settings.sectionChanges": "دیدگا دەگۆڕێت",
    "settings.diffMinimap": "کەمترین نەخشەی جیاوازی",
    "settings.diffMinimapOn": "نیشان دراوە",
    "settings.diffMinimapOff": "شاراوە",
    "settings.diffMinimapOnHint": "شریتێکی تێڕوانینی گشتی VS Code-style لە تەنیشت جیاوازییەکە پیشان بدە — بیکێشە بۆ ڕۆشتن؛ نیشاندەری گۆڕانکاری لەخۆدەگرێت.",
    "settings.diffMinimapOffHint": "هیچ شریتێکی تێڕوانینێکی گشتی نییە — جیاوازییەکە بە شێوەیەکی ئاسایی بچۆ.",
    "settings.commitReview": "پێداچوونەوە بە پابەندبوون",
    "settings.commitReviewOn": "پێش پابەندبوون پرسیار بکە",
    "settings.commitReviewOff": "ڕاستەوخۆ پابەندبە",
    "settings.commitReviewOnHint": "لیستی کۆتایی پەڕگەکان و پشکنینی پەیامەکان پیشان بدە پێش دروستکردنی پابەندبوونی ناوخۆیی.",
    "settings.commitReviewOffHint": "دوگمەی Commit دەستبەجێ commit ی ناوخۆیی دروست دەکات.",
    "settings.svnBranchDialog": "دیالۆگی لقەکانی شێوازی SVN",
    "settings.svnBranchDialogHint": "لە کاتی دروستکردنی لقێکدا هەڵگری فۆڵدەر-دار پیشان بدە (لەبری ئەوەی ئاماژەیەکی سادە).",
    "settings.branchDialog": "دیالۆگی لق",
    "settings.branchDialogSvnTitle": "بە شێوازی SVN",
    "settings.branchDialogNativeVscode": "شێوازی VS Code",
    "settings.branchDialogNativeVs": "شێوازی ڤیژوئال ستۆدیۆ",
    "settings.branchDialogNativeHint": "پرۆمپتی لقە بنیات نراوەکەی IDE ی تۆ.",
    "settings.close": "داخستن",
    "settings.done": "تەواو",
    "settings.sectionAdvanced": "پێشکەوتوو",
    "settings.gitSource": "Git جێبەجێ دەکرێت",
    "settings.gitSourceBuiltin": "بنیات نراوە",
    "settings.gitSourceBuiltinHint": "دووانەی git لە درێژکراوەی Git ی ناوەکی IDE بەکاربهێنە.",
    "settings.gitSourceCustom": "ڕێڕەوی تایبەتمەند",
    "settings.gitPath": "ڕێگا بۆ git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "گەڕان بە...",
    "settings.jargon": "زاراوەی گیت",
    "settings.jargonTranslate": "وەرگێڕان",
    "settings.jargonEnglish": "بە زمانی ئینگلیزی بیهێڵەرەوە",
    "settings.jargonTranslateHint": "وەرگێڕانی زاراوەی Git (ڕاکێشان، پاڵدان، پابەندبوون، لق...) بۆ زمانی ڕووکارەکە.",
    "settings.jargonEnglishHint": "زاراوەی Git (ڕاکێشان، پاڵدان، پابەندبوون، لق...) بە زمانی ئینگلیزی بهێڵەرەوە؛ هەموو شتێکی تر وەربگێڕن.",
    "settings.sectionMerge": "تێکەڵکردن",
    "settings.mergeMode": "شێوازی تێکەڵکردن",
    "settings.mergeModeMerge": "یەکگرتنی ئاسایی",
    "settings.mergeModeSquash": "یەکگرتنی سکواش",
    "settings.mergeModeMergeHint": "لقەکە وەک خۆی تێکەڵ دەکات: کۆمیتەکانی دەبنە بەشێک لە مێژووی لقی ئێستا، بە کۆمیتێکی تێکەڵاو (یان خێرا بۆ پێشەوە کاتێک کە دەتوانرێت).",
    "settings.mergeModeSquashHint": "تەواوی لقەکە دەڕووخێنێت بۆ یەک کۆمیت لەسەر لقی ئێستا (git merge --squash). کۆمیتەکانی لقەکە خۆیان هەرگیز ناچنە ناو مێژووی لقی ئێستا — تەنها یەک کۆمیت لەوێ دەبینیت. لقەکە خۆی بەبێ دەست لێدان هێڵراوەتەوە، بۆیە کۆمیتەکانی لە هێڵی خۆیدا بە دیار دەمێننەوە.",
    "settings.mergedView": "کۆمیتەکانی تێکەڵکراو لە گرافەکەدا",
    "settings.mergedViewBranch": "تەنها لەسەر لقی خۆیان",
    "settings.mergedViewTarget": "هەروەها لەو لقەی کە تێکەڵ بوون",
    "settings.mergedViewBranchHint": "کۆمیتەکانی لقێکی تێکەڵکراو تەنها لە هێڵی خۆیاندا دەکێشرێن؛ یەکگرتنەکە وەک پەیوەستکەرێک دەردەکەوێت. ڕوانگەی کلاسیک.",
    "settings.mergedViewTargetHint": "ئەو کۆمیتانەی کە تێکەڵکردنێک هێنراوەتە ناوەوە لە هێڵی لقە وەرگرەکەداشدا کێشراون، لە ژێر کۆمیتکردنی یەکگرتنەکەدا کۆدەکرێنەوە و هاوتا دەکرێن لەگەڵ ئەسڵەکان — چونکە ئەو لقە بەڕاستی ئێستا ئەوانی تێدایە. نوسخەکان کاڵ و سەریان بەو لقەیە کە لەسەری نووسراوە، بۆیە بە ڕوونی دەمێنێتەوە کە لەوێ دروست نەکراون. یەکگرتنی سکواش کاریگەری لەسەر نییە: سکواش یەک کۆمیتێکی ئاسایی دەنووسێت، بۆیە تەنها ئەو کۆمیتە هەرگیز دەردەکەوێت.",
    "legend.title": "ئەفسانە",
    "legend.head": "HEAD / لقی ئێستا",
    "legend.local": "لقی ناوخۆیی",
    "legend.remote": "لقێکی دوور",
    "legend.remoteOnly": "تەنها لە هەوردا (نەکێشراوە)",
    "legend.tag": "تاگ (وەشان)",
    "legend.commit": "ئەنجام دان",
    "legend.stash": "ستاش (کاری ڕەفە)",
    "legend.mergedIn": "بە تێکەڵکردنێک هاتە ژوورەوە (لە لقێکی تردا نووسراوە)",
    "legend.nodes": "سندوقەکان",
    "legend.lines": "هێڵەکان",
    "legend.edgeParent": "پابەندبوونی پێشوو لەسەر هەمان هێڵ",
    "legend.edgeMerge": "لقی تێکەڵاو — تیرەکە ئاماژە بە کۆمیتی تێکەڵکردن دەکات",
    "legend.edgeBranch": "لقێک لێرەدا چەقۆی لێدا",
    "legend.edgeStash": "ستاش و ئەو کۆمیتەی کە لەسەری ڕەفە بوو",
    "legend.edgeMergedChain": "هێڵی ئەم لقە کە بەناو کۆمیتەکانی تێکەڵکراودا دەڕوات",
    "legend.edgeMergedTie": "هەمان کۆمیت لەسەر ئەو لقەی کە لەسەری نووسراوە",
    "menu.jumpHead": "⌖ بڕۆ بۆ وەرگرتنی پارە",
    "menu.resetView": "⤢ دیمەنی ڕێستکردنەوە",
    "menu.createBranch": "لێرەوە لقێک دروست بکە...",
    "menu.checkout": "ئەم کۆمیتە بپشکنە",
    "menu.copySha": "کۆپی commit SHA",
    "menu.pushBranch": "لقی پاڵنانی \"{name}\".",
    "menu.renameCommit": "گۆڕینی ناوی پەیامی commit...",
    "menu.undoCommit": "Undo commit (گۆڕانکارییەکان بهێڵەرەوە)...",
    "menu.viewChanges": "بینینی گۆڕانکارییەکان...",
    "menu.jumpToOriginal": "بڕۆ بۆ کۆمیتەکەی ئەسڵی",
    "changes.title": "گۆڕانکاری لە {sha}",
    "changes.tabChanged": "گۆڕا",
    "changes.tabAll": "هەموو فایلەکان",
    "changes.added": "زیاد کرا",
    "changes.modified": "دەستکاری کراوە",
    "changes.deleted": "سڕاوەتەوە",
    "changes.renamed": "ناوی گۆڕدرا",
    "changes.noChanges": "ئەم کۆمیتە هیچ فایلێک ناگۆڕێت.",
    "changes.loading": "بارکردنی جیاوازی...",
    "changes.selectFile": "فایلێک هەڵبژێرە بۆ بینینی گۆڕانکارییەکانی.",
    "changes.original": "ڕەسەن",
    "changes.changed": "ئەم پابەندبوونە",
    "changes.binary": "فایلێکی دووانەیی — هیچ جیاوازییەکی دەق نییە بۆ پیشاندانی.",
    "changes.tooLarge": "فایلەکە زۆر گەورەیە بۆ ئەوەی جیاوازی بکات.",
    "changes.renamedFrom": "ناوی گۆڕاوە لە {path}",
    "changes.close": "داخستن",
    "changes.prevChange": "گۆڕانکاری پێشوو",
    "changes.nextChange": "گۆڕانی داهاتوو",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "هەموو فۆڵدەرەکان فراوان بکە",
    "changes.searchPlaceholder": "گەڕان بەدوای فایلەکاندا...",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "قەبارەی گەڕاندنەوە",
    "find.open": "لەم جیاوازییەدا بگەڕێ (Ctrl+F)",
    "find.placeholder": "لە جیاوازیدا بدۆزەرەوە...",
    "find.prev": "یاری پێشوو",
    "find.next": "یاری داهاتوو",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "هیچ ئەنجامێک نییە",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "گەڕانەوە",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashPop": "پۆپ ستاش (بەکارهێنان و لابردنی)",
    "menu.stashDrop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "بۆ ناو (ئێستا)",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "خێرا بەرەو پێشەوە — پێویست بە هیچ پابەندبوونێکی تێکەڵکردن ناکات (تیک لە \"دروستکردنی پابەندبوونێکی تێکەڵکردن\" بکە بۆ ناچارکردنی یەکێک).",
    "merge.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflictsWarning": "ئەم یەکگرتنە ناکۆکی هەیە — هێشتا دەتوانیت یەکبخەیت، پاشان لە دەستکاریکەردا چارەسەریان بکەیت.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "سڕاوەتەوە",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "پەیامی commit تێکەڵ بکە",
    "merge.messagePlaceholder": "تێکەڵکردنی لق 'سەرچاوە'.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "ڕەتکردنەوە",
    "merge.previewError": "نەتوانرا پێشبینییەک حیساب بکرێت: {message}",
    "merge.squashBadge": "سکواش",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "لەسەر {refs}",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "هەمووی فراوان بکە",
    "newBranch.collapseAll": "هەمووی داڕمێنە",
    "newBranch.expandFolder": "فۆڵدەر فراوان بکە",
    "newBranch.collapseFolder": "فۆڵدەری داڕمان",
    "newBranch.name": "ناوی لق",
    "newBranch.namePlaceholder": "لقی من",
    "newBranch.fullName": "ناوی تەواو: {name}",
    "newBranch.invalid": "ناوی لقێکی نادروست",
    "newBranch.exists": "لقێک بەم ناوەوە پێشتر بوونی هەیە",
    "newBranch.checkout": "لقی کۆتایی هاتنی دوای دروستکردن",
    "newBranch.create": "دروستکردن",
    "newBranch.cancel": "ڕەتکردنەوە",
    "status.loading": "بارکردنی گرافیک...",
    "status.summary": "{repo}پێشاندانی {commits} پابەندبوونەکان، {refs} refs",
    "status.branchCreated": "لقی \"{name}\" لە {sha} دروستکراوە",
    "status.error": "هەڵە: {message}",
    "status.opFailed": "ئۆپەراسیۆنەکە شکستی هێنا",
    "status.fetching": "هێنانی...",
    "status.pulling": "ڕاکێشانی...",
    "status.pushing": "پاڵنانی...",
    "status.committing": "دروستکردنی لۆکاڵی کۆمیت...",
    "status.commitCreated": "دروستکردنی local commit {sha}: {message}",
    "status.syncing": "هاوکاتکردن...",
    "status.noHead": "هیچ کۆتایییەکی ئێستا لە گرافەکەدا نەدۆزراوەتەوە.",
    "status.undoing": "پووچەڵکردنەوەی کۆمیت...",
    "status.commitUndone": "پابەندبوون نەکراوەتەوە — گۆڕانکارییەکان گەڕاونەتەوە ناو دارە کارکردنەکە.",
    "status.undoConflict": "ناکۆکییەکانی لێدان پاشەکشە بکە — لە سەرنوسەردا چارەسەریان بکە، پاشان بەردەوام بە.",
    "status.stashApplying": "بەکارهێنانی stash...",
    "status.stashPopping": "پۆپینگ ستاش...",
    "status.stashDropping": "کەوتنە خوارەوەی ستاش...",
    "status.stashApplied": "ستاش داوای کرد.",
    "status.stashPopped": "ستاش پۆپێکی کرد.",
    "status.stashDropped": "ستاش کەوتە خوارەوە.",
    "status.stashConflict": "ناکۆکییەکان کۆبکەرەوە — لە سەرنوسەردا چارەسەریان بکە.",
    "status.merging": "تێکەڵکردنی...",
    "status.merged": "تێکەڵکردن تەواو بوو.",
    "status.mergeConflict": "تێکەڵکردن ناکۆکی هەیە — لە سەرنوسەردا چارەسەریان بکە، پاشان پابەند بکە.",
    "search.title": "گەڕان",
    "search.tooltip": "گەڕان بە پابەندبوون",
    "search.placeholder": "گەڕان بە پابەندبوون...",
    "search.previous": "ئەنجامی پێشوو",
    "search.next": "ئەنجامی داهاتوو",
    "search.noResults": "هیچ ئەنجامێک نییە",
    "search.resultCount": "{index}/{count}",
    "search.filter": "دۆخی فلتەر",
    "search.highlight": "دۆخی تیشک خستنە سەر",
    "search.filterMode": "پیشاندانی گەڕان",
    "search.filterModeFilter": "فلتەر (شاردنەوەی ئەوانەی نەگونجاون)",
    "search.filterModeHighlight": "تیشک بخەرە سەر (هەموو پیشان بدە، یەکسانییەکان نیشانە بکە)",
    "search.filterModeHint": "چۆن ئەنجامەکانی گەڕان لە گرافەکەدا پیشان دەدرێن هەڵبژێرە.",
    "search.visibility": "شوێن بگەڕێ",
    "search.visibilityToolbar": "دوگمەی توولبار",
    "search.visibilityAlwaysVisible": "هەمیشە-بینراو بار",
    "search.visibilityHint": "گەڕان وەک ئایکۆنی توولبار یان باری بەردەوام لە سەرەوە پیشان بدە.",
    "footer.github": "گیت هاب",
  },
  dv: {
    "toolbar.refresh": "⟳ ރިފްރެޝް ކުރުން",
    "toolbar.fetch": "⤓ ގެނައުން",
    "toolbar.pull": "⇩ ދަމައިގަތުން",
    "toolbar.push": "⇧ ކޮއްޕާލާށެވެ",
    "toolbar.commit": "✓ ކޮމިޓްކުރުން",
    "toolbar.sync": "⇅ ސިންކް ކުރުން",
    "toolbar.jumpHead": "⌖ ޗެކްއައުޓަށް ދާށެވެ",
    "toolbar.reset": "⤢ ވިއު ރީސެޓްކުރުން",
    "toolbar.settings": "⚙ ސެޓިންގސް",
    "details.header": "ކޮމިޓް ޑީޓެއިލްސް",
    "details.close": "ލެއްޕުން",
    "details.sha": "ޝާ",
    "details.shortSha": "ކުރު ޝާ",
    "details.message": "މެސެޖް",
    "details.author": "ލިޔުންތެރިޔާ",
    "details.date": "ތާރީޚް",
    "details.labels": "ލޭބަލްތައް",
    "details.location": "ސަރަހައްދު",
    "details.currentHead": "HEAD — މިހާރު ޗެކްއައުޓް",
    "settings.title": "ސެޓިންގސް",
    "settings.language": "ބަސް",
    "settings.theme": "ތީމް",
    "settings.themeLight": "އަލި",
    "settings.themeDark": "އަނދިރި",
    "settings.themeLightHint": "އަލި ފަހަތުގައި، އަނދިރި ލިޔުމެކެވެ.",
    "settings.themeDarkHint": "އަނދިރި ފަހަތުގައި، އަލި ލިޔުމެކެވެ.",
    "settings.mainBranch": "މައި ބްރާންޗް",
    "settings.mainBranchAuto": "އޮޓޮމެޓިކް",
    "settings.mainBranchSearch": "ބްރާންޗްތައް ހޯދުން...",
    "settings.mainBranchNoMatch": "އެއްގޮތް ގޮފިތަކެއް ނެތެވެ",
    "settings.display": "ޑިސްޕްލޭ ސްޓައިލް",
    "settings.displayModern": "ޒަމާނީ",
    "settings.displayClassic": "ކްލާސިކް އެވެ",
    "settings.displayModernHint": "ހިލޭ ކެންވަސް — ޑްރެގް ޓު ޕެން، ސްކްރޯލް ޓު ޒޫމް.",
    "settings.displayClassicHint": "ފިކްސްޑް ކެންވަސް — ޓްރަންކް ކަނާތްފަރާތުގައި ޕިންކޮށްފައި، ޒޫމްއެއް ނެތް، ހަމައެކަނި ސްކްރޯލްކޮށްފައި (އެސްވީއެން ރިވިޝަން ގްރާފް ފަދައިން) އެވެ.",
    "settings.sectionGeneral": "އާންމު",
    "settings.sectionGraph": "ގްރާފް",
    "settings.sectionChanges": "ވިއު ބަދަލުކުރެއެވެ",
    "settings.diffMinimap": "ޑިފް މިނިމެޕް",
    "settings.diffMinimapOn": "ދައްކައިފިއެވެ",
    "settings.diffMinimapOff": "ފޮރުވިފައި",
    "settings.diffMinimapOnHint": "ޑިފްގެ ކައިރީގައި ވީއެސް ކޯޑް ސްޓައިލްގެ އޯވަރވިއު ސްޓްރިޕްއެއް ދައްކާ — ސްކްރޯލް ކުރުމަށް ދަމާލާށެވެ؛ ބަދަލު މާކެރެއް ހިމެނެއެވެ.",
    "settings.diffMinimapOffHint": "ނޯ އޯވަރވިއު ސްޓްރިޕް — އާންމުކޮށް ޑިފް ސްކްރޯލް ކުރާށެވެ.",
    "settings.commitReview": "ރިވިއު ކޮމިޓްކުރުން",
    "settings.commitReviewOn": "ކޮމިޓް ކުރުމުގެ ކުރިން އަހާށެވެ",
    "settings.commitReviewOff": "ސީދާ ކޮމިޓް ކުރާށެވެ",
    "settings.commitReviewOnHint": "ލޯކަލް ކޮމިޓް އުފެއްދުމުގެ ކުރިން ފައިނަލް ފައިލް ލިސްޓާއި މެސެޖް ޗެކް ދައްކާށެވެ.",
    "settings.commitReviewOffHint": "ކޮމިޓް ބަޓަން އިން ވަގުތުން ލޯކަލް ކޮމިޓް އުފެދެއެވެ.",
    "settings.svnBranchDialog": "އެސްވީއެން ސްޓައިލްގެ ބްރާންޗް ޑައިލޮގް",
    "settings.svnBranchDialogHint": "ބްރާންޗެއް އުފައްދާއިރު (ސާދާ ޕްރޮމްޕްޓެއްގެ ބަދަލުގައި) ފޯލްޑަރ-ޓްރީ ޕިކަރ އެއް ދައްކާށެވެ.",
    "settings.branchDialog": "ގޮފި ޑައިލޮގް",
    "settings.branchDialogSvnTitle": "އެސްވީއެން ސްޓައިލްގައެވެ",
    "settings.branchDialogNativeVscode": "ވީއެސް ކޯޑް ސްޓައިލް",
    "settings.branchDialogNativeVs": "ވިޝުއަލް ސްޓޫޑިއޯ ސްޓައިލްގައެވެ",
    "settings.branchDialogNativeHint": "ތިބާގެ އައިޑީއީގެ ބިލްޓް-އިން ބްރާންޗް ޕްރޮމްޕްޓް.",
    "settings.close": "ލެއްޕުން",
    "settings.done": "ނިމިއްޖު",
    "settings.sectionAdvanced": "އެޑްވާންސްޑް",
    "settings.gitSource": "ގިޓް އެގްޒެކެޓެބަލް އެވެ",
    "settings.gitSourceBuiltin": "ބިލްޓް-އިން",
    "settings.gitSourceBuiltinHint": "އައިޑީއީގެ ބިލްޓް-އިން ގިޓް އެކްސްޓެންޝަން އިން ގިޓް ބައިނަރީ ބޭނުން ކުރާށެވެ.",
    "settings.gitSourceCustom": "ކަސްޓަމް މަގު",
    "settings.gitPath": "ގިޓް އަށް ދާން އޮތް މަގު",
    "settings.gitPathPlaceholder": "/usr/bin/git އެވެ",
    "settings.gitPathBrowse": "ބްރައުޒް...",
    "settings.jargon": "ގިޓް ޓާމްސް",
    "settings.jargonTranslate": "ތަރުޖަމާކުރުން",
    "settings.jargonEnglish": "އިނގިރޭސި ބަހުން ބަހައްޓާށެވެ",
    "settings.jargonTranslateHint": "ގިޓް ޓާމްސް (ޕުލް، ޕުޝް، ކޮމިޓް، ބްރާންޗް...) އިންޓަރފޭސް ލޭންގުއޭޖަށް ތަރުޖަމާކުރުން.",
    "settings.jargonEnglishHint": "ގިޓް ޓާމްސް (ޕުލް، ޕުޝް، ކޮމިޓް، ބްރާންޗް...) އިނގިރޭސި ބަހުން ބެހެއްޓުން؛ އެހެން ހުރިހާ ކަމެއް ތަރުޖަމާ ކުރާށެވެ.",
    "settings.sectionMerge": "މާރޖް ކުރުން",
    "settings.mergeMode": "މާޖް ސްޓައިލް",
    "settings.mergeModeMerge": "ނޯމަލް މާރޖް",
    "settings.mergeModeSquash": "ސްކޮޝް މާރޖް ކުރުމެވެ",
    "settings.mergeModeMergeHint": "ބްރާންޗް އެގޮތުގައި މާރޖްކުރުން: އޭގެ ކޮމިޓްތައް މިހާރުގެ ބްރާންޗްގެ ތާރީޚުގެ ބަޔަކަށް ވެގެންދާއިރު، މާރޖް ކޮމިޓެއް ގުޅިގެން (ނުވަތަ ކުރެވެން އޮތް ނަމަ ފަސްޓް ފޯރވަރޑް ކުރެވެއެވެ).",
    "settings.mergeModeSquashHint": "މިހާރު ހުރި ބްރާންޗްގައި އެއް ކޮމިޓަކަށް މުޅި ބްރާންޗް ކޮލަޕްސް ކުރެއެވެ (git merge --squash). ބްރާންޗްގެ އަމިއްލަ ކޮމިޓްތައް ދުވަހަކުވެސް މިހާރުގެ ބްރާންޗްގެ ތާރީޚަށް ނުވަދެއެވެ — އެތަނުން ފެންނަނީ އެންމެ ކޮމިޓްއެކެވެ. ޚުދު ގޮފިވެސް އަތްނުލާ ދޫކޮށްލާތީ، އޭގެ ކޮމިޓްތައް ފެންނަނީ އަމިއްލަ ލޭންގައެވެ.",
    "settings.mergedView": "ގްރާފްގައި މާރޖްޑް ކޮމިޓްތައް",
    "settings.mergedViewBranch": "ހަމައެކަނި އަމިއްލަ ގޮފިގައެވެ",
    "settings.mergedViewTarget": "ހަމައެހެންމެ އެމީހުން އެއްކޮށްލި ގޮފިގައެވެ",
    "settings.mergedViewBranchHint": "އެއްކޮށްފައިވާ ބްރާންޗެއްގެ ކޮމިޓްތައް ކުރަހާފައިވަނީ ހަމައެކަނި އެމީހުންގެ އަމިއްލަ ލޭންގައެވެ؛ އެ މާރޖް ފެންނަނީ ކަނެކްޓަރެއްގެ ގޮތުގައެވެ. އެ ކްލާސިކް ވިއު އެވެ.",
    "settings.mergedViewTargetHint": "މާރޖަކުން ގެންނަ ކޮމިޓްތައް ރިސީވިންގ ބްރާންޗްގެ ލޭންގައި ވެސް ކުރަހާ، މާރޖް ކޮމިޓްގެ ދަށުގައި ސްޓޭކްކޮށް، އޮރިޖިނަލްތަކާ ލެވެލްކޮށްފައި ހުރެއެވެ — ސަބަބަކީ އެ ބްރާންޗްގައި ހަގީގަތުގައި މިހާރު އެ ކޮމިޓްތައް ހިމެނޭތީއެވެ. އެ ކޮޕީތައް ފަނޑުވެފައި ބޯ ޖަހާފައި ހުންނަނީ އެ ކޮޕީތައް ލިޔެފައިވާ ގޮފިން ކަމުން އެ ކޮޕީތައް އެތަނުގައި ހަދާފައި ނުވާކަން ސާފުވެ އެވެ. ސްކޮޝް މާރޖްތަކަށް އަސަރެއް ނުކުރެއެވެ: ސްކޮޝް އެއް ލިޔަނީ އާދައިގެ ކޮމިޓެއް ކަމުން ދުވަހަކުވެސް ފެންނަނީ ހަމައެކަނި އެ ކޮމިޓް އެވެ.",
    "legend.title": "ލެޖެންޑް",
    "legend.head": "ހެޑް / މިހާރުގެ ގޮފި",
    "legend.local": "ލޯކަލް ބްރާންޗް",
    "legend.remote": "ރިމޯޓް ބްރާންޗެކެވެ",
    "legend.remoteOnly": "ހަމައެކަނި ވިލާގަނޑުގައި (ދަމައިގެން ނޫން)",
    "legend.tag": "ޓެގް (ވަރޝަން)",
    "legend.commit": "ކޮމިޓް",
    "legend.stash": "ސްޓޭޝް (ޝެލްވްޑް ވޯކް)",
    "legend.mergedIn": "މާރޖަކާއެކު ވަދެގެން އައީ (އެހެން ބްރާންޗެއްގައި ލިޔެފައި)",
    "legend.nodes": "ފޮށިތަކެވެ",
    "legend.lines": "ލައިންތަކެވެ",
    "legend.edgeParent": "ކުރީގެ ކޮމިޓް އެއް ލައިނުގައި",
    "legend.edgeMerge": "މާރޖް-އިން ބްރާންޗް — މާރޖް ކޮމިޓް އަށް ދައްކާ ތިރި ތިރިއެވެ",
    "legend.edgeBranch": "މިތަނުން ގޮފިގަނޑެއް ފޯކް ކޮށްލިއެވެ",
    "legend.edgeStash": "ސްޓޭޝް އާއި ކޮމިޓް އެ ޝެލްފް ކޮށްފައި ހުއްޓެވެ",
    "legend.edgeMergedChain": "މި ބްރާންޗްގެ ލައިން މާރޖްޑް-އިން ކޮމިޓްތަކުގެ ތެރެއިން ހިނގާ ގޮތަށެވެ",
    "legend.edgeMergedTie": "ހަމަ އެ ކޮމިޓް ބްރާންޗްގައި ލިޔެފައި އޮތީ",
    "menu.jumpHead": "⌖ ޗެކްއައުޓަށް ދާށެވެ",
    "menu.resetView": "⤢ ވިއު ރީސެޓްކުރުން",
    "menu.createBranch": "މިތަނުން ބްރާންޗް އުފެއްދުން...",
    "menu.checkout": "މި ކޮމިޓް ޗެކްއައުޓް ކޮށްލައްވާށެވެ",
    "menu.copySha": "ކޮޕީ ކޮމިޓް ޝާ",
    "menu.pushBranch": "ޕުޝް ބްރާންޗް \"{name}\" އެވެ.",
    "menu.renameCommit": "ކޮމިޓް މެސެޖުގެ ނަން ބަދަލުކުރުން...",
    "menu.undoCommit": "ކޮމިޓް އަންޑޯ (ބަދަލުތައް ބެހެއްޓުން)...",
    "menu.viewChanges": "ބަދަލުތައް ބަލާ...",
    "menu.jumpToOriginal": "އަސްލު ކޮމިޓަށް ދާށެވެ",
    "changes.title": "އެކްސްޕީއެލްއެޗް0އެކްސް އަށް އަންނަ ބަދަލުތައް",
    "changes.tabChanged": "ބަދަލުވެއްޖެއެވެ",
    "changes.tabAll": "ހުރިހާ ފައިލްތަކެއް",
    "changes.added": "އިތުރުކޮށްފިއެވެ",
    "changes.modified": "ބަދަލުކޮށްފައި",
    "changes.deleted": "ޑިލީޓް ކޮށްފިއެވެ",
    "changes.renamed": "ނަން ބަދަލުކޮށްފިއެވެ",
    "changes.noChanges": "މި ކޮމިޓް އިން އެއްވެސް ފައިލަކަށް ބަދަލެއް ނާދެއެވެ.",
    "changes.loading": "ލޯޑިންގ ޑިފް...",
    "changes.selectFile": "އޭގެ ބަދަލުތައް ބަލާލުމަށް ފައިލެއް ހޮވާށެވެ.",
    "changes.original": "އަސްލު",
    "changes.changed": "މި ކޮމިޓް",
    "changes.binary": "ބައިނަރީ ފައިލް — ދައްކަން ޓެކްސްޓް ޑިފް އެއް ނެތެވެ.",
    "changes.tooLarge": "ފައިލް މާ ބޮޑުކަމުން ޑިފް.",
    "changes.renamedFrom": "އެކްސްޕީއެލްއެޗް0އެކްސް އިން ނަން ބަދަލުކޮށްފައިވެއެވެ",
    "changes.close": "ލެއްޕުން",
    "changes.prevChange": "ކުރީގެ ބަދަލެވެ",
    "changes.nextChange": "ދެން ބަދަލެއް",
    "changes.collapseAll": "ހުރިހާ ފޯލްޑަރެއް ކޮލަޕްސް ކޮށްލާށެވެ",
    "changes.expandAll": "ހުރިހާ ފޯލްޑަރެއް ފުޅާކުރުން",
    "changes.searchPlaceholder": "ފައިލްތައް ހޯދުން...",
    "changes.noSearchResults": "އެއްވެސް ފައިލެއް ތިޔަ ހޯއްދެވުމާ އެއްގޮތެވެ.",
    "changes.maximize": "މެކްސިމަޒް ކުރުން",
    "changes.restore": "ސައިޒު ރިސްޓޯރ ކުރާށެވެ",
    "find.open": "މި ޑިފް (Ctrl+F) ގައި ހޯދާށެވެ.",
    "find.placeholder": "ފައިންޑް އިން ޑިފް...",
    "find.prev": "ކުރީ މެޗެވެ",
    "find.next": "ދެން މެޗް",
    "find.close": "ހޯދުން ބަންދުކުރުން",
    "find.noResults": "ނަތީޖާއެއް ނެތެވެ",
    "commit.title": "ބަދަލުތައް ކޮމިޓްކުރުން",
    "commit.close": "ލެއްޕުން",
    "commit.loading": "ކިޔެވުން މަސައްކަތްކުރާ ގަހެއް...",
    "commit.noChanges": "ކޮމިޓް ކުރަން ލޯކަލް ބަދަލެއް ނެތެވެ.",
    "commit.selectFile": "އޭގެ ތަފާތު ބަލާލުމަށް ފައިލެއް ހޮވާށެވެ.",
    "commit.selectAll": "ހުރިހާ އެއްޗެއް ހޮވާށެވެ",
    "commit.selectNone": "ހުރިހާ ކަމެއް ސާފުކޮށްލާށެވެ",
    "commit.message": "ކޮމިޓް މެސެޖް",
    "commit.messagePlaceholder": "ބަދަލު ބަޔާންކޮށްދެއްވާށެވެ",
    "commit.selectedCount": "{count} ފައިލް(ތަކެއް) ހޮވާފައިވެއެވެ",
    "commit.review": "ރިވިއު",
    "commit.reviewTitle": "ކޮމިޓް ކުރުމުގެ ކުރިން ފައިނަލް ޗެކްކުރުން",
    "commit.reviewSummary": "މެސެޖާއެކު {count} ފައިލް(ތަކެއް) ކޮމިޓްކުރުން: {message}",
    "commit.back": "ފަހަތް",
    "commit.commit": "ލޯކަލް ކޮމިޓް އުފެއްދުން",
    "menu.stashApply": "ސްޓޭޝް އުނގުޅާށެވެ",
    "menu.stashPop": "ޕޮޕް ސްޓޭޝް (އެޕްލައި & ރިމޫވް)",
    "menu.stashDrop": "ޑްރޮޕް ސްޓޭޝް...",
    "merge.title": "މާޖް ބްރާންޗް",
    "merge.route": "މިހާރު ހުރި ގޮފިއަށް އެއްކޮށްލުން",
    "merge.source": "(މަޞްދަރު) އިން",
    "merge.target": "އިންޓޯ (މިހާރުގެ)",
    "merge.loading": "މާރޖް ތަޙުލީލު ކުރަމުން...",
    "merge.upToDate": "މިހާރުވެސް ތާރީޚާ އެއްގޮތަށް — އެއްކޮށްލާނެ އެއްވެސް ކަމެއް ނެތެވެ.",
    "merge.fastForward": "ފަސްޓް-ފޯވާޑް — މާރޖް ކޮމިޓް އެއް ނުޖެހޭ (އެއް ފޯސް ކުރުމަށް “މާޖް ކޮމިޓް އުފެއްދުން” އަށް ޓިކް ކުރާށެވެ).",
    "merge.summary": "{files} ފައިލް(ތަކެއް) ބަދަލުވެ، {conflicts} ކޮންފްލިކްޓް(ތަކެއް) .",
    "merge.conflictsWarning": "މި މާރޖްގައި ކޮންފްލިކްޓްތައް ހުރެއެވެ — އަދިވެސް މާރޖް ކުރެވިދާނެ، ދެން އެޑިޓަރުގައި އެކަންކަން ހައްލުކުރެވިދާނެއެވެ.",
    "merge.noChanges": "ފައިލަށް ބަދަލެއް ނާދެއެވެ.",
    "merge.files": "ނަތީޖާއެއްގެ ގޮތުން އަންނަ ބަދަލުތަކެވެ",
    "merge.added": "އިތުރުކޮށްފިއެވެ",
    "merge.modified": "ބަދަލުކޮށްފައި",
    "merge.deleted": "ޑިލީޓް ކޮށްފިއެވެ",
    "merge.conflict": "ފިތުނަ",
    "merge.message": "މާރޖް ކޮމިޓް މެސެޖް",
    "merge.messagePlaceholder": "ބްރާންޗް 'ސޯސް' އެއްކޮށްލާށެވެ.",
    "merge.messageFfHint": "ފަސްޓް ފޯވާޑް މާރޖެއްގައި އަޅާނުލާ (އެއްވެސް ކޮމިޓެއް ނުއުފެދެއެވެ).",
    "merge.noFastForward": "މާޖް ކޮމިޓް އުފެއްދުން (ފަސްޓް-ފޯވާޑް އެއް ނެތް)",
    "merge.noFastForwardHint": "އަބަދުވެސް މާޖް ކޮމިޓް ރެކޯޑް ކުރާނީ، ބްރާންޗަށް ފަސްޓް ފޯވާޑް ކުރެވޭ ވަގުތުތަކުގައި ވެސް.",
    "merge.merge": "މާރޖް ކުރުން",
    "merge.cancel": "ކެންސަލް",
    "merge.previewError": "ޕްރިވިއުއެއް ކަމްޕުއުޓް ނުކުރެވުނު: {message}",
    "merge.squashBadge": "ސްކޮޝް އެވެ",
    "merge.squashNote": "ސްކޮޝް މާޖް — ކޮންމެ ބަދަލެއް ލޭންޑް ކުރަނީ “{target}” ގައި ONE ކޮމިޓް އެއްގެ ގޮތުގައެވެ؛ “އެކްސްޕީއެލްއެޗް1އެކްސް” ގެ އަމިއްލަ ކޮމިޓްތައް އޭގެ ތާރީޚުން ބޭރުގައި ހުރެއެވެ. ސެޓިންގސް › މާރޖް އަށް އަނބުރާ ބަދަލުވާށެވެ.",
    "merge.messageSquash": "ކޮމިޓް މެސެޖް",
    "merge.squashMessagePlaceholder": "'ސޯސް' އިން ސްކޮޝްޑް ބަދަލުތައް.",
    "newBranch.title": "ބްރާންޗް އުފެއްދުން",
    "newBranch.startPoint": "އެކްސްޕީއެލްއެޗް0އެކްސް އިން ފެށިގެން އާ ބްރާންޗެކެވެ",
    "newBranch.startPointOn": "އެކްސްޕީއެލްއެޗް0އެކްސް ގައި",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "ފޯލްޑަރ ފުޅާކުރުން",
    "newBranch.collapseFolder": "ކޮލަޕްސް ފޯލްޑަރ",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "މައި-ބްރާންޗް",
    "newBranch.fullName": "ފުރިހަމަ ނަން: އެކްސްޕީއެލްއެޗް0އެކްސް",
    "newBranch.invalid": "ސައްހަ ނޫން ގޮފި ނަމެވެ",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "އުފެއްދުމަށްފަހު ޗެކްއައުޓް ބްރާންޗް",
    "newBranch.create": "ތައްޔާރުކުރުން",
    "newBranch.cancel": "ކެންސަލް",
    "status.loading": "ލޯޑިންގ ގްރާފް...",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "އެރަރ: އެކްސްޕީއެލްއެޗް0އެކްސް",
    "status.opFailed": "އޮޕަރޭޝަން ފެއިލްވެއްޖެއެވެ",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "ދަމައިގަންނަނީ...",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "ލޯކަލް ކޮމިޓް އުފެއްދުމުގެ...",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "ކޮމިޓް އަންޑޯ ކުރަނީ...",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "ޕޮޕިންގ ސްޓޭޝް...",
    "status.stashDropping": "ޑްރޮޕިންގ ސްޓޭޝް...",
    "status.stashApplied": "ސްޓޭޝް އެޕްލައި ކުރިއެވެ.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "ސްޓޭޝް ކޮންފްލިކްޓްސް — އެޑިޓަރުގައި އެކަންކަން ހައްލުކުރުން.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "މާރޖް ނިމިއްޖެއެވެ.",
    "status.mergeConflict": "މާރޖްގައި ކޮންފްލިކްޓްތައް ހުރެއެވެ — އެޑިޓަރުގައި އެކަންކަން ހައްލުކޮށް، ދެން ކޮމިޓްކުރުން.",
    "search.title": "ހޯދުން",
    "search.tooltip": "ސަރޗް ކޮމިޓްސް",
    "search.placeholder": "ސަރޗް ކޮމިޓްސް...",
    "search.previous": "ކުރީގެ ނަތީޖާ",
    "search.next": "ދެން ނަތީޖާ",
    "search.noResults": "ނަތީޖާއެއް ނެތެވެ",
    "search.resultCount": "އެކްސްޕީއެލްއެޗް0އެކްސް/އެކްސްޕީއެލްއެޗް1އެކްސް",
    "search.filter": "ފިލްޓަރ މޯޑް",
    "search.highlight": "ހައިލައިޓް މޯޑް",
    "search.filterMode": "ސަރޗް ޑިސްޕްލޭ",
    "search.filterModeFilter": "ފިލްޓަރ (ނުގުޅޭ ތަންތަން ފޮރުވުން)",
    "search.filterModeHighlight": "ހައިލައިޓް (ހުރިހާ އެއްޗެއް ދައްކާ، މެޗުތައް ފާހަގަކުރުން)",
    "search.filterModeHint": "ގްރާފްގައި ހޯދުމުގެ ނަތީޖާ ދައްކާނެ ގޮތް ހޮވާށެވެ.",
    "search.visibility": "ލޮކޭޝަން ހޯދުން",
    "search.visibilityToolbar": "ޓޫލްބާ ބަޓަން އެވެ",
    "search.visibilityAlwaysVisible": "އަބަދުވެސް ފެންނަ ބާރެވެ",
    "search.visibilityHint": "މަތީގައި ޓޫލްބާ އައިކޮން ނުވަތަ ޕަރސިސްޓެންޓް ބާރެއްގެ ގޮތުގައި ސަރޗް ދައްކާށެވެ.",
    "footer.github": "ގިޓްހަބް އެވެ",
  },
  gom: {
    "toolbar.refresh": "⟳ ताजें करचें",
    "toolbar.fetch": "⤓ हाडून घेवप",
    "toolbar.pull": "⇩ ओडून घेवप",
    "toolbar.push": "⇧ धक्को मारप",
    "toolbar.commit": "✓ कमिट करप",
    "toolbar.sync": "⇅ समक्रमण करप",
    "toolbar.jumpHead": "⌖ चेकआउटांत वचप",
    "toolbar.reset": "⤢ दृश्य रीसेट करचें",
    "toolbar.settings": "⚙ सेटिंग्ज",
    "details.header": "तपशील कमिट करचें",
    "details.close": "धांपप",
    "details.sha": "शा",
    "details.shortSha": "लघु शा",
    "details.message": "संदेश",
    "details.author": "लेखक",
    "details.date": "तारीख",
    "details.labels": "लेबलां",
    "details.location": "थळ",
    "details.currentHead": "HEAD — सद्याचें चेकआउट",
    "settings.title": "सेटिंग्ज",
    "settings.language": "भास",
    "settings.theme": "माथाळो",
    "settings.themeLight": "उजवाड",
    "settings.themeDark": "काळोख",
    "settings.themeLightHint": "हळदुवो पार्श्वभूंय, काळो मजकूर.",
    "settings.themeDarkHint": "गडद पार्श्वभूंय, उजवाडाचो मजकूर.",
    "settings.mainBranch": "मुखेल शाखा",
    "settings.mainBranchAuto": "स्वयंचलीत",
    "settings.mainBranchSearch": "शाखा सोदप...",
    "settings.mainBranchNoMatch": "जुळपी फांटे नात",
    "settings.display": "प्रदर्शन शैली",
    "settings.displayModern": "आधुनीक",
    "settings.displayClassic": "क्लासिक",
    "settings.displayModernHint": "मुक्त कॅनव्हास — पॅन करपाक ओडून, झूम करपाक स्क्रोल करात.",
    "settings.displayClassicHint": "स्थिर कॅनव्हास — ट्रंक डावें पिन केला, झूम ना, फकत स्क्रोल करात (SVN पुनरावलोकन आलेखा प्रमाण).",
    "settings.sectionGeneral": "सर्वसादारण",
    "settings.sectionGraph": "आलेख",
    "settings.sectionChanges": "दृश्य बदलता",
    "settings.diffMinimap": "डिफ मिनीमॅप",
    "settings.diffMinimapOn": "दाखयला",
    "settings.diffMinimapOff": "लिपिल्लें",
    "settings.diffMinimapOnHint": "diff च्या कुशीक VS कोड-शैलीची नियाळ पट्टी दाखोवची — स्क्रोल करपाक ओडची; बदल चिन्नांचो आस्पाव केला.",
    "settings.diffMinimapOffHint": "नियाळ पट्टी ना — फरक सामान्यपणान स्क्रोल करचो.",
    "settings.commitReview": "नियाळ घेवप",
    "settings.commitReviewOn": "कमिट करचे पयलीं विचारात",
    "settings.commitReviewOff": "थेट कमिट करचें",
    "settings.commitReviewOnHint": "थळावो कमिट तयार करचे पयलीं निमाणी फायल वळेरी आनी संदेश तपासणी दाखोवची.",
    "settings.commitReviewOffHint": "Commit बटण थळावें कमिट रोखडेंच तयार करता.",
    "settings.svnBranchDialog": "एसव्हीएन-शैलीचो शाखा संवाद",
    "settings.svnBranchDialogHint": "शाखा तयार करतना (सादे प्रॉम्प्ट बदला) फोल्डर-झाड पिकर दाखोवचो.",
    "settings.branchDialog": "शाखा संवाद",
    "settings.branchDialogSvnTitle": "एसव्हीएन-शैलीचें",
    "settings.branchDialogNativeVscode": "व्हीएस कोड शैली",
    "settings.branchDialogNativeVs": "व्हिज्युअल स्टुडिओ शैली",
    "settings.branchDialogNativeHint": "तुमच्या IDE चो बिल्ट-इन शाखा प्रॉम्प्ट.",
    "settings.close": "धांपप",
    "settings.done": "जालें",
    "settings.sectionAdvanced": "आधुनीक",
    "settings.gitSource": "गिट कार्यान्वयन करपाक मेळटा",
    "settings.gitSourceBuiltin": "बिल्ट-इन केल्लें",
    "settings.gitSourceBuiltinHint": "IDE च्या बिल्ट-इन Git विस्तारांतल्यान git बाइनरी वापरात.",
    "settings.gitSourceCustom": "सानुकूल मार्ग",
    "settings.gitPath": "गिट करपाक मार्ग",
    "settings.gitPathPlaceholder": "/युएसआर / बिन / गिट",
    "settings.gitPathBrowse": "ब्राउझ करचें...",
    "settings.jargon": "गिट अटी",
    "settings.jargonTranslate": "अणकार करचो",
    "settings.jargonEnglish": "इंग्लीशींत दवरात",
    "settings.jargonTranslateHint": "Git संज्ञा (पुल, पुश, कमिट, शाखा...) संवाद भाशेंत अणकार करात.",
    "settings.jargonEnglishHint": "Git संज्ञा (पुल, पुश, कमिट, शाखा...) इंग्लीशींत दवरात; हेर सगळें अणकारप.",
    "settings.sectionMerge": "विलीन करप",
    "settings.mergeMode": "विलीन करपाची शैली",
    "settings.mergeModeMerge": "सामान्य विलीनीकरण",
    "settings.mergeModeSquash": "स्क्वास विलीन जावप",
    "settings.mergeModeMergeHint": "शाखा आसा तशी विलीन करता: ताचीं कमिटां सद्याच्या शाखेच्या इतिहासाचो भाग जातात, विलीनीकरण कमिट (वा शक्य आसल्यार फास्ट-फॉरवर्ड) जोडटात.",
    "settings.mergeModeSquashHint": "सद्याच्या शाखेचेर एकाच कमिटांत पुराय शाखा कोसळटा (git merge --squash). शाखेचीं स्वताचीं कमिटां सद्याच्या शाखेच्या इतिहासांत केन्नाच प्रवेश करिनात — थंय तुमकां फकत एक कमिट दिसता. शाखा स्वता अस्पृश्य सोडल्या, देखून ताचीं कमिटां स्वताच्या लेनांत दिसतात.",
    "settings.mergedView": "आलेखांत विलीन केल्लीं कमिटां",
    "settings.mergedViewBranch": "फकत स्वताच्या फांट्याचेर",
    "settings.mergedViewTarget": "तशेंच तांकां विलीन केल्ल्या फांट्यांत",
    "settings.mergedViewBranchHint": "विलीन केल्ल्या शाखेचीं कमिटां फकत तांच्या स्वताच्या लेनांत काडटात; विलीनीकरण जोडणी म्हणून दाखयता. अभिजात नदर.",
    "settings.mergedViewTargetHint": "विलीनीकरणान हाडिल्लीं कमिटां घेवपी शाखेच्या लेनांतय काडटात, विलीनीकरण कमिटाच्या सकयल रांकोन दवरतात आनी मूळ कडेन समांतर करतात — कारण त्या शाखेंत खरेंच आतां त्यो आसतात. प्रती फिकट आसून त्यो बरयल्ल्यो फांट्या वांगडा तकलेच्यो आसतात, देखून तीं थंय तयार जावंक नाशिल्लीं हें स्पश्ट उरता. स्क्वास विलीनीकरणांचेर परिणाम जायना: स्क्वास एक सादारण कमिट बरयता, देखून फकत तो कमिट केन्नाच दिसता.",
    "legend.title": "आख्यायिका",
    "legend.head": "HEAD / सद्याचो शाखा",
    "legend.local": "थळावो शाखा",
    "legend.remote": "रिमोट शाखा",
    "legend.remoteOnly": "फकत मेघांत (ओडून घेतिल्लें ना) .",
    "legend.tag": "टॅग (आवृत्ती) 1.1.",
    "legend.commit": "करप",
    "legend.stash": "स्टॅश (शेल्फ काम) 1.1.",
    "legend.mergedIn": "विलीनीकरण (दुसऱ्या शाखेचेर बरयल्लें) घेवन भितर आयलो",
    "legend.nodes": "पेटी",
    "legend.lines": "ओळीं",
    "legend.edgeParent": "त्याच ओळीचेर आदलें कमिट",
    "legend.edgeMerge": "विलीन केल्लो शाखा — बाण विलीनीकरण कमिटाचेर दाखयता",
    "legend.edgeBranch": "हांगा एक फांटो कांटून उडयलो",
    "legend.edgeStash": "स्टॅश आनी तो शेल्फ लायिल्लो कमिट",
    "legend.edgeMergedChain": "विलीन केल्ल्या कमिटांतल्यान चलपी ह्या शाखेची ओळ",
    "legend.edgeMergedTie": "फांट्याचेर तेंच कमिट बरयल्लें",
    "menu.jumpHead": "⌖ चेकआउटांत वचप",
    "menu.resetView": "⤢ दृश्य रीसेट करचें",
    "menu.createBranch": "हांगाच्यान शाखा तयार करात...",
    "menu.checkout": "हें कमिट पळयात",
    "menu.copySha": "कॉपी कमिट SHA",
    "menu.pushBranch": "पुश शाखा \"{name}\".",
    "menu.renameCommit": "कमिट संदेशाचें नांव बदलचें...",
    "menu.undoCommit": "कमिट पूर्ववत करात (बदल दवरात)...",
    "menu.viewChanges": "बदल पळोवचे...",
    "menu.jumpToOriginal": "मूळ कमिटांत वचचें",
    "changes.title": "{sha} त बदल",
    "changes.tabChanged": "बदलले",
    "changes.tabAll": "सगळ्यो फायली",
    "changes.added": "जोडले",
    "changes.modified": "सुदारीत केल्लें",
    "changes.deleted": "काडून उडयलां",
    "changes.renamed": "नांव बदल्लें",
    "changes.noChanges": "ही कमिट खंयचीच फायल बदलना.",
    "changes.loading": "लोडिंग डिफ...",
    "changes.selectFile": "ताचे बदल पळोवपाक फायल निवडात.",
    "changes.original": "मूळ",
    "changes.changed": "हें कमिट",
    "changes.binary": "द्विपदी फायल — दाखोवपाक मजकूर फरक ना.",
    "changes.tooLarge": "फायल फरक करपाक सामकी व्हडली आसा.",
    "changes.renamedFrom": "{path} कडल्यान नांव बदललां",
    "changes.close": "धांपप",
    "changes.prevChange": "आदलो बदल",
    "changes.nextChange": "फुडलो बदल",
    "changes.collapseAll": "सगळे फोल्डर संकुचीत करात",
    "changes.expandAll": "सगळे फोल्डर विस्तारात",
    "changes.searchPlaceholder": "फायली सोदप...",
    "changes.noSearchResults": "तुमच्या सोदाक जुळपी खंयचीच फायली ना.",
    "changes.maximize": "चडांत चड करप",
    "changes.restore": "आकार परतून मेळोवचो",
    "find.open": "ह्या फरकांत सोद (Ctrl+F)",
    "find.placeholder": "diff मदीं सोदून काडात...",
    "find.prev": "आदली मॅच",
    "find.next": "फुडली मॅच",
    "find.close": "सोद बंद करात",
    "find.noResults": "ताचो निकाल ना",
    "commit.title": "बदल कमिट करप",
    "commit.close": "धांपप",
    "commit.loading": "वाचन काम करपी झाड...",
    "commit.noChanges": "कमिट करपाक थळावे बदल नात.",
    "commit.selectFile": "ताचो फरक पळोवपाक फायल निवडात.",
    "commit.selectAll": "सगळें निवडात",
    "commit.selectNone": "सगळें निवळ करचें",
    "commit.message": "संदेश कमिट करचो",
    "commit.messagePlaceholder": "बदलाचें वर्णन करात",
    "commit.selectedCount": "{count} फायल (फायली) निवडल्या",
    "commit.review": "नियाळ",
    "commit.reviewTitle": "कमिट करचे पयलीं निमाणी तपासणी",
    "commit.reviewSummary": "संदेशा वांगडा {count} फायल (फायली) कमिट करात: {message}",
    "commit.back": "फाटीं",
    "commit.commit": "थळावें कमिट तयार करचें",
    "menu.stashApply": "स्टॅश लागू करप",
    "menu.stashPop": "पॉप स्टॅश (लागू & काडून उडोवप)",
    "menu.stashDrop": "ड्रॉप स्टॅश...",
    "merge.title": "शाखा विलीन करप",
    "merge.route": "सद्याच्या शाखेंत विलीन जावप",
    "merge.source": "(स्रोत) कडल्यान",
    "merge.target": "(सद्याच्या) 1000000 .",
    "merge.loading": "विलीनीकरणाचें विश्लेशण करप...",
    "merge.upToDate": "पयलींच अद्ययावत — विलीन करपाक कांयच ना.",
    "merge.fastForward": "फास्ट-फॉरवर्ड — विलीनीकरण कमिटाची गरज ना (एक सक्ती करपाक “विलीनीकरण कमिट तयार करात” टिक करात).",
    "merge.summary": "{files} फायल(फायली) बदलल्या, {conflicts} संघर्श(स).",
    "merge.conflictsWarning": "ह्या विलीनीकरणाक संघर्श आसात — तुमी अजूनय विलीन करूंक शकतात, मागीर ते संपादकांत सोडोवंक शकतात.",
    "merge.noChanges": "फायल बदलना.",
    "merge.files": "ताका लागून बदल जातात",
    "merge.added": "जोडले",
    "merge.modified": "सुदारीत केल्लें",
    "merge.deleted": "काडून उडयलां",
    "merge.conflict": "संघर्श",
    "merge.message": "कमिट संदेश विलीन करचो",
    "merge.messagePlaceholder": "शाखा 'स्रोत' विलीन करप.",
    "merge.messageFfHint": "फास्ट-फॉरवर्ड विलीनीकरणाचेर दुर्लक्ष केलां (खंयचोच कमिट तयार केल्लो ना).",
    "merge.noFastForward": "विलीनीकरण कमिट तयार करात (फास्ट-फॉरवर्ड ना)",
    "merge.noFastForwardHint": "शाखे फास्ट-फॉरवर्ड करूंक शकता तेन्ना लेगीत, सदांच विलीनीकरण कमिट रेकॉर्ड करात.",
    "merge.merge": "विलीन करप",
    "merge.cancel": "रद्द",
    "merge.previewError": "पूर्वावलोकन गणना करूंक शकलो ना: {message}",
    "merge.squashBadge": "स्क्वास हें झाड",
    "merge.squashNote": "स्क्वास विलीनीकरण — दरेक बदल “{target}” चेर ONE कमिट म्हणून उडयता; “{source}” ची स्वताची कमिट ताच्या इतिहासा भायर उरता. मांडावळी › विलीन करप हातूंत परत वचचें.",
    "merge.messageSquash": "संदेश कमिट करचो",
    "merge.squashMessagePlaceholder": "'स्रोत' कडल्यान स्क्वाश केल्ले बदल.",
    "newBranch.title": "शाखा तयार करप",
    "newBranch.startPoint": "{sha} पासून सुरू जावपी नवो शाखा",
    "newBranch.startPointOn": "{refs} चेर",
    "newBranch.location": "थळ",
    "newBranch.locationRoot": "(मूळ)",
    "newBranch.expandAll": "सगळें विस्तार करचें",
    "newBranch.collapseAll": "सगळें कोसळप",
    "newBranch.expandFolder": "फोल्डर विस्तार करचो",
    "newBranch.collapseFolder": "फोल्डर कोसळोवप",
    "newBranch.name": "शाखेचें नांव",
    "newBranch.namePlaceholder": "माझ्या-शाखा",
    "newBranch.fullName": "पुराय नांव: {name}",
    "newBranch.invalid": "अवैध शाखेचें नांव",
    "newBranch.exists": "ह्या नांवाचो एक फांटो पयलींच अस्तित्वांत आसा",
    "newBranch.checkout": "तयार केल्या उपरांत चेकआउट शाखा",
    "newBranch.create": "निर्माण करचें",
    "newBranch.cancel": "रद्द",
    "status.loading": "आलेख लोड करप...",
    "status.summary": "{repo}{commits} कमिट दाखोवप, {refs} रेफ",
    "status.branchCreated": "{sha} हांगा \"{name}\" शाखा तयार केल्या",
    "status.error": "त्रुटी: {message}",
    "status.opFailed": "ऑपरेशन अपेशी थारलें",
    "status.fetching": "हाडून...",
    "status.pulling": "ओडून घेवप...",
    "status.pushing": "धक्को मारप...",
    "status.committing": "थळावें कमिट तयार करप...",
    "status.commitCreated": "थळावें कमिट तयार केलां {sha}: {message}",
    "status.syncing": "समक्रमण करप...",
    "status.noHead": "आलेखांत सद्याचो चेकआउट मेळूंक ना.",
    "status.undoing": "कमिट पूर्ववत करप...",
    "status.commitUndone": "कमिट अनडन — बदल परतून काम करपी झाडांत आसात.",
    "status.undoConflict": "हिट संघर्श पूर्ववत करात — संपादकांत ते सोडोवचे, मागीर चालू दवरात.",
    "status.stashApplying": "स्टॅश लागू करप...",
    "status.stashPopping": "पॉपिंग स्टॅश...",
    "status.stashDropping": "स्टॅश सोडप...",
    "status.stashApplied": "स्टॅश लागू केलो.",
    "status.stashPopped": "स्टॅश पॉप जालो.",
    "status.stashDropped": "स्टॅश पडलो.",
    "status.stashConflict": "संघर्श स्टॅश करात — ते संपादकांत सोडोवचे.",
    "status.merging": "विलीन करप...",
    "status.merged": "विलीनीकरण पुराय जालें.",
    "status.mergeConflict": "विलीनीकरणांत संघर्श आसात — ते संपादकांत सोडोवचे, मागीर कमिट करचें.",
    "search.title": "सोद",
    "search.tooltip": "सोद कमिट करता",
    "search.placeholder": "सोद कमिट करता...",
    "search.previous": "आदलो निकाल",
    "search.next": "फुडलो निकाल",
    "search.noResults": "ताचो निकाल ना",
    "search.resultCount": "एक्सपीएलएच0एक्स/एक्सपीएलएच1एक्स",
    "search.filter": "फिल्टर मोड",
    "search.highlight": "हायलाइट मोड",
    "search.filterMode": "सोद प्रदर्शन",
    "search.filterModeFilter": "फिल्टर (जोडनाशिल्लें लिपोवप) .",
    "search.filterModeHighlight": "हायलाइट करचें (सगलें दाखोवचें, जुळोवणी चिन्न करचें)",
    "search.filterModeHint": "आलेखांत सोद परिणाम कशे दाखयतात तें निवडात.",
    "search.visibility": "थळ सोदचें",
    "search.visibilityToolbar": "साधनपट्टी बटण",
    "search.visibilityAlwaysVisible": "सदांच दिसपी बार",
    "search.visibilityHint": "सोद वयल्यान साधनपट्टी चिन्न वा टिकपी पट्टी म्हणून दाखोवचो.",
    "footer.github": "गिटहब हें नांव",
  },
  doi: {
    "toolbar.refresh": "⟳ ताज़ा करना",
    "toolbar.fetch": "⤓ लाओ",
    "toolbar.pull": "⇩ खींचना",
    "toolbar.push": "⇧ धक्का देना",
    "toolbar.commit": "✓ कमिट करो",
    "toolbar.sync": "⇅ सिंक करना",
    "toolbar.jumpHead": "⌖ चेकआउट पर जाओ",
    "toolbar.reset": "⤢ दृश्य रीसेट करो",
    "toolbar.settings": "⚙ सेटिंग्स दी",
    "details.header": "विवरण कमिट करो",
    "details.close": "बंद",
    "details.sha": "शा",
    "details.shortSha": "लघु शा",
    "details.message": "सनेहा",
    "details.author": "लेखक",
    "details.date": "तरीक",
    "details.labels": "लेबल",
    "details.location": "थाहर",
    "details.currentHead": "HEAD — वर्तमान चेकआउट",
    "settings.title": "सेटिंग्स",
    "settings.language": "भाशा",
    "settings.theme": "थीम",
    "settings.themeLight": "लो",
    "settings.themeDark": "न्हेरा",
    "settings.themeLightHint": "हल्की पृष्ठभूमि, गहरे रंग दा पाठ।",
    "settings.themeDarkHint": "गहरे रंग की पृष्ठभूमि, हल्के पाठ।",
    "settings.mainBranch": "मुख्य शाखा",
    "settings.mainBranchAuto": "स्वचालित",
    "settings.mainBranchSearch": "शाखाएं दी तलाश करो...",
    "settings.mainBranchNoMatch": "कोई मिलान शाखाएं नहीं",
    "settings.display": "प्रदर्शन शैली",
    "settings.displayModern": "अजकना",
    "settings.displayClassic": "क्लासिक",
    "settings.displayModernHint": "मुफ्त कैनवास — पैन च खींचो, ज़ूम करने लेई स्क्रॉल करो।",
    "settings.displayClassicHint": "फिक्स्ड कैनवास — ट्रंक बाएं पिन कीता गेआ, कोई ज़ूम नेईं, सिर्फ स्क्रॉल करो (एसवीएन संशोधन ग्राफ दी तर्ज पर)।",
    "settings.sectionGeneral": "आम",
    "settings.sectionGraph": "ग्राफ",
    "settings.sectionChanges": "दृष्टिकोण बदलदा ऐ",
    "settings.diffMinimap": "डिफ मिनीमैप",
    "settings.diffMinimapOn": "दिखाया गया",
    "settings.diffMinimapOff": "गुज्झा",
    "settings.diffMinimapOnHint": "डिफ दे बगल च इक वीएस कोड-शैली अवलोकन पट्टी दस्सो — स्क्रॉल करने लेई इसगी खींचो; बदलाव दे निशान शामल कीते गे।",
    "settings.diffMinimapOffHint": "कोई अवलोकन पट्टी नेईं — अंतर गी सामान्य रूप कन्नै स्क्रॉल करो।",
    "settings.commitReview": "समीक्षा कमिट करो",
    "settings.commitReviewOn": "कमिट करने कोला पैह्ले पुच्छो",
    "settings.commitReviewOff": "सीधे कमिट करो",
    "settings.commitReviewOnHint": "लोकल कमिट बनाने थमां पैह् ले इक अंतिम फाइल सूची ते संदेश जांच दस्सो.",
    "settings.commitReviewOffHint": "कमिट बटन फौरन लोकल कमिट बनांदा ऐ।",
    "settings.svnBranchDialog": "एसवीएन-शैली शाखा संवाद",
    "settings.svnBranchDialogHint": "शाखा बनांदे बेल्लै (साधारण प्रॉम्प्ट दी बजाय) इक फोल्डर-ट्री पिकर दस्सो.",
    "settings.branchDialog": "शाखा संवाद",
    "settings.branchDialogSvnTitle": "एसवीएन-शैली दा",
    "settings.branchDialogNativeVscode": "वी एस कोड शैली",
    "settings.branchDialogNativeVs": "विजुअल स्टूडियो शैली",
    "settings.branchDialogNativeHint": "तुंदी आईडीई दा बिल्ट-इन शाखा प्रॉम्प्ट।",
    "settings.close": "बंद",
    "settings.done": "होई गेआ",
    "settings.sectionAdvanced": "उन्नत",
    "settings.gitSource": "गिट एक्जीक्यूटिव",
    "settings.gitSourceBuiltin": "बिल्ट इन",
    "settings.gitSourceBuiltinHint": "आईडीई दे बिल्ट-इन Git एक्सटेंशन थमां git बाइनरी दा इस्तेमाल करो.",
    "settings.gitSourceCustom": "कस्टम पथ",
    "settings.gitPath": "गिट करने दा रस्ता",
    "settings.gitPathPlaceholder": "/ यूएसआर / बिन / गिट",
    "settings.gitPathBrowse": "ब्राउज़ करें...",
    "settings.jargon": "गिट शर्तें",
    "settings.jargonTranslate": "अनुवाद करना",
    "settings.jargonEnglish": "अंग्रेजी च रखो",
    "settings.jargonTranslateHint": "Git शब्दें (पुल, पुश, कमिट, शाखा...) दा इंटरफेस भाशा च अनुवाद करो।",
    "settings.jargonEnglishHint": "गिट शब्द (पुल, पुश, कमिट, शाखा...) गी अंग्रेजी च रक्खो; बाकी सब दा अनुवाद करो।",
    "settings.sectionMerge": "विलय कर दे",
    "settings.mergeMode": "मर्ज स्टाइल",
    "settings.mergeModeMerge": "सामान्य विलय करना",
    "settings.mergeModeSquash": "स्क्वैश विलय करना",
    "settings.mergeModeMergeHint": "शाखा गी जि'यां ऐ उ'यां गै मर्ज करदा ऐ: इसदे कमिट मौजूदा शाखा दे इतिहास दा हिस्सा बनी जंदे न, जिस च इक मर्ज कमिट (जां संभव होने पर फास्ट-फॉरवर्ड कीता जंदा ऐ) कन्नै जुड़ी जंदा ऐ।",
    "settings.mergeModeSquashHint": "मौजूदा शाखा पर इक कमिट च पूरी शाखा गी ढहदा ऐ (git merge --squash). शाखा दे अपने कमिट कदें बी मौजूदा शाखा दे इतिहास च नेईं औंदे न — तुस उत्थें सिर्फ इक कमिट दिक्खदे ओ। शाखा गी गै अछूता छोड़ी दित्ता जंदा ऐ, इस करी ओह्दे कमिट अपनी गै लेन च दिक्खने गी मिलदे रौंह्दे न।",
    "settings.mergedView": "ग्राफ च मर्ज कीते गेदे कमिट",
    "settings.mergedViewBranch": "सिर्फ अपनी शाखा पर ही",
    "settings.mergedViewTarget": "शाखा च बी उनेंगी विलय करी दित्ता गेआ हा",
    "settings.mergedViewBranchHint": "मर्ज कीती गेदी शाखा दे कमिट सिर्फ उंदी अपनी लेन च गै खिच्चे जंदे न; मर्ज इक कनेक्टर दे रूप च दस्सेआ जंदा ऐ। क्लासिक नज़ारा।",
    "settings.mergedViewTargetHint": "इक मर्ज च लाए गेदे कमिटें गी रिसीविंग शाखा दी लेन च बी खींचया जंदा ऐ, मर्ज कमिट दे हेठ ढेर कीता जंदा ऐ ते मूल कन्नै समतल कीता जंदा ऐ — कीजे उस शाखा च सच्चें गै हून एह् शामल न. नकल पीली ते सिर जिस शाखा उप्पर लिखी गेदी ही, इस करी एह् साफ रौंह्दा ऐ जे ओह् उत्थें नेईं बनाई गेदी ही। स्क्वैश मर्ज अप्रभावित होंदे न: इक स्क्वैश इक साधारण कमिट लिखदा ऐ, इसलेई सिर्फ ओह कमिट कदें बी दिक्खने गी मिलदा ऐ।",
    "legend.title": "म्हान",
    "legend.head": "HEAD / वर्तमान शाखा",
    "legend.local": "लोकल शाखा",
    "legend.remote": "दूरस्थ शाखा",
    "legend.remoteOnly": "सिर्फ बादल विच (खींच नहीं)",
    "legend.tag": "टैग (संस्करण) ऐ",
    "legend.commit": "पाबंद रौहना",
    "legend.stash": "स्टैश (अलमारी दा कम्म)",
    "legend.mergedIn": "मर्ज कन्नै अंदर आया (दूजी शाखा पर लिखे दा)",
    "legend.nodes": "बक्से",
    "legend.lines": "लाइनें",
    "legend.edgeParent": "उसी लाइन पर पिछले कमिट",
    "legend.edgeMerge": "मर्ज-इन शाखा — तीर मर्ज कमिट पर इशारा करदा ऐ",
    "legend.edgeBranch": "इत्थे इक शाखा कांटा मारी",
    "legend.edgeStash": "स्टैश और कमिट इस पर शेल्फ लगाया गया",
    "legend.edgeMergedChain": "मर्ज-इन कमिटें दे राहें चलने आह् ली इस शाखा दी लाइन",
    "legend.edgeMergedTie": "शाखा पर उसी कमिट पर लिखे दा हा",
    "menu.jumpHead": "⌖ चेकआउट पर जाओ",
    "menu.resetView": "⤢ दृश्य रीसेट करो",
    "menu.createBranch": "इत्थूं तों शाखा बनाओ...",
    "menu.checkout": "इस कमिट गी चेकआउट करो",
    "menu.copySha": "कॉपी कमिट एसएचए",
    "menu.pushBranch": "धक्का शाखा \"{name}\"।",
    "menu.renameCommit": "कमिट संदेश दा नाम बदलो...",
    "menu.undoCommit": "कमिट पूर्ववत करो (बदलाव रखो)...",
    "menu.viewChanges": "बदलाव दिक्खो...",
    "menu.jumpToOriginal": "मूल कमिट पर जाओ",
    "changes.title": "{sha} च बदलाव",
    "changes.tabChanged": "बदल गया",
    "changes.tabAll": "सारे फाइलें",
    "changes.added": "जोड़ा गया",
    "changes.modified": "संशोधित कीता गेआ",
    "changes.deleted": "डिलीट कर दिया",
    "changes.renamed": "नाम बदले दा",
    "changes.noChanges": "एह् कमिट कोई बी फाइल नेईं बदलदा ऐ.",
    "changes.loading": "लोड हो रहा है डिफ...",
    "changes.selectFile": "इसदे बदलावें गी दिक्खने लेई इक फाइल चुनो.",
    "changes.original": "मूल",
    "changes.changed": "इस कमिट दा",
    "changes.binary": "बाइनरी फ़ाइल — कोई पाठ diff दिखाने लेई।",
    "changes.tooLarge": "फाइल डिफर करने आस्तै मती बड्डी ऐ।",
    "changes.renamedFrom": "{path} थमां नाम बदली दित्ता गेआ",
    "changes.close": "बंद",
    "changes.prevChange": "पिछले बदलाव",
    "changes.nextChange": "अगला बदलाव",
    "changes.collapseAll": "सारे फोल्डर ढहाओ",
    "changes.expandAll": "सारे फोल्डर विस्तार करो",
    "changes.searchPlaceholder": "फाइलें दी खोज करो...",
    "changes.noSearchResults": "कोई बी फाइल तुंदी खोज कन्नै मेल नेईं खंदा।",
    "changes.maximize": "अधिकतम करो",
    "changes.restore": "आकार बहाल करो",
    "find.open": "इस डिफ (Ctrl+F) च सर्च करो",
    "find.placeholder": "डिफ में ढूंढें...",
    "find.prev": "पिछले मैच",
    "find.next": "अगला मैच",
    "find.close": "खोज बंद करो",
    "find.noResults": "कोई नतीजा नहीं",
    "commit.title": "बदलाव कमिट करो",
    "commit.close": "बंद",
    "commit.loading": "पढ़ना कम्म करने आला पेड़...",
    "commit.noChanges": "कमिट करने आस्तै कोई लोकल बदलाव नेईं ऐ।",
    "commit.selectFile": "इसदा डिफ दिखने लेई इक फाइल चुनो।",
    "commit.selectAll": "सारे चुनें",
    "commit.selectNone": "सब साफ कर दे",
    "commit.message": "संदेश कमिट करो",
    "commit.messagePlaceholder": "बदलाव दा बखान करो",
    "commit.selectedCount": "{count} फ़ाइल (एं) चुनी गेई ऐ",
    "commit.review": "समीक्षा",
    "commit.reviewTitle": "कमिट करने शा पैह् ले अंतिम जांच करो",
    "commit.reviewSummary": "संदेश कन्नै {count} फाइल (एं) गी कमिट करो: {message}",
    "commit.back": "पिच्छै",
    "commit.commit": "लोकल कमिट बनाओ",
    "menu.stashApply": "स्टैश लगाओ",
    "menu.stashPop": "पॉप स्टैश (लागू & हटाओ)",
    "menu.stashDrop": "ड्रॉप स्टैश...",
    "merge.title": "शाखा मर्ज करो",
    "merge.route": "वर्तमान शाखा च विलय करना",
    "merge.source": "(स्रोत) थमां",
    "merge.target": "(वर्तमान) च",
    "merge.loading": "विलय दा विश्लेषण करदे होई...",
    "merge.upToDate": "पहले से ही अद्यतन — विलय करने के लिए कुछ भी नहीं।",
    "merge.fastForward": "फास्ट-फॉरवर्ड — कोई मर्ज कमिट दी लोड़ नेईं ऐ (इक गी मजबूर करने आस्तै \"मर्ज कमिट बनाओ\" टिक करो)।",
    "merge.summary": "{files} फ़ाइल (s) बदली गेई, {conflicts} टकराव (s)।",
    "merge.conflictsWarning": "इस मर्ज च टकराव न — तुस अजें बी मर्ज करी सकदे ओ, फ्ही उनेंगी संपादक च हल करी सकदे ओ.",
    "merge.noChanges": "कोई फाइल बदलदा नेईं।",
    "merge.files": "नतीजे च बदलाव",
    "merge.added": "जोड़ा गया",
    "merge.modified": "संशोधित कीता गेआ",
    "merge.deleted": "डिलीट कर दिया",
    "merge.conflict": "झगड़ा",
    "merge.message": "कमिट संदेश मर्ज करो",
    "merge.messagePlaceholder": "शाखा 'स्रोत' गी मर्ज करो।",
    "merge.messageFfHint": "फास्ट-फॉरवर्ड मर्ज पर अनदेखा कीता गेआ (कोई कमिट नेईं बनाया जंदा ऐ)।",
    "merge.noFastForward": "मर्ज कमिट बनाओ (कोई फास्ट-फॉरवर्ड नेईं)",
    "merge.noFastForwardHint": "हमेशा इक मर्ज कमिट रिकार्ड करो, उसलै बी जिसलै शाखा फास्ट-फॉरवर्ड करी सकदी ही।",
    "merge.merge": "विलय कर दे",
    "merge.cancel": "रद्द",
    "merge.previewError": "पूर्वावलोकन दी गणना नेईं करी सकेआ: {message}",
    "merge.squashBadge": "स्क्वैश",
    "merge.squashNote": "स्क्वैश मर्ज — हर बदलाव \"{target}\" पर ONE कमिट दे रूप च उतरदा ऐ; “{source}” दे अपने कमिट अपने इतिहास थमां बाहर रौंह्दे न। सेटिंग्स › मर्ज च वापस स्विच करो।",
    "merge.messageSquash": "संदेश कमिट करो",
    "merge.squashMessagePlaceholder": "'स्रोत' थमां स्क्वैश कीते गेदे बदलाव।",
    "newBranch.title": "शाखा बनाओ",
    "newBranch.startPoint": "{sha} थमां शुरू होने आह् ली नमीं शाखा",
    "newBranch.startPointOn": "एक्सपीएलएच0एक्स पर",
    "newBranch.location": "थाहर",
    "newBranch.locationRoot": "(जड़)",
    "newBranch.expandAll": "सब विस्तार दे",
    "newBranch.collapseAll": "ढह दे सब",
    "newBranch.expandFolder": "फोल्डर विस्तार करो",
    "newBranch.collapseFolder": "फोल्डर ढहना",
    "newBranch.name": "शाखा दा नाम",
    "newBranch.namePlaceholder": "मेरी-शाखा",
    "newBranch.fullName": "पूरा नाम: एक्सपीएलएच0एक्स",
    "newBranch.invalid": "अमान्य शाखा दा नाम",
    "newBranch.exists": "इस नां कन्नै इक शाखा पैह्लें गै मौजूद ऐ",
    "newBranch.checkout": "रचना दे बाद शाखा चेकआउट करो",
    "newBranch.create": "बनाना",
    "newBranch.cancel": "रद्द",
    "status.loading": "ग्राफ लोड होआ करदा ऐ...",
    "status.summary": "{repo}{commits} कमिट, {refs} रेफर्स दिखाने",
    "status.branchCreated": "{sha} पर शाखा \"{name}\" बनाई",
    "status.error": "त्रुटि: एक्सपीएलएच0एक्स",
    "status.opFailed": "ऑपरेशन फेल हो गया",
    "status.fetching": "लाने आला...",
    "status.pulling": "खींचते हुए...",
    "status.pushing": "धक्का देंदे होई...",
    "status.committing": "लोकल कमिट बनाना...",
    "status.commitCreated": "स्थानीय कमिट {sha} बनाया: {message}",
    "status.syncing": "सिंक करदे होई...",
    "status.noHead": "ग्राफ च कोई वर्तमान चेकआउट नेईं लब्भा।",
    "status.undoing": "कमिट गी पूर्ववत करदे होई...",
    "status.commitUndone": "कमिट अनडन — बदलाव वापस कम्म करने आह् ले पेड़ च ऐ।",
    "status.undoConflict": "हिट टकराव गी पूर्ववत करो — संपादक च उनेंगी हल करो, फिर जारी रक्खो।",
    "status.stashApplying": "स्टैश लागू करदे होई...",
    "status.stashPopping": "पॉपिंग स्टैश...",
    "status.stashDropping": "स्टैश छोड़ने दा...",
    "status.stashApplied": "स्टैश लागू हो गया।",
    "status.stashPopped": "स्टैश पॉप हो गया।",
    "status.stashDropped": "स्टैश गिरा दे।",
    "status.stashConflict": "टकराव स्टैश करो — संपादक च उंदा हल करो।",
    "status.merging": "विलय करदे होई...",
    "status.merged": "मर्ज पूरा हो गया।",
    "status.mergeConflict": "मर्ज च टकराव होंदे न — उनेंगी संपादक च हल करो, फिर कमिट करो।",
    "search.title": "तपाश",
    "search.tooltip": "खोज कमिट करदा ऐ",
    "search.placeholder": "खोज कमिट करदा ऐ...",
    "search.previous": "पिछला नतीजा",
    "search.next": "अगला नतीजा",
    "search.noResults": "कोई नतीजा नहीं",
    "search.resultCount": "एक्सपीएलएच0एक्स/एक्सपीएलएच1एक्स",
    "search.filter": "फ़िल्टर मोड",
    "search.highlight": "हाइलाइट मोड",
    "search.filterMode": "खोज प्रदर्शन करना",
    "search.filterModeFilter": "फ़िल्टर (गैर-मेल छुपाओ)",
    "search.filterModeHighlight": "हाइलाइट करो (सभनें गी दस्सो, मिलान गी चिऱन्नत करो)",
    "search.filterModeHint": "ग्राफ च खोज दे नतीजे कि’यां प्रदर्शत होंदे न, इसदा चयन करो।",
    "search.visibility": "लोकेशन दी खोज करो",
    "search.visibilityToolbar": "टूलबार बटन",
    "search.visibilityAlwaysVisible": "सदा-दिक्खने वाली बार",
    "search.visibilityHint": "खोज गी शीर्शक पर टूलबार आइकन जां लगातार बार दे रूप च दस्सो।",
    "footer.github": "गिटहब",
  },
  ne: {
    "toolbar.refresh": "⟳ रिफ्रेस गर्नुहोस्",
    "toolbar.fetch": "⤓ ल्याउनुहोस्",
    "toolbar.pull": "⇩ तान्नुहोस्",
    "toolbar.push": "⇧ धक्का",
    "toolbar.commit": "✓ प्रतिबद्धता",
    "toolbar.sync": "⇅ सिंक",
    "toolbar.jumpHead": "⌖ चेकआउटमा जानुहोस्",
    "toolbar.reset": "⤢ दृश्य रिसेट गर्नुहोस्",
    "toolbar.settings": "⚙ सेटिङहरू",
    "details.header": "कमिट विवरणहरू",
    "details.close": "बन्द गर्नुहोस्",
    "details.sha": "SHA",
    "details.shortSha": "छोटो SHA",
    "details.message": "सन्देश",
    "details.author": "लेखक",
    "details.date": "मिति",
    "details.labels": "लेबलहरू",
    "details.location": "स्थान",
    "details.currentHead": "हेड - हालको चेकआउट",
    "settings.title": "सेटिङहरू",
    "settings.language": "भाषा",
    "settings.theme": "विषयवस्तु",
    "settings.themeLight": "उज्यालो",
    "settings.themeDark": "अँध्यारो",
    "settings.themeLightHint": "हल्का पृष्ठभूमि, गाढा पाठ।",
    "settings.themeDarkHint": "गाढा पृष्ठभूमि, हल्का पाठ।",
    "settings.mainBranch": "मुख्य शाखा",
    "settings.mainBranchAuto": "स्वचालित",
    "settings.mainBranchSearch": "शाखाहरू खोज्नुहोस्…",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModernHint": "नि: शुल्क क्यानभास - प्यानमा तान्नुहोस्, जुम गर्न स्क्रोल गर्नुहोस्।",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "सामान्य",
    "settings.sectionGraph": "ग्राफ",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOff": "लुकेको",
    "settings.diffMinimapOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOffHint": "कुनै सिंहावलोकन पट्टी छैन - सामान्य रूपमा भिन्नता स्क्रोल गर्नुहोस्।",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "प्रतिबद्ध हुनु अघि सोध्नुहोस्",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "कमिट बटनले तुरुन्तै स्थानीय कमिट सिर्जना गर्दछ।",
    "settings.svnBranchDialog": "SVN-शैली शाखा संवाद",
    "settings.svnBranchDialogHint": "शाखा सिर्जना गर्दा फोल्डर-ट्री पिकर देखाउनुहोस् (साधारण प्रम्प्टको सट्टा)।",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "SVN शैली",
    "settings.branchDialogNativeVscode": "VS कोड शैली",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "तपाईंको IDE को निर्मित शाखा प्रम्प्ट।",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "सकियो",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Git कार्यान्वयन योग्य",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "IDE को निर्मित Git विस्तारबाट git बाइनरी प्रयोग गर्नुहोस्।",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "ब्राउज गर्नुहोस्...",
    "settings.jargon": "Git सर्तहरू",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "अंग्रेजीमा राख्नुहोस्",
    "settings.jargonTranslateHint": "इन्टरफेस भाषामा Git सर्तहरू (पुल, पुश, कमिट, शाखा...) अनुवाद गर्नुहोस्।",
    "settings.jargonEnglishHint": "अंग्रेजीमा Git सर्तहरू (पुल, पुश, कमिट, शाखा...) राख्नुहोस्; अरू सबै अनुवाद गर्नुहोस्।",
    "settings.sectionMerge": "मर्ज गर्नुहोस्",
    "settings.mergeMode": "शैली मर्ज गर्नुहोस्",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "स्क्वास मर्ज",
    "settings.mergeModeMergeHint": "शाखालाई यसरी मर्ज गर्दछ: यसका कमिटहरू हालको शाखाको इतिहासको अंश बन्छन्, मर्ज कमिट (वा सम्भव भएमा द्रुत-फर्वार्ड) द्वारा जोडिएको हुन्छ।",
    "settings.mergeModeSquashHint": "हालको शाखा (git merge --squash) मा सम्पूर्ण शाखालाई एकल कमिटमा संक्षिप्त गर्दछ। शाखाको आफ्नै कमिटहरू हालको शाखाको इतिहासमा कहिल्यै प्रवेश गर्दैनन् - तपाईंले त्यहाँ एउटा मात्र कमिट देख्नुहुन्छ। शाखा आफैं अछुतो रहन्छ, त्यसैले यसको कमिटहरू आफ्नै लेनमा देखिने रहन्छन्।",
    "settings.mergedView": "ग्राफमा मर्ज गरिएका कमिटहरू",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "मर्ज गरिएको शाखाको कमिटहरू आफ्नै लेनमा मात्र कोरिन्छन्; मर्ज कनेक्टरको रूपमा देखा पर्दछ। क्लासिक दृश्य।",
    "settings.mergedViewTargetHint": "मर्जमा ल्याइएका कमिटहरू प्राप्त गर्ने शाखाको लेनमा पनि कोरिएका हुन्छन्, मर्ज कमिट अन्तर्गत स्ट्याक गरिएका हुन्छन् र मूलसँग स्तर हुन्छन् - किनभने त्यो शाखाले वास्तवमा ती अब समावेश गर्दछ। प्रतिलिपिहरू फिक्का छन् र ती शाखाहरूमा लेखिएका छन्, त्यसैले यो स्पष्ट रहन्छ कि तिनीहरू त्यहाँ बनाइएका थिएनन्। स्क्वास मर्जहरू अप्रभावित छन्: स्क्वाशले एउटा साधारण कमिट लेख्छ, त्यसैले त्यो कमिट मात्र देखापर्छ।",
    "legend.title": "पौराणिक कथा",
    "legend.head": "हेड / हालको शाखा",
    "legend.local": "स्थानीय शाखा",
    "legend.remote": "टाढाको शाखा",
    "legend.remoteOnly": "केवल बादलमा (न तानिएको)",
    "legend.tag": "ट्याग (संस्करण)",
    "legend.commit": "प्रतिबद्ध",
    "legend.stash": "स्ट्यास (अलब काम)",
    "legend.mergedIn": "मर्जको साथ आयो (अर्को शाखामा लेखिएको)",
    "legend.nodes": "बक्सहरू",
    "legend.lines": "रेखाहरू",
    "legend.edgeParent": "एउटै लाइनमा अघिल्लो प्रतिबद्धता",
    "legend.edgeMerge": "मर्ज-इन शाखा - मर्ज कमिटमा तीर बिन्दुहरू",
    "legend.edgeBranch": "यहाँ एउटा शाखा बन्द भयो",
    "legend.edgeStash": "स्ट्यास र कमिट यसमा राखिएको थियो",
    "legend.edgeMergedChain": "यो शाखाको लाइन मर्ज-इन कमिटहरू मार्फत चलिरहेको छ",
    "legend.edgeMergedTie": "शाखामा पनि त्यही कमिट लेखिएको थियो",
    "menu.jumpHead": "⌖ चेकआउटमा जानुहोस्",
    "menu.resetView": "⤢ दृश्य रिसेट गर्नुहोस्",
    "menu.createBranch": "यहाँबाट शाखा बनाउनुहोस्...",
    "menu.checkout": "यो प्रतिबद्धता जाँच गर्नुहोस्",
    "menu.copySha": "कमिट SHA प्रतिलिपि गर्नुहोस्",
    "menu.pushBranch": "पुश शाखा \"{name}\"",
    "menu.renameCommit": "प्रतिबद्ध सन्देश पुन: नामाकरण गर्नुहोस्...",
    "menu.undoCommit": "प्रतिबद्धता अनडू गर्नुहोस् (परिवर्तनहरू राख्नुहोस्)...",
    "menu.viewChanges": "परिवर्तनहरू हेर्नुहोस्...",
    "menu.jumpToOriginal": "मूल प्रतिबद्धतामा जानुहोस्",
    "node.mergedInTooltip": "मर्ज {merge} ले यो प्रतिबद्धतालाई \"{target}\" मा ल्यायो - यो \"{origin}\" मा लेखिएको थियो।",
    "changes.title": "{sha} मा परिवर्तनहरू",
    "changes.tabChanged": "परिवर्तन भयो",
    "changes.tabAll": "सबै फाइलहरू",
    "changes.added": "थपियो",
    "changes.modified": "परिमार्जित",
    "changes.deleted": "मेटाइयो",
    "changes.renamed": "पुन: नामाकरण गरियो",
    "changes.noChanges": "यो प्रतिबद्धता कुनै फाइल परिवर्तन गर्दैन।",
    "changes.loading": "भिन्नता लोड गर्दै...",
    "changes.selectFile": "यसको परिवर्तनहरू हेर्न फाइल चयन गर्नुहोस्।",
    "changes.original": "मौलिक",
    "changes.changed": "यो प्रतिबद्धता",
    "changes.binary": "बाइनरी फाइल - देखाउन कुनै पाठ भिन्नता छैन।",
    "changes.tooLarge": "फाइल फरक गर्न धेरै ठूलो छ।",
    "changes.renamedFrom": "{path} बाट पुन: नामाकरण गरियो",
    "changes.close": "बन्द गर्नुहोस्",
    "changes.prevChange": "अघिल्लो परिवर्तन",
    "changes.nextChange": "अर्को परिवर्तन",
    "changes.collapseAll": "सबै फोल्डरहरू संक्षिप्त गर्नुहोस्",
    "changes.expandAll": "सबै फोल्डरहरू विस्तार गर्नुहोस्",
    "changes.searchPlaceholder": "फाइलहरू खोज्नुहोस्...",
    "changes.noSearchResults": "कुनै पनि फाइल तपाईको खोजसँग मेल खाँदैन।",
    "changes.maximize": "अधिकतम गर्नुहोस्",
    "changes.restore": "आकार पुनर्स्थापित गर्नुहोस्",
    "find.open": "यो भिन्नतामा खोज्नुहोस् (Ctrl+F)",
    "find.placeholder": "फरकमा फेला पार्नुहोस्...",
    "find.prev": "अघिल्लो खेल",
    "find.next": "अर्को खेल",
    "find.close": "खोज बन्द गर्नुहोस्",
    "find.noResults": "कुनै परिणाम छैन",
    "commit.title": "परिवर्तनहरू प्रतिबद्ध गर्नुहोस्",
    "commit.close": "बन्द गर्नुहोस्",
    "commit.loading": "काम गर्ने रूख पढ्दै...",
    "commit.noChanges": "प्रतिबद्ध गर्न कुनै स्थानीय परिवर्तनहरू छैनन्।",
    "commit.selectFile": "यसको भिन्नता हेर्न फाइल चयन गर्नुहोस्।",
    "commit.selectAll": "सबै चयन गर्नुहोस्",
    "commit.selectNone": "सबै खाली गर्नुहोस्",
    "commit.message": "सन्देश पठाउनुहोस्",
    "commit.messagePlaceholder": "परिवर्तनको वर्णन गर्नुहोस्",
    "commit.selectedCount": "{count} फाइल(हरू) चयन गरियो",
    "commit.review": "समीक्षा गर्नुहोस्",
    "commit.reviewTitle": "प्रतिबद्ध गर्नु अघि अन्तिम जाँच",
    "commit.reviewSummary": "सन्देश सहित {count} फाइल(हरू) कमिट गर्नुहोस्: {message}",
    "commit.back": "पछाडि",
    "commit.commit": "स्थानीय प्रतिबद्धता सिर्जना गर्नुहोस्",
    "menu.stashApply": "स्ट्यास लागू गर्नुहोस्",
    "menu.stashPop": "पप स्ट्यास (लागू गर्नुहोस् र हटाउनुहोस्)",
    "menu.stashDrop": "थुपार्नुहोस्...",
    "merge.title": "शाखा मर्ज गर्नुहोस्",
    "merge.route": "हालको शाखामा मर्ज गर्दै",
    "merge.source": "बाट (स्रोत)",
    "merge.target": "भित्र (वर्तमान)",
    "merge.loading": "मर्जको विश्लेषण गर्दै...",
    "merge.upToDate": "पहिले नै अप टु डेट - मर्ज गर्न केहि छैन।",
    "merge.fastForward": "फास्ट-फर्वार्ड - कुनै मर्ज प्रतिबद्धता आवश्यक छैन (एकलाई बल गर्न \"मर्ज कमिट सिर्जना गर्नुहोस्\" मा टिक गर्नुहोस्)।",
    "merge.summary": "{files} फाइल(हरू) परिवर्तन भयो, {conflicts} विवाद(हरू)।",
    "merge.conflictsWarning": "यो मर्जमा विवादहरू छन् — तपाईं अझै पनि मर्ज गर्न सक्नुहुन्छ, त्यसपछि सम्पादकमा समाधान गर्नुहोस्।",
    "merge.noChanges": "कुनै फाइल परिवर्तन छैन।",
    "merge.files": "परिणाम स्वरूप परिवर्तनहरू",
    "merge.added": "थपियो",
    "merge.modified": "परिमार्जित",
    "merge.deleted": "मेटाइयो",
    "merge.conflict": "द्वन्द्व",
    "merge.message": "प्रतिबद्ध सन्देश मर्ज गर्नुहोस्",
    "merge.messagePlaceholder": "शाखा 'स्रोत' मर्ज गर्नुहोस्",
    "merge.messageFfHint": "द्रुत-फर्वार्ड मर्जमा बेवास्ता गरियो (कुनै कमिट सिर्जना गरिएको छैन)।",
    "merge.noFastForward": "मर्ज कमिट सिर्जना गर्नुहोस् (फास्ट-फर्वार्ड छैन)",
    "merge.noFastForwardHint": "शाखा फास्ट-फर्वार्ड गर्दा पनि सधैं मर्ज कमिट रेकर्ड गर्नुहोस्।",
    "merge.merge": "मर्ज गर्नुहोस्",
    "merge.cancel": "रद्द गर्नुहोस्",
    "merge.previewError": "पूर्वावलोकन गणना गर्न सकिएन: {message}",
    "merge.squashBadge": "स्क्वास",
    "merge.squashNote": "स्क्वास मर्ज — प्रत्येक परिवर्तन \"{target}\" मा एक प्रतिबद्धताको रूपमा अवतरण हुन्छ; \"{source}\" को आफ्नै प्रतिबद्धताहरू यसको इतिहासबाट बाहिर रहन्छन्। सेटिङहरूमा फर्कनुहोस् › मर्ज गर्नुहोस्।",
    "merge.messageSquash": "सन्देश पठाउनुहोस्",
    "merge.squashMessagePlaceholder": "'स्रोत' बाट स्क्वाश गरिएका परिवर्तनहरू",
    "newBranch.title": "शाखा बनाउनुहोस्",
    "newBranch.startPoint": "{sha} बाट सुरु हुने नयाँ शाखा",
    "newBranch.startPointOn": "{refs} मा",
    "newBranch.location": "स्थान",
    "newBranch.locationRoot": "(मूल)",
    "newBranch.expandAll": "सबै विस्तार गर्नुहोस्",
    "newBranch.collapseAll": "सबै संक्षिप्त गर्नुहोस्",
    "newBranch.expandFolder": "फोल्डर विस्तार गर्नुहोस्",
    "newBranch.collapseFolder": "फोल्डर संक्षिप्त गर्नुहोस्",
    "newBranch.name": "शाखाको नाम",
    "newBranch.namePlaceholder": "मेरो शाखा",
    "newBranch.fullName": "पूरा नाम: {name}",
    "newBranch.invalid": "अमान्य शाखा नाम",
    "newBranch.exists": "यस नामको शाखा पहिले नै अवस्थित छ",
    "newBranch.checkout": "सिर्जना पछि चेकआउट शाखा",
    "newBranch.create": "सिर्जना गर्नुहोस्",
    "newBranch.cancel": "रद्द गर्नुहोस्",
    "status.loading": "ग्राफ लोड गर्दै...",
    "status.summary": "{repo} {commits} कमिटहरू देखाउँदै, {refs} refs",
    "status.branchCreated": "{sha} मा शाखा \"{name}\" सिर्जना गरियो",
    "status.error": "त्रुटि: {message}",
    "status.opFailed": "सञ्चालन असफल भयो",
    "status.fetching": "ल्याउँदै...",
    "status.pulling": "तान्दै...",
    "status.pushing": "धकेल्दै...",
    "status.committing": "स्थानीय प्रतिबद्धता सिर्जना गर्दै...",
    "status.commitCreated": "स्थानीय प्रतिबद्धता {sha}: {message} सिर्जना गरियो",
    "status.syncing": "सिंक गर्दै...",
    "status.noHead": "ग्राफमा हालको चेकआउट फेला परेन।",
    "status.undoing": "प्रतिबद्धता अनडू गर्दै...",
    "status.commitUndone": "कमिट अनडू गर्नुहोस् — परिवर्तनहरू काम गर्ने रूखमा फर्किएका छन्।",
    "status.undoConflict": "हिट विवादहरू अनडू गर्नुहोस् - तिनीहरूलाई सम्पादकमा समाधान गर्नुहोस्, त्यसपछि जारी राख्नुहोस्।",
    "status.stashApplying": "स्ट्यास लागू गर्दै...",
    "status.stashPopping": "पपिङ स्ट्यास...",
    "status.stashDropping": "थुप्रो छोड्दै...",
    "status.stashApplied": "स्ट्यास लागू गरियो।",
    "status.stashPopped": "स्ट्यास पप भयो।",
    "status.stashDropped": "स्ट्यास खस्यो।",
    "status.stashConflict": "स्ट्यास विवादहरू - तिनीहरूलाई सम्पादकमा समाधान गर्नुहोस्।",
    "status.merging": "मर्ज गर्दै...",
    "status.merged": "मर्ज पूरा भयो।",
    "status.mergeConflict": "मर्जमा विवादहरू छन् - तिनीहरूलाई सम्पादकमा समाधान गर्नुहोस्, त्यसपछि कमिट गर्नुहोस्।",
    "search.title": "खोज्नुहोस्",
    "search.tooltip": "खोज कमिट",
    "search.placeholder": "खोज कमिटहरू...",
    "search.previous": "अघिल्लो नतिजा",
    "search.next": "अर्को परिणाम",
    "search.noResults": "कुनै परिणाम छैन",
    "search.resultCount": "{index}/{count}",
    "search.filter": "फिल्टर मोड",
    "search.highlight": "हाइलाइट मोड",
    "search.filterMode": "खोज प्रदर्शन",
    "search.filterModeFilter": "फिल्टर (गैर-मिलान लुकाउनुहोस्)",
    "search.filterModeHighlight": "हाइलाइट गर्नुहोस् (सबै देखाउनुहोस्, मिलान चिन्ह लगाउनुहोस्)",
    "search.filterModeHint": "ग्राफमा खोजी परिणामहरू कसरी देखाइन्छ भनेर छान्नुहोस्।",
    "search.visibility": "स्थान खोज्नुहोस्",
    "search.visibilityToolbar": "उपकरणपट्टी बटन",
    "search.visibilityAlwaysVisible": "सधैं देखिने पट्टी",
    "search.visibilityHint": "खोजलाई उपकरणपट्टी प्रतिमा वा शीर्षमा निरन्तर पट्टीको रूपमा देखाउनुहोस्।",
    "footer.github": "GitHub",
  },
  bho: {
    "toolbar.refresh": "⟳ ताज़ा कर दिहल जाला",
    "toolbar.fetch": "⤓ ले आवे के बा",
    "toolbar.pull": "⇩ खींच के ले आवल जाला",
    "toolbar.push": "⇧ धक्का दिहल जाला",
    "toolbar.commit": "✓ कमिट करे के बा",
    "toolbar.sync": "⇅ सिंक कइल जाला",
    "toolbar.jumpHead": "⌖ चेकआउट पर जाइए",
    "toolbar.reset": "⤢ दृश्य के रीसेट करीं",
    "toolbar.settings": "⚙ सेटिंग्स के बारे में बतावल गइल बा",
    "details.header": "विस्तार से कमिट करीं",
    "details.close": "बंद करीं",
    "details.sha": "शा के बा",
    "details.shortSha": "लघु एसएचए के बा",
    "details.message": "सनेस",
    "details.author": "लेखक",
    "details.date": "तारीख",
    "details.labels": "लेबल के बारे में बतावल गइल बा",
    "details.location": "अस्थान",
    "details.currentHead": "HEAD — वर्तमान चेकआउट के बा",
    "settings.title": "सेटिंग्स के बारे में बतावल गइल बा",
    "settings.language": "भाखा",
    "settings.theme": "विषय",
    "settings.themeLight": "उजियार",
    "settings.themeDark": "अन्हरिया",
    "settings.themeLightHint": "हल्का पृष्ठभूमि, गहरे रंग के पाठ।",
    "settings.themeDarkHint": "गहरे रंग के पृष्ठभूमि, हल्का पाठ।",
    "settings.mainBranch": "मुख्य शाखा के बा",
    "settings.mainBranchAuto": "स्वचालित बा",
    "settings.mainBranchSearch": "शाखा के खोज करीं...",
    "settings.mainBranchNoMatch": "कवनो मिलान शाखा ना होखे",
    "settings.display": "प्रदर्शन शैली के बा",
    "settings.displayModern": "आधुनिक",
    "settings.displayClassic": "क्लासिक के बा",
    "settings.displayModernHint": "मुफ्त कैनवास — पैन में ड्रैग करीं, ज़ूम करे खातिर स्क्रॉल करीं।",
    "settings.displayClassicHint": "फिक्स कैनवास — ट्रंक बाईं ओर पिन कइल गइल, कवनो ज़ूम नइखे, खाली स्क्रॉल करीं (एसवीएन रिवीजन ग्राफ नियर)।",
    "settings.sectionGeneral": "साधारन",
    "settings.sectionGraph": "ग्राफ के बा",
    "settings.sectionChanges": "दृष्टिकोण बदल देला",
    "settings.diffMinimap": "डिफ मिनीमैप के बा",
    "settings.diffMinimapOn": "देखावल गइल बा",
    "settings.diffMinimapOff": "छिपल",
    "settings.diffMinimapOnHint": "डिफ के बगल में वीएस कोड-शैली के अवलोकन पट्टी देखाईं — स्क्रॉल करे खातिर एकरा के खींची; बदलाव के निशान शामिल बा।",
    "settings.diffMinimapOffHint": "कवनो अवलोकन पट्टी ना — सामान्य रूप से अंतर के स्क्रॉल करीं।",
    "settings.commitReview": "समीक्षा के प्रतिबद्धता जतावे के बा",
    "settings.commitReviewOn": "कमिट करे से पहिले पूछ लीं",
    "settings.commitReviewOff": "सीधे कमिट करे के बा",
    "settings.commitReviewOnHint": "स्थानीय कमिट बनावे से पहिले अंतिम फाइल सूची आ संदेश जांच देखाईं।",
    "settings.commitReviewOffHint": "Commit बटन तुरंत स्थानीय कमिट बनावेला।",
    "settings.svnBranchDialog": "एसवीएन-शैली के शाखा संवाद बा",
    "settings.svnBranchDialogHint": "शाखा बनावे के समय (साधारण प्रॉम्प्ट के बजाय) फोल्डर-ट्री पिकर देखाईं।",
    "settings.branchDialog": "शाखा के संवाद के बारे में बतावल गइल बा",
    "settings.branchDialogSvnTitle": "एसवीएन स्टाइल के बा",
    "settings.branchDialogNativeVscode": "वीएस कोड स्टाइल के बा",
    "settings.branchDialogNativeVs": "विजुअल स्टूडियो स्टाइल के बा",
    "settings.branchDialogNativeHint": "राउर आईडीई के बिल्ट-इन शाखा प्रॉम्प्ट बा।",
    "settings.close": "बंद करीं",
    "settings.done": "हो गईल",
    "settings.sectionAdvanced": "उन्नत",
    "settings.gitSource": "गिट एक्जीक्यूटिव के बा",
    "settings.gitSourceBuiltin": "बिल्ट-इन के बा",
    "settings.gitSourceBuiltinHint": "आईडीई के बिल्ट-इन गिट एक्सटेंशन से git बाइनरी के इस्तेमाल करीं।",
    "settings.gitSourceCustom": "कस्टम पथ के बा",
    "settings.gitPath": "गिट के रास्ता बा",
    "settings.gitPathPlaceholder": "/usr/bin/git के बा",
    "settings.gitPathBrowse": "ब्राउज़ करीं...",
    "settings.jargon": "गिट के शब्दन के बारे में बतावल गइल बा",
    "settings.jargonTranslate": "अनुवाद",
    "settings.jargonEnglish": "अंग्रेजी में रखे के बा",
    "settings.jargonTranslateHint": "Git शब्द (पुल, पुश, कमिट, शाखा...) के इंटरफेस भाषा में अनुवाद करीं।",
    "settings.jargonEnglishHint": "गिट के शब्द (पुल, पुश, कमिट, शाखा...) के अंगरेजी में रखीं; बाकी सब कुछ के अनुवाद करीं।",
    "settings.sectionMerge": "विलय हो गइल बा",
    "settings.mergeMode": "मर्ज स्टाइल के बा",
    "settings.mergeModeMerge": "सामान्य विलय होला",
    "settings.mergeModeSquash": "स्क्वैश के विलय हो जाला",
    "settings.mergeModeMergeHint": "शाखा के जइसन बा ओइसने मर्ज करे ला: एकर कमिट वर्तमान शाखा के इतिहास के हिस्सा बन जाला, मर्ज कमिट (या संभव होखे पर फास्ट-फोरवर्ड) द्वारा जोड़ल जाला।",
    "settings.mergeModeSquashHint": "पूरा शाखा के वर्तमान शाखा पर एकही कमिट में ढह देला (git merge --squash)। शाखा के आपन कमिट कबो वर्तमान शाखा के इतिहास में ना आवेला — रउआ उहाँ खाली एगो कमिट देखाई देला। शाखा खुद अछूता छोड़ दिहल जाला, एहसे ओकर कमिट अपना लेन में लउकत रहेला।",
    "settings.mergedView": "ग्राफ में मर्ज कइल कमिट",
    "settings.mergedViewBranch": "खाली अपना शाखा पर",
    "settings.mergedViewTarget": "साथ ही जवना शाखा में उ लोग के विलय हो गईल रहे",
    "settings.mergedViewBranchHint": "मर्ज कइल शाखा के कमिट खाली अपना लेन में खींचा जाला; मर्ज एगो कनेक्टर के रूप में देखावल जाला। क्लासिक दृश्य के बा।",
    "settings.mergedViewTargetHint": "मर्ज के ले आवल कमिट सभ के रिसीविंग शाखा के लेन में भी खींचा जाला, मर्ज कमिट के नीचे ढेर कइल जाला आ मूल के साथ लेवल कइल जाला — काहें से कि ओह शाखा में अब वास्तव में ई सभ सामिल बाड़ें। प्रतिलिपि पीयर बा आ जवना शाखा पर लिखल गइल रहे ओकरा साथे सिर बा, एहसे साफ बा कि ऊ ओहिजा ना बनल रहे. स्क्वैश मर्ज पर कवनो असर ना पड़े ला: स्क्वैश एगो साधारण कमिट लिखे ला, एह से खाली ऊ कमिट कबो लउके ला।",
    "legend.title": "दंतकथा",
    "legend.head": "HEAD / वर्तमान शाखा के बा",
    "legend.local": "स्थानीय शाखा के बा",
    "legend.remote": "दूरस्थ शाखा के बा",
    "legend.remoteOnly": "खाली बादल में (खींचल ना)",
    "legend.tag": "टैग (संस्करण) के बा।",
    "legend.commit": "बंध गईल",
    "legend.stash": "स्टैश (अलमारी में राखल काम) 1.1.",
    "legend.mergedIn": "मर्ज (दूसरा शाखा पर लिखल) के साथ अंदर आइल",
    "legend.nodes": "बक्सा के बा",
    "legend.lines": "लाइनन के बा",
    "legend.edgeParent": "एही लाइन पर पिछला कमिट कइल गइल",
    "legend.edgeMerge": "मर्ज-इन शाखा — तीर मर्ज कमिट पर इशारा करे ला",
    "legend.edgeBranch": "इहाँ एगो डाढ़ कांटा हो गईल",
    "legend.edgeStash": "स्टैश आ कमिट जवना पर एकरा के शेल्फ पर राखल गइल रहे",
    "legend.edgeMergedChain": "मर्ज-इन कमिट के माध्यम से चले वाली एह शाखा के लाइन",
    "legend.edgeMergedTie": "जवना शाखा पर लिखल रहे उहे कमिट",
    "menu.jumpHead": "⌖ चेकआउट पर जाइए",
    "menu.resetView": "⤢ दृश्य के रीसेट करीं",
    "menu.createBranch": "इहाँ से शाखा बनाईं...",
    "menu.checkout": "एह कमिट के देखल जाव",
    "menu.copySha": "कॉपी कमिट एसएचए के बा",
    "menu.pushBranch": "धक्का शाखा \"{name}\" के बा।",
    "menu.renameCommit": "कमिट संदेश के नाम बदल के...",
    "menu.undoCommit": "कमिट के पूर्ववत करीं (बदलाव रखीं)...",
    "menu.viewChanges": "बदलाव देखल जाव...",
    "menu.jumpToOriginal": "मूल कमिट पर जाइए",
    "changes.title": "{sha} में बदलाव भइल बा",
    "changes.tabChanged": "बदल गइल बा",
    "changes.tabAll": "सब फाइल के बा",
    "changes.added": "जोड़ल गइल बा",
    "changes.modified": "संशोधित कइल गइल बा",
    "changes.deleted": "हटा दिहल गइल बा",
    "changes.renamed": "नाम बदल दिहल गइल",
    "changes.noChanges": "ई कमिट कवनो फाइल में बदलाव ना करे ला।",
    "changes.loading": "लोड हो रहल बा डिफ...",
    "changes.selectFile": "ओकर बदलाव देखे खातिर कवनो फाइल चुनीं.",
    "changes.original": "असली",
    "changes.changed": "ई प्रतिबद्धता बा",
    "changes.binary": "बाइनरी फाइल — देखावे खातिर कवनो टेक्स्ट डिफ ना।",
    "changes.tooLarge": "फाइल बहुते बड़हन बा जवना से डिफफ ना हो सके.",
    "changes.renamedFrom": "{path} से नाम बदल दिहल गइल",
    "changes.close": "बंद करीं",
    "changes.prevChange": "पहिले के बदलाव भइल बा",
    "changes.nextChange": "अगिला बदलाव के बा",
    "changes.collapseAll": "सभ फोल्डर के संकुचित कर दीं",
    "changes.expandAll": "सभ फोल्डर के विस्तार करीं",
    "changes.searchPlaceholder": "फाइल खोजल जाव...",
    "changes.noSearchResults": "कवनो फाइल राउर खोज से मेल ना खात बा.",
    "changes.maximize": "अधिकतम कइल जाव",
    "changes.restore": "आकार के बहाल कर दिहल जाव",
    "find.open": "एह डिफ (Ctrl+F) में खोज करीं",
    "find.placeholder": "डिफ में खोजल जाला...",
    "find.prev": "पिछला मैच के बा",
    "find.next": "अगिला मैच बा",
    "find.close": "खोज बंद कर दीं",
    "find.noResults": "कवनो नतीजा ना निकलल",
    "commit.title": "बदलाव के कमिट करीं",
    "commit.close": "बंद करीं",
    "commit.loading": "पढ़त बानी काम करे वाला पेड़...",
    "commit.noChanges": "प्रतिबद्धता खातिर कवनो स्थानीय बदलाव नइखे.",
    "commit.selectFile": "ओकर अंतर देखे खातिर कवनो फाइल चुनीं.",
    "commit.selectAll": "सब के चुनीं",
    "commit.selectNone": "सब साफ कर दीं",
    "commit.message": "संदेश कमिट करीं",
    "commit.messagePlaceholder": "बदलाव के वर्णन करीं",
    "commit.selectedCount": "{count} फाइल (फाइल) चुनल गइल बा",
    "commit.review": "समीक्षा",
    "commit.reviewTitle": "कमिट से पहिले अंतिम जांच करीं",
    "commit.reviewSummary": "संदेश के साथ {count} फाइल (फाइल) के कमिट करीं: {message}",
    "commit.back": "पीछे",
    "commit.commit": "स्थानीय कमिट बनाईं",
    "menu.stashApply": "स्टैश लगावे के बा",
    "menu.stashPop": "पॉप स्टैश (लागू & हटाईं) के बा।",
    "menu.stashDrop": "ड्रॉप स्टैश के बा...",
    "merge.title": "शाखा के विलय कर दिहल जाव",
    "merge.route": "वर्तमान शाखा में विलय हो रहल बा",
    "merge.source": "(स्रोत) से मिलल बा",
    "merge.target": "में (वर्तमान) के बा।",
    "merge.loading": "विलय के विश्लेषण हो रहल बा...",
    "merge.upToDate": "पहिलहीं से अपडेट बा — विलय करे खातिर कुछ नइखे.",
    "merge.fastForward": "फास्ट-फोरवर्ड — कवनो मर्ज कमिट के जरूरत नइखे (एक के मजबूर करे खातिर “मर्ज कमिट बनाईं” पर टिक करीं)।",
    "merge.summary": "{files} फाइल (फाइल) बदल गइल, {conflicts} टकराव (स)।",
    "merge.conflictsWarning": "एह मर्ज में टकराव बा — रउआँ अबहियों मर्ज क सकत बानी, फिर संपादक में हल कर सकत बानी।",
    "merge.noChanges": "कवनो फाइल में बदलाव ना होखे.",
    "merge.files": "एकरा चलते बदलाव होखता",
    "merge.added": "जोड़ल गइल बा",
    "merge.modified": "संशोधित कइल गइल बा",
    "merge.deleted": "हटा दिहल गइल बा",
    "merge.conflict": "टकराव",
    "merge.message": "कमिट संदेश के मर्ज करीं",
    "merge.messagePlaceholder": "शाखा 'स्रोत' के विलय करीं।",
    "merge.messageFfHint": "फास्ट-फोरवर्ड मर्ज पर अनदेखी कइल जाला (कवनो कमिट ना बनावल जाला)।",
    "merge.noFastForward": "मर्ज कमिट बनाईं (फास्ट-फोरवर्ड ना)",
    "merge.noFastForwardHint": "हमेशा मर्ज कमिट रिकार्ड करीं, तबहूँ जब शाखा फास्ट-फॉरवर्ड कर सके।",
    "merge.merge": "विलय हो गइल बा",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "पूर्वावलोकन के गणना ना कइल जा सकल: {message}",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "शाखा बनावे के बा",
    "newBranch.startPoint": "{sha} से शुरू होखे वाला नया शाखा",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "ऑपरेशन फेल हो गइल",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "तलाशी",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "एक्सपीएलएच0एक्स/एक्सपीएलएच1एक्स के बा",
    "search.filter": "फिल्टर मोड के बा",
    "search.highlight": "हाइलाइट मोड के बा",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "हाइलाइट करीं (सब देखाईं, मिलान के निशान करीं)",
    "search.filterModeHint": "चुनीं कि ग्राफ में खोज परिणाम कइसे देखावल जाला.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  mr: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "⇩ ओढा",
    "toolbar.push": "⇧ ढकलणे",
    "toolbar.commit": "✓ वचनबद्ध",
    "toolbar.sync": "⇅ सिंक",
    "toolbar.jumpHead": "⌖ चेकआउट वर जा",
    "toolbar.reset": "⤢ दृश्य रीसेट करा",
    "toolbar.settings": "⚙ सेटिंग्ज",
    "details.header": "कमिट तपशील",
    "details.close": "बंद करा",
    "details.sha": "SHA",
    "details.shortSha": "लहान SHA",
    "details.message": "संदेश",
    "details.author": "लेखक",
    "details.date": "तारीख",
    "details.labels": "लेबल्स",
    "details.location": "स्थान",
    "details.currentHead": "हेड — वर्तमान चेकआउट",
    "settings.title": "सेटिंग्ज",
    "settings.language": "भाषा",
    "settings.theme": "थीम",
    "settings.themeLight": "प्रकाश",
    "settings.themeDark": "गडद",
    "settings.themeLightHint": "हलकी पार्श्वभूमी, गडद मजकूर.",
    "settings.themeDarkHint": "गडद पार्श्वभूमी, हलका मजकूर.",
    "settings.mainBranch": "मुख्य शाखा",
    "settings.mainBranchAuto": "स्वयंचलित",
    "settings.mainBranchSearch": "शाखा शोधा…",
    "settings.mainBranchNoMatch": "जुळणाऱ्या शाखा नाहीत",
    "settings.display": "प्रदर्शन शैली",
    "settings.displayModern": "आधुनिक",
    "settings.displayClassic": "क्लासिक",
    "settings.displayModernHint": "विनामूल्य कॅनव्हास — पॅनवर ड्रॅग करा, झूम करण्यासाठी स्क्रोल करा.",
    "settings.displayClassicHint": "निश्चित कॅनव्हास — ट्रंक डावीकडे पिन केलेला, झूम नाही, फक्त स्क्रोल करा (SVN पुनरावृत्ती आलेखाप्रमाणे).",
    "settings.sectionGeneral": "सामान्य",
    "settings.sectionGraph": "आलेख",
    "settings.sectionChanges": "दृश्य बदलते",
    "settings.diffMinimap": "भिन्न मिनीमॅप",
    "settings.diffMinimapOn": "दाखवले",
    "settings.diffMinimapOff": "लपलेले",
    "settings.diffMinimapOnHint": "डिफच्या बाजूला VS कोड-शैलीचे विहंगावलोकन पट्टी दाखवा — स्क्रोल करण्यासाठी ड्रॅग करा; बदल मार्कर समाविष्ट.",
    "settings.diffMinimapOffHint": "विहंगावलोकन पट्टी नाही — सामान्यपणे फरक स्क्रोल करा.",
    "settings.commitReview": "पुनरावलोकन वचनबद्ध करा",
    "settings.commitReviewOn": "कमिट करण्यापूर्वी विचारा",
    "settings.commitReviewOff": "थेट वचनबद्ध",
    "settings.commitReviewOnHint": "स्थानिक कमिट तयार करण्यापूर्वी अंतिम फाइल सूची आणि संदेश तपासा.",
    "settings.commitReviewOffHint": "कमिट बटण ताबडतोब स्थानिक कमिट तयार करते.",
    "settings.svnBranchDialog": "SVN-शैलीतील शाखा संवाद",
    "settings.svnBranchDialogHint": "शाखा तयार करताना फोल्डर-ट्री पिकर दाखवा (साध्या प्रॉम्प्टऐवजी).",
    "settings.branchDialog": "शाखा संवाद",
    "settings.branchDialogSvnTitle": "SVN-शैली",
    "settings.branchDialogNativeVscode": "व्हीएस कोड शैली",
    "settings.branchDialogNativeVs": "व्हिज्युअल स्टुडिओ शैली",
    "settings.branchDialogNativeHint": "तुमचा IDE चा अंगभूत शाखा प्रॉम्प्ट.",
    "settings.close": "बंद करा",
    "settings.done": "झाले",
    "settings.sectionAdvanced": "प्रगत",
    "settings.gitSource": "Git एक्झिक्युटेबल",
    "settings.gitSourceBuiltin": "अंगभूत",
    "settings.gitSourceBuiltinHint": "IDE च्या अंगभूत Git एक्स्टेंशनमधून git बायनरी वापरा.",
    "settings.gitSourceCustom": "सानुकूल मार्ग",
    "settings.gitPath": "गिटचा मार्ग",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ब्राउझ करा...",
    "settings.jargon": "Git अटी",
    "settings.jargonTranslate": "भाषांतर करा",
    "settings.jargonEnglish": "इंग्रजीत ठेवा",
    "settings.jargonTranslateHint": "इंटरफेस भाषेत Git संज्ञांचे (पुल, पुश, कमिट, ब्रँच...) भाषांतर करा.",
    "settings.jargonEnglishHint": "Git शब्द (पुल, पुश, कमिट, ब्रँच...) इंग्रजीत ठेवा; बाकी सर्व भाषांतर करा.",
    "settings.sectionMerge": "विलीन करा",
    "settings.mergeMode": "शैली विलीन करा",
    "settings.mergeModeMerge": "सामान्य विलीनीकरण",
    "settings.mergeModeSquash": "स्क्वॅश विलीन",
    "settings.mergeModeMergeHint": "शाखा जशी आहे तशी विलीन करते: त्याची कमिट सध्याच्या शाखेच्या इतिहासाचा भाग बनतात, विलीन कमिट (किंवा शक्य असेल तेव्हा फास्ट-फॉरवर्ड) द्वारे जोडले जातात.",
    "settings.mergeModeSquashHint": "सध्याच्या शाखेवर (git merge --squash) संपूर्ण शाखा एकाच कमिटमध्ये संकुचित करते. शाखेच्या स्वत:च्या कमिट कधीही वर्तमान शाखेच्या इतिहासात प्रवेश करत नाहीत — तुम्हाला तिथे फक्त एक कमिट दिसेल. शाखा स्वतःच अस्पर्शित राहिली आहे, म्हणून तिच्या कमिट स्वतःच्या गल्लीत दृश्यमान राहतात.",
    "settings.mergedView": "आलेखामध्ये विलीन केलेले कमिट",
    "settings.mergedViewBranch": "फक्त त्यांच्याच शाखेत",
    "settings.mergedViewTarget": "शाखेतही ते विलीन झाले",
    "settings.mergedViewBranchHint": "विलीन केलेल्या शाखेच्या कमिट फक्त त्यांच्या स्वतःच्या लेनमध्ये काढल्या जातात; विलीनीकरण कनेक्टर म्हणून दिसते. क्लासिक दृश्य.",
    "settings.mergedViewTargetHint": "आणलेले विलीनीकरण प्राप्त करणाऱ्या शाखेच्या लेनमध्ये देखील काढले जाते, विलीनीकरण कमिट अंतर्गत स्टॅक केले जाते आणि मूळच्या बरोबरीने स्तर केले जाते — कारण त्या शाखेत ते आता आहेत. प्रती फिकट गुलाबी आहेत आणि त्या ज्या शाखेवर लिहिल्या होत्या त्या फांदीच्या दिशेने आहेत, त्यामुळे ते तिथे बनवलेले नव्हते हे स्पष्ट होते. स्क्वॅश विलीनीकरण प्रभावित होत नाही: स्क्वॅश एक सामान्य कमिट लिहितो, त्यामुळे फक्त ती कमिट दिसते.",
    "legend.title": "दंतकथा",
    "legend.head": "प्रमुख / वर्तमान शाखा",
    "legend.local": "स्थानिक शाखा",
    "legend.remote": "दूरस्थ शाखा",
    "legend.remoteOnly": "फक्त ढगात (खेचले नाही)",
    "legend.tag": "टॅग (आवृत्ती)",
    "legend.commit": "वचनबद्ध",
    "legend.stash": "स्टॅश (शेल्फ केलेले काम)",
    "legend.mergedIn": "विलीनीकरणासह आले (दुसऱ्या शाखेवर लिहिलेले)",
    "legend.nodes": "पेट्या",
    "legend.lines": "ओळी",
    "legend.edgeParent": "त्याच ओळीवर मागील कमिट",
    "legend.edgeMerge": "विलीन केलेली शाखा — मर्ज कमिटमधील बाण बिंदू",
    "legend.edgeBranch": "येथे एक शाखा बंद पडली",
    "legend.edgeStash": "स्टॅश आणि कमिट त्यावर ठेवली होती",
    "legend.edgeMergedChain": "या शाखेची ओळ विलीन-इन कमिटमधून चालते",
    "legend.edgeMergedTie": "तीच कमिट ज्या शाखेवर लिहिली होती",
    "menu.jumpHead": "⌖ चेकआउट वर जा",
    "menu.resetView": "⤢ दृश्य रीसेट करा",
    "menu.createBranch": "येथून शाखा तयार करा...",
    "menu.checkout": "हे वचन तपासा",
    "menu.copySha": "SHA कॉपी करा",
    "menu.pushBranch": "पुश शाखा \"{name}\"",
    "menu.renameCommit": "कमिट मेसेजचे नाव बदला...",
    "menu.undoCommit": "कमिट पूर्ववत करा (बदल ठेवा)…",
    "menu.viewChanges": "बदल पहा...",
    "menu.jumpToOriginal": "मूळ कमिट वर जा",
    "node.mergedInTooltip": "मर्ज {merge} ने ही कमिट \"{target}\" मध्ये आणली - ती \"{origin}\" वर लिहिलेली होती.",
    "changes.title": "{sha} मध्ये बदल",
    "changes.tabChanged": "बदलले",
    "changes.tabAll": "सर्व फायली",
    "changes.added": "जोडले",
    "changes.modified": "सुधारित",
    "changes.deleted": "हटवले",
    "changes.renamed": "नाव बदलले",
    "changes.noChanges": "हे कमिट कोणत्याही फायली बदलत नाही.",
    "changes.loading": "फरक लोड करत आहे...",
    "changes.selectFile": "बदल पाहण्यासाठी फाइल निवडा.",
    "changes.original": "मूळ",
    "changes.changed": "हे वचनबद्ध",
    "changes.binary": "बायनरी फाइल — दर्शविण्यासाठी कोणताही मजकूर फरक नाही.",
    "changes.tooLarge": "फाईल फार मोठी आहे.",
    "changes.renamedFrom": "{path} वरून पुनर्नामित केले",
    "changes.close": "बंद करा",
    "changes.prevChange": "मागील बदल",
    "changes.nextChange": "पुढील बदल",
    "changes.collapseAll": "सर्व फोल्डर संकुचित करा",
    "changes.expandAll": "सर्व फोल्डर विस्तृत करा",
    "changes.searchPlaceholder": "फायली शोधा…",
    "changes.noSearchResults": "तुमच्या शोधाशी कोणत्याही फाइल्स जुळत नाहीत.",
    "changes.maximize": "जास्तीत जास्त करा",
    "changes.restore": "आकार पुनर्संचयित करा",
    "find.open": "या फरकामध्ये शोधा (Ctrl+F)",
    "find.placeholder": "फरक शोधा...",
    "find.prev": "मागील सामना",
    "find.next": "पुढचा सामना",
    "find.close": "शोध बंद करा",
    "find.noResults": "कोणतेही परिणाम नाहीत",
    "commit.title": "बदल वचनबद्ध करा",
    "commit.close": "बंद करा",
    "commit.loading": "कार्यरत झाड वाचत आहे…",
    "commit.noChanges": "कमिट करण्यासाठी कोणतेही स्थानिक बदल नाहीत.",
    "commit.selectFile": "त्यातील फरक पाहण्यासाठी फाइल निवडा.",
    "commit.selectAll": "सर्व निवडा",
    "commit.selectNone": "सर्व साफ करा",
    "commit.message": "वचनबद्ध संदेश",
    "commit.messagePlaceholder": "बदलाचे वर्णन करा",
    "commit.selectedCount": "{count} फाइल निवडली",
    "commit.review": "पुनरावलोकन करा",
    "commit.reviewTitle": "कमिट करण्यापूर्वी अंतिम तपासणी",
    "commit.reviewSummary": "संदेशासह {count} फाईल कमिट करा: {message}",
    "commit.back": "मागे",
    "commit.commit": "स्थानिक कमिट तयार करा",
    "menu.stashApply": "स्टॅश लावा",
    "menu.stashPop": "पॉप स्टॅश (लागू करा आणि काढा)",
    "menu.stashDrop": "स्टॅश टाका...",
    "merge.title": "शाखा विलीन करा",
    "merge.route": "सध्याच्या शाखेत विलीन होत आहे",
    "merge.source": "(स्रोत) कडून",
    "merge.target": "मध्ये (वर्तमान)",
    "merge.loading": "विलीनीकरणाचे विश्लेषण करत आहे...",
    "merge.upToDate": "आधीच अद्ययावत — विलीन करण्यासाठी काहीही नाही.",
    "merge.fastForward": "फास्ट-फॉरवर्ड — विलीनीकरण कमिटची आवश्यकता नाही (जबरदस्ती करण्यासाठी “मर्ज कमिट तयार करा” वर टिक करा).",
    "merge.summary": "{files} फाईल बदलली, {conflicts} विवाद",
    "merge.conflictsWarning": "या विलीनीकरणामध्ये विरोधाभास आहेत — तुम्ही तरीही विलीन करू शकता, नंतर संपादकामध्ये त्यांचे निराकरण करू शकता.",
    "merge.noChanges": "फाइल बदलत नाही.",
    "merge.files": "परिणामी बदल",
    "merge.added": "जोडले",
    "merge.modified": "सुधारित",
    "merge.deleted": "हटवले",
    "merge.conflict": "संघर्ष",
    "merge.message": "प्रतिबद्ध संदेश विलीन करा",
    "merge.messagePlaceholder": "शाखा 'स्रोत' विलीन करा",
    "merge.messageFfHint": "फास्ट-फॉरवर्ड विलीनीकरणाकडे दुर्लक्ष केले (कोणतीही कमिट तयार केलेली नाही).",
    "merge.noFastForward": "मर्ज कमिट तयार करा (फास्ट फॉरवर्ड नाही)",
    "merge.noFastForwardHint": "शाखा फास्ट-फॉरवर्ड करू शकत असताना देखील नेहमी मर्ज कमिट रेकॉर्ड करा.",
    "merge.merge": "विलीन करा",
    "merge.cancel": "रद्द करा",
    "merge.previewError": "पूर्वावलोकनाची गणना करू शकलो नाही: {message}",
    "merge.squashBadge": "स्क्वॅश",
    "merge.squashNote": "स्क्वॅश विलीनीकरण — प्रत्येक बदल “{target}” वर एक कमिट म्हणून येतो; “{source}” ची स्वतःची कमिटं त्याच्या इतिहासापासून दूर राहतात. सेटिंग्जमध्ये परत स्विच करा › मर्ज करा.",
    "merge.messageSquash": "वचनबद्ध संदेश",
    "merge.squashMessagePlaceholder": "'स्रोत' वरून स्क्वॅश केलेले बदल",
    "newBranch.title": "शाखा तयार करा",
    "newBranch.startPoint": "नवीन शाखा {sha} पासून सुरू होत आहे",
    "newBranch.startPointOn": "{refs} वर",
    "newBranch.location": "स्थान",
    "newBranch.locationRoot": "(मूळ)",
    "newBranch.expandAll": "सर्व विस्तृत करा",
    "newBranch.collapseAll": "सर्व संकुचित करा",
    "newBranch.expandFolder": "फोल्डर विस्तृत करा",
    "newBranch.collapseFolder": "फोल्डर संकुचित करा",
    "newBranch.name": "शाखेचे नाव",
    "newBranch.namePlaceholder": "माझी शाखा",
    "newBranch.fullName": "पूर्ण नाव: {name}",
    "newBranch.invalid": "अवैध शाखेचे नाव",
    "newBranch.exists": "या नावाची शाखा आधीपासून अस्तित्वात आहे",
    "newBranch.checkout": "निर्मितीनंतर शाखा तपासा",
    "newBranch.create": "तयार करा",
    "newBranch.cancel": "रद्द करा",
    "status.loading": "आलेख लोड करत आहे...",
    "status.summary": "{repo} {commits} कमिट दाखवत आहे, {refs} refs",
    "status.branchCreated": "{sha} येथे \"{name}\" शाखा तयार केली",
    "status.error": "त्रुटी: {message}",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "आणत आहे...",
    "status.pulling": "खेचत आहे...",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "स्थानिक कमिट तयार करत आहे...",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "सिंक करत आहे...",
    "status.noHead": "आलेखामध्ये कोणतेही वर्तमान चेकआउट आढळले नाही.",
    "status.undoing": "वचनबद्धता पूर्ववत करत आहे...",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "हिट विरोधाभास पूर्ववत करा — संपादकात त्यांचे निराकरण करा, नंतर सुरू ठेवा.",
    "status.stashApplying": "स्टॅश लागू करत आहे...",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "स्टॅश लागू.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "विलीन होत आहे...",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "शोध कमिट",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "कोणतेही परिणाम नाहीत",
    "search.resultCount": "{index}/{count}",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "हायलाइट मोड",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "आलेखामध्ये शोध परिणाम कसे प्रदर्शित केले जातात ते निवडा.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "GitHub",
  },
  mai: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.sync": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "प्रतिबद्ध विवरण",
    "details.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.sha": "शा",
    "details.shortSha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.author": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.date": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.labels": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.location": "स्थान",
    "details.currentHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.theme": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLight": "हल्लुक",
    "settings.themeDark": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeLightHint": "हल्की पृष्ठभूमि, गहरे रंग के पाठ।",
    "settings.themeDarkHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranch": "मुख्य शाखा",
    "settings.mainBranchAuto": "स्वचालित",
    "settings.mainBranchSearch": "शाखा खोजें...",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "क्लासिक",
    "settings.displayModernHint": "मुफ्त कैनवास — पैन मे घसीटें, ज़ूम करबाक लेल स्क्रॉल करू।",
    "settings.displayClassicHint": "फिक्स्ड कैनवास — ट्रंक बामा दिस पिन कएल गेल, कोनो जूम नहि, केवल स्क्रॉल करू (एसवीएन संशोधन ग्राफ जकाँ)।",
    "settings.sectionGeneral": "सामान्य",
    "settings.sectionGraph": "ग्राफ",
    "settings.sectionChanges": "दृश्य बदलैत अछि",
    "settings.diffMinimap": "डिफ मिनीमैप",
    "settings.diffMinimapOn": "देखाओल गेल अछि",
    "settings.diffMinimapOff": "नुकाएल",
    "settings.diffMinimapOnHint": "diff क बगल मे एकटा VS कोड-शैली अवलोकन पट्टी देखाउ — एकरा स्क्रॉल करबाक लेल खींचू; परिवर्तन मार्कर शामिल अछि।",
    "settings.diffMinimapOffHint": "कोनो अवलोकन पट्टी नहि — अंतर केँ सामान्य रूप सँ स्क्रॉल करू.",
    "settings.commitReview": "समीक्षा प्रतिबद्ध",
    "settings.commitReviewOn": "कमिट करबा स पहिने पूछू",
    "settings.commitReviewOff": "सीधा प्रतिबद्ध करू",
    "settings.commitReviewOnHint": "स्थानीय कमिट बनाबय सँ पहिने एकटा अंतिम फाइल सूची आओर संदेश जाँच देखाउ.",
    "settings.commitReviewOffHint": "Commit बटन स्थानीय कमिट तुरंत बनाबै छै.",
    "settings.svnBranchDialog": "एसवीएन-शैली शाखा संवाद",
    "settings.svnBranchDialogHint": "शाखा बनाबय काल (एकटा सरल प्रॉम्प्ट के बजाय) फोल्डर-ट्री पिकर देखाउ.",
    "settings.branchDialog": "शाखा संवाद",
    "settings.branchDialogSvnTitle": "एसवीएन-शैली के",
    "settings.branchDialogNativeVscode": "वीएस कोड शैली",
    "settings.branchDialogNativeVs": "विजुअल स्टूडियो शैली",
    "settings.branchDialogNativeHint": "अहाँक आईडीई क' अंतर्निहित शाखा प्रॉम्प्ट.",
    "settings.close": "बंद",
    "settings.done": "कयल गेल",
    "settings.sectionAdvanced": "आगू बढ़ल",
    "settings.gitSource": "गिट एक्जीक्यूटिव",
    "settings.gitSourceBuiltin": "अंतर्निहित",
    "settings.gitSourceBuiltinHint": "आईडीई क' अंतर्निहित Git एक्सटेंशन सँ git बाइनरी क' उपयोग करू.",
    "settings.gitSourceCustom": "कस्टम पथ",
    "settings.gitPath": "गिट के लिये पथ",
    "settings.gitPathPlaceholder": "/ usr / बिन / गिट",
    "settings.gitPathBrowse": "ब्राउज़ करू...",
    "settings.jargon": "गिट शब्द",
    "settings.jargonTranslate": "भाषांतर केनाइ",
    "settings.jargonEnglish": "अंग्रेजी मे राखू",
    "settings.jargonTranslateHint": "Git शब्दक (पुल, पुश, कमिट, शाखा...) क' इंटरफेस भाषा मे अनुवाद करू.",
    "settings.jargonEnglishHint": "गिट शब्द (पुल, पुश, कमिट, शाखा...) अंग्रेजी मे राखू; बाकी सब किछु अनुवाद करू।",
    "settings.sectionMerge": "विलय करब",
    "settings.mergeMode": "मर्ज शैली",
    "settings.mergeModeMerge": "सामान्य विलय",
    "settings.mergeModeSquash": "स्क्वैश विलय",
    "settings.mergeModeMergeHint": "शाखा कें जेना छै तेना मर्ज करयत छै: ओकर कमिट वर्तमान शाखा कें इतिहास कें हिस्सा बइन जायत छै, एकटा मर्ज कमिट (या संभव भेला पर फास्ट-फोरवर्ड) सं जुड़ल छै.",
    "settings.mergeModeSquashHint": "वर्तमान शाखा (git merge --squash) पर एकटा कमिट मे पूरा शाखा केँ ढहैत अछि. शाखा केरऽ अपनऽ कमिट वर्तमान शाखा केरऽ इतिहास म॑ कहियो प्रवेश नै करै छै — आपने वहाँ खाली एगो कमिट देखै छियै । शाखा स्वयं अछूत छोड़ि देल गेल अछि, तेँ ओकर कमिट अपन लेन मे देखाइत रहैत अछि ।",
    "settings.mergedView": "ग्राफ मे मर्ज कमिट",
    "settings.mergedViewBranch": "मात्र अपन डारि पर",
    "settings.mergedViewTarget": "संगहि ओहि शाखा मे सेहो ओहि मे विलीन भ गेल छल",
    "settings.mergedViewBranchHint": "एकटा मर्ज शाखाक कमिट केवल ओकर अपन लेन मे खींचल जाइत छैक; मर्ज एकटा कनेक्टर के रूप मे देखाइत अछि. क्लासिक दृश्य।",
    "settings.mergedViewTargetHint": "एक मर्ज म॑ लानलऽ गेलऽ कमिट क॑ रिसीविंग शाखा केरऽ लेन म॑ भी खींचलऽ जाय छै, मर्ज कमिट के नीचें ढेर करलऽ जाय छै आरू मूल के साथ लेवल करलऽ जाय छै — कैन्हेंकि वू शाखा म॑ वास्तव म॑ अब॑ ओकरा समाहित करलऽ जाय छै । प्रति सभ पीयर आ ओहि शाखाक संग हेड अछि जाहि पर लिखल गेल छल, तेँ स्पष्ट रहैत अछि जे ओतहि नहि बनल छल । स्क्वैश मर्ज अप्रभावित छै: स्क्वैश एकटा साधारण कमिट लिखै छै, तेँ केवल वू कमिट कहियो प्रकट होय छै.",
    "legend.title": "किंवदंती",
    "legend.head": "HEAD / वर्तमान शाखा",
    "legend.local": "स्थानीय शाखा",
    "legend.remote": "दूरस्थ शाखा",
    "legend.remoteOnly": "केवल मेघ मे (खींचल नहि)",
    "legend.tag": "टैग (संस्करण) २.",
    "legend.commit": "प्रतिबद्ध",
    "legend.stash": "स्टैश (अलमारी मे राखल काज) २.",
    "legend.mergedIn": "एकटा मर्ज (दोसर शाखा पर लिखल) के संग आबि गेल",
    "legend.nodes": "बक्सा",
    "legend.lines": "पाँति",
    "legend.edgeParent": "पिछला कमिट ओही लाइन पर",
    "legend.edgeMerge": "मर्ज-इन शाखा — तीर मर्ज कमिट पर इशारा करैत अछि",
    "legend.edgeBranch": "एतय एकटा डारि हँसुआ भ गेल",
    "legend.edgeStash": "स्टैश आ कमिट पर एकरा शेल्फ पर राखल गेल छल",
    "legend.edgeMergedChain": "मर्ज-इन कमिट के माध्यम सं चलय वाला एहि शाखा के लाइन",
    "legend.edgeMergedTie": "जे शाखा पर लिखल गेल छल ओहि पर सेहो वैह कमिट",
    "menu.jumpHead": "⌖ चेकआउट पर जाउ",
    "menu.resetView": "⤢ दृश्य रीसेट करू",
    "menu.createBranch": "एतय स शाखा बनाउ...",
    "menu.checkout": "एहि कमिट के चेकआउट करू",
    "menu.copySha": "प्रतिलिपि कमिट SHA",
    "menu.pushBranch": "धक्का शाखा \"{name}\"।",
    "menu.renameCommit": "कमिट संदेश के नाम बदलू...",
    "menu.undoCommit": "कमिट पूर्ववत करू (परिवर्तन राखू)...",
    "menu.viewChanges": "परिवर्तन देखू...",
    "menu.jumpToOriginal": "मूल कमिट पर जाउ",
    "changes.title": "{sha} मे परिवर्तन",
    "changes.tabChanged": "बदलि गेल",
    "changes.tabAll": "सब फाइल",
    "changes.added": "जोड़ा गेल",
    "changes.modified": "संशोधित",
    "changes.deleted": "मेटा देल गेल",
    "changes.renamed": "नाम बदलि देल गेल",
    "changes.noChanges": "ई कमिट कोनो फाइल नहि बदलैत अछि.",
    "changes.loading": "लोड हो रहल अछि diff...",
    "changes.selectFile": "ओकर परिवर्तन देखबाक लेल कोनो फाइल चुनू.",
    "changes.original": "मूल",
    "changes.changed": "ई प्रतिबद्धता",
    "changes.binary": "बाइनरी फाइल — कोनो पाठ diff नहि देखाब' लेल.",
    "changes.tooLarge": "फाइल बहुत पैघ अछि जे डिफ नहि क' सकैत अछि.",
    "changes.renamedFrom": "{path} सँ नाम बदलल गेल",
    "changes.close": "बंद",
    "changes.prevChange": "पूर्व परिवर्तन",
    "changes.nextChange": "अगिला परिवर्तन",
    "changes.collapseAll": "सभ फोल्डर संकुचित करू",
    "changes.expandAll": "सभ फोल्डर विस्तार करू",
    "changes.searchPlaceholder": "फाइल खोजू...",
    "changes.noSearchResults": "कोनो फाइल अहाँक खोजसँ मेल नहि खाइत अछि।",
    "changes.maximize": "अधिकतम करब",
    "changes.restore": "आकार पुनर्स्थापित करू",
    "find.open": "एहि डिफ (Ctrl+F) मे खोजू",
    "find.placeholder": "diff में खोजें |",
    "find.prev": "पिछला मैच",
    "find.next": "अगिला मैच",
    "find.close": "खोज बंद करू",
    "find.noResults": "कोनो परिणाम नहि",
    "commit.title": "परिवर्तन प्रतिबद्ध करू",
    "commit.close": "बंद",
    "commit.loading": "पढ़ैत काजक गाछ...",
    "commit.noChanges": "कोनो स्थानीय परिवर्तनक प्रतिबद्धता नहि अछि।",
    "commit.selectFile": "ओकर अंतर देखबाक लेल कोनो फाइल चुनू.",
    "commit.selectAll": "सब चुनू",
    "commit.selectNone": "सब साफ करू",
    "commit.message": "संदेश प्रतिबद्ध करू",
    "commit.messagePlaceholder": "परिवर्तन के वर्णन कीजिए",
    "commit.selectedCount": "{count} फाइल (सभ) चुनल गेल",
    "commit.review": "समीक्षा",
    "commit.reviewTitle": "कमिट स पहिने अंतिम जांच",
    "commit.reviewSummary": "संदेश क संग {count} फाइल (सभ) प्रतिबद्ध करू: {message}",
    "commit.back": "पाछू",
    "commit.commit": "स्थानीय कमिट बनाउ",
    "menu.stashApply": "स्टैश लगाओ",
    "menu.stashPop": "पॉप स्टैश (लागू & हटाउ)",
    "menu.stashDrop": "ड्रॉप स्टैश...",
    "merge.title": "मर्ज शाखा",
    "merge.route": "वर्तमान शाखा मे विलय",
    "merge.source": "(स्रोत) सँ",
    "merge.target": "(वर्तमान) में",
    "merge.loading": "विलय के विश्लेषण क रहल छी...",
    "merge.upToDate": "पहिने स अद्यतन — विलय करबाक लेल किछु नहि।",
    "merge.fastForward": "फास्ट-फोरवर्ड — कोनो मर्ज कमिट के जरूरत नै छै (एकटा के मजबूर करय लेल “मर्ज कमिट बनाउ” पर टिक करू)।",
    "merge.summary": "{files} फाइल (s) बदलल गेल, {conflicts} संघर्ष (s).",
    "merge.conflictsWarning": "एहि विलय मे टकराव अछि — अहाँ एखनो मर्ज क' सकैत छी, तखन ओकरा संपादक मे समाधान करू.",
    "merge.noChanges": "कोनो फाइल परिवर्तन नहि।",
    "merge.files": "परिणामस्वरूप परिवर्तन",
    "merge.added": "जोड़ा गेल",
    "merge.modified": "संशोधित",
    "merge.deleted": "मेटा देल गेल",
    "merge.conflict": "टकराव",
    "merge.message": "कमिट संदेश मर्ज करू",
    "merge.messagePlaceholder": "शाखा 'स्रोत' विलय करू।",
    "merge.messageFfHint": "फास्ट-फोरवर्ड मर्ज पर अनदेखी कएल गेल (कोनो कमिट नहि बनैत अछि)।",
    "merge.noFastForward": "एकटा मर्ज कमिट बनाउ (कोनो फास्ट-फोरवर्ड नहि)",
    "merge.noFastForwardHint": "हमेशा मर्ज कमिट रिकॉर्ड करू, तखनो जखन शाखा फास्ट-फॉरवर्ड क' सकैत छल.",
    "merge.merge": "विलय करब",
    "merge.cancel": "रद्द",
    "merge.previewError": "पूर्वावलोकन क गणना नहि कए सकल: {message}",
    "merge.squashBadge": "स्क्वैश",
    "merge.squashNote": "स्क्वैश मर्ज — हर परिवर्तन “{target}” पर ONE कमिट के रूप म॑ उतरै छै; “{source}” केरऽ अपनऽ कमिट ओकरऽ इतिहास स॑ बाहर रह॑ छै । सेटिंग्स › मर्ज मे वापस स्विच करू।",
    "merge.messageSquash": "संदेश प्रतिबद्ध करू",
    "merge.squashMessagePlaceholder": "'स्रोत' सँ कुचलल परिवर्तन।",
    "newBranch.title": "शाखा बनाइए",
    "newBranch.startPoint": "{sha} स शुरू भ रहल नव शाखा",
    "newBranch.startPointOn": "{refs} पर",
    "newBranch.location": "स्थान",
    "newBranch.locationRoot": "(जड़)",
    "newBranch.expandAll": "सब विस्तार करू",
    "newBranch.collapseAll": "सब ढहब",
    "newBranch.expandFolder": "फोल्डर विस्तार करू",
    "newBranch.collapseFolder": "फोल्डर संकुचित करू",
    "newBranch.name": "शाखा के नाम",
    "newBranch.namePlaceholder": "हमर-शाखा",
    "newBranch.fullName": "पूरा नाम: {name}",
    "newBranch.invalid": "अमान्य शाखा नाम",
    "newBranch.exists": "एहि नामक शाखा पहिनेसँ अछि",
    "newBranch.checkout": "निर्माण के बाद चेकआउट शाखा",
    "newBranch.create": "बनाउ",
    "newBranch.cancel": "रद्द",
    "status.loading": "ग्राफ लोड भ रहल अछि...",
    "status.summary": "{repo}{commits} कमिट दिखा रहल अछि, {refs} संदर्भ",
    "status.branchCreated": "{sha} पर शाखा \"{name}\" बनाओल गेल",
    "status.error": "त्रुटि: {message}",
    "status.opFailed": "ऑपरेशन फेल भ गेल",
    "status.fetching": "लाबय के...",
    "status.pulling": "खींचैत...",
    "status.pushing": "धक्का दैत...",
    "status.committing": "स्थानीय कमिट बना रहल अछि...",
    "status.commitCreated": "स्थानीय कमिट {sha} बनाओल गेल: {message}",
    "status.syncing": "सिंक क रहल छी...",
    "status.noHead": "ग्राफ मे कोनो वर्तमान चेकआउट नहि भेटल।",
    "status.undoing": "कमिट के पूर्ववत क रहल छी...",
    "status.commitUndone": "कमिट अनडन — परिवर्तन वापस काज करय बला गाछ मे अछि.",
    "status.undoConflict": "हिट टकराव पूर्ववत करू — संपादक मे ओकर समाधान करू, फेर जारी राखू.",
    "status.stashApplying": "स्टैश लागू करब...",
    "status.stashPopping": "पॉपिंग स्टैश...",
    "status.stashDropping": "स्टैश छोड़ैत अछि...",
    "status.stashApplied": "स्टैश लागू भेल।",
    "status.stashPopped": "स्टैश पॉप भ गेल।",
    "status.stashDropped": "स्टैश खसि पड़ल।",
    "status.stashConflict": "टकराव स्टैश करू — संपादक मे ओकर समाधान करू।",
    "status.merging": "विलय भ रहल अछि...",
    "status.merged": "विलय पूरा भ गेल।",
    "status.mergeConflict": "मर्ज मे टकराव अछि — संपादक मे हल करू, फेर कमिट करू.",
    "search.title": "खोजनाइ",
    "search.tooltip": "खोज कमिट करैत अछि",
    "search.placeholder": "खोज कमिट करैत अछि...",
    "search.previous": "पिछला रिजल्ट",
    "search.next": "अगिला परिणाम",
    "search.noResults": "कोनो परिणाम नहि",
    "search.resultCount": "एक्सपीएलएच0एक्स/एक्सपीएलएच1एक्स",
    "search.filter": "फ़िल्टर मोड",
    "search.highlight": "हाइलाइट मोड",
    "search.filterMode": "खोज प्रदर्शन",
    "search.filterModeFilter": "फ़िल्टर (गैर-मेल नुकाउ)",
    "search.filterModeHighlight": "हाइलाइट करू (सब देखाउ, मिलान चिन्हित करू)",
    "search.filterModeHint": "ग्राफ मे खोज परिणाम कोना प्रदर्शित होइत अछि से चुनू।",
    "search.visibility": "खोज स्थान",
    "search.visibilityToolbar": "टूलबार बटन",
    "search.visibilityAlwaysVisible": "सदैव-दृश्यमान बार",
    "search.visibilityHint": "खोज कए टूलबार आइकन या ऊपर पर लगातार बार क रूप मे देखाउ.",
    "footer.github": "गिटहब",
  },
  sa: {
    "toolbar.refresh": "⟳ ताजगी",
    "toolbar.fetch": "⤓ आनयतु",
    "toolbar.pull": "⇩ खींच",
    "toolbar.push": "⇧ धक्का",
    "toolbar.commit": "✓ प्रतिबद्ध",
    "toolbar.sync": "⇅ समन्वयनम्",
    "toolbar.jumpHead": "⌖ चेकआउट् प्रति गच्छन्तु",
    "toolbar.reset": "⤢ दृश्यं पुनः सेट् कुर्वन्तु",
    "toolbar.settings": "⚙ सेटिंग्स्",
    "details.header": "विवरणं प्रतिबद्धं कुर्वन्तु",
    "details.close": "पिधानं करोतु",
    "details.sha": "शा",
    "details.shortSha": "लघु शा",
    "details.message": "सन्देशः",
    "details.author": "लेखकः",
    "details.date": "दिनाङ्कः",
    "details.labels": "लेबल्स्",
    "details.location": "स्थानीय",
    "details.currentHead": "HEAD — वर्तमान चेकआउट",
    "settings.title": "सेटिंग्स्",
    "settings.language": "भाषा",
    "settings.theme": "विषयवस्तु",
    "settings.themeLight": "प्रकाशः",
    "settings.themeDark": "तिमिर",
    "settings.themeLightHint": "हल्की पृष्ठभूमि, अन्धकार पाठ।",
    "settings.themeDarkHint": "गहरी पृष्ठभूमि, हल्की पाठ।",
    "settings.mainBranch": "मुख्य शाखा",
    "settings.mainBranchAuto": "स्वतः",
    "settings.mainBranchSearch": "शाखाः अन्वेष्टुम्...",
    "settings.mainBranchNoMatch": "न मेलशाखाः",
    "settings.display": "प्रदर्शनशैली",
    "settings.displayModern": "आधुनिक",
    "settings.displayClassic": "क्लासिक",
    "settings.displayModernHint": "निःशुल्कं कैनवासः — पैन् मध्ये कर्षयन्तु, जूम कर्तुं स्क्रॉल कुर्वन्तु ।",
    "settings.displayClassicHint": "नियतं कैनवासः — ट्रंकः वामभागे पिनः, कोऽपि जूमः नास्ति, केवलं स्क्रॉलं कुर्वन्तु (SVN पुनरीक्षणलेखः इव) ।",
    "settings.sectionGeneral": "सामान्य",
    "settings.sectionGraph": "आलेखः",
    "settings.sectionChanges": "दृश्यं परिवर्तयति",
    "settings.diffMinimap": "Diff minimap",
    "settings.diffMinimapOn": "दर्शितम्",
    "settings.diffMinimapOff": "अदृष्ट",
    "settings.diffMinimapOnHint": "diff इत्यस्य पार्श्वे VS Code-शैल्याः अवलोकनपट्टिकां दर्शयन्तु — स्क्रॉलं कर्तुं कर्षयन्तु; परिवर्तनचिह्नानि समाविष्टानि।",
    "settings.diffMinimapOffHint": "अवलोकनपट्टिका नास्ति — सामान्यतया diff स्क्रॉल कुर्वन्तु ।",
    "settings.commitReview": "समीक्षा प्रतिबद्ध",
    "settings.commitReviewOn": "प्रतिबद्धतां कर्तुं पूर्वं पृच्छन्तु",
    "settings.commitReviewOff": "प्रत्यक्षतया प्रतिबद्धतां कुर्वन्तु",
    "settings.commitReviewOnHint": "स्थानीयप्रतिबद्धतां निर्मातुं पूर्वं अन्तिमसञ्चिकासूचीं सन्देशपरीक्षां च दर्शयतु ।",
    "settings.commitReviewOffHint": "Commit बटन् तत्क्षणमेव स्थानीयं commit निर्माति ।",
    "settings.svnBranchDialog": "SVN-शैल्याः शाखासंवादः",
    "settings.svnBranchDialogHint": "शाखां निर्मायन्ते सति (सरलप्रोम्प्ट् इत्यस्य स्थाने) फोल्डर्-ट्री-पिकरं दर्शयन्तु ।",
    "settings.branchDialog": "शाखासंवादः",
    "settings.branchDialogSvnTitle": "एसवीएन-शैली",
    "settings.branchDialogNativeVscode": "VS कोड शैली",
    "settings.branchDialogNativeVs": "विजुअल स्टूडियो शैली",
    "settings.branchDialogNativeHint": "भवतः IDE इत्यस्य अन्तःनिर्मितशाखाप्रोम्प्ट् ।",
    "settings.close": "पिधानं करोतु",
    "settings.done": "कृतम्‌",
    "settings.sectionAdvanced": "उन्नतं",
    "settings.gitSource": "गिट् एक्जीक्यूटिव",
    "settings.gitSourceBuiltin": "अन्तर्निर्मितम्",
    "settings.gitSourceBuiltinHint": "IDE इत्यस्य अन्तःनिर्मितस्य Git विस्तारात् git द्विचक्रीयस्य उपयोगं कुर्वन्तु ।",
    "settings.gitSourceCustom": "कस्टम पथ",
    "settings.gitPath": "पथः गिट्",
    "settings.gitPathPlaceholder": "/उस्र/बिन/गिट",
    "settings.gitPathBrowse": "ब्राउज़ करें...",
    "settings.jargon": "गिट् पदम्",
    "settings.jargonTranslate": "अनुवदति",
    "settings.jargonEnglish": "आङ्ग्लभाषायां स्थापयतु",
    "settings.jargonTranslateHint": "Git पदानाम् (pull, push, commit, branch...) अन्तरफलकभाषायां अनुवादं कुर्वन्तु ।",
    "settings.jargonEnglishHint": "Git पदं (pull, push, commit, branch...) आङ्ग्लभाषायां स्थापयन्तु; अन्यत् सर्वं अनुवादयतु।",
    "settings.sectionMerge": "विलयः",
    "settings.mergeMode": "विलयनशैली",
    "settings.mergeModeMerge": "सामान्यं विलयनम्",
    "settings.mergeModeSquash": "स्क्वैश विलीनता",
    "settings.mergeModeMergeHint": "शाखां यथावत् विलीनं करोति: तस्य कमिट्स् वर्तमानशाखायाः इतिहासस्य भागः भवन्ति, विलयन कमिटेन (अथवा सम्भवे शीघ्रं अग्रे प्रेषिताः) सम्मिलिताः भवन्ति ।",
    "settings.mergeModeSquashHint": "वर्तमानशाखायां (git merge --squash) एकस्मिन् कमिट् मध्ये सम्पूर्णं शाखां संकुचति । शाखायाः स्वकीयानि कमिट्स् वर्तमानशाखायाः इतिहासे कदापि न प्रविशन्ति — तत्र भवन्तः केवलं एकं कमिटं पश्यन्ति । शाखा एव अस्पृष्टा एव अवशिष्टा अस्ति, अतः तस्याः कमिट्स् स्वस्य लेन् मध्ये दृश्यमानाः तिष्ठन्ति ।",
    "settings.mergedView": "आलेखे विलीनाः प्रतिबद्धाः",
    "settings.mergedViewBranch": "केवलं स्वशाखायां एव",
    "settings.mergedViewTarget": "शाखायां अपि ते विलीनाः आसन्",
    "settings.mergedViewBranchHint": "विलीनशाखायाः कमिट्स् केवलं स्वस्य लेन् मध्ये आकृष्टाः भवन्ति; विलयः संयोजकरूपेण दर्शयति । शास्त्रीयं दृश्यम्।",
    "settings.mergedViewTargetHint": "एकेन विलीनीकरणेन आनयितानि कमिट्स् ग्राहकशाखायाः लेन् इत्यत्र अपि आकृष्टाः भवन्ति, मर्ज कमिटस्य अधः स्तम्भिताः भवन्ति तथा च मूलैः सह समतलाः भवन्ति — यतः तस्मिन् शाखायां वास्तवमेव इदानीं तानि सन्ति प्रतिकृतयः विवर्णाः सन्ति, यस्मिन् शाखायां ताः लिखिताः आसन्, तस्य शिरः च सन्ति, अतः तत्र न निर्मिताः इति स्पष्टं तिष्ठति । स्क्वैश-विलयनं अप्रभावितं भवति: स्क्वैशः एकं साधारणं कमिटं लिखति, अतः केवलं तत् कमिटं कदापि दृश्यते ।",
    "legend.title": "आख्यान",
    "legend.head": "HEAD / वर्तमान शाखा",
    "legend.local": "स्थानीय शाखा",
    "legend.remote": "दूरस्थ शाखा",
    "legend.remoteOnly": "केवलं मेघे (न आकृष्य) २.",
    "legend.tag": "टैग (संस्करण) ९.",
    "legend.commit": "प्रविश्",
    "legend.stash": "स्टैश (अलमारीकृत कार्य) २.",
    "legend.mergedIn": "विलयेन सह अन्तः आगतः (अन्यशाखायां लिखितः) ।",
    "legend.nodes": "पेटी",
    "legend.lines": "रेखाः",
    "legend.edgeParent": "पूर्वस्मिन् एव रेखायां प्रतिबद्धः",
    "legend.edgeMerge": "विलीन-शाखा — बाणः विलय-प्रतिबद्धतायां सूचयति",
    "legend.edgeBranch": "अत्र एकः शाखा विच्छिन्नः अभवत्",
    "legend.edgeStash": "Stash and the commit it was shelved on",
    "legend.edgeMergedChain": "अस्याः शाखायाः रेखा विलीन-अन्तर्गत-द्वारा धावति",
    "legend.edgeMergedTie": "यस्मिन् शाखायां लिखितम् आसीत् तस्मिन् एव कमिटम्",
    "menu.jumpHead": "⌖ चेकआउट् प्रति गच्छन्तु",
    "menu.resetView": "⤢ दृश्यं पुनः सेट् कुर्वन्तु",
    "menu.createBranch": "इतः शाखा रचयन्तु...",
    "menu.checkout": "एतत् प्रतिबद्धं पश्यन्तु",
    "menu.copySha": "प्रतिलिपि प्रतिबद्धः SHA",
    "menu.pushBranch": "पुश शाखा \"{name}\"।",
    "menu.renameCommit": "commit सन्देशस्य नाम परिवर्तनं कुर्वन्तु...",
    "menu.undoCommit": "प्रतिबद्धतां पूर्ववत् कुर्वन्तु (परिवर्तनं स्थापयन्तु)...",
    "menu.viewChanges": "परिवर्तनं पश्यन्तु...",
    "menu.jumpToOriginal": "मूलप्रतिबद्धतां गच्छन्तु",
    "changes.title": "{sha} इत्यस्मिन् परिवर्तनम्",
    "changes.tabChanged": "परिवर्तितम्",
    "changes.tabAll": "सर्वाणि सञ्चिकाः",
    "changes.added": "योजितम्",
    "changes.modified": "परिवर्तित",
    "changes.deleted": "विलोपितम्",
    "changes.renamed": "नामान्तरितम्",
    "changes.noChanges": "एतत् प्रतिबद्धं कोऽपि सञ्चिकां न परिवर्तयति ।",
    "changes.loading": "लोडिंग diff...",
    "changes.selectFile": "तस्याः परिवर्तनं द्रष्टुं सञ्चिकां चिनोतु ।",
    "changes.original": "मूल",
    "changes.changed": "एतत् प्रतिबद्धम्",
    "changes.binary": "द्विचक्रीयसञ्चिका — दर्शयितुं पाठविभेदः नास्ति ।",
    "changes.tooLarge": "सञ्चिका अतीव विशाला अस्ति यत् diff कर्तुं न शक्नोति।",
    "changes.renamedFrom": "{path} इत्यस्मात् पुनर्नामकरणं कृतम्",
    "changes.close": "पिधानं करोतु",
    "changes.prevChange": "पूर्वपरिवर्तनम्",
    "changes.nextChange": "अग्रिमः परिवर्तनः",
    "changes.collapseAll": "सर्वाणि पुटकानि संकुचितं कुर्वन्तु",
    "changes.expandAll": "सर्वाणि पुटकानि विस्तारयन्तु",
    "changes.searchPlaceholder": "सञ्चिकाः अन्वेष्टुम्...",
    "changes.noSearchResults": "भवतः अन्वेषणेन सह कोऽपि सञ्चिकाः न मेलति ।",
    "changes.maximize": "अधिकतमं कुरुत",
    "changes.restore": "आकारं पुनः स्थापयतु",
    "find.open": "अस्मिन् diff (Ctrl+F) मध्ये अन्वेषणं कुर्वन्तु ।",
    "find.placeholder": "diff इत्यत्र अन्वेष्टुम्...",
    "find.prev": "पूर्व मैच",
    "find.next": "अग्रिमः मेलः",
    "find.close": "अन्वेषणं पिधाय",
    "find.noResults": "न परिणामः",
    "commit.title": "परिवर्तनं प्रतिबद्धं कुर्वन्तु",
    "commit.close": "पिधानं करोतु",
    "commit.loading": "पठन् कार्यवृक्षः...",
    "commit.noChanges": "प्रतिबद्धतां कर्तुं स्थानीयपरिवर्तनानि नास्ति।",
    "commit.selectFile": "तस्य diff द्रष्टुं सञ्चिकां चिनोतु ।",
    "commit.selectAll": "सर्वाणि चिनोतु",
    "commit.selectNone": "सर्वाणि स्वच्छानि कुर्वन्तु",
    "commit.message": "सन्देशं प्रतिबद्धं कुर्वन्तु",
    "commit.messagePlaceholder": "परिवर्तनस्य वर्णनं कुरुत",
    "commit.selectedCount": "{count} सञ्चिका (सञ्चिकाः) चयनिता",
    "commit.review": "परिशीलयतु",
    "commit.reviewTitle": "प्रतिबद्धतः पूर्वं अन्तिमपरीक्षा",
    "commit.reviewSummary": "{count} सञ्चिका(सञ्चिकाः) सन्देशेन सह प्रतिबद्धं कुर्वन्तु: {message}",
    "commit.back": "पृष्ठभागः",
    "commit.commit": "स्थानीयप्रतिबद्धता रचयन्तु",
    "menu.stashApply": "स्तशं प्रयोजयन्तु",
    "menu.stashPop": "पॉप स्टैश (apply & remove) 1.1.",
    "menu.stashDrop": "ड्रॉप स्टैश...",
    "merge.title": "विलीनीकरण शाखा",
    "merge.route": "वर्तमानशाखायां विलयः",
    "merge.source": "(स्रोतः) तः २.",
    "merge.target": "Into (वर्तमान) २.",
    "merge.loading": "विलयस्य विश्लेषणं कुर्वन्...",
    "merge.upToDate": "पूर्वमेव अद्यतनम् — विलीनीकरणार्थं किमपि नास्ति ।",
    "merge.fastForward": "द्रुत-अग्रे — विलयन-प्रतिबद्धतायाः आवश्यकता नास्ति (एकं बाध्यं कर्तुं “विलय-प्रतिबद्धतां रचयतु” इति टिकं कुर्वन्तु) ।",
    "merge.summary": "{files} सञ्चिका(s) परिवर्तिता, {conflicts} द्वन्द्व(s) ।",
    "merge.conflictsWarning": "अस्मिन् विलयस्य विग्रहाः सन्ति — भवान् अद्यापि विलयं कर्तुं शक्नोति, ततः सम्पादके समाधानं कर्तुं शक्नोति ।",
    "merge.noChanges": "सञ्चिकापरिवर्तनं न भवति।",
    "merge.files": "परिणामी परिवर्तनम्",
    "merge.added": "योजितम्",
    "merge.modified": "परिवर्तित",
    "merge.deleted": "विलोपितम्",
    "merge.conflict": "द्वंद",
    "merge.message": "commit सन्देशं विलीनं कुर्वन्तु",
    "merge.messagePlaceholder": "शाखा 'स्रोत' विलययतु।",
    "merge.messageFfHint": "द्रुत-अग्रे विलयने उपेक्षितम् (कोऽपि कमिटः न निर्मितः) ।",
    "merge.noFastForward": "विलयप्रतिबद्धतां रचयन्तु (न द्रुत-अग्रे)",
    "merge.noFastForwardHint": "सदैव विलयप्रतिबद्धतां अभिलेखयन्तु, यदा अपि शाखा शीघ्रं अग्रे गन्तुं शक्नोति स्म ।",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "पूर्वावलोकनस्य गणनां कर्तुं न शक्तवान्: {message}",
    "merge.squashBadge": "स्क्वैश",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "सन्देशं प्रतिबद्धं कुर्वन्तु",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "{refs} इत्यत्र",
    "newBranch.location": "स्थानीय",
    "newBranch.locationRoot": "(मूलं)",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "पुटं विस्तारयन्तु",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "शाखा नाम",
    "newBranch.namePlaceholder": "मम-शाखा",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "निर्मियताम्",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "आलेखं लोड् भवति...",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "स्थानीयप्रतिबद्धतायाः निर्माणं...",
    "status.commitCreated": "स्थानीयप्रतिबद्धता {sha}: {message} निर्मितम्",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "stash इत्यस्य प्रयोगः...",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "footer.github": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
  },
  hi: {
    "toolbar.refresh": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.fetch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.pull": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.push": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.commit": "✓ प्रतिबद्ध",
    "toolbar.sync": "⇅ समन्वयित करें",
    "toolbar.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.reset": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "toolbar.settings": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.header": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.close": "बंद करना",
    "details.sha": "शा",
    "details.shortSha": "लघु SHA",
    "details.message": "संदेश",
    "details.author": "लेखक",
    "details.date": "तारीख",
    "details.labels": "लेबल",
    "details.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "details.currentHead": "प्रमुख - वर्तमान चेकआउट",
    "settings.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.language": "भाषा",
    "settings.theme": "विषय",
    "settings.themeLight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDark": "अँधेरा",
    "settings.themeLightHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.themeDarkHint": "गहरा पृष्ठभूमि, हल्का पाठ.",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "कोई मेल खाती शाखाएं नहीं",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "क्लासिक",
    "settings.displayModernHint": "मुफ़्त कैनवास - पैन करने के लिए खींचें, ज़ूम करने के लिए स्क्रॉल करें।",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "ग्राफ़",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "डिफ मिनिमैप",
    "settings.diffMinimapOn": "दिखाया गया है",
    "settings.diffMinimapOff": "छिपा हुआ",
    "settings.diffMinimapOnHint": "अंतर के बगल में एक वीएस कोड-शैली अवलोकन पट्टी दिखाएं - इसे स्क्रॉल करने के लिए खींचें; परिवर्तन मार्कर शामिल हैं।",
    "settings.diffMinimapOffHint": "कोई अवलोकन पट्टी नहीं - अंतर को सामान्य रूप से स्क्रॉल करें।",
    "settings.commitReview": "समीक्षा करें",
    "settings.commitReviewOn": "प्रतिबद्ध होने से पहले पूछें",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "स्थानीय कमिट बनाने से पहले अंतिम फ़ाइल सूची और संदेश जाँच दिखाएँ।",
    "settings.commitReviewOffHint": "कमिट बटन तुरंत स्थानीय कमिट बनाता है।",
    "settings.svnBranchDialog": "एसवीएन-शैली शाखा संवाद",
    "settings.svnBranchDialogHint": "शाखा बनाते समय फ़ोल्डर-ट्री पिकर दिखाएँ (सरल संकेत के बजाय)।",
    "settings.branchDialog": "शाखा संवाद",
    "settings.branchDialogSvnTitle": "SVN शैली",
    "settings.branchDialogNativeVscode": "वीएस कोड शैली",
    "settings.branchDialogNativeVs": "विजुअल स्टूडियो शैली",
    "settings.branchDialogNativeHint": "आपकी IDE की अंतर्निहित शाखा प्रॉम्प्ट।",
    "settings.close": "बंद करना",
    "settings.done": "हो गया",
    "settings.sectionAdvanced": "विकसित",
    "settings.gitSource": "गिट निष्पादन योग्य",
    "settings.gitSourceBuiltin": "में निर्मित",
    "settings.gitSourceBuiltinHint": "आईडीई के अंतर्निहित गिट एक्सटेंशन से गिट बाइनरी का उपयोग करें।",
    "settings.gitSourceCustom": "कस्टम पथ",
    "settings.gitPath": "गिट का पथ",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ब्राउज़ करें...",
    "settings.jargon": "गिट शर्तें",
    "settings.jargonTranslate": "अनुवाद",
    "settings.jargonEnglish": "अंग्रेजी में रखें",
    "settings.jargonTranslateHint": "Git शब्दों (पुल, पुश, कमिट, ब्रांच...) का इंटरफ़ेस भाषा में अनुवाद करें।",
    "settings.jargonEnglishHint": "Git शब्द (पुल, पुश, कमिट, ब्रांच...) अंग्रेजी में रखें; बाकी सभी चीज़ों का अनुवाद करें.",
    "settings.sectionMerge": "मर्ज",
    "settings.mergeMode": "मर्ज शैली",
    "settings.mergeModeMerge": "सामान्य विलय",
    "settings.mergeModeSquash": "स्क्वैश मर्ज",
    "settings.mergeModeMergeHint": "शाखा को वैसे ही मर्ज करता है जैसे वह है: इसकी प्रतिबद्धताएं वर्तमान शाखा के इतिहास का हिस्सा बन जाती हैं, एक मर्ज प्रतिबद्धता से जुड़ जाती हैं (या जब संभव हो तो तेजी से अग्रेषित की जाती हैं)।",
    "settings.mergeModeSquashHint": "वर्तमान शाखा पर पूरी शाखा को एक ही कमिट में संक्षिप्त कर देता है (git merge --squash)। शाखा की अपनी प्रतिबद्धताएं कभी भी वर्तमान शाखा के इतिहास में दर्ज नहीं होती हैं - आप वहां केवल एक प्रतिबद्धता देखते हैं। शाखा को स्वयं अछूता छोड़ दिया गया है, इसलिए इसकी प्रतिबद्धताएं अपनी ही लेन में दिखाई देती रहती हैं।",
    "settings.mergedView": "ग्राफ़ में मर्ज किए गए कमिट",
    "settings.mergedViewBranch": "केवल अपनी ही शाखा पर",
    "settings.mergedViewTarget": "साथ ही जिस शाखा में उनका विलय हुआ",
    "settings.mergedViewBranchHint": "मर्ज की गई शाखा की प्रतिबद्धताएं केवल उनकी अपनी लेन में ही बनाई जाती हैं; मर्ज एक कनेक्टर के रूप में दिखाई देता है। क्लासिक दृश्य.",
    "settings.mergedViewTargetHint": "मर्ज में लाए गए कमिट को प्राप्तकर्ता शाखा की लेन में भी खींचा जाता है, मर्ज कमिट के नीचे रखा जाता है और मूल के साथ समतल किया जाता है - क्योंकि उस शाखा में वास्तव में अब वे शामिल हैं। प्रतियां पीली हैं और उसी शाखा की ओर ले जाई गई हैं जिस पर वे लिखी गई थीं, इसलिए यह स्पष्ट है कि उन्हें वहां नहीं बनाया गया था। स्क्वैश मर्ज अप्रभावित रहते हैं: एक स्क्वैश एक सामान्य कमिट लिखता है, इसलिए केवल वह कमिट ही प्रकट होता है।",
    "legend.title": "दंतकथा",
    "legend.head": "प्रमुख/वर्तमान शाखा",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "छिपाना (छिपा हुआ काम)",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "⌖ चेकआउट पर जाएं",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "प्रतिलिपि प्रतिबद्ध SHA",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "प्रतिबद्ध संदेश का नाम बदलें...",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.mergeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpToOriginal": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "node.mergedInTooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.title": "{sha} में परिवर्तन",
    "changes.tabChanged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tabAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.original": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.changed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.binary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.tooLarge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.renamedFrom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.prevChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.nextChange": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.searchPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.noSearchResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.maximize": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "changes.restore": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.open": "इस अंतर में खोजें (Ctrl+F)",
    "find.placeholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.prev": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "find.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectFile": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.back": "पीछे",
    "commit.commit": "स्थानीय प्रतिबद्धता बनाएं",
    "menu.stashApply": "स्टैश लागू करें",
    "menu.stashPop": "पॉप स्टैश (लागू करें और हटाएं)",
    "menu.stashDrop": "भंडारण छोड़ें...",
    "merge.title": "मर्ज शाखा",
    "merge.route": "वर्तमान शाखा में विलय",
    "merge.source": "से (स्रोत)",
    "merge.target": "में (वर्तमान)",
    "merge.loading": "मर्ज का विश्लेषण किया जा रहा है...",
    "merge.upToDate": "पहले से ही अद्यतित - मर्ज करने के लिए कुछ भी नहीं।",
    "merge.fastForward": "फ़ास्ट-फ़ॉरवर्ड - किसी मर्ज कमिट की आवश्यकता नहीं है (किसी को मजबूर करने के लिए \"मर्ज कमिट बनाएं\" पर निशान लगाएं)।",
    "merge.summary": "{files} फ़ाइल(फ़ाइलें) बदल गईं, {conflicts} विरोध।",
    "merge.conflictsWarning": "इस मर्ज में विरोध है - आप अभी भी मर्ज कर सकते हैं, फिर उन्हें संपादक में हल कर सकते हैं।",
    "merge.noChanges": "कोई फ़ाइल परिवर्तन नहीं.",
    "merge.files": "परिणामी परिवर्तन",
    "merge.added": "जोड़ा",
    "merge.modified": "संशोधित",
    "merge.deleted": "हटाए गए",
    "merge.conflict": "टकराव",
    "merge.message": "प्रतिबद्ध संदेश मर्ज करें",
    "merge.messagePlaceholder": "मर्ज शाखा 'स्रोत'",
    "merge.messageFfHint": "फ़ास्ट-फ़ॉरवर्ड मर्ज पर ध्यान नहीं दिया गया (कोई प्रतिबद्धता नहीं बनाई गई है)।",
    "merge.noFastForward": "एक मर्ज कमिट बनाएं (कोई तेज़-फ़ॉरवर्ड नहीं)",
    "merge.noFastForwardHint": "मर्ज कमिट को हमेशा रिकॉर्ड करें, भले ही शाखा तेजी से आगे बढ़ रही हो।",
    "merge.merge": "मर्ज",
    "merge.cancel": "रद्द करना",
    "merge.previewError": "पूर्वावलोकन की गणना नहीं की जा सकी: {message}",
    "merge.squashBadge": "स्क्वाश",
    "merge.squashNote": "स्क्वैश मर्ज - प्रत्येक परिवर्तन \"{target}\" पर ONE की प्रतिबद्धता के अनुसार होता है; \"{source}\" की अपनी प्रतिबद्धताएँ इसके इतिहास से बाहर हैं। सेटिंग्स ›मर्ज में वापस जाएँ।",
    "merge.messageSquash": "प्रतिबद्ध संदेश",
    "merge.squashMessagePlaceholder": "'स्रोत' से कुचले गए परिवर्तन",
    "newBranch.title": "शाखा बनाएँ",
    "newBranch.startPoint": "नई शाखा {sha} से शुरू हो रही है",
    "newBranch.startPointOn": "{refs} पर",
    "newBranch.location": "जगह",
    "newBranch.locationRoot": "(जड़)",
    "newBranch.expandAll": "सभी का विस्तार",
    "newBranch.collapseAll": "सभी को संकुचित करें",
    "newBranch.expandFolder": "फ़ोल्डर का विस्तार करें",
    "newBranch.collapseFolder": "फ़ोल्डर संक्षिप्त करें",
    "newBranch.name": "शाखा का नाम",
    "newBranch.namePlaceholder": "मेरी शाखा",
    "newBranch.fullName": "पूरा नाम: {name}",
    "newBranch.invalid": "अमान्य शाखा नाम",
    "newBranch.exists": "इस नाम की एक शाखा पहले से मौजूद है",
    "newBranch.checkout": "निर्माण के बाद चेकआउट शाखा",
    "newBranch.create": "बनाएं",
    "newBranch.cancel": "रद्द करना",
    "status.loading": "ग्राफ़ लोड हो रहा है...",
    "status.summary": "{repo}{commits} कमिट दिखा रहा है, {refs} रेफरी",
    "status.branchCreated": "{sha} पर शाखा \"{name}\" बनाई गई",
    "status.error": "त्रुटि: {message}",
    "status.opFailed": "प्रचालन विफल रहा",
    "status.fetching": "लाया जा रहा है...",
    "status.pulling": "खींच रहा हूँ...",
    "status.pushing": "धक्का दे रहा है...",
    "status.committing": "स्थानीय प्रतिबद्धता बनाई जा रही है...",
    "status.commitCreated": "स्थानीय प्रतिबद्धता {sha} बनाई गई: {message}",
    "status.syncing": "सिंक हो रहा है...",
    "status.noHead": "ग्राफ़ में कोई वर्तमान चेकआउट नहीं मिला।",
    "status.undoing": "प्रतिबद्धता पूर्ववत की जा रही है...",
    "status.commitUndone": "प्रतिबद्धता पूर्ववत - कार्यशील वृक्ष में परिवर्तन वापस आ गए हैं।",
    "status.undoConflict": "हिट विवादों को पूर्ववत करें - उन्हें संपादक में हल करें, फिर जारी रखें।",
    "status.stashApplying": "स्टैश लागू किया जा रहा है...",
    "status.stashPopping": "छिपाव छिपाया जा रहा है...",
    "status.stashDropping": "भंडारण कम हो रहा है...",
    "status.stashApplied": "स्टैश लागू किया गया.",
    "status.stashPopped": "छिपाव खुल गया।",
    "status.stashDropped": "भंडार गिरा दिया गया.",
    "status.stashConflict": "स्टैश संघर्ष - उन्हें संपादक में हल करें।",
    "status.merging": "विलय हो रहा है...",
    "status.merged": "विलय पूरा हुआ.",
    "status.mergeConflict": "मर्ज में विरोधाभास हैं - उन्हें संपादक में हल करें, फिर प्रतिबद्ध करें।",
    "search.title": "खोज",
    "search.tooltip": "खोज प्रतिबद्ध है",
    "search.placeholder": "खोज प्रतिबद्ध...",
    "search.previous": "पिछला परिणाम",
    "search.next": "अगला परिणाम",
    "search.noResults": "कोई परिणाम नहीं",
    "search.resultCount": "{index}/{count}",
    "search.filter": "फ़िल्टर मोड",
    "search.highlight": "हाइलाइट मोड",
    "search.filterMode": "खोज प्रदर्शन",
    "search.filterModeFilter": "फ़िल्टर करें (गैर-मिलान छिपाएँ)",
    "search.filterModeHighlight": "हाइलाइट करें (सभी दिखाएं, मिलान चिह्नित करें)",
    "search.filterModeHint": "चुनें कि खोज परिणाम ग्राफ़ में कैसे प्रदर्शित होते हैं।",
    "search.visibility": "स्थान खोजें",
    "search.visibilityToolbar": "टूलबार बटन",
    "search.visibilityAlwaysVisible": "सदैव दृश्यमान बार",
    "search.visibilityHint": "खोज को टूलबार आइकन या शीर्ष पर लगातार बार के रूप में दिखाएं।",
    "footer.github": "GitHub",
  },
  as: {
    "toolbar.refresh": "⟳ সতেজ কৰক",
    "toolbar.fetch": "⤓ আনিব",
    "toolbar.pull": "⇩ টানিব",
    "toolbar.push": "⇧ ঠেলি দিয়া",
    "toolbar.commit": "✓ কমিট কৰা",
    "toolbar.sync": "⇅ ছিঙ্ক",
    "toolbar.jumpHead": "⌖ চেকআউটলৈ যাওক",
    "toolbar.reset": "⤢ দৃশ্য পুনৰায় সেট কৰক",
    "toolbar.settings": "⚙ ছেটিংছ",
    "details.header": "বিৱৰণ কমিট কৰক",
    "details.close": "বন্ধ",
    "details.sha": "SHA",
    "details.shortSha": "চুটি SHA",
    "details.message": "বাৰ্তা",
    "details.author": "লিখক",
    "details.date": "তাৰিখ",
    "details.labels": "লেবেলসমূহ",
    "details.location": "অৱস্থান",
    "details.currentHead": "HEAD — বৰ্তমানৰ চেকআউট",
    "settings.title": "ছেটিংছ",
    "settings.language": "ভাষা",
    "settings.theme": "বিষয়বস্তু",
    "settings.themeLight": "পাতল",
    "settings.themeDark": "অন্ধকাৰ",
    "settings.themeLightHint": "পোহৰৰ পটভূমি, গাঢ় লিখনী।",
    "settings.themeDarkHint": "গাঢ় পটভূমি, পোহৰৰ লিখনী।",
    "settings.mainBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchAuto": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchSearch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mainBranchNoMatch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.display": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayModern": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.displayClassic": "ক্লাছিক",
    "settings.displayModernHint": "বিনামূলীয়া কেনভাছ — পেনলৈ টানি নিয়ক, জুম কৰিবলৈ স্ক্ৰল কৰক।",
    "settings.displayClassicHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGeneral": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionGraph": "গ্ৰাফ",
    "settings.sectionChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimap": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOn": "দেখুওৱা হৈছে",
    "settings.diffMinimapOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.diffMinimapOnHint": "পাৰ্থক্যৰ কাষত এটা VS ক'ড-শৈলী অভাৰভিউ ষ্ট্ৰিপ দেখুৱাওক — ইয়াক স্ক্ৰল কৰিবলে টানি নিয়ক; পৰিৱৰ্তন চিহ্নিতকাৰী অন্তৰ্ভুক্ত কৰা হৈছে।",
    "settings.diffMinimapOffHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReview": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOff": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOnHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.commitReviewOffHint": "Commit বুটামে স্থানীয় কমিট তৎক্ষণাত সৃষ্টি কৰে।",
    "settings.svnBranchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.svnBranchDialogHint": "এটা শাখা সৃষ্টি কৰাৰ সময়ত এটা ফোল্ডাৰ-বৃক্ষ পিকাৰ দেখুৱাওক (এটা সৰল প্ৰমপ্টৰ পৰিবৰ্তে)।",
    "settings.branchDialog": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogSvnTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVscode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeVs": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.branchDialogNativeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.close": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.done": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionAdvanced": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltinHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustom": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceCustomHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPath": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathPlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitPathBrowse": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargon": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglish": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonTranslateHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeMergeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergeModeSquashHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTarget": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewBranchHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.mergedViewTargetHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.head": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.local": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.remoteOnly": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.tag": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.stash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.mergedIn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.nodes": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.lines": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeParent": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMerge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeStash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedChain": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "legend.edgeMergedTie": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.jumpHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.resetView": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.createBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.copySha": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.pushBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.deleteBranch": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.renameCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.undoCommit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.viewChanges": "পরিবর্তন দেখুন...",
    "menu.jumpToOriginal": "মূল কমিটলৈ যাওক",
    "changes.title": "{sha} ত পৰিবৰ্তনসমূহ",
    "changes.tabChanged": "সলনি হ’ল",
    "changes.tabAll": "সকলো ফাইল",
    "changes.added": "যোগ কৰা হৈছে",
    "changes.modified": "পৰিৱৰ্তিত",
    "changes.deleted": "মচি পেলোৱা হৈছে",
    "changes.renamed": "পুনৰ নামকৰণ কৰা হৈছে",
    "changes.noChanges": "এই কমিটে কোনো ফাইল সলনি নকৰে।",
    "changes.loading": "লোডিং diff...",
    "changes.selectFile": "এটা ফাইল নিৰ্ব্বাচন কৰক ইয়াৰ পৰিবৰ্তনসমূহ চাবলৈ।",
    "changes.original": "মূল",
    "changes.changed": "এই কমিট",
    "changes.binary": "বাইনাৰী ফাইল — দেখুৱাবলৈ কোনো লিখনী পাৰ্থক্য নাই।",
    "changes.tooLarge": "ফাইলটো ডিফ কৰিবলৈ বহুত ডাঙৰ।",
    "changes.renamedFrom": "{path} ৰ পৰা পুনৰ নামকৰণ কৰা হৈছে",
    "changes.close": "বন্ধ",
    "changes.prevChange": "পূৰ্বৰ পৰিৱৰ্তন",
    "changes.nextChange": "পৰৱৰ্তী পৰিৱৰ্তন",
    "changes.collapseAll": "সকলো ফোল্ডাৰ সংকুচিত কৰক",
    "changes.expandAll": "সকলো ফোল্ডাৰ প্ৰসাৰিত কৰক",
    "changes.searchPlaceholder": "ফাইলসমূহ সন্ধান কৰক...",
    "changes.noSearchResults": "আপোনাৰ সন্ধানৰ সৈতে কোনো ফাইল মিল নাই।",
    "changes.maximize": "সৰ্বাধিক কৰক",
    "changes.restore": "আকাৰ পুনৰুদ্ধাৰ কৰক",
    "find.open": "এই পাৰ্থক্যত সন্ধান কৰক (Ctrl+F)",
    "find.placeholder": "diff মধ্যে খুঁজুন...",
    "find.prev": "পূৰ্বৰ মেচ",
    "find.next": "পৰৱৰ্তী মেচ",
    "find.close": "সন্ধান বন্ধ কৰক",
    "find.noResults": "কোনো ফলাফল নাই",
    "commit.title": "পৰিবৰ্তনসমূহ কমিট কৰক",
    "commit.close": "বন্ধ",
    "commit.loading": "ট্যাগ এর সাথে ছবি 'Reding working tree':",
    "commit.noChanges": "প্ৰতিশ্ৰুতি দিবলগীয়া কোনো স্থানীয় পৰিৱৰ্তন নাই।",
    "commit.selectFile": "এটা ফাইল নিৰ্ব্বাচন কৰক ইয়াৰ পাৰ্থক্য চাবলৈ।",
    "commit.selectAll": "সকলো নিৰ্বাচন কৰক",
    "commit.selectNone": "সকলো পৰিষ্কাৰ কৰক",
    "commit.message": "বাৰ্তা কমিট কৰক",
    "commit.messagePlaceholder": "পৰিৱৰ্তনৰ বৰ্ণনা কৰা",
    "commit.selectedCount": "{count} ফাইল(সমূহ) নিৰ্বাচিত কৰা হৈছে",
    "commit.review": "পুনৰীক্ষণ",
    "commit.reviewTitle": "কমিট কৰাৰ আগতে চূড়ান্ত পৰীক্ষা কৰক",
    "commit.reviewSummary": "{count} ফাইল(সমূহ) বাৰ্তাৰ সৈতে কমিট কৰক: {message}",
    "commit.back": "পিছলৈ",
    "commit.commit": "স্থানীয় কমিট সৃষ্টি কৰক",
    "menu.stashApply": "ষ্টেচ প্ৰয়োগ কৰক",
    "menu.stashPop": "পপ stash (প্ৰয়োগ & আঁতৰাওক)",
    "menu.stashDrop": "ড্রপ stash...",
    "merge.title": "মাৰ্জ ব্ৰাঞ্চ",
    "merge.route": "বৰ্তমানৰ শাখাত একত্ৰিত হোৱা",
    "merge.source": "(উৎস)ৰ পৰা",
    "merge.target": "ইনটু (বৰ্তমান)",
    "merge.loading": "মাৰ্জ বিশ্লেষণ...",
    "merge.upToDate": "ইতিমধ্যে আপডেট — একত্ৰিত কৰিবলৈ একো নাই।",
    "merge.fastForward": "দ্ৰুত-আগলৈ — কোনো মাৰ্জ কমিটৰ প্ৰয়োজন নাই (“এটা জোৰ কৰিবলৈ “এটা মাৰ্জ কমিট সৃষ্টি কৰক” টিক কৰক)।",
    "merge.summary": "{files} ফাইল(সমূহ) সলনি কৰা হৈছে, {conflicts} সংঘাত(সমূহ)।",
    "merge.conflictsWarning": "এই একত্ৰীকৰণৰ সংঘাত আছে — আপুনি এতিয়াও একত্ৰিত কৰিব পাৰে, তাৰ পিছত সম্পাদকত সমাধান কৰিব পাৰে।",
    "merge.noChanges": "কোনো ফাইল পৰিবৰ্তন নহয়।",
    "merge.files": "ফলত পৰিৱৰ্তন",
    "merge.added": "যোগ কৰা হৈছে",
    "merge.modified": "পৰিৱৰ্তিত",
    "merge.deleted": "মচি পেলোৱা হৈছে",
    "merge.conflict": "বিবাদ",
    "merge.message": "কমিট বাৰ্তা একত্ৰিত কৰক",
    "merge.messagePlaceholder": "শাখা 'উৎস' একত্ৰিত কৰক।",
    "merge.messageFfHint": "এটা ফাষ্ট-ফৰৱাৰ্ড মাৰ্জত আওকাণ কৰা হৈছে (কোনো কমিট সৃষ্টি কৰা হোৱা নাই)।",
    "merge.noFastForward": "এটা মাৰ্জ কমিট সৃষ্টি কৰক (কোনো ফাষ্ট-ফৰৱাৰ্ড নাই)",
    "merge.noFastForwardHint": "সদায় এটা মাৰ্জ কমিট ৰেকৰ্ড কৰক, আনকি যেতিয়া শাখাই ফাষ্ট-ফৰৱাৰ্ড কৰিব পাৰে।",
    "merge.merge": "মাৰ্জ কৰক",
    "merge.cancel": "বাতিল কৰক",
    "merge.previewError": "এটা পূৰ্বদৰ্শন গণনা কৰিব পৰা নগ'ল: {message}",
    "merge.squashBadge": "স্কোয়াচ",
    "merge.squashNote": "স্কোয়াচ মাৰ্জ — প্ৰতিটো পৰিৱৰ্তন “{target}” ত ONE কমিট হিচাপে লেণ্ড হয়; “{source}” ৰ নিজৰ কমিটসমূহ ইয়াৰ ইতিহাসৰ বাহিৰত থাকে। ছেটিংছ › মাৰ্জত ঘূৰি যাওক।",
    "merge.messageSquash": "বাৰ্তা কমিট কৰক",
    "merge.squashMessagePlaceholder": "'উৎস'ৰ পৰা স্কোৱাচ কৰা পৰিবৰ্তনসমূহ।",
    "newBranch.title": "শাখা সৃষ্টি কৰক",
    "newBranch.startPoint": "{sha} ৰ পৰা আৰম্ভ হোৱা নতুন শাখা",
    "newBranch.startPointOn": "{refs} ত",
    "newBranch.location": "অৱস্থান",
    "newBranch.locationRoot": "(শিপা)",
    "newBranch.expandAll": "সকলো প্ৰসাৰিত কৰক",
    "newBranch.collapseAll": "সকলো ভাঙি পেলাওক",
    "newBranch.expandFolder": "ফোল্ডাৰ প্ৰসাৰিত কৰক",
    "newBranch.collapseFolder": "ফোল্ডাৰ সংকুচিত কৰক",
    "newBranch.name": "শাখাৰ নাম",
    "newBranch.namePlaceholder": "মোৰ-শাখা",
    "newBranch.fullName": "সম্পূৰ্ণ নাম: {name}",
    "newBranch.invalid": "অবৈধ শাখাৰ নাম",
    "newBranch.exists": "এই নামৰ এটা শাখা ইতিমধ্যে আছে",
    "newBranch.checkout": "সৃষ্টিৰ পিছত শাখা চেকআউট কৰক",
    "newBranch.create": "সৃষ্টি কৰা",
    "newBranch.cancel": "বাতিল কৰক",
    "status.loading": "লোডিং গ্রাফ...",
    "status.summary": "{repo}{commits} কমিটসমূহ দেখুৱাইছে, {refs} refs",
    "status.branchCreated": "{sha} ত \"{name}\" শাখা সৃষ্টি কৰা হৈছে",
    "status.error": "ত্ৰুটি: {message}",
    "status.opFailed": "অপাৰেচন বিফল হ’ল",
    "status.fetching": "Fetching...",
    "status.pulling": "টানিব...",
    "status.pushing": "ঠেলি...",
    "status.committing": "স্থানীয় কমিট সৃষ্টি কৰা...",
    "status.commitCreated": "স্থানীয় কমিট সৃষ্টি কৰা হৈছে {sha}: {message}",
    "status.syncing": "ছিংকিং...",
    "status.noHead": "গ্ৰাফত কোনো বৰ্তমানৰ চেকআউট পোৱা নগ'ল।",
    "status.undoing": "কমিট বাতিল কৰা হৈছে...",
    "status.commitUndone": "কমিট অনডন — পৰিবৰ্তনসমূহ কাম কৰা গছত পুনৰ আহিছে।",
    "status.undoConflict": "হিট সংঘাতসমূহ পূৰ্বৱৰ্তী অৱস্থালৈ ঘূৰাই আনিব — সম্পাদকত সেইবোৰ সমাধান কৰক, তাৰ পিছত আগবাঢ়ক।",
    "status.stashApplying": "স্ট্যাশ প্রয়োগ...",
    "status.stashPopping": "পপিং stash...",
    "status.stashDropping": "ড্ৰপিং stash...",
    "status.stashApplied": "ষ্টেচ প্ৰয়োগ কৰিলে।",
    "status.stashPopped": "ষ্টেছ পপ কৰিলে।",
    "status.stashDropped": "ষ্টেছ ড্ৰপ হৈ গ’ল।",
    "status.stashConflict": "সংঘাতসমূহ ষ্টেচ কৰক — সম্পাদকত সেইবোৰ সমাধান কৰক।",
    "status.merging": "মাৰ্জিং...",
    "status.merged": "মাৰ্জ সম্পূৰ্ণ হ'ল।",
    "status.mergeConflict": "মাৰ্জৰ সংঘাত আছে — সম্পাদকত সমাধান কৰক, তাৰ পিছত কমিট কৰক।",
    "search.title": "সন্ধান",
    "search.tooltip": "সন্ধান কমিট কৰে",
    "search.placeholder": "অনুসন্ধান কমিট...",
    "search.previous": "পূৰ্বৰ ফলাফল",
    "search.next": "পৰৱৰ্তী ফলাফল",
    "search.noResults": "কোনো ফলাফল নাই",
    "search.resultCount": "এক্সপিএলএইচ০এক্স/এক্সপিএলএইচ১এক্স",
    "search.filter": "ফিল্টাৰ ধৰণ",
    "search.highlight": "হাইলাইট মোড",
    "search.filterMode": "সন্ধান প্ৰদৰ্শন",
    "search.filterModeFilter": "ফিল্টাৰ কৰক (অমিল লুকুৱাওক)",
    "search.filterModeHighlight": "হাইলাইট কৰক (সকলো দেখুৱাওক, মিল চিহ্নিত কৰক)",
    "search.filterModeHint": "গ্ৰাফত সন্ধানৰ ফলাফল কেনেকৈ প্ৰদৰ্শিত হয় বাছক।",
    "search.visibility": "স্থান সন্ধান কৰক",
    "search.visibilityToolbar": "সঁজুলিদণ্ডিকা বুটাম",
    "search.visibilityAlwaysVisible": "সদায়-দৃশ্যমান বাৰ",
    "search.visibilityHint": "সন্ধানক এটা সঁজুলিদণ্ডিকা আইকন বা ওপৰত স্থায়ী বাৰ হিচাপে দেখুৱাওক।",
    "footer.github": "GitHub",
  },
  bn: {
    "toolbar.refresh": "⟳ রিফ্রেশ",
    "toolbar.fetch": "⤓ আনুন",
    "toolbar.pull": "⇩ টান",
    "toolbar.push": "⇧ ধাক্কা",
    "toolbar.commit": "✓ প্রতিশ্রুতিবদ্ধ",
    "toolbar.sync": "⇅ সিঙ্ক",
    "toolbar.jumpHead": "⌖ চেকআউটে যান",
    "toolbar.reset": "⤢ ভিউ রিসেট করুন",
    "toolbar.settings": "⚙ সেটিংস",
    "details.header": "কমিট বিবরণ",
    "details.close": "বন্ধ",
    "details.sha": "SHA",
    "details.shortSha": "সংক্ষিপ্ত SHA",
    "details.message": "বার্তা",
    "details.author": "লেখক",
    "details.date": "তারিখ",
    "details.labels": "লেবেল",
    "details.location": "অবস্থান",
    "details.currentHead": "হেড — বর্তমান চেকআউট",
    "settings.title": "সেটিংস",
    "settings.language": "ভাষা",
    "settings.theme": "থিম",
    "settings.themeLight": "আলো",
    "settings.themeDark": "অন্ধকার",
    "settings.themeLightHint": "হালকা ব্যাকগ্রাউন্ড, গাঢ় লেখা।",
    "settings.themeDarkHint": "গাঢ় পটভূমি, হালকা পাঠ্য।",
    "settings.mainBranch": "প্রধান শাখা",
    "settings.mainBranchAuto": "স্বয়ংক্রিয়",
    "settings.mainBranchSearch": "শাখা অনুসন্ধান করুন…",
    "settings.mainBranchNoMatch": "কোন মিল শাখা",
    "settings.display": "প্রদর্শন শৈলী",
    "settings.displayModern": "আধুনিক",
    "settings.displayClassic": "ক্লাসিক",
    "settings.displayModernHint": "ফ্রি ক্যানভাস — প্যানে টেনে আনুন, জুম করতে স্ক্রোল করুন।",
    "settings.displayClassicHint": "স্থির ক্যানভাস — ট্রাঙ্ক বামে পিন করা, জুম নেই, শুধুমাত্র স্ক্রোল করুন (SVN রিভিশন গ্রাফের মতো)।",
    "settings.sectionGeneral": "সাধারণ",
    "settings.sectionGraph": "গ্রাফ",
    "settings.sectionChanges": "দৃষ্টিভঙ্গি পরিবর্তন করে",
    "settings.diffMinimap": "ডিফ মিনিম্যাপ",
    "settings.diffMinimapOn": "দেখানো হয়েছে",
    "settings.diffMinimapOff": "লুকানো",
    "settings.diffMinimapOnHint": "ডিফের পাশে একটি VS কোড-স্টাইল ওভারভিউ স্ট্রিপ দেখান — স্ক্রোল করতে টেনে আনুন; পরিবর্তন মার্কার অন্তর্ভুক্ত.",
    "settings.diffMinimapOffHint": "কোনো ওভারভিউ স্ট্রিপ নেই — পার্থক্যটি সাধারণভাবে স্ক্রোল করুন।",
    "settings.commitReview": "পর্যালোচনা কমিট",
    "settings.commitReviewOn": "কমিট করার আগে জিজ্ঞাসা করুন",
    "settings.commitReviewOff": "সরাসরি অঙ্গীকার করুন",
    "settings.commitReviewOnHint": "স্থানীয় কমিট তৈরি করার আগে একটি চূড়ান্ত ফাইল তালিকা এবং বার্তা চেক দেখান।",
    "settings.commitReviewOffHint": "কমিট বোতামটি অবিলম্বে স্থানীয় কমিট তৈরি করে।",
    "settings.svnBranchDialog": "SVN-শৈলী শাখা ডায়ালগ",
    "settings.svnBranchDialogHint": "একটি শাখা তৈরি করার সময় একটি ফোল্ডার-ট্রি পিকার দেখান (একটি সাধারণ প্রম্পটের পরিবর্তে)।",
    "settings.branchDialog": "শাখা ডায়ালগ",
    "settings.branchDialogSvnTitle": "SVN-শৈলী",
    "settings.branchDialogNativeVscode": "ভিএস কোড শৈলী",
    "settings.branchDialogNativeVs": "ভিজ্যুয়াল স্টুডিও শৈলী",
    "settings.branchDialogNativeHint": "আপনার IDE এর অন্তর্নির্মিত শাখা প্রম্পট।",
    "settings.close": "বন্ধ",
    "settings.done": "সম্পন্ন",
    "settings.sectionAdvanced": "উন্নত",
    "settings.gitSource": "গিট এক্সিকিউটেবল",
    "settings.gitSourceBuiltin": "অন্তর্নির্মিত",
    "settings.gitSourceBuiltinHint": "IDE এর বিল্ট-ইন গিট এক্সটেনশন থেকে গিট বাইনারি ব্যবহার করুন।",
    "settings.gitSourceCustom": "কাস্টম পথ",
    "settings.gitPath": "গিট পথ",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ব্রাউজ করুন...",
    "settings.jargon": "গিট শর্তাবলী",
    "settings.jargonTranslate": "অনুবাদ করুন",
    "settings.jargonEnglish": "ইংরেজিতে রাখুন",
    "settings.jargonTranslateHint": "ইন্টারফেসের ভাষায় গিট পদগুলি (টান, ধাক্কা, কমিট, শাখা...) অনুবাদ করুন।",
    "settings.jargonEnglishHint": "ইংরেজিতে গিট পদগুলি (টান, ধাক্কা, কমিট, শাখা…) রাখুন; অন্য সবকিছু অনুবাদ করুন।",
    "settings.sectionMerge": "একত্রিত করুন",
    "settings.mergeMode": "স্টাইল মার্জ করুন",
    "settings.mergeModeMerge": "সাধারণ একত্রীকরণ",
    "settings.mergeModeSquash": "স্কোয়াশ মার্জ",
    "settings.mergeModeMergeHint": "শাখাটিকে যেমন আছে তেমনি একত্রিত করে: এর প্রতিশ্রুতিগুলি বর্তমান শাখার ইতিহাসের অংশ হয়ে ওঠে, একটি মার্জ কমিট (অথবা সম্ভব হলে দ্রুত-ফরওয়ার্ড) দ্বারা যুক্ত হয়।",
    "settings.mergeModeSquashHint": "বর্তমান শাখায় (গিট মার্জ --স্কোয়াশ) পুরো শাখাটিকে একটি একক প্রতিশ্রুতিতে সঙ্কুচিত করে। শাখার নিজস্ব প্রতিশ্রুতিগুলি কখনই বর্তমান শাখার ইতিহাসে প্রবেশ করে না — আপনি সেখানে শুধুমাত্র একটি প্রতিশ্রুতি দেখতে পান। শাখাটি নিজেই অস্পর্শিত থাকে, তাই এর প্রতিশ্রুতিগুলি তার নিজস্ব গলিতে দৃশ্যমান থাকে।",
    "settings.mergedView": "গ্রাফে মার্জ করা কমিট",
    "settings.mergedViewBranch": "শুধুমাত্র তাদের নিজস্ব শাখায়",
    "settings.mergedViewTarget": "এছাড়াও শাখায় তারা একীভূত হয়েছিল",
    "settings.mergedViewBranchHint": "একটি একত্রিত শাখার প্রতিশ্রুতি শুধুমাত্র তাদের নিজস্ব লেনে আঁকা হয়; মার্জ একটি সংযোগকারী হিসাবে দেখায়। ক্লাসিক ভিউ।",
    "settings.mergedViewTargetHint": "একটি মার্জ আনা কমিটগুলি প্রাপ্তি শাখার গলিতেও আঁকা হয়, মার্জ কমিটের নীচে স্ট্যাক করা হয় এবং মূলের সাথে লেভেল - কারণ সেই শাখায় এখন সেগুলি রয়েছে৷ অনুলিপিগুলি ফ্যাকাশে এবং যে শাখায় তারা লেখা হয়েছিল তার সাথে রয়েছে, তাই এটি পরিষ্কার থাকে যে সেগুলি সেখানে তৈরি করা হয়নি। স্কোয়াশ একত্রীকরণ প্রভাবিত হয় না: একটি স্কোয়াশ একটি সাধারণ প্রতিশ্রুতি লেখে, তাই কেবলমাত্র সেই প্রতিশ্রুতিটি উপস্থিত হয়।",
    "legend.title": "কিংবদন্তি",
    "legend.head": "প্রধান / বর্তমান শাখা",
    "legend.local": "স্থানীয় শাখা",
    "legend.remote": "দূরবর্তী শাখা",
    "legend.remoteOnly": "শুধুমাত্র মেঘের মধ্যে (টানা হয় না)",
    "legend.tag": "ট্যাগ (সংস্করণ)",
    "legend.commit": "কমিট",
    "legend.stash": "লুকিয়ে রাখা (তাক করা কাজ)",
    "legend.mergedIn": "একত্রিত হয়ে এসেছিল (অন্য শাখায় লেখা)",
    "legend.nodes": "বাক্স",
    "legend.lines": "লাইন",
    "legend.edgeParent": "একই লাইনে পূর্ববর্তী প্রতিশ্রুতি",
    "legend.edgeMerge": "মার্জ-ইন শাখা — মার্জ কমিট এ তীর বিন্দু",
    "legend.edgeBranch": "একটি শাখা এখানে বন্ধ কাঁটা",
    "legend.edgeStash": "লুকিয়ে রাখা এবং প্রতিশ্রুতি এটি রাখা হয়েছে",
    "legend.edgeMergedChain": "এই শাখার লাইন মার্জ-ইন কমিটের মাধ্যমে চলছে",
    "legend.edgeMergedTie": "এটি যে শাখায় লেখা হয়েছিল সেখানে একই প্রতিশ্রুতি",
    "menu.jumpHead": "⌖ চেকআউটে যান",
    "menu.resetView": "⤢ ভিউ রিসেট করুন",
    "menu.createBranch": "এখান থেকে শাখা তৈরি করুন...",
    "menu.checkout": "এই কমিট চেকআউট",
    "menu.copySha": "কপি কমিট SHA",
    "menu.pushBranch": "পুশ শাখা \"{name}\"",
    "menu.renameCommit": "প্রতিশ্রুতি বার্তার নাম পরিবর্তন করুন...",
    "menu.undoCommit": "প্রতিশ্রুতি পূর্বাবস্থায় ফেরান (পরিবর্তনগুলি রাখুন)...",
    "menu.viewChanges": "পরিবর্তনগুলি দেখুন...",
    "menu.jumpToOriginal": "মূল প্রতিশ্রুতিতে যান",
    "node.mergedInTooltip": "মার্জ {merge} এই কমিটটিকে \"{target}\"-এ নিয়ে এসেছে এবং এটি \"{origin}\"-এ লেখা ছিল।",
    "changes.title": "{sha} এ পরিবর্তন",
    "changes.tabChanged": "পরিবর্তিত হয়েছে",
    "changes.tabAll": "সমস্ত ফাইল",
    "changes.added": "যোগ করা হয়েছে",
    "changes.modified": "পরিবর্তিত",
    "changes.deleted": "মুছে ফেলা হয়েছে",
    "changes.renamed": "নাম পরিবর্তন করা হয়েছে",
    "changes.noChanges": "এই কমিট কোন ফাইল পরিবর্তন.",
    "changes.loading": "পার্থক্য লোড হচ্ছে...",
    "changes.selectFile": "এর পরিবর্তনগুলি দেখতে একটি ফাইল নির্বাচন করুন৷",
    "changes.original": "আসল",
    "changes.changed": "এই প্রতিশ্রুতি",
    "changes.binary": "বাইনারি ফাইল - দেখানোর জন্য কোন টেক্সট ডিফ নেই।",
    "changes.tooLarge": "ফাইলটি পার্থক্য করার জন্য খুব বড়।",
    "changes.renamedFrom": "{path} থেকে নাম পরিবর্তন করা হয়েছে",
    "changes.close": "বন্ধ",
    "changes.prevChange": "আগের পরিবর্তন",
    "changes.nextChange": "পরবর্তী পরিবর্তন",
    "changes.collapseAll": "সমস্ত ফোল্ডার সঙ্কুচিত করুন",
    "changes.expandAll": "সমস্ত ফোল্ডার প্রসারিত করুন",
    "changes.searchPlaceholder": "ফাইল অনুসন্ধান করুন...",
    "changes.noSearchResults": "কোনো ফাইল আপনার অনুসন্ধানের সাথে মেলে না।",
    "changes.maximize": "সর্বাধিক করুন",
    "changes.restore": "আকার পুনরুদ্ধার করুন",
    "find.open": "এই পার্থক্যে অনুসন্ধান করুন (Ctrl+F)",
    "find.placeholder": "পার্থক্য খুঁজুন…",
    "find.prev": "আগের ম্যাচ",
    "find.next": "পরের ম্যাচ",
    "find.close": "অনুসন্ধান বন্ধ করুন",
    "find.noResults": "কোন ফলাফল নেই",
    "commit.title": "পরিবর্তন কমিট",
    "commit.close": "বন্ধ",
    "commit.loading": "কাজের গাছ পড়া হচ্ছে...",
    "commit.noChanges": "প্রতিশ্রুতি কোন স্থানীয় পরিবর্তন আছে.",
    "commit.selectFile": "এর পার্থক্য দেখতে একটি ফাইল নির্বাচন করুন।",
    "commit.selectAll": "সব নির্বাচন করুন",
    "commit.selectNone": "সব সাফ করুন",
    "commit.message": "প্রতিশ্রুতি বার্তা",
    "commit.messagePlaceholder": "পরিবর্তনের বর্ণনা দাও",
    "commit.selectedCount": "{count} ফাইল(গুলি) নির্বাচন করা হয়েছে৷",
    "commit.review": "পর্যালোচনা",
    "commit.reviewTitle": "কমিট করার আগে চূড়ান্ত চেক",
    "commit.reviewSummary": "বার্তা সহ {count} ফাইল(গুলি) কমিট করুন: {message}",
    "commit.back": "ফিরে",
    "commit.commit": "স্থানীয় প্রতিশ্রুতি তৈরি করুন",
    "menu.stashApply": "স্ট্যাশ প্রয়োগ করুন",
    "menu.stashPop": "পপ স্ট্যাশ (প্রয়োগ করুন এবং সরান)",
    "menu.stashDrop": "লুকিয়ে রাখুন...",
    "merge.title": "শাখা একীভূত করুন",
    "merge.route": "বর্তমান শাখায় একীভূত হচ্ছে",
    "merge.source": "(উৎস) থেকে",
    "merge.target": "মধ্যে (বর্তমান)",
    "merge.loading": "মার্জ বিশ্লেষণ করা হচ্ছে...",
    "merge.upToDate": "ইতিমধ্যেই আপ টু ডেট — একত্রিত করার কিছু নেই৷",
    "merge.fastForward": "ফাস্ট-ফরোয়ার্ড — কোনো একত্রীকরণ প্রতিশ্রুতির প্রয়োজন নেই (একটি জোর করতে \"একটি মার্জ কমিট তৈরি করুন\" টিক দিন)।",
    "merge.summary": "{files} ফাইল(গুলি) পরিবর্তিত হয়েছে, {conflicts} দ্বন্দ্ব(গুলি)৷",
    "merge.conflictsWarning": "এই একত্রীকরণে বিরোধ রয়েছে — আপনি এখনও একত্রীকরণ করতে পারেন, তারপর সম্পাদকে সেগুলি সমাধান করুন৷",
    "merge.noChanges": "কোনো ফাইল পরিবর্তন হয় না।",
    "merge.files": "ফলাফল পরিবর্তন",
    "merge.added": "যোগ করা হয়েছে",
    "merge.modified": "পরিবর্তিত",
    "merge.deleted": "মুছে ফেলা হয়েছে",
    "merge.conflict": "দ্বন্দ্ব",
    "merge.message": "কমিট মেসেজ মার্জ করুন",
    "merge.messagePlaceholder": "শাখা 'উৎস' একত্রিত করুন",
    "merge.messageFfHint": "একটি দ্রুত-ফরোয়ার্ড মার্জ এ উপেক্ষা করা হয়েছে (কোন কমিট তৈরি করা হয় না)।",
    "merge.noFastForward": "একটি মার্জ কমিট তৈরি করুন (কোন ফাস্ট-ফরওয়ার্ড নয়)",
    "merge.noFastForwardHint": "সর্বদা একটি মার্জ কমিট রেকর্ড করুন, এমনকি যখন শাখাটি দ্রুত এগিয়ে যেতে পারে।",
    "merge.merge": "একত্রিত করুন",
    "merge.cancel": "বাতিল করুন",
    "merge.previewError": "একটি পূর্বরূপ গণনা করা যায়নি: {message}",
    "merge.squashBadge": "স্কোয়াশ",
    "merge.squashNote": "স্কোয়াশ একত্রীকরণ — প্রতিটি পরিবর্তন “{target}”-এ এক প্রতিশ্রুতি হিসাবে আসে; \"{source}\" এর নিজস্ব প্রতিশ্রুতি তার ইতিহাসের বাইরে থাকে। সেটিংস › মার্জ এ ফিরে যান।",
    "merge.messageSquash": "প্রতিশ্রুতি বার্তা",
    "merge.squashMessagePlaceholder": "'উৎস' থেকে স্কোয়াশ করা পরিবর্তন",
    "newBranch.title": "শাখা তৈরি করুন",
    "newBranch.startPoint": "নতুন শাখা {sha} থেকে শুরু হচ্ছে",
    "newBranch.startPointOn": "{refs}-এ",
    "newBranch.location": "অবস্থান",
    "newBranch.locationRoot": "(মূল)",
    "newBranch.expandAll": "সব প্রসারিত",
    "newBranch.collapseAll": "সব সঙ্কুচিত করুন",
    "newBranch.expandFolder": "ফোল্ডার প্রসারিত করুন",
    "newBranch.collapseFolder": "ফোল্ডার সঙ্কুচিত করুন",
    "newBranch.name": "শাখার নাম",
    "newBranch.namePlaceholder": "আমার-শাখা",
    "newBranch.fullName": "পুরো নাম: {name}",
    "newBranch.invalid": "অবৈধ শাখার নাম",
    "newBranch.exists": "এই নামের একটি শাখা ইতিমধ্যেই বিদ্যমান৷",
    "newBranch.checkout": "সৃষ্টির পর চেকআউট শাখা",
    "newBranch.create": "তৈরি করুন",
    "newBranch.cancel": "বাতিল করুন",
    "status.loading": "গ্রাফ লোড হচ্ছে...",
    "status.summary": "{repo} {commits} কমিট দেখানো হচ্ছে, {refs} রেফ",
    "status.branchCreated": "{sha}-এ \"{name}\" শাখা তৈরি করা হয়েছে",
    "status.error": "ত্রুটি: {message}",
    "status.opFailed": "অপারেশন ব্যর্থ হয়েছে",
    "status.fetching": "আনা হচ্ছে...",
    "status.pulling": "টানছে...",
    "status.pushing": "ঠেলে দেওয়া হচ্ছে...",
    "status.committing": "স্থানীয় প্রতিশ্রুতি তৈরি করা হচ্ছে...",
    "status.commitCreated": "স্থানীয় কমিট {sha} তৈরি করা হয়েছে: {message}",
    "status.syncing": "সিঙ্ক হচ্ছে...",
    "status.noHead": "গ্রাফে কোনো বর্তমান চেকআউট পাওয়া যায়নি।",
    "status.undoing": "প্রতিশ্রুতি পূর্বাবস্থায় ফেরানো হচ্ছে...",
    "status.commitUndone": "কমিট পূর্বাবস্থায় — পরিবর্তনগুলি কার্যকারী গাছে ফিরে এসেছে৷",
    "status.undoConflict": "হিট দ্বন্দ্ব পূর্বাবস্থায় ফেরান — সম্পাদকে সেগুলি সমাধান করুন, তারপর চালিয়ে যান।",
    "status.stashApplying": "স্ট্যাশ প্রয়োগ করা হচ্ছে...",
    "status.stashPopping": "পপিং স্ট্যাশ...",
    "status.stashDropping": "লুকিয়ে রাখা হচ্ছে...",
    "status.stashApplied": "স্ট্যাশ প্রয়োগ করা হয়েছে।",
    "status.stashPopped": "লুকিয়ে রাখা হয়েছে.",
    "status.stashDropped": "লুকিয়ে রাখা হয়েছে।",
    "status.stashConflict": "স্ট্যাশ দ্বন্দ্ব — সম্পাদকে সেগুলি সমাধান করুন।",
    "status.merging": "একত্রিত হচ্ছে...",
    "status.merged": "মার্জ সম্পন্ন হয়েছে।",
    "status.mergeConflict": "একত্রীকরণের দ্বন্দ্ব রয়েছে — সম্পাদকে সেগুলি সমাধান করুন, তারপরে প্রতিশ্রুতি দিন।",
    "search.title": "অনুসন্ধান করুন",
    "search.tooltip": "অনুসন্ধান কমিট",
    "search.placeholder": "অনুসন্ধান কমিট...",
    "search.previous": "পূর্ববর্তী ফলাফল",
    "search.next": "পরবর্তী ফলাফল",
    "search.noResults": "কোন ফলাফল নেই",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ফিল্টার মোড",
    "search.highlight": "হাইলাইট মোড",
    "search.filterMode": "অনুসন্ধান প্রদর্শন",
    "search.filterModeFilter": "ফিল্টার (অমিল লুকান)",
    "search.filterModeHighlight": "হাইলাইট করুন (সমস্ত দেখান, মিল চিহ্নিত করুন)",
    "search.filterModeHint": "গ্রাফে অনুসন্ধানের ফলাফলগুলি কীভাবে প্রদর্শিত হবে তা চয়ন করুন৷",
    "search.visibility": "অবস্থান অনুসন্ধান করুন",
    "search.visibilityToolbar": "টুলবার বোতাম",
    "search.visibilityAlwaysVisible": "সর্বদা দৃশ্যমান বার",
    "search.visibilityHint": "শীর্ষে একটি টুলবার আইকন বা ক্রমাগত বার হিসাবে অনুসন্ধান দেখান।",
    "footer.github": "গিটহাব",
  },
  pa: {
    "toolbar.refresh": "⟳ ਤਾਜ਼ਾ ਕਰੋ",
    "toolbar.fetch": "⤓ ਪ੍ਰਾਪਤ ਕਰੋ",
    "toolbar.pull": "⇩ ਖਿੱਚੋ",
    "toolbar.push": "⇧ ਧੱਕਾ",
    "toolbar.commit": "✓ ਵਚਨਬੱਧਤਾ",
    "toolbar.sync": "⇅ ਸਿੰਕ",
    "toolbar.jumpHead": "⌖ ਚੈੱਕਆਉਟ 'ਤੇ ਜਾਓ",
    "toolbar.reset": "⤢ ਦ੍ਰਿਸ਼ ਰੀਸੈਟ ਕਰੋ",
    "toolbar.settings": "⚙ ਸੈਟਿੰਗਾਂ",
    "details.header": "ਕਮਿਟ ਵੇਰਵੇ",
    "details.close": "ਬੰਦ ਕਰੋ",
    "details.sha": "SHA",
    "details.shortSha": "ਛੋਟਾ SHA",
    "details.message": "ਸੁਨੇਹਾ",
    "details.author": "ਲੇਖਕ",
    "details.date": "ਮਿਤੀ",
    "details.labels": "ਲੇਬਲ",
    "details.location": "ਟਿਕਾਣਾ",
    "details.currentHead": "HEAD — ਮੌਜੂਦਾ ਚੈੱਕਆਉਟ",
    "settings.title": "ਸੈਟਿੰਗਾਂ",
    "settings.language": "ਭਾਸ਼ਾ",
    "settings.theme": "ਥੀਮ",
    "settings.themeLight": "ਚਾਨਣ",
    "settings.themeDark": "ਹਨੇਰਾ",
    "settings.themeLightHint": "ਹਲਕਾ ਬੈਕਗ੍ਰਾਊਂਡ, ਗੂੜ੍ਹਾ ਟੈਕਸਟ।",
    "settings.themeDarkHint": "ਗੂੜ੍ਹਾ ਬੈਕਗ੍ਰਾਊਂਡ, ਹਲਕਾ ਟੈਕਸਟ।",
    "settings.mainBranch": "ਮੁੱਖ ਸ਼ਾਖਾ",
    "settings.mainBranchAuto": "ਆਟੋਮੈਟਿਕ",
    "settings.mainBranchSearch": "ਸ਼ਾਖਾਵਾਂ ਖੋਜੋ…",
    "settings.mainBranchNoMatch": "ਕੋਈ ਮੇਲ ਖਾਂਦੀਆਂ ਸ਼ਾਖਾਵਾਂ ਨਹੀਂ ਹਨ",
    "settings.display": "ਡਿਸਪਲੇ ਸ਼ੈਲੀ",
    "settings.displayModern": "ਆਧੁਨਿਕ",
    "settings.displayClassic": "ਕਲਾਸਿਕ",
    "settings.displayModernHint": "ਮੁਫਤ ਕੈਨਵਸ — ਪੈਨ ਕਰਨ ਲਈ ਖਿੱਚੋ, ਜ਼ੂਮ ਕਰਨ ਲਈ ਸਕ੍ਰੋਲ ਕਰੋ।",
    "settings.displayClassicHint": "ਸਥਿਰ ਕੈਨਵਸ — ਤਣੇ ਨੂੰ ਖੱਬੇ ਪਾਸੇ ਪਿੰਨ ਕੀਤਾ ਗਿਆ, ਕੋਈ ਜ਼ੂਮ ਨਹੀਂ, ਸਿਰਫ਼ ਸਕ੍ਰੋਲ ਕਰੋ (ਜਿਵੇਂ ਕਿ SVN ਸੰਸ਼ੋਧਨ ਗ੍ਰਾਫ)।",
    "settings.sectionGeneral": "ਜਨਰਲ",
    "settings.sectionGraph": "ਗ੍ਰਾਫ਼",
    "settings.sectionChanges": "ਦ੍ਰਿਸ਼ ਬਦਲਦਾ ਹੈ",
    "settings.diffMinimap": "ਡਿਫ ਮਿਨੀਮੈਪ",
    "settings.diffMinimapOn": "ਦਿਖਾਇਆ ਗਿਆ",
    "settings.diffMinimapOff": "ਲੁਕਿਆ ਹੋਇਆ",
    "settings.diffMinimapOnHint": "ਡਿਫ ਦੇ ਕੋਲ ਇੱਕ VS ਕੋਡ-ਸ਼ੈਲੀ ਦੀ ਸੰਖੇਪ ਜਾਣਕਾਰੀ ਦਿਖਾਓ — ਇਸਨੂੰ ਸਕ੍ਰੌਲ ਕਰਨ ਲਈ ਖਿੱਚੋ; ਬਦਲਾਅ ਮਾਰਕਰ ਸ਼ਾਮਲ ਹਨ।",
    "settings.diffMinimapOffHint": "ਕੋਈ ਸੰਖੇਪ ਸਟ੍ਰਿਪ ਨਹੀਂ — ਆਮ ਤੌਰ 'ਤੇ ਅੰਤਰ ਨੂੰ ਸਕ੍ਰੋਲ ਕਰੋ।",
    "settings.commitReview": "ਸਮੀਖਿਆ ਦੀ ਵਚਨਬੱਧਤਾ ਕਰੋ",
    "settings.commitReviewOn": "ਵਚਨਬੱਧਤਾ ਤੋਂ ਪਹਿਲਾਂ ਪੁੱਛੋ",
    "settings.commitReviewOff": "ਸਿੱਧੇ ਵਚਨਬੱਧ",
    "settings.commitReviewOnHint": "ਸਥਾਨਕ ਕਮਿਟ ਬਣਾਉਣ ਤੋਂ ਪਹਿਲਾਂ ਇੱਕ ਅੰਤਮ ਫਾਈਲ ਸੂਚੀ ਅਤੇ ਸੁਨੇਹਾ ਜਾਂਚ ਦਿਖਾਓ।",
    "settings.commitReviewOffHint": "ਕਮਿਟ ਬਟਨ ਤੁਰੰਤ ਸਥਾਨਕ ਕਮਿਟ ਬਣਾਉਂਦਾ ਹੈ।",
    "settings.svnBranchDialog": "SVN-ਸ਼ੈਲੀ ਸ਼ਾਖਾ ਡਾਇਲਾਗ",
    "settings.svnBranchDialogHint": "ਬ੍ਰਾਂਚ ਬਣਾਉਣ ਵੇਲੇ ਫੋਲਡਰ-ਟਰੀ ਪੀਕਰ ਦਿਖਾਓ (ਸਧਾਰਨ ਪ੍ਰੋਂਪਟ ਦੀ ਬਜਾਏ)।",
    "settings.branchDialog": "ਬ੍ਰਾਂਚ ਡਾਇਲਾਗ",
    "settings.branchDialogSvnTitle": "SVN-ਸ਼ੈਲੀ",
    "settings.branchDialogNativeVscode": "VS ਕੋਡ ਸ਼ੈਲੀ",
    "settings.branchDialogNativeVs": "ਵਿਜ਼ੂਅਲ ਸਟੂਡੀਓ ਸ਼ੈਲੀ",
    "settings.branchDialogNativeHint": "ਤੁਹਾਡੇ IDE ਦਾ ਬਿਲਟ-ਇਨ ਬ੍ਰਾਂਚ ਪ੍ਰੋਂਪਟ।",
    "settings.close": "ਬੰਦ ਕਰੋ",
    "settings.done": "ਹੋ ਗਿਆ",
    "settings.sectionAdvanced": "ਉੱਨਤ",
    "settings.gitSource": "Git ਚੱਲਣਯੋਗ",
    "settings.gitSourceBuiltin": "ਬਿਲਟ-ਇਨ",
    "settings.gitSourceBuiltinHint": "IDE ਦੇ ਬਿਲਟ-ਇਨ ਗਿੱਟ ਐਕਸਟੈਂਸ਼ਨ ਤੋਂ ਗਿੱਟ ਬਾਈਨਰੀ ਦੀ ਵਰਤੋਂ ਕਰੋ।",
    "settings.gitSourceCustom": "ਕਸਟਮ ਮਾਰਗ",
    "settings.gitPath": "Git ਲਈ ਮਾਰਗ",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ਬ੍ਰਾਊਜ਼ ਕਰੋ...",
    "settings.jargon": "Git ਦੀਆਂ ਸ਼ਰਤਾਂ",
    "settings.jargonTranslate": "ਅਨੁਵਾਦ ਕਰੋ",
    "settings.jargonEnglish": "ਅੰਗਰੇਜ਼ੀ ਵਿੱਚ ਰੱਖੋ",
    "settings.jargonTranslateHint": "ਇੰਟਰਫੇਸ ਭਾਸ਼ਾ ਵਿੱਚ ਗਿੱਟ ਸ਼ਬਦਾਂ (ਪੁੱਲ, ਪੁਸ਼, ਕਮਿਟ, ਸ਼ਾਖਾ…) ਦਾ ਅਨੁਵਾਦ ਕਰੋ।",
    "settings.jargonEnglishHint": "ਅੰਗਰੇਜ਼ੀ ਵਿੱਚ ਗਿੱਟ ਦੇ ਸ਼ਬਦਾਂ (ਪੁੱਲ, ਪੁਸ਼, ਕਮਿਟ, ਸ਼ਾਖਾ…) ਰੱਖੋ; ਬਾਕੀ ਸਭ ਦਾ ਅਨੁਵਾਦ ਕਰੋ।",
    "settings.sectionMerge": "ਮਿਲਾਓ",
    "settings.mergeMode": "ਸ਼ੈਲੀ ਨੂੰ ਮਿਲਾਓ",
    "settings.mergeModeMerge": "ਸਧਾਰਣ ਅਭੇਦ",
    "settings.mergeModeSquash": "ਸਕੁਐਸ਼ ਅਭੇਦ",
    "settings.mergeModeMergeHint": "ਬ੍ਰਾਂਚ ਨੂੰ ਇਸ ਤਰ੍ਹਾਂ ਮਿਲਾਉਂਦਾ ਹੈ ਜਿਵੇਂ ਕਿ ਇਹ ਹੈ: ਇਸ ਦੀਆਂ ਕਮਿਟਾਂ ਮੌਜੂਦਾ ਸ਼ਾਖਾ ਦੇ ਇਤਿਹਾਸ ਦਾ ਹਿੱਸਾ ਬਣ ਜਾਂਦੀਆਂ ਹਨ, ਇੱਕ ਅਭੇਦ ਪ੍ਰਤੀਬੱਧ (ਜਾਂ ਸੰਭਵ ਹੋਣ 'ਤੇ ਫਾਸਟ-ਫਾਰਵਰਡ) ਨਾਲ ਜੁੜੀਆਂ ਹੁੰਦੀਆਂ ਹਨ।",
    "settings.mergeModeSquashHint": "ਮੌਜੂਦਾ ਸ਼ਾਖਾ (git merge --squash) 'ਤੇ ਪੂਰੀ ਸ਼ਾਖਾ ਨੂੰ ਇੱਕ ਸਿੰਗਲ ਕਮਿਟ ਵਿੱਚ ਸਮੇਟਦਾ ਹੈ। ਬ੍ਰਾਂਚ ਦੇ ਆਪਣੇ ਕਮਿਟ ਕਦੇ ਵੀ ਮੌਜੂਦਾ ਬ੍ਰਾਂਚ ਦੇ ਇਤਿਹਾਸ ਵਿੱਚ ਦਾਖਲ ਨਹੀਂ ਹੁੰਦੇ - ਤੁਸੀਂ ਉੱਥੇ ਸਿਰਫ਼ ਇੱਕ ਕਮਿਟ ਦੇਖਦੇ ਹੋ। ਸ਼ਾਖਾ ਆਪਣੇ ਆਪ ਵਿੱਚ ਅਛੂਤ ਰਹਿ ਜਾਂਦੀ ਹੈ, ਇਸਲਈ ਇਸ ਦੀਆਂ ਕਮਿਟਸ ਆਪਣੀ ਗਲੀ ਵਿੱਚ ਦਿਖਾਈ ਦਿੰਦੀਆਂ ਹਨ।",
    "settings.mergedView": "ਗ੍ਰਾਫ ਵਿੱਚ ਵਿਲੀਨ ਕੀਤੇ ਗਏ",
    "settings.mergedViewBranch": "ਸਿਰਫ ਆਪਣੀ ਹੀ ਸ਼ਾਖਾ 'ਤੇ",
    "settings.mergedViewTarget": "ਦੀ ਸ਼ਾਖਾ ਵਿੱਚ ਵੀ ਮਿਲਾ ਦਿੱਤਾ ਗਿਆ",
    "settings.mergedViewBranchHint": "ਇੱਕ ਵਿਲੀਨ ਸ਼ਾਖਾ ਦੇ ਕਮਿਟ ਸਿਰਫ ਉਹਨਾਂ ਦੀ ਆਪਣੀ ਲੇਨ ਵਿੱਚ ਖਿੱਚੇ ਜਾਂਦੇ ਹਨ; ਅਭੇਦ ਇੱਕ ਕਨੈਕਟਰ ਦੇ ਰੂਪ ਵਿੱਚ ਦਿਖਾਈ ਦਿੰਦਾ ਹੈ। ਕਲਾਸਿਕ ਦ੍ਰਿਸ਼.",
    "settings.mergedViewTargetHint": "ਲਿਆਂਦੇ ਗਏ ਅਭੇਦ ਨੂੰ ਪ੍ਰਾਪਤ ਕਰਨ ਵਾਲੀ ਸ਼ਾਖਾ ਦੀ ਲੇਨ ਵਿੱਚ ਵੀ ਖਿੱਚਿਆ ਜਾਂਦਾ ਹੈ, ਮਰਜ ਕਮਿਟ ਦੇ ਹੇਠਾਂ ਸਟੈਕ ਕੀਤਾ ਜਾਂਦਾ ਹੈ ਅਤੇ ਮੂਲ ਦੇ ਨਾਲ ਪੱਧਰ ਹੁੰਦਾ ਹੈ - ਕਿਉਂਕਿ ਉਸ ਸ਼ਾਖਾ ਵਿੱਚ ਅਸਲ ਵਿੱਚ ਹੁਣ ਉਹ ਸ਼ਾਮਲ ਹਨ। ਕਾਪੀਆਂ ਫਿੱਕੀਆਂ ਹਨ ਅਤੇ ਉਸ ਸ਼ਾਖਾ ਦੇ ਨਾਲ ਹਨ ਜਿਸ 'ਤੇ ਉਹ ਲਿਖੀਆਂ ਗਈਆਂ ਸਨ, ਇਸ ਲਈ ਇਹ ਸਪੱਸ਼ਟ ਰਹਿੰਦਾ ਹੈ ਕਿ ਉਹ ਉੱਥੇ ਨਹੀਂ ਬਣਾਈਆਂ ਗਈਆਂ ਸਨ। ਸਕੁਐਸ਼ ਅਭੇਦ ਪ੍ਰਭਾਵਤ ਨਹੀਂ ਹੁੰਦੇ: ਇੱਕ ਸਕੁਐਸ਼ ਇੱਕ ਆਮ ਵਚਨਬੱਧਤਾ ਲਿਖਦਾ ਹੈ, ਇਸਲਈ ਸਿਰਫ ਉਹੀ ਪ੍ਰਤੀਬੱਧਤਾ ਪ੍ਰਗਟ ਹੁੰਦੀ ਹੈ।",
    "legend.title": "ਦੰਤਕਥਾ",
    "legend.head": "ਹੈੱਡ / ਮੌਜੂਦਾ ਸ਼ਾਖਾ",
    "legend.local": "ਸਥਾਨਕ ਸ਼ਾਖਾ",
    "legend.remote": "ਰਿਮੋਟ ਸ਼ਾਖਾ",
    "legend.remoteOnly": "ਸਿਰਫ਼ ਬੱਦਲ ਵਿੱਚ (ਖਿੱਚਿਆ ਨਹੀਂ ਗਿਆ)",
    "legend.tag": "ਟੈਗ (ਵਰਜਨ)",
    "legend.commit": "ਵਚਨਬੱਧਤਾ",
    "legend.stash": "ਸਟੈਸ਼ (ਰੱਖਿਆ ਕੰਮ)",
    "legend.mergedIn": "ਇੱਕ ਮਰਜ ਦੇ ਨਾਲ ਆਇਆ (ਕਿਸੇ ਹੋਰ ਸ਼ਾਖਾ 'ਤੇ ਲਿਖਿਆ)",
    "legend.nodes": "ਬਕਸੇ",
    "legend.lines": "ਲਾਈਨਾਂ",
    "legend.edgeParent": "ਉਸੇ ਲਾਈਨ 'ਤੇ ਪਿਛਲੀ ਕਮਿਟ",
    "legend.edgeMerge": "ਵਿਲੀਨ-ਵਿੱਚ ਸ਼ਾਖਾ — ਮਰਜ ਕਮਿਟ 'ਤੇ ਤੀਰ ਬਿੰਦੂ",
    "legend.edgeBranch": "ਇੱਥੇ ਇੱਕ ਸ਼ਾਖਾ ਬੰਦ ਹੋ ਗਈ",
    "legend.edgeStash": "ਸਟੈਸ਼ ਅਤੇ ਵਚਨਬੱਧਤਾ ਨੂੰ ਇਸ 'ਤੇ ਰੱਖਿਆ ਗਿਆ ਸੀ",
    "legend.edgeMergedChain": "ਇਸ ਬ੍ਰਾਂਚ ਦੀ ਲਾਈਨ ਵਿਲੀਨ-ਇਨ ਕਮਿਟਾਂ ਰਾਹੀਂ ਚੱਲਦੀ ਹੈ",
    "legend.edgeMergedTie": "ਜਿਸ ਸ਼ਾਖਾ 'ਤੇ ਇਹ ਲਿਖਿਆ ਗਿਆ ਸੀ ਉਸ 'ਤੇ ਵੀ ਉਹੀ ਕਮਿਟ",
    "menu.jumpHead": "⌖ ਚੈੱਕਆਉਟ 'ਤੇ ਜਾਓ",
    "menu.resetView": "⤢ ਦ੍ਰਿਸ਼ ਰੀਸੈਟ ਕਰੋ",
    "menu.createBranch": "ਇੱਥੋਂ ਬ੍ਰਾਂਚ ਬਣਾਓ...",
    "menu.checkout": "ਇਸ ਵਚਨਬੱਧਤਾ ਦੀ ਜਾਂਚ ਕਰੋ",
    "menu.copySha": "ਪ੍ਰਤੀਬੱਧ SHA ਨੂੰ ਕਾਪੀ ਕਰੋ",
    "menu.pushBranch": "ਪੁਸ਼ ਸ਼ਾਖਾ \"{name}\"",
    "menu.renameCommit": "ਪ੍ਰਤੀਬੱਧ ਸੰਦੇਸ਼ ਦਾ ਨਾਮ ਬਦਲੋ...",
    "menu.undoCommit": "ਵਚਨਬੱਧਤਾ ਨੂੰ ਅਣਡੂ ਕਰੋ (ਤਬਦੀਲੀਆਂ ਰੱਖੋ)…",
    "menu.viewChanges": "ਬਦਲਾਅ ਦੇਖੋ…",
    "menu.jumpToOriginal": "ਮੂਲ ਵਚਨਬੱਧਤਾ 'ਤੇ ਜਾਓ",
    "node.mergedInTooltip": "{merge} ਨੂੰ ਮਿਲਾਓ \"{target}\" ਵਿੱਚ ਇਸ ਪ੍ਰਤੀਬੱਧਤਾ ਨੂੰ ਲਿਆਇਆ - ਇਹ \"{origin}\" ਉੱਤੇ ਲਿਖਿਆ ਗਿਆ ਸੀ.",
    "changes.title": "{sha} ਵਿੱਚ ਬਦਲਾਅ",
    "changes.tabChanged": "ਬਦਲਿਆ",
    "changes.tabAll": "ਸਾਰੀਆਂ ਫਾਈਲਾਂ",
    "changes.added": "ਜੋੜਿਆ ਗਿਆ",
    "changes.modified": "ਸੋਧਿਆ ਗਿਆ",
    "changes.deleted": "ਮਿਟਾਇਆ ਗਿਆ",
    "changes.renamed": "ਨਾਮ ਬਦਲਿਆ",
    "changes.noChanges": "ਇਹ ਵਚਨਬੱਧਤਾ ਕੋਈ ਫਾਈਲਾਂ ਨਹੀਂ ਬਦਲਦੀ.",
    "changes.loading": "ਅੰਤਰ ਲੋਡ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ...",
    "changes.selectFile": "ਇਸ ਦੀਆਂ ਤਬਦੀਲੀਆਂ ਦੇਖਣ ਲਈ ਇੱਕ ਫਾਈਲ ਚੁਣੋ।",
    "changes.original": "ਮੂਲ",
    "changes.changed": "ਇਹ ਵਚਨਬੱਧਤਾ",
    "changes.binary": "ਬਾਈਨਰੀ ਫਾਈਲ — ਦਿਖਾਉਣ ਲਈ ਕੋਈ ਟੈਕਸਟ ਡਿਫ ਨਹੀਂ।",
    "changes.tooLarge": "ਫਾਈਲ ਬਹੁਤ ਵੱਡੀ ਹੈ",
    "changes.renamedFrom": "{path} ਤੋਂ ਨਾਮ ਬਦਲਿਆ ਗਿਆ",
    "changes.close": "ਬੰਦ ਕਰੋ",
    "changes.prevChange": "ਪਿਛਲੀ ਤਬਦੀਲੀ",
    "changes.nextChange": "ਅਗਲੀ ਤਬਦੀਲੀ",
    "changes.collapseAll": "ਸਾਰੇ ਫੋਲਡਰਾਂ ਨੂੰ ਸਮੇਟੋ",
    "changes.expandAll": "ਸਾਰੇ ਫੋਲਡਰਾਂ ਦਾ ਵਿਸਤਾਰ ਕਰੋ",
    "changes.searchPlaceholder": "ਫ਼ਾਈਲਾਂ ਖੋਜੋ…",
    "changes.noSearchResults": "ਕੋਈ ਫ਼ਾਈਲ ਤੁਹਾਡੀ ਖੋਜ ਨਾਲ ਮੇਲ ਨਹੀਂ ਖਾਂਦੀ।",
    "changes.maximize": "ਵੱਧ ਤੋਂ ਵੱਧ",
    "changes.restore": "ਆਕਾਰ ਰੀਸਟੋਰ ਕਰੋ",
    "find.open": "ਇਸ ਅੰਤਰ (Ctrl+F) ਵਿੱਚ ਖੋਜੋ",
    "find.placeholder": "ਅੰਤਰ ਵਿੱਚ ਲੱਭੋ...",
    "find.prev": "ਪਿਛਲਾ ਮੈਚ",
    "find.next": "ਅਗਲਾ ਮੈਚ",
    "find.close": "ਖੋਜ ਬੰਦ ਕਰੋ",
    "find.noResults": "ਕੋਈ ਨਤੀਜਾ ਨਹੀਂ",
    "commit.title": "ਤਬਦੀਲੀਆਂ ਦਾ ਵਾਅਦਾ ਕਰੋ",
    "commit.close": "ਬੰਦ ਕਰੋ",
    "commit.loading": "ਵਰਕਿੰਗ ਟ੍ਰੀ ਪੜ੍ਹ ਰਿਹਾ ਹੈ...",
    "commit.noChanges": "ਪ੍ਰਤੀਬੱਧ ਕਰਨ ਲਈ ਕੋਈ ਸਥਾਨਕ ਤਬਦੀਲੀਆਂ ਨਹੀਂ ਹਨ।",
    "commit.selectFile": "ਇੱਕ ਫਾਈਲ ਦਾ ਫਰਕ ਦੇਖਣ ਲਈ ਚੁਣੋ।",
    "commit.selectAll": "ਸਭ ਚੁਣੋ",
    "commit.selectNone": "ਸਭ ਸਾਫ਼ ਕਰੋ",
    "commit.message": "ਪ੍ਰਤੀਬੱਧ ਸੁਨੇਹਾ",
    "commit.messagePlaceholder": "ਤਬਦੀਲੀ ਦਾ ਵਰਣਨ ਕਰੋ",
    "commit.selectedCount": "{count} ਫਾਈਲਾਂ ਚੁਣੀਆਂ ਗਈਆਂ",
    "commit.review": "ਸਮੀਖਿਆ",
    "commit.reviewTitle": "ਕਮਿਟ ਤੋਂ ਪਹਿਲਾਂ ਅੰਤਿਮ ਜਾਂਚ",
    "commit.reviewSummary": "ਸੁਨੇਹੇ ਦੇ ਨਾਲ {count} ਫਾਈਲਾਂ ਨੂੰ ਕਮਿਟ ਕਰੋ: {message}",
    "commit.back": "ਪਿੱਛੇ",
    "commit.commit": "ਸਥਾਨਕ ਪ੍ਰਤੀਬੱਧਤਾ ਬਣਾਓ",
    "menu.stashApply": "ਸਟੈਸ਼ ਲਾਗੂ ਕਰੋ",
    "menu.stashPop": "ਪੌਪ ਸਟੈਸ਼ (ਲਾਗੂ ਕਰੋ ਅਤੇ ਹਟਾਓ)",
    "menu.stashDrop": "ਛੁਪਾਓ...",
    "merge.title": "ਬ੍ਰਾਂਚ ਨੂੰ ਮਿਲਾਓ",
    "merge.route": "ਮੌਜੂਦਾ ਸ਼ਾਖਾ ਵਿੱਚ ਰਲੇਵਾਂ",
    "merge.source": "(ਸਰੋਤ) ਤੋਂ",
    "merge.target": "ਵਿੱਚ (ਮੌਜੂਦਾ)",
    "merge.loading": "ਵਿਲੀਨਤਾ ਦਾ ਵਿਸ਼ਲੇਸ਼ਣ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ...",
    "merge.upToDate": "ਪਹਿਲਾਂ ਹੀ ਅੱਪ ਟੂ ਡੇਟ — ਮਿਲਾਨ ਲਈ ਕੁਝ ਨਹੀਂ।",
    "merge.fastForward": "ਫਾਸਟ-ਫਾਰਵਰਡ - ਕਿਸੇ ਮਰਜ ਕਮਿਟ ਦੀ ਲੋੜ ਨਹੀਂ ਹੈ (ਇੱਕ ਨੂੰ ਮਜਬੂਰ ਕਰਨ ਲਈ \"ਇੱਕ ਅਭੇਦ ਪ੍ਰਤੀਬੱਧ ਬਣਾਓ\" 'ਤੇ ਨਿਸ਼ਾਨ ਲਗਾਓ)।",
    "merge.summary": "{files} ਫ਼ਾਈਲ(ਜ਼) ਬਦਲੀ ਗਈ, {conflicts} ਟਕਰਾਅ।",
    "merge.conflictsWarning": "ਇਸ ਅਭੇਦ ਵਿੱਚ ਵਿਵਾਦ ਹਨ — ਤੁਸੀਂ ਅਜੇ ਵੀ ਅਭੇਦ ਹੋ ਸਕਦੇ ਹੋ, ਫਿਰ ਉਹਨਾਂ ਨੂੰ ਸੰਪਾਦਕ ਵਿੱਚ ਹੱਲ ਕਰ ਸਕਦੇ ਹੋ।",
    "merge.noChanges": "ਕੋਈ ਫ਼ਾਈਲ ਬਦਲਾਅ ਨਹੀਂ।",
    "merge.files": "ਨਤੀਜੇ ਵਜੋਂ ਤਬਦੀਲੀਆਂ",
    "merge.added": "ਜੋੜਿਆ ਗਿਆ",
    "merge.modified": "ਸੋਧਿਆ ਗਿਆ",
    "merge.deleted": "ਮਿਟਾਇਆ ਗਿਆ",
    "merge.conflict": "ਟਕਰਾਅ",
    "merge.message": "ਵਚਨਬੱਧ ਸੰਦੇਸ਼ ਨੂੰ ਮਿਲਾਓ",
    "merge.messagePlaceholder": "ਬ੍ਰਾਂਚ 'ਸਰੋਤ' ਨੂੰ ਮਿਲਾਓ",
    "merge.messageFfHint": "ਫਾਸਟ-ਫਾਰਵਰਡ ਮਰਜ 'ਤੇ ਅਣਡਿੱਠ ਕੀਤਾ ਗਿਆ (ਕੋਈ ਪ੍ਰਤੀਬੱਧ ਨਹੀਂ ਬਣਾਇਆ ਗਿਆ)।",
    "merge.noFastForward": "ਇੱਕ ਅਭੇਦ ਪ੍ਰਤੀਬੱਧ ਬਣਾਓ (ਕੋਈ ਫਾਸਟ-ਫਾਰਵਰਡ ਨਹੀਂ)",
    "merge.noFastForwardHint": "ਹਮੇਸ਼ਾ ਇੱਕ ਮਰਜ ਕਮਿਟ ਰਿਕਾਰਡ ਕਰੋ, ਭਾਵੇਂ ਬ੍ਰਾਂਚ ਫਾਸਟ-ਫਾਰਵਰਡ ਕਰ ਸਕਦੀ ਹੋਵੇ।",
    "merge.merge": "ਮਿਲਾਓ",
    "merge.cancel": "ਰੱਦ ਕਰੋ",
    "merge.previewError": "ਪ੍ਰੀਵਿਊ ਦੀ ਗਣਨਾ ਨਹੀਂ ਕੀਤੀ ਜਾ ਸਕੀ: {message}",
    "merge.squashBadge": "ਮਿੱਧਣਾ",
    "merge.squashNote": "ਸਕੁਐਸ਼ ਮਰਜ — “{target}” 'ਤੇ ਇਕ ਵਚਨਬੱਧਤਾ ਦੇ ਤੌਰ 'ਤੇ ਹਰ ਤਬਦੀਲੀ ਆਉਂਦੀ ਹੈ; “{source}” ਦੇ ਆਪਣੇ ਕਮਿਟ ਇਸ ਦੇ ਇਤਿਹਾਸ ਤੋਂ ਬਾਹਰ ਰਹਿੰਦੇ ਹਨ। ਸੈਟਿੰਗਾਂ › ਮਿਲਾਓ ਵਿੱਚ ਵਾਪਸ ਸਵਿੱਚ ਕਰੋ।",
    "merge.messageSquash": "ਪ੍ਰਤੀਬੱਧ ਸੁਨੇਹਾ",
    "merge.squashMessagePlaceholder": "'ਸਰੋਤ' ਤੋਂ ਸਕੁਐਸ਼ ਕੀਤੀਆਂ ਤਬਦੀਲੀਆਂ",
    "newBranch.title": "ਸ਼ਾਖਾ ਬਣਾਓ",
    "newBranch.startPoint": "{sha} ਤੋਂ ਸ਼ੁਰੂ ਹੋਣ ਵਾਲੀ ਨਵੀਂ ਸ਼ਾਖਾ",
    "newBranch.startPointOn": "{refs} 'ਤੇ",
    "newBranch.location": "ਟਿਕਾਣਾ",
    "newBranch.locationRoot": "(ਜੜ੍ਹ)",
    "newBranch.expandAll": "ਸਭ ਦਾ ਵਿਸਤਾਰ ਕਰੋ",
    "newBranch.collapseAll": "ਸਭ ਨੂੰ ਸੰਕੁਚਿਤ ਕਰੋ",
    "newBranch.expandFolder": "ਫੋਲਡਰ ਦਾ ਵਿਸਤਾਰ ਕਰੋ",
    "newBranch.collapseFolder": "ਫੋਲਡਰ ਨੂੰ ਸਮੇਟੋ",
    "newBranch.name": "ਸ਼ਾਖਾ ਦਾ ਨਾਮ",
    "newBranch.namePlaceholder": "ਮੇਰੀ ਸ਼ਾਖਾ",
    "newBranch.fullName": "ਪੂਰਾ ਨਾਮ: {name}",
    "newBranch.invalid": "ਅਵੈਧ ਸ਼ਾਖਾ ਨਾਮ",
    "newBranch.exists": "ਇਸ ਨਾਮ ਦੀ ਇੱਕ ਸ਼ਾਖਾ ਪਹਿਲਾਂ ਹੀ ਮੌਜੂਦ ਹੈ",
    "newBranch.checkout": "ਰਚਨਾ ਦੇ ਬਾਅਦ ਚੈਕਆਉਟ ਸ਼ਾਖਾ",
    "newBranch.create": "ਬਣਾਓ",
    "newBranch.cancel": "ਰੱਦ ਕਰੋ",
    "status.loading": "ਗ੍ਰਾਫ਼ ਲੋਡ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ...",
    "status.summary": "{repo} {commits} ਕਮਿਟ, {refs} ਰੈਫਸ ਦਿਖਾ ਰਿਹਾ ਹੈ",
    "status.branchCreated": "{sha} 'ਤੇ \"{name}\" ਸ਼ਾਖਾ ਬਣਾਈ ਗਈ",
    "status.error": "ਗਲਤੀ: {message}",
    "status.opFailed": "ਕਾਰਵਾਈ ਅਸਫਲ ਰਹੀ",
    "status.fetching": "ਪ੍ਰਾਪਤ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ...",
    "status.pulling": "ਖਿੱਚ ਰਿਹਾ ਹੈ...",
    "status.pushing": "ਧੱਕ ਰਿਹਾ ਹੈ...",
    "status.committing": "ਸਥਾਨਕ ਪ੍ਰਤੀਬੱਧਤਾ ਬਣਾਈ ਜਾ ਰਹੀ ਹੈ...",
    "status.commitCreated": "ਸਥਾਨਕ ਕਮਿਟ {sha} ਬਣਾਇਆ ਗਿਆ: {message}",
    "status.syncing": "ਸਿੰਕ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ...",
    "status.noHead": "ਗ੍ਰਾਫ ਵਿੱਚ ਕੋਈ ਮੌਜੂਦਾ ਚੈਕਆਉਟ ਨਹੀਂ ਮਿਲਿਆ।",
    "status.undoing": "ਵਚਨਬੱਧਤਾ ਨੂੰ ਅਣਡੂ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ...",
    "status.commitUndone": "ਕਮਿਟ ਅਨਡਨ — ਕੰਮ ਕਰਨ ਵਾਲੇ ਰੁੱਖ ਵਿੱਚ ਤਬਦੀਲੀਆਂ ਵਾਪਸ ਆ ਗਈਆਂ ਹਨ।",
    "status.undoConflict": "ਹਿੱਟ ਵਿਵਾਦਾਂ ਨੂੰ ਅਣਡੂ ਕਰੋ — ਉਹਨਾਂ ਨੂੰ ਸੰਪਾਦਕ ਵਿੱਚ ਹੱਲ ਕਰੋ, ਫਿਰ ਜਾਰੀ ਰੱਖੋ।",
    "status.stashApplying": "ਸਟੈਸ਼ ਨੂੰ ਲਾਗੂ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ...",
    "status.stashPopping": "ਪੋਪਿੰਗ ਸਟੈਸ਼...",
    "status.stashDropping": "ਸਟੇਸ਼ ਸੁੱਟ ਰਿਹਾ ਹੈ...",
    "status.stashApplied": "ਸਟੈਸ਼ ਲਾਗੂ ਕੀਤਾ ਗਿਆ।",
    "status.stashPopped": "ਸਟੈਸ਼ ਪੌਪ ਕੀਤਾ ਗਿਆ।",
    "status.stashDropped": "ਸਟੈਸ਼ ਸੁੱਟਿਆ।",
    "status.stashConflict": "ਸਟੈਸ਼ ਵਿਵਾਦ - ਉਹਨਾਂ ਨੂੰ ਸੰਪਾਦਕ ਵਿੱਚ ਹੱਲ ਕਰੋ।",
    "status.merging": "ਵਿਲੀਨ ਹੋ ਰਿਹਾ ਹੈ...",
    "status.merged": "ਮਿਲਾਨ ਪੂਰਾ ਹੋਇਆ।",
    "status.mergeConflict": "ਅਭੇਦ ਵਿੱਚ ਵਿਵਾਦ ਹਨ - ਉਹਨਾਂ ਨੂੰ ਸੰਪਾਦਕ ਵਿੱਚ ਹੱਲ ਕਰੋ, ਫਿਰ ਕਮਿਟ ਕਰੋ।",
    "search.title": "ਖੋਜ",
    "search.tooltip": "ਖੋਜ ਕਮਿਟ ਕਰਦਾ ਹੈ",
    "search.placeholder": "ਖੋਜ ਕਮਿਟ…",
    "search.previous": "ਪਿਛਲਾ ਨਤੀਜਾ",
    "search.next": "ਅਗਲਾ ਨਤੀਜਾ",
    "search.noResults": "ਕੋਈ ਨਤੀਜਾ ਨਹੀਂ",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ਫਿਲਟਰ ਮੋਡ",
    "search.highlight": "ਹਾਈਲਾਈਟ ਮੋਡ",
    "search.filterMode": "ਖੋਜ ਡਿਸਪਲੇ",
    "search.filterModeFilter": "ਫਿਲਟਰ (ਗੈਰ-ਮੇਲ ਲੁਕਾਓ)",
    "search.filterModeHighlight": "ਉਜਾਗਰ ਕਰੋ (ਸਾਰੇ ਦਿਖਾਓ, ਮੇਲ ਦੀ ਨਿਸ਼ਾਨਦੇਹੀ ਕਰੋ)",
    "search.filterModeHint": "ਚੁਣੋ ਕਿ ਗ੍ਰਾਫ ਵਿੱਚ ਖੋਜ ਨਤੀਜੇ ਕਿਵੇਂ ਪ੍ਰਦਰਸ਼ਿਤ ਹੁੰਦੇ ਹਨ।",
    "search.visibility": "ਖੋਜ ਸਥਾਨ",
    "search.visibilityToolbar": "ਟੂਲਬਾਰ ਬਟਨ",
    "search.visibilityAlwaysVisible": "ਸਦਾ-ਦ੍ਰਿਸ਼ਟੀ ਵਾਲੀ ਪੱਟੀ",
    "search.visibilityHint": "ਸਿਖਰ 'ਤੇ ਟੂਲਬਾਰ ਪ੍ਰਤੀਕ ਜਾਂ ਨਿਰੰਤਰ ਬਾਰ ਵਜੋਂ ਖੋਜ ਦਿਖਾਓ।",
    "footer.github": "GitHub",
  },
  gu: {
    "toolbar.refresh": "⟳ તાજું કરો",
    "toolbar.fetch": "⤓ મેળવો",
    "toolbar.pull": "⇩ ખેંચો",
    "toolbar.push": "⇧ દબાણ",
    "toolbar.commit": "✓ પ્રતિબદ્ધ",
    "toolbar.sync": "⇅ સમન્વયન",
    "toolbar.jumpHead": "⌖ ચેકઆઉટ પર જાઓ",
    "toolbar.reset": "⤢ દૃશ્ય રીસેટ કરો",
    "toolbar.settings": "⚙ સેટિંગ્સ",
    "details.header": "કમિટ વિગતો",
    "details.close": "બંધ કરો",
    "details.sha": "SHA",
    "details.shortSha": "ટૂંકા SHA",
    "details.message": "સંદેશ",
    "details.author": "લેખક",
    "details.date": "તારીખ",
    "details.labels": "લેબલ્સ",
    "details.location": "સ્થાન",
    "details.currentHead": "હેડ — વર્તમાન ચેકઆઉટ",
    "settings.title": "સેટિંગ્સ",
    "settings.language": "ભાષા",
    "settings.theme": "થીમ",
    "settings.themeLight": "પ્રકાશ",
    "settings.themeDark": "શ્યામ",
    "settings.themeLightHint": "આછું પૃષ્ઠભૂમિ, ઘેરો ટેક્સ્ટ.",
    "settings.themeDarkHint": "ડાર્ક બેકગ્રાઉન્ડ, લાઇટ ટેક્સ્ટ.",
    "settings.mainBranch": "મુખ્ય શાખા",
    "settings.mainBranchAuto": "સ્વયંસંચાલિત",
    "settings.mainBranchSearch": "શાખાઓ શોધો...",
    "settings.mainBranchNoMatch": "કોઈ મેળ ખાતી શાખાઓ નથી",
    "settings.display": "પ્રદર્શન શૈલી",
    "settings.displayModern": "આધુનિક",
    "settings.displayClassic": "ઉત્તમ",
    "settings.displayModernHint": "મફત કેનવાસ — પેન પર ખેંચો, ઝૂમ કરવા માટે સ્ક્રોલ કરો.",
    "settings.displayClassicHint": "સ્થિર કેનવાસ — ટ્રંક ડાબે પિન કરેલું, કોઈ ઝૂમ નહીં, ફક્ત સ્ક્રોલ કરો (જેમ કે SVN પુનરાવર્તન ગ્રાફ).",
    "settings.sectionGeneral": "જનરલ",
    "settings.sectionGraph": "આલેખ",
    "settings.sectionChanges": "દૃશ્ય બદલાય છે",
    "settings.diffMinimap": "ડિફ મિનિમેપ",
    "settings.diffMinimapOn": "બતાવેલ",
    "settings.diffMinimapOff": "છુપાયેલ",
    "settings.diffMinimapOnHint": "તફાવતની બાજુમાં VS કોડ-શૈલીની વિહંગાવલોકન પટ્ટી બતાવો — તેને સ્ક્રોલ કરવા માટે ખેંચો; ફેરફાર માર્કર્સ સમાવેશ થાય છે.",
    "settings.diffMinimapOffHint": "કોઈ વિહંગાવલોકન પટ્ટી નથી — સામાન્ય રીતે તફાવતને સ્ક્રોલ કરો.",
    "settings.commitReview": "પ્રતિબદ્ધ સમીક્ષા",
    "settings.commitReviewOn": "પ્રતિબદ્ધતા પહેલા પૂછો",
    "settings.commitReviewOff": "સીધા પ્રતિબદ્ધ",
    "settings.commitReviewOnHint": "સ્થાનિક કમિટ બનાવતા પહેલા અંતિમ ફાઇલ સૂચિ અને સંદેશ તપાસો બતાવો.",
    "settings.commitReviewOffHint": "કમિટ બટન તરત જ સ્થાનિક કમિટ બનાવે છે.",
    "settings.svnBranchDialog": "SVN-શૈલી શાખા સંવાદ",
    "settings.svnBranchDialogHint": "શાખા બનાવતી વખતે ફોલ્ડર-ટ્રી પીકર બતાવો (સાદા પ્રોમ્પ્ટને બદલે).",
    "settings.branchDialog": "શાખા સંવાદ",
    "settings.branchDialogSvnTitle": "SVN-શૈલી",
    "settings.branchDialogNativeVscode": "VS કોડ શૈલી",
    "settings.branchDialogNativeVs": "વિઝ્યુઅલ સ્ટુડિયો શૈલી",
    "settings.branchDialogNativeHint": "તમારા IDE નો બિલ્ટ-ઇન બ્રાન્ચ પ્રોમ્પ્ટ.",
    "settings.close": "બંધ કરો",
    "settings.done": "થઈ ગયું",
    "settings.sectionAdvanced": "ઉન્નત",
    "settings.gitSource": "Git એક્ઝેક્યુટેબલ",
    "settings.gitSourceBuiltin": "બિલ્ટ-ઇન",
    "settings.gitSourceBuiltinHint": "IDE ના બિલ્ટ-ઇન ગિટ એક્સ્ટેંશનમાંથી ગિટ બાઈનરીનો ઉપયોગ કરો.",
    "settings.gitSourceCustom": "કસ્ટમ પાથ",
    "settings.gitPath": "ગિટનો માર્ગ",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "બ્રાઉઝ કરો...",
    "settings.jargon": "Git શરતો",
    "settings.jargonTranslate": "અનુવાદ કરો",
    "settings.jargonEnglish": "અંગ્રેજીમાં રાખો",
    "settings.jargonTranslateHint": "ગિટના શબ્દો (પુલ, પુશ, કમિટ, બ્રાન્ચ...)ને ઇન્ટરફેસ ભાષામાં અનુવાદિત કરો.",
    "settings.jargonEnglishHint": "Git શબ્દો (પુલ, પુશ, કમિટ, બ્રાન્ચ...) અંગ્રેજીમાં રાખો; બીજું બધું ભાષાંતર કરો.",
    "settings.sectionMerge": "મર્જ કરો",
    "settings.mergeMode": "શૈલી મર્જ કરો",
    "settings.mergeModeMerge": "સામાન્ય મર્જ",
    "settings.mergeModeSquash": "સ્ક્વોશ મર્જ",
    "settings.mergeModeMergeHint": "બ્રાન્ચને જેમ છે તેમ મર્જ કરે છે: તેની કમિટ વર્તમાન શાખાના ઇતિહાસનો ભાગ બની જાય છે, મર્જ કમિટ (અથવા શક્ય હોય ત્યારે ફાસ્ટ-ફોરવર્ડ) દ્વારા જોડાય છે.",
    "settings.mergeModeSquashHint": "વર્તમાન શાખા (git merge --squash) પર સમગ્ર શાખાને એક જ કમિટમાં સંકુચિત કરે છે. શાખાની પોતાની પ્રતિબદ્ધતા વર્તમાન શાખાના ઇતિહાસમાં ક્યારેય દાખલ થતી નથી — તમે ત્યાં માત્ર એક પ્રતિબદ્ધ જુઓ છો. શાખા પોતે જ અસ્પૃશ્ય રહી ગઈ છે, તેથી તેની પ્રતિબદ્ધતા તેની પોતાની ગલીમાં દૃશ્યમાન રહે છે.",
    "settings.mergedView": "ગ્રાફમાં મર્જ કરેલ કમિટ",
    "settings.mergedViewBranch": "ફક્ત તેમની પોતાની શાખા પર",
    "settings.mergedViewTarget": "બ્રાન્ચમાં પણ તેઓ ભળી ગયા હતા",
    "settings.mergedViewBranchHint": "મર્જ કરેલ શાખાની કમિટ માત્ર તેમની પોતાની લેનમાં દોરવામાં આવે છે; મર્જ કનેક્ટર તરીકે દેખાય છે. ક્લાસિક દૃશ્ય.",
    "settings.mergedViewTargetHint": "મર્જમાં લાવવામાં આવેલ કમિટ્સને પ્રાપ્ત કરનાર શાખાની ગલીમાં પણ દોરવામાં આવે છે, મર્જ કમિટ હેઠળ સ્ટેક કરવામાં આવે છે અને મૂળ સાથે લેવલ કરવામાં આવે છે - કારણ કે તે શાખામાં ખરેખર હવે તે સમાવિષ્ટ છે. નકલો નિસ્તેજ છે અને તેઓ જે શાખા પર લખવામાં આવી હતી તેની સાથે છે, તેથી તે સ્પષ્ટ રહે છે કે તે ત્યાં બનાવવામાં આવી ન હતી. સ્ક્વોશ મર્જ અપ્રભાવિત છે: સ્ક્વોશ એક સામાન્ય પ્રતિબદ્ધતા લખે છે, તેથી માત્ર તે પ્રતિબદ્ધતા જ દેખાય છે.",
    "legend.title": "દંતકથા",
    "legend.head": "હેડ / વર્તમાન શાખા",
    "legend.local": "સ્થાનિક શાખા",
    "legend.remote": "દૂરસ્થ શાખા",
    "legend.remoteOnly": "ફક્ત વાદળમાં (ખેંચાયેલ નથી)",
    "legend.tag": "ટેગ (સંસ્કરણ)",
    "legend.commit": "પ્રતિબદ્ધ",
    "legend.stash": "સંતાડવાની જગ્યા (છાજલીઓનું કામ)",
    "legend.mergedIn": "મર્જ સાથે આવ્યો (બીજી શાખા પર લખાયેલ)",
    "legend.nodes": "બોક્સ",
    "legend.lines": "રેખાઓ",
    "legend.edgeParent": "આ જ લાઇન પર અગાઉની પ્રતિબદ્ધતા",
    "legend.edgeMerge": "મર્જ-ઇન બ્રાન્ચ — મર્જ કમિટ પર તીર બિંદુઓ",
    "legend.edgeBranch": "અહીં એક શાખા બંધ થઈ ગઈ",
    "legend.edgeStash": "સંતાડવાની જગ્યા અને પ્રતિબદ્ધતા તેને છાવરવામાં આવી હતી",
    "legend.edgeMergedChain": "આ શાખાની લાઇન મર્જ-ઇન કમિટમાંથી પસાર થાય છે",
    "legend.edgeMergedTie": "જે બ્રાન્ચ પર તે લખેલું હતું તેના પર પણ આ જ કમિટ",
    "menu.jumpHead": "⌖ ચેકઆઉટ પર જાઓ",
    "menu.resetView": "⤢ દૃશ્ય રીસેટ કરો",
    "menu.createBranch": "અહીંથી શાખા બનાવો...",
    "menu.checkout": "આ પ્રતિબદ્ધતા તપાસો",
    "menu.copySha": "પ્રતિબદ્ધ SHA કૉપિ કરો",
    "menu.pushBranch": "પુશ શાખા \"{name}\"",
    "menu.renameCommit": "પ્રતિબદ્ધ સંદેશનું નામ બદલો...",
    "menu.undoCommit": "પ્રતિબદ્ધતાને પૂર્વવત્ કરો (ફેરફારો રાખો)...",
    "menu.viewChanges": "ફેરફારો જુઓ...",
    "menu.jumpToOriginal": "મૂળ કમિટ પર જાઓ",
    "node.mergedInTooltip": "{merge} ને મર્જ કરો આ કમિટને \"{target}\" માં લાવ્યું - તે \"{origin}\" પર લખેલું હતું.",
    "changes.title": "{sha} માં ફેરફારો",
    "changes.tabChanged": "બદલાયેલ",
    "changes.tabAll": "બધી ફાઇલો",
    "changes.added": "ઉમેર્યું",
    "changes.modified": "સંશોધિત",
    "changes.deleted": "કાઢી નાખ્યું",
    "changes.renamed": "નામ બદલ્યું",
    "changes.noChanges": "આ કમિટ કોઈ ફાઈલોમાં ફેરફાર કરતું નથી.",
    "changes.loading": "તફાવત લોડ કરી રહ્યું છે...",
    "changes.selectFile": "તેના ફેરફારો જોવા માટે ફાઇલ પસંદ કરો.",
    "changes.original": "મૂળ",
    "changes.changed": "આ પ્રતિબદ્ધતા",
    "changes.binary": "બાઈનરી ફાઇલ - બતાવવા માટે કોઈ ટેક્સ્ટ ડિફ નથી.",
    "changes.tooLarge": "તફાવત માટે ફાઇલ ખૂબ મોટી છે.",
    "changes.renamedFrom": "{path} થી નામ બદલ્યું",
    "changes.close": "બંધ કરો",
    "changes.prevChange": "અગાઉનો ફેરફાર",
    "changes.nextChange": "આગામી ફેરફાર",
    "changes.collapseAll": "બધા ફોલ્ડર્સ સંકુચિત કરો",
    "changes.expandAll": "બધા ફોલ્ડર્સ વિસ્તૃત કરો",
    "changes.searchPlaceholder": "ફાઈલો શોધો...",
    "changes.noSearchResults": "કોઈ ફાઇલો તમારી શોધ સાથે મેળ ખાતી નથી.",
    "changes.maximize": "મહત્તમ કરો",
    "changes.restore": "પુનઃસ્થાપિત કદ",
    "find.open": "આ તફાવતમાં શોધો (Ctrl+F)",
    "find.placeholder": "તફાવતમાં શોધો...",
    "find.prev": "અગાઉની મેચ",
    "find.next": "આગામી મેચ",
    "find.close": "શોધ બંધ કરો",
    "find.noResults": "કોઈ પરિણામ નથી",
    "commit.title": "ફેરફારો કરો",
    "commit.close": "બંધ કરો",
    "commit.loading": "વર્કિંગ ટ્રી વાંચી રહ્યાં છીએ…",
    "commit.noChanges": "પ્રતિબદ્ધ કરવા માટે કોઈ સ્થાનિક ફેરફારો નથી.",
    "commit.selectFile": "તેનો તફાવત જોવા માટે ફાઇલ પસંદ કરો.",
    "commit.selectAll": "બધા પસંદ કરો",
    "commit.selectNone": "બધું સાફ કરો",
    "commit.message": "સંદેશ મોકલો",
    "commit.messagePlaceholder": "ફેરફારનું વર્ણન કરો",
    "commit.selectedCount": "{count} ફાઇલ(ઓ) પસંદ કરી",
    "commit.review": "સમીક્ષા",
    "commit.reviewTitle": "પ્રતિબદ્ધતા પહેલા અંતિમ તપાસ",
    "commit.reviewSummary": "સંદેશ સાથે {count} ફાઇલ(ઓ) મોકલો: {message}",
    "commit.back": "પાછળ",
    "commit.commit": "સ્થાનિક પ્રતિબદ્ધતા બનાવો",
    "menu.stashApply": "સંતાડવાની જગ્યા લાગુ કરો",
    "menu.stashPop": "પૉપ સ્ટેશ (લાગુ કરો અને દૂર કરો)",
    "menu.stashDrop": "છુપાવો...",
    "merge.title": "શાખા મર્જ કરો",
    "merge.route": "વર્તમાન શાખામાં વિલીનીકરણ",
    "merge.source": "(સ્રોત) તરફથી",
    "merge.target": "માં (વર્તમાન)",
    "merge.loading": "મર્જનું વિશ્લેષણ કરી રહ્યું છે...",
    "merge.upToDate": "પહેલેથી જ અપ ટૂ ડેટ — મર્જ કરવા માટે કંઈ નથી.",
    "merge.fastForward": "ફાસ્ટ-ફોરવર્ડ — મર્જ કમિટની જરૂર નથી (એકને દબાણ કરવા માટે \"મર્જ કમિટ બનાવો\" પર ટિક કરો).",
    "merge.summary": "{files} ફાઇલ(ઓ) બદલાઈ, {conflicts} સંઘર્ષ(ઓ).",
    "merge.conflictsWarning": "આ મર્જમાં તકરાર છે — તમે હજી પણ મર્જ કરી શકો છો, પછી તેને એડિટરમાં ઉકેલી શકો છો.",
    "merge.noChanges": "કોઈ ફાઇલમાં ફેરફાર નથી.",
    "merge.files": "પરિણામી ફેરફારો",
    "merge.added": "ઉમેર્યું",
    "merge.modified": "સંશોધિત",
    "merge.deleted": "કાઢી નાખ્યું",
    "merge.conflict": "સંઘર્ષ",
    "merge.message": "પ્રતિબદ્ધ સંદેશ મર્જ કરો",
    "merge.messagePlaceholder": "શાખા 'સ્રોત' મર્જ કરો",
    "merge.messageFfHint": "ફાસ્ટ-ફોરવર્ડ મર્જ પર અવગણવામાં આવે છે (કોઈ પ્રતિબદ્ધતા બનાવવામાં આવતી નથી).",
    "merge.noFastForward": "મર્જ કમિટ બનાવો (ફાસ્ટ-ફોરવર્ડ નહીં)",
    "merge.noFastForwardHint": "હંમેશા મર્જ કમિટ રેકોર્ડ કરો, પછી ભલે બ્રાન્ચ ફાસ્ટ ફોરવર્ડ કરી શકે.",
    "merge.merge": "મર્જ કરો",
    "merge.cancel": "રદ કરો",
    "merge.previewError": "પૂર્વાવલોકનની ગણતરી કરી શકાઈ નથી: {message}",
    "merge.squashBadge": "સ્ક્વોશ",
    "merge.squashNote": "સ્ક્વોશ મર્જ - દરેક ફેરફાર \"{target}\" પર એક પ્રતિબદ્ધ તરીકે આવે છે; \"{source}\" ની પોતાની પ્રતિબદ્ધતા તેના ઇતિહાસથી દૂર રહે છે. સેટિંગ્સમાં પાછા સ્વિચ કરો › મર્જ કરો.",
    "merge.messageSquash": "સંદેશ મોકલો",
    "merge.squashMessagePlaceholder": "'સ્રોત' માંથી સ્ક્વેશ્ડ ફેરફારો",
    "newBranch.title": "શાખા બનાવો",
    "newBranch.startPoint": "{sha} થી શરૂ થતી નવી શાખા",
    "newBranch.startPointOn": "{refs} પર",
    "newBranch.location": "સ્થાન",
    "newBranch.locationRoot": "(મૂળ)",
    "newBranch.expandAll": "બધાને વિસ્તૃત કરો",
    "newBranch.collapseAll": "બધા સંકુચિત કરો",
    "newBranch.expandFolder": "ફોલ્ડર વિસ્તૃત કરો",
    "newBranch.collapseFolder": "ફોલ્ડર સંકુચિત કરો",
    "newBranch.name": "શાખાનું નામ",
    "newBranch.namePlaceholder": "મારી શાખા",
    "newBranch.fullName": "પૂરું નામ: {name}",
    "newBranch.invalid": "અમાન્ય શાખા નામ",
    "newBranch.exists": "આ નામની શાખા પહેલેથી જ અસ્તિત્વમાં છે",
    "newBranch.checkout": "બનાવટ પછી ચેકઆઉટ શાખા",
    "newBranch.create": "બનાવો",
    "newBranch.cancel": "રદ કરો",
    "status.loading": "ગ્રાફ લોડ કરી રહ્યું છે...",
    "status.summary": "{repo} {commits} કમિટ બતાવે છે, {refs} રેફ",
    "status.branchCreated": "{sha} પર \"{name}\" શાખા બનાવી",
    "status.error": "ભૂલ: {message}",
    "status.opFailed": "ઓપરેશન નિષ્ફળ થયું",
    "status.fetching": "આનયન કરી રહ્યું છે...",
    "status.pulling": "ખેંચી રહ્યું છે...",
    "status.pushing": "દબાણ કરી રહ્યું છે...",
    "status.committing": "સ્થાનિક કમિટ બનાવી રહ્યાં છીએ...",
    "status.commitCreated": "સ્થાનિક કમિટ {sha} બનાવ્યું: {message}",
    "status.syncing": "સમન્વયિત કરી રહ્યું છે...",
    "status.noHead": "ગ્રાફમાં કોઈ વર્તમાન ચેકઆઉટ મળ્યું નથી.",
    "status.undoing": "પ્રતિબદ્ધતાને પૂર્વવત્ કરી રહ્યાં છીએ...",
    "status.commitUndone": "પૂર્વવત્ કરો - ફેરફારો કાર્યકારી વૃક્ષમાં પાછા આવ્યા છે.",
    "status.undoConflict": "હિટ તકરારને પૂર્વવત્ કરો — તેમને સંપાદકમાં ઉકેલો, પછી ચાલુ રાખો.",
    "status.stashApplying": "સ્ટૅશ લાગુ કરી રહ્યાં છીએ...",
    "status.stashPopping": "પોપિંગ સ્ટેશ...",
    "status.stashDropping": "છુપાવી રહ્યું છે...",
    "status.stashApplied": "સંતાડવાની જગ્યા લાગુ કરી.",
    "status.stashPopped": "સંતાડવાની જગ્યા પૉપ થઈ.",
    "status.stashDropped": "સંતાડવાની જગ્યા છોડી દીધી.",
    "status.stashConflict": "સંતાડવાની તકરાર - તેમને સંપાદકમાં ઉકેલો.",
    "status.merging": "મર્જ કરી રહ્યું છે...",
    "status.merged": "મર્જ પૂર્ણ થયું.",
    "status.mergeConflict": "મર્જમાં તકરાર છે — તેમને એડિટરમાં ઉકેલો, પછી કમિટ કરો.",
    "search.title": "શોધો",
    "search.tooltip": "શોધ કરે છે",
    "search.placeholder": "શોધ કમિટ...",
    "search.previous": "અગાઉનું પરિણામ",
    "search.next": "આગામી પરિણામ",
    "search.noResults": "કોઈ પરિણામ નથી",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ફિલ્ટર મોડ",
    "search.highlight": "હાઇલાઇટ મોડ",
    "search.filterMode": "શોધ પ્રદર્શન",
    "search.filterModeFilter": "ફિલ્ટર (બિન-મેચ છુપાવો)",
    "search.filterModeHighlight": "હાઇલાઇટ કરો (બધા બતાવો, મેચોને ચિહ્નિત કરો)",
    "search.filterModeHint": "ગ્રાફમાં શોધ પરિણામો કેવી રીતે પ્રદર્શિત થાય છે તે પસંદ કરો.",
    "search.visibility": "સ્થાન શોધો",
    "search.visibilityToolbar": "ટૂલબાર બટન",
    "search.visibilityAlwaysVisible": "હંમેશા દૃશ્યમાન બાર",
    "search.visibilityHint": "શોધને ટૂલબાર આયકન તરીકે અથવા ટોચ પર પર્સિસ્ટન્ટ બાર તરીકે બતાવો.",
    "footer.github": "GitHub",
  },
  or: {
    "toolbar.refresh": "⟳ ସତେଜ କରନ୍ତୁ |",
    "toolbar.fetch": "Etch ଆଣ |",
    "toolbar.pull": "⇩ ଟାଣ |",
    "toolbar.push": "⇧ ପୁସ୍",
    "toolbar.commit": "✓ ପ୍ରତିଶ୍ରୁତି",
    "toolbar.sync": "⇅ ସିଙ୍କ୍",
    "toolbar.jumpHead": "Check ଚେକଆଉଟ୍ କୁ ଯାଆନ୍ତୁ |",
    "toolbar.reset": "View ଦୃଶ୍ୟ ପୁନ et ସେଟ୍ କରନ୍ତୁ |",
    "toolbar.settings": "⚙ ସେଟିଂସମୂହ",
    "details.header": "ସବିଶେଷ ବିବରଣୀ ଦିଅନ୍ତୁ |",
    "details.close": "ବନ୍ଦ",
    "details.sha": "SHA",
    "details.shortSha": "ସଂକ୍ଷିପ୍ତ SHA |",
    "details.message": "ବାର୍ତ୍ତା |",
    "details.author": "ଲେଖକ",
    "details.date": "ତାରିଖ",
    "details.labels": "ଲେବଲ୍ |",
    "details.location": "ଅବସ୍ଥାନ",
    "details.currentHead": "HEAD - ସାମ୍ପ୍ରତିକ ଚେକଆଉଟ୍ |",
    "settings.title": "ସେଟିଂସମୂହ",
    "settings.language": "ଭାଷା",
    "settings.theme": "ଥିମ୍",
    "settings.themeLight": "ଆଲୋକ",
    "settings.themeDark": "ଅନ୍ଧାର",
    "settings.themeLightHint": "ହାଲୁକା ପୃଷ୍ଠଭୂମି, ଗା dark ଼ ପାଠ |",
    "settings.themeDarkHint": "ଗା ark ପୃଷ୍ଠଭୂମି, ହାଲୁକା ପାଠ |",
    "settings.mainBranch": "ମୁଖ୍ୟ ଶାଖା |",
    "settings.mainBranchAuto": "ସ୍ୱୟଂଚାଳିତ |",
    "settings.mainBranchSearch": "ଶାଖା ଖୋଜ…",
    "settings.mainBranchNoMatch": "କ No ଣସି ମେଳ ଖାଉଥିବା ଶାଖା ନାହିଁ |",
    "settings.display": "ପ୍ରଦର୍ଶନ ଶ style ଳୀ",
    "settings.displayModern": "ଆଧୁନିକ",
    "settings.displayClassic": "କ୍ଲାସିକ୍ |",
    "settings.displayModernHint": "ମାଗଣା କାନଭାସ୍ - ପ୍ୟାନକୁ ଟାଣନ୍ତୁ, ଜୁମ୍ କରିବାକୁ ସ୍କ୍ରୋଲ୍ କରନ୍ତୁ |",
    "settings.displayClassicHint": "ଫିକ୍ସଡ୍ କାନଭାସ୍ - ଟ୍ରଙ୍କ୍ ପିନ୍ଡ୍ ବାମ, କ zo ଣସି ଜୁମ୍ ନାହିଁ, କେବଳ ସ୍କ୍ରୋଲ୍ କରନ୍ତୁ (SVN ସଂଶୋଧନ ଗ୍ରାଫ୍ ପରି) |",
    "settings.sectionGeneral": "ଜେନେରାଲ୍ |",
    "settings.sectionGraph": "ଗ୍ରାଫ୍",
    "settings.sectionChanges": "ଦୃଶ୍ୟ ପରିବର୍ତ୍ତନ କରେ |",
    "settings.diffMinimap": "ଡିଫ୍ ମିନିମ୍ୟାପ୍ |",
    "settings.diffMinimapOn": "ପ୍ରଦର୍ଶିତ |",
    "settings.diffMinimapOff": "ଲୁକ୍କାୟିତ |",
    "settings.diffMinimapOnHint": "ଡିଫ୍ ପାଖରେ ଏକ VS କୋଡ୍-ଷ୍ଟାଇଲ୍ ପୂର୍ବାବଲୋକନ ଷ୍ଟ୍ରିପ୍ ଦେଖାନ୍ତୁ - ଏହାକୁ ସ୍କ୍ରୋଲ୍ କରିବାକୁ ଟାଣନ୍ତୁ; ମାର୍କର ପରିବର୍ତ୍ତନ ଅନ୍ତର୍ଭୁକ୍ତ |",
    "settings.diffMinimapOffHint": "କ over ଣସି ପୂର୍ବାବଲୋକନ ଷ୍ଟ୍ରିପ୍ ନାହିଁ - ସାଧାରଣତ the ଡିଫ୍ ସ୍କ୍ରୋଲ୍ କରନ୍ତୁ |",
    "settings.commitReview": "ସମୀକ୍ଷା ସମୀକ୍ଷା କରନ୍ତୁ |",
    "settings.commitReviewOn": "ପ୍ରତିଶ୍ରୁତି ପୂର୍ବରୁ ପଚାର |",
    "settings.commitReviewOff": "ସିଧାସଳଖ ପ୍ରତିଶ୍ରୁତି ଦିଅ |",
    "settings.commitReviewOnHint": "ସ୍ଥାନୀୟ ପ୍ରତିଶ୍ରୁତି ସୃଷ୍ଟି କରିବା ପୂର୍ବରୁ ଏକ ଅନ୍ତିମ ଫାଇଲ୍ ତାଲିକା ଏବଂ ବାର୍ତ୍ତା ଯାଞ୍ଚ ଦେଖାନ୍ତୁ |",
    "settings.commitReviewOffHint": "ପ୍ରତିବଦ୍ଧତା ବଟନ୍ ତୁରନ୍ତ ସ୍ଥାନୀୟ ପ୍ରତିବଦ୍ଧତା ସୃଷ୍ଟି କରେ |",
    "settings.svnBranchDialog": "SVN ଶ style ଳୀ ଶାଖା ସଂଳାପ |",
    "settings.svnBranchDialogHint": "ଏକ ଶାଖା ସୃଷ୍ଟି କରିବା ସମୟରେ ଏକ ଫୋଲ୍ଡର-ବୃକ୍ଷ ଚୟନକର୍ତ୍ତା ଦେଖାନ୍ତୁ (ଏକ ସରଳ ପ୍ରମ୍ପ୍ଟ ବଦଳରେ) |",
    "settings.branchDialog": "ଶାଖା ସଂଳାପ |",
    "settings.branchDialogSvnTitle": "SVN ଶ style ଳୀ |",
    "settings.branchDialogNativeVscode": "VS କୋଡ୍ ଶ style ଳୀ |",
    "settings.branchDialogNativeVs": "ଭିଜୁଆଲ୍ ଷ୍ଟୁଡିଓ ଶ style ଳୀ",
    "settings.branchDialogNativeHint": "ତୁମର IDE ର ବିଲ୍ଟ-ଇନ୍ ଶାଖା ପ୍ରମ୍ପ୍ଟ |",
    "settings.close": "ବନ୍ଦ",
    "settings.done": "ସମାପ୍ତ",
    "settings.sectionAdvanced": "ଉନ୍ନତ",
    "settings.gitSource": "ଏକଜେକ୍ୟୁଟେବଲ୍",
    "settings.gitSourceBuiltin": "ବିଲ୍ଟ-ଇନ୍ |",
    "settings.gitSourceBuiltinHint": "IDE ର ବିଲ୍ଟ-ଇନ୍ ଗିଟ୍ ଏକ୍ସଟେନ୍ସନ୍ ରୁ git ବାଇନାରୀ ବ୍ୟବହାର କରନ୍ତୁ |",
    "settings.gitSourceCustom": "କଷ୍ଟମ୍ ପଥ",
    "settings.gitSourceCustomHint": "ଆପଣଙ୍କର ନିଜ git ଏକଜେକ୍ୟୁଟେବଲ୍ ପାଇଁ ପଥ ନିର୍ଦ୍ଦିଷ୍ଟ କରନ୍ତୁ (ଯଥା / usr / bin / git କିମ୍ବା C:\\…\\git.exe) |",
    "settings.gitPath": "ଯିବା ପାଇଁ ପଥ",
    "settings.gitPathPlaceholder": "/ usr / bin / git",
    "settings.gitPathBrowse": "ବ୍ରାଉଜ୍…",
    "settings.jargon": "ସର୍ତ୍ତାବଳୀ",
    "settings.jargonTranslate": "ଅନୁବାଦ କର |",
    "settings.jargonEnglish": "ଇଂରାଜୀରେ ରଖନ୍ତୁ |",
    "settings.jargonTranslateHint": "ଇଣ୍ଟରଫେସ୍ ଭାଷାରେ ଗିଟ୍ ସର୍ତ୍ତାବଳୀ (ଟାଣ, ଠେଲିବା, କରିବା, ଶାଖା…) ଅନୁବାଦ କର |",
    "settings.jargonEnglishHint": "ଇଂରାଜୀରେ ଗିଟ୍ ସର୍ତ୍ତାବଳୀ (ଟାଣ, ଠେଲିବା, କରିବା, ଶାଖା…) ରଖ; ଅନ୍ୟ ସବୁକିଛି ଅନୁବାଦ କର |",
    "settings.sectionMerge": "ମିଶ୍ରଣ କରନ୍ତୁ |",
    "settings.mergeMode": "ଶ style ଳୀ ମିଶ୍ରଣ କରନ୍ତୁ |",
    "settings.mergeModeMerge": "ସାଧାରଣ ମିଶ୍ରଣ |",
    "settings.mergeModeSquash": "ସ୍କ୍ୱାସ୍ ମିଶ୍ରଣ |",
    "settings.mergeModeMergeHint": "ଶାଖାକୁ ଯେପରି ଭାବରେ ମିଶ୍ରଣ କରେ: ଏହାର ପ୍ରତିବଦ୍ଧତା ବର୍ତ୍ତମାନର ଶାଖାର ଇତିହାସର ଏକ ଅଂଶ ହୋଇଯାଏ, ଏକ ମିଶ୍ରଣ ପ୍ରତିଶ୍ରୁତି (କିମ୍ବା ଯେତେବେଳେ ସମ୍ଭବ ଶୀଘ୍ର ଅଗ୍ରଗାମୀ) ଦ୍ୱାରା ଯୋଗ ଦିଆଯାଏ |",
    "settings.mergeModeSquashHint": "ସାମ୍ପ୍ରତିକ ଶାଖା (git merge --squash) ଉପରେ ଏକକ ଶାଖାରେ ସମଗ୍ର ଶାଖାକୁ ଭାଙ୍ଗିଦିଏ | ଶାଖାର ନିଜସ୍ୱ ପ୍ରତିବଦ୍ଧତା ବର୍ତ୍ତମାନର ଶାଖାର ଇତିହାସରେ କେବେବି ପ୍ରବେଶ କରେ ନାହିଁ - ଆପଣ ସେଠାରେ କେବଳ ଗୋଟିଏ ପ୍ରତିବଦ୍ଧତା ଦେଖନ୍ତି | ଶାଖା ନିଜେ ଛୁଇଁ ନାହିଁ, ତେଣୁ ଏହାର କାର୍ଯ୍ୟଗୁଡ଼ିକ ନିଜ ଗାଡ଼ିରେ ଦୃଶ୍ୟମାନ ହୁଏ |",
    "settings.mergedView": "ଗ୍ରାଫରେ ମିଶ୍ରିତ କାର୍ଯ୍ୟ",
    "settings.mergedViewBranch": "କେବଳ ନିଜ ଶାଖାରେ |",
    "settings.mergedViewTarget": "ଶାଖାରେ ମଧ୍ୟ ସେମାନେ ମିଶ୍ରିତ ହୋଇଥିଲେ |",
    "settings.mergedViewBranchHint": "ଏକ ମିଶ୍ରିତ ଶାଖାର କାର୍ଯ୍ୟଗୁଡ଼ିକ କେବଳ ନିଜ ଗାଡ଼ିରେ ଅଙ୍କିତ ହୁଏ; ମିଶ୍ରଣ ଏକ ସଂଯୋଜକ ଭାବରେ ଦେଖାଯାଏ | କ୍ଲାସିକ୍ ଦୃଶ୍ୟ |",
    "settings.mergedViewTargetHint": "ଅଣାଯାଇଥିବା ମିଶ୍ରଣଗୁଡିକ ଗ୍ରହଣକାରୀ ଶାଖାର ଗାଡ଼ିରେ ମଧ୍ୟ ଅଙ୍କିତ ହୋଇଛି, ମିଶ୍ରଣ ପ୍ରତିଶ୍ରୁତି ଏବଂ ମୂଳ ସ୍ତର ସହିତ ଷ୍ଟାକ୍ ହୋଇଛି - କାରଣ ସେହି ଶାଖା ପ୍ରକୃତରେ ସେଗୁଡ଼ିକୁ ଧାରଣ କରିଛି | ନକଲଗୁଡ଼ିକ ଫିକା ପଡିଛି ଏବଂ ସେମାନେ ଲେଖାଯାଇଥିବା ଶାଖା ସହିତ ମୁହାଁଇଛନ୍ତି, ତେଣୁ ଏହା ସ୍ପଷ୍ଟ ହୋଇଛି ଯେ ସେଗୁଡ଼ିକ ସେଠାରେ ତିଆରି କରାଯାଇ ନାହିଁ | ସ୍କ୍ୱାସ୍ ମିଶ୍ରଣ ପ୍ରଭାବିତ ନୁହେଁ: ଏକ ସ୍କ୍ୱାସ୍ ଗୋଟିଏ ସାଧାରଣ ପ୍ରତିବଦ୍ଧତା ଲେଖେ, ତେଣୁ କେବଳ ସେହି ପ୍ରତିବଦ୍ଧତା ଦେଖାଯାଏ |",
    "legend.title": "କିମ୍ବଦନ୍ତୀ",
    "legend.head": "HEAD / ସାମ୍ପ୍ରତିକ ଶାଖା |",
    "legend.local": "ସ୍ଥାନୀୟ ଶାଖା |",
    "legend.remote": "ସୁଦୂର ଶାଖା |",
    "legend.remoteOnly": "କେବଳ ମେଘରେ (ଟାଣାଯାଇ ନାହିଁ)",
    "legend.tag": "ଟ୍ୟାଗ୍ (ସଂସ୍କରଣ)",
    "legend.commit": "ପ୍ରତିବଦ୍ଧତା |",
    "legend.stash": "ଷ୍ଟାଶ୍ (ସେଲଭ୍ କାମ)",
    "legend.mergedIn": "ଏକ ମିଶ୍ରଣ ସହିତ ଆସିଲା (ଅନ୍ୟ ଶାଖାରେ ଲେଖା)",
    "legend.nodes": "ବାକ୍ସଗୁଡ଼ିକ",
    "legend.lines": "ରେଖା |",
    "legend.edgeParent": "ସମାନ ଧାଡିରେ ପୂର୍ବ ପ୍ରତିବଦ୍ଧତା |",
    "legend.edgeMerge": "ମିଶ୍ରିତ ଶାଖା - ମିଶ୍ରଣ ପ୍ରତିଶ୍ରୁତିରେ ତୀର ପଏଣ୍ଟ |",
    "legend.edgeBranch": "ଏଠାରେ ଏକ ଶାଖା ବନ୍ଦ ହୋଇଗଲା |",
    "legend.edgeStash": "ଷ୍ଟାସ୍ ଏବଂ ପ୍ରତିବଦ୍ଧତା ଉପରେ ସେଭ୍ କରାଯାଇଥିଲା |",
    "legend.edgeMergedChain": "ମିଶ୍ରିତ କାର୍ଯ୍ୟଗୁଡ଼ିକ ମାଧ୍ୟମରେ ଚାଲୁଥିବା ଏହି ଶାଖାର ରେଖା |",
    "legend.edgeMergedTie": "ଶାଖା ଉପରେ ସମାନ ପ୍ରତିବଦ୍ଧତା ଉପରେ ଲେଖାଯାଇଥିଲା |",
    "menu.jumpHead": "Check ଚେକଆଉଟ୍ କୁ ଯାଆନ୍ତୁ |",
    "menu.resetView": "View ଦୃଶ୍ୟ ପୁନ et ସେଟ୍ କରନ୍ତୁ |",
    "menu.createBranch": "ଏଠାରୁ ଶାଖା ସୃଷ୍ଟି କରନ୍ତୁ…",
    "menu.checkout": "ଏହି ପ୍ରତିବଦ୍ଧତା ଯା Check ୍ଚ କରନ୍ତୁ |",
    "menu.copySha": "SHA କପି କରନ୍ତୁ |",
    "menu.pushBranch": "ଶାଖା \"{name}\" କୁ ପୁସ୍ କର |",
    "menu.renameBranch": "ଶାଖାର ନାମ \"{name}\" â |",
    "menu.deleteBranch": "ଶାଖା \"{name}\" â ବିଲୋପ କରନ୍ତୁ |",
    "menu.renameCommit": "ପ୍ରତିଶ୍ରୁତି ବାର୍ତ୍ତା ପୁନ ame ନାମ କରନ୍ତୁ…",
    "menu.undoCommit": "ପ୍ରତ୍ୟାହାର କରନ୍ତୁ (ପରିବର୍ତ୍ତନ ରଖନ୍ତୁ)…",
    "menu.viewChanges": "ପରିବର୍ତ୍ତନଗୁଡିକ ଦେଖନ୍ତୁ…",
    "menu.mergeBranch": "\"{source}\" କୁ \"{target}\" â into ରେ ମିଶ୍ରଣ କରନ୍ତୁ |",
    "menu.jumpToOriginal": "ମୂଳ ପ୍ରତିବଦ୍ଧତାକୁ ଯାଆନ୍ତୁ |",
    "node.mergedInTooltip": "ମିଶ୍ରଣ {merge} ଏହି ପ୍ରତିବଦ୍ଧତାକୁ \"{target}\" ରେ ଆଣିଲା — ଏହା \"{origin}\" ରେ ଲେଖାଯାଇଥିଲା |",
    "changes.title": "{sha} ରେ ପରିବର୍ତ୍ତନ",
    "changes.tabChanged": "ପରିବର୍ତ୍ତିତ",
    "changes.tabAll": "ସମସ୍ତ ଫାଇଲ୍ |",
    "changes.added": "ଯୋଡା ଯାଇଛି |",
    "changes.modified": "ପରିବର୍ତ୍ତିତ",
    "changes.deleted": "ବିଲୋପ",
    "changes.renamed": "ନାମ ପରିବର୍ତ୍ତନ",
    "changes.noChanges": "ଏହି ପ୍ରତିବଦ୍ଧତା କ files ଣସି ଫାଇଲ୍ ପରିବର୍ତ୍ତନ କରେ ନାହିଁ |",
    "changes.loading": "ଡିଫ୍ ଲୋଡିଙ୍ଗ୍…",
    "changes.selectFile": "ଏହାର ପରିବର୍ତ୍ତନ ଦେଖିବାକୁ ଏକ ଫାଇଲ୍ ଚୟନ କରନ୍ତୁ |",
    "changes.original": "ମୂଳ",
    "changes.changed": "ଏହି ପ୍ରତିବଦ୍ଧତା |",
    "changes.binary": "ବାଇନାରୀ ଫାଇଲ୍ - ଦେଖାଇବାକୁ କ text ଣସି ପାଠ୍ୟ ଭିନ୍ନ ନୁହେଁ |",
    "changes.tooLarge": "ଭିନ୍ନ ହେବା ପାଇଁ ଫାଇଲ୍ ବହୁତ ବଡ ଅଟେ |",
    "changes.renamedFrom": "{path} ରୁ ପୁନ amed ନାମିତ |",
    "changes.close": "ବନ୍ଦ",
    "changes.prevChange": "ପୂର୍ବ ପରିବର୍ତ୍ତନ",
    "changes.nextChange": "ପରବର୍ତ୍ତୀ ପରିବର୍ତ୍ତନ |",
    "changes.collapseAll": "ସମସ୍ତ ଫୋଲ୍ଡର୍ ଗୁଡିକୁ ଭାଙ୍ଗନ୍ତୁ |",
    "changes.expandAll": "ସମସ୍ତ ଫୋଲ୍ଡର ବିସ୍ତାର କରନ୍ତୁ |",
    "changes.searchPlaceholder": "ଫାଇଲଗୁଡିକ ଖୋଜ…",
    "changes.noSearchResults": "କ search ଣସି ଫାଇଲ ଆପଣଙ୍କ ସନ୍ଧାନ ସହିତ ମେଳ ଖାଉ ନାହିଁ |",
    "changes.maximize": "ସର୍ବାଧିକ କରନ୍ତୁ |",
    "changes.restore": "ଆକାର ପୁନରୁଦ୍ଧାର କରନ୍ତୁ |",
    "find.open": "ଏହି ଡିଫ୍ (Ctrl + F) ରେ ଖୋଜ |",
    "find.placeholder": "Diff ରେ ଖୋଜ…",
    "find.prev": "ପୂର୍ବ ମ୍ୟାଚ୍",
    "find.next": "ପରବର୍ତ୍ତୀ ମ୍ୟାଚ୍ |",
    "find.close": "ସନ୍ଧାନ ବନ୍ଦ କରନ୍ତୁ |",
    "find.noResults": "କ results ଣସି ଫଳାଫଳ ନାହିଁ |",
    "commit.title": "ପରିବର୍ତ୍ତନ କର |",
    "commit.close": "ବନ୍ଦ",
    "commit.loading": "କାର୍ଯ୍ୟ ବୃକ୍ଷ ପ Reading ଼ିବା…",
    "commit.noChanges": "କରିବାକୁ କ local ଣସି ସ୍ଥାନୀୟ ପରିବର୍ତ୍ତନ ନାହିଁ |",
    "commit.selectFile": "ଏହାର ଭିନ୍ନତା ଦେଖିବାକୁ ଏକ ଫାଇଲ୍ ଚୟନ କରନ୍ତୁ |",
    "commit.selectAll": "ସମସ୍ତ ଚୟନ କରନ୍ତୁ |",
    "commit.selectNone": "ସମସ୍ତ ସଫା କର |",
    "commit.message": "ପ୍ରତିବଦ୍ଧ ବାର୍ତ୍ତା |",
    "commit.messagePlaceholder": "ପରିବର୍ତ୍ତନ ବର୍ଣ୍ଣନା କର |",
    "commit.selectedCount": "{count} ଫାଇଲ୍ (ଗୁଡିକ) ଚୟନ କରାଯାଇଛି |",
    "commit.review": "ସମୀକ୍ଷା କରନ୍ତୁ |",
    "commit.reviewTitle": "ପ୍ରତିଶ୍ରୁତି ପୂର୍ବରୁ ଅନ୍ତିମ ଯାଞ୍ଚ |",
    "commit.reviewSummary": "ବାର୍ତ୍ତା ସହିତ {count} ଫାଇଲ୍ (ଗୁଡିକ) କୁ ସମର୍ପଣ କରନ୍ତୁ: {message} |",
    "commit.back": "ପଛକୁ",
    "commit.commit": "ସ୍ଥାନୀୟ ପ୍ରତିବଦ୍ଧତା ସୃଷ୍ଟି କରନ୍ତୁ |",
    "menu.stashApply": "ଷ୍ଟାଶ୍ ପ୍ରୟୋଗ କରନ୍ତୁ |",
    "menu.stashPop": "ପପ୍ ଷ୍ଟାଶ୍ (ପ୍ରୟୋଗ ଏବଂ ଅପସାରଣ)",
    "menu.stashDrop": "ଡ୍ରପ୍ ଷ୍ଟାଶ୍…",
    "merge.title": "ଶାଖା ମିଶ୍ରଣ କରନ୍ତୁ |",
    "merge.route": "ସାମ୍ପ୍ରତିକ ଶାଖାରେ ମିଶ୍ରଣ |",
    "merge.source": "(ଉତ୍ସ) ରୁ",
    "merge.target": "ଭିତରକୁ (ସାମ୍ପ୍ରତିକ)",
    "merge.loading": "ମିଶ୍ରଣ ବିଶ୍ଳେଷଣ…",
    "merge.upToDate": "ପୂର୍ବରୁ ଅଦ୍ୟାବଧି - ମିଶ୍ରଣ ପାଇଁ କିଛି ନାହିଁ |",
    "merge.fastForward": "ଦ୍ରୁତ-ଅଗ୍ରଗାମୀ - କ mer ଣସି ମିଶ୍ରଣ ପ୍ରତିଶ୍ରୁତି ଆବଶ୍ୟକ ନାହିଁ (ଗୋଟିଏକୁ ବାଧ୍ୟ କରିବା ପାଇଁ “ଏକ ମିଶ୍ରଣ ସଂକଳ୍ପ ସୃଷ୍ଟି” ଟିକ୍ କରନ୍ତୁ) |",
    "merge.summary": "{files} ଫାଇଲ୍ (ଗୁଡିକ) ପରିବର୍ତ୍ତିତ ହେଲା, {conflicts} ବିବାଦ (ଗୁଡିକ) |",
    "merge.conflictsWarning": "ଏହି ମିଶ୍ରଣରେ ଦ୍ୱନ୍ଦ୍ୱ ଅଛି - ଆପଣ ତଥାପି ମିଶ୍ରଣ କରିପାରିବେ, ତାପରେ ସେଗୁଡ଼ିକୁ ସମ୍ପାଦକରେ ସମାଧାନ କରନ୍ତୁ |",
    "merge.noChanges": "କ file ଣସି ଫାଇଲ୍ ପରିବର୍ତ୍ତନ ହୁଏ ନାହିଁ |",
    "merge.files": "ଫଳାଫଳ ପରିବର୍ତ୍ତନ",
    "merge.added": "ଯୋଡା ଯାଇଛି |",
    "merge.modified": "ପରିବର୍ତ୍ତିତ",
    "merge.deleted": "ବିଲୋପ",
    "merge.conflict": "ଦ୍ୱନ୍ଦ୍ୱ |",
    "merge.message": "ମିଶ୍ରଣ ବାର୍ତ୍ତା ମିଶ୍ରଣ କରନ୍ତୁ |",
    "merge.messagePlaceholder": "ଶାଖା 'ଉତ୍ସ' ମିଶ୍ରଣ କରନ୍ତୁ |",
    "merge.messageFfHint": "ଏକ ଦ୍ରୁତ-ଅଗ୍ରଗାମୀ ମିଶ୍ରଣକୁ ଅଣଦେଖା (କ commit ଣସି ପ୍ରତିବଦ୍ଧତା ସୃଷ୍ଟି ହୁଏ ନାହିଁ) |",
    "merge.noFastForward": "ଏକ ମିଶ୍ରଣ ପ୍ରତିବଦ୍ଧତା ସୃଷ୍ଟି କରନ୍ତୁ (କ fast ଣସି ଦ୍ରୁତ-ଅଗ୍ରଗାମୀ ନାହିଁ)",
    "merge.noFastForwardHint": "ଏକ ମିଶ୍ରଣ ପ୍ରତିବଦ୍ଧତାକୁ ସର୍ବଦା ରେକର୍ଡ କର, ଯଦିଓ ଶାଖା ଦ୍ରୁତ ଗତିରେ ଆଗକୁ ଯାଇପାରେ |",
    "merge.merge": "ମିଶ୍ରଣ କରନ୍ତୁ |",
    "merge.cancel": "ବାତିଲ୍ କରନ୍ତୁ |",
    "merge.previewError": "ଏକ ପୂର୍ବାବଲୋକନ ଗଣନା କରିପାରିଲା ନାହିଁ: {message} |",
    "merge.squashBadge": "ସ୍କ୍ୱାସ୍ |",
    "merge.squashNote": "ସ୍କ୍ୱାସ୍ ମିଶ୍ରଣ - “{target}” ଉପରେ ONE ପ୍ରତିବଦ୍ଧତା ପରି ପ୍ରତ୍ୟେକ ପରିବର୍ତ୍ତନ ଭୂମି; “{source}” ର ନିଜସ୍ୱ ପ୍ରତିବଦ୍ଧତା ଏହାର ଇତିହାସରୁ ଦୂରେଇ ରହିବ | ସେଟିଂସମୂହ ›ମିଶ୍ରଣକୁ ଫେରନ୍ତୁ |",
    "merge.messageSquash": "ପ୍ରତିବଦ୍ଧ ବାର୍ତ୍ତା |",
    "merge.squashMessagePlaceholder": "'ଉତ୍ସ' ରୁ ସ୍କ୍ୱାସ୍ ପରିବର୍ତ୍ତନ |",
    "newBranch.title": "ଶାଖା ସୃଷ୍ଟି କରନ୍ତୁ |",
    "newBranch.startPoint": "{sha} ରୁ ନୂତନ ଶାଖା |",
    "newBranch.startPointOn": "{refs} ରେ",
    "newBranch.location": "ଅବସ୍ଥାନ",
    "newBranch.locationRoot": "(ମୂଳ)",
    "newBranch.expandAll": "ସମସ୍ତ ବିସ୍ତାର କରନ୍ତୁ |",
    "newBranch.collapseAll": "ସମସ୍ତ ଭୁଶୁଡ଼ନ୍ତୁ |",
    "newBranch.expandFolder": "ଫୋଲ୍ଡର ବିସ୍ତାର କରନ୍ତୁ |",
    "newBranch.collapseFolder": "ଫୋଲ୍ଡର ଭୁଶୁଡ଼ିବା |",
    "newBranch.name": "ଶାଖା ନାମ",
    "newBranch.namePlaceholder": "ମୋର ଶାଖା",
    "newBranch.fullName": "ସମ୍ପୂର୍ଣ୍ଣ ନାମ: {name} |",
    "newBranch.invalid": "ଅବ val ଧ ଶାଖା ନାମ |",
    "newBranch.exists": "ଏହି ନାମ ସହିତ ଏକ ଶାଖା ପୂର୍ବରୁ ବିଦ୍ୟମାନ ଅଛି |",
    "newBranch.checkout": "ସୃଷ୍ଟି ପରେ ଚେକଆଉଟ୍ ଶାଖା |",
    "newBranch.create": "ସୃଷ୍ଟି କରନ୍ତୁ |",
    "newBranch.cancel": "ବାତିଲ୍ କରନ୍ତୁ |",
    "status.loading": "ଗ୍ରାଫ୍ ଲୋଡ୍ କରୁଛି…",
    "status.summary": "{repo} {commits} ପ୍ରଦର୍ଶନ କରିବା, {refs} ରେଫର୍ |",
    "status.branchCreated": "{sha} ରେ ଶାଖା \"{name}\" ସୃଷ୍ଟି |",
    "status.error": "ତ୍ରୁଟି: {message}",
    "status.opFailed": "ଅପରେସନ୍ ବିଫଳ ହେଲା |",
    "status.fetching": "ଫେଚ୍…",
    "status.pulling": "ଟାଣିବା…",
    "status.pushing": "ଠେଲିବା…",
    "status.committing": "ସ୍ଥାନୀୟ ପ୍ରତିବଦ୍ଧତା ସୃଷ୍ଟି କରିବା…",
    "status.commitCreated": "ସ୍ଥାନୀୟ ପ୍ରତିବଦ୍ଧତା {sha}: {message} |",
    "status.syncing": "ସିଙ୍କ୍…",
    "status.noHead": "ଗ୍ରାଫରେ କ current ଣସି ସାମ୍ପ୍ରତିକ ଚେକଆଉଟ୍ ମିଳିଲା ନାହିଁ |",
    "status.undoing": "ପ୍ରତିବଦ୍ଧତା ପ୍ରତ୍ୟାହାର…",
    "status.commitUndone": "ପୂର୍ବବତ୍ କରନ୍ତୁ - ପରିବର୍ତ୍ତନଗୁଡିକ କାର୍ଯ୍ୟ ବୃକ୍ଷରେ ଫେରିଛି |",
    "status.undoConflict": "ହିଟ୍ ବିବାଦକୁ ପୂର୍ବବତ୍ କରନ୍ତୁ - ଏଡିଟର୍ ରେ ସମାଧାନ କରନ୍ତୁ, ତାପରେ ଜାରି ରଖନ୍ତୁ |",
    "status.stashApplying": "ଷ୍ଟାଶ୍ ପ୍ରୟୋଗ…",
    "status.stashPopping": "ଷ୍ଟାଶ୍ ପପିଙ୍ଗ୍…",
    "status.stashDropping": "ଷ୍ଟାଶ୍ ଡ୍ରପ୍…",
    "status.stashApplied": "ଷ୍ଟାଶ ପ୍ରୟୋଗ ହୋଇଛି |",
    "status.stashPopped": "ଷ୍ଟାଶ୍ ପପ୍ ହୋଇଛି |",
    "status.stashDropped": "ଷ୍ଟାଶ୍ ଖସିଗଲା |",
    "status.stashConflict": "ଷ୍ଟାଶ୍ ଦ୍ୱନ୍ଦ - ଏଡିଟର୍ ରେ ସମାଧାନ କର |",
    "status.merging": "ମିଶ୍ରଣ…",
    "status.merged": "ମିଶ୍ରଣ ସମାପ୍ତ ହୋଇଛି |",
    "status.mergeConflict": "ମିଶ୍ରଣରେ ଦ୍ୱନ୍ଦ୍ୱ ଅଛି - ସେମାନଙ୍କୁ ସମ୍ପାଦକରେ ସମାଧାନ କର, ତାପରେ ପ୍ରତିଶ୍ରୁତି ଦିଅ |",
    "search.title": "ସନ୍ଧାନ କର |",
    "search.tooltip": "ସନ୍ଧାନ କାର୍ଯ୍ୟ",
    "search.placeholder": "ସନ୍ଧାନ କାର୍ଯ୍ୟ…",
    "search.previous": "ପୂର୍ବ ଫଳାଫଳ",
    "search.next": "ପରବର୍ତ୍ତୀ ଫଳାଫଳ",
    "search.noResults": "କ results ଣସି ଫଳାଫଳ ନାହିଁ |",
    "search.resultCount": "{index} / {count} |",
    "search.filter": "ଫିଲ୍ଟର୍ ମୋଡ୍ |",
    "search.highlight": "ମୋଡ୍ ହାଇଲାଇଟ୍ କରନ୍ତୁ |",
    "search.filterMode": "ସନ୍ଧାନ ପ୍ରଦର୍ଶନ",
    "search.filterModeFilter": "ଫିଲ୍ଟର୍ (ଅଣ-ମେଳକ ଲୁଚାନ୍ତୁ)",
    "search.filterModeHighlight": "ହାଇଲାଇଟ୍ କରନ୍ତୁ (ସମସ୍ତ ଦେଖାନ୍ତୁ, ମାର୍କ ମେଳ କରନ୍ତୁ)",
    "search.filterModeHint": "ସନ୍ଧାନ ଫଳାଫଳଗୁଡିକ ଗ୍ରାଫରେ କିପରି ପ୍ରଦର୍ଶିତ ହୁଏ ତାହା ବାଛନ୍ତୁ |",
    "search.visibility": "ଅବସ୍ଥାନ ଖୋଜ |",
    "search.visibilityToolbar": "ଟୁଲ୍ ବାର୍ ବଟନ୍ |",
    "search.visibilityAlwaysVisible": "ସର୍ବଦା ଦୃଶ୍ୟମାନ ଦଣ୍ଡିକା |",
    "search.visibilityHint": "ଶୀର୍ଷରେ ଏକ ଟୁଲ୍ ବାର୍ ଆଇକନ୍ କିମ୍ବା ସ୍ଥାୟୀ ଦଣ୍ଡ ଭାବରେ ସନ୍ଧାନ ଦେଖାନ୍ତୁ |",
    "footer.github": "GitHub",
  },
  ta: {
    "toolbar.refresh": "⟳ புதுப்பிக்கவும்",
    "toolbar.fetch": "⤓ எடுக்கவும்",
    "toolbar.pull": "⇩ இழுக்கவும்",
    "toolbar.push": "⇧ தள்ளு",
    "toolbar.commit": "✓ உறுதி",
    "toolbar.sync": "⇅ ஒத்திசைவு",
    "toolbar.jumpHead": "⌖ செக்அவுட்டுக்குச் செல்லவும்",
    "toolbar.reset": "⤢ காட்சியை மீட்டமைக்கவும்",
    "toolbar.settings": "⚙ அமைப்புகள்",
    "details.header": "பொறுப்பு விவரங்கள்",
    "details.close": "மூடு",
    "details.sha": "SHA",
    "details.shortSha": "குறுகிய SHA",
    "details.message": "செய்தி",
    "details.author": "ஆசிரியர்",
    "details.date": "தேதி",
    "details.labels": "லேபிள்கள்",
    "details.location": "இடம்",
    "details.currentHead": "HEAD - தற்போதைய செக்அவுட்",
    "settings.title": "அமைப்புகள்",
    "settings.language": "மொழி",
    "settings.theme": "தீம்",
    "settings.themeLight": "ஒளி",
    "settings.themeDark": "இருள்",
    "settings.themeLightHint": "ஒளி பின்னணி, இருண்ட உரை.",
    "settings.themeDarkHint": "இருண்ட பின்னணி, ஒளி உரை.",
    "settings.mainBranch": "முக்கிய கிளை",
    "settings.mainBranchAuto": "தானியங்கி",
    "settings.mainBranchSearch": "கிளைகளைத் தேடு…",
    "settings.mainBranchNoMatch": "பொருந்தக்கூடிய கிளைகள் இல்லை",
    "settings.display": "காட்சி நடை",
    "settings.displayModern": "நவீனமானது",
    "settings.displayClassic": "கிளாசிக்",
    "settings.displayModernHint": "இலவச கேன்வாஸ் - பான் செய்ய இழுக்கவும், பெரிதாக்க உருட்டவும்.",
    "settings.displayClassicHint": "நிலையான கேன்வாஸ் - ட்ரங்க் இடப்புறம் பொருத்தப்பட்டது, பெரிதாக்கம் இல்லை, உருட்டல் மட்டும் (SVN திருத்த வரைபடம் போன்றவை).",
    "settings.sectionGeneral": "பொது",
    "settings.sectionGraph": "வரைபடம்",
    "settings.sectionChanges": "பார்வையை மாற்றுகிறது",
    "settings.diffMinimap": "வேறுபாடு மினிமேப்",
    "settings.diffMinimapOn": "காட்டப்பட்டது",
    "settings.diffMinimapOff": "மறைக்கப்பட்டது",
    "settings.diffMinimapOnHint": "வித்தியாசத்திற்கு அருகில் VS கோட்-பாணி மேலோட்டப் பட்டையைக் காட்டு - உருட்ட அதை இழுக்கவும்; மாற்ற குறிப்பான்கள் சேர்க்கப்பட்டுள்ளது.",
    "settings.diffMinimapOffHint": "மேலோட்டப் பட்டை இல்லை - வித்தியாசத்தை சாதாரணமாக உருட்டவும்.",
    "settings.commitReview": "மறுஆய்வு செய்யுங்கள்",
    "settings.commitReviewOn": "உறுதியளிக்கும் முன் கேளுங்கள்",
    "settings.commitReviewOff": "நேரடியாக உறுதியளிக்கவும்",
    "settings.commitReviewOnHint": "லோக்கல் கமிட்டை உருவாக்கும் முன் இறுதி கோப்புப் பட்டியலையும் செய்திச் சரிபார்ப்பையும் காட்டவும்.",
    "settings.commitReviewOffHint": "கமிட் பொத்தான் உள்ளூர் உறுதிமொழியை உடனடியாக உருவாக்குகிறது.",
    "settings.svnBranchDialog": "SVN பாணி கிளை உரையாடல்",
    "settings.svnBranchDialogHint": "கிளையை உருவாக்கும் போது கோப்புறை-மரம் பிக்கரைக் காட்டு (எளிய வரியில் பதிலாக).",
    "settings.branchDialog": "கிளை உரையாடல்",
    "settings.branchDialogSvnTitle": "எஸ்விஎன் பாணி",
    "settings.branchDialogNativeVscode": "VS குறியீடு பாணி",
    "settings.branchDialogNativeVs": "விஷுவல் ஸ்டுடியோ பாணி",
    "settings.branchDialogNativeHint": "உங்கள் IDE இன் உள்ளமைக்கப்பட்ட கிளை ப்ராம்ட்.",
    "settings.close": "மூடு",
    "settings.done": "முடிந்தது",
    "settings.sectionAdvanced": "மேம்பட்டது",
    "settings.gitSource": "ஜிட் இயங்கக்கூடியது",
    "settings.gitSourceBuiltin": "உள்ளமைக்கப்பட்ட",
    "settings.gitSourceBuiltinHint": "IDE இன் உள்ளமைக்கப்பட்ட Git நீட்டிப்பிலிருந்து git பைனரியைப் பயன்படுத்தவும்.",
    "settings.gitSourceCustom": "விருப்ப பாதை",
    "settings.gitPath": "Gitக்கான பாதை",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "உலாவுக…",
    "settings.jargon": "Git விதிமுறைகள்",
    "settings.jargonTranslate": "மொழிபெயர்",
    "settings.jargonEnglish": "ஆங்கிலத்தில் வைத்திருங்கள்",
    "settings.jargonTranslateHint": "Git சொற்களை (புல், புஷ், கமிட், கிளை...) இடைமுக மொழியில் மொழிபெயர்க்கவும்.",
    "settings.jargonEnglishHint": "Git விதிமுறைகளை (புல், புஷ், கமிட், கிளை...) ஆங்கிலத்தில் வைத்திருங்கள்; மற்ற அனைத்தையும் மொழிபெயர்க்கவும்.",
    "settings.sectionMerge": "ஒன்றிணைக்கவும்",
    "settings.mergeMode": "பாணியை ஒன்றிணைக்கவும்",
    "settings.mergeModeMerge": "சாதாரண இணைப்பு",
    "settings.mergeModeSquash": "ஸ்குவாஷ் ஒன்றிணைப்பு",
    "settings.mergeModeMergeHint": "கிளையை அப்படியே ஒருங்கிணைக்கிறது: அதன் கமிட்கள் தற்போதைய கிளையின் வரலாற்றின் ஒரு பகுதியாக மாறும், ஒரு இணைப்பு உறுதியுடன் (அல்லது முடிந்தால் வேகமாக அனுப்பப்படும்).",
    "settings.mergeModeSquashHint": "தற்போதைய கிளையில் (git merge --squash) முழுக் கிளையையும் ஒரே கமிட் ஆகச் சுருக்குகிறது. கிளையின் சொந்த கமிட்கள் தற்போதைய கிளையின் வரலாற்றில் ஒருபோதும் நுழைவதில்லை - அங்கு ஒரு கமிட் மட்டுமே நீங்கள் பார்க்கிறீர்கள். கிளையே தீண்டப்படாமல் விடப்படுகிறது, எனவே அதன் கமிட்கள் அதன் சொந்த பாதையில் தெரியும்.",
    "settings.mergedView": "வரைபடத்தில் இணைக்கப்பட்ட கமிட்கள்",
    "settings.mergedViewBranch": "அவர்களின் சொந்த கிளையில் மட்டுமே",
    "settings.mergedViewTarget": "கிளையிலும் அவை இணைக்கப்பட்டன",
    "settings.mergedViewBranchHint": "இணைக்கப்பட்ட கிளையின் கமிட்கள் அவற்றின் சொந்த பாதையில் மட்டுமே வரையப்படுகின்றன; இணைப்பு இணைப்பாகக் காட்டப்படும். உன்னதமான பார்வை.",
    "settings.mergedViewTargetHint": "ஒரு இணைப்பு கொண்டுவரப்பட்ட கமிட்கள், பெறுதல் கிளையின் பாதையிலும் வரையப்பட்டு, இணைத்தல் உறுதியின் கீழ் அடுக்கப்பட்டு, அசல்களுடன் சமன் செய்யப்படுகின்றன - ஏனெனில் அந்தக் கிளை உண்மையில் இப்போது அவற்றைக் கொண்டுள்ளது. பிரதிகள் வெளிர் மற்றும் அவை எழுதப்பட்ட கிளையுடன் தலையில் உள்ளன, எனவே அவை அங்கு உருவாக்கப்படவில்லை என்பது தெளிவாகிறது. ஸ்குவாஷ் இணைப்புகள் பாதிக்கப்படாது: ஒரு ஸ்குவாஷ் ஒரு சாதாரண உறுதியை எழுதுகிறது, அதனால் அந்த உறுதி மட்டுமே எப்போதும் தோன்றும்.",
    "legend.title": "புராணக்கதை",
    "legend.head": "HEAD / தற்போதைய கிளை",
    "legend.local": "உள்ளூர் கிளை",
    "legend.remote": "தொலைதூர கிளை",
    "legend.remoteOnly": "மேகத்தில் மட்டும் (இழுக்கப்படவில்லை)",
    "legend.tag": "குறிச்சொல் (பதிப்பு)",
    "legend.commit": "உறுதி",
    "legend.stash": "ஸ்டாஷ் (கிடைக்கப்பட்ட வேலை)",
    "legend.mergedIn": "ஒரு இணைப்பில் வந்தது (மற்றொரு கிளையில் எழுதப்பட்டது)",
    "legend.nodes": "பெட்டிகள்",
    "legend.lines": "கோடுகள்",
    "legend.edgeParent": "முந்தைய கமிட் அதே வரிசையில்",
    "legend.edgeMerge": "இணைக்கப்பட்ட கிளை - ஒன்றிணைந்த அம்புக்குறிகள்",
    "legend.edgeBranch": "ஒரு கிளை இங்கே துண்டிக்கப்பட்டது",
    "legend.edgeStash": "ஸ்டாஷ் மற்றும் அது கைவிடப்பட்டது",
    "legend.edgeMergedChain": "இந்த கிளையின் லைன் மெர்ஜ்-இன் கமிட்கள் வழியாக செல்கிறது",
    "legend.edgeMergedTie": "அது எழுதப்பட்ட கிளையிலும் அதே உறுதி",
    "menu.jumpHead": "⌖ செக்அவுட்டுக்குச் செல்லவும்",
    "menu.resetView": "⤢ காட்சியை மீட்டமைக்கவும்",
    "menu.createBranch": "இங்கிருந்து கிளையை உருவாக்கவும்...",
    "menu.checkout": "இந்த உறுதிமொழியை சரிபார்க்கவும்",
    "menu.copySha": "SHA ஐ நகலெடு",
    "menu.pushBranch": "புஷ் கிளை \"{name}\"",
    "menu.renameCommit": "உறுதி செய்தியை மறுபெயரிடுங்கள்…",
    "menu.undoCommit": "உறுதியை செயல்தவிர் (மாற்றங்களை வைத்திருங்கள்)…",
    "menu.viewChanges": "மாற்றங்களைக் காண்க...",
    "menu.jumpToOriginal": "அசல் உறுதிமொழிக்குச் செல்லவும்",
    "changes.title": "{sha} இல் மாற்றங்கள்",
    "changes.tabChanged": "மாற்றப்பட்டது",
    "changes.tabAll": "அனைத்து கோப்புகள்",
    "changes.added": "சேர்க்கப்பட்டது",
    "changes.modified": "மாற்றியமைக்கப்பட்டது",
    "changes.deleted": "நீக்கப்பட்டது",
    "changes.renamed": "மறுபெயரிடப்பட்டது",
    "changes.noChanges": "இந்த பொறுப்பு எந்த கோப்புகளையும் மாற்றாது.",
    "changes.loading": "வித்தியாசத்தை ஏற்றுகிறது…",
    "changes.selectFile": "அதன் மாற்றங்களைக் காண கோப்பைத் தேர்ந்தெடுக்கவும்.",
    "changes.original": "அசல்",
    "changes.changed": "இந்த உறுதி",
    "changes.binary": "பைனரி கோப்பு - காட்ட உரை வேறுபாடு இல்லை.",
    "changes.tooLarge": "கோப்பு மிகவும் பெரியதாக உள்ளது.",
    "changes.renamedFrom": "{path} இலிருந்து மறுபெயரிடப்பட்டது",
    "changes.close": "மூடு",
    "changes.prevChange": "முந்தைய மாற்றம்",
    "changes.nextChange": "அடுத்த மாற்றம்",
    "changes.collapseAll": "எல்லா கோப்புறைகளையும் சுருக்கவும்",
    "changes.expandAll": "அனைத்து கோப்புறைகளையும் விரிவாக்கு",
    "changes.searchPlaceholder": "கோப்புகளைத் தேடு…",
    "changes.noSearchResults": "உங்கள் தேடலுக்குப் பொருந்தக்கூடிய கோப்புகள் எதுவும் இல்லை.",
    "changes.maximize": "அதிகப்படுத்து",
    "changes.restore": "அளவை மீட்டெடுக்கவும்",
    "find.open": "இந்த வேறுபாட்டில் தேடவும் (Ctrl+F)",
    "find.placeholder": "வித்தியாசத்தில் கண்டுபிடி...",
    "find.prev": "முந்தைய போட்டி",
    "find.next": "அடுத்த போட்டி",
    "find.close": "தேடலை மூடு",
    "find.noResults": "முடிவுகள் இல்லை",
    "commit.title": "மாற்றங்களைச் செய்யுங்கள்",
    "commit.close": "மூடு",
    "commit.loading": "வேலை செய்யும் மரத்தைப் படித்தல்…",
    "commit.noChanges": "செய்ய வேண்டிய உள்ளூர் மாற்றங்கள் எதுவும் இல்லை.",
    "commit.selectFile": "அதன் வித்தியாசத்தைக் காண கோப்பைத் தேர்ந்தெடுக்கவும்.",
    "commit.selectAll": "அனைத்தையும் தேர்ந்தெடுக்கவும்",
    "commit.selectNone": "அனைத்தையும் அழிக்கவும்",
    "commit.message": "உறுதி செய்தி",
    "commit.messagePlaceholder": "மாற்றத்தை விவரிக்கவும்",
    "commit.selectedCount": "{count} கோப்பு(கள்) தேர்ந்தெடுக்கப்பட்டன",
    "commit.review": "மதிப்பாய்வு",
    "commit.reviewTitle": "உறுதி செய்வதற்கு முன் இறுதிச் சரிபார்ப்பு",
    "commit.reviewSummary": "செய்தியுடன் {count} கோப்பு(கள்) உறுதி: {message}",
    "commit.back": "மீண்டும்",
    "commit.commit": "உள்ளூர் உறுதிமொழியை உருவாக்கவும்",
    "menu.stashApply": "ஸ்டாஷைப் பயன்படுத்துங்கள்",
    "menu.stashPop": "பாப் ஸ்டாஷ் (விண்ணப்பித்து அகற்று)",
    "menu.stashDrop": "ஸ்டாஷை கைவிடு...",
    "merge.title": "கிளையை இணைக்கவும்",
    "merge.route": "தற்போதைய கிளையில் இணைகிறது",
    "merge.source": "(மூலத்திலிருந்து)",
    "merge.target": "(தற்போதைய)",
    "merge.loading": "இணைப்பதை பகுப்பாய்வு செய்கிறது…",
    "merge.upToDate": "ஏற்கனவே புதுப்பித்த நிலையில் - ஒன்றிணைக்க எதுவும் இல்லை.",
    "merge.fastForward": "ஃபாஸ்ட்-ஃபார்வர்டு - ஒன்றிணைப்பு உறுதி தேவையில்லை (ஒன்றை கட்டாயப்படுத்த \"ஒரு ஒன்றிணைப்பு உறுதியை உருவாக்கு\" என்பதை டிக் செய்யவும்).",
    "merge.summary": "{files} கோப்பு(கள்) மாற்றப்பட்டது, {conflicts} முரண்பாடு(கள்).",
    "merge.conflictsWarning": "இந்த இணைப்பில் முரண்பாடுகள் உள்ளன - நீங்கள் இன்னும் ஒன்றிணைக்கலாம், பின்னர் அவற்றை எடிட்டரில் தீர்க்கலாம்.",
    "merge.noChanges": "கோப்பு மாற்றங்கள் இல்லை.",
    "merge.files": "விளைந்த மாற்றங்கள்",
    "merge.added": "சேர்க்கப்பட்டது",
    "merge.modified": "மாற்றியமைக்கப்பட்டது",
    "merge.deleted": "நீக்கப்பட்டது",
    "merge.conflict": "மோதல்",
    "merge.message": "உறுதி செய்தியை இணைக்கவும்",
    "merge.messagePlaceholder": "கிளை 'மூலத்தை' இணைக்கவும்",
    "merge.messageFfHint": "வேகமாக முன்னேறும் இணைப்பில் புறக்கணிக்கப்பட்டது (எந்த உறுதியும் உருவாக்கப்படவில்லை).",
    "merge.noFastForward": "ஒன்றிணைக்கும் உறுதிமொழியை உருவாக்கவும் (வேகமாக முன்னோக்கி செல்ல வேண்டாம்)",
    "merge.noFastForwardHint": "கிளை வேகமாக முன்னோக்கிச் செல்லும்போது கூட, எப்போதும் ஒன்றிணைந்த உறுதிமொழியைப் பதிவுசெய்யவும்.",
    "merge.merge": "ஒன்றிணைக்கவும்",
    "merge.cancel": "ரத்து செய்",
    "merge.previewError": "முன்னோட்டத்தை கணக்கிட முடியவில்லை: {message}",
    "merge.squashBadge": "ஸ்குவாஷ்",
    "merge.squashNote": "ஸ்குவாஷ் ஒன்றிணைப்பு - ஒவ்வொரு மாற்றமும் \"{target}\" இல் ஒரு உறுதிமொழியாக மாறுகிறது; \"{source}\" இன் சொந்த கடமைகள் அதன் வரலாற்றிலிருந்து விலகி இருக்கும். Settings › Merge என்பதில் மீண்டும் மாறவும்.",
    "merge.messageSquash": "உறுதி செய்தி",
    "merge.squashMessagePlaceholder": "'மூலத்திலிருந்து' மாற்றப்பட்ட மாற்றங்கள்",
    "newBranch.title": "கிளையை உருவாக்கவும்",
    "newBranch.startPoint": "{sha} இலிருந்து புதிய கிளை தொடங்குகிறது",
    "newBranch.startPointOn": "{refs} இல்",
    "newBranch.location": "இடம்",
    "newBranch.locationRoot": "(வேர்)",
    "newBranch.expandAll": "அனைத்தையும் விரிவாக்கு",
    "newBranch.collapseAll": "அனைத்தையும் சுருக்கவும்",
    "newBranch.expandFolder": "கோப்புறையை விரிவாக்கு",
    "newBranch.collapseFolder": "கோப்புறையைச் சுருக்கு",
    "newBranch.name": "கிளை பெயர்",
    "newBranch.namePlaceholder": "என்-கிளை",
    "newBranch.fullName": "முழு பெயர்: {name}",
    "newBranch.invalid": "தவறான கிளை பெயர்",
    "newBranch.exists": "இந்தப் பெயரில் ஒரு கிளை ஏற்கனவே உள்ளது",
    "newBranch.checkout": "உருவாக்கிய பிறகு கிளையைச் சரிபார்க்கவும்",
    "newBranch.create": "உருவாக்கு",
    "newBranch.cancel": "ரத்து செய்",
    "status.loading": "வரைபடத்தை ஏற்றுகிறது…",
    "status.summary": "{repo} {commits} ஐக் காட்டுகிறது, {refs} குறிக்கிறது",
    "status.branchCreated": "{sha} இல் \"{name}\" கிளை உருவாக்கப்பட்டது",
    "status.error": "பிழை: {message}",
    "status.opFailed": "ஆபரேஷன் தோல்வியடைந்தது",
    "status.fetching": "எடுக்கிறது…",
    "status.pulling": "இழுக்கிறது…",
    "status.pushing": "தள்ளுகிறது…",
    "status.committing": "உள்ளூர் பொறுப்பை உருவாக்குகிறது…",
    "status.commitCreated": "உருவாக்கப்பட்டது உள்ளூர் கமிட் {sha}: {message}",
    "status.syncing": "ஒத்திசைக்கிறது...",
    "status.noHead": "வரைபடத்தில் தற்போதைய செக்அவுட் எதுவும் இல்லை.",
    "status.undoing": "உறுதியை ரத்துசெய்கிறது…",
    "status.commitUndone": "உறுதியானது செயல்தவிர்க்கப்பட்டது - வேலை செய்யும் மரத்தில் மாற்றங்கள் மீண்டும் வந்துள்ளன.",
    "status.undoConflict": "ஹிட் மோதல்களை செயல்தவிர் - அவற்றை எடிட்டரில் தீர்த்து, பிறகு தொடரவும்.",
    "status.stashApplying": "ஸ்டாஷைப் பயன்படுத்துகிறது…",
    "status.stashPopping": "பாப்பிங் ஸ்டாஷ்…",
    "status.stashDropping": "ஸ்டாஷை கைவிடுகிறது…",
    "status.stashApplied": "ஸ்டாஷ் பயன்படுத்தப்பட்டது.",
    "status.stashPopped": "ஸ்டாஷ் தோன்றியது.",
    "status.stashDropped": "ஸ்டாஷ் கைவிடப்பட்டது.",
    "status.stashConflict": "ஸ்டாஷ் மோதல்கள் - அவற்றை எடிட்டரில் தீர்க்கவும்.",
    "status.merging": "ஒன்றிணைக்கிறது…",
    "status.merged": "ஒன்றிணைத்தல் முடிந்தது.",
    "status.mergeConflict": "ஒன்றிணைப்பதில் முரண்பாடுகள் உள்ளன - அவற்றை எடிட்டரில் சரிசெய்து, பின்னர் உறுதியளிக்கவும்.",
    "search.title": "தேடு",
    "search.tooltip": "தேடல் உறுதி செய்கிறது",
    "search.placeholder": "தேடல் உறுதி செய்கிறது…",
    "search.previous": "முந்தைய முடிவு",
    "search.next": "அடுத்த முடிவு",
    "search.noResults": "முடிவுகள் இல்லை",
    "search.resultCount": "{index}/{count}",
    "search.filter": "வடிகட்டி முறை",
    "search.highlight": "ஹைலைட் பயன்முறை",
    "search.filterMode": "தேடல் காட்சி",
    "search.filterModeFilter": "வடிப்பான் (பொருந்தாதவற்றை மறை)",
    "search.filterModeHighlight": "முன்னிலைப்படுத்தவும் (அனைத்தையும் காட்டு, பொருத்தங்களைக் குறிக்கவும்)",
    "search.filterModeHint": "வரைபடத்தில் தேடல் முடிவுகள் எவ்வாறு காட்டப்படும் என்பதைத் தேர்வுசெய்யவும்.",
    "search.visibility": "இடம் தேடு",
    "search.visibilityToolbar": "கருவிப்பட்டி பொத்தான்",
    "search.visibilityAlwaysVisible": "எப்போதும் தெரியும் பார்",
    "search.visibilityHint": "தேடலை ஒரு கருவிப்பட்டி ஐகானாக அல்லது மேலே உள்ள நிரந்தர பட்டியாகக் காட்டு.",
    "footer.github": "கிட்ஹப்",
  },
  te: {
    "toolbar.refresh": "⟳ రిఫ్రెష్",
    "toolbar.fetch": "⤓ పొందండి",
    "toolbar.pull": "⇩ లాగండి",
    "toolbar.push": "⇧ పుష్",
    "toolbar.commit": "✓ కట్టుబడి",
    "toolbar.sync": "⇅ సమకాలీకరించు",
    "toolbar.jumpHead": "⌖ చెక్అవుట్‌కి వెళ్లండి",
    "toolbar.reset": "⤢ వీక్షణను రీసెట్ చేయండి",
    "toolbar.settings": "⚙ సెట్టింగ్‌లు",
    "details.header": "కమిట్ వివరాలు",
    "details.close": "మూసివేయి",
    "details.sha": "SHA",
    "details.shortSha": "చిన్న SHA",
    "details.message": "సందేశం",
    "details.author": "రచయిత",
    "details.date": "తేదీ",
    "details.labels": "లేబుల్స్",
    "details.location": "స్థానం",
    "details.currentHead": "HEAD — ప్రస్తుత చెక్అవుట్",
    "settings.title": "సెట్టింగ్‌లు",
    "settings.language": "భాష",
    "settings.theme": "థీమ్",
    "settings.themeLight": "కాంతి",
    "settings.themeDark": "చీకటి",
    "settings.themeLightHint": "తేలికపాటి నేపథ్యం, ​​చీకటి వచనం.",
    "settings.themeDarkHint": "ముదురు నేపథ్యం, ​​తేలికపాటి వచనం.",
    "settings.mainBranch": "ప్రధాన శాఖ",
    "settings.mainBranchAuto": "ఆటోమేటిక్",
    "settings.mainBranchSearch": "శాఖలను శోధించండి...",
    "settings.mainBranchNoMatch": "సరిపోలే శాఖలు లేవు",
    "settings.display": "ప్రదర్శన శైలి",
    "settings.displayModern": "ఆధునిక",
    "settings.displayClassic": "క్లాసిక్",
    "settings.displayModernHint": "ఉచిత కాన్వాస్ — పాన్ చేయడానికి లాగండి, జూమ్ చేయడానికి స్క్రోల్ చేయండి.",
    "settings.displayClassicHint": "స్థిర కాన్వాస్ — ట్రంక్ ఎడమవైపు పిన్ చేయబడింది, జూమ్ లేదు, స్క్రోల్ మాత్రమే (SVN రివిజన్ గ్రాఫ్ లాగా).",
    "settings.sectionGeneral": "జనరల్",
    "settings.sectionGraph": "గ్రాఫ్",
    "settings.sectionChanges": "వీక్షణను మారుస్తుంది",
    "settings.diffMinimap": "తేడా మినిమ్యాప్",
    "settings.diffMinimapOn": "చూపబడింది",
    "settings.diffMinimapOff": "దాచబడింది",
    "settings.diffMinimapOnHint": "తేడా పక్కన VS కోడ్-శైలి స్థూలదృష్టి స్ట్రిప్‌ను చూపండి — దానిని స్క్రోల్ చేయడానికి లాగండి; మార్పు గుర్తులను చేర్చారు.",
    "settings.diffMinimapOffHint": "ఓవర్‌వ్యూ స్ట్రిప్ లేదు - తేడాను సాధారణంగా స్క్రోల్ చేయండి.",
    "settings.commitReview": "కమిట్ రివ్యూ",
    "settings.commitReviewOn": "కట్టుబడి ముందు అడగండి",
    "settings.commitReviewOff": "నేరుగా కట్టుబడి",
    "settings.commitReviewOnHint": "స్థానిక నిబద్ధతను సృష్టించే ముందు తుది ఫైల్ జాబితా మరియు సందేశ తనిఖీని చూపండి.",
    "settings.commitReviewOffHint": "కమిట్ బటన్ వెంటనే లోకల్ కమిట్‌ను సృష్టిస్తుంది.",
    "settings.svnBranchDialog": "SVN-శైలి బ్రాంచ్ డైలాగ్",
    "settings.svnBranchDialogHint": "శాఖను సృష్టించేటప్పుడు ఫోల్డర్-ట్రీ పికర్‌ను చూపండి (సాధారణ ప్రాంప్ట్‌కు బదులుగా).",
    "settings.branchDialog": "బ్రాంచ్ డైలాగ్",
    "settings.branchDialogSvnTitle": "SVN-శైలి",
    "settings.branchDialogNativeVscode": "VS కోడ్ శైలి",
    "settings.branchDialogNativeVs": "విజువల్ స్టూడియో శైలి",
    "settings.branchDialogNativeHint": "మీ IDE యొక్క అంతర్నిర్మిత శాఖ ప్రాంప్ట్.",
    "settings.close": "మూసివేయి",
    "settings.done": "పూర్తయింది",
    "settings.sectionAdvanced": "అధునాతనమైనది",
    "settings.gitSource": "Git ఎక్జిక్యూటబుల్",
    "settings.gitSourceBuiltin": "అంతర్నిర్మిత",
    "settings.gitSourceBuiltinHint": "IDE యొక్క అంతర్నిర్మిత Git పొడిగింపు నుండి git బైనరీని ఉపయోగించండి.",
    "settings.gitSourceCustom": "అనుకూల మార్గం",
    "settings.gitPath": "Git కు మార్గం",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "బ్రౌజ్ చేయండి...",
    "settings.jargon": "Git నిబంధనలు",
    "settings.jargonTranslate": "అనువదించు",
    "settings.jargonEnglish": "ఆంగ్లంలో ఉంచండి",
    "settings.jargonTranslateHint": "Git నిబంధనలను (పుల్, పుష్, కమిట్, బ్రాంచ్...) ఇంటర్‌ఫేస్ భాషలోకి అనువదించండి.",
    "settings.jargonEnglishHint": "ఆంగ్లంలో Git నిబంధనలను (పుల్, పుష్, కమిట్, బ్రాంచ్...) ఉంచండి; మిగతావన్నీ అనువదించండి.",
    "settings.sectionMerge": "విలీనం చేయండి",
    "settings.mergeMode": "శైలిని విలీనం చేయండి",
    "settings.mergeModeMerge": "సాధారణ విలీనం",
    "settings.mergeModeSquash": "స్క్వాష్ విలీనం",
    "settings.mergeModeMergeHint": "బ్రాంచ్‌ను అలాగే విలీనం చేస్తుంది: దాని కమిట్‌లు ప్రస్తుత బ్రాంచ్ చరిత్రలో భాగమవుతాయి, విలీన కమిట్‌తో (లేదా సాధ్యమైనప్పుడు ఫాస్ట్ ఫార్వార్డ్ చేయబడుతుంది).",
    "settings.mergeModeSquashHint": "ప్రస్తుత శాఖలో (git merge --squash) మొత్తం శాఖను ఒకే కమిట్‌గా కుదిస్తుంది. బ్రాంచ్ యొక్క స్వంత కమిట్‌లు ప్రస్తుత బ్రాంచ్ చరిత్రలో ఎప్పుడూ ప్రవేశించవు - మీరు అక్కడ ఒక కమిట్‌ని మాత్రమే చూస్తారు. శాఖ కూడా తాకబడదు, కాబట్టి దాని కమిట్‌లు దాని స్వంత లేన్‌లో కనిపిస్తాయి.",
    "settings.mergedView": "గ్రాఫ్‌లో కమిట్‌లు విలీనం చేయబడ్డాయి",
    "settings.mergedViewBranch": "వారి సొంత శాఖలో మాత్రమే",
    "settings.mergedViewTarget": "శాఖలో కూడా వాటిని విలీనం చేశారు",
    "settings.mergedViewBranchHint": "విలీనమైన శాఖ యొక్క కమిట్‌లు వారి స్వంత లేన్‌లో మాత్రమే డ్రా చేయబడతాయి; విలీనం కనెక్టర్‌గా చూపబడుతుంది. క్లాసిక్ వీక్షణ.",
    "settings.mergedViewTargetHint": "తీసుకురాబడిన కమిట్‌లు స్వీకరించే శాఖ యొక్క లేన్‌లో కూడా డ్రా చేయబడతాయి, విలీన కమిట్ కింద పేర్చబడి, అసలైన వాటితో స్థాయిని కలిగి ఉంటాయి - ఎందుకంటే ఆ శాఖ నిజంగా ఇప్పుడు వాటిని కలిగి ఉంది. కాపీలు లేతగా ఉన్నాయి మరియు అవి వ్రాసిన శాఖతో తలపై ఉన్నాయి, కాబట్టి అవి అక్కడ తయారు చేయబడలేదని స్పష్టంగా తెలుస్తుంది. స్క్వాష్ విలీనాలు ప్రభావితం కావు: ఒక స్క్వాష్ ఒక సాధారణ కమిట్‌ను వ్రాస్తాడు, కనుక ఆ కమిట్ మాత్రమే ఎప్పుడూ కనిపిస్తుంది.",
    "legend.title": "లెజెండ్",
    "legend.head": "హెడ్ ​​/ ప్రస్తుత శాఖ",
    "legend.local": "స్థానిక శాఖ",
    "legend.remote": "రిమోట్ శాఖ",
    "legend.remoteOnly": "క్లౌడ్‌లో మాత్రమే (లాగబడలేదు)",
    "legend.tag": "ట్యాగ్ (వెర్షన్)",
    "legend.commit": "కట్టుబడి",
    "legend.stash": "స్టాష్ (నిలిపివేయబడిన పని)",
    "legend.mergedIn": "విలీనంతో వచ్చింది (మరొక శాఖపై వ్రాయబడింది)",
    "legend.nodes": "పెట్టెలు",
    "legend.lines": "లైన్లు",
    "legend.edgeParent": "గతంలో ఇదే లైన్‌లో కమిట్",
    "legend.edgeMerge": "విలీనమైన బ్రాంచ్ - విలీనానికి సంబంధించిన బాణం పాయింట్లు",
    "legend.edgeBranch": "ఇక్కడ ఒక శాఖ చీలిపోయింది",
    "legend.edgeStash": "స్టాష్ మరియు నిబద్ధత అది నిలిపివేయబడింది",
    "legend.edgeMergedChain": "ఈ బ్రాంచ్ లైన్ మెర్జ్-ఇన్ ద్వారా నడుస్తుంది",
    "legend.edgeMergedTie": "అది వ్రాసిన శాఖపై అదే కమిట్",
    "menu.jumpHead": "⌖ చెక్అవుట్‌కి వెళ్లండి",
    "menu.resetView": "⤢ వీక్షణను రీసెట్ చేయండి",
    "menu.createBranch": "ఇక్కడి నుండి శాఖను సృష్టించండి...",
    "menu.checkout": "ఈ నిబద్ధతను తనిఖీ చేయండి",
    "menu.copySha": "SHAని కాపీ చేయండి",
    "menu.pushBranch": "పుష్ బ్రాంచ్ \"{name}\"",
    "menu.renameCommit": "కమిట్ మెసేజ్ పేరు మార్చండి...",
    "menu.undoCommit": "నిబద్ధతను రద్దు చేయండి (మార్పులను కొనసాగించండి)…",
    "menu.viewChanges": "మార్పులను వీక్షించండి...",
    "menu.jumpToOriginal": "అసలు కమిట్‌కి వెళ్లండి",
    "changes.title": "{sha}లో మార్పులు",
    "changes.tabChanged": "మార్చబడింది",
    "changes.tabAll": "అన్ని ఫైల్‌లు",
    "changes.added": "చేర్చబడింది",
    "changes.modified": "సవరించబడింది",
    "changes.deleted": "తొలగించబడింది",
    "changes.renamed": "పేరు మార్చారు",
    "changes.noChanges": "ఈ కమిట్ ఏ ఫైల్‌లను మార్చదు.",
    "changes.loading": "తేడా లోడ్ అవుతోంది…",
    "changes.selectFile": "దాని మార్పులను చూడటానికి ఫైల్‌ను ఎంచుకోండి.",
    "changes.original": "అసలైనది",
    "changes.changed": "ఈ నిబద్ధత",
    "changes.binary": "బైనరీ ఫైల్ — చూపించడానికి టెక్స్ట్ తేడా లేదు.",
    "changes.tooLarge": "ఫైల్ చాలా పెద్దదిగా ఉంది.",
    "changes.renamedFrom": "{path} నుండి పేరు మార్చబడింది",
    "changes.close": "మూసివేయి",
    "changes.prevChange": "మునుపటి మార్పు",
    "changes.nextChange": "తదుపరి మార్పు",
    "changes.collapseAll": "అన్ని ఫోల్డర్‌లను కుదించు",
    "changes.expandAll": "అన్ని ఫోల్డర్‌లను విస్తరించండి",
    "changes.searchPlaceholder": "ఫైళ్లను వెతకండి...",
    "changes.noSearchResults": "మీ శోధనకు సరిపోలే ఫైల్‌లు ఏవీ లేవు.",
    "changes.maximize": "గరిష్టీకరించు",
    "changes.restore": "పరిమాణాన్ని పునరుద్ధరించండి",
    "find.open": "ఈ తేడాలో శోధించండి (Ctrl+F)",
    "find.placeholder": "తేడాలో కనుగొనండి…",
    "find.prev": "మునుపటి మ్యాచ్",
    "find.next": "తదుపరి మ్యాచ్",
    "find.close": "శోధనను మూసివేయండి",
    "find.noResults": "ఫలితాలు లేవు",
    "commit.title": "మార్పులకు కట్టుబడి ఉండండి",
    "commit.close": "మూసివేయి",
    "commit.loading": "పని చెట్టును చదవడం…",
    "commit.noChanges": "కట్టుబడి ఉండటానికి స్థానిక మార్పులు లేవు.",
    "commit.selectFile": "దాని తేడాను చూడటానికి ఫైల్‌ను ఎంచుకోండి.",
    "commit.selectAll": "అన్నింటినీ ఎంచుకోండి",
    "commit.selectNone": "అన్నీ క్లియర్ చేయండి",
    "commit.message": "కట్టుబడి సందేశం",
    "commit.messagePlaceholder": "మార్పును వివరించండి",
    "commit.selectedCount": "{count} ఫైల్(లు) ఎంచుకోబడ్డాయి",
    "commit.review": "సమీక్షించండి",
    "commit.reviewTitle": "కమిట్ అయ్యే ముందు తుది తనిఖీ",
    "commit.reviewSummary": "సందేశంతో {count} ఫైల్(లు)కి కట్టుబడి ఉండండి: {message}",
    "commit.back": "వెనుకకు",
    "commit.commit": "స్థానిక నిబద్ధతను సృష్టించండి",
    "menu.stashApply": "స్టాష్ వర్తించు",
    "menu.stashPop": "పాప్ స్టాష్ (వర్తించు & తీసివేయి)",
    "menu.stashDrop": "డ్రాప్ స్టాష్…",
    "merge.title": "శాఖను విలీనం చేయండి",
    "merge.route": "ప్రస్తుత శాఖలో విలీనం",
    "merge.source": "(మూలం) నుండి",
    "merge.target": "(ప్రస్తుతం) లోకి",
    "merge.loading": "విలీనాన్ని విశ్లేషిస్తోంది...",
    "merge.upToDate": "ఇప్పటికే తాజాగా ఉంది — విలీనం చేయడానికి ఏమీ లేదు.",
    "merge.fastForward": "ఫాస్ట్ ఫార్వార్డ్ — విలీన నిబద్ధత అవసరం లేదు (ఒకదానిని బలవంతం చేయడానికి \"ఒక విలీన నిబద్ధతను సృష్టించు\" టిక్ చేయండి).",
    "merge.summary": "{files} ఫైల్(లు) మార్చబడ్డాయి, {conflicts} వైరుధ్యం(లు).",
    "merge.conflictsWarning": "ఈ విలీనం వైరుధ్యాలను కలిగి ఉంది - మీరు ఇప్పటికీ విలీనం చేయవచ్చు, ఆపై వాటిని ఎడిటర్‌లో పరిష్కరించవచ్చు.",
    "merge.noChanges": "ఫైల్ మార్పులు లేవు.",
    "merge.files": "ఫలితంగా మార్పులు",
    "merge.added": "చేర్చబడింది",
    "merge.modified": "సవరించబడింది",
    "merge.deleted": "తొలగించబడింది",
    "merge.conflict": "సంఘర్షణ",
    "merge.message": "నిబద్ధత సందేశాన్ని విలీనం చేయండి",
    "merge.messagePlaceholder": "శాఖ 'మూలం' విలీనం",
    "merge.messageFfHint": "ఫాస్ట్-ఫార్వర్డ్ విలీనంపై విస్మరించబడింది (ఏ నిబద్ధత సృష్టించబడలేదు).",
    "merge.noFastForward": "విలీన నిబద్ధతను సృష్టించండి (ఫాస్ట్ ఫార్వార్డ్ లేదు)",
    "merge.noFastForwardHint": "బ్రాంచ్ ఫాస్ట్ ఫార్వార్డ్ చేయగలిగినప్పటికీ, విలీన నిబద్ధతను ఎల్లప్పుడూ రికార్డ్ చేయండి.",
    "merge.merge": "విలీనం చేయండి",
    "merge.cancel": "రద్దు చేయి",
    "merge.previewError": "పరిదృశ్యాన్ని గణించడం సాధ్యపడలేదు: {message}",
    "merge.squashBadge": "స్క్వాష్",
    "merge.squashNote": "స్క్వాష్ విలీనం — ప్రతి మార్పు “{target}”లో ఒక వ్యక్తి కట్టుబడి ఉంటుంది; \"{source}\" యొక్క స్వంత కమిట్‌లు దాని చరిత్ర నుండి దూరంగా ఉంటాయి. సెట్టింగ్‌లు › విలీనంలో తిరిగి మారండి.",
    "merge.messageSquash": "కట్టుబడి సందేశం",
    "merge.squashMessagePlaceholder": "'మూలం' నుండి స్క్వాడ్ మార్పులు",
    "newBranch.title": "శాఖను సృష్టించండి",
    "newBranch.startPoint": "{sha} నుండి కొత్త శాఖ ప్రారంభమవుతుంది",
    "newBranch.startPointOn": "{refs}లో",
    "newBranch.location": "స్థానం",
    "newBranch.locationRoot": "(రూట్)",
    "newBranch.expandAll": "అన్నింటినీ విస్తరించండి",
    "newBranch.collapseAll": "అన్నింటినీ కుదించు",
    "newBranch.expandFolder": "ఫోల్డర్‌ని విస్తరించండి",
    "newBranch.collapseFolder": "ఫోల్డర్‌ను కుదించు",
    "newBranch.name": "శాఖ పేరు",
    "newBranch.namePlaceholder": "నా శాఖ",
    "newBranch.fullName": "పూర్తి పేరు: {name}",
    "newBranch.invalid": "చెల్లని శాఖ పేరు",
    "newBranch.exists": "ఈ పేరుతో ఒక శాఖ ఇప్పటికే ఉంది",
    "newBranch.checkout": "సృష్టి తర్వాత శాఖను చెక్అవుట్ చేయండి",
    "newBranch.create": "సృష్టించు",
    "newBranch.cancel": "రద్దు చేయి",
    "status.loading": "గ్రాఫ్ లోడ్ అవుతోంది…",
    "status.summary": "{repo} {commits} కమిట్‌లను చూపుతోంది, {refs} సూచిస్తుంది",
    "status.branchCreated": "{sha} వద్ద \"{name}\" శాఖను సృష్టించారు",
    "status.error": "లోపం: {message}",
    "status.opFailed": "ఆపరేషన్ విఫలమైంది",
    "status.fetching": "పొందుతోంది...",
    "status.pulling": "లాగుతోంది...",
    "status.pushing": "నెట్టడం...",
    "status.committing": "స్థానిక నిబద్ధతను సృష్టిస్తోంది...",
    "status.commitCreated": "సృష్టించబడిన స్థానిక కమిట్ {sha}: {message}",
    "status.syncing": "సమకాలీకరిస్తోంది...",
    "status.noHead": "గ్రాఫ్‌లో ప్రస్తుత చెక్అవుట్ ఏదీ కనుగొనబడలేదు.",
    "status.undoing": "నిబద్ధతను రద్దు చేస్తోంది…",
    "status.commitUndone": "నిబద్ధత రద్దు చేయబడింది - పని చేసే చెట్టులో మార్పులు తిరిగి వచ్చాయి.",
    "status.undoConflict": "హిట్ వైరుధ్యాలను అన్డు చేయండి — వాటిని ఎడిటర్‌లో పరిష్కరించండి, ఆపై కొనసాగించండి.",
    "status.stashApplying": "స్టాష్‌ని వర్తింపజేస్తోంది…",
    "status.stashPopping": "పాపింగ్ స్టాష్…",
    "status.stashDropping": "నిల్వను వదిలివేస్తోంది…",
    "status.stashApplied": "స్టాష్ వర్తింపజేయబడింది.",
    "status.stashPopped": "స్టాష్ పాప్ చేయబడింది.",
    "status.stashDropped": "స్టాష్ పడిపోయింది.",
    "status.stashConflict": "స్టాష్ వైరుధ్యాలు — వాటిని ఎడిటర్‌లో పరిష్కరించండి.",
    "status.merging": "విలీనం చేస్తోంది...",
    "status.merged": "విలీనం పూర్తయింది.",
    "status.mergeConflict": "విలీనం వైరుధ్యాలను కలిగి ఉంది - వాటిని ఎడిటర్‌లో పరిష్కరించండి, ఆపై కట్టుబడి ఉండండి.",
    "search.title": "శోధించండి",
    "search.tooltip": "శోధన కట్టుబడి ఉంటుంది",
    "search.placeholder": "శోధన కట్టుబడి ఉంది…",
    "search.previous": "మునుపటి ఫలితం",
    "search.next": "తదుపరి ఫలితం",
    "search.noResults": "ఫలితాలు లేవు",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ఫిల్టర్ మోడ్",
    "search.highlight": "హైలైట్ మోడ్",
    "search.filterMode": "శోధన ప్రదర్శన",
    "search.filterModeFilter": "ఫిల్టర్ (నాన్-మ్యాచ్‌లను దాచండి)",
    "search.filterModeHighlight": "హైలైట్ చేయండి (అన్నీ చూపించు, సరిపోలికలను గుర్తించండి)",
    "search.filterModeHint": "గ్రాఫ్‌లో శోధన ఫలితాలు ఎలా ప్రదర్శించబడతాయో ఎంచుకోండి.",
    "search.visibility": "స్థానాన్ని శోధించండి",
    "search.visibilityToolbar": "టూల్ బార్ బటన్",
    "search.visibilityAlwaysVisible": "ఎల్లప్పుడూ కనిపించే బార్",
    "search.visibilityHint": "శోధనను టూల్‌బార్ చిహ్నంగా లేదా ఎగువన నిరంతర బార్‌గా చూపండి.",
    "footer.github": "GitHub",
  },
  kn: {
    "toolbar.refresh": "⟳ ರಿಫ್ರೆಶ್ ಮಾಡಿ",
    "toolbar.fetch": "⤓ ಪಡೆದುಕೊಳ್ಳಿ",
    "toolbar.pull": "⇩ ಎಳೆಯಿರಿ",
    "toolbar.push": "⇧ ತಳ್ಳು",
    "toolbar.commit": "✓ ಬದ್ಧತೆ",
    "toolbar.sync": "⇅ ಸಿಂಕ್",
    "toolbar.jumpHead": "⌖ ಚೆಕ್ಔಟ್ಗೆ ಹೋಗಿ",
    "toolbar.reset": "⤢ ವೀಕ್ಷಣೆಯನ್ನು ಮರುಹೊಂದಿಸಿ",
    "toolbar.settings": "⚙ ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
    "details.header": "ಕಮಿಟ್ ವಿವರಗಳು",
    "details.close": "ಮುಚ್ಚಿ",
    "details.sha": "SHA",
    "details.shortSha": "ಚಿಕ್ಕ SHA",
    "details.message": "ಸಂದೇಶ",
    "details.author": "ಲೇಖಕ",
    "details.date": "ದಿನಾಂಕ",
    "details.labels": "ಲೇಬಲ್‌ಗಳು",
    "details.location": "ಸ್ಥಳ",
    "details.currentHead": "HEAD - ಪ್ರಸ್ತುತ ಚೆಕ್ಔಟ್",
    "settings.title": "ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
    "settings.language": "ಭಾಷೆ",
    "settings.theme": "ಥೀಮ್",
    "settings.themeLight": "ಬೆಳಕು",
    "settings.themeDark": "ಕತ್ತಲು",
    "settings.themeLightHint": "ಬೆಳಕಿನ ಹಿನ್ನೆಲೆ, ಡಾರ್ಕ್ ಪಠ್ಯ.",
    "settings.themeDarkHint": "ಡಾರ್ಕ್ ಹಿನ್ನೆಲೆ, ಬೆಳಕಿನ ಪಠ್ಯ.",
    "settings.mainBranch": "ಮುಖ್ಯ ಶಾಖೆ",
    "settings.mainBranchAuto": "ಸ್ವಯಂಚಾಲಿತ",
    "settings.mainBranchSearch": "ಶಾಖೆಗಳನ್ನು ಹುಡುಕಿ...",
    "settings.mainBranchNoMatch": "ಯಾವುದೇ ಹೊಂದಾಣಿಕೆಯ ಶಾಖೆಗಳಿಲ್ಲ",
    "settings.display": "ಪ್ರದರ್ಶನ ಶೈಲಿ",
    "settings.displayModern": "ಆಧುನಿಕ",
    "settings.displayClassic": "ಕ್ಲಾಸಿಕ್",
    "settings.displayModernHint": "ಉಚಿತ ಕ್ಯಾನ್ವಾಸ್ - ಪ್ಯಾನ್ ಮಾಡಲು ಎಳೆಯಿರಿ, ಜೂಮ್ ಮಾಡಲು ಸ್ಕ್ರಾಲ್ ಮಾಡಿ.",
    "settings.displayClassicHint": "ಸ್ಥಿರ ಕ್ಯಾನ್ವಾಸ್ - ಎಡಕ್ಕೆ ಟ್ರಂಕ್ ಪಿನ್ ಮಾಡಲಾಗಿದೆ, ಜೂಮ್ ಇಲ್ಲ, ಸ್ಕ್ರಾಲ್ ಮಾತ್ರ (SVN ಪರಿಷ್ಕರಣೆ ಗ್ರಾಫ್‌ನಂತೆ).",
    "settings.sectionGeneral": "ಸಾಮಾನ್ಯ",
    "settings.sectionGraph": "ಗ್ರಾಫ್",
    "settings.sectionChanges": "ವೀಕ್ಷಣೆಯನ್ನು ಬದಲಾಯಿಸುತ್ತದೆ",
    "settings.diffMinimap": "ವ್ಯತ್ಯಾಸ ಮಿನಿಮ್ಯಾಪ್",
    "settings.diffMinimapOn": "ತೋರಿಸಲಾಗಿದೆ",
    "settings.diffMinimapOff": "ಮರೆಮಾಡಲಾಗಿದೆ",
    "settings.diffMinimapOnHint": "ವ್ಯತ್ಯಾಸದ ಪಕ್ಕದಲ್ಲಿ VS ಕೋಡ್-ಶೈಲಿಯ ಅವಲೋಕನ ಪಟ್ಟಿಯನ್ನು ತೋರಿಸಿ - ಸ್ಕ್ರಾಲ್ ಮಾಡಲು ಅದನ್ನು ಎಳೆಯಿರಿ; ಬದಲಾವಣೆ ಮಾರ್ಕರ್‌ಗಳನ್ನು ಒಳಗೊಂಡಿದೆ.",
    "settings.diffMinimapOffHint": "ಅವಲೋಕನ ಪಟ್ಟಿ ಇಲ್ಲ - ವ್ಯತ್ಯಾಸವನ್ನು ಸಾಮಾನ್ಯವಾಗಿ ಸ್ಕ್ರಾಲ್ ಮಾಡಿ.",
    "settings.commitReview": "ಬದ್ಧ ವಿಮರ್ಶೆ",
    "settings.commitReviewOn": "ಒಪ್ಪಿಸುವ ಮೊದಲು ಕೇಳಿ",
    "settings.commitReviewOff": "ನೇರವಾಗಿ ಒಪ್ಪಿಸಿ",
    "settings.commitReviewOnHint": "ಸ್ಥಳೀಯ ಬದ್ಧತೆಯನ್ನು ರಚಿಸುವ ಮೊದಲು ಅಂತಿಮ ಫೈಲ್ ಪಟ್ಟಿ ಮತ್ತು ಸಂದೇಶ ಪರಿಶೀಲನೆಯನ್ನು ತೋರಿಸಿ.",
    "settings.commitReviewOffHint": "ಕಮಿಟ್ ಬಟನ್ ಸ್ಥಳೀಯ ಬದ್ಧತೆಯನ್ನು ತಕ್ಷಣವೇ ರಚಿಸುತ್ತದೆ.",
    "settings.svnBranchDialog": "SVN-ಶೈಲಿಯ ಶಾಖೆಯ ಸಂವಾದ",
    "settings.svnBranchDialogHint": "ಶಾಖೆಯನ್ನು ರಚಿಸುವಾಗ ಫೋಲ್ಡರ್-ಟ್ರೀ ಪಿಕ್ಕರ್ ಅನ್ನು ತೋರಿಸಿ (ಸರಳ ಪ್ರಾಂಪ್ಟ್ ಬದಲಿಗೆ).",
    "settings.branchDialog": "ಶಾಖೆಯ ಸಂವಾದ",
    "settings.branchDialogSvnTitle": "SVN-ಶೈಲಿ",
    "settings.branchDialogNativeVscode": "VS ಕೋಡ್ ಶೈಲಿ",
    "settings.branchDialogNativeVs": "ವಿಷುಯಲ್ ಸ್ಟುಡಿಯೋ ಶೈಲಿ",
    "settings.branchDialogNativeHint": "ನಿಮ್ಮ IDE ನ ಅಂತರ್ನಿರ್ಮಿತ ಶಾಖೆಯ ಪ್ರಾಂಪ್ಟ್.",
    "settings.close": "ಮುಚ್ಚಿ",
    "settings.done": "ಮುಗಿದಿದೆ",
    "settings.sectionAdvanced": "ಸುಧಾರಿತ",
    "settings.gitSource": "Git ಕಾರ್ಯಗತಗೊಳಿಸಬಹುದಾದ",
    "settings.gitSourceBuiltin": "ಅಂತರ್ನಿರ್ಮಿತ",
    "settings.gitSourceBuiltinHint": "IDE ನ ಅಂತರ್ನಿರ್ಮಿತ Git ವಿಸ್ತರಣೆಯಿಂದ git ಬೈನರಿ ಬಳಸಿ.",
    "settings.gitSourceCustom": "ಕಸ್ಟಮ್ ಮಾರ್ಗ",
    "settings.gitPath": "Git ಗೆ ಮಾರ್ಗ",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ಬ್ರೌಸ್ ಮಾಡಿ...",
    "settings.jargon": "Git ನಿಯಮಗಳು",
    "settings.jargonTranslate": "ಅನುವಾದಿಸಿ",
    "settings.jargonEnglish": "ಇಂಗ್ಲೀಷ್ ನಲ್ಲಿ ಇರಿಸಿಕೊಳ್ಳಿ",
    "settings.jargonTranslateHint": "Git ಪದಗಳನ್ನು (ಪುಲ್, ಪುಶ್, ಕಮಿಟ್, ಶಾಖೆ...) ಇಂಟರ್ಫೇಸ್ ಭಾಷೆಗೆ ಅನುವಾದಿಸಿ.",
    "settings.jargonEnglishHint": "ಇಂಗ್ಲಿಷ್‌ನಲ್ಲಿ Git ಪದಗಳನ್ನು (ಪುಲ್, ಪುಶ್, ಕಮಿಟ್, ಬ್ರಾಂಚ್...) ಇರಿಸಿಕೊಳ್ಳಿ; ಉಳಿದೆಲ್ಲವನ್ನೂ ಅನುವಾದಿಸಿ.",
    "settings.sectionMerge": "ವಿಲೀನಗೊಳಿಸಿ",
    "settings.mergeMode": "ಶೈಲಿಯನ್ನು ವಿಲೀನಗೊಳಿಸಿ",
    "settings.mergeModeMerge": "ಸಾಮಾನ್ಯ ವಿಲೀನ",
    "settings.mergeModeSquash": "ಸ್ಕ್ವ್ಯಾಷ್ ವಿಲೀನ",
    "settings.mergeModeMergeHint": "ಶಾಖೆಯನ್ನು ಹಾಗೆಯೇ ವಿಲೀನಗೊಳಿಸುತ್ತದೆ: ಅದರ ಕಮಿಟ್‌ಗಳು ಪ್ರಸ್ತುತ ಶಾಖೆಯ ಇತಿಹಾಸದ ಭಾಗವಾಗುತ್ತವೆ, ವಿಲೀನ ಬದ್ಧತೆಯಿಂದ ಸೇರಿಕೊಳ್ಳುತ್ತವೆ (ಅಥವಾ ಸಾಧ್ಯವಾದಾಗ ವೇಗವಾಗಿ ಫಾರ್ವರ್ಡ್ ಮಾಡಲಾಗುತ್ತದೆ).",
    "settings.mergeModeSquashHint": "ಪ್ರಸ್ತುತ ಶಾಖೆಯಲ್ಲಿ ಸಂಪೂರ್ಣ ಶಾಖೆಯನ್ನು ಒಂದೇ ಕಮಿಟ್ ಆಗಿ ಕುಗ್ಗಿಸುತ್ತದೆ (git merge --squash). ಶಾಖೆಯ ಸ್ವಂತ ಕಮಿಟ್‌ಗಳು ಪ್ರಸ್ತುತ ಶಾಖೆಯ ಇತಿಹಾಸವನ್ನು ಎಂದಿಗೂ ನಮೂದಿಸುವುದಿಲ್ಲ - ನೀವು ಅಲ್ಲಿ ಒಂದು ಕಮಿಟ್ ಅನ್ನು ಮಾತ್ರ ನೋಡುತ್ತೀರಿ. ಶಾಖೆಯು ಅಸ್ಪೃಶ್ಯವಾಗಿ ಉಳಿದಿದೆ, ಆದ್ದರಿಂದ ಅದರ ಬದ್ಧತೆಗಳು ತನ್ನದೇ ಆದ ಲೇನ್‌ನಲ್ಲಿ ಗೋಚರಿಸುತ್ತವೆ.",
    "settings.mergedView": "ಗ್ರಾಫ್‌ನಲ್ಲಿ ವಿಲೀನಗೊಂಡ ಕಮಿಟ್‌ಗಳು",
    "settings.mergedViewBranch": "ಅವರ ಸ್ವಂತ ಶಾಖೆಯಲ್ಲಿ ಮಾತ್ರ",
    "settings.mergedViewTarget": "ಅಲ್ಲದೆ ಶಾಖೆಯಲ್ಲಿ ಅವರು ವಿಲೀನಗೊಂಡರು",
    "settings.mergedViewBranchHint": "ವಿಲೀನಗೊಂಡ ಶಾಖೆಯ ಕಮಿಟ್‌ಗಳನ್ನು ತಮ್ಮದೇ ಲೇನ್‌ನಲ್ಲಿ ಮಾತ್ರ ಎಳೆಯಲಾಗುತ್ತದೆ; ವಿಲೀನವು ಕನೆಕ್ಟರ್ ಆಗಿ ತೋರಿಸುತ್ತದೆ. ಕ್ಲಾಸಿಕ್ ನೋಟ.",
    "settings.mergedViewTargetHint": "ವಿಲೀನವನ್ನು ತರಲಾದ ಕಮಿಟ್‌ಗಳನ್ನು ಸ್ವೀಕರಿಸುವ ಶಾಖೆಯ ಲೇನ್‌ನಲ್ಲಿಯೂ ಎಳೆಯಲಾಗುತ್ತದೆ, ವಿಲೀನ ಬದ್ಧತೆಯ ಅಡಿಯಲ್ಲಿ ಜೋಡಿಸಲಾಗುತ್ತದೆ ಮತ್ತು ಮೂಲದೊಂದಿಗೆ ಮಟ್ಟ ಹಾಕಲಾಗುತ್ತದೆ - ಏಕೆಂದರೆ ಆ ಶಾಖೆಯು ಈಗ ಅವುಗಳನ್ನು ಒಳಗೊಂಡಿದೆ. ಪ್ರತಿಗಳು ಮಸುಕಾದವು ಮತ್ತು ಅವುಗಳನ್ನು ಬರೆಯಲಾದ ಶಾಖೆಯೊಂದಿಗೆ ಶಿರೋನಾಮೆ ಮಾಡುತ್ತವೆ, ಆದ್ದರಿಂದ ಅವುಗಳನ್ನು ಅಲ್ಲಿ ಮಾಡಲಾಗಿಲ್ಲ ಎಂಬುದು ಸ್ಪಷ್ಟವಾಗಿದೆ. ಸ್ಕ್ವ್ಯಾಷ್ ವಿಲೀನಗಳು ಪರಿಣಾಮ ಬೀರುವುದಿಲ್ಲ: ಸ್ಕ್ವ್ಯಾಷ್ ಒಂದು ಸಾಮಾನ್ಯ ಕಮಿಟ್ ಅನ್ನು ಬರೆಯುತ್ತದೆ, ಆದ್ದರಿಂದ ಅದು ಮಾತ್ರ ಯಾವಾಗಲೂ ಕಾಣಿಸಿಕೊಳ್ಳುತ್ತದೆ.",
    "legend.title": "ದಂತಕಥೆ",
    "legend.head": "ಹೆಡ್ / ಪ್ರಸ್ತುತ ಶಾಖೆ",
    "legend.local": "ಸ್ಥಳೀಯ ಶಾಖೆ",
    "legend.remote": "ರಿಮೋಟ್ ಶಾಖೆ",
    "legend.remoteOnly": "ಮೋಡದಲ್ಲಿ ಮಾತ್ರ (ಎಳೆಯಲಾಗಿಲ್ಲ)",
    "legend.tag": "ಟ್ಯಾಗ್ (ಆವೃತ್ತಿ)",
    "legend.commit": "ಬದ್ಧತೆ",
    "legend.stash": "ಸ್ಟ್ಯಾಶ್ (ಕಪಾಟು ಮಾಡಿದ ಕೆಲಸ)",
    "legend.mergedIn": "ವಿಲೀನದೊಂದಿಗೆ ಬಂದಿತು (ಇನ್ನೊಂದು ಶಾಖೆಯಲ್ಲಿ ಬರೆಯಲಾಗಿದೆ)",
    "legend.nodes": "ಪೆಟ್ಟಿಗೆಗಳು",
    "legend.lines": "ಸಾಲುಗಳು",
    "legend.edgeParent": "ಅದೇ ಸಾಲಿನಲ್ಲಿ ಹಿಂದಿನ ಬದ್ಧತೆ",
    "legend.edgeMerge": "ವಿಲೀನಗೊಂಡ ಶಾಖೆ - ವಿಲೀನ ಬದ್ಧತೆಯ ಬಾಣದ ಬಿಂದುಗಳು",
    "legend.edgeBranch": "ಇಲ್ಲಿ ಒಂದು ಶಾಖೆ ಕವಲೊಡೆಯಿತು",
    "legend.edgeStash": "ಸ್ಟ್ಯಾಶ್ ಮತ್ತು ಬದ್ಧತೆಯನ್ನು ಸ್ಥಗಿತಗೊಳಿಸಲಾಗಿದೆ",
    "legend.edgeMergedChain": "ಈ ಶಾಖೆಯ ಲೈನ್ ವಿಲೀನ-ಇನ್ ಕಮಿಟ್‌ಗಳ ಮೂಲಕ ಚಲಿಸುತ್ತದೆ",
    "legend.edgeMergedTie": "ಅದನ್ನು ಬರೆದ ಶಾಖೆಯ ಮೇಲೆ ಅದೇ ಬದ್ಧತೆ",
    "menu.jumpHead": "⌖ ಚೆಕ್ಔಟ್ಗೆ ಹೋಗಿ",
    "menu.resetView": "⤢ ವೀಕ್ಷಣೆಯನ್ನು ಮರುಹೊಂದಿಸಿ",
    "menu.createBranch": "ಇಲ್ಲಿಂದ ಶಾಖೆಯನ್ನು ರಚಿಸಿ...",
    "menu.checkout": "ಈ ಬದ್ಧತೆಯನ್ನು ಪರಿಶೀಲಿಸಿ",
    "menu.copySha": "SHA ಬದ್ಧತೆಯನ್ನು ನಕಲಿಸಿ",
    "menu.pushBranch": "ಪುಶ್ ಶಾಖೆ \"{name}\"",
    "menu.renameCommit": "ಬದ್ಧತೆಯ ಸಂದೇಶವನ್ನು ಮರುಹೆಸರಿಸಿ...",
    "menu.undoCommit": "ಬದ್ಧತೆಯನ್ನು ರದ್ದುಗೊಳಿಸಿ (ಬದಲಾವಣೆಗಳನ್ನು ಇರಿಸಿಕೊಳ್ಳಿ)...",
    "menu.viewChanges": "ಬದಲಾವಣೆಗಳನ್ನು ವೀಕ್ಷಿಸಿ...",
    "menu.jumpToOriginal": "ಮೂಲ ಬದ್ಧತೆಗೆ ಹೋಗಿ",
    "changes.title": "{sha} ನಲ್ಲಿ ಬದಲಾವಣೆಗಳು",
    "changes.tabChanged": "ಬದಲಾಗಿದೆ",
    "changes.tabAll": "ಎಲ್ಲಾ ಫೈಲ್‌ಗಳು",
    "changes.added": "ಸೇರಿಸಲಾಗಿದೆ",
    "changes.modified": "ಮಾರ್ಪಡಿಸಲಾಗಿದೆ",
    "changes.deleted": "ಅಳಿಸಲಾಗಿದೆ",
    "changes.renamed": "ಮರುನಾಮಕರಣ ಮಾಡಲಾಗಿದೆ",
    "changes.noChanges": "ಈ ಬದ್ಧತೆಯು ಯಾವುದೇ ಫೈಲ್‌ಗಳನ್ನು ಬದಲಾಯಿಸುವುದಿಲ್ಲ.",
    "changes.loading": "ವ್ಯತ್ಯಾಸವನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...",
    "changes.selectFile": "ಅದರ ಬದಲಾವಣೆಗಳನ್ನು ನೋಡಲು ಫೈಲ್ ಅನ್ನು ಆಯ್ಕೆಮಾಡಿ.",
    "changes.original": "ಮೂಲ",
    "changes.changed": "ಈ ಬದ್ಧತೆ",
    "changes.binary": "ಬೈನರಿ ಫೈಲ್ — ತೋರಿಸಲು ಯಾವುದೇ ಪಠ್ಯ ವ್ಯತ್ಯಾಸವಿಲ್ಲ.",
    "changes.tooLarge": "ಫೈಲ್ ತುಂಬಾ ದೊಡ್ಡದಾಗಿದೆ.",
    "changes.renamedFrom": "{path} ನಿಂದ ಮರುಹೆಸರಿಸಲಾಗಿದೆ",
    "changes.close": "ಮುಚ್ಚಿ",
    "changes.prevChange": "ಹಿಂದಿನ ಬದಲಾವಣೆ",
    "changes.nextChange": "ಮುಂದಿನ ಬದಲಾವಣೆ",
    "changes.collapseAll": "ಎಲ್ಲಾ ಫೋಲ್ಡರ್‌ಗಳನ್ನು ಸಂಕುಚಿಸಿ",
    "changes.expandAll": "ಎಲ್ಲಾ ಫೋಲ್ಡರ್‌ಗಳನ್ನು ವಿಸ್ತರಿಸಿ",
    "changes.searchPlaceholder": "ಫೈಲ್‌ಗಳನ್ನು ಹುಡುಕಿ...",
    "changes.noSearchResults": "ನಿಮ್ಮ ಹುಡುಕಾಟಕ್ಕೆ ಯಾವುದೇ ಫೈಲ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುವುದಿಲ್ಲ.",
    "changes.maximize": "ಗರಿಷ್ಠಗೊಳಿಸು",
    "changes.restore": "ಗಾತ್ರವನ್ನು ಮರುಸ್ಥಾಪಿಸಿ",
    "find.open": "ಈ ವ್ಯತ್ಯಾಸದಲ್ಲಿ ಹುಡುಕಿ (Ctrl+F)",
    "find.placeholder": "ವ್ಯತ್ಯಾಸದಲ್ಲಿ ಹುಡುಕಿ...",
    "find.prev": "ಹಿಂದಿನ ಪಂದ್ಯ",
    "find.next": "ಮುಂದಿನ ಪಂದ್ಯ",
    "find.close": "ಹುಡುಕಾಟವನ್ನು ಮುಚ್ಚಿ",
    "find.noResults": "ಯಾವುದೇ ಫಲಿತಾಂಶಗಳಿಲ್ಲ",
    "commit.title": "ಬದಲಾವಣೆಗಳನ್ನು ಒಪ್ಪಿಸಿ",
    "commit.close": "ಮುಚ್ಚಿ",
    "commit.loading": "ಕೆಲಸ ಮಾಡುವ ಮರವನ್ನು ಓದುವುದು...",
    "commit.noChanges": "ಬದ್ಧರಾಗಲು ಯಾವುದೇ ಸ್ಥಳೀಯ ಬದಲಾವಣೆಗಳಿಲ್ಲ.",
    "commit.selectFile": "ಅದರ ವ್ಯತ್ಯಾಸವನ್ನು ನೋಡಲು ಫೈಲ್ ಅನ್ನು ಆಯ್ಕೆಮಾಡಿ.",
    "commit.selectAll": "ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ",
    "commit.selectNone": "ಎಲ್ಲವನ್ನೂ ತೆರವುಗೊಳಿಸಿ",
    "commit.message": "ಬದ್ಧ ಸಂದೇಶ",
    "commit.messagePlaceholder": "ಬದಲಾವಣೆಯನ್ನು ವಿವರಿಸಿ",
    "commit.selectedCount": "{count} ಫೈಲ್(ಗಳು) ಆಯ್ಕೆ ಮಾಡಲಾಗಿದೆ",
    "commit.review": "ವಿಮರ್ಶೆ",
    "commit.reviewTitle": "ಬದ್ಧತೆಯ ಮೊದಲು ಅಂತಿಮ ಪರಿಶೀಲನೆ",
    "commit.reviewSummary": "ಸಂದೇಶದೊಂದಿಗೆ {count} ಫೈಲ್(ಗಳನ್ನು) ಒಪ್ಪಿಸಿ: {message}",
    "commit.back": "ಹಿಂದೆ",
    "commit.commit": "ಸ್ಥಳೀಯ ಬದ್ಧತೆಯನ್ನು ರಚಿಸಿ",
    "menu.stashApply": "ಸ್ಟಾಶ್ ಅನ್ನು ಅನ್ವಯಿಸಿ",
    "menu.stashPop": "ಪಾಪ್ ಸ್ಟ್ಯಾಶ್ (ಅನ್ವಯಿಸಿ ಮತ್ತು ತೆಗೆದುಹಾಕಿ)",
    "menu.stashDrop": "ಡ್ರಾಪ್ ಸ್ಟ್ಯಾಶ್...",
    "merge.title": "ಶಾಖೆಯನ್ನು ವಿಲೀನಗೊಳಿಸಿ",
    "merge.route": "ಪ್ರಸ್ತುತ ಶಾಖೆಯಲ್ಲಿ ವಿಲೀನಗೊಳ್ಳುತ್ತಿದೆ",
    "merge.source": "(ಮೂಲ) ಇಂದ",
    "merge.target": "ಒಳಗೆ (ಪ್ರಸ್ತುತ)",
    "merge.loading": "ವಿಲೀನವನ್ನು ವಿಶ್ಲೇಷಿಸಲಾಗುತ್ತಿದೆ...",
    "merge.upToDate": "ಈಗಾಗಲೇ ನವೀಕೃತವಾಗಿದೆ - ವಿಲೀನಗೊಳಿಸಲು ಏನೂ ಇಲ್ಲ.",
    "merge.fastForward": "ಫಾಸ್ಟ್-ಫಾರ್ವರ್ಡ್ - ಯಾವುದೇ ವಿಲೀನ ಬದ್ಧತೆಯ ಅಗತ್ಯವಿಲ್ಲ (ಒಂದನ್ನು ಒತ್ತಾಯಿಸಲು \"ವಿಲೀನ ಬದ್ಧತೆಯನ್ನು ರಚಿಸಿ\" ಎಂದು ಟಿಕ್ ಮಾಡಿ).",
    "merge.summary": "{files} ಫೈಲ್(ಗಳು) ಬದಲಾಗಿದೆ, {conflicts} ಸಂಘರ್ಷ(ಗಳು).",
    "merge.conflictsWarning": "ಈ ವಿಲೀನವು ಸಂಘರ್ಷಗಳನ್ನು ಹೊಂದಿದೆ - ನೀವು ಇನ್ನೂ ವಿಲೀನಗೊಳಿಸಬಹುದು, ನಂತರ ಅವುಗಳನ್ನು ಸಂಪಾದಕದಲ್ಲಿ ಪರಿಹರಿಸಬಹುದು.",
    "merge.noChanges": "ಯಾವುದೇ ಫೈಲ್ ಬದಲಾವಣೆಗಳಿಲ್ಲ.",
    "merge.files": "ಪರಿಣಾಮವಾಗಿ ಬದಲಾವಣೆಗಳು",
    "merge.added": "ಸೇರಿಸಲಾಗಿದೆ",
    "merge.modified": "ಮಾರ್ಪಡಿಸಲಾಗಿದೆ",
    "merge.deleted": "ಅಳಿಸಲಾಗಿದೆ",
    "merge.conflict": "ಸಂಘರ್ಷ",
    "merge.message": "ಬದ್ಧತೆಯ ಸಂದೇಶವನ್ನು ವಿಲೀನಗೊಳಿಸಿ",
    "merge.messagePlaceholder": "ಶಾಖೆ 'ಮೂಲ' ವಿಲೀನಗೊಳಿಸಿ",
    "merge.messageFfHint": "ಫಾಸ್ಟ್-ಫಾರ್ವರ್ಡ್ ವಿಲೀನದಲ್ಲಿ ನಿರ್ಲಕ್ಷಿಸಲಾಗಿದೆ (ಯಾವುದೇ ಬದ್ಧತೆಯನ್ನು ರಚಿಸಲಾಗಿಲ್ಲ).",
    "merge.noFastForward": "ವಿಲೀನ ಬದ್ಧತೆಯನ್ನು ರಚಿಸಿ (ಫಾಸ್ಟ್-ಫಾರ್ವರ್ಡ್ ಇಲ್ಲ)",
    "merge.noFastForwardHint": "ಶಾಖೆಯು ಫಾಸ್ಟ್-ಫಾರ್ವರ್ಡ್ ಮಾಡಬಹುದಾದಾಗಲೂ ಯಾವಾಗಲೂ ವಿಲೀನ ಬದ್ಧತೆಯನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಿ.",
    "merge.merge": "ವಿಲೀನಗೊಳಿಸಿ",
    "merge.cancel": "ರದ್ದುಮಾಡಿ",
    "merge.previewError": "ಪೂರ್ವವೀಕ್ಷಣೆಯನ್ನು ಲೆಕ್ಕಾಚಾರ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ: {message}",
    "merge.squashBadge": "ಸ್ಕ್ವ್ಯಾಷ್",
    "merge.squashNote": "ಸ್ಕ್ವ್ಯಾಷ್ ವಿಲೀನ - \"{target}\" ನಲ್ಲಿ ಒಂದು ಬದ್ಧತೆಯಂತೆ ಪ್ರತಿ ಬದಲಾವಣೆಯು ನೆಲಸುತ್ತದೆ; \"{source}\" ನ ಸ್ವಂತ ಬದ್ಧತೆಗಳು ಅದರ ಇತಿಹಾಸದಿಂದ ಹೊರಗುಳಿಯುತ್ತವೆ. ಸೆಟ್ಟಿಂಗ್‌ಗಳು › ವಿಲೀನದಲ್ಲಿ ಹಿಂತಿರುಗಿ.",
    "merge.messageSquash": "ಬದ್ಧ ಸಂದೇಶ",
    "merge.squashMessagePlaceholder": "'ಮೂಲ'ದಿಂದ ಸ್ಕ್ವಾಶ್ಡ್ ಬದಲಾವಣೆಗಳು",
    "newBranch.title": "ಶಾಖೆಯನ್ನು ರಚಿಸಿ",
    "newBranch.startPoint": "{sha} ನಿಂದ ಪ್ರಾರಂಭವಾಗುವ ಹೊಸ ಶಾಖೆ",
    "newBranch.startPointOn": "{refs} ನಲ್ಲಿ",
    "newBranch.location": "ಸ್ಥಳ",
    "newBranch.locationRoot": "(ಮೂಲ)",
    "newBranch.expandAll": "ಎಲ್ಲವನ್ನೂ ವಿಸ್ತರಿಸಿ",
    "newBranch.collapseAll": "ಎಲ್ಲವನ್ನೂ ಕುಗ್ಗಿಸಿ",
    "newBranch.expandFolder": "ಫೋಲ್ಡರ್ ಅನ್ನು ವಿಸ್ತರಿಸಿ",
    "newBranch.collapseFolder": "ಫೋಲ್ಡರ್ ಕುಗ್ಗಿಸಿ",
    "newBranch.name": "ಶಾಖೆಯ ಹೆಸರು",
    "newBranch.namePlaceholder": "ನನ್ನ ಶಾಖೆ",
    "newBranch.fullName": "ಪೂರ್ಣ ಹೆಸರು: {name}",
    "newBranch.invalid": "ಅಮಾನ್ಯ ಶಾಖೆಯ ಹೆಸರು",
    "newBranch.exists": "ಈ ಹೆಸರಿನ ಶಾಖೆಯು ಈಗಾಗಲೇ ಅಸ್ತಿತ್ವದಲ್ಲಿದೆ",
    "newBranch.checkout": "ರಚಿಸಿದ ನಂತರ ಶಾಖೆಯನ್ನು ಚೆಕ್ಔಟ್ ಮಾಡಿ",
    "newBranch.create": "ರಚಿಸಿ",
    "newBranch.cancel": "ರದ್ದುಮಾಡಿ",
    "status.loading": "ಗ್ರಾಫ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ...",
    "status.summary": "{repo} {commits} ಬದ್ಧತೆಗಳನ್ನು ತೋರಿಸಲಾಗುತ್ತಿದೆ, {refs} ಉಲ್ಲೇಖಿಸುತ್ತದೆ",
    "status.branchCreated": "{sha} ನಲ್ಲಿ \"{name}\" ಶಾಖೆಯನ್ನು ರಚಿಸಲಾಗಿದೆ",
    "status.error": "ದೋಷ: {message}",
    "status.opFailed": "ಕಾರ್ಯಾಚರಣೆ ವಿಫಲವಾಗಿದೆ",
    "status.fetching": "ತರಲಾಗುತ್ತಿದೆ...",
    "status.pulling": "ಎಳೆಯಲಾಗುತ್ತಿದೆ...",
    "status.pushing": "ತಳ್ಳುತ್ತಿದೆ...",
    "status.committing": "ಸ್ಥಳೀಯ ಬದ್ಧತೆಯನ್ನು ರಚಿಸಲಾಗುತ್ತಿದೆ...",
    "status.commitCreated": "ರಚಿಸಲಾಗಿದೆ ಸ್ಥಳೀಯ ಬದ್ಧತೆ {sha}: {message}",
    "status.syncing": "ಸಿಂಕ್ ಮಾಡಲಾಗುತ್ತಿದೆ...",
    "status.noHead": "ಗ್ರಾಫ್‌ನಲ್ಲಿ ಯಾವುದೇ ಪ್ರಸ್ತುತ ಚೆಕ್‌ಔಟ್ ಕಂಡುಬಂದಿಲ್ಲ.",
    "status.undoing": "ಬದ್ಧತೆಯನ್ನು ರದ್ದುಗೊಳಿಸಲಾಗುತ್ತಿದೆ...",
    "status.commitUndone": "ಬದ್ಧತೆಯನ್ನು ರದ್ದುಗೊಳಿಸಲಾಗಿದೆ - ಕೆಲಸ ಮಾಡುವ ಮರದಲ್ಲಿ ಬದಲಾವಣೆಗಳು ಹಿಂತಿರುಗಿವೆ.",
    "status.undoConflict": "ಹಿಟ್ ಸಂಘರ್ಷಗಳನ್ನು ರದ್ದುಗೊಳಿಸಿ - ಅವುಗಳನ್ನು ಸಂಪಾದಕದಲ್ಲಿ ಪರಿಹರಿಸಿ, ನಂತರ ಮುಂದುವರಿಸಿ.",
    "status.stashApplying": "ಸ್ಟಾಶ್ ಅನ್ನು ಅನ್ವಯಿಸಲಾಗುತ್ತಿದೆ...",
    "status.stashPopping": "ಪಾಪಿಂಗ್ ಸ್ಟಾಶ್...",
    "status.stashDropping": "ಸ್ಟಾಶ್ ಅನ್ನು ಬಿಡಲಾಗುತ್ತಿದೆ...",
    "status.stashApplied": "ಸ್ಟ್ಯಾಶ್ ಅನ್ವಯಿಸಲಾಗಿದೆ.",
    "status.stashPopped": "ಸ್ಟಾಶ್ ಪಾಪ್ಡ್.",
    "status.stashDropped": "ಸ್ಟಾಶ್ ಕೈಬಿಡಲಾಯಿತು.",
    "status.stashConflict": "ಸ್ಟ್ಯಾಶ್ ಸಂಘರ್ಷಗಳು - ಅವುಗಳನ್ನು ಸಂಪಾದಕದಲ್ಲಿ ಪರಿಹರಿಸಿ.",
    "status.merging": "ವಿಲೀನಗೊಳಿಸಲಾಗುತ್ತಿದೆ...",
    "status.merged": "ವಿಲೀನ ಪೂರ್ಣಗೊಂಡಿದೆ.",
    "status.mergeConflict": "ವಿಲೀನವು ಸಂಘರ್ಷಗಳನ್ನು ಹೊಂದಿದೆ - ಅವುಗಳನ್ನು ಸಂಪಾದಕದಲ್ಲಿ ಪರಿಹರಿಸಿ, ನಂತರ ಒಪ್ಪಿಸಿ.",
    "search.title": "ಹುಡುಕು",
    "search.tooltip": "ಹುಡುಕಾಟ ಬದ್ಧವಾಗಿದೆ",
    "search.placeholder": "ಹುಡುಕಾಟ ಬದ್ಧವಾಗಿದೆ...",
    "search.previous": "ಹಿಂದಿನ ಫಲಿತಾಂಶ",
    "search.next": "ಮುಂದಿನ ಫಲಿತಾಂಶ",
    "search.noResults": "ಯಾವುದೇ ಫಲಿತಾಂಶಗಳಿಲ್ಲ",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ಫಿಲ್ಟರ್ ಮೋಡ್",
    "search.highlight": "ಹೈಲೈಟ್ ಮೋಡ್",
    "search.filterMode": "ಹುಡುಕಾಟ ಪ್ರದರ್ಶನ",
    "search.filterModeFilter": "ಫಿಲ್ಟರ್ (ಹೊಂದಾಣಿಕೆಯಾಗದವುಗಳನ್ನು ಮರೆಮಾಡಿ)",
    "search.filterModeHighlight": "ಹೈಲೈಟ್ ಮಾಡಿ (ಎಲ್ಲವನ್ನೂ ತೋರಿಸಿ, ಹೊಂದಾಣಿಕೆಗಳನ್ನು ಗುರುತಿಸಿ)",
    "search.filterModeHint": "ಗ್ರಾಫ್‌ನಲ್ಲಿ ಹುಡುಕಾಟ ಫಲಿತಾಂಶಗಳನ್ನು ಹೇಗೆ ಪ್ರದರ್ಶಿಸಲಾಗುತ್ತದೆ ಎಂಬುದನ್ನು ಆರಿಸಿ.",
    "search.visibility": "ಸ್ಥಳವನ್ನು ಹುಡುಕಿ",
    "search.visibilityToolbar": "ಟೂಲ್‌ಬಾರ್ ಬಟನ್",
    "search.visibilityAlwaysVisible": "ಯಾವಾಗಲೂ ಗೋಚರಿಸುವ ಬಾರ್",
    "search.visibilityHint": "ಮೇಲ್ಭಾಗದಲ್ಲಿ ಟೂಲ್‌ಬಾರ್ ಐಕಾನ್ ಅಥವಾ ನಿರಂತರ ಪಟ್ಟಿಯಂತೆ ಹುಡುಕಾಟವನ್ನು ತೋರಿಸಿ.",
    "footer.github": "GitHub",
  },
  ml: {
    "toolbar.refresh": "⟳ പുതുക്കുക",
    "toolbar.fetch": "⤓ ലഭ്യമാക്കുക",
    "toolbar.pull": "⇩ വലിക്കുക",
    "toolbar.push": "⇧ പുഷ്",
    "toolbar.commit": "✓ പ്രതിബദ്ധത",
    "toolbar.sync": "⇅ സമന്വയിപ്പിക്കുക",
    "toolbar.jumpHead": "⌖ ചെക്ക്ഔട്ടിലേക്ക് പോകുക",
    "toolbar.reset": "⤢ കാഴ്ച പുനഃസജ്ജമാക്കുക",
    "toolbar.settings": "⚙ ക്രമീകരണങ്ങൾ",
    "details.header": "കമ്മിറ്റ് വിശദാംശങ്ങൾ",
    "details.close": "അടയ്ക്കുക",
    "details.sha": "SHA",
    "details.shortSha": "ഹ്രസ്വ SHA",
    "details.message": "സന്ദേശം",
    "details.author": "രചയിതാവ്",
    "details.date": "തീയതി",
    "details.labels": "ലേബലുകൾ",
    "details.location": "സ്ഥാനം",
    "details.currentHead": "ഹെഡ് - നിലവിലെ ചെക്ക്ഔട്ട്",
    "settings.title": "ക്രമീകരണങ്ങൾ",
    "settings.language": "ഭാഷ",
    "settings.theme": "തീം",
    "settings.themeLight": "വെളിച്ചം",
    "settings.themeDark": "ഇരുട്ട്",
    "settings.themeLightHint": "ഇളം പശ്ചാത്തലം, ഇരുണ്ട വാചകം.",
    "settings.themeDarkHint": "ഇരുണ്ട പശ്ചാത്തലം, ഇളം വാചകം.",
    "settings.mainBranch": "പ്രധാന ശാഖ",
    "settings.mainBranchAuto": "ഓട്ടോമാറ്റിക്",
    "settings.mainBranchSearch": "ശാഖകൾ തിരയുക...",
    "settings.mainBranchNoMatch": "പൊരുത്തപ്പെടുന്ന ശാഖകളൊന്നുമില്ല",
    "settings.display": "പ്രദർശന ശൈലി",
    "settings.displayModern": "ആധുനികം",
    "settings.displayClassic": "ക്ലാസിക്",
    "settings.displayModernHint": "സൗജന്യ ക്യാൻവാസ് - പാൻ ചെയ്യാൻ വലിച്ചിടുക, സൂം ചെയ്യാൻ സ്ക്രോൾ ചെയ്യുക.",
    "settings.displayClassicHint": "ഫിക്സഡ് ക്യാൻവാസ് - ഇടതുവശത്ത് തുമ്പിക്കൈ പിൻ ചെയ്തു, സൂം ഇല്ല, സ്ക്രോൾ മാത്രം (SVN റിവിഷൻ ഗ്രാഫ് പോലെ).",
    "settings.sectionGeneral": "ജനറൽ",
    "settings.sectionGraph": "ഗ്രാഫ്",
    "settings.sectionChanges": "കാഴ്ച മാറ്റുന്നു",
    "settings.diffMinimap": "വ്യത്യാസം മിനിമാപ്പ്",
    "settings.diffMinimapOn": "കാണിച്ചിരിക്കുന്നു",
    "settings.diffMinimapOff": "മറച്ചിരിക്കുന്നു",
    "settings.diffMinimapOnHint": "വ്യത്യാസത്തിന് അരികിൽ ഒരു VS കോഡ് ശൈലിയിലുള്ള അവലോകന സ്ട്രിപ്പ് കാണിക്കുക - സ്ക്രോൾ ചെയ്യാൻ അത് വലിച്ചിടുക; മാറ്റ മാർക്കറുകൾ ഉൾപ്പെടുത്തിയിട്ടുണ്ട്.",
    "settings.diffMinimapOffHint": "അവലോകന സ്ട്രിപ്പ് ഇല്ല - വ്യത്യാസം സാധാരണയായി സ്ക്രോൾ ചെയ്യുക.",
    "settings.commitReview": "അവലോകനം നടത്തുക",
    "settings.commitReviewOn": "പ്രതിജ്ഞയെടുക്കുന്നതിന് മുമ്പ് ചോദിക്കുക",
    "settings.commitReviewOff": "നേരിട്ട് കമ്മിറ്റ് ചെയ്യുക",
    "settings.commitReviewOnHint": "ലോക്കൽ കമ്മിറ്റ് സൃഷ്‌ടിക്കുന്നതിന് മുമ്പ് അന്തിമ ഫയൽ ലിസ്റ്റും സന്ദേശ പരിശോധനയും കാണിക്കുക.",
    "settings.commitReviewOffHint": "കമ്മിറ്റ് ബട്ടൺ ഉടനടി പ്രാദേശിക പ്രതിബദ്ധത സൃഷ്ടിക്കുന്നു.",
    "settings.svnBranchDialog": "SVN-രീതിയിലുള്ള ബ്രാഞ്ച് ഡയലോഗ്",
    "settings.svnBranchDialogHint": "ഒരു ബ്രാഞ്ച് സൃഷ്ടിക്കുമ്പോൾ ഒരു ഫോൾഡർ-ട്രീ പിക്കർ കാണിക്കുക (ഒരു ലളിതമായ പ്രോംപ്റ്റിന് പകരം).",
    "settings.branchDialog": "ബ്രാഞ്ച് ഡയലോഗ്",
    "settings.branchDialogSvnTitle": "എസ്വിഎൻ ശൈലി",
    "settings.branchDialogNativeVscode": "വിഎസ് കോഡ് ശൈലി",
    "settings.branchDialogNativeVs": "വിഷ്വൽ സ്റ്റുഡിയോ ശൈലി",
    "settings.branchDialogNativeHint": "നിങ്ങളുടെ IDE-യുടെ ബിൽറ്റ്-ഇൻ ബ്രാഞ്ച് പ്രോംപ്റ്റ്.",
    "settings.close": "അടയ്ക്കുക",
    "settings.done": "ചെയ്തു",
    "settings.sectionAdvanced": "വിപുലമായ",
    "settings.gitSource": "ജിറ്റ് എക്സിക്യൂട്ടബിൾ",
    "settings.gitSourceBuiltin": "അന്തർനിർമ്മിത",
    "settings.gitSourceBuiltinHint": "IDE-യുടെ ബിൽറ്റ്-ഇൻ Git വിപുലീകരണത്തിൽ നിന്ന് git ബൈനറി ഉപയോഗിക്കുക.",
    "settings.gitSourceCustom": "ഇഷ്ടാനുസൃത പാത",
    "settings.gitPath": "ജിറ്റിലേക്കുള്ള പാത",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ബ്രൗസ് ചെയ്യുക...",
    "settings.jargon": "Git നിബന്ധനകൾ",
    "settings.jargonTranslate": "വിവർത്തനം ചെയ്യുക",
    "settings.jargonEnglish": "ഇംഗ്ലീഷിൽ സൂക്ഷിക്കുക",
    "settings.jargonTranslateHint": "Git നിബന്ധനകൾ (വലിക്കുക, തള്ളുക, കമ്മിറ്റ് ചെയ്യുക, ബ്രാഞ്ച്...) ഇൻ്റർഫേസ് ഭാഷയിലേക്ക് വിവർത്തനം ചെയ്യുക.",
    "settings.jargonEnglishHint": "ഇംഗ്ലീഷിൽ Git നിബന്ധനകൾ (വലിക്കുക, തള്ളുക, കമ്മിറ്റ് ചെയ്യുക, ബ്രാഞ്ച്...) സൂക്ഷിക്കുക; മറ്റെല്ലാം വിവർത്തനം ചെയ്യുക.",
    "settings.sectionMerge": "ലയിപ്പിക്കുക",
    "settings.mergeMode": "ശൈലി ലയിപ്പിക്കുക",
    "settings.mergeModeMerge": "സാധാരണ ലയനം",
    "settings.mergeModeSquash": "സ്ക്വാഷ് ലയനം",
    "settings.mergeModeMergeHint": "ബ്രാഞ്ചിനെ അതേപടി ലയിപ്പിക്കുന്നു: അതിൻ്റെ കമ്മിറ്റുകൾ നിലവിലെ ബ്രാഞ്ചിൻ്റെ ചരിത്രത്തിൻ്റെ ഭാഗമായിത്തീരുന്നു, ഒരു ലയന പ്രതിബദ്ധത (അല്ലെങ്കിൽ സാധ്യമാകുമ്പോൾ വേഗത്തിൽ ഫോർവേഡ് ചെയ്യുന്നു).",
    "settings.mergeModeSquashHint": "നിലവിലെ ബ്രാഞ്ചിൽ (git merge --squash) ഒരൊറ്റ കമ്മിറ്റിലേക്ക് മുഴുവൻ ശാഖയും ചുരുക്കുന്നു. ബ്രാഞ്ചിൻ്റെ സ്വന്തം കമ്മിറ്റുകൾ നിലവിലെ ബ്രാഞ്ചിൻ്റെ ചരിത്രത്തിൽ ഒരിക്കലും പ്രവേശിക്കില്ല - അവിടെ ഒരു കമ്മിറ്റ് മാത്രമേ നിങ്ങൾ കാണൂ. ശാഖ തന്നെ സ്പർശിക്കാതെ അവശേഷിക്കുന്നു, അതിനാൽ അതിൻ്റെ പ്രതിബദ്ധത അതിൻ്റെ സ്വന്തം പാതയിൽ ദൃശ്യമാകും.",
    "settings.mergedView": "ഗ്രാഫിൽ ലയിപ്പിച്ച കമ്മിറ്റുകൾ",
    "settings.mergedViewBranch": "സ്വന്തം ശാഖയിൽ മാത്രം",
    "settings.mergedViewTarget": "ശാഖയിലും അവർ ലയിച്ചു",
    "settings.mergedViewBranchHint": "ഒരു ലയിപ്പിച്ച ശാഖയുടെ കമ്മിറ്റുകൾ അവരുടെ സ്വന്തം പാതയിൽ മാത്രം വരയ്ക്കുന്നു; ലയനം ഒരു കണക്ടറായി കാണിക്കുന്നു. ക്ലാസിക് കാഴ്ച.",
    "settings.mergedViewTargetHint": "ഒരു ലയനം കൊണ്ടുവരുന്ന കമ്മിറ്റുകൾ സ്വീകരിക്കുന്ന ബ്രാഞ്ചിൻ്റെ ലെയ്‌നിലും വരയ്ക്കുന്നു, ലയന കമ്മിറ്റിന് കീഴിൽ അടുക്കി ഒറിജിനലുമായി ലെവൽ ചെയ്യുന്നു - കാരണം ആ ബ്രാഞ്ചിൽ ഇപ്പോൾ അവ ശരിക്കും അടങ്ങിയിരിക്കുന്നു. പകർപ്പുകൾ വിളറിയതും അവ എഴുതിയ ശാഖയുടെ തലയുള്ളതുമാണ്, അതിനാൽ അവ അവിടെ നിർമ്മിച്ചതല്ലെന്ന് വ്യക്തമാണ്. സ്ക്വാഷ് ലയനങ്ങളെ ബാധിക്കില്ല: ഒരു സ്ക്വാഷ് ഒരു സാധാരണ കമ്മിറ്റ് എഴുതുന്നു, അതിനാൽ ആ പ്രതിബദ്ധത മാത്രമേ എപ്പോഴെങ്കിലും ദൃശ്യമാകൂ.",
    "legend.title": "ഇതിഹാസം",
    "legend.head": "ഹെഡ് / നിലവിലെ ബ്രാഞ്ച്",
    "legend.local": "പ്രാദേശിക ശാഖ",
    "legend.remote": "വിദൂര ശാഖ",
    "legend.remoteOnly": "മേഘത്തിൽ മാത്രം (വലിച്ചിട്ടില്ല)",
    "legend.tag": "ടാഗ് (പതിപ്പ്)",
    "legend.commit": "പ്രതിബദ്ധത",
    "legend.stash": "സ്‌റ്റാഷ് (ഷെൽഡ് വർക്ക്)",
    "legend.mergedIn": "ഒരു ലയനത്തോടെ വന്നു (മറ്റൊരു ശാഖയിൽ എഴുതിയത്)",
    "legend.nodes": "പെട്ടികൾ",
    "legend.lines": "വരികൾ",
    "legend.edgeParent": "ഇതേ ലൈനിൽ മുൻ കമ്മിറ്റ്",
    "legend.edgeMerge": "ലയിപ്പിച്ച ശാഖ - ലയന കമ്മിറ്റിലെ അമ്പടയാളം",
    "legend.edgeBranch": "ഇവിടെ ഒരു ശാഖ പിളർന്നു",
    "legend.edgeStash": "സ്റ്റാഷും കമ്മിറ്റും അത് ഉപേക്ഷിച്ചു",
    "legend.edgeMergedChain": "ഈ ബ്രാഞ്ചിൻ്റെ ലൈൻ മെർജ്-ഇൻ കമ്മിറ്റിലൂടെ കടന്നുപോകുന്നു",
    "legend.edgeMergedTie": "എഴുതിയ ശാഖയിലും അതേ പ്രതിബദ്ധത",
    "menu.jumpHead": "⌖ ചെക്ക്ഔട്ടിലേക്ക് പോകുക",
    "menu.resetView": "⤢ കാഴ്ച പുനഃസജ്ജമാക്കുക",
    "menu.createBranch": "ഇവിടെ നിന്ന് ബ്രാഞ്ച് സൃഷ്‌ടിക്കുക...",
    "menu.checkout": "ഈ പ്രതിബദ്ധത പരിശോധിക്കുക",
    "menu.copySha": "SHA കമ്മിറ്റ് ചെയ്യുക",
    "menu.pushBranch": "പുഷ് ബ്രാഞ്ച് \"{name}\"",
    "menu.renameCommit": "കമ്മിറ്റ് സന്ദേശത്തിൻ്റെ പേരുമാറ്റുക...",
    "menu.undoCommit": "പ്രതിബദ്ധത പഴയപടിയാക്കുക (മാറ്റങ്ങൾ സൂക്ഷിക്കുക)...",
    "menu.viewChanges": "മാറ്റങ്ങൾ കാണുക...",
    "menu.jumpToOriginal": "യഥാർത്ഥ പ്രതിബദ്ധതയിലേക്ക് പോകുക",
    "changes.title": "{sha}-ലെ മാറ്റങ്ങൾ",
    "changes.tabChanged": "മാറ്റി",
    "changes.tabAll": "എല്ലാ ഫയലുകളും",
    "changes.added": "ചേർത്തു",
    "changes.modified": "പരിഷ്കരിച്ചു",
    "changes.deleted": "ഇല്ലാതാക്കി",
    "changes.renamed": "പേരുമാറ്റി",
    "changes.noChanges": "ഈ കമ്മിറ്റ് ഫയലുകളൊന്നും മാറ്റുന്നില്ല.",
    "changes.loading": "വ്യത്യാസം ലോഡുചെയ്യുന്നു...",
    "changes.selectFile": "അതിൻ്റെ മാറ്റങ്ങൾ കാണുന്നതിന് ഒരു ഫയൽ തിരഞ്ഞെടുക്കുക.",
    "changes.original": "ഒറിജിനൽ",
    "changes.changed": "ഈ പ്രതിബദ്ധത",
    "changes.binary": "ബൈനറി ഫയൽ — കാണിക്കാൻ ടെക്സ്റ്റ് വ്യത്യാസമില്ല.",
    "changes.tooLarge": "ഫയൽ വളരെ വലുതാണ്.",
    "changes.renamedFrom": "{path} എന്നതിൽ നിന്ന് പുനർനാമകരണം ചെയ്തു",
    "changes.close": "അടയ്ക്കുക",
    "changes.prevChange": "മുമ്പത്തെ മാറ്റം",
    "changes.nextChange": "അടുത്ത മാറ്റം",
    "changes.collapseAll": "എല്ലാ ഫോൾഡറുകളും ചുരുക്കുക",
    "changes.expandAll": "എല്ലാ ഫോൾഡറുകളും വികസിപ്പിക്കുക",
    "changes.searchPlaceholder": "ഫയലുകൾ തിരയുക...",
    "changes.noSearchResults": "നിങ്ങളുടെ തിരയലുമായി പൊരുത്തപ്പെടുന്ന ഫയലുകളൊന്നുമില്ല.",
    "changes.maximize": "പരമാവധിയാക്കുക",
    "changes.restore": "വലിപ്പം പുനഃസ്ഥാപിക്കുക",
    "find.open": "ഈ വ്യത്യാസത്തിൽ തിരയുക (Ctrl+F)",
    "find.placeholder": "വ്യത്യാസത്തിൽ കണ്ടെത്തുക...",
    "find.prev": "മുമ്പത്തെ മത്സരം",
    "find.next": "അടുത്ത മത്സരം",
    "find.close": "തിരയൽ അടയ്ക്കുക",
    "find.noResults": "ഫലങ്ങളൊന്നുമില്ല",
    "commit.title": "മാറ്റങ്ങൾ വരുത്തുക",
    "commit.close": "അടയ്ക്കുക",
    "commit.loading": "ജോലി ചെയ്യുന്ന മരം വായിക്കുന്നു...",
    "commit.noChanges": "പ്രതിബദ്ധതയുള്ള പ്രാദേശിക മാറ്റങ്ങളൊന്നുമില്ല.",
    "commit.selectFile": "അതിൻ്റെ വ്യത്യാസം കാണുന്നതിന് ഒരു ഫയൽ തിരഞ്ഞെടുക്കുക.",
    "commit.selectAll": "എല്ലാം തിരഞ്ഞെടുക്കുക",
    "commit.selectNone": "എല്ലാം മായ്‌ക്കുക",
    "commit.message": "കമ്മിറ്റ് സന്ദേശം",
    "commit.messagePlaceholder": "മാറ്റം വിവരിക്കുക",
    "commit.selectedCount": "{count} ഫയൽ(കൾ) തിരഞ്ഞെടുത്തു",
    "commit.review": "അവലോകനം",
    "commit.reviewTitle": "കമ്മിറ്റ് ചെയ്യുന്നതിന് മുമ്പ് അന്തിമ പരിശോധന",
    "commit.reviewSummary": "സന്ദേശത്തോടുകൂടിയ {count} ഫയൽ(കൾ) സമർപ്പിക്കുക: {message}",
    "commit.back": "തിരികെ",
    "commit.commit": "പ്രാദേശിക പ്രതിബദ്ധത സൃഷ്ടിക്കുക",
    "menu.stashApply": "സ്റ്റാഷ് പ്രയോഗിക്കുക",
    "menu.stashPop": "പോപ്പ് സ്റ്റാഷ് (പ്രയോഗിച്ച് നീക്കം ചെയ്യുക)",
    "menu.stashDrop": "ഡ്രോപ്പ് സ്റ്റാഷ്...",
    "merge.title": "ബ്രാഞ്ച് ലയിപ്പിക്കുക",
    "merge.route": "നിലവിലെ ശാഖയിൽ ലയിക്കുന്നു",
    "merge.source": "(ഉറവിടം) നിന്ന്",
    "merge.target": "(നിലവിലെ)",
    "merge.loading": "ലയനം വിശകലനം ചെയ്യുന്നു...",
    "merge.upToDate": "ഇതിനകം കാലികമായി - ലയിപ്പിക്കാൻ ഒന്നുമില്ല.",
    "merge.fastForward": "ഫാസ്റ്റ് ഫോർവേഡ് - ലയന കമ്മിറ്റ് ആവശ്യമില്ല (ഒന്ന് നിർബന്ധിക്കാൻ \"ഒരു ലയന പ്രതിബദ്ധത സൃഷ്ടിക്കുക\" ടിക്ക് ചെയ്യുക).",
    "merge.summary": "{files} ഫയൽ(കൾ) മാറ്റി, {conflicts} വൈരുദ്ധ്യം(ങ്ങൾ).",
    "merge.conflictsWarning": "ഈ ലയനത്തിന് വൈരുദ്ധ്യങ്ങളുണ്ട് - നിങ്ങൾക്ക് ഇപ്പോഴും ലയിപ്പിക്കാം, തുടർന്ന് അവ എഡിറ്ററിൽ പരിഹരിക്കാം.",
    "merge.noChanges": "ഫയൽ മാറ്റങ്ങളൊന്നുമില്ല.",
    "merge.files": "തത്ഫലമായുണ്ടാകുന്ന മാറ്റങ്ങൾ",
    "merge.added": "ചേർത്തു",
    "merge.modified": "പരിഷ്കരിച്ചു",
    "merge.deleted": "ഇല്ലാതാക്കി",
    "merge.conflict": "സംഘർഷം",
    "merge.message": "കമ്മിറ്റ് സന്ദേശം ലയിപ്പിക്കുക",
    "merge.messagePlaceholder": "ശാഖ 'ഉറവിടം' ലയിപ്പിക്കുക",
    "merge.messageFfHint": "ഫാസ്റ്റ് ഫോർവേഡ് ലയനത്തിൽ അവഗണിച്ചു (പ്രതിബദ്ധതയൊന്നും സൃഷ്ടിക്കപ്പെട്ടിട്ടില്ല).",
    "merge.noFastForward": "ഒരു ലയന പ്രതിബദ്ധത സൃഷ്‌ടിക്കുക (ഫാസ്റ്റ് ഫോർവേഡ് ഇല്ല)",
    "merge.noFastForwardHint": "ബ്രാഞ്ചിന് ഫാസ്റ്റ് ഫോർവേഡ് ചെയ്യാൻ കഴിയുമെങ്കിലും, എപ്പോഴും ഒരു ലയന പ്രതിബദ്ധത രേഖപ്പെടുത്തുക.",
    "merge.merge": "ലയിപ്പിക്കുക",
    "merge.cancel": "റദ്ദാക്കുക",
    "merge.previewError": "ഒരു പ്രിവ്യൂ കണക്കാക്കാൻ കഴിഞ്ഞില്ല: {message}",
    "merge.squashBadge": "സ്ക്വാഷ്",
    "merge.squashNote": "സ്ക്വാഷ് ലയനം - \"{target}\"-ൽ ഒരു പ്രതിജ്ഞാബദ്ധത പോലെ ഓരോ മാറ്റവും സംഭവിക്കുന്നു; “{source}”-ൻ്റെ സ്വന്തം കമ്മിറ്റുകൾ അതിൻ്റെ ചരിത്രത്തിൽ നിന്ന് വിട്ടുനിൽക്കുന്നു. ക്രമീകരണങ്ങൾ › ലയിപ്പിക്കുക എന്നതിൽ തിരികെ മാറുക.",
    "merge.messageSquash": "കമ്മിറ്റ് സന്ദേശം",
    "merge.squashMessagePlaceholder": "'ഉറവിടത്തിൽ' നിന്നുള്ള സ്ക്വാഷ് ചെയ്ത മാറ്റങ്ങൾ",
    "newBranch.title": "ബ്രാഞ്ച് ഉണ്ടാക്കുക",
    "newBranch.startPoint": "{sha} മുതൽ ആരംഭിക്കുന്ന പുതിയ ശാഖ",
    "newBranch.startPointOn": "{refs}-ൽ",
    "newBranch.location": "സ്ഥാനം",
    "newBranch.locationRoot": "(റൂട്ട്)",
    "newBranch.expandAll": "എല്ലാം വികസിപ്പിക്കുക",
    "newBranch.collapseAll": "എല്ലാം ചുരുക്കുക",
    "newBranch.expandFolder": "ഫോൾഡർ വികസിപ്പിക്കുക",
    "newBranch.collapseFolder": "ഫോൾഡർ ചുരുക്കുക",
    "newBranch.name": "ശാഖയുടെ പേര്",
    "newBranch.namePlaceholder": "എൻ്റെ ശാഖ",
    "newBranch.fullName": "മുഴുവൻ പേര്: {name}",
    "newBranch.invalid": "ശാഖയുടെ പേര് അസാധുവാണ്",
    "newBranch.exists": "ഈ പേരിലുള്ള ഒരു ശാഖ ഇതിനകം നിലവിലുണ്ട്",
    "newBranch.checkout": "സൃഷ്ടിച്ചതിനുശേഷം ബ്രാഞ്ച് ചെക്ക്ഔട്ട് ചെയ്യുക",
    "newBranch.create": "സൃഷ്ടിക്കുക",
    "newBranch.cancel": "റദ്ദാക്കുക",
    "status.loading": "ഗ്രാഫ് ലോഡ് ചെയ്യുന്നു...",
    "status.summary": "{repo} {commits} പ്രതിബദ്ധത കാണിക്കുന്നു, {refs} സൂചിപ്പിക്കുന്നു",
    "status.branchCreated": "{sha}-ൽ \"{name}\" ബ്രാഞ്ച് സൃഷ്ടിച്ചു",
    "status.error": "പിശക്: {message}",
    "status.opFailed": "ഓപ്പറേഷൻ പരാജയപ്പെട്ടു",
    "status.fetching": "ലഭ്യമാക്കുന്നു...",
    "status.pulling": "വലിക്കുന്നു...",
    "status.pushing": "തള്ളുന്നു...",
    "status.committing": "പ്രാദേശിക പ്രതിബദ്ധത സൃഷ്ടിക്കുന്നു...",
    "status.commitCreated": "സൃഷ്ടിച്ച പ്രാദേശിക പ്രതിബദ്ധത {sha}: {message}",
    "status.syncing": "സമന്വയിപ്പിക്കുന്നു...",
    "status.noHead": "ഗ്രാഫിൽ നിലവിലെ ചെക്ക്ഔട്ടൊന്നും കണ്ടെത്തിയില്ല.",
    "status.undoing": "പ്രതിബദ്ധത പഴയപടിയാക്കുന്നു...",
    "status.commitUndone": "പ്രതിജ്ഞാബദ്ധത പഴയപടിയാക്കി - പ്രവർത്തിക്കുന്ന ട്രീയിൽ മാറ്റങ്ങൾ തിരിച്ചെത്തി.",
    "status.undoConflict": "ഹിറ്റ് വൈരുദ്ധ്യങ്ങൾ പഴയപടിയാക്കുക - എഡിറ്ററിൽ അവ പരിഹരിക്കുക, തുടർന്ന് തുടരുക.",
    "status.stashApplying": "സ്റ്റാഷ് പ്രയോഗിക്കുന്നു...",
    "status.stashPopping": "പോപ്പിംഗ് സ്റ്റാഷ്…",
    "status.stashDropping": "നിക്ഷേപം ഉപേക്ഷിക്കുന്നു...",
    "status.stashApplied": "സ്റ്റാഷ് പ്രയോഗിച്ചു.",
    "status.stashPopped": "സ്റ്റാഷ് പോപ്പ് ചെയ്തു.",
    "status.stashDropped": "സ്റ്റാഷ് ഇടിഞ്ഞു.",
    "status.stashConflict": "സ്റ്റാഷ് വൈരുദ്ധ്യങ്ങൾ - എഡിറ്ററിൽ അവ പരിഹരിക്കുക.",
    "status.merging": "ലയിപ്പിക്കുന്നു...",
    "status.merged": "ലയനം പൂർത്തിയായി.",
    "status.mergeConflict": "ലയിപ്പിക്കുന്നതിന് വൈരുദ്ധ്യങ്ങളുണ്ട് - അവ എഡിറ്ററിൽ പരിഹരിക്കുക, തുടർന്ന് പ്രതിജ്ഞാബദ്ധമാക്കുക.",
    "search.title": "തിരയുക",
    "search.tooltip": "തിരയൽ കമ്മിറ്റ് ചെയ്യുന്നു",
    "search.placeholder": "തിരയൽ പ്രതിബദ്ധത…",
    "search.previous": "മുൻ ഫലം",
    "search.next": "അടുത്ത ഫലം",
    "search.noResults": "ഫലങ്ങളൊന്നുമില്ല",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ഫിൽട്ടർ മോഡ്",
    "search.highlight": "ഹൈലൈറ്റ് മോഡ്",
    "search.filterMode": "തിരയൽ ഡിസ്പ്ലേ",
    "search.filterModeFilter": "ഫിൽട്ടർ ചെയ്യുക (പൊരുത്തമില്ലാത്തവ മറയ്ക്കുക)",
    "search.filterModeHighlight": "ഹൈലൈറ്റ് ചെയ്യുക (എല്ലാം കാണിക്കുക, പൊരുത്തങ്ങൾ അടയാളപ്പെടുത്തുക)",
    "search.filterModeHint": "ഗ്രാഫിൽ തിരയൽ ഫലങ്ങൾ എങ്ങനെ പ്രദർശിപ്പിക്കണമെന്ന് തിരഞ്ഞെടുക്കുക.",
    "search.visibility": "ലൊക്കേഷൻ തിരയുക",
    "search.visibilityToolbar": "ടൂൾബാർ ബട്ടൺ",
    "search.visibilityAlwaysVisible": "എപ്പോഴും കാണാവുന്ന ബാർ",
    "search.visibilityHint": "മുകളിൽ ഒരു ടൂൾബാർ ഐക്കൺ അല്ലെങ്കിൽ പെർസിസ്റ്റൻ്റ് ബാർ ആയി തിരയൽ കാണിക്കുക.",
    "footer.github": "GitHub",
  },
  si: {
    "toolbar.refresh": "⟳ නැවුම් කරන්න",
    "toolbar.fetch": "⤓ ගන්න",
    "toolbar.pull": "⇩ අදින්න",
    "toolbar.push": "⇧ තල්ලු කරන්න",
    "toolbar.commit": "✓ කැපවීම",
    "toolbar.sync": "⇅ සමමුහුර්ත කරන්න",
    "toolbar.jumpHead": "⌖ පිටවීම වෙත යන්න",
    "toolbar.reset": "⤢ දර්ශනය යළි පිහිටුවන්න",
    "toolbar.settings": "⚙ සැකසීම්",
    "details.header": "බැඳීම් විස්තර",
    "details.close": "වසන්න",
    "details.sha": "SHA",
    "details.shortSha": "කෙටි SHA",
    "details.message": "පණිවිඩය",
    "details.author": "කර්තෘ",
    "details.date": "දිනය",
    "details.labels": "ලේබල්",
    "details.location": "ස්ථානය",
    "details.currentHead": "HEAD - වත්මන් පිටවීම",
    "settings.title": "සැකසීම්",
    "settings.language": "භාෂාව",
    "settings.theme": "තේමාව",
    "settings.themeLight": "ආලෝකය",
    "settings.themeDark": "අඳුරු",
    "settings.themeLightHint": "සැහැල්ලු පසුබිම, අඳුරු පෙළ.",
    "settings.themeDarkHint": "අඳුරු පසුබිම, සැහැල්ලු පෙළ.",
    "settings.mainBranch": "ප්රධාන ශාඛාව",
    "settings.mainBranchAuto": "ස්වයංක්රීය",
    "settings.mainBranchSearch": "ශාඛා සොයන්න...",
    "settings.mainBranchNoMatch": "ගැලපෙන ශාඛා නොමැත",
    "settings.display": "සංදර්ශක විලාසය",
    "settings.displayModern": "නවීන",
    "settings.displayClassic": "සම්භාව්ය",
    "settings.displayModernHint": "නිදහස් කැන්වසය - පෑන් කිරීමට අදින්න, විශාලනය කිරීමට අනුචලනය කරන්න.",
    "settings.displayClassicHint": "ස්ථාවර කැන්වසය - කඳ වමට ඇමිණීම, විශාලනය නැත, අනුචලනය පමණක් (SVN සංශෝධන ප්‍රස්තාරය වැනි).",
    "settings.sectionGeneral": "ජෙනරාල්",
    "settings.sectionGraph": "ප්‍රස්තාරය",
    "settings.sectionChanges": "දර්ශනය වෙනස් කරයි",
    "settings.diffMinimap": "වෙනස අවම සිතියම",
    "settings.diffMinimapOn": "පෙන්වා ඇත",
    "settings.diffMinimapOff": "සැඟවී ඇත",
    "settings.diffMinimapOnHint": "වෙනස අසල VS කේත මාදිලියේ දළ විශ්ලේෂණ තීරුවක් පෙන්වන්න - අනුචලනය කිරීමට එය අදින්න; වෙනස් සලකුණු ඇතුළත්.",
    "settings.diffMinimapOffHint": "දළ විශ්ලේෂණ තීරුවක් නැත - වෙනස සාමාන්‍යයෙන් අනුචලනය කරන්න.",
    "settings.commitReview": "සමාලෝචනය කැප කරන්න",
    "settings.commitReviewOn": "කැපවීමට පෙර විමසන්න",
    "settings.commitReviewOff": "කෙලින්ම කැපවෙන්න",
    "settings.commitReviewOnHint": "දේශීය කැපවීම සෑදීමට පෙර අවසාන ගොනු ලැයිස්තුවක් සහ පණිවිඩ පරීක්ෂාවක් පෙන්වන්න.",
    "settings.commitReviewOffHint": "Commit බොත්තම වහාම දේශීය කැපවීම නිර්මාණය කරයි.",
    "settings.svnBranchDialog": "SVN මාදිලියේ ශාඛා සංවාදය",
    "settings.svnBranchDialogHint": "ශාඛාවක් නිර්මාණය කිරීමේදී ෆෝල්ඩර-ගස් පිකර් එකක් පෙන්වන්න (සරල විමසුමක් වෙනුවට).",
    "settings.branchDialog": "ශාඛා සංවාදය",
    "settings.branchDialogSvnTitle": "SVN-විලාසය",
    "settings.branchDialogNativeVscode": "VS කේත ශෛලිය",
    "settings.branchDialogNativeVs": "විෂුවල් ස්ටුඩියෝ විලාසය",
    "settings.branchDialogNativeHint": "ඔබගේ IDE හි බිල්ට් ශාඛා විමසුම.",
    "settings.close": "වසන්න",
    "settings.done": "කළා",
    "settings.sectionAdvanced": "උසස්",
    "settings.gitSource": "Git ක්‍රියාත්මක කළ හැකි",
    "settings.gitSourceBuiltin": "බිල්ට්-ඉන්",
    "settings.gitSourceBuiltinHint": "IDE හි ඇති Git දිගුවෙන් git ද්විමය භාවිතා කරන්න.",
    "settings.gitSourceCustom": "අභිරුචි මාර්ගය",
    "settings.gitPath": "git වෙත මාර්ගය",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "බ්‍රවුස් කරන්න...",
    "settings.jargon": "Git කොන්දේසි",
    "settings.jargonTranslate": "පරිවර්තනය කරන්න",
    "settings.jargonEnglish": "ඉංග්රීසි භාෂාවෙන් තබා ගන්න",
    "settings.jargonTranslateHint": "Git පද (අදින්න, තල්ලු කරන්න, කැපවීම, ශාඛාව...) අතුරු මුහුණත භාෂාවට පරිවර්තනය කරන්න.",
    "settings.jargonEnglishHint": "Git පද (අදින්න, තල්ලු කරන්න, කැප කරන්න, ශාඛාව...) ඉංග්‍රීසියෙන් තබා ගන්න; අනෙක් සියල්ල පරිවර්තනය කරන්න.",
    "settings.sectionMerge": "ඒකාබද්ධ කරන්න",
    "settings.mergeMode": "විලාසය ඒකාබද්ධ කරන්න",
    "settings.mergeModeMerge": "සාමාන්ය ඒකාබද්ධ කිරීම",
    "settings.mergeModeSquash": "ස්කොෂ් ඒකාබද්ධ",
    "settings.mergeModeMergeHint": "ශාඛාව එලෙසම ඒකාබද්ධ කරයි: එහි කැපවීම් වත්මන් ශාඛාවේ ඉතිහාසයේ කොටසක් බවට පත් වේ, ඒකාබද්ධ කිරීමේ කැපවීමකින් සම්බන්ධ වේ (හෝ හැකි විට වේගයෙන් ඉදිරියට යවනු ලැබේ).",
    "settings.mergeModeSquashHint": "වත්මන් ශාඛාවේ (git merge --squash) සම්පූර්ණ ශාඛාව තනි කැපවීමකට හකුළයි. ශාඛාවේම කැපවීම් වත්මන් ශාඛාවේ ඉතිහාසයට කිසිදා ඇතුල් නොවේ - ඔබ එහි දකින්නේ එක් කැපවීමක් පමණි. ශාඛාව ස්පර්ශ නොවී ඉතිරිව ඇත, එබැවින් එහි කැපවීම් එහිම මංතීරුවේ දෘශ්‍යමාන වේ.",
    "settings.mergedView": "ප්‍රස්ථාරයේ ඒකාබද්ධ කළ කැපවීම්",
    "settings.mergedViewBranch": "ඔවුන්ගේම ශාඛාවේ පමණි",
    "settings.mergedViewTarget": "ශාඛාව තුළ ද ඔවුන් ඒකාබද්ධ විය",
    "settings.mergedViewBranchHint": "ඒකාබද්ධ කරන ලද ශාඛාවක වගකීම් අඳිනු ලබන්නේ ඔවුන්ගේම මංතීරුවේ පමණි; ඒකාබද්ධ කිරීම සම්බන්ධකයක් ලෙස පෙන්වයි. සම්භාව්ය දර්ශනය.",
    "settings.mergedViewTargetHint": "ඒකාබද්ධ කිරීමක් ගෙන එන ලද බැඳීම් ලබන ශාඛාවේ මංතීරුවේ ද ඇඳ ඇත, ඒකාබද්ධ කිරීමේ කැපවීම යටතේ ගොඩ ගසා මුල් පිටපත් සමඟ මට්ටම් කරයි - මන්ද එම ශාඛාවේ දැන් ඒවා අඩංගු වේ. පිටපත් සුදුමැලි වන අතර ඒවා ලියා ඇති ශාඛාව සමඟින් පිහිටා ඇත, එබැවින් ඒවා එහි සාදා නොතිබූ බව පැහැදිලිය. ස්කොෂ් ඒකාබද්ධ කිරීම් බල නොපායි: ස්කොෂ් එක සාමාන්‍ය කැපවීමක් ලියයි, එබැවින් එම කැපවීම පමණක් දිස්වේ.",
    "legend.title": "පුරාවෘත්තය",
    "legend.head": "HEAD / වත්මන් ශාඛාව",
    "legend.local": "දේශීය ශාඛාව",
    "legend.remote": "දුරස්ථ ශාඛාව",
    "legend.remoteOnly": "වලාකුළේ පමණි (ඇදගෙන නැත)",
    "legend.tag": "ටැගය (අනුවාදය)",
    "legend.commit": "කැපවෙන්න",
    "legend.stash": "ස්ටෑෂ් (රාක්ක වැඩ)",
    "legend.mergedIn": "ඒකාබද්ධයක් සමඟ පැමිණියේය (වෙනත් ශාඛාවක ලියා ඇත)",
    "legend.nodes": "පෙට්ටි",
    "legend.lines": "රේඛා",
    "legend.edgeParent": "එකම රේඛාවක පෙර කැපවීම",
    "legend.edgeMerge": "ඒකාබද්ධ කරන ලද ශාඛාව - ඒකාබද්ධ කිරීමෙහි ඇති ඊතලය",
    "legend.edgeBranch": "අත්තක් මෙතනින් ගැලවිලා ගියා",
    "legend.edgeStash": "ස්ටාෂ් සහ එය අත්හැර දමන ලද කැපවීම",
    "legend.edgeMergedChain": "මෙම ශාඛාවේ රේඛාව ඒකාබද්ධ කිරීම හරහා දිව යයි",
    "legend.edgeMergedTie": "එය ලියා ඇති ශාඛාවේ එකම කැපවීම",
    "menu.jumpHead": "⌖ පිටවීම වෙත යන්න",
    "menu.resetView": "⤢ දර්ශනය යළි පිහිටුවන්න",
    "menu.createBranch": "මෙහි ශාඛාවක් සාදන්න...",
    "menu.checkout": "මෙම කැපවීම පරීක්ෂා කරන්න",
    "menu.copySha": "SHA පිටපත් කරන්න",
    "menu.pushBranch": "තල්ලු ශාඛාව \"{name}\"",
    "menu.renameCommit": "කැපවීමේ පණිවිඩය නැවත නම් කරන්න...",
    "menu.undoCommit": "කැපවීම අහෝසි කරන්න (වෙනස්කම් තබා ගන්න)...",
    "menu.viewChanges": "වෙනස්කම් බලන්න...",
    "menu.jumpToOriginal": "මුල් කැපවීම වෙත යන්න",
    "changes.title": "{sha} හි වෙනස්කම්",
    "changes.tabChanged": "වෙනස් කළා",
    "changes.tabAll": "සියලුම ගොනු",
    "changes.added": "එකතු කරන ලදී",
    "changes.modified": "වෙනස් කර ඇත",
    "changes.deleted": "මකා දමන ලදී",
    "changes.renamed": "නැවත නම් කරන ලදී",
    "changes.noChanges": "මෙම වගකීම ගොනු කිසිවක් වෙනස් නොකරයි.",
    "changes.loading": "වෙනස පූරණය වෙමින්...",
    "changes.selectFile": "එහි වෙනස්කම් බැලීමට ගොනුවක් තෝරන්න.",
    "changes.original": "මුල්",
    "changes.changed": "මෙම කැපවීම",
    "changes.binary": "ද්විමය ගොනුව - පෙන්වීමට පෙළ වෙනසක් නැත.",
    "changes.tooLarge": "ගොනුව වෙනස් කිරීමට විශාල වැඩිය.",
    "changes.renamedFrom": "{path} වෙතින් නැවත නම් කරන ලදී",
    "changes.close": "වසන්න",
    "changes.prevChange": "පෙර වෙනස",
    "changes.nextChange": "ඊළඟ වෙනස",
    "changes.collapseAll": "සියලුම ෆෝල්ඩර හකුළන්න",
    "changes.expandAll": "සියලුම ෆෝල්ඩර පුළුල් කරන්න",
    "changes.searchPlaceholder": "ගොනු සොයන්න...",
    "changes.noSearchResults": "ඔබගේ සෙවුමට ගැලපෙන ගොනු නොමැත.",
    "changes.maximize": "උපරිම කරන්න",
    "changes.restore": "ප්රමාණය ප්රතිෂ්ඨාපනය කරන්න",
    "find.open": "මෙම වෙනසෙහි සොයන්න (Ctrl+F)",
    "find.placeholder": "වෙනස තුළ සොයන්න...",
    "find.prev": "පෙර තරගය",
    "find.next": "ඊළඟ තරගය",
    "find.close": "සෙවුම වසන්න",
    "find.noResults": "ප්‍රතිඵල නැත",
    "commit.title": "වෙනස්කම් කරන්න",
    "commit.close": "වසන්න",
    "commit.loading": "වැඩ කරන ගස කියවීම…",
    "commit.noChanges": "කිරීමට දේශීය වෙනස්කම් නොමැත.",
    "commit.selectFile": "එහි වෙනස බැලීමට ගොනුවක් තෝරන්න.",
    "commit.selectAll": "සියල්ල තෝරන්න",
    "commit.selectNone": "සියල්ල හිස් කරන්න",
    "commit.message": "පණිවිඩය කැප කරන්න",
    "commit.messagePlaceholder": "වෙනස විස්තර කරන්න",
    "commit.selectedCount": "{count} ගොනු(ය) තෝරා ඇත",
    "commit.review": "සමාලෝචනය කරන්න",
    "commit.reviewTitle": "කැපවීමට පෙර අවසාන පරීක්ෂාව",
    "commit.reviewSummary": "පණිවිඩය සමඟ {count} ගොනු(ය) කැප කරන්න: {message}",
    "commit.back": "ආපසු",
    "commit.commit": "දේශීය කැපවීම සාදන්න",
    "menu.stashApply": "ස්ටෑෂ් යොදන්න",
    "menu.stashPop": "පොප් ස්ටෑෂ් (අයදුම් කර ඉවත් කරන්න)",
    "menu.stashDrop": "ගබඩාව අතහරින්න...",
    "merge.title": "ශාඛාව ඒකාබද්ධ කරන්න",
    "merge.route": "වත්මන් ශාඛාවට ඒකාබද්ධ කිරීම",
    "merge.source": "(මූලාශ්රය) වෙතින්",
    "merge.target": "තුලට (වත්මන්)",
    "merge.loading": "ඒකාබද්ධ කිරීම විශ්ලේෂණය කරමින්...",
    "merge.upToDate": "දැනටමත් යාවත්කාලීන - ඒකාබද්ධ කිරීමට කිසිවක් නැත.",
    "merge.fastForward": "වේගයෙන් ඉදිරියට - ඒකාබද්ධ කැපවීමක් අවශ්‍ය නොවේ (එකක් බල කිරීමට \"ඒකාබද්ධ කැපවීමක් සාදන්න\" සලකුණු කරන්න).",
    "merge.summary": "{files} ගොනු(ය) වෙනස් විය, {conflicts} ගැටුම්(ය).",
    "merge.conflictsWarning": "මෙම ඒකාබද්ධ කිරීමෙහි ගැටුම් ඇත - ඔබට තවමත් ඒකාබද්ධ කළ හැක, පසුව ඒවා සංස්කාරකය තුළ විසඳාගත හැක.",
    "merge.noChanges": "ගොනු වෙනස්කම් නොමැත.",
    "merge.files": "ප්රතිඵල වෙනස්කම්",
    "merge.added": "එකතු කරන ලදී",
    "merge.modified": "වෙනස් කර ඇත",
    "merge.deleted": "මකා දමන ලදී",
    "merge.conflict": "ගැටුම",
    "merge.message": "කැපවීමේ පණිවිඩය ඒකාබද්ධ කරන්න",
    "merge.messagePlaceholder": "ශාඛාව 'මූලාශ්රය' ඒකාබද්ධ කරන්න",
    "merge.messageFfHint": "වේගවත්-ඉදිරි ඒකාබද්ධ කිරීමකදී නොසලකා හරින ලදී (කිසිදු කැපවීමක් නිර්මාණය නොවේ).",
    "merge.noFastForward": "ඒකාබද්ධ කැපවීමක් සාදන්න (ඉක්මන්-ඉදිරියක් නැත)",
    "merge.noFastForwardHint": "ශාඛාව වේගයෙන් ඉදිරියට යා හැකි විට පවා, සෑම විටම ඒකාබද්ධ කිරීමේ කැපවීමක් වාර්තා කරන්න.",
    "merge.merge": "ඒකාබද්ධ කරන්න",
    "merge.cancel": "අවලංගු කරන්න",
    "merge.previewError": "පෙරදසුනක් ගණනය කළ නොහැකි විය: {message}",
    "merge.squashBadge": "ස්කොශ්",
    "merge.squashNote": "ස්කොෂ් ඒකාබද්ධ කිරීම - සෑම වෙනස්කමක්ම \"{target}\" මත එක් අයෙකු විසින් සිදු කරනු ලැබේ; “{source}”ගේම කැපවීම් එහි ඉතිහාසයෙන් බැහැරව සිටියි. Settings › Merge හි ආපසු මාරු වන්න.",
    "merge.messageSquash": "පණිවිඩය කැප කරන්න",
    "merge.squashMessagePlaceholder": "'මූලාශ්‍රයෙන්' පොඩි කළ වෙනස්කම්",
    "newBranch.title": "ශාඛාවක් සාදන්න",
    "newBranch.startPoint": "{sha} සිට ආරම්භ වන නව ශාඛාව",
    "newBranch.startPointOn": "{refs} මත",
    "newBranch.location": "ස්ථානය",
    "newBranch.locationRoot": "(මූල)",
    "newBranch.expandAll": "සියල්ල පුළුල් කරන්න",
    "newBranch.collapseAll": "සියල්ල හකුළන්න",
    "newBranch.expandFolder": "ෆෝල්ඩරය පුළුල් කරන්න",
    "newBranch.collapseFolder": "ෆෝල්ඩරය හකුළන්න",
    "newBranch.name": "ශාඛාවේ නම",
    "newBranch.namePlaceholder": "මගේ ශාඛාව",
    "newBranch.fullName": "සම්පූර්ණ නම: {name}",
    "newBranch.invalid": "වලංගු නොවන ශාඛා නාමය",
    "newBranch.exists": "මෙම නම සහිත ශාඛාවක් දැනටමත් පවතී",
    "newBranch.checkout": "නිර්මාණය කිරීමෙන් පසු ශාඛාව පිරික්සන්න",
    "newBranch.create": "නිර්මාණය කරන්න",
    "newBranch.cancel": "අවලංගු කරන්න",
    "status.loading": "ප්‍රස්තාරය පූරණය කරමින්...",
    "status.summary": "{repo} {commits} කැපවීම් පෙන්වමින්, {refs} යොමු කරයි",
    "status.branchCreated": "{sha} හි \"{name}\" ශාඛාව සාදන ලදී",
    "status.error": "දෝෂය: {message}",
    "status.opFailed": "මෙහෙයුම අසාර්ථක විය",
    "status.fetching": "ලබා ගනිමින්...",
    "status.pulling": "අදින්නේ...",
    "status.pushing": "තල්ලු කරමින්...",
    "status.committing": "දේශීය කැපවීම නිර්මාණය කරමින්...",
    "status.commitCreated": "සාදන ලද දේශීය කැපවීම {sha}: {message}",
    "status.syncing": "සමමුහුර්ත කරමින්...",
    "status.noHead": "ප්‍රස්ථාරයේ වත්මන් පිටවීමක් හමු නොවීය.",
    "status.undoing": "කැපවීම අහෝසි කරමින්...",
    "status.commitUndone": "කැපවීම අවලංගුයි - වැඩ කරන ගසෙහි වෙනස්කම් නැවත පැමිණේ.",
    "status.undoConflict": "පහර ගැටුම් අහෝසි කරන්න - ඒවා සංස්කාරකය තුළ විසඳන්න, ඉන්පසු දිගටම කරගෙන යන්න.",
    "status.stashApplying": "ගබඩාව යොදමින්...",
    "status.stashPopping": "පොපිං තොගය...",
    "status.stashDropping": "තොග අතහරිනවා...",
    "status.stashApplied": "Stash යොදන ලදී.",
    "status.stashPopped": "ස්ටාෂ් මතු විය.",
    "status.stashDropped": "ස්ටාෂ් පහත වැටුණි.",
    "status.stashConflict": "ස්ටෑෂ් ගැටුම් - ඒවා සංස්කාරකයේ විසඳන්න.",
    "status.merging": "ඒකාබද්ධ කරමින්...",
    "status.merged": "ඒකාබද්ධ කිරීම සම්පූර්ණයි.",
    "status.mergeConflict": "ඒකාබද්ධ කිරීමට ගැටුම් ඇත - ඒවා සංස්කාරකයේ විසඳා, පසුව කැප කරන්න.",
    "search.title": "සොයන්න",
    "search.tooltip": "සෙවුම් කැප කරයි",
    "search.placeholder": "සෙවුම් සිදු කරයි…",
    "search.previous": "පෙර ප්රතිඵලය",
    "search.next": "ඊළඟ ප්රතිඵලය",
    "search.noResults": "ප්‍රතිඵල නැත",
    "search.resultCount": "{index}/{count}",
    "search.filter": "පෙරහන් මාදිලිය",
    "search.highlight": "උද්දීපන මාදිලිය",
    "search.filterMode": "සෙවුම් සංදර්ශකය",
    "search.filterModeFilter": "පෙරහන (නොගැලපීම් සඟවන්න)",
    "search.filterModeHighlight": "උද්දීපනය කරන්න (සියල්ල පෙන්වන්න, ගැලපීම් ලකුණු කරන්න)",
    "search.filterModeHint": "ප්‍රස්ථාරයේ සෙවුම් ප්‍රතිඵල පෙන්වන ආකාරය තෝරන්න.",
    "search.visibility": "ස්ථානය සොයන්න",
    "search.visibilityToolbar": "මෙවලම් තීරු බොත්තම",
    "search.visibilityAlwaysVisible": "සෑම විටම පෙනෙන තීරුව",
    "search.visibilityHint": "සෙවුම මෙවලම් තීරු නිරූපකයක් ලෙස හෝ ඉහළින්ම පවතින තීරුවක් ලෙස පෙන්වන්න.",
    "footer.github": "GitHub",
  },
  th: {
    "toolbar.refresh": "⟳ รีเฟรช",
    "toolbar.fetch": "⤓ ดึงข้อมูล",
    "toolbar.pull": "⇩ ดึง",
    "toolbar.push": "⇧ ดัน",
    "toolbar.commit": "✓ มุ่งมั่น",
    "toolbar.sync": "⇅ ซิงค์",
    "toolbar.jumpHead": "⌖ ไปที่การชำระเงิน",
    "toolbar.reset": "⤢ รีเซ็ตมุมมอง",
    "toolbar.settings": "⚙ การตั้งค่า",
    "details.header": "รายละเอียดความมุ่งมั่น",
    "details.close": "ปิด",
    "details.sha": "ชะอำ",
    "details.shortSha": "SHA แบบสั้น",
    "details.message": "ข้อความ",
    "details.author": "ผู้เขียน",
    "details.date": "วันที่",
    "details.labels": "ป้ายกำกับ",
    "details.location": "ที่ตั้ง",
    "details.currentHead": "HEAD - การชำระเงินปัจจุบัน",
    "settings.title": "การตั้งค่า",
    "settings.language": "ภาษา",
    "settings.theme": "ธีม",
    "settings.themeLight": "แสงสว่าง",
    "settings.themeDark": "มืด",
    "settings.themeLightHint": "พื้นหลังสีอ่อน ข้อความสีเข้ม",
    "settings.themeDarkHint": "พื้นหลังสีเข้ม ข้อความสีอ่อน",
    "settings.mainBranch": "สาขาหลัก",
    "settings.mainBranchAuto": "อัตโนมัติ",
    "settings.mainBranchSearch": "ค้นหาสาขา…",
    "settings.mainBranchNoMatch": "ไม่มีสาขาที่ตรงกัน",
    "settings.display": "สไตล์การแสดงผล",
    "settings.displayModern": "ทันสมัย",
    "settings.displayClassic": "คลาสสิค",
    "settings.displayModernHint": "แคนวาสฟรี — ลากเพื่อแพน เลื่อนเพื่อซูม",
    "settings.displayClassicHint": "ผ้าใบคงที่ — ท้ายรถปักหมุดไว้ทางซ้าย ไม่มีการซูม เลื่อนเท่านั้น (เช่น กราฟการแก้ไข SVN)",
    "settings.sectionGeneral": "ทั่วไป",
    "settings.sectionGraph": "กราฟ",
    "settings.sectionChanges": "เปลี่ยนมุมมอง",
    "settings.diffMinimap": "แผนที่ย่อส่วนต่าง",
    "settings.diffMinimapOn": "แสดงแล้ว",
    "settings.diffMinimapOff": "ที่ซ่อนอยู่",
    "settings.diffMinimapOnHint": "แสดงแถบภาพรวมสไตล์ VS Code ข้างส่วนต่าง — ลากเพื่อเลื่อน รวมเครื่องหมายการเปลี่ยนแปลง",
    "settings.diffMinimapOffHint": "ไม่มีแถบภาพรวม — เลื่อนส่วนต่างตามปกติ",
    "settings.commitReview": "มุ่งมั่นทบทวน",
    "settings.commitReviewOn": "ถามก่อนที่จะกระทำ",
    "settings.commitReviewOff": "กระทำโดยตรง",
    "settings.commitReviewOnHint": "แสดงรายการไฟล์สุดท้ายและการตรวจสอบข้อความก่อนสร้างการคอมมิตในเครื่อง",
    "settings.commitReviewOffHint": "ปุ่ม Commit จะสร้างการคอมมิตในเครื่องทันที",
    "settings.svnBranchDialog": "กล่องโต้ตอบสาขาสไตล์ SVN",
    "settings.svnBranchDialogHint": "แสดงเครื่องมือเลือกแผนผังโฟลเดอร์เมื่อสร้างสาขา (แทนการแจ้งแบบธรรมดา)",
    "settings.branchDialog": "กล่องโต้ตอบสาขา",
    "settings.branchDialogSvnTitle": "สไตล์ SVN",
    "settings.branchDialogNativeVscode": "สไตล์ VS Code",
    "settings.branchDialogNativeVs": "สไตล์วิชวลสตูดิโอ",
    "settings.branchDialogNativeHint": "พรอมต์สาขาในตัวของ IDE ของคุณ",
    "settings.close": "ปิด",
    "settings.done": "เสร็จแล้ว",
    "settings.sectionAdvanced": "ขั้นสูง",
    "settings.gitSource": "Git ปฏิบัติการได้",
    "settings.gitSourceBuiltin": "บิวท์อิน",
    "settings.gitSourceBuiltinHint": "ใช้ไบนารี git จากส่วนขยาย Git ในตัวของ IDE",
    "settings.gitSourceCustom": "เส้นทางที่กำหนดเอง",
    "settings.gitPath": "เส้นทางสู่คอมไพล์",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "เรียกดู...",
    "settings.jargon": "เงื่อนไขคอมไพล์",
    "settings.jargonTranslate": "แปล",
    "settings.jargonEnglish": "เอาไว้เป็นภาษาอังกฤษ",
    "settings.jargonTranslateHint": "แปลคำศัพท์ Git (pull, push, commit, Branch…) เป็นภาษาอินเทอร์เฟซ",
    "settings.jargonEnglishHint": "รักษาเงื่อนไข Git (pull, push, commit, Branch…) เป็นภาษาอังกฤษ แปลอย่างอื่นทั้งหมด",
    "settings.sectionMerge": "ผสาน",
    "settings.mergeMode": "ผสานสไตล์",
    "settings.mergeModeMerge": "ผสานปกติ",
    "settings.mergeModeSquash": "สควอชผสาน",
    "settings.mergeModeMergeHint": "รวมสาขาตามที่เป็นอยู่: การคอมมิตกลายเป็นส่วนหนึ่งของประวัติของสาขาปัจจุบัน เข้าร่วมด้วยการคอมมิตแบบผสาน (หรือกรอไปข้างหน้าเมื่อเป็นไปได้)",
    "settings.mergeModeSquashHint": "ยุบสาขาทั้งหมดเป็นการกระทำเดียวในสาขาปัจจุบัน (git Merge --squash) Commit ของ Branch จะไม่เข้าสู่ประวัติของ Branch ปัจจุบัน — คุณจะเห็นเพียง Commit เดียวเท่านั้นที่นั่น ตัวสาขานั้นไม่ถูกแตะต้อง ดังนั้นการกระทำของมันจึงยังคงมองเห็นได้ในเลนของมันเอง",
    "settings.mergedView": "การรวมคอมมิตในกราฟ",
    "settings.mergedViewBranch": "เฉพาะสาขาของตนเองเท่านั้น",
    "settings.mergedViewTarget": "นอกจากนี้ในสาขาที่พวกเขารวมเข้าด้วยกัน",
    "settings.mergedViewBranchHint": "คอมมิตของสาขาที่รวมเข้าด้วยกันจะถูกดึงออกมาในเลนของตัวเองเท่านั้น การผสานจะแสดงเป็นตัวเชื่อมต่อ วิวสุดคลาสสิค.",
    "settings.mergedViewTargetHint": "การคอมมิตที่ Merge ที่นำเข้ามาจะถูกวาดในช่องทางของสาขาที่ได้รับเช่นกัน ซึ่งซ้อนกันอยู่ใต้ Merge Commit และระดับเดียวกับต้นฉบับ — เพราะสาขานั้นมีพวกมันอยู่จริงๆ ในตอนนี้ สำเนามีสีซีดและมีกิ่งก้านที่เขียนไว้ ดังนั้นจึงชัดเจนว่าไม่ได้จัดทำขึ้นที่นั่น การรวมสควอชจะไม่ได้รับผลกระทบ: สควอชเขียนคอมมิตธรรมดาหนึ่งคอมมิต ดังนั้นคอมมิตนั้นเท่านั้นที่ปรากฏ",
    "legend.title": "ตำนาน",
    "legend.head": "HEAD/สาขาปัจจุบัน",
    "legend.local": "สาขาท้องถิ่น",
    "legend.remote": "สาขาระยะไกล",
    "legend.remoteOnly": "เฉพาะในคลาวด์ (ไม่ดึง)",
    "legend.tag": "แท็ก (เวอร์ชัน)",
    "legend.commit": "ให้สัญญา",
    "legend.stash": "เก็บของ (งานวางของ)",
    "legend.mergedIn": "เข้ามาด้วยการผสาน (เขียนในสาขาอื่น)",
    "legend.nodes": "กล่อง",
    "legend.lines": "เส้น",
    "legend.edgeParent": "การกระทำก่อนหน้าในบรรทัดเดียวกัน",
    "legend.edgeMerge": "สาขาที่ผสานเข้า - ลูกศรชี้ไปที่การผสาน",
    "legend.edgeBranch": "สาขาแตกที่นี่",
    "legend.edgeStash": "Stash และ Commit มันถูกเก็บเข้าลิ้นชัก",
    "legend.edgeMergedChain": "บรรทัดของสาขานี้ทำงานผ่านการรวมเข้า",
    "legend.edgeMergedTie": "คอมมิตเดียวกันกับสาขาที่เขียนไว้",
    "menu.jumpHead": "⌖ ไปที่การชำระเงิน",
    "menu.resetView": "⤢ รีเซ็ตมุมมอง",
    "menu.createBranch": "สร้างสาขาจากที่นี่...",
    "menu.checkout": "ชำระเงินคอมมิตนี้",
    "menu.copySha": "คัดลอกคอมมิต SHA",
    "menu.pushBranch": "พุชสาขา \"{name}\"",
    "menu.renameCommit": "เปลี่ยนชื่อข้อความยืนยัน...",
    "menu.undoCommit": "ยกเลิกการคอมมิต (เก็บการเปลี่ยนแปลง)...",
    "menu.viewChanges": "ดูการเปลี่ยนแปลง...",
    "menu.jumpToOriginal": "ไปที่การกระทำดั้งเดิม",
    "changes.title": "การเปลี่ยนแปลงใน {sha}",
    "changes.tabChanged": "เปลี่ยน",
    "changes.tabAll": "ไฟล์ทั้งหมด",
    "changes.added": "เพิ่มแล้ว",
    "changes.modified": "ดัดแปลง",
    "changes.deleted": "ลบแล้ว",
    "changes.renamed": "เปลี่ยนชื่อแล้ว",
    "changes.noChanges": "คอมมิตนี้ไม่มีการเปลี่ยนแปลงไฟล์",
    "changes.loading": "กำลังโหลดความแตกต่าง...",
    "changes.selectFile": "เลือกไฟล์เพื่อดูการเปลี่ยนแปลง",
    "changes.original": "ต้นฉบับ",
    "changes.changed": "ความมุ่งมั่นนี้",
    "changes.binary": "ไฟล์ไบนารี — ไม่มีข้อความต่างที่จะแสดง",
    "changes.tooLarge": "ไฟล์มีขนาดใหญ่เกินกว่าจะแตกต่าง",
    "changes.renamedFrom": "เปลี่ยนชื่อจาก {path}",
    "changes.close": "ปิด",
    "changes.prevChange": "การเปลี่ยนแปลงครั้งก่อน",
    "changes.nextChange": "การเปลี่ยนแปลงครั้งต่อไป",
    "changes.collapseAll": "ยุบโฟลเดอร์ทั้งหมด",
    "changes.expandAll": "ขยายโฟลเดอร์ทั้งหมด",
    "changes.searchPlaceholder": "ค้นหาไฟล์...",
    "changes.noSearchResults": "ไม่มีไฟล์ที่ตรงกับการค้นหาของคุณ",
    "changes.maximize": "ขยายใหญ่สุด",
    "changes.restore": "คืนค่าขนาด",
    "find.open": "ค้นหาในส่วนต่างนี้ (Ctrl+F)",
    "find.placeholder": "ค้นหาในความแตกต่าง...",
    "find.prev": "นัดที่แล้ว",
    "find.next": "นัดต่อไป",
    "find.close": "ปิดการค้นหา",
    "find.noResults": "ไม่มีผลลัพธ์",
    "commit.title": "ยอมรับการเปลี่ยนแปลง",
    "commit.close": "ปิด",
    "commit.loading": "กำลังอ่านแผนผังการทำงาน...",
    "commit.noChanges": "ไม่มีการเปลี่ยนแปลงในเครื่องที่ต้องกระทำ",
    "commit.selectFile": "เลือกไฟล์เพื่อดูความแตกต่าง",
    "commit.selectAll": "เลือกทั้งหมด",
    "commit.selectNone": "เคลียร์ทั้งหมด",
    "commit.message": "ส่งข้อความยืนยัน",
    "commit.messagePlaceholder": "อธิบายการเปลี่ยนแปลง",
    "commit.selectedCount": "เลือกไฟล์ {count} แล้ว",
    "commit.review": "ทบทวน",
    "commit.reviewTitle": "ตรวจสอบขั้นสุดท้ายก่อนที่จะกระทำ",
    "commit.reviewSummary": "คอมมิตไฟล์ {count} พร้อมข้อความ: {message}",
    "commit.back": "กลับ",
    "commit.commit": "สร้างคอมมิตท้องถิ่น",
    "menu.stashApply": "ใช้ที่ซ่อน",
    "menu.stashPop": "Pop stash (ใช้ & ลบ)",
    "menu.stashDrop": "ทิ้งของสะสม…",
    "merge.title": "รวมสาขา",
    "merge.route": "ผสานเข้ากับสาขาปัจจุบัน",
    "merge.source": "จาก (ที่มา)",
    "merge.target": "เข้าสู่ (ปัจจุบัน)",
    "merge.loading": "กำลังวิเคราะห์การรวม...",
    "merge.upToDate": "อัปเดตแล้ว — ไม่มีอะไรจะรวม",
    "merge.fastForward": "กรอไปข้างหน้า — ไม่จำเป็นต้องคอมมิตการรวม (ทำเครื่องหมายที่ “สร้างคอมมิตการรวม” เพื่อบังคับใช้)",
    "merge.summary": "ไฟล์ {files} มีการเปลี่ยนแปลง ข้อขัดแย้ง {conflicts}",
    "merge.conflictsWarning": "การผสานนี้มีข้อขัดแย้ง — คุณยังคงสามารถผสานได้ จากนั้นแก้ไขในตัวแก้ไข",
    "merge.noChanges": "ไม่มีการเปลี่ยนแปลงไฟล์",
    "merge.files": "ส่งผลให้เกิดการเปลี่ยนแปลง",
    "merge.added": "เพิ่มแล้ว",
    "merge.modified": "ดัดแปลง",
    "merge.deleted": "ลบแล้ว",
    "merge.conflict": "ขัดแย้ง",
    "merge.message": "รวมข้อความยืนยัน",
    "merge.messagePlaceholder": "รวมสาขา 'แหล่งที่มา'",
    "merge.messageFfHint": "ละเว้นในการรวมกรอไปข้างหน้า (ไม่มีการสร้างคอมมิต)",
    "merge.noFastForward": "สร้างการคอมมิตแบบผสาน (ไม่ต้องกรอไปข้างหน้า)",
    "merge.noFastForwardHint": "บันทึกการรวมคอมมิตเสมอ แม้ว่าสาขาจะสามารถกรอไปข้างหน้าได้ก็ตาม",
    "merge.merge": "ผสาน",
    "merge.cancel": "ยกเลิก",
    "merge.previewError": "ไม่สามารถคำนวณการแสดงตัวอย่าง: {message}",
    "merge.squashBadge": "สควอช",
    "merge.squashNote": "Squash Merge — ทุกการเปลี่ยนแปลงจะเกิดขึ้นเมื่อ ONE กระทำกับ “{target}”; ความมุ่งมั่นของ “{source}” นั้นไม่อยู่ในประวัติศาสตร์ สลับกลับในการตั้งค่า > ผสาน",
    "merge.messageSquash": "ส่งข้อความยืนยัน",
    "merge.squashMessagePlaceholder": "การเปลี่ยนแปลงที่แบนจาก 'แหล่งที่มา'",
    "newBranch.title": "สร้างสาขา",
    "newBranch.startPoint": "สาขาใหม่เริ่มต้นที่ {sha}",
    "newBranch.startPointOn": "บน {refs}",
    "newBranch.location": "ที่ตั้ง",
    "newBranch.locationRoot": "(ราก)",
    "newBranch.expandAll": "ขยายทั้งหมด",
    "newBranch.collapseAll": "ยุบทั้งหมด",
    "newBranch.expandFolder": "ขยายโฟลเดอร์",
    "newBranch.collapseFolder": "ยุบโฟลเดอร์",
    "newBranch.name": "ชื่อสาขา",
    "newBranch.namePlaceholder": "สาขาของฉัน",
    "newBranch.fullName": "ชื่อเต็ม: {name}",
    "newBranch.invalid": "ชื่อสาขาไม่ถูกต้อง",
    "newBranch.exists": "มีสาขาที่ใช้ชื่อนี้อยู่แล้ว",
    "newBranch.checkout": "สาขาชำระเงินหลังการสร้าง",
    "newBranch.create": "สร้าง",
    "newBranch.cancel": "ยกเลิก",
    "status.loading": "กำลังโหลดกราฟ...",
    "status.summary": "{repo}กำลังแสดง {commits} คอมมิต {refs} อ้างอิง",
    "status.branchCreated": "สร้างสาขา \"{name}\" ที่ {sha}",
    "status.error": "ข้อผิดพลาด: {message}",
    "status.opFailed": "การดำเนินการล้มเหลว",
    "status.fetching": "กำลังดึงข้อมูล...",
    "status.pulling": "กำลังดึง...",
    "status.pushing": "กำลังผลักดัน…",
    "status.committing": "กำลังสร้างคอมมิตท้องถิ่น...",
    "status.commitCreated": "สร้างคอมมิตโลคัล {sha}: {message}",
    "status.syncing": "กำลังซิงค์...",
    "status.noHead": "ไม่พบการชำระเงินปัจจุบันในกราฟ",
    "status.undoing": "กำลังยกเลิกการคอมมิต...",
    "status.commitUndone": "ยกเลิกการคอมมิต — การเปลี่ยนแปลงจะกลับมาอยู่ในแผนผังการทำงานอีกครั้ง",
    "status.undoConflict": "เลิกทำข้อขัดแย้งของ Hit — แก้ไขในตัวแก้ไขแล้วดำเนินการต่อ",
    "status.stashApplying": "กำลังใช้ที่ซ่อน...",
    "status.stashPopping": "โผล่ที่ซ่อน…",
    "status.stashDropping": "กำลังทิ้งที่ซ่อน…",
    "status.stashApplied": "ใช้การซ่อนแล้ว",
    "status.stashPopped": "คลังเก็บของโผล่ออกมา",
    "status.stashDropped": "คลังเก็บของลดลง",
    "status.stashConflict": "ข้อขัดแย้งในการซ่อน — แก้ไขมันในตัวแก้ไข",
    "status.merging": "กำลังผสาน...",
    "status.merged": "ผสานเสร็จสมบูรณ์",
    "status.mergeConflict": "Merge มีข้อขัดแย้ง — แก้ไขข้อขัดแย้งในตัวแก้ไข จากนั้นจึงคอมมิต",
    "search.title": "ค้นหา",
    "search.tooltip": "การค้นหากระทำ",
    "search.placeholder": "ค้นหาคอมมิต...",
    "search.previous": "ผลลัพธ์ก่อนหน้า",
    "search.next": "ผลลัพธ์ต่อไป",
    "search.noResults": "ไม่มีผลลัพธ์",
    "search.resultCount": "{index}/{count}",
    "search.filter": "โหมดตัวกรอง",
    "search.highlight": "โหมดไฮไลท์",
    "search.filterMode": "การแสดงการค้นหา",
    "search.filterModeFilter": "ตัวกรอง (ซ่อนรายการที่ไม่ตรงกัน)",
    "search.filterModeHighlight": "ไฮไลท์ (แสดงทั้งหมด ทำเครื่องหมายรายการที่ตรงกัน)",
    "search.filterModeHint": "เลือกวิธีแสดงผลการค้นหาในกราฟ",
    "search.visibility": "ค้นหาตำแหน่ง",
    "search.visibilityToolbar": "ปุ่มแถบเครื่องมือ",
    "search.visibilityAlwaysVisible": "แถบที่มองเห็นได้เสมอ",
    "search.visibilityHint": "แสดงการค้นหาเป็นไอคอนแถบเครื่องมือหรือแถบถาวรที่ด้านบน",
    "footer.github": "GitHub",
  },
  lo: {
    "toolbar.refresh": "⟳ ໂຫຼດຫນ້າຈໍຄືນ",
    "toolbar.fetch": "⤓ ເອົາ",
    "toolbar.pull": "⇩ ດຶງ",
    "toolbar.push": "⇧ ຍູ້",
    "toolbar.commit": "✓ ຫມັ້ນສັນຍາ",
    "toolbar.sync": "⇅ ຊິງຄ໌",
    "toolbar.jumpHead": "⌖ ໄປຈ່າຍເງິນ",
    "toolbar.reset": "⤢ ຣີເຊັດມຸມມອງ",
    "toolbar.settings": "⚙ ການຕັ້ງຄ່າ",
    "details.header": "ຄໍາຫມັ້ນສັນຍາລາຍລະອຽດ",
    "details.close": "ປິດ",
    "details.sha": "SHA",
    "details.shortSha": "SHA ສັ້ນ",
    "details.message": "ຂໍ້ຄວາມ",
    "details.author": "ຜູ້ຂຽນ",
    "details.date": "ວັນທີ",
    "details.labels": "ປ້າຍກຳກັບ",
    "details.location": "ສະຖານທີ່",
    "details.currentHead": "HEAD — ການຈ່າຍເງິນປະຈຸບັນ",
    "settings.title": "ການຕັ້ງຄ່າ",
    "settings.language": "ພາສາ",
    "settings.theme": "ຫົວຂໍ້",
    "settings.themeLight": "ແສງສະຫວ່າງ",
    "settings.themeDark": "ມືດ",
    "settings.themeLightHint": "ພື້ນຫຼັງອ່ອນ, ຂໍ້ຄວາມມືດ.",
    "settings.themeDarkHint": "ພື້ນຫຼັງມືດ, ຂໍ້ຄວາມເບົາ.",
    "settings.mainBranch": "ສາຂາຫຼັກ",
    "settings.mainBranchAuto": "ອັດຕະໂນມັດ",
    "settings.mainBranchSearch": "ຊອກຫາສາຂາ...",
    "settings.mainBranchNoMatch": "ບໍ່ມີສາຂາທີ່ກົງກັນ",
    "settings.display": "ຮູບແບບການສະແດງ",
    "settings.displayModern": "ທັນສະໄຫມ",
    "settings.displayClassic": "ຄລາສິກ",
    "settings.displayModernHint": "ຜ້າໃບຟຣີ — ລາກເພື່ອເລື່ອນ, ເລື່ອນເພື່ອຊູມ.",
    "settings.displayClassicHint": "ຜ້າໃບຄົງທີ່ — ລຳຕົ້ນຖືກປັກໄວ້ຊ້າຍ, ບໍ່ມີການຊູມ, ເລື່ອນເທົ່ານັ້ນ (ຄືກັບກຣາບການດັດແກ້ SVN).",
    "settings.sectionGeneral": "ທົ່ວໄປ",
    "settings.sectionGraph": "ກຣາບ",
    "settings.sectionChanges": "ການປ່ຽນແປງມຸມມອງ",
    "settings.diffMinimap": "ແຜນທີ່ຫຍໍ້ສ່ວນຕ່າງ",
    "settings.diffMinimapOn": "ສະແດງໃຫ້ເຫັນ",
    "settings.diffMinimapOff": "ເຊື່ອງໄວ້",
    "settings.diffMinimapOnHint": "ສະແດງແຖບພາບລວມແບບ VS Code ຂ້າງສ່ວນຕ່າງ — ລາກມັນເພື່ອເລື່ອນ; ເຄື່ອງຫມາຍການປ່ຽນແປງລວມ.",
    "settings.diffMinimapOffHint": "ບໍ່ມີແຖບພາບລວມ — ເລື່ອນຄວາມແຕກຕ່າງຕາມປົກກະຕິ.",
    "settings.commitReview": "ຄໍາຫມັ້ນສັນຍາການທົບທວນຄືນ",
    "settings.commitReviewOn": "ຖາມກ່ອນຄໍາຫມັ້ນສັນຍາ",
    "settings.commitReviewOff": "ຄໍາຫມັ້ນສັນຍາໂດຍກົງ",
    "settings.commitReviewOnHint": "ສະແດງລາຍຊື່ໄຟລ໌ສຸດທ້າຍແລະການກວດສອບຂໍ້ຄວາມກ່ອນທີ່ຈະສ້າງຄໍາຫມັ້ນສັນຍາໃນທ້ອງຖິ່ນ.",
    "settings.commitReviewOffHint": "ປຸ່ມ Commit ສ້າງຄໍາຫມັ້ນສັນຍາໃນທ້ອງຖິ່ນທັນທີ.",
    "settings.svnBranchDialog": "ກ່ອງໂຕ້ຕອບສາຂາແບບ SVN",
    "settings.svnBranchDialogHint": "ສະແດງຕົວເລືອກໂຟນເດີ-ຕົ້ນໄມ້ເມື່ອສ້າງສາຂາ (ແທນທີ່ຈະເປັນການເຕືອນແບບງ່າຍໆ).",
    "settings.branchDialog": "ກ່ອງໂຕ້ຕອບສາຂາ",
    "settings.branchDialogSvnTitle": "SVN ແບບ",
    "settings.branchDialogNativeVscode": "VS ຮູບແບບລະຫັດ",
    "settings.branchDialogNativeVs": "ຮູບແບບ Visual Studio",
    "settings.branchDialogNativeHint": "ການເຕືອນສາຂາໃນຕົວຂອງ IDE ຂອງທ່ານ.",
    "settings.close": "ປິດ",
    "settings.done": "ສຳເລັດແລ້ວ",
    "settings.sectionAdvanced": "ຂັ້ນສູງ",
    "settings.gitSource": "Git ສາມາດປະຕິບັດໄດ້",
    "settings.gitSourceBuiltin": "ໃນຕົວ",
    "settings.gitSourceBuiltinHint": "ໃຊ້ git binary ຈາກສ່ວນຂະຫຍາຍ Git ໃນຕົວຂອງ IDE.",
    "settings.gitSourceCustom": "ເສັ້ນທາງທີ່ກໍາຫນົດເອງ",
    "settings.gitPath": "ເສັ້ນທາງໄປຫາ git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ຊອກຫາ...",
    "settings.jargon": "ເງື່ອນໄຂ Git",
    "settings.jargonTranslate": "ແປ",
    "settings.jargonEnglish": "ຮັກສາເປັນພາສາອັງກິດ",
    "settings.jargonTranslateHint": "ແປຄຳອະທິບາຍກັບຄືນເປັນ ອັງກິດ (ສະຫະລັດ) ແປພາສາ Git terms (pull, push, commit, branch…) into the interface language.",
    "settings.jargonEnglishHint": "ຮັກສາຂໍ້ກໍານົດ Git (ດຶງ, ຍູ້, ຫມັ້ນສັນຍາ, ສາຂາ…) ໃນພາສາອັງກິດ; ແປທຸກຢ່າງອື່ນ.",
    "settings.sectionMerge": "ຮວມ",
    "settings.mergeMode": "ຮວມຮູບແບບ",
    "settings.mergeModeMerge": "ລວມປົກກະຕິ",
    "settings.mergeModeSquash": "Squash ລວມ",
    "settings.mergeModeMergeHint": "ລວມສາຂາຕາມທີ່ມັນເປັນ: ຄໍາຫມັ້ນສັນຍາຂອງມັນກາຍເປັນສ່ວນຫນຶ່ງຂອງປະຫວັດສາດຂອງສາຂາໃນປະຈຸບັນ, ເຂົ້າຮ່ວມໂດຍຄໍາຫມັ້ນສັນຍາ merge (ຫຼືສົ່ງຕໍ່ໄວເມື່ອເປັນໄປໄດ້).",
    "settings.mergeModeSquashHint": "ຫຍໍ້ສາຂາທັງໝົດເຂົ້າໄປໃນສັນຍາດຽວໃນສາຂາປະຈຸບັນ (git merge --squash). ຄໍາຫມັ້ນສັນຍາຂອງສາຂາຂອງຕົນເອງບໍ່ເຄີຍເຂົ້າໄປໃນປະຫວັດຂອງສາຂາໃນປະຈຸບັນ - ທ່ານພຽງແຕ່ເຫັນຄໍາຫມັ້ນສັນຍາຫນຶ່ງເທົ່ານັ້ນ. ສາຂາຂອງມັນເອງຖືກປະໄວ້ untouched, ສະນັ້ນ commits ຂອງຕົນຢູ່ສັງເກດເຫັນຢູ່ໃນເສັ້ນທາງຂອງຕົນເອງ.",
    "settings.mergedView": "commits ປະສົມປະສານຢູ່ໃນເສັ້ນສະແດງ",
    "settings.mergedViewBranch": "ພຽງແຕ່ຢູ່ໃນສາຂາຂອງຕົນເອງ",
    "settings.mergedViewTarget": "ນອກຈາກນີ້ຢູ່ໃນສາຂາທີ່ເຂົາເຈົ້າໄດ້ຖືກລວມເຂົ້າ",
    "settings.mergedViewBranchHint": "ຄໍາຫມັ້ນສັນຍາຂອງສາຂາທີ່ລວມເຂົ້າກັນໄດ້ຖືກແຕ້ມຢູ່ໃນເສັ້ນທາງຂອງຕົນເອງເທົ່ານັ້ນ; ການລວມຕົວສະແດງເປັນຕົວເຊື່ອມຕໍ່. ທັດສະນະຄລາສສິກ.",
    "settings.mergedViewTargetHint": "ຄໍາຫມັ້ນສັນຍາ merge ນໍາມາໄດ້ຖືກແຕ້ມຢູ່ໃນເສັ້ນທາງຂອງສາຂາທີ່ໄດ້ຮັບເຊັ່ນດຽວກັນ, stacked ພາຍໃຕ້ຄໍາຫມັ້ນສັນຍາ merge ແລະລະດັບກັບຕົ້ນສະບັບ - ເນື່ອງຈາກວ່າສາຂານັ້ນກໍ່ມີພວກມັນໃນປັດຈຸບັນ. ສໍາເນົາແມ່ນຈືດໆແລະຫົວກັບສາຂາທີ່ເຂົາເຈົ້າໄດ້ຖືກຂຽນໄວ້, ດັ່ງນັ້ນມັນຄົງຈະເຫັນໄດ້ຊັດເຈນວ່າພວກເຂົາບໍ່ໄດ້ເຮັດຢູ່ທີ່ນັ້ນ. ການຮວມ Squash ແມ່ນບໍ່ໄດ້ຮັບຜົນກະທົບ: squash ຂຽນຄໍາຫມັ້ນສັນຍາທໍາມະດາຫນຶ່ງ, ດັ່ງນັ້ນພຽງແຕ່ຄໍາຫມັ້ນສັນຍາທີ່ເຄີຍປາກົດ.",
    "legend.title": "ນິທານ",
    "legend.head": "HEAD / ສາຂາປະຈຸບັນ",
    "legend.local": "ສາຂາທ້ອງຖິ່ນ",
    "legend.remote": "ສາຂາໄລຍະໄກ",
    "legend.remoteOnly": "ພຽງແຕ່ຢູ່ໃນຄລາວ (ບໍ່ໄດ້ດຶງ)",
    "legend.tag": "ແທັກ (ເວີຊັນ)",
    "legend.commit": "ຄໍາຫມັ້ນສັນຍາ",
    "legend.stash": "ບ່ອນເກັບມ້ຽນ",
    "legend.mergedIn": "ເຂົ້າມາດ້ວຍການລວມຕົວ (ຂຽນຢູ່ສາຂາອື່ນ)",
    "legend.nodes": "ກ່ອງ",
    "legend.lines": "ສາຍ",
    "legend.edgeParent": "ສັນຍາທີ່ຜ່ານມາຢູ່ໃນເສັ້ນດຽວກັນ",
    "legend.edgeMerge": "ສາຂາທີ່ລວມເຂົ້າກັນ — ລູກສອນຊີ້ຢູ່ທີ່ຄໍາຫມັ້ນສັນຍາ merge",
    "legend.edgeBranch": "ງ່າໜຶ່ງແຕກອອກຢູ່ທີ່ນີ້",
    "legend.edgeStash": "Stash ແລະຄໍາຫມັ້ນສັນຍາທີ່ມັນຖືກ shelved ສຸດ",
    "legend.edgeMergedChain": "ສາຍຂອງສາຂານີ້ແລ່ນຜ່ານ commits ປະສົມປະສານ",
    "legend.edgeMergedTie": "ຄໍາຫມັ້ນສັນຍາດຽວກັນກັບສາຂາທີ່ມັນຖືກຂຽນໄວ້",
    "menu.jumpHead": "⌖ ໄປຈ່າຍເງິນ",
    "menu.resetView": "⤢ ຣີເຊັດມຸມມອງ",
    "menu.createBranch": "ສ້າງສາຂາຈາກບ່ອນນີ້...",
    "menu.checkout": "ກວດສອບຄໍາຫມັ້ນສັນຍານີ້",
    "menu.copySha": "ສຳເນົາ SHA",
    "menu.pushBranch": "ສາຂາ Push \"{name}\"",
    "menu.deleteBranch": "ລົບສາຂາ \"{name}\"⢦",
    "menu.renameCommit": "ປ່ຽນຊື່ຂໍ້ຄວາມທີ່ສັນຍາ...",
    "menu.undoCommit": "ຍົກເລີກສັນຍາ (ຮັກສາການປ່ຽນແປງ)…",
    "menu.viewChanges": "ເບິ່ງການປ່ຽນແປງ...",
    "menu.mergeBranch": "ຮວມ \"{source}\" ເຂົ້າໄປໃນ \"{target}\"⢦",
    "menu.jumpToOriginal": "ໄປຫາຄໍາຫມັ້ນສັນຍາຕົ້ນສະບັບ",
    "changes.title": "ການປ່ຽນແປງໃນ {sha}",
    "changes.tabChanged": "ປ່ຽນແປງ",
    "changes.tabAll": "ໄຟລ໌ທັງໝົດ",
    "changes.added": "ເພີ່ມແລ້ວ",
    "changes.modified": "ດັດແກ້",
    "changes.deleted": "ລຶບແລ້ວ",
    "changes.renamed": "ປ່ຽນຊື່ແລ້ວ",
    "changes.noChanges": "ຄໍາຫມັ້ນສັນຍານີ້ບໍ່ປ່ຽນແປງໄຟລ໌ໃດໆ.",
    "changes.loading": "ກຳລັງໂຫຼດຄວາມແຕກຕ່າງ...",
    "changes.selectFile": "ເລືອກໄຟລ໌ເພື່ອເບິ່ງການປ່ຽນແປງຂອງມັນ.",
    "changes.original": "ຕົ້ນສະບັບ",
    "changes.changed": "ຄໍາຫມັ້ນສັນຍານີ້",
    "changes.binary": "ໄຟລ​໌​ຖານ​ສອງ — ບໍ່​ມີ​ຂໍ້​ຄວາມ​ແຕກ​ຕ່າງ​ທີ່​ຈະ​ສະ​ແດງ​ໃຫ້​ເຫັນ​.",
    "changes.tooLarge": "ໄຟລ໌ໃຫຍ່ເກີນໄປທີ່ຈະແຕກຕ່າງກັນ.",
    "changes.renamedFrom": "ປ່ຽນຊື່ຈາກ {path}",
    "changes.close": "ປິດ",
    "changes.prevChange": "ການປ່ຽນແປງທີ່ຜ່ານມາ",
    "changes.nextChange": "ການປ່ຽນແປງຕໍ່ໄປ",
    "changes.collapseAll": "ຫຍໍ້ໂຟນເດີທັງໝົດ",
    "changes.expandAll": "ຂະຫຍາຍໂຟນເດີທັງໝົດ",
    "changes.searchPlaceholder": "ຊອກຫາໄຟລ໌...",
    "changes.noSearchResults": "ບໍ່ມີໄຟລ໌ທີ່ກົງກັບການຊອກຫາຂອງທ່ານ.",
    "changes.maximize": "ສູງສຸດ",
    "changes.restore": "ຟື້ນຟູຂະໜາດ",
    "find.open": "ຊອກຫາໃນຄວາມແຕກຕ່າງນີ້ (Ctrl+F)",
    "find.placeholder": "ຊອກຫາຄວາມແຕກຕ່າງ...",
    "find.prev": "ການແຂ່ງຂັນທີ່ຜ່ານມາ",
    "find.next": "ນັດຕໍ່ໄປ",
    "find.close": "ປິດການຊອກຫາ",
    "find.noResults": "ບໍ່ມີຜົນໄດ້ຮັບ",
    "commit.title": "ສັນຍາການປ່ຽນແປງ",
    "commit.close": "ປິດ",
    "commit.loading": "ກຳລັງອ່ານຕົ້ນໄມ້…",
    "commit.noChanges": "ບໍ່ມີການປ່ຽນແປງທ້ອງຖິ່ນທີ່ຈະມຸ່ງຫມັ້ນ.",
    "commit.selectFile": "ເລືອກໄຟລ໌ເພື່ອເບິ່ງຄວາມແຕກຕ່າງຂອງມັນ.",
    "commit.selectAll": "ເລືອກທັງໝົດ",
    "commit.selectNone": "ລຶບລ້າງທັງໝົດ",
    "commit.message": "ສັນຍາຂໍ້ຄວາມ",
    "commit.messagePlaceholder": "ອະທິບາຍການປ່ຽນແປງ",
    "commit.selectedCount": "ເລືອກໄຟລ໌ {count} ແລ້ວ",
    "commit.review": "ທົບທວນຄືນ",
    "commit.reviewTitle": "ກວດ​ສອບ​ຄັ້ງ​ສຸດ​ທ້າຍ​ກ່ອນ​ທີ່​ຈະ​ຫມັ້ນ​ສັນ​ຍາ​",
    "commit.reviewSummary": "ສົ່ງໄຟລ໌ {count} ດ້ວຍຂໍ້ຄວາມ: {message}",
    "commit.back": "ກັບຄືນໄປບ່ອນ",
    "commit.commit": "ສ້າງຄໍາຫມັ້ນສັນຍາໃນທ້ອງຖິ່ນ",
    "menu.stashApply": "ນຳໃຊ້ບ່ອນເກັບມ້ຽນ",
    "menu.stashPop": "Pop stash (ນຳໃຊ້ ແລະ ລຶບ)",
    "menu.stashDrop": "ວາງ​ສະ​ຖານ​ທີ່...",
    "merge.title": "ລວມສາຂາ",
    "merge.route": "ການລວມເຂົ້າກັບສາຂາໃນປະຈຸບັນ",
    "merge.source": "ຈາກ (ແຫຼ່ງ)",
    "merge.target": "ເຂົ້າໄປໃນ (ປະຈຸບັນ)",
    "merge.loading": "ກຳລັງວິເຄາະການຮວມ...",
    "merge.upToDate": "ອັບເດດແລ້ວ — ບໍ່ມີຫຍັງທີ່ຈະຮວມກັນໄດ້.",
    "merge.fastForward": "ກ້າວໄປຂ້າງໜ້າໄວ — ບໍ່ຈຳເປັນຕ້ອງມີຄຳໝັ້ນສັນຍາລວມ (ໝາຍຕິກ “Create a merge commit” ເພື່ອບັງຄັບອັນໜຶ່ງ).",
    "merge.summary": "ປ່ຽນໄຟລ໌ {files}, {conflicts} ຂໍ້ຂັດແຍ່ງ.",
    "merge.conflictsWarning": "ການຮວມນີ້ມີຄວາມຂັດແຍ້ງກັນ — ທ່ານຍັງສາມາດຮວມກັນໄດ້, ຈາກນັ້ນແກ້ໄຂພວກມັນໃນຕົວແກ້ໄຂ.",
    "merge.noChanges": "ບໍ່ມີການປ່ຽນແປງໄຟລ໌.",
    "merge.files": "ຜົນໄດ້ຮັບການປ່ຽນແປງ",
    "merge.added": "ເພີ່ມແລ້ວ",
    "merge.modified": "ດັດແກ້",
    "merge.deleted": "ລຶບແລ້ວ",
    "merge.conflict": "ການຂັດແຍ້ງ",
    "merge.message": "Merge commit ຂໍ້ຄວາມ",
    "merge.messagePlaceholder": "ລວມສາຂາ 'ແຫຼ່ງ'",
    "merge.messageFfHint": "ຖືກລະເລີຍໃນການລວມຕົວແບບເລັ່ງດ່ວນ (ບໍ່ມີການຕັ້ງໃຈສ້າງ).",
    "merge.noFastForward": "ສ້າງ​ຄໍາ​ຫມັ້ນ​ສັນ​ຍາ​ການ​ລວມ (ບໍ່​ມີ​ການ​ໄປ​ຂ້າງ​ຫນ້າ​ໄວ​)",
    "merge.noFastForwardHint": "ບັນທຶກຄຳໝັ້ນສັນຍາລວມສະເໝີ, ເຖິງແມ່ນວ່າສາຂາສາມາດໄປໜ້າໄດ້ໄວກໍຕາມ.",
    "merge.merge": "ຮວມ",
    "merge.cancel": "ຍົກເລີກ",
    "merge.previewError": "ບໍ່ສາມາດຄິດໄລ່ຕົວຢ່າງ: {message}",
    "merge.squashBadge": "ສະຄັຊ",
    "merge.squashNote": "Squash merge — ທຸກ​ການ​ປ່ຽນ​ແປງ​ທີ່​ດິນ​ເປັນ​ຫນຶ່ງ​ຄໍາ​ຫມັ້ນ​ສັນ​ຍາ​ກ່ຽວ​ກັບ “{target}​”​; ຄໍາຫມັ້ນສັນຍາຂອງຕົນເອງ \"{source}\" ບໍ່ໄດ້ຢູ່ໃນປະຫວັດສາດຂອງມັນ. ປ່ຽນກັບຄືນໃນການຕັ້ງຄ່າ › ຮວມ.",
    "merge.messageSquash": "ສັນຍາຂໍ້ຄວາມ",
    "merge.squashMessagePlaceholder": "Squashed ການປ່ຽນແປງຈາກ 'ແຫຼ່ງ'",
    "newBranch.title": "ສ້າງສາຂາ",
    "newBranch.startPoint": "ສາຂາໃໝ່ເລີ່ມຈາກ {sha}",
    "newBranch.startPointOn": "ໃນ {refs}",
    "newBranch.location": "ສະຖານທີ່",
    "newBranch.locationRoot": "(ຮາກ)",
    "newBranch.expandAll": "ຂະຫຍາຍທັງໝົດ",
    "newBranch.collapseAll": "ຫຍໍ້ທັງໝົດ",
    "newBranch.expandFolder": "ຂະຫຍາຍໂຟນເດີ",
    "newBranch.collapseFolder": "ຫຍໍ້ໂຟນເດີ",
    "newBranch.name": "ຊື່ສາຂາ",
    "newBranch.namePlaceholder": "ສາຂາຂອງຂ້ອຍ",
    "newBranch.fullName": "ຊື່ເຕັມ: {name}",
    "newBranch.invalid": "ຊື່ສາຂາບໍ່ຖືກຕ້ອງ",
    "newBranch.exists": "ສາຂາທີ່ມີຊື່ນີ້ມີຢູ່ແລ້ວ",
    "newBranch.checkout": "ສາຂາ Checkout ຫຼັງຈາກການສ້າງ",
    "newBranch.create": "ສ້າງ",
    "newBranch.cancel": "ຍົກເລີກ",
    "status.loading": "ກຳລັງໂຫຼດກຣາບ...",
    "status.summary": "{repo} ສະແດງ {commits} commits, {refs} refs",
    "status.branchCreated": "ສ້າງສາຂາ \"{name}\" ທີ່ {sha}",
    "status.error": "ຜິດພາດ: {message}",
    "status.opFailed": "ການດໍາເນີນງານລົ້ມເຫລວ",
    "status.fetching": "ກຳລັງດຶງຂໍ້ມູນ...",
    "status.pulling": "ກຳລັງດຶງ...",
    "status.pushing": "ກຳລັງຊຸກຍູ້...",
    "status.committing": "ກຳລັງສ້າງສັນຍາທ້ອງຖິ່ນ...",
    "status.commitCreated": "ສ້າງຄໍາຫມັ້ນສັນຍາໃນທ້ອງຖິ່ນ {sha}: {message}",
    "status.syncing": "ກຳລັງຊິ້ງຂໍ້ມູນ...",
    "status.noHead": "ບໍ່ພົບການເຊັກເອົາປະຈຸບັນໃນກາຟ.",
    "status.undoing": "ກຳລັງຍົກເລີກຄຳໝັ້ນສັນຍາ...",
    "status.commitUndone": "ຄໍາຫມັ້ນສັນຍາຍົກເລີກ - ການປ່ຽນແປງກັບຄືນມາຢູ່ໃນຕົ້ນໄມ້ທີ່ເຮັດວຽກ.",
    "status.undoConflict": "ຍົກ​ເລີກ​ການ​ຂັດ​ແຍ່ງ​ກັນ — ແກ້​ໄຂ​ໃຫ້​ເຂົາ​ເຈົ້າ​ຢູ່​ໃນ​ບັນ​ນາ​ທິ​ການ​, ຫຼັງ​ຈາກ​ນັ້ນ​ສືບ​ຕໍ່​.",
    "status.stashApplying": "ກຳລັງນຳໃຊ້ຄັງເກັບ...",
    "status.stashPopping": "ກຳ​ລັງ​ເກັບ​ຮັກ​ສາ...",
    "status.stashDropping": "ກຳ​ລັງ​ເກັບ​ຮັກ​ສາ​ໄວ້...",
    "status.stashApplied": "ນຳໃຊ້ Stash ແລ້ວ.",
    "status.stashPopped": "Stash ປະກົດຂຶ້ນ.",
    "status.stashDropped": "Stash ຫຼຸດລົງ.",
    "status.stashConflict": "ການຂັດແຍ້ງໃນ Stash — ແກ້ໄຂພວກມັນໃນຕົວແກ້ໄຂ.",
    "status.merging": "ຮວມ...",
    "status.merged": "ຮວມສຳເລັດແລ້ວ.",
    "status.mergeConflict": "ການຮວມມີຂໍ້ຂັດແຍ່ງ — ແກ້ໄຂພວກມັນໃນບັນນາທິການ, ຈາກນັ້ນໃຫ້ຄຳໝັ້ນສັນຍາ.",
    "search.title": "ຊອກຫາ",
    "search.tooltip": "ຄົ້ນຫາຄໍາຫມັ້ນສັນຍາ",
    "search.placeholder": "ຄົ້ນຫາຄໍາໝັ້ນສັນຍາ...",
    "search.previous": "ຜົນໄດ້ຮັບທີ່ຜ່ານມາ",
    "search.next": "ຜົນໄດ້ຮັບຕໍ່ໄປ",
    "search.noResults": "ບໍ່ມີຜົນໄດ້ຮັບ",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ໂໝດການກັ່ນຕອງ",
    "search.highlight": "ໂໝດໄຮໄລ້",
    "search.filterMode": "ຄົ້ນຫາການສະແດງຜົນ",
    "search.filterModeFilter": "ການກັ່ນຕອງ (ເຊື່ອງການບໍ່ກົງກັນ)",
    "search.filterModeHighlight": "ຈຸດ​ເດັ່ນ (ສະ​ແດງ​ໃຫ້​ເຫັນ​ທັງ​ຫມົດ​, ຫມາຍ​ທີ່​ກົງ​ກັນ​)",
    "search.filterModeHint": "ເລືອກວິທີການສະແດງຜົນການຄົ້ນຫາໃນກາຟ.",
    "search.visibility": "ຊອກຫາສະຖານທີ່",
    "search.visibilityToolbar": "ປຸ່ມແຖບເຄື່ອງມື",
    "search.visibilityAlwaysVisible": "ແຖບທີ່ເຫັນໄດ້ສະເໝີ",
    "search.visibilityHint": "ສະແດງການຊອກຫາເປັນໄອຄອນແຖບເຄື່ອງມື ຫຼືແຖບຄົງຢູ່ຢູ່ເທິງສຸດ.",
    "footer.github": "GitHub",
  },
  my: {
    "toolbar.refresh": "⟳ ပြန်လည်စတင်ပါ။",
    "toolbar.fetch": "⤓ ထုတ်ယူခြင်း။",
    "toolbar.pull": "⇩ ဆွဲပါ။",
    "toolbar.push": "⇧ တွန်းပါ။",
    "toolbar.commit": "✓ ကတိတည်ပါ။",
    "toolbar.sync": "⇅ စင့်ခ်လုပ်ခြင်း။",
    "toolbar.jumpHead": "⌖ ငွေရှင်းရန် သွားပါ။",
    "toolbar.reset": "⤢ မြင်ကွင်းကို ပြန်လည်သတ်မှတ်ပါ။",
    "toolbar.settings": "⚙ ဆက်တင်များ",
    "details.header": "အသေးစိတ်အချက်များ",
    "details.close": "ပိတ်လိုက်",
    "details.sha": "SHA",
    "details.shortSha": "SHA အတို",
    "details.message": "မက်ဆေ့ချ်",
    "details.author": "စာရေးသူ",
    "details.date": "ရက်စွဲ",
    "details.labels": "တံဆိပ်များ",
    "details.location": "တည်နေရာ",
    "details.currentHead": "HEAD - လက်ရှိငွေရှင်းခြင်း။",
    "settings.title": "ဆက်တင်များ",
    "settings.language": "ဘာသာစကား",
    "settings.theme": "အပြင်အဆင်",
    "settings.themeLight": "အလင်း",
    "settings.themeDark": "အမှောင်",
    "settings.themeLightHint": "အလင်းနောက်ခံ၊ မှောင်သောစာသား။",
    "settings.themeDarkHint": "အနက်ရောင်နောက်ခံ၊ အလင်းစာသား။",
    "settings.mainBranch": "ပင်မအခက်",
    "settings.mainBranchAuto": "အလိုအလျောက်",
    "settings.mainBranchSearch": "အကိုင်းအခက်ရှာရန်...",
    "settings.mainBranchNoMatch": "ကိုက်ညီသောအကိုင်းအခက်မရှိပါ။",
    "settings.display": "ပြသမှုပုံစံ",
    "settings.displayModern": "ခေတ်မီသည်။",
    "settings.displayClassic": "ဂန္ထဝင်",
    "settings.displayModernHint": "အခမဲ့ ကင်းဗတ်စ် — ဝေ့ရန် ဆွဲယူ၊ ချုံ့ချဲ့ရန် လှိမ့်ပါ။",
    "settings.displayClassicHint": "ပြင်ဆင်ထားသော ကင်းဗတ်စ် — ပင်စည်ကို ဘယ်ဘက်တွင် ပင်ထိုးထားသည်၊ ဇူးမ်မရှိပါ၊ လှိမ့်ရန်သာ (SVN တည်းဖြတ်ဂရပ်ကဲ့သို့)။",
    "settings.sectionGeneral": "အထွေထွေ",
    "settings.sectionGraph": "ဂရပ်",
    "settings.sectionChanges": "အမြင်ပြောင်းလဲခြင်း။",
    "settings.diffMinimap": "ကွာခြားချက်အသေးစားမြေပုံ",
    "settings.diffMinimapOn": "ပြသည်။",
    "settings.diffMinimapOff": "ဝှက်ထားသည်။",
    "settings.diffMinimapOnHint": "ကွဲပြားမှုဘေးရှိ VS ကုဒ်ပုံစံ ခြုံငုံသုံးသပ်ချက်ကို ပြပါ — ၎င်းကို လှိမ့်ရန် ဆွဲယူပါ။ ပြောင်းလဲမှု အမှတ်အသားများ ပါဝင်ပါသည်။",
    "settings.diffMinimapOffHint": "ခြုံငုံသုံးသပ်ချက်အကွက်မရှိပါ — ကွဲပြားမှုကို ပုံမှန်အတိုင်း ရွှေ့ပါ။",
    "settings.commitReview": "ပြန်လည်သုံးသပ်မှုပြုပါ။",
    "settings.commitReviewOn": "မထိုးခင် မေးပါ။",
    "settings.commitReviewOff": "တိုက်ရိုက်အပ်နှံပါ။",
    "settings.commitReviewOnHint": "ဒေသတွင်း ကွန်မန့်ကို မဖန်တီးမီ နောက်ဆုံးဖိုင်စာရင်းနှင့် မက်ဆေ့ချ်စစ်ဆေးခြင်းကို ပြပါ။",
    "settings.commitReviewOffHint": "Commit ခလုတ်သည် local commit ကို ချက်ချင်းဖန်တီးပေးပါသည်။",
    "settings.svnBranchDialog": "SVN ပုံစံ ဌာနခွဲ ဒိုင်ယာလော့ခ်",
    "settings.svnBranchDialogHint": "အကိုင်းအခက်တစ်ခုဖန်တီးသောအခါ (ရိုးရှင်းသောအမှာစာအစား) ဖိုလ်ဒါ-သစ်ပင်ရွေးချယ်မှုကိုပြသပါ။",
    "settings.branchDialog": "ကိုင်း ဒိုင်ယာလော့ခ်",
    "settings.branchDialogSvnTitle": "SVN ပုံစံ",
    "settings.branchDialogNativeVscode": "VS ကုဒ်စတိုင်",
    "settings.branchDialogNativeVs": "Visual Studio စတိုင်",
    "settings.branchDialogNativeHint": "သင့် IDE ၏ built-in ဌာနခွဲအမှာစာ။",
    "settings.close": "ပိတ်လိုက်",
    "settings.done": "ပြီးပြီ။",
    "settings.sectionAdvanced": "အဆင့်မြင့်",
    "settings.gitSource": "Git အကောင်ထည်ဖော်နိုင်ပါတယ်။",
    "settings.gitSourceBuiltin": "တပ်ဆင်ပြီး",
    "settings.gitSourceBuiltinHint": "IDE ၏ built-in Git တိုးချဲ့မှုမှ git binary ကိုသုံးပါ။",
    "settings.gitSourceCustom": "စိတ်ကြိုက်ပြောမယ်။",
    "settings.gitSourceCustomHint": "သင့်ကိုယ်ပိုင် git လည်ပတ်နိုင်သောလမ်းကြောင်းကို သတ်မှတ်ပါ (ဥပမာ /usr/bin/git သို့မဟုတ် C:\\⢦\\git.exe)။",
    "settings.gitPath": "git သို့သွားရာလမ်း",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ကြည့်ရှုရန်...",
    "settings.jargon": "Git ဝေါဟာရများ",
    "settings.jargonTranslate": "ဘာသာပြန်ပါ။",
    "settings.jargonEnglish": "အင်္ဂလိပ်လို သိမ်းထားပါ။",
    "settings.jargonTranslateHint": "Git ဝေါဟာရများ (ဆွဲ၊ တွန်း၊ လုပ်၊ အကိုင်းအခက်…) ကို အင်တာဖေ့စ်ဘာသာစကားသို့ ဘာသာပြန်ပါ။",
    "settings.jargonEnglishHint": "Keep Git ဝေါဟာရများကို အင်္ဂလိပ်လို (ဆွဲ၊ တွန်း၊ ကတိ၊ ကိုင်း…)၊ အခြားအရာအားလုံးကို ဘာသာပြန်ပါ။",
    "settings.sectionMerge": "ပေါင်းစည်းပါ။",
    "settings.mergeMode": "ပေါင်းစပ်စတိုင်",
    "settings.mergeModeMerge": "ပုံမှန်ပေါင်းစည်းခြင်း။",
    "settings.mergeModeSquash": "Squash ပေါင်းစပ်ခြင်း။",
    "settings.mergeModeMergeHint": "ဌာနခွဲကို ၎င်းကဲ့သို့ ပေါင်းစည်းခြင်း- ၎င်း၏ ကတိကဝတ်များသည် ပေါင်းစည်းမှု ကတိကဝတ်ဖြင့် ပေါင်းစည်းခြင်း (သို့မဟုတ် ဖြစ်နိုင်လျှင် အမြန်ပေးပို့သည်)။",
    "settings.mergeModeSquashHint": "အကိုင်းအခက်တစ်ခုလုံးကို လက်ရှိအကိုင်းအခက်ပေါ်ရှိ တစ်ခုတည်းသော ကတိပြုချက်အဖြစ် ပြိုကျစေသည် (git merge --squash)။ ဌာနခွဲ၏ကိုယ်ပိုင်ကတိက၀တ်များသည် လက်ရှိဌာနခွဲ၏သမိုင်းကို ဘယ်သောအခါမှမထည့်ပါ — ထိုတွင် ကတိတစ်ခုသာမြင်ရသည်။ အကိုင်းအခက်ကိုယ်နှိုက်ကို မထိမထိထားသောကြောင့် ၎င်း၏ ကတိကဝတ်များကို ၎င်း၏လမ်းကြောတွင် မြင်နေရပါသည်။",
    "settings.mergedView": "ဂရပ်တွင် ပေါင်းစည်းခြင်း ကျူးလွန်သည်။",
    "settings.mergedViewBranch": "ကိုယ့်အခက်တွေချည်းပဲ။",
    "settings.mergedViewTarget": "အကိုင်းအခက်တွင်လည်း ပေါင်းစည်းခဲ့သည်။",
    "settings.mergedViewBranchHint": "ပေါင်းစည်းထားသော ဌာနခွဲတစ်ခု၏ ကတိကဝတ်များကို ၎င်းတို့၏ ကိုယ်ပိုင်လမ်းကြောင်းတွင်သာ ရေးဆွဲထားသည်။ ပေါင်းစည်းမှုသည် ချိတ်ဆက်ကိရိယာတစ်ခုအဖြစ် ပေါ်လာသည်။ ဂန္ထဝင်အမြင်။",
    "settings.mergedViewTargetHint": "ပေါင်းစည်းခြင်း ကတိကဝတ်များကို လက်ခံဌာနခွဲ၏လမ်းကြောတွင် ရေးဆွဲထားပြီး၊ ပေါင်းစည်းမှု ကတိကဝတ်အောက်တွင် အစီအမံများ နှင့် မူလပုံစံများနှင့် အဆင့်ဖြစ်သည် — ထိုဌာနခွဲသည် ယခု အမှန်တကယ် ပါဝင်နေသောကြောင့် ဖြစ်သည်။ မိတ္တူများသည် အရောင်ဖျော့ပြီး သူတို့ရေးထားသော အကိုင်းအခက်များဖြင့် ဦးခေါင်းပါသောကြောင့် ၎င်းတို့ကို ထိုနေရာတွင် မပြုလုပ်ခဲ့ခြင်းမှာ ထင်ရှားပါသည်။ Squash ပေါင်းစည်းခြင်းကို သက်ရောက်မှုမရှိပါ- squash သည် သာမန် commit တစ်ခုကို ရေးသားသည်၊ ထို့ကြောင့် ထိုကတိကသာလျှင် အမြဲပေါ်လာပါသည်။",
    "legend.title": "ဒဏ္ဍာရီ",
    "legend.head": "HEAD / လက်ရှိဌာနခွဲ",
    "legend.local": "ပြည်တွင်းဘဏ်ခွဲ",
    "legend.remote": "အဝေးကိုင်း၊",
    "legend.remoteOnly": "တိမ်ထဲတွင်သာ (မဆွဲ)၊",
    "legend.tag": "တဂ် (ဗားရှင်း)",
    "legend.commit": "ကျူးလွန်သည်။",
    "legend.stash": "Stash (ဘေးမဲ့အလုပ်)",
    "legend.mergedIn": "ပေါင်းစည်းခြင်း (အခြားအကိုင်းအခက်တွင် ရေးထားသည်)၊",
    "legend.nodes": "သေတ္တာများ",
    "legend.lines": "လိုင်းများ",
    "legend.edgeParent": "ယခင် commit သည် တူညီသောလိုင်းဖြစ်သည်။",
    "legend.edgeMerge": "ပေါင်းစည်းထားသောဌာနခွဲ — ပေါင်းစည်းမှုတွင် မြှားညွှန်သည်။",
    "legend.edgeBranch": "ဤနေရာတွင် သစ်ကိုင်းတစ်ကိုင်း ကျိုးသွားသည်",
    "legend.edgeStash": "Stash နှင့် commit တို့ကို သိမ်းဆည်းထားသည်။",
    "legend.edgeMergedChain": "ပေါင်းစည်းထားသော ကွန်မန့်များမှတဆင့် ဤဌာနခွဲ၏ လိုင်း",
    "legend.edgeMergedTie": "ဌာနခွဲမှာ ရေးထားတဲ့ ကတိကဝတ်အတိုင်းပါပဲ။",
    "menu.jumpHead": "⌖ ငွေရှင်းရန် သွားပါ။",
    "menu.resetView": "⤢ မြင်ကွင်းကို ပြန်လည်သတ်မှတ်ပါ။",
    "menu.createBranch": "ဤနေရာမှ ဘဏ်ခွဲကို ဖန်တီးပါ...",
    "menu.checkout": "ဒီကွန်မန့်ကို စစ်ကြည့်ပါ။",
    "menu.copySha": "commit SHA ကို ကူးယူပါ။",
    "menu.pushBranch": "Push ဌာနခွဲ \"{name}\"",
    "menu.renameBranch": "ဌာနခွဲ \"{name}\" အမည်ပြောင်း",
    "menu.deleteBranch": "အကိုင်းအခက် \"{name}\"â ¢ ဖျက်ပါ။",
    "menu.renameCommit": "commit message ကို အမည်ပြောင်းပါ...",
    "menu.undoCommit": "ကတိကဝတ်ကို ပြန်ဖျက်ပါ (အပြောင်းအလဲများကို သိမ်းဆည်းပါ)…",
    "menu.viewChanges": "အပြောင်းအလဲများကိုကြည့်ပါ…",
    "menu.mergeBranch": "\"{source}\" ကို \"{target}\" နှင့် ပေါင်းစည်းပါ။",
    "menu.jumpToOriginal": "မူရင်း commit သို့သွားပါ။",
    "node.mergedInTooltip": "{merge} သည် ဤကတိကဝတ်ကို \"{target}\" သို့ ပေါင်းစည်းပြီး \"{origin}\" တွင် ရေးသားထားသည်။",
    "changes.title": "{sha} တွင်ပြောင်းလဲမှုများ",
    "changes.tabChanged": "ပြောင်းသွားတယ်။",
    "changes.tabAll": "ဖိုင်များအားလုံး",
    "changes.added": "ထပ်ပြောပါသည်။",
    "changes.modified": "ပြင်ဆင်ထားသည်။",
    "changes.deleted": "ဖျက်လိုက်",
    "changes.renamed": "အမည်ပြောင်းသည်။",
    "changes.noChanges": "ဤကျူးလွန်မှုသည် ဖိုင်များကို ပြောင်းလဲခြင်းမရှိပါ။",
    "changes.loading": "ကွဲပြားမှုကို ဖွင့်နေသည်…",
    "changes.selectFile": "၎င်း၏ပြောင်းလဲမှုများကိုကြည့်ရှုရန် ဖိုင်တစ်ခုကို ရွေးချယ်ပါ။",
    "changes.original": "မူရင်း",
    "changes.changed": "ဒီကတိကဝတ်",
    "changes.binary": "Binary ဖိုင် — ပြရန် စာသားကွာခြားချက်မရှိပါ။",
    "changes.tooLarge": "ဖိုင်သည် ကွဲပြားရန် ကြီးလွန်းသည်။",
    "changes.renamedFrom": "{path} မှ အမည်ပြောင်းထားသည်။",
    "changes.close": "ပိတ်လိုက်",
    "changes.prevChange": "ယခင်ပြောင်းလဲမှု",
    "changes.nextChange": "နောက်ပြောင်းလဲမှု",
    "changes.collapseAll": "ဖိုင်တွဲအားလုံးကို ခေါက်သိမ်းပါ။",
    "changes.expandAll": "ဖိုင်တွဲအားလုံးကို ချဲ့ပါ။",
    "changes.searchPlaceholder": "ဖိုင်များကို ရှာဖွေပါ...",
    "changes.noSearchResults": "သင့်ရှာဖွေမှုနှင့် ကိုက်ညီသောဖိုင်များမရှိပါ။",
    "changes.maximize": "ချဲ့ပါ။",
    "changes.restore": "အရွယ်အစားကို ပြန်ယူပါ။",
    "find.open": "ဤကွဲပြားမှုတွင် ရှာဖွေပါ (Ctrl+F)",
    "find.placeholder": "ကွဲပြားမှုကိုရှာပါ...",
    "find.prev": "ယခင်ပွဲ",
    "find.next": "နောက်ပွဲ",
    "find.close": "ရှာဖွေမှုကို ပိတ်ပါ။",
    "find.noResults": "ရလဒ်မရှိပါ။",
    "commit.title": "အပြောင်းအလဲများကို ကျူးလွန်ပါ။",
    "commit.close": "ပိတ်လိုက်",
    "commit.loading": "သစ်ပင်ဖတ်ခြင်း...",
    "commit.noChanges": "ကတိပြုရန် ဒေသဆိုင်ရာ အပြောင်းအလဲများ မရှိပါ။",
    "commit.selectFile": "၎င်း၏ကွဲပြားမှုကိုကြည့်ရန် ဖိုင်တစ်ခုကို ရွေးပါ။",
    "commit.selectAll": "အားလုံးကို ရွေးပါ။",
    "commit.selectNone": "အားလုံးကိုရှင်းပါ။",
    "commit.message": "မက်ဆေ့ချ်ပေးပါ။",
    "commit.messagePlaceholder": "ပြောင်းလဲမှုကို ဖော်ပြပါ။",
    "commit.selectedCount": "{count} ဖိုင်(များ)ကို ရွေးထားသည်။",
    "commit.review": "သုံးသပ်ချက်",
    "commit.reviewTitle": "ကျူးလွန်ခြင်းမပြုမီ အပြီးသတ်စစ်ဆေးပါ။",
    "commit.reviewSummary": "{count} ဖိုင်(များ)ကို မက်ဆေ့ချ်ဖြင့် ပေးပို့ပါ- {message}",
    "commit.back": "ကျော",
    "commit.commit": "ဒေသတွင်းကတိကဝတ်ကိုဖန်တီးပါ။",
    "menu.stashApply": "သိုလှောင်မှုကို အသုံးပြုပါ။",
    "menu.stashPop": "Pop stash (အသုံးချပြီး ဖယ်ရှားရန်)",
    "menu.stashDrop": "သိမ်းဆည်းရန်...",
    "merge.title": "ဘဏ်ခွဲပေါင်းစည်းပါ။",
    "merge.route": "လက်ရှိဌာနခွဲသို့ ပေါင်းစည်းခြင်း။",
    "merge.source": "မှ (အရင်းအမြစ်)",
    "merge.target": "သို့ (လက်ရှိ)",
    "merge.loading": "ပေါင်းစည်းမှုကို ပိုင်းခြားစိတ်ဖြာနေသည်...",
    "merge.upToDate": "အပ်ဒိတ်ဖြစ်နေပါပြီ။",
    "merge.fastForward": "ရှေ့သို့ အမြန် — ပေါင်းစည်းရန် ကတိကဝတ် မလိုအပ်ပါ (တစ်ခုအား တွန်းအားပေးရန် \"ပေါင်းစည်းမှု ဖန်တီးရန်\" ကို အမှန်ခြစ်ပါ။",
    "merge.summary": "{files} ဖိုင်(များ) ပြောင်းသွားသည်၊ {conflicts} ပဋိပက္ခ(များ)။",
    "merge.conflictsWarning": "ဤပေါင်းစည်းမှုတွင် ကွဲလွဲမှုများရှိနေသည် — သင် ပေါင်းစည်းနိုင်ပါသေးသည်၊ ထို့နောက် ၎င်းတို့ကို တည်းဖြတ်သူတွင် ဖြေရှင်းပါ။",
    "merge.noChanges": "ဖိုင်အပြောင်းအလဲမရှိပါ။",
    "merge.files": "ရလဒ်အပြောင်းအလဲများ",
    "merge.added": "ထပ်ပြောပါသည်။",
    "merge.modified": "ပြင်ဆင်ထားသည်။",
    "merge.deleted": "ဖျက်လိုက်",
    "merge.conflict": "ပဋိပက္ခ",
    "merge.message": "စာတိုကို ပေါင်းစည်းပါ။",
    "merge.messagePlaceholder": "ဌာနခွဲ 'အရင်းအမြစ်' ပေါင်းစည်းခြင်း",
    "merge.messageFfHint": "လျင်မြန်သော ပေါင်းစည်းမှုတွင် လျစ်လျူရှုခဲ့သည် (ကတိကဝတ်ကို မဖန်တီးထား)။",
    "merge.noFastForward": "ပေါင်းစည်းမှုတစ်ခုဖန်တီးပါ (အလျင်အမြန် ရှေ့သို့မတိုးပါ)",
    "merge.noFastForwardHint": "ဌာနခွဲက မြန်မြန်ဆန်ဆန် ရှေ့တိုးနိုင်ရင်တောင် ပေါင်းစည်းရေးကတိကို အမြဲမှတ်တမ်းတင်ပါ။",
    "merge.merge": "ပေါင်းစည်းပါ။",
    "merge.cancel": "မလုပ်တော့",
    "merge.previewError": "အစမ်းကြည့်ရှုမှုကို မတွက်ချက်နိုင်ခဲ့ပါ- {message}",
    "merge.squashBadge": "စကွပ်ရှ်",
    "merge.squashNote": "Squash ပေါင်းစည်းခြင်း — “{target}” တွင် ONE ကတိကဝတ်အဖြစ် ပြောင်းလဲမှုတိုင်းသည် မြေနေရာ၊ “{source}” ၏ ကိုယ်ပိုင် ကတိကဝတ်များသည် ၎င်း၏သမိုင်းနှင့် မကင်းပါ။ ဆက်တင်များ › ပေါင်းစည်းခြင်းတွင် ပြန်ပြောင်းပါ။",
    "merge.messageSquash": "မက်ဆေ့ချ်ပေးပါ။",
    "merge.squashMessagePlaceholder": "'ရင်းမြစ်' မှ Squashed အပြောင်းအလဲများ",
    "newBranch.title": "ဘဏ်ခွဲကိုဖန်တီးပါ။",
    "newBranch.startPoint": "{sha} မှ စတင်သော ဘဏ်ခွဲအသစ်",
    "newBranch.startPointOn": "{refs} တွင်",
    "newBranch.location": "တည်နေရာ",
    "newBranch.locationRoot": "(အမြစ်)",
    "newBranch.expandAll": "အားလုံးကိုချဲ့ပါ။",
    "newBranch.collapseAll": "အားလုံးကို ခေါက်သိမ်းပါ။",
    "newBranch.expandFolder": "ဖိုင်တွဲကိုချဲ့ပါ။",
    "newBranch.collapseFolder": "ဖိုင်တွဲကို ခေါက်သိမ်းပါ။",
    "newBranch.name": "ဘဏ်ခွဲအမည်",
    "newBranch.namePlaceholder": "ငါ့ကိုင်း၊",
    "newBranch.fullName": "နာမည်အပြည့်အစုံ- {name}",
    "newBranch.invalid": "ဌာနခွဲအမည် မမှန်ကန်ပါ။",
    "newBranch.exists": "ဤအမည်ဖြင့် ဌာနခွဲတစ်ခု ရှိနှင့်ပြီးဖြစ်သည်။",
    "newBranch.checkout": "ဖန်တီးပြီးနောက် ငွေထုတ်ဌာနခွဲ",
    "newBranch.create": "ဖန်တီးပါ။",
    "newBranch.cancel": "မလုပ်တော့",
    "status.loading": "ဂရပ်ကို ဖွင့်နေသည်…",
    "status.summary": "{repo} ပြသခြင်း {commits} ကတိပြုသည်၊ {refs} ကိုးကားချက်",
    "status.branchCreated": "{sha} တွင် ဌာနခွဲ \"{name}\" ကို ဖန်တီးခဲ့သည်။",
    "status.error": "အမှား- {message}",
    "status.opFailed": "လည်ပတ်မှု မအောင်မြင်ပါ။",
    "status.fetching": "ရယူနေသည်...",
    "status.pulling": "ဆွဲနေသည်...",
    "status.pushing": "တွန်းနေသည်...",
    "status.committing": "ဒေသတွင်းကတိကဝတ်ကို ဖန်တီးနေသည်...",
    "status.commitCreated": "ပြည်တွင်းကတိပြုချက် {sha}: {message} ကို ဖန်တီးထားသည်။",
    "status.syncing": "စင့်ခ်လုပ်နေသည်...",
    "status.noHead": "ဂရပ်တွင် လက်ရှိငွေပေးချေမှု မတွေ့ပါ။",
    "status.undoing": "ကတိကဝတ်ကို ပြန်ဖျက်နေသည်…",
    "status.commitUndone": "ကတိမတည်ခြင်း - အပြောင်းအလဲများသည် အလုပ်လုပ်သည့်သစ်ပင်တွင် ပြန်လည်ရောက်ရှိနေပါသည်။",
    "status.undoConflict": "ထိမိနေသော ပဋိပက္ခများကို ပြန်ဖျက်ပါ — တည်းဖြတ်သူတွင် ၎င်းတို့ကို ဖြေရှင်းပါ၊ ထို့နောက် ဆက်လက်လုပ်ဆောင်ပါ။",
    "status.stashApplying": "သိုလှောင်မှုကို သုံးနေသည်…",
    "status.stashPopping": "အမှိုက်ပုံးပေါ်လာသည်...",
    "status.stashDropping": "သိမ်းဆည်းနေသည်...",
    "status.stashApplied": "Stash သုံးထားသည်။",
    "status.stashPopped": "Stash ပေါ်လာသည်။",
    "status.stashDropped": "Stash ပြုတ်ကျသွားတယ်။",
    "status.stashConflict": "ပဋိပက္ခများကို သိမ်းဆည်းပါ — တည်းဖြတ်သူတွင် ၎င်းတို့ကို ဖြေရှင်းပါ။",
    "status.merging": "ပေါင်းစည်းခြင်း…",
    "status.merged": "ပေါင်းစည်းပြီးပါပြီ။",
    "status.mergeConflict": "ပေါင်းစည်းခြင်းတွင် ပဋိပက္ခများရှိသည် — ၎င်းတို့ကို တည်းဖြတ်သူတွင် ဖြေရှင်းပါ၊ ထို့နောက် ကတိပြုပါ။",
    "search.title": "ရှာရန်",
    "search.tooltip": "ရှာဖွေမှု ကျူးလွန်သည်။",
    "search.placeholder": "ရှာဖွေမှုလုပ်ဆောင်သည်...",
    "search.previous": "ယခင်ရလဒ်",
    "search.next": "နောက်ရလဒ်",
    "search.noResults": "ရလဒ်မရှိပါ။",
    "search.resultCount": "{index}/{count}",
    "search.filter": "စစ်ထုတ်ခြင်းမုဒ်",
    "search.highlight": "အသားပေးမုဒ်",
    "search.filterMode": "ရှာဖွေမှုပြသခြင်း။",
    "search.filterModeFilter": "စစ်ထုတ်ခြင်း (မတူညီမှုများကို ဖျောက်ပါ)",
    "search.filterModeHighlight": "အသားပေးဖော်ပြပါ (အားလုံးပြပါ၊ ကိုက်ညီမှုအမှတ်အသားပါ)",
    "search.filterModeHint": "ဂရပ်တွင် ရှာဖွေမှုရလဒ်များကို မည်သို့ပြသမည်ကို ရွေးချယ်ပါ။",
    "search.visibility": "တည်နေရာကို ရှာဖွေပါ။",
    "search.visibilityToolbar": "Toolbar ကိုနှိပ်ပါ။",
    "search.visibilityAlwaysVisible": "အမြဲတမ်းမြင်နိုင်သောဘား",
    "search.visibilityHint": "ရှာဖွေမှုကို ကိရိယာဘားသင်္ကေတ သို့မဟုတ် ထိပ်ရှိ ဆက်တိုက်ဘားအဖြစ် ပြသပါ။",
    "footer.github": "GitHub",
  },
  ka: {
    "toolbar.refresh": "⟳ განაახლეთ",
    "toolbar.fetch": "⤓ მოტანა",
    "toolbar.pull": "⇩ გაიყვანეთ",
    "toolbar.push": "⇧ ბიძგი",
    "toolbar.commit": "✓ ვალდებულება",
    "toolbar.sync": "⇅ სინქრონიზაცია",
    "toolbar.jumpHead": "⌖ გადადით შეკვეთაზე",
    "toolbar.reset": "⤢ გადატვირთეთ ხედი",
    "toolbar.settings": "⚙ პარამეტრები",
    "details.header": "ჩააბარეთ დეტალები",
    "details.close": "დახურვა",
    "details.sha": "SHA",
    "details.shortSha": "მოკლე SHA",
    "details.message": "შეტყობინება",
    "details.author": "ავტორი",
    "details.date": "თარიღი",
    "details.labels": "ეტიკეტები",
    "details.location": "მდებარეობა",
    "details.currentHead": "HEAD - მიმდინარე გადახდა",
    "settings.title": "პარამეტრები",
    "settings.language": "ენა",
    "settings.theme": "თემა",
    "settings.themeLight": "სინათლე",
    "settings.themeDark": "ბნელი",
    "settings.themeLightHint": "ღია ფონი, მუქი ტექსტი.",
    "settings.themeDarkHint": "მუქი ფონი, ღია ტექსტი.",
    "settings.mainBranch": "მთავარი ფილიალი",
    "settings.mainBranchAuto": "ავტომატური",
    "settings.mainBranchSearch": "ფილიალების ძიება…",
    "settings.mainBranchNoMatch": "შესაბამისი ტოტები არ არის",
    "settings.display": "ჩვენების სტილი",
    "settings.displayModern": "თანამედროვე",
    "settings.displayClassic": "კლასიკური",
    "settings.displayModernHint": "უფასო ტილო — გადაათრიეთ გადასატანად, გადაახვიეთ გასადიდებლად.",
    "settings.displayClassicHint": "ფიქსირებული ტილო — საყრდენი მიმაგრებულია მარცხნივ, მასშტაბირების გარეშე, მხოლოდ გადახვევა (როგორც SVN რევიზიის გრაფიკი).",
    "settings.sectionGeneral": "გენერალი",
    "settings.sectionGraph": "გრაფიკი",
    "settings.sectionChanges": "ცვლის ხედს",
    "settings.diffMinimap": "განსხვავებების მინი რუკა",
    "settings.diffMinimapOn": "ნაჩვენებია",
    "settings.diffMinimapOff": "დამალული",
    "settings.diffMinimapOnHint": "VS Code-ის სტილის მიმოხილვის ზოლის ჩვენება განსხვავების გვერდით — გადაიტანეთ გადახვევისთვის; მოყვება შეცვლის მარკერები.",
    "settings.diffMinimapOffHint": "მიმოხილვის ზოლი არ არის - ჩვეულებრივ გადაახვიეთ განსხვავება.",
    "settings.commitReview": "მიმოხილვის ვალდებულება",
    "settings.commitReviewOn": "იკითხეთ ჩადენამდე",
    "settings.commitReviewOff": "პირდაპირი ვალდებულება",
    "settings.commitReviewOnHint": "აჩვენეთ საბოლოო ფაილების სია და შეტყობინებების შემოწმება ლოკალური commit-ის შექმნამდე.",
    "settings.commitReviewOffHint": "Commit ღილაკი დაუყოვნებლივ ქმნის ადგილობრივ commit-ს.",
    "settings.svnBranchDialog": "SVN სტილის ფილიალის დიალოგი",
    "settings.svnBranchDialogHint": "ფილიალის შექმნისას აჩვენეთ საქაღალდე-ხის ამომრჩევი (უბრალო მოთხოვნის ნაცვლად).",
    "settings.branchDialog": "ფილიალის დიალოგი",
    "settings.branchDialogSvnTitle": "SVN სტილი",
    "settings.branchDialogNativeVscode": "VS კოდის სტილი",
    "settings.branchDialogNativeVs": "ვიზუალური სტუდიის სტილი",
    "settings.branchDialogNativeHint": "თქვენი IDE-ის ჩაშენებული ფილიალის მოთხოვნა.",
    "settings.close": "დახურვა",
    "settings.done": "შესრულებულია",
    "settings.sectionAdvanced": "გაფართოებული",
    "settings.gitSource": "Git შესრულებადი",
    "settings.gitSourceBuiltin": "ჩაშენებული",
    "settings.gitSourceBuiltinHint": "გამოიყენეთ git ორობითი IDE-ის ჩაშენებული Git გაფართოებიდან.",
    "settings.gitSourceCustom": "მორგებული გზა",
    "settings.gitPath": "გზა გით",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "დათვალიერება…",
    "settings.jargon": "Git პირობები",
    "settings.jargonTranslate": "თარგმნეთ",
    "settings.jargonEnglish": "შეინახეთ ინგლისურად",
    "settings.jargonTranslateHint": "თარგმნეთ Git-ის ტერმინები (გაყვანილობა, დაყენება, ჩადენა, განშტოება…) ინტერფეისის ენაზე.",
    "settings.jargonEnglishHint": "შეინახეთ Git ტერმინები (pull, push, commit, branch…) ინგლისურად; თარგმნეთ ყველაფერი დანარჩენი.",
    "settings.sectionMerge": "შერწყმა",
    "settings.mergeMode": "შერწყმის სტილი",
    "settings.mergeModeMerge": "ნორმალური შერწყმა",
    "settings.mergeModeSquash": "Squash შერწყმა",
    "settings.mergeModeMergeHint": "აერთიანებს ფილიალს ისე, როგორც არის: მისი ვალდებულებები ხდება მიმდინარე ფილიალის ისტორიის ნაწილი, რომელსაც უერთდება შერწყმის ვალდებულება (ან გადადის სწრაფად, როდესაც ეს შესაძლებელია).",
    "settings.mergeModeSquashHint": "აკეცავს მთელ ფილიალს ერთ commit-ად მიმდინარე ფილიალზე (git merge --squash). ფილიალის საკუთარი ვალდებულებები არასოდეს შედის ამჟამინდელი ფილიალის ისტორიაში - იქ მხოლოდ ერთ ჩადენას ხედავთ. თავად ტოტი ხელუხლებელი რჩება, ამიტომ მისი ვალდებულებები თვალსაჩინო რჩება საკუთარ შესახვევში.",
    "settings.mergedView": "შერწყმული ჩადენები გრაფიკში",
    "settings.mergedViewBranch": "მხოლოდ საკუთარ ფილიალზე",
    "settings.mergedViewTarget": "ასევე ფილიალში ისინი გაერთიანდნენ",
    "settings.mergedViewBranchHint": "გაერთიანებული ფილიალის ვალდებულებები შედგენილია მხოლოდ საკუთარ ხაზში; შერწყმა გამოჩნდება როგორც დამაკავშირებელი. კლასიკური ხედი.",
    "settings.mergedViewTargetHint": "შემოტანილი შერწყმის დავალებები შედგენილია მიმღები ფილიალის ზოლშიც, დაწყობილია შერწყმის ჩადენის ქვეშ და ტოლდება ორიგინალებთან - რადგან ეს ფილიალი ნამდვილად შეიცავს მათ ახლა. ასლები ფერმკრთალია და დაწერილია იმ ტოტით, რომელზეც ეწერა, ასე რომ, ცხადი რჩება, რომ ისინი იქ არ გაკეთებულა. Squash-ის შერწყმა გავლენას არ ახდენს: squash წერს ერთ ჩვეულებრივ commit-ს, ასე რომ, მხოლოდ ეს commit გამოჩნდება.",
    "legend.title": "ლეგენდა",
    "legend.head": "HEAD / მიმდინარე ფილიალი",
    "legend.local": "ადგილობრივი ფილიალი",
    "legend.remote": "დისტანციური ფილიალი",
    "legend.remoteOnly": "მხოლოდ ღრუბელში (არ გამოყვანილია)",
    "legend.tag": "ტეგი (ვერსია)",
    "legend.commit": "ვალდებულება",
    "legend.stash": "Stash (თაროზე დაწყობილი სამუშაო)",
    "legend.mergedIn": "შემოვიდა შერწყმით (დაწერილია სხვა ფილიალზე)",
    "legend.nodes": "ყუთები",
    "legend.lines": "ხაზები",
    "legend.edgeParent": "წინა ჩადენა იმავე ხაზზე",
    "legend.edgeMerge": "გაერთიანებული ფილიალი - ისარი მიუთითებს შერწყმის დასრულებაზე",
    "legend.edgeBranch": "აქ ტოტი გაწყდა",
    "legend.edgeStash": "Stash და ვალდებულება, რომელზედაც თაროზე იყო განთავსებული",
    "legend.edgeMergedChain": "ამ ფილიალის ხაზი გადის გაერთიანებული კომიტების მეშვეობით",
    "legend.edgeMergedTie": "იგივე ვალდებულება ტოტზე ეწერა",
    "menu.jumpHead": "⌖ გადადით შეკვეთაზე",
    "menu.resetView": "⤢ გადატვირთეთ ხედი",
    "menu.createBranch": "შექმენით ფილიალი აქედან…",
    "menu.checkout": "შეამოწმეთ ეს ვალდებულება",
    "menu.copySha": "დააკოპირეთ commit SHA",
    "menu.pushBranch": "Push ფილიალი \"{name}\"",
    "menu.deleteBranch": "წაშალეთ ფილიალი \"{name}\" --¦",
    "menu.renameCommit": "გადარქმევის შეტყობინების გაგზავნა…",
    "menu.undoCommit": "ვალდებულების გაუქმება (ცვლილებების შენარჩუნება)…",
    "menu.viewChanges": "ცვლილებების ნახვა…",
    "menu.mergeBranch": "შეაერთეთ „{source}“ „{target}“-ში",
    "menu.jumpToOriginal": "გადადით თავდაპირველ დავალებაში",
    "node.mergedInTooltip": "{merge}-ის შერწყმამ შემოიტანა ეს ვალდებულება \"{target}\"-ში - ის დაიწერა \"{origin}\"-ზე.",
    "changes.title": "ცვლილებები {sha}-ში",
    "changes.tabChanged": "შეიცვალა",
    "changes.tabAll": "ყველა ფაილი",
    "changes.added": "დამატებულია",
    "changes.modified": "შეცვლილია",
    "changes.deleted": "წაშლილია",
    "changes.renamed": "გადაერქვა",
    "changes.noChanges": "ეს commit არ ცვლის ფაილებს.",
    "changes.loading": "იტვირთება განსხვავება…",
    "changes.selectFile": "აირჩიეთ ფაილი მისი ცვლილებების სანახავად.",
    "changes.original": "ორიგინალი",
    "changes.changed": "ეს ვალდებულება",
    "changes.binary": "ორობითი ფაილი — ტექსტის საჩვენებლად განსხვავება არ არის.",
    "changes.tooLarge": "ფაილი ძალიან დიდია განსხვავებისთვის.",
    "changes.renamedFrom": "გადაერქვა {path}-დან",
    "changes.close": "დახურვა",
    "changes.prevChange": "წინა ცვლილება",
    "changes.nextChange": "შემდეგი ცვლილება",
    "changes.collapseAll": "ყველა საქაღალდის ჩაკეცვა",
    "changes.expandAll": "გააფართოვეთ ყველა საქაღალდე",
    "changes.searchPlaceholder": "ფაილების ძიება…",
    "changes.noSearchResults": "არცერთი ფაილი არ შეესაბამება თქვენს ძიებას.",
    "changes.maximize": "მაქსიმიზაცია",
    "changes.restore": "ზომის აღდგენა",
    "find.open": "ძიება ამ განსხვავებაში (Ctrl+F)",
    "find.placeholder": "იპოვე განსხვავებაში…",
    "find.prev": "წინა მატჩი",
    "find.next": "შემდეგი მატჩი",
    "find.close": "დახურეთ ძებნა",
    "find.noResults": "არანაირი შედეგი",
    "commit.title": "ცვლილებების შესრულება",
    "commit.close": "დახურვა",
    "commit.loading": "სამუშაო ხის კითხვა…",
    "commit.noChanges": "ადგილობრივი ცვლილებები არ არის შესასრულებელი.",
    "commit.selectFile": "აირჩიეთ ფაილი მისი განსხვავების სანახავად.",
    "commit.selectAll": "აირჩიეთ ყველა",
    "commit.selectNone": "გაასუფთავე ყველა",
    "commit.message": "შეტყობინების გაგზავნა",
    "commit.messagePlaceholder": "აღწერეთ ცვლილება",
    "commit.selectedCount": "არჩეულია {count} ფაილ(ები).",
    "commit.review": "მიმოხილვა",
    "commit.reviewTitle": "საბოლოო შემოწმება ჩადენამდე",
    "commit.reviewSummary": "ჩააბარეთ {count} ფაილ(ები) შეტყობინებით: {message}",
    "commit.back": "უკან",
    "commit.commit": "შექმენით ლოკალური ვალდებულება",
    "menu.stashApply": "წაისვით შენახვა",
    "menu.stashPop": "Pop stash (გამოიყენეთ და წაშალეთ)",
    "menu.stashDrop": "ჩამოაგდე სათავსო…",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.source": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.target": "შევიდა (მიმდინარე)",
    "merge.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.upToDate": "უკვე განახლებულია - შერწყმა არაფერია.",
    "merge.fastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.summary": "{files} ფაილ(ები) შეიცვალა, {conflicts} კონფლიქტ(ებ)ი.",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "ჩემი ფილიალი",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "ფილიალი ამ სახელით უკვე არსებობს",
    "newBranch.checkout": "შეკვეთის ფილიალი შექმნის შემდეგ",
    "newBranch.create": "შექმნა",
    "newBranch.cancel": "გაუქმება",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "შეიქმნა ფილიალი \"{name}\" {sha}-ზე",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "გაყვანა…",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "მიმდინარეობს ადგილობრივი კომისიის შექმნა…",
    "status.commitCreated": "შეიქმნა ლოკალური ვალდებულება {sha}: {message}",
    "status.syncing": "სინქრონიზაცია…",
    "status.noHead": "ამჟამინდელი გადახდა არ მოიძებნა გრაფიკში.",
    "status.undoing": "ვალდებულების გაუქმება…",
    "status.commitUndone": "დასრულება გაუქმებულია - ცვლილებები დაბრუნდა სამუშაო ხეში.",
    "status.undoConflict": "დარტყმის კონფლიქტების გაუქმება — მოაგვარეთ ისინი რედაქტორში, შემდეგ გააგრძელეთ.",
    "status.stashApplying": "მიმდინარეობს დამახსოვრების გამოყენება…",
    "status.stashPopping": "იშლება სათავსო…",
    "status.stashDropping": "იშლება სათავსო…",
    "status.stashApplied": "Stash-მა მიმართა.",
    "status.stashPopped": "სტეში ამოვარდა.",
    "status.stashDropped": "Stash დაეცა.",
    "status.stashConflict": "Stash კონფლიქტები — მოაგვარეთ ისინი რედაქტორში.",
    "status.merging": "გაერთიანება…",
    "status.merged": "შერწყმა დასრულდა.",
    "status.mergeConflict": "შერწყმას აქვს კონფლიქტები — მოაგვარეთ ისინი რედაქტორში, შემდეგ ჩაიდინეთ.",
    "search.title": "ძიება",
    "search.tooltip": "ძებნა ავალდებულებს",
    "search.placeholder": "ძიება ახორციელებს…",
    "search.previous": "წინა შედეგი",
    "search.next": "შემდეგი შედეგი",
    "search.noResults": "არანაირი შედეგი",
    "search.resultCount": "{index}/{count}",
    "search.filter": "ფილტრის რეჟიმი",
    "search.highlight": "მონიშვნის რეჟიმი",
    "search.filterMode": "ძებნის ჩვენება",
    "search.filterModeFilter": "ფილტრი (არაშესაბამისების დამალვა)",
    "search.filterModeHighlight": "მონიშვნა (ყველას ჩვენება, შესატყვისების მონიშვნა)",
    "search.filterModeHint": "აირჩიეთ, როგორ გამოჩნდება ძიების შედეგები გრაფიკზე.",
    "search.visibility": "მდებარეობის ძიება",
    "search.visibilityToolbar": "ხელსაწყოთა ზოლის ღილაკი",
    "search.visibilityAlwaysVisible": "ყოველთვის ხილული ბარი",
    "search.visibilityHint": "აჩვენეთ ძიება, როგორც ხელსაწყოთა ზოლის ხატულა ან მუდმივი ზოლი ზედა.",
    "footer.github": "GitHub",
  },
  ti: {
    "toolbar.refresh": "⟳ ምሕዳስ",
    "toolbar.fetch": "⤓ ኣምጽኡ",
    "toolbar.pull": "⇩ ስሓብ",
    "toolbar.push": "⇧ ምድፋእ",
    "toolbar.commit": "✓ ቃል ምእታው",
    "toolbar.sync": "⇅ ምትእስሳር",
    "toolbar.jumpHead": "⌖ ናብ ምውጻእ ገንዘብ ኪድ",
    "toolbar.reset": "⤢ ርእይቶ ዳግማይ ምትካል",
    "toolbar.settings": "⚙ ቅጥዕታት",
    "details.header": "ዝርዝር ምፍጻም",
    "details.close": "ዕፁው",
    "details.sha": "ሻ",
    "details.shortSha": "ሓጻር ሻ",
    "details.message": "መልእኽቲ",
    "details.author": "ጸሓፊ",
    "details.date": "ዕለት",
    "details.labels": "መለለዪታት",
    "details.location": "ኣንፈት",
    "details.currentHead": "HEAD — ህሉው ምውጻእ ገንዘብ",
    "settings.title": "ቅጥዕታት",
    "settings.language": "ቋንቋ",
    "settings.theme": "ጭብጢ",
    "settings.themeLight": "ብርሃን",
    "settings.themeDark": "ፀልማት",
    "settings.themeLightHint": "ብርሃን ድሕረ ባይታ፣ ጸሊም ጽሑፍ።",
    "settings.themeDarkHint": "ጸሊም ድሕረ ባይታ፣ ብርሃን ጽሑፍ።",
    "settings.mainBranch": "ቀንዲ ጨንፈር",
    "settings.mainBranchAuto": "ኣውቶማቲክ",
    "settings.mainBranchSearch": "ጨናፍር ድለዩ...",
    "settings.mainBranchNoMatch": "ዝሰማማዕ ጨናፍር የለን",
    "settings.display": "ቅዲ ምርኢት",
    "settings.displayModern": "ዘበናዊ",
    "settings.displayClassic": "ክላሲክ",
    "settings.displayModernHint": "ነጻ ካንቫስ — ናብ ፓን ስሓብ፣ ናብ ምዕባይ ስክሮል።",
    "settings.displayClassicHint": "ጽኑዕ ካንቫስ — ትራንክ ንጸጋም ተሰቒሉ፣ ዙም የለን፣ ስክሮል ጥራይ (ከም SVN ሪቪዥን ግራፍ)።",
    "settings.sectionGeneral": "ሓፈሻዊ",
    "settings.sectionGraph": "ግራፍ",
    "settings.sectionChanges": "ኣረኣእያ ይቕይር",
    "settings.diffMinimap": "ዲፍ ምኒማፕ",
    "settings.diffMinimapOn": "ተራእዩ።",
    "settings.diffMinimapOff": "ሕቡእ",
    "settings.diffMinimapOnHint": "ኣብ ጎኒ እቲ ዲፍ VS Code-style ሓፈሻዊ ትዕዝብቲ ስትሪፕ ኣርኢ — ንምስግጋር ስሓቦ፤ ናይ ለውጢ ምልክታት ተሓዊሱ።",
    "settings.diffMinimapOffHint": "ሓፈሻዊ ትዕዝብቲ ስትሪፕ የለን — ነቲ ፍልልይ ብንቡር ስክሮል ግበሮ።",
    "settings.commitReview": "ዳግመ ግምት ምግባር",
    "settings.commitReviewOn": "ቅድሚ ምፍጻምካ ሕተት።",
    "settings.commitReviewOff": "ብቐጥታ ቃል ምእታው",
    "settings.commitReviewOnHint": "ቅድሚ ንሎካል ኮሚት ምፍጣርካ ናይ መወዳእታ ዝርዝር ፋይላትን መልእኽቲ ምፍታሽን ኣርኢ።",
    "settings.commitReviewOffHint": "እቲ Commit ዝብል መጠወቒ ነቲ local commit ብኡንብኡ ይፈጥር።",
    "settings.svnBranchDialog": "SVN-ቅዲ ጨንፈር ዝርርብ",
    "settings.svnBranchDialogHint": "ጨንፈር ክትፈጥር ከለኻ (ኣብ ክንዲ ቀሊል ምልክት) ፎልደር-ትሪ መምረጺ ኣርኢ።",
    "settings.branchDialog": "ጨንፈር ዝርርብ",
    "settings.branchDialogSvnTitle": "SVN-ቅዲ",
    "settings.branchDialogNativeVscode": "VS ኮድ ቅዲ",
    "settings.branchDialogNativeVs": "ቅዲ ቪዥዋል ስቱድዮ",
    "settings.branchDialogNativeHint": "ናይ IDE ናትካ ኣብ ውሽጢ ዝተሃንጸ ጨንፈር ምልክት።",
    "settings.close": "ዕፁው",
    "settings.done": "ምውዳእ",
    "settings.sectionAdvanced": "ዝተመሓየሸ",
    "settings.gitSource": "Git ፈጻሚ ዝኾነ",
    "settings.gitSourceBuiltin": "ኣብ ውሽጢ ዝተሃንጸ",
    "settings.gitSourceBuiltinHint": "ካብቲ ናይ IDE ኣብ ውሽጡ ዘሎ Git መቐጸልታ git binary ተጠቐም።",
    "settings.gitSourceCustom": "ብሕታዊ መንገዲ",
    "settings.gitPath": "መንገዲ ናብ git",
    "settings.gitPathPlaceholder": "/usr/bin/git ዝብል ጽሑፍ ኣሎ።",
    "settings.gitPathBrowse": "ዳህሰስ...",
    "settings.jargon": "ናይ ጊት ቃላት",
    "settings.jargonTranslate": "ምትርጓም",
    "settings.jargonEnglish": "ብእንግሊዝኛ ምሓዝ",
    "settings.jargonTranslateHint": "ቃላት Git (ስሕበት፡ ምድፋእ፡ ምፍጻም፡ ጨንፈር...) ናብ ቋንቋ መተሓላለፊ ተርጉሞም።",
    "settings.jargonEnglishHint": "ቃላት Git (pull, push, commit, branch...) ብእንግሊዝኛ ምሓዝ፤ ካልእ ኩሉ ተርጉምዎ።",
    "settings.sectionMerge": "ምውህሃድ",
    "settings.mergeMode": "ቅዲ ምውህሃድ",
    "settings.mergeModeMerge": "ንቡር ውህደት",
    "settings.mergeModeSquash": "ስኳሽ ምውህሃድ",
    "settings.mergeModeMergeHint": "ነቲ ጨንፈር ከም ዘለዎ ይወሃሃዶ: ኮሚትታቱ ኣካል ናይቲ ሕጂ ዘሎ ጨንፈር ታሪኽ ይኾኑ፣ ብናይ ምውህሃድ ኮሚት ይጽንበሩ (ወይ ብዝተኻእለ መጠን ቅልጡፍ ንቕድሚት ይኸይድ)።",
    "settings.mergeModeSquashHint": "ንብምልኡ እቲ ጨንፈር ናብ ሓደ commit ኣብቲ ናይ ሕጂ ጨንፈር የፍርሶ (git merge --squash)። ናይቲ ጨንፈር ናይ ገዛእ ርእሱ ኮሚት ኣብ ታሪኽ ናይቲ ሕጂ ዘሎ ጨንፈር ፈጺሙ ኣይኣቱን እዩ — ኣብኡ ሓደ ኮሚት ጥራይ ኢኻ ትርኢ። እቲ ጨንፈር ንባዕሉ ከይተተንከፈ ስለዝተርፍ፡ ኮሚታቱ ኣብ ናይ ገዛእ ርእሱ መስመር ይርአ።",
    "settings.mergedView": "ኣብቲ ግራፍ ዝተዋሃሃዱ ኮሚትስ",
    "settings.mergedViewBranch": "ኣብ ናይ ገዛእ ርእሶም ጨንፈር ጥራይ",
    "settings.mergedViewTarget": "ኣብቲ ዝተዋሃሃዱሉ ጨንፈር እውን",
    "settings.mergedViewBranchHint": "ናይ ሓደ ዝተዋሃሃደ ጨንፈር ኮሚት ኣብ ናይ ገዛእ ርእሱ መስመር ጥራይ እዩ ዝስኣል፤ እቲ ምውህሃድ ከም መተሓላለፊ ይረአ። እቲ ክላሲካል ኣረኣእያ።",
    "settings.mergedViewTargetHint": "እቶም ሓደ ውህደት ዘምጽኦም commits ኣብቲ ናይቲ ተቐባሊ ጨንፈር መስመር እውን ይስኣሉ፣ ኣብ ትሕቲ እቲ merge commit ተደራሪቦም ምስቶም ኦሪጅናላት ይመዓራረዩ — ምኽንያቱ እቲ ጨንፈር ብሓቂ ሕጂ ዝሓዘ እዩ። እቶም ቅዳሓት ፍሕሽው ዝበሃሉን ምስቲ ዝተጻሕፈሉ ጨንፈር ዝምርኮሱን ስለዝኾኑ ኣብኡ ከምዘይተሰርሑ ንጹር ኮይኑ ይቕጽል። ምውህሃድ ስኳሽ ኣይጽሎን እዩ: ስኳሽ ሓደ ተራ ኮሚት ይጽሕፍ: ስለዚ እቲ ኮሚት ጥራይ እዩ ዝረአ።",
    "legend.title": "ኣፈ ታሪኽ",
    "legend.head": "HEAD / ናይ ሕጂ ጨንፈር",
    "legend.local": "ጨንፈር ከባቢ",
    "legend.remote": "ርሑቕ ጨንፈር",
    "legend.remoteOnly": "ኣብ ደበና ጥራይ (ዘይተሰሓበ)",
    "legend.tag": "መለለዪ (ስሪት)",
    "legend.commit": "ተበገሰ",
    "legend.stash": "ስታሽ (ኣብ መደርደሪ ዝስራሕ ስራሕ)",
    "legend.mergedIn": "ምስ ምውህሃድ (ኣብ ካልእ ጨንፈር ዝተጻሕፈ) ኣትዩ",
    "legend.nodes": "ሳጹናት",
    "legend.lines": "መስመራት።",
    "legend.edgeParent": "ቅድሚ ሕጂ ኣብ ተመሳሳሊ መስመር ምፍጻም",
    "legend.edgeMerge": "ዝተዋሃሃደ ጨንፈር — እቲ ፍላጻ ናብቲ ናይ ምውህሃድ ምፍጻም የመልክት።",
    "legend.edgeBranch": "ኣብዚ ሓደ ጨንፈር ፎርክ ተገይሩ",
    "legend.edgeStash": "ስታሽን እቲ ኣብ መደርደሪ ዝተቐመጠሉ ኮሚትን።",
    "legend.edgeMergedChain": "እዚ ጨንፈር መስመር ብመንገዲ እቶም ዝተዋሃሃዱ ኮሚት ዝጎዪ",
    "legend.edgeMergedTie": "ኣብቲ ዝተጻሕፈሉ ጨንፈር እውን ተመሳሳሊ ኮሚት እዩ።",
    "menu.jumpHead": "⌖ ናብ ምውጻእ ገንዘብ ኪድ",
    "menu.resetView": "⤢ ርእይቶ ዳግማይ ምትካል",
    "menu.createBranch": "ካብዚ ጨንፈር ምፍጣር...",
    "menu.checkout": "ነዚ ኮሚት ርኣይዎ።",
    "menu.copySha": "ቅዳሕ commit SHA",
    "menu.pushBranch": "ጨንፈር ምድፋእ \"{name}\"።",
    "menu.renameCommit": "መልእኽቲ commit ዳግማይ ስመዮ...",
    "menu.undoCommit": "Undo commit (ለውጢ ምዕቃብ)...",
    "menu.viewChanges": "ለውጢ ርአ...",
    "menu.jumpToOriginal": "ናብቲ ኦሪጅናል ኮሚት ኪድ",
    "changes.title": "ኣብ {sha} ዝመጽእ ለውጢ",
    "changes.tabChanged": "ተቐይሩ",
    "changes.tabAll": "ኩሎም ፋይላት",
    "changes.added": "ተወሰኸ",
    "changes.modified": "ተቐይሩ",
    "changes.deleted": "ተደምሲሱ",
    "changes.renamed": "ስሙ ተቐይሩ",
    "changes.noChanges": "እዚ commit ዝኾነ ፋይል ኣይቅይርን እዩ።",
    "changes.loading": "ኣብ ምጽዓን ፍልልይ...",
    "changes.selectFile": "ለውጥታቱ ንምርኣይ ፋይል ምረጽ።",
    "changes.original": "ኦርጂናል",
    "changes.changed": "እዚ ቃል ኪዳን እዚ",
    "changes.binary": "ክልተኣዊ ፋይል — ንምርኣይ ዝኾነ ናይ ጽሑፍ ፍልልይ የለን።",
    "changes.tooLarge": "ፋይል ኣዝዩ ዓቢ እዩ diff.",
    "changes.renamedFrom": "ካብ {path} ተሰይሙ",
    "changes.close": "ዕፁው",
    "changes.prevChange": "ቅድሚ ሕጂ ዝነበረ ለውጢ",
    "changes.nextChange": "ዝቕጽል ለውጢ",
    "changes.collapseAll": "ኩሎም ኣቃፊራታት ኣፍርሶም",
    "changes.expandAll": "ኩሎም ኣቃፊራታት ኣስፍሕ",
    "changes.searchPlaceholder": "ፋይላት ድለዩ...",
    "changes.noSearchResults": "ምስ ምድላይካ ዝሰማማዕ ፋይል የለን።",
    "changes.maximize": "Maximize ምግባር",
    "changes.restore": "ዓቐን ምምላስ",
    "find.open": "ኣብዚ diff (Ctrl+F) ድለዩ",
    "find.placeholder": "ኣብ diff ረኸብ...",
    "find.prev": "ዝሓለፈ ግጥም",
    "find.next": "ዝቕጽል ግጥም",
    "find.close": "ምድላይ ዕጸዎ።",
    "find.noResults": "ውጽኢት የለን",
    "commit.title": "ለውጢ ምፍጻም",
    "commit.close": "ዕፁው",
    "commit.loading": "ንባብ ናይ ስራሕ ገረብ...",
    "commit.noChanges": "ክትፍጽሞ ዘለካ ናይ ከባቢ ለውጢ የለን።",
    "commit.selectFile": "ፍልልዩ ንምርኣይ ፋይል ምረጽ።",
    "commit.selectAll": "ኩሉ ምረጽ",
    "commit.selectNone": "ኩሉ ኣጽርዮ",
    "commit.message": "መልእኽቲ ምፍጻም",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "{count} ፋይል(ታት) ተመሪጹ",
    "commit.review": "ገምጋም",
    "commit.reviewTitle": "ቅድሚ ምፍታሽ ናይ መወዳእታ ምፍታሽ",
    "commit.reviewSummary": "{count} ፋይል(ታት) ምስ መልእኽቲ ምፍጻም: {message}",
    "commit.back": "ዝባን",
    "commit.commit": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashApply": "ስታሽ ምጥቃም",
    "menu.stashPop": "ፖፕ ስታሽ (ምልከት & ምእላይ)",
    "menu.stashDrop": "ድሮፕ ስታሽ...",
    "merge.title": "ጨንፈር ምውህሃድ",
    "merge.route": "ናብቲ ሕጂ ዘሎ ጨንፈር ምውህሃድ",
    "merge.source": "ካብ (ፍልፍል)",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "ምትንታን ምውህሃድ...",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "ቅልጡፍ ንቕድሚት — ዝኾነ ናይ ምውህሃድ ምፍጻም ኣየድልን (ንሓደ ንምግዳድ “Create a merge commit” ዝብል ምልክት ግበር)።",
    "merge.summary": "{files} ፋይል(ታት) ተቐይሩ፣ {conflicts} ግርጭት(ታት)።",
    "merge.conflictsWarning": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noChanges": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.files": "ውጽኢቱ ዝኾነ ለውጢ",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "ተቐይሩ",
    "merge.deleted": "ተደምሲሱ",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "Merge commit ዝብል መልእኽቲ",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "ኣብ ቅልጡፍ ንቕድሚት ዝኸይድ ውህደት ዕሽሽ ተባሂሉ (ዝኾነ commit ኣይፍጠርን)።",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "ስኳሽ ምውህሃድ — ነፍሲ ወከፍ ለውጢ ከም ONE commit ኣብ “{target}” ይዓልብ፤ “{source}” ናይ ገዛእ ርእሱ ቃል ኪዳን ካብ ታሪኹ ወጻኢ ይጸንሕ። ኣብ ቅጥዕታት › ምውህሃድ ተመሊስና ንቕይር።",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "ምፍራስ ኣቃፊራ",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "ናተይ-ጨንፈር",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "ምትእስሳር...",
    "status.noHead": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplying": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.tooltip": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.placeholder": "ምድላይ ቃል ይኣቱ...",
    "search.previous": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.next": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.noResults": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.resultCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.highlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterMode": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeFilter": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHighlight": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.filterModeHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibility": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityToolbar": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "search.visibilityAlwaysVisible": "ኩሉ ግዜ ዝርአ ባር",
    "search.visibilityHint": "ምድላይ ከም መሳርሒ ባር ምልክት ወይ ቀዋሚ ባር ኣብ ላዕሊ ኣርኢ።",
    "footer.github": "ጊትሃብ",
  },
  am: {
    "toolbar.refresh": "⟳ አድስ",
    "toolbar.fetch": "⤓ አምጣ",
    "toolbar.pull": "⇩ ጎትት።",
    "toolbar.push": "⇧ ግፋ",
    "toolbar.commit": "✓ ቁርጠኝነት",
    "toolbar.sync": "⇅ አመሳስል።",
    "toolbar.jumpHead": "⌖ ወደ ፍተሻ ይሂዱ",
    "toolbar.reset": "⤢ እይታን ዳግም አስጀምር",
    "toolbar.settings": "⚙ ቅንብሮች",
    "details.header": "ዝርዝሮችን አስገባ",
    "details.close": "ገጠመ",
    "details.sha": "SHA",
    "details.shortSha": "አጭር SHA",
    "details.message": "መልእክት",
    "details.author": "ደራሲ",
    "details.date": "ቀን",
    "details.labels": "መለያዎች",
    "details.location": "አካባቢ",
    "details.currentHead": "HEAD - ወቅታዊ ፍተሻ",
    "settings.title": "ቅንብሮች",
    "settings.language": "ቋንቋ",
    "settings.theme": "ጭብጥ",
    "settings.themeLight": "ብርሃን",
    "settings.themeDark": "ጨለማ",
    "settings.themeLightHint": "ፈካ ያለ ዳራ፣ ጨለማ ጽሑፍ።",
    "settings.themeDarkHint": "ጨለማ ዳራ፣ ቀላል ጽሑፍ።",
    "settings.mainBranch": "ዋና ቅርንጫፍ",
    "settings.mainBranchAuto": "አውቶማቲክ",
    "settings.mainBranchSearch": "ቅርንጫፎችን ፈልግ…",
    "settings.mainBranchNoMatch": "ምንም ተዛማጅ ቅርንጫፎች የሉም",
    "settings.display": "የማሳያ ዘይቤ",
    "settings.displayModern": "ዘመናዊ",
    "settings.displayClassic": "ክላሲክ",
    "settings.displayModernHint": "ነፃ ሸራ - ወደ መጥበሻ ይጎትቱ፣ ለማጉላት ያሸብልሉ።",
    "settings.displayClassicHint": "ቋሚ ሸራ - ግንዱ በግራ ተሰክቷል፣ ምንም ማጉላት የለም፣ ማሸብለል ብቻ (እንደ SVN ክለሳ ግራፍ)።",
    "settings.sectionGeneral": "አጠቃላይ",
    "settings.sectionGraph": "ግራፍ",
    "settings.sectionChanges": "እይታን ይለውጣል",
    "settings.diffMinimap": "ሚኒማፕ ልዩነት",
    "settings.diffMinimapOn": "ታይቷል።",
    "settings.diffMinimapOff": "ተደብቋል",
    "settings.diffMinimapOnHint": "ከልዩነቱ አጠገብ የVS Code-style አጠቃላይ እይታን አሳይ - ለመሸብለል ይጎትቱት። የለውጥ ምልክቶች ተካትተዋል.",
    "settings.diffMinimapOffHint": "ምንም የአጠቃላይ እይታ የለም - ልዩነቱን በመደበኛነት ያሸብልሉ።",
    "settings.commitReview": "ግምገማ ስጥ",
    "settings.commitReviewOn": "ከመግባትዎ በፊት ይጠይቁ",
    "settings.commitReviewOff": "በቀጥታ ግባ",
    "settings.commitReviewOnHint": "አካባቢያዊ ቁርጠኝነትን ከመፍጠርዎ በፊት የመጨረሻውን የፋይል ዝርዝር እና የመልእክት ማረጋገጫ አሳይ።",
    "settings.commitReviewOffHint": "የቃል አዝራሩ ወዲያውኑ የአካባቢውን ቁርጠኝነት ይፈጥራል።",
    "settings.svnBranchDialog": "የSVN አይነት የቅርንጫፍ ንግግር",
    "settings.svnBranchDialogHint": "ቅርንጫፍ ሲፈጥሩ (ከቀላል ጥያቄ ይልቅ) የአቃፊ-ዛፍ መራጭ ያሳዩ።",
    "settings.branchDialog": "የቅርንጫፍ ንግግር",
    "settings.branchDialogSvnTitle": "የኤስቪኤን አይነት",
    "settings.branchDialogNativeVscode": "የቪኤስ ኮድ ዘይቤ",
    "settings.branchDialogNativeVs": "ቪዥዋል ስቱዲዮ ዘይቤ",
    "settings.branchDialogNativeHint": "የእርስዎ IDE አብሮ የተሰራ የቅርንጫፍ ጥያቄ።",
    "settings.close": "ገጠመ",
    "settings.done": "ተከናውኗል",
    "settings.sectionAdvanced": "የላቀ",
    "settings.gitSource": "Git ሊተገበር የሚችል",
    "settings.gitSourceBuiltin": "አብሮ የተሰራ",
    "settings.gitSourceBuiltinHint": "ከ IDE አብሮገነብ Git ቅጥያ የ git binary ይጠቀሙ።",
    "settings.gitSourceCustom": "ብጁ መንገድ",
    "settings.gitPath": "ወደ ጂት የሚወስደው መንገድ",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "አስስ…",
    "settings.jargon": "Git ውሎች",
    "settings.jargonTranslate": "ተርጉም።",
    "settings.jargonEnglish": "በእንግሊዘኛ አቆይ",
    "settings.jargonTranslateHint": "የጊት ቃላትን (መጎተት፣ መግፋት፣ መፈጸም፣ ቅርንጫፍ…) ወደ የበይነገጽ ቋንቋ ተርጉም።",
    "settings.jargonEnglishHint": "የጊት ቃላትን (መጎተት፣ መግፋት፣ መፈጸም፣ ቅርንጫፍ…) በእንግሊዝኛ አቆይ፤ ሌላውን ሁሉ ተርጉም.",
    "settings.sectionMerge": "አዋህድ",
    "settings.mergeMode": "ቅጥ ያጣምሩ",
    "settings.mergeModeMerge": "መደበኛ ውህደት",
    "settings.mergeModeSquash": "ስኳሽ ውህደት",
    "settings.mergeModeMergeHint": "ቅርንጫፉን ባለበት ሁኔታ ያዋህዳል፡ ድርጊቱ የአሁኑ የቅርንጫፍ ታሪክ አካል ይሆናል፣ በውህደት ቃል (ወይም በሚቻል ጊዜ በፍጥነት የሚተላለፍ)።",
    "settings.mergeModeSquashHint": "አሁን ባለው ቅርንጫፍ (git merge --squash) ላይ መላውን ቅርንጫፍ ወደ አንድ ቁርጠኝነት ይሰብራል። የቅርንጫፉ የራሱ ተግባራት አሁን ባለው የቅርንጫፍ ታሪክ ውስጥ በጭራሽ አይገቡም - እዚያ ሲፈጽም አንድ ብቻ ነው የሚያዩት። ቅርንጫፉ ራሱ ሳይነካ ይቀራል፣ ስለዚህ ድርጊቱ በራሱ መስመር ላይ ይታያል።",
    "settings.mergedView": "የተዋሃዱ ተግባራት በግራፉ ውስጥ",
    "settings.mergedViewBranch": "በራሳቸው ቅርንጫፍ ላይ ብቻ",
    "settings.mergedViewTarget": "በተጨማሪም በቅርንጫፍ ውስጥ ተቀላቅለዋል",
    "settings.mergedViewBranchHint": "የተዋሃዱ የቅርንጫፍ ስራዎች በራሳቸው መንገድ ብቻ ይሳሉ; ውህደቱ እንደ ማገናኛ ሆኖ ይታያል. ክላሲክ እይታ።",
    "settings.mergedViewTargetHint": "የገቡት ውህደቶች በተቀባዩ ቅርንጫፍ መስመር ላይ እንዲሁም በውህደቱ ስር ተቆልለው ከመጀመሪያዎቹ ጋር ደረጃ ላይ ተደርገዋል - ምክንያቱም ያ ቅርንጫፍ አሁን ስለያዘ። ቅጂዎቹ ገርጥ ያሉ እና ከተፃፉበት ቅርንጫፍ ጋር ይመራሉ፣ ስለዚህ እዚያ እንዳልተሰራቸው ግልጽ ነው። የስኳሽ ውህደቶች ምንም ጉዳት የላቸውም፡ ዱባ አንድ ተራ ቁርጠኝነትን ይጽፋል፣ ስለዚህ ያ ቁርጠኝነት ብቻ ይታያል።",
    "legend.title": "አፈ ታሪክ",
    "legend.head": "HEAD / የአሁኑ ቅርንጫፍ",
    "legend.local": "የአካባቢ ቅርንጫፍ",
    "legend.remote": "የርቀት ቅርንጫፍ",
    "legend.remoteOnly": "በደመና ውስጥ ብቻ (ያልተጎተተ)",
    "legend.tag": "መለያ (ስሪት)",
    "legend.commit": "ቁርጠኝነት",
    "legend.stash": "ስታሽ (የተሸፈነ ሥራ)",
    "legend.mergedIn": "ውህደት ይዞ ነው የመጣው (በሌላ ቅርንጫፍ ላይ የተጻፈ)",
    "legend.nodes": "ሳጥኖች",
    "legend.lines": "መስመሮች",
    "legend.edgeParent": "ቀደም ሲል በተመሳሳይ መስመር ላይ መፈጸም",
    "legend.edgeMerge": "የተዋሃደ ቅርንጫፍ - በውህደቱ ላይ ያለው ቀስት ይጠቁማል",
    "legend.edgeBranch": "አንድ ቅርንጫፍ እዚህ ተነጠቀ",
    "legend.edgeStash": "ስታሽ እና ቁርጠኝነት ተጠብቆ ቆይቷል",
    "legend.edgeMergedChain": "የዚህ ቅርንጫፍ መስመር በተዋሃዱ ውህዶች ውስጥ ነው።",
    "legend.edgeMergedTie": "በተፃፈው ቅርንጫፍ ላይ ተመሳሳይ ቁርጠኝነት",
    "menu.jumpHead": "⌖ ወደ ፍተሻ ይሂዱ",
    "menu.resetView": "⤢ እይታን ዳግም አስጀምር",
    "menu.createBranch": "ቅርንጫፍ ከዚህ ፍጠር…",
    "menu.checkout": "ይህንን ቃል ኪዳን ይመልከቱ",
    "menu.copySha": "የ SHA ቅዳ",
    "menu.pushBranch": "ቅርንጫፍን \"{name}\" ግፋ",
    "menu.deleteBranch": "ቅርንጫፍ \"{name}\" ሰርዝ",
    "menu.renameCommit": "የቃል ቃል እንደገና ይሰይሙ…",
    "menu.undoCommit": "ቁርጠኝነትን ይቀልብሱ (ለውጦቹን ይቀጥሉ)…",
    "menu.viewChanges": "ለውጦችን ይመልከቱ…",
    "menu.mergeBranch": "\"{source}\" ወደ \"{target}\" አዋህድ",
    "menu.jumpToOriginal": "ወደ ዋናው ቁርጠኝነት ይሂዱ",
    "changes.title": "በ{sha} ውስጥ ለውጦች",
    "changes.tabChanged": "ተለውጧል",
    "changes.tabAll": "ሁሉም ፋይሎች",
    "changes.added": "ታክሏል።",
    "changes.modified": "ተሻሽሏል።",
    "changes.deleted": "ተሰርዟል።",
    "changes.renamed": "እንደገና ተሰይሟል",
    "changes.noChanges": "ይህ ቁርጠኝነት ምንም ፋይሎችን አይቀይርም።",
    "changes.loading": "ልዩነትን በመጫን ላይ…",
    "changes.selectFile": "ለውጦቹን ለማየት ፋይል ይምረጡ።",
    "changes.original": "ኦሪጅናል",
    "changes.changed": "ይህ ቁርጠኝነት",
    "changes.binary": "ሁለትዮሽ ፋይል - ምንም የሚታይ የጽሑፍ ልዩነት የለም።",
    "changes.tooLarge": "ፋይሉ ለመለያየት በጣም ትልቅ ነው።",
    "changes.renamedFrom": "ከ{path} ተሰይሟል",
    "changes.close": "ገጠመ",
    "changes.prevChange": "ያለፈ ለውጥ",
    "changes.nextChange": "የሚቀጥለው ለውጥ",
    "changes.collapseAll": "ሁሉንም አቃፊዎች ሰብስብ",
    "changes.expandAll": "ሁሉንም አቃፊዎች ዘርጋ",
    "changes.searchPlaceholder": "ፋይሎችን ፈልግ…",
    "changes.noSearchResults": "ከፍለጋዎ ጋር የሚዛመዱ ፋይሎች የሉም።",
    "changes.maximize": "ከፍ አድርግ",
    "changes.restore": "መጠንን ወደነበረበት መልስ",
    "find.open": "በዚህ ልዩነት (Ctrl+F) ይፈልጉ",
    "find.placeholder": "በልዩነት ያግኙ…",
    "find.prev": "የቀድሞ ግጥሚያ",
    "find.next": "ቀጣይ ግጥሚያ",
    "find.close": "ፍለጋን ዝጋ",
    "find.noResults": "ምንም ውጤት የለም።",
    "commit.title": "ለውጦችን አስገባ",
    "commit.close": "ገጠመ",
    "commit.loading": "የሚሰራ ዛፍ በማንበብ…",
    "commit.noChanges": "ለመፈጸም ምንም የአካባቢ ለውጦች የሉም።",
    "commit.selectFile": "ልዩነቱን ለማየት ፋይል ይምረጡ።",
    "commit.selectAll": "ሁሉንም ይምረጡ",
    "commit.selectNone": "ሁሉንም አጽዳ",
    "commit.message": "መልእክት አስገባ",
    "commit.messagePlaceholder": "ለውጡን ይግለጹ",
    "commit.selectedCount": "{count} ፋይል(ዎች) ተመርጧል",
    "commit.review": "ግምገማ",
    "commit.reviewTitle": "ከመግባቱ በፊት የመጨረሻ ማረጋገጫ",
    "commit.reviewSummary": "{count} ፋይል(ዎች) ከመልዕክት ጋር አስገባ፡ {message}",
    "commit.back": "ተመለስ",
    "commit.commit": "አካባቢያዊ ቁርጠኝነት ይፍጠሩ",
    "menu.stashApply": "ቆሻሻን ይተግብሩ",
    "menu.stashPop": "ፖፕ ስታሽ (ተግብር እና አስወግድ)",
    "menu.stashDrop": "ቆሻሻን ጣል…",
    "merge.title": "ቅርንጫፍ አዋህድ",
    "merge.route": "አሁን ባለው ቅርንጫፍ ውስጥ መቀላቀል",
    "merge.source": "ከ (ምንጭ)",
    "merge.target": "ወደ (የአሁኑ)",
    "merge.loading": "ውህደትን በመተንተን ላይ…",
    "merge.upToDate": "ቀድሞውኑ የተዘመነ - ምንም የሚዋሃድ የለም።",
    "merge.fastForward": "ፈጣን ወደፊት - ምንም ውህደት አያስፈልግም (አንድን ለማስገደድ \"የውህደት ቃል ፍጠር\" የሚለውን ምልክት ያድርጉ)።",
    "merge.summary": "{files} ፋይል(ዎች) ተቀይሯል፣ {conflicts} ግጭት(ዎች)።",
    "merge.conflictsWarning": "ይህ ውህደት ግጭቶች አሉት - አሁንም ማዋሃድ እና በአርታዒው ውስጥ መፍታት ይችላሉ።",
    "merge.noChanges": "ምንም የፋይል ለውጥ የለም።",
    "merge.files": "የውጤት ለውጦች",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.message": "የቃል ቃልን አዋህድ",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "በፍጥነት ወደፊት ውህደት ላይ ችላ ተብሏል (ምንም ቁርጠኝነት አልተፈጠረም)።",
    "merge.noFastForward": "የውህደት ቃል ይፍጠሩ (ፈጣን-ወደ ፊት የለም)",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "ቅድመ እይታን ማስላት አልተቻለም፡ {message}",
    "merge.squashBadge": "ስኳሽ",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPointOn": "በ{refs} ላይ",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "ሁሉንም ዘርጋ",
    "newBranch.collapseAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "አቃፊ ሰብስብ",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "ከተፈጠረ በኋላ ቅርንጫፍን ይመልከቱ",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.opFailed": "ክወና አልተሳካም።",
    "status.fetching": "በማምጣት ላይ…",
    "status.pulling": "በመጎተት ላይ…",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "የአካባቢ ቁርጠኝነትን በመፍጠር ላይ…",
    "status.commitCreated": "የተፈጠረ አካባቢያዊ ግዴታ {sha}፡ {message}",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "ምንም የአሁኑ ፍተሻ ​​በግራፉ ላይ አልተገኘም።",
    "status.undoing": "ቁርጠኝነትን በመቀልበስ ላይ…",
    "status.commitUndone": "ተሰርዟል - ለውጦች ወደ ሥራው ዛፍ ተመልሰዋል።",
    "status.undoConflict": "ግጭቶችን ይቀልብሱ - በአርታዒው ውስጥ ይፍቱ እና ይቀጥሉ።",
    "status.stashApplying": "ስታሽ በመተግበር ላይ…",
    "status.stashPopping": "መጣያ ብቅ ይላል…",
    "status.stashDropping": "ቆሻሻን በመጣል ላይ…",
    "status.stashApplied": "ስታሽ ተተግብሯል።",
    "status.stashPopped": "ስታሽ ብቅ አለ።",
    "status.stashDropped": "ቆሻሻ መጣል።",
    "status.stashConflict": "የጭረት ግጭቶች - በአርታዒው ውስጥ ይፍቷቸው።",
    "status.merging": "በማዋሃድ ላይ…",
    "status.merged": "ውህደት ተጠናቅቋል።",
    "status.mergeConflict": "ውህደት ግጭቶች አሉት - በአርታዒው ውስጥ ይፍቷቸው እና ከዚያ ይግቡ።",
    "search.title": "ፈልግ",
    "search.tooltip": "ፍለጋ ይሰራል",
    "search.placeholder": "ፍለጋ ይሰራል…",
    "search.previous": "ያለፈው ውጤት",
    "search.next": "ቀጣይ ውጤት",
    "search.noResults": "ምንም ውጤት የለም።",
    "search.resultCount": "{index}/{count}",
    "search.filter": "የማጣሪያ ሁነታ",
    "search.highlight": "የድምቀት ሁነታ",
    "search.filterMode": "የፍለጋ ማሳያ",
    "search.filterModeFilter": "አጣራ (ተዛማጆች ያልሆኑ ደብቅ)",
    "search.filterModeHighlight": "አድምቅ (ሁሉንም አሳይ፣ ተዛማጆችን ምልክት አድርግ)",
    "search.filterModeHint": "የፍለጋ ውጤቶች በግራፉ ውስጥ እንዴት እንደሚታዩ ይምረጡ።",
    "search.visibility": "አካባቢን ፈልግ",
    "search.visibilityToolbar": "የመሳሪያ አሞሌ አዝራር",
    "search.visibilityAlwaysVisible": "ሁልጊዜ የሚታይ አሞሌ",
    "search.visibilityHint": "ፍለጋን እንደ የመሳሪያ አሞሌ አዶ ወይም ቋሚ አሞሌ ከላይ አሳይ።",
    "footer.github": "GitHub",
  },
  km: {
    "toolbar.refresh": "⟳ ធ្វើឱ្យស្រស់",
    "toolbar.fetch": "⤓ យក",
    "toolbar.pull": "⇩ ទាញ",
    "toolbar.push": "⇧ រុញ",
    "toolbar.commit": "✓ ប្តេជ្ញាចិត្ត",
    "toolbar.sync": "⇅ ធ្វើសមកាលកម្ម",
    "toolbar.jumpHead": "⌖ ចូលទៅកាន់ការបង់ប្រាក់",
    "toolbar.reset": "⤢ កំណត់ទិដ្ឋភាពឡើងវិញ",
    "toolbar.settings": "⚙ ការកំណត់",
    "details.header": "សេចក្តីលម្អិតការប្តេជ្ញាចិត្ត",
    "details.close": "បិទ",
    "details.sha": "SHA",
    "details.shortSha": "SHA ខ្លី",
    "details.message": "សារ",
    "details.author": "អ្នកនិពន្ធ",
    "details.date": "កាលបរិច្ឆេទ",
    "details.labels": "ស្លាក",
    "details.location": "ទីតាំង",
    "details.currentHead": "HEAD - ការទូទាត់បច្ចុប្បន្ន",
    "settings.title": "ការកំណត់",
    "settings.language": "ភាសា",
    "settings.theme": "ប្រធានបទ",
    "settings.themeLight": "ពន្លឺ",
    "settings.themeDark": "ងងឹត",
    "settings.themeLightHint": "ផ្ទៃខាងក្រោយស្រាល អត្ថបទងងឹត។",
    "settings.themeDarkHint": "ផ្ទៃខាងក្រោយងងឹត អត្ថបទស្រាល។",
    "settings.mainBranch": "សាខាចម្បង",
    "settings.mainBranchAuto": "ស្វ័យប្រវត្តិ",
    "settings.mainBranchSearch": "ស្វែងរកសាខា…",
    "settings.mainBranchNoMatch": "មិនមានសាខាដែលត្រូវគ្នា។",
    "settings.display": "រចនាប័ទ្មបង្ហាញ",
    "settings.displayModern": "ទំនើប",
    "settings.displayClassic": "បុរាណ",
    "settings.displayModernHint": "ផ្ទាំងក្រណាត់ឥតគិតថ្លៃ — អូសដើម្បីរុញ រំកិលដើម្បីពង្រីក។",
    "settings.displayClassicHint": "ផ្ទាំងក្រណាត់ដែលបានជួសជុល — ដើមត្រូវបានខ្ទាស់ខាងឆ្វេង គ្មានការពង្រីក រមូរតែប៉ុណ្ណោះ (ដូចជាក្រាហ្វ SVN កែប្រែ)។",
    "settings.sectionGeneral": "ទូទៅ",
    "settings.sectionGraph": "ក្រាហ្វ",
    "settings.sectionChanges": "ផ្លាស់ប្តូរទិដ្ឋភាព",
    "settings.diffMinimap": "ផែនទីតូចខុសគ្នា",
    "settings.diffMinimapOn": "បានបង្ហាញ",
    "settings.diffMinimapOff": "លាក់",
    "settings.diffMinimapOnHint": "បង្ហាញបន្ទះទិដ្ឋភាពទូទៅនៃរចនាប័ទ្មកូដ VS នៅក្បែរភាពខុសគ្នា — អូសវាដើម្បីរមូរ។ ការផ្លាស់ប្តូរសញ្ញាសម្គាល់រួមបញ្ចូល។",
    "settings.diffMinimapOffHint": "មិនមានបន្ទះទិដ្ឋភាពទូទៅទេ - រំកិលភាពខុសគ្នាជាធម្មតា។",
    "settings.commitReview": "ប្តេជ្ញាពិនិត្យឡើងវិញ",
    "settings.commitReviewOn": "សួរមុនពេលធ្វើ",
    "settings.commitReviewOff": "ប្តេជ្ញាដោយផ្ទាល់",
    "settings.commitReviewOnHint": "បង្ហាញបញ្ជីឯកសារចុងក្រោយ និងការពិនិត្យសារ មុនពេលបង្កើតការប្តេជ្ញាចិត្តក្នុងតំបន់។",
    "settings.commitReviewOffHint": "ប៊ូតុង Commit បង្កើតការប្តេជ្ញាក្នុងតំបន់ភ្លាមៗ។",
    "settings.svnBranchDialog": "ប្រអប់​សាខា​រចនាប័ទ្ម SVN",
    "settings.svnBranchDialogHint": "បង្ហាញអ្នករើសមែកធាង folder នៅពេលបង្កើតសាខាមួយ (ជំនួសឱ្យប្រអប់បញ្ចូលសាមញ្ញ)។",
    "settings.branchDialog": "ប្រអប់​សាខា",
    "settings.branchDialogSvnTitle": "រចនាប័ទ្ម SVN",
    "settings.branchDialogNativeVscode": "រចនាប័ទ្ម VS កូដ",
    "settings.branchDialogNativeVs": "រចនាប័ទ្ម Visual Studio",
    "settings.branchDialogNativeHint": "ប្រអប់បញ្ចូលសាខាដែលភ្ជាប់មកជាមួយ IDE របស់អ្នក។",
    "settings.close": "បិទ",
    "settings.done": "រួចរាល់",
    "settings.sectionAdvanced": "កម្រិតខ្ពស់",
    "settings.gitSource": "Git អាចប្រតិបត្តិបាន។",
    "settings.gitSourceBuiltin": "ភ្ជាប់មកជាមួយ",
    "settings.gitSourceBuiltinHint": "ប្រើ git binary ពីផ្នែកបន្ថែម Git ដែលភ្ជាប់មកជាមួយ IDE ។",
    "settings.gitSourceCustom": "ផ្លូវផ្ទាល់ខ្លួន",
    "settings.gitSourceCustomHint": "បញ្ជាក់ផ្លូវទៅកាន់ git របស់អ្នកដែលអាចប្រតិបត្តិបាន (ឧ. /usr/bin/git ឬ C:\\⢦\\git.exe)។",
    "settings.gitPath": "ផ្លូវទៅកាន់ git",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "រុករក...",
    "settings.jargon": "លក្ខខណ្ឌ Git",
    "settings.jargonTranslate": "បកប្រែ",
    "settings.jargonEnglish": "ទុកជាភាសាអង់គ្លេស",
    "settings.jargonTranslateHint": "បកប្រែពាក្យ Git (ទាញ, រុញ, ប្តេជ្ញា, សាខា…) ទៅជាភាសាចំណុចប្រទាក់។",
    "settings.jargonEnglishHint": "រក្សាលក្ខខណ្ឌ Git (ទាញ, រុញ, ប្តេជ្ញា, សាខា…) ជាភាសាអង់គ្លេស; បកប្រែអ្វីៗផ្សេងទៀត។",
    "settings.sectionMerge": "បញ្ចូលគ្នា",
    "settings.mergeMode": "រចនាប័ទ្មបញ្ចូលគ្នា",
    "settings.mergeModeMerge": "ការរួមបញ្ចូលគ្នាធម្មតា។",
    "settings.mergeModeSquash": "Squash បញ្ចូលគ្នា",
    "settings.mergeModeMergeHint": "បញ្ចូលសាខាដូចដែលវាគឺជា៖ ការប្តេជ្ញាចិត្តរបស់វាក្លាយជាផ្នែកមួយនៃប្រវត្តិសាខាបច្ចុប្បន្ន ចូលរួមដោយការប្តេជ្ញាចិត្តបញ្ចូលគ្នា (ឬបញ្ជូនបន្តយ៉ាងលឿននៅពេលដែលអាចធ្វើទៅបាន)។",
    "settings.mergeModeSquashHint": "បង្រួមសាខាទាំងមូលទៅជាការប្តេជ្ញាចិត្តតែមួយនៅលើសាខាបច្ចុប្បន្ន (git merge --squash) ។ ការប្តេជ្ញាចិត្តផ្ទាល់របស់សាខាមិនដែលចូលក្នុងប្រវត្តិសាខាបច្ចុប្បន្នទេ - អ្នកឃើញតែការប្រព្រឹត្តិមួយនៅទីនោះ។ សាខាខ្លួនឯងមិនត្រូវបានប៉ះពាល់ទេ ដូច្នេះការប្តេជ្ញាចិត្តរបស់វានៅតែអាចមើលឃើញនៅក្នុងផ្លូវរបស់វា។",
    "settings.mergedView": "បញ្ចូល​គ្នា​ប្រព្រឹត្ត​នៅ​ក្នុង​ក្រាហ្វ",
    "settings.mergedViewBranch": "មានតែនៅលើសាខារបស់ពួកគេប៉ុណ្ណោះ។",
    "settings.mergedViewTarget": "ផងដែរនៅក្នុងសាខាដែលពួកគេត្រូវបានបញ្ចូលគ្នាទៅក្នុង",
    "settings.mergedViewBranchHint": "ការប្តេជ្ញាចិត្តរបស់សាខារួមបញ្ចូលគ្នាត្រូវបានគូរតែនៅក្នុងផ្លូវផ្ទាល់ខ្លួនរបស់ពួកគេប៉ុណ្ណោះ។ ការបញ្ចូលគ្នាបង្ហាញជាឧបករណ៍ភ្ជាប់។ ទិដ្ឋភាពបុរាណ។",
    "settings.mergedViewTargetHint": "ការបញ្ចូលការបញ្ចូលចូលគ្នាគឺត្រូវបានគូរនៅក្នុងផ្លូវរបស់សាខាទទួលផងដែរ ដោយដាក់ជង់នៅក្រោមការបញ្ចូលបញ្ចូលគ្នា និងកម្រិតជាមួយនឹងប្រភពដើម — ដោយសារតែសាខានោះពិតជាមានពួកវាឥឡូវនេះ។ ច្បាប់ចម្លងមានសភាពស្លេក ហើយមានក្បាលជាមួយនឹងសាខាដែលពួកគេត្រូវបានសរសេរ ដូច្នេះវានៅតែច្បាស់ថាពួកគេមិនត្រូវបានធ្វើនៅទីនោះ។ ការបញ្ចូល Squash គឺមិនប៉ះពាល់ទេ៖ squash សរសេរការប្តេជ្ញាចិត្តធម្មតាមួយ ដូច្នេះមានតែការប្តេជ្ញាចិត្តដែលធ្លាប់លេចចេញមក។",
    "legend.title": "រឿងព្រេង",
    "legend.head": "HEAD / សាខាបច្ចុប្បន្ន",
    "legend.local": "សាខាក្នុងស្រុក",
    "legend.remote": "សាខាពីចម្ងាយ",
    "legend.remoteOnly": "មានតែនៅក្នុងពពកប៉ុណ្ណោះ (មិនត្រូវបានទាញ)",
    "legend.tag": "ស្លាក (កំណែ)",
    "legend.commit": "ប្តេជ្ញាចិត្ត",
    "legend.stash": "កន្លែងដាក់ឥវ៉ាន់ (កន្លែងដាក់ឥវ៉ាន់)",
    "legend.mergedIn": "បានចូលមកជាមួយនឹងការបញ្ចូលគ្នា (សរសេរនៅលើសាខាមួយផ្សេងទៀត)",
    "legend.nodes": "ប្រអប់",
    "legend.lines": "បន្ទាត់",
    "legend.edgeParent": "ការប្តេជ្ញាចិត្តពីមុននៅលើបន្ទាត់ដូចគ្នា។",
    "legend.edgeMerge": "សាខារួមបញ្ចូលគ្នា - ព្រួញចង្អុលទៅការបញ្ចូលបញ្ចូលគ្នា",
    "legend.edgeBranch": "មែកធាងមួយបានបែកចេញពីទីនេះ",
    "legend.edgeStash": "Stash និងការប្តេជ្ញាចិត្តដែលវាត្រូវបានទុកនៅលើ",
    "legend.edgeMergedChain": "បន្ទាត់របស់សាខានេះដំណើរការតាមរយៈ commits រួមបញ្ចូលគ្នា",
    "legend.edgeMergedTie": "ការប្តេជ្ញាចិត្តដូចគ្នានៅលើសាខាដែលវាត្រូវបានសរសេរនៅលើ",
    "menu.jumpHead": "⌖ ចូលទៅកាន់ការបង់ប្រាក់",
    "menu.resetView": "⤢ កំណត់ទិដ្ឋភាពឡើងវិញ",
    "menu.createBranch": "បង្កើតសាខាពីទីនេះ…",
    "menu.checkout": "ពិនិត្យមើលការប្តេជ្ញាចិត្តនេះ។",
    "menu.copySha": "ចម្លង commit SHA",
    "menu.pushBranch": "សាខារុញ \"{name}\"",
    "menu.deleteBranch": "លុប​សាខា \"{name}\"â",
    "menu.renameCommit": "ប្តូរ​ឈ្មោះ​សារ​ប្រព្រឹត្ត...",
    "menu.undoCommit": "មិន​ធ្វើ​ការ​ប្តេជ្ញា​ចិត្ត (រក្សា​ការ​ផ្លាស់​ប្តូ​រ)…",
    "menu.viewChanges": "មើលការផ្លាស់ប្តូរ…",
    "menu.mergeBranch": "បញ្ចូល \"{source}\" ទៅជា \"{target}\" ⢦",
    "menu.jumpToOriginal": "ទៅកាន់ការប្តេជ្ញាចិត្តដើម",
    "changes.title": "ការផ្លាស់ប្តូរនៅក្នុង {sha}",
    "changes.tabChanged": "បានផ្លាស់ប្តូរ",
    "changes.tabAll": "ឯកសារទាំងអស់។",
    "changes.added": "បន្ថែម",
    "changes.modified": "កែប្រែ",
    "changes.deleted": "បានលុប",
    "changes.renamed": "ប្តូរឈ្មោះ",
    "changes.noChanges": "ការប្តេជ្ញាចិត្តនេះមិនផ្លាស់ប្តូរឯកសារទេ។",
    "changes.loading": "កំពុងផ្ទុកភាពខុសគ្នា...",
    "changes.selectFile": "ជ្រើសរើសឯកសារដើម្បីមើលការផ្លាស់ប្តូររបស់វា។",
    "changes.original": "ដើម",
    "changes.changed": "ការប្តេជ្ញាចិត្តនេះ។",
    "changes.binary": "ឯកសារគោលពីរ - មិនមានអត្ថបទខុសគ្នាដើម្បីបង្ហាញទេ។",
    "changes.tooLarge": "ឯកសារធំពេកមិនអាចបំបែកបាន។",
    "changes.renamedFrom": "បានប្តូរឈ្មោះពី {path}",
    "changes.close": "បិទ",
    "changes.prevChange": "ការផ្លាស់ប្តូរពីមុន",
    "changes.nextChange": "ការផ្លាស់ប្តូរបន្ទាប់",
    "changes.collapseAll": "បង្រួមថតទាំងអស់។",
    "changes.expandAll": "ពង្រីកថតឯកសារទាំងអស់។",
    "changes.searchPlaceholder": "ស្វែងរកឯកសារ…",
    "changes.noSearchResults": "គ្មានឯកសារដែលត្រូវគ្នានឹងការស្វែងរករបស់អ្នកទេ។",
    "changes.maximize": "អតិបរមា",
    "changes.restore": "ស្តារទំហំ",
    "find.open": "ស្វែងរកក្នុងភាពខុសគ្នានេះ (Ctrl+F)",
    "find.placeholder": "ស្វែងរកភាពខុសគ្នា…",
    "find.prev": "ការប្រកួតមុន។",
    "find.next": "ការប្រកួតបន្ទាប់",
    "find.close": "បិទការស្វែងរក",
    "find.noResults": "គ្មានលទ្ធផល",
    "commit.title": "ប្តេជ្ញាការផ្លាស់ប្តូរ",
    "commit.close": "បិទ",
    "commit.loading": "ការអានដើមឈើការងារ…",
    "commit.noChanges": "មិនមានការផ្លាស់ប្តូរក្នុងស្រុកដើម្បីប្តេជ្ញាចិត្តទេ។",
    "commit.selectFile": "ជ្រើសរើសឯកសារដើម្បីមើលភាពខុសគ្នារបស់វា។",
    "commit.selectAll": "ជ្រើសរើសទាំងអស់។",
    "commit.selectNone": "ជម្រះទាំងអស់។",
    "commit.message": "ផ្ញើសារ",
    "commit.messagePlaceholder": "ពិពណ៌នាអំពីការផ្លាស់ប្តូរ",
    "commit.selectedCount": "ឯកសារ {count} ត្រូវបានជ្រើសរើស",
    "commit.review": "ពិនិត្យឡើងវិញ",
    "commit.reviewTitle": "ពិនិត្យចុងក្រោយមុនពេលប្តេជ្ញាចិត្ត",
    "commit.reviewSummary": "បញ្ចូលឯកសារ {count} ជាមួយសារ៖ {message}",
    "commit.back": "ត្រឡប់មកវិញ",
    "commit.commit": "បង្កើតការប្តេជ្ញាចិត្តក្នុងតំបន់",
    "menu.stashApply": "អនុវត្តឃ្លាំង",
    "menu.stashPop": "Pop stash (អនុវត្ត និងលុបចេញ)",
    "menu.stashDrop": "ទម្លាក់​ទុក…",
    "merge.title": "សាខាបញ្ចូលគ្នា",
    "merge.route": "បញ្ចូលគ្នាទៅក្នុងសាខាបច្ចុប្បន្ន",
    "merge.source": "ពី (ប្រភព)",
    "merge.target": "ចូល (បច្ចុប្បន្ន)",
    "merge.loading": "កំពុង​វិភាគ​បញ្ចូល​គ្នា...",
    "merge.upToDate": "ទាន់សម័យរួចហើយ — គ្មានអ្វីត្រូវបញ្ចូលគ្នាទេ។",
    "merge.fastForward": "ឆ្ពោះទៅមុខយ៉ាងរហ័ស — មិនចាំបាច់មានការប្តេជ្ញាចិត្តបញ្ចូលគ្នាទេ (គូស “បង្កើតការប្តេជ្ញាចិត្តបញ្ចូលគ្នា” ដើម្បីបង្ខំមួយ)។",
    "merge.summary": "ឯកសារ {files} បានផ្លាស់ប្តូរ ការប៉ះទង្គិច {conflicts} ។",
    "merge.conflictsWarning": "ការបញ្ចូលគ្នានេះមានជម្លោះ — អ្នកនៅតែអាចបញ្ចូលចូលគ្នា បន្ទាប់មកដោះស្រាយពួកវានៅក្នុងកម្មវិធីនិពន្ធ។",
    "merge.noChanges": "មិនមានការផ្លាស់ប្តូរឯកសារទេ។",
    "merge.files": "ការផ្លាស់ប្តូរលទ្ធផល",
    "merge.added": "បន្ថែម",
    "merge.modified": "កែប្រែ",
    "merge.deleted": "បានលុប",
    "merge.conflict": "ជម្លោះ",
    "merge.message": "បញ្ចូល​ពាក្យ​សន្យា",
    "merge.messagePlaceholder": "បញ្ចូលសាខា 'ប្រភព'",
    "merge.messageFfHint": "មិនអើពើលើការបញ្ចូលគ្នាយ៉ាងលឿន (គ្មានការប្តេជ្ញាចិត្តត្រូវបានបង្កើតឡើង)។",
    "merge.noFastForward": "បង្កើត​ការ​បញ្ចូល​គ្នា​ប្តេជ្ញា​ចិត្ត (មិន​មាន​ការ​ទៅ​មុខ​លឿន​)",
    "merge.noFastForwardHint": "តែងតែកត់ត្រាការប្តេជ្ញាចិត្តបញ្ចូលគ្នា ទោះបីជាសាខាអាចទៅមុខបានលឿនក៏ដោយ។",
    "merge.merge": "បញ្ចូលគ្នា",
    "merge.cancel": "បោះបង់",
    "merge.previewError": "មិនអាចគណនាការមើលជាមុនបានទេ៖ {message}",
    "merge.squashBadge": "មឹក",
    "merge.squashNote": "Squash បញ្ចូលគ្នា — រាល់ការផ្លាស់ប្តូរនឹងកើតឡើងនៅពេលដែលការប្តេជ្ញាចិត្តមួយនៅលើ “{target}”; ការប្តេជ្ញាចិត្តផ្ទាល់ខ្លួនរបស់ “{source}” នៅតែចេញពីប្រវត្តិរបស់វា។ ត្រឡប់​មក​វិញ​នៅ​ក្នុង​ការ​កំណត់ › បញ្ចូល​គ្នា។",
    "merge.messageSquash": "ផ្ញើសារ",
    "merge.squashMessagePlaceholder": "ការផ្លាស់ប្តូរ Squashed ពី 'ប្រភព'",
    "newBranch.title": "បង្កើតសាខា",
    "newBranch.startPoint": "សាខាថ្មីចាប់ផ្តើមពី {sha}",
    "newBranch.startPointOn": "នៅលើ {refs}",
    "newBranch.location": "ទីតាំង",
    "newBranch.locationRoot": "(ឫស)",
    "newBranch.expandAll": "ពង្រីកទាំងអស់។",
    "newBranch.collapseAll": "ដួលរលំទាំងអស់។",
    "newBranch.expandFolder": "ពង្រីកថត",
    "newBranch.collapseFolder": "បង្រួមថត",
    "newBranch.name": "ឈ្មោះសាខា",
    "newBranch.namePlaceholder": "សាខារបស់ខ្ញុំ",
    "newBranch.fullName": "ឈ្មោះពេញ៖ {name}",
    "newBranch.invalid": "ឈ្មោះសាខាមិនត្រឹមត្រូវ",
    "newBranch.exists": "សាខាដែលមានឈ្មោះនេះមានរួចហើយ",
    "newBranch.checkout": "សាខា Checkout បន្ទាប់ពីការបង្កើត",
    "newBranch.create": "បង្កើត",
    "newBranch.cancel": "បោះបង់",
    "status.loading": "កំពុងផ្ទុកក្រាហ្វ...",
    "status.summary": "{repo} បង្ហាញ {commits} ប្រព្រឹត្ត, {refs} យោង",
    "status.branchCreated": "បានបង្កើតសាខា \"{name}\" នៅ {sha}",
    "status.error": "កំហុស៖ {message}",
    "status.opFailed": "ប្រតិបត្តិការបានបរាជ័យ",
    "status.fetching": "កំពុងទៅយក...",
    "status.pulling": "កំពុងទាញ…",
    "status.pushing": "កំពុង​រុញ...",
    "status.committing": "កំពុង​បង្កើត​ការ​ប្តេជ្ញាចិត្ត​ក្នុង​តំបន់...",
    "status.commitCreated": "បានបង្កើតការប្តេជ្ញាចិត្តក្នុងតំបន់ {sha}: {message}",
    "status.syncing": "កំពុង​ធ្វើ​សមកាលកម្ម...",
    "status.noHead": "រកមិនឃើញការបង់ប្រាក់បច្ចុប្បន្ននៅក្នុងក្រាហ្វទេ។",
    "status.undoing": "មិន​ធ្វើ​ការ​សន្យា...",
    "status.commitUndone": "ប្តេជ្ញាមិនធ្វើវិញ - ការផ្លាស់ប្តូរគឺត្រឡប់មកវិញនៅក្នុងមែកធាងដំណើរការ។",
    "status.undoConflict": "មិន​ធ្វើ​វិញ​ការ​ប៉ះ​ទង្គិច​គ្នា — ដោះស្រាយ​វា​នៅ​ក្នុង​កម្មវិធី​និពន្ធ​, បន្ទាប់​មក​បន្ត​។",
    "status.stashApplying": "កំពុង​អនុវត្ត​ឃ្លាំង...",
    "status.stashPopping": "កំពុង​ស្តុក​ទុក…",
    "status.stashDropping": "កំពុង​ទម្លាក់​ស្តុក...",
    "status.stashApplied": "បានអនុវត្ត Stash ។",
    "status.stashPopped": "ស្តុកបានលេចចេញមក។",
    "status.stashDropped": "Stash បានធ្លាក់ចុះ។",
    "status.stashConflict": "ជម្លោះ​ក្នុង​ការ​រក្សាទុក — ដោះស្រាយ​វា​នៅ​ក្នុង​កម្មវិធី​និពន្ធ។",
    "status.merging": "ការ​បញ្ចូល​គ្នា…",
    "status.merged": "ការបញ្ចូលគ្នាបានបញ្ចប់។",
    "status.mergeConflict": "ការរួមបញ្ចូលគ្នាមានជម្លោះ — ដោះស្រាយពួកវានៅក្នុងកម្មវិធីនិពន្ធ បន្ទាប់មកប្តេជ្ញា។",
    "search.title": "ស្វែងរក",
    "search.tooltip": "ការ​ស្វែងរក​ប្រព្រឹត្ត",
    "search.placeholder": "ការ​ស្វែងរក​ប្តេជ្ញា...",
    "search.previous": "លទ្ធផលពីមុន",
    "search.next": "លទ្ធផលបន្ទាប់",
    "search.noResults": "គ្មានលទ្ធផល",
    "search.resultCount": "{index}/{count}",
    "search.filter": "របៀបតម្រង",
    "search.highlight": "របៀបបន្លិច",
    "search.filterMode": "ស្វែងរកការបង្ហាញ",
    "search.filterModeFilter": "តម្រង (លាក់ការមិនផ្គូផ្គង)",
    "search.filterModeHighlight": "បន្លិច (បង្ហាញទាំងអស់ សម្គាល់ការប្រកួត)",
    "search.filterModeHint": "ជ្រើសរើសរបៀបដែលលទ្ធផលស្វែងរកត្រូវបានបង្ហាញនៅក្នុងក្រាហ្វ។",
    "search.visibility": "ស្វែងរកទីតាំង",
    "search.visibilityToolbar": "ប៊ូតុងរបារឧបករណ៍",
    "search.visibilityAlwaysVisible": "របារដែលអាចមើលឃើញជានិច្ច",
    "search.visibilityHint": "បង្ហាញការស្វែងរកជារូបតំណាងរបារឧបករណ៍ ឬរបារបន្តនៅផ្នែកខាងលើ។",
    "footer.github": "GitHub",
  },
  "zh-tw": {
    "toolbar.refresh": "⟳ 刷新",
    "toolbar.fetch": "⤓ 獲取",
    "toolbar.pull": "⇩ 拉動",
    "toolbar.push": "⇧ 推",
    "toolbar.commit": "✓ 承諾",
    "toolbar.sync": "⇅ 同步",
    "toolbar.jumpHead": "⌖ 前往結帳",
    "toolbar.reset": "⤢ 重置視圖",
    "toolbar.settings": "⚙ 設定",
    "details.header": "提交詳細信息",
    "details.close": "關閉",
    "details.sha": "SHA",
    "details.shortSha": "短SHA",
    "details.message": "訊息",
    "details.author": "作者",
    "details.date": "日期",
    "details.labels": "標籤",
    "details.location": "地點",
    "details.currentHead": "HEAD — 目前結帳",
    "settings.title": "設定",
    "settings.language": "語言",
    "settings.theme": "主題",
    "settings.themeLight": "光",
    "settings.themeDark": "黑暗的",
    "settings.themeLightHint": "淺色背景，深色文字。",
    "settings.themeDarkHint": "深色背景，淺色文字。",
    "settings.mainBranch": "主要分行",
    "settings.mainBranchAuto": "自動的",
    "settings.mainBranchSearch": "搜尋分行...",
    "settings.mainBranchNoMatch": "沒有匹配的分支",
    "settings.display": "展示風格",
    "settings.displayModern": "現代的",
    "settings.displayClassic": "經典的",
    "settings.displayModernHint": "自由畫布 — 拖曳進行平移，滾動進行縮放。",
    "settings.displayClassicHint": "固定畫布 - 主幹固定在左側，無縮放，僅滾動（如 SVN 修訂圖）。",
    "settings.sectionGeneral": "一般的",
    "settings.sectionGraph": "圖形",
    "settings.sectionChanges": "改變視圖",
    "settings.diffMinimap": "差異小地圖",
    "settings.diffMinimapOn": "如圖所示",
    "settings.diffMinimapOff": "隱",
    "settings.diffMinimapOnHint": "在差異旁邊顯示 VS Code 風格的概述條 — 拖曳它進行捲動；包括更改標記。",
    "settings.diffMinimapOffHint": "沒有概覽條 - 正常滾動差異。",
    "settings.commitReview": "提交審核",
    "settings.commitReviewOn": "提交前詢問",
    "settings.commitReviewOff": "直接提交",
    "settings.commitReviewOnHint": "在建立本地提交之前顯示最終文件清單和訊息檢查。",
    "settings.commitReviewOffHint": "“提交”按鈕立即建立本地提交。",
    "settings.svnBranchDialog": "SVN風格的分支對話框",
    "settings.svnBranchDialogHint": "建立分支時顯示資料夾樹選擇器（而不是簡單的提示）。",
    "settings.branchDialog": "分支對話框",
    "settings.branchDialogSvnTitle": "SVN風格",
    "settings.branchDialogNativeVscode": "VS 程式碼風格",
    "settings.branchDialogNativeVs": "視覺工作室風格",
    "settings.branchDialogNativeHint": "您的 IDE 的內建分支提示符號。",
    "settings.close": "關閉",
    "settings.done": "完畢",
    "settings.sectionAdvanced": "先進的",
    "settings.gitSource": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.gitSourceBuiltin": "內建",
    "settings.gitSourceBuiltinHint": "使用 IDE 內建 Git 擴充功能中的 git 二進位。",
    "settings.gitSourceCustom": "自訂路徑",
    "settings.gitSourceCustomHint": "指定您自己的 git 執行檔的路徑（例如 /usr/bin/git 或 C:\\…\\git.exe）。",
    "settings.gitPath": "git 的路徑",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "瀏覽…",
    "settings.jargon": "Git 術語",
    "settings.jargonTranslate": "翻譯",
    "settings.jargonEnglish": "保留英文",
    "settings.jargonTranslateHint": "將 Git 術語（拉、推、提交、分支…）翻譯成介面語言。",
    "settings.jargonEnglishHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "settings.sectionMerge": "合併",
    "settings.mergeMode": "合併風格",
    "settings.mergeModeMerge": "正常合併",
    "settings.mergeModeSquash": "壁球合併",
    "settings.mergeModeMergeHint": "按原樣合併分支：其提交成為當前分支歷史記錄的一部分，透過合併提交加入（或在可能的情況下快轉）。",
    "settings.mergeModeSquashHint": "將整個分支折疊為目前分支上的單一提交（git merge --squash）。分支自己的提交永遠不會進入當前分支的歷史記錄 - 您只能在那裡看到一項提交。分支本身保持不變，因此它的提交在自己的通道中保持可見。",
    "settings.mergedView": "圖表中的合併提交",
    "settings.mergedViewBranch": "只在自己的分支上",
    "settings.mergedViewTarget": "也在分支中他們被合併到",
    "settings.mergedViewBranchHint": "合併分支的提交僅在其自己的通道中繪製；合併顯示為連接器。經典的觀點。",
    "settings.mergedViewTargetHint": "合併引入的提交也會在接收分支的通道中繪製，堆疊在合併提交下並與原始提交保持同一水平 - 因為該分支現在確實包含它們。這些副本是蒼白的，並以它們所寫的樹枝為標題，因此很明顯它們不是在那裡製作的。 Squash 合併不受影響：squash 寫入一個普通提交，因此只有該提交出現。",
    "legend.title": "傳奇",
    "legend.head": "HEAD/當前分支",
    "legend.local": "當地分公司",
    "legend.remote": "遠端分支",
    "legend.remoteOnly": "僅在雲端（未拉取）",
    "legend.tag": "標籤（版本）",
    "legend.commit": "犯罪",
    "legend.stash": "藏匿（擱置的工作）",
    "legend.mergedIn": "合併進來（寫在另一個分支上）",
    "legend.nodes": "盒子",
    "legend.lines": "線路",
    "legend.edgeParent": "同一行的上一次提交",
    "legend.edgeMerge": "合併分支－箭頭指向合併提交",
    "legend.edgeBranch": "這裡分叉了一根樹枝",
    "legend.edgeStash": "儲存和它被擱置的提交",
    "legend.edgeMergedChain": "該分支的行貫穿合併的提交",
    "legend.edgeMergedTie": "其寫入的分支上的相同提交",
    "menu.jumpHead": "⌖ 前往結帳",
    "menu.resetView": "⤢ 重置視圖",
    "menu.createBranch": "從這裡建立分支...",
    "menu.checkout": "檢查這個提交",
    "menu.copySha": "複製提交 SHA",
    "menu.pushBranch": "推送分支“{name}”",
    "menu.renameBranch": "重新命名分支“{name}”…",
    "menu.deleteBranch": "刪除分支“{name}”…",
    "menu.renameCommit": "重新命名提交訊息...",
    "menu.undoCommit": "撤銷提交（保留更改）...",
    "menu.viewChanges": "查看更改...",
    "menu.mergeBranch": "將“{source}”合併為“{target}”…",
    "menu.jumpToOriginal": "轉到原始提交",
    "node.mergedInTooltip": "合併 {merge} 將此提交放入“{target}”中 - 它寫在“{origin}”上。",
    "changes.title": "{sha} 的變化",
    "changes.tabChanged": "改變了",
    "changes.tabAll": "所有文件",
    "changes.added": "額外",
    "changes.modified": "修改的",
    "changes.deleted": "已刪除",
    "changes.renamed": "更名",
    "changes.noChanges": "此提交沒有更改任何文件。",
    "changes.loading": "加載差異...",
    "changes.selectFile": "選擇一個文件以查看其更改。",
    "changes.original": "原來的",
    "changes.changed": "這次提交",
    "changes.binary": "二進位檔案 - 沒有文字差異可顯示。",
    "changes.tooLarge": "文件太大，無法比較。",
    "changes.renamedFrom": "由 {path} 重新命名",
    "changes.close": "關閉",
    "changes.prevChange": "之前的變更",
    "changes.nextChange": "下一步更改",
    "changes.collapseAll": "折疊所有資料夾",
    "changes.expandAll": "展開所有資料夾",
    "changes.searchPlaceholder": "搜尋文件...",
    "changes.noSearchResults": "沒有與您的搜尋相符的文件。",
    "changes.maximize": "最大化",
    "changes.restore": "恢復尺寸",
    "find.open": "在此差異中搜尋 (Ctrl+F)",
    "find.placeholder": "在差異中尋找...",
    "find.prev": "上一場比賽",
    "find.next": "下一場比賽",
    "find.close": "關閉搜尋",
    "find.noResults": "沒有結果",
    "commit.title": "提交更改",
    "commit.close": "關閉",
    "commit.loading": "閱讀工作樹...",
    "commit.noChanges": "沒有要提交的本地更改。",
    "commit.selectFile": "選擇一個檔案以查看其差異。",
    "commit.selectAll": "選擇全部",
    "commit.selectNone": "全部清除",
    "commit.message": "提交訊息",
    "commit.messagePlaceholder": "描述變化",
    "commit.selectedCount": "已選擇 {count} 文件",
    "commit.review": "審查",
    "commit.reviewTitle": "提交前的最終檢查",
    "commit.reviewSummary": "提交帶有訊息的 {count} 檔案：{message}",
    "commit.back": "後退",
    "commit.commit": "建立本地提交",
    "menu.stashApply": "應用程式隱藏",
    "menu.stashPop": "彈出隱藏（應用和刪除）",
    "menu.stashDrop": "放下藏品…",
    "merge.title": "合併分支",
    "merge.route": "合併到當前分支",
    "merge.source": "來自（來源）",
    "merge.target": "進入（當前）",
    "merge.loading": "正在分析合併...",
    "merge.upToDate": "已經是最新的了——沒有什麼可以合併的。",
    "merge.fastForward": "快轉 — 不需要合併提交（勾選「建立合併提交」以強制合併提交）。",
    "merge.summary": "{files} 檔案已更改，{conflicts} 衝突。",
    "merge.conflictsWarning": "此合併有衝突 - 您仍然可以合併，然後在編輯器中解決它們。",
    "merge.noChanges": "沒有文件更改。",
    "merge.files": "由此產生的變化",
    "merge.added": "額外",
    "merge.modified": "修改的",
    "merge.deleted": "已刪除",
    "merge.conflict": "衝突",
    "merge.message": "合併提交訊息",
    "merge.messagePlaceholder": "合併分支“來源”",
    "merge.messageFfHint": "快進合併時被忽略（不建立提交）。",
    "merge.noFastForward": "建立合併提交（無快轉）",
    "merge.noFastForwardHint": "始終記錄合併提交，即使分支可以快轉。",
    "merge.merge": "合併",
    "merge.cancel": "取消",
    "merge.previewError": "無法計算預覽：{message}",
    "merge.squashBadge": "壁球",
    "merge.squashNote": "Squash 合併 — 每一項更改都會作為“{target}”上的一個提交提交； “{source}”自己的承諾不存在於其歷史中。在“設定”>“合併”中切換回來。",
    "merge.messageSquash": "提交訊息",
    "merge.squashMessagePlaceholder": "壓縮來自“來源”的更改",
    "newBranch.title": "建立分支",
    "newBranch.startPoint": "從 {sha} 開始的新分支",
    "newBranch.startPointOn": "在 {refs} 上",
    "newBranch.location": "地點",
    "newBranch.locationRoot": "（根）",
    "newBranch.expandAll": "全部展開",
    "newBranch.collapseAll": "全部折疊",
    "newBranch.expandFolder": "展開資料夾",
    "newBranch.collapseFolder": "折疊資料夾",
    "newBranch.name": "分行名稱",
    "newBranch.namePlaceholder": "我的分支",
    "newBranch.fullName": "全名：{name}",
    "newBranch.invalid": "分支名稱無效",
    "newBranch.exists": "同名的分支已存在",
    "newBranch.checkout": "建立後簽出分支",
    "newBranch.create": "創造",
    "newBranch.cancel": "取消",
    "status.loading": "正在載入圖表...",
    "status.summary": "{repo}顯示 {commits} 提交、{refs} 參考",
    "status.branchCreated": "在 {sha} 建立分支“{name}”",
    "status.error": "錯誤：{message}",
    "status.opFailed": "操作失敗",
    "status.fetching": "正在獲取…",
    "status.pulling": "拉…",
    "status.pushing": "推動…",
    "status.committing": "建立本地提交...",
    "status.commitCreated": "建立本地提交 {sha}: {message}",
    "status.syncing": "正在同步...",
    "status.noHead": "圖表中未找到目前結帳。",
    "status.undoing": "正在撤銷提交...",
    "status.commitUndone": "提交撤銷－變更返回工作樹。",
    "status.undoConflict": "撤銷命中衝突 - 在編輯器中解決它們，然後繼續。",
    "status.stashApplying": "正在應用隱藏…",
    "status.stashPopping": "彈出藏品…",
    "status.stashDropping": "掉落藏品…",
    "status.stashApplied": "隱藏應用。",
    "status.stashPopped": "藏匿彈出。",
    "status.stashDropped": "儲藏室掉落。",
    "status.stashConflict": "隱藏衝突 - 在編輯器中解決它們。",
    "status.merging": "合併…",
    "status.merged": "合併完成。",
    "status.mergeConflict": "合併有衝突 - 在編輯器中解決它們，然後提交。",
    "search.title": "搜尋",
    "search.tooltip": "搜尋提交",
    "search.placeholder": "搜尋提交...",
    "search.previous": "之前的結果",
    "search.next": "下一個結果",
    "search.noResults": "沒有結果",
    "search.resultCount": "{index}/{count}",
    "search.filter": "過濾模式",
    "search.highlight": "高亮模式",
    "search.filterMode": "搜尋顯示",
    "search.filterModeFilter": "過濾器（隱藏不匹配）",
    "search.filterModeHighlight": "突出顯示（顯示全部，標記匹配項）",
    "search.filterModeHint": "選擇搜尋結果在圖表中的顯示方式。",
    "search.visibility": "搜尋地點",
    "search.visibilityToolbar": "工具列按鈕",
    "search.visibilityAlwaysVisible": "始終可見的欄",
    "search.visibilityHint": "將搜尋顯示為工具列圖示或頂部的永久列。",
    "footer.github": "GitHub",
  },
  ja: {
    "toolbar.refresh": "⟳ リフレッシュ",
    "toolbar.fetch": "⤓ フェッチ",
    "toolbar.pull": "⇩引く",
    "toolbar.push": "⇧プッシュ",
    "toolbar.commit": "✓ コミット",
    "toolbar.sync": "⇅ 同期",
    "toolbar.jumpHead": "⌖ チェックアウトに行く",
    "toolbar.reset": "⤢ ビューをリセット",
    "toolbar.settings": "⚙ 設定",
    "details.header": "コミットの詳細",
    "details.close": "近い",
    "details.sha": "社",
    "details.shortSha": "短いSHA",
    "details.message": "メッセージ",
    "details.author": "著者",
    "details.date": "日付",
    "details.labels": "ラベル",
    "details.location": "位置",
    "details.currentHead": "HEAD — 現在のチェックアウト",
    "settings.title": "設定",
    "settings.language": "言語",
    "settings.theme": "テーマ",
    "settings.themeLight": "ライト",
    "settings.themeDark": "暗い",
    "settings.themeLightHint": "明るい背景、暗いテキスト。",
    "settings.themeDarkHint": "暗い背景、明るいテキスト。",
    "settings.mainBranch": "本店",
    "settings.mainBranchAuto": "自動",
    "settings.mainBranchSearch": "ブランチを検索…",
    "settings.mainBranchNoMatch": "一致するブランチがありません",
    "settings.display": "表示スタイル",
    "settings.displayModern": "モダンな",
    "settings.displayClassic": "クラシック",
    "settings.displayModernHint": "フリー キャンバス — ドラッグしてパンし、スクロールしてズームします。",
    "settings.displayClassicHint": "固定キャンバス — トランクは左に固定され、ズームなし、スクロールのみ (SVN リビジョン グラフのように)。",
    "settings.sectionGeneral": "一般的な",
    "settings.sectionGraph": "グラフ",
    "settings.sectionChanges": "ビューの変更",
    "settings.diffMinimap": "差分ミニマップ",
    "settings.diffMinimapOn": "示されている",
    "settings.diffMinimapOff": "隠れた",
    "settings.diffMinimapOnHint": "diff の横に VS Code スタイルの概要ストリップを表示します。ドラッグしてスクロールします。チェンジマーカーも付属。",
    "settings.diffMinimapOffHint": "概要ストリップはありません - 通常どおり差分をスクロールします。",
    "settings.commitReview": "レビューをコミットする",
    "settings.commitReviewOn": "コミットする前に尋ねる",
    "settings.commitReviewOff": "直接コミットする",
    "settings.commitReviewOnHint": "ローカルコミットを作成する前に、最終的なファイルリストとメッセージチェックを表示します。",
    "settings.commitReviewOffHint": "[コミット] ボタンをクリックすると、ローカル コミットがすぐに作成されます。",
    "settings.svnBranchDialog": "SVN スタイルの分岐ダイアログ",
    "settings.svnBranchDialogHint": "ブランチを作成するときに (単純なプロンプトの代わりに) フォルダー ツリー ピッカーを表示します。",
    "settings.branchDialog": "ブランチダイアログ",
    "settings.branchDialogSvnTitle": "SVN スタイル",
    "settings.branchDialogNativeVscode": "VSコードスタイル",
    "settings.branchDialogNativeVs": "Visual Studio スタイル",
    "settings.branchDialogNativeHint": "IDE の組み込みブランチ プロンプト。",
    "settings.close": "近い",
    "settings.done": "終わり",
    "settings.sectionAdvanced": "高度な",
    "settings.gitSource": "Git 実行可能ファイル",
    "settings.gitSourceBuiltin": "内蔵",
    "settings.gitSourceBuiltinHint": "IDE の組み込み Git 拡張機能の git バイナリを使用します。",
    "settings.gitSourceCustom": "カスタムパス",
    "settings.gitPath": "gitへのパス",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "ブラウズ…",
    "settings.jargon": "Git 用語",
    "settings.jargonTranslate": "翻訳する",
    "settings.jargonEnglish": "英語で保管してください",
    "settings.jargonTranslateHint": "Git の用語 (プル、プッシュ、コミット、ブランチなど) をインターフェイス言語に翻訳します。",
    "settings.jargonEnglishHint": "Git 用語 (プル、プッシュ、コミット、ブランチなど) は英語にしてください。他のすべてを翻訳します。",
    "settings.sectionMerge": "マージ",
    "settings.mergeMode": "結合スタイル",
    "settings.mergeModeMerge": "通常のマージ",
    "settings.mergeModeSquash": "スカッシュマージ",
    "settings.mergeModeMergeHint": "ブランチをそのままマージします。そのコミットは現在のブランチの履歴の一部となり、マージ コミットによって結合されます (または可能な場合は早送りされます)。",
    "settings.mergeModeSquashHint": "ブランチ全体を現在のブランチ上の単一のコミットに折りたたみます (git merge --squash)。ブランチ自身のコミットは現在のブランチの履歴には決して入りません。そこではコミットが 1 つだけ表示されます。ブランチ自体は変更されないため、そのコミットは独自のレーンに表示されたままになります。",
    "settings.mergedView": "グラフ内のマージされたコミット",
    "settings.mergedViewBranch": "自分のブランチのみで",
    "settings.mergedViewTarget": "ブランチでもマージされました",
    "settings.mergedViewBranchHint": "マージされたブランチのコミットは、独自のレーンにのみ描画されます。マージはコネクタとして表示されます。古典的なビュー。",
    "settings.mergedViewTargetHint": "マージによってもたらされたコミットは、受信側ブランチのレーンにも描画され、マージ コミットの下に積み重ねられ、元のコミットと同じレベルになります。これは、そのブランチに実際にコミットが含まれているためです。コピーは青白く、書かれた枝が付いているので、そこで作られたものではないことは明らかです。スカッシュのマージは影響を受けません。スカッシュは 1 つの通常のコミットを書き込むため、そのコミットのみが表示されます。",
    "legend.title": "伝説",
    "legend.head": "HEAD / 現在のブランチ",
    "legend.local": "地方支店",
    "legend.remote": "リモートブランチ",
    "legend.remoteOnly": "クラウド内のみ（プルではない）",
    "legend.tag": "タグ(バージョン)",
    "legend.commit": "専念",
    "legend.stash": "隠し場所（棚上げされた作品）",
    "legend.mergedIn": "マージとともに入ってきました (別のブランチに書き込まれました)",
    "legend.nodes": "ボックス",
    "legend.lines": "ライン",
    "legend.edgeParent": "同じ行の前のコミット",
    "legend.edgeMerge": "マージされたブランチ — 矢印はマージ コミットを指します",
    "legend.edgeBranch": "ここで分岐した枝",
    "legend.edgeStash": "スタッシュとそれが保留されたコミット",
    "legend.edgeMergedChain": "マージされたコミットを通るこのブランチの行",
    "legend.edgeMergedTie": "書き込まれたブランチ上の同じコミット",
    "menu.jumpHead": "⌖ チェックアウトに行く",
    "menu.resetView": "⤢ ビューをリセット",
    "menu.createBranch": "ここからブランチを作成…",
    "menu.checkout": "このコミットをチェックアウトする",
    "menu.copySha": "コミットSHAのコピー",
    "menu.pushBranch": "ブランチ「{name}」をプッシュします",
    "menu.renameBranch": "ブランチの名前を「{name}」に変更します。",
    "menu.deleteBranch": "ブランチ「{name}」を削除します。",
    "menu.renameCommit": "コミットメッセージの名前を変更…",
    "menu.undoCommit": "コミットを取り消します (変更を保持します)…",
    "menu.viewChanges": "変更を表示…",
    "menu.mergeBranch": "「{source}」を「{target}」にマージします。",
    "menu.jumpToOriginal": "元のコミットに移動する",
    "node.mergedInTooltip": "{merge} をマージすると、このコミットは「{target}」に取り込まれました。これは「{origin}」に書き込まれました。",
    "changes.title": "{sha} の変更点",
    "changes.tabChanged": "変更されました",
    "changes.tabAll": "すべてのファイル",
    "changes.added": "追加した",
    "changes.modified": "修正済み",
    "changes.deleted": "削除されました",
    "changes.renamed": "名前変更",
    "changes.noChanges": "このコミットではファイルは変更されません。",
    "changes.loading": "差分を読み込み中…",
    "changes.selectFile": "ファイルを選択してその変更を確認します。",
    "changes.original": "オリジナル",
    "changes.changed": "このコミット",
    "changes.binary": "バイナリ ファイル — 表示するテキストの差分はありません。",
    "changes.tooLarge": "ファイルが大きすぎて差分を取得できません。",
    "changes.renamedFrom": "{path}から名前変更",
    "changes.close": "近い",
    "changes.prevChange": "前回の変更",
    "changes.nextChange": "次の変更",
    "changes.collapseAll": "すべてのフォルダーを折りたたむ",
    "changes.expandAll": "すべてのフォルダーを展開します",
    "changes.searchPlaceholder": "ファイルを検索…",
    "changes.noSearchResults": "検索に一致するファイルはありません。",
    "changes.maximize": "最大化する",
    "changes.restore": "復元サイズ",
    "find.open": "この差分内を検索 (Ctrl+F)",
    "find.placeholder": "差分で検索…",
    "find.prev": "前回の試合",
    "find.next": "次の試合",
    "find.close": "検索を閉じる",
    "find.noResults": "結果はありません",
    "commit.title": "変更をコミットする",
    "commit.close": "近い",
    "commit.loading": "作業ツリーを読み取り中…",
    "commit.noChanges": "コミットするローカル変更はありません。",
    "commit.selectFile": "ファイルを選択すると、その差分が表示されます。",
    "commit.selectAll": "すべて選択",
    "commit.selectNone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.message": "コミットメッセージ",
    "commit.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.selectedCount": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.review": "レビュー",
    "commit.reviewTitle": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.reviewSummary": "メッセージ {message} を含む {count} ファイルをコミットします。",
    "commit.back": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "commit.commit": "ローカルコミットを作成する",
    "menu.stashApply": "スタッシュを適用する",
    "menu.stashPop": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "menu.stashDrop": "隠し場所をドロップ…",
    "merge.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.route": "現在のブランチにマージする",
    "merge.source": "から（情報源）",
    "merge.target": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.loading": "マージを分析中…",
    "merge.upToDate": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.fastForward": "早送り — マージ コミットは必要ありません (マージ コミットを強制するには、[マージ コミットを作成する] にチェックを入れます)。",
    "merge.summary": "{files} ファイルが変更され、{conflicts} が競合しました。",
    "merge.conflictsWarning": "このマージには競合がありますが、マージしてエディタで解決することは可能です。",
    "merge.noChanges": "ファイルの変更はありません。",
    "merge.files": "結果として生じる変化",
    "merge.added": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.modified": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.deleted": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.conflict": "対立",
    "merge.message": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageFfHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForward": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.noFastForwardHint": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.merge": "マージ",
    "merge.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.previewError": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashBadge": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashNote": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.messageSquash": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "merge.squashMessagePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.title": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.startPoint": "{sha} から始まる新しいブランチ",
    "newBranch.startPointOn": "{refs}上",
    "newBranch.location": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.locationRoot": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.expandAll": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseAll": "すべて折りたたむ",
    "newBranch.expandFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.collapseFolder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.name": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.namePlaceholder": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.fullName": "フルネーム：{name}",
    "newBranch.invalid": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.exists": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.checkout": "作成後のチェックアウトブランチ",
    "newBranch.create": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "newBranch.cancel": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.loading": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.summary": "{repo}{commits} コミット、{refs} 参照を表示",
    "status.branchCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.error": "エラー:{message}",
    "status.opFailed": "操作が失敗しました",
    "status.fetching": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pulling": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.pushing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.committing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitCreated": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.syncing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.noHead": "現在のチェックアウトがグラフ内に見つかりません。",
    "status.undoing": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.commitUndone": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.undoConflict": "ヒットの競合を元に戻します。エディターで競合を解決してから続行します。",
    "status.stashApplying": "スタッシュを適用しています…",
    "status.stashPopping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropping": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashApplied": "スタッシュが適用されました。",
    "status.stashPopped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashDropped": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.stashConflict": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.merging": "結合中…",
    "status.merged": "Error 500 (Server Error)!!1500.That’s an error.There was an error. Please try again later.That’s all we know.",
    "status.mergeConflict": "Merge には競合があります。エディターで競合を解決してからコミットします。",
    "search.title": "検索",
    "search.tooltip": "コミットの検索",
    "search.placeholder": "コミットを検索…",
    "search.previous": "前回の結果",
    "search.next": "次の結果",
    "search.noResults": "結果はありません",
    "search.resultCount": "{index}/{count}",
    "search.filter": "フィルターモード",
    "search.highlight": "ハイライトモード",
    "search.filterMode": "検索表示",
    "search.filterModeFilter": "フィルター (一致しないものを非表示)",
    "search.filterModeHighlight": "ハイライト (すべて表示、一致マークを付ける)",
    "search.filterModeHint": "検索結果をグラフに表示する方法を選択します。",
    "search.visibility": "検索場所",
    "search.visibilityToolbar": "ツールバーボタン",
    "search.visibilityAlwaysVisible": "常に表示されるバー",
    "search.visibilityHint": "検索をツールバー アイコンまたは上部に永続的なバーとして表示します。",
    "footer.github": "GitHub",
  },
  "mni-mtei": {
  },
  ko: {
    "toolbar.refresh": "⟳ 새로고침",
    "toolbar.fetch": "⤓ 가져오기",
    "toolbar.pull": "⇩ 당기기",
    "toolbar.push": "⇧ 푸시",
    "toolbar.commit": "✓ 커밋",
    "toolbar.sync": "⇅ 동기화",
    "toolbar.jumpHead": "⌖ 결제하러 가기",
    "toolbar.reset": "⤢ 보기 재설정",
    "toolbar.settings": "⚙ 설정",
    "details.header": "커밋 세부정보",
    "details.close": "닫다",
    "details.sha": "SHA",
    "details.shortSha": "짧은 SHA",
    "details.message": "메시지",
    "details.author": "작가",
    "details.date": "날짜",
    "details.labels": "라벨",
    "details.location": "위치",
    "details.currentHead": "HEAD — 현재 결제",
    "settings.title": "설정",
    "settings.language": "언어",
    "settings.theme": "주제",
    "settings.themeLight": "빛",
    "settings.themeDark": "어두운",
    "settings.themeLightHint": "밝은 배경, 어두운 텍스트.",
    "settings.themeDarkHint": "어두운 배경, 밝은 텍스트.",
    "settings.mainBranch": "본점",
    "settings.mainBranchAuto": "오토매틱",
    "settings.mainBranchSearch": "지점 검색…",
    "settings.mainBranchNoMatch": "일치하는 분기가 없습니다.",
    "settings.display": "표시 스타일",
    "settings.displayModern": "현대의",
    "settings.displayClassic": "권위 있는",
    "settings.displayModernHint": "무료 캔버스 — 드래그하여 이동하고 스크롤하여 확대/축소합니다.",
    "settings.displayClassicHint": "고정 캔버스 - 트렁크가 왼쪽에 고정되고, 확대/축소되지 않고 스크롤만 가능합니다(SVN 개정 그래프와 유사).",
    "settings.sectionGeneral": "일반적인",
    "settings.sectionGraph": "그래프",
    "settings.sectionChanges": "변경사항 보기",
    "settings.diffMinimap": "미니맵 차이",
    "settings.diffMinimapOn": "표시됨",
    "settings.diffMinimapOff": "숨겨진",
    "settings.diffMinimapOnHint": "diff 옆에 VS Code 스타일 개요 스트립을 표시합니다. 드래그하여 스크롤합니다. 변경 마커가 포함되어 있습니다.",
    "settings.diffMinimapOffHint": "개요 스트립 없음 — diff를 정상적으로 스크롤합니다.",
    "settings.commitReview": "검토 커밋",
    "settings.commitReviewOn": "커밋하기 전에 물어보세요",
    "settings.commitReviewOff": "직접 커밋",
    "settings.commitReviewOnHint": "로컬 커밋을 생성하기 전에 최종 파일 목록과 메시지 확인을 표시합니다.",
    "settings.commitReviewOffHint": "Commit 버튼을 누르면 로컬 커밋이 즉시 생성됩니다.",
    "settings.svnBranchDialog": "SVN 스타일 분기 대화 상자",
    "settings.svnBranchDialogHint": "분기를 생성할 때 폴더 트리 선택기를 표시합니다(간단한 프롬프트 대신).",
    "settings.branchDialog": "분기 대화상자",
    "settings.branchDialogSvnTitle": "SVN 스타일",
    "settings.branchDialogNativeVscode": "VS 코드 스타일",
    "settings.branchDialogNativeVs": "비주얼 스튜디오 스타일",
    "settings.branchDialogNativeHint": "IDE의 내장 분기 프롬프트입니다.",
    "settings.close": "닫다",
    "settings.done": "완료",
    "settings.sectionAdvanced": "고급의",
    "settings.gitSource": "Git 실행 파일",
    "settings.gitSourceBuiltin": "내장",
    "settings.gitSourceBuiltinHint": "IDE에 내장된 Git 확장의 git 바이너리를 사용하세요.",
    "settings.gitSourceCustom": "맞춤 경로",
    "settings.gitPath": "자식 경로",
    "settings.gitPathPlaceholder": "/usr/bin/git",
    "settings.gitPathBrowse": "먹다…",
    "settings.jargon": "힘내 용어",
    "settings.jargonTranslate": "번역하다",
    "settings.jargonEnglish": "영어로 유지",
    "settings.jargonTranslateHint": "Git 용어(풀, 푸시, 커밋, 분기…)를 인터페이스 언어로 번역합니다.",
    "settings.jargonEnglishHint": "Git 용어(풀, 푸시, 커밋, 분기…)를 영어로 유지하세요. 다른 모든 것을 번역하십시오.",
    "settings.sectionMerge": "병합",
    "settings.mergeMode": "스타일 병합",
    "settings.mergeModeMerge": "일반 병합",
    "settings.mergeModeSquash": "스쿼시 병합",
    "settings.mergeModeMergeHint": "브랜치를 있는 그대로 병합합니다. 해당 커밋은 현재 브랜치 기록의 일부가 되고 병합 커밋으로 결합됩니다(또는 가능한 경우 빨리 감기).",
    "settings.mergeModeSquashHint": "전체 분기를 현재 분기의 단일 커밋으로 축소합니다(git merge --squash). 브랜치 자체 커밋은 현재 브랜치의 기록에 절대 포함되지 않습니다. 거기에는 하나의 커밋만 표시됩니다. 브랜치 자체는 그대로 유지되므로 해당 커밋은 자체 레인에 계속 표시됩니다.",
    "settings.mergedView": "그래프의 병합된 커밋",
    "settings.mergedViewBranch": "자신의 지점에서만",
    "settings.mergedViewTarget": "또한 지점에서 그들은 병합되었습니다.",
    "settings.mergedViewBranchHint": "병합된 브랜치의 커밋은 해당 레인에서만 그려집니다. 병합은 커넥터로 표시됩니다. 고전적인 견해.",
    "settings.mergedViewTargetHint": "병합을 통해 가져온 커밋은 수신 브랜치의 레인에도 그려지며, 병합 커밋 아래에 쌓여 원본과 같은 수준이 됩니다. 왜냐하면 이제 해당 브랜치에 커밋이 실제로 포함되어 있기 때문입니다. 사본은 창백하고 쓰여진 가지의 머리에 붙어 있으므로 그곳에서 만들어지지 않았다는 것이 분명합니다. 스쿼시 병합은 영향을 받지 않습니다. 스쿼시는 하나의 일반 커밋을 작성하므로 해당 커밋만 나타납니다.",
    "legend.title": "전설",
    "legend.head": "HEAD / 현재 지점",
    "legend.local": "현지 지점",
    "legend.remote": "원격 지점",
    "legend.remoteOnly": "클라우드에서만(풀어오지 않음)",
    "legend.tag": "태그(버전)",
    "legend.commit": "저지르다",
    "legend.stash": "Stash(보관 작업)",
    "legend.mergedIn": "병합으로 들어옴(다른 지점에 작성됨)",
    "legend.nodes": "상자",
    "legend.lines": "윤곽",
    "legend.edgeParent": "같은 줄의 이전 커밋",
    "legend.edgeMerge": "병합된 분기 — 병합 커밋을 가리키는 화살표",
    "legend.edgeBranch": "여기서 갈라진 가지",
    "legend.edgeStash": "Stash와 보류된 커밋",
    "legend.edgeMergedChain": "병합된 커밋을 통해 실행되는 이 브랜치의 라인",
    "legend.edgeMergedTie": "작성된 브랜치의 동일한 커밋",
    "menu.jumpHead": "⌖ 결제하러 가기",
    "menu.resetView": "⤢ 보기 재설정",
    "menu.createBranch": "여기에서 지점을 생성하세요...",
    "menu.checkout": "이 커밋을 확인하세요",
    "menu.copySha": "커밋 SHA 복사",
    "menu.pushBranch": "푸시 브랜치 \"{name}\"",
    "menu.renameBranch": "지점 \"{name}\" 이름 바꾸기…",
    "menu.deleteBranch": "\"{name}\" 브랜치 삭제…",
    "menu.renameCommit": "커밋 메시지 이름 바꾸기…",
    "menu.undoCommit": "커밋 취소(변경 사항 유지)…",
    "menu.viewChanges": "변경사항 보기…",
    "menu.mergeBranch": "\"{source}\"를 \"{target}\"로 병합…",
    "menu.jumpToOriginal": "원래 커밋으로 이동",
    "node.mergedInTooltip": "Merge {merge}는 이 커밋을 \"{target}\"로 가져왔습니다. 이 커밋은 \"{origin}\"에 작성되었습니다.",
    "changes.title": "{sha}의 변경 사항",
    "changes.tabChanged": "변경됨",
    "changes.tabAll": "모든 파일",
    "changes.added": "추가됨",
    "changes.modified": "수정됨",
    "changes.deleted": "삭제됨",
    "changes.renamed": "이름이 변경됨",
    "changes.noChanges": "이 커밋은 파일을 변경하지 않습니다.",
    "changes.loading": "차이점 로드 중…",
    "changes.selectFile": "변경 사항을 보려면 파일을 선택하세요.",
    "changes.original": "원래의",
    "changes.changed": "이 커밋",
    "changes.binary": "바이너리 파일 — 표시할 텍스트 차이점이 없습니다.",
    "changes.tooLarge": "파일이 너무 커서 비교할 수 없습니다.",
    "changes.renamedFrom": "{path}에서 이름이 변경됨",
    "changes.close": "닫다",
    "changes.prevChange": "이전 변경사항",
    "changes.nextChange": "다음 변경사항",
    "changes.collapseAll": "모든 폴더 축소",
    "changes.expandAll": "모든 폴더 확장",
    "changes.searchPlaceholder": "파일 검색…",
    "changes.noSearchResults": "검색어와 일치하는 파일이 없습니다.",
    "changes.maximize": "최대화",
    "changes.restore": "복원 크기",
    "find.open": "이 차이점에서 검색(Ctrl+F)",
    "find.placeholder": "차이점에서 찾기…",
    "find.prev": "이전 경기",
    "find.next": "다음 경기",
    "find.close": "검색 닫기",
    "find.noResults": "결과 없음",
    "commit.title": "변경 사항 커밋",
    "commit.close": "닫다",
    "commit.loading": "작업 트리를 읽는 중…",
    "commit.noChanges": "커밋할 로컬 변경 사항이 없습니다.",
    "commit.selectFile": "차이점을 보려면 파일을 선택하세요.",
    "commit.selectAll": "모두 선택",
    "commit.selectNone": "모두 지우기",
    "commit.message": "커밋 메시지",
    "commit.messagePlaceholder": "변경사항을 설명하세요.",
    "commit.selectedCount": "{count} 파일이 선택됨",
    "commit.review": "검토",
    "commit.reviewTitle": "커밋 전 최종 확인",
    "commit.reviewSummary": "메시지가 포함된 {count} 파일 커밋: {message}",
    "commit.back": "뒤쪽에",
    "commit.commit": "로컬 커밋 만들기",
    "menu.stashApply": "스태시 적용",
    "menu.stashPop": "팝 스태시(적용 및 제거)",
    "menu.stashDrop": "보관함 삭제…",
    "merge.title": "병합 지점",
    "merge.route": "현재 분기에 병합",
    "merge.source": "(출처)에서",
    "merge.target": "(현재)로",
    "merge.loading": "병합 분석 중…",
    "merge.upToDate": "이미 최신 상태이므로 병합할 항목이 없습니다.",
    "merge.fastForward": "빨리 감기 — 병합 커밋이 필요하지 않습니다(강제하려면 \"병합 커밋 생성\"을 선택하세요).",
    "merge.summary": "{files} 파일이 변경되었습니다. {conflicts} 충돌이 발생했습니다.",
    "merge.conflictsWarning": "이 병합에는 충돌이 있습니다. 여전히 병합한 다음 편집기에서 해결할 수 있습니다.",
    "merge.noChanges": "파일이 변경되지 않습니다.",
    "merge.files": "결과 변경 사항",
    "merge.added": "추가됨",
    "merge.modified": "수정됨",
    "merge.deleted": "삭제됨",
    "merge.conflict": "갈등",
    "merge.message": "커밋 메시지 병합",
    "merge.messagePlaceholder": "분기 '소스' 병합",
    "merge.messageFfHint": "빨리 감기 병합에서는 무시됩니다(커밋이 생성되지 않음).",
    "merge.noFastForward": "병합 커밋 생성(빨리 감기 없음)",
    "merge.noFastForwardHint": "브랜치가 빨리 감길 수 있는 경우에도 항상 병합 커밋을 기록하세요.",
    "merge.merge": "병합",
    "merge.cancel": "취소",
    "merge.previewError": "미리보기를 계산할 수 없습니다: {message}",
    "merge.squashBadge": "스쿼시",
    "merge.squashNote": "스쿼시 병합 — 모든 변경 사항은 \"{target}\"에 대한 하나의 커밋으로 이루어집니다. “{source}” 자체 커밋은 기록에 남지 않습니다. 설정 › 병합에서 다시 전환하세요.",
    "merge.messageSquash": "커밋 메시지",
    "merge.squashMessagePlaceholder": "'소스'의 변경 사항이 압축되었습니다.",
    "newBranch.title": "지점 생성",
    "newBranch.startPoint": "{sha}에서 시작하는 새로운 브랜치",
    "newBranch.startPointOn": "{refs}에서",
    "newBranch.location": "위치",
    "newBranch.locationRoot": "(뿌리)",
    "newBranch.expandAll": "모두 펼치기",
    "newBranch.collapseAll": "모두 접기",
    "newBranch.expandFolder": "폴더 확장",
    "newBranch.collapseFolder": "폴더 축소",
    "newBranch.name": "지점명",
    "newBranch.namePlaceholder": "내 지점",
    "newBranch.fullName": "전체 이름: {name}",
    "newBranch.invalid": "잘못된 지점 이름",
    "newBranch.exists": "이 이름을 가진 분기가 이미 존재합니다.",
    "newBranch.checkout": "생성 후 Checkout 브랜치",
    "newBranch.create": "만들다",
    "newBranch.cancel": "취소",
    "status.loading": "그래프 로드 중…",
    "status.summary": "{repo}{commits} 커밋 표시, {refs} 참조",
    "status.branchCreated": "{sha}에 \"{name}\" 브랜치를 생성했습니다.",
    "status.error": "오류: {message}",
    "status.opFailed": "작업 실패",
    "status.fetching": "가져오는 중…",
    "status.pulling": "당기는 중…",
    "status.pushing": "미는…",
    "status.committing": "로컬 커밋 생성 중…",
    "status.commitCreated": "생성된 로컬 커밋 {sha}: {message}",
    "status.syncing": "동기화 중…",
    "status.noHead": "그래프에 현재 결제가 없습니다.",
    "status.undoing": "커밋을 실행취소하는 중…",
    "status.commitUndone": "커밋 취소 — 변경 사항이 작업 트리에 다시 표시됩니다.",
    "status.undoConflict": "적중 충돌을 취소하고 편집기에서 문제를 해결한 다음 계속하세요.",
    "status.stashApplying": "숨김 적용 중…",
    "status.stashPopping": "터지는 은닉처…",
    "status.stashDropping": "숨김 삭제 중…",
    "status.stashApplied": "스태시가 적용되었습니다.",
    "status.stashPopped": "스태쉬가 터졌습니다.",
    "status.stashDropped": "스태쉬가 떨어졌습니다.",
    "status.stashConflict": "스태시 충돌 — 편집기에서 해결하세요.",
    "status.merging": "병합 중…",
    "status.merged": "병합이 완료되었습니다.",
    "status.mergeConflict": "병합에 충돌이 있습니다. 편집기에서 충돌을 해결한 다음 커밋하세요.",
    "search.title": "찾다",
    "search.tooltip": "커밋 검색",
    "search.placeholder": "커밋 검색…",
    "search.previous": "이전 결과",
    "search.next": "다음 결과",
    "search.noResults": "결과 없음",
    "search.resultCount": "{index}/{count}",
    "search.filter": "필터 모드",
    "search.highlight": "하이라이트 모드",
    "search.filterMode": "검색 표시",
    "search.filterModeFilter": "필터(일치하지 않는 항목 숨기기)",
    "search.filterModeHighlight": "강조 표시(모두 표시, 일치 항목 표시)",
    "search.filterModeHint": "검색 결과가 그래프에 표시되는 방식을 선택합니다.",
    "search.visibility": "위치 검색",
    "search.visibilityToolbar": "툴바 버튼",
    "search.visibilityAlwaysVisible": "항상 보이는 바",
    "search.visibilityHint": "상단에 도구 모음 아이콘 또는 영구 표시줄로 검색을 표시합니다.",
    "footer.github": "GitHub",
  },
};

export type MsgKey = keyof Dict;

/**
 * Languages whose base {@link DICTS} entries are hand-written (never fed through
 * the auto-translator). Their base text is authored in the *keep-Git-terms-in-
 * English* style — a translated sentence with words like *branch* / *commit* /
 * *merge* / *stash* / *pull* left in English — so for these the "keep in English"
 * setting simply shows the base string, and the fully-translated rendering comes
 * from the `tr` side of {@link JARGON}. `en` is trivially in this set.
 */
const HAND_MAINTAINED_LANGS: ReadonlySet<Lang> = new Set<Lang>(["en", "hu"]);

/**
 * Keys whose text carries Git jargon (*pull*, *push*, *commit*, *branch*,
 * *merge*, *stash*, *checkout*, *fetch*, *sync*, …), grouping the Legend, the
 * toolbar, context menus and status lines.
 *
 * With "keep Git terms in English" ON, {@link t} renders these keys so that only
 * the Git terms stay English and the rest of the line is in the active language:
 *   - hand-maintained languages ({@link HAND_MAINTAINED_LANGS}) — the base string
 *     is already authored that way, so it is used as-is;
 *   - auto-translated languages — term-level substitution isn't feasible, so the
 *     whole line falls back to the English base (no per-language upkeep, no
 *     translator awareness).
 * With the setting OFF, the fully-translated `tr` side of {@link JARGON} is used
 * where present, otherwise the (fully-translated) base string.
 *
 * This list is the single source of truth; keep it in sync when adding a
 * jargon-bearing key.
 */
const JARGON_KEYS: ReadonlySet<MsgKey> = new Set<MsgKey>([
  "toolbar.fetch", "toolbar.pull", "toolbar.push", "toolbar.commit", "toolbar.sync",
  "toolbar.jumpHead", "details.header", "legend.head", "legend.local", "legend.remote",
  "legend.remoteOnly", "legend.stash", "legend.edgeMerge", "legend.edgeBranch",
  "legend.edgeStash", "legend.edgeMergedChain", "legend.edgeMergedTie", "menu.checkout",
  "menu.copySha", "menu.pushBranch", "menu.renameBranch", "menu.deleteBranch",
  "menu.renameCommit", "menu.undoCommit", "menu.mergeBranch", "menu.stashApply",
  "menu.stashPop", "menu.stashDrop", "changes.changed", "commit.title", "commit.message",
  "commit.commit", "merge.title", "merge.route", "merge.merge", "newBranch.checkout",
  "status.fetching", "status.pulling", "status.pushing", "status.committing",
  "status.commitCreated", "status.syncing", "status.commitUndone", "status.stashApplying",
  "status.stashPopping", "status.stashDropping", "status.stashApplied", "status.stashPopped",
  "status.stashDropped", "status.merging", "status.merged", "status.mergeConflict",
]);

/**
 * Optional per-language refinement of the *translated* (setting-OFF) rendering
 * for {@link JARGON_KEYS}, for the hand-maintained languages. Only `tr` is read;
 * anything omitted falls back to the auto-translated base string in
 * {@link DICTS}. The ON direction never consults this — see {@link JARGON_KEYS}.
 */
type JargonEntry = { en?: string; tr?: string };

const JARGON: Partial<Record<Lang, Partial<Record<MsgKey, JargonEntry>>>> = {
  hu: {
    "toolbar.fetch": { tr: "⤓ Lekérés" },
    "toolbar.pull": { tr: "⇩ Lehúzás" },
    "toolbar.push": { tr: "⇧ Feltöltés" },
    "toolbar.commit": { tr: "✓ Beküldés" },
    "toolbar.sync": { en: "⇅ Sync" },
    "toolbar.jumpHead": { tr: "⌖ Ugrás a váltásra" },
    "details.header": { tr: "Beküldés részletei" },
    "legend.head": { tr: "HEAD / aktuális ág" },
    "legend.local": { tr: "Lokális ág" },
    "legend.remote": { tr: "Távoli ág" },
    "legend.remoteOnly": { tr: "Csak a felhőben (nincs lehúzva)" },
    "legend.stash": { tr: "Félretett munka" },
    "legend.edgeMerge": { tr: "Beolvasztott ág — a nyíl az összefésülő commitra mutat" },
    "legend.edgeBranch": { tr: "Innen ágazik le egy ág" },
    "legend.edgeStash": { tr: "Félretett munka és a commit, amire félre lett téve" },
    "legend.edgeMergedChain": { tr: "Ennek az ágnak a vonala a beolvasztott commitokon át" },
    "legend.edgeMergedTie": { tr: "Ugyanaz a commit azon az ágon, ahol készült" },
    "menu.checkout": { tr: "Váltás erre a beküldésre" },
    "menu.copySha": { tr: "Beküldés SHA másolása" },
    "menu.pushBranch": { tr: '"{name}" ág feltöltése' },
    "menu.renameBranch": { tr: '"{name}" ág átnevezése…' },
    "menu.deleteBranch": { tr: '"{name}" ág törlése…' },
    "menu.renameCommit": { tr: "Beküldés üzenet átnevezése…" },
    "menu.undoCommit": { tr: "Beküldés visszavonása (változások megtartása)…" },
    "menu.mergeBranch": { en: '"{source}" merge ebbe: "{target}"…' },
    "menu.stashApply": { tr: "Félretett munka alkalmazása" },
    "menu.stashPop": { tr: "Félretett munka kivétele (alkalmaz és töröl)" },
    "menu.stashDrop": { tr: "Félretett munka eldobása…" },
    "changes.changed": { tr: "Ebben a beküldésben" },
    "commit.title": { tr: "Változások beküldése" },
    "commit.message": { tr: "Beküldés üzenet" },
    "commit.commit": { tr: "Lokális beküldés létrehozása" },
    "merge.title": { en: "Branch merge", tr: "Ág összeolvasztása" },
    "merge.route": { tr: "Összeolvasztás az aktuális ágba" },
    "merge.merge": { en: "Merge" },
    "newBranch.checkout": { tr: "Váltás az ágra létrehozás után" },
    "status.fetching": { tr: "Lekérés folyamatban…" },
    "status.pulling": { tr: "Lehúzás folyamatban…" },
    "status.pushing": { tr: "Feltöltés folyamatban…" },
    "status.committing": { tr: "Lokális beküldés létrehozása…" },
    "status.commitCreated": { tr: "Lokális beküldés létrejött {sha}: {message}" },
    "status.syncing": { en: "Sync folyamatban…" },
    "status.commitUndone": { tr: "Beküldés visszavonva — a változások visszakerültek a working tree-be." },
    "status.stashApplying": { tr: "Félretett munka alkalmazása…" },
    "status.stashPopping": { tr: "Félretett munka kivétele…" },
    "status.stashDropping": { tr: "Félretett munka eldobása…" },
    "status.stashApplied": { tr: "Félretett munka alkalmazva." },
    "status.stashPopped": { tr: "Félretett munka kivéve." },
    "status.stashDropped": { tr: "Félretett munka eldobva." },
    "status.merging": { en: "Merge folyamatban…" },
    "status.merged": { tr: "Az összeolvasztás elkészült." },
    "status.mergeConflict": { tr: "Az összeolvasztás konfliktusos — oldd fel a szerkesztőben, majd commitold." },
  },
  zh: {
    "toolbar.fetch": { en: "⤓ Fetch" },
    "toolbar.pull": { en: "⇩ Pull" },
    "toolbar.push": { en: "⇧ Push" },
    "toolbar.commit": { en: "✓ Commit" },
    "toolbar.sync": { en: "⇅ Sync" },
    "toolbar.jumpHead": { en: "⌖ 跳转到 checkout 位置" },
    "details.header": { en: "Commit 详情" },
    "legend.head": { en: "HEAD / 当前 branch" },
    "legend.local": { en: "本地 branch" },
    "legend.remote": { en: "远程 branch" },
    "legend.remoteOnly": { en: "仅在云端（尚未 pull）" },
    "legend.stash": { en: "Stash（暂存的工作）" },
    "legend.edgeMerge": { en: "被 merge 进来的 branch——箭头指向 merge commit" },
    "legend.edgeBranch": { en: "从这里分出了一个 branch" },
    "legend.edgeStash": { en: "Stash 及其所基于的 commit" },
    "legend.edgeMergedChain": { en: "本 branch 的线穿过被 merge 进来的 commit" },
    "legend.edgeMergedTie": { en: "同一个 commit 在它实际创建的 branch 上" },
    "menu.checkout": { en: "Checkout 此 commit" },
    "menu.copySha": { en: "复制 commit SHA" },
    "menu.pushBranch": { en: 'Push branch “{name}”' },
    "menu.renameBranch": { en: '重命名 branch “{name}”…' },
    "menu.deleteBranch": { en: '删除 branch “{name}”…' },
    "menu.renameCommit": { en: "重命名 commit 信息…" },
    "menu.undoCommit": { en: "撤销 commit（保留更改）…" },
    "menu.mergeBranch": { en: '将 “{source}” merge 到 “{target}”…' },
    "menu.stashApply": { en: "应用 stash" },
    "menu.stashPop": { en: "弹出 stash（应用并移除）" },
    "menu.stashDrop": { en: "丢弃 stash…" },
    "changes.changed": { en: "此 commit" },
    "commit.title": { en: "Commit Changes" },
    "commit.message": { en: "Commit message" },
    "commit.commit": { en: "Create local commit" },
    "merge.title": { en: "Merge branch" },
    "merge.route": { en: "Merge 到当前 branch" },
    "merge.merge": { en: "Merge" },
    "newBranch.checkout": { en: "创建后 checkout branch" },
    "status.fetching": { en: "正在 Fetch…" },
    "status.pulling": { en: "正在 Pull…" },
    "status.pushing": { en: "正在 Push…" },
    "status.committing": { en: "正在创建本地 commit…" },
    "status.commitCreated": { en: "已创建本地 commit {sha}: {message}" },
    "status.syncing": { en: "正在 Sync…" },
    "status.commitUndone": { en: "Commit 已撤销 — 更改已回到工作树中。" },
    "status.stashApplying": { en: "正在应用 stash…" },
    "status.stashPopping": { en: "正在弹出 stash…" },
    "status.stashDropping": { en: "正在丢弃 stash…" },
    "status.stashApplied": { en: "Stash 已应用。" },
    "status.stashPopped": { en: "Stash 已弹出。" },
    "status.stashDropped": { en: "Stash 已丢弃。" },
    "status.merging": { en: "正在 Merge…" },
    "status.merged": { en: "Merge 完成。" },
    "status.mergeConflict": { en: "Merge 存在冲突 — 请在编辑器中解决，然后 commit。" },
  },
  ru: {
    "toolbar.fetch": { en: "⤓ Fetch" },
    "toolbar.pull": { en: "⇩ Pull" },
    "toolbar.push": { en: "⇧ Push" },
    "toolbar.commit": { en: "✓ Commit" },
    "toolbar.sync": { en: "⇅ Sync" },
    "toolbar.jumpHead": { tr: "⌖ Перейти к переключению" },
    "details.header": { en: "Сведения о commit’е" },
    "legend.head": { en: "HEAD / текущий branch" },
    "legend.local": { en: "Локальный branch" },
    "legend.remote": { en: "Удалённый branch" },
    "legend.remoteOnly": { en: "Только в облаке (не сделан pull)" },
    "legend.stash": { tr: "Спрятанное (отложенная работа)" },
    "legend.edgeMerge": { en: "Влитый branch — стрелка указывает на merge commit" },
    "legend.edgeBranch": { en: "Отсюда ответвился branch" },
    "legend.edgeStash": { en: "Stash и commit, на котором он отложен" },
    "legend.edgeMergedChain": { en: "Линия этого branch, проходящая через влитые commit-ы" },
    "legend.edgeMergedTie": { en: "Тот же commit на branch, где он был создан" },
    "menu.checkout": { en: "Checkout на этот commit" },
    "menu.copySha": { en: "Копировать SHA commit’а" },
    "menu.pushBranch": { en: 'Push branch «{name}»' },
    "menu.renameBranch": { en: 'Переименовать branch «{name}»…' },
    "menu.deleteBranch": { en: 'Удалить branch «{name}»…' },
    "menu.renameCommit": { en: "Изменить сообщение commit’а…" },
    "menu.undoCommit": { en: "Отменить commit (сохранить изменения)…" },
    "menu.mergeBranch": { en: 'Merge «{source}» в «{target}»…' },
    "menu.stashApply": { tr: "Применить спрятанное" },
    "menu.stashPop": { tr: "Извлечь спрятанное (применить и удалить)" },
    "menu.stashDrop": { tr: "Отбросить спрятанное…" },
    "changes.changed": { en: "Этот commit" },
    "commit.title": { en: "Commit Changes" },
    "commit.message": { en: "Commit message" },
    "commit.commit": { en: "Создать локальный commit" },
    "merge.title": { en: "Merge branch" },
    "merge.route": { en: "Merge в текущий branch" },
    "merge.merge": { en: "Merge" },
    "newBranch.checkout": { en: "Checkout branch после создания" },
    "status.fetching": { en: "Fetch…" },
    "status.pulling": { en: "Pull…" },
    "status.pushing": { en: "Push…" },
    "status.committing": { en: "Создание локального commit…" },
    "status.commitCreated": { en: "Создан локальный commit {sha}: {message}" },
    "status.syncing": { en: "Sync…" },
    "status.commitUndone": { en: "Commit отменён — изменения вернулись в рабочее дерево." },
    "status.stashApplying": { tr: "Применение спрятанного…" },
    "status.stashPopping": { tr: "Извлечение спрятанного…" },
    "status.stashDropping": { tr: "Отбрасывание спрятанного…" },
    "status.stashApplied": { tr: "Спрятанное применено." },
    "status.stashPopped": { tr: "Спрятанное извлечено." },
    "status.stashDropped": { tr: "Спрятанное отброшено." },
    "status.merging": { en: "Merge…" },
    "status.merged": { en: "Merge завершён." },
    "status.mergeConflict": { en: "В merge есть конфликты — разрешите их в редакторе, затем сделайте commit." },
  },
};

const STORAGE_KEY = "revGraph.lang";

function isLang(v: unknown): v is Lang {
  return typeof v === "string" && LANG_CODES.has(v);
}

/**
 * The default language when the user hasn't picked one yet. A host may override
 * it by defining `window.__REV_GRAPH_DEFAULT_LANG__` before the bundle loads
 * (the DevEco Studio flavor sets it to "zh"); otherwise it's English. An
 * explicit stored choice always wins over this — see load().
 */
function hostDefaultLang(): Lang {
  try {
    const v = (globalThis as { __REV_GRAPH_DEFAULT_LANG__?: unknown }).__REV_GRAPH_DEFAULT_LANG__;
    if (isLang(v)) return v;
  } catch {
    /* ignore */
  }
  return DEFAULT_LANG;
}

function load(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (isLang(v)) return v;
  } catch {
    /* localStorage may be unavailable; fall back to the host/default. */
  }
  return hostDefaultLang();
}

let current: Lang = load();
const listeners = new Set<() => void>();

/** Reflect the active language's writing direction on the document root. */
function applyDir(lang: Lang): void {
  try {
    document.documentElement.setAttribute("dir", isRTL(lang) ? "rtl" : "ltr");
  } catch {
    /* no document (e.g. unit tests) */
  }
}
applyDir(current);

// ---- Keep-Git-terms-in-English setting ------------------------------------
// A separate localStorage-backed toggle (like the display-mode / theme settings)
// that reuses this module's listener set, so switching it re-renders everything
// that already reacts to a language change. Off by default → jargon is translated.
const JARGON_STORAGE_KEY = "revGraph.keepJargonEnglish";

function loadJargon(): boolean {
  try {
    return localStorage.getItem(JARGON_STORAGE_KEY) === "en";
  } catch {
    return false;
  }
}

let keepJargonEnglish = loadJargon();

/** Whether Git terms (pull/push/commit/…) are kept in English. */
export function getKeepJargonEnglish(): boolean {
  return keepJargonEnglish;
}

/** Toggle keep-jargon-English, persist it, and notify subscribers to re-render. */
export function setKeepJargonEnglish(on: boolean): void {
  if (on === keepJargonEnglish) return;
  keepJargonEnglish = on;
  try {
    localStorage.setItem(JARGON_STORAGE_KEY, on ? "en" : "tr");
  } catch {
    /* ignore persistence failures */
  }
  listeners.forEach((l) => l());
}

/** The active language. */
export function getLang(): Lang {
  return current;
}

/** Switch language, persist it, and notify subscribers so the UI re-renders. */
export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore persistence failures */
  }
  applyDir(current);
  listeners.forEach((l) => l());
}

/** Subscribe to language changes; returns an unsubscribe function. */
export function onLangChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Translate a key in the active language, interpolating `{placeholders}`. */
export function t(key: MsgKey, params?: Record<string, string | number>): string {
  let s = DICTS[current]?.[key] ?? DICTS[DEFAULT_LANG][key] ?? key;
  // Git-jargon handling (no-op for English, whose base is already all-English).
  if (current !== DEFAULT_LANG && JARGON_KEYS.has(key)) {
    if (keepJargonEnglish) {
      // ON: keep only the Git terms in English. Hand-maintained languages
      // already author their base string that way, so leave `s` alone; for
      // auto-translated languages fall back to the full English base.
      if (!HAND_MAINTAINED_LANGS.has(current)) s = DICTS[DEFAULT_LANG][key] ?? s;
    } else {
      // OFF: use the fully-translated form; prefer the hand-tuned `tr` variant.
      const tr = JARGON[current]?.[key]?.tr;
      if (tr != null) s = tr;
    }
  }
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.split(`{${k}}`).join(String(v));
    }
  }
  return s;
}
